import { useState, useEffect, useRef, useMemo, useCallback, Fragment, Component } from "react";
import QRCode from "qrcode";
import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, updateProfile, fetchSignInMethodsForEmail } from "firebase/auth";

// ─── Firebase config ──────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyAedg0Bto06EgUtJq01xN6kP0qki6Aa_OQ",
  authDomain: "vehicleiq-38ba2.firebaseapp.com",
  projectId: "vehicleiq-38ba2",
  storageBucket: "vehicleiq-38ba2.firebasestorage.app",
  messagingSenderId: "679456492023",
  appId: "1:679456492023:web:5d3ca9220192b55dc046f2",
  measurementId: "G-GJQ5RSERGF"
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
auth.languageCode = "fr";

// ─── Backend API URL ──────────────────────────────────────────────────────────
const API_URL = "https://vehicleiq-backend-production.up.railway.app";

// ─── Architecture SaaS multi-domaine ──────────────────────────────────────────
// Site PUBLIC (marketing, SEO, blog, formulaires lead)      → vehicleiq.fr
// APPLICATION authentifiée (Mon espace, services, dashboards) → user.vehicleiq.fr
//
// Le MÊME build est déployé sur les deux sous-domaines : le comportement est
// décidé à l'exécution selon l'hôte, ce qui évite tout dédoublement de code et
// garantit qu'un seul codebase reste la source de vérité.
//
// • Sur vehicleiq.fr  : tout ce qui nécessite une connexion redirige vers l'app.
// • Sur user.vehicleiq.fr : l'application complète (auth Firebase + services).
// • En local/preview/Railway : aucune séparation (comportement historique).
const VIQ_APP_ORIGIN    = "https://user.vehicleiq.fr";
const VIQ_PUBLIC_ORIGIN = "https://vehicleiq.fr";
// Interrupteur maître : passez à false pour désactiver instantanément la
// séparation (ex. si le sous-domaine n'est pas encore en ligne) — le site
// redevient alors un mono-domaine, sans aucune redirection.
const VIQ_SPLIT_ENABLED = true;
function viqHost() { try { return (window.location.hostname || "").toLowerCase(); } catch (e) { return ""; } }
// 'app'    → user.vehicleiq.fr           (application authentifiée)
// 'public' → vehicleiq.fr / www.…        (site public marketing)
// 'dev'    → localhost / preview / Railway : aucune séparation
function viqEnv() {
  const h = viqHost();
  if (h.startsWith("user.")) return "app";
  if (VIQ_SPLIT_ENABLED && (h === "vehicleiq.fr" || h === "www.vehicleiq.fr")) return "public";
  return "dev";
}
// Ouvre l'application (depuis le site public) dans un NOUVEL ONGLET, en
// conservant l'intention via ?go=… ; repli sur le même onglet si le popup est
// bloqué (cas d'un déclenchement hors clic utilisateur).
// v36 — Lien formulaire lead personnalisé de Lorentz : plusieurs CTA du site
// public doivent y renvoyer directement (demande de démo, décodage plaque,
// CTA finaux) plutôt que de créer un compte.
const VIQ_LEAD_FORM_URL = "https://vehicleiq.fr/?lead=c8099c37824483e3";
function openLeadForm() {
  try { window.open(VIQ_LEAD_FORM_URL, "_blank", "noopener,noreferrer"); }
  catch (e) { window.location.href = VIQ_LEAD_FORM_URL; }
}

function viqGoToApp(go) {
  const url = VIQ_APP_ORIGIN + (go ? "/?go=" + encodeURIComponent(go) : "/");
  try {
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) window.location.assign(url);
  } catch (e) {
    try { window.location.href = url; } catch (_) {}
  }
}


// Application du thème AVANT le premier rendu (évite un flash au chargement).
// Le mode clair (beige premium) est le défaut. Exécuté à l'évaluation du module.
(function applyInitialTheme() {
  try {
    if (typeof document === "undefined") return;
    const saved = (typeof localStorage !== "undefined") ? localStorage.getItem("viq_theme") : null;
    const dark = saved === "dark"; // défaut : clair
    const apply = () => document.body && document.body.classList.toggle("dark-mode", dark);
    if (document.body) apply();
    else document.addEventListener("DOMContentLoaded", apply);
  } catch (e) { /* no-op */ }
})();

// ─── Favicon officiel VehicleIQ (variante 2 : « V » doré + point bleu sur crème) ──
// Posé à l'exécution via data-URI : indépendant d'un fichier déposé, et garantit
// le bon favicon même si index.html n'est pas mis à jour. Pour le remplacer par
// un .ico/.png hébergé, modifier index.html à la place.
(function setFavicon() {
  try {
    if (typeof document === "undefined") return;
    const svg = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'><rect width='64' height='64' rx='14' fill='%23F8F4EC'/><circle cx='18' cy='20' r='5' fill='%231E2A4A'/><text x='36' y='46' font-family='Georgia, serif' font-size='38' font-weight='700' fill='%23A8803A' text-anchor='middle'>V</text></svg>";
    const href = "data:image/svg+xml," + svg;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.type = "image/svg+xml";
    link.href = href;
  } catch (e) { /* no-op */ }
})();

// ─── Conditions Générales d'Utilisation et de Vente (PDF) ─────────────────────
// Le fichier PDF doit être déposé à la racine du frontend (même niveau que index.html),
// uploadé via FileZilla lors du déploiement. On le référence par une URL relative
// pour rester indépendant du domaine. Modifiable ici si le chemin change.
const CGV_PDF_URL = "/CGU_CGV_VehicleIQ.pdf";
const CGV_PDF_FILENAME = "CGU-CGV-VehicleIQ.pdf";
// Charte graphique (PDF) — déposer Charte_Graphique_VehicleIQ.pdf à la racine du frontend.
const CHARTE_PDF_URL = "/Charte_Graphique_VehicleIQ.pdf";

// Lance le téléchargement du PDF des CGU/CGV (création d'un lien <a download> éphémère).
function downloadCGV() {
  try {
    const a = document.createElement("a");
    a.href = CGV_PDF_URL;
    a.download = CGV_PDF_FILENAME;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (e) {
    // Fallback : ouvrir dans un nouvel onglet si le download programmatique échoue
    window.open(CGV_PDF_URL, "_blank", "noopener");
  }
}

// ─── Indice VIQ — libellé avec date du jour (ISO) ─────────────────────────────
// Remplace les anciennes appellations "Score IQ" et "IQ Score de Confiance"
const viqIndexLabel = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `Indice VIQ_${yyyy}-${mm}-${dd}`;
};



// ─── Blog articles data (10 articles SEO) ────────────────────────────────────
const BLOG_ARTICLES = [
  {
    id: "cout-carte-grise-malus-2026-import",
    cat: "Fiscalité",
    emoji: "🧾",
    title: "Coût de la carte grise et malus écologique 2026 : le guide complet (import compris)",
    excerpt: "Taxe régionale, malus CO₂ WLTP, malus au poids, décote import : comment se calcule réellement le coût d'immatriculation d'un véhicule en France en 2026, y compris pour un import d'Allemagne.",
    date: "9 juin 2026",
    read: "7 min",
    seo: "cout carte grise malus écologique 2026 immatriculation import allemagne belgique luxembourg",
    body: `
<h2>De quoi se compose le coût d'une carte grise en 2026 ?</h2>
<p>Le coût total d'un certificat d'immatriculation (ex-carte grise) ne se résume pas à une seule taxe. Il agrège plusieurs lignes distinctes, dont certaines varient fortement selon le véhicule, sa motorisation et la région du titulaire. Pour un professionnel qui achète et revend, une cote fiable de ce coût conditionne directement la marge.</p>
<div class="highlight">Quatre composantes principales : la taxe régionale (Y.1), le malus CO₂, le malus au poids (TMOM) et les taxes fixes de gestion et d'acheminement.</div>
<h2>1. La taxe régionale (Y.1)</h2>
<p>C'est le socle du coût. Elle se calcule en multipliant la <strong>puissance fiscale (CV)</strong> du véhicule par le <strong>tarif du cheval fiscal</strong> voté par la région du titulaire. En 2026, ce tarif s'échelonne d'environ 36,5 € (Hauts-de-France) à 60 € (Grand Est, Normandie, PACA) selon les régions. L'Île-de-France ajoute par ailleurs une majoration forfaitaire au profit de l'autorité organisatrice des mobilités.</p>
<p>Bon à savoir : pour un véhicule de plus de 10 ans, la taxe régionale est réduite de moitié.</p>
<h2>2. Le malus écologique au CO₂</h2>
<p>Le malus CO₂ frappe les véhicules les plus émetteurs lors de leur première immatriculation en France. Il suit un <strong>barème annuel</strong> exprimé en grammes de CO₂ par kilomètre (norme WLTP), qui se durcit chaque année : le seuil de déclenchement baisse et le plafond augmente. En 2026, ce plafond atteint un niveau record.</p>
<h3>Le cas des véhicules importés d'occasion</h3>
<p>Pour un véhicule d'occasion importé (Allemagne, Belgique, Luxembourg, autre pays de l'UE), le malus n'est pas appliqué au plein tarif neuf : il bénéficie d'une <strong>décote selon l'ancienneté</strong> du véhicule. Plus la première mise en circulation est lointaine, plus l'abattement est élevé, jusqu'à exonération totale au-delà d'un certain âge. Les motorisations hybrides bénéficient en outre d'un abattement spécifique sur les émissions retenues.</p>
<div class="highlight">Un véhicule importé de plus de 15 ans est exonéré de malus CO₂ et de malus au poids — un point décisif pour le sourcing de youngtimers et de véhicules de collection.</div>
<h2>3. Le malus au poids (TMOM)</h2>
<p>La taxe sur la masse en ordre de marche s'ajoute au malus CO₂ pour les véhicules les plus lourds. En 2026, elle se déclenche dès <strong>1 500 kg</strong>, avec un barème progressif par tranches. Les véhicules électriques et hydrogène en sont exonérés, et des abattements s'appliquent aux hybrides et hybrides rechargeables. Comme le malus CO₂, elle profite de la décote import pour les véhicules d'occasion venus de l'UE.</p>
<h2>4. Les taxes fixes</h2>
<p>S'ajoutent enfin une taxe de gestion et une taxe d'acheminement du titre, de quelques euros, identiques pour tous les dossiers.</p>
<h2>Pourquoi estimer ce coût avant d'acheter à l'import</h2>
<p>Acheter un véhicule premium 10 à 20 % moins cher en Allemagne peut sembler une excellente affaire — jusqu'à ce que le malus et le coût d'immatriculation viennent rogner, voire annuler, l'économie réalisée. Sur un véhicule fortement émetteur récemment immatriculé, le malus CO₂ peut représenter plusieurs dizaines de milliers d'euros. La décote selon l'ancienneté change radicalement l'équation d'une année de mise en circulation à l'autre.</p>
<p>C'est exactement le calcul que l'outil <strong>Carte grise & malus</strong> de VehicleIQ automatise : à partir de la région, de la puissance fiscale, des émissions WLTP, de la masse et de la date de première immatriculation — ou directement par décodage de plaque qui préremplit immatriculation, VIN et véhicule — il restitue en quelques secondes une cote détaillée, ligne par ligne, du coût d'immatriculation, import compris. De quoi sécuriser une marge avant même de se déplacer.</p>
<p><em>Cote indicative fondée sur les barèmes 2026 ; le montant officiel est calculé lors de la démarche sur le service public, et les barèmes évoluent chaque année (loi de finances).</em></p>
`
  },
  {
    id: "marche-occasion-france-2025",
    cat: "Marché",
    emoji: "📊",
    title: "État du marché de l'occasion en France : chiffres, tendances et perspectives 2025",
    excerpt: "Le marché de l'occasion français a franchi les 5,7 millions de transactions en 2024. Décryptage des dynamiques qui reconfigurent le secteur pour les professionnels.",
    date: "12 mai 2026",
    read: "8 min",
    seo: "marché occasion france 2025 statistiques",
    body: `
<h2>Un marché en mutation profonde</h2>
<p>Avec <strong>5,7 millions de véhicules d'occasion</strong> échangés en 2024 selon les données AAA Data, le marché VO français confirme sa résistance face aux turbulences économiques. Si le neuf souffre (+2,1% seulement sur l'année), l'occasion s'impose comme le vrai moteur du secteur automobile.</p>
<div class="highlight">Le ratio VO/VN atteint désormais 2,7 transactions d'occasion pour 1 vente de neuf — un record historique qui consacre le marché de la seconde main comme pilier du secteur.</div>
<h2>Les grandes tendances 2024-2025</h2>
<h3>1. La compression des prix s'accélère</h3>
<p>Après la flambée post-Covid (+18% en 2021-2022), les prix VO marquent une correction de <strong>-6 à -9%</strong> sur les segments généralistes. Les SUV hybrides résistent mieux (-2,3%) tandis que les diesels ancienne génération plongent (-14% pour les véhicules > 150 000 km).</p>
<h3>2. Le kilométrique moyen à la vente repart à la hausse</h3>
<p>Le kilométrage moyen des VO vendus est remonté à 78 400 km en 2024, contre 71 200 km en 2022. Signe que les acheteurs, contraints budgétairement, acceptent des véhicules plus roulés.</p>
<h3>3. La digitalisation s'installe durablement</h3>
<p>63% des transactions VO impliquent désormais une phase de recherche en ligne prolongée (contre 41% en 2019). Les plateformes LeBonCoin Auto, AutoScout24 et La Centrale concentrent 78% du volume d'annonces.</p>
<h2>Ce que cela signifie pour les professionnels</h2>
<p>Dans ce contexte de compression des marges et de volatilité des prix, la <strong>précision de la Cote</strong> devient un avantage compétitif décisif. Une erreur de 5% sur la Cote d'un VO à 25 000 € représente 1 250 € de marge non capturée — ou de perte sèche si la reprise est sur-estimée.</p>
<p>C'est précisément pour répondre à ce besoin que des outils comme VehicleIQ émergent : calibrer le prix juste en temps réel, pas sur des barèmes figés à J-30.</p>
`
  },
  {
    id: "acteurs-marche-occasion-argus-lacentrale-autoscout",
    cat: "Concurrence",
    emoji: "🏁",
    title: "Les 3 acteurs majeurs du marché VO en France : Argus, La Centrale, AutoScout24",
    excerpt: "Argus, La Centrale, AutoScout24 — trois modèles différents, trois lectures du marché. Analyse comparative de leur positionnement et de leurs limites pour les pros.",
    date: "5 mai 2026",
    read: "7 min",
    seo: "argus la centrale autoscout24 comparatif cote automobile",
    body: `
<h2>L'Argus : la référence historique sous pression</h2>
<p>Fondé en 1927, <strong>L'Argus</strong> est la référence de la cote automobile en France. Sa méthodologie repose sur des barèmes calculés à partir de transactions réelles et de panels de professionnels, mis à jour mensuellement.</p>
<div class="highlight">Force : opposabilité juridique reconnue. Limite : mise à jour mensuelle dans un marché qui bouge quotidiennement.</div>
<p>Pour un gestionnaire de flotte qui achète 30 véhicules par semaine, une cote Argus vieille de 28 jours peut représenter un écart de marché de 4 à 8% sur un segment volatil.</p>
<h2>La Centrale : le volume comme force brute</h2>
<p><strong>La Centrale</strong> agrège plus de 650 000 annonces actives et propose une cote basée sur la médiane des prix constatés. Avantage : elle reflète le prix réel du marché en temps quasi-réel. Limite : elle ne distingue pas les annonces pertinentes (kilométrage cohérent, état correct) des outliers qui biaisent la médiane.</p>
<h2>AutoScout24 : la puissance européenne</h2>
<p>Avec 30 millions de véhicules référencés en Europe, <strong>AutoScout24</strong> offre une perspective transfrontalière unique. Particulièrement utile pour les véhicules premium dont les prix varient selon les marchés (Allemagne, Belgique, Italie).</p>
<h2>Ce qui manque à tous les trois</h2>
<p>Aucun de ces acteurs ne propose de <strong>fusion algorithmique multi-sources</strong> couplée à l'historique SIV officiel en une seule requête. C'est exactement le créneau que VehicleIQ a identifié et breveté.</p>
`
  },
  {
    id: "vehicleiq-valeur-ajoutee-disruption",
    cat: "VehicleIQ",
    emoji: "⚡",
    title:"VehicleIQ.fr : en quoi est-il réellement disruptif sur le marché de la Cote auto ?",
    excerpt:"Algorithme INPI, fusion 70/30 marché/algo, API SIV officielle, pay-as-you-go — ce qui différencie réellement VehicleIQ des outils de Cote traditionnels.",
    date: "28 avril 2026",
    read: "6 min",
    seo:"vehicleiq disruption cote automobile algorithme IA",
    body: `
<h2>La disruption n'est pas dans le produit, elle est dans la temporalité</h2>
<p>Ce qui différencie VehicleIQ n'est pas d'être "meilleur" que l'Argus ou La Centrale. C'est d'opérer sur une <strong>temporalité différente</strong> : le temps réel contre le barème mensuel.</p>
<div class="highlight">Quand le marché des SUV compacts recule de 3% en 10 jours suite à une annonce fiscale, VehicleIQ l'intègre immédiatement. L'Argus attendra sa prochaine mise à jour mensuelle.</div>
<h2>L'algorithme VIQ.AI® Engine : 3 niveaux de différenciation</h2>
<h3>1. La fusion 70/30 brevetée INPI (v5 mai 2026)</h3>
<p>La Cote finale combine 70% de données marché temps réel (LeBonCoin, AutoScout24, La Centrale, Facebook Marketplace) et 30% d'un algorithme propriétaire à 12 coefficients incluant un multiplicateur pays constructeur. Ce ratio a été calibré pour coller au plus près du marché réel tout en évitant la sur-réactivité aux outliers. Les cotes restent des estimations indicatives.</p>
<h3>2. L'intégration SIV officielle</h3>
<p>Via l'API du Ministère de l'Intérieur, chaque cote peut être couplée à l'historique officiel : kilométrage au contrôle technique, nombre de propriétaires, sinistres déclarés. Un rapport détaillé, pas une opinion.</p>
<h3>3. Le modèle économique pay-as-you-go</h3>
<p>Aucun acteur B2B ne propose ça : zéro abonnement, zéro minimum, paiement au tarif unique de 4,99 € par dossier. Pour une PME qui traite 50 reprises par mois, c'est une économie de 4 000 à 12 000 € annuels vs les offres concurrentes.</p>
`
  },
  {
    id: "controle-historique-vehicule-vin-plaque",
    cat: "Services",
    emoji: "🔍",
    title: "Contrôle historique VIN & plaque : pourquoi c'est indispensable avant toute reprise",
    excerpt: "Fraude kilométrique, sinistres non déclarés, gages, faux CT — le contrôle historique via SIV protège acheteurs et professionnels. Mode d'emploi.",
    date: "20 avril 2026",
    read: "5 min",
    seo: "controle historique vehicule VIN plaque immatriculation SIV",
    body: `
<h2>La fraude kilométrique : un fléau sous-estimé</h2>
<p>Selon une étude Carfax Europe, <strong>1 véhicule d'occasion sur 7</strong> présente une anomalie kilométrique en France. Pour un professionnel de la reprise, l'absence de vérification systématique expose à une décote de 1 500 à 4 000 € par véhicule mal estimé.</p>
<h2>Ce que contient un rapport historique SIV</h2>
<ul>
<li><strong>Kilométrage au dernier contrôle technique</strong> : le seul kilométrage relevé au contrôle technique</li>
<li><strong>Nombre de propriétaires</strong> : chaque transfert de carte grise est tracé</li>
<li><strong>Historique des CT</strong> : résultats, contre-visites, date du prochain CT</li>
<li><strong>Gage et opposition</strong> : le véhicule est-il sous crédit non soldé ?</li>
<li><strong>Déclaration d'accident</strong> : sinistres déclarés en assurance</li>
</ul>
<div class="highlight">Le contrôle VehicleIQ interroge l'API SIV officielle du Ministère de l'Intérieur — les mêmes données qu'une préfecture, accessibles en 30 secondes.</div>
<h2>Pour qui c'est critique ?</h2>
<p>Les professionnels les plus exposés sont les mandataires indépendants (pas de réseau d'audit), les gestionnaires de flotte (volume élevé = risque démultiplié) et les particuliers achetant sans courtier.</p>
`
  },
  {
    id: "estimation-prix-vehicule-occasion-algorithme",
    cat: "Services",
    emoji: "💰",
    title:"Cote de prix VO : comment l'IA calibre le bon prix en temps réel",
    excerpt:"La Cote d'un véhicule d'occasion n'est pas un calcul fixe. Découvrez comment VIQ.AI® Engine fusionne données marché et algorithme propriétaire pour coller au marché réel.",
    date: "14 avril 2026",
    read: "5 min",
    seo:"cote prix vehicule occasion algorithme IA temps réel",
    body: `
<h2>Pourquoi les barèmes traditionnels sont insuffisants</h2>
<p>Une cote Argus est calculée sur des transactions passées, lissées sur 30 jours. Dans un marché qui réagit en quelques jours à des événements (annonce fiscale, arrivée d'un nouveau modèle, saisonnalité), ce décalage crée des opportunités d'arbitrage — dans les deux sens.</p>
<h2>La mécanique VIQ.AI® Engine</h2>
<p>L'algorithme opère en 3 phases :</p>
<ul>
<li><strong>Scan temps réel</strong> : 100 000+ annonces actives sur LeBonCoin, AutoScout24, La Centrale</li>
<li><strong>Filtrage cohérence</strong> : élimination des outliers (prix anormalement bas = fraude probable, prix hauts = particuliers mal informés)</li>
<li><strong>Calibrage 11 coefficients</strong> : kilométrage, état déclaré, géographie, saisonnalité, segment, motorisation, couleur, options, financement inclus</li>
</ul>
<div class="highlight">Résultat : une cote indicative calibrée sur le marché réel, sur +8000 modèles, en 30 secondes. Sans saisie manuelle au-delà de la plaque d'immatriculation.</div>
`
  },
  {
    id: "recommandations-achat-ia-vehicule",
    cat: "Services",
    emoji: "🎯",
    title: "Recommandations d'achat IA : trouver le bon véhicule sans passer des heures à chercher",
    excerpt: "10 questions, 30 secondes, 10 annonces qualifiées. Comment VehicleIQ utilise l'IA pour scorer et filtrer les meilleures offres selon votre profil.",
    date: "7 avril 2026",
    read: "4 min",
    seo: "recommandation achat vehicule occasion IA personnalisé",
    body: `
<h2>Le problème : trop d'annonces, trop peu de signal</h2>
<p>LeBonCoin propose plus de 800 000 annonces automobile actives. AutoScout24 en affiche 650 000. Pour un acheteur avec un budget et des critères précis, ce volume est contre-productif : il noie le signal dans le bruit.</p>
<h2>Le système de scoring VehicleIQ</h2>
<p>En répondant à 10 questions (budget, usage, kilométrage souhaité, carburant préféré, taille, boîte, options prioritaires), l'IA construit un profil d'acheteur vectorisé. Elle le confronte ensuite aux annonces actives pour générer un score de pertinence sur 100.</p>
<ul>
<li><strong>Score > 85</strong> : correspondance quasi-parfaite</li>
<li><strong>Alerte fraude intégrée</strong> : prix anormalement bas détecté automatiquement</li>
<li><strong>Prix marché validé</strong> : chaque annonce est comparée à la Cote VIQ pour détecter les sur-évaluations</li>
</ul>
<div class="highlight">Pour un gestionnaire de flotte qui renouvelle 30 véhicules par trimestre, le gain de temps est de 2 à 4 heures par dossier.</div>
`
  },
  {
    id: "vehicule-electrique-marche-occasion-impact",
    cat: "Tendances",
    emoji: "⚡",
    title: "Véhicule électrique et marché de l'occasion : ce que le grand changement va bouleverser",
    excerpt: "La montée en puissance de l'électrique rebat les cartes du marché VO. Valeur résiduelle, batterie, recharge, fiscalité — les pros doivent se préparer maintenant.",
    date: "1 avril 2026",
    read: "7 min",
    seo: "vehicule electrique occasion marché impact valeur résiduelle",
    body: `
<h2>L'électrique d'occasion : un marché en formation rapide</h2>
<p>En 2024, <strong>78 000 véhicules électriques</strong> ont été vendus en occasion en France (+42% vs 2023). Ce chiffre reste marginal (1,3% du total VO) mais la courbe est exponentielle.</p>
<h2>La valeur résiduelle : le vrai défi</h2>
<p>Les premières générations d'électriques (Zoe, Leaf, e-Golf) subissent des décotes spectaculaires : -55 à -65% à 5 ans contre -40% pour un thermique équivalent. La raison : l'incertitude sur la santé de la batterie.</p>
<div class="highlight">Un Renault Zoe 2019 cote aujourd'hui 9 500 à 12 000 € sur le marché français, contre 22 000 € neuf en 2019. La dépréciation dépasse celle d'un Clio diesel équivalent.</div>
<h2>Ce que cela signifie pour les pros</h2>
<p>La reprise d'un VE exige un diagnostic batterie certifié. Sans cela, la Cote est impossible. Les outils traditionnels (Argus, cotes constructeur) n'intègrent pas encore cette donnée. VehicleIQ travaille à l'intégration du score batterie dans son algorithme pour 2025.</p>
`
  },
  {
    id: "diesel-occasion-avenir-marche-france",
    cat: "Tendances",
    emoji: "🔧",
    title: "Le diesel d'occasion en France : faut-il encore en vendre ou en acheter en 2025 ?",
    excerpt: "ZFE, taxe malus, image dégradée — le diesel souffre. Mais reste le moteur dominant du parc VO. Analyse des dynamiques pour les professionnels.",
    date: "24 mars 2026",
    read: "6 min",
    seo: "diesel occasion france 2025 avenir vente achat ZFE",
    body: `
<h2>Le diesel : toujours dominant, mais pour combien de temps ?</h2>
<p>Malgré sa mauvaise presse, le diesel représente encore <strong>48% des véhicules d'occasion</strong> vendus en France en 2024. Pour les véhicules utilitaires, ce chiffre dépasse 70%.</p>
<h2>Les facteurs qui pèsent sur les prix</h2>
<ul>
<li><strong>ZFE (Zones à Faibles Émissions)</strong> : 45 agglomérations ont ou auront des restrictions. Un diesel Crit'Air 2 est encore accepté dans la plupart des villes, mais les perspectives inquiètent les acheteurs.</li>
<li><strong>Malus kilométrique</strong> : les diesels > 110g CO2/km sont pénalisés à la revente</li>
<li><strong>Coût de l'entretien</strong> : la particularité du FAP pèse sur les acheteurs informés</li>
</ul>
<div class="highlight">Un diesel Crit'Air 3 ou 4 peut présenter une décote supplémentaire de 8 à 15% dans les régions avec ZFE actives. Un paramètre que les outils traditionnels n'intègrent pas encore dans leurs cotations.</div>
<h2>La stratégie professionnelle</h2>
<p>Pour les concessionnaires, la reprise de diesel ancienne génération (> 2015) est un actif à risque. La liquidité se dégrade. VehicleIQ intègre la variable géographique et le coefficient ZFE dans son algorithme pour les villes concernées.</p>
`
  },
  {
    id: "zfe-politique-publique-automobile-france",
    cat: "Réglementation",
    emoji: "🏙️",
    title: "ZFE, vignette Crit'Air et politiques publiques : ce qui change pour le marché auto",
    excerpt: "Les Zones à Faibles Émissions reconfigurent le marché VO dans les grandes agglomérations. Ce que chaque professionnel doit savoir pour adapter son stock.",
    date: "17 mars 2026",
    read: "6 min",
    seo: "ZFE zones faibles emissions crit'air voiture occasion impact",
    body: `
<h2>Le cadre légal : où en est-on ?</h2>
<p>La loi LOM (Loi d'Orientation des Mobilités) impose aux agglomérations de plus de 150 000 habitants de mettre en place une ZFE. En 2025, <strong>11 métropoles</strong> ont des ZFE actives avec des restrictions effectives (Paris, Lyon, Grenoble, Strasbourg, Rouen, Reims, Montpellier, Nice, Toulouse, Bordeaux, Marseille).</p>
<h2>Crit'Air : le classement qui reconfigure le marché</h2>
<ul>
<li><strong>Crit'Air 1</strong> (essence > 2011, hybrides) : autorisés partout</li>
<li><strong>Crit'Air 2</strong> (diesel > 2011, essence > 2006) : restreints dans certaines ZFE aux heures de pointe</li>
<li><strong>Crit'Air 3, 4, 5</strong> : interdits dans la plupart des ZFE actives</li>
</ul>
<div class="highlight">L'impact sur la valeur : un véhicule Crit'Air 3 vendu en Île-de-France subit une décote de 12 à 18% supplémentaire par rapport au même modèle en zone rurale. VehicleIQ intègre ce paramètre géographique dans ses cotes.</div>
<h2>La stratégie stock pour les pros</h2>
<p>Éviter les reprises Crit'Air 3+ dans les grandes agglomérations sauf prix très agressif. Privilégier les hybrides, les Crit'Air 1 et les électriques qui bénéficient au contraire d'une prime dans les marchés urbains.</p>
`
  },
  {
    id: "gestion-flotte-automobile-professionnelle-optimisation",
    cat: "Gestion",
    emoji: "🚛",
    title: "Gestion de flotte automobile : comment optimiser le renouvellement avec la data",
    excerpt: "Les gestionnaires de flotte font face à un défi croissant : renouveler au bon moment, au bon prix. La data temps réel change la donne.",
    date: "10 mars 2026",
    read: "5 min",
    seo: "gestion flotte automobile optimisation renouvellement data IA",
    body: `
<h2>Le cycle de renouvellement : un calcul complexe mal outillé</h2>
<p>La plupart des gestionnaires de flotte décident du moment de renouvellement sur des critères kilométriques fixes (ex: 120 000 km ou 4 ans). Mais ce modèle ignore la dynamique du marché VO : revendre un véhicule au moment où sa valeur résiduelle se contracte, c'est laisser de l'argent sur la table.</p>
<h2>La valeur de la data temps réel</h2>
<p>Avec VehicleIQ, un gestionnaire peut estimer en temps réel la valeur de cession de chaque véhicule de sa flotte et la comparer à la courbe de dépréciation projetée. Si le marché offre 15% de plus que la valeur résiduelle théorique à J+180, c'est le signal pour arbitrer maintenant.</p>
<div class="highlight">Une flotte de 50 véhicules optimisée avec des cotes VIQ.AI® au lieu de barèmes constructeur peut générer un delta de valeur résiduelle de 45 000 à 80 000 € sur un cycle de 3 ans.</div>
<h2>L'intégration dans le workflow</h2>
<p>VehicleIQ propose des cotes en simultané (jusqu'à 3 véhicules en parallèle), exportables en PDF pour chaque fiche de cession. Un processus qui réduit le temps de traitement de 85% par rapport aux méthodes traditionnelles.</p>
`
  },
];


// ─── HeroRotatingText — animated cycling professional segments ──────────────
function HeroRotatingText() {
  const words = [
    "professionnels de l'occasion",
    "concessionnaires auto",
    "gestionnaires de flotte",
    "assureurs & gestionnaires",
    "agences de location auto",
  ];
  const [idx, setIdx] = useState(0);
  const [animOut, setAnimOut] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimOut(true);
      setTimeout(() => {
        setIdx(i => (i + 1) % words.length);
        setAnimOut(false);
      }, 500);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="hero-rotate-wrap">
      <em className={`hero-rotate-word${animOut ? " out" : ""}`}
          style={{ color:"var(--accent)", fontStyle:"normal" }}>
        {words[idx]}
      </em>
    </span>
  );
}


// ─── BlogPage — full page blog ───────────────────────────────────────────────
function BlogPage({ onClose, onContact }) {
  const [article, setArticle] = useState(null);
  const [catFilter, setCatFilter] = useState("Tous");
  // v73 — Règle : les articles publiés depuis le Super Admin sont maintenant
  // persistés côté serveur (voir /api/blog/articles) — fusionnés ici avec
  // les articles SEO statiques existants, sans les modifier.
  const [backendArticles, setBackendArticles] = useState([]);
  useEffect(() => {
    apiGet("/api/blog/articles").then(r => {
      if (r?.success) {
        setBackendArticles(r.articles.map(a => ({
          id: "custom-" + a.id, title: a.title, cat: a.category || "VehicleIQ", emoji: a.emoji || "📰",
          excerpt: a.excerpt, body: a.body, coverImage: a.cover_image,
          metaTitle: a.meta_title, metaDescription: a.meta_description, slug: a.slug,
          date: new Date(a.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }),
          read: "5 min",
        })));
      }
    }).catch(() => {});
  }, []);
  const allArticles = [...backendArticles, ...BLOG_ARTICLES];
  const cats = ["Tous", ...Array.from(new Set(allArticles.map(a => a.cat)))];
  const filtered = catFilter === "Tous" ? allArticles : allArticles.filter(a => a.cat === catFilter);

  // v82 — Règle SEO/GEO : upgrade vers viqSetMeta (canonical, og:*, twitter:*
  // — absents jusqu'ici sur le blog) + données structurées Article, en plus
  // du titre/description déjà gérés. Toujours appelé sans condition (règle
  // des Hooks), le comportement conditionnel reste à l'intérieur.
  useEffect(() => {
    if (!article) { viqSetMeta({ title: "Blog VehicleIQ — Ressources & Analyses du marché automobile", description: "Actualités, tendances et analyses du marché de la voiture d'occasion en France : cote, réglementation, outils professionnels.", path: "/blog" }); return; }
    const articlePath = article.slug ? `/blog/${article.slug}` : "/blog";
    viqSetMeta({
      title: article.metaTitle || article.title,
      description: article.metaDescription || article.excerpt,
      path: articlePath,
      image: article.coverImage,
      type: "article",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.metaDescription || article.excerpt,
        "datePublished": article.date,
        ...(article.coverImage ? { "image": article.coverImage } : {}),
        "author": { "@type": "Organization", "name": "VehicleIQ" },
        "publisher": { "@type": "Organization", "name": "VehicleIQ", "url": "https://vehicleiq.fr" },
      },
    });
  }, [article]);

  if (article) {
    return (
      <div className="blog-overlay">
        <div className="blog-nav">
          <button onClick={() => setArticle(null)}
            style={{ display:"flex", alignItems:"center", gap:".4rem", fontSize:".82rem", color:"var(--sub)", background:"none", border:"none", cursor:"pointer" }}>
            ← Retour au blog
          </button>
          <div className="logo" style={{ fontSize:".95rem" }}>
            <div className="logo-dot"/>Vehicle<span>IQ</span> <span style={{ color:"var(--muted)", fontWeight:400, fontSize:".75rem", marginLeft:4 }}>Blog</span>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="blog-article">
          <div className="blog-article-cat">{article.cat}</div>
          <h1 className="blog-article-title">{article.title}</h1>
          <div className="blog-article-meta">
            <span>✍️ Équipe VehicleIQ</span>
            <span>📅 {article.date}</span>
            <span>⏱ {article.read} de lecture</span>
          </div>
          {article.coverImage && <img src={article.coverImage} alt={article.title} style={{ width: "100%", maxHeight: 360, objectFit: "cover", borderRadius: 12, margin: "1rem 0" }} />}
          <div className="blog-article-body" dangerouslySetInnerHTML={{ __html: article.body }} />
          {/* CTA bottom */}
          <div style={{ marginTop:"3rem", padding:"2rem", background:"rgba(168,128,58,.05)", border:"1px solid rgba(168,128,58,.15)", borderRadius:14, textAlign:"center" }}>
            <div style={{ fontSize:"1.1rem", fontWeight:800, color:"var(--text)", marginBottom:".5rem" }}>
              Prêt à tester VehicleIQ Pro ?
            </div>
            <div style={{ fontSize:".85rem", color:"var(--sub)", marginBottom:"1.2rem" }}>
              5 cotes offertes · Sans engagement · SIRET requis pour l'accès Pro
            </div>
            <div style={{ display:"flex", gap:".8rem", justifyContent:"center", flexWrap:"wrap" }}>
              <button className="btn btn-accent" onClick={onClose}>Commencer maintenant →</button>
              <button className="btn btn-outline" onClick={onContact}>Nous contacter</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-overlay">
      <div className="blog-nav">
        <div className="logo" style={{ fontSize:"1rem" }}>
          <div className="logo-dot"/>Vehicle<span>IQ</span> <span style={{ color:"var(--muted)", fontWeight:400, fontSize:".75rem", marginLeft:4 }}>Blog</span>
        </div>
        <div style={{ display:"flex", gap:".4rem", flexWrap:"wrap" }}>
          {cats.map(c => (
            <button key={c} onClick={() => setCatFilter(c)}
              style={{ padding:".28rem .65rem", borderRadius:50, border:"1px solid", fontSize:".72rem", cursor:"pointer",
                borderColor: catFilter===c ? "var(--accent)" : "var(--border)",
                background:  catFilter===c ? "var(--accent)" : "transparent",
                color:       catFilter===c ? "#fff" : "var(--sub)" }}>
              {c}
            </button>
          ))}
        </div>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>
      <div className="blog-hero">
        <div className="blog-hero-tag">
          <span>✍️</span> Intelligence Automobile · Blog Pro
        </div>
        <h1 className="blog-hero-title">Ressources & Analyses</h1>
        <p className="blog-hero-sub">Marchés, tendances, réglementation — le blog des professionnels de l'automobile</p>
      </div>
      <div className="blog-grid">
        {filtered.map(art => (
          <div key={art.id} className="blog-card" onClick={() => setArticle(art)}>
            <div className="blog-card-img">
              <span>{art.emoji}</span>
              <div className="blog-card-cat">{art.cat}</div>
            </div>
            <div className="blog-card-body">
              <div className="blog-card-title">{art.title}</div>
              <div className="blog-card-excerpt">{art.excerpt}</div>
              <div className="blog-card-meta">
                <span className="blog-card-date">📅 {art.date}</span>
                <span className="blog-card-read">· {art.read}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── RecentArticles — 3 latest articles on homepage ─────────────────────────
function RecentArticles({ onOpenBlog }) {
  const recent = BLOG_ARTICLES.slice(0, 3);
  return (
    <div className="recent-articles">
      <div style={{ maxWidth:"1100px", margin:"0 auto" }}>
        <div className="sec-eyebrow" style={{ color:"var(--accent)" }}>Blog</div>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:"1rem", marginBottom:0 }}>
          <div className="sec-title" style={{ marginBottom:0 }}>Dernières analyses</div>
          <button className="btn btn-ghost btn-sm" onClick={onOpenBlog}>
            Voir tous les articles →
          </button>
        </div>
        <div className="recent-art-grid">
          {recent.map(art => (
            <div key={art.id} className="blog-card" onClick={onOpenBlog}
              style={{ cursor:"pointer" }}>
              <div className="blog-card-img" style={{ height:140 }}>
                <span>{art.emoji}</span>
                <div className="blog-card-cat">{art.cat}</div>
              </div>
              <div className="blog-card-body">
                <div className="blog-card-title">{art.title}</div>
                <div className="blog-card-excerpt" style={{ WebkitLineClamp:2, overflow:"hidden", display:"-webkit-box", WebkitBoxOrient:"vertical" }}>
                  {art.excerpt}
                </div>
                <div className="blog-card-meta">
                  <span className="blog-card-date">📅 {art.date}</span>
                  <span className="blog-card-read">· {art.read}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Sound utility — DÉSACTIVÉ ────────────────────────────────────────────
// La fonction est conservée vide pour éviter de casser les appels existants.
// L'utilisateur a demandé le retrait du son sur clic de service.
function playEstimationClick() {
  // No-op
}

// ─── useCountUp — compteur animé 0 → valeur finale ──────────────────────────
function useCountUp(target, duration = 900, trigger = true) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!trigger || !target) return;
    let start = null;
    const from = 0;
    const step = (ts) => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / duration, 1);
      // easeOutExpo
      const ease = prog === 1 ? 1 : 1 - Math.pow(2, -10 * prog);
      setVal(Math.round(from + (target - from) * ease));
      if (prog < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, trigger]);
  return val;
}


// ─── ScoreGauge — jauge circulaire SVG ───────────────────────────────────────
// v21 — Écran de blocage réutilisable pour les fonctionnalités réservées aux
// comptes Pro (Solo ou Multi-agents) : Aide à l'achat, Estimateur de malus,
// Contrôler un véhicule, formulaires clients, cotation par photos.
function ProFeatureGate({ title, onUpgrade, onClose }) {
  return (
    <div style={{ textAlign: "center", padding: "2.5rem 1.5rem" }}>
      <div style={{ fontSize: "2.2rem", marginBottom: ".8rem" }}>🔒</div>
      <div style={{ fontSize: "1.15rem", fontWeight: 800, color: "var(--text)", marginBottom: ".5rem" }}>
        {title || "Fonctionnalité réservée aux comptes Pro"}
      </div>
      <div style={{ fontSize: ".88rem", color: "var(--sub)", maxWidth: 420, margin: "0 auto 1.4rem", lineHeight: 1.5 }}>
        Cette fonctionnalité fait partie des forfaits <strong>Pro Solo</strong> et <strong>Pro Multi-agents</strong>. Passez à un compte professionnel pour y accéder.
      </div>
      <div style={{ display: "flex", gap: ".6rem", justifyContent: "center", flexWrap: "wrap" }}>
        <button className="btn btn-accent" onClick={onUpgrade}>Voir les forfaits Pro →</button>
        {onClose && <button className="btn btn-ghost" onClick={onClose}>Fermer</button>}
      </div>
    </div>
  );
}

function ScoreGauge({ score, size = 80, animate = true }) {
  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    if (!animate) { setDisplayed(score); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / 1000, 1);
      const ease = 1 - Math.pow(2, -10 * prog);
      setDisplayed(Math.round(score * ease));
      if (prog < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [score]);

  const r = (size / 2) - 6;
  const circ = 2 * Math.PI * r;
  const pct = displayed / 100;
  const color = displayed >= 80 ? "#00C864" : displayed >= 60 ? "#FFB800" : "var(--accent)";
  const trackColor = "rgba(255,255,255,.07)";
  return (
    <div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
      <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={trackColor} strokeWidth={5} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset .05s linear", filter:`drop-shadow(0 0 4px ${color}88)` }}
        />
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
        <span style={{ fontFamily:"var(--font-head)", fontSize: size*0.22+"px", fontWeight:700, color, lineHeight:1 }}>{displayed}</span>
        <span style={{ fontSize: size*0.10+"px", color:"var(--muted)", lineHeight:1.2 }}>/100</span>
      </div>
    </div>
  );
}

// ─── IQGaugeHero — Jauge circulaire premium pour la page d'accueil ──────────
function IQGaugeHero({ price = 42580, low = 38200, high = 47100, confidence = 94, freshHours = 3 }) {
  const [displayed, setDisplayed] = useState(0);
  const [ringPct, setRingPct] = useState(0);
  const isFresh = freshHours <= 24;

  useEffect(() => {
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / 1200, 1);
      const ease = 1 - Math.pow(2, -10 * prog);
      setDisplayed(Math.round(price * ease));
      setRingPct(ease * (confidence / 100));
      if (prog < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [price, confidence]);

  const size = 110;
  const r = 46;
  const circ = 2 * Math.PI * r;
  const accent = "var(--accent)";
  const freshColor = isFresh ? "#0F766E" : "#F59E0B";

  return (
    <div className="iq-gauge-card">
      <div style={{ fontSize:".7rem", fontWeight:700, color:"var(--muted)", letterSpacing:"2px", textTransform:"uppercase" }}>
        {viqIndexLabel()}
      </div>
      <div className="iq-gauge-header">
        <div className="iq-gauge-ring">
          <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border)" strokeWidth={7} />
            <circle cx={size/2} cy={size/2} r={r} fill="none"
              stroke={accent} strokeWidth={7}
              strokeDasharray={circ} strokeDashoffset={circ * (1 - ringPct)}
              strokeLinecap="round"
              className={isFresh ? "iq-gauge-ring-fresh" : ""}
              style={{ transition:"stroke-dashoffset .05s linear" }}
            />
          </svg>
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontFamily:"var(--font-mono)", fontSize:"22px", fontWeight:700, color:"var(--accent)", lineHeight:1 }}>
              {Math.round(ringPct * 100)}
            </span>
            <span style={{ fontSize:"9px", color:"var(--muted)", lineHeight:1.4 }}>/100</span>
          </div>
        </div>
        <div className="iq-gauge-info">
          <div className="iq-gauge-price">
            {displayed.toLocaleString("fr-FR")} €
          </div>
          <div className="iq-gauge-range">
            Fourchette : {low.toLocaleString("fr-FR")} € — {high.toLocaleString("fr-FR")} €
          </div>
          <div className="iq-gauge-freshness" style={{ color: freshColor }}>
            <div className="fresh-dot" style={{ background: freshColor, boxShadow: `0 0 6px ${freshColor}` }} />
            {isFresh
              ? `Scan web il y a ${freshHours}h — données fraîches`
              : `Données actualisées il y a ${freshHours}h`}
          </div>
        </div>
      </div>

      {/* Sparkline 7 jours */}
      <SparklineChart />

      {/* Sources */}
      <div style={{ display:"flex", gap:".6rem", flexWrap:"wrap" }}>
        {["LeBonCoin", "AutoScout24", "La Centrale", "Facebook Marketplace"].map(src => (
          <span key={src} style={{ fontSize:".68rem", background:"rgba(168,128,58,.08)", border:"1px solid rgba(168,128,58,.15)", color:"var(--accent)", padding:".2rem .55rem", borderRadius:"50px", fontWeight:600 }}>
            {src}
          </span>
        ))}
        <span style={{ fontSize:".68rem", color:"var(--muted)", marginLeft:"auto", fontFamily:"var(--font-mono)" }}>
          Basé sur 124 annonces
        </span>
      </div>
    </div>
  );
}

// ─── SparklineChart — micro-graphique 7 jours marché web ─────────────────────
function SparklineChart() {
  // Données simulées : prix moyen sur 7 jours (€)
  const days = ["J-6","J-5","J-4","J-3","J-2","J-1","Auj"];
  const prices = [41200, 41800, 40900, 42100, 42400, 42100, 42580];
  const min = Math.min(...prices) - 500;
  const max = Math.max(...prices) + 500;
  const W = 300; const H = 60;
  const pts = prices.map((p, i) => {
    const x = (i / (prices.length - 1)) * W;
    const y = H - ((p - min) / (max - min)) * H;
    return `${x},${y}`;
  });
  const polyline = pts.join(" ");
  const areaPath = `M${pts[0]} L${pts.slice(1).join(" L")} L${W},${H} L0,${H} Z`;

  return (
    <div>
      <div style={{ fontSize:".72rem", color:"var(--muted)", marginBottom:".4rem", fontWeight:600 }}>
        Évolution marché web — 7 derniers jours
      </div>
      <div className="iq-sparkline">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.25"/>
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0"/>
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#sparkGrad)" />
          <polyline points={polyline} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          {/* Point actuel */}
          <circle cx={pts[pts.length-1].split(",")[0]} cy={pts[pts.length-1].split(",")[1]}
            r="4" fill="var(--accent)" stroke="white" strokeWidth="2"/>
        </svg>
      </div>
      <div className="iq-sparkline-label">
        {days.map(d => <span key={d}>{d}</span>)}
      </div>
    </div>
  );
}

// ─── DepreciationChart — courbe dépréciation VehicleIQ vs Argus ──────────────
function DepreciationChart() {
  // Données : années 0→8, prix index 100
  const years = [0,1,2,3,4,5,6,7,8];
  const viqData   = [100, 83, 70, 60, 52, 45, 39, 34, 30]; // VIQ.AI Engine (plus précis)
  const argusData = [100, 80, 67, 58, 51, 46, 41, 37, 34]; // Argus (lissé)
  const W = 400; const H = 160;
  const padL = 40; const padB = 30; const padR = 10; const padT = 10;
  const cW = W - padL - padR;
  const cH = H - padB - padT;

  const toXY = (arr) => arr.map((v, i) => {
    const x = padL + (i / (arr.length - 1)) * cW;
    const y = padT + (1 - v / 100) * cH;
    return { x, y };
  });

  const viqPts = toXY(viqData);
  const argusPts = toXY(argusData);

  const linePath = (pts) => pts.map((p,i) => `${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaPath = (pts) => linePath(pts) + ` L${pts[pts.length-1].x},${padT+cH} L${padL},${padT+cH} Z`;

  const yLabels = [100, 75, 50, 25];
  const tickX = years.map((y,i) => padL + (i / (years.length-1)) * cW);

  return (
    <div className="depreciation-card">
      <div>
        <div className="dep-chart-title">Courbe de dépréciation — Renault Clio (segment généraliste)</div>
        <div className="dep-chart-sub">VIQ.AI® Engine vs Argus — indice base 100</div>
      </div>
      <div className="dep-chart-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width:"100%", height:"100%" }}>
          <defs>
            <linearGradient id="viqGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.18"/>
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0"/>
            </linearGradient>
            <linearGradient id="argusGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#A8803A" stopOpacity="0.1"/>
              <stop offset="100%" stopColor="#A8803A" stopOpacity="0"/>
            </linearGradient>
          </defs>
          {/* Grille */}
          {yLabels.map(y => {
            const cy = padT + (1 - y/100) * cH;
            return <g key={y}>
              <line x1={padL} y1={cy} x2={W-padR} y2={cy} stroke="var(--border)" strokeWidth="1" strokeDasharray="3,3"/>
              <text x={padL-5} y={cy+4} fontSize="9" fill="var(--muted)" textAnchor="end" fontFamily="JetBrains Mono,monospace">{y}</text>
            </g>;
          })}
          {/* X-axis */}
          {years.map((y,i) => (
            <text key={y} x={tickX[i]} y={H-6} fontSize="9" fill="var(--muted)" textAnchor="middle" fontFamily="JetBrains Mono,monospace">An {y}</text>
          ))}
          {/* Argus area */}
          <path d={areaPath(argusPts)} fill="url(#argusGrad)"/>
          <path d={linePath(argusPts)} fill="none" stroke="#A8803A" strokeWidth="1.2" strokeDasharray="4,3" opacity=".55"/>
          {/* VIQ area */}
          <path d={areaPath(viqPts)} fill="url(#viqGrad)"/>
          <path d={linePath(viqPts)} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round"/>
          {/* Points VIQ */}
          {viqPts.map((p,i) => (
            <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="var(--accent)" stroke="white" strokeWidth="1.5"/>
          ))}
          {/* Annotation "Tendance marché" */}
          <rect x={W-padR-78} y={padT+8} width="76" height="18" rx="4" fill="rgba(168,128,58,.1)" stroke="rgba(168,128,58,.25)" strokeWidth="1"/>
          <text x={W-padR-40} y={padT+21} fontSize="9" fill="var(--accent)" textAnchor="middle" fontWeight="700" fontFamily="JetBrains Mono,monospace">Tendance indicative</text>
        </svg>
      </div>
      <div className="dep-legend">
        <div className="dep-legend-item">
          <div className="dep-legend-dot" style={{ background:"var(--accent)" }}/>
          VIQ.AI® Engine (temps réel)
        </div>
        <div className="dep-legend-item">
          <div className="dep-legend-dot" style={{ background:"#A8803A", height:"2px", borderStyle:"dashed", borderColor:"#A8803A", borderTop:"2px dashed #A8803A", background:"transparent", opacity:.55 }}/>
          Argus classique
        </div>
      </div>
      <div className="dep-chart-note">
        Données simulées à titre illustratif · Calibrage réel sur LeBonCoin, AutoScout24, La Centrale
      </div>
    </div>
  );
}


// ─── LiveTicker — Bandeau de cotations marché en temps réel ──────────────────
function LiveTicker() {
  const items = [
    { car:"BMW Série 3 — 2021", km:"62 000 km", price:"32 400 €", delta:"+1.2%" , up:true  },
    { car:"Peugeot 3008 — 2022", km:"38 000 km", price:"27 900 €", delta:"-0.8%", up:false },
    { car:"Mercedes GLC — 2020", km:"55 000 km", price:"41 500 €", delta:"+2.1%", up:true  },
    { car:"Volkswagen Golf 8 — 2023", km:"18 000 km", price:"23 800 €", delta:"+0.5%", up:true  },
    { car:"Renault Captur — 2022", km:"29 000 km", price:"19 200 €", delta:"-1.4%", up:false },
    { car:"Toyota Yaris Cross — 2023", km:"12 000 km", price:"22 500 €", delta:"+0.9%", up:true  },
    { car:"Audi Q3 — 2021", km:"44 000 km", price:"33 700 €", delta:"+1.7%", up:true  },
    { car:"Ford Kuga PHEV — 2022", km:"31 000 km", price:"28 100 €", delta:"-0.3%", up:false },
  ];
  const doubled = [...items, ...items]; // double for seamless loop
  return (
    <div className="ticker-bar">
      <div className="ticker-label">LIVE MARCHÉ</div>
      <div className="ticker-track">
        <div className="ticker-inner">
          {doubled.map((it, i) => (
            <div key={i} className="ticker-item">
              <span className="ticker-car">{it.car}</span>
              <span className="ticker-price">{it.price}</span>
              <span className={it.up ? "ticker-up" : "ticker-down"}>{it.delta}</span>
              <span style={{ opacity:.4 }}>·</span>
              <span style={{ opacity:.5 }}>{it.km}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── LiveMarketPanel — Cotations marché en direct (dashboard Pro) ────────────
// Présentation professionnelle des cotations live : tableau avec tendances,
// rafraîchissement périodique léger (variation simulée tant qu'il n'y a pas de
// flux temps réel branché). Affiché dans le Tableau de bord Pro.
function MakeBadge({ make, size = 26 }) {
  const m = (make || "").toLowerCase();
  const MAP = {
    bmw: ["#1C69D4", "BMW"], audi: ["#BB0A30", "Audi"], mercedes: ["#111", "MB"],
    volkswagen: ["#001E50", "VW"], renault: ["#FFCC00", "RN"], peugeot: ["#1B2B4A", "PG"],
    citroën: ["#6E2233", "CT"], citroen: ["#6E2233", "CT"], toyota: ["#EB0A1E", "TY"],
    ford: ["#003478", "Fd"], tesla: ["#CC0000", "T"], dacia: ["#646B52", "DC"],
    hyundai: ["#002C5F", "Hy"], kia: ["#05141F", "KIA"], fiat: ["#941711", "Ft"],
    nissan: ["#C3002F", "Ns"], opel: ["#F7FF14", "Op"], seat: ["#33302E", "St"],
    skoda: ["#0E3A2F", "Sk"], volvo: ["#003057", "Vo"], mini: ["#111", "Mi"],
    porsche: ["#1A1A1F", "Po"], "range rover": ["#1B2B22", "RR"], "land rover": ["#005A2B", "LR"],
    maserati: ["#0C2340", "Ma"], jaguar: ["#23292B", "Jg"], lexus: ["#1A1A1F", "Lx"], cupra: ["#2E2A26", "Cu"],
  };
  const key = Object.keys(MAP).find(k => m.startsWith(k)) || null;
  const [bg, label] = key ? MAP[key] : ["#8E939E", (make || "?").slice(0, 2)];
  const dark = bg === "#FFCC00" || bg === "#F7FF14";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: 7, background: bg, color: dark ? "#1A1A1F" : "#fff",
      fontWeight: 800, fontSize: size * 0.34, fontFamily: "var(--font-body)", flexShrink: 0,
      letterSpacing: "-.02em", lineHeight: 1, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.06)",
    }}>{label}</span>
  );
}

function LiveMarketPanel({ limit, preview = false, onExpand } = {}) {
  const base = [
    { car: "BMW Série 3", make: "BMW", year: 2021, km: 62000, price: 32400, delta: 1.2, fuel: "Diesel", type: "Berline" },
    { car: "Peugeot 3008", make: "Peugeot", year: 2022, km: 38000, price: 27900, delta: -0.8, fuel: "Hybride", type: "SUV" },
    { car: "Mercedes GLC", make: "Mercedes", year: 2020, km: 55000, price: 41500, delta: 2.1, fuel: "Diesel", type: "SUV" },
    { car: "Volkswagen Golf 8", make: "Volkswagen", year: 2023, km: 18000, price: 23800, delta: 0.5, fuel: "Essence", type: "Compacte" },
    { car: "Renault Captur", make: "Renault", year: 2022, km: 29000, price: 19200, delta: -1.4, fuel: "Essence", type: "SUV" },
    { car: "Toyota Yaris Cross", make: "Toyota", year: 2023, km: 12000, price: 22500, delta: 0.9, fuel: "Hybride", type: "SUV" },
    { car: "Audi Q3", make: "Audi", year: 2021, km: 44000, price: 33700, delta: 1.7, fuel: "Diesel", type: "SUV" },
    { car: "Ford Kuga PHEV", make: "Ford", year: 2022, km: 31000, price: 28100, delta: -0.3, fuel: "Hybride rech.", type: "SUV" },
    { car: "Tesla Model 3", make: "Tesla", year: 2022, km: 35000, price: 29900, delta: 1.1, fuel: "Électrique", type: "Berline" },
    { car: "Dacia Sandero", make: "Dacia", year: 2023, km: 14000, price: 14200, delta: 0.4, fuel: "Essence", type: "Citadine" },
    { car: "Citroën C3", make: "Citroën", year: 2022, km: 26000, price: 15400, delta: -0.6, fuel: "Essence", type: "Citadine" },
    { car: "Volkswagen T-Roc", make: "Volkswagen", year: 2021, km: 41000, price: 26800, delta: 0.8, fuel: "Essence", type: "SUV" },
    { car: "Renault Clio 5", make: "Renault", year: 2023, km: 16000, price: 17900, delta: 0.3, fuel: "Hybride", type: "Citadine" },
    { car: "Peugeot 208", make: "Peugeot", year: 2022, km: 23000, price: 18200, delta: -0.5, fuel: "Essence", type: "Citadine" },
    { car: "Audi A3 Sportback", make: "Audi", year: 2021, km: 48000, price: 29600, delta: 1.4, fuel: "Diesel", type: "Compacte" },
    { car: "BMW X1", make: "BMW", year: 2022, km: 33000, price: 38900, delta: 1.9, fuel: "Diesel", type: "SUV" },
    { car: "Mercedes Classe A", make: "Mercedes", year: 2021, km: 39000, price: 28400, delta: -0.9, fuel: "Essence", type: "Compacte" },
    { car: "Hyundai Tucson", make: "Hyundai", year: 2022, km: 28000, price: 30700, delta: 0.6, fuel: "Hybride", type: "SUV" },
    { car: "Kia Sportage", make: "Kia", year: 2023, km: 15000, price: 32200, delta: 1.0, fuel: "Hybride", type: "SUV" },
    { car: "Fiat 500 électrique", make: "Fiat", year: 2022, km: 19000, price: 16800, delta: -1.1, fuel: "Électrique", type: "Citadine" },
    // ── Coupés ──
    { car: "Audi A5 Coupé", make: "Audi", year: 2021, km: 40000, price: 39900, delta: 0.9, fuel: "Essence", type: "Coupé" },
    { car: "BMW Série 4 Coupé", make: "BMW", year: 2022, km: 27000, price: 47500, delta: 1.2, fuel: "Diesel", type: "Coupé" },
    // ── Segment premium (≈ 50–95 k€) ──
    { car: "Porsche Macan", make: "Porsche", year: 2022, km: 38000, price: 62900, delta: 1.3, fuel: "Essence", type: "SUV" },
    { car: "BMW X5 xDrive", make: "BMW", year: 2021, km: 51000, price: 58500, delta: 0.7, fuel: "Diesel", type: "SUV" },
    { car: "Mercedes GLE", make: "Mercedes", year: 2022, km: 34000, price: 71200, delta: -0.5, fuel: "Hybride rech.", type: "SUV" },
    { car: "Audi Q7", make: "Audi", year: 2021, km: 47000, price: 64800, delta: 1.1, fuel: "Diesel", type: "SUV" },
    { car: "Range Rover Velar", make: "Range Rover", year: 2022, km: 29000, price: 59700, delta: 0.6, fuel: "Diesel", type: "SUV" },
    { car: "Mercedes Classe E Coupé", make: "Mercedes", year: 2021, km: 33000, price: 54900, delta: 0.4, fuel: "Diesel", type: "Coupé" },
    // ── Segment ultra premium (≥ 100 k€) ──
    { car: "Porsche 911 Carrera", make: "Porsche", year: 2022, km: 18000, price: 132000, delta: 2.4, fuel: "Essence", type: "Coupé" },
    { car: "Range Rover Autobiography", make: "Range Rover", year: 2023, km: 22000, price: 138500, delta: 1.8, fuel: "Diesel", type: "SUV" },
    { car: "Mercedes Classe S", make: "Mercedes", year: 2022, km: 28000, price: 109000, delta: -0.7, fuel: "Hybride rech.", type: "Berline" },
    { car: "Porsche Cayenne Turbo", make: "Porsche", year: 2022, km: 31000, price: 112000, delta: 1.5, fuel: "Essence", type: "SUV" },
  ];
  const [rows, setRows] = useState(base);
  const [pulse, setPulse] = useState(0);
  // Rafraîchissement léger toutes les ~6s : micro-variation des prix/tendances
  // pour donner l'effet « live ». À remplacer par un vrai flux quand dispo.
  useEffect(() => {
    const t = setInterval(() => {
      setRows(prev => prev.map(r => {
        const drift = (Math.random() - 0.5) * 0.6; // ±0.3 %
        const newDelta = Math.max(-4, Math.min(4, r.delta + drift));
        const newPrice = Math.round(r.price * (1 + drift / 100) / 10) * 10;
        return { ...r, delta: Number(newDelta.toFixed(1)), price: newPrice };
      }));
      setPulse(p => p + 1);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const fmt = (n) => Math.abs(Math.round(n)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const lastUpdate = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const displayRows = limit ? rows.slice(0, limit) : rows;

  // Segments de prix (du plus cher au plus accessible)
  const LM_SEGMENTS = [
    { key: "ultra",         label: "Ultra premium", hint: "≥ 100 000 €",        min: 100000 },
    { key: "premium",       label: "Premium",       hint: "50 000 – 100 000 €", min: 50000 },
    { key: "intermediaire", label: "Intermédiaire", hint: "30 000 – 50 000 €",  min: 30000 },
    { key: "accessible",    label: "Accessible",    hint: "< 30 000 €",         min: 0 },
  ];
  const lmSegIdx = (price) => LM_SEGMENTS.findIndex(s => price >= s.min);
  // Groupé par segment (du plus cher au plus accessible), puis par type de véhicule
  // (cluster, type le plus coté d'abord), et prix décroissant au sein de chaque type.
  const lmGrouped = LM_SEGMENTS.map((seg, si) => {
    const inSeg = displayRows.filter(r => lmSegIdx(r.price) === si);
    const typeMax = {};
    inSeg.forEach(r => { const t = r.type || "Autre"; typeMax[t] = Math.max(typeMax[t] || 0, r.price); });
    const sorted = [...inSeg].sort((a, b) => {
      const ta = typeMax[a.type || "Autre"], tb = typeMax[b.type || "Autre"];
      if (tb !== ta) return tb - ta;
      const tcmp = (a.type || "").localeCompare(b.type || "");
      if (tcmp !== 0) return tcmp;
      return b.price - a.price;
    });
    return { seg, rows: sorted };
  }).filter(g => g.rows.length);

  const clickable = preview && typeof onExpand === "function";
  const wrapStyle = {
    background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 14,
    padding: "1.2rem 1.4rem", marginBottom: "1.4rem",
    cursor: clickable ? "pointer" : "default", transition: "border-color .15s, box-shadow .15s, transform .15s",
  };

  return (
    <div
      style={wrapStyle}
      onClick={clickable ? onExpand : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onExpand(); } } : undefined}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.boxShadow = "var(--shadow)"; e.currentTarget.style.transform = "translateY(-1px)"; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; } : undefined}
      title={clickable ? "Voir le Live Marché complet dans l'Outil de suivi métier" : undefined}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".5rem", marginBottom: "1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".6rem" }}>
          <span style={{ position: "relative", display: "inline-flex", width: 9, height: 9 }}>
            <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "var(--green)", opacity: .35, animation: "viqLivePulse 1.8s ease-out infinite" }} />
            <span style={{ position: "relative", width: 9, height: 9, borderRadius: "50%", background: "var(--green)" }} />
          </span>
          <div style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: "1.05rem", color: "var(--text)" }}>
            Live Marché
          </div>
          <span style={{ fontSize: ".58rem", fontWeight: 700, color: "var(--green)", background: "var(--green-soft)", border: "1px solid rgba(46,111,92,.25)", padding: ".12rem .5rem", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".5px" }}>
            En direct
          </span>
        </div>
        <div style={{ fontSize: ".68rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
          Cotations VIQ.AI® · maj {lastUpdate}
        </div>
      </div>

      {/* Bandeau synthèse marché (masqué en mode aperçu compact) */}
      {!preview && (() => {
        const up = rows.filter(r => r.delta >= 0).length;
        const down = rows.length - up;
        const avg = rows.reduce((a, r) => a + r.delta, 0) / (rows.length || 1);
        const stat = (label, val, color) => (
          <div style={{ flex: "1 1 120px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, padding: ".6rem .8rem" }}>
            <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontFamily: "var(--font-mono)" }}>{label}</div>
            <div style={{ fontSize: "1.05rem", fontWeight: 800, color: color || "var(--text)", fontFamily: "var(--font-head)" }}>{val}</div>
          </div>
        );
        return (
          <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginBottom: "1rem" }}>
            {stat("Modèles suivis", rows.length)}
            {stat("Tendance moyenne 7j", `${avg >= 0 ? "▲ +" : "▼ "}${avg.toFixed(1)} %`, avg >= 0 ? "var(--green)" : "var(--red)")}
            {stat("En hausse", `${up}`, "var(--green)")}
            {stat("En baisse", `${down}`, "var(--red)")}
          </div>
        );
      })()}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".82rem" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--muted)", fontSize: ".66rem", textTransform: "uppercase", letterSpacing: ".5px" }}>
              <th style={{ padding: ".5rem .6rem", fontWeight: 700 }}>Véhicule</th>
              <th style={{ padding: ".5rem .6rem", fontWeight: 700 }}>Type</th>
              <th style={{ padding: ".5rem .6rem", fontWeight: 700 }}>Énergie</th>
              <th style={{ padding: ".5rem .6rem", fontWeight: 700 }}>Année</th>
              <th style={{ padding: ".5rem .6rem", fontWeight: 700, textAlign: "right" }}>Km</th>
              <th style={{ padding: ".5rem .6rem", fontWeight: 700, textAlign: "right" }}>Cote estimée</th>
              <th style={{ padding: ".5rem .6rem", fontWeight: 700, textAlign: "right" }}>Tendance 7j</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const renderRow = (r) => (
                <tr key={r.car} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: ".55rem .6rem", fontWeight: 600, color: "var(--text)" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: ".55rem" }}>
                      <MakeBadge make={r.make} size={26} />
                      <span>{r.car}</span>
                    </span>
                  </td>
                  <td style={{ padding: ".55rem .6rem", fontSize: ".74rem" }}>
                    <span style={{ background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", color: "var(--accent)", borderRadius: 20, padding: ".15rem .55rem", whiteSpace: "nowrap", fontWeight: 600 }}>{r.type || "—"}</span>
                  </td>
                  <td style={{ padding: ".55rem .6rem", color: "var(--sub)", fontSize: ".74rem" }}>
                    <span style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 20, padding: ".15rem .55rem", whiteSpace: "nowrap" }}>{r.fuel}</span>
                  </td>
                  <td style={{ padding: ".55rem .6rem", color: "var(--sub)" }}>{r.year}</td>
                  <td style={{ padding: ".55rem .6rem", color: "var(--sub)", textAlign: "right", fontFamily: "var(--font-mono)" }}>{fmt(r.km)} km</td>
                  <td style={{ padding: ".55rem .6rem", color: "var(--text)", textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 700 }}>{fmt(r.price)} €</td>
                  <td style={{ padding: ".55rem .6rem", textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 700, color: r.delta >= 0 ? "var(--green)" : "var(--red)" }}>
                    {r.delta >= 0 ? "▲" : "▼"} {Math.abs(r.delta).toFixed(1)} %
                  </td>
                </tr>
              );
              // Aperçu compact (landing) : liste à plat, du plus cher au plus accessible.
              if (limit) return [...displayRows].sort((a, b) => b.price - a.price).map(renderRow);
              // Vue complète : en-têtes de segment de prix + véhicules regroupés par type.
              const els = [];
              lmGrouped.forEach(g => {
                els.push(
                  <tr key={"seg-" + g.seg.key}>
                    <td colSpan={7} style={{ padding: ".7rem .6rem .35rem", borderTop: "2px solid var(--accent)" }}>
                      <span style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: ".82rem", color: "var(--text)" }}>{g.seg.label}</span>
                      <span style={{ fontSize: ".66rem", color: "var(--muted)", marginLeft: ".5rem", fontFamily: "var(--font-mono)" }}>{g.seg.hint} · {g.rows.length} modèle{g.rows.length > 1 ? "s" : ""}</span>
                    </td>
                  </tr>
                );
                g.rows.forEach(r => els.push(renderRow(r)));
              });
              return els;
            })()}
          </tbody>
        </table>
      </div>
      {clickable ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".5rem", marginTop: ".9rem" }}>
          <div style={{ fontSize: ".66rem", color: "var(--muted)", fontStyle: "italic" }}>
            Aperçu · cotations VIQ.AI® à titre indicatif
          </div>
          <span style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--accent)" }}>
            Voir le Live Marché complet →
          </span>
        </div>
      ) : (
        <div style={{ fontSize: ".66rem", color: "var(--muted)", marginTop: ".8rem", fontStyle: "italic" }}>
          Indicateur de tendance basé sur l'agrégation marché VIQ.AI® (100+ plateformes). À titre indicatif.
        </div>
      )}
    </div>
  );
}

// ─── LogosStrip — Marquee clients pros ───────────────────────────────────────
function LogosStrip() {
  const clients = [
    { name:"Spoticar",      color:"var(--accent)" },
    { name:"Aramisauto",    color:"#E85D04" },
    { name:"AutoHero",      color:"#0F766E" },
    { name:"Autosur",       color:"#7C3AED" },
    { name:"Caarea",        color:"#F59E0B" },
    { name:"Carrefour Auto",color:"#E63946" },
    { name:"Opel Retail",   color:"#1A1D2E" },
    { name:"LLD Pro",       color:"var(--accent)" },
    { name:"Fleetback",     color:"#0F766E" },
    { name:"AutoScout24",   color:"#E85D04" },
  ];
  const doubled = [...clients, ...clients];
  return (
    <div className="logos-strip">
      <div className="logos-label">+100 plateformes analysées en temps réel</div>
      <div className="logos-track">
        <div className="logos-inner">
          {doubled.map((c, i) => (
            <div key={i} className="logo-pill">
              <div className="logo-pip" style={{ background: c.color }} />
              {c.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── WhySection — Pourquoi les pros choisissent VehicleIQ ────────────────────
// ─── HomePricingSection — tarification simple, intégrée au scroll ───────────
// Brief conversion : "Une seule offre mise en avant. Simple. Sans jargon."
// L'offre pay-as-you-go est mise en avant car c'est celle qui lève le plus
// d'objections pour l'ICP (aucun engagement, aucun risque) — Pro Solo est
// mentionné en repli pour les volumes élevés, sans diluer le message principal.
function HomePricingSection({ onLaunch, onSeeAll }) {
  return (
    <div style={{ padding: "4rem 1.5rem", textAlign: "center", background: "var(--card)" }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Tarification</div>
        <h2 style={{ fontFamily: "var(--font-head)", fontSize: "1.8rem", fontWeight: 800, color: "var(--text)", margin: ".5rem 0 1.6rem" }}>
          Un prix simple. Pas d'abonnement obligatoire.
        </h2>
        <div style={{ background: "var(--bg)", border: "2px solid var(--accent)", borderRadius: 16, padding: "2rem 1.8rem" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "3rem", fontWeight: 700, color: "var(--green)", lineHeight: 1 }}>
            4,99 €
          </div>
          <div style={{ fontSize: ".9rem", color: "var(--sub)", marginTop: ".3rem", marginBottom: "1.4rem" }}>
            par véhicule analysé — payez seulement ce que vous utilisez
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".5rem", textAlign: "left", marginBottom: "1.6rem", fontSize: ".9rem", color: "var(--text)" }}>
            <div>✓ 5 analyses offertes à la création du compte</div>
            <div>✓ Aucun engagement, aucune carte requise pour essayer</div>
            <div>✓ Rapport complet à chaque analyse</div>
          </div>
          <button className="btn btn-accent" style={{ width: "100%", justifyContent: "center", padding: ".85rem", fontSize: "1rem" }} onClick={onLaunch}>
            Essayer gratuitement →
          </button>
        </div>
        <div style={{ marginTop: "1.4rem", fontSize: ".85rem", color: "var(--sub)" }}>
          Volume important ? <button onClick={onSeeAll} style={{ background: "none", border: "none", color: "var(--accent)", fontWeight: 700, cursor: "pointer", textDecoration: "underline", fontSize: "inherit" }}>Voir l'offre Pro Solo (399 €/mois, illimité) →</button>
        </div>
      </div>
    </div>
  );
}

// ─── ProblemSection — la journée type du marchand, avant VehicleIQ ──────────
function ProblemSection() {
  const steps = [
    { icon: "🔍", label: "Éplucher les annonces" },
    { icon: "⚖️", label: "Comparer les prix à la main" },
    { icon: "🔧", label: "Chercher les défauts cachés" },
    { icon: "📋", label: "Vérifier l'historique du véhicule" },
    { icon: "✍️", label: "Rédiger l'annonce de revente" },
  ];
  return (
    <div className="problem-section" style={{ padding: "4rem 1.5rem", textAlign: "center", background: "var(--bg)" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Le quotidien du marchand VO</div>
        <h2 style={{ fontFamily: "var(--font-head)", fontSize: "1.8rem", fontWeight: 800, color: "var(--text)", margin: ".5rem 0 2rem" }}>
          Avant de reprendre un véhicule, il faut tout vérifier.
        </h2>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: ".8rem", marginBottom: "2.2rem" }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: ".5rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, padding: ".7rem 1.1rem", fontSize: ".88rem", color: "var(--sub)", fontWeight: 600 }}>
              <span style={{ fontSize: "1.1rem" }}>{s.icon}</span> {s.label}
            </div>
          ))}
        </div>
        <p style={{ fontSize: "1.15rem", color: "var(--text)", fontWeight: 600, lineHeight: 1.6, marginBottom: ".4rem" }}>
          Tout ça prend 20 à 30 minutes par véhicule.
        </p>
        <p style={{ fontFamily: "var(--font-head)", fontSize: "1.5rem", fontWeight: 800, color: "var(--green)" }}>
          VehicleIQ le fait en moins de 30 secondes.
        </p>
      </div>
    </div>
  );
}

function WhySection() {
  const cards = [
    {
      n:"01", title:"Cote calibrée sur le marché réel", metric:"données temps réel",
      desc:"L'analyse temps réel des plateformes auto françaises (LeBonCoin, AutoScout24, La Centrale, etc.) et le croisement avec les données officielles SIV — pour une cote calibrée sur le marché du moment, pas sur des barèmes mensuels. Il s'agit d'une estimation indicative, non contractuelle.",
    },
    {
      n:"02", title:"Contrôle & fiabilité en un clic", metric:"HistoVec + score VIQ.AI®",
      desc:"Plaque ou VIN → identité officielle, historique HistoVec (propriétaires, CT, gage, sinistres) et score de fiabilité avec points de vigilance sur le modèle, le tout enregistré dans le Dossier VIQ du véhicule. De quoi décider un achat en confiance, pas au feeling.",
    },
    {
      n:"03", title:"Aide à l'achat & recherche d'opportunités", metric:"budget → recommandations",
      desc:"Indiquez un budget et des critères, VehicleIQ identifie les véhicules qui correspondent réellement — cote neuve, cote de marché et points forts/de vigilance pour chaque proposition.",
    },
    {
      n:"04", title:"Suivi de vente & tableau de bord", metric:"marges, KPIs, activité",
      desc:"Au-delà de la cote : chaque Dossier VIQ garde la trace complète d'un véhicule — marges suivies, taux de conversion, historique et documents (mandats, CERFA) générés automatiquement, jusqu'à la vente.",
    },
    {
      n:"05", title:"Formulaires clients — vos leads, automatiquement", metric:"lien personnalisé",
      desc:"Un lien à votre nom que vos clients remplissent eux-mêmes (cote, recherche d'achat ou carte grise) : la demande arrive déjà qualifiée dans votre tableau de bord, prête à être traitée.",
    },
    {
      n:"06", title:"Sans abonnement, sans engagement", metric:"À partir de 4,99 € / dossier",
      desc:"Vous ne payez que quand vous estimez : tarif unique de 4,99 € par dossier, 5 cotes offertes et déjà actives à la création du compte. Là où la concurrence facture 250 à 600 €/mois, VehicleIQ s'adapte à votre rythme.",
    },
  ];
  return (
    <div className="why-section">
      <div className="why-inner">
        <div className="sec-eyebrow" style={{ color:"var(--gold)" }}>Pourquoi VehicleIQ</div>
        <div className="sec-title">Un <em>Dossier VIQ</em><br />par véhicule, pas six outils séparés</div>
        <div className="sec-sub">
          Garages VO, marchands, revendeurs et mandataires indépendants — chaque véhicule a son Dossier VIQ, alimenté automatiquement du premier contrôle jusqu'à la vente.
        </div>
        <div className="why-grid">
          {cards.map(c => (
            <div key={c.n} className="why-card">
              <div className="why-num">{c.n}</div>
              <div className="why-card-title">{c.title}</div>
              <div className="why-card-desc">{c.desc}</div>
              <div className="why-metric" style={{ fontSize: c.metric.includes("·") ? ".62rem" : ".7rem" }}>◆ {c.metric}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── TestimonialsSection — Avis clients pros ──────────────────────────────────
function TestimonialsSection() {
  const reviews = [
    {
      stars:5,
      quote:"On a intégré VehicleIQ dans notre processus de reprise. Le temps de traitement d'un dossier est passé de 20 minutes à moins de 2 minutes. L'impact sur notre marge est direct et mesurable.",
      name:"Julien Moreau",  role:"Gérant — Auto Moreau VO (Lyon)",
      avatar:"JM", bg:"var(--accent)", badge:"Garage VO indépendant",
    },
    {
      stars:5,
      quote:"La Cote calibrée sur le marché en temps réel nous a permis d'arrêter de sous-coter nos véhicules. On a récupéré en moyenne 1 200 € par vente. Sur 300 véhicules/an, le ROI est évident.",
      name:"Sarah Leconte",  role:"Marchand VO — Nord (3 salariés)",
      avatar:"SL", bg:"#0F766E", badge:"Marchand VO indépendant",
    },
    {
      stars:5,
      quote:"Je suis mandataire indépendant. VehicleIQ me donne les mêmes outils qu'une grande enseigne, à la Cote près. Le rapport PDF est suffisamment professionnel pour être présenté directement à mes clients.",
      name:"Karim Boufenara", role:"Mandataire VO — Île-de-France",
      avatar:"KB", bg:"#F59E0B", badge:"Mandataire VO indépendant",
    },
  ];
  return (
    <div className="testi-section">
      <div className="testi-inner">
        <div className="sec-eyebrow" style={{ color:"var(--muted)" }}>Témoignages</div>
        <div className="sec-title">Ce que disent nos utilisateurs pro</div>
        <div className="testi-grid">
          {reviews.map((r, i) => (
            <div key={i} className="testi-card">
              <div className="testi-stars">
                {[...Array(r.stars)].map((_,j) => <span key={j} className="star">★</span>)}
              </div>
              <div className="testi-quote">{r.quote}</div>
              <div className="testi-author">
                <div className="testi-avatar" style={{ background: r.bg }}>
                  {r.avatar}
                </div>
                <div>
                  <div className="testi-name">{r.name}</div>
                  <div className="testi-role">{r.role}</div>
                  <div className="testi-badge">✦ {r.badge}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── DashboardMockupSection — mockup du VRAI Tableau de bord Pro ───────────
// Reproduit fidèlement le rendu visible dans le produit (Pro Dashboard) avec des
// données illustratives crédibles. Toutes les valeurs sont fixes ici (démo statique).
function DashboardMockupSection() {
  // KPIs fictifs d'un marchand auto représentatif (47 estim. / 12 ventes loggées)
  const kpis = [
    { label: "Marge totale",                value: "+42 800 €",  sub: "12 ventes avec marge tracée",   color: "var(--green)" },
    { label: "Marge moyenne / véhicule",    value: "+3 570 €",   sub: "sur 287 500 € vendus",          color: "var(--accent)" },
    { label: "Écart cote / vente",          value: "±4,2 %",     sub: "indicatif · sur 12 ventes",    color: "var(--green)" },
    { label: "Conversion estim. → vente",   value: "25,5 %",     sub: "12 ventes sur 47 estim.",       color: "var(--accent)" },
  ];

  // Top 5 véhicules les plus rentables
  const top5 = [
    { rank: 1, label: "BMW Série 3 320d — 2021",       buy: 22500, sell: 28900, platform: "AutoScout24",  marginPct: 28 },
    { rank: 2, label: "Tesla Model 3 LR — 2022",       buy: 28000, sell: 33200, platform: "La Centrale",   marginPct: 19 },
    { rank: 3, label: "Peugeot 3008 GT — 2022",        buy: 21800, sell: 25400, platform: "LeBonCoin",     marginPct: 17 },
    { rank: 4, label: "Renault Megane E-Tech — 2023",  buy: 27500, sell: 30200, platform: "AutoScout24",   marginPct: 10 },
    { rank: 5, label: "Mercedes Classe A 180d — 2020", buy: 19800, sell: 21900, platform: "LeBonCoin",     marginPct: 11 },
  ];

  // Performance par plateforme (3 plateformes top)
  const platforms = [
    { name: "AutoScout24",     count: 5, avg_sold: 26800, avg_margin: 4200 },
    { name: "La Centrale",     count: 4, avg_sold: 24500, avg_margin: 3100 },
    { name: "LeBonCoin",       count: 3, avg_sold: 22000, avg_margin: 2400 },
  ];

  // Mix Pro / Particulier
  const mix = { pro: 7, particulier: 5, pro_avg: 4100, part_avg: 2950 };
  const mixTotal = mix.pro + mix.particulier;
  const proPct = Math.round((mix.pro / mixTotal) * 100);
  const partPct = 100 - proPct;

  return (
    <div className="dash-mock-section">
      <div className="dash-mock-inner">
        <div className="sec-eyebrow">Plateforme</div>
        <h2 className="dash-mock-h2">Votre tableau de bord commercial</h2>
        <p className="dash-mock-sub">
          Marge réelle, précision des cotes, top véhicules, performance par plateforme —
          tout ce qu'un marchand auto veut savoir, en un coup d'œil.
        </p>

        <div className="dash-mock-window dash-mock-window-light">
          <div className="dash-mock-titlebar dash-mock-titlebar-light">
            <span className="dash-dot dash-dot-r" />
            <span className="dash-dot dash-dot-y" />
            <span className="dash-dot dash-dot-g" />
            <span className="dash-mock-url dash-mock-url-light">app.vehicleiq.fr/dashboard-pro</span>
          </div>
          <div className="dash-mock-body dash-mock-body-light">

            {/* En-tête : Tableau de bord Pro + SIRET vérifié */}
            <div className="dash-pro-header">
              <div className="dash-pro-header-left">
                <div className="dash-pro-title">📊 Outil de suivi métier</div>
                <div className="dash-pro-sub">SIRET ••• ••• ••• 00018 · 47 cotes · 12 ventes loggées</div>
              </div>
              <div className="dash-pro-badge">◆ SIRET VÉRIFIÉ</div>
            </div>

            {/* 4 cartes KPI */}
            <div className="dash-pro-kpi-grid">
              {kpis.map((k, i) => (
                <div key={i} className="dash-pro-kpi-card">
                  <div className="dash-pro-kpi-label">{k.label}</div>
                  <div className="dash-pro-kpi-val" style={{color: k.color}}>{k.value}</div>
                  <div className="dash-pro-kpi-sub">{k.sub}</div>
                </div>
              ))}
            </div>

            {/* Grid 3 colonnes : Top 5 véhicules + Plateformes + Mix client */}
            <div className="dash-pro-analytics-grid">

              {/* Top 5 véhicules */}
              <div className="dash-pro-analytics-card">
                <div className="dash-pro-analytics-title">🏆 Top 5 véhicules les plus rentables</div>
                <div className="dash-pro-analytics-body">
                  {top5.map((v, i) => (
                    <div key={i} className={"dash-pro-top5-row" + (i === 0 ? " dash-pro-top5-best" : "")}>
                      <span className="dash-pro-top5-rank">#{v.rank}</span>
                      <div className="dash-pro-top5-info">
                        <div className="dash-pro-top5-name">{v.label}</div>
                        <div className="dash-pro-top5-detail">
                          Achat {v.buy.toLocaleString("fr-FR")} € → Vente {v.sell.toLocaleString("fr-FR")} € · {v.platform}
                        </div>
                      </div>
                      <div className="dash-pro-top5-margin">
                        <div className="dash-pro-top5-margin-eur">+{(v.sell - v.buy).toLocaleString("fr-FR")} €</div>
                        <div className="dash-pro-top5-margin-pct">+{v.marginPct}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Performance par plateforme */}
              <div className="dash-pro-analytics-card">
                <div className="dash-pro-analytics-title">🛒 Performance par plateforme</div>
                <div className="dash-pro-analytics-body">
                  {platforms.map((p, i) => (
                    <div key={i} className="dash-pro-plat-row">
                      <div className="dash-pro-plat-info">
                        <div className="dash-pro-plat-name">{p.name}</div>
                        <div className="dash-pro-plat-detail">{p.count} ventes · prix moyen {p.avg_sold.toLocaleString("fr-FR")} €</div>
                      </div>
                      <div className="dash-pro-plat-margin">+{p.avg_margin.toLocaleString("fr-FR")} €</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mix Pro / Particulier */}
              <div className="dash-pro-analytics-card">
                <div className="dash-pro-analytics-title">👥 Mix Pro / Particulier</div>
                <div className="dash-pro-analytics-body">
                  {/* Barre proportionnelle */}
                  <div className="dash-pro-mix-bar">
                    <div style={{width: proPct + "%", background: "#1F5AE6"}} />
                    <div style={{width: partPct + "%", background: "var(--accent)"}} />
                  </div>
                  <div className="dash-pro-mix-row">
                    <span className="dash-pro-mix-dot" style={{background: "#1F5AE6"}} />
                    <span className="dash-pro-mix-label"><strong>Pro</strong> · {mix.pro} ventes ({proPct}%)</span>
                    <span className="dash-pro-mix-margin">marge moy. +{mix.pro_avg.toLocaleString("fr-FR")} €</span>
                  </div>
                  <div className="dash-pro-mix-row">
                    <span className="dash-pro-mix-dot" style={{background: "var(--accent)"}} />
                    <span className="dash-pro-mix-label"><strong>Particulier</strong> · {mix.particulier} ventes ({partPct}%)</span>
                    <span className="dash-pro-mix-margin">marge moy. +{mix.part_avg.toLocaleString("fr-FR")} €</span>
                  </div>
                  {/* Mini segments */}
                  <div className="dash-pro-segments-mini">
                    <div className="dash-pro-segments-title">Rotation par segment</div>
                    <div className="dash-pro-seg-row"><span>10–30k €</span><span>8 v</span><span className="up">+3 100 €</span></div>
                    <div className="dash-pro-seg-row"><span>30–80k €</span><span>3 v</span><span className="up">+5 200 €</span></div>
                    <div className="dash-pro-seg-row"><span>{"< 10k €"}</span><span>1 v</span><span className="up">+800 €</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CarteGriseServiceCard — service complémentaire (hors « 4 moteurs ») ─────
// Carte de service secondaire mettant en avant l'estimation carte grise & malus.
// N'altère pas la logique « une plateforme, quatre moteurs » : c'est un complément.
function CarteGriseServiceCard({ onOpen }) {
  const points = [
    ["🗺️", "Taxe régionale (Y.1)", "Barème à jour par région — dont majoration IDFM en Île-de-France."],
    ["🌫️", "Malus CO₂ WLTP", "Barème annuel 2021→2026 avec décote import selon l'ancienneté."],
    ["⚖️", "Malus au poids (TMOM)", "Dès 1 500 kg (2026), abattements hybrides pris en compte."],
    ["🇩🇪", "Véhicule importé", "Allemagne, Belgique, Luxembourg ou autre UE — décote par ancienneté."],
  ];
  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "1.5rem 2rem 3rem" }}>
      <div className="sec-eyebrow" style={{ color: "var(--accent)", marginBottom: ".8rem" }}>Service complémentaire</div>
      <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 18, padding: "1.8rem 2rem", display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: "2rem", alignItems: "center" }} className="cg-service-card">
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", fontSize: ".72rem", fontWeight: 700, letterSpacing: ".5px", textTransform: "uppercase", color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", borderRadius: 20, padding: ".25rem .7rem", marginBottom: ".9rem" }}>🧾 Carte grise & malus écologique</div>
          <h3 style={{ fontFamily: "var(--font-head)", fontSize: "1.6rem", lineHeight: 1.2, color: "var(--text)", margin: "0 0 .7rem" }}>Estimez le coût d'immatriculation en France, import compris</h3>
          <p style={{ color: "var(--sub)", fontSize: ".92rem", lineHeight: 1.65, margin: "0 0 1.2rem" }}>
            Calculez en quelques secondes le coût réel d'une carte grise : taxe régionale, malus CO₂ et malus au poids, y compris pour un véhicule importé d'Allemagne, de Belgique ou du Luxembourg, avec décote selon l'ancienneté. Décodez une plaque pour préremplir l'immatriculation, le VIN et le véhicule.
          </p>
          <button className="btn btn-accent" onClick={onOpen} style={{ cursor: "pointer" }}>Estimer une carte grise →</button>
        </div>
        <div style={{ display: "grid", gap: ".7rem" }}>
          {points.map(([ic, t, d]) => (
            <div key={t} style={{ display: "flex", gap: ".7rem", alignItems: "flex-start", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 12, padding: ".75rem .9rem" }}>
              <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>{ic}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: ".84rem", color: "var(--text)" }}>{t}</div>
                <div style={{ fontSize: ".76rem", color: "var(--sub)", lineHeight: 1.45 }}>{d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── FourEnginesSection — "Une plateforme, quatre moteurs" ──────────────────
function FourEnginesSection({ onCheck, onSell, onBuy, onMarket }) {
  const engines = [
    { icon: "✦", title:"Je sais si le prix est le bon",
      desc:"Une cote calibrée sur le marché réel, pas un barème mensuel figé. 4,99 € le dossier, pas d'abonnement.",
      cta: "Estimer un véhicule", action: onSell },
    { icon: "⊚", title: "Toutes les infos, sans aucune saisie",
      desc: "Plaque ou VIN → historique complet, kilométrage, sinistres, contrôle technique. Rapport PDF prêt à transmettre.",
      illustration: true },
    { icon: "◭", title: "Je repère les bonnes affaires",
      desc: "Indiquez un budget, VehicleIQ identifie les véhicules qui correspondent vraiment — avec cote et points de vigilance.",
      cta: "Trouver un véhicule", action: onBuy },
  ];
  return (
    <div className="engines-section">
      <div className="engines-inner">
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Ce que vous obtenez</div>
        <div className="sec-title">Une décision claire,<br /><em>en 30 secondes</em></div>
        <p className="engines-sub">Trois outils, une seule saisie : la plaque du véhicule.</p>
        <div className="engines-grid">
          {engines.map(e => (
            <div key={e.title} className="engine-card">
              <div className="engine-icon">{e.icon}</div>
              <div className="engine-title">{e.title}</div>
              <div className="engine-desc">{e.desc}</div>
              {e.illustration ? (
                <button onClick={openLeadForm} title="Décoder une plaque — être recontacté"
                  style={{ background: "rgba(168,128,58,.04)", border: "1px solid rgba(168,128,58,.18)", borderRadius: 10, padding: ".7rem .8rem", cursor: "pointer", display: "flex", alignItems: "center", gap: ".5rem", width: "100%", textAlign: "left" }}>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: ".82rem", fontWeight: 700, letterSpacing: "1.5px", color: "var(--text)", background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 6, padding: ".4rem .7rem", flexShrink: 0 }}>
                    AB-123-CD
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: ".3rem", color: "var(--accent)", fontWeight: 700, fontSize: ".85rem" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35"/></svg>
                    Décoder →
                  </span>
                </button>
              ) : (
                <button className="engine-cta" onClick={e.action}>{e.cta} →</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SolutionsByActorSection — "Des solutions pour chaque acteur" ───────────
function SolutionsByActorSection({ onContact }) {
  const sols = [
    { t: "Garages VO indépendants", d: "Cote et reprise pour garages achat/revente VO" },
    { t: "Marchands VO", d: "Optimisation des achats et des marges pour marchands indépendants" },
    { t: "Revendeurs VO", d: "Pricing et rotation de stock optimisée" },
    { t: "Mandataires VO", d: "Sourcing et cote pour mandataires indépendants" },
  ];
  return (
    <div className="actors-section">
      <div className="actors-inner">
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Solutions métier</div>
        <div className="sec-title">Conçu pour les<br /><em>indépendants du VO</em></div>
        <p className="actors-sub">VehicleIQ s'adresse exclusivement aux garages, marchands, revendeurs et mandataires VO indépendants — segment sous-équipé en France.</p>
        <div className="actors-grid">
          {sols.map(s => (
            <button key={s.t} className="actor-card" onClick={onContact}>
              <div className="actor-title">{s.t}</div>
              <div className="actor-desc">{s.d}</div>
              <span className="actor-link">Découvrir →</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SeoBrandPagesSection — "Estimez n'importe quel véhicule" (grille SEO) ───
function SeoBrandPagesSection({ onSell }) {
  const items = [
    { tag: "Marque", label: "Cote Peugeot 208" },
    { tag: "Marque", label: "Cote Renault Clio" },
    { tag: "Marque", label: "Prix Tesla Model 3" },
    { tag: "Marque", label: "Cote BMW Série 3" },
    { tag: "Recherche", label: "Combien vaut ma voiture ?" },
    { tag: "Recherche", label:"Cote reprise auto" },
    { tag: "Recherche", label: "Prix voiture occasion" },
    { tag: "Outil", label: "Cote voiture gratuite" },
    { tag: "Outil", label:"Logiciel cote auto pro" },
    { tag: "Outil", label: "Alternative Argus en ligne" },
  ];
  return (
    <div className="seopg-section">
      <div className="seopg-inner">
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Cote par véhicule</div>
        <div className="sec-title">Estimez <em>n'importe quel véhicule</em></div>
        <p className="seopg-sub">Cote de marché en temps réel pour chaque marque, modèle et besoin du VO.</p>
        <div className="seopg-grid">
          {items.map(it => (
            <button key={it.label} className="seopg-card" onClick={onSell}>
              <span className="seopg-tag">{it.tag}</span>
              <span className="seopg-label">{it.label}</span>
              <span className="seopg-arrow">Estimer →</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ArgusAlternativeSection — l'alternative pour les indépendants du VO ────
function ArgusAlternativeSection({ onLaunch }) {
  // v32 — Comparatif simplifié à 4 colonnes (brief conversion) : VehicleIQ vs
  // Argus vs CarVertical vs recherche manuelle. Remplace l'ancien tableau à 6
  // concurrents anonymisés (42 cellules) qui noyait le message et affichait un
  // prix obsolète (149 €, ne correspondant à aucune offre actuelle).
  const cols = ["Argus", "CarVertical", "Recherche manuelle", "VehicleIQ"];
  const rows = [
    ["Temps par véhicule", "5 min", "5 min", "20-30 min", "30 secondes"],
    ["Historique officiel SIV", "Non", "Partiel", "Manuel (HistoVec)", "Oui, automatique"],
    ["Problèmes connus du modèle", "Non", "Non", "À chercher soi-même", "Oui, automatique"],
    ["Cote calibrée marché réel", "Non (barème)", "Non", "Manuel (annonces)", "Oui"],
    ["Prix", "~20-30 €/rapport", "~30 €/rapport", "Gratuit, mais 20-30 min", "4,99 € le dossier"],
    ["Abonnement obligatoire", "Non", "Non", "—", "Non"],
  ];
  return (
    <div className="argus-alt-section">
      <div className="argus-alt-inner">
        <h2 className="argus-alt-h2">Pourquoi VehicleIQ plutôt qu'une recherche manuelle</h2>
        <p className="argus-alt-seo">
          Un seul outil remplace l'Argus, CarVertical et des heures de recherche manuelle — au tarif unique de 4,99 € par dossier.
        </p>
        <div className="argus-alt-table compare-multi">
          <div className="argus-alt-trow argus-alt-thead">
            <div>Critère</div>
            {cols.map((c, i) => (
              <div key={i} className={i === cols.length - 1 ? "argus-alt-viq-head" : ""}>{c}</div>
            ))}
          </div>
          {rows.map((row, i) => (
            <div className="argus-alt-trow" key={i}>
              <div className="argus-alt-crit">{row[0]}</div>
              {row.slice(1).map((v, j) => (
                <div key={j} className={`argus-alt-cell ${j === row.length - 2 ? "argus-alt-viq" : ""}`}>
                  {v === true ? <span className="argus-ok">✓</span> :
                   v === false ? <span className="argus-x">—</span> : v}
                </div>
              ))}
            </div>
          ))}
        </div>
        <p className="argus-alt-fineprint" style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: ".6rem", marginBottom: "1rem", textAlign: "center", lineHeight: 1.5 }}>
          Tarifs concurrents : ordres de grandeur observés sur les offres publiques courantes. Les conditions précises varient selon volume et négociation.
        </p>
        <button className="btn btn-accent argus-alt-cta" onClick={onLaunch}>
          Essayer gratuitement →
        </button>
      </div>
    </div>
  );
}

// ─── ValuePropositionSection — proposition de valeur & positionnement ────────
function ValuePropositionSection({ onLaunch }) {
  const pillars = [
    {
      icon: "📈", n: "01",
      title: "Vendre, pas seulement reprendre",
      desc:"Les cotes classiques donnent un chiffre ; les outils de reprise tirent le prix vers le bas. VehicleIQ optimise votre prix de vente et votre délai d'écoulement — une promesse de marge, pas une simple cote.",
    },
    {
      icon: "🔄", n: "02",
      title: "La boucle qui s'améliore avec l'usage",
      desc: "Votre suivi de vente réinjecte les prix réellement obtenus pour calibrer le moteur VIQ.AI®. Plus vous l'utilisez, plus il devient précis sur vos segments — un avantage de données que la cote figée ne peut offrir.",
    },
    {
      icon: "🧩", n: "03",
      title: "Tout-en-un, accessible",
      desc:"Cote, comparatif multi-plateformes, mandat de vente, certificat de cession et analytics — dans une seule interface, en pay-as-you-go. La profondeur des grands éditeurs, l'accessibilité d'une app.",
    },
  ];
  const positions = [
    { t: "Cotes grand public", d: "Un chiffre neutre, payant à l'acte ou financé par l'audience. Pas de pilotage commercial." },
    { t: "Data providers premium", d: "Couverture et historique massifs, mais licences lourdes réservées aux constructeurs et grands réseaux." },
    { t: "Outils de reprise (DMS)", d: "Intégrés aux suites de concession, souvent orientés rachat — donc prix tiré vers le bas." },
    { t: "VehicleIQ", d: "Le copilote commercial du marchand VO indépendant : profondeur analytique et accessibilité, orienté marge.", viq: true },
  ];
  return (
    <div className="engines-section">
      <div className="engines-inner">
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Notre proposition de valeur</div>
        <div className="sec-title">Le <em>copilote commercial</em><br />du marchand VO indépendant</div>
        <p className="engines-sub" style={{ maxWidth: 680 }}>
          De la Cote au mandat, une seule plateforme pour estimer juste, positionner sur le bon portail,
          vendre plus vite et tracer ses marges — sans licence lourde ni biais de reprise.
        </p>
        <div className="why-grid" style={{ marginTop: "1rem" }}>
          {pillars.map(p => (
            <div key={p.n} className="why-card">
              <div style={{ fontSize: "1.8rem", marginBottom: ".6rem" }}>{p.icon}</div>
              <div className="why-num" style={{ fontSize: "1.4rem", color: "var(--accent)", marginBottom: ".4rem" }}>{p.n}</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.15rem", fontWeight: 800, color: "var(--text)", marginBottom: ".5rem" }}>{p.title}</div>
              <div style={{ fontSize: ".88rem", color: "var(--sub)", lineHeight: 1.6 }}>{p.desc}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: "3rem" }}>
          <div style={{ textAlign: "center", fontFamily: "var(--font-head)", fontSize: "1.3rem", fontWeight: 800, color: "var(--text)", marginBottom: "1.4rem" }}>
            Où se situe VehicleIQ sur le marché
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem", maxWidth: 1000, margin: "0 auto" }}>
            {positions.map(pos => (
              <div key={pos.t} style={{
                background: pos.viq ? "var(--accent-soft)" : "var(--bg)",
                border: pos.viq ? "1.5px solid var(--accent)" : "1px solid var(--border)",
                borderRadius: 14, padding: "1.2rem 1.3rem",
              }}>
                <div style={{ fontWeight: 800, color: pos.viq ? "var(--accent)" : "var(--text)", marginBottom: ".4rem", fontSize: ".98rem" }}>
                  {pos.viq ? "★ " : ""}{pos.t}
                </div>
                <div style={{ fontSize: ".84rem", color: "var(--sub)", lineHeight: 1.55 }}>{pos.d}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ textAlign: "center", marginTop: "2.4rem" }}>
          <button className="btn btn-accent" onClick={onLaunch}>Tester gratuitement →</button>
        </div>
      </div>
    </div>
  );
}

// ─── ProcessSection — Comment ça marche ──────────────────────────────────────
function ProcessSection({ onLaunch }) {
  const steps = [
    {
      icon:"🚗", n:"01",
      title:"Je saisis une plaque",
      desc:"Plaque d'immatriculation française ou VIN. Aucune autre saisie nécessaire.",
      tag:"Compte créé en 2 min · 5 cotes offertes",
    },
    {
      icon:"⚡", n:"02",
      title:"VehicleIQ récupère les données",
      desc:"Historique officiel, cote de marché, problèmes connus du modèle — automatiquement.",
      tag:"API SIV officielle · 30 secondes",
    },
    {
      icon:"✅", n:"03",
      title:"Je prends ma décision",
      desc:"Reprendre ou non, à quel prix, et une annonce prête à publier. Rapport PDF exportable.",
      tag:"Rapport PDF · prêt à transmettre",
    },
  ];
  return (
    <div className="process-section" id="comment-ca-marche">
      <div className="process-inner">
        <div className="sec-eyebrow" style={{ color:"var(--accent)" }}>Comment ça marche</div>
        <div className="sec-title">De la plaque à la décision<br />en <em>30 secondes</em></div>
        <div className="process-steps">
          {steps.map(s => (
            <div key={s.n} className="process-step">
              <div className="process-num">{s.n}</div>
              <div className="process-icon">{s.icon}</div>
              <div className="process-content">
                <div className="process-title">{s.title}</div>
                <div className="process-desc">{s.desc}</div>
                <div className="process-tag">{s.tag}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign:"center", marginTop:"2.5rem" }}>
          <button className="btn btn-accent" style={{ padding:".75rem 2rem", fontSize:".95rem" }}
            onClick={onLaunch}>
            Essayer gratuitement →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TrustStrip — Bande certifications & sécurité ────────────────────────────
function TrustStrip() {
  const certs = [
    { icon:"💳", name:"Sans abonnement", desc:"Pay-as-you-go" },
    { icon:"🔐", name:"Firebase Auth", desc:"Authentification sécurisée" },
    { icon:"💳", name:"Stripe", desc:"Paiements certifiés PCI" },
    { icon:"🛡️", name:"RGPD", desc:"Données conformes" },
    { icon:"🏢", name:"SIV Officiel", desc:"API Ministère de l'Intérieur" },
    { icon:"⭐", name:"4.9/5", desc:"Note utilisateurs pro", highlight:true },
  ];
  return (
    <div className="trust-strip">
      <div className="trust-inner">
        <div className="trust-label">Certifications & Sécurité</div>
        <div className="trust-certs">
          {certs.map(c => (
            <div key={c.name} className={"cert-item" + (c.highlight ? " cert-item-hl" : "")}>
              <span className="cert-icon">{c.icon}</span>
              <div>
                <div className="cert-name">{c.name}</div>
                <div className="cert-desc">{c.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── BrandsAndPlatformsSection — marques estimables + plateformes analysées ──
function BrandsAndPlatformsSection() {
  // Marques estimables (échantillon représentatif sur 950+ modèles catalogue)
  const brands = [
    "Renault", "Peugeot", "Citroën", "Volkswagen", "BMW", "Mercedes-Benz", "Audi",
    "Toyota", "Ford", "Opel", "Dacia", "Fiat", "Nissan", "Hyundai", "Kia", "Seat",
    "Škoda", "Mini", "Volvo", "Mazda", "Honda", "Suzuki", "Jeep", "Land Rover",
    "Porsche", "Tesla", "Alfa Romeo", "DS", "Cupra", "Lexus", "Jaguar", "Mitsubishi",
    "Ferrari", "Lamborghini", "Aston Martin", "McLaren", "Bentley", "Maserati",
    "Rolls-Royce", "Alpine", "Smart", "Abarth", "Polestar", "MG", "BYD",
  ];
  // Plateformes analysées en temps réel (échantillon sur 100+)
  const platforms = [
    "LeBonCoin", "AutoScout24", "La Centrale", "Facebook Marketplace", "ParuVendu",
    "Aramisauto", "L'Argus", "Caradisiac", "Auto Plus", "Motorlegend", "OLX",
    "Mobile.de", "CarAndClassic", "Bring a Trailer", "Vivastreet Auto", "Promoneuve",
    "Reezocar", "Spoticar", "CapCar", "Heycar", "Autohero", "Zoomcar Pro", "VParts",
    "L'Automobile Magazine", "Largus Pro", "Motorlegend Cote", "Kidioui", "Elite Auto",
  ];

  return (
    <div className="brands-platforms-section" style={{ padding: "4rem 1.5rem", maxWidth: 1200, margin: "0 auto" }}>
      {/* Marques */}
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Couverture catalogue</div>
        <h2 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(1.5rem, 4vw, 2.2rem)", color: "var(--text)", letterSpacing: "-.02em", margin: ".4rem 0 .6rem" }}>
          Toutes les marques, du généraliste à l'exception
        </h2>
        <p style={{ fontSize: ".92rem", color: "var(--sub)", maxWidth: 620, margin: "0 auto 2rem", lineHeight: 1.6 }}>
          Plus de <strong style={{ color: "var(--accent)" }}>950 modèles</strong> référencés avec prix constructeur officiel France — de la citadine à l'hypercar.
        </p>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", justifyContent: "center", marginBottom: "4rem" }}>
        {brands.map((b, i) => (
          <span key={b} style={{
            fontSize: ".82rem", padding: ".4rem .9rem",
            background: "var(--white)", border: "1.5px solid var(--border)",
            borderRadius: 100, color: "var(--text)", fontWeight: 500,
            transition: "all .15s", cursor: "default",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text)"; e.currentTarget.style.transform = "translateY(0)"; }}>
            {b}
          </span>
        ))}
        <span style={{
          fontSize: ".82rem", padding: ".4rem .9rem",
          background: "var(--accent)", color: "#fff",
          borderRadius: 100, fontWeight: 600,
        }}>
          + 900 autres modèles
        </span>
      </div>

      {/* Plateformes */}
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <div className="sec-eyebrow" style={{ color: "var(--green)" }}>Sources marché temps réel</div>
        <h2 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(1.5rem, 4vw, 2.2rem)", color: "var(--text)", letterSpacing: "-.02em", margin: ".4rem 0 .6rem" }}>
          100+ plateformes analysées en continu
        </h2>
        <p style={{ fontSize: ".92rem", color: "var(--sub)", maxWidth: 640, margin: "0 auto 2rem", lineHeight: 1.6 }}>
          Notre moteur croise en <strong style={{ color: "var(--green)" }}>temps réel</strong> les annonces actives des principales places de marché auto pour calibrer le prix juste — là où l'Argus se fige sur un barème mensuel.
        </p>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", justifyContent: "center" }}>
        {platforms.map((p, i) => (
          <span key={p} style={{
            fontSize: ".8rem", padding: ".38rem .85rem",
            background: "var(--white)", border: "1.5px solid var(--border)",
            borderRadius: 8, color: "var(--text)", fontWeight: 500,
            display: "inline-flex", alignItems: "center", gap: ".4rem",
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)", flexShrink: 0 }} />
            {p}
          </span>
        ))}
        <span style={{
          fontSize: ".8rem", padding: ".38rem .85rem",
          background: "var(--green)", color: "#fff",
          borderRadius: 8, fontWeight: 600,
        }}>
          + 70 autres sources
        </span>
      </div>
    </div>
  );
}
// Modèle "Pay what you estimate" : aucune notion de crédit/pack côté utilisateur.
// L'utilisateur voit uniquement le prix en € de la prochaine estimation, qui
// dégresse automatiquement selon son volume mensuel. Le backend gère les paliers.
function PricingModal({ onClose, onSignup, user, pricingUsage }) {
  // Détection : l'utilisateur connecté bénéficie déjà de l'essai gratuit.
  // On grise alors l'offre "Essai gratuit" (déjà active).
  const isLoggedIn = !!user;
  // État de facturation : sait-on si une carte est déjà enregistrée ? Sert à router
  // les clics sur les offres d'abonnement directement vers la souscription (sans
  // repasser par l'enregistrement de carte) lorsque la carte existe déjà.
  const [billing, setBilling] = useState(null); // { has_payment_method, card, subscription }
  const [subModal, setSubModal] = useState(null); // null | "unlimited" | "multi_agent"
  // v31 — Bascule mensuel/annuel pour les offres Pro Solo et Pro Multi-Agent.
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // "monthly" | "annual"
  useEffect(() => {
    if (!user) { setBilling(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const h = await getAuthHeaders();
        if (!h) return;
        const r = await fetch(`${API_URL}/api/billing/current`, { headers: h });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setBilling(d);
      } catch (e) { /* silencieux */ }
    })();
    return () => { cancelled = true; };
  }, [user]);
  const hasCard = !!billing?.has_payment_method;
  const hasActiveSub = !!billing?.subscription;
  const freeUsed = pricingUsage?.used_total !== undefined ? pricingUsage.used_total : (pricingUsage?.used_this_month || 0);
  const freeQuota = pricingUsage?.free_total || pricingUsage?.free_per_month || 5;
  const freeRemaining = Math.max(0, freeQuota - freeUsed);
  // v31 — Prix affichés + % d'économie annuel (même calcul que server.js
  // annualDiscountPct, dupliqué ici pour l'affichage sans aller-retour API).
  const SOLO_MONTHLY = 399, SOLO_ANNUAL = 2999;
  const MULTI_MONTHLY = 799, MULTI_ANNUAL = 4999;
  // v110 — Promotions admin actives : ancien prix barré / nouveau prix réduit,
  // affichées ici comme sur les autres pages tarifs (même source que le
  // panneau admin et que le montant réellement facturé par Stripe).
  const [promotions, setPromotions] = useState(null);
  useEffect(() => { apiGet("/api/plans/promotions").then(r => { if (r?.success) setPromotions(r.promotions); }).catch(() => {}); }, []);
  const soloPromo = promotions?.unlimited?.[billingPeriod];
  const multiPromo = promotions?.multi_agent_3?.[billingPeriod];
  const soloDiscountPct = Math.round((1 - SOLO_ANNUAL / (SOLO_MONTHLY * 12)) * 100);
  const multiDiscountPct = Math.round((1 - MULTI_ANNUAL / (MULTI_MONTHLY * 12)) * 100);
  const isAnnual = billingPeriod === "annual";

  // Paliers tarifaires affichés (synchro avec server.js PRICING_TIERS)
  const tiers = [
    {
      label: "Essai gratuit",
      eyebrow: "Découverte",
      price: "0 €",
      per:"5 cotes offertes et déjà actives à la création du compte",
      target: "Pour tester sans engagement · offertes automatiquement, sans activation",
      features: [
        "✓ Cotes VIQ.AI® complètes",
        "✓ Contrôle VIN / Plaque gratuit",
        "🛡 Score de fiabilité véhicule",
        "✓ Aucune carte bancaire requise",
        "✓ Données identiques au plan payant",
      ],
      cta: "Démarrer gratuitement",
      best: false,
      color: "var(--green)",
    },
    {
      label: "Starter",
      eyebrow: "À l'usage",
      price: "4,99 €",
      per:"par dossier",
      target: "Garages, marchands & mandataires indépendants",
      sub: "Tarif unique — vous ne payez que ce que vous utilisez",
      features: [
        "✓ Cotes illimitées",
        "✓ Sans abonnement · sans engagement",
        "✓ Outil de suivi métier complet",
        "✓ Comparatif 5 plateformes",
        "✓ Suivi de vente · marges trackées",
        "✓ Export PDF · rapport détaillé",
        "🛡 Score de fiabilité véhicule",
      ],
      cta: "Commencer",
      best: false,
      color: "var(--accent)",
    },
    {
      label: "Pro Solo",
      eyebrow: "⭐ Le plus populaire",
      price: soloPromo?.discountPct
        ? `${Math.round(soloPromo.finalPriceCents / 100).toLocaleString("fr-FR")} €`
        : (isAnnual ? `${SOLO_ANNUAL.toLocaleString("fr-FR")} €` : `${SOLO_MONTHLY} €`),
      oldPrice: soloPromo?.discountPct ? `${Math.round(soloPromo.basePriceCents / 100).toLocaleString("fr-FR")} €` : null,
      promoLabel: soloPromo?.discountPct ? soloPromo.label : null,
      per: isAnnual ? `par an · cotes illimitées · soit ${(SOLO_ANNUAL/12).toFixed(0)} €/mois (-${soloDiscountPct}%)` : "par mois · cotes illimitées",
      target: "Garages à fort volume · 80+ cotes/mois",
      sub: isAnnual ? `Forfait annuel · -${soloDiscountPct}% vs paiement mensuel` : "Forfait mensuel · rentable dès 82 cotes/mois",
      features: [
        "✓ Cotes illimitées",
        "✓ Outil de suivi métier complet",
        "✓ Comparatif 5 plateformes",
        "✓ Suivi de vente · marges trackées",
        "✓ Export PDF · rapport détaillé",
        "📝 Génération de mandats de vente",
        "📄 Génération de CERFA de cession VO",
        "🛒 Aide à l'achat (recommandations IA)",
        "🧾 Estimateur de malus & carte grise",
        "🔗 Lien client leads personnalisé",
        "📸 Analyse véhicule & cotation par photo (vision IA · illimité)",
        "🛡 Score de fiabilité + 5 points de vigilance automatiques sur vos leads (gratuit)",
        "🔳 QR code personnalisé (collecte de leads)",
        "✓ Facturation mensuelle fixe",
        "✓ Résiliable à tout moment",
        "🚀 Sans frais d'installation",
        "🎓 Formation offerte",
        "📸 Service Photo IA inclus (activation sur demande)",
        "📡 Diffusion Multiplateformes incluse (activation sur demande)",
        "📋 Service administratif : déclaration fiscale",
      ],
      cta: "Choisir Pro Solo",
      best: true,
      color: "var(--green)",
      isUnlimited: true,
    },
    {
      label: "Pro Multi-Agent",
      eyebrow: "Pour les organisations",
      price: multiPromo?.discountPct
        ? `${Math.round(multiPromo.finalPriceCents / 100).toLocaleString("fr-FR")} €`
        : (isAnnual ? `${MULTI_ANNUAL.toLocaleString("fr-FR")} €` : `${MULTI_MONTHLY} €`),
      oldPrice: multiPromo?.discountPct ? `${Math.round(multiPromo.basePriceCents / 100).toLocaleString("fr-FR")} €` : null,
      promoLabel: multiPromo?.discountPct ? multiPromo.label : null,
      per: isAnnual ? `par an — 3 agents inclus · soit ${(MULTI_ANNUAL/12).toFixed(0)} €/mois (-${multiDiscountPct}%)` : "HT / mois — 3 agents inclus",
      target: "Marchands, équipes multi-collaborateurs",
      sub: isAnnual ? `Sans engagement · -${multiDiscountPct}% vs paiement mensuel · SIRET requis` : "Sans engagement · résiliable à tout moment · SIRET requis",
      features: [
        "✓ 3 agents inclus (1 propriétaire + 2 collaborateurs)",
        "✓ Cotes illimitées",
        "⚡ 10 cotes simultanées (vs 3)",
        "🎯 12 recommandations d'achat par requête (vs 8)",
        "✓ Compte dédié + SLA 99,5 %",
        "✓ Multi-utilisateurs avec rôles",
        "📝 Génération de mandats de vente",
        "📄 Génération de CERFA de cession VO",
        "🛒 Aide à l'achat (recommandations IA)",
        "🧾 Estimateur de malus & carte grise",
        "🔗 Lien client leads personnalisé",
        "📸 Analyse véhicule & cotation par photo (vision IA · illimité)",
        "🛡 Score de fiabilité + 5 points de vigilance automatiques sur vos leads (gratuit)",
        "🔳 QR code personnalisé (collecte de leads)",
        "✓ Facturation mensualisée (CB ou virement)",
        "✓ Support prioritaire sous 24h",
        "🚀 Sans frais d'installation",
        "🎓 Formation offerte",
        "📸 Service Photo IA inclus (activation sur demande)",
        "📡 Diffusion Multiplateformes incluse (activation sur demande)",
        "📋 Service administratif : déclaration fiscale",
      ],
      cta: "Souscrire Pro Multi-Agent",
      best: false,
      color: "var(--accent2)",
      isMultiAgent: true, // v5 : flag déclencheur du flow d'abonnement direct
    },
  ];

  // Bandeau informatif : les 5 cotes gratuites sont offertes ET déjà actives,
  // sans aucune activation nécessaire (le mécanisme "+5 bonus" est retiré).
  const bonusBanner = user && pricingUsage ? (
    <div style={{ background: "var(--accent-soft)", border: "1.5px solid rgba(168,128,58,.3)", borderRadius: 12, padding: ".85rem 1.1rem", margin: "0 0 1.1rem", fontSize: ".82rem", color: "var(--text)", lineHeight: 1.5 }}>
      <strong style={{ color: "var(--green)" }}>✓ Vous avez obtenu 5 usages gratuits automatiquement à la création de votre compte.</strong>
      {" "}Déjà actifs, sans activation nécessaire.
    </div>
  ) : null;

  // (legacy) handler email — conservé pour rétro-compatibilité éventuelle
  // mais plus utilisé puisque l'offre passe en abonnement direct
  const handleQuoteRequest = () => {
    const subject = encodeURIComponent("Question sur l'offre Pro Multi-Agent VehicleIQ");
    const body = encodeURIComponent("Bonjour l'équipe VehicleIQ,\n\nJe souhaite plus d'informations sur l'offre Pro Multi-Agent 799 €/mois.\n\nCordialement,");
    window.dispatchEvent(new CustomEvent("viq-open-contact", { detail: { mailto: "mailto:contact@vehicleiq.fr?subject=" + subject + "&body=" + body } }));
  };

  return (
    <>
    {subModal === "unlimited" && (
      <SubscribeUnlimitedModal
        onClose={() => { setSubModal(null); onClose(); }}
        user={user}
        card={billing?.card}
        period={billingPeriod}
      />
    )}
    {subModal === "multi_agent" && (
      <SubscribeMultiAgentModal
        onClose={() => { setSubModal(null); onClose(); }}
        user={user}
        card={billing?.card}
        period={billingPeriod}
      />
    )}
    {!subModal && (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel panel-wide" style={{ maxWidth: 1240, animation: "slideUp .25s ease" }}>
        <div className="panel-header">
          <div>
            <div className="panel-title">Tarification</div>
            <div className="panel-sub">Payez ce que vous estimez. Sans abonnement, sans engagement, sans surprise.</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

          {/* Hero pricing message */}
          <div style={{
            textAlign: "center",
            background: "linear-gradient(135deg, rgba(168,128,58,.06), rgba(168,128,58,.01) 60%, transparent)",
            border: "1.5px solid rgba(168,128,58,.18)",
            borderRadius: 14,
            padding: "1.4rem 1.5rem"
          }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.6rem", color: "var(--text)", lineHeight: 1.2, letterSpacing: "-.01em", marginBottom: ".4rem" }}>
              <span style={{ color: "var(--accent)", fontWeight: 600 }}>4,99 €</span> par dossier professionnel, tarif unique.
            </div>
            <div style={{ fontSize: ".85rem", color: "var(--sub)", lineHeight: 1.6, maxWidth: 620, margin: "0 auto" }}>
              Là où la concurrence facture <strong style={{ color: "var(--text)" }}>250 à 600 €/mois en abonnement</strong>,
              VehicleIQ vous facture <strong style={{ color: "var(--text)" }}>uniquement vos cotes</strong>.
            </div>
          </div>

          {/* Bandeau informatif : 5 cotes gratuites offertes et déjà actives */}
          {bonusBanner}

          {/* v42 — Bascule mensuel/annuel : le badge de réduction est sorti de
              cette ligne centrée et placé en dessous, sur sa propre ligne à
              hauteur réservée. Avant, il apparaissait EN LIGNE juste après
              "Annuel" dans une rangée centrée (justify-content:center) : son
              apparition élargissait le contenu et recentrait toute la
              rangée, ce qui faisait visuellement "sauter" le switch vers la
              gauche à chaque bascule. */}
          <div style={{ textAlign: "center", margin: "0 0 1.2rem" }}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: ".7rem" }}>
              <span style={{ fontSize: ".82rem", fontWeight: billingPeriod === "monthly" ? 700 : 400, color: billingPeriod === "monthly" ? "var(--text)" : "var(--sub)" }}>Mensuel</span>
              <button
                onClick={() => setBillingPeriod(p => p === "monthly" ? "annual" : "monthly")}
                aria-label="Basculer entre facturation mensuelle et annuelle"
                style={{
                  position: "relative", width: 48, height: 26, borderRadius: 20, border: "none", cursor: "pointer",
                  background: isAnnual ? "var(--green)" : "var(--border)", transition: "background .2s", flexShrink: 0, padding: 0,
                }}>
                <span style={{
                  position: "absolute", top: 3, left: isAnnual ? 25 : 3, width: 20, height: 20, borderRadius: "50%",
                  background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.3)",
                }} />
              </button>
              <span style={{ fontSize: ".82rem", fontWeight: isAnnual ? 700 : 400, color: isAnnual ? "var(--text)" : "var(--sub)" }}>
                Annuel
              </span>
            </div>
            <div style={{ minHeight: "1.7rem", marginTop: ".5rem" }}>
              {isAnnual && (
                <span style={{ fontSize: ".68rem", fontWeight: 700, color: "var(--green)", background: "rgba(46,111,92,.1)", border: "1px solid rgba(46,111,92,.25)", borderRadius: 20, padding: ".2rem .6rem", display: "inline-block" }}>
                  -{soloDiscountPct}% Pro Solo · -{multiDiscountPct}% Pro Multi-Agent
                </span>
              )}
            </div>
          </div>

          {/* 4 colonnes tiers (auto-fit pour responsive) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: ".9rem" }}>
            {tiers.map((t, i) => {
              // ── Détection de l'offre EN COURS de l'utilisateur ──────────────
              // • Essai gratuit : actif tant qu'il reste des estimations offertes (sans abonnement)
              // • Starter (à l'usage) : actif si carte enregistrée + essais épuisés + aucun abonnement
              // • Pro Solo / Multi-Agent : actif selon billing.subscription.plan_code
              const isFreeTier = t.price === "0 €";
              const isStarterTier = t.label === "Starter";
              const planCode = billing?.subscription?.plan_code || null;
              const alreadyActive = isFreeTier && isLoggedIn; // carte grisée + "Déjà obtenu"
              const freeActive = isFreeTier && isLoggedIn && freeRemaining > 0 && !hasActiveSub;
              const starterActive = isStarterTier && isLoggedIn && hasCard && !hasActiveSub && freeRemaining === 0;
              const starterPending = isStarterTier && isLoggedIn && hasCard && !hasActiveSub && freeRemaining > 0;
              const soloActive = !!t.isUnlimited && planCode === "unlimited";
              const multiActive = !!t.isMultiAgent && planCode === "multi_agent_3";
              const currentOffer = starterActive || soloActive || multiActive;
              const ctaLocked = alreadyActive || currentOffer || starterPending;
              return (
              <div key={i} style={{
                background: alreadyActive ? "var(--bg)" : "var(--white)",
                border: `1.5px solid ${currentOffer ? "var(--green)" : alreadyActive ? "var(--border)" : t.best ? "var(--accent)" : t.isUnlimited ? "var(--green)" : "var(--border)"}`,
                borderRadius: 14,
                padding: "1.4rem 1.4rem 1.2rem",
                position: "relative",
                boxShadow: currentOffer ? "0 0 0 4px rgba(46,111,92,.10)" : alreadyActive ? "none" : t.best ? "0 0 0 4px rgba(168,128,58,.08)" : t.isUnlimited ? "0 0 0 4px rgba(46,111,92,.06)" : "none",
                transition: "all .15s",
                display: "flex", flexDirection: "column",
                opacity: alreadyActive ? 0.72 : 1,
              }}>
                {/* Badge "actif / offre en cours" */}
                {(freeActive || currentOffer) && (
                  <div style={{
                    position: "absolute", top: -10, right: 14,
                    background: "var(--green)", color: "#fff",
                    fontSize: ".6rem", fontWeight: 700, padding: ".2rem .6rem",
                    borderRadius: 100, textTransform: "uppercase", letterSpacing: ".5px",
                    fontFamily: "var(--font-mono)",
                  }}>
                    {currentOffer ? "✓ Offre en cours" : "✓ Actif"}
                  </div>
                )}
                {/* Eyebrow */}
                <div style={{ fontSize: ".62rem", fontWeight: 700, color: t.best ? "var(--accent)" : "var(--muted)", textTransform: "uppercase", letterSpacing: "1.4px", fontFamily: "var(--font-mono)", marginBottom: ".5rem" }}>
                  {t.eyebrow}
                </div>
                {/* Title */}
                <div style={{ fontFamily: "var(--font-head)", fontSize: "1.5rem", fontWeight: 600, color: "var(--text)", letterSpacing: "-.01em", marginBottom: ".15rem" }}>
                  {t.label}
                </div>
                {/* Target / persona */}
                <div style={{ fontSize: ".75rem", color: "var(--sub)", lineHeight: 1.5, marginBottom: "1rem", minHeight: "2.4rem" }}>
                  {alreadyActive ? (
                    <>Il vous reste <strong style={{ color: "var(--accent)" }}>{freeRemaining} cote{freeRemaining > 1 ? "s" : ""} offerte{freeRemaining > 1 ? "s" : ""}</strong>. Ensuite, vous basculerez au paiement à l'usage (4,99 €/dossier) — ou souscrivez une offre mensuelle sans engagement.</>
                  ) : t.target}
                </div>
                {/* Price */}
                {t.oldPrice && (
                  <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".15rem" }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: "1rem", color: "var(--muted)", textDecoration: "line-through" }}>{t.oldPrice}</span>
                    {t.promoLabel && (
                      <span style={{ fontSize: ".62rem", fontWeight: 700, color: "#fff", background: "var(--red)", borderRadius: 6, padding: ".1rem .45rem" }}>{t.promoLabel}</span>
                    )}
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "baseline", gap: ".4rem", marginBottom: ".25rem" }}>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: "2.4rem", fontWeight: 600, color: t.oldPrice ? "var(--red)" : t.color, lineHeight: 1, letterSpacing: "-.02em" }}>
                    {t.price}
                  </div>
                </div>
                <div style={{ fontSize: ".75rem", color: "var(--sub)", marginBottom: t.sub ? ".4rem" : "1rem" }}>
                  {t.per}
                </div>
                {t.sub && (
                  <div style={{ fontSize: ".7rem", color: "var(--muted)", lineHeight: 1.5, marginBottom: "1rem", fontStyle: "italic" }}>
                    {t.sub}
                  </div>
                )}
                {/* Grille dégressive pour le plan Pro */}
                {t.tiers && (
                  <div style={{
                    background: "var(--bg)", border: "1px solid var(--border)",
                    borderRadius: 8, padding: ".7rem .8rem", marginBottom: "1rem",
                    fontSize: ".72rem"
                  }}>
                    <div style={{ fontSize: ".6rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.1px", fontFamily: "var(--font-mono)", marginBottom: ".45rem" }}>
                      Dégressif automatique
                    </div>
                    {t.tiers.map((tier, ti) => (
                      <div key={ti} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: ".2rem 0", borderBottom: ti < t.tiers.length - 1 ? "1px dashed var(--border)" : "none" }}>
                        <span style={{ color: "var(--sub)", fontSize: ".7rem" }}>{tier.range}</span>
                        <span style={{ display: "flex", alignItems: "baseline", gap: ".3rem" }}>
                          <span style={{ fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-mono)" }}>{tier.price}</span>
                          {tier.saving && (
                            <span style={{ fontSize: ".62rem", color: "var(--green)", fontWeight: 700, background: "var(--green-soft)", padding: ".05rem .3rem", borderRadius: 3 }}>{tier.saving}</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Features */}
                <div style={{ display: "flex", flexDirection: "column", gap: ".35rem", marginBottom: "1rem", flex: 1 }}>
                  {t.features.map((f, fi) => (
                    <div key={fi} style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.4 }}>
                      {f}
                    </div>
                  ))}
                </div>
                {/* CTA */}
                <button
                  className={ctaLocked ? "btn btn-ghost" : t.best ? "btn btn-accent" : "btn btn-ghost"}
                  disabled={ctaLocked}
                  style={{
                    width: "100%", padding: ".7rem 1rem", fontSize: ".88rem", justifyContent: "center",
                    cursor: ctaLocked ? "default" : "pointer",
                    opacity: ctaLocked ? 0.65 : 1,
                    ...(currentOffer ? { color: "var(--green)", fontWeight: 700 } : {}),
                  }}
                  onClick={() => {
                    if (ctaLocked) return;
                    // Si l'utilisateur est connecté ET a déjà une carte enregistrée,
                    // on ouvre DIRECTEMENT la modale de souscription du plan choisi,
                    // sans repasser par l'enregistrement de carte.
                    if (t.isMultiAgent) {
                      if (isLoggedIn && hasCard) {
                        setSubModal("multi_agent");
                        return;
                      }
                      // Pas de carte → flow d'enregistrement carte, avec intent mémorisé
                      onClose();
                      try { sessionStorage.setItem("viq_subscribe_intent", "multi_agent_3"); } catch(_) {}
                      onSignup();
                      return;
                    }
                    if (t.isUnlimited) {
                      if (isLoggedIn && hasCard) {
                        setSubModal("unlimited");
                        return;
                      }
                      onClose();
                      try { sessionStorage.setItem("viq_subscribe_intent", "unlimited"); } catch(_) {}
                      onSignup();
                      return;
                    }
                    if (t.isQuote) handleQuoteRequest();
                    else { onClose(); onSignup(); }
                  }}>
                  {currentOffer ? "✓ Offre en cours"
                    : starterPending ? "✓ Carte enregistrée · bascule auto après vos essais"
                    : alreadyActive ? "✓ Déjà obtenu"
                    : `${t.cta}${!t.isQuote && !t.isMultiAgent ? " →" : t.isMultiAgent ? " →" : ""}`}
                </button>
              </div>
              );
            })}
          </div>

          {/* Offre Intégration API VIQ.AI® */}
          <div style={{ background: "var(--bg)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".25rem", flexWrap: "wrap" }}>
              <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)" }}>
                Intégration API VIQ.AI®
              </div>
              <span style={{ fontSize: ".6rem", fontWeight: 700, color: "var(--accent2)", background: "rgba(31,90,230,.08)", border: "1px solid rgba(31,90,230,.2)", padding: ".12rem .45rem", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".5px" }}>Marque blanche · sur mesure</span>
            </div>
            <div style={{ fontSize: ".72rem", color: "var(--sub)", marginBottom: "1rem", lineHeight: 1.5 }}>
              Intégrez le moteur de Cote VIQ.AI® directement dans vos propres outils. Personnalisation poussée, marque blanche et nom de domaine dédié disponibles.
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".84rem" }}>
              <thead>
                <tr style={{ background: "var(--deep)", borderBottom: "1.5px solid var(--border)" }}>
                  <th style={{ padding: ".55rem .8rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".5px" }}>Forfait API (mensuel)</th>
                  <th style={{ padding: ".55rem .8rem", textAlign: "right", color: "var(--sub)", fontWeight: 600, fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".5px" }}>Prix</th>
                  <th style={{ padding: ".55rem .8rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".5px" }}>Profil</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: ".55rem .8rem", fontWeight: 700, color: "var(--text)" }}>1 000 appels API / mois</td>
                  <td style={{ padding: ".55rem .8rem", textAlign: "right", fontWeight: 700, color: "var(--text)" }}>Sur devis</td>
                  <td style={{ padding: ".55rem .8rem", color: "var(--sub)" }}>Intégration ponctuelle · POC</td>
                </tr>
                <tr style={{ borderBottom: "1px solid var(--border)", background: "rgba(168,128,58,.05)" }}>
                  <td style={{ padding: ".55rem .8rem", fontWeight: 700, color: "var(--accent)" }}>2 500 appels API / mois <span style={{ fontSize: ".6rem", color: "#fff", background: "var(--accent)", padding: ".1rem .4rem", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".5px", marginLeft: ".3rem" }}>Populaire</span></td>
                  <td style={{ padding: ".55rem .8rem", textAlign: "right", fontWeight: 700, color: "var(--accent)" }}>Sur devis</td>
                  <td style={{ padding: ".55rem .8rem", color: "var(--sub)" }}>Volume intermédiaire</td>
                </tr>
                <tr>
                  <td style={{ padding: ".55rem .8rem", fontWeight: 700, color: "var(--text)" }}>7 000 appels API / mois</td>
                  <td style={{ padding: ".55rem .8rem", textAlign: "right", fontWeight: 700, color: "var(--text)" }}>Sur devis</td>
                  <td style={{ padding: ".55rem .8rem", color: "var(--sub)" }}>Fort volume</td>
                </tr>
              </tbody>
            </table>
            <div style={{ fontSize: ".72rem", color: "var(--sub)", marginTop: ".7rem", lineHeight: 1.5, background: "var(--white)", border: "1px solid var(--border)", borderRadius: 8, padding: ".7rem .9rem" }}>
              <strong style={{ color: "var(--text)" }}>Modules activables :</strong> chaque fonction de l'Outil de suivi métier (cote, scan d'achat, décodage plaque, carte grise, comparatif plateformes, Live Marché, documents, analyses, formulaires clients) est un produit distinct intégrable, avec son propre volume d'appels API <strong>mensuel</strong> estimé. Le devis additionne les modules retenus.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", margin: ".9rem 0" }}>
              {["🎨 Personnalisation", "🌐 Nom de domaine dédié", "🔑 Clés API + sandbox", "📞 Accompagnement technique"].map(f => (
                <span key={f} style={{ fontSize: ".7rem", color: "var(--sub)", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 20, padding: ".25rem .7rem" }}>{f}</span>
              ))}
            </div>
            <button className="btn btn-accent" style={{ alignSelf: "flex-start" }}
              onClick={() => {
                const subject = "Demande — Intégration API VIQ.AI®";
                const body = encodeURIComponent("Bonjour l'équipe VehicleIQ,\n\nJe souhaite intégrer l'API VIQ.AI® et recevoir un devis.\n\nVolume d'appels API estimé / mois : ____\nPersonnalisation souhaitée : (marque blanche / nom de domaine dédié / aucune)\nCas d'usage : ____\n\nForfait envisagé :\n[ ] 1 000 appels API / mois\n[ ] 2 500 appels API / mois\n[ ] 7 000 appels API / mois\n\nCordialement");
                window.dispatchEvent(new CustomEvent("viq-open-contact", { detail: { mailto: "mailto:contact@vehicleiq.fr?subject=" + encodeURIComponent(subject) + "&body=" + body } }));
              }}>
              ✉ Contacter l'équipe : contact@vehicleiq.fr
            </button>
          </div>

          {/* Service Photo IA — inclus dans les forfaits Pro, activation sur demande */}
          <div style={{ background: "var(--bg)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".25rem", flexWrap: "wrap" }}>
              <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)" }}>
                📸 Studio Photo IA
              </div>
              <span style={{ fontSize: ".6rem", fontWeight: 700, color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", padding: ".12rem .45rem", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".5px" }}>Inclus Pro · sur demande</span>
            </div>
            <div style={{ fontSize: ".72rem", color: "var(--sub)", marginBottom: "1rem", lineHeight: 1.5 }}>
              Photographie IA et optimisation d'images de véhicules : détourage automatique, fond studio ou showroom réaliste, rendu homogène pour vos annonces. Inclus dans les forfaits Pro Solo et Pro Multi-Agent — activation sur simple demande, sans coût supplémentaire.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", margin: ".4rem 0 1rem" }}>
              {["✂️ Détourage automatique", "🏛️ Showroom réaliste", "💡 Optimisation lumière", "🔁 Rendu homogène"].map(f => (
                <span key={f} style={{ fontSize: ".7rem", color: "var(--sub)", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 20, padding: ".25rem .7rem" }}>{f}</span>
              ))}
            </div>
            <button className="btn btn-accent" style={{ alignSelf: "flex-start" }}
              onClick={() => {
                const subject = "Activation — Studio Photo IA VehicleIQ";
                const body = encodeURIComponent("Bonjour l'équipe VehicleIQ,\n\nJe suis client Pro et je souhaite activer le service Studio Photo IA (inclus dans mon forfait).\n\nVolume de véhicules à traiter / mois : ____\nType de rendu souhaité : (fond studio blanc / showroom réaliste / les deux)\nPlateformes de diffusion : ____\n\nCordialement");
                window.dispatchEvent(new CustomEvent("viq-open-contact", { detail: { mailto: "mailto:contact@vehicleiq.fr?subject=" + encodeURIComponent(subject) + "&body=" + body } }));
              }}>
              ✉ Demander l'activation : contact@vehicleiq.fr
            </button>
          </div>

          {/* Diffusion Multiplateformes — incluse dans les forfaits Pro, activation sur demande */}
          <div style={{ background: "var(--bg)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".25rem", flexWrap: "wrap" }}>
              <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)" }}>
                📡 Diffusion automatique Multiplateformes
              </div>
              <span style={{ fontSize: ".6rem", fontWeight: 700, color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", padding: ".12rem .45rem", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".5px" }}>Inclus Pro · sur demande</span>
            </div>
            <div style={{ fontSize: ".72rem", color: "var(--sub)", marginBottom: "1rem", lineHeight: 1.5 }}>
              Publiez automatiquement votre stock sur les principaux portails (La Centrale, LeBonCoin, AutoScout24, Google, réseaux sociaux…) en une seule saisie. Mise à jour temps réel et règles de diffusion personnalisées. Inclus dans les forfaits Pro Solo et Pro Multi-Agent — activation sur simple demande, sans coût supplémentaire.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", margin: ".4rem 0 1rem" }}>
              {["🌐 100+ portails & médias", "⌨️ Saisie unique", "🔄 Mise à jour temps réel", "🎯 Règles de diffusion", "📊 Stats par portail"].map(f => (
                <span key={f} style={{ fontSize: ".7rem", color: "var(--sub)", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 20, padding: ".25rem .7rem" }}>{f}</span>
              ))}
            </div>
            <button className="btn btn-accent" style={{ alignSelf: "flex-start" }}
              onClick={() => {
                const subject = "Activation — Diffusion automatique Multiplateformes VehicleIQ";
                const body = encodeURIComponent("Bonjour l'équipe VehicleIQ,\n\nJe suis client Pro et je souhaite activer le service de diffusion automatique de mes annonces (inclus dans mon forfait).\n\nNombre de véhicules en stock : ____\nPortails souhaités : (La Centrale / LeBonCoin / AutoScout24 / réseaux sociaux / autres)\nLogiciel/DMS actuel : ____\nFréquence de mise à jour du stock : ____\n\nCordialement");
                window.dispatchEvent(new CustomEvent("viq-open-contact", { detail: { mailto: "mailto:contact@vehicleiq.fr?subject=" + encodeURIComponent(subject) + "&body=" + body } }));
              }}>
              ✉ Demander l'activation : contact@vehicleiq.fr
            </button>
          </div>

          <div style={{ background: "rgba(168,128,58,.04)", border: "1px solid rgba(168,128,58,.12)", borderRadius: 10, padding: "1rem 1.2rem" }}>
            <div style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--text)", marginBottom: ".6rem" }}>✅ Inclus dans tous les plans</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem" }}>
              {["API SIV officielle", "Contrôle VIN gratuit & illimité", "Données temps réel", "Export PDF", "Score VIQ.AI® de précision", "Sans abonnement", "Sans engagement", "Carte bancaire facultative pour tester"].map(item => (
                <span key={item} style={{ fontSize: ".72rem", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 50, padding: ".22rem .7rem", color: "var(--sub)" }}>
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Footer info — pas de CTA redondant car chaque carte tier a déjà son propre bouton */}
          <div style={{ fontSize: ".72rem", color: "var(--muted)", textAlign: "center", padding: ".5rem 1rem", lineHeight: 1.6 }}>
            5 cotes offertes et déjà actives à la création du compte · Aucune carte bancaire requise pour démarrer · SIRET requis pour le plan Pro
          </div>

          {/* v5 : lien CGV en bas de la tarification */}
          <div style={{
            padding: "1rem 1.2rem", borderTop: "1px dashed var(--border)",
            fontSize: ".72rem", color: "var(--muted)", lineHeight: 1.6, textAlign: "center",
          }}>
            En souscrivant à l'un de ces plans, vous acceptez nos{" "}
            <button
              onClick={() => { try { window.dispatchEvent(new CustomEvent("viq-open-legal", { detail: "cgv" })); } catch(_) {} }}
              style={{ background: "none", border: "none", color: "var(--accent)", padding: 0, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", fontSize: "inherit" }}>
              Conditions Générales de Vente
            </button>.
            {" "}TVA non applicable, art. 293 B du CGI · Tarifs en euros HT.
          </div>
        </div>
      </div>
    </div>
    )}
    </>
  );
}


// ─── PlaqueAutoFill — auto-fill form from plate number ───────────────────────
// Construit la désignation complète d'un véhicule : marque + modèle + version,
// en évitant les doublons (la version contient parfois déjà le modèle).
// Source unique utilisée par TOUS les décodeurs de plaque → sortie cohérente.
function buildVehicleFullName(data) {
  const clean = (s) => {
    if (s == null) return "";
    const t = String(s).trim().replace(/\s+/g, " ");
    if (t === "" || t === "—" || t === "-" || t.toLowerCase() === "n/a") return "";
    return t;
  };
  const containsCI = (h, n) => (h && n) ? h.toLowerCase().includes(n.toLowerCase()) : false;
  const make    = clean(data && (data.make || data.marque));
  const model   = clean(data && (data.model || data.modele));
  const version = clean(data && (data.version || data.sra_commercial));
  const parts = [];
  if (make) parts.push(make);
  if (model && !containsCI(parts.join(" "), model)) parts.push(model);
  if (version && !containsCI(parts.join(" "), version)) {
    // Si la version contient déjà le modèle, on remplace le modèle par la version
    // pour éviter "Toyota Prius Prius 1.5 VVT-i" → "Toyota Prius 1.5 VVT-i"
    if (model && containsCI(version, model)) {
      const idx = parts.findIndex(p => p.toLowerCase() === model.toLowerCase());
      if (idx >= 0) parts.splice(idx, 1);
    }
    parts.push(version);
  }
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

function PlaqueAutoFill({ onFill, onImportDossier }) {
  const [plate, setPlate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState(null); // données récupérées (VIN, marque, etc.)
  const [importBusy, setImportBusy] = useState(false);
  const [importMsg, setImportMsg] = useState(null);

  const handleLookup = async () => {
    const clean = plate.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (clean.length < 7) { setError("Format invalide — ex : AB-123-CD"); return; }
    setLoading(true); setError(""); setDone(false); setVehicleInfo(null);
    try {
      const data = await apiGet("/api/check/" + clean + "?is_pro=false&siret=");
      if (!data.success || !data.data) {
        setError("Véhicule non trouvé. Vous pouvez saisir les informations manuellement.");
      } else {
        onFill(data.data);
        setVehicleInfo(data.data);
        setDone(true);
      }
    } catch(e) {
      setError("Erreur de connexion — saisissez les informations manuellement.");
    }
    setLoading(false);
  };

  const fmt = (v) => {
    const c = v.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 9);
    // Format SIV : AA-000-AA
    const p1 = c.slice(0,2), p2 = c.slice(2,5), p3 = c.slice(5,7);
    if (c.length > 5) return `${p1}-${p2}-${p3}`;
    if (c.length > 2) return `${p1}-${p2}`;
    return p1;
  };

  return (
    <div style={{ background:"rgba(168,128,58,.04)", border:"1px solid rgba(168,128,58,.15)", borderRadius:10, padding:".9rem 1.1rem", marginBottom:"1.1rem" }}>
      <div style={{ fontSize:".72rem", fontWeight:700, color:"var(--accent)", textTransform:"uppercase", letterSpacing:"1px", marginBottom:".6rem", display:"flex", alignItems:"center", gap:".4rem" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35"/></svg>
        Récupérer les données par plaque
      </div>
      <div style={{ display:"flex", gap:".5rem", alignItems:"center", flexWrap:"wrap" }}>
        <input
          value={plate}
          onChange={e => { setPlate(fmt(e.target.value)); setError(""); setDone(false); setVehicleInfo(null); }}
          onKeyDown={e => e.key === "Enter" && handleLookup()}
          placeholder="AB-123-CD"
          maxLength={9}
          style={{ flex:"0 0 auto", background:"var(--white)", border:"1.5px solid var(--border)", color:"var(--text)", fontFamily:"var(--font-mono)", fontSize:"1rem", padding:".55rem .9rem", borderRadius:8, outline:"none", letterSpacing:"2px", textTransform:"uppercase", fontWeight:700, width:160 }}
          onFocus={e => e.target.style.borderColor="var(--accent)"}
          onBlur={e => e.target.style.borderColor="var(--border)"}
        />
        <button className="btn btn-accent btn-sm"
          onClick={handleLookup}
          disabled={loading || plate.replace(/[^a-zA-Z0-9]/g,"").length < 7}
          style={{ opacity: plate.replace(/[^a-zA-Z0-9]/g,"").length < 7 ? .5 : 1 }}>
          {loading ? (
            <span style={{ display:"flex", alignItems:"center", gap:".35rem" }}>
              <span style={{ width:12, height:12, border:"2px solid rgba(255,255,255,.4)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin .7s linear infinite", display:"inline-block" }}/>
              Recherche…
            </span>
          ) : "Récupérer →"}
        </button>
        {/* Carte d'info véhicule récupéré : VIN + marque/modèle */}
        {done && vehicleInfo && (
          <div style={{
            flex:"1 1 280px",
            minWidth:240,
            background:"linear-gradient(135deg, rgba(0,200,100,.08), rgba(168,128,58,.06))",
            border:"1px solid rgba(0,200,100,.3)",
            borderRadius:8,
            padding:".5rem .8rem",
            display:"flex",
            flexDirection:"column",
            gap:".15rem",
            animation:"slideUp .3s ease"
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:".4rem" }}>
              <span style={{ fontSize:".75rem", color:"var(--green)", fontWeight:700 }}>✓ Véhicule identifié</span>
              {vehicleInfo.make && (
                <span style={{ fontSize:".7rem", color:"var(--text)", fontWeight:600 }}>
                  · {vehicleInfo.make}{vehicleInfo.model ? ` ${vehicleInfo.model}` : ""}
                  {vehicleInfo.year ? ` (${vehicleInfo.year})` : ""}
                </span>
              )}
            </div>
            {vehicleInfo.vin && (
              <div style={{ display:"flex", alignItems:"center", gap:".4rem", marginTop:".2rem" }}>
                <span style={{ fontSize:".62rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:".5px", fontWeight:600 }}>VIN</span>
                <code style={{
                  fontFamily:"var(--font-mono)",
                  fontSize:".78rem",
                  background:"rgba(0,0,0,.25)",
                  padding:".15rem .5rem",
                  borderRadius:4,
                  color:"var(--accent)",
                  fontWeight:700,
                  letterSpacing:"1px",
                  userSelect:"all",
                  cursor:"copy"
                }}
                title="Cliquer pour sélectionner"
                onClick={(e) => {
                  // Sélection du VIN au clic pour copier facilement
                  const range = document.createRange();
                  range.selectNodeContents(e.currentTarget);
                  const sel = window.getSelection();
                  sel.removeAllRanges();
                  sel.addRange(range);
                }}>
                  {vehicleInfo.vin}
                </code>
              </div>
            )}
            {/* v101 — Estimation instantanée, uniquement à partir de la plaque (comme
                La Centrale) : affichée dès que le décodage répond, sans écran de
                chargement. Le questionnaire ci-dessous sert désormais à AFFINER
                cette estimation provisoire, plus à en produire une 1ère. */}
            {vehicleInfo.marketValue && (
              <div style={{ marginTop: ".45rem", paddingTop: ".45rem", borderTop: "1px solid rgba(0,200,100,.25)" }}>
                <div style={{ fontSize: ".92rem", fontWeight: 800, color: "var(--accent)" }}>
                  💰 Estimation instantanée : {vehicleInfo.marketValue}
                </div>
                <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".1rem" }}>
                  Provisoire, à partir de la plaque seule — complétez les champs ci-dessous pour affiner votre Cote VIQ.AI®.
                </div>
              </div>
            )}
          </div>
        )}
        {done && !vehicleInfo?.vin && <span style={{ fontSize:".8rem", color:"var(--green)", fontWeight:700 }}>✓</span>}
      </div>
      {/* v55 — Reprendre les données d'un Dossier VIQ déjà existant pour cette
          plaque (photos, HistoVec) — l'Estimation ouverte normalement n'en
          hérite pas autrement, seul "Continuer" depuis le Dossier VIQ le faisait. */}
      {done && onImportDossier && (
        <div style={{ marginTop: ".5rem" }}>
          <button type="button" disabled={importBusy}
            onClick={async () => {
              setImportBusy(true); setImportMsg(null);
              const msg = await onImportDossier(plate.replace(/[^a-zA-Z0-9]/g, "").toUpperCase());
              setImportMsg(msg || "Aucun Dossier VIQ existant pour cette plaque.");
              setImportBusy(false);
            }}
            style={{ background: "none", border: "none", cursor: importBusy ? "wait" : "pointer", fontSize: ".76rem", color: "var(--accent)", fontWeight: 600, textDecoration: "underline", opacity: importBusy ? .6 : 1 }}>
            {importBusy ? "Recherche…" : "📂 Importer les données du dossier existant"}
          </button>
          {importMsg && <div style={{ fontSize: ".72rem", color: importMsg.startsWith("✓") ? "var(--green)" : "var(--muted)", marginTop: ".3rem" }}>{importMsg}</div>}
        </div>
      )}
      {error && <div style={{ fontSize:".73rem", color:"#F59E0B", marginTop:".4rem" }}>⚠ {error}</div>}
      <div style={{ fontSize:".65rem", color:"var(--muted)", marginTop:".4rem" }}>
        Format SIV · Ex : AB-123-CD · Les champs seront pré-remplis automatiquement
      </div>
      {onImportDossier && (
        <div style={{ fontSize: ".65rem", color: "var(--muted)", marginTop: ".3rem", fontStyle: "italic" }}>
          💡 Véhicule déjà traité ? La plaque d'un Dossier VIQ existant importe aussi ses photos et son HistoVec.
        </div>
      )}
    </div>
  );
}

// ─── ContactModal — Formulaire de contact via HubSpot ────────────────────────
// Le formulaire est intégralement géré par HubSpot (portail 148450863).
// Les leads arrivent directement dans le CRM HubSpot — plus de mailto.
function ContactModal({ onClose }) {
  // Le script HubSpot v2 scanne tous les `.hs-form-frame` présents dans le DOM
  // au moment de son chargement et les remplace par une iframe vers le
  // formulaire hébergé. Comme la modale apparaît dynamiquement (et que React
  // démonte le DOM à la fermeture), on injecte le script à chaque ouverture :
  //   - Au mount : on ajoute le <script> dans <body>. HubSpot scanne et rend.
  //   - Au unmount : on retire le <script>. La prochaine ouverture en injectera
  //     un nouveau → re-scan → formulaire rendu à nouveau.
  // Cette approche reste légère (~50 KB) et garantit un rendu fiable sans
  // dépendre d'une API HubSpot globale (window.hbspt) qui n'est pas exposée
  // par cet embed v2.
  useEffect(() => {
    const HUBSPOT_SRC = "https://js-eu1.hsforms.net/forms/embed/148450863.js";
    const script = document.createElement("script");
    script.src = HUBSPOT_SRC;
    script.defer = true;
    script.setAttribute("data-viq-hubspot", "1");
    document.body.appendChild(script);
    return () => {
      try { document.body.removeChild(script); } catch(_) {}
    };
  }, []);

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel contact-modal" style={{ animation:"slideUp .25s ease" }}>
        <div className="panel-header">
          <div>
            <div className="panel-title">Nous contacter</div>
            <div className="panel-sub">Un expert VehicleIQ vous rappelle sous 24h</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          <div className="contact-form">
            {/* Bandeau info */}
            <div style={{ background:"rgba(168,128,58,.05)", border:"1px solid rgba(168,128,58,.15)", borderRadius:8, padding:".65rem .9rem", fontSize:".78rem", color:"var(--sub)", marginBottom:".9rem" }}>
              📞 Démonstration personnalisée · Durée 30 min · Visio ou téléphone
            </div>
            {/* Conteneur HubSpot — le script remplit ce div par une iframe au mount */}
            <div
              className="hs-form-frame"
              data-region="eu1"
              data-form-id="b81df723-4c12-44a7-aa5f-ef081e4b5ddf"
              data-portal-id="148450863"
            />
            {/* Repli e-mail fiable : si le formulaire HubSpot ne se charge pas, ou si l'utilisateur
                préfère l'e-mail direct. Le clic déclenche le mailto (avec objet/corps pré-remplis
                quand le contact a été ouvert depuis un CTA spécifique). */}
            <div style={{ marginTop: ".9rem", paddingTop: ".8rem", borderTop: "1px solid var(--border)", fontSize: ".78rem", color: "var(--sub)", textAlign: "center", lineHeight: 1.6 }}>
              Le formulaire ne s'affiche pas ? Écrivez-nous directement à{" "}
              <a
                href={(() => {
                  let d = {}; try { d = window.__viq_contact_fallback || {}; } catch (_) {}
                  return d.mailto || "mailto:contact@vehicleiq.fr?subject=" + encodeURIComponent("Demande de contact VehicleIQ") + "&body=" + encodeURIComponent("Bonjour l'équipe VehicleIQ,\n\n\n\nCordialement,");
                })()}
                style={{ color: "var(--accent)", fontWeight: 700 }}
              >contact@vehicleiq.fr</a>
            </div>
            <div style={{ fontSize:".68rem", color:"var(--muted)", textAlign:"center", marginTop:".8rem" }}>
              En soumettant ce formulaire, vous acceptez d'être recontacté par VehicleIQ.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DemoBookingModal — Réservation via Calendly inline ─────────────────────
// Embed officiel Calendly. Le script est chargé une seule fois (idempotent)
// à l'ouverture de la modale ; la div .calendly-inline-widget est détectée
// automatiquement par le script. Nettoyage au démontage pour éviter les
// fuites de listeners entre ouvertures successives.
function DemoBookingModal({ onClose }) {
  useEffect(() => {
    const existing = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
    if (!existing) {
      const s = document.createElement("script");
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      document.head.appendChild(s);
    } else if (window.Calendly && window.Calendly.initInlineWidgets) {
      // Script déjà présent → forcer ré-init car Calendly n'injecte pas
      // automatiquement l'iframe quand la div est remontée après démontage.
      setTimeout(() => { try { window.Calendly.initInlineWidgets(); } catch(_) {} }, 60);
    }
    return () => {
      const widget = document.getElementById("calendly-embed-target");
      if (widget) widget.innerHTML = "";
    };
  }, []);

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 760, width: "100%", animation: "slideUp .25s ease" }}>
        <div className="panel-header">
          <div>
            <div className="panel-title">Réserver une démo VehicleIQ</div>
            <div className="panel-sub">Choisissez un créneau qui vous convient · 30 minutes en visio</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body" style={{ padding: 0 }}>
          <div
            id="calendly-embed-target"
            className="calendly-inline-widget"
            data-url="https://calendly.com/bonglorentz/presentation-vehicleiq_fr?hide_gdpr_banner=1"
            style={{ minWidth: 320, height: 700 }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── ProCtaSection — Call to action final dark premium ────────────────────────
function ProCtaSection({ onStart, onDemo }) {
  return (
    <div className="pro-cta-section">
      <div className="pro-cta-inner">
        <div className="pro-cta-badge">◆ &nbsp;Offre professionnelle</div>
        <div className="pro-cta-title">
          Prêt à transformer<br />votre manière de travailler ?
        </div>
        <div className="pro-cta-sub">
          Rejoignez les professionnels de l'automobile qui font confiance à VehicleIQ pour chaque décision d'achat, de reprise et de Cote.
          5 cotes offertes, aucune carte bancaire requise.
        </div>
        <div className="pro-cta-btns">
          <button className="btn-gold" onClick={onStart}>
            Commencer maintenant →
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── FinanceSimulator — Simulation LLD / LOA pour pros (Math.js style) ────────
//
// Formules utilisées :
// - Annuité (paiement mensuel constant)        : P = C × t/(1 - (1+t)^-n)
//   où C = capital financé, t = taux mensuel, n = nb mensualités
// - Pour LLD/LOA : capital amorti = (prix - VR) — la valeur résiduelle reste
//   à payer au terme (option d'achat LOA) ou est restituée (LLD)
// - Coût total = mensualité × n + apport (+ VR pour LOA si rachat)
//
// Sources : pratiques marché 2026 (BPIfrance, ARVAL, ALD Automotive, Stellantis Bank)
function FinanceSimulator({ initialPrice = 25000, vehicleLabel = "" }) {
  const [type, setType]           = useState("LLD"); // LLD | LOA
  const [price, setPrice]         = useState(initialPrice);
  const [downPmt, setDownPmt]     = useState(Math.round(initialPrice * 0.10 / 100) * 100); // 10% par défaut
  const [duration, setDuration]   = useState(48); // mois
  const [annualKm, setAnnualKm]   = useState(15000);
  const [taeg, setTaeg]           = useState(4.5); // taux annuel effectif global %
  const [residualPct, setResPct]  = useState(35); // VR en % du prix neuf

  // Synchroniser le prix avec le véhicule sélectionné
  useEffect(() => {
    if (initialPrice && initialPrice !== price) {
      setPrice(initialPrice);
      setDownPmt(Math.round(initialPrice * 0.10 / 100) * 100);
    }
  }, [initialPrice]);

  // Calculs financiers
  const calc = useMemo(() => {
    const P = Number(price) || 0;
    const apport = Math.min(P, Number(downPmt) || 0);
    const VR = type === "LLD"
      ? Math.round(P * (residualPct / 100))   // LLD : véhicule restitué (VR théorique du loueur)
      : Math.round(P * (residualPct / 100));   // LOA : option d'achat à la fin
    const capitalFinance = Math.max(0, P - apport - VR);
    const n = Math.max(1, Number(duration) || 48);
    const tMonthly = Math.max(0, (Number(taeg) || 0) / 100 / 12);

    // Mensualité : si taux > 0 → formule d'annuité ; sinon division simple
    let mensualite;
    if (tMonthly > 0) {
      // Mensualité du capital financé + intérêts sur la VR (qui n'est pas amortie)
      const mensCapital = capitalFinance * tMonthly / (1 - Math.pow(1 + tMonthly, -n));
      const mensInterets = VR * tMonthly; // intérêts sur la valeur résiduelle (pratique courante LOA/LLD)
      mensualite = mensCapital + mensInterets;
    } else {
      mensualite = capitalFinance / n;
    }

    // Coût total
    const totalLoyers = mensualite * n;
    const coutTotalLLD = apport + totalLoyers;            // LLD : restitution sans rachat
    const coutTotalLOA = apport + totalLoyers + VR;        // LOA : avec levée d'option d'achat
    const coutTotal = type === "LLD" ? coutTotalLLD : coutTotalLOA;

    // Coût du financement (intérêts + frais)
    const coutFinancier = coutTotal - (type === "LOA" ? P : (P - VR));

    // Coût par km
    const totalKm = (annualKm * n) / 12;
    const coutParKm = totalKm > 0 ? coutTotal / totalKm : 0;

    return {
      mensualite: Math.round(mensualite * 100) / 100,
      apport: apport,
      VR: VR,
      capitalFinance: capitalFinance,
      totalLoyers: Math.round(totalLoyers),
      coutTotal: Math.round(coutTotal),
      coutFinancier: Math.round(coutFinancier),
      coutParKm: Math.round(coutParKm * 100) / 100,
      totalKm: Math.round(totalKm),
    };
  }, [price, downPmt, duration, taeg, residualPct, type, annualKm]);

  // Helper barres horizontales
  const Bar = ({ label, value, max, color, suffix = " €" }) => (
    <div style={{ marginBottom: ".5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: ".25rem" }}>
        <span style={{ fontSize: ".7rem", color: "var(--sub)" }}>{label}</span>
        <span style={{ fontSize: ".75rem", fontWeight: 700, color: color, fontFamily: "var(--font-mono)" }}>
          {Number(value).toLocaleString("fr-FR")}{suffix}
        </span>
      </div>
      <div style={{ height: 6, background: "var(--border)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: Math.min(100, max ? (value / max) * 100 : 0) + "%",
          background: color,
          borderRadius: 3,
          transition: "width .4s ease"
        }} />
      </div>
    </div>
  );

  return (
    <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".5rem", marginBottom: "1rem" }}>
        <div>
          <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "1px" }}>
            💼 Simulation de financement
          </div>
          <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".15rem" }}>
            Calcul mensualités LLD / LOA — usage professionnel
            {vehicleLabel ? ` · ${vehicleLabel}` : ""}
          </div>
        </div>
        {/* Toggle LLD / LOA */}
        <div style={{ display: "inline-flex", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "3px" }}>
          {["LLD", "LOA"].map(t => (
            <button key={t} onClick={() => setType(t)}
              style={{
                padding: ".35rem .85rem",
                borderRadius: 6,
                border: "none",
                background: type === t ? "var(--accent)" : "transparent",
                color: type === t ? "#fff" : "var(--sub)",
                fontSize: ".72rem",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all .15s"
              }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Layout : inputs gauche + résultat droite */}
      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: "1.2rem" }}>

        {/* ── Inputs ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: ".7rem" }}>
          {/* Prix */}
          <div>
            <label style={{ fontSize: ".65rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>Prix d'achat TTC</label>
            <div style={{ display: "flex", alignItems: "center", gap: ".4rem", marginTop: ".25rem" }}>
              <input
                type="number"
                value={price}
                onChange={e => setPrice(Math.max(0, Number(e.target.value) || 0))}
                style={{ flex: 1, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: ".4rem .6rem", color: "var(--text)", fontFamily: "var(--font-mono)", fontSize: ".85rem" }}
              />
              <span style={{ fontSize: ".75rem", color: "var(--muted)" }}>€</span>
            </div>
          </div>

          {/* Apport */}
          <div>
            <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: ".65rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>
              <span>Apport / 1er loyer majoré</span>
              <span style={{ fontFamily: "var(--font-mono)", color: "var(--accent)", fontWeight: 700 }}>{Number(downPmt).toLocaleString("fr-FR")} €</span>
            </label>
            <input
              type="range"
              min={0}
              max={price * 0.5}
              step={500}
              value={downPmt}
              onChange={e => setDownPmt(Number(e.target.value))}
              style={{ width: "100%", marginTop: ".25rem", accentColor: "var(--accent)" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".6rem", color: "var(--muted)" }}>
              <span>0 €</span><span>{price ? Math.round(downPmt / price * 100) : 0}%</span><span>50%</span>
            </div>
          </div>

          {/* Durée */}
          <div>
            <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: ".65rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>
              <span>Durée du contrat</span>
              <span style={{ fontFamily: "var(--font-mono)", color: "var(--accent)", fontWeight: 700 }}>{duration} mois ({Math.round(duration / 12 * 10) / 10} ans)</span>
            </label>
            <div style={{ display: "flex", gap: ".3rem", marginTop: ".25rem" }}>
              {[24, 36, 48, 60, 72].map(d => (
                <button key={d} onClick={() => setDuration(d)}
                  style={{
                    flex: 1,
                    padding: ".35rem 0",
                    borderRadius: 6,
                    border: "1px solid",
                    borderColor: duration === d ? "var(--accent)" : "var(--border)",
                    background: duration === d ? "rgba(168,128,58,.1)" : "var(--card)",
                    color: duration === d ? "var(--accent)" : "var(--sub)",
                    fontSize: ".7rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all .15s"
                  }}>{d}m</button>
              ))}
            </div>
          </div>

          {/* Km annuels */}
          <div>
            <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: ".65rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>
              <span>Kilométrage annuel</span>
              <span style={{ fontFamily: "var(--font-mono)", color: "var(--accent)", fontWeight: 700 }}>{Number(annualKm).toLocaleString("fr-FR")} km/an</span>
            </label>
            <input
              type="range"
              min={5000}
              max={45000}
              step={1000}
              value={annualKm}
              onChange={e => setAnnualKm(Number(e.target.value))}
              style={{ width: "100%", marginTop: ".25rem", accentColor: "var(--accent)" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".6rem", color: "var(--muted)" }}>
              <span>5 000</span><span>15 000</span><span>30 000</span><span>45 000</span>
            </div>
          </div>

          {/* TAEG */}
          <div>
            <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: ".65rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>
              <span>TAEG (taux annuel effectif)</span>
              <span style={{ fontFamily: "var(--font-mono)", color: "var(--accent)", fontWeight: 700 }}>{taeg.toFixed(2)} %</span>
            </label>
            <input
              type="range"
              min={1}
              max={9}
              step={0.1}
              value={taeg}
              onChange={e => setTaeg(Number(e.target.value))}
              style={{ width: "100%", marginTop: ".25rem", accentColor: "var(--accent)" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".6rem", color: "var(--muted)" }}>
              <span>1%</span><span>4,5%</span><span>9%</span>
            </div>
          </div>

          {/* Valeur résiduelle */}
          <div>
            <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: ".65rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 600 }}>
              <span>Valeur résiduelle (% prix)</span>
              <span style={{ fontFamily: "var(--font-mono)", color: "var(--accent)", fontWeight: 700 }}>{residualPct}% · {calc.VR.toLocaleString("fr-FR")} €</span>
            </label>
            <input
              type="range"
              min={10}
              max={60}
              step={1}
              value={residualPct}
              onChange={e => setResPct(Number(e.target.value))}
              style={{ width: "100%", marginTop: ".25rem", accentColor: "var(--accent)" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".6rem", color: "var(--muted)" }}>
              <span>10%</span><span>35%</span><span>60%</span>
            </div>
          </div>
        </div>

        {/* ── Résultat ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: ".8rem" }}>
          {/* Carte mensualité */}
          <div style={{
            background: "linear-gradient(135deg, rgba(168,128,58,.08), rgba(168,128,58,.04))",
            border: "1px solid rgba(168,128,58,.25)",
            borderRadius: 10,
            padding: "1rem 1.2rem"
          }}>
            <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontWeight: 700 }}>
              Mensualité {type}
            </div>
            <div style={{ fontSize: "2rem", fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-mono)", letterSpacing: "-.04em", lineHeight: 1.1, marginTop: ".15rem" }}>
              {calc.mensualite.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €
            </div>
            <div style={{ fontSize: ".68rem", color: "var(--sub)", marginTop: ".25rem" }}>
              HT/mois · {duration} mensualités · {(Math.round(calc.mensualite * 1.20 * 100) / 100).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} € TTC
            </div>
          </div>

          {/* Décomposition */}
          <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, padding: ".9rem 1rem" }}>
            <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontWeight: 700, marginBottom: ".6rem" }}>
              Décomposition financière
            </div>
            <Bar label="Apport initial" value={calc.apport} max={calc.coutTotal} color="#60A5FA" />
            <Bar label="Total des loyers" value={calc.totalLoyers} max={calc.coutTotal} color="var(--accent)" />
            {type === "LOA" && (
              <Bar label="Option d'achat (VR)" value={calc.VR} max={calc.coutTotal} color="#A78BFA" />
            )}
            <Bar label="Coût du financement" value={calc.coutFinancier} max={calc.coutTotal} color="#F59E0B" />
          </div>

          {/* Synthèse coût */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem" }}>
            <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: ".7rem .8rem" }}>
              <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", fontWeight: 600, letterSpacing: ".5px" }}>Coût total {type}</div>
              <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-mono)", marginTop: ".1rem" }}>
                {calc.coutTotal.toLocaleString("fr-FR")} €
              </div>
              <div style={{ fontSize: ".58rem", color: "var(--muted)", marginTop: ".15rem" }}>
                {type === "LOA" ? "Avec achat" : "Hors rachat"}
              </div>
            </div>
            <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: ".7rem .8rem" }}>
              <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", fontWeight: 600, letterSpacing: ".5px" }}>Coût par km</div>
              <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-mono)", marginTop: ".1rem" }}>
                {calc.coutParKm.toFixed(2).replace(".", ",")} €
              </div>
              <div style={{ fontSize: ".58rem", color: "var(--muted)", marginTop: ".15rem" }}>
                Sur {calc.totalKm.toLocaleString("fr-FR")} km
              </div>
            </div>
          </div>

          {/* Note explicative */}
          <div style={{ fontSize: ".62rem", color: "var(--muted)", lineHeight: 1.5, padding: ".5rem .7rem", background: "rgba(168,128,58,.04)", border: "1px solid rgba(168,128,58,.12)", borderRadius: 6 }}>
            <strong style={{ color: "var(--accent)" }}>ℹ️</strong> {type === "LLD"
              ? "LLD : véhicule restitué en fin de contrat. Pas d'option d'achat. Loyers déductibles fiscalement (sauf TVNR > seuils CO₂)."
              : "LOA : option d'achat à la fin du contrat (VR). Permet d'acquérir le véhicule ou de le restituer. TVA sur les loyers."}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── ThemeToggleButton — bascule thème sombre/clair (réutilisable) ───────────
// Composant autonome : lit le thème courant depuis document.body et bascule via
// l'évènement global "viq-toggle-theme" (écouté dans App). Utilisable dans
// n'importe quelle modale/dashboard sans recevoir setDarkMode en prop.
function ThemeToggleButton({ size = 34, title }) {
  const [isDark, setIsDark] = useState(
    typeof document !== "undefined" && document.body.classList.contains("dark-mode")
  );
  useEffect(() => {
    const sync = () => setIsDark(document.body.classList.contains("dark-mode"));
    const obs = new MutationObserver(sync);
    if (typeof document !== "undefined") {
      obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    }
    return () => obs.disconnect();
  }, []);
  return (
    <button
      title={title || (isDark ? "Mode clair" : "Mode sombre")}
      aria-label={isDark ? "Activer le mode clair" : "Activer le mode sombre"}
      onClick={() => window.dispatchEvent(new Event("viq-toggle-theme"))}
      style={{
        width: size, height: size, borderRadius: "50%",
        background: "none", border: "1.5px solid var(--border)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", flexShrink: 0, transition: "all .15s", fontSize: "14px",
        color: "var(--text)",
      }}>
      {isDark ? "☀️" : "🌙"}
    </button>
  );
}


// ─── ProServiceLeadCard — carte d'offre Pro générique (sur devis) ────────────
// Carte réutilisable pour les services complémentaires réservés aux comptes
// Pro Solo / Pro Multi-Agent (abonnement actif). Aucun traitement n'est fait
// ici : capture de lead via e-mail pré-rempli (comme l'offre Intégration API).
// `service` : { icon, title, lockedName, desc, features[], quoteSubject, quoteBody }
// Carte de service "document" (mandat / cession) — même présentation que les
// cartes Studio Photo IA / Diffusion : cadre + description + features + bouton.
function ProDocServiceCard({ icon, title, badge, desc, features, ctaLabel, onCta, iconPath }) {
  const wrap = {
    background: "var(--card)", border: "1.5px solid var(--border)",
    borderRadius: 14, padding: "1.3rem 1.5rem", marginBottom: "1.4rem",
    position: "relative", overflow: "hidden",
  };
  return (
    <div style={wrap}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 80% at 90% 10%, rgba(168,128,58,.08), transparent 60%)", pointerEvents: "none" }} />
      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap", marginBottom: ".5rem" }}>
          <span style={{ display: "inline-flex", color: "var(--accent)" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{iconPath}</svg>
          </span>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1.1rem", fontWeight: 800, color: "var(--text)" }}>{title}</div>
          <span style={{ fontSize: ".58rem", fontWeight: 700, color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", padding: ".12rem .5rem", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".5px" }}>{badge}</span>
        </div>
        <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.55, marginBottom: "1rem", maxWidth: 640 }}>{desc}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginBottom: "1.1rem" }}>
          {features.map(f => (
            <span key={f} style={{ fontSize: ".7rem", color: "var(--sub)", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 20, padding: ".25rem .7rem" }}>{f}</span>
          ))}
        </div>
        <button className="btn btn-accent" style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: ".4rem" }} onClick={onCta}>
          {ctaLabel}
        </button>
      </div>
    </div>
  );
}

function ProServiceLeadCard({ user, siret, onOpenPricing, service }) {
  const [sub, setSub] = useState(undefined); // undefined = chargement, null = aucun, objet = actif
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet("/api/billing/current");
        if (!cancelled) setSub(data && data.subscription ? data.subscription : null);
      } catch (e) { if (!cancelled) setSub(null); }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const isPro = !!sub; // Pro Solo OU Pro Multi-Agent = abonnement actif
  const planLabel = sub ? (sub.amount_cents >= 70000 ? "Pro Multi-Agent" : "Pro Solo") : "";

  const requestQuote = () => {
    const subject = service.quoteSubject;
    const body = encodeURIComponent(
      "Bonjour l'équipe VehicleIQ,\n\n" +
      service.quoteIntro + "\n\n" +
      `Compte : ${user?.email || "—"}\n` +
      `Offre actuelle : ${planLabel || "—"}\n` +
      `SIRET : ${siret || "—"}\n\n` +
      service.quoteFields + "\n\n" +
      "Cordialement"
    );
    window.dispatchEvent(new CustomEvent("viq-open-contact", { detail: { mailto: "mailto:contact@vehicleiq.fr?subject=" + encodeURIComponent(subject) + "&body=" + body } }));
  };

  const wrap = {
    background: "var(--card)", border: "1.5px solid var(--border)",
    borderRadius: 14, padding: "1.3rem 1.5rem", marginBottom: "1.4rem",
    position: "relative", overflow: "hidden",
  };

  return (
    <div style={wrap}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 80% at 90% 10%, rgba(168,128,58,.08), transparent 60%)", pointerEvents: "none" }} />
      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap", marginBottom: ".5rem" }}>
          <span style={{ fontSize: "1.3rem" }}>{service.icon}</span>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1.1rem", fontWeight: 800, color: "var(--text)" }}>
            {service.title}
          </div>
          <span style={{ fontSize: ".58rem", fontWeight: 700, color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", padding: ".12rem .5rem", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".5px" }}>
            Nouveau · sur devis
          </span>
        </div>

        <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.55, marginBottom: "1rem", maxWidth: 640 }}>
          {service.desc}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginBottom: "1.1rem" }}>
          {service.features.map(f => (
            <span key={f} style={{ fontSize: ".7rem", color: "var(--sub)", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 20, padding: ".25rem .7rem" }}>{f}</span>
          ))}
        </div>

        {sub === undefined ? (
          <div style={{ fontSize: ".78rem", color: "var(--muted)" }}>Chargement…</div>
        ) : isPro ? (
          <div>
            <div style={{ fontSize: ".72rem", color: "var(--green)", fontWeight: 700, marginBottom: ".7rem" }}>
              ✓ Inclus dans votre offre {planLabel} — activation sur simple demande, sans coût supplémentaire.
            </div>
            <button className="btn btn-accent" style={{ alignSelf: "flex-start" }} onClick={requestQuote}>
              ✉ Demander l'activation : contact@vehicleiq.fr
            </button>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: ".74rem", color: "var(--sub)", marginBottom: ".7rem", padding: ".6rem .8rem", background: "var(--bg)", border: "1px dashed var(--border-strong)", borderRadius: 8 }}>
              🔒 {service.lockedName} est réservé aux comptes <strong>Pro Solo</strong> et <strong>Pro Multi-Agent</strong>.
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => onOpenPricing && onOpenPricing()}>
              Voir les offres Pro →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Définitions des services Pro (inclus dans les forfaits Pro, activation sur demande).
const PRO_SERVICE_PHOTO = {
  icon: "📸",
  title: "Studio Photo IA",
  lockedName: "Le Studio Photo IA",
  desc: "Transformez vos photos de véhicules prises au smartphone en visuels qualité studio : détourage automatique, fond studio ou showroom réaliste, rendu homogène pour vos annonces. Vendez plus vite avec des images premium.",
  features: ["✂️ Détourage automatique", "🏛️ Showroom réaliste", "💡 Optimisation lumière", "🔁 Rendu homogène"],
  quoteSubject: "Activation — Studio Photo IA VehicleIQ",
  quoteIntro: "Je suis client Pro et je souhaite activer le service Studio Photo IA (inclus dans mon forfait, photographie IA et optimisation d'images de véhicules).",
  quoteFields: "Volume de véhicules à traiter / mois : ____\nType de rendu souhaité : (fond studio blanc / showroom réaliste / les deux)\nPlateformes de diffusion : (LeBonCoin / La Centrale / site / autres)",
};
const PRO_SERVICE_MULTIDIFF = {
  icon: "📡",
  title: "Diffusion automatique Multiplateformes",
  lockedName: "La Diffusion automatique Multiplateformes",
  desc: "Publiez automatiquement votre stock de véhicules sur les principaux portails (La Centrale, LeBonCoin, AutoScout24, Google, réseaux sociaux…) en une seule saisie. Mise à jour en temps réel, règles de diffusion personnalisées, et suivi des performances par portail.",
  features: ["🌐 100+ portails & médias", "⌨️ Saisie unique", "🔄 Mise à jour temps réel", "🎯 Règles de diffusion", "📊 Stats par portail"],
  quoteSubject: "Activation — Diffusion automatique Multiplateformes VehicleIQ",
  quoteIntro: "Je suis client Pro et je souhaite activer le service de diffusion automatique de mes annonces (inclus dans mon forfait).",
  quoteFields: "Nombre de véhicules en stock : ____\nPortails souhaités : (La Centrale / LeBonCoin / AutoScout24 / réseaux sociaux / autres)\nLogiciel/DMS actuel : ____\nFréquence de mise à jour du stock : ____",
};

// Conserve l'ancien composant comme alias (rétro-compatibilité).
function PhotoStudioLeadCard({ user, siret, onOpenPricing }) {
  return <ProServiceLeadCard user={user} siret={siret} onOpenPricing={onOpenPricing} service={PRO_SERVICE_PHOTO} />;
}


// ─── ProDashboardModal — Tableau de bord pro (SIRET requis) ──────────────────
// ─── DocumentsPanel — reconsulter les mandats & cessions créés ───────────────
// Badge logo de plateforme — pastille colorée aux couleurs de marque (SVG inline,
// pas de fichier externe). Couvre les principales plateformes auto FR.
function PlatformLogo({ name, size = 22 }) {
  const n = (name || "").toLowerCase();
  let bg = "#8E939E", label = (name || "?").slice(0, 2).toUpperCase();
  if (n.includes("leboncoin") || n.includes("bon coin")) { bg = "#FF6E14"; label = "lbc"; }
  else if (n.includes("centrale")) { bg = "#E2001A"; label = "LC"; }
  else if (n.includes("facebook") || n.includes("marketplace") || n.includes("meta")) { bg = "#1877F2"; label = "f"; }
  else if (n.includes("autoscout")) { bg = "#0B5FFF"; label = "AS"; }
  else if (n.includes("paruvendu")) { bg = "#0F8A3C"; label = "PV"; }
  else if (n.includes("argus")) { bg = "#1E2A4A"; label = "Ar"; }
  else if (n.includes("ouicar") || n.includes("oui")) { bg = "#00A39C"; label = "Oui"; }
  else if (n.includes("autotrader")) { bg = "#E4002B"; label = "AT"; }
  else if (n.includes("largus") || n.includes("l'argus")) { bg = "#1E2A4A"; label = "Ar"; }
  else if (n.includes("instagram")) { bg = "#C13584"; label = "ig"; }
  else if (n.includes("google")) { bg = "#4285F4"; label = "G"; }
  else if (n.includes("site") || n.includes("vitrine")) { bg = "#A8803A"; label = "🌐"; }
  else if (n.includes("non renseign")) { bg = "#B8BCC4"; label = "—"; }
  const fs = label.length > 2 ? size * 0.34 : size * 0.42;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: 6, background: bg, color: "#fff",
      fontWeight: 800, fontSize: fs, fontFamily: "var(--font-body)", flexShrink: 0,
      letterSpacing: "-.02em", lineHeight: 1,
    }}>{label}</span>
  );
}

// ─── Calculateur carte grise + malus écologique (France 2026) ────────────────
// Barèmes de référence — à réactualiser chaque année (loi de finances).
const CG_CV_REGION = {
  // v7 : option "France entière" — moyenne des 13 régions métropolitaines,
  // pour un scan/estimation qui n'est pas encore rattaché à une région précise.
  "France entière": 53,
  "Auvergne-Rhône-Alpes": 43, "Bourgogne-Franche-Comté": 51, "Bretagne": 55,
  "Centre-Val de Loire": 55, "Corse": 53, "Grand Est": 60, "Hauts-de-France": 36.5,
  "Île-de-France": 54.95, "Normandie": 60, "Nouvelle-Aquitaine": 53, "Occitanie": 51,
  "Pays de la Loire": 55, "Provence-Alpes-Côte d'Azur": 60,
  "Guadeloupe": 50, "Martinique": 51, "Guyane": 42.5, "La Réunion": 56, "Mayotte": 30,
};
const CG_IDF_MAJO = 14; // €/CV — majoration forfaitaire IDFM 2026
// Points d'ancrage officiels (service-public, Argus, lois de finances) reliés par
// interpolation linéaire par morceaux. Chaque entrée : [g/km, montant €].
// En deçà du 1er point → 0 ; au-delà du dernier → plafond de l'année.
const CG_CO2_ANCHORS = {
  2026: [[108,50],[120,260],[130,983],[140,2049],[150,3784],[160,6724],[170,11003],[180,19790],[185,29243],[190,50250],[192,80000]],
  2025: [[113,50],[120,353],[130,983],[136,1074],[140,2049],[150,3784],[154,4026],[160,6724],[170,11003],[180,19790],[190,50000],[193,70000]],
  2024: [[118,50],[125,140],[130,330],[140,1172],[150,2544],[160,4806],[170,8770],[180,16270],[190,40000],[194,60000]],
  2023: [[123,50],[130,178],[140,983],[150,2386],[160,4279],[170,7019],[180,12012],[190,29243],[200,45000],[226,50000]],
  2022: [[128,50],[135,158],[145,933],[155,2294],[165,4279],[175,7741],[185,15506],[195,30000],[210,40000],[224,40000]],
  2021: [[133,50],[140,190],[150,983],[160,2308],[170,4279],[180,8211],[190,17490],[200,27000],[218,30000]],
};
function cgDecoteImport(mois) {
  const m = Math.max(0, mois);
  if (m < 1) return 0;
  if (m >= 181) return 1;
  const jalons = [[1,.03],[6,.08],[12,.10],[24,.19],[36,.28],[48,.33],[60,.40],[72,.46],[96,.55],[120,.64],[156,.85],[180,.98],[181,1]];
  let prev = [0, 0];
  for (const j of jalons) {
    if (m <= j[0]) { const [pm,pc]=prev,[cm,cc]=j; return pc + (cc-pc)*((m-pm)/((cm-pm)||1)); }
    prev = j;
  }
  return 1;
}
function cgMalusCO2(co2, annee) {
  let yr = annee;
  if (yr >= 2026) yr = 2026; else if (yr < 2021) yr = 2021;
  const A = CG_CO2_ANCHORS[yr] || CG_CO2_ANCHORS[2026];
  if (!co2 || co2 < A[0][0]) return 0;
  const last = A[A.length - 1];
  if (co2 >= last[0]) return last[1];
  for (let i = 1; i < A.length; i++) {
    if (co2 <= A[i][0]) {
      const [g0, m0] = A[i - 1], [g1, m1] = A[i];
      return Math.round((m0 + (m1 - m0) * ((co2 - g0) / (g1 - g0))) / 10) * 10;
    }
  }
  return last[1];
}
function cgMalusPoids(poids, annee, abattement = 0) {
  if (!poids || annee < 2024) return 0;
  const seuil = annee >= 2026 ? 1500 : 1600;
  const p = poids - abattement;
  if (p <= seuil) return 0;
  const tranches = annee >= 2026
    ? [[1599,10],[1699,15],[1799,20],[1899,25],[1999,30],[Infinity,30]]
    : [[1799,10],[1899,15],[1999,20],[2099,25],[Infinity,30]];
  let base = seuil, total = 0;
  for (const [borne, tarif] of tranches) {
    const larg = Math.min(p, borne) - base;
    if (larg > 0) { total += larg * tarif; base += larg; }
    if (p <= borne) break;
  }
  return Math.round(total);
}
function cgCalcul({ region, cv, co2, poids, energie, dateImmat, isImport, abattementPoids = 0 }) {
  const now = new Date();
  const d = dateImmat ? new Date(dateImmat) : now;
  const mois = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  const annee = d.getFullYear();
  const plus10 = mois >= 120, plus15 = mois >= 181;
  let tarifCV = CG_CV_REGION[region] ?? 50;
  let taxeReg = (cv || 0) * tarifCV;
  let majo = region === "Île-de-France" ? CG_IDF_MAJO * (cv || 0) : 0;
  if (plus10) { taxeReg /= 2; majo /= 2; }
  taxeReg = Math.round(taxeReg + majo);
  const propre = energie === "Électrique" || energie === "Hydrogène";
  let mCO2 = 0, mPoids = 0, dec = 0;
  if (isImport && !plus15 && !propre) {
    dec = cgDecoteImport(mois);
    let ab = 0;
    if (energie === "Hybride rechargeable") ab = 200;
    else if (energie === "Hybride") ab = 100;
    mCO2 = Math.round(cgMalusCO2(co2, annee) * (1 - dec));
    mPoids = Math.round(cgMalusPoids(poids, annee, ab + abattementPoids) * (1 - dec));
  }
  const plafond = annee >= 2026 ? 80000 : 70000;
  const malusTotal = Math.min(mCO2 + mPoids, plafond);
  const fixes = 13.76;
  const total = Math.round((taxeReg + malusTotal + fixes) * 100) / 100;
  return { taxeReg, mCO2, mPoids, malusTotal, fixes, total, mois, annee, plus10, plus15, dec, tarifCV };
}

// ─── Scan d'opportunité : calcul de marge nette à l'achat ─────────────────────
// Le pro saisit un véhicule à acheter (modèle, année, km, motorisation, prix
// demandé, transport). VehicleIQ estime la COTE DE REVENTE France (moteur VIQ.AI®)
// et calcule le COÛT D'IMMATRICULATION/MALUS (cgCalcul réutilisé), puis affiche
// la MARGE NETTE = revente − (prix achat + malus + transport). Pas de scraping :
// c'est un vérificateur de marge à la demande, pas un scanner continu.
function ScanOpportunite({ isUnlimitedPlan }) {
  const [f, setF] = useState({
    makeModel: "", year: "", km: "", fuel: "Essence", gearbox: "Manuelle", state: "Bon",
    prixAchat: "", transport: "", co2: "", poids: "", ch: "", region: "Hauts-de-France",
    isImport: true, paysAchat: "Allemagne", dateImmat: "",
  });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const lbl = { fontSize: ".7rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem", display: "block" };
  const fld = { width: "100%", padding: ".55rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".88rem", outline: "none", boxSizing: "border-box" };
  const g2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".7rem", marginBottom: ".7rem" };
  const g3 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: ".7rem", marginBottom: ".7rem" };
  const eur = (v) => (v || v === 0) ? Math.round(Number(v)).toLocaleString("fr-FR") + " €" : "—";

  const scan = async () => {
    setErr(null);
    if (!f.makeModel.trim()) { setErr("Indiquez la marque et le modèle du véhicule à acheter."); return; }
    if (!f.prixAchat || Number(f.prixAchat) <= 0) { setErr("Indiquez le prix d'achat demandé."); return; }
    setBusy(true); setRes(null);
    try {
      // 1) Cote de REVENTE France via le moteur VIQ.AI® (même endpoint que l'estimation)
      const ans = {
        make: f.makeModel.trim(),
        year_sell: f.year || "2020",
        km_sell: f.km || "80000",
        fuel_sell: f.fuel,
        gearbox_sell: f.gearbox,
        state: f.state,
        ct: "Oui",
        urgency: "Normale",
        seller_type: "Professionnel",
        city_sell: "France",
      };
      const raw = await apiPost("/api/annonce/generate", ans);
      const resp = (raw?.success && raw?.data) ? raw.data : raw;
      const reventeMid = resp?.argus_mid || resp?.prix_viq || resp?.argus_low || 0;
      const reventeLow = resp?.argus_low || reventeMid;
      const reventeHigh = resp?.argus_high || reventeMid;
      if (!reventeMid) { setErr("La cote de revente n'a pas pu être calculée. Précisez le modèle exact."); setBusy(false); return; }

      // 2) Coût d'immatriculation / MALUS via cgCalcul (réutilisé tel quel)
      const cv = f.ch ? Math.max(1, Math.round((Number(f.co2) || 0) / 45 + Math.pow((Number(f.ch) * 0.7355) / 40, 1.6))) : 0;
      const cg = cgCalcul({
        region: f.region, cv, co2: Number(f.co2) || 0, poids: Number(f.poids) || 0,
        energie: f.fuel, dateImmat: f.dateImmat, isImport: f.isImport,
      });

      // 3) Marge nette = revente − (prix achat + coût immat + transport)
      const prixAchat = Number(f.prixAchat) || 0;
      const transport = Number(f.transport) || 0;
      const coutImmat = cg.total || 0;
      const coutTotal = prixAchat + transport + coutImmat;
      const margeMid = Math.round(reventeMid - coutTotal);
      const margeLow = Math.round(reventeLow - coutTotal);
      const margeHigh = Math.round(reventeHigh - coutTotal);
      const margePct = reventeMid ? Math.round((margeMid / reventeMid) * 100) : 0;

      setRes({
        reventeLow, reventeMid, reventeHigh, prixAchat, transport, coutImmat,
        malusCO2: cg.mCO2, malusPoids: cg.mPoids, taxeReg: cg.taxeReg, decote: cg.dec,
        coutTotal, margeMid, margeLow, margeHigh, margePct,
        verdict: margeMid >= 3000 ? "bonne" : margeMid >= 1000 ? "moyenne" : margeMid >= 0 ? "faible" : "negative",
      });
    } catch (e) {
      if (isPaymentRequiredError(e)) { handlePaymentRequired(e); setErr("Paiement requis pour lancer un scan (compté comme une cote)."); }
      else setErr(e?.message || "Le scan a échoué. Réessayez.");
    } finally {
      setBusy(false);
    }
  };

  const verdictStyle = {
    bonne:    { color: "var(--green)",  bg: "rgba(46,111,92,.1)",  label: "🟢 Bonne affaire" },
    moyenne:  { color: "var(--accent)", bg: "rgba(168,128,58,.1)", label: "🟡 Marge correcte" },
    faible:   { color: "var(--accent)", bg: "rgba(168,128,58,.08)",label: "🟠 Marge faible" },
    negative: { color: "var(--red)",    bg: "rgba(176,90,58,.1)",  label: "🔴 Opération à perte" },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div className="saas-dash-content-header">
        <h2 className="saas-dash-content-title">🎯 Scan d'opportunité</h2>
        <div className="saas-dash-content-sub">
          Calculez la marge nette d'un achat : VehicleIQ estime la revente France, déduit le coût d'immatriculation (malus inclus) et le transport, et vous dit si l'affaire est rentable.
        </div>
      </div>

      <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.2rem" }}>
        <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".7rem" }}>Véhicule à acheter</div>
        <div style={{ marginBottom: ".7rem" }}>
          <label style={lbl}>Marque & modèle exact</label>
          <input style={fld} value={f.makeModel} onChange={e => set("makeModel", e.target.value)} placeholder="Ex : Mercedes Classe A 200 AMG Line" />
        </div>
        <div style={g3}>
          <div><label style={lbl}>Année</label><input style={fld} value={f.year} onChange={e => set("year", e.target.value.replace(/\D/g, ""))} placeholder="2022" /></div>
          <div><label style={lbl}>Kilométrage</label><input style={fld} value={f.km} onChange={e => set("km", e.target.value.replace(/\D/g, ""))} placeholder="45000" /></div>
          <div><label style={lbl}>Énergie</label>
            <select style={fld} value={f.fuel} onChange={e => set("fuel", e.target.value)}>
              {["Essence", "Diesel", "Hybride", "Hybride rechargeable", "Électrique", "Hydrogène"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <div style={g3}>
          <div><label style={lbl}>Boîte</label>
            <select style={fld} value={f.gearbox} onChange={e => set("gearbox", e.target.value)}>
              {["Manuelle", "Automatique"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div><label style={lbl}>État</label>
            <select style={fld} value={f.state} onChange={e => set("state", e.target.value)}>
              {["Très bon", "Bon", "Moyen", "À rénover"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Région d'immat.</label>
            <select style={fld} value={f.region} onChange={e => set("region", e.target.value)}>
              {Object.keys(CG_CV_REGION).map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", margin: ".4rem 0 .7rem" }}>Coûts d'acquisition</div>
        <div style={g3}>
          <div><label style={lbl}>Prix d'achat demandé</label><input style={fld} value={f.prixAchat} onChange={e => set("prixAchat", e.target.value.replace(/\D/g, ""))} placeholder="25490" /></div>
          <div><label style={lbl}>Transport (€)</label><input style={fld} value={f.transport} onChange={e => set("transport", e.target.value.replace(/\D/g, ""))} placeholder="990" /></div>
          <div><label style={lbl}>Import UE ?</label>
            <select style={fld} value={f.isImport ? "oui" : "non"} onChange={e => set("isImport", e.target.value === "oui")}>
              <option value="oui">Oui (import)</option><option value="non">Non (France)</option>
            </select>
          </div>
        </div>
        <details style={{ marginBottom: ".7rem" }}>
          <summary style={{ fontSize: ".74rem", color: "var(--sub)", cursor: "pointer", fontWeight: 600 }}>Données pour le malus (CO₂, poids, puissance) — optionnel mais recommandé</summary>
          <div style={{ ...g3, marginTop: ".6rem" }}>
            <div><label style={lbl}>CO₂ (g/km WLTP)</label><input style={fld} value={f.co2} onChange={e => set("co2", e.target.value.replace(/\D/g, ""))} placeholder="142" /></div>
            <div><label style={lbl}>Poids (kg)</label><input style={fld} value={f.poids} onChange={e => set("poids", e.target.value.replace(/\D/g, ""))} placeholder="1480" /></div>
            <div><label style={lbl}>Puissance (ch)</label><input style={fld} value={f.ch} onChange={e => set("ch", e.target.value.replace(/\D/g, ""))} placeholder="163" /></div>
          </div>
          <div><label style={lbl}>1ère mise en circulation (pour décote malus import)</label><input style={fld} type="date" value={f.dateImmat} onChange={e => set("dateImmat", e.target.value)} /></div>
        </details>

        <button className="btn btn-accent" disabled={busy} onClick={scan}
          style={{ width: "100%", padding: ".75rem", fontSize: ".92rem", fontWeight: 700 }}>
          {busy ? "Analyse en cours…" : "🎯 Scanner l'opportunité"}
        </button>
        {err && <div style={{ marginTop: ".6rem", fontSize: ".8rem", color: "var(--red)", fontWeight: 600 }}>⚠ {err}</div>}
        <div style={{ fontSize: ".64rem", color: "var(--muted)", marginTop: ".5rem", textAlign: "center" }}>
          {isUnlimitedPlan ? "Inclus dans votre abonnement Pro (illimité)." : "Compté comme une cote (4,99 € si vos essais offerts sont épuisés)."}
        </div>
      </div>

      {res && (
        <div style={{ background: "var(--white)", border: "2px solid " + verdictStyle[res.verdict].color, borderRadius: 14, padding: "1.3rem", animation: "slideUp .4s ease" }}>
          <div style={{ display: "inline-block", fontSize: ".82rem", fontWeight: 800, color: verdictStyle[res.verdict].color, background: verdictStyle[res.verdict].bg, borderRadius: 20, padding: ".3rem .9rem", marginBottom: "1rem" }}>
            {verdictStyle[res.verdict].label}
          </div>
          <div style={{ textAlign: "center", marginBottom: "1.2rem" }}>
            <div style={{ fontSize: ".68rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontWeight: 700 }}>Marge nette estimée</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "2.8rem", fontWeight: 700, color: verdictStyle[res.verdict].color, lineHeight: 1.1 }}>
              {res.margeMid >= 0 ? "+" : ""}{eur(res.margeMid)}
            </div>
            <div style={{ fontSize: ".82rem", color: "var(--sub)" }}>
              soit {res.margePct >= 0 ? "+" : ""}{res.margePct}% · fourchette {eur(res.margeLow)} → {eur(res.margeHigh)}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", fontSize: ".85rem" }}>
            {[
              ["Prix d'achat demandé", "− " + eur(res.prixAchat), "var(--text)"],
              ["Transport", "− " + eur(res.transport), "var(--text)"],
              ["Coût d'immatriculation (dont malus)", "− " + eur(res.coutImmat), "var(--text)"],
              ["= Coût de revient total", eur(res.coutTotal), "var(--sub)"],
              ["Cote de revente France (VIQ.AI®)", "+ " + eur(res.reventeMid), "var(--green)"],
            ].map((row, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: ".35rem 0", borderBottom: i < 4 ? "1px solid var(--border)" : "none", fontWeight: i === 3 || i === 4 ? 700 : 400 }}>
                <span style={{ color: "var(--sub)" }}>{row[0]}</span>
                <span style={{ color: row[2], fontWeight: 700 }}>{row[1]}</span>
              </div>
            ))}
          </div>
          {res.coutImmat > 0 && (res.malusCO2 > 0 || res.malusPoids > 0) && (
            <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: ".7rem", lineHeight: 1.5 }}>
              Détail immatriculation : taxe régionale {eur(res.taxeReg)}, malus CO₂ {eur(res.malusCO2)}, malus poids {eur(res.malusPoids)}{res.decote > 0 ? ` (décote import −${Math.round(res.decote * 100)}%)` : ""}.
            </div>
          )}
          <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".8rem", fontStyle: "italic" }}>
            Cote indicative VIQ.AI® — la revente réelle dépend de l'état précis, des options et du marché local au moment de la vente.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── v76/v77 — Statuts de pipeline CRM (colonne Actions), partagés entre les
// répertoires (prospects externes + Mes leads) — une couleur DISTINCTE par
// statut (7 au total), pour les différencier au premier coup d'œil.
const PIPELINE_STATUS_META = {
  a_contacter:     { label: "À contacter",      color: "#d97706", bg: "rgba(217,119,6,.1)",   group: "a_realiser" },
  a_relancer:      { label: "À relancer",       color: "#dc2626", bg: "rgba(220,38,38,.1)",   group: "a_realiser" },
  devis_a_envoyer: { label: "Devis à envoyer",  color: "#9333ea", bg: "rgba(147,51,234,.1)",  group: "a_realiser" },
  contacte:        { label: "Contacté",         color: "#0d9488", bg: "rgba(13,148,136,.1)",  group: "realise" },
  rdv_pris:        { label: "RDV pris",         color: "#2563eb", bg: "rgba(37,99,235,.1)",   group: "realise" },
  devis_envoye:    { label: "Devis envoyé",     color: "#db2777", bg: "rgba(219,39,119,.1)",  group: "realise" },
  devis_paye:      { label: "Devis payé",       color: "#16a34a", bg: "rgba(22,163,74,.1)",   group: "realise" },
};
// Composant partagé — même sélecteur coloré dans les deux répertoires.
function PipelineStatusSelect({ value, onChange }) {
  const meta = PIPELINE_STATUS_META[value] || PIPELINE_STATUS_META.a_contacter;
  return (
    <select value={value || "a_contacter"} onChange={e => onChange(e.target.value)}
      style={{ fontSize: ".7rem", fontWeight: 700, padding: ".25rem .5rem", borderRadius: 6, border: "1px solid " + meta.color, background: meta.bg, color: meta.color, cursor: "pointer" }}>
      <optgroup label="À réaliser">
        <option value="a_contacter">À contacter</option>
        <option value="a_relancer">À relancer</option>
        <option value="devis_a_envoyer">Devis à envoyer</option>
      </optgroup>
      <optgroup label="Réalisé">
        <option value="contacte">Contacté</option>
        <option value="rdv_pris">RDV pris</option>
        <option value="devis_envoye">Devis envoyé</option>
        <option value="devis_paye">Devis payé</option>
      </optgroup>
    </select>
  );
}

// ─── v43 — Catégories pour l'agent de prospection (miroir de la liste serveur) ──
const PROSPECTION_CATEGORIES = [
  "Marchand automobile", "Garage automobile", "Négoce automobile", "Import automobile",
  "Mandataire automobile", "Préparateur esthétique automobile", "Detailing automobile",
  "Photographe automobile", "Convoyeur automobile", "Transport automobile",
  "Logiciel automobile", "DMS automobile", "CRM automobile", "Cabinet comptable automobile",
];

// ─── v46 — Prospection client (répertoire partagé + email/WhatsApp) ───────
// Table filtrable/triable d'un répertoire déjà peuplé par le cron mensuel
// (aucune recherche en direct, aucune attente). Zone d'envoi : sélection de
// véhicule(s) depuis les Dossiers VIQ (PDF généré et joint pour l'email),
// choix du canal (email ≤50/lot, WhatsApp ≤5/lot).
// v78 — Règle : éditeur enrichi pour le corps du message de prospection
// (gras, italique, taille de police, puces, séparateur, image) — remplace
// le simple textarea, sans toucher au reste de la structure de la campagne
// (objet, canal, destinataires, envoi restent inchangés). contentEditable +
// execCommand : toujours largement supporté pour ce niveau de mise en forme
// simple, et évite d'ajouter une dépendance d'éditeur WYSIWYG complète.
function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  const lastEmitted = useRef(null); // dernière valeur émise PAR ce composant lui-même

  useEffect(() => {
    if (!ref.current) return;
    // Ne resynchronise que si le changement vient de L'EXTÉRIEUR (ex. le
    // modèle de message reconstruit à la sélection d'un véhicule) — jamais
    // en réaction à la propre frappe de l'utilisateur, sous peine de faire
    // sauter la position du curseur à chaque caractère.
    if (value !== lastEmitted.current) {
      ref.current.innerHTML = value || "";
      lastEmitted.current = value;
    }
  }, [value]);

  const emit = () => {
    const html = ref.current?.innerHTML || "";
    lastEmitted.current = html;
    onChange(html);
  };

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const btnStyle = { fontSize: ".76rem", fontWeight: 700, padding: ".3rem .55rem", borderRadius: 6, border: "1px solid var(--border)", background: "var(--card)", color: "var(--sub)", cursor: "pointer" };

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: ".35rem", marginBottom: ".4rem", padding: ".4rem", background: "var(--bg)", border: "1px solid var(--border)", borderBottom: "none", borderRadius: "8px 8px 0 0" }}>
        <button type="button" title="Gras" style={{ ...btnStyle, fontWeight: 900 }} onClick={() => exec("bold")}>B</button>
        <button type="button" title="Italique" style={{ ...btnStyle, fontStyle: "italic" }} onClick={() => exec("italic")}>I</button>
        <select title="Taille de police" style={btnStyle} defaultValue="" onChange={e => { if (e.target.value) exec("fontSize", e.target.value); e.target.value = ""; }}>
          <option value="" disabled>Taille</option>
          <option value="2">Petite</option>
          <option value="3">Normale</option>
          <option value="5">Grande</option>
          <option value="7">Très grande</option>
        </select>
        <button type="button" title="Liste à puces" style={btnStyle} onClick={() => exec("insertUnorderedList")}>• Liste</button>
        <button type="button" title="Insérer un séparateur" style={btnStyle} onClick={() => exec("insertHorizontalRule")}>— Trait</button>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        style={{ minHeight: 220, maxHeight: 420, overflowY: "auto", padding: ".7rem .8rem", border: "1px solid var(--border)", borderRadius: "0 0 8px 8px", background: "var(--card)", color: "var(--text)", fontSize: ".88rem", lineHeight: 1.6 }}
      />
    </div>
  );
}

// Composant partagé — filtres Statut CRM / Actions, placés À L'INTÉRIEUR de
// chaque répertoire (correction v77 : ils flottaient auparavant au-dessus,
// hors de la zone concernée, et le tableau Répertoire prospects ignorait de
// toute façon le résultat filtré — voir le correctif sur audienceRows).
// v83 — Règle : agent IA de prospection téléphonique — modal de
// confirmation avant de lancer un appel réel (coût + conformité), montrant
// explicitement la phrase d'ouverture obligatoire de l'IA.
function VoiceCallModal({ contact, targetType = "directory", onClose, onCallLaunched }) {
  const [calling, setCalling] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [scheduled, setScheduled] = useState(false);
  // v85 — Règle : programmation (date/heure), durée max et objectif
  // principal propres à cet appel.
  const [scheduledAt, setScheduledAt] = useState("");
  const [maxDuration, setMaxDuration] = useState(""); // vide = repli sur le réglage global de l'agent
  const [objective, setObjective] = useState("");
  const launch = async () => {
    setCalling(true); setError(null);
    try {
      // v93 — Règle : l'agent vocal est aussi disponible pour "Mes leads" —
      // targetType distingue directory/lead ; l'id affiché ("lead-N") est
      // dépréfixé avant l'envoi, comme pour setPipelineStatus.
      const rawId = targetType === "lead" ? String(contact.id).replace(/^lead-/, "") : contact.id;
      const r = await apiPost("/api/voice/call", {
        target_id: rawId, target_type: targetType, phone: contact.phone, contact_name: contact.name,
        scheduled_at: scheduledAt || undefined,
        max_duration_override: maxDuration ? parseInt(maxDuration) : undefined,
        objective: objective || undefined,
      });
      if (r?.success) { if (r.scheduled) setScheduled(true); else setDone(true); onCallLaunched && onCallLaunched(); }
      else setError(r?.error || "Appel impossible.");
    } catch (e) { setError(e?.message || "Appel impossible."); }
    finally { setCalling(false); }
  };
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 460 }}>
        <div className="panel-header">
          <div className="panel-title">📞 Appeler {contact.name}</div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: "1.2rem", display: "flex", flexDirection: "column", gap: ".8rem" }}>
          <div style={{ fontSize: ".82rem", color: "var(--sub)" }}>Numéro : <strong style={{ fontFamily: "var(--font-mono)" }}>{contact.phone}</strong></div>
          <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: ".8rem", fontSize: ".8rem", color: "var(--text)", fontStyle: "italic" }}>
            « Bonjour, je suis un assistant vocal intelligence artificielle de VehicleIQ (...) Cet appel est enregistré à des fins de qualité. Avez-vous un instant ? »
          </div>
          <div style={{ fontSize: ".74rem", color: "var(--muted)" }}>
            L'IA s'annonce comme telle dès le début de l'appel (obligation légale), présente VehicleIQ brièvement et propose un essai gratuit. Elle respecte immédiatement toute demande de ne plus être rappelé(e).
          </div>
          {/* v85 — Règle : programmation + durée max + objectif principal de l'appel. */}
          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: ".78rem", color: "var(--sub)", display: "block", marginBottom: ".3rem" }}>Programmer l'appel (optionnel)</label>
            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
              min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
              style={{ width: "100%", fontSize: ".82rem", padding: ".45rem .6rem", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)", boxSizing: "border-box" }} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: ".78rem", color: "var(--sub)", display: "block", marginBottom: ".3rem" }}>Durée maximale (secondes) — vide = réglage par défaut</label>
            <input type="number" min={30} max={600} value={maxDuration} onChange={e => setMaxDuration(e.target.value)} placeholder="ex. 180"
              style={{ width: "100%", fontSize: ".82rem", padding: ".45rem .6rem", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)", boxSizing: "border-box" }} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label style={{ fontSize: ".78rem", color: "var(--sub)", display: "block", marginBottom: ".3rem" }}>Objectif principal de l'appel (optionnel)</label>
            <textarea value={objective} onChange={e => setObjective(e.target.value)} placeholder="ex. Proposer un essai gratuit et obtenir un rendez-vous de démonstration"
              style={{ width: "100%", minHeight: 60, fontSize: ".82rem", padding: ".45rem .6rem", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)", boxSizing: "border-box", resize: "vertical" }} />
          </div>
          {error && <div style={{ fontSize: ".8rem", color: "var(--red, #c0392b)" }}>⚠ {error}</div>}
          {done ? (
            <div style={{ fontSize: ".84rem", color: "var(--green)", fontWeight: 600 }}>✓ Appel lancé — retrouvez-le dans l'historique des appels.</div>
          ) : scheduled ? (
            <div style={{ fontSize: ".84rem", color: "var(--green)", fontWeight: 600 }}>✓ Appel programmé — retrouvez-le dans l'historique des appels.</div>
          ) : (
            <button className="btn btn-accent" disabled={calling} onClick={launch} style={{ justifyContent: "center" }}>
              {calling ? "Lancement…" : scheduledAt ? "Programmer l'appel →" : "Lancer l'appel →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CrmActionFilters({ crmStatus, onCrmStatus, pipeline, onPipeline }) {
  return (
    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: ".9rem" }}>
      <label style={{ display: "flex", alignItems: "center", gap: ".4rem", fontSize: ".8rem", color: "var(--sub)" }}>
        Statut CRM :
        <select value={crmStatus} onChange={e => onCrmStatus(e.target.value)}
          style={{ fontSize: ".8rem", padding: ".3rem .5rem", borderRadius: 6, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)" }}>
          <option value="all">Tous</option>
          <option value="contacted">Contacté</option>
          <option value="not_contacted">Jamais contacté</option>
        </select>
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: ".4rem", fontSize: ".8rem", color: "var(--sub)" }}>
        Actions :
        <select value={pipeline} onChange={e => onPipeline(e.target.value)}
          style={{ fontSize: ".8rem", padding: ".3rem .5rem", borderRadius: 6, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)" }}>
          <option value="all">Toutes</option>
          <optgroup label="À réaliser">
            <option value="a_contacter">À contacter</option>
            <option value="a_relancer">À relancer</option>
            <option value="devis_a_envoyer">Devis à envoyer</option>
          </optgroup>
          <optgroup label="Réalisé">
            <option value="contacte">Contacté</option>
            <option value="rdv_pris">RDV pris</option>
            <option value="devis_envoye">Devis envoyé</option>
            <option value="devis_paye">Devis payé</option>
          </optgroup>
        </select>
      </label>
    </div>
  );
}

function ProspectionAgent({ user, vehicleContext }) {
  // v50 — Deux publics distincts : le répertoire externe partagé (catégories
  // B2B + éventuellement le groupe VehicleIQ pour le propriétaire), et
  // désormais "Mes leads" — les demandes reçues par CE compte pro via son
  // propre formulaire client (/api/pro-leads, déjà filtré par pro_uid).
  // Chaque pro ne voit et ne contacte que SES PROPRES leads.
  const [audience, setAudience] = useState("directory"); // directory | myleads
  const [categories, setCategories] = useState([]);
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [directory, setDirectory] = useState([]);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [directorySize, setDirectorySize] = useState(0);
  const [dirLoading, setDirLoading] = useState(false);
  // v65 — Sélection GLOBALE : stocke l'objet contact COMPLET (pas juste un
  // booléen par id), pour que la sélection survive au changement d'onglet
  // (Répertoire ↔ Mes leads) ET au changement de page — avant, la sélection
  // était un simple {id: bool} recalculé contre la liste COURANTE, donc
  // invisible/perdue dès qu'on quittait la page/l'onglet où le contact avait
  // été coché. Ne JAMAIS remettre à {} au changement d'onglet.
  const [selectedContacts, setSelectedContacts] = useState({}); // id -> ligne complète
  const toggleSelect = (row) => {
    setSelectedContacts(prev => {
      const next = { ...prev };
      if (next[row.id]) delete next[row.id]; else next[row.id] = row;
      return next;
    });
  };
  // v65 — Filtres Email/Fixe/Mobile, cumulables (ET logique, pas OU) :
  // "Fixe" = numéro FR commençant par 01-05, "Mobile" = 06/07.
  const [filterEmail, setFilterEmail] = useState(false);
  const [filterFixe, setFilterFixe] = useState(false);
  const [filterMobile, setFilterMobile] = useState(false);
  const [filterImported, setFilterImported] = useState(false); // v75 — contacts importés manuellement/en masse par ce pro
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false); // v77 — menu déroulant multisélection des catégories
  const [contactTypeMenuOpen, setContactTypeMenuOpen] = useState(false); // v78 — menu déroulant multisélection Email/Fixe/Mobile/Importés
  const [filtersOpen, setFiltersOpen] = useState(false); // v78 — filtres avancés masqués par défaut
  const [filterCrmStatus, setFilterCrmStatus] = useState("all"); // v76 — all | contacted | not_contacted
  const [filterPipeline, setFilterPipeline] = useState("all"); // v76 — all | l'un des 7 statuts de pipeline
  const isFixePhone = (p) => !!p && /^(?:\+33|0)\s*[1-5]/.test(String(p).replace(/[.\-\s]/g, "").replace(/^\+33/, "0"));
  const isMobilePhone = (p) => !!p && /^(?:\+33|0)\s*[67]/.test(String(p).replace(/[.\-\s]/g, "").replace(/^\+33/, "0"));
  const applyContactFilters = (rows) => rows.filter(r => {
    if (filterEmail && !r.email) return false;
    if (filterFixe && !isFixePhone(r.phone)) return false;
    if (filterMobile && !isMobilePhone(r.phone)) return false;
    return true;
  });
  // v49 — Groupe interne "VehicleIQ" (comptes inscrits) : le SERVEUR décide
  // seul qui peut le voir (propriétaire uniquement) — jamais un flag côté
  // client. On se contente d'afficher ou non le chip de catégorie selon ce
  // que le serveur a confirmé dans sa réponse.
  const [canSeeInternal, setCanSeeInternal] = useState(false);
  const visibleCategories = [...PROSPECTION_CATEGORIES, "Import", ...(canSeeInternal ? ["VehicleIQ"] : [])];
  const PAGE_SIZE = 25;

  const toggleCategory = (c) => { setPage(1); setCategories(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]); };

  useEffect(() => {
    if (audience !== "directory") return;
    let cancelled = false;
    setDirLoading(true);
    const params = new URLSearchParams();
    if (categories.length) params.set("categories", categories.join(","));
    if (region.trim()) params.set("region", region.trim());
    if (city.trim()) params.set("city", city.trim());
    if (search.trim()) params.set("search", search.trim());
    if (filterEmail) params.set("hasEmail", "1");
    if (filterFixe) params.set("phoneType", "fixe");
    else if (filterMobile) params.set("phoneType", "mobile");
    if (filterImported) params.set("importedOnly", "1");
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));
    apiGet(`/api/prospection/directory?${params.toString()}`).then(r => {
      if (cancelled || !r?.success) return;
      setDirectory(r.prospects || []);
      setDirectoryTotal(r.total || 0);
      setLastRefreshedAt(r.last_refreshed_at);
      setDirectorySize(r.directory_size || 0);
      setCanSeeInternal(!!r.can_see_internal);
    }).finally(() => { if (!cancelled) setDirLoading(false); });
    return () => { cancelled = true; };
  }, [audience, categories, region, city, search, page, filterEmail, filterFixe, filterMobile, filterImported]);

  // v66 — Filtres changés → repartir en page 1 (comme les autres filtres),
  // sinon on reste sur une page qui peut ne plus exister pour le nouveau résultat.
  useEffect(() => { setPage(1); }, [filterEmail, filterFixe, filterMobile, filterImported]);

  // v50 — "Mes leads" : chargé une seule fois (léger, pas de filtres serveur
  // nécessaires pour un usage pro individuel), filtré/recherché côté client.
  const [myLeads, setMyLeads] = useState(null);
  const [leadSearch, setLeadSearch] = useState("");
  useEffect(() => {
    if (audience !== "myleads" || myLeads !== null) return;
    apiGet("/api/pro-leads").then(r => { if (r?.success) setMyLeads(r.leads || []); }).catch(() => setMyLeads([]));
  }, [audience, myLeads]);

  // v66 — "Mes campagnes" : historique des campagnes envoyées.
  const [campaigns, setCampaigns] = useState(null);
  useEffect(() => {
    if (audience !== "campaigns" || campaigns !== null) return;
    Promise.all([
      apiGet("/api/prospection/campaigns").catch(() => ({ campaigns: [] })),
      apiGet("/api/prospection/scheduled").catch(() => ({ scheduled: [] })),
    ]).then(([sent, sched]) => {
      const sentList = (sent?.campaigns || []).map(c => ({ ...c, _scheduled: false }));
      const schedList = (sched?.scheduled || []).filter(s => s.status === "pending").map(s => ({ ...s, _scheduled: true, status: "scheduled" }));
      setCampaigns([...schedList, ...sentList]);
    });
  }, [audience, campaigns]);
  // v83 — Règle : historique des appels de l'agent IA, même logique de
  // chargement paresseux que Mes campagnes.
  const [voiceCalls, setVoiceCalls] = useState(null);
  const [voiceConfigured, setVoiceConfigured] = useState(true);
  const [voiceReason, setVoiceReason] = useState(null);
  useEffect(() => {
    if (audience !== "calls" || voiceCalls !== null) return;
    apiGet("/api/voice/calls").then(r => { if (r?.success) { setVoiceCalls(r.calls || []); setVoiceConfigured(!!r.configured); } }).catch(() => setVoiceCalls([]));
    // v117 — Récupère la RAISON exacte du "non configuré" plutôt que d'afficher
    // un message générique impossible à diagnostiquer à distance.
    apiGet("/api/voice/config-status").then(r => { if (r?.success && !r.configured && r.reason) setVoiceReason(r.reason); }).catch(() => {});
  }, [audience, voiceCalls]);
  // v75 — Règle : relancer une campagne déjà effectuée.
  const [resendingId, setResendingId] = useState(null);
  const resendCampaign = async (id) => {
    setResendingId(id);
    try {
      const r = await apiPost(`/api/prospection/campaigns/${id}/resend`, {});
      if (r?.success) { setCampaigns(null); } // force un rechargement de la liste
      else alert(r?.error || "Relance impossible.");
    } catch (e) { alert(e?.message || "Relance impossible."); }
    finally { setResendingId(null); }
  };

  // v65 — Ne remet plus la sélection à zéro : changer d'onglet doit
  // uniquement changer la liste AFFICHÉE, jamais ce qui est sélectionné.
  const switchAudience = (a) => { setAudience(a); };

  // v60 — Import CSV/XLSX de prospects, avec correspondance de colonnes
  // CONFIRMÉE par l'utilisateur (voir IMPORT_COLUMN_ALIASES / guessImportColumnMap
  // plus haut) : avant, une correspondance automatique rigide faisait
  // disparaître silencieusement toutes les lignes dès que l'en-tête du
  // fichier ne correspondait à aucun alias connu — c'était le bug remonté
  // ("l'import de lead ne fonctionne pas").
  const IMPORT_EXPECTED_FIELDS = [
    { key: "name", label: "Nom / Société", required: true },
    { key: "email", label: "Email", required: false },
    { key: "phone", label: "Téléphone", required: false },
    { key: "city", label: "Ville", required: false },
    { key: "region", label: "Région", required: false },
    { key: "website", label: "Site web", required: false },
  ];
  const [importStage, setImportStage] = useState("upload"); // upload | mapping | preview
  const [rawRows, setRawRows] = useState(null); // lignes brutes, clés = en-têtes originaux du fichier
  const [rawHeaders, setRawHeaders] = useState([]);
  const [columnMap, setColumnMap] = useState({}); // { name: "<en-tête du fichier>", email: "...", ... }
  const [importRows, setImportRows] = useState(null); // aperçu mappé, avant envoi
  const [importScope, setImportScope] = useState("private");
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  // v64 — Ajout manuel d'un contact (Nom, Catégorie, Ville, Contact) — même
  // circuit d'import que le CSV, une seule ligne, catégorie choisie
  // explicitement au lieu du repli "Import".
  const [importMode, setImportMode] = useState("file"); // file | manual
  const [manualLead, setManualLead] = useState({ name: "", category: PROSPECTION_CATEGORIES[0], city: "", email: "", phone: "" });
  const [manualBusy, setManualBusy] = useState(false);

  const submitManualLead = async () => {
    setImportError(null);
    if (!manualLead.name.trim()) { setImportError("Le nom est requis."); return; }
    if (!manualLead.email.trim() && !manualLead.phone.trim()) { setImportError("Renseignez au moins un email ou un téléphone."); return; }
    setManualBusy(true);
    try {
      const r = await apiPost("/api/prospection/import", {
        rows: [{ name: manualLead.name, category: manualLead.category, city: manualLead.city, email: manualLead.email, phone: manualLead.phone }],
        scope: importScope,
      });
      if (r?.success) {
        setImportResult(r);
        setManualLead({ name: "", category: PROSPECTION_CATEGORIES[0], city: "", email: "", phone: "" });
      } else setImportError(r?.error || "Ajout impossible.");
    } catch (e) { setImportError(e?.message || "Ajout impossible."); }
    finally { setManualBusy(false); }
  };

  const resetImport = () => {
    setImportStage("upload"); setRawRows(null); setRawHeaders([]); setColumnMap({});
    setImportRows(null); setImportError(null); setImportResult(null);
  };

  const handleImportFile = async (file) => {
    setImportError(null); setImportResult(null); setImportRows(null);
    if (!file) return;
    try {
      let rows = [];
      if (/\.xlsx?$/i.test(file.name)) {
        const XLSX = await loadXLSX();
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(sheet, { defval: "" }).map(row => {
          const obj = {};
          Object.entries(row).forEach(([k, v]) => { obj[String(k).trim()] = String(v).trim(); });
          return obj;
        });
      } else {
        const text = await file.text();
        rows = parseCSVText(text);
      }
      if (!rows.length) { setImportError("Fichier vide ou illisible."); return; }
      const headers = Object.keys(rows[0]);
      if (!headers.length) { setImportError("Aucune colonne détectée dans ce fichier."); return; }
      setRawHeaders(headers);
      setRawRows(rows.slice(0, 500));
      setColumnMap(guessImportColumnMap(headers));
      setImportStage("mapping");
    } catch (e) { setImportError("Fichier illisible : " + (e?.message || "format non reconnu")); }
  };

  const confirmColumnMapping = () => {
    setImportError(null);
    if (!columnMap.name && !columnMap.email) { setImportError("Associez au moins la colonne Nom/Société ou Email."); return; }
    const mapped = rawRows.map(r => ({
      name: columnMap.name ? r[columnMap.name] : null,
      email: columnMap.email ? r[columnMap.email] : null,
      phone: columnMap.phone ? r[columnMap.phone] : null,
      city: columnMap.city ? r[columnMap.city] : null,
      region: columnMap.region ? r[columnMap.region] : null,
      website: columnMap.website ? r[columnMap.website] : null,
    })).filter(r => r.name || r.email);
    if (!mapped.length) { setImportError("Aucune ligne exploitable avec cette correspondance de colonnes."); return; }
    setImportRows(mapped);
    setImportStage("preview");
  };

  const runImport = async () => {
    if (!importRows || !importRows.length) return;
    setImportBusy(true); setImportError(null);
    try {
      const r = await apiPost("/api/prospection/import", { rows: importRows, scope: importScope });
      if (r?.success) { setImportResult(r); setImportRows(null); }
      else setImportError(r?.error || "Import impossible.");
    } catch (e) { setImportError(e?.message || "Import impossible."); }
    finally { setImportBusy(false); }
  };

  const leadTypeLabel = { estimation: "Estimation", achat: "Aide à l'achat", cartegrise: "Carte grise" };
  // v51 — Un même client qui soumet plusieurs demandes ne doit apparaître
  // qu'UNE fois : on regroupe par identité de contact (email, sinon
  // téléphone), on garde la ligne la plus récente pour l'affichage/l'envoi,
  // et on ajoute le nombre d'occurrences plutôt que de répéter la ligne.
  const groupedLeads = useMemo(() => {
    const groups = new Map();
    (myLeads || []).forEach(l => {
      const key = (l.client_email || l.client_phone || l.client_name || `id-${l.id}`).toLowerCase();
      const g = groups.get(key);
      if (!g || new Date(l.created_at) > new Date(g.created_at)) {
        groups.set(key, { ...l, occurrence_count: (g?.occurrence_count || 0) + 1 });
      } else {
        g.occurrence_count = (g.occurrence_count || 1) + 1;
      }
    });
    return Array.from(groups.values()).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [myLeads]);
  const filteredLeads = groupedLeads.filter(l => {
    if (!leadSearch.trim()) return true;
    const s = leadSearch.toLowerCase();
    return (l.client_name || "").toLowerCase().includes(s) || (l.client_email || "").toLowerCase().includes(s);
  });
  // Normalise les deux sources vers la même forme pour la sélection/l'envoi.
  const audienceRowsRaw = audience === "directory" ? directory : filteredLeads.map(l => ({
    id: `lead-${l.id}`, name: l.client_name || l.client_email || "Client", category: leadTypeLabel[l.type] || l.type,
    city: null, email: l.client_email, phone: l.client_phone, occurrenceCount: l.occurrence_count,
    contacted: l.contacted, last_contact_channel: l.last_contact_channel, last_contact_at: l.last_contact_at,
    pipeline_status: l.pipeline_status || "a_contacter", // v76 — colonne Actions sur Mes leads
    // v91 — Règle : statut déclaré par le client (Pro/Particulier/Autre) —
    // déjà collecté et validé, jamais affiché nulle part jusqu'ici.
    profile_type: (typeof l.data === "string" ? (() => { try { return JSON.parse(l.data)?.profile_type; } catch (e) { return null; } })() : l.data?.profile_type) || null,
  }));
  // v65 — Filtres Email/Fixe/Mobile cumulables, appliqués à la liste affichée.
  const audienceRowsFiltered = applyContactFilters(audienceRowsRaw)
    .filter(r => filterCrmStatus === "all" || (filterCrmStatus === "contacted" ? r.contacted : !r.contacted))
    .filter(r => filterPipeline === "all" || r.pipeline_status === filterPipeline || (!r.pipeline_status && filterPipeline === "a_contacter"));
  // v76 — Règle : tri cliquable sur les colonnes Nom/Catégorie/Ville/Contact/
  // Statut CRM/Actions, partagé entre les deux répertoires (tri côté client :
  // s'applique à ce qui est déjà affiché — la page courante pour le
  // répertoire externe paginé, la liste complète pour Mes leads).
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };
  const SORT_KEY = {
    name: r => (r.name || "").toLowerCase(),
    category: r => (r.category || "").toLowerCase(),
    city: r => (r.city || "").toLowerCase(),
    contact: r => (r.email || r.phone || "").toLowerCase(),
    status: r => r.contacted ? 1 : 0,
    actions: r => PIPELINE_STATUS_META[r.pipeline_status]?.label || "À contacter",
  };
  const audienceRows = sortCol ? [...audienceRowsFiltered].sort((a, b) => {
    const ka = SORT_KEY[sortCol](a), kb = SORT_KEY[sortCol](b);
    const cmp = ka < kb ? -1 : ka > kb ? 1 : 0;
    return sortDir === "asc" ? cmp : -cmp;
  }) : audienceRowsFiltered;
  const SortableTh = ({ col, children }) => (
    <th onClick={() => toggleSort(col)} style={{ padding: ".4rem .3rem", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
      {children} {sortCol === col ? (sortDir === "asc" ? "▲" : "▼") : <span style={{ opacity: .3 }}>↕</span>}
    </th>
  );

  // v53 — CRM léger : badge de statut de contact, partagé entre les deux tables.
  const crmStatus = (r) => r.contacted
    ? <span style={{ fontSize: ".74rem", color: "var(--green)", fontWeight: 600 }}>
        ✓ Contacté {r.last_contact_channel === "whatsapp" ? "💬" : "✉"} · {r.last_contact_at ? new Date(r.last_contact_at).toLocaleDateString("fr-FR") : ""}
      </span>
    : <span style={{ fontSize: ".74rem", color: "var(--muted)" }}>Jamais contacté</span>;

  // ── Sélection véhicule(s) ──
  const [dossierOptions, setDossierOptions] = useState(null);
  const [selectedVehicles, setSelectedVehicles] = useState(() => vehicleContext ? [vehicleContext] : []);
  useEffect(() => {
    if (vehicleContext) return; // déjà fourni par le Dossier VIQ, pas besoin du sélecteur
    apiGet("/api/dossiers").then(r => { if (r?.success) setDossierOptions(r.dossiers || []); }).catch(() => setDossierOptions([]));
  }, [vehicleContext]);

  const toggleVehicle = (d) => {
    setSelectedVehicles(prev => prev.some(v => v.id === d.id) ? prev.filter(v => v.id !== d.id) : [...prev, {
      id: d.id, label: `${d.make || "Véhicule"} ${d.model || ""}${d.year ? " · " + d.year : ""}`.trim(),
      price: d.latest_estimation?.prix_viq || d.latest_estimation?.argus_mid || null,
      plaque: d.plaque, latestEstimation: d.latest_estimation, latestCheck: d.latest_check,
    }]);
  };
  // v72 — Règle 2 : proposer un lot entier en un clic depuis le CRM — même
  // logique que toggleVehicle, appliquée à tous les véhicules du lot.
  const toggleLotVehicles = (members) => {
    setSelectedVehicles(prev => {
      const allIn = members.every(d => prev.some(v => v.id === d.id));
      if (allIn) return prev.filter(v => !members.some(d => d.id === v.id));
      const toAdd = members.filter(d => !prev.some(v => v.id === d.id)).map(d => ({
        id: d.id, label: `${d.make || "Véhicule"} ${d.model || ""}${d.year ? " · " + d.year : ""}`.trim(),
        price: d.latest_estimation?.prix_viq || d.latest_estimation?.argus_mid || null,
        plaque: d.plaque, latestEstimation: d.latest_estimation, latestCheck: d.latest_check,
      }));
      return [...prev, ...toAdd];
    });
  };

  // v75 — Règle : colonne "Actions" — statut de pipeline par contact.
  const setPipelineStatus = async (targetType, targetId, status) => {
    // v76 — Règle : la colonne Actions doit aussi fonctionner sur Mes leads
    // (targetId est préfixé "lead-N" côté affichage, mais myLeads stocke des
    // id numériques bruts — il faut retirer le préfixe pour l'update local).
    if (targetType === "lead") {
      const rawId = parseInt(String(targetId).replace(/^lead-/, ""), 10);
      setMyLeads(prev => prev ? prev.map(l => l.id === rawId ? { ...l, pipeline_status: status } : l) : prev);
    } else {
      setDirectory(prev => prev.map(r => r.id === targetId ? { ...r, pipeline_status: status } : r)); // optimiste
    }
    try {
      const rawTargetId = targetType === "lead" ? String(targetId).replace(/^lead-/, "") : targetId;
      const r = await apiPost("/api/prospection/pipeline-status", { target_type: targetType, target_id: rawTargetId, status });
      if (!r?.success) throw new Error(r?.error);
    } catch (e) { /* échec silencieux : l'utilisateur peut réessayer, pas bloquant */ }
  };

  // v75 — Règle : modifier un contact importé (jamais le répertoire partagé).
  const [editingContact, setEditingContact] = useState(null);
  // v83 — Règle : agent IA de prospection téléphonique.
  const [voiceCallTarget, setVoiceCallTarget] = useState(null);
  // v93 — Règle : appels simultanés sur une sélection multiple — chaque
  // appel part en parallèle (Promise.all), le plafond de simultanéité est
  // appliqué côté serveur (voir _voiceCallPreflight) : au-delà, l'appel
  // échoue proprement avec un message clair plutôt que d'être lancé quand
  // même et de saturer le budget.
  const [bulkCalling, setBulkCalling] = useState(false);
  const [bulkCallResult, setBulkCallResult] = useState(null);
  const bulkCallSelection = async () => {
    const targets = selectedRows.filter(r => r.phone);
    if (!targets.length || bulkCalling) return;
    setBulkCalling(true); setBulkCallResult(null);
    try {
      const results = await Promise.all(targets.map(r => {
        const isLead = String(r.id).startsWith("lead-");
        const rawId = isLead ? String(r.id).replace(/^lead-/, "") : r.id;
        return apiPost("/api/voice/call", { target_id: rawId, target_type: isLead ? "lead" : "directory", phone: r.phone, contact_name: r.name })
          .then(res => ({ ok: !!res?.success, error: res?.error }))
          .catch(() => ({ ok: false, error: "Erreur réseau" }));
      }));
      const launched = results.filter(r => r.ok).length;
      const failed = results.length - launched;
      setBulkCallResult(failed > 0
        ? `${launched} appel(s) lancé(s) — ${failed} n'ont pas pu être lancés (limite d'appels simultanés ou quotidienne probablement atteinte).`
        : `${launched} appel(s) lancé(s) simultanément.`);
      setVoiceCalls(null);
    } finally {
      setBulkCalling(false);
    }
  };
  const [contactDraft, setContactDraft] = useState({ name: "", category: "", city: "", email: "", phone: "" });
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState(null);
  const saveContact = async () => {
    setContactSaving(true); setContactError(null);
    try {
      const r = await apiPut(`/api/prospection/directory/${editingContact.id}`, contactDraft);
      if (r?.success) {
        setDirectory(prev => prev.map(row => row.id === editingContact.id ? { ...row, ...r.contact } : row));
        setEditingContact(null);
      } else setContactError(r?.error || "Modification impossible.");
    } catch (e) { setContactError(e?.message || "Modification impossible."); }
    finally { setContactSaving(false); }
  };

  // ── Canal + message ──
  const [channel, setChannel] = useState("email");
  const [showPreview, setShowPreview] = useState(false); // v64 — aperçu de l'email habillé
  // v74 — Les plafonds affichés (sélecteur de canal + "X/Y destinataires")
  // étaient figés en dur (10 email / 5 SMS-WhatsApp), sans lien avec les
  // valeurs réellement configurées par le Super Admin (app_settings). Ces
  // valeurs par défaut ne servent plus que de repli le temps du chargement.
  const [prospectionLimits, setProspectionLimits] = useState({ maxEmailsPerDay: 10, maxSmsPerDay: 5, maxWhatsappPerDay: 5 });
  useEffect(() => {
    apiGet("/api/prospection/limits").then(r => { if (r?.success) setProspectionLimits(r.limits); }).catch(() => {});
  }, []);
  const cap = channel === "email" ? prospectionLimits.maxEmailsPerDay : channel === "sms" ? prospectionLimits.maxSmsPerDay : prospectionLimits.maxWhatsappPerDay;
  const [subject, setSubject] = useState("Véhicule(s) disponible(s)");
  // v80 — Règle : l'import d'image en ligne dans l'éditeur ne fonctionnait
  // pas correctement — remplacé par un vrai mécanisme de pièce jointe,
  // envoyée avec l'email comme n'importe quel fichier joint (comme les PDF
  // véhicule déjà générés).
  const [userAttachments, setUserAttachments] = useState([]); // [{ filename, base64 }]
  const [attachError, setAttachError] = useState(null);
  // v85 — Règle : programmation de campagne (date/heure d'envoi) — email uniquement.
  const [scheduledAt, setScheduledAt] = useState("");
  const [scheduledResult, setScheduledResult] = useState(null);
  const addAttachment = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*,.pdf";
    input.onchange = async (e) => {
      const f = e.target.files?.[0];
      if (!f) return;
      setAttachError(null);
      try {
        let base64;
        if (f.type.startsWith("image/")) {
          base64 = await compressImageFile(f, 1600, 0.85);
        } else {
          base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(f);
          });
        }
        setUserAttachments(prev => [...prev, { filename: f.name, base64 }]);
      } catch (err) { setAttachError("Fichier invalide."); }
    };
    input.click();
  };
  const removeAttachment = (i) => setUserAttachments(prev => prev.filter((_, idx) => idx !== i));

  const [body, setBody] = useState(
    "Bonjour,<br><br>Je vous propose le(s) véhicule(s) suivant(s), actuellement disponible(s) :<br><br>[la liste se complète automatiquement selon votre sélection]<br><br>N'hésitez pas à me contacter si cela correspond à l'un de vos clients.<br><br>Cordialement,"
  );
  useEffect(() => {
    if (!selectedVehicles.length) return;
    const list = selectedVehicles.map(v => `• ${v.label}${v.price ? ` — ${Math.round(v.price).toLocaleString("fr-FR")} €` : ""}${v.plaque ? ` (${v.plaque})` : ""}`).join("<br>");
    setBody(`Bonjour,<br><br>Je vous propose le(s) véhicule(s) suivant(s), actuellement disponible(s) :<br><br>${list}<br><br>N'hésitez pas à me contacter si cela correspond à l'un de vos clients.<br><br>Cordialement,`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicles.map(v => v.id).join(",")]);

  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [channelLinks, setChannelLinks] = useState(null); // v64 — générique WhatsApp + SMS
  const [pdfDownloads, setPdfDownloads] = useState(null); // v64 — PDF à joindre manuellement (WhatsApp/SMS ne transportent pas de pièce jointe)
  const [pdfBusy, setPdfBusy] = useState(false);

  // v65 — Sélection GLOBALE : Object.values(selectedContacts) contient les
  // objets complets de TOUS les contacts cochés, Répertoire + Mes leads
  // confondus, quelle que soit la page/l'onglet où ils ont été cochés.
  const selectedRows = Object.values(selectedContacts);
  const eligible = channel === "email" ? selectedRows.filter(r => r.email) : selectedRows.filter(r => r.phone);

  const runSend = async () => {
    setSendErr(null); setSendResult(null); setChannelLinks(null); setPdfDownloads(null); setScheduledResult(null);
    if (!eligible.length) { setSendErr(`Sélectionnez au moins un destinataire avec ${channel === "email" ? "un email" : "un téléphone"}.`); return; }
    if (scheduledAt && channel !== "email") { setSendErr("La programmation n'est disponible que pour le canal Email."); return; }
    setSending(true);
    try {
      let attachments = [];
      const isDeepLinkChannel = channel === "whatsapp" || channel === "sms";
      if ((channel === "email" || isDeepLinkChannel) && selectedVehicles.length) {
        setPdfBusy(true);
        for (const v of selectedVehicles.slice(0, 5)) {
          try {
            // v51 — Garde-fou : ne jamais joindre un PDF de cote sans prix
            // réel (ex. si latestEstimation vient d'une demande de lead sans
            // cote calculée) — mieux vaut retomber sur le PDF de Contrôle,
            // ou ne rien joindre, qu'un PDF affichant "0 €" partout.
            const est = v.latestEstimation;
            const hasRealPrice = !!(est && (est.prix_viq != null || est.argus_mid != null || est.argus_low != null));
            const entry = hasRealPrice
              ? { type: "sell", results: est, data: { make: v.label } }
              : v.latestCheck ? { type: "check", results: v.latestCheck, data: { plaque: v.plaque } } : null;
            if (!entry) continue;
            const dataUri = await exportEntryToPDF(entry, { asDataUri: true });
            if (dataUri) attachments.push({ filename: `VehicleIQ_${(v.label || "vehicule").replace(/[^a-zA-Z0-9]/g, "_")}.pdf`, base64: dataUri });
          } catch (e) { /* un PDF manquant ne doit pas bloquer l'envoi */ }
        }
        setPdfBusy(false);
      }
      // v80 — Pièces jointes ajoutées manuellement par le pro (remplace
      // l'ancienne image en ligne, qui ne fonctionnait pas).
      attachments = [...attachments, ...userAttachments];
      // v64 — WhatsApp/SMS ne peuvent pas transporter de pièce jointe (un
      // lien wa.me/sms: ne préremplit que du texte, c'est une limitation de
      // ces apps, pas de VehicleIQ) — on garde les PDF générés pour un
      // téléchargement manuel juste à côté des liens.
      if (isDeepLinkChannel && attachments.length) setPdfDownloads(attachments);

      const r = await apiPost("/api/prospection/send", {
        recipients: eligible.map(x => ({ id: x.id, email: x.email, phone: x.phone, name: x.name })),
        subject, body, senderName: user?.displayName || undefined,
        channel, attachments: channel === "email" ? attachments : [],
        vehicle_dossier_ids: selectedVehicles.map(v => v.id).filter(Boolean),
        scheduled_at: scheduledAt || undefined,
      });
      if (!r?.success) { setSendErr(r?.error || "Envoi impossible."); return; }
      if (r.scheduled) { setScheduledResult({ ok: true, at: r.scheduled_at }); setScheduledAt(""); return; }
      // v75 — SMS peut désormais être un vrai envoi (OVHcloud configuré) ou
      // un lien sms: (repli) — on distingue selon la réponse reçue, pas
      // seulement selon le canal choisi.
      if (Array.isArray(r.links)) setChannelLinks(r.links);
      else setSendResult({ ok: true, sent: r.sent, failed: r.failed });
    } catch (e) { setSendErr(e?.message || "Envoi impossible."); }
    finally { setSending(false); setPdfBusy(false); }
  };

  const lbl = { fontSize: ".7rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem", display: "block" };
  const fld = { padding: ".5rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".84rem", boxSizing: "border-box" };
  const totalPages = Math.max(1, Math.ceil(directoryTotal / PAGE_SIZE));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem" }}>
      <div className="saas-dash-content-header">
        <h2 className="saas-dash-content-title">🤝 Prospection client</h2>
        <div className="saas-dash-content-sub">
          Répertoire de professionnels de l'automobile, actualisé automatiquement chaque mois. Contactez-les par email (avec PDF du véhicule en pièce jointe) ou WhatsApp.
        </div>
      </div>

      {vehicleContext && (
        <div style={{ background: "var(--accent-soft)", border: "1px solid var(--accent)", borderRadius: 10, padding: ".7rem 1rem", fontSize: ".82rem" }}>
          📣 Vous proposez : <strong>{vehicleContext.label}</strong>{vehicleContext.price ? ` — ${vehicleContext.price}` : ""}
        </div>
      )}

      {/* v50 — Deux publics : le répertoire externe partagé, ou mes propres leads */}
      <div style={{ display: "flex", gap: ".5rem" }}>
        {[["directory", "🌐 Répertoire externe"], ["myleads", "📥 Mes leads"], ["campaigns", "📊 Mes campagnes"], ["calls", "📞 Mes appels"], ["import", "📤 Importer"]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => switchAudience(id)}
            style={{ flex: 1, padding: ".6rem", borderRadius: 10, cursor: "pointer", fontSize: ".84rem", fontWeight: 700,
              border: "1.5px solid " + (audience === id ? "var(--accent)" : "var(--border)"),
              background: audience === id ? "var(--accent-soft)" : "var(--white)", color: audience === id ? "var(--accent)" : "var(--sub)" }}>
            {label}
          </button>
        ))}
      </div>

      {/* v65 — Compteur de sélection GLOBALE, toujours visible, mis à jour
          instantanément (dérivé directement de selectedContacts). */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".6rem", padding: ".6rem .9rem", background: selectedRows.length ? "var(--accent-soft)" : "var(--card)", border: "1px solid " + (selectedRows.length ? "var(--accent)" : "var(--border)"), borderRadius: 10 }}>
        <span style={{ fontSize: ".84rem", fontWeight: 700, color: selectedRows.length ? "var(--accent)" : "var(--muted)" }}>
          {selectedRows.length > 0 ? `${selectedRows.length} contact${selectedRows.length > 1 ? "s" : ""} sélectionné${selectedRows.length > 1 ? "s" : ""}` : "Aucun contact sélectionné"}
        </span>
        <div style={{ display: "flex", gap: ".8rem", alignItems: "center" }}>
          {/* v93 — Règle : appels simultanés sur la sélection — le nombre
              réellement concurrent est plafonné côté serveur (réglage
              admin) ; les appels au-delà du plafond échouent proprement et
              sont signalés, plutôt que d'échouer silencieusement. */}
          {selectedRows.filter(r => r.phone).length > 1 && (
            <button type="button" disabled={bulkCalling} onClick={bulkCallSelection}
              style={{ background: "none", border: "1px solid var(--accent)", borderRadius: 8, padding: ".3rem .7rem", cursor: bulkCalling ? "wait" : "pointer", fontSize: ".78rem", fontWeight: 700, color: "var(--accent)" }}>
              {bulkCalling ? "Lancement…" : `📞 Appeler ${selectedRows.filter(r => r.phone).length} contacts (IA)`}
            </button>
          )}
          {selectedRows.length > 0 && (
            <button type="button" onClick={() => setSelectedContacts({})}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".76rem", color: "var(--muted)", textDecoration: "underline" }}>
              Tout désélectionner
            </button>
          )}
        </div>
      </div>
      {bulkCallResult && (
        <div style={{ fontSize: ".78rem", color: "var(--sub)", padding: ".4rem .2rem" }}>{bulkCallResult}</div>
      )}

      {/* ── Filtres + répertoire ── */}
      {audience === "directory" && (
      <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: ".5rem", marginBottom: ".8rem" }}>
          <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px" }}>Répertoire prospects</div>
          <div style={{ fontSize: ".7rem", color: "var(--muted)" }}>
            {directorySize > 0
              ? `${directorySize} professionnels au total · actualisé le ${lastRefreshedAt ? new Date(lastRefreshedAt).toLocaleDateString("fr-FR") : "—"}`
              : "Répertoire en cours de constitution (première actualisation en arrière-plan) — revenez dans quelques heures."}
          </div>
        </div>
        {/* v77/v78 — Catégories et type de contact en menus déroulants à
            multisélection, alignés sur une même ligne, plus un bouton pour
            afficher/masquer le reste des filtres (masqués par défaut). */}
        <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", alignItems: "center", marginBottom: ".8rem" }}>
          <div style={{ position: "relative" }}>
            <button type="button" onClick={() => setCategoryMenuOpen(o => !o)}
              style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".82rem", fontWeight: 600, padding: ".5rem .8rem", borderRadius: 8, cursor: "pointer",
                border: "1.5px solid " + (categories.length ? "var(--accent)" : "var(--border)"),
                background: categories.length ? "var(--accent-soft)" : "var(--card)", color: categories.length ? "var(--accent)" : "var(--sub)" }}>
              {categories.length > 0 ? `${categories.length} catégorie${categories.length > 1 ? "s" : ""} sélectionnée${categories.length > 1 ? "s" : ""}` : "Toutes les catégories"}
              <span style={{ fontSize: ".7rem" }}>{categoryMenuOpen ? "▲" : "▼"}</span>
            </button>
            {categoryMenuOpen && (
              <>
                <div onClick={() => setCategoryMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
                <div style={{ position: "absolute", top: "calc(100% + .4rem)", left: 0, zIndex: 20, background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".6rem", minWidth: 280, maxHeight: 320, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.12)" }}>
                  {categories.length > 0 && (
                    <button type="button" onClick={() => categories.forEach(c => toggleCategory(c))}
                      style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: ".76rem", color: "var(--accent)", padding: ".3rem 0", marginBottom: ".3rem", borderBottom: "1px solid var(--border)" }}>
                      ✕ Tout désélectionner
                    </button>
                  )}
                  {visibleCategories.map(c => (
                    <label key={c} style={{ display: "flex", alignItems: "center", gap: ".55rem", padding: ".35rem .2rem", cursor: "pointer", fontSize: ".82rem", color: "var(--text)" }}>
                      <input type="checkbox" checked={categories.includes(c)} onChange={() => toggleCategory(c)} />
                      {c}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* v78 — Email/Fixe/Mobile/Importés regroupés dans UN SEUL menu
              déroulant à multisélection, au lieu de 4 cases à cocher séparées. */}
          <div style={{ position: "relative" }}>
            {(() => {
              const contactTypeCount = [filterEmail, filterFixe, filterMobile, filterImported].filter(Boolean).length;
              return (
                <button type="button" onClick={() => setContactTypeMenuOpen(o => !o)}
                  style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".82rem", fontWeight: 600, padding: ".5rem .8rem", borderRadius: 8, cursor: "pointer",
                    border: "1.5px solid " + (contactTypeCount ? "var(--accent)" : "var(--border)"),
                    background: contactTypeCount ? "var(--accent-soft)" : "var(--card)", color: contactTypeCount ? "var(--accent)" : "var(--sub)" }}>
                  {contactTypeCount > 0 ? `${contactTypeCount} filtre${contactTypeCount > 1 ? "s" : ""} de contact` : "Type de contact"}
                  <span style={{ fontSize: ".7rem" }}>{contactTypeMenuOpen ? "▲" : "▼"}</span>
                </button>
              );
            })()}
            {contactTypeMenuOpen && (
              <>
                <div onClick={() => setContactTypeMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
                <div style={{ position: "absolute", top: "calc(100% + .4rem)", left: 0, zIndex: 20, background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".6rem", minWidth: 240, boxShadow: "0 8px 24px rgba(0,0,0,.12)" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: ".55rem", padding: ".35rem .2rem", cursor: "pointer", fontSize: ".82rem", color: "var(--text)" }}>
                    <input type="checkbox" checked={filterEmail} onChange={e => setFilterEmail(e.target.checked)} />
                    ✉ Email
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: ".55rem", padding: ".35rem .2rem", cursor: "pointer", fontSize: ".82rem", color: "var(--text)" }}>
                    <input type="checkbox" checked={filterFixe} onChange={e => { setFilterFixe(e.target.checked); if (e.target.checked) setFilterMobile(false); }} />
                    ☎ Téléphone fixe (01-05)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: ".55rem", padding: ".35rem .2rem", cursor: "pointer", fontSize: ".82rem", color: "var(--text)" }}>
                    <input type="checkbox" checked={filterMobile} onChange={e => { setFilterMobile(e.target.checked); if (e.target.checked) setFilterFixe(false); }} />
                    📱 Mobile / WhatsApp (06-07)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: ".55rem", padding: ".35rem .2rem", cursor: "pointer", fontSize: ".82rem", color: "var(--text)" }}>
                    <input type="checkbox" checked={filterImported} onChange={e => setFilterImported(e.target.checked)} />
                    📥 Importés manuellement
                  </label>
                </div>
              </>
            )}
          </div>

          <button type="button" onClick={() => setFiltersOpen(o => !o)}
            style={{ display: "flex", alignItems: "center", gap: ".4rem", fontSize: ".82rem", fontWeight: 600, padding: ".5rem .8rem", borderRadius: 8, cursor: "pointer", border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--sub)" }}>
            🔎 {filtersOpen ? "Masquer les filtres" : "Afficher les filtres"}
          </button>
        </div>

        {filtersOpen && (
          <>
            <CrmActionFilters crmStatus={filterCrmStatus} onCrmStatus={setFilterCrmStatus} pipeline={filterPipeline} onPipeline={setFilterPipeline} />
            <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginBottom: ".9rem" }}>
              <input style={{ ...fld, flex: "1 1 160px" }} placeholder="Région" value={region} onChange={e => { setPage(1); setRegion(e.target.value); }} />
              <input style={{ ...fld, flex: "1 1 160px" }} placeholder="Ville" value={city} onChange={e => { setPage(1); setCity(e.target.value); }} />
              <input style={{ ...fld, flex: "1 1 160px" }} placeholder="Nom de l'entreprise" value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} />
            </div>
          </>
        )}

        {dirLoading ? (
          <div style={{ fontSize: ".82rem", color: "var(--muted)", textAlign: "center", padding: "1.5rem" }}>Chargement…</div>
        ) : directory.length === 0 ? (
          <div style={{ fontSize: ".82rem", color: "var(--muted)", textAlign: "center", padding: "1.5rem", fontStyle: "italic" }}>Aucun résultat pour ces filtres.</div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem" }}>
                <thead>
                  <tr style={{ borderBottom: "1.5px solid var(--border)", textAlign: "left" }}>
                    <th style={{ padding: ".4rem .3rem", width: 30 }}></th>
                    <SortableTh col="name">Nom</SortableTh>
                    <SortableTh col="category">Catégorie</SortableTh>
                    <SortableTh col="city">Ville</SortableTh>
                    <SortableTh col="contact">Contact</SortableTh>
                    <SortableTh col="status">Statut CRM</SortableTh>
                    <SortableTh col="actions">Actions</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {audienceRows.map(r => (
                    <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: ".4rem .3rem" }}>
                        <input type="checkbox" checked={!!selectedContacts[r.id]} disabled={!r.email && !r.phone}
                          onChange={() => toggleSelect(r)} />
                      </td>
                      <td style={{ padding: ".4rem .3rem", fontWeight: 600 }}>{r.name}{r.website && <div style={{ fontWeight: 400, fontSize: ".7rem", color: "var(--muted)" }}>{r.website}</div>}</td>
                      <td style={{ padding: ".4rem .3rem", color: "var(--sub)" }}>{r.category}</td>
                      <td style={{ padding: ".4rem .3rem", color: "var(--sub)" }}>{r.city || "—"}{r.region ? ` (${r.region})` : ""}</td>
                      <td style={{ padding: ".4rem .3rem", color: "var(--sub)" }}>
                        {r.email && <div>✉ <a href={`mailto:${r.email}`} onClick={e => e.stopPropagation()} style={{ color: "inherit" }}>{r.email}</a></div>}
                        {r.phone && <div>📞 <a href={`tel:${r.phone}`} onClick={e => e.stopPropagation()} style={{ color: "inherit" }}>{r.phone}</a></div>}
                        {!r.email && !r.phone && <span style={{ fontStyle: "italic", color: "var(--muted)" }}>Aucun contact public</span>}
                      </td>
                      <td style={{ padding: ".4rem .3rem" }}>{crmStatus(r)}</td>
                      <td style={{ padding: ".4rem .3rem" }}>
                        <div style={{ marginBottom: ".3rem" }}>
                          <PipelineStatusSelect value={r.pipeline_status} onChange={v => setPipelineStatus("directory", r.id, v)} />
                        </div>
                        {/* v75 — Modifier un contact importé uniquement (jamais le répertoire partagé). */}
                        {r.owner_uid === user?.uid && (
                          <button onClick={() => { setEditingContact(r); setContactDraft({ name: r.name || "", category: r.category || "", city: r.city || "", email: r.email || "", phone: r.phone || "" }); }}
                            style={{ fontSize: ".68rem", background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: ".15rem .45rem", cursor: "pointer", color: "var(--sub)" }}>
                            ✏️ Modifier
                          </button>
                        )}
                        {/* v83 — Règle : agent vocal IA — lance un appel de prospection sur ce contact. */}
                        {r.phone && (
                          <button onClick={() => setVoiceCallTarget({ contact: r, type: "directory" })}
                            style={{ fontSize: ".68rem", background: "none", border: "1px solid var(--accent)", borderRadius: 6, padding: ".15rem .45rem", cursor: "pointer", color: "var(--accent)", marginTop: ".3rem", display: "block" }}>
                            📞 Appeler (IA)
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: ".8rem", marginTop: ".8rem" }}>
                <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Précédent</button>
                <span style={{ fontSize: ".76rem", color: "var(--muted)" }}>Page {page}/{totalPages}</span>
                <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant ›</button>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* v50 — "Mes leads" : demandes reçues via mon propre formulaire client */}
      {audience === "myleads" && (
        <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.2rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: ".5rem", marginBottom: ".8rem" }}>
            <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px" }}>Mes leads</div>
            <div style={{ fontSize: ".7rem", color: "var(--muted)" }}>
              {groupedLeads.length} contact{groupedLeads.length > 1 ? "s" : ""} distinct{groupedLeads.length > 1 ? "s" : ""}
              {(myLeads || []).length !== groupedLeads.length ? ` (${(myLeads || []).length} demandes au total)` : ""}
            </div>
          </div>
          <input style={{ ...fld, width: "100%", marginBottom: ".9rem" }} placeholder="Rechercher par nom ou email…" value={leadSearch} onChange={e => setLeadSearch(e.target.value)} />
          <CrmActionFilters crmStatus={filterCrmStatus} onCrmStatus={setFilterCrmStatus} pipeline={filterPipeline} onPipeline={setFilterPipeline} />
          {myLeads === null ? (
            <div style={{ fontSize: ".82rem", color: "var(--muted)", textAlign: "center", padding: "1.5rem" }}>Chargement…</div>
          ) : filteredLeads.length === 0 ? (
            <div style={{ fontSize: ".82rem", color: "var(--muted)", textAlign: "center", padding: "1.5rem", fontStyle: "italic" }}>
              {myLeads.length === 0 ? "Aucun lead reçu pour l'instant — partagez votre formulaire client pour commencer à en recevoir." : "Aucun résultat pour cette recherche."}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem" }}>
                <thead>
                  <tr style={{ borderBottom: "1.5px solid var(--border)", textAlign: "left" }}>
                    <th style={{ padding: ".4rem .3rem", width: 30 }}></th>
                    <SortableTh col="name">Nom</SortableTh>
                    <SortableTh col="category">Type de demande</SortableTh>
                    <th style={{ padding: ".4rem .3rem" }}>Profil</th>
                    <SortableTh col="contact">Contact</SortableTh>
                    <th style={{ padding: ".4rem .3rem" }}>Reçu le</th>
                    <SortableTh col="status">Statut CRM</SortableTh>
                    <SortableTh col="actions">Actions</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {audienceRows.map(r => {
                    const original = filteredLeads.find(l => `lead-${l.id}` === r.id);
                    return (
                      <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: ".4rem .3rem" }}>
                          <input type="checkbox" checked={!!selectedContacts[r.id]} disabled={!r.email && !r.phone}
                            onChange={() => toggleSelect(r)} />
                        </td>
                        <td style={{ padding: ".4rem .3rem", fontWeight: 600 }}>
                          {r.name}
                          {r.occurrenceCount > 1 && (
                            <span style={{ marginLeft: ".4rem", fontSize: ".68rem", fontWeight: 700, color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 10, padding: ".05rem .45rem" }}>
                              ×{r.occurrenceCount}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: ".4rem .3rem", color: "var(--sub)" }}>{r.category}</td>
                        <td style={{ padding: ".4rem .3rem" }}>
                          {r.profile_type && (
                            <span style={{ fontSize: ".72rem", fontWeight: 700, padding: ".1rem .5rem", borderRadius: 10, color: r.profile_type === "pro" ? "var(--accent)" : "var(--sub)", background: r.profile_type === "pro" ? "var(--accent-soft)" : "var(--card)", border: "1px solid " + (r.profile_type === "pro" ? "rgba(168,128,58,.3)" : "var(--border)") }}>
                              {r.profile_type === "pro" ? "Pro achat/revente" : r.profile_type === "particulier" ? "Particulier" : "Autre"}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: ".4rem .3rem", color: "var(--sub)" }}>
                          {r.email && <div>✉ <a href={`mailto:${r.email}`} onClick={e => e.stopPropagation()} style={{ color: "inherit" }}>{r.email}</a></div>}
                          {r.phone && <div>📞 <a href={`tel:${r.phone}`} onClick={e => e.stopPropagation()} style={{ color: "inherit" }}>{r.phone}</a></div>}
                        </td>
                        <td style={{ padding: ".4rem .3rem", color: "var(--muted)", fontSize: ".74rem" }}>
                          {original?.created_at ? new Date(original.created_at).toLocaleDateString("fr-FR") : "—"}
                        </td>
                        <td style={{ padding: ".4rem .3rem" }}>{crmStatus(r)}</td>
                        <td style={{ padding: ".4rem .3rem" }}>
                          {/* v76 — Colonne Actions manquante sur Mes leads, ajoutée ici (même composant partagé que le répertoire externe). */}
                          <PipelineStatusSelect value={r.pipeline_status} onChange={v => setPipelineStatus("lead", r.id, v)} />
                          {/* v93 — Règle : l'agent vocal IA doit aussi être disponible pour Mes leads. */}
                          {r.phone && (
                            <button onClick={() => setVoiceCallTarget({ contact: r, type: "lead" })}
                              style={{ fontSize: ".68rem", background: "none", border: "1px solid var(--accent)", borderRadius: 6, padding: ".15rem .45rem", cursor: "pointer", color: "var(--accent)", marginTop: ".3rem", display: "block" }}>
                              📞 Appeler (IA)
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* v66 — Historique des campagnes */}
      {audience === "campaigns" && (
        <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.2rem" }}>
          <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".7rem" }}>
            Mes campagnes
          </div>
          {campaigns === null ? (
            <div style={{ fontSize: ".82rem", color: "var(--muted)" }}>Chargement…</div>
          ) : campaigns.length === 0 ? (
            <div style={{ fontSize: ".84rem", color: "var(--muted)", background: "var(--card)", borderRadius: 10, padding: "1rem", textAlign: "center" }}>
              Aucune campagne envoyée pour l'instant.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem" }}>
                <thead>
                  <tr style={{ borderBottom: "1.5px solid var(--border)", textAlign: "left" }}>
                    <th style={{ padding: ".4rem .3rem" }}>Date</th>
                    <th style={{ padding: ".4rem .3rem" }}>Type</th>
                    <th style={{ padding: ".4rem .3rem" }}>Sujet</th>
                    <th style={{ padding: ".4rem .3rem" }}>Destinataires</th>
                    <th style={{ padding: ".4rem .3rem" }}>Envoyés</th>
                    <th style={{ padding: ".4rem .3rem" }}>Statut</th>
                    <th style={{ padding: ".4rem .3rem" }}>Progression</th>
                    <th style={{ padding: ".4rem .3rem" }}>Taux d'ouverture</th>
                    <th style={{ padding: ".4rem .3rem" }}>Taux de réponse</th>
                    <th style={{ padding: ".4rem .3rem" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(c => {
                    const CHANNEL_LABEL = { email: "✉ Email", whatsapp: "💬 WhatsApp", sms: "📱 SMS" };
                    const recipCount = c._scheduled ? (typeof c.recipients === "string" ? JSON.parse(c.recipients).length : (c.recipients || []).length) : c.recipient_count;
                    const pct = !c._scheduled && c.recipient_count > 0 ? Math.round((c.sent_count / c.recipient_count) * 100) : 0;
                    return (
                      <tr key={(c._scheduled ? "sched-" : "sent-") + c.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: ".4rem .3rem", color: "var(--muted)", fontSize: ".74rem", whiteSpace: "nowrap" }}>
                          {c._scheduled
                            ? <>🕓 {new Date(c.scheduled_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</>
                            : (c.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—")}
                        </td>
                        <td style={{ padding: ".4rem .3rem" }}>{CHANNEL_LABEL[c.channel] || c.channel}</td>
                        <td style={{ padding: ".4rem .3rem", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.subject || "—"}</td>
                        <td style={{ padding: ".4rem .3rem" }}>{recipCount}</td>
                        <td style={{ padding: ".4rem .3rem" }}>
                          {c._scheduled ? "—" : (c.channel === "email" ? `${c.sent_count} email(s)` : c.channel === "sms" ? `${c.sent_count} SMS` : `${c.sent_count} lien(s)`)}
                          {!c._scheduled && c.failed_count > 0 && <span style={{ color: "var(--red, #c0392b)" }}> · {c.failed_count} échec(s)</span>}
                        </td>
                        <td style={{ padding: ".4rem .3rem" }}>
                          <span style={{ fontSize: ".72rem", fontWeight: 700, color: c._scheduled ? "var(--accent)" : (c.status?.includes("prepared") ? "var(--accent)" : "var(--green)"), background: c._scheduled ? "var(--accent-soft)" : (c.status?.includes("prepared") ? "var(--accent-soft)" : "rgba(46,111,92,.1)"), borderRadius: 10, padding: ".1rem .55rem" }}>
                            {c._scheduled ? "Programmée" : (c.status?.includes("prepared") ? "Liens préparés" : "Envoyée")}
                          </span>
                        </td>
                        <td style={{ padding: ".4rem .3rem", minWidth: 90 }}>
                          {!c._scheduled && (
                            <div style={{ background: "var(--card)", borderRadius: 6, height: 6, overflow: "hidden" }}>
                              <div style={{ width: `${pct}%`, height: "100%", background: "var(--accent)" }} />
                            </div>
                          )}
                        </td>
                        {/* v75 — Taux d'ouverture réel (webhooks Resend) si un suivi existe pour
                            cette campagne, sinon honnêtement "Non suivi" plutôt qu'inventé. */}
                        <td style={{ padding: ".4rem .3rem", fontSize: ".78rem", fontWeight: c.open_rate != null ? 700 : 400, color: c.open_rate != null ? "var(--green)" : "var(--muted)", fontStyle: c.open_rate != null ? "normal" : "italic" }}>
                          {c._scheduled ? "—" : (c.open_rate != null ? `${c.open_rate}%` : "Non suivi")}
                        </td>
                        {/* v75 — Le taux de réponse n'est structurellement pas mesurable via
                            l'API email (les réponses arrivent dans la boîte mail du pro, pas
                            via Resend) — jamais inventé, on pointe vers le suivi manuel. */}
                        <td style={{ padding: ".4rem .3rem", color: "var(--muted)", fontStyle: "italic", fontSize: ".74rem" }} title="Non mesurable via l'API email — suivez les réponses avec le statut CRM (colonne Actions) du répertoire.">
                          {c._scheduled ? "—" : "Non disponible"}
                        </td>
                        <td style={{ padding: ".4rem .3rem" }}>
                          {c._scheduled ? (
                            <button onClick={async () => { await apiPost(`/api/prospection/scheduled/${c.id}/cancel`, {}); setCampaigns(null); }}
                              style={{ fontSize: ".7rem", fontWeight: 700, background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: ".2rem .5rem", cursor: "pointer", color: "var(--red, #c0392b)", whiteSpace: "nowrap" }}>
                              Annuler
                            </button>
                          ) : c.channel === "email" && (
                            <button disabled={resendingId === c.id} onClick={() => resendCampaign(c.id)}
                              style={{ fontSize: ".7rem", fontWeight: 700, background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: ".2rem .5rem", cursor: "pointer", color: "var(--accent)", whiteSpace: "nowrap" }}>
                              {resendingId === c.id ? "…" : "↻ Relancer"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".8rem" }}>
                Le taux d'ouverture est suivi automatiquement via Resend dès que le webhook est configuré. Le taux de réponse n'est en revanche pas mesurable par l'API email (les réponses arrivent directement dans votre boîte mail) — suivez-les avec le statut CRM (colonne Actions) du répertoire.
              </div>
            </div>
          )}
        </div>
      )}

      {/* v83 — Historique de l'agent IA de prospection téléphonique */}
      {audience === "calls" && (
        <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.2rem" }}>
          <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".7rem" }}>
            Mes appels (agent IA)
          </div>
          {voiceCalls === null ? (
            <div style={{ fontSize: ".82rem", color: "var(--muted)" }}>Chargement…</div>
          ) : !voiceConfigured ? (
            <div style={{ fontSize: ".84rem", color: "var(--muted)", background: "var(--card)", borderRadius: 10, padding: "1rem", textAlign: "center" }}>
              L'agent vocal IA n'est pas encore configuré pour votre compte (identifiants téléphonie manquants côté serveur, ou fonctionnalité non activée par l'administrateur).
              {voiceReason && (
                <div style={{ marginTop: ".5rem", fontSize: ".78rem", color: "var(--sub)" }}>
                  Détail technique : {voiceReason}
                </div>
              )}
            </div>
          ) : voiceCalls.length === 0 ? (
            <div style={{ fontSize: ".84rem", color: "var(--muted)", background: "var(--card)", borderRadius: 10, padding: "1rem", textAlign: "center" }}>
              Aucun appel lancé pour l'instant. Depuis le Répertoire externe, utilisez le bouton « 📞 Appeler (IA) » sur un contact ayant un numéro de téléphone.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem" }}>
                <thead>
                  <tr style={{ borderBottom: "1.5px solid var(--border)", textAlign: "left" }}>
                    <th style={{ padding: ".4rem .3rem" }}>Date</th>
                    <th style={{ padding: ".4rem .3rem" }}>Contact</th>
                    <th style={{ padding: ".4rem .3rem" }}>Numéro</th>
                    <th style={{ padding: ".4rem .3rem" }}>Statut</th>
                    <th style={{ padding: ".4rem .3rem" }}>Durée</th>
                    <th style={{ padding: ".4rem .3rem" }}>Résumé / transcript</th>
                  </tr>
                </thead>
                <tbody>
                  {voiceCalls.map(c => {
                    const STATUS_META = {
                      queued: { label: "En file", color: "var(--muted)" },
                      scheduled: { label: "Programmé", color: "var(--accent)" },
                      calling: { label: "En cours…", color: "var(--accent)" },
                      completed: { label: "Terminé", color: "var(--green)" },
                      failed: { label: "Échec", color: "var(--red, #c0392b)" },
                      no_answer: { label: "Sans réponse", color: "var(--muted)" },
                      busy: { label: "Occupé", color: "var(--muted)" },
                      opted_out: { label: "Ne plus appeler", color: "var(--red, #c0392b)" },
                    };
                    const meta = STATUS_META[c.status] || { label: c.status, color: "var(--muted)" };
                    const mins = Math.floor((c.duration_seconds || 0) / 60), secs = (c.duration_seconds || 0) % 60;
                    let transcript = [];
                    try { transcript = typeof c.transcript === "string" ? JSON.parse(c.transcript) : (c.transcript || []); } catch (e) {}
                    return (
                      <tr key={c.id} style={{ borderBottom: "1px solid var(--border)", verticalAlign: "top" }}>
                        <td style={{ padding: ".4rem .3rem", color: "var(--muted)", fontSize: ".74rem", whiteSpace: "nowrap" }}>
                          {c.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </td>
                        <td style={{ padding: ".4rem .3rem", fontWeight: 600 }}>{c.contact_name || "—"}</td>
                        <td style={{ padding: ".4rem .3rem", fontFamily: "var(--font-mono)" }}>{c.phone}</td>
                        <td style={{ padding: ".4rem .3rem" }}>
                          <span style={{ fontSize: ".72rem", fontWeight: 700, color: meta.color }}>{meta.label}</span>
                          {c.status === "scheduled" && c.scheduled_at && (
                            <div style={{ fontSize: ".68rem", color: "var(--muted)" }}>{new Date(c.scheduled_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                          )}
                          {c.status === "scheduled" && (
                            <button onClick={async () => { await apiPost(`/api/voice/scheduled/${c.id}/cancel`, {}); setVoiceCalls(null); }}
                              style={{ fontSize: ".66rem", background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: ".1rem .4rem", cursor: "pointer", color: "var(--red, #c0392b)", marginTop: ".2rem" }}>
                              Annuler
                            </button>
                          )}
                        </td>
                        <td style={{ padding: ".4rem .3rem", whiteSpace: "nowrap" }}>{c.duration_seconds ? `${mins}:${String(secs).padStart(2, "0")}` : "—"}</td>
                        <td style={{ padding: ".4rem .3rem", maxWidth: 320 }}>
                          {transcript.length > 0 ? (
                            <details>
                              <summary style={{ cursor: "pointer", fontSize: ".78rem", color: "var(--accent)" }}>Voir l'échange ({transcript.length} tour{transcript.length > 1 ? "s" : ""})</summary>
                              <div style={{ marginTop: ".4rem", display: "flex", flexDirection: "column", gap: ".3rem", fontSize: ".74rem" }}>
                                {transcript.map((t, i) => (
                                  <div key={i}><strong style={{ color: t.role === "agent" ? "var(--accent)" : "var(--sub)" }}>{t.role === "agent" ? "IA" : "Contact"} :</strong> {t.text}</div>
                                ))}
                              </div>
                            </details>
                          ) : <span style={{ color: "var(--muted)", fontStyle: "italic", fontSize: ".76rem" }}>Aucun échange enregistré</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* v60 — Import CSV/XLSX de prospects, avec correspondance de colonnes confirmée */}
      {audience === "import" && (
        <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.2rem" }}>
          <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".7rem" }}>
            Ajouter des contacts
          </div>

          {/* v64 — Fichier vs ajout manuel */}
          <div style={{ display: "flex", gap: ".5rem", marginBottom: "1rem" }}>
            {[["file", "📎 Importer un fichier"], ["manual", "✍️ Ajouter un contact"]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => { setImportMode(id); setImportError(null); }}
                style={{ flex: 1, padding: ".55rem", borderRadius: 10, cursor: "pointer", fontSize: ".82rem", fontWeight: 700,
                  border: "1.5px solid " + (importMode === id ? "var(--accent)" : "var(--border)"),
                  background: importMode === id ? "var(--accent-soft)" : "var(--card)", color: importMode === id ? "var(--accent)" : "var(--sub)" }}>
                {label}
              </button>
            ))}
          </div>

          {importMode === "manual" && !importResult && (
            <div>
              <div style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: "1rem", lineHeight: 1.5 }}>
                Ajoutez un professionnel que vous connaissez déjà — même moteur de dédoublonnage que l'import de fichier.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: ".6rem", marginBottom: "1rem" }}>
                <input value={manualLead.name} onChange={e => setManualLead(m => ({ ...m, name: e.target.value }))} placeholder="Nom / société *"
                  style={{ padding: ".6rem .8rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".85rem" }} />
                <select value={manualLead.category} onChange={e => setManualLead(m => ({ ...m, category: e.target.value }))}
                  style={{ padding: ".6rem .8rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".85rem" }}>
                  {PROSPECTION_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input value={manualLead.city} onChange={e => setManualLead(m => ({ ...m, city: e.target.value }))} placeholder="Ville"
                  style={{ padding: ".6rem .8rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".85rem" }} />
                <div style={{ display: "flex", gap: ".6rem" }}>
                  <input value={manualLead.email} onChange={e => setManualLead(m => ({ ...m, email: e.target.value }))} placeholder="Email"
                    style={{ flex: 1, padding: ".6rem .8rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".85rem" }} />
                  <input value={manualLead.phone} onChange={e => setManualLead(m => ({ ...m, phone: e.target.value }))} placeholder="Téléphone"
                    style={{ flex: 1, padding: ".6rem .8rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".85rem" }} />
                </div>
              </div>

              <div style={{ fontSize: ".76rem", fontWeight: 700, color: "var(--sub)", marginBottom: ".4rem" }}>Visibilité</div>
              <div style={{ display: "flex", gap: ".5rem", marginBottom: "1rem" }}>
                {[["private", "🔒 Conserver dans mon compte"], ["shared", "🌐 Partager à tous"]].map(([id, label]) => (
                  <button key={id} type="button" onClick={() => setImportScope(id)}
                    style={{ flex: 1, padding: ".5rem .6rem", borderRadius: 10, cursor: "pointer", fontSize: ".78rem", fontWeight: 700,
                      border: "1.5px solid " + (importScope === id ? "var(--accent)" : "var(--border)"),
                      background: importScope === id ? "var(--accent-soft)" : "var(--card)", color: importScope === id ? "var(--accent)" : "var(--text)" }}>
                    {label}
                  </button>
                ))}
              </div>

              <button className="btn btn-accent" disabled={manualBusy} onClick={submitManualLead} style={{ width: "100%", justifyContent: "center" }}>
                {manualBusy ? "Ajout en cours…" : "Ajouter ce contact"}
              </button>
            </div>
          )}

          {importMode === "file" && importStage === "upload" && (
            <>
              <div style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: "1rem", lineHeight: 1.5 }}>
                Peu importe les en-têtes de votre fichier — à l'étape suivante, vous les associez vous-même aux champs attendus (Nom, Email, Téléphone…). Les doublons (même email, dans la même portée) sont ignorés automatiquement.
              </div>
              <input type="file" accept=".csv,.xlsx,.xls" id="prospect-import-file"
                onChange={e => handleImportFile(e.target.files?.[0])} style={{ display: "none" }} />
              <label htmlFor="prospect-import-file" style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", padding: ".7rem 1.2rem", borderRadius: 10, border: "1.5px dashed var(--border)", cursor: "pointer", background: "var(--card)", color: "var(--sub)", fontSize: ".84rem", fontWeight: 600 }}>
                📎 Choisir un fichier .csv ou .xlsx
              </label>
            </>
          )}

          {importError && <div style={{ marginTop: ".8rem", fontSize: ".82rem", color: "var(--red, #c0392b)" }}>⚠ {importError}</div>}

          {importMode === "file" && importStage === "mapping" && rawRows && (
            <>
              <div style={{ fontSize: ".84rem", fontWeight: 700, marginBottom: ".3rem" }}>Associez vos colonnes</div>
              <div style={{ fontSize: ".76rem", color: "var(--sub)", marginBottom: ".9rem" }}>
                {rawRows.length} ligne(s) détectée(s) dans le fichier. Pour chaque champ ci-dessous, choisissez la colonne correspondante (déjà pré-associées quand la correspondance était évidente).
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: ".6rem", marginBottom: "1rem" }}>
                {IMPORT_EXPECTED_FIELDS.map(f => (
                  <div key={f.key} style={{ display: "flex", alignItems: "center", gap: ".8rem" }}>
                    <div style={{ width: 130, flexShrink: 0, fontSize: ".8rem", fontWeight: 600 }}>
                      {f.label}{f.required ? " *" : ""}
                    </div>
                    <select value={columnMap[f.key] || ""} onChange={e => setColumnMap(m => ({ ...m, [f.key]: e.target.value || undefined }))}
                      style={{ flex: 1, padding: ".5rem .6rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".82rem" }}>
                      <option value="">— Aucune —</option>
                      {rawHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: ".7rem", color: "var(--muted)", marginBottom: "1rem" }}>* Nom/Société ou Email requis (au moins l'un des deux).</div>
              <div style={{ display: "flex", gap: ".6rem" }}>
                <button className="btn btn-outline" style={{ flex: 1, justifyContent: "center" }} onClick={resetImport}>
                  Annuler
                </button>
                <button className="btn btn-accent" style={{ flex: 1, justifyContent: "center" }} onClick={confirmColumnMapping}>
                  Continuer →
                </button>
              </div>
            </>
          )}

          {importMode === "file" && importStage === "preview" && importRows && (
            <>
              <div style={{ fontSize: ".84rem", fontWeight: 700, marginBottom: ".6rem" }}>{importRows.length} ligne(s) prête(s) à importer</div>
              <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8, marginBottom: "1rem" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".76rem" }}>
                  <thead>
                    <tr style={{ background: "var(--card)", textAlign: "left" }}>
                      <th style={{ padding: ".35rem .5rem" }}>Nom</th>
                      <th style={{ padding: ".35rem .5rem" }}>Email</th>
                      <th style={{ padding: ".35rem .5rem" }}>Téléphone</th>
                      <th style={{ padding: ".35rem .5rem" }}>Ville</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importRows.slice(0, 50).map((r, i) => (
                      <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                        <td style={{ padding: ".3rem .5rem" }}>{r.name || "—"}</td>
                        <td style={{ padding: ".3rem .5rem" }}>{r.email || "—"}</td>
                        <td style={{ padding: ".3rem .5rem" }}>{r.phone || "—"}</td>
                        <td style={{ padding: ".3rem .5rem" }}>{r.city || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importRows.length > 50 && <div style={{ padding: ".4rem .5rem", fontSize: ".72rem", color: "var(--muted)" }}>… et {importRows.length - 50} de plus</div>}
              </div>

              <div style={{ fontSize: ".76rem", fontWeight: 700, color: "var(--sub)", marginBottom: ".4rem" }}>Visibilité</div>
              <div style={{ display: "flex", gap: ".5rem", marginBottom: "1rem" }}>
                {[["private", "🔒 Conserver dans mon compte", "Visible uniquement par vous."], ["shared", "🌐 Partager à tous", "Rejoint le répertoire partagé, visible par tous les pros."]].map(([id, label, hint]) => (
                  <button key={id} type="button" onClick={() => setImportScope(id)}
                    style={{ flex: 1, textAlign: "left", padding: ".6rem .8rem", borderRadius: 10, cursor: "pointer",
                      border: "1.5px solid " + (importScope === id ? "var(--accent)" : "var(--border)"),
                      background: importScope === id ? "var(--accent-soft)" : "var(--card)" }}>
                    <div style={{ fontSize: ".82rem", fontWeight: 700, color: importScope === id ? "var(--accent)" : "var(--text)" }}>{label}</div>
                    <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: ".15rem" }}>{hint}</div>
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", gap: ".6rem" }}>
                <button className="btn btn-outline" style={{ flex: 1, justifyContent: "center" }} onClick={() => setImportStage("mapping")}>
                  ← Revoir les colonnes
                </button>
                <button className="btn btn-accent" disabled={importBusy} style={{ flex: 1, justifyContent: "center" }} onClick={runImport}>
                  {importBusy ? "Import en cours…" : `Importer ${importRows.length} contact(s)`}
                </button>
              </div>
            </>
          )}

          {importResult && (
            <div style={{ padding: "1rem", background: "var(--card)", borderRadius: 10 }}>
              <div style={{ fontSize: ".86rem", fontWeight: 700, color: "var(--green)", marginBottom: ".3rem" }}>
                ✓ {importResult.inserted} contact(s) importé(s){importResult.skipped ? `, ${importResult.skipped} ignoré(s) (doublon ou ligne vide)` : ""}
              </div>
              <div style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: ".8rem" }}>
                {importResult.scope === "shared" ? "Ajoutés au répertoire partagé, catégorie « Import »." : "Conservés dans votre compte uniquement, catégorie « Import »."}
              </div>
              <div style={{ display: "flex", gap: ".6rem" }}>
                <button className="btn btn-outline btn-sm" onClick={resetImport}>
                  Importer un autre fichier
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => { setImportResult(null); switchAudience("directory"); setCategories(["Import"]); }}>
                  Voir dans le répertoire →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!vehicleContext && (
        <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.2rem" }}>
          <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".7rem" }}>
            Véhicule(s) à proposer (optionnel)
          </div>
          {dossierOptions === null ? (
            <div style={{ fontSize: ".82rem", color: "var(--muted)" }}>Chargement de vos Dossiers VIQ…</div>
          ) : dossierOptions.length === 0 ? (
            <div style={{ fontSize: ".82rem", color: "var(--muted)", fontStyle: "italic" }}>Aucun Dossier VIQ pour l'instant.</div>
          ) : (() => {
            // v72 — Règle 2 : regroupe les véhicules d'un même lot, pour
            // pouvoir les proposer ensemble en un clic plutôt qu'un par un.
            const groups = [];
            const lotIndex = {};
            dossierOptions.forEach(d => {
              if (d.lot_id && lotIndex[d.lot_id] != null) { groups[lotIndex[d.lot_id]].items.push(d); return; }
              if (d.lot_id) lotIndex[d.lot_id] = groups.length;
              groups.push({ lot_id: d.lot_id || null, lot_name: d.lot_name || null, items: [d] });
            });
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: ".6rem", maxHeight: 220, overflowY: "auto" }}>
                {groups.map(g => {
                  if (g.lot_id && g.items.length > 1) {
                    const allIn = g.items.every(d => selectedVehicles.some(v => v.id === d.id));
                    return (
                      <div key={"lot-" + g.lot_id} style={{ border: "1.5px solid var(--accent)", borderRadius: 10, padding: ".5rem .6rem", background: "rgba(168,128,58,.04)" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".5rem", marginBottom: ".4rem", flexWrap: "wrap" }}>
                          <span style={{ fontSize: ".74rem", fontWeight: 700, color: "var(--accent)" }}>📦 {g.lot_name || `Lot de ${g.items.length} véhicules`}</span>
                          <button type="button" onClick={() => toggleLotVehicles(g.items)}
                            style={{ fontSize: ".72rem", fontWeight: 700, padding: ".2rem .6rem", borderRadius: 20, cursor: "pointer",
                              border: "1.5px solid var(--accent)", background: allIn ? "var(--accent)" : "var(--card)", color: allIn ? "#fff" : "var(--accent)" }}>
                            {allIn ? "✓ Tout le lot proposé" : "Proposer tout le lot"}
                          </button>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem" }}>
                          {g.items.map(d => {
                            const on = selectedVehicles.some(v => v.id === d.id);
                            return (
                              <button key={d.id} type="button" onClick={() => toggleVehicle(d)}
                                style={{
                                  fontSize: ".76rem", fontWeight: 600, padding: ".3rem .7rem", borderRadius: 20, cursor: "pointer",
                                  border: "1.5px solid " + (on ? "var(--accent)" : "var(--border)"),
                                  background: on ? "var(--accent-soft)" : "var(--card)", color: on ? "var(--accent)" : "var(--sub)",
                                }}>
                                {on ? "✓ " : ""}{d.make || "Véhicule"} {d.model || ""}{d.year ? ` · ${d.year}` : ""}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  const d = g.items[0];
                  const on = selectedVehicles.some(v => v.id === d.id);
                  return (
                    <button key={d.id} type="button" onClick={() => toggleVehicle(d)} style={{ alignSelf: "flex-start",
                      fontSize: ".76rem", fontWeight: 600, padding: ".3rem .7rem", borderRadius: 20, cursor: "pointer",
                      border: "1.5px solid " + (on ? "var(--accent)" : "var(--border)"),
                      background: on ? "var(--accent-soft)" : "var(--card)", color: on ? "var(--accent)" : "var(--sub)",
                    }}>
                      {on ? "✓ " : ""}{d.make || "Véhicule"} {d.model || ""}{d.year ? ` · ${d.year}` : ""}
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Canal + message + envoi ── */}
      <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.2rem" }}>
        <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".7rem" }}>
          Message ({eligible.length}/{cap} destinataire{eligible.length > 1 ? "s" : ""})
        </div>
        <div style={{ display: "flex", gap: ".5rem", marginBottom: ".8rem" }}>
          {[["email", "✉ Email", prospectionLimits.maxEmailsPerDay], ["whatsapp", "💬 WhatsApp", prospectionLimits.maxWhatsappPerDay], ["sms", "📱 SMS", prospectionLimits.maxSmsPerDay]].map(([id, label, max]) => {
            // v81 — Règle : SMS désactivé également (comme WhatsApp) en attendant une intégration jugée aboutie — les deux marqués "Bientôt".
            const disabled = id === "whatsapp" || id === "sms";
            return (
              <button key={id} type="button" disabled={disabled} onClick={() => { setChannel(id); setSendErr(null); setSendResult(null); setChannelLinks(null); setPdfDownloads(null); }}
                style={{ flex: 1, padding: ".5rem", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer", fontSize: ".82rem", fontWeight: 700,
                  border: "1.5px solid " + (channel === id ? "var(--accent)" : "var(--border)"),
                  background: channel === id ? "var(--accent-soft)" : "var(--card)", color: disabled ? "var(--muted)" : (channel === id ? "var(--accent)" : "var(--sub)"),
                  opacity: disabled ? .6 : 1 }}>
                {label} {disabled ? <span style={{ fontWeight: 700, fontSize: ".64rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 4, padding: "1px 5px", marginLeft: ".2rem" }}>Bientôt</span> : <span style={{ fontWeight: 400, fontSize: ".7rem" }}>(max {max})</span>}
              </button>
            );
          })}
        </div>
        {channel === "email" && (
          <div style={{ marginBottom: ".7rem" }}>
            <label style={lbl}>Objet</label>
            <input style={{ ...fld, width: "100%" }} value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
        )}
        <div style={{ marginBottom: ".5rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label style={lbl}>Message</label>
          {channel === "email" && (
            <button type="button" onClick={() => setShowPreview(p => !p)}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".74rem", color: "var(--accent)", fontWeight: 600, textDecoration: "underline" }}>
              {showPreview ? "Masquer l'aperçu" : "👁 Aperçu de l'email"}
            </button>
          )}
        </div>
        <div style={{ marginBottom: ".8rem" }}>
          <RichTextEditor value={body} onChange={setBody} />
        </div>
        {channel === "email" && (
          <div style={{ marginBottom: ".9rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap" }}>
              <button type="button" onClick={addAttachment}
                style={{ fontSize: ".78rem", fontWeight: 600, padding: ".4rem .7rem", borderRadius: 8, cursor: "pointer", border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--sub)" }}>
                📎 Ajouter une pièce jointe
              </button>
              {attachError && <span style={{ fontSize: ".76rem", color: "var(--red, #c0392b)" }}>{attachError}</span>}
            </div>
            {userAttachments.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginTop: ".5rem" }}>
                {userAttachments.map((a, i) => (
                  <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", fontSize: ".76rem", padding: ".25rem .5rem", borderRadius: 20, background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.3)", color: "var(--accent)" }}>
                    📎 {a.filename}
                    <button type="button" onClick={() => removeAttachment(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent)", fontWeight: 700, padding: 0, lineHeight: 1 }}>✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {channel === "email" && showPreview && (
          <div style={{ marginBottom: "1rem", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", fontFamily: "Georgia, 'Times New Roman', serif" }}>
            <div style={{ background: "linear-gradient(135deg, #f6f3ec, #e9e4d8)", padding: "16px 20px", borderBottom: "3px solid #A8803A" }}>
              <div style={{ fontSize: "1.15rem", fontWeight: 800, color: "#A8803A" }}>VehicleIQ</div>
              <div style={{ fontSize: ".7rem", color: "#6b6558", marginTop: 2 }}>Cote VO professionnelle · VIQ.AI®</div>
            </div>
            <div style={{ padding: "18px 20px", color: "#1a1a1a", fontSize: ".85rem", lineHeight: 1.65 }}>
              {body ? <div dangerouslySetInnerHTML={{ __html: body }} /> : <span style={{ color: "var(--muted)", fontStyle: "italic" }}>(votre message apparaîtra ici)</span>}
            </div>
            <div style={{ background: "#f6f3ec", padding: "14px 20px", borderTop: "1px solid #e6e1d6" }}>
              <div style={{ fontWeight: 700, fontSize: ".82rem", color: "#1a1a1a" }}>{user?.siret_name || user?.name || "Votre nom"}</div>
              {user?.phone && <div style={{ fontSize: ".76rem", color: "#555", marginTop: 2 }}>📞 {user.phone}</div>}
              {user?.email && <div style={{ fontSize: ".76rem", color: "#555", marginTop: 2 }}>✉️ {user.email}</div>}
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px dashed #d8d2c2", fontSize: ".66rem", color: "#999" }}>
                Message envoyé via VehicleIQ.fr dans le cadre d'une démarche de prospection professionnelle B2B. Pour ne plus recevoir ce type de message, répondez "STOP".
              </div>
            </div>
          </div>
        )}
        {channel === "email" && (
          <div style={{ fontSize: ".64rem", color: "var(--muted)", marginBottom: ".8rem" }}>
            Un PDF par véhicule sélectionné est généré et joint automatiquement. Le pied de page reprend vos coordonnées et un désabonnement.
          </div>
        )}
        {channel === "whatsapp" && (
          <div style={{ fontSize: ".64rem", color: "var(--muted)", marginBottom: ".8rem" }}>
            WhatsApp Business exige un modèle pré-approuvé pour tout message initié par une entreprise — VehicleIQ génère donc un lien pré-rempli par destinataire, que vous ouvrez et envoyez vous-même depuis WhatsApp. Un lien ne peut pas transporter de pièce jointe : si des véhicules sont sélectionnés, leur PDF est généré à côté pour un envoi manuel.
          </div>
        )}
        {channel === "sms" && (
          <div style={{ fontSize: ".64rem", color: "var(--muted)", marginBottom: ".8rem" }}>
            Pas de passerelle SMS payante souscrite (Twilio, OVH…) : VehicleIQ génère un lien pré-rempli par destinataire, ouvert dans votre app SMS, que vous envoyez vous-même — comme WhatsApp, sans pièce jointe possible.
          </div>
        )}
        {/* v85 — Règle : programmation d'envoi (date/heure) — email uniquement. */}
        {channel === "email" && (
          <div style={{ display: "flex", alignItems: "center", gap: ".6rem", marginBottom: ".8rem", flexWrap: "wrap" }}>
            <label style={{ fontSize: ".8rem", color: "var(--sub)" }}>Programmer l'envoi (optionnel) :</label>
            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
              min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
              style={{ fontSize: ".82rem", padding: ".4rem .6rem", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)" }} />
            {scheduledAt && <button type="button" onClick={() => setScheduledAt("")} style={{ fontSize: ".76rem", background: "none", border: "none", color: "var(--muted)", cursor: "pointer" }}>✕ Annuler</button>}
          </div>
        )}
        <button className="btn btn-accent" disabled={sending || !eligible.length} onClick={runSend} style={{ justifyContent: "center" }}>
          {sending ? (pdfBusy ? "Génération des PDF…" : "Envoi en cours…") : scheduledAt ? `🕓 Programmer l'envoi à ${eligible.length} contact${eligible.length > 1 ? "s" : ""}` : channel === "email" ? `📧 Envoyer à ${eligible.length} contact${eligible.length > 1 ? "s" : ""}` : `${channel === "whatsapp" ? "💬" : "📱"} Préparer ${eligible.length} lien${eligible.length > 1 ? "s" : ""} ${channel === "whatsapp" ? "WhatsApp" : "SMS"}`}
        </button>
        {scheduledResult?.ok && (
          <div style={{ marginTop: ".6rem", fontSize: ".82rem", color: "var(--green)" }}>
            ✓ Campagne programmée pour le {new Date(scheduledResult.at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}. Retrouvez-la dans Mes campagnes.
          </div>
        )}
        {sendErr && <div style={{ marginTop: ".6rem", fontSize: ".8rem", color: "var(--red, #c0392b)" }}>⚠ {sendErr}</div>}
        {sendResult?.ok && (
          <div style={{ marginTop: ".6rem", fontSize: ".82rem", color: "var(--green)" }}>
            ✓ {sendResult.sent} {channel === "sms" ? "SMS" : `email${sendResult.sent > 1 ? "s" : ""}`} envoyé{sendResult.sent > 1 ? "s" : ""}{sendResult.failed > 0 ? ` (${sendResult.failed} échec${sendResult.failed > 1 ? "s" : ""})` : ""}.
          </div>
        )}
        {pdfDownloads && pdfDownloads.length > 0 && (
          <div style={{ marginTop: ".8rem", padding: ".7rem .9rem", background: "var(--card)", borderRadius: 8 }}>
            <div style={{ fontSize: ".76rem", fontWeight: 700, marginBottom: ".4rem" }}>📎 PDF à joindre manuellement</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem" }}>
              {pdfDownloads.map((a, i) => (
                <a key={i} href={`data:application/pdf;base64,${a.base64}`} download={a.filename} className="btn btn-outline btn-sm" style={{ textDecoration: "none", fontSize: ".74rem" }}>
                  ⬇ {a.filename}
                </a>
              ))}
            </div>
          </div>
        )}
        {channelLinks && channelLinks.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", marginTop: ".8rem" }}>
            {channelLinks.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm" style={{ justifyContent: "center", textDecoration: "none" }}>
                {channel === "whatsapp" ? "💬 Ouvrir WhatsApp" : "📱 Ouvrir SMS"} — {l.name}
              </a>
            ))}
          </div>
        )}
      </div>
      {editingContact && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setEditingContact(null)}>
          <div className="panel" style={{ maxWidth: 420 }}>
            <div className="panel-header">
              <div className="panel-title">✏️ Modifier le contact</div>
              <button className="btn-icon" onClick={() => setEditingContact(null)}>✕</button>
            </div>
            <div style={{ padding: "1.2rem", display: "flex", flexDirection: "column", gap: ".7rem" }}>
              <div className="field" style={{ margin: 0 }}><label>Nom</label><input className="input" value={contactDraft.name} onChange={e => setContactDraft(d => ({ ...d, name: e.target.value }))} /></div>
              <div className="field" style={{ margin: 0 }}><label>Catégorie</label>
                <select className="input" value={contactDraft.category} onChange={e => setContactDraft(d => ({ ...d, category: e.target.value }))}>
                  {visibleCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="field" style={{ margin: 0 }}><label>Ville</label><input className="input" value={contactDraft.city} onChange={e => setContactDraft(d => ({ ...d, city: e.target.value }))} /></div>
              <div className="field" style={{ margin: 0 }}><label>Email</label><input className="input" value={contactDraft.email} onChange={e => setContactDraft(d => ({ ...d, email: e.target.value }))} /></div>
              <div className="field" style={{ margin: 0 }}><label>Téléphone</label><input className="input" value={contactDraft.phone} onChange={e => setContactDraft(d => ({ ...d, phone: e.target.value }))} /></div>
              {contactError && <div style={{ fontSize: ".8rem", color: "var(--red, #c0392b)" }}>⚠ {contactError}</div>}
              <button className="btn btn-accent" disabled={contactSaving} onClick={saveContact} style={{ justifyContent: "center" }}>
                {contactSaving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
      {voiceCallTarget && <VoiceCallModal contact={voiceCallTarget.contact} targetType={voiceCallTarget.type} onClose={() => setVoiceCallTarget(null)} onCallLaunched={() => setVoiceCalls(null)} />}
    </div>
  );
}

function CarteGriseCalculator({ prefill }) {
  const p = prefill || {};
  const [f, setF] = useState(() => {
    // CV fiscale pré-calculée si le prefill fournit la puissance ch (formule CO₂).
    const initCv = p.cv || (p.ch ? Math.max(1, Math.round((Number(p.co2) || 0) / 45 + Math.pow((Number(p.ch) * 0.7355) / 40, 1.6))) : "");
    return {
      region: "Hauts-de-France", ch: p.ch || "", cv: initCv, co2: p.co2 || "", poids: p.poids || "",
      energie: p.fuel || "Essence", dateImmat: p.dateImmat || "", isImport: p.isImport !== undefined ? !!p.isImport : true,
      paysAchat: p.paysAchat || "Allemagne",
      immat: p.immat || "", vin: p.vin || "", makeModel: p.makeModel || "",
    };
  });
  const [pdfBusy, setPdfBusy] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  // Conversions de puissance. 1 ch (métrique/PS) = 0,7355 kW.
  // Puissance fiscale (CV) : formule administrative CO₂ — PA = (CO₂/45) + (kW/40)^1,6, arrondie.
  const PS_TO_KW = 0.7355;
  const chToKw = (ch) => ch ? Math.round(Number(ch) * PS_TO_KW) : "";
  const computeCv = (kw, co2) => {
    const k = Number(kw) || 0; if (!k) return "";
    const pa = (Number(co2) || 0) / 45 + Math.pow(k / 40, 1.6);
    return Math.max(1, Math.round(pa));
  };
  const kw = chToKw(f.ch);
  // Saisie de la puissance (ch) → recalcule kW + CV fiscaux (CV reste éditable ensuite).
  const setCh = (val) => {
    const ch = String(val).replace(/[^\d]/g, "");
    setF(prev => ({ ...prev, ch, cv: ch ? computeCv(chToKw(ch), prev.co2) : prev.cv }));
  };
  // La CV fiscale dépend du CO₂ : on la recalcule quand le CO₂ change (si une puissance est saisie).
  const setCo2 = (val) => {
    const co2 = String(val).replace(/[^\d]/g, "");
    setF(prev => ({ ...prev, co2, cv: prev.ch ? computeCv(chToKw(prev.ch), co2) : prev.cv }));
  };
  // Normalise l'énergie décodée vers les options exactes du sélecteur.
  const normEnergie = (raw) => {
    const s = String(raw || "").toLowerCase().trim();
    if (!s || s === "—") return "";
    if (s.includes("hybride") && (s.includes("recharge") || s.includes("plug") || s.includes("phev"))) return "Hybride rechargeable";
    if (s.includes("hybride") || s.includes("hev")) return "Hybride";
    if (s.includes("électr") || s.includes("electr")) return "Électrique";
    if (s.includes("hydrog")) return "Hydrogène";
    if (s.includes("diesel") || s.includes("gazole") || s === "go" || s.includes("hdi") || s.includes("tdi") || s.includes("dci")) return "Diesel";
    if (s.includes("essence") || s.includes("petrol") || s.includes("sp95") || s.includes("sp98")) return "Essence";
    return "";
  };
  const r = (f.cv || f.co2 || f.poids) ? cgCalcul({
    region: f.region, cv: Number(f.cv) || 0, co2: Number(f.co2) || 0, poids: Number(f.poids) || 0,
    energie: f.energie, dateImmat: f.dateImmat, isImport: f.isImport,
  }) : null;
  const eur = (v) => (v || v === 0) ? Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + " €" : "—";

  const lbl = { fontSize: ".7rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem", display: "block" };
  const fld = { width: "100%", padding: ".55rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".88rem", outline: "none", boxSizing: "border-box" };
  const g2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".7rem", marginBottom: ".7rem" };
  const g3 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: ".7rem", marginBottom: ".7rem" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div className="saas-dash-content-header">
        <h2 className="saas-dash-content-title">🧾 Carte grise & malus écologique</h2>
        <div className="saas-dash-content-sub">
          Estimez le coût d'immatriculation en France (taxe régionale + malus CO₂ + malus au poids), y compris pour un véhicule importé.
        </div>
      </div>

      <PlateLookupInline defaultPlate={f.immat}
        onResult={(d) => setF(prev => {
          const next = { ...prev,
            immat: d.immat || prev.immat,
            vin: d.vin || prev.vin,
            makeModel: d.makeModel || prev.makeModel,
            energie: normEnergie(d.fuel) || prev.energie,
            co2: (d.co2 !== "" && d.co2 != null) ? String(d.co2) : prev.co2,
            poids: (d.weight !== "" && d.weight != null) ? String(d.weight) : prev.poids,
            dateImmat: d.date_month || prev.dateImmat,
          };
          // Puissance : kW officiel → ch ; CV fiscale officielle conservée telle quelle.
          if (d.power_kw) next.ch = String(Math.round(Number(d.power_kw) / 0.7355));
          if (d.power_cv) next.cv = String(d.power_cv);
          else if (next.ch && next.co2) next.cv = String(computeCv(chToKw(next.ch), next.co2));
          return next;
        })} />

      <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.3rem" }}>
        <div style={g2}>
          <div><label style={lbl}>Immatriculation</label><input style={fld} value={f.immat} onChange={e => set("immat", e.target.value.toUpperCase())} placeholder="AA-123-BB" /></div>
          <div><label style={lbl}>VIN (n° de série)</label><input style={fld} value={f.vin} onChange={e => set("vin", e.target.value.toUpperCase())} placeholder="VF…" /></div>
        </div>
        <div style={{ marginBottom: ".7rem" }}>
          <label style={lbl}>Marque et modèle</label>
          <input style={fld} value={f.makeModel} onChange={e => set("makeModel", e.target.value)} placeholder="Ex : Peugeot 508 BlueHDi 130" />
        </div>
        <div style={g2}>
          <div><label style={lbl}>Région d'immatriculation</label>
            <select style={{ ...fld, cursor: "pointer" }} value={f.region} onChange={e => set("region", e.target.value)}>
              {Object.keys(CG_CV_REGION).map(rg => <option key={rg} value={rg}>{rg}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Énergie</label>
            <select style={{ ...fld, cursor: "pointer" }} value={f.energie} onChange={e => set("energie", e.target.value)}>
              {["Essence","Diesel","Hybride","Hybride rechargeable","Électrique","Hydrogène"].map(en => <option key={en} value={en}>{en}</option>)}
            </select>
          </div>
        </div>
        <div style={g3}>
          <div><label style={lbl}>Puissance (ch)</label><input className="num-spin" style={fld} type="number" value={f.ch} onChange={e => setCh(e.target.value)} placeholder="ex : 150" /></div>
          <div><label style={lbl}>Puissance fiscale (CV)</label><input className="num-spin" style={fld} type="number" value={f.cv} onChange={e => set("cv", e.target.value)} placeholder="auto" /></div>
          <div><label style={lbl}>Puissance (kW)</label><input style={{ ...fld, background: "var(--bg)", color: "var(--sub)" }} type="number" value={kw} readOnly tabIndex={-1} placeholder="auto" /></div>
        </div>
        <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: "-.4rem", marginBottom: ".7rem", lineHeight: 1.45 }}>
          Saisissez la puissance en chevaux (ch) : la puissance en kW et la puissance fiscale (CV) sont converties automatiquement. La CV fiscale (formule CO₂) reste modifiable si vous disposez de la valeur exacte indiquée sur la carte grise.
        </div>
        <div style={g2}>
          <div><label style={lbl}>Émissions CO₂ (g/km, WLTP)</label><input className="num-spin" style={fld} type="number" value={f.co2} onChange={e => setCo2(e.target.value)} placeholder="ex : 150" /></div>
          <div><label style={lbl}>Masse en ordre de marche (kg)</label><input className="num-spin" style={fld} type="number" value={f.poids} onChange={e => set("poids", e.target.value)} placeholder="ex : 1600" /></div>
        </div>
        <div style={{ marginBottom: ".7rem" }}>
          <label style={lbl}>Date de 1ʳᵉ immatriculation (mois / année)</label>
          <input style={fld} type="month" value={f.dateImmat} onChange={e => set("dateImmat", e.target.value)} />
        </div>
        <div style={{ marginBottom: ".3rem" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", cursor: "pointer", fontSize: ".85rem", color: "var(--sub)" }}>
            <input type="checkbox" checked={f.isImport} onChange={e => set("isImport", e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--accent)" }} />
            <span>Véhicule <strong>importé</strong> (1ʳᵉ immatriculation en France)</span>
          </label>
        </div>
        {f.isImport && (
          <div style={{ marginTop: ".5rem" }}>
            <label style={lbl}>Pays d'achat</label>
            <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
              {["Allemagne", "Belgique", "Luxembourg", "Autre UE"].map(pa => (
                <button key={pa} type="button" onClick={() => set("paysAchat", pa)}
                  style={{ padding: ".4rem .8rem", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: ".8rem",
                    border: "1.5px solid " + (f.paysAchat === pa ? "var(--accent)" : "var(--border)"),
                    background: f.paysAchat === pa ? "var(--accent-soft)" : "var(--card)", color: f.paysAchat === pa ? "var(--accent)" : "var(--text)" }}>{pa}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {r && (
        <div style={{ background: "var(--accent-soft)", border: "1.5px solid var(--accent)", borderRadius: 12, padding: "1.3rem 1.4rem" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1.1rem", fontWeight: 800, color: "var(--text)", marginBottom: ".8rem" }}>Cote du coût d'immatriculation</div>
          {[
            ["Taxe régionale (Y.1)", `${f.cv || 0} CV × ${eur(r.tarifCV)}${f.region === "Île-de-France" ? " + majo. IDFM" : ""}${r.plus10 ? " · ÷2 (>10 ans)" : ""}`, r.taxeReg],
            ...(f.isImport ? [
              ["Malus CO₂", r.plus15 ? "Exonéré (>15 ans)" : `barème ${r.annee} · décote import ${Math.round(r.dec*100)} %`, r.mCO2],
              ["Malus au poids (TMOM)", r.plus15 ? "Exonéré (>15 ans)" : `barème ${r.annee} · décote ${Math.round(r.dec*100)} %`, r.mPoids],
            ] : []),
            ["Taxes fixes (gestion + acheminement)", "11 € + 2,76 €", r.fixes],
          ].map(([k, sub, v], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: ".5rem 0", borderBottom: "1px solid rgba(168,128,58,.2)" }}>
              <div><div style={{ fontWeight: 600, color: "var(--text)", fontSize: ".88rem" }}>{k}</div><div style={{ fontSize: ".72rem", color: "var(--sub)" }}>{sub}</div></div>
              <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--text)", whiteSpace: "nowrap" }}>{eur(v)}</div>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: ".8rem", marginTop: ".3rem" }}>
            <div style={{ fontWeight: 800, color: "var(--text)", fontSize: "1rem" }}>Total estimé</div>
            <div style={{ fontFamily: "var(--font-mono)", fontWeight: 800, color: "var(--accent)", fontSize: "1.3rem" }}>{eur(r.total)}</div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
            <button className="btn btn-accent" disabled={pdfBusy}
              onClick={() => {
                if (pdfBusy) return;
                setPdfBusy(true);
                (async () => { await exportCarteGrisePDF(f, r); })().finally(() => setPdfBusy(false));
              }}
              style={{ display: "inline-flex", alignItems: "center", gap: ".45rem", opacity: pdfBusy ? .6 : 1, cursor: pdfBusy ? "wait" : "pointer" }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/></svg>
              {pdfBusy ? "Génération…" : "Télécharger le PDF"}
            </button>
          </div>
        </div>
      )}

      <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic", lineHeight: 1.5 }}>
        Cote indicative fondée sur les barèmes 2026 (taxe régionale par région, malus CO₂ WLTP, malus au poids dès 1 500 kg, décote import par ancienneté — art. L.421-7-2 du CIBS). Le montant officiel est calculé lors de la démarche sur le service public. Les barèmes évoluent chaque année.
      </div>
    </div>
  );
}

function DocumentsPanel({ onCreateMandat, onCreateCession }) {
  const [mandats, setMandats] = useState(null);
  const [cessions, setCessions] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [m, c] = await Promise.all([
          apiGet("/api/mandats").catch(() => ({ mandats: [] })),
          apiGet("/api/cessions").catch(() => ({ cessions: [] })),
        ]);
        if (!alive) return;
        setMandats(Array.isArray(m?.mandats) ? m.mandats : []);
        setCessions(Array.isArray(c?.cessions) ? c.cessions : []);
      } catch (e) {
        if (alive) { setErr("Impossible de charger les documents pour le moment."); setMandats([]); setCessions([]); }
      }
    })();
    return () => { alive = false; };
  }, []);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
  const fmtEur = (v) => (v || v === 0) ? Number(v).toLocaleString("fr-FR") + " €" : "—";
  const commLabel = (t, v) => {
    if (!t) return "—";
    if (t === "pourcentage") return `${v || "—"} %`;
    if (t === "forfait") return `Forfait ${fmtEur(v)}`;
    if (t === "net_vendeur") return `Net vendeur ${fmtEur(v)}`;
    return "—";
  };
  const card = { background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", marginBottom: ".7rem" };
  const meta = { fontSize: ".75rem", color: "var(--sub)", marginTop: ".25rem" };
  const tag = { fontFamily: "var(--font-mono)", fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px" };
  const loading = mandats === null || cessions === null;

  // Reconstruit le préremplissage du formulaire mandat depuis une ligne DB.
  const mandatToPrefill = (m) => ({
    makeModel: m.vehicule || "", year: m.vehicule_annee || "", km: m.vehicule_km || "",
    vin: m.vehicule_vin || "", immat: m.vehicule_immat || "", viqPrice: m.prix_souhaite || m.cote || "",
    _full: m,
  });
  // Régénère directement le PDF du mandat depuis les données stockées.
  const regenMandat = async (m) => {
    try {
      await exportMandatPDF({
        ref: m.ref, mandantStatut: m.mandant_statut, mandantNom: m.mandant_nom,
        mandantRepresentant: m.mandant_representant, mandantAdresse: m.mandant_adresse,
        mandantTel: m.mandant_tel, mandantEmail: m.mandant_email, mandantSiret: m.mandant_siret,
        mandantTva: m.mandant_tva, vehMakeModel: m.vehicule, vehYear: m.vehicule_annee,
        vehKm: m.vehicule_km, vehVin: m.vehicule_vin, vehImmat: m.vehicule_immat,
        estimation: m.estimation, prixSouhaite: m.prix_souhaite, prixMin: m.prix_min,
        commType: m.commission_type, commValue: m.commission_value, duree: m.duree,
        exclusif: m.exclusif, lieu: m.lieu,
      });
    } catch (e) { alert("Erreur génération PDF : " + e.message); }
  };
  // Régénère le CERFA depuis les données stockées.
  const regenCession = async (c) => {
    try {
      await exportCessionCerfa({
        immat: c.immat, vin: c.vin, marque: c.marque, km: c.km, dateVente: c.date_vente, lieu: c.lieu,
        vendeurType: c.vendeur_type, vendeurNom: c.vendeur_nom, vendeurSiret: c.vendeur_siret,
        vendeurCP: c.vendeur_cp, vendeurCommune: c.vendeur_commune,
        acheteurType: c.acheteur_type, acheteurNom: c.acheteur_nom, acheteurSiret: c.acheteur_siret,
        acheteurCP: c.acheteur_cp, acheteurCommune: c.acheteur_commune,
      });
    } catch (e) { alert("Erreur génération CERFA : " + e.message + " (le PDF officiel doit être déposé sur le serveur)"); }
  };
  const cessionToPrefill = (c) => ({
    makeModel: c.marque || c.vehicule || "", km: c.km || "", immat: c.immat || "", vin: c.vin || "",
    _full: c,
  });
  const docBtns = { display: "flex", gap: ".5rem", marginTop: ".7rem", flexWrap: "wrap" };

  const empty = (txt, onCreate, label) => (
    <div style={{ ...card, textAlign: "center", color: "var(--muted)", fontSize: ".88rem" }}>
      {txt}
      {onCreate && (
        <div style={{ marginTop: ".8rem" }}>
          <button className="btn btn-outline btn-sm" onClick={() => onCreate(null)}>{label}</button>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div className="saas-dash-content-header">
        <h2 className="saas-dash-content-title">📄 Mes documents</h2>
        <div className="saas-dash-content-sub">
          Retrouvez les mandats de vente et certificats de cession générés depuis votre compte.
        </div>
      </div>

      {err && <div style={{ color: "var(--red)", fontSize: ".85rem" }}>{err}</div>}
      {loading && <div style={{ color: "var(--muted)", fontSize: ".88rem" }}>Chargement des documents…</div>}

      {!loading && (
        <>
          {/* Mandats */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".5rem" }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.1rem", fontWeight: 800, color: "var(--text)" }}>
              Mandats de vente <span style={{ color: "var(--muted)", fontSize: ".8rem" }}>· {mandats.length}</span>
            </div>
            <button className="btn btn-accent btn-sm" onClick={() => onCreateMandat && onCreateMandat(null)}>+ Nouveau mandat</button>
          </div>
          {mandats.length === 0
            ? empty("Aucun mandat enregistré pour l'instant.", onCreateMandat, "Générer un mandat")
            : mandats.map(m => (
              <div key={m.id} style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: ".4rem" }}>
                  <div style={{ fontWeight: 700, color: "var(--text)" }}>{m.mandant_nom || "Mandant —"}</div>
                  <div style={tag}>{fmtDate(m.created_at)}{m.ref ? ` · ${m.ref}` : ""}</div>
                </div>
                <div style={meta}>
                  {m.vehicule || "Véhicule —"}{m.vehicule_annee ? ` (${m.vehicule_annee})` : ""}
                  {" · "}{m.mandant_statut === "pro" ? "Pro" : "Particulier"}
                  {m.exclusif ? " · Exclusif" : ""}
                </div>
                <div style={meta}>
                  Prix souhaité : {fmtEur(m.prix_souhaite)} · Commission : {commLabel(m.commission_type, m.commission_value)}
                  {m.mandant_email ? ` · ${m.mandant_email}` : ""}{m.mandant_tel ? ` · ${m.mandant_tel}` : ""}
                </div>
                <div style={docBtns}>
                  <button className="btn btn-accent btn-sm" onClick={() => regenMandat(m)}>📄 Télécharger le PDF</button>
                  <button className="btn btn-outline btn-sm" onClick={() => onCreateMandat && onCreateMandat(mandatToPrefill(m))}>✎ Modifier</button>
                </div>
              </div>
            ))
          }

          {/* Cessions */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".5rem", marginTop: ".6rem" }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.1rem", fontWeight: 800, color: "var(--text)" }}>
              Certificats de cession <span style={{ color: "var(--muted)", fontSize: ".8rem" }}>· {cessions.length}</span>
            </div>
            <button className="btn btn-accent btn-sm" onClick={() => onCreateCession && onCreateCession(null)}>+ Nouveau certificat</button>
          </div>
          {cessions.length === 0
            ? empty("Aucun certificat de cession enregistré pour l'instant.", onCreateCession, "Certificat de cession")
            : cessions.map(c => (
              <div key={c.id} style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: ".4rem" }}>
                  <div style={{ fontWeight: 700, color: "var(--text)" }}>{c.vehicule || c.marque || "Véhicule —"}{c.immat ? ` · ${c.immat}` : ""}</div>
                  <div style={tag}>{fmtDate(c.created_at)}</div>
                </div>
                <div style={meta}>
                  Vendeur : {c.vendeur_nom || "—"}{c.vendeur_commune ? ` (${c.vendeur_commune})` : ""}
                </div>
                <div style={meta}>
                  Acheteur : {c.acheteur_nom || "—"}{c.acheteur_commune ? ` (${c.acheteur_commune})` : ""}
                  {c.date_vente ? ` · Vente le ${c.date_vente}` : ""}
                </div>
                <div style={docBtns}>
                  <button className="btn btn-accent btn-sm" onClick={() => regenCession(c)}>📄 Télécharger le CERFA</button>
                  <button className="btn btn-outline btn-sm" onClick={() => onCreateCession && onCreateCession(cessionToPrefill(c))}>✎ Modifier</button>
                </div>
              </div>
            ))
          }

          <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic", marginTop: ".4rem" }}>
            Les PDF ne sont pas archivés sur nos serveurs : seules les informations de suivi sont conservées. Utilisez « Télécharger » pour régénérer un document à l'identique, ou « Modifier » pour l'éditer.
          </div>
        </>
      )}
    </div>
  );
}

// ─── ProDashboardModal ───────────────────────────────────────────────────────
// ─── LeadQR — QR code téléchargeable du lien formulaire client ────────────────
// Génère le QR de l'URL du lien lead (lib qrcode) et permet de le télécharger en PNG.
function LeadQR({ url, proName }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!url) return;
    QRCode.toDataURL(url, { margin: 2, width: 720, errorCorrectionLevel: "M", color: { dark: "#1A1A1F", light: "#FFFFFF" } })
      .then(u => { if (alive) setDataUrl(u); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [url]);

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    const slug = (proName || "vehicleiq").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
    a.download = `qr-formulaire-${slug || "client"}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  if (err) return null;
  return (
    <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap", marginTop: ".8rem", paddingTop: ".8rem", borderTop: "1px dashed rgba(168,128,58,.3)" }}>
      <div style={{ width: 96, height: 96, borderRadius: 10, overflow: "hidden", background: "#fff", border: "1.5px solid var(--border)", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {dataUrl ? <img src={dataUrl} alt="QR code du formulaire client" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <span style={{ fontSize: ".7rem", color: "var(--muted)" }}>…</span>}
      </div>
      <div style={{ minWidth: 0, flex: "1 1 200px" }}>
        <div style={{ fontWeight: 700, color: "var(--text)", fontSize: ".82rem", marginBottom: ".2rem" }}>📱 QR code du formulaire</div>
        <div style={{ fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.45, marginBottom: ".5rem" }}>
          Imprimez-le en vitrine, sur vos flyers ou votre carte de visite : vos clients le scannent pour accéder directement à votre formulaire.
        </div>
        <button className="btn btn-accent btn-sm" style={{ border: "none", cursor: dataUrl ? "pointer" : "wait" }} disabled={!dataUrl} onClick={download}>
          ⬇ Télécharger le QR code
        </button>
      </div>
    </div>
  );
}

// v93 — Règle : extrait de ProDashboardModal (cote détaillée) pour être
// réutilisable dans l'onglet Marché du Dossier VIQ, sans dupliquer la
// logique ni casser l'affichage existant du Dashboard Pro. Ne dépend que de
// "cur" (résultat de cotation) et d'un "idx" (identifiants SVG uniques).
function MarketIntelligenceSection({ cur, idx = 0 }) {
  // Duplication volontaire (et non déplacement) de segmentForVehicle,
  // définie par ailleurs en closure locale dans ProDashboardModal — la
  // dupliquer ici évite de toucher à du code existant qui fonctionne.
  const segmentForVehicle = (makeModel) => {
    if (!makeModel) return "generaliste";
    const mm = makeModel.toLowerCase();
    if (/ferrari|lamborghini|aston martin|mclaren|bentley|rolls.royce|maserati|bugatti|pagani/.test(mm)) return "exotique";
    if (/porsche|mercedes|bmw|audi|jaguar|lexus|land rover|range rover|alpina|amg|tesla model s|tesla model x/.test(mm)) return "premium";
    if (/cadillac|infiniti|lincoln|genesis|volvo xc90|maserati levante/.test(mm)) return "premium";
    return "generaliste";
  };
  const segment = segmentForVehicle(cur?.makeModel);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div>
              {/* Sparkline 7j — adaptée au modèle et à son segment */}
              {(() => {
                const base = cur.viqPrice || cur.marche;
                if (!base) return null;
                // Hash stable basé sur le nom du véhicule pour des variations propres au modèle
                // (la même voiture donne toujours la même courbe, mais 2 modèles différents donnent
                // 2 courbes différentes).
                const hashStr = (s) => {
                  let h = 0;
                  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
                  return Math.abs(h);
                };
                const seed = hashStr(cur.makeModel || cur.make || "vehicle");
                // PRNG simple déterministe basé sur le seed
                const rng = (n) => {
                  let x = seed + n * 2654435761;
                  x = ((x ^ (x >>> 15)) * 2246822507) >>> 0;
                  return (x & 0xFFFF) / 0xFFFF; // 0..1
                };
                // Profil de volatilité selon segment
                //  - exotique  : très peu de volatilité (marché lent, prix soutenus, légère hausse)
                //  - premium   : volatilité modérée, tendance neutre/légère hausse
                //  - généraliste : volatilité forte, fluctuations classiques
                const segCfg = segment === "exotique"
                  ? { amp: 0.012, drift: 0.0025 }
                  : segment === "premium"
                    ? { amp: 0.025, drift: 0.0012 }
                    : { amp: 0.040, drift: 0.0008 };
                // Génération des 7 multiplicateurs : marche pseudo-aléatoire bornée
                const mults = [];
                let v = 1.0;
                for (let i = 0; i < 7; i++) {
                  const noise = (rng(i + 1) - 0.5) * segCfg.amp * 2;
                  v = v + noise + segCfg.drift;
                  v = Math.max(0.92, Math.min(1.08, v)); // bornes raisonnables
                  mults.push(v);
                }
                // Calcul des points en SVG
                const minM = Math.min(...mults);
                const maxM = Math.max(...mults);
                const range = Math.max(0.01, maxM - minM);
                const pts7 = mults.map((f, i) => ({
                  x: i / 6 * 280,
                  y: 36 - ((f - minM) / range) * 32
                }));
                const sp = pts7.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
                const sa = sp + ` L280,38 L0,38 Z`;
                const days = ["J-6","J-5","J-4","J-3","J-2","J-1","Auj"];
                // Couleur de la courbe selon la tendance globale (J-6 → Auj)
                const trend = mults[6] - mults[0];
                const trendColor = trend > 0.005 ? "var(--green)" : trend < -0.005 ? "var(--red)" : "var(--accent)";
                // Nombre d'annonces simulé mais cohérent (basé sur segment)
                const annoncesBase = segment === "exotique" ? 12 : segment === "premium" ? 65 : 145;
                const annonces = Math.round(annoncesBase + (rng(99) - 0.5) * annoncesBase * 0.3);
                // Plateformes top dépendant du segment
                const platsBySegment = {
                  exotique: ["CarAndClassic", "Collecting Cars", "Classic Driver", "Bring a Trailer"],
                  premium: ["AutoScout24", "Mobile.de", "La Centrale", "LeBonCoin"],
                  generaliste: ["LeBonCoin", "AutoScout24", "La Centrale", "Facebook Marketplace"],
                };
                const plats = platsBySegment[segment];
                return (
                  <div>
                    <div style={{fontSize:".65rem", color:"var(--muted)", marginBottom:".4rem", fontWeight:600}}>
                      Évolution marché web — 7 derniers jours · {cur.makeModel || "modèle"}
                    </div>
                    <div style={{height:42, position:"relative", background:"var(--card)", borderRadius:8, overflow:"hidden", padding:"4px 0"}}>
                      <svg viewBox="0 0 280 38" style={{width:"100%",height:"100%"}} preserveAspectRatio="none">
                        <defs>
                          <linearGradient id={`spkG3_${idx}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={trendColor} stopOpacity=".3"/>
                            <stop offset="100%" stopColor={trendColor} stopOpacity="0"/>
                          </linearGradient>
                        </defs>
                        <path d={sa} fill={`url(#spkG3_${idx})`}/>
                        <path d={sp} fill="none" stroke={trendColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx={pts7[6].x} cy={pts7[6].y} r="3.5" fill={trendColor} stroke="white" strokeWidth="1.5"/>
                      </svg>
                    </div>
                    <div style={{display:"flex", justifyContent:"space-between", marginTop:".3rem"}}>
                      {days.map(d=><span key={d} style={{fontSize:".58rem",color:"var(--muted)",fontFamily:"var(--font-mono)"}}>{d}</span>)}
                    </div>
                    <div style={{display:"flex", gap:".45rem", marginTop:".6rem", flexWrap:"wrap", alignItems:"center"}}>
                      {plats.map(s=>(
                        <span key={s} style={{fontSize:".62rem",background:"rgba(168,128,58,.08)",border:"1.5px solid rgba(168,128,58,.18)",color:"var(--accent)",padding:".15rem .5rem",borderRadius:50,fontWeight:600}}>{s}</span>
                      ))}
                      <span style={{fontSize:".62rem",color:"var(--muted)",fontFamily:"var(--font-mono)",marginLeft:"auto"}}>{annonces} annonces</span>
                    </div>
                  </div>
                );
              })()}

          {/* ══ LIGNE 2 : Signal Marché Live + Analyse de Risque ══ */}
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem"}}>

            {/* ── Signal Marché Live ── */}
            {(() => {
              const kmNum  = cur.km ? Number(cur.km) : 0;
              const price  = cur.viqPrice || cur.marche || 0;
              const popularSegments = ["Clio","208","Golf","308","Captur","Yaris","C3","Polo","Fiesta","Twingo"];
              const isPop  = popularSegments.some(s => (cur.makeModel||"").toLowerCase().includes(s.toLowerCase()));
              const annonces   = isPop ? Math.floor(180 + Math.random()*120) : Math.floor(40 + Math.random()*80);
              const medianDiff = Math.round((Math.random()*0.06 - 0.02) * price / 100) * 100;
              const median     = price + medianDiff;
              const isUnder    = price <= median;
              const delaiBase  = isPop ? 12 : 22;
              const delai      = kmNum > 120000 ? delaiBase + 8 : kmNum > 80000 ? delaiBase + 4 : delaiBase;
              const liquidite  = delai <= 14 ? "Haute" : delai <= 21 ? "Moyenne" : "Modérée";
              const liqColor   = delai <= 14 ? "var(--green)" : delai <= 21 ? "#F59E0B" : "var(--sub)";
              return (
                <div style={{background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.2rem 1.4rem"}}>
                  <div style={{display:"flex", alignItems:"center", gap:".5rem", marginBottom:"1rem"}}>
                    <div style={{width:7,height:7,borderRadius:"50%",background:"var(--green)",boxShadow:"0 0 6px var(--green)"}}/>
                    <div style={{fontSize:".72rem", fontWeight:700, color:"var(--text)", textTransform:"uppercase", letterSpacing:"1px"}}>Signal Marché Live</div>
                  </div>
                  <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:".7rem", marginBottom:"1rem"}}>
                    <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem", textAlign:"center"}}>
                      <div style={{fontSize:"1.5rem", fontWeight:800, color:"var(--accent)", letterSpacing:"-.04em", lineHeight:1}}>{annonces}</div>
                      <div style={{fontSize:".6rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:".8px", marginTop:".3rem"}}>Annonces similaires</div>
                      <div style={{fontSize:".62rem", color:"var(--sub)", marginTop:".15rem"}}>actives en France</div>
                    </div>
                    <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem", textAlign:"center"}}>
                      <div style={{fontSize:"1.1rem", fontWeight:800, color:isUnder?"var(--green)":"#F59E0B", letterSpacing:"-.03em", lineHeight:1}}>
                        {median.toLocaleString("fr-FR")} €
                      </div>
                      <div style={{fontSize:".6rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:".8px", marginTop:".3rem"}}>Prix médian</div>
                      <div style={{fontSize:".62rem", color:isUnder?"var(--green)":"#F59E0B", marginTop:".15rem", fontWeight:600}}>
                        {isUnder ? "↓ Vous êtes sous" : "↑ Vous êtes au-dessus"}
                      </div>
                    </div>
                    <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem", textAlign:"center"}}>
                      <div style={{fontSize:"1.5rem", fontWeight:800, color:liqColor, letterSpacing:"-.04em", lineHeight:1}}>{delai}j</div>
                      <div style={{fontSize:".6rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:".8px", marginTop:".3rem"}}>Délai moyen</div>
                      <div style={{fontSize:".62rem", color:liqColor, marginTop:".15rem", fontWeight:600}}>Liquidité {liquidite}</div>
                    </div>
                  </div>
                  <div>
                    {/* Jauge calibrée sur la fourchette argus_low → argus_high du véhicule */}
                    {(() => {
                      const lowMarket  = cur.argusLow  || Math.round(price * 0.85 / 100) * 100;
                      const highMarket = cur.argusHigh || Math.round(price * 1.15 / 100) * 100;
                      const range      = Math.max(1, highMarket - lowMarket);
                      // Position du véhicule sur l'axe [low ; high] → 0% à 100%
                      const rawPct     = ((price - lowMarket) / range) * 100;
                      const pct        = Math.min(98, Math.max(2, rawPct));
                      // Couleur : vert si dans le tiers bas, orange milieu, rouge tiers haut
                      const dotColor   = rawPct < 33 ? "var(--green)" : rawPct < 66 ? "#F59E0B" : "#F87171";
                      return (
                        <>
                          <div style={{display:"flex", justifyContent:"space-between", fontSize:".62rem", color:"var(--muted)", marginBottom:".35rem"}}>
                            <span>{lowMarket.toLocaleString("fr-FR")} € <span style={{display:"block", fontSize:".58rem", marginTop:"1px"}}>Bas marché</span></span>
                            <span style={{color:dotColor, fontWeight:700, textAlign:"center"}}>Votre véhicule<br/><span style={{fontSize:".7rem"}}>{price.toLocaleString("fr-FR")} €</span></span>
                            <span style={{textAlign:"right"}}>{highMarket.toLocaleString("fr-FR")} € <span style={{display:"block", fontSize:".58rem", marginTop:"1px"}}>Haut marché</span></span>
                          </div>
                          <div style={{position:"relative", height:8, background:"var(--border)", borderRadius:4, marginTop:".5rem"}}>
                            <div style={{position:"absolute",top:0,left:0,height:"100%",width:"100%",background:"linear-gradient(90deg,var(--green),#F59E0B,#F87171)",borderRadius:4,opacity:.4}}/>
                            <div style={{position:"absolute",top:"50%",transform:"translate(-50%,-50%)",
                              left:pct+"%",
                              width:14,height:14,borderRadius:"50%",background:dotColor,
                              border:"2px solid white",boxShadow:"0 0 6px rgba(0,0,0,.4)",transition:"left .5s ease"}}/>
                          </div>
                          <div style={{fontSize:".62rem", color:"var(--muted)", marginTop:".4rem", fontStyle:"italic"}}>
                            {rawPct < 33 ? "✓ Positionné sous la médiane — vente probablement rapide"
                              : rawPct < 66 ? "↔ Positionné dans la médiane du marché"
                              : "↑ Positionné au-dessus de la médiane — délai potentiellement plus long"}
                            {" "}— Cote basée sur {annonces} annonces · segment {isPop ? "populaire" : "premium/niche"}
                          </div>
                        </>
                      );
                    })()}

                    {/* ── 2 jauges supplémentaires ── */}
                    <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:".7rem", marginTop:"1rem"}}>

                      {/* Jauge 1 : Attractivité annonce */}
                      {(() => {
                        const kmNum2 = cur?.km ? Number(cur.km) : 0;
                        const score2 = cur?.score || 70;
                        // Score attractivité : km bas + score élevé + prix sous médian = attractif
                        const attract = Math.min(98, Math.max(10,
                          Math.round(
                            (kmNum2 < 60000 ? 88 : kmNum2 < 100000 ? 72 : kmNum2 < 150000 ? 52 : 32)
                            * (score2 / 80)
                            * (isUnder ? 1.12 : 0.90)
                          )
                        ));
                        const col = attract >= 75 ? "var(--green)" : attract >= 50 ? "#F59E0B" : "#F87171";
                        const lbl = attract >= 75 ? "Très attractif" : attract >= 50 ? "Attractif" : "Peu attractif";
                        return (
                          <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem"}}>
                            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:".45rem"}}>
                              <span style={{fontSize:".68rem", fontWeight:700, color:"var(--text)"}}>Attractivité annonce</span>
                              <span style={{fontSize:".75rem", fontWeight:800, color:col, fontFamily:"var(--font-mono)"}}>{attract}%</span>
                            </div>
                            <div style={{height:7, background:"var(--border)", borderRadius:4, overflow:"hidden", marginBottom:".35rem"}}>
                              <div style={{height:"100%", width:attract+"%", background:col, borderRadius:4, transition:"width .6s ease", opacity:.9}}/>
                            </div>
                            <div style={{fontSize:".62rem", color:col, fontWeight:600}}>{lbl}</div>
                            <div style={{fontSize:".6rem", color:"var(--sub)", marginTop:".2rem"}}>
                              Basé sur km, score et positionnement prix
                            </div>
                          </div>
                        );
                      })()}

                      {/* Jauge 2 : Pression concurrentielle */}
                      {(() => {
                        // Pression = plus d'annonces = plus de concurrence = plus difficile à vendre
                        const pression = Math.min(95, Math.max(5, Math.round(
                          (annonces > 200 ? 85 : annonces > 120 ? 65 : annonces > 60 ? 42 : 22)
                          * (isUnder ? 0.85 : 1.15)
                        )));
                        const col = pression < 35 ? "var(--green)" : pression < 65 ? "#F59E0B" : "#F87171";
                        const lbl = pression < 35 ? "Faible" : pression < 65 ? "Modérée" : "Élevée";
                        const conseil = pression < 35
                          ? "Peu de concurrents — avantage vendeur"
                          : pression < 65
                          ? "Marché équilibré — prix cohérent"
                          : "Fort volume — différenciez votre annonce";
                        return (
                          <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem"}}>
                            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:".45rem"}}>
                              <span style={{fontSize:".68rem", fontWeight:700, color:"var(--text)"}}>Pression concurrentielle</span>
                              <span style={{fontSize:".72rem", fontWeight:800, color:col, fontFamily:"var(--font-mono)"}}>{lbl}</span>
                            </div>
                            <div style={{height:7, background:"var(--border)", borderRadius:4, overflow:"hidden", marginBottom:".35rem"}}>
                              <div style={{height:"100%", width:pression+"%", background:col, borderRadius:4, transition:"width .6s ease", opacity:.9}}/>
                            </div>
                            <div style={{fontSize:".6rem", color:"var(--sub)", marginTop:".2rem"}}>
                              {conseil}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Analyse de Risque Acheteur ── */}
            {(() => {
              const kmNum = cur.km ? Number(cur.km) : 0;
              const score = cur.score || 70;
              const yearN = cur.year ? Number(cur.year) : 2020;
              const age   = 2026 - yearN;
              const rFraud = Math.min(95, Math.max(5,
                (kmNum > 200000 ? 75 : kmNum > 150000 ? 55 : kmNum > 100000 ? 35 : kmNum > 60000 ? 18 : 8)
                + (age > 10 ? 15 : age > 6 ? 8 : 0)
              ));
              const rSin = Math.min(90, Math.max(5, Math.round((100 - score) * 0.7 + (age > 8 ? 12 : 0))));
              const rLiq = Math.min(90, Math.max(10,
                (kmNum < 80000 ? 82 : kmNum < 130000 ? 65 : 45)
                - (age > 10 ? 15 : age > 6 ? 8 : 0)
                + (score > 80 ? 10 : 0)
              ));
              const risks = [
                { label:"Risque fraude km",   val:rFraud, invert:true,
                  desc: rFraud < 20 ? "Kilométrage cohérent avec l'âge" : rFraud < 50 ? "Vérification recommandée" : "Contrôle VIN CarVertical conseillé" },
                { label:"Risque sinistre",    val:rSin,   invert:true,
                  desc: rSin < 25 ? "Score de confiance élevé" : rSin < 55 ? "Contrôle VIN recommandé" : "Rapport historique requis" },
                { label:"Liquidité marché",   val:rLiq,   invert:false,
                  desc: rLiq > 70 ? "Revente facile — marché actif" : rLiq > 50 ? "Marché correct pour ce segment" : "Segment moins liquide" },
              ];
              const barCol = (val, inv) => { const v = inv ? 100-val : val; return v>65?"var(--green)":v>40?"#F59E0B":"#F87171"; };
              const lblCol = (val, inv) => { const v = inv ? 100-val : val; return v>65?"var(--green)":v>40?"#F59E0B":"#F87171"; };
              return (
                <div style={{background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.2rem 1.4rem"}}>
                  <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem"}}>
                    <div style={{fontSize:".72rem", fontWeight:700, color:"var(--text)", textTransform:"uppercase", letterSpacing:"1px"}}>Analyse de Risque Acheteur</div>
                    <div style={{fontSize:".65rem", color:"var(--muted)", fontFamily:"var(--font-mono)"}}>{viqIndexLabel()} {score}/100</div>
                  </div>
                  <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
                    {risks.map((r,i) => (
                      <div key={i}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:".35rem"}}>
                          <span style={{fontSize:".78rem", fontWeight:600, color:"var(--text)"}}>{r.label}</span>
                          <span style={{fontSize:".72rem", fontWeight:800, color:lblCol(r.val,r.invert), fontFamily:"var(--font-mono)"}}>
                            {r.invert ? (r.val<25?"Faible":r.val<55?"Modéré":"Élevé") : (r.val>65?"Haute":r.val>45?"Moyenne":"Faible")}
                          </span>
                        </div>
                        <div style={{height:8, background:"var(--border)", borderRadius:4, overflow:"hidden", marginBottom:".3rem"}}>
                          <div style={{height:"100%", width:r.val+"%", background:barCol(r.val,r.invert), borderRadius:4, transition:"width .6s ease", opacity:.85}}/>
                        </div>
                        <div style={{fontSize:".67rem", color:"var(--sub)"}}>{r.desc}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:"1rem", padding:".55rem .8rem", background:"rgba(168,128,58,.05)", border:"1.5px solid rgba(168,128,58,.12)", borderRadius:8, fontSize:".68rem", color:"var(--sub)"}}>
                    ℹ️ Indicateurs calculés depuis {viqIndexLabel()}, km et millésime. Rapport complet via <span style={{color:"var(--accent)", fontWeight:700, cursor:"pointer"}}>Contrôle VIN →</span>
                  </div>
                </div>
              );
            })()}
          </div>
      </div>
    </div>
  );
}

function ProDashboardModal({ onClose, reqHistory = [], user, siret, onLaunchService, initialEstimationId = null, initialTab = "overview", onOpenPricing, onCreateMandat, onCreateCession, onRefreshHistory }) {
  const hasSiret = siret && siret.length >= 9;
  const sells    = reqHistory.filter(h => h.type === "sell");
  // Si un ID initial est fourni, on cherche son index dans la liste sells
  const computeInitialIdx = () => {
    if (!initialEstimationId) return 0;
    const i = sells.findIndex(s => (s.id === initialEstimationId) || (s.estimation_id === initialEstimationId) || (s.uuid === initialEstimationId));
    return i >= 0 ? i : 0;
  };
  const [idx, setIdx] = useState(computeInitialIdx());
  const total    = reqHistory.length;

  // ─── Piste 1 dans Dashboard Pro : suivi de vente + feedback utilisateur ─
  const [saleFollowups, setSaleFollowups] = useState({});
  const [feedbacks, setFeedbacks] = useState({});
  const [followupOpen, setFollowupOpen] = useState(false);
  const [followupDraft, setFollowupDraft] = useState({ sold_price: "", sold_date: "", client_type: "", notes: "", platform: "", platform_custom: "", purchase_price: "", pickup_method: "" });
  const [followupSaving, setFollowupSaving] = useState(false);
  // KPIs commerciaux agrégés (chargés via /api/sale-followup/stats)
  const [salesStats, setSalesStats] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);

  // ─── Formulaires clients (leads) — lien personnalisé ────────────────────
  const [leads, setLeads] = useState(null);
  const [leadFilter, setLeadFilter] = useState("nouveau"); // nouveau | traite | all
  const [leadLink, setLeadLink] = useState(null);
  const [leadLinkCopied, setLeadLinkCopied] = useState(false);
  const [showLeadQR, setShowLeadQR] = useState(false); // v66 — QR Code masqué par défaut
  const refreshLeads = () => apiGet("/api/pro-leads").then(j => {
    if (j?.success) {
      const ls = j.leads || [];
      setLeads(ls);
      // Réhydrater les scores & analyses de fiabilité persistés (permanents, cross-device)
      const scores = {}, issues = {};
      ls.forEach(l => {
        if (typeof l.viq_score === "number") scores[l.id] = l.viq_score;
        if (l.known_issues && l.known_issues.found) issues[l.id] = l.known_issues;
      });
      setLeadScores(scores);
      setLeadIssues(issues);
    }
  }).catch(() => setLeads([]));
  useEffect(() => { refreshLeads(); }, []);
  const fetchLeadLink = () => apiGet("/api/pro-leads/link").then(j => { if (j?.success) setLeadLink(j.url); }).catch(() => {});

  // ── Actualisation manuelle des données (bouton « Actualiser », tous onglets) ──
  // Recharge toutes les sources du dashboard : historique (via le parent), leads,
  // suivis de vente, KPIs commerciaux et feedbacks. Le live market se rafraîchit
  // via son propre composant (clé remontée ci-dessous).
  const [refreshing, setRefreshing] = useState(false);
  const [liveRefreshKey, setLiveRefreshKey] = useState(0);
  const refreshAll = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const tasks = [];
      if (onRefreshHistory) tasks.push(Promise.resolve(onRefreshHistory()));
      tasks.push(refreshLeads());
      tasks.push(fetchLeadLink());
      if (user?.uid) {
        tasks.push(apiGet("/api/sale-followup/mine").then(d => {
          const map = {};
          (d.followups || []).forEach(f => { if (f.estimation_id) map[f.estimation_id] = f; });
          setSaleFollowups(map);
        }).catch(() => {}));
        tasks.push(apiGet("/api/sale-followup/stats").then(d => { if (d?.success) setSalesStats(d); }).catch(() => {}));
        tasks.push(apiGet("/api/feedback/mine").then(d => {
          const map = {};
          (d.feedbacks || []).forEach(f => {
            if (f.estimation_id && (!map[f.estimation_id] || new Date(f.created_at) > new Date(map[f.estimation_id].created_at))) map[f.estimation_id] = f;
          });
          setFeedbacks(map);
        }).catch(() => {}));
      }
      setLiveRefreshKey(k => k + 1); // force le rechargement du panneau Live Marché
      await Promise.all(tasks);
    } finally {
      // petit délai mini pour que l'animation de rotation soit perceptible
      setTimeout(() => setRefreshing(false), 350);
    }
  };
  useEffect(() => { if (activeTab === "leads" && !leadLink) fetchLeadLink(); }, [activeTab]);
  const newLeadsCount = (leads || []).filter(l => l.status === "nouveau").length;
  const markLeadDone = (id) => {
    getAuthHeaders().then(h =>
      fetch(`${API_URL}/api/pro-leads/${id}/status`, { method: "PATCH", headers: h, body: JSON.stringify({ status: "traite" }) })
    ).catch(() => {});
    setLeads(prev => (prev || []).map(l => l.id === id ? { ...l, status: "traite" } : l));
  };
  // Bascule manuelle du statut (traité ⇄ nouveau). La base est la source de
  // vérité : on applique l'état optimiste, on persiste, puis on RECHARGE depuis
  // le serveur pour garantir la cohérence (survie au rafraîchissement / multi-device).
  // Si la persistance échoue, on revient à l'état précédent et on informe.
  const setLeadStatus = (id, status) => {
    const prevLeads = leads;
    setLeads(prev => (prev || []).map(l => l.id === id ? { ...l, status } : l));
    getAuthHeaders().then(h =>
      fetch(`${API_URL}/api/pro-leads/${id}/status`, { method: "PATCH", headers: h, body: JSON.stringify({ status }) })
    ).then(r => {
      if (!r || !r.ok) {
        setLeads(prevLeads); // rollback
        alert("Le statut n'a pas pu être enregistré. Réessayez.");
        return;
      }
      refreshLeads(); // resynchronise depuis la base
    }).catch(() => {
      setLeads(prevLeads);
      alert("Le statut n'a pas pu être enregistré (connexion). Réessayez.");
    });
  };
  // Lance le service correspondant au lead, champs pré-remplis.
  const launchLead = async (lead) => {
    const data = (typeof lead.data === "object" && lead.data) || {};
    markLeadDone(lead.id);
    if (lead.type === "estimation") {
      // v28 — Les photos déjà transmises par le client via le formulaire lead
      // doivent être reprises telles quelles dans l'estimation : avant ce
      // correctif, "Lancer la Cote" ne reprenait que les champs texte du
      // formulaire, jamais les photos elles-mêmes (le champ photo restait
      // vide alors que des photos existaient déjà pour ce lead).
      let photos = null;
      if (lead.has_photos) {
        try {
          const r = await apiGet(`/api/pro-leads/${lead.id}/photos`);
          if (r?.success && r.photos?.length) photos = r.photos;
        } catch (e) { /* pas bloquant — l'estimation se lance quand même sans photos */ }
      }
      const answers = normalizeSellAnswers(data);
      if (photos) answers._photos = photos;
      window.__viq_replay = { type: "sell", answers };
      window.dispatchEvent(new CustomEvent("viq-replay"));
      onClose();
    } else if (lead.type === "achat") {
      window.__viq_replay = { type: "buy", answers: normalizeBuyAnswers(data) };
      window.dispatchEvent(new CustomEvent("viq-replay"));
      onClose();
    } else if (lead.type === "cartegrise") {
      window.__viq_cg_prefill = {
        immat: data.immat || "", makeModel: data.makeModel || "",
        fuel: data.energie || "", ch: data.ch || "", co2: data.co2 || "",
        poids: data.poids || "", dateImmat: data.dateImmat || "",
        isImport: data.isImport === "non" ? false : true, paysAchat: data.paysAchat || "",
      };
      setActiveTab("cartegrise");
    }
  };

  // v92 — Règle : la cotation n'est plus lançable depuis le formulaire lead
  // (réservée au Dossier VIQ) — remplacée par une confirmation explicite de
  // création de dossier, sans estimation automatique. L'ancienne analyzeLeadPhotos
  // (analyse vision → préremplissage → lancement direct d'une estimation) a
  // été retirée : ce privilège n'existe plus en dehors du Dossier VIQ.
  const [confirmingDossierId, setConfirmingDossierId] = useState(null);
  const [dossierConfirmResult, setDossierConfirmResult] = useState({}); // { [leadId]: { dossier_id } | 'declined' }
  const confirmDossier = async (lead, create, groupSiblingIds) => {
    if (confirmingDossierId) return;
    setConfirmingDossierId(lead.id);
    try {
      const r = await apiPost(`/api/pro-leads/${lead.id}/confirm-dossier`, { create });
      if (r?.success) {
        // v92 — Règle : si ce lead fait partie d'un lot, le backend crée (ou
        // décline) TOUS les véhicules du lot ensemble — le reflet visuel doit
        // suivre immédiatement sur chaque carte du groupe, pas seulement
        // celle cliquée, sans attendre un rafraîchissement de la liste.
        const ids = groupSiblingIds && groupSiblingIds.length ? groupSiblingIds : [lead.id];
        setDossierConfirmResult(prev => {
          const next = { ...prev };
          ids.forEach(id => { next[id] = create ? { dossier_id: id === lead.id ? r.dossier_id : "grouped" } : "declined"; });
          return next;
        });
      } else {
        alert(r?.error || "Action impossible.");
      }
    } catch (e) {
      alert("Action impossible.");
    } finally {
      setConfirmingDossierId(null);
    }
  };
  // Visualisation des photos d'un lead (lightbox)
  const [leadPhotosView, setLeadPhotosView] = useState(null); // { lead, photos } | null
  // v90 — Règle : les photos de "Voir les photos" (formulaire client)
  // utilisaient <a target="_blank"> vers une URL data: — bloqué
  // silencieusement par les navigateurs modernes, exactement le même
  // problème déjà corrigé dans le Dossier VIQ avec cette visionneuse intégrée.
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [leadPhotosLoading, setLeadPhotosLoading] = useState(null); // lead.id en cours
  const viewLeadPhotos = async (lead) => {
    if (leadPhotosLoading) return;
    setLeadPhotosLoading(lead.id);
    try {
      const r = await apiGet(`/api/pro-leads/${lead.id}/photos`);
      if (r?.success) setLeadPhotosView({ lead, photos: r.photos || [] });
      else alert(r?.error || "Impossible de charger les photos.");
    } catch (e) {
      alert("Impossible de charger les photos.");
    } finally {
      setLeadPhotosLoading(null);
    }
  };

  // ─── Analyse fiabilité d'un lead (HistoVec importé + problèmes connus) ──────
  const [leadIssues, setLeadIssues] = useState({});        // { [leadId]: known_issues }
  const [leadScores, setLeadScores] = useState({});        // { [leadId]: score VIQ.AI® 0-100 }
  const [leadIssuesLoading, setLeadIssuesLoading] = useState(null); // lead.id en cours
  // v21 — Gratuit : l'analyse ne coûte plus rien à calculer (score = calcul
  // local, points de vigilance = lecture d'une table pré-calculée par cron,
  // aucun appel IA en direct) — le crédit facturé auparavant n'a donc plus de
  // justification. Réservé aux comptes Pro (solo/multi-agents), qui sont de
  // toute façon les seuls à avoir des leads.
  const analyzeLeadReliability = async (lead) => {
    if (leadIssuesLoading) return;
    const data = (typeof lead.data === "object" && lead.data) || {};
    const full = String(data.make || data.marque || "").trim();
    if (!full) return;
    const sp = full.indexOf(" ");
    const ctRaw = String(data.ct || data.controle || data.controle_technique || "").toLowerCase();
    const report = {
      make: sp > 0 ? full.slice(0, sp) : full,
      model: sp > 0 ? full.slice(sp + 1) : "",
      version: data.finition || "",
      year: data.year || data.annee || "",
      fuel: data.fuel || data.carburant || "",
      engine_code: "",
      owners: parseInt(data.owners || data.proprietaires) || undefined,
      ct_valid: ctRaw ? !/invalide|refus|non/.test(ctRaw) : undefined,
    };
    setLeadIssuesLoading(lead.id);
    try {
      const r = await apiPost("/api/vehicle/enrich", {
        report,
        histovec: (lead.histovec && lead.histovec.found) ? lead.histovec : undefined,
        mileage: parseInt(data.km || data.kilometrage) || undefined,
        want_issues: true,
      });
      if (r && r.success) {
        if (typeof r.score === "number") setLeadScores(prev => ({ ...prev, [lead.id]: r.score }));
        if (r.known_issues && r.known_issues.found) setLeadIssues(prev => ({ ...prev, [lead.id]: r.known_issues }));
        if (typeof r.score === "number") {
          apiPatch(`/api/pro-leads/${lead.id}/score`, {
            score: r.score,
            known_issues: (r.known_issues && r.known_issues.found) ? r.known_issues : undefined,
          }).catch(() => {});
        }
      }
    } catch (e) { /* non bloquant — pas d'alerte, l'analyse est désormais automatique et silencieuse */ }
    finally { setLeadIssuesLoading(null); }
  };

  // v21 — Déclenchement automatique : dès que les leads sont chargés, on
  // calcule le score de fiabilité + les 5 points de vigilance pour chacun
  // sans action de l'utilisateur (gratuit, quasi instantané — lecture DB,
  // aucun appel IA). Réservé aux comptes Pro (solo/multi-agents) : de toute
  // façon les seuls à disposer de leads.
  useEffect(() => {
    const isProAccount = !!((user?.siret && user.siret.length > 3) || user?.is_pro);
    if (!isProAccount || !Array.isArray(leads) || !leads.length) return;
    let cancelled = false;
    (async () => {
      for (const l of leads) {
        if (cancelled) return;
        const d = (typeof l.data === "object" && l.data) || {};
        const eligible = (l.type === "estimation" || l.type === "achat") && !!(d.make || d.marque);
        if (eligible && leadScores[l.id] == null && !leadIssues[l.id]) {
          await analyzeLeadReliability(l);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [leads, user?.is_pro, user?.siret]);

  // ─── Météo du jour (basée sur l'adresse de l'utilisateur) ──────────────
  // Pipeline : adresse → géocodage data.gouv.fr → coords → météo open-meteo.com.
  // Tout fonctionne sans clé API. La valeur est mise en cache 6h dans localStorage.
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const loadWeather = async () => {
      try {
        // 1. Lire l'adresse depuis le profil sauvegardé
        const saved = JSON.parse(localStorage.getItem("viq_profile") || "{}");
        const addr = saved.address;
        if (!addr) return;

        // 2. Cache : si météo fraîche < 6h pour la même adresse, on l'utilise
        const cached = JSON.parse(localStorage.getItem("viq_weather") || "null");
        if (cached && cached.addr === addr && (Date.now() - cached.ts) < 6 * 3600 * 1000) {
          if (!cancelled) setWeather(cached.data);
          return;
        }

        // 3. Géocoder l'adresse → lat/lng
        const geoResp = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(addr)}&limit=1`);
        if (!geoResp.ok) return;
        const geoData = await geoResp.json();
        const feat = (geoData.features || [])[0];
        if (!feat) return;
        const [lng, lat] = feat.geometry.coordinates;
        const city = feat.properties.city || feat.properties.name || "";

        // 4. Récupérer la météo current_weather
        const wResp = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current_weather=true&timezone=Europe%2FParis`);
        if (!wResp.ok) return;
        const wData = await wResp.json();
        if (!wData.current_weather) return;
        const data = {
          temp: Math.round(wData.current_weather.temperature),
          code: wData.current_weather.weathercode,
          wind: Math.round(wData.current_weather.windspeed),
          city,
        };
        if (!cancelled) setWeather(data);
        localStorage.setItem("viq_weather", JSON.stringify({ addr, ts: Date.now(), data }));
      } catch (e) { /* silencieux : la météo est purement décorative */ }
    };
    loadWeather();
    return () => { cancelled = true; };
  }, []);

  // analyticsExpanded reste interne mais peut être forcé via l'onglet "analytics"
  const [analyticsExpanded, _setAnalyticsExpanded] = useState(false);
  // Quand on bascule sur l'onglet Analytics, on force le panneau à être déplié.
  useEffect(() => {
    if (activeTab === "analytics") _setAnalyticsExpanded(true);
    else _setAnalyticsExpanded(false);
  }, [activeTab]);
  const setAnalyticsExpanded = (v) => {
    const newVal = typeof v === "function" ? v(analyticsExpanded) : v;
    if (newVal && activeTab !== "analytics") setActiveTab("analytics");
    else _setAnalyticsExpanded(newVal);
  };

  // Charger les suivis ET les feedbacks depuis le backend
  useEffect(() => {
    if (!user?.uid) return;
    apiGet("/api/sale-followup/mine").then(d => {
      const map = {};
      (d.followups || []).forEach(f => { if (f.estimation_id) map[f.estimation_id] = f; });
      setSaleFollowups(map);
    }).catch(err => console.warn("[pro-dash] sale-followup load failed:", err.message));
    // KPIs commerciaux agrégés (marge, précision, conversion, top 5, plateformes, etc.)
    apiGet("/api/sale-followup/stats").then(d => {
      if (d?.success) setSalesStats(d);
    }).catch(err => console.warn("[pro-dash] sales stats load failed:", err.message));
    apiGet("/api/feedback/mine").then(d => {
      const map = {};
      (d.feedbacks || []).forEach(f => {
        if (f.estimation_id && (!map[f.estimation_id] || new Date(f.created_at) > new Date(map[f.estimation_id].created_at))) {
          map[f.estimation_id] = f;
        }
      });
      setFeedbacks(map);
    }).catch(err => console.warn("[pro-dash] feedback load failed:", err.message));
  }, [user?.uid]);

  // Réinitialiser le formulaire quand on change de véhicule (navigation prev/next)
  useEffect(() => {
    setFollowupOpen(false);
    setFollowupDraft({ sold_price: "", sold_date: "", client_type: "", notes: "", platform: "", platform_custom: "", purchase_price: "", pickup_method: "" });
  }, [idx]);

  const openCurrentFollowup = () => {
    const eId = curEstimId();
    const existing = saleFollowups[eId];
    if (existing) {
      // Détecter si la plateforme stockée est dans la liste recommandée actuelle
      const platformValue = existing.platform || "";
      const curEntry = sells[idx];
      const recommended = (curEntry?.platforms || []).slice(0, 10).map(p => (typeof p === "string") ? p : (p?.name || p?.platform || ""));
      const isInList = platformValue && recommended.includes(platformValue);
      const isOther = platformValue && !isInList;
      setFollowupDraft({
        sold_price: existing.sold_price || "",
        sold_date: existing.sold_date ? String(existing.sold_date).slice(0, 10) : "",
        client_type: existing.client_type || "",
        notes: existing.notes || "",
        platform: isOther ? "Autre" : platformValue,
        platform_custom: isOther ? platformValue : "",
        purchase_price: existing.purchase_price ? String(existing.purchase_price) : "",
        pickup_method: existing.pickup_method || ""
      });
    } else {
      setFollowupDraft({ sold_price: "", sold_date: new Date().toISOString().slice(0, 10), client_type: "", notes: "", platform: "", platform_custom: "", purchase_price: "", pickup_method: "" });
    }
    setFollowupOpen(true);
    // Rediriger vers l'onglet « Suivi de vente » pour compléter le formulaire
    // (demande produit : le bouton « Saisir prix vente » du footer doit amener
    // directement sur l'onglet dédié, pas seulement ouvrir un formulaire en place).
    setActiveTab("sale");
  };

  const saveCurrentFollowup = async () => {
    const curEntry = sells[idx];
    if (!curEntry) return;
    if (!followupDraft.sold_price || isNaN(parseInt(followupDraft.sold_price, 10))) {
      alert("Veuillez saisir le prix de vente réel (en €).");
      return;
    }
    setFollowupSaving(true);
    const eId = curEstimId();
    try {
      // Plateforme : si "Autre" sélectionné, on prend platform_custom ; sinon platform (nom)
      const platformValue = followupDraft.platform === "Autre"
        ? (followupDraft.platform_custom || "Autre").trim().slice(0, 80)
        : (followupDraft.platform || "").trim().slice(0, 80) || null;
      // Prix d'achat : optionnel — si vide, on n'envoie pas
      const ppStr = String(followupDraft.purchase_price || "").trim();
      const purchasePrice = ppStr && !isNaN(parseInt(ppStr, 10)) && parseInt(ppStr, 10) > 0
        ? parseInt(ppStr, 10)
        : null;

      const payload = {
        estimation_id: eId,
        vehicle_data: curEntry.answers || curEntry.data || {},
        estimation_data: curEntry.results || curEntry.result || {},
        sold_price: parseInt(followupDraft.sold_price, 10),
        sold_date: followupDraft.sold_date || null,
        client_type: followupDraft.client_type || "unknown",
        notes: followupDraft.notes || null,
        platform: platformValue,
        purchase_price: purchasePrice,
        // v5 : méthode de retrait du véhicule par l'acheteur (utilisée par les analyses consolidées)
        pickup_method: followupDraft.pickup_method || null
      };
      const resp = await apiPost("/api/sale-followup", payload);
      setSaleFollowups(prev => ({ ...prev, [eId]: { ...payload, id: resp.id } }));
      setFollowupOpen(false);
      // Recharger les KPIs agrégés (marge, top 5, plateformes, etc.)
      apiGet("/api/sale-followup/stats").then(d => {
        if (d?.success) setSalesStats(d);
      }).catch(() => {});
      alert("✅ Prix de vente enregistré.");
    } catch (err) {
      console.error("[pro-dash] save followup error:", err.status, err.message, err.detail);
      const detail = err.detail && err.detail.pg_code ? `\n\nCode : ${err.detail.pg_code}` : "";
      alert("❌ " + (err.message || "Erreur d'enregistrement") + detail);
    } finally {
      setFollowupSaving(false);
    }
  };

  // Helper : id stable pour l'estimation courante
  const curEstimId = () => {
    const e = sells[idx];
    if (!e) return null;
    return e.estimation_id || e.id || `est_${e.date}`;
  };

  const fmtPrice = (v) => {
    if (!v) return null;
    const n = typeof v === "string" ? parseFloat(v.replace(/[^\d.]/g,"")) : v;
    return isNaN(n) || n <= 0 ? null : n;
  };

  // Build enriched sell entries — real field structure from SellPanel
  const entries = sells.map((s, i) => {
    const r = s.results || {};
    const a = s.answers  || {};
    // VIQ.AI price — use stored prix_viq first (exact algo result), fallback to formula
    const argusLow   = fmtPrice(r.argus_low);
    const argusHigh  = fmtPrice(r.argus_high);
    // Si argus_mid absent (anciennes estimations), calculer le vrai milieu — PAS argus_high
    const argusMid   = fmtPrice(r.argus_mid)
                     || (argusLow && argusHigh ? Math.round((argusLow + argusHigh) / 2 / 100) * 100 : null)
                     || argusLow
                     || argusHigh;
    const viqPrice   = fmtPrice(r.prix_viq)
                     || ((argusMid || argusLow) ? Math.round((argusMid || argusLow) * 0.95 / 100) * 100 : null)
                     || fmtPrice(r.price || r.estimated_price || a.estimatedPrice);
    // Fusion marché 70/30
    const marche     = argusLow && argusMid
      ? Math.round((argusMid * 0.70 + argusLow * 0.30) * 0.95 / 100) * 100
      : null;
    // Identity — answers fields from sell form
    const make      = a.make  || r.make  || "";
    const model     = a.model || r.model || "";
    const makeModel = [make, model].filter(Boolean).join(" ") ||
                      r.annonce_titre?.split("—")[0]?.trim() || "Véhicule estimé";
    const year      = a.year  || r.year  || a.annee    || "";
    const km        = a.km    || r.km    || a.mileage  || null;
    const fuel      = a.fuel_sell  || a.fuel  || r.fuel  || "";
    const gearbox   = a.gearbox_sell || a.gearbox || r.gearbox || "";
    const state     = a.state || "";
    // Plaque / VIN — issus du décodage de plaque (champs possibles variés)
    const immat     = a.immat || a.immatriculation || a.plate || a.plaque || r.immat || r.immatriculation || r.plate || s.plate || s.immat || "";
    const vin       = a.vin || r.vin || s.vin || a.vin_number || r.vin_number || "";
    // Score from real field
    const score     = r.score_vente || r.score || null;
    // v13 : distinguer explicitement score de fiabilité (VIQ.AI®) de score de
    // vente — `r.score` seul est ambigu (peut être l'un ou l'autre selon le
    // type d'entrée). `r.reliability_score` est le champ explicite écrit par
    // enrichSell/enrichVehicle depuis cette version ; à défaut, on ne déduit
    // le repli que pour les entrées sans score_vente (donc pas un "sell").
    const reliabilityScore = r.reliability_score ?? (r.score_vente == null ? r.score : null);
    const knownIssuesData  = r.known_issues || null;
    const date      = s.date
      ? new Date(s.date).toLocaleDateString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric"})
      : "";
    // TAM — Tendance Actuelle Marché 90/10
    const tam       = argusLow && argusMid
      ? Math.round((argusMid * 0.90 + argusLow * 0.10) * 0.95 / 100) * 100
      : null;
    // Delta VIQ vs marché
    const delta     = marche && argusLow
      ? Math.round((marche - argusLow) / argusLow * 100)
      : null;
    // Plateformes — real field name is 'plateformes' (array of objects)
    const platforms = r.plateformes || r.platforms || r.sell_platforms || [];
    return {
      makeModel, make, model, year, km, fuel, gearbox, state, immat, vin,
      viqPrice, argusLow, argusMid, argusHigh, marche, tam, delta, score, date,
      reliabilityScore, knownIssuesData,
      platforms, raw: s,
    };
  });

  const cur = entries[idx] || null;
  const prev = () => setIdx(i => Math.max(0, i-1));
  const next = () => setIdx(i => Math.min(entries.length-1, i+1));

  // Depr. spark — VIQ.AI 70/30 curve (faster depreciation, market-reactive)
  // Helpers pour identifier le segment du véhicule depuis cur.makeModel
  // Les véhicules premium se déprécient PLUS LENTEMENT au début mais plus vite à 5+ ans,
  // les sportives/exotiques conservent leur valeur, les généralistes suivent la courbe Argus.
  const segmentForVehicle = (makeModel) => {
    if (!makeModel) return "generaliste";
    const mm = makeModel.toLowerCase();
    // Exotiques / collection (faible dépréciation, voire ré-appréciation après 5-7 ans)
    if (/ferrari|lamborghini|aston martin|mclaren|bentley|rolls.royce|maserati|bugatti|pagani/.test(mm)) return "exotique";
    // Premium allemandes + sportives (dépréciation lente puis accélération)
    if (/porsche|mercedes|bmw|audi|jaguar|lexus|land rover|range rover|alpina|amg|tesla model s|tesla model x/.test(mm)) return "premium";
    // SUV premium / luxe
    if (/cadillac|infiniti|lincoln|genesis|volvo xc90|maserati levante/.test(mm)) return "premium";
    return "generaliste";
  };
  const segment = segmentForVehicle(cur?.makeModel);
  // Multiplicateurs par segment, années 0→7
  const SEGMENT_CURVES = {
    exotique:    [100, 95, 92, 90, 88, 87, 88, 90], // ré-appréciation après 5 ans
    premium:     [100, 88, 78, 70, 62, 55, 48, 42], // dépréciation lente début, accélère
    generaliste: [100, 90, 80, 72, 64, 57, 51, 46], // courbe classique
  };
  const ARGUS_CURVES = {
    exotique:    [100, 92, 86, 81, 77, 74, 73, 73], // argus voit moins l'aspect collection
    premium:     [100, 86, 75, 66, 58, 51, 45, 39],
    generaliste: [100, 88, 78, 69, 62, 56, 50, 45],
  };
  const buildSpark = (base) => {
    if (!base) return [];
    return SEGMENT_CURVES[segment].map((pct, i) => ({
      x: i * 44, y: 80 - (pct/100)*72, v: Math.round(base * pct/100),
    }));
  };
  const buildArgusSpk = (base) => {
    if (!base) return [];
    return ARGUS_CURVES[segment].map((pct, i) => ({
      x: i * 44, y: 80 - (pct/100)*72, v: Math.round(base * pct/100),
    }));
  };

  // ── GATE ──────────────────────────────────────────────────────────────────
  if (!hasSiret) {
    return (
      <div className="pro-dash-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
        <div className="pro-dash-panel" style={{maxWidth:480}}>
          <div className="pro-dash-header">
            <div className="pro-dash-title">
              📊 Outil de suivi métier
              <span className="pro-dash-badge">Accès restreint</span>
            </div>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="pro-dash-gate">
            <div className="pro-dash-gate-icon">🏢</div>
            <div className="pro-dash-gate-title">Réservé aux comptes professionnels</div>
            <div className="pro-dash-gate-sub">
              L'Outil de suivi métier est accessible uniquement aux comptes disposant d'un SIRET validé.
              Renseignez votre SIRET dans la section Tarifs pour débloquer l'accès.
            </div>
            <button className="btn btn-accent" style={{marginTop:".5rem"}} onClick={onClose}>
              Renseigner mon SIRET →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── EMPTY STATE ────────────────────────────────────────────────────────────
  if (entries.length === 0) {
    return (
      <div className="pro-dash-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
        <div className="pro-dash-panel" style={{maxWidth:520}}>
          <div className="pro-dash-header">
            <div className="pro-dash-title">📊 Outil de suivi métier <span className="pro-dash-badge">◆ SIRET vérifié</span></div>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="pro-dash-gate">
            <div className="pro-dash-gate-icon">📈</div>
            <div className="pro-dash-gate-title">Aucune cote dans l'historique</div>
            <div className="pro-dash-gate-sub">
              Lancez votre première cote pour que les données apparaissent ici sous forme de fiche analytique complète.
            </div>
            <button className="btn btn-accent" style={{marginTop:".5rem"}} onClick={onClose}>
              Commencer maintenant →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── DASHBOARD PRINCIPAL ─────────────────────────────────────────────────────
  const spark     = buildSpark(cur?.viqPrice || cur?.marche);
  const sparkArgus = buildArgusSpk(cur?.argusLow);
  const sparkPath = spark.map((p,i) => `${i===0?"M":"L"}${p.x},${p.y.toFixed(1)}`).join(" ");
  const sparkArea = spark.length > 1
    ? sparkPath + ` L${spark[spark.length-1].x},80 L0,80 Z` : "";
  const sparkArgusPath = sparkArgus.map((p,i) => `${i===0?"M":"L"}${p.x},${p.y.toFixed(1)}`).join(" ");

  // Comparison bar widths
  const allPrices = [cur?.viqPrice, cur?.marche, cur?.tam, cur?.argusLow, cur?.argusMid].filter(Boolean);
  const maxP = allPrices.length ? Math.max(...allPrices) * 1.05 : 1;
  const barPct = (v) => v ? Math.round(v/maxP*100) : 0;

  const deltaColor = cur?.delta > 5 ? "#4ADE80" : cur?.delta < -5 ? "#F87171" : "#F59E0B";
  const deltaLabel = cur?.delta === null ? "—"
    : cur.delta > 0 ? `+${cur.delta}% vs marché`
    : `${cur.delta}% vs marché`;

  // ─── KPIs commerciaux : désormais calculés côté backend (/api/sale-followup/stats)
  //     et exposés via le state `salesStats`. Le bloc d'affichage utilise directement
  //     ces données réelles : marge totale, marge moyenne, précision, conversion, top 5,
  //     plateformes, évolution mensuelle, mix client.



  return (
    <div className="pro-dash-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="pro-dash-panel">

        {/* ── HEADER ── */}
        <div className="pro-dash-header">
          <div style={{display:"flex", flexDirection:"column", gap:".15rem"}}>
            <div className="pro-dash-title">
              📊 Outil de suivi métier
              <span className="pro-dash-badge">◆ SIRET vérifié</span>
            </div>
            <div style={{fontSize:".72rem", color:"var(--muted)", fontFamily:"var(--font-mono)"}}>
              SIRET {siret.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/,"$1 $2 $3 $4")} · {entries.length} cote{entries.length>1?"s":""} enregistrée{entries.length>1?"s":""}
            </div>
          </div>
          {/* Navigation flèches */}
          <div style={{display:"flex", alignItems:"center", gap:".6rem"}}>
            <span style={{fontSize:".75rem", color:"var(--muted)", fontFamily:"var(--font-mono)"}}>
              {idx+1} / {entries.length}
            </span>
            <button onClick={prev} disabled={idx===0}
              style={{width:32,height:32,borderRadius:8,border:"1.5px solid var(--border)",background:"var(--bg)",cursor:idx===0?"not-allowed":"pointer",color:idx===0?"var(--muted)":"var(--text)",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s",opacity:idx===0?.4:1}}>
              ‹
            </button>
            <button onClick={next} disabled={idx===entries.length-1}
              style={{width:32,height:32,borderRadius:8,border:"1.5px solid var(--border)",background:"var(--bg)",cursor:idx===entries.length-1?"not-allowed":"pointer",color:idx===entries.length-1?"var(--muted)":"var(--text)",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s",opacity:idx===entries.length-1?.4:1}}>
              ›
            </button>
            <button onClick={refreshAll} disabled={refreshing} title="Actualiser les données de l'onglet"
              aria-label="Actualiser"
              style={{width:32,height:32,borderRadius:8,border:"1.5px solid var(--border)",background:"var(--bg)",cursor:refreshing?"wait":"pointer",color:"var(--text)",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: refreshing ? "viq-spin .8s linear infinite" : "none" }}>
                <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
            <ThemeToggleButton size={32} />
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* v36 — Simplification Dashboard Pro : la barre d'onglets complète
            n'est visible que sur "Vue d'ensemble". Les autres sections ne
            sont plus atteignables qu'en cliquant sur leur raccourci depuis la
            Vue d'ensemble — un bouton retour remplace la barre partout ailleurs. */}
        {activeTab === "overview" ? (
        <div className="saas-dash-tabs-bar" role="navigation" aria-label="Navigation tableau de bord">
          <button className={`saas-dash-tab active`} onClick={() => setActiveTab("overview")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg>
            <span>Vue d'ensemble</span>
          </button>
        </div>
        ) : (
          <div style={{ padding: ".7rem 1rem", borderBottom: "1px solid var(--border)" }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setActiveTab("overview")}>← Vue d'ensemble</button>
          </div>
        )}

        <div className="saas-dash-content">

        {/* ── TABLEAU DE BORD COMMERCIAL : KPIs réels (sale_followup) ── */}
        {activeTab === "overview" && (() => {
          const stats = salesStats || {};
          const k = stats.kpis || {};
          // Helpers d'affichage
          const fmtEur = (n) => n === null || n === undefined ? "—" : (n >= 0 ? "+" : "") + Math.round(n).toLocaleString("fr-FR") + " €";
          const fmtEurAbs = (n) => n === null || n === undefined ? "—" : Math.round(n).toLocaleString("fr-FR") + " €";
          const fmtPct = (n) => n === null || n === undefined ? "—" : Math.round(n * 10) / 10 + " %";

          // Précision color : <5% excellent (vert), 5-10% bon (ambre), >10% à améliorer (rouge)
          const precPct = k.precision_pct;
          const precisionColor = precPct === null || precPct === undefined ? "var(--muted)"
            : precPct < 5 ? "var(--green)" : precPct < 10 ? "var(--accent)" : "#9B2C2C";
          const precisionLabel = precPct === null || precPct === undefined ? "données insuffisantes"
            : precPct < 5 ? "excellente" : precPct < 10 ? "bonne" : "à améliorer";

          // Conversion color
          const convRate = k.conversion_rate;
          const conversionColor = convRate === null || convRate === undefined ? "var(--muted)"
            : convRate >= 30 ? "var(--green)" : convRate >= 15 ? "var(--accent)" : "var(--sub)";

          return (
            <>
              {/* ─── Bandeau de bienvenue + météo ─── */}
              {(() => {
                // Salutation contextuelle selon l'heure
                const h = new Date().getHours();
                const salut = h < 6 ? "Bonne nuit" : h < 12 ? "Bonjour" : h < 18 ? "Bel après-midi" : "Bonne soirée";
                // Prénom : on extrait le premier mot du displayName ou la part avant @ de l'email
                const fullName = user?.displayName || user?.email?.split("@")[0] || "";
                const firstName = fullName.split(/[\s.]/)[0].replace(/^./, c => c.toUpperCase());
                // Icône météo selon code WMO
                const weatherIcon = (code) => {
                  if (code === 0) return "☀️";        // ciel clair
                  if (code <= 2) return "🌤️";        // peu nuageux
                  if (code === 3) return "☁️";        // couvert
                  if (code <= 49) return "🌫️";       // brouillard
                  if (code <= 67) return "🌧️";       // pluie
                  if (code <= 77) return "❄️";       // neige
                  if (code <= 86) return "🌦️";       // averses
                  if (code <= 99) return "⛈️";       // orage
                  return "🌤️";
                };
                return (
                  <div style={{
                    background: "linear-gradient(135deg, rgba(168,128,58,.06), rgba(168,128,58,.01) 60%, transparent)",
                    border: "1.5px solid var(--border)",
                    borderRadius: 14,
                    padding: "1.1rem 1.4rem",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: "1rem", marginBottom: ".4rem"
                  }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: ".15rem", minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)", letterSpacing: "-.01em" }}>
                        {salut}{firstName ? `, ${firstName}` : ""} 👋
                      </div>
                      <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.5 }}>
                        J'espère que vous passez une excellente journée. Voici votre tableau de bord à jour.
                      </div>
                    </div>
                    {weather && (
                      <div style={{
                        display: "flex", alignItems: "center", gap: ".7rem",
                        background: "var(--white)", border: "1.5px solid var(--border)",
                        borderRadius: 100, padding: ".5rem 1rem .5rem .8rem",
                        boxShadow: "0 2px 8px rgba(20,30,55,.04)"
                      }}>
                        <span style={{ fontSize: "1.5rem", lineHeight: 1 }}>{weatherIcon(weather.code)}</span>
                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                          <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-head)" }}>
                            {weather.temp}°C
                          </span>
                          {weather.city && (
                            <span style={{ fontSize: ".66rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)" }}>
                              {weather.city}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ─── 4 cartes KPI principales ─── */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: ".6rem",
                marginBottom: ".8rem"
              }}>
                {/* Carte 1 : Marge totale */}
                <div style={{
                  background: "linear-gradient(135deg, rgba(4,120,87,.08), rgba(4,120,87,.02))",
                  border: "1.5px solid rgba(4,120,87,.2)",
                  borderRadius: 10, padding: "0.8rem 1rem"
                }}>
                  <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)", marginBottom: ".25rem" }}>
                    Marge totale
                  </div>
                  <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "var(--green)", fontFamily: "var(--font-head)", lineHeight: 1.1 }}>
                    {fmtEur(k.total_margin)}
                  </div>
                  <div style={{ fontSize: ".62rem", color: "var(--sub)", marginTop: ".2rem" }}>
                    {k.nb_sales_with_margin || 0} vente{(k.nb_sales_with_margin || 0) > 1 ? "s" : ""} avec marge tracée
                  </div>
                </div>

                {/* Carte 2 : Marge moyenne */}
                <div style={{
                  background: "linear-gradient(135deg, rgba(168,128,58,.06), rgba(168,128,58,.01))",
                  border: "1.5px solid rgba(168,128,58,.2)",
                  borderRadius: 10, padding: "0.8rem 1rem"
                }}>
                  <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)", marginBottom: ".25rem" }}>
                    Marge moyenne / véhicule
                  </div>
                  <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-head)", lineHeight: 1.1 }}>
                    {fmtEur(k.avg_margin)}
                  </div>
                  <div style={{ fontSize: ".62rem", color: "var(--sub)", marginTop: ".2rem" }}>
                    {k.total_sold_volume ? "sur " + k.total_sold_volume.toLocaleString("fr-FR") + " € vendus" : "—"}
                  </div>
                </div>

                {/* Carte 3 : Précision VIQ.AI */}
                <div style={{
                  background: "var(--card)",
                  border: "1.5px solid var(--border)",
                  borderRadius: 10, padding: "0.8rem 1rem"
                }}>
                  <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)", marginBottom: ".25rem" }}>
                    Précision VIQ.AI®
                  </div>
                  <div style={{ fontSize: "1.35rem", fontWeight: 800, color: precisionColor, fontFamily: "var(--font-head)", lineHeight: 1.1 }}>
                    {precPct !== null && precPct !== undefined ? "±" + fmtPct(precPct) : "—"}
                  </div>
                  <div style={{ fontSize: ".62rem", color: "var(--sub)", marginTop: ".2rem" }}>
                    {k.precision_sample_size > 0
                      ? precisionLabel + " · sur " + k.precision_sample_size + " vente" + (k.precision_sample_size > 1 ? "s" : "")
                      : "renseigner des prix de vente"}
                  </div>
                </div>

                {/* Carte 4 : Taux de conversion */}
                <div style={{
                  background: "var(--card)",
                  border: "1.5px solid var(--border)",
                  borderRadius: 10, padding: "0.8rem 1rem"
                }}>
                  <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)", marginBottom: ".25rem" }}>
                    Conversion estim. → vente
                  </div>
                  <div style={{ fontSize: "1.35rem", fontWeight: 800, color: conversionColor, fontFamily: "var(--font-head)", lineHeight: 1.1 }}>
                    {convRate !== null && convRate !== undefined ? fmtPct(convRate) : "—"}
                  </div>
                  <div style={{ fontSize: ".62rem", color: "var(--sub)", marginTop: ".2rem" }}>
                    {k.nb_ventes || 0} vente{(k.nb_ventes || 0) > 1 ? "s" : ""} sur {k.nb_estimations || 0} estim.
                  </div>
                </div>
              </div>

              {/* ─── SYNTHÈSE DES AUTRES ONGLETS ─── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: "0.9rem", marginTop: "0.4rem" }}>

                {/* Mini Formulaires clients — demandes reçues via le lien personnalisé */}
                <div style={{ background: "var(--white)", border: "1.5px solid " + (newLeadsCount > 0 ? "rgba(168,128,58,.4)" : "var(--border)"), borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem", boxShadow: newLeadsCount > 0 ? "0 0 0 4px rgba(168,128,58,.06)" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>📥</span> Formulaires clients
                      {newLeadsCount > 0 && (
                        <span style={{ fontSize: ".6rem", fontWeight: 800, background: "var(--accent)", color: "#fff", borderRadius: 20, padding: ".1rem .45rem" }}>{newLeadsCount}</span>
                      )}
                    </div>
                    <button onClick={() => setActiveTab("leads")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Voir tout →</button>
                  </div>
                  {leads === null ? (
                    <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>Chargement…</div>
                  ) : leads.length === 0 ? (
                    <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic", lineHeight: 1.5 }}>
                      Aucune demande reçue pour l'instant. Partagez votre lien client personnalisé pour recevoir vos premières demandes.
                    </div>
                  ) : (() => {
                    const byType = { estimation: 0, achat: 0, cartegrise: 0 };
                    leads.forEach(l => { if (byType[l.type] !== undefined) byType[l.type]++; });
                    const last = leads[0]; // tri DESC côté serveur
                    const lastDate = last?.created_at ? new Date(last.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : null;
                    return (
                      <>
                        <div style={{ display: "flex", alignItems: "baseline", gap: ".5rem" }}>
                          <span style={{ fontSize: "1.35rem", fontWeight: 800, color: newLeadsCount > 0 ? "var(--accent)" : "var(--text)", fontFamily: "var(--font-head)", lineHeight: 1 }}>{newLeadsCount}</span>
                          <span style={{ fontSize: ".72rem", color: "var(--sub)" }}>nouvelle{newLeadsCount > 1 ? "s" : ""} demande{newLeadsCount > 1 ? "s" : ""} à traiter · {leads.length} au total</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
                          {[
                            ["🚗", "Cotes véhicule", byType.cote],
                            ["🔎", "Recherches d'achat", byType.achat],
                            ["🧾", "Cartes grises & malus", byType.cartegrise],
                          ].map(([icon, label, n], i) => (
                            <div key={label} style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".75rem", padding: ".3rem 0", borderBottom: i < 2 ? "1px solid var(--border)" : "none" }}>
                              <span style={{ minWidth: 22 }}>{icon}</span>
                              <span style={{ flex: 1, color: "var(--text)" }}>{label}</span>
                              <span style={{ fontWeight: 700, color: n > 0 ? "var(--text)" : "var(--muted)", fontFamily: "var(--font-head)" }}>{n}</span>
                            </div>
                          ))}
                        </div>
                        {lastDate && (
                          <div style={{ fontSize: ".66rem", color: "var(--muted)" }}>
                            Dernière demande : {lastDate}{last?.client_name ? " · " + last.client_name : ""}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Mini Analytics — Top 3 véhicules */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>🏆</span> Top 3 véhicules
                    </div>
                    <button onClick={() => setActiveTab("analytics")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Voir tout →</button>
                  </div>
                  {(stats.top5 || []).length === 0 ? (
                    <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>Aucune vente avec prix d'achat renseigné.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
                      {(stats.top5 || []).slice(0, 3).map((t, i) => {
                        const vd = t.vehicle || {};
                        const label = vd.make_model || vd.makeModel || ((vd.make || "") + " " + (vd.model || "")).trim() || "Véhicule #" + t.id;
                        return (
                          <div key={t.id || i} style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".75rem", padding: ".3rem 0", borderBottom: i < 2 ? "1px solid var(--border)" : "none" }}>
                            <span style={{ fontFamily: "var(--font-head)", fontWeight: 800, color: i === 0 ? "var(--accent)" : "var(--muted)", minWidth: 22 }}>#{i + 1}</span>
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text)" }} title={label}>{label}</span>
                            <span style={{ fontWeight: 700, color: (t.margin || 0) >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)" }}>
                              {(t.margin || 0) >= 0 ? "+" : ""}{Math.round(t.margin || 0).toLocaleString("fr-FR")} €
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Mini Plateformes — Top 3 */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>🌐</span> Top plateformes
                    </div>
                    <button onClick={() => setActiveTab("platforms")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Voir tout →</button>
                  </div>
                  {(stats.platforms || []).length === 0 ? (
                    <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>Aucune vente trackée par plateforme.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
                      {(stats.platforms || []).slice(0, 3).map((p, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".75rem", padding: ".3rem 0", borderBottom: i < 2 ? "1px solid var(--border)" : "none" }}>
                          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text)", fontWeight: 600 }}>{p.name}</span>
                          <span style={{ fontSize: ".68rem", color: "var(--sub)" }}>{p.count || 0} vente{(p.count || 0) > 1 ? "s" : ""}</span>
                          {p.avg_margin !== null && p.avg_margin !== undefined ? (
                            <span style={{ fontWeight: 700, color: p.avg_margin >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)" }}>
                              {p.avg_margin >= 0 ? "+" : ""}{Math.round(p.avg_margin).toLocaleString("fr-FR")} €
                            </span>
                          ) : (
                            <span style={{ fontSize: ".68rem", color: "var(--muted)", fontStyle: "italic" }}>n/a</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mini Évolution — 3 derniers mois */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>📈</span> Évolution récente
                    </div>
                    <button onClick={() => setActiveTab("analytics")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Voir tout →</button>
                  </div>
                  {(stats.top_months || []).length === 0 ? (
                    <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>Pas encore de données mensuelles.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
                      {(stats.top_months || []).slice(0, 3).map((m, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".75rem", padding: ".3rem 0", borderBottom: i < 2 ? "1px solid var(--border)" : "none" }}>
                          <span style={{ minWidth: 60, color: "var(--text)", fontWeight: 600 }}>{m.label || m.month || "—"}</span>
                          <span style={{ fontSize: ".68rem", color: "var(--sub)", flex: 1 }}>{m.count || 0} vente{(m.count || 0) > 1 ? "s" : ""}</span>
                          <span style={{ fontWeight: 700, color: (m.margin || 0) >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)" }}>
                            {(m.margin || 0) >= 0 ? "+" : ""}{Math.round(m.margin || 0).toLocaleString("fr-FR")} €
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Mini Estimation détaillée — véhicule courant */}
                {cur && (
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                        <span>🚗</span> Cote en cours
                      </div>
                      <button onClick={() => setActiveTab("vehicle")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Détails →</button>
                    </div>
                    <div style={{ fontSize: ".82rem", color: "var(--text)", fontWeight: 600 }}>{cur.makeModel || "—"}</div>
                    <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap", fontSize: ".7rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>
                      {cur.year && <span>{cur.year}</span>}
                      {cur.km && <span>{Number(cur.km).toLocaleString("fr-FR")} km</span>}
                      {cur.fuel && <span>{cur.fuel}</span>}
                    </div>
                    {cur.viqPrice && (
                      <div style={{ marginTop: ".3rem", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: ".68rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)" }}>Cote VIQ.AI®</span>
                        <span style={{ fontSize: "1.15rem", fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-head)" }}>{Number(cur.viqPrice).toLocaleString("fr-FR")} €</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Mini Suivi de vente */}
                {cur && (
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                        <span>💰</span> Suivi de vente
                      </div>
                      <button onClick={() => setActiveTab("sale")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Gérer →</button>
                    </div>
                    {(() => {
                      const existing = saleFollowups[curEstimId && curEstimId()];
                      if (existing) return (
                        <div>
                          <div style={{ fontSize: ".7rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", marginBottom: ".2rem" }}>Prix vendu</div>
                          <div style={{ fontSize: "1.15rem", fontWeight: 800, color: "var(--green)", fontFamily: "var(--font-head)" }}>{Number(existing.sold_price).toLocaleString("fr-FR")} €</div>
                        </div>
                      );
                      return <div style={{ fontSize: ".75rem", color: "var(--muted)", fontStyle: "italic" }}>Aucun prix de vente renseigné pour ce véhicule. Cliquez sur "Gérer" pour le saisir.</div>;
                    })()}
                  </div>
                )}

                {/* Mini Mix Pro/Particulier — TOUJOURS affiché */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>👥</span> Mix client
                    </div>
                    <button onClick={() => setActiveTab("analytics")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Voir tout →</button>
                  </div>
                  {(() => {
                    const cm = stats.client_mix || {};
                    const total = (cm.pro_count || 0) + (cm.particulier_count || 0);
                    if (total === 0) {
                      return <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>Aucune segmentation client disponible pour l'instant. Renseignez les ventes pour activer.</div>;
                    }
                    const proPct = Math.round((cm.pro_count || 0) / total * 100);
                    return (
                      <>
                        <div style={{ display: "flex", height: 8, background: "var(--deep)", borderRadius: 100, overflow: "hidden" }}>
                          <div style={{ width: `${proPct}%`, background: "#3B82F6" }} />
                          <div style={{ width: `${100 - proPct}%`, background: "var(--accent)" }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".72rem", color: "var(--sub)" }}>
                          <span><span style={{ color: "#3B82F6" }}>●</span> Pro {proPct}% · {cm.pro_count || 0} v.</span>
                          <span><span style={{ color: "var(--accent)" }}>●</span> Particulier {100 - proPct}% · {cm.particulier_count || 0} v.</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Mini Conseils IA — si véhicule courant a des recommandations */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>💡</span> Conseils IA
                    </div>
                    {cur && (
                      <button onClick={() => setActiveTab("vehicle")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Voir tout →</button>
                    )}
                  </div>
                  {(() => {
                    if (!cur) return <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>Sélectionnez une cote pour voir les recommandations IA.</div>;
                    const r = cur.raw?.results || cur.raw?.result || {};
                    const conseilPrix = r.conseil_prix || r.recommandation_prix || null;
                    const conseilsVente = r.conseils_vente || r.points_attention || [];
                    if (!conseilPrix && !conseilsVente.length) {
                      return <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>Aucun conseil disponible pour cette cote.</div>;
                    }
                    const first = conseilPrix || conseilsVente[0];
                    const text = typeof first === "string" ? first : (first?.texte || first?.text || JSON.stringify(first));
                    return (
                      <div style={{ fontSize: ".74rem", color: "var(--text)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {text}
                      </div>
                    );
                  })()}
                </div>

                {/* Mini Activité du mois — estimations + ventes ce mois */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>📅</span> Activité du mois
                    </div>
                    <button onClick={() => setActiveTab("list")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Voir tout →</button>
                  </div>
                  {(() => {
                    // Compter les estimations de ce mois calendaire
                    const now = new Date();
                    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                    const thisMonth = entries.filter(e => {
                      const d = e.date ? new Date(e.date) : (e.created_at ? new Date(e.created_at) : null);
                      return d && d >= monthStart;
                    });
                    // Ventes trackées ce mois
                    const salesThisMonth = thisMonth.filter(e => saleFollowups[e.id || e.estim_id || e.uuid]).length;
                    const monthLabel = now.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
                    return (
                      <>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem" }}>
                          <div>
                            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", fontWeight: 800, color: "var(--accent)", lineHeight: 1 }}>
                              {thisMonth.length}
                            </div>
                            <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", marginTop: ".15rem" }}>
                              Cotes
                            </div>
                          </div>
                          <div>
                            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", fontWeight: 800, color: "var(--green)", lineHeight: 1 }}>
                              {salesThisMonth}
                            </div>
                            <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", marginTop: ".15rem" }}>
                              Ventes
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: ".68rem", color: "var(--sub)", textTransform: "capitalize", fontStyle: "italic" }}>
                          {monthLabel}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Mini À faire — ventes à compléter */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>⚡</span> À faire
                    </div>
                    <button onClick={() => setActiveTab("sale")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Gérer →</button>
                  </div>
                  {(() => {
                    // Compter estimations sans suivi de vente renseigné
                    const untracked = entries.filter(e => !saleFollowups[e.id || e.estim_id || e.uuid]);
                    if (untracked.length === 0) {
                      return (
                        <div style={{ fontSize: ".75rem", color: "var(--green)", lineHeight: 1.5 }}>
                          ✅ Toutes vos cotes sont à jour. Beau travail !
                        </div>
                      );
                    }
                    return (
                      <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
                        <div>
                          <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", fontWeight: 800, color: "var(--accent)", lineHeight: 1 }}>
                            {untracked.length}
                          </div>
                          <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", marginTop: ".15rem" }}>
                            cote{untracked.length > 1 ? "s" : ""} sans suivi
                          </div>
                        </div>
                        <div style={{ fontSize: ".7rem", color: "var(--sub)", lineHeight: 1.5 }}>
                          Renseignez les prix de vente pour affiner vos marges et la précision VIQ.AI®.
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Mini Précision détaillée — focus sur l'écart moyen */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>🎯</span> Précision moteur
                    </div>
                    <button onClick={() => setActiveTab("analytics")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Voir tout →</button>
                  </div>
                  {(() => {
                    const pp = k.precision_pct;
                    if (pp === null || pp === undefined) {
                      return <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>Au moins 3 ventes avec prix renseigné nécessaires.</div>;
                    }
                    const color = pp < 5 ? "var(--green)" : pp < 10 ? "var(--accent)" : "#9B2C2C";
                    const label = pp < 5 ? "Excellente" : pp < 10 ? "Bonne" : "À améliorer";
                    const ringPct = Math.max(0, Math.min(100, 100 - pp * 4));
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                        {/* Mini jauge circulaire */}
                        <div style={{ position: "relative", width: 56, height: 56, flexShrink: 0 }}>
                          <svg width="56" height="56" viewBox="0 0 56 56" style={{ transform: "rotate(-90deg)" }}>
                            <circle cx="28" cy="28" r="22" fill="none" stroke="var(--deep)" strokeWidth="5" />
                            <circle cx="28" cy="28" r="22" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round"
                              strokeDasharray={`${ringPct * 1.382} 138.2`} />
                          </svg>
                          <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                            <span style={{ fontSize: ".82rem", fontWeight: 800, color, fontFamily: "var(--font-head)", lineHeight: 1 }}>±{Math.round(pp * 10) / 10}%</span>
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: ".82rem", fontWeight: 700, color, marginBottom: ".15rem" }}>{label}</div>
                          <div style={{ fontSize: ".68rem", color: "var(--sub)", lineHeight: 1.4 }}>
                            sur {k.nb_sales_with_margin || k.nb_ventes || 0} vente{(k.nb_sales_with_margin || k.nb_ventes || 0) > 1 ? "s" : ""} avec prix réel renseigné
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Mini Précision Plateformes — best/worst */}
                {(stats.platforms || []).length >= 2 && (
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                        <span>🏆</span> Best vs Worst plateforme
                      </div>
                      <button onClick={() => setActiveTab("platforms")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Voir tout →</button>
                    </div>
                    {(() => {
                      const sorted = [...(stats.platforms || [])].filter(p => p.avg_margin !== null && p.avg_margin !== undefined).sort((a, b) => b.avg_margin - a.avg_margin);
                      if (sorted.length < 2) return <div style={{ fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>Pas assez de plateformes avec marges trackées.</div>;
                      const best = sorted[0], worst = sorted[sorted.length - 1];
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: ".5rem", padding: ".4rem .5rem", background: "rgba(4,120,87,.06)", borderRadius: 6 }}>
                            <span style={{ fontSize: ".62rem", fontWeight: 700, color: "var(--green)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)" }}>Best</span>
                            <span style={{ flex: 1, fontSize: ".74rem", color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{best.name}</span>
                            <span style={{ fontWeight: 700, color: "var(--green)", fontFamily: "var(--font-head)", fontSize: ".82rem" }}>+{Math.round(best.avg_margin).toLocaleString("fr-FR")} €</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: ".5rem", padding: ".4rem .5rem", background: "rgba(185,28,28,.05)", borderRadius: 6 }}>
                            <span style={{ fontSize: ".62rem", fontWeight: 700, color: "#9B2C2C", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)" }}>Worst</span>
                            <span style={{ flex: 1, fontSize: ".74rem", color: "var(--text)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{worst.name}</span>
                            <span style={{ fontWeight: 700, color: worst.avg_margin >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)", fontSize: ".82rem" }}>{worst.avg_margin >= 0 ? "+" : ""}{Math.round(worst.avg_margin).toLocaleString("fr-FR")} €</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* v43 — Scan d'opportunité : jusqu'ici accessible seulement si on
                    savait que l'onglet "scan" existait — aucun raccourci ne le
                    montrait. */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>🎯</span> Scan d'opportunité
                    </div>
                    <button onClick={() => setActiveTab("scan")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Ouvrir →</button>
                  </div>
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", lineHeight: 1.5 }}>
                    Calculez la marge nette potentielle d'un véhicule à acheter (revente estimée − prix − malus − transport).
                  </div>
                </div>

                {/* v43 — Agent IA de prospection */}
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>🤝</span> Prospection client
                    </div>
                    <button onClick={() => setActiveTab("prospection")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600 }}>Ouvrir →</button>
                  </div>
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", lineHeight: 1.5 }}>
                    Trouvez des professionnels de l'automobile sur le web (garages, mandataires, detailing…) et contactez-les par email groupé.
                  </div>
                </div>
              </div>
            </>
          );
        })()}

        {/* ── ANALYTICS : Top véhicules, Plateformes, Évolution, Mix Pro/Particulier ── */}
        {activeTab === "analytics" && (() => {
          const stats = salesStats || {};
          return (
            <>
              <div className="saas-dash-content-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: ".6rem" }}>
                <div>
                  <h2 className="saas-dash-content-title">Analyses détaillées</h2>
                  <div className="saas-dash-content-sub">Top véhicules, plateformes, évolution mensuelle et mix client.</div>
                </div>
                <button className="btn btn-accent btn-sm" style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", flexShrink: 0 }}
                  onClick={() => exportAnalyseDetailleePDF(stats, siret).catch(err => alert("Erreur génération du rapport : " + err.message))}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/></svg>
                  Générer un rapport
                </button>
              </div>
              {/* ─── Panneau Analytics — TOUJOURS affiché sur cet onglet ─── */}
              {true && (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  gap: "0.8rem",
                  marginBottom: "1rem"
                }}>
                  {/* === Top 7 véhicules les plus rentables === */}
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 10, padding: "0.9rem 1.1rem" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", marginBottom: ".6rem", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>🏆</span> Top 7 véhicules les plus rentables
                    </div>
                    {(stats.top5 || []).length === 0 ? (
                      <div style={{ fontSize: ".74rem", color: "var(--muted)", fontStyle: "italic", padding: ".5rem 0" }}>
                        Aucune vente avec prix d'achat renseigné.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: ".35rem" }}>
                        {(stats.top5 || []).map((t, i) => {
                          const vd = t.vehicle || {};
                          const label = vd.make_model || vd.makeModel || ((vd.make || "") + " " + (vd.model || "")).trim() || "Véhicule #" + t.id;
                          return (
                            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: ".6rem", padding: ".35rem .5rem", background: i === 0 ? "rgba(4,120,87,.04)" : "transparent", borderRadius: 5 }}>
                              <span style={{ fontSize: ".7rem", fontWeight: 800, color: i === 0 ? "var(--green)" : "var(--muted)", minWidth: 18, fontFamily: "var(--font-head)" }}>#{i + 1}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: ".76rem", fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {label}
                                </div>
                                <div style={{ fontSize: ".62rem", color: "var(--sub)" }}>
                                  Achat {Math.round(t.purchase_price).toLocaleString("fr-FR")} € → Vente {Math.round(t.sold_price).toLocaleString("fr-FR")} €
                                  {t.platform ? "  ·  " + t.platform : ""}
                                </div>
                              </div>
                              <div style={{ textAlign: "right" }}>
                                <div style={{ fontSize: ".82rem", fontWeight: 800, color: "var(--green)", fontFamily: "var(--font-head)" }}>
                                  +{Math.round(t.margin).toLocaleString("fr-FR")} €
                                </div>
                                <div style={{ fontSize: ".58rem", color: "var(--sub)" }}>
                                  +{t.margin_pct}%
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* === Performance par plateforme === */}
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 10, padding: "0.9rem 1.1rem" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", marginBottom: ".6rem", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>🛒</span> Performance par plateforme
                    </div>
                    {(stats.platforms || []).length === 0 ? (
                      <div style={{ fontSize: ".74rem", color: "var(--muted)", fontStyle: "italic", padding: ".5rem 0" }}>
                        Aucune plateforme renseignée dans vos ventes.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
                        {(stats.platforms || []).slice(0, 9).map((p, i) => (
                          <div key={p.name} style={{ display: "flex", alignItems: "center", gap: ".5rem", padding: ".3rem .4rem" }}>
                            <PlatformLogo name={p.name} size={20} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: ".76rem", fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {p.name}
                              </div>
                              <div style={{ fontSize: ".6rem", color: "var(--sub)" }}>
                                {p.count} vente{p.count > 1 ? "s" : ""}  ·  prix moyen {p.avg_sold.toLocaleString("fr-FR")} €
                              </div>
                            </div>
                            {p.avg_margin !== null ? (
                              <div style={{ fontSize: ".74rem", fontWeight: 700, color: p.avg_margin >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)" }}>
                                {p.avg_margin >= 0 ? "+" : ""}{p.avg_margin.toLocaleString("fr-FR")} €
                              </div>
                            ) : (
                              <div style={{ fontSize: ".7rem", color: "var(--muted)", fontStyle: "italic" }}>n/a</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* === Évolution mensuelle (sparkline + chiffres) === */}
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 10, padding: "0.9rem 1.1rem" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", marginBottom: ".6rem", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>📈</span> Évolution sur 12 mois
                    </div>
                    {(() => {
                      const months = stats.monthly || [];
                      const totalMonths = months.reduce((a, m) => a + m.count, 0);
                      if (totalMonths === 0) {
                        return <div style={{ fontSize: ".74rem", color: "var(--muted)", fontStyle: "italic", padding: ".5rem 0" }}>
                          Aucune vente enregistrée sur les 12 derniers mois.
                        </div>;
                      }
                      // Sparkline SVG simple : barres proportionnelles à la marge
                      const maxMargin = Math.max(...months.map(m => Math.abs(m.margin)), 1);
                      const barW = 100 / months.length;
                      return (
                        <>
                          <svg viewBox="0 0 100 30" preserveAspectRatio="none" style={{ width: "100%", height: 50, marginBottom: ".4rem" }}>
                            {months.map((m, i) => {
                              const h = maxMargin > 0 ? (Math.abs(m.margin) / maxMargin) * 25 : 0;
                              const isPositive = m.margin >= 0;
                              return (
                                <rect key={i}
                                  x={i * barW + barW * 0.15}
                                  y={isPositive ? 28 - h : 28}
                                  width={barW * 0.7}
                                  height={h || 0.5}
                                  fill={isPositive ? "var(--green)" : "#9B2C2C"}
                                  opacity={m.count > 0 ? 0.8 : 0.2} />
                              );
                            })}
                          </svg>
                          {/* Labels des mois — on étiquette 1 mois sur 3 ET toujours le dernier (mois courant) */}
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".55rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
                            {months.filter((_, i) => i % 3 === 0 || i === months.length - 1).map((m, i) => (
                              <span key={i}>{m.label}</span>
                            ))}
                          </div>
                          {/* Récap chiffré : dernier mois actif */}
                          <div style={{ marginTop: ".5rem", paddingTop: ".5rem", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", fontSize: ".7rem" }}>
                            <div>
                              <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>30 derniers j</div>
                              <div style={{ fontWeight: 700, color: "var(--text)" }}>
                                {months[months.length - 1]?.count || 0} vente{(months[months.length - 1]?.count || 0) > 1 ? "s" : ""}
                              </div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>Marge mois</div>
                              <div style={{ fontWeight: 700, color: (months[months.length - 1]?.margin || 0) >= 0 ? "var(--green)" : "#9B2C2C" }}>
                                {Math.round(months[months.length - 1]?.margin || 0).toLocaleString("fr-FR")} €
                              </div>
                            </div>
                          </div>
                          {/* ─── Top 6 mois les plus performants (tri par marge) ─── */}
                          {(stats.top_months || []).length > 0 && (
                            <div style={{ marginTop: ".7rem", paddingTop: ".7rem", borderTop: "1px solid var(--border)" }}>
                              <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontFamily: "var(--font-mono)", marginBottom: ".4rem" }}>
                                Top 9 mois · classés par marge
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: ".25rem" }}>
                                {(stats.top_months || []).map((m, i) => (
                                  <div key={m.key} style={{
                                    display: "grid",
                                    gridTemplateColumns: "20px 1fr auto auto",
                                    alignItems: "center", gap: ".5rem",
                                    padding: ".25rem .4rem",
                                    background: i === 0 ? "rgba(4,120,87,.04)" : "transparent",
                                    borderRadius: 4
                                  }}>
                                    <span style={{ fontSize: ".62rem", fontWeight: 800, color: i === 0 ? "var(--green)" : "var(--muted)", fontFamily: "var(--font-head)" }}>#{i + 1}</span>
                                    <div style={{ fontSize: ".72rem", color: "var(--text)", fontWeight: 600 }}>
                                      {m.label}
                                      <span style={{ fontSize: ".58rem", color: "var(--sub)", fontWeight: 400, marginLeft: ".35rem" }}>
                                        · {m.count} vente{m.count > 1 ? "s" : ""}
                                      </span>
                                    </div>
                                    <span style={{ fontSize: ".64rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>
                                      CA {(m.sold_volume / 1000).toFixed(0)}k €
                                    </span>
                                    <span style={{ fontSize: ".74rem", fontWeight: 700, color: m.margin >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)", minWidth: 70, textAlign: "right" }}>
                                      {m.margin >= 0 ? "+" : ""}{Math.round(m.margin).toLocaleString("fr-FR")} €
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* === Mix Pro / Particulier === */}
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 10, padding: "0.9rem 1.1rem" }}>
                    <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", marginBottom: ".6rem", display: "flex", alignItems: "center", gap: ".4rem" }}>
                      <span>👥</span> Mix Pro / Particulier
                    </div>
                    {(() => {
                      const cm = stats.client_mix || {};
                      const total = (cm.pro?.count || 0) + (cm.particulier?.count || 0) + (cm.unknown?.count || 0);
                      if (total === 0) {
                        return <div style={{ fontSize: ".74rem", color: "var(--muted)", fontStyle: "italic", padding: ".5rem 0" }}>
                          Type de client non renseigné dans vos ventes.
                        </div>;
                      }
                      const rows = [
                        { key: "pro", label: "Pro", color: "#1F5AE6", icon: "🏢" },
                        { key: "particulier", label: "Particulier", color: "var(--accent)", icon: "👤" },
                        { key: "unknown", label: "Non renseigné", color: "var(--muted)", icon: "•" },
                      ];
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                          {/* Barre proportionnelle */}
                          <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", border: "1.5px solid var(--border)" }}>
                            {rows.map(r => {
                              const pct = total > 0 ? (cm[r.key]?.count || 0) / total * 100 : 0;
                              if (pct === 0) return null;
                              return <div key={r.key} style={{ width: pct + "%", background: r.color }} />;
                            })}
                          </div>
                          {/* Détails */}
                          {rows.map(r => {
                            const c = cm[r.key] || { count: 0, avg_margin: null };
                            if (c.count === 0) return null;
                            const pct = Math.round((c.count / total) * 100);
                            return (
                              <div key={r.key} style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flexShrink: 0 }} />
                                <span style={{ fontSize: ".72rem", color: "var(--text)", flex: 1 }}>
                                  <strong>{r.label}</strong> · {c.count} vente{c.count > 1 ? "s" : ""} ({pct}%)
                                </span>
                                {c.avg_margin !== null && (
                                  <span style={{ fontSize: ".68rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>
                                    marge moy. {c.avg_margin >= 0 ? "+" : ""}{c.avg_margin.toLocaleString("fr-FR")} €
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* ─── Rotation par segment de prix (toujours visible) ─── */}
                    {(stats.price_segments || []).some(seg => seg.count > 0) && (
                      <div style={{ marginTop: ".8rem", paddingTop: ".7rem", borderTop: "1px solid var(--border)" }}>
                        <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontFamily: "var(--font-mono)", marginBottom: ".4rem" }}>
                          Rotation par segment de prix
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
                          {(stats.price_segments || []).map(seg => {
                            const totalSegs = (stats.price_segments || []).reduce((a, b) => a + b.count, 0);
                            const segPct = totalSegs > 0 ? Math.round((seg.count / totalSegs) * 100) : 0;
                            const isEmpty = seg.count === 0;
                            return (
                              <div key={seg.key} style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(80px, auto) 1fr auto",
                                alignItems: "center", gap: ".5rem",
                                opacity: isEmpty ? 0.5 : 1,
                                padding: ".2rem 0"
                              }}>
                                <span style={{ fontSize: ".72rem", color: "var(--text)", fontWeight: 600 }}>
                                  {seg.label}
                                </span>
                                <div style={{ display: "flex", alignItems: "center", gap: ".4rem", minWidth: 0 }}>
                                  {/* Mini-barre proportionnelle */}
                                  <div style={{ flex: 1, height: 4, background: "var(--card)", borderRadius: 2, overflow: "hidden", minWidth: 30 }}>
                                    {!isEmpty && (
                                      <div style={{ width: segPct + "%", height: "100%", background: "var(--accent)", opacity: 0.7 }} />
                                    )}
                                  </div>
                                  <span style={{ fontSize: ".62rem", color: "var(--sub)", fontFamily: "var(--font-mono)", minWidth: 50, textAlign: "right" }}>
                                    {seg.count} v · {segPct}%
                                  </span>
                                </div>
                                <span style={{ fontSize: ".7rem", fontWeight: 700, color: seg.avg_margin === null ? "var(--muted)" : seg.avg_margin >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)", minWidth: 70, textAlign: "right" }}>
                                  {seg.avg_margin === null ? "—" : (seg.avg_margin >= 0 ? "+" : "") + seg.avg_margin.toLocaleString("fr-FR") + " €"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ─── Top agents (visible uniquement si compte entreprise / org manager) ─── */}
                    {stats.top_agents && stats.top_agents.length > 0 && (
                      <div style={{ marginTop: ".8rem", paddingTop: ".7rem", borderTop: "1px solid var(--border)" }}>
                        <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontFamily: "var(--font-mono)", marginBottom: ".4rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span>🏅 Top Suivi Ventes</span>
                          {stats.org_view?.company_name && (
                            <span style={{ textTransform: "none", letterSpacing: 0, fontSize: ".58rem", color: "var(--muted)", fontFamily: "var(--font-body)" }}>
                              {stats.org_view.nb_members} membre{stats.org_view.nb_members > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
                          {stats.top_agents.map((a, i) => (
                            <div key={a.user_uid} style={{
                              display: "grid",
                              gridTemplateColumns: "20px 1fr auto",
                              alignItems: "center", gap: ".5rem",
                              padding: ".25rem .4rem",
                              background: i === 0 ? "rgba(168,128,58,.05)" : "transparent",
                              borderRadius: 4
                            }}>
                              <span style={{ fontSize: ".62rem", fontWeight: 800, color: i === 0 ? "var(--accent)" : "var(--muted)", fontFamily: "var(--font-head)" }}>#{i + 1}</span>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: ".74rem", color: "var(--text)", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {a.name}
                                </div>
                                <div style={{ fontSize: ".58rem", color: "var(--sub)" }}>
                                  {a.nb_ventes} vente{a.nb_ventes > 1 ? "s" : ""} · CA {(a.total_sold / 1000).toFixed(0)}k €
                                  {a.avg_margin !== null && (
                                    <span> · moy. {a.avg_margin >= 0 ? "+" : ""}{a.avg_margin.toLocaleString("fr-FR")} €</span>
                                  )}
                                </div>
                              </div>
                              <span style={{ fontSize: ".82rem", fontWeight: 800, color: a.total_margin >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)", minWidth: 70, textAlign: "right" }}>
                                {a.total_margin >= 0 ? "+" : ""}{Math.round(a.total_margin).toLocaleString("fr-FR")} €
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* ── ONGLET PLATEFORMES — placeholder si pas de véhicule sélectionné ── */}
        {activeTab === "platforms" && !cur && (
          <div style={{ padding: "2.5rem 1.5rem", textAlign: "center", color: "var(--sub)", background: "var(--card)", border: "1.5px dashed var(--border)", borderRadius: 12 }}>
            <div style={{ fontSize: "2rem", marginBottom: ".5rem" }}>🌐</div>
            <div style={{ fontSize: ".95rem", fontWeight: 600, color: "var(--text)", marginBottom: ".4rem", fontFamily: "var(--font-head)" }}>
              Aucune cote sélectionnée
            </div>
            <div style={{ fontSize: ".82rem", maxWidth: 420, margin: "0 auto", lineHeight: 1.6 }}>
              Sélectionnez une cote depuis l'onglet "Vue d'ensemble" pour voir le comparatif des 5 plateformes recommandées.
            </div>
          </div>
        )}

        {/* ── ONGLET PLATEFORMES : stats globales par plateforme (ventes trackées) ── */}
        {activeTab === "platforms" && salesStats && (salesStats.platforms || []).length > 0 && (
          <div style={{ padding: "1.1rem 1.3rem", background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: ".5rem", marginBottom: ".8rem" }}>
              <div style={{ fontSize: ".88rem", fontWeight: 700, color: "var(--text)" }}>
                🏆 Vos performances globales par plateforme
              </div>
              <div style={{ fontSize: ".66rem", color: "var(--sub)", fontStyle: "italic" }}>
                sur l'ensemble de vos ventes trackées
              </div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".78rem" }}>
              <thead>
                <tr style={{ background: "var(--deep)" }}>
                  {["Plateforme", "Ventes", "Mix Pro / Part.", "CA total", "Marge totale", "Marge moyenne"].map(h => (
                    <th key={h} style={{ padding: ".5rem .8rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(salesStats.platforms || []).slice(0, 9).map((p, i) => {
                  const caTotal = (p.avg_sold || 0) * (p.count || 0);
                  const margeTotale = p.avg_margin !== null && p.avg_margin !== undefined ? p.avg_margin * (p.count || 0) : null;
                  return (
                    <tr key={i} style={{ borderBottom: i < Math.min(salesStats.platforms.length, 9) - 1 ? "1px solid var(--border)" : "none" }}>
                      <td style={{ padding: ".55rem .8rem", fontWeight: 600, color: "var(--text)" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: ".5rem" }}>
                          <PlatformLogo name={p.name} size={22} />
                          <span>{p.name || "—"}</span>
                        </span>
                      </td>
                      <td style={{ padding: ".55rem .8rem", color: "var(--sub)" }}>{p.count || 0}</td>
                      <td style={{ padding: ".55rem .8rem", color: "var(--sub)", fontFamily: "var(--font-mono)", fontSize: ".74rem" }}>
                        <span style={{ color: "#1877F2", fontWeight: 700 }}>{p.pro || 0}</span>
                        <span style={{ color: "var(--muted)" }}> / </span>
                        <span style={{ color: "var(--accent)", fontWeight: 700 }}>{p.particulier || 0}</span>
                      </td>
                      <td style={{ padding: ".55rem .8rem", color: "var(--text)", fontFamily: "var(--font-mono)" }}>
                        {Math.round(caTotal).toLocaleString("fr-FR")} €
                      </td>
                      <td style={{ padding: ".55rem .8rem", fontWeight: 700, fontFamily: "var(--font-head)", color: margeTotale !== null ? (margeTotale >= 0 ? "var(--green)" : "#9B2C2C") : "var(--muted)" }}>
                        {margeTotale !== null ? ((margeTotale >= 0 ? "+" : "") + Math.round(margeTotale).toLocaleString("fr-FR") + " €") : "n/a"}
                      </td>
                      <td style={{ padding: ".55rem .8rem", color: p.avg_margin !== null && p.avg_margin !== undefined ? (p.avg_margin >= 0 ? "var(--green)" : "#9B2C2C") : "var(--muted)", fontWeight: 600 }}>
                        {p.avg_margin !== null && p.avg_margin !== undefined ? ((p.avg_margin >= 0 ? "+" : "") + Math.round(p.avg_margin).toLocaleString("fr-FR") + " €") : "n/a"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── COMPARATIF ANNONCES 5 PLATEFORMES POUR L'ESTIMATION EN COURS ── */}
        {activeTab === "platforms" && cur && (() => {
          // Prix de référence VIQ pour l'estimation actuellement affichée
          const prixVIQ = cur.viqPrice || cur.marche || cur.argusMid || null;
          if (!prixVIQ) return null;
          // Récupérer les 5 plateformes recommandées de cur
          const platforms = (cur.platforms || []).slice(0, 5);
          if (!platforms.length) return null;
          // Calcul variance par positionnement plateforme (cohérent avec page estim.)
          const computeVariance = (name) => {
            const n = (name || "").toLowerCase();
            if (n.includes("centrale") || n.includes("autoscout")) return 1.04;
            if (n.includes("leboncoin") || n.includes("marketplace") || n.includes("facebook")) return 0.98;
            if (n.includes("paruvendu") || n.includes("argus") || n.includes("oui")) return 1.00;
            if (n.includes("autotrader") || n.includes("dealer") || n.includes("pro")) return 1.06;
            if (n.includes("monaco") || n.includes("luxury") || n.includes("prestige")) return 1.08;
            return 1.01;
          };
          return (
            <div className="pro-dash-platforms-block" style={{padding:"1.1rem 1.3rem", background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12}}>
              {/* Header : titre + prix VIQ explicite */}
              <div style={{display:"flex", alignItems:"baseline", justifyContent:"space-between", flexWrap:"wrap", gap:".5rem", marginBottom:".8rem"}}>
                <div>
                  <div style={{fontSize:".88rem", fontWeight:700, color:"var(--text)"}}>
                    📊 Prix indicatifs sur les 5 plateformes recommandées
                  </div>
                  <div style={{fontSize:".66rem", color:"var(--sub)", fontStyle:"italic", marginTop:".15rem"}}>
                    pour {cur.makeModel} · variance ±2-8 % selon positionnement
                  </div>
                </div>
                {/* Badges côte à côte : Estimation VIQ + Prix de vente réel (si saisi) */}
                <div style={{display:"flex", gap:".5rem", flexWrap:"wrap"}}>
                  {/* Badge VIQ — gradient orange */}
                  <div style={{background:"linear-gradient(135deg, var(--accent) 0%, var(--accent2) 100%)", color:"#fff", padding:".5rem .9rem", borderRadius:9, textAlign:"right", minWidth:140}}>
                    <div style={{fontSize:".58rem", textTransform:"uppercase", letterSpacing:"1px", fontFamily:"var(--font-mono)", opacity:.85, fontWeight:600}}>
                      Cote VIQ.AI®
                    </div>
                    <div style={{fontSize:"1.15rem", fontWeight:800, fontFamily:"var(--font-head)", marginTop:".1rem"}}>
                      {prixVIQ.toLocaleString("fr-FR")} €
                    </div>
                  </div>
                  {/* Badge Prix de vente réel — vert (apparaît si saleFollowups existe pour ce véhicule) */}
                  {(() => {
                    const eId = curEstimId();
                    const sf = saleFollowups[eId];
                    if (!sf || !sf.sold_price) return null;
                    const ecart = sf.sold_price - prixVIQ;
                    const ecartPct = Math.round((ecart / prixVIQ) * 100 * 10) / 10;
                    return (
                      <div style={{background:"linear-gradient(135deg, var(--green) 0%, #065F46 100%)", color:"#fff", padding:".5rem .9rem", borderRadius:9, textAlign:"right", minWidth:140}}>
                        <div style={{fontSize:".58rem", textTransform:"uppercase", letterSpacing:"1px", fontFamily:"var(--font-mono)", opacity:.85, fontWeight:600}}>
                          Prix de vente réel
                        </div>
                        <div style={{fontSize:"1.15rem", fontWeight:800, fontFamily:"var(--font-head)", marginTop:".1rem"}}>
                          {Number(sf.sold_price).toLocaleString("fr-FR")} €
                        </div>
                        <div style={{fontSize:".58rem", opacity:.85, marginTop:".1rem"}}>
                          {ecart > 0 ? "+" : ""}{ecartPct}% vs VIQ
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              {/* Grille 5 plateformes */}
              <div className="pro-dash-platforms-grid" style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:".55rem"}}>
                {platforms.map((p, i) => {
                  const platName = (typeof p === "string" ? p : (p.name || p.plateforme || p.platform || `Plateforme ${i + 1}`)).trim();
                  const score    = typeof p === "object" ? (p.score_vente || p.score || null) : null;
                  const variance = computeVariance(platName);
                  const prix     = Math.round(prixVIQ * variance / 100) * 100;
                  const ecart    = prix - prixVIQ;
                  const ecartPct = Math.round((ecart / prixVIQ) * 100 * 10) / 10;
                  const colors   = ["#0F766E", "#4ADE80", "#60A5FA", "#F59E0B", "#A78BFA"];
                  return (
                    <div key={i} style={{background:"var(--white)", border:"1.5px solid var(--border)", borderRadius:9, padding:".7rem .8rem", position:"relative"}}>
                      <div style={{display:"flex", alignItems:"center", gap:".4rem", marginBottom:".35rem"}}>
                        <div style={{width:22, height:22, borderRadius:5, background:`rgba(${i===0?"15,118,110":i===1?"74,222,128":i===2?"96,165,250":i===3?"245,158,11":"167,139,250"},.18)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:".68rem", fontWeight:800, color:colors[i], flexShrink:0}}>
                          {i + 1}
                        </div>
                        <span style={{fontSize:".75rem", fontWeight:700, color:"var(--text)", lineHeight:1.15, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}} title={platName}>
                          {platName.length > 18 ? platName.slice(0, 17) + "…" : platName}
                        </span>
                      </div>
                      <div style={{fontSize:"1.05rem", fontWeight:800, color:"var(--accent)", fontFamily:"var(--font-head)", lineHeight:1.1}}>
                        {prix.toLocaleString("fr-FR")} €
                      </div>
                      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:".35rem", fontSize:".62rem"}}>
                        <span style={{color: ecart > 0 ? "var(--green, var(--green))" : ecart < 0 ? "var(--sub)" : "var(--muted)", fontWeight:600}}>
                          {ecart > 0 ? "+" : ""}{ecartPct}% vs VIQ
                        </span>
                        {score && (
                          <span style={{color: score >= 85 ? "var(--green)" : score >= 70 ? "#F59E0B" : "var(--sub)", fontWeight:700, fontFamily:"var(--font-mono)"}}>
                            {score}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{fontSize:".6rem", color:"var(--muted)", marginTop:".55rem", textAlign:"center", fontStyle:"italic"}}>
                Cote calculée à partir de l'algorithme VIQ.AI® · à titre de repère, ne se substitue pas à l'annonce réelle
              </div>
            </div>
        );})()}

        {/* ── BLOC CONSEILS VIQ.AI® (issus de result.conseil_prix + conseils_vente) ── */}
        {activeTab === "vehicle" && cur && (() => {
          const r = cur.raw?.results || cur.raw?.result || {};
          const conseilPrix    = r.conseil_prix || r.recommandation_prix || null;
          const conseilsVente  = r.conseils_vente || r.points_attention || [];
          const pointsForts    = r.points_forts || [];
          // Si aucun conseil, ne pas afficher le bloc
          if (!conseilPrix && !conseilsVente.length && !pointsForts.length) return null;
          return (
            <div className="pro-dash-conseils-block" style={{padding:"1.1rem 1.3rem", background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12}}>
              <div style={{display:"flex", alignItems:"baseline", justifyContent:"space-between", flexWrap:"wrap", gap:".5rem", marginBottom:".7rem"}}>
                <div style={{fontSize:".88rem", fontWeight:700, color:"var(--text)"}}>
                  💡 Conseils VIQ.AI® · pour {cur.makeModel}
                </div>
                <div style={{fontSize:".62rem", color:"var(--sub)", fontStyle:"italic"}}>
                  Recommandations stratégiques générées lors de la Cote
                </div>
              </div>
              {/* Conseil principal (1 phrase, fond ambré) */}
              {conseilPrix && (
                <div style={{background:"rgba(168,128,58,.06)", border:"1.5px solid rgba(168,128,58,.18)", borderRadius:8, padding:".7rem .9rem", marginBottom:".7rem"}}>
                  <div style={{fontSize:".82rem", color:"var(--text)", lineHeight:1.5}}>
                    <strong style={{color:"var(--accent)"}}>Conseil pratique :</strong> {conseilPrix}
                  </div>
                </div>
              )}
              {/* Grille à 2 colonnes : Points forts + Conseils de vente */}
              <div className="pro-dash-conseils-grid" style={{display:"grid", gridTemplateColumns: (pointsForts.length > 0 && conseilsVente.length > 0) ? "1fr 1fr" : "1fr", gap:".7rem"}}>
                {pointsForts.length > 0 && (
                  <div style={{background:"var(--white)", border:"1.5px solid var(--border)", borderRadius:8, padding:".7rem .9rem"}}>
                    <div style={{fontSize:".62rem", color:"var(--green, var(--green))", textTransform:"uppercase", letterSpacing:".8px", marginBottom:".4rem", fontFamily:"var(--font-mono)", fontWeight:700}}>
                      ✓ Points forts
                    </div>
                    {pointsForts.slice(0, 5).map((p, i) => (
                      <div key={i} style={{fontSize:".76rem", color:"var(--sub)", marginBottom:".25rem", lineHeight:1.4}}>
                        ✓ {p}
                      </div>
                    ))}
                  </div>
                )}
                {conseilsVente.length > 0 && (
                  <div style={{background:"var(--white)", border:"1.5px solid var(--border)", borderRadius:8, padding:".7rem .9rem"}}>
                    <div style={{fontSize:".62rem", color:"var(--accent2, var(--accent-h))", textTransform:"uppercase", letterSpacing:".8px", marginBottom:".4rem", fontFamily:"var(--font-mono)", fontWeight:700}}>
                      → Conseils de vente
                    </div>
                    {conseilsVente.slice(0, 5).map((p, i) => (
                      <div key={i} style={{fontSize:".76rem", color:"var(--sub)", marginBottom:".25rem", lineHeight:1.4}}>
                        → {p}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
        );})()}

        {/* ── ONGLET SUIVI DE VENTE — toujours présent quand activeTab=sale ── */}
        {activeTab === "sale" && (() => {
          const stats = salesStats || {};
          const k = stats.kpis || {};
          // Récupérer les estimations qui ont un suivi de vente enregistré
          const trackedSales = entries
            .map((e, i) => ({ entry: e, idx: i, followup: saleFollowups[e.id || e.estim_id || e.uuid] }))
            .filter(x => x.followup)
            .slice(0, 8);
          return (
            <>
              <div className="saas-dash-content-header">
                <h2 className="saas-dash-content-title">💰 Suivi de vente</h2>
                <div className="saas-dash-content-sub">
                  Enregistrez les prix de vente réels pour calibrer la précision du moteur VIQ.AI® et suivre vos marges.
                </div>
              </div>

              {/* Card véhicule courant — saisie ou récap */}
              {cur && !followupOpen && (() => {
                const eId = curEstimId && curEstimId();
                const existing = eId ? saleFollowups[eId] : null;
                return (
                  <div style={{ padding: "1.5rem", background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12 }}>
                    <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text)", marginBottom: ".4rem", fontFamily: "var(--font-head)" }}>
                      {existing ? "✅" : "📝"} {cur.makeModel || "Véhicule courant"}
                    </div>
                    {existing ? (
                      <>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: ".8rem", marginTop: "1rem" }}>
                          <div>
                            <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700 }}>Prix vendu</div>
                            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--green)", fontFamily: "var(--font-head)", marginTop: ".15rem" }}>
                              {Number(existing.sold_price).toLocaleString("fr-FR")} €
                            </div>
                          </div>
                          {existing.purchase_price && (
                            <div>
                              <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700 }}>Prix d'achat</div>
                              <div style={{ fontSize: "1.3rem", fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-head)", marginTop: ".15rem" }}>
                                {Number(existing.purchase_price).toLocaleString("fr-FR")} €
                              </div>
                            </div>
                          )}
                          {existing.purchase_price && (
                            <div>
                              <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700 }}>Marge</div>
                              <div style={{ fontSize: "1.3rem", fontWeight: 800, color: (existing.sold_price - existing.purchase_price) >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)", marginTop: ".15rem" }}>
                                {(existing.sold_price - existing.purchase_price) >= 0 ? "+" : ""}{Math.round(existing.sold_price - existing.purchase_price).toLocaleString("fr-FR")} €
                              </div>
                            </div>
                          )}
                          {existing.platform && (
                            <div>
                              <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700 }}>Plateforme</div>
                              <div style={{ fontSize: ".95rem", fontWeight: 600, color: "var(--text)", marginTop: ".25rem" }}>
                                {existing.platform}
                              </div>
                            </div>
                          )}
                        </div>
                        <button onClick={() => setFollowupOpen(true)} className="btn btn-ghost" style={{ marginTop: "1.2rem", padding: ".5rem 1rem", fontSize: ".8rem" }}>
                          ✏️ Modifier les informations
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1.2rem" }}>
                          Enregistrez le prix de vente réel obtenu pour ce véhicule. Cette donnée nous permet de calibrer la précision du moteur VIQ.AI® et alimente votre tableau de marge.
                        </div>
                        <button onClick={() => setFollowupOpen(true)} className="btn btn-accent" style={{ padding: ".7rem 1.4rem", fontSize: ".88rem" }}>
                          + Renseigner le prix de vente
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}

              {!cur && (
                <div style={{ padding: "2rem 1.5rem", textAlign: "center", color: "var(--sub)", background: "var(--card)", border: "1.5px dashed var(--border)", borderRadius: 12 }}>
                  <div style={{ fontSize: "1.8rem", marginBottom: ".4rem" }}>💰</div>
                  <div style={{ fontSize: ".9rem", fontWeight: 600, color: "var(--text)", marginBottom: ".3rem", fontFamily: "var(--font-head)" }}>
                    Aucune cote sélectionnée
                  </div>
                  <div style={{ fontSize: ".78rem", maxWidth: 400, margin: "0 auto", lineHeight: 1.6 }}>
                    Sélectionnez une cote depuis l'onglet "Cotes" pour la suivre.
                  </div>
                </div>
              )}

              {/* Stats globales de suivi */}
              {(k.nb_sales_with_margin || k.total_margin) && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: ".8rem" }}>
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem" }}>
                    <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".3rem" }}>Ventes suivies</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-head)" }}>
                      {k.nb_ventes || 0}
                    </div>
                    <div style={{ fontSize: ".7rem", color: "var(--sub)", marginTop: ".15rem" }}>
                      {k.nb_sales_with_margin || 0} avec marge tracée
                    </div>
                  </div>
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem" }}>
                    <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".3rem" }}>CA total vendu</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-head)" }}>
                      {k.total_sold_volume ? Math.round(k.total_sold_volume).toLocaleString("fr-FR") + " €" : "—"}
                    </div>
                  </div>
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem" }}>
                    <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".3rem" }}>Marge totale</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: 800, color: (k.total_margin || 0) >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)" }}>
                      {k.total_margin !== null && k.total_margin !== undefined ? ((k.total_margin >= 0 ? "+" : "") + Math.round(k.total_margin).toLocaleString("fr-FR") + " €") : "—"}
                    </div>
                  </div>
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem" }}>
                    <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".3rem" }}>Marge moyenne</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: 800, color: (k.avg_margin || 0) >= 0 ? "var(--green)" : "#9B2C2C", fontFamily: "var(--font-head)" }}>
                      {k.avg_margin !== null && k.avg_margin !== undefined ? ((k.avg_margin >= 0 ? "+" : "") + Math.round(k.avg_margin).toLocaleString("fr-FR") + " €") : "—"}
                    </div>
                  </div>
                </div>
              )}

              {/* Historique des dernières ventes trackées */}
              {trackedSales.length > 0 && (
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: ".5rem" }}>
                    <div style={{ fontSize: ".88rem", fontWeight: 700, color: "var(--text)" }}>
                      📋 Dernières ventes enregistrées
                    </div>
                    <div style={{ fontSize: ".66rem", color: "var(--sub)", fontStyle: "italic" }}>
                      {trackedSales.length} vente{trackedSales.length > 1 ? "s" : ""} affichée{trackedSales.length > 1 ? "s" : ""}
                    </div>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".78rem" }}>
                    <thead>
                      <tr style={{ background: "var(--deep)" }}>
                        {["Véhicule", "Prix vendu", "Marge", "Plateforme", ""].map(h => (
                          <th key={h} style={{ padding: ".5rem .8rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".68rem", textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {trackedSales.map(({ entry: e, idx: i, followup: f }, n) => {
                        const margin = f.purchase_price ? (f.sold_price - f.purchase_price) : null;
                        return (
                          <tr key={n} style={{ borderBottom: n < trackedSales.length - 1 ? "1px solid var(--border)" : "none" }}>
                            <td style={{ padding: ".55rem .8rem", fontWeight: 600, color: "var(--text)" }}>
                              {e.makeModel || `Cote ${i + 1}`}
                            </td>
                            <td style={{ padding: ".55rem .8rem", color: "var(--text)", fontFamily: "var(--font-mono)", fontWeight: 700 }}>
                              {Number(f.sold_price).toLocaleString("fr-FR")} €
                            </td>
                            <td style={{ padding: ".55rem .8rem", fontWeight: 700, fontFamily: "var(--font-head)", color: margin !== null ? (margin >= 0 ? "var(--green)" : "#9B2C2C") : "var(--muted)" }}>
                              {margin !== null ? ((margin >= 0 ? "+" : "") + Math.round(margin).toLocaleString("fr-FR") + " €") : "—"}
                            </td>
                            <td style={{ padding: ".55rem .8rem", color: "var(--sub)", fontSize: ".74rem" }}>
                              {f.platform || "—"}
                            </td>
                            <td style={{ padding: ".55rem .8rem", textAlign: "right" }}>
                              <button onClick={() => { setIdx(i); setActiveTab("vehicle"); }} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: ".72rem", fontWeight: 600 }}>
                                Voir →
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {trackedSales.length === 0 && cur && (
                <div style={{ padding: "1.5rem", background: "var(--card)", border: "1.5px dashed var(--border)", borderRadius: 12, textAlign: "center" }}>
                  <div style={{ fontSize: ".85rem", color: "var(--sub)", lineHeight: 1.6 }}>
                    📊 Aucune vente trackée pour l'instant. Renseignez votre première vente pour démarrer le suivi.
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* ── FORMULAIRE SUIVI DE VENTE pour le véhicule courant ──
             Rendu uniquement dans l'onglet dédié « Suivi de vente ». Avant, il
             s'affichait aussi dans l'onglet « Cote détaillée », où il
             apparaissait sous le bloc 💡 Conseils VIQ.AI® et semblait masqué.
             Le bouton « Saisir prix vente » redirige désormais vers cet onglet. */}
        {followupOpen && cur && activeTab === "sale" && (
          <div className="pro-dash-followup-form" style={{padding:"1.2rem 1.4rem", background:"var(--white)", border:"1.5px solid var(--accent)", borderRadius:12}}>

            {/* ── En-tête : récap véhicule pour faciliter la saisie ── */}
            <div style={{
              background: "linear-gradient(135deg, rgba(168,128,58,.05), rgba(168,128,58,.01))",
              border: "1.5px solid rgba(168,128,58,.18)",
              borderRadius: 10,
              padding: "1rem 1.2rem",
              marginBottom: "1rem"
            }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: ".8rem", flexWrap: "wrap", marginBottom: ".5rem" }}>
                <div>
                  <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".2rem" }}>
                    💰 Suivi de vente — Cote #{idx + 1}
                  </div>
                  <div style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)", letterSpacing: "-.01em" }}>
                    {cur.makeModel || "Véhicule"}
                  </div>
                </div>
                {cur.date && (
                  <div style={{ fontSize: ".7rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>
                    Estimé le {cur.date}
                  </div>
                )}
              </div>

              {/* Caractéristiques véhicule en pilules */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginBottom: ".8rem" }}>
                {cur.year && (
                  <span style={{ fontSize: ".7rem", padding: ".25rem .65rem", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 100, color: "var(--text)", fontFamily: "var(--font-mono)" }}>
                    📅 {cur.year}
                  </span>
                )}
                {cur.km && (
                  <span style={{ fontSize: ".7rem", padding: ".25rem .65rem", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 100, color: "var(--text)", fontFamily: "var(--font-mono)" }}>
                    🛣️ {Number(cur.km).toLocaleString("fr-FR")} km
                  </span>
                )}
                {cur.fuel && (
                  <span style={{ fontSize: ".7rem", padding: ".25rem .65rem", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 100, color: "var(--text)" }}>
                    ⛽ {cur.fuel}
                  </span>
                )}
                {cur.gearbox && (
                  <span style={{ fontSize: ".7rem", padding: ".25rem .65rem", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 100, color: "var(--text)" }}>
                    ⚙️ {cur.gearbox}
                  </span>
                )}
                {cur.score && (
                  <span style={{ fontSize: ".7rem", padding: ".25rem .65rem", background: "rgba(168,128,58,.1)", border: "1px solid rgba(168,128,58,.25)", borderRadius: 100, color: "var(--accent)", fontWeight: 700, fontFamily: "var(--font-mono)" }}>
                    🛡️ Fiabilité {cur.reliabilityScore ?? cur.score}/100
                  </span>
                )}
              </div>

              {/* Bloc estimations de référence : VIQ.AI® + côte Argus si dispo */}
              {(cur.viqPrice || cur.argusLow || cur.argusHigh) && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: ".6rem" }}>
                  {cur.viqPrice && (
                    <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".55rem .8rem" }}>
                      <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".15rem" }}>Cote VIQ.AI®</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: ".5rem", flexWrap: "wrap" }}>
                        <div style={{ fontSize: "1.15rem", fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-head)" }}>
                          {Number(cur.viqPrice).toLocaleString("fr-FR")} €
                        </div>
                        <button
                          type="button"
                          onClick={() => setFollowupDraft(d => ({ ...d, sold_price: String(Math.round(cur.viqPrice)) }))}
                          style={{
                            fontSize: ".62rem", padding: ".18rem .5rem",
                            background: "rgba(168,128,58,.1)", border: "1px solid rgba(168,128,58,.25)",
                            borderRadius: 100, color: "var(--accent)", cursor: "pointer",
                            fontWeight: 600, fontFamily: "var(--font-body)",
                            transition: "background .15s"
                          }}
                          title="Pré-remplir le prix de vente avec la Cote VIQ.AI®"
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(168,128,58,.2)"}
                          onMouseLeave={e => e.currentTarget.style.background = "rgba(168,128,58,.1)"}
                        >
                          ↓ Utiliser
                        </button>
                      </div>
                    </div>
                  )}
                  {(cur.argusLow && cur.argusHigh) && (
                    <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".55rem .8rem" }}>
                      <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".15rem" }}>Côte Argus</div>
                      <div style={{ fontSize: ".88rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-mono)" }}>
                        {Number(cur.argusLow).toLocaleString("fr-FR")} – {Number(cur.argusHigh).toLocaleString("fr-FR")} €
                      </div>
                    </div>
                  )}
                  {cur.marche && !cur.viqPrice && (
                    <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".55rem .8rem" }}>
                      <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".15rem" }}>Prix marché médian</div>
                      <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)" }}>
                        {Number(cur.marche).toLocaleString("fr-FR")} €
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{fontSize:".74rem", color:"var(--sub)", marginBottom:"1rem", lineHeight:1.5}}>
              Renseignez le prix de vente réel obtenu. Information confidentielle utilisée uniquement pour calibrer nos cotes.
            </div>

            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:".7rem", marginBottom:".7rem"}}>
              <div>
                <label style={{fontSize:".68rem", color:"var(--sub)", display:"block", marginBottom:".25rem", fontWeight:600}}>Prix de vente réel (€) *</label>
                <input type="number" placeholder={cur.viqPrice ? `ex : ${Math.round(cur.viqPrice).toLocaleString("fr-FR")}` : "ex : 12 500"} value={followupDraft.sold_price}
                  onChange={ev => setFollowupDraft(d => ({ ...d, sold_price: ev.target.value }))}
                  style={{width:"100%", padding:".55rem .7rem", border:"1.5px solid var(--border)", borderRadius:6, fontSize:".88rem", background:"var(--white)", fontFamily:"var(--font-mono)"}} />
                {/* Calcul automatique de l'écart vs estimation VIQ */}
                {followupDraft.sold_price && cur.viqPrice && (() => {
                  const sp = Number(followupDraft.sold_price);
                  if (!sp || sp <= 0) return null;
                  const delta = sp - cur.viqPrice;
                  const deltaPct = (delta / cur.viqPrice) * 100;
                  const color = Math.abs(deltaPct) < 5 ? "var(--green)" : Math.abs(deltaPct) < 10 ? "var(--accent)" : "#9B2C2C";
                  return (
                    <div style={{ fontSize: ".68rem", color, marginTop: ".3rem", fontFamily: "var(--font-mono)" }}>
                      Écart vs VIQ.AI® : {delta >= 0 ? "+" : ""}{Math.round(delta).toLocaleString("fr-FR")} € ({delta >= 0 ? "+" : ""}{deltaPct.toFixed(1)} %)
                    </div>
                  );
                })()}
              </div>
              <div>
                <label style={{fontSize:".68rem", color:"var(--sub)", display:"block", marginBottom:".25rem", fontWeight:600}}>Date de vente</label>
                <input type="date" value={followupDraft.sold_date}
                  onChange={ev => setFollowupDraft(d => ({ ...d, sold_date: ev.target.value }))}
                  style={{width:"100%", padding:".55rem .7rem", border:"1.5px solid var(--border)", borderRadius:6, fontSize:".88rem", background:"var(--white)"}} />
              </div>
            </div>
            <div style={{marginBottom:".5rem"}}>
              <label style={{fontSize:".66rem", color:"var(--sub)", display:"block", marginBottom:".2rem"}}>Type de client</label>
              <div style={{display:"flex", gap:".4rem"}}>
                {[["pro", "Pro"], ["particulier", "Particulier"]].map(([val, lab]) => (
                  <button key={val} type="button"
                    onClick={() => setFollowupDraft(d => ({ ...d, client_type: val }))}
                    className="btn btn-ghost btn-sm"
                    style={{flex:1, fontSize:".78rem",
                      background: followupDraft.client_type === val ? "var(--accent)" : "transparent",
                      color: followupDraft.client_type === val ? "#fff" : "var(--text)",
                      borderColor: followupDraft.client_type === val ? "var(--accent)" : "var(--border)"}}>
                    {lab}
                  </button>
                ))}
              </div>
            </div>
            {/* ── Plateforme de vente : dropdown alimenté par les 10 plateformes recommandées du véhicule ── */}
            <div style={{marginBottom:".5rem"}}>
              <label style={{fontSize:".66rem", color:"var(--sub)", display:"block", marginBottom:".2rem"}}>
                Plateforme de vente
              </label>
              <select value={followupDraft.platform}
                onChange={ev => setFollowupDraft(d => ({ ...d, platform: ev.target.value, platform_custom: ev.target.value === "Autre" ? d.platform_custom : "" }))}
                style={{width:"100%", padding:".45rem .6rem", border:"1.5px solid var(--border)", borderRadius:5, fontSize:".82rem", background:"var(--white)", color: followupDraft.platform ? "var(--text)" : "var(--muted)"}}>
                <option value="">— Sélectionner —</option>
                {(cur.platforms || []).slice(0, 10).map((p, i) => {
                  const name = (typeof p === "string") ? p : (p?.name || p?.platform || "");
                  if (!name) return null;
                  return <option key={i} value={name}>{name}</option>;
                })}
                <option value="Autre">Autre…</option>
              </select>
              {/* Si "Autre" sélectionné, champ texte libre apparait */}
              {followupDraft.platform === "Autre" && (
                <input type="text" placeholder="Nom de la plateforme"
                  value={followupDraft.platform_custom}
                  onChange={ev => setFollowupDraft(d => ({ ...d, platform_custom: ev.target.value }))}
                  maxLength={80}
                  style={{width:"100%", marginTop:".35rem", padding:".4rem .6rem", border:"1.5px solid var(--border)", borderRadius:5, fontSize:".78rem", background:"var(--white)"}} />
              )}
            </div>
            {/* ── v5 : Méthode de retrait du véhicule ── */}
            <div style={{marginBottom:".5rem"}}>
              <label style={{fontSize:".66rem", color:"var(--sub)", display:"block", marginBottom:".2rem"}}>
                Méthode de retrait du véhicule
              </label>
              <div style={{display:"flex", gap:".4rem"}}>
                {[
                  ["concession", "🏢 En concession", "L'acheteur vient récupérer le véhicule sur place"],
                  ["expedition", "🚚 Expédition / Livraison", "Le véhicule est livré chez l'acheteur"],
                ].map(([val, lab, tooltip]) => (
                  <button key={val} type="button" title={tooltip}
                    onClick={() => setFollowupDraft(d => ({ ...d, pickup_method: val }))}
                    className="btn btn-ghost btn-sm"
                    style={{flex:1, fontSize:".78rem",
                      background: followupDraft.pickup_method === val ? "var(--accent)" : "transparent",
                      color: followupDraft.pickup_method === val ? "#fff" : "var(--text)",
                      borderColor: followupDraft.pickup_method === val ? "var(--accent)" : "var(--border)"}}>
                    {lab}
                  </button>
                ))}
              </div>
            </div>
            {/* ── Prix d'achat + marge calculée ── */}
            <div style={{marginBottom:".5rem"}}>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:".5rem"}}>
                <div>
                  <label style={{fontSize:".66rem", color:"var(--sub)", display:"block", marginBottom:".2rem"}}>
                    Prix d'achat (€) <span style={{color:"var(--muted)", fontWeight:400}}>· facultatif</span>
                  </label>
                  <input type="number" placeholder="ex : 10 000"
                    value={followupDraft.purchase_price}
                    onChange={ev => setFollowupDraft(d => ({ ...d, purchase_price: ev.target.value }))}
                    style={{width:"100%", padding:".45rem .6rem", border:"1.5px solid var(--border)", borderRadius:5, fontSize:".82rem", background:"var(--white)"}} />
                </div>
                <div>
                  <label style={{fontSize:".66rem", color:"var(--sub)", display:"block", marginBottom:".2rem"}}>
                    Marge brute (€) <span style={{color:"var(--muted)", fontWeight:400}}>· calculée</span>
                  </label>
                  {(() => {
                    const sp = parseInt(followupDraft.sold_price, 10);
                    const pp = parseInt(followupDraft.purchase_price, 10);
                    const validSp = !isNaN(sp) && sp > 0;
                    const validPp = !isNaN(pp) && pp > 0;
                    if (!validSp || !validPp) {
                      return (
                        <div style={{width:"100%", padding:".45rem .6rem", border:"1.5px solid var(--border)", borderRadius:5, fontSize:".82rem", background:"var(--card)", color:"var(--muted)", fontStyle:"italic"}}>
                          —
                        </div>
                      );
                    }
                    const margin = sp - pp;
                    const marginPct = pp > 0 ? Math.round((margin / pp) * 100) : 0;
                    const isPositive = margin >= 0;
                    return (
                      <div style={{
                        width:"100%", padding:".45rem .6rem",
                        border:"1.5px solid " + (isPositive ? "var(--green)" : "#9B2C2C"),
                        borderRadius:5, fontSize:".82rem",
                        background: isPositive ? "rgba(4,120,87,.06)" : "rgba(185,28,28,.06)",
                        color: isPositive ? "var(--green)" : "#9B2C2C", fontWeight:700,
                        display:"flex", justifyContent:"space-between", alignItems:"center"
                      }}>
                        <span>{isPositive ? "+" : ""}{margin.toLocaleString("fr-FR")} €</span>
                        <span style={{fontSize:".68rem", fontWeight:400}}>
                          {isPositive ? "+" : ""}{marginPct} %
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div style={{marginBottom:".7rem"}}>
              <label style={{fontSize:".66rem", color:"var(--sub)", display:"block", marginBottom:".2rem"}}>Notes (facultatif)</label>
              <input type="text" placeholder="ex : Reprise sous garantie, négocié à -200 €..."
                value={followupDraft.notes}
                onChange={ev => setFollowupDraft(d => ({ ...d, notes: ev.target.value }))}
                style={{width:"100%", padding:".45rem .6rem", border:"1.5px solid var(--border)", borderRadius:5, fontSize:".8rem", background:"var(--white)"}} />
            </div>
            <div style={{display:"flex", gap:".5rem"}}>
              <button className="btn btn-accent btn-sm" disabled={followupSaving}
                style={{flex:2, fontSize:".82rem", opacity: followupSaving ? .6 : 1}}
                onClick={saveCurrentFollowup}>
                {followupSaving ? "Enregistrement…" : "💾 Enregistrer"}
              </button>
              <button className="btn btn-ghost btn-sm" style={{flex:1, fontSize:".82rem"}}
                onClick={() => setFollowupOpen(false)}>
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Section détaillée du véhicule courant — visible uniquement sur l'onglet 'vehicle' */}
        {activeTab === "vehicle" && cur && (
        <div className="pro-dash-body">

          {/* ── VEHICLE IDENTITY STRIP ── */}
          <div style={{background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.2rem 1.4rem", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:".8rem"}}>
            <div>
              <div style={{fontSize:".65rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:"1.5px", fontWeight:700, marginBottom:".3rem", fontFamily:"var(--font-mono)", display:"flex", alignItems:"center", gap:".6rem", flexWrap:"wrap"}}>
                <span>Cote #{idx+1}</span>
                {cur.date && <span style={{color:"var(--muted)", fontWeight:500, textTransform:"none", letterSpacing:0}}>· {cur.date}</span>}
              </div>
              <div style={{fontSize:"1.4rem", fontWeight:800, letterSpacing:"-.03em", color:"var(--text)", lineHeight:1.1}}>
                {cur.makeModel}
              </div>
              {/* Piste 1 : prix prévu par l'utilisateur (si feedback existe) */}
              {(() => {
                const eId = cur.raw?.estimation_id || cur.raw?.id || `est_${cur.raw?.date}`;
                const fb = feedbacks[eId];
                if (!fb || !fb.user_price) return null;
                // Récupérer le prix ajusté si présent (feedback "non réaliste" avec calcul)
                const adj = fb.estimation_data?.prix_ajuste || null;
                const adjExp = fb.estimation_data?.prix_ajuste_explanation || null;
                return (
                  <div style={{fontSize:".74rem", color:"var(--sub)", marginTop:".3rem", lineHeight:1.5}}>
                    💭 Prix prévu par l'utilisateur : <strong style={{color:"var(--accent)"}}>{Number(fb.user_price).toLocaleString("fr-FR")} €</strong>
                    {adj && (
                      <span style={{marginLeft:".5rem"}}>
                        · 🎯 Cote ajustée : <strong style={{color:"var(--green, var(--green))"}}>{Number(adj).toLocaleString("fr-FR")} €</strong>
                        <span style={{fontSize:".65rem", color:"var(--muted)", marginLeft:".3rem"}}>(moyenne pondérée 50/50)</span>
                      </span>
                    )}
                  </div>
                );
              })()}
              <div style={{display:"flex", gap:".8rem", marginTop:".4rem", flexWrap:"wrap"}}>
                {cur.year && <span style={{fontSize:".75rem", color:"var(--sub)", fontFamily:"var(--font-mono)"}}>{cur.year}</span>}
                {cur.km   && <span style={{fontSize:".75rem", color:"var(--sub)", fontFamily:"var(--font-mono)"}}>{Number(cur.km).toLocaleString("fr-FR")} km</span>}
                {cur.fuel && <span style={{fontSize:".75rem", color:"var(--sub)"}}>{cur.fuel}</span>}
                {cur.gearbox && <span style={{fontSize:".75rem", color:"var(--sub)"}}>{cur.gearbox}</span>}
              </div>
              {(cur.immat || cur.vin) && (
                <div style={{display:"flex", gap:".5rem", marginTop:".45rem", flexWrap:"wrap"}}>
                  {cur.immat && (
                    <span style={{fontSize:".7rem", fontFamily:"var(--font-mono)", color:"var(--text)", background:"var(--card)", border:"1px solid var(--border)", borderRadius:6, padding:".15rem .5rem", letterSpacing:".5px"}}>
                      <span style={{color:"var(--muted)"}}>IMMAT</span> {cur.immat}
                    </span>
                  )}
                  {cur.vin && (
                    <span style={{fontSize:".7rem", fontFamily:"var(--font-mono)", color:"var(--text)", background:"var(--card)", border:"1px solid var(--border)", borderRadius:6, padding:".15rem .5rem", letterSpacing:".5px"}}>
                      <span style={{color:"var(--muted)"}}>VIN</span> {cur.vin}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div style={{display:"flex", alignItems:"center", gap:"1.2rem", flexWrap:"wrap"}}>
              {cur.score && (
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:"1.6rem", fontWeight:800, color:"var(--accent)", lineHeight:1}}>{cur.score}</div>
                  <div style={{fontSize:".62rem", color:"var(--muted)", fontFamily:"var(--font-mono)", marginTop:2}}>SCORE IQ</div>
                </div>
              )}
              {cur.delta !== null && (
                <div style={{background:"rgba(168,128,58,.07)", border:"1.5px solid rgba(168,128,58,.18)", borderRadius:10, padding:".5rem .9rem", textAlign:"center"}}>
                  <div style={{fontSize:"1rem", fontWeight:800, color:deltaColor}}>{deltaLabel}</div>
                  <div style={{fontSize:".58rem", color:"var(--muted)", fontFamily:"var(--font-mono)", marginTop:2, textTransform:"uppercase", letterSpacing:".5px"}}>Positionnement marché</div>
                </div>
              )}
              <div className="estim-actions-wrap" style={{textAlign:"right"}}>
                <div className="estim-actions">
                  {/* Partager l'estimation sur WhatsApp (déplacé depuis le footer) */}
                  <button className="estim-action-btn whatsapp"
                    onClick={() => {
                      const lines = [
                        `🚗 *${cur.makeModel}*`,
                        cur.year ? `📅 ${cur.year}` : null,
                        cur.km ? `🛣️ ${Number(cur.km).toLocaleString("fr-FR")} km` : null,
                        cur.fuel ? `⛽ ${cur.fuel}` : null,
                        cur.gearbox ? `⚙️ ${cur.gearbox}` : null,
                        "",
                        cur.viqPrice ? `💰 *Prix VIQ.AI® : ${cur.viqPrice.toLocaleString("fr-FR")} €*` : null,
                        cur.argusLow && cur.argusHigh ? `📊 Côte Argus : ${cur.argusLow.toLocaleString("fr-FR")} € → ${cur.argusHigh.toLocaleString("fr-FR")} €` : null,
                        (cur.reliabilityScore ?? cur.score) ? `🛡️ Score de fiabilité VIQ.AI® : ${cur.reliabilityScore ?? cur.score}/100` : null,
                        "",
                        "Cote propulsée par VehicleIQ — vehicleiq.fr",
                      ].filter(Boolean);
                      window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener,noreferrer");
                    }}
                    title={`Partager l'estimation de ${cur.makeModel} sur WhatsApp`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884"/>
                    </svg>
                    <span>Partager</span>
                  </button>
                  <button className="estim-action-btn"
                    onClick={() => onCreateMandat && onCreateMandat({
                      makeModel: cur.makeModel, year: cur.year, km: cur.km, fuel: cur.fuel, vin: cur.vin, immat: cur.immat,
                      gearbox: cur.gearbox, viqPrice: cur.viqPrice, estimation_id: curEstimId(),
                    })}
                    title="Générer un mandat de vente pré-rempli avec ce véhicule">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/>
                    </svg>
                    <span>Générer un mandat</span>
                  </button>
                  <button className="estim-action-btn"
                    onClick={() => onCreateCession && onCreateCession({
                      makeModel: cur.makeModel, year: cur.year, km: cur.km, fuel: cur.fuel, vin: cur.vin, immat: cur.immat,
                      gearbox: cur.gearbox, viqPrice: cur.viqPrice,
                    })}
                    title="Certificat de cession CERFA + Histovec">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 15l2 2 4-4"/>
                    </svg>
                    <span>Certificat de cession</span>
                  </button>
                  <button className="estim-action-btn"
                    onClick={() => setActiveTab("cartegrise")}
                    title="Calculer la carte grise et le malus écologique de ce véhicule">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4"/>
                    </svg>
                    <span>Carte grise</span>
                  </button>
                  <button className="estim-action-btn primary"
                    onClick={() => exportEntryToPDF(cur.raw || cur).catch(err => alert("Export PDF échoué : " + err.message))}
                    title="Exporter l'analyse complète de ce véhicule (3 pages PDF)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 12 15 15"/>
                    </svg>
                    <span>Exporter PDF</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── PRICE COMPARISON ── */}
          <div className="pro-dash-price-grid" style={{display:"grid", gridTemplateColumns:"1.2fr 1fr", gap:"1.1rem"}}>

            {/* Price bars */}
            <div style={{background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.3rem"}}>
              <div style={{fontFamily:"var(--font-head)", fontSize:"1.1rem", fontWeight:500, color:"var(--text)", marginBottom:".2rem", letterSpacing:"-.01em"}}>Comparaison de prix</div>
              <div style={{fontSize:".7rem", color:"var(--muted)", marginBottom:"1.2rem"}}>Cote VIQ.AI® Engine (70/30) · TAM (90/10) · Côte Argus</div>

              {/* Estimation VIQ.AI® Engine — prix algo exact (argus_mid × 0.95) */}
              <div style={{marginBottom:"1.2rem"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:".5rem"}}>
                  <div style={{display:"flex", alignItems:"center", gap:".4rem"}}>
                    <div style={{width:10,height:10,borderRadius:3,background:"var(--accent)",flexShrink:0}}/>
                    <span style={{fontSize:".75rem", fontWeight:700, color:"var(--text)"}}>Cote VIQ.AI® Engine</span>
                  </div>
                  <span style={{fontSize:"1.1rem", fontWeight:800, color:"var(--accent)", fontFamily:"var(--font-mono)"}}>
                    {cur.viqPrice ? cur.viqPrice.toLocaleString("fr-FR")+" €" : "—"}
                  </span>
                </div>
                {/* Barre superposée : Argus high (orange) en fond + VIQ (bleu) par-dessus */}
                <div style={{position:"relative", height:14, background:"var(--border)", borderRadius:7, overflow:"hidden"}}>
                  {/* Couche 1 : Argus haut (orange transparent), va jusqu'à argus_high */}
                  {cur.argusHigh && (
                    <div style={{position:"absolute", top:0, left:0, height:"100%",
                      width:barPct(cur.argusHigh)+"%",
                      background:"rgba(168,128,58,.18)", borderRadius:7,
                      transition:"width .6s ease"}}/>
                  )}
                  {/* Couche 2 : VIQ.AI® Engine (orange plein), prix recommandé */}
                  <div style={{position:"absolute", top:0, left:0, height:"100%",
                    width:barPct(cur.viqPrice)+"%",
                    background:"linear-gradient(90deg,var(--accent),#C49654)",
                    borderRadius:7, transition:"width .6s ease", opacity:.92}}/>
                  {/* Marqueur fin Argus haut (trait orange foncé) */}
                  {cur.argusHigh && cur.viqPrice && (
                    <div style={{position:"absolute", top:0,
                      left:`calc(${barPct(cur.argusHigh)}% - 1px)`,
                      height:"100%", width:2, background:"#A8803A"}}/>
                  )}
                </div>
                <div style={{display:"flex", gap:".9rem", marginTop:".4rem", flexWrap:"wrap"}}>
                  <div style={{display:"flex", alignItems:"center", gap:".3rem"}}>
                    <div style={{width:8,height:8,borderRadius:2,background:"var(--accent)"}}/>
                    <span style={{fontSize:".65rem", color:"var(--muted)"}}>VIQ.AI® Engine (argus_mid × 0.95)</span>
                  </div>
                  <div style={{display:"flex", alignItems:"center", gap:".3rem"}}>
                    <div style={{width:8,height:8,borderRadius:2,background:"rgba(168,128,58,.4)"}}/>
                    <span style={{fontSize:".65rem", color:"var(--muted)"}}>Côte Argus haute {cur.argusHigh ? `(${cur.argusHigh.toLocaleString("fr-FR")} €)` : ""}</span>
                  </div>
                </div>
              </div>

              {/* TAM — Tendance Actuelle Marché 90/10 */}
              <div style={{marginBottom:"1rem"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:".35rem"}}>
                  <div style={{display:"flex", alignItems:"center", gap:".4rem"}}>
                    <div style={{width:10,height:10,borderRadius:3,background:"#0F766E",flexShrink:0}}/>
                    <span style={{fontSize:".75rem", fontWeight:700, color:"var(--text)"}}>Tendance Actuelle Marché <span style={{fontSize:".65rem", color:"var(--muted)", fontWeight:400}}>(90/10)</span></span>
                  </div>
                  <span style={{fontSize:"1.05rem", fontWeight:800, color:"var(--green)", fontFamily:"var(--font-mono)"}}>
                    {cur.tam ? cur.tam.toLocaleString("fr-FR")+" €" : "—"}
                  </span>
                </div>
                <div style={{height:10, background:"var(--border)", borderRadius:5, overflow:"hidden"}}>
                  <div style={{height:"100%", width:barPct(cur.tam)+"%", background:"linear-gradient(90deg,#0F766E,#34D4BC)", borderRadius:5, transition:"width .6s ease"}}/>
                </div>
              </div>

              {/* Côte Argus — fourchette complète low → high avec marqueur low */}
              <div>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:".35rem"}}>
                  <div style={{display:"flex", alignItems:"center", gap:".4rem"}}>
                    <div style={{width:10,height:10,borderRadius:3,background:"#A8803A",flexShrink:0}}/>
                    <span style={{fontSize:".75rem", fontWeight:700, color:"var(--text)"}}>Côte Argus</span>
                  </div>
                  <span style={{fontSize:"1.05rem", fontWeight:800, color:"#A8803A", fontFamily:"var(--font-mono)"}}>
                    {cur.argusLow ? cur.argusLow.toLocaleString("fr-FR")+" €" : "—"}
                    {cur.argusHigh && cur.argusHigh !== cur.argusLow ? ` → ${cur.argusHigh.toLocaleString("fr-FR")} €` : ""}
                  </span>
                </div>
                {/* Barre étendue de 0 à argus_high, avec trait vertical à argus_low */}
                <div style={{position:"relative", height:10, background:"var(--border)", borderRadius:5, overflow:"hidden"}}>
                  {/* Couche 1 : zone "marché Argus" (de 0 à argusHigh) en orange clair */}
                  {cur.argusHigh && (
                    <div style={{height:"100%", width:barPct(cur.argusHigh)+"%",
                      background:"linear-gradient(90deg,rgba(168,128,58,.45),#D4A455)",
                      borderRadius:5, transition:"width .6s ease"}}/>
                  )}
                  {/* Couche 2 : marqueur trait vertical pour Argus low (limite basse marché) */}
                  {cur.argusLow && cur.argusHigh && cur.argusLow !== cur.argusHigh && (
                    <div style={{position:"absolute", top:-2,
                      left:`calc(${barPct(cur.argusLow)}% - 1px)`,
                      height:"calc(100% + 4px)", width:2,
                      background:"var(--accent)", boxShadow:"0 0 4px rgba(168,128,58,.5)"}}
                      title={`Argus bas : ${cur.argusLow.toLocaleString("fr-FR")} €`}/>
                  )}
                </div>
                {/* Légende low/high */}
                {cur.argusLow && cur.argusHigh && (
                  <div style={{display:"flex", justifyContent:"space-between", marginTop:".3rem", fontSize:".62rem", color:"var(--muted)"}}>
                    <span>Bas marché : <strong style={{color:"var(--accent)"}}>{cur.argusLow.toLocaleString("fr-FR")} €</strong></span>
                    <span>Haut marché : <strong style={{color:"#A8803A"}}>{cur.argusHigh.toLocaleString("fr-FR")} €</strong></span>
                  </div>
                )}
              </div>

              {/* Delta insight */}
              {cur.marche && cur.tam && (
                <div style={{marginTop:"1.2rem", padding:".6rem .9rem", background:"rgba(168,128,58,.05)", border:"1.5px solid rgba(168,128,58,.15)", borderRadius:8}}>
                  <span style={{fontSize:".72rem", color:"var(--sub)"}}>
                    Cote VIQ.AI® (70/30) {cur.marche > cur.tam ? "supérieure" : "inférieure"} au TAM (90/10) de{" "}
                    <strong style={{color:deltaColor}}>{Math.abs(Math.round((cur.marche - cur.tam)/cur.tam*100))}%</strong>.{" "}
                    {cur.delta > 5 ? "Prix de vente optimisé pour un marché acheteur actif."
                     : cur.delta < -5 ? "Opportunité d'achat — véhicule sous-coté."
                     : "Pricing en ligne avec le marché actuel."}
                  </span>
                </div>
              )}
            </div>

            {/* Depreciation curve */}
            <div style={{background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.3rem"}}>
              <div style={{fontFamily:"var(--font-head)", fontSize:"1.1rem", fontWeight:500, color:"var(--text)", marginBottom:".2rem", letterSpacing:"-.01em", display:"flex", alignItems:"center", flexWrap:"wrap", gap:".4rem"}}>
                Courbe de dépréciation
                {segment === "exotique" && <span style={{ fontFamily:"var(--font-mono)", fontSize:".58rem", padding:".18rem .55rem", background:"rgba(168,128,58,.12)", color:"var(--accent)", borderRadius:100, fontWeight:600, textTransform:"uppercase", letterSpacing:".5px" }}>Exotique · collection</span>}
                {segment === "premium" && <span style={{ fontFamily:"var(--font-mono)", fontSize:".58rem", padding:".18rem .55rem", background:"rgba(30,42,74,.08)", color:"var(--accent2)", borderRadius:100, fontWeight:600, textTransform:"uppercase", letterSpacing:".5px" }}>Premium</span>}
                {segment === "generaliste" && <span style={{ fontFamily:"var(--font-mono)", fontSize:".58rem", padding:".18rem .55rem", background:"var(--card)", color:"var(--sub)", borderRadius:100, fontWeight:600, textTransform:"uppercase", letterSpacing:".5px" }}>Généraliste</span>}
              </div>
              <div style={{fontSize:".7rem", color:"var(--muted)", marginBottom:".9rem"}}>
                {segment === "exotique" ? "Projection 8 ans · les exotiques résistent à la dépréciation et s'apprécient après 5 ans"
                  : segment === "premium" ? "Projection 8 ans · dépréciation lente les 3 premières années, accélération après"
                  : "VIQ.AI® 70/30 vs Argus — projection 8 ans"}
              </div>
              {spark.length > 1 ? (
                <>
                  <div style={{position:"relative", height:88}}>
                    <svg viewBox="0 0 308 88" style={{width:"100%", height:"100%"}} preserveAspectRatio="none">
                      <defs>
                        <linearGradient id={`dg${idx}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--accent)" stopOpacity=".18"/>
                          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0"/>
                        </linearGradient>
                        <linearGradient id={`dga${idx}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#A8803A" stopOpacity=".12"/>
                          <stop offset="100%" stopColor="#A8803A" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                      {[0,.33,.66,1].map(pct => (
                        <line key={pct} x1="0" y1={80*(1-pct)} x2="308" y2={80*(1-pct)}
                          stroke="var(--border)" strokeWidth="1" strokeDasharray="3,4"/>
                      ))}
                      {/* Argus area + line (fond) */}
                      {sparkArgus.length > 1 && (
                        <path d={sparkArgusPath + ` L${sparkArgus[sparkArgus.length-1].x},80 L0,80 Z`}
                          fill={`url(#dga${idx})`}/>
                      )}
                      {sparkArgusPath && (
                        <path d={sparkArgusPath} fill="none" stroke="#A8803A"
                          strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"
                          strokeDasharray="4,3" opacity=".55"/>
                      )}
                      {/* VIQ.AI 70/30 area + line (dessus) */}
                      {sparkArea && <path d={sparkArea} fill={`url(#dg${idx})`}/>}
                      <path d={sparkPath} fill="none" stroke="var(--accent)" strokeWidth="1.5"
                        strokeLinecap="round" strokeLinejoin="round"/>
                      {spark.map((p,i) => (
                        <circle key={i} cx={p.x} cy={p.y} r={i===0?3.5:2}
                          fill="var(--accent)" stroke="var(--bg)" strokeWidth="1.2"/>
                      ))}
                    </svg>
                  </div>
                  <div style={{display:"flex", justifyContent:"space-between", marginTop:".5rem"}}>
                    {["An 0","An 2","An 4","An 6","An 8"].map((l,i) => (
                      <span key={i} style={{fontSize:".62rem", color:"var(--muted)", fontFamily:"var(--font-mono)"}}>{l}</span>
                    ))}
                  </div>
                  {/* Légende courbes */}
                  <div style={{display:"flex", gap:".9rem", marginTop:".6rem", marginBottom:".5rem"}}>
                    <div style={{display:"flex", alignItems:"center", gap:".3rem"}}>
                      <div style={{width:14,height:2,borderRadius:1,background:"var(--accent)"}}/>
                      <span style={{fontSize:".62rem", color:"var(--muted)"}}>VIQ.AI® 70/30</span>
                    </div>
                    <div style={{display:"flex", alignItems:"center", gap:".3rem"}}>
                      <svg width="14" height="2"><line x1="0" y1="1" x2="14" y2="1" stroke="#A8803A" strokeWidth="1.2" strokeDasharray="3,2" opacity=".55"/></svg>
                      <span style={{fontSize:".62rem", color:"var(--muted)"}}>Argus</span>
                    </div>
                  </div>
                  <div style={{marginTop:".3rem", display:"flex", justifyContent:"space-between"}}>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:".6rem", color:"var(--muted)"}}>VIQ.AI® Engine</div>
                      <div style={{fontSize:".85rem", fontWeight:700, color:"var(--accent)", fontFamily:"var(--font-mono)"}}>
                        {(cur.viqPrice||cur.marche)?.toLocaleString("fr-FR")} €
                      </div>
                    </div>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:".6rem", color:"var(--muted)"}}>Argus An 0</div>
                      <div style={{fontSize:".85rem", fontWeight:700, color:"#A8803A", fontFamily:"var(--font-mono)"}}>
                        {cur.argusLow?.toLocaleString("fr-FR") || "—"} €
                      </div>
                    </div>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:".6rem", color:"var(--muted)"}}>VIQ dans 4 ans</div>
                      <div style={{fontSize:".85rem", fontWeight:700, color:"var(--sub)", fontFamily:"var(--font-mono)"}}>
                        {cur.viqPrice ? Math.round((cur.viqPrice||0)*0.64).toLocaleString("fr-FR")+" €" : "—"}
                      </div>
                    </div>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:".6rem", color:"var(--muted)"}}>VIQ dans 8 ans</div>
                      <div style={{fontSize:".85rem", fontWeight:700, color:"#F87171", fontFamily:"var(--font-mono)"}}>
                        {cur.viqPrice ? Math.round((cur.viqPrice||0)*0.46).toLocaleString("fr-FR")+" €" : "—"}
                      </div>
                    </div>
                  </div>
                  {/* ── Conseil contextuel ── */}
                  {(() => {
                    const loss4  = cur.viqPrice ? cur.viqPrice - Math.round(cur.viqPrice*0.64) : (cur.marche ? cur.marche - Math.round(cur.marche*0.64) : 0);
                    const diff   = cur.marche && cur.tam ? Math.round((cur.marche - cur.tam)/cur.tam*100) : 0;
                    const isAbove = diff >= 0;
                    // Contexte kilométrage
                    const kmNum = cur.km ? Number(cur.km) : 0;
                    const loss8pct = Math.round((1 - 0.46) * 100);
                    const tamGap = cur.tam ? Math.round(Math.abs(cur.tam - cur.marche)).toLocaleString("fr-FR") : "—";
                    // Choisir le conseil le plus pertinent selon les données disponibles
                    let headline, detail, color;
                    if (kmNum > 100000) {
                      headline = "⚠️ Kilométrage élevé — optimisez votre prix";
                      detail = `Au-delà de 100 000 km, la décote s'accélère. L'écart TAM/VIQ.AI® de ${tamGap} € reflète cette sensibilité. Agir rapidement limite la perte.`;
                      color = "#F59E0B";
                    } else if (cur.score && cur.score >= 80) {
                      headline = "✅ Score de confiance élevé — dossier vendable";
                      detail = `Avec un ${viqIndexLabel()} de ${cur.score}/100, ce véhicule présente un fort potentiel marché. Perte estimée sur 4 ans : ~${loss4.toLocaleString("fr-FR")} € (−36%).`;
                      color = "var(--green)";
                    } else if (isAbove) {
                      headline = "⚡ Fenêtre de vente optimale : maintenant";
                      detail = `Ce véhicule perdra ~${loss4.toLocaleString("fr-FR")} € en 4 ans (−36%). Vendre maintenant maximise votre retour sur investissement.`;
                      color = "var(--accent)";
                    } else {
                      headline = `📉 Dépréciation sur 8 ans : −${loss8pct}%`;
                      detail = `Perte estimée à 8 ans : ~${(cur.marche ? cur.marche - Math.round(cur.marche*0.46) : 0).toLocaleString("fr-FR")} €. Écart VIQ.AI® / TAM actuel : ${tamGap} €.`;
                      color = "var(--sub)";
                    }
                    if (!cur.viqPrice && !cur.marche) return null;
                    return (
                      <div style={{
                        marginTop:"1rem", padding:".85rem 1rem",
                        background: "rgba(168,128,58,.05)",
                        border: "1.5px solid rgba(168,128,58,.15)",
                        borderRadius:10,
                      }}>
                        <div style={{fontSize:".8rem", fontWeight:800,
                          color: color,
                          marginBottom:".3rem", letterSpacing:"-.01em"}}>
                          {headline}
                        </div>
                        <div style={{fontSize:".75rem", color:"var(--sub)", lineHeight:1.6}}>
                          {detail}
                        </div>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:88,color:"var(--muted)",fontSize:".82rem"}}>
                  Prix estimé requis
                </div>
              )}
            </div>
          </div>

          {/* ══ LIGNE 1 : Plateformes + Indice VIQ ══ */}
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem"}}>

            {/* ── Plateformes ── */}
            <div style={{background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.2rem 1.4rem"}}>
              <div style={{fontSize:".72rem", fontWeight:700, color:"var(--text)", marginBottom:".9rem", textTransform:"uppercase", letterSpacing:"1px"}}>Plateformes recommandées</div>
              {cur.platforms?.length > 0 ? (
                <div style={{display:"flex", flexDirection:"column", gap:".5rem"}}>
                  {cur.platforms.slice(0,5).map((pl,i) => {
                    const name  = typeof pl==="string" ? pl : pl.name || String(pl);
                    const score = typeof pl==="object" ? (pl.score_vente || pl.score || null) : null;
                    const colors= ["#0F766E","#4ADE80","#60A5FA","#F59E0B","#A78BFA"];
                    return (
                      <div key={i} style={{display:"flex", alignItems:"center", gap:".65rem", padding:".55rem .8rem", background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, transition:"border-color .15s"}}>
                        <div style={{width:28, height:28, borderRadius:7, background:`rgba(${i===0?"0,191,165":i===1?"74,222,128":i===2?"96,165,250":i===3?"245,158,11":"167,139,250"},.15)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:".7rem", fontWeight:800, color:colors[i], flexShrink:0}}>
                          {i+1}
                        </div>
                        <span style={{fontSize:".85rem", fontWeight:600, color:"var(--text)", flex:1}}>{name}</span>
                        {score && (
                          <div style={{display:"flex", alignItems:"center", gap:".4rem", flexShrink:0}}>
                            <div style={{width:48, height:5, background:"var(--border)", borderRadius:3, overflow:"hidden"}}>
                              <div style={{height:"100%", width:score+"%", background:score>=85?"var(--green)":score>=70?"#F59E0B":"var(--sub)", borderRadius:3}}/>
                            </div>
                            <span style={{fontSize:".68rem", fontFamily:"var(--font-mono)", color: score>=85?"var(--green)":score>=70?"#F59E0B":"var(--sub)", fontWeight:700, minWidth:28}}>{score}%</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{color:"var(--muted)", fontSize:".82rem", padding:"1rem 0", textAlign:"center"}}>Données plateformes non disponibles</div>
              )}
            </div>

            {/* ── Indice VIQ détaillé ── */}
            <div style={{background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.2rem 1.4rem"}}>
              <div style={{fontSize:".65rem", fontWeight:700, color:"var(--muted)", letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:".9rem"}}>{viqIndexLabel()}</div>
              <div style={{display:"flex", alignItems:"center", gap:"1.2rem", marginBottom:"1.1rem"}}>
                {/* Donut large */}
                {(() => {
                  const sc = cur.score || 0;
                  const R=34, C=2*Math.PI*R;
                  const col = sc>=85 ? "#0F766E" : sc>=65 ? "var(--accent)" : "#F59E0B";
                  return (
                    <div style={{position:"relative", width:82, height:82, flexShrink:0}}>
                      <svg width="82" height="82" style={{transform:"rotate(-90deg)"}}>
                        <circle cx="41" cy="41" r={R} fill="none" stroke="var(--border)" strokeWidth={7}/>
                        <circle cx="41" cy="41" r={R} fill="none" stroke={col} strokeWidth={7}
                          strokeDasharray={C} strokeDashoffset={C*(1-sc/100)}
                          strokeLinecap="round"
                          style={{filter:`drop-shadow(0 0 5px ${col}88)`, transition:"stroke-dashoffset .6s ease"}}/>
                      </svg>
                      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                        <span style={{fontSize:"1.1rem",fontWeight:800,color:col,lineHeight:1}}>{sc||"—"}</span>
                        <span style={{fontSize:".52rem",color:"var(--muted)"}}>/100</span>
                      </div>
                    </div>
                  );
                })()}
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:"1.55rem", fontWeight:800, color:"var(--text)", letterSpacing:"-.04em", lineHeight:1}}>
                    {(cur.viqPrice||cur.marche)?.toLocaleString("fr-FR")} €
                  </div>
                  <div style={{fontSize:".75rem", color:"var(--sub)", marginTop:".3rem"}}>
                    Fourchette : {cur.argusLow?.toLocaleString("fr-FR") || "—"} € — {(cur.argusMid||cur.argusLow)?.toLocaleString("fr-FR") || "—"} €
                  </div>
                  <div style={{display:"flex", alignItems:"center", gap:".4rem", marginTop:".55rem"}}>
                    <div style={{width:7,height:7,borderRadius:"50%",background:"var(--green)",boxShadow:"0 0 5px var(--green)",flexShrink:0}}/>
                    <span style={{fontSize:".68rem", color:"var(--green)", fontWeight:600, fontFamily:"var(--font-mono)"}}>Scan web — données fraîches</span>
                  </div>
                </div>
              </div>
              {/* Sparkline 7j — adaptée au modèle et à son segment */}
              {(() => {
                const base = cur.viqPrice || cur.marche;
                if (!base) return null;
                // Hash stable basé sur le nom du véhicule pour des variations propres au modèle
                // (la même voiture donne toujours la même courbe, mais 2 modèles différents donnent
                // 2 courbes différentes).
                const hashStr = (s) => {
                  let h = 0;
                  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
                  return Math.abs(h);
                };
                const seed = hashStr(cur.makeModel || cur.make || "vehicle");
                // PRNG simple déterministe basé sur le seed
                const rng = (n) => {
                  let x = seed + n * 2654435761;
                  x = ((x ^ (x >>> 15)) * 2246822507) >>> 0;
                  return (x & 0xFFFF) / 0xFFFF; // 0..1
                };
                // Profil de volatilité selon segment
                //  - exotique  : très peu de volatilité (marché lent, prix soutenus, légère hausse)
                //  - premium   : volatilité modérée, tendance neutre/légère hausse
                //  - généraliste : volatilité forte, fluctuations classiques
                const segCfg = segment === "exotique"
                  ? { amp: 0.012, drift: 0.0025 }
                  : segment === "premium"
                    ? { amp: 0.025, drift: 0.0012 }
                    : { amp: 0.040, drift: 0.0008 };
                // Génération des 7 multiplicateurs : marche pseudo-aléatoire bornée
                const mults = [];
                let v = 1.0;
                for (let i = 0; i < 7; i++) {
                  const noise = (rng(i + 1) - 0.5) * segCfg.amp * 2;
                  v = v + noise + segCfg.drift;
                  v = Math.max(0.92, Math.min(1.08, v)); // bornes raisonnables
                  mults.push(v);
                }
                // Calcul des points en SVG
                const minM = Math.min(...mults);
                const maxM = Math.max(...mults);
                const range = Math.max(0.01, maxM - minM);
                const pts7 = mults.map((f, i) => ({
                  x: i / 6 * 280,
                  y: 36 - ((f - minM) / range) * 32
                }));
                const sp = pts7.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
                const sa = sp + ` L280,38 L0,38 Z`;
                const days = ["J-6","J-5","J-4","J-3","J-2","J-1","Auj"];
                // Couleur de la courbe selon la tendance globale (J-6 → Auj)
                const trend = mults[6] - mults[0];
                const trendColor = trend > 0.005 ? "var(--green)" : trend < -0.005 ? "var(--red)" : "var(--accent)";
                // Nombre d'annonces simulé mais cohérent (basé sur segment)
                const annoncesBase = segment === "exotique" ? 12 : segment === "premium" ? 65 : 145;
                const annonces = Math.round(annoncesBase + (rng(99) - 0.5) * annoncesBase * 0.3);
                // Plateformes top dépendant du segment
                const platsBySegment = {
                  exotique: ["CarAndClassic", "Collecting Cars", "Classic Driver", "Bring a Trailer"],
                  premium: ["AutoScout24", "Mobile.de", "La Centrale", "LeBonCoin"],
                  generaliste: ["LeBonCoin", "AutoScout24", "La Centrale", "Facebook Marketplace"],
                };
                const plats = platsBySegment[segment];
                return (
                  <div>
                    <div style={{fontSize:".65rem", color:"var(--muted)", marginBottom:".4rem", fontWeight:600}}>
                      Évolution marché web — 7 derniers jours · {cur.makeModel || "modèle"}
                    </div>
                    <div style={{height:42, position:"relative", background:"var(--card)", borderRadius:8, overflow:"hidden", padding:"4px 0"}}>
                      <svg viewBox="0 0 280 38" style={{width:"100%",height:"100%"}} preserveAspectRatio="none">
                        <defs>
                          <linearGradient id={`spkG3_${idx}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={trendColor} stopOpacity=".3"/>
                            <stop offset="100%" stopColor={trendColor} stopOpacity="0"/>
                          </linearGradient>
                        </defs>
                        <path d={sa} fill={`url(#spkG3_${idx})`}/>
                        <path d={sp} fill="none" stroke={trendColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx={pts7[6].x} cy={pts7[6].y} r="3.5" fill={trendColor} stroke="white" strokeWidth="1.5"/>
                      </svg>
                    </div>
                    <div style={{display:"flex", justifyContent:"space-between", marginTop:".3rem"}}>
                      {days.map(d=><span key={d} style={{fontSize:".58rem",color:"var(--muted)",fontFamily:"var(--font-mono)"}}>{d}</span>)}
                    </div>
                    <div style={{display:"flex", gap:".45rem", marginTop:".6rem", flexWrap:"wrap", alignItems:"center"}}>
                      {plats.map(s=>(
                        <span key={s} style={{fontSize:".62rem",background:"rgba(168,128,58,.08)",border:"1.5px solid rgba(168,128,58,.18)",color:"var(--accent)",padding:".15rem .5rem",borderRadius:50,fontWeight:600}}>{s}</span>
                      ))}
                      <span style={{fontSize:".62rem",color:"var(--muted)",fontFamily:"var(--font-mono)",marginLeft:"auto"}}>{annonces} annonces</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ══ LIGNE 2 : Signal Marché Live + Analyse de Risque ══ */}
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem"}}>

            {/* ── Signal Marché Live ── */}
            {(() => {
              const kmNum  = cur.km ? Number(cur.km) : 0;
              const price  = cur.viqPrice || cur.marche || 0;
              const popularSegments = ["Clio","208","Golf","308","Captur","Yaris","C3","Polo","Fiesta","Twingo"];
              const isPop  = popularSegments.some(s => (cur.makeModel||"").toLowerCase().includes(s.toLowerCase()));
              const annonces   = isPop ? Math.floor(180 + Math.random()*120) : Math.floor(40 + Math.random()*80);
              const medianDiff = Math.round((Math.random()*0.06 - 0.02) * price / 100) * 100;
              const median     = price + medianDiff;
              const isUnder    = price <= median;
              const delaiBase  = isPop ? 12 : 22;
              const delai      = kmNum > 120000 ? delaiBase + 8 : kmNum > 80000 ? delaiBase + 4 : delaiBase;
              const liquidite  = delai <= 14 ? "Haute" : delai <= 21 ? "Moyenne" : "Modérée";
              const liqColor   = delai <= 14 ? "var(--green)" : delai <= 21 ? "#F59E0B" : "var(--sub)";
              return (
                <div style={{background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.2rem 1.4rem"}}>
                  <div style={{display:"flex", alignItems:"center", gap:".5rem", marginBottom:"1rem"}}>
                    <div style={{width:7,height:7,borderRadius:"50%",background:"var(--green)",boxShadow:"0 0 6px var(--green)"}}/>
                    <div style={{fontSize:".72rem", fontWeight:700, color:"var(--text)", textTransform:"uppercase", letterSpacing:"1px"}}>Signal Marché Live</div>
                  </div>
                  <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:".7rem", marginBottom:"1rem"}}>
                    <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem", textAlign:"center"}}>
                      <div style={{fontSize:"1.5rem", fontWeight:800, color:"var(--accent)", letterSpacing:"-.04em", lineHeight:1}}>{annonces}</div>
                      <div style={{fontSize:".6rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:".8px", marginTop:".3rem"}}>Annonces similaires</div>
                      <div style={{fontSize:".62rem", color:"var(--sub)", marginTop:".15rem"}}>actives en France</div>
                    </div>
                    <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem", textAlign:"center"}}>
                      <div style={{fontSize:"1.1rem", fontWeight:800, color:isUnder?"var(--green)":"#F59E0B", letterSpacing:"-.03em", lineHeight:1}}>
                        {median.toLocaleString("fr-FR")} €
                      </div>
                      <div style={{fontSize:".6rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:".8px", marginTop:".3rem"}}>Prix médian</div>
                      <div style={{fontSize:".62rem", color:isUnder?"var(--green)":"#F59E0B", marginTop:".15rem", fontWeight:600}}>
                        {isUnder ? "↓ Vous êtes sous" : "↑ Vous êtes au-dessus"}
                      </div>
                    </div>
                    <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem", textAlign:"center"}}>
                      <div style={{fontSize:"1.5rem", fontWeight:800, color:liqColor, letterSpacing:"-.04em", lineHeight:1}}>{delai}j</div>
                      <div style={{fontSize:".6rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:".8px", marginTop:".3rem"}}>Délai moyen</div>
                      <div style={{fontSize:".62rem", color:liqColor, marginTop:".15rem", fontWeight:600}}>Liquidité {liquidite}</div>
                    </div>
                  </div>
                  <div>
                    {/* Jauge calibrée sur la fourchette argus_low → argus_high du véhicule */}
                    {(() => {
                      const lowMarket  = cur.argusLow  || Math.round(price * 0.85 / 100) * 100;
                      const highMarket = cur.argusHigh || Math.round(price * 1.15 / 100) * 100;
                      const range      = Math.max(1, highMarket - lowMarket);
                      // Position du véhicule sur l'axe [low ; high] → 0% à 100%
                      const rawPct     = ((price - lowMarket) / range) * 100;
                      const pct        = Math.min(98, Math.max(2, rawPct));
                      // Couleur : vert si dans le tiers bas, orange milieu, rouge tiers haut
                      const dotColor   = rawPct < 33 ? "var(--green)" : rawPct < 66 ? "#F59E0B" : "#F87171";
                      return (
                        <>
                          <div style={{display:"flex", justifyContent:"space-between", fontSize:".62rem", color:"var(--muted)", marginBottom:".35rem"}}>
                            <span>{lowMarket.toLocaleString("fr-FR")} € <span style={{display:"block", fontSize:".58rem", marginTop:"1px"}}>Bas marché</span></span>
                            <span style={{color:dotColor, fontWeight:700, textAlign:"center"}}>Votre véhicule<br/><span style={{fontSize:".7rem"}}>{price.toLocaleString("fr-FR")} €</span></span>
                            <span style={{textAlign:"right"}}>{highMarket.toLocaleString("fr-FR")} € <span style={{display:"block", fontSize:".58rem", marginTop:"1px"}}>Haut marché</span></span>
                          </div>
                          <div style={{position:"relative", height:8, background:"var(--border)", borderRadius:4, marginTop:".5rem"}}>
                            <div style={{position:"absolute",top:0,left:0,height:"100%",width:"100%",background:"linear-gradient(90deg,var(--green),#F59E0B,#F87171)",borderRadius:4,opacity:.4}}/>
                            <div style={{position:"absolute",top:"50%",transform:"translate(-50%,-50%)",
                              left:pct+"%",
                              width:14,height:14,borderRadius:"50%",background:dotColor,
                              border:"2px solid white",boxShadow:"0 0 6px rgba(0,0,0,.4)",transition:"left .5s ease"}}/>
                          </div>
                          <div style={{fontSize:".62rem", color:"var(--muted)", marginTop:".4rem", fontStyle:"italic"}}>
                            {rawPct < 33 ? "✓ Positionné sous la médiane — vente probablement rapide"
                              : rawPct < 66 ? "↔ Positionné dans la médiane du marché"
                              : "↑ Positionné au-dessus de la médiane — délai potentiellement plus long"}
                            {" "}— Cote basée sur {annonces} annonces · segment {isPop ? "populaire" : "premium/niche"}
                          </div>
                        </>
                      );
                    })()}

                    {/* ── 2 jauges supplémentaires ── */}
                    <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:".7rem", marginTop:"1rem"}}>

                      {/* Jauge 1 : Attractivité annonce */}
                      {(() => {
                        const kmNum2 = cur?.km ? Number(cur.km) : 0;
                        const score2 = cur?.score || 70;
                        // Score attractivité : km bas + score élevé + prix sous médian = attractif
                        const attract = Math.min(98, Math.max(10,
                          Math.round(
                            (kmNum2 < 60000 ? 88 : kmNum2 < 100000 ? 72 : kmNum2 < 150000 ? 52 : 32)
                            * (score2 / 80)
                            * (isUnder ? 1.12 : 0.90)
                          )
                        ));
                        const col = attract >= 75 ? "var(--green)" : attract >= 50 ? "#F59E0B" : "#F87171";
                        const lbl = attract >= 75 ? "Très attractif" : attract >= 50 ? "Attractif" : "Peu attractif";
                        return (
                          <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem"}}>
                            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:".45rem"}}>
                              <span style={{fontSize:".68rem", fontWeight:700, color:"var(--text)"}}>Attractivité annonce</span>
                              <span style={{fontSize:".75rem", fontWeight:800, color:col, fontFamily:"var(--font-mono)"}}>{attract}%</span>
                            </div>
                            <div style={{height:7, background:"var(--border)", borderRadius:4, overflow:"hidden", marginBottom:".35rem"}}>
                              <div style={{height:"100%", width:attract+"%", background:col, borderRadius:4, transition:"width .6s ease", opacity:.9}}/>
                            </div>
                            <div style={{fontSize:".62rem", color:col, fontWeight:600}}>{lbl}</div>
                            <div style={{fontSize:".6rem", color:"var(--sub)", marginTop:".2rem"}}>
                              Basé sur km, score et positionnement prix
                            </div>
                          </div>
                        );
                      })()}

                      {/* Jauge 2 : Pression concurrentielle */}
                      {(() => {
                        // Pression = plus d'annonces = plus de concurrence = plus difficile à vendre
                        const pression = Math.min(95, Math.max(5, Math.round(
                          (annonces > 200 ? 85 : annonces > 120 ? 65 : annonces > 60 ? 42 : 22)
                          * (isUnder ? 0.85 : 1.15)
                        )));
                        const col = pression < 35 ? "var(--green)" : pression < 65 ? "#F59E0B" : "#F87171";
                        const lbl = pression < 35 ? "Faible" : pression < 65 ? "Modérée" : "Élevée";
                        const conseil = pression < 35
                          ? "Peu de concurrents — avantage vendeur"
                          : pression < 65
                          ? "Marché équilibré — prix cohérent"
                          : "Fort volume — différenciez votre annonce";
                        return (
                          <div style={{background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:9, padding:".8rem .9rem"}}>
                            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:".45rem"}}>
                              <span style={{fontSize:".68rem", fontWeight:700, color:"var(--text)"}}>Pression concurrentielle</span>
                              <span style={{fontSize:".72rem", fontWeight:800, color:col, fontFamily:"var(--font-mono)"}}>{lbl}</span>
                            </div>
                            <div style={{height:7, background:"var(--border)", borderRadius:4, overflow:"hidden", marginBottom:".35rem"}}>
                              <div style={{height:"100%", width:pression+"%", background:col, borderRadius:4, transition:"width .6s ease", opacity:.9}}/>
                            </div>
                            <div style={{fontSize:".6rem", color:"var(--sub)", marginTop:".2rem"}}>
                              {conseil}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Analyse de Risque Acheteur ── */}
            {(() => {
              const kmNum = cur.km ? Number(cur.km) : 0;
              const score = cur.score || 70;
              const yearN = cur.year ? Number(cur.year) : 2020;
              const age   = 2026 - yearN;
              const rFraud = Math.min(95, Math.max(5,
                (kmNum > 200000 ? 75 : kmNum > 150000 ? 55 : kmNum > 100000 ? 35 : kmNum > 60000 ? 18 : 8)
                + (age > 10 ? 15 : age > 6 ? 8 : 0)
              ));
              const rSin = Math.min(90, Math.max(5, Math.round((100 - score) * 0.7 + (age > 8 ? 12 : 0))));
              const rLiq = Math.min(90, Math.max(10,
                (kmNum < 80000 ? 82 : kmNum < 130000 ? 65 : 45)
                - (age > 10 ? 15 : age > 6 ? 8 : 0)
                + (score > 80 ? 10 : 0)
              ));
              const risks = [
                { label:"Risque fraude km",   val:rFraud, invert:true,
                  desc: rFraud < 20 ? "Kilométrage cohérent avec l'âge" : rFraud < 50 ? "Vérification recommandée" : "Contrôle VIN CarVertical conseillé" },
                { label:"Risque sinistre",    val:rSin,   invert:true,
                  desc: rSin < 25 ? "Score de confiance élevé" : rSin < 55 ? "Contrôle VIN recommandé" : "Rapport historique requis" },
                { label:"Liquidité marché",   val:rLiq,   invert:false,
                  desc: rLiq > 70 ? "Revente facile — marché actif" : rLiq > 50 ? "Marché correct pour ce segment" : "Segment moins liquide" },
              ];
              const barCol = (val, inv) => { const v = inv ? 100-val : val; return v>65?"var(--green)":v>40?"#F59E0B":"#F87171"; };
              const lblCol = (val, inv) => { const v = inv ? 100-val : val; return v>65?"var(--green)":v>40?"#F59E0B":"#F87171"; };
              return (
                <div style={{background:"var(--bg)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.2rem 1.4rem"}}>
                  <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem"}}>
                    <div style={{fontSize:".72rem", fontWeight:700, color:"var(--text)", textTransform:"uppercase", letterSpacing:"1px"}}>Analyse de Risque Acheteur</div>
                    <div style={{fontSize:".65rem", color:"var(--muted)", fontFamily:"var(--font-mono)"}}>{viqIndexLabel()} {score}/100</div>
                  </div>
                  <div style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
                    {risks.map((r,i) => (
                      <div key={i}>
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:".35rem"}}>
                          <span style={{fontSize:".78rem", fontWeight:600, color:"var(--text)"}}>{r.label}</span>
                          <span style={{fontSize:".72rem", fontWeight:800, color:lblCol(r.val,r.invert), fontFamily:"var(--font-mono)"}}>
                            {r.invert ? (r.val<25?"Faible":r.val<55?"Modéré":"Élevé") : (r.val>65?"Haute":r.val>45?"Moyenne":"Faible")}
                          </span>
                        </div>
                        <div style={{height:8, background:"var(--border)", borderRadius:4, overflow:"hidden", marginBottom:".3rem"}}>
                          <div style={{height:"100%", width:r.val+"%", background:barCol(r.val,r.invert), borderRadius:4, transition:"width .6s ease", opacity:.85}}/>
                        </div>
                        <div style={{fontSize:".67rem", color:"var(--sub)"}}>{r.desc}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:"1rem", padding:".55rem .8rem", background:"rgba(168,128,58,.05)", border:"1.5px solid rgba(168,128,58,.12)", borderRadius:8, fontSize:".68rem", color:"var(--sub)"}}>
                    ℹ️ Indicateurs calculés depuis {viqIndexLabel()}, km et millésime. Rapport complet via <span style={{color:"var(--accent)", fontWeight:700, cursor:"pointer"}}>Contrôle VIN →</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ══ LIGNE 2.5 : Simulateur de financement LLD/LOA ══ */}
          <FinanceSimulator
            initialPrice={cur.viqPrice || cur.argusMid || 25000}
            vehicleLabel={cur.makeModel || ""}
          />

        </div>
        )}
        {/* /activeTab vehicle */}

        {/* ── ONGLET ANALYSE CONSOLIDÉE : ventes croisées par mois × segment ── */}
        {activeTab === "consolidated" && (() => {
          // Construire la liste de followups exploitables (avec sold_date)
          const followupList = Object.values(saleFollowups).filter(f => f && f.sold_price && f.sold_date);
          if (followupList.length === 0) {
            return (
              <div style={{ padding: "3rem 1.5rem", textAlign: "center", background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12 }}>
                <div style={{ fontSize: "2.5rem", marginBottom: ".5rem" }}>📊</div>
                <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "var(--text)", marginBottom: ".4rem" }}>Aucune vente enregistrée pour l'instant</div>
                <div style={{ fontSize: ".82rem", color: "var(--sub)", maxWidth: 480, margin: "0 auto", lineHeight: 1.5 }}>
                  Dès que vous aurez enregistré des prix de vente réels dans l'onglet « Suivi de vente », votre analyse consolidée apparaîtra ici — segmentée par type de client, méthode de retrait et plateforme.
                </div>
                <button onClick={() => setActiveTab("sale")} style={{ marginTop: "1rem", background: "var(--accent)", color: "#fff", border: "none", padding: ".6rem 1.2rem", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: ".82rem" }}>
                  Enregistrer une vente →
                </button>
              </div>
            );
          }

          // ── 12 derniers mois (du plus ancien au plus récent) ──
          const now = new Date();
          const months = [];
          for (let i = 11; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.push({
              key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
              label: d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }).replace(".", ""),
              date: d,
            });
          }

          // ── Segments de prix : 4 tranches (une barre par tranche, chaque mois) ──
          const PRICE_TIERS = [
            { label: "< 15 k€",  test: p => p < 15000 },
            { label: "15–35 k€", test: p => p >= 15000 && p < 35000 },
            { label: "35–80 k€", test: p => p >= 35000 && p < 80000 },
            { label: "80–150 k€ · Premium",       test: p => p >= 80000 && p < 150000 },
            { label: "150–225 k€ · Ultra premium", test: p => p >= 150000 && p < 225000 },
            { label: "> 225 k€ · Rares",           test: p => p >= 225000 },
          ];
          const priceTierIdx = (p) => {
            const v = Number(p) || 0;
            for (let i = 0; i < PRICE_TIERS.length; i++) if (PRICE_TIERS[i].test(v)) return i;
            return PRICE_TIERS.length - 1;
          };

          // Classifieurs de segment (réutilisés pour les 3 graphiques)
          const clientSeg = f => {
            const ct = f.client_type || "unknown";
            if (ct === "pro") return "Pro";
            if (ct === "particulier") return "Particulier";
            return "Non précisé";
          };
          const pickupSeg = f => {
            if (f.pickup_method === "concession") return "En concession";
            if (f.pickup_method === "expedition") return "Expédition / Livraison";
            return "Non précisé";
          };
          const platformSeg = f => {
            const p = (f.platform || "").trim();
            return p || "Non précisé";
          };

          // ── Agrégation par mois × segment × tranche de prix ──
          // Structure : { segment: { monthKey: [t0, t1, t2, t3] } }
          const aggregateByPrice = (segmentFn) => {
            const data = {};
            followupList.forEach(f => {
              const d = new Date(f.sold_date);
              if (isNaN(d.getTime())) return;
              const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
              const seg = segmentFn(f);
              if (!seg) return;
              const ti = priceTierIdx(f.sold_price);
              if (!data[seg]) data[seg] = {};
              if (!data[seg][mk]) data[seg][mk] = new Array(PRICE_TIERS.length).fill(0);
              data[seg][mk][ti] += 1;
            });
            return data;
          };

          const byClient = aggregateByPrice(clientSeg);
          const byPickup = aggregateByPrice(pickupSeg);
          const byPlatform = aggregateByPrice(platformSeg);

          // Top 6 plateformes (par total toutes tranches confondues), reste en "Autres"
          const platformTotals = Object.entries(byPlatform).map(([k, mm]) => {
            let t = 0; Object.values(mm).forEach(arr => arr.forEach(n => t += n)); return [k, t];
          });
          platformTotals.sort((a, b) => b[1] - a[1]);
          const topPlatforms = platformTotals.slice(0, 6).map(([k]) => k);
          const byPlatformTop = {};
          Object.entries(byPlatform).forEach(([seg, monthMap]) => {
            const key = topPlatforms.includes(seg) ? seg : "Autres";
            if (!byPlatformTop[key]) byPlatformTop[key] = {};
            Object.entries(monthMap).forEach(([mk, arr]) => {
              if (!byPlatformTop[key][mk]) byPlatformTop[key][mk] = new Array(PRICE_TIERS.length).fill(0);
              for (let i = 0; i < PRICE_TIERS.length; i++) byPlatformTop[key][mk][i] += arr[i] || 0;
            });
          });

          // ── Stats globales en haut ──
          const totalVentes = followupList.length;
          const last3Months = months.slice(-3).map(m => m.key);
          const ventesRecentes = followupList.filter(f => {
            const d = new Date(f.sold_date);
            const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            return last3Months.includes(mk);
          }).length;
          const totalCA = followupList.reduce((s, f) => s + (Number(f.sold_price) || 0), 0);

          // ── Palette de couleurs (pour les segments) ──
          const COLORS = ["#A8803A", "#1F5AE6", "#2E6F5C", "#9B2C2C", "#6E6E78", "#C9A96E", "#5A5A66", "#3A3A45"];

          // ── Composant graphique : une barre par tranche de prix et par mois,
          //    chaque barre empilée par segment (couleur). ──
          const GroupedPriceBarChart = ({ title, sub, data, height = 220 }) => {
            const segments = Object.keys(data);
            const chartH = height;
            const N = PRICE_TIERS.length;          // nb. de tranches de prix
            const GROUP_W = 64;                    // largeur réservée par mois
            const barW = 7, gap = 2;
            const groupInner = N * barW + (N - 1) * gap;
            const padLeft = (GROUP_W - groupInner) / 2;
            const vbW = months.length * GROUP_W;

            // Max sur (mois × tranche), empilé sur tous les segments → normalisation
            let maxVal = 0;
            months.forEach(m => {
              for (let t = 0; t < N; t++) {
                let sum = 0;
                segments.forEach(seg => { sum += (data[seg]?.[m.key]?.[t] || 0); });
                if (sum > maxVal) maxVal = sum;
              }
            });
            maxVal = Math.max(1, maxVal);

            return (
              <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem", marginBottom: "1rem" }}>
                <div style={{ marginBottom: ".4rem" }}>
                  <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)" }}>{title}</div>
                  <div style={{ fontSize: ".72rem", color: "var(--sub)", marginTop: ".15rem" }}>{sub}</div>
                </div>
                {/* Légende segments (couleurs) */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem .9rem", margin: ".5rem 0 .4rem", fontSize: ".7rem" }}>
                  {segments.map((seg, i) => (
                    <div key={seg} style={{ display: "flex", alignItems: "center", gap: ".3rem", color: "var(--sub)" }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
                      <span>{seg}</span>
                    </div>
                  ))}
                </div>
                {/* Légende tranches de prix (ordre des barres) */}
                <div style={{ fontSize: ".66rem", color: "var(--muted)", marginBottom: ".7rem" }}>
                  ${PRICE_TIERS.length} barres / mois (gauche → droite) : {PRICE_TIERS.map((pt, i) => `${i + 1}·${pt.label}`).join("   ")}
                </div>
                {/* Chart SVG pleine largeur */}
                <div style={{ width: "100%", height: chartH + 44, position: "relative" }}>
                  <svg viewBox={`0 0 ${vbW} ${chartH + 40}`} preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
                    {/* Lignes de grille horizontales */}
                    {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
                      <line key={i} x1="0" y1={chartH * (1 - t)} x2={vbW} y2={chartH * (1 - t)}
                        stroke="#E5E5EA" strokeWidth="0.5" strokeDasharray="2,3" />
                    ))}
                    {/* Groupes par mois */}
                    {months.map((m, mi) => {
                      const gx = mi * GROUP_W;
                      return (
                        <g key={m.key}>
                          {PRICE_TIERS.map((pt, ti) => {
                            const bx = gx + padLeft + ti * (barW + gap);
                            let yOffset = chartH;
                            const stack = segments.map((seg, si) => {
                              const v = data[seg]?.[m.key]?.[ti] || 0;
                              const h = (v / maxVal) * chartH;
                              yOffset -= h;
                              return { seg, v, h, y: yOffset, color: COLORS[si % COLORS.length] };
                            });
                            const tierTotal = stack.reduce((s, b) => s + b.v, 0);
                            const stackH = stack.reduce((s, b) => s + b.h, 0);
                            return (
                              <g key={ti}>
                                {stack.map((b, bi) => b.h > 0 && (
                                  <rect key={bi} x={bx} y={b.y} width={barW} height={b.h} fill={b.color} opacity={0.9}>
                                    <title>{b.seg} · {pt.label} — {m.label} : {b.v}</title>
                                  </rect>
                                ))}
                                {tierTotal > 0 && (
                                  <text x={bx + barW / 2} y={chartH - stackH - 3}
                                    textAnchor="middle" fontSize="7" fill="var(--text)" fontWeight="700">{tierTotal}</text>
                                )}
                                {/* indice de tranche sous chaque barre */}
                                <text x={bx + barW / 2} y={chartH + 10}
                                  textAnchor="middle" fontSize="6" fill="var(--muted)">{ti + 1}</text>
                              </g>
                            );
                          })}
                          {/* Label mois */}
                          <text x={gx + GROUP_W / 2} y={chartH + 24}
                            textAnchor="middle" fontSize="9" fill="var(--sub)">{m.label}</text>
                        </g>
                      );
                    })}
                    {/* Ligne de base */}
                    <line x1="0" y1={chartH} x2={vbW} y2={chartH} stroke="var(--border)" strokeWidth="1" />
                  </svg>
                </div>
              </div>
            );
          };

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {/* En-tête */}
              <div className="saas-dash-content-header">
                <h2 className="saas-dash-content-title">Analyse consolidée</h2>
                <div className="saas-dash-content-sub">
                  Vos ventes sur les 12 derniers mois, ventilées par type de client, méthode de retrait et plateforme.
                </div>
              </div>

              {/* KPIs synthétiques */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: ".8rem", marginBottom: ".5rem" }}>
                {[
                  { label: "Ventes totales", value: totalVentes, sub: "Toutes périodes" },
                  { label: "Ventes 3 derniers mois", value: ventesRecentes, sub: `${last3Months.length > 0 ? months.find(m => m.key === last3Months[0])?.label : ""} → aujourd'hui` },
                  { label: "Chiffre d'affaires", value: totalCA.toLocaleString("fr-FR") + " €", sub: "Total cumulé" },
                  { label: "Plateformes actives", value: Object.keys(byPlatform).length, sub: "Canaux utilisés" },
                ].map((k, i) => (
                  <div key={i} style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".9rem 1.1rem" }}>
                    <div style={{ fontSize: ".62rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 700, marginBottom: ".25rem" }}>{k.label}</div>
                    <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", color: "var(--text)", fontWeight: 700, lineHeight: 1.1 }}>{k.value}</div>
                    <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".2rem" }}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {/* Graphiques mensualisés — une barre par tranche de prix, chaque mois */}
              <GroupedPriceBarChart
                title="Ventes par type de client"
                sub="Pour chaque mois, une barre par tranche de prix ; la couleur ventile Pro / Particulier."
                data={byClient}
              />
              <GroupedPriceBarChart
                title="Ventes par méthode de retrait"
                sub="Pour chaque mois, une barre par tranche de prix ; la couleur ventile concession vs expédition / livraison."
                data={byPickup}
              />
              <GroupedPriceBarChart
                title="Ventes par plateforme (top 6 + Autres)"
                sub="Pour chaque mois, une barre par tranche de prix ; la couleur ventile par plateforme."
                data={byPlatformTop}
              />

              {/* Note méthodologique */}
              <div style={{ background: "rgba(168,128,58,.05)", border: "1px solid rgba(168,128,58,.18)", borderRadius: 8, padding: ".8rem 1rem", fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.55 }}>
                <strong style={{ color: "var(--accent)" }}>📊 Lecture :</strong> chaque mois affiche <strong>une barre par tranche de prix</strong> (1·&lt;15 k€, 2·15–35 k€, 3·35–80 k€, 4·80–150 k€ Premium, 5·150–225 k€ Ultra premium, 6·&gt;225 k€). À l'intérieur d'une barre, les couleurs ventilent par segment ; le nombre au-dessus est le total de la tranche. Les segments « Non précisé » apparaissent quand l'information n'a pas été renseignée dans le suivi de vente — pensez à compléter ces champs pour affiner vos analyses.
              </div>
            </div>
          );
        })()}

        {/* ── ONGLET ESTIMATIONS : liste de toutes les estimations ── */}
        {activeTab === "list" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className="saas-dash-content-header">
              <h2 className="saas-dash-content-title">Toutes les cotes</h2>
              <div className="saas-dash-content-sub">
                {entries.length} cote{entries.length>1?"s":""} enregistrée{entries.length>1?"s":""} — cliquez sur un véhicule pour le sélectionner.
              </div>
            </div>
            <div style={{background:"var(--white)", border:"1.5px solid var(--border)", borderRadius:12, padding:"1.4rem 1.6rem"}}>
              <div style={{display:"flex", flexWrap:"wrap", gap:".5rem"}}>
                {entries.map((e,i) => (
                  <button key={i} onClick={() => { setIdx(i); setActiveTab("vehicle"); }}
                    style={{
                      padding:".5rem 1rem", borderRadius:100, border:"1.5px solid",
                      borderColor: i===idx ? "var(--accent)" : "var(--border)",
                      background:  i===idx ? "var(--accent)" : "var(--white)",
                      color:       i===idx ? "#fff" : "var(--text)",
                      fontSize:".78rem", fontFamily:"var(--font-body)", cursor:"pointer",
                      fontWeight:  i===idx ? 700 : 500, transition:"all .12s",
                      whiteSpace:"nowrap",
                      display: "inline-flex", alignItems: "center", gap: ".5rem"
                    }}>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: ".66rem", opacity: .7 }}>#{i+1}</span>
                    <span>{e.makeModel?.split(" ").slice(0,3).join(" ") || `Cote ${i+1}`}</span>
                    {e.year && <span style={{ fontSize: ".66rem", opacity: .7 }}>· {e.year}</span>}
                  </button>
                ))}
              </div>
              <div style={{fontSize:".7rem", color:"var(--muted)", fontFamily:"var(--font-mono)", marginTop:"1.2rem", paddingTop:"1rem", borderTop:"1px solid var(--border)"}}>
                Astuce : utilisez les flèches ‹ › du header pour naviguer rapidement entre les cotes.
              </div>
            </div>
          </div>
        )}

        {activeTab === "livemarket" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className="saas-dash-content-header">
              <h2 className="saas-dash-content-title">Live Marché</h2>
              <div className="saas-dash-content-sub">
                Cotations VIQ.AI® en direct : tendances de prix agrégées sur 100+ plateformes, mises à jour en continu.
              </div>
            </div>
            <LiveMarketPanel key={liveRefreshKey} />
          </div>
        )}

        {activeTab === "documents" && (
          <DocumentsPanel onCreateMandat={onCreateMandat} onCreateCession={onCreateCession} />
        )}

        {activeTab === "cartegrise" && (
          !((user?.siret && user.siret.length > 3) || user?.is_pro) ? (
            <ProFeatureGate title="Estimateur de malus — réservé aux comptes Pro"
              onUpgrade={() => window.dispatchEvent(new CustomEvent("viq-open-pricing"))} />
          ) : (
          <CarteGriseCalculator prefill={(() => {
            // Pré-remplissage depuis un formulaire client (prioritaire), sinon estimation courante.
            const lp = window.__viq_cg_prefill;
            if (lp) { window.__viq_cg_prefill = null; return lp; }
            return cur ? { fuel: cur.fuel, immat: cur.immat, vin: cur.vin } : null;
          })()} />
          )
        )}

        {activeTab === "scan" && (
          <ScanOpportunite isUnlimitedPlan={["unlimited", "multi_agent_3"].includes(user?.subscription_plan) || !!(user?.plan_limits?.is_multi_agent)} />
        )}

        {activeTab === "prospection" && (
          <ProspectionAgent user={user} />
        )}

        {/* ── ONGLET FORMULAIRES CLIENTS : lien personnalisé + leads reçus ── */}
        {activeTab === "leads" && (
          !((user?.siret && user.siret.length > 3) || user?.is_pro) ? (
            <ProFeatureGate title="Formulaires clients — réservé aux comptes Pro"
              onUpgrade={() => window.dispatchEvent(new CustomEvent("viq-open-pricing"))} />
          ) :
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className="saas-dash-content-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
              <div>
                <h2 className="saas-dash-content-title">📥 Formulaires clients</h2>
                <div className="saas-dash-content-sub">
                  Partagez votre lien personnalisé : vos clients remplissent leur demande (cote, recherche d'achat ou carte grise & malus) et elle arrive ici, prête à être lancée en un clic.
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowLeadQR(v => !v)}
                style={{ whiteSpace: "nowrap", flexShrink: 0 }} title="Afficher ou masquer le QR Code de votre lien">
                {showLeadQR ? "🙈 Masquer le QR Code" : "👁 Afficher le QR Code"}
              </button>
            </div>

            {/* Lien partageable */}
            <div style={{ background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", borderRadius: 12, padding: "1rem 1.1rem" }}>
              <div style={{ fontSize: ".72rem", fontWeight: 800, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: ".5rem" }}>🔗 Mon lien client personnalisé</div>
              {leadLink ? (
                <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", alignItems: "center" }}>
                  <input readOnly value={leadLink} onFocus={e => e.target.select()}
                    style={{ flex: "1 1 260px", padding: ".55rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".8rem", fontFamily: "var(--font-mono)" }} />
                  <button className="btn btn-accent btn-sm" style={{ border: "none", cursor: "pointer" }}
                    onClick={() => { try { navigator.clipboard.writeText(leadLink); setLeadLinkCopied(true); setTimeout(() => setLeadLinkCopied(false), 2000); } catch (e) {} }}>
                    {leadLinkCopied ? "✓ Copié !" : "📋 Copier"}
                  </button>
                  <a className="btn btn-ghost btn-sm" style={{ textDecoration: "none" }}
                    href={"https://wa.me/?text=" + encodeURIComponent("Bonjour ! Pour faire estimer votre véhicule, recevoir des conseils d'achat ou estimer votre carte grise, remplissez ce court formulaire : " + leadLink)}
                    target="_blank" rel="noopener noreferrer">💬 Partager via WhatsApp</a>
                </div>
              ) : (
                <div style={{ fontSize: ".8rem", color: "var(--sub)" }}>Génération du lien…</div>
              )}
              <div style={{ fontSize: ".7rem", color: "var(--sub)", marginTop: ".5rem", lineHeight: 1.5 }}>
                Envoyez-le par SMS, WhatsApp, email, ou intégrez-le à votre site / page Google. Chaque demande envoyée via ce lien vous est attribuée.
              </div>
              {/* v66 — QR Code masqué par défaut, affiché uniquement à la demande
                  (bouton ci-dessus) — "Actualiser" n'avait plus vraiment de sens
                  depuis que la liste se recharge déjà automatiquement. */}
              {leadLink && showLeadQR && <LeadQR url={leadLink} proName={user?.siret_name || user?.name} />}
            </div>

            {/* Liste des demandes */}
            {leads === null ? (
              <div style={{ color: "var(--sub)", fontSize: ".85rem" }}>Chargement…</div>
            ) : leads.length === 0 ? (
              <div style={{ background: "var(--card)", border: "1.5px dashed var(--border)", borderRadius: 12, padding: "1.6rem", textAlign: "center", color: "var(--sub)", fontSize: ".85rem" }}>
                Aucune demande pour l'instant. Partagez votre lien ci-dessus pour recevoir vos premières demandes clients.
              </div>
            ) : (() => {
              const nbNouveau = leads.filter(l => l.status === "nouveau").length;
              const nbTraite  = leads.filter(l => l.status === "traite").length;
              const filtered = leadFilter === "all" ? leads : leads.filter(l => (l.status || "nouveau") === leadFilter);
              const FilterChip = ({ k, label, count }) => (
                <button onClick={() => setLeadFilter(k)}
                  style={{ fontSize: ".74rem", fontWeight: 700, cursor: "pointer", padding: ".3rem .8rem", borderRadius: 20,
                    border: "1px solid " + (leadFilter === k ? "var(--accent)" : "var(--border)"),
                    background: leadFilter === k ? "var(--accent)" : "var(--card)",
                    color: leadFilter === k ? "#fff" : "var(--sub)" }}>
                  {label} {count != null && <span style={{ opacity: .8 }}>({count})</span>}
                </button>
              );
              return (
                <>
                  <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", marginBottom: ".9rem" }}>
                    <FilterChip k="nouveau" label="● À traiter" count={nbNouveau} />
                    <FilterChip k="traite" label="✓ Traités" count={nbTraite} />
                    <FilterChip k="all" label="Tous" count={leads.length} />
                  </div>
                  {filtered.length === 0 ? (
                    <div style={{ background: "var(--card)", border: "1.5px dashed var(--border)", borderRadius: 12, padding: "1.4rem", textAlign: "center", color: "var(--sub)", fontSize: ".82rem" }}>
                      {leadFilter === "nouveau" ? "Aucune demande à traiter — tout est à jour. 🎉" : leadFilter === "traite" ? "Aucune demande traitée pour l'instant." : "Aucune demande."}
                    </div>
                  ) : (
                  <div style={{ display: "grid", gap: ".7rem" }}>
                    {(() => {
                      // v66 — Regroupe les leads soumis ensemble (même lot_id) : un
                      // lot doit rester visuellement un lot, pas des lignes séparées
                      // sans lien entre elles. Le rendu de CHAQUE carte individuelle
                      // ci-dessous est strictement inchangé — seul le regroupement
                      // autour est nouveau.
                      const groups = [];
                      const lotIndex = {};
                      filtered.forEach(l => {
                        if (l.lot_id && lotIndex[l.lot_id] != null) { groups[lotIndex[l.lot_id]].items.push(l); return; }
                        if (l.lot_id) lotIndex[l.lot_id] = groups.length;
                        groups.push({ lot_id: l.lot_id || null, items: [l] });
                      });
                      return groups.map(g => {
                        const cards = g.items.map(l => {
                      const data = (typeof l.data === "object" && l.data) || {};
                  const META = {
                    estimation: { icon: "🚗", label:"Cote véhicule", cta:"Lancer la Cote →" },
                    achat: { icon: "🔎", label: "Recherche d'achat", cta: "Trouver le véhicule →" },
                    cartegrise: { icon: "🧾", label: "Carte grise & malus", cta: "Calculer carte grise & malus →" },
                  };
                  const m = META[l.type] || { icon: "📄", label: l.type, cta: "Lancer →" };
                  const chips = Object.entries(data).filter(([, v]) => v !== "" && v != null).slice(0, 8);
                  return (
                    <div key={l.id} style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 12, padding: ".9rem 1rem", opacity: l.status === "traite" ? .75 : 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: ".8rem", flexWrap: "wrap", alignItems: "flex-start" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: "flex", gap: ".5rem", alignItems: "center", flexWrap: "wrap", marginBottom: ".3rem" }}>
                            <span style={{ fontSize: ".68rem", fontWeight: 800, background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 20, padding: ".15rem .6rem" }}>{m.icon} {m.label}</span>
                            {l.status === "nouveau"
                              ? <span style={{ fontSize: ".62rem", fontWeight: 800, background: "rgba(46,111,92,.1)", color: "var(--green)", borderRadius: 20, padding: ".15rem .55rem" }}>● Nouveau</span>
                              : <span style={{ fontSize: ".62rem", fontWeight: 700, color: "var(--muted)", background: "var(--bg)", borderRadius: 20, padding: ".15rem .55rem" }}>✓ Traité</span>}
                            <button
                              onClick={() => setLeadStatus(l.id, l.status === "traite" ? "nouveau" : "traite")}
                              title={l.status === "traite" ? "Marquer comme non traité" : "Marquer comme traité"}
                              style={{ fontSize: ".6rem", fontWeight: 700, cursor: "pointer", background: "none", border: "1px solid var(--border)", borderRadius: 20, padding: ".12rem .5rem", color: "var(--sub)" }}>
                              {l.status === "traite" ? "↩ Rouvrir" : "✓ Marquer traité"}
                            </button>
                            <span style={{ fontSize: ".66rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{l.created_at ? new Date(l.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                            {l.histovec && l.histovec.found && (
                              <span title="Rapport HistoVec importé par le client" style={{ fontSize: ".62rem", fontWeight: 800, background: "rgba(46,111,92,.1)", color: "var(--green)", border: "1px solid rgba(46,111,92,.3)", borderRadius: 20, padding: ".15rem .55rem" }}>🛡 HistoVec</span>
                            )}
                          </div>
                          <div style={{ fontWeight: 700, color: "var(--text)", fontSize: ".88rem" }}>{l.client_name}</div>
                          <div style={{ display: "flex", gap: ".7rem", flexWrap: "wrap", fontSize: ".74rem", color: "var(--sub)", marginTop: ".15rem" }}>
                            {l.client_email && <a href={"mailto:" + l.client_email} style={{ color: "var(--accent)", textDecoration: "none" }}>✉️ {l.client_email}</a>}
                            {l.client_phone && <a href={"tel:" + String(l.client_phone).replace(/[\s.]/g, "")} style={{ color: "var(--accent)", textDecoration: "none" }}>📞 {l.client_phone}</a>}
                          </div>
                        </div>
                        {/* v92 — Règle : la cotation n'est plus lançable depuis le formulaire
                            lead (réservée au Dossier VIQ) — remplacée par une confirmation
                            explicite de création de dossier pour les leads d'estimation.
                            Les leads achat/carte grise ne sont pas des cotations : inchangés. */}
                        {l.type === "estimation" ? (
                          (() => {
                            const result = dossierConfirmResult[l.id];
                            const dossierId = l.dossier_id || (result && result.dossier_id);
                            const declined = l.dossier_declined || result === "declined";
                            if (dossierId) {
                              return <span style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--green)", whiteSpace: "nowrap", flexShrink: 0 }}>✓ Dossier VIQ créé — voir dans Dossiers VIQ</span>;
                            }
                            if (declined) {
                              return (
                                <button className="btn btn-ghost btn-sm" style={{ whiteSpace: "nowrap", flexShrink: 0, color: "var(--muted)" }}
                                  onClick={() => confirmDossier(l, true, g.items.map(it => it.id))} disabled={confirmingDossierId === l.id}>
                                  Dossier non créé — créer quand même ?
                                </button>
                              );
                            }
                            return (
                              <div style={{ display: "flex", gap: ".4rem", alignItems: "center", flexShrink: 0 }}>
                                <span style={{ fontSize: ".76rem", color: "var(--sub)", whiteSpace: "nowrap" }}>{g.items.length > 1 ? "Créer un Dossier VIQ pour ce lot ?" : "Créer un Dossier VIQ ?"}</span>
                                <button className="btn btn-accent btn-sm" style={{ border: "none", cursor: "pointer", whiteSpace: "nowrap" }}
                                  disabled={confirmingDossierId === l.id} onClick={() => confirmDossier(l, true, g.items.map(it => it.id))}>
                                  {confirmingDossierId === l.id ? "…" : "✓ Oui"}
                                </button>
                                <button className="btn btn-ghost btn-sm" style={{ whiteSpace: "nowrap" }}
                                  disabled={confirmingDossierId === l.id} onClick={() => confirmDossier(l, false, g.items.map(it => it.id))}>
                                  Non
                                </button>
                              </div>
                            );
                          })()
                        ) : (
                          <button className="btn btn-accent btn-sm" style={{ border: "none", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => launchLead(l)}>{m.cta}</button>
                        )}
                        {/* v21 : l'analyse de fiabilité est désormais automatique, gratuite et
                            silencieuse (calcul local + lecture d'une table pré-calculée, plus
                            aucun appel IA en direct) — plus de bouton à cliquer ni de retry. */}
                        {(l.type === "estimation" || l.type === "achat") && (typeof l.data === "object") && (l.data.make || l.data.marque) && !leadIssues[l.id] && leadScores[l.id] == null && leadIssuesLoading === l.id && (
                          <span style={{ fontSize: ".74rem", color: "var(--muted)", whiteSpace: "nowrap", flexShrink: 0, display: "flex", alignItems: "center", gap: ".35rem" }}>
                            <span className="loader-dot-pulse" style={{ width: 5, height: 5 }} /> Analyse de fiabilité…
                          </span>
                        )}
                        {leadScores[l.id] != null && (
                          <div title="Score de fiabilité VIQ.AI® (identique au décodage de plaque)" style={{ display: "flex", alignItems: "center", gap: ".4rem", flexShrink: 0 }}>
                            <ScoreGauge score={leadScores[l.id]} size={46} />
                            <span style={{ fontSize: ".62rem", color: "var(--sub)", fontWeight: 700, lineHeight: 1.15 }}>Score<br/>VIQ.AI®</span>
                          </div>
                        )}
                        {l.type === "estimation" && l.has_photos && (
                          <button className="btn btn-ghost btn-sm" disabled={leadPhotosLoading === l.id}
                            style={{ borderColor: "var(--accent)", color: "var(--accent)", cursor: leadPhotosLoading === l.id ? "wait" : "pointer", whiteSpace: "nowrap", flexShrink: 0, fontWeight: 700 }}
                            title="Voir les photos transmises par le client"
                            onClick={() => viewLeadPhotos(l)}>
                            {leadPhotosLoading === l.id ? "Chargement…" : `👁 Voir les photos (${l.photo_count})`}
                          </button>
                        )}
                      </div>
                      {chips.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: ".3rem .5rem", marginTop: ".55rem", paddingTop: ".55rem", borderTop: "1px dashed var(--border)" }}>
                          {chips.map(([k, v]) => (
                            <span key={k} style={{ fontSize: ".68rem", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 5, padding: ".15rem .5rem", color: "var(--sub)" }}>
                              <span style={{ color: "var(--muted)" }}>{k}</span> <span style={{ color: "var(--text)", fontWeight: 600 }}>{String(v)}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      {l.histovec && l.histovec.found && <HistovecSummary hv={l.histovec} />}
                      {leadScores[l.id] != null && (
                        <div style={{ marginTop: ".6rem", fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.5, background: "var(--bg)", border: "1px solid var(--border)", borderLeft: "3px solid var(--accent)", borderRadius: 8, padding: ".55rem .75rem" }}>
                          <strong style={{ color: "var(--text)" }}>🛡 Points de vigilance : </strong>{vigilancePoints(leadScores[l.id], leadIssues[l.id])}
                        </div>
                      )}
                      {(leadIssues[l.id] || leadIssuesLoading === l.id) && (
                        <KnownIssuesPanel data={leadIssues[l.id]} loading={leadIssuesLoading === l.id && !leadIssues[l.id]} />
                      )}
                    </div>
                  );
                        });
                        if (g.lot_id && g.items.length > 1) {
                          return (
                            <div key={"lot-" + g.lot_id} style={{ border: "2px solid var(--accent)", borderRadius: 14, padding: ".8rem", background: "rgba(168,128,58,.04)" }}>
                              <div style={{ fontSize: ".72rem", fontWeight: 800, color: "var(--accent)", marginBottom: ".6rem" }}>
                                📦 Lot de {g.items.length} véhicules — {g.items[0].client_name}
                              </div>
                              <div style={{ display: "grid", gap: ".7rem" }}>{cards}</div>
                            </div>
                          );
                        }
                        return cards[0];
                      });
                    })()}
              </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === "services" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div className="saas-dash-content-header">
              <h2 className="saas-dash-content-title">Services Pro</h2>
              <div className="saas-dash-content-sub">
                Des services complémentaires réservés aux comptes Pro, proposés sur devis selon votre volume.
              </div>
            </div>
            <ProDocServiceCard
              title="Générer un mandat de vente"
              badge="Inclus · Pro"
              desc="Éditez en quelques clics un mandat de vente d'intermédiation conforme (14 articles), pré-rempli avec les informations du véhicule estimé. Renseignez le mandant (particulier ou pro), le prix et la commission : le PDF prêt à signer est généré et archivé dans vos Documents."
              features={["📝 Pré-rempli depuis la Cote", "⚖️ Conforme (14 articles)", "👤 Particulier ou Pro", "💾 Archivé dans Documents"]}
              ctaLabel="Générer un mandat de vente"
              onCta={() => onCreateMandat && onCreateMandat(null)}
              iconPath={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></>}
            />
            <ProDocServiceCard
              title="Certificat de cession & Histovec"
              badge="Inclus · Pro"
              desc="Remplissez et téléchargez le certificat de cession officiel CERFA 15776 (les deux exemplaires) directement depuis la plateforme, pré-rempli avec le véhicule. Accédez aussi au lien Histovec officiel pour l'historique administratif."
              features={["📄 CERFA 15776 officiel", "🚗 Pré-rempli véhicule", "👥 Vendeur + acheteur", "🔗 Lien Histovec officiel"]}
              ctaLabel="Créer un certificat de cession"
              onCta={() => onCreateCession && onCreateCession(null)}
              iconPath={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 15l2 2 4-4"/></>}
            />
            <ProDocServiceCard
              title="Carte grise & malus écologique"
              badge="Inclus · Pro"
              desc="Estimez en quelques secondes le coût d'immatriculation en France : taxe régionale, malus CO₂ et malus au poids — y compris pour un véhicule importé d'Allemagne, de Belgique ou du Luxembourg, avec décote selon l'ancienneté. Données par saisie ou décodage de plaque."
              features={["🧾 Taxe régionale par région", "🌱 Malus CO₂ (barème 2026)", "⚖️ Malus au poids", "🇪🇺 Décote import"]}
              ctaLabel="Ouvrir le calculateur"
              onCta={() => setActiveTab("cartegrise")}
              iconPath={<><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4"/></>}
            />
            <ProServiceLeadCard user={user} siret={siret} onOpenPricing={onOpenPricing} service={PRO_SERVICE_PHOTO} />
            <ProServiceLeadCard user={user} siret={siret} onOpenPricing={onOpenPricing} service={PRO_SERVICE_MULTIDIFF} />
          </div>
        )}

        {/* ── Compta & fiscalité — bientôt disponible ── */}
        {activeTab === "compta" && (
          <div style={{ textAlign: "center", padding: "3rem 1.5rem", maxWidth: 620, margin: "0 auto" }}>
            <div style={{ fontSize: "2.4rem", marginBottom: "1rem" }}>🧮</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", fontWeight: 700, color: "var(--text)", marginBottom: ".6rem" }}>
              Compta & fiscalité — bientôt disponible
            </div>
            <div style={{ fontSize: ".88rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1.6rem" }}>
              Un nouveau volet du tableau de bord pour centraliser le suivi administratif et fiscal de votre activité VO : marge sur marge (régime TVA sur marge), export comptable de vos ventes, déclarations facilitées et suivi de votre trésorerie liée aux transactions. Toujours dans le même esprit VehicleIQ — pensé pour les indépendants comme pour les organisations multi-agents.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", justifyContent: "center", marginBottom: "1.8rem" }}>
              {["📊 Suivi TVA sur marge", "📤 Export comptable", "🧾 Déclarations facilitées", "💶 Suivi de trésorerie"].map(f => (
                <span key={f} style={{ fontSize: ".74rem", color: "var(--sub)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 20, padding: ".3rem .8rem" }}>{f}</span>
              ))}
            </div>
            <button className="btn btn-accent" onClick={() => {
              const subject = encodeURIComponent("Compta & fiscalité — être prévenu du lancement");
              const body = encodeURIComponent("Bonjour l'équipe VehicleIQ,\n\nJe souhaite être informé(e) dès que le module Compta & fiscalité sera disponible.\n\nCordialement");
              window.dispatchEvent(new CustomEvent("viq-open-contact", { detail: { mailto: "mailto:contact@vehicleiq.fr?subject=" + subject + "&body=" + body } }));
            }}>
              📩 Être prévenu du lancement
            </button>
          </div>
        )}

          </div>
          {/* /saas-dash-content */}

      </div>

      {/* Lightbox : photos transmises par un lead (visualisation avant analyse) */}
      {leadPhotosView && (
        <div onClick={() => setLeadPhotosView(null)}
          style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(0,0,0,.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "var(--card)", borderRadius: 16, maxWidth: 880, width: "100%", maxHeight: "88vh", overflow: "auto", border: "1.5px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.3rem", borderBottom: "1.5px solid var(--border)", position: "sticky", top: 0, background: "var(--card)", zIndex: 1 }}>
              <div>
                <div style={{ fontWeight: 800, color: "var(--text)", fontSize: "1rem" }}>📸 Photos du véhicule</div>
                <div style={{ fontSize: ".76rem", color: "var(--sub)", marginTop: ".15rem" }}>
                  {leadPhotosView.lead.client_name} · {leadPhotosView.photos.length} photo{leadPhotosView.photos.length > 1 ? "s" : ""} transmise{leadPhotosView.photos.length > 1 ? "s" : ""}
                </div>
              </div>
              <button onClick={() => setLeadPhotosView(null)}
                style={{ width: 34, height: 34, borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--bg)", color: "var(--text)", cursor: "pointer", fontSize: "1rem", flexShrink: 0 }}>✕</button>
            </div>
            <div style={{ padding: "1.2rem 1.3rem" }}>
              {leadPhotosView.photos.length === 0 ? (
                <div style={{ textAlign: "center", color: "var(--sub)", padding: "2rem" }}>Aucune photo.</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: ".8rem" }}>
                  {leadPhotosView.photos.map((src, i) => (
                    <div key={i} onClick={() => setLightboxSrc(src)}
                      style={{ display: "block", borderRadius: 10, overflow: "hidden", border: "1.5px solid var(--border)", aspectRatio: "4/3", background: "var(--bg)", cursor: "zoom-in" }}>
                      <img src={src} alt={"Photo " + (i + 1)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: ".7rem", marginTop: "1.2rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setLeadPhotosView(null)} style={{ cursor: "pointer" }}>Fermer</button>
              </div>
              <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: ".6rem", textAlign: "right" }}>
                Pour estimer ce véhicule, créez d'abord son Dossier VIQ depuis la liste des leads.
              </div>
            </div>
          </div>
        </div>
      )}
      {/* v90 — Visionneuse plein écran intégrée (mêmes principes que le
          Dossier VIQ) — clic n'importe où pour fermer. */}
      {lightboxSrc && (
        <div onClick={() => setLightboxSrc(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.88)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", cursor: "zoom-out" }}>
          <img src={lightboxSrc} alt="Photo agrandie" style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 8, boxShadow: "0 20px 60px rgba(0,0,0,.5)" }} />
          <button onClick={() => setLightboxSrc(null)} aria-label="Fermer"
            style={{ position: "absolute", top: "1.2rem", right: "1.2rem", width: 40, height: 40, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.15)", color: "#fff", fontSize: "1.3rem", cursor: "pointer" }}>✕</button>
        </div>
      )}
    </div>
  );
}
function MarketSection({ onLaunch, user }) {
  return (
    <div className="market-section">
      <div className="market-section-title">VIQ.AI® Engine <em>en action</em></div>
      <div className="market-section-sub">
        Visualisez l'algorithme propriétaire en action — en temps réel, sur le marché français. Les valeurs affichées sont des estimations indicatives.
      </div>
      <div className="market-grid">
        <IQGaugeHero price={42580} low={38200} high={47100} confidence={94} freshHours={3} />
        <DepreciationChart />
      </div>
      <div style={{ textAlign:"center", marginTop:"2.5rem" }}>
        <button className="btn btn-accent" style={{ padding:".75rem 2rem", fontSize:".95rem" }}
          onClick={onLaunch}>
          Commencer maintenant →
        </button>
        <div style={{ fontSize:".75rem", color:"var(--muted)", marginTop:".6rem" }}>
          Cote personnalisée sur votre véhicule · Données marché en temps réel
        </div>
      </div>
    </div>
  );
}

// ─── API helpers ──────────────────────────────────────────────────────────────
async function getAuthHeaders() {
  const h = { "Content-Type": "application/json" };
  // Le middleware auth.js attend l'UID Firebase (pas le JWT token).
  // window.__viq_uid est posé à la connexion, mais perdu au rechargement de page ;
  // auth.currentUser peut être null tant que Firebase n'a pas restauré la session.
  // On se replie donc sur l'UID persisté dans viq_session pour que les appels
  // authentifiés fonctionnent immédiatement après un refresh (KPIs, leads, etc.).
  let sessionUid = null;
  try { sessionUid = (JSON.parse(localStorage.getItem("viq_session") || "{}") || {}).uid || null; } catch (e) {}
  const uid = window.__viq_uid || (auth.currentUser && auth.currentUser.uid) || sessionUid || null;
  if (uid) h["Authorization"] = `Bearer ${uid}`;
  return h;
}

// ─── Helper Relancer une estimation passée ────────────────────────────────
// Utilise le mécanisme global viq-replay déjà câblé dans App.
// Construit le payload `replay` à partir des données d'historique.
// Param `entry` : objet de l'historique avec { type, answers/data/results, vin, ... }
// Si onAfter est fourni, l'appelle juste après le dispatch (typiquement pour fermer la modale courante).
// Normalise les réponses d'un historique vers le format attendu par les Questionnaires.
// Sources possibles : DB (searches.input), mapping Pro Dashboard (year/km/fuel), SellPanel callbackAnswers.
// Le Questionnaire compare les clés à `q.id` (make, year_sell, km_sell, fuel_sell, gearbox_sell, state pour Sell).
function normalizeSellAnswers(src) {
  if (!src || typeof src !== "object") return {};
  const out = {};
  // Marque + modèle : on essaye plusieurs sources
  const make = src.make || src.makeModel || src.make_model || "";
  const model = src.model || "";
  if (make && model && !make.toLowerCase().includes(String(model).toLowerCase())) {
    out.make = (make + " " + model).trim();
  } else if (make) {
    out.make = String(make).trim();
  }
  // Finition (rarement remplie en histo, ok si vide)
  if (src.finition) out.finition = src.finition;
  // Année : Questionnaire attend year_sell
  const year = src.year_sell || src.year || src.annee || src.first_registration_year;
  if (year) out.year_sell = parseInt(year, 10) || year;
  // Kilométrage : Questionnaire attend km_sell
  const km = src.km_sell || src.km || src.mileage || src.kilometrage;
  if (km !== undefined && km !== null && km !== "") {
    out.km_sell = typeof km === "string" ? parseInt(km.replace(/[^\d]/g, ""), 10) : km;
  }
  // Carburant : Questionnaire attend fuel_sell
  const fuel = src.fuel_sell || src.fuel || src.carburant;
  if (fuel) out.fuel_sell = fuel;
  // Boîte : Questionnaire attend gearbox_sell
  const gb = src.gearbox_sell || src.gearbox || src.boite;
  if (gb) out.gearbox_sell = gb;
  // État : les sources externes (formulaires clients, historique) utilisent des
  // libellés courts ("Très bon", "À rénover"…) alors que le questionnaire attend
  // ses options longues exactes ("Très bon — micro-rayures…"). On mappe par
  // préfixe + alias pour que le radio se pré-coche correctement.
  if (src.state) {
    const raw = String(src.state).trim();
    let opts = [];
    try { opts = (SELL_QUESTIONS.find(q => q.id === "state") || {}).options || []; } catch (e) {}
    const low = raw.toLowerCase();
    const ALIAS = { "à rénover": "Médiocre", "a renover": "Médiocre", "à restaurer": "Médiocre", "très bon état": "Très bon", "bon état": "Bon", "état correct": "Correct" };
    const target = ALIAS[low] || raw;
    const exact = opts.find(o => o === raw);
    const prefix = opts.find(o => o.toLowerCase().startsWith(String(target).toLowerCase()));
    out.state = exact || prefix || raw;
  }
  // CT / propriétaires / délai de vente : match exact ou par préfixe contre
  // les options du questionnaire (les formulaires clients envoient les mêmes libellés).
  const matchOpt = (qid, raw) => {
    if (!raw) return undefined;
    const v = String(raw).trim();
    let opts = [];
    try { opts = (SELL_QUESTIONS.find(q => q.id === qid) || {}).options || []; } catch (e) {}
    return opts.find(o => o === v)
        || opts.find(o => o.toLowerCase().startsWith(v.toLowerCase()))
        || opts.find(o => v.toLowerCase().startsWith(o.toLowerCase().split(" (")[0]))
        || v;
  };
  if (src.ct) out.ct = matchOpt("ct", src.ct);
  if (src.owners) out.owners = matchOpt("owners", src.owners);
  if (src.urgency || src.delai) {
    let rawUrg = String(src.urgency || src.delai).trim();
    // "Normale" n'existe plus (fusionnée avec "Rapide") — mapper les anciennes valeurs
    if (/^imm[ée]diate/i.test(rawUrg)) rawUrg = "Rapide"; // stratégie immédiate retirée → repli Rapide
    out.urgency = matchOpt("urgency", rawUrg);
  }
  // Options & équipements : chaîne " · " (format multi_select), "|" ou tableau →
  // on re-matche chaque entrée sur les libellés exacts et on re-joint en " · ".
  if (src.options) {
    let list = Array.isArray(src.options) ? src.options : String(src.options).split(/\s·\s|\s\|\s/).map(x => x.trim()).filter(Boolean);
    let opts = [];
    try { opts = (SELL_QUESTIONS.find(q => q.id === "options") || {}).options || []; } catch (e) {}
    const matched = list.map(item =>
      opts.find(o => o === item) || opts.find(o => o.toLowerCase().startsWith(String(item).toLowerCase().slice(0, 12)))
    ).filter(Boolean);
    if (matched.length) out.options = matched.join(" · ");
  }
  if (src.options_detail) out.options_detail = src.options_detail;
  // v66 — VIN/plaque n'étaient JAMAIS repris ici (seuls make/année/km/état
  // l'étaient) : "Lancer la Cote" depuis un lead (lot ou non) ouvrait
  // l'Estimation avec Photos + HistoVec déjà présents mais sans identifiant
  // véhicule, malgré 'immat' déjà transmis par le formulaire client.
  const rawId = (src.vin || src.plaque || src.immat || "").toString().replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  if (rawId.length === 17) out.vin = rawId;
  else if (rawId.length >= 6) out.plaque = rawId;
  return out;
}

function normalizeBuyAnswers(src) {
  if (!src || typeof src !== "object") return {};
  const out = {};
  // Budget
  const budget = src.budget || src.max_price;
  if (budget) out.budget = typeof budget === "string" ? parseInt(budget.replace(/[^\d]/g, ""), 10) : budget;
  if (src.fuel)     out.fuel    = src.fuel;
  if (src.segment)  out.segment = src.segment;
  if (src.brands)   out.brands  = src.brands;
  if (src.year)     out.year    = typeof src.year === "string" ? parseInt(src.year, 10) : src.year;
  if (src.km)       out.km      = typeof src.km === "string" ? parseInt(String(src.km).replace(/[^\d]/g, ""), 10) : src.km;
  if (src.gearbox)  out.gearbox = src.gearbox;
  if (src.usage)    out.usage   = src.usage;
  if (src.sellers)  out.sellers = src.sellers;
  if (src.import)   out.import  = src.import;
  return out;
}

function triggerReplay(entry, onAfter) {
  try {
    if (!entry) return;
    const type = entry.type || entry.kind;
    // On agrège TOUS les conteneurs possibles d'answers/data en un seul objet
    // (le mapping Pro Dashboard expose les champs au TOP-LEVEL, pas dans .answers)
    const raw = {
      ...(entry.raw || {}),
      ...(entry.data || {}),
      ...(entry.input || {}),
      ...(entry.answers || {}),
      // Champs top-level (cas du mapping Pro Dashboard)
      ...(entry.make ? { make: entry.make } : {}),
      ...(entry.model ? { model: entry.model } : {}),
      ...(entry.makeModel ? { makeModel: entry.makeModel } : {}),
      ...(entry.year ? { year: entry.year } : {}),
      ...(entry.km !== undefined ? { km: entry.km } : {}),
      ...(entry.fuel ? { fuel: entry.fuel } : {}),
      ...(entry.gearbox ? { gearbox: entry.gearbox } : {}),
      ...(entry.state ? { state: entry.state } : {}),
    };
    if (type === "check") {
      const vin = raw.vin || raw.plate || raw.immatriculation || entry.vin || "";
      if (!vin) {
        alert("Impossible de relancer : VIN/plaque introuvable dans l'historique.");
        return;
      }
      window.__viq_replay = { type: "check", vin };
    } else if (type === "sell") {
      const normalized = normalizeSellAnswers(raw);
      if (!normalized.make && !normalized.year_sell) {
        alert("Impossible de relancer : données du véhicule introuvables dans l'historique.");
        return;
      }
      window.__viq_replay = { type: "sell", answers: normalized };
    } else if (type === "buy") {
      const normalized = normalizeBuyAnswers(raw);
      window.__viq_replay = { type: "buy", answers: normalized };
    } else {
      alert("Type de Cote non supporté pour la relance : " + (type || "inconnu"));
      return;
    }
    window.dispatchEvent(new CustomEvent("viq-replay"));
    if (typeof onAfter === "function") setTimeout(onAfter, 50);
  } catch (e) {
    console.warn("[triggerReplay] error:", e?.message);
  }
}

async function apiGet(path) {
  const res = await fetch(`${API_URL}${path}`, { headers: await getAuthHeaders() });
  if (!res.ok) {
    // v7 : on tente de récupérer le vrai message d'erreur backend (JSON
    // {error, code}) plutôt que de le jeter — sinon tous les appelants
    // perdent l'info utile (ex: "service indisponible" devient "Erreur: 400").
    let msg = `Erreur API: ${res.status}`;
    let code = null;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      if (body?.code) code = body.code;
    } catch (e) { /* pas de JSON exploitable — on garde le message générique */ }
    const err = new Error(msg);
    err.code = code;
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// ─── Compression d'image avant upload (analyse vision par photos) ────────────
// Réduit fortement le poids des photos smartphone (souvent 5-10 Mo) en les
// redimensionnant à 1600px max et en ré-encodant en JPEG qualité 0.8 → ~300-500 ko.
// Évite les rejets de payload et accélère l'envoi. Renvoie une data-URL JPEG.
// En cas d'échec (navigateur exotique), retombe sur le fichier brut en base64.
function compressImageFile(file, maxDim = 1600, quality = 0.8) {
  return new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          try {
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
              if (width >= height) { height = Math.round(height * maxDim / width); width = maxDim; }
              else { width = Math.round(width * maxDim / height); height = maxDim; }
            }
            const canvas = document.createElement("canvas");
            canvas.width = width; canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL("image/jpeg", quality));
          } catch (e) { resolve(reader.result); }
        };
        img.onerror = () => resolve(reader.result);
        img.src = reader.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    } catch (e) { resolve(null); }
  });
}

async function apiPost(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // Tenter de lire la vraie erreur du backend (au lieu de juste le code HTTP)
    let detail = null;
    try { detail = await res.json(); } catch(_) {}
    const err = new Error((detail && detail.error) || `Erreur API: ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
}

// v11 — Découplage des appels IA lents (estimation, aide à l'achat) : au lieu
// d'attendre une seule requête HTTP potentiellement longue (recherche web +
// génération, parfois 10-30s), on soumet en mode asynchrone (`async: true`)
// et on interroge /api/jobs/:id à intervalles réguliers. Évite qu'un proxy
// intermédiaire (Railway, navigateur) ne coupe une connexion trop longue avant
// que le serveur ait fini de répondre.
async function apiPostAsync(path, body, { pollMs = 1500, timeoutMs = 120000, onPartial } = {}) {
  const start = await apiPost(path, { ...body, async: true });
  if (!start?.jobId) {
    // Le backend n'a pas basculé en mode job (ex: ancienne version) — traiter
    // `start` comme une réponse directe déjà complète, par sécurité.
    return start;
  }
  const jobId = start.jobId;
  const deadline = Date.now() + timeoutMs;
  let partialReported = false;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, pollMs));
    const poll = await apiGet(`/api/jobs/${jobId}`);
    if (poll.status === "done") return poll.result;
    if (poll.status === "error") {
      const err = new Error(poll.error || "Le traitement a échoué.");
      err.status = poll.errorHttpStatus || 500;
      err.detail = poll.errorDetail || null;
      throw err;
    }
    // v14 : un résultat "partiel" (ex: cote 100% algo, publiée dès qu'elle est
    // calculée côté serveur, avant la fusion marché IA) peut arriver pendant
    // que le job est encore "processing" — on le signale une seule fois.
    if (poll.status === "processing" && poll.result && poll.result.partial && onPartial && !partialReported) {
      partialReported = true;
      try { onPartial(poll.result); } catch (e) {}
    }
    // "processing" → on continue à interroger
  }
  throw new Error("Le traitement prend plus de temps que prévu. Réessayez dans un instant.");
}

async function apiPatch(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: "PATCH",
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = null;
    try { detail = await res.json(); } catch (_) {}
    const err = new Error((detail && detail.error) || `Erreur API: ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
}

async function apiPut(path, body) {
  const res = await fetch(`${API_URL}${path}`, {
    method: "PUT",
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = null;
    try { detail = await res.json(); } catch (_) {}
    const err = new Error((detail && detail.error) || `Erreur API: ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
}

// ─── Fiabilité & HistoVec — composants partagés (Contrôler / Estimer / Lead) ──
// Réutilisés pour : importer un rapport HistoVec (optionnel), afficher la
// synthèse des problèmes connus (recherche web) et les points à surveiller à
// l'achat. Tous purement présentationnels + appels apiPost vers le backend.

function _fileToDataURL(f) {
  return new Promise((resolve, reject) => {
    const rd = new FileReader();
    rd.onload = () => resolve(rd.result);
    rd.onerror = reject;
    rd.readAsDataURL(f);
  });
}

function _severityStyle(g) {
  if (g === "majeur") return { bg: "rgba(176,90,58,.12)", bd: "rgba(176,90,58,.45)", fg: "#E08A6A", label: "Majeur" };
  if (g === "mineur") return { bg: "rgba(46,111,92,.12)", bd: "rgba(46,111,92,.45)", fg: "#5FB89E", label: "Mineur" };
  return { bg: "rgba(255,184,0,.10)", bd: "rgba(255,184,0,.35)", fg: "var(--accent2)", label: "Modéré" };
}

// Carte métrique unifiée du dashboard super admin (présentation harmonisée).
function AdminMetricCard({ icon, value, label, sub, accent = "var(--accent)" }) {
  return (
    <div style={{ background: "var(--white)", border: "1px solid var(--border)", borderTop: "3px solid " + accent, borderRadius: 12, padding: ".9rem 1.05rem", display: "flex", flexDirection: "column", gap: ".15rem", minHeight: 94 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".4rem" }}>
        <span style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)", fontWeight: 700 }}>{label}</span>
        {icon && <span style={{ fontSize: ".9rem", opacity: .75, lineHeight: 1 }}>{icon}</span>}
      </div>
      <div style={{ fontFamily: "var(--font-head)", fontSize: "1.65rem", fontWeight: 800, color: accent, letterSpacing: "-.02em", lineHeight: 1.05 }}>
        {typeof value === "number" ? value.toLocaleString("fr-FR") : value}
      </div>
      {sub && <div style={{ fontSize: ".68rem", color: "var(--sub)", lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

// ─── Bouton + zone d'import d'un rapport HistoVec (PDF, captures, ou texte collé).
function HistovecImport({ onImported, compact }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);

  const submit = async (payload) => {
    setBusy(true); setErr("");
    try {
      const r = await apiPost("/api/histovec/import", payload);
      if (r && r.success && r.histovec) { onImported && onImported(r.histovec); setOpen(false); }
      else setErr((r && r.error) || "Rapport HistoVec non reconnu.");
    } catch (e) { setErr((e && e.message) || "Import impossible."); }
    finally { setBusy(false); }
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setBusy(true); setErr("");
    try {
      const images = []; let pdf = null;
      for (const f of arr.slice(0, 6)) {
        if (f.type === "application/pdf") { if (!pdf) pdf = await _fileToDataURL(f); }
        else if (f.type && f.type.startsWith("image/")) { images.push(await compressImageFile(f)); }
      }
      const payload = {};
      if (images.length) payload.images = images;
      if (pdf) payload.pdf = pdf;
      if (!payload.images && !payload.pdf) { setErr("Formats acceptés : PDF ou image."); setBusy(false); return; }
      await submit(payload);
    } catch (e) { setErr((e && e.message) || "Import impossible."); setBusy(false); }
  };

  return (
    <div style={{ border: "1px dashed var(--border)", borderRadius: 8, padding: compact ? ".7rem .8rem" : ".9rem 1rem", background: "rgba(30,136,229,.03)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".6rem", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: ".86rem", display: "flex", alignItems: "center", gap: ".45rem", flexWrap: "wrap" }}>
            🛡 Importer un rapport HistoVec <span style={{ color: "var(--muted)", fontWeight: 500 }}>(optionnel)</span>
            <span style={{
              fontSize: ".6rem", fontWeight: 800, letterSpacing: ".4px", textTransform: "uppercase",
              color: "var(--accent)", background: "rgba(168,128,58,.12)", border: "1px solid rgba(168,128,58,.3)",
              borderRadius: 100, padding: ".12rem .5rem",
            }}>
              ★ Recommandé
            </span>
          </div>
          <div style={{ fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.5, marginTop: ".15rem" }}>
            Affinez le score VIQ.AI® et complétez le rapport (propriétaires, CT, sinistres, gage…). Rapport gratuit sur{" "}
            <a href="https://histovec.interieur.gouv.fr" target="_blank" rel="noreferrer" style={{ color: "#90CAF9" }}>histovec.interieur.gouv.fr</a>.
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setOpen(o => !o)} style={{ fontSize: ".78rem", whiteSpace: "nowrap" }}>
          {busy ? "Analyse…" : open ? "Fermer" : "Importer"}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: ".7rem", display: "flex", flexDirection: "column", gap: ".5rem" }}>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple style={{ display: "none" }}
            onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} />
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()} style={{ fontSize: ".78rem", justifyContent: "center" }}>
            {busy ? "Analyse en cours…" : "📄 Choisir le PDF / une capture du rapport"}
          </button>
        </div>
      )}
      {err && <div style={{ marginTop: ".5rem", fontSize: ".74rem", color: "#E08A6A" }}>{err}</div>}
    </div>
  );
}

// Résumé des champs extraits d'un rapport CarVertical importé.
function CarVerticalSummary({ cv }) {
  if (!cv || !cv.found) return null;
  const flag = (v, badLabel, goodLabel) => v === true
    ? <span style={{ color: "#E08A6A", fontWeight: 700 }}>{badLabel}</span>
    : v === false ? <span style={{ color: "#5FB89E", fontWeight: 600 }}>{goodLabel}</span>
    : <span style={{ color: "var(--muted)" }}>—</span>;
  const rows = [
    ["Kilométrage vérifié", cv.mileage_verified != null ? (cv.mileage_verified ? "Oui" : "Non") : "—"],
    ["Dernier kilométrage connu", cv.last_known_mileage != null ? Number(cv.last_known_mileage).toLocaleString("fr-FR") + " km" : "—"],
    ["Propriétaires", cv.owners_count != null ? cv.owners_count : "—"],
    ["Sinistres déclarés", cv.accidents_declared != null ? cv.accidents_declared : "—"],
    ["Usage précédent", cv.previous_usage || "—"],
    ["Valeur marché estimée", cv.market_value_estimate != null ? Number(cv.market_value_estimate).toLocaleString("fr-FR") + " €" : "—"],
  ];
  return (
    <div style={{ marginTop: ".7rem", padding: ".8rem 1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".6rem" }}>
        <span style={{ fontWeight: 700, fontSize: ".85rem" }}>🔧 Données CarVertical importées</span>
        <span className="status-badge" style={{ background: "rgba(46,111,92,.12)", color: "#5FB89E", border: "1px solid rgba(46,111,92,.35)", fontSize: ".66rem" }}>vérifié</span>
      </div>
      <div className="data-grid">
        {rows.map(([l, v]) => (
          <div key={l} className="data-item"><span className="data-label">{l}</span><span className="data-value">{v}</span></div>
        ))}
      </div>
      <div style={{ display: "flex", gap: "1.2rem", flexWrap: "wrap", marginTop: ".6rem", fontSize: ".78rem" }}>
        <div>Compteur trafiqué : {flag(cv.mileage_rollback_detected, "⚠ Détecté", "Non détecté")}</div>
        <div>Volé : {flag(cv.stolen, "⚠ Déclaré volé", "Non")}</div>
      </div>
      {cv.report_date && (
        <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: ".5rem" }}>Rapport daté du {cv.report_date}</div>
      )}
      {(() => {
        const items = [];
        if (cv.stolen === true) items.push(["alert", "Véhicule déclaré volé — n'achetez pas et signalez-le."]);
        if (cv.mileage_rollback_detected === true) items.push(["alert", "Compteur kilométrique trafiqué détecté — risque élevé, méfiance sur l'ensemble des autres données déclarées."]);
        if (typeof cv.accidents_declared === "number" && cv.accidents_declared > 0) items.push(["warn", `${cv.accidents_declared} sinistre(s) déclaré(s) — exigez les factures de réparation et vérifiez la structure.`]);
        if (!items.some(i => i[0] === "alert") && cv.stolen === false && cv.mileage_rollback_detected !== true)
          items.push(["ok", "Aucun signal d'alerte CarVertical : pas de vol déclaré, compteur cohérent."]);
        if (!items.length) return null;
        const col = (k) => k === "alert" ? "#E08A6A" : k === "warn" ? "var(--accent2)" : "#5FB89E";
        const ico = (k) => k === "alert" ? "⛔" : k === "warn" ? "⚠" : "✓";
        return (
          <div style={{ marginTop: ".7rem", paddingTop: ".6rem", borderTop: "1px dashed var(--border)" }}>
            <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".4rem" }}>Interprétation CarVertical</div>
            <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
              {items.map(([k, txt], i) => (
                <div key={i} style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.5 }}>
                  <span style={{ color: col(k), fontWeight: 700 }}>{ico(k)} </span>{txt}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Résumé des champs extraits d'un rapport HistoVec importé.
function HistovecSummary({ hv }) {
  if (!hv || !hv.found) return null;
  const yn = (v) => v === true ? "Oui" : v === false ? "Non" : "—";
  const flag = (v, badLabel, goodLabel) => v === true
    ? <span style={{ color: "#E08A6A", fontWeight: 700 }}>{badLabel}</span>
    : v === false ? <span style={{ color: "#5FB89E", fontWeight: 600 }}>{goodLabel}</span>
    : <span style={{ color: "var(--muted)" }}>—</span>;
  const rows = [
    ["Propriétaires", hv.nb_proprietaires != null ? hv.nb_proprietaires : "—"],
    ["CT en cours de validité", hv.ct_valide != null ? yn(hv.ct_valide) : "—"],
    ["Dernier CT", hv.ct_dernier || "—"],
    ["Sinistres déclarés", hv.sinistres_declares != null ? hv.sinistres_declares : "—"],
    ["Kilométrage HistoVec", hv.km_dernier_connu != null ? Number(hv.km_dernier_connu).toLocaleString("fr-FR") + " km" : "—"],
  ];
  return (
    <div style={{ marginTop: ".7rem", padding: ".8rem 1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".6rem" }}>
        <span style={{ fontWeight: 700, fontSize: ".85rem" }}>🛡 Données HistoVec importées</span>
        <span className="status-badge" style={{ background: "rgba(46,111,92,.12)", color: "#5FB89E", border: "1px solid rgba(46,111,92,.35)", fontSize: ".66rem" }}>vérifié</span>
      </div>
      <div className="data-grid">
        {rows.map(([l, v]) => (
          <div key={l} className="data-item"><span className="data-label">{l}</span><span className="data-value">{v}</span></div>
        ))}
      </div>
      <div style={{ display: "flex", gap: "1.2rem", flexWrap: "wrap", marginTop: ".6rem", fontSize: ".78rem" }}>
        <div>Volé : {flag(hv.vole, "⚠ Déclaré volé", "Non")}</div>
        <div>Gage : {flag(hv.gage, "⚠ Gagé", "Non gagé")}</div>
        <div>Opposition : {flag(hv.opposition, "⚠ Opposition", "Aucune")}</div>
      </div>
      {hv.situation_administrative && (
        <div style={{ marginTop: ".5rem", fontSize: ".76rem", color: "var(--sub)" }}>Situation administrative : {hv.situation_administrative}</div>
      )}
      {(() => {
        // Interprétation HistoVec : lecture en clair, classée par gravité.
        const items = [];
        if (hv.vole === true) items.push(["alert", "Véhicule déclaré volé — n'achetez pas et signalez-le."]);
        if (hv.gage === true) items.push(["alert", "Gage actif — la vente est bloquée tant que le gage n'est pas levé (demandez la mainlevée)."]);
        if (hv.opposition === true) items.push(["alert", "Opposition au transfert (OTCI) — l'immatriculation à votre nom est bloquée."]);
        if (hv.ct_valide === false) items.push(["warn", "Contrôle technique non valide — prévoir le passage et d'éventuelles contre-visites avant achat."]);
        if (typeof hv.sinistres_declares === "number" && hv.sinistres_declares > 0) items.push(["warn", `${hv.sinistres_declares} sinistre(s) déclaré(s) — exigez les factures de réparation et vérifiez la structure.`]);
        if (typeof hv.nb_proprietaires === "number") {
          if (hv.nb_proprietaires >= 4) items.push(["warn", `Nombre de propriétaires élevé (${hv.nb_proprietaires}) — historique d'usage à examiner de près.`]);
          else if (hv.nb_proprietaires === 1) items.push(["ok", "Première main — historique de possession favorable."]);
        }
        if (!items.some(i => i[0] === "alert") && hv.gage === false && hv.opposition === false && hv.vole === false)
          items.push(["ok", "Situation administrative saine : pas de gage, pas d'opposition, non volé."]);
        if (!items.length) return null;
        const col = (k) => k === "alert" ? "#E08A6A" : k === "warn" ? "var(--accent2)" : "#5FB89E";
        const ico = (k) => k === "alert" ? "⛔" : k === "warn" ? "⚠" : "✓";
        return (
          <div style={{ marginTop: ".7rem", paddingTop: ".6rem", borderTop: "1px dashed var(--border)" }}>
            <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".4rem" }}>Interprétation HistoVec</div>
            <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
              {items.map(([k, txt], i) => (
                <div key={i} style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.5 }}>
                  <span style={{ color: col(k), fontWeight: 700 }}>{ico(k)} </span>{txt}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// v116 — Lien d'affiliation CarVertical. Le VIN (ou, à défaut, la plaque) est
// injecté dans sub3 pour pré-remplir la pre-check section côté CarVertical :
// l'utilisateur n'a rien à ressaisir. sub1 identifie la source (vehicleiq).
// Conformément aux règles d'usage de la marque CarVertical, le bandeau
// officiel fourni est utilisé tel quel, sans modification ni recoloration.
// Bandeau officiel CarVertical embarque directement dans le bundle (data
// URI) : aucun fichier a deposer dans public/, donc plus de risque de
// bandeau invisible si l'asset n'a pas ete copie au deploiement. Image
// fournie par le partenaire, utilisee telle quelle (seule une reduction de
// palette sans perte visible a ete appliquee pour alleger le bundle).
const CARVERTICAL_BANNER_SRC = "data:image/png;base64,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";
const CARVERTICAL_AFF_BASE = "https://www.carvertical.deal/2J5W8FS/F3SJK5/";
function buildCarVerticalUrl(vinOrPlate) {
  const id = String(vinOrPlate || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  const params = new URLSearchParams({ uid: "392", source_id: "AFF", sub1: "vehicleiq" });
  if (id) params.set("sub3", id);
  return `${CARVERTICAL_AFF_BASE}?${params.toString()}`;
}

// Bandeau officiel CarVertical (asset fourni par le partenaire). Affiché tel
// quel ; le seul paramétrage est le VIN/plaque transmis dans le lien.
function CarVerticalBanner({ vin, plaque, style }) {
  const identifier = vin || plaque || "";
  return (
    <a href={buildCarVerticalUrl(identifier)} target="_blank" rel="noopener noreferrer sponsored"
      title="Obtiens ton rapport d'historique — proposé par CarVertical"
      style={{ display: "block", borderRadius: 12, overflow: "hidden", lineHeight: 0, boxShadow: "0 2px 10px rgba(0,0,0,.08)", ...style }}>
      <img src={CARVERTICAL_BANNER_SRC} alt="Obtiens ton rapport d'historique — proposé par CarVertical"
        style={{ width: "100%", height: "auto", display: "block" }} />
    </a>
  );
}

// v57 — CarVertical n'a pas encore de clé API (contrat non signé) : import
// manuel (PDF/capture/texte, analysé par Claude), EXACTEMENT comme HistoVec
// ci-dessus — plus de lancement automatique par VIN tant qu'aucune clé n'est
// disponible.
function CarVerticalImport({ onImported, compact, vin, plaque }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);

  const submit = async (payload) => {
    setBusy(true); setErr("");
    try {
      const r = await apiPost("/api/carvertical/import", payload);
      if (r && r.success && r.carvertical) { onImported && onImported(r.carvertical); setOpen(false); }
      else setErr((r && r.error) || "Rapport CarVertical non reconnu.");
    } catch (e) { setErr((e && e.message) || "Import impossible."); }
    finally { setBusy(false); }
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setBusy(true); setErr("");
    try {
      const images = []; let pdf = null;
      for (const f of arr.slice(0, 6)) {
        if (f.type === "application/pdf") { if (!pdf) pdf = await _fileToDataURL(f); }
        else if (f.type && f.type.startsWith("image/")) { images.push(await compressImageFile(f)); }
      }
      const payload = {};
      if (images.length) payload.images = images;
      if (pdf) payload.pdf = pdf;
      if (!payload.images && !payload.pdf) { setErr("Formats acceptés : PDF ou image."); setBusy(false); return; }
      await submit(payload);
    } catch (e) { setErr((e && e.message) || "Import impossible."); setBusy(false); }
  };

  return (
    <div style={{ border: "1px dashed var(--border)", borderRadius: 8, padding: compact ? ".7rem .8rem" : ".9rem 1rem", background: "rgba(168,128,58,.04)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".6rem", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: ".86rem", display: "flex", alignItems: "center", gap: ".45rem", flexWrap: "wrap" }}>
            🔧 Importer un rapport CarVertical <span style={{ color: "var(--muted)", fontWeight: 500 }}>(optionnel)</span>
          </div>
          <div style={{ fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.5, marginTop: ".15rem" }}>
            Historique international (import/export, compteur trafiqué, sinistres) — complète HistoVec.
            {(vin || plaque)
              ? " Obtenez le rapport de CE véhicule (VIN pré-rempli), puis importez-le ici."
              : " Obtenez votre rapport, puis importez-le ici."}
          </div>
          {/* Bandeau partenaire officiel — VIN/plaque transmis automatiquement */}
          <CarVerticalBanner vin={vin} plaque={plaque} style={{ marginTop: ".55rem", maxWidth: 340 }} />
        </div>
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setOpen(o => !o)} style={{ fontSize: ".78rem", whiteSpace: "nowrap" }}>
          {busy ? "Analyse…" : open ? "Fermer" : "Importer"}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: ".7rem", display: "flex", flexDirection: "column", gap: ".5rem" }}>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" multiple style={{ display: "none" }}
            onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} />
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()} style={{ fontSize: ".78rem", justifyContent: "center" }}>
            {busy ? "Analyse en cours…" : "📄 Choisir le PDF / une capture du rapport"}
          </button>
        </div>
      )}
      {err && <div style={{ marginTop: ".5rem", fontSize: ".74rem", color: "#E08A6A" }}>{err}</div>}
    </div>
  );
}

// Synthèse fiabilité + points à surveiller (problèmes connus, recherche web).
// Résumé textuel des points de vigilance à partir du score de fiabilité VIQ.AI®
// et des problèmes connus (common_issues). Réutilisé : Estimation, Contrôle, Leads.
function vigilancePoints(score, known) {
  const issues = (known && Array.isArray(known.common_issues)) ? known.common_issues : [];
  const majors = issues.filter(i => i && i.gravite === "majeur").map(i => i.titre).filter(Boolean);
  let head;
  if (typeof score !== "number") head = "Analyse de fiabilité indisponible pour ce véhicule.";
  else if (score >= 80) head = `Bonne fiabilité (${score}/100) — peu de points de vigilance connus sur ce modèle.`;
  else if (score >= 60) head = `Fiabilité correcte (${score}/100) — quelques points à surveiller avant achat ou revente.`;
  else head = `Vigilance accrue (${score}/100) — faiblesses connues sur ce modèle / millésime.`;
  let detail = "";
  if (majors.length) detail = " Points majeurs : " + majors.slice(0, 3).join(", ") + ".";
  else if (issues.length) detail = " À vérifier : " + issues.slice(0, 3).map(i => i.titre).filter(Boolean).join(", ") + ".";
  return head + detail;
}

// v13 : messages de progression détaillés pendant l'analyse de fiabilité —
// remplace le texte statique unique par une rotation de sous-étapes, pour que
// l'utilisateur voie concrètement ce qui se passe pendant l'attente.
const RELIABILITY_ANALYSIS_STEPS = [
  "Recherche sur les forums spécialisés…",
  "Analyse des blogs et articles fiabilité…",
  "Vérification des rappels constructeur…",
  "Recoupement des retours propriétaires…",
  "Calcul du score de fiabilité VIQ.AI®…",
];
function ReliabilityAnalysisLoader() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % RELIABILITY_ANALYSIS_STEPS.length), 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ marginTop: "1rem", padding: ".9rem 1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: ".82rem", color: "var(--sub)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
        <span className="loader-dot-pulse" style={{ width: 6, height: 6 }} />
        🔎 {RELIABILITY_ANALYSIS_STEPS[idx]}
      </div>
      <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: ".3rem" }}>Analyse de la fiabilité du modèle en cours (forums, blogs, rappels constructeur)</div>
    </div>
  );
}

// Calcule le top 5 des points de vigilance à partir d'une synthèse "known
// issues" — utilise vigilance_top5 s'il est déjà fourni par le serveur (v13),
// sinon le recalcule côté client pour les entrées mises en cache AVANT ce
// champ (rétrocompatibilité historique / anciens exports PDF régénérés).
function getVigilanceTop5(data) {
  if (!data) return [];
  if (Array.isArray(data.vigilance_top5) && data.vigilance_top5.length) return data.vigilance_top5;
  const out = [];
  const push = (l) => { if (l && out.length < 5 && !out.includes(l)) out.push(l); };
  const issues = Array.isArray(data.common_issues) ? data.common_issues : [];
  issues.filter(i => i.gravite === "majeur").forEach(i => push(`${i.zone} — ${i.titre}`));
  (data.recalls || []).forEach(r => push(`Rappel constructeur : ${r}`));
  issues.filter(i => i.gravite === "modéré").forEach(i => push(`${i.zone} — ${i.titre}`));
  (data.to_watch || []).forEach(w => push(w));
  issues.filter(i => i.gravite === "mineur").forEach(i => push(`${i.zone} — ${i.titre}`));
  return out;
}

function KnownIssuesPanel({ data, loading }) {
  if (loading) {
    return <ReliabilityAnalysisLoader />;
  }
  if (!data || !data.found) return null;
  const relColor = data.reliability_label === "Bonne" ? "#5FB89E"
    : data.reliability_label === "Fragile" ? "#E08A6A"
    : "var(--accent2)";
  const top5 = getVigilanceTop5(data);
  return (
    <div style={{ marginTop: "1rem", padding: "1rem 1.1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".6rem", flexWrap: "wrap", marginBottom: ".6rem" }}>
        <div style={{ fontWeight: 700, fontSize: ".92rem" }}>🛡️ Score de fiabilité VIQ.AI® <span style={{ fontWeight: 500, color: "var(--muted)", fontSize: ".76rem" }}>(état mécanique/administratif — distinct du score de vente)</span></div>
        <span className="status-badge" style={{ background: "rgba(168,128,58,.10)", color: relColor, border: `1px solid ${relColor}55`, fontSize: ".72rem" }}>
          Fiabilité : {data.reliability_label}{data.reliability_score != null ? ` · ${data.reliability_score}/100` : ""}
        </span>
      </div>
      {data.summary && <div style={{ fontSize: ".84rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: ".8rem" }}>{data.summary}</div>}

      {top5.length > 0 && (
        <div style={{ marginBottom: ".9rem", padding: ".7rem .9rem", background: "rgba(224,138,106,.06)", border: "1px solid rgba(224,138,106,.25)", borderRadius: 6 }}>
          <div style={{ fontSize: ".78rem", fontWeight: 700, marginBottom: ".4rem", color: "#E08A6A" }}>⚠️ Top {top5.length} points de vigilance majeurs</div>
          <ol style={{ margin: 0, paddingLeft: "1.2rem", display: "flex", flexDirection: "column", gap: ".3rem" }}>
            {top5.map((w, i) => <li key={i} style={{ fontSize: ".82rem", color: "var(--text)", lineHeight: 1.5, fontWeight: 600 }}>{w}</li>)}
          </ol>
        </div>
      )}

      {Array.isArray(data.common_issues) && data.common_issues.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
          <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>Problèmes fréquents sur ce modèle / millésime</div>
          {data.common_issues.map((it, i) => {
            const s = _severityStyle(it.gravite);
            return (
              <div key={i} style={{ padding: ".6rem .8rem", background: "var(--deep)", border: "1px solid var(--border)", borderRadius: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
                  <span style={{ fontSize: ".66rem", fontWeight: 700, padding: ".1rem .45rem", borderRadius: 4, background: s.bg, color: s.fg, border: `1px solid ${s.bd}` }}>{s.label}</span>
                  <span style={{ fontSize: ".7rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{it.zone}</span>
                  <span style={{ fontWeight: 700, fontSize: ".85rem" }}>{it.titre}</span>
                </div>
                {it.detail && <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.5, marginTop: ".25rem" }}>{it.detail}</div>}
                {(it.km_typique || it.cout_estime) && (
                  <div style={{ display: "flex", gap: "1rem", marginTop: ".3rem", fontSize: ".72rem", color: "var(--muted)" }}>
                    {it.km_typique && <span>📍 {it.km_typique}</span>}
                    {it.cout_estime && <span>💶 {it.cout_estime}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {Array.isArray(data.to_watch) && data.to_watch.length > 0 && (
        <div style={{ marginTop: ".9rem", padding: ".7rem .9rem", background: "rgba(255,184,0,.05)", border: "1px solid rgba(255,184,0,.2)", borderRadius: 6 }}>
          <div style={{ fontSize: ".78rem", fontWeight: 700, marginBottom: ".4rem", color: "var(--accent2)" }}>👁 À surveiller / vérifier avant l'achat</div>
          <ul style={{ margin: 0, paddingLeft: "1.1rem", display: "flex", flexDirection: "column", gap: ".25rem" }}>
            {data.to_watch.map((w, i) => <li key={i} style={{ fontSize: ".79rem", color: "var(--sub)", lineHeight: 1.5 }}>{w}</li>)}
          </ul>
        </div>
      )}

      {Array.isArray(data.mechanical_tips) && data.mechanical_tips.length > 0 && (
        <div style={{ marginTop: ".7rem", padding: ".7rem .9rem", background: "rgba(46,111,92,.05)", border: "1px solid rgba(46,111,92,.2)", borderRadius: 6 }}>
          <div style={{ fontSize: ".78rem", fontWeight: 700, marginBottom: ".4rem", color: "#5FB89E" }}>🔧 Conseils mécaniques & entretiens couramment pratiqués</div>
          <ul style={{ margin: 0, paddingLeft: "1.1rem", display: "flex", flexDirection: "column", gap: ".25rem" }}>
            {data.mechanical_tips.map((w, i) => <li key={i} style={{ fontSize: ".79rem", color: "var(--sub)", lineHeight: 1.5 }}>{w}</li>)}
          </ul>
        </div>
      )}

      {Array.isArray(data.strong_points) && data.strong_points.length > 0 && (
        <div style={{ marginTop: ".7rem", fontSize: ".78rem", color: "var(--sub)" }}>
          <span style={{ fontWeight: 700, color: "#5FB89E" }}>Points forts : </span>{data.strong_points.join(" · ")}
        </div>
      )}

      {Array.isArray(data.recalls) && data.recalls.length > 0 && (
        <div style={{ marginTop: ".5rem", fontSize: ".76rem", color: "#E08A6A" }}>
          <span style={{ fontWeight: 700 }}>Rappels constructeur : </span>{data.recalls.join(" · ")}
        </div>
      )}

      {Array.isArray(data.sources) && data.sources.length > 0 && (
        <div style={{ marginTop: ".7rem", fontSize: ".7rem", color: "var(--muted)" }}>
          Sources : {data.sources.map((s, i) => (
            <span key={i}>{i > 0 ? " · " : ""}<a href={s.url} target="_blank" rel="noreferrer" style={{ color: "#90CAF9" }}>{s.titre || "lien"}</a></span>
          ))}
        </div>
      )}
      <div style={{ marginTop: ".7rem", fontSize: ".67rem", color: "var(--muted)", fontStyle: "italic", lineHeight: 1.5 }}>
        Synthèse indicative issue de retours publics (forums, blogs, articles fiabilité). Ne remplace pas un contrôle mécanique. Vérifiez chaque point sur le véhicule réel.
      </div>
    </div>
  );
}

// ─── Pay-as-you-go : gestion d'un refus de paiement (402) sur une estimation ──
// Le backend renvoie 402 avec { error:"no_card"|"card_declined", message } quand
// les estimations offertes (quota à vie) sont épuisées et que le débit immédiat
// échoue (pas de carte / carte refusée). On affiche le message et on ouvre
// l'onglet Paiement de « Mon espace » pour que l'utilisateur règle la situation.
// Retourne true si l'erreur était bien un 402 paiement (→ l'appelant doit STOPPER,
// sans déclencher de fallback local).
function isPaymentRequiredError(e) {
  return e && e.status === 402 && e.detail &&
    (e.detail.error === "no_card" || e.detail.error === "card_declined");
}
function handlePaymentRequired(e) {
  const msg = (e.detail && e.detail.message)
    || "Vos cotes offertes sont épuisées. Ajoutez un moyen de paiement pour continuer (4,99 € par dossier).";
  try { alert(msg); } catch(_) {}
  try { window.dispatchEvent(new Event("viq-open-payment-tab")); } catch(_) {}
}

async function apiDelete(path) {
  const res = await fetch(`${API_URL}${path}`, {
    method: "DELETE",
    headers: await getAuthHeaders(),
  });
  if (!res.ok) {
    let detail = null;
    try { detail = await res.json(); } catch(_) {}
    const err = new Error((detail && detail.error) || `Erreur API: ${res.status}`);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
}


// ─── SVG Payment & Partner Logos ─────────────────────────────────────────────

const CarteBleueIcon = () => (
  <svg width="46" height="28" viewBox="0 0 46 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 3, overflow: "hidden" }}>
    <defs>
      <linearGradient id="cbG" x1="0" y1="0" x2="46" y2="0" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#0055A4"/>
        <stop offset="42%" stopColor="#7aacd6"/>
        <stop offset="58%" stopColor="#d97070"/>
        <stop offset="100%" stopColor="#CC0000"/>
      </linearGradient>
      <clipPath id="cbC"><rect width="46" height="28" rx="3"/></clipPath>
    </defs>
    <rect width="46" height="28" rx="3" fill="url(#cbG)"/>
    <g clipPath="url(#cbC)">
      {/* "e" — cercle blanc avec encoche droite et trait horizontal */}
      <circle cx="16.5" cy="14" r="8.5" fill="white"/>
      <rect x="21" y="5.5" width="5" height="17" fill="url(#cbG)"/>
      <rect x="11.5" y="12.5" width="13" height="3" fill="url(#cbG)"/>
      {/* "B" — tige verticale + 2 demi-capsules */}
      <rect x="26.5" y="6" width="3" height="16" rx="1" fill="white"/>
      <rect x="29.5" y="6" width="8" height="7" rx="3.5" fill="white"/>
      <rect x="29.5" y="15" width="8" height="7" rx="3.5" fill="white"/>
      <rect x="26.5" y="12.5" width="11" height="3" fill="url(#cbG)"/>
    </g>
  </svg>
);

const PCIDSSIcon = () => (
  <svg width="38" height="28" viewBox="0 0 38 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 3 }}>
    <rect width="38" height="28" rx="3" fill="#1a6b6b"/>
    <text x="4" y="11" fontSize="7" fontWeight="900" fontFamily="Arial" fill="white">PCI</text>
    <text x="4" y="20" fontSize="6" fontWeight="700" fontFamily="Arial" fill="white">DSS</text>
    <polyline points="22,8 26,18 35,6" stroke="#44dd66" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const VisaSVG = () => (
  <svg width="46" height="28" viewBox="0 0 46 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="46" height="28" rx="4" fill="#1A1F71"/>
    <path d="M19.5 19.5H16.8L18.5 8.5H21.2L19.5 19.5Z" fill="white"/>
    <path d="M28.8 8.7C28.2 8.5 27.3 8.3 26.2 8.3C23.5 8.3 21.6 9.7 21.6 11.7C21.6 13.2 23 14 24.1 14.5C25.2 15 25.6 15.4 25.6 15.9C25.6 16.7 24.6 17.1 23.7 17.1C22.5 17.1 21.9 16.9 20.9 16.5L20.5 16.3L20.1 18.8C20.8 19.1 22.1 19.4 23.4 19.4C26.3 19.4 28.1 18 28.1 15.9C28.1 14.7 27.3 13.8 25.7 13.1C24.7 12.6 24.1 12.3 24.1 11.7C24.1 11.2 24.7 10.6 25.9 10.6C26.9 10.6 27.6 10.8 28.2 11L28.5 11.1L28.8 8.7Z" fill="white"/>
    <path d="M32.5 8.5H30.4C29.8 8.5 29.3 8.7 29.1 9.3L25.3 19.5H28.2L28.8 17.8H32.3L32.7 19.5H35.3L32.5 8.5ZM29.6 15.7C29.8 15.2 30.8 12.5 30.8 12.5C30.8 12.5 31.1 11.7 31.3 11.2L31.5 12.4C31.5 12.4 32.1 15.1 32.2 15.7H29.6Z" fill="white"/>
    <path d="M14.5 8.5L11.8 15.9L11.5 14.5C11 12.9 9.4 11.1 7.7 10.2L10.1 19.5H13L17.4 8.5H14.5Z" fill="white"/>
    <path d="M9.3 8.5H4.8L4.7 8.7C8.2 9.6 10.5 11.7 11.5 14.5L10.5 9.3C10.3 8.7 9.9 8.5 9.3 8.5Z" fill="#FAA61A"/>
  </svg>
);

const MastercardSVG = () => (
  <svg width="46" height="28" viewBox="0 0 46 28" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="46" height="28" rx="4" fill="#252525"/>
    <circle cx="17" cy="14" r="8" fill="#EB001B"/>
    <circle cx="29" cy="14" r="8" fill="#F79E1B"/>
    <path d="M23 7.8C24.8 9.1 26 11.4 26 14C26 16.6 24.8 18.9 23 20.2C21.2 18.9 20 16.6 20 14C20 11.4 21.2 9.1 23 7.8Z" fill="#FF5F00"/>
  </svg>
);

const APIPlaqueSVG = () => (
  <svg width="130" height="26" viewBox="0 0 130 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Icône plaque */}
    <rect x="1" y="4" width="26" height="18" rx="3" fill="#1565C0" stroke="#1565C0" strokeWidth="1"/>
    <rect x="3" y="6" width="22" height="14" rx="2" fill="#1E88E5"/>
    <text x="5" y="17" fill="white" fontFamily="Arial Black, sans-serif" fontSize="7" fontWeight="900" letterSpacing="0.5">AB·123</text>
    {/* Texte */}
    <text x="32" y="11" fill="#1E88E5" fontFamily="Arial Black, sans-serif" fontSize="8" fontWeight="900" letterSpacing="0.3">API</text>
    <text x="32" y="21" fill="white" fontFamily="Arial, sans-serif" fontSize="8" fontWeight="600" letterSpacing="0.2">PLAQUE</text>
    <text x="62" y="21" fill="#1E88E5" fontFamily="Arial, sans-serif" fontSize="8" fontWeight="400">immatriculation</text>
  </svg>
);
// Alias for legacy references
// alias removed

const GoogleSVG = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.6 10.23c0-.68-.06-1.36-.18-2H10v3.79h5.38a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.9-1.75 2.99-4.33 2.99-7.31z" fill="#4285F4"/>
    <path d="M10 20c2.7 0 4.96-.89 6.61-2.42l-3.23-2.5c-.9.6-2.04.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12H1.08v2.58A10 10 0 0 0 10 20z" fill="#34A853"/>
    <path d="M4.41 11.92A6.01 6.01 0 0 1 4.1 10c0-.67.12-1.32.31-1.92V5.5H1.08A10 10 0 0 0 0 10c0 1.61.39 3.13 1.08 4.5l3.33-2.58z" fill="#FBBC05"/>
    <path d="M10 3.96c1.47 0 2.79.51 3.83 1.5l2.87-2.87A9.94 9.94 0 0 0 10 0 10 10 0 0 0 1.08 5.5l3.33 2.58C5.2 5.72 7.4 3.96 10 3.96z" fill="#EA4335"/>
  </svg>
);

// ─── Design tokens ────────────────────────────────────────────────────────────
const CSS = `
  /*
   * VehicleIQ Design System v2.0 — Mai 2026
   * Inspiration : Stripe Atlas, Linear, Cursor, audi.com, signalapp.com
   *
   * Polices :
   *  - Body : "Inter" (très lisible, neutre, premium fintech)
   *  - Headlines : "Fraunces" (serif moderne, légèrement geometrique,
   *                touche éditoriale humaine — utilisée par Lufthansa, Stripe blog)
   *  - Mono : "JetBrains Mono" (chiffres tabulaires, lisibilité supérieure)
   *
   * Palette light (par défaut) :
   *  - Fond : off-white légèrement chaud (pas de blanc froid clinique)
   *  - Accent : bleu profond sérieux (Audi/BMW heritage)
   *  - Secondaire : ambre chaleureux (continuité orange marque)
   *  - Vert/Rouge : tons mûris (vert sapin, bordeaux)
   */
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700;800&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    /* ─── Surfaces (blanc cassé crème — beige adouci) ──────────────────── */
    --white: #FDFBF6;       /* Crème très clair — cartes/surfaces */
    --bg: #F8F4EC;          /* Blanc cassé chaud — fond de page (adouci) */
    --card: #FDFBF6;        /* Cartes/surfaces : crème clair */
    --deep: #F0EBE0;        /* Surface enfoncée, beige doux */
    --border: #E8E1D4;      /* Bordures subtiles, ton ivoire clair */
    --border-strong: #D6CDBD;

    /* ─── Texte ─────────────────────────────────────────────────────── */
    --text: #15171C;        /* Encre profonde, contraste élevé */
    --sub: #4E525B;         /* Texte secondaire, calme */
    --muted: #8E939E;       /* Tertiaire, mentions */

    /* ─── Couleurs marque (refonte premium SaaS) ────────────────────── */
    /* Doré antique : signature haut de gamme, moins criard que l'orange,
       évoque la valeur, le sérieux, le métier de marchand auto. */
    --accent: #A8803A;          /* Doré antique — accent primaire signature */
    --accent-h: #8A6829;        /* Doré profond, hover */
    --accent-soft: #F7EFDC;     /* Tinte ivoire doux, fond pills/highlights */
    --accent2: #1E2A4A;         /* Bleu nuit — accent secondaire, gravitas */
    --accent2-h: #131C36;
    --accent2-soft: #E8ECF4;

    /* ─── Sémantiques (atténuées, sophistiquées) ────────────────────── */
    --green: #2E6F5C;       /* Vert forêt foncé, business serieux */
    --green-soft: #E8F2EE;
    --red: #9B2C2C;         /* Bordeaux profond */
    --red-soft: #F7EAEA;
    --orange: var(--accent);
    --purple: #4E3A8A;

    /* Compatibilité existante (ne pas supprimer, références anciennes) */
    --blue: var(--accent2);

    /* ─── Typographie premium SaaS ──────────────────────────────────── */
    /* Manrope : sans-serif moderne, chaleureux, esthétique SaaS premium
       (Linear, Cron, Vercel). Plus humain qu'Inter sans être identifiable
       comme "IA". Excellent en multi-graisses. */
    --font-body: 'Manrope', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
    /* Instrument Serif : serif éditorial premium, lettres élégantes,
       contre-points subtils. Évoque l'édition, le métier de connaisseur,
       sans tomber dans le tech-look. */
    --font-head: 'Instrument Serif', Georgia, 'Times New Roman', serif;
    --font-mono: 'JetBrains Mono', 'SF Mono', Consolas, monospace;

    /* ─── Geometry (plus aéré, premium) ─────────────────────────────── */
    --radius: 10px;
    --radius-lg: 16px;
    --radius-xl: 24px;

    /* ─── Shadows (très douces, classieuses) ────────────────────────── */
    --shadow-sm: 0 1px 2px rgba(21,23,28,.04), 0 1px 1px rgba(21,23,28,.03);
    --shadow:    0 4px 14px rgba(21,23,28,.05), 0 1px 3px rgba(21,23,28,.04);
    --shadow-lg: 0 24px 48px rgba(21,23,28,.07), 0 8px 22px rgba(21,23,28,.04);
    --shadow-xl: 0 32px 72px rgba(21,23,28,.10), 0 12px 28px rgba(21,23,28,.06);
  }

  html {
    /* Réduction globale légère de la taille de police : 16 → 15px.
       Toute la hiérarchie typographique (rem) se contracte proportionnellement,
       sans toucher aux polices ni aux ratios. Effet : interface plus dense,
       cohérente avec le style sobre iautos-like. */
    font-size: 15px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
  html {
    /* v42 — Filet de sécurité anti-débordement horizontal mobile : en
       complément de overflow-x:hidden sur body (déjà présent), certains
       navigateurs mobiles autorisent quand même un scroll horizontal du
       document si UN SEUL élément dépasse — ce filet l'empêche au niveau
       racine, quel que soit l'élément fautif. */
    max-width: 100%;
    overflow-x: hidden;
  }
  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-body);
    font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11', 'ss01';
    line-height: 1.6;
    overflow-x: hidden;
    letter-spacing: -0.005em;
  }

  /* Headlines : Fraunces avec optical sizing fluide — style unifié hero/sections/modales */
  h1, h2, h3, h4,
  .panel-title, .sec-title, .market-section-title,
  .hero-headline, .hero-h1, .onboarding-logo {
    font-family: var(--font-head);
    font-feature-settings: 'ss01', 'ss02', 'ss03';
    letter-spacing: -0.02em;
    font-weight: 800;
  }
  /* Toute mention <em> dans un titre principal s'affiche en orange ambre, non-italique */
  h1 em, h2 em, h3 em, h4 em,
  .panel-title em, .sec-title em, .market-section-title em,
  .hero-headline em, .hero-h1 em {
    color: var(--accent);
    font-style: normal;
    font-weight: inherit;
  }

  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: 4px; transition: background .15s; }
  ::-webkit-scrollbar-thumb:hover { background: var(--muted); }

  ::selection { background: var(--accent-soft); color: var(--accent-h); }

  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

  .app { min-height: 100vh; }

  /* ─── NAV ─── */
  .hero {
    min-height: 100vh;
    display: flex; flex-direction: column;
    background: var(--white);
  }
  .hero-grid { display: none; }
  .hero-gradient { display: none; }
  .hero-content { flex: 1; display: flex; flex-direction: column; }

  nav {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 1.6rem; height: 60px;
    border: 1px solid var(--border);
    background: var(--white);
    /* Navbar flottante : détachée des bords, arrondie, ombre premium.
       top = marge de respiration (le bandeau ticker a été retiré). */
    position: fixed; top: 18px; left: 50%; transform: translateX(-50%);
    width: calc(100% - 3rem); max-width: 1280px; z-index: 200;
    border-radius: 999px;
    box-shadow: var(--shadow-lg);
  }
  /* Compensation : .hero porte le padding-top pour ne pas pousser tout .app
     (sinon les modales en position:fixed à l'intérieur de .app — qui a un
     opacity:* qui crée un containing block — se retrouvent décalées).
     ~94px = 18px (offset) + 60px (navbar) + marge. */
  .hero { padding-top: 96px; }
  /* Page d'accueil user.vehicleiq.fr (AppHome) : même largeur que la navbar
     (calc(100% - 3rem), max-width 1280px, centrée) pour un alignement visuel
     cohérent entre le bandeau nav "pilule" et le contenu en dessous. */
  .app-home-wrap { width: calc(100% - 3rem); max-width: 1280px; margin: 0 auto; }
  @media (max-width: 768px) {
    /* Mobile : respecter l'encoche / barre système (safe-area) + plus d'air en haut.
       Sans ça, la navbar paraît collée au bord supérieur de l'écran. */
    nav { width: calc(100% - 1.6rem); top: calc(18px + env(safe-area-inset-top, 0px)); padding: 0 1rem; }
    .app-home-wrap { width: calc(100% - 1.6rem); }
    .hero { padding-top: calc(96px + env(safe-area-inset-top, 0px)); }
  }
  /* Brand mark unifié (topbar + footer) */
  .brand-mark {
    display: inline-flex; flex-direction: column; align-items: flex-start;
    gap: 3px; line-height: 1;
  }
  .brand-mark-clickable { cursor: pointer; transition: opacity .15s ease; }
  .brand-mark-clickable:hover { opacity: .72; }
  .brand-mark-clickable:focus-visible { outline: 2px solid var(--accent); outline-offset: 4px; border-radius: 4px; }
  .brand-mark .logo {
    font-family: var(--font-head); font-size: 1.32rem; letter-spacing: -.02em;
    color: var(--text); font-weight: 700; display: flex; align-items: center; gap: 8px;
    font-feature-settings: 'ss01';
  }
  .brand-mark .logo-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--accent2); flex-shrink: 0;
    box-shadow: 0 0 0 2px var(--accent2-soft);
  }
  .brand-mark .logo span { color: var(--accent); font-style: italic; }
  .brand-tagline {
    font-family: 'JetBrains Mono', monospace;
    font-size: .54rem;
    letter-spacing: 2.5px;
    color: var(--muted);
    text-transform: uppercase;
    font-weight: 500;
    padding-left: 14px; /* aligné après le dot + son gap */
  }
  /* Variante footer : conservation de l'indent pour aligner avec le V après le dot */
  .brand-mark.brand-footer .brand-tagline { padding-left: 14px; }

  .logo {
    font-family: var(--font-head); font-size: 1.32rem; letter-spacing: -.02em;
    color: var(--text); font-weight: 700; display: flex; align-items: center; gap: 8px;
    font-feature-settings: 'ss01';
  }
  .logo-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--accent2); flex-shrink: 0;
    box-shadow: 0 0 0 2px var(--accent2-soft);
  }
  .logo span { color: var(--accent); font-style: italic; }
  .nav-center { display: flex; align-items: center; gap: .25rem; }
  .nav-link {
    font-size: .88rem; font-weight: 500; color: var(--sub);
    padding: .45rem .8rem; border-radius: var(--radius); cursor: pointer;
    transition: all .15s; background: none; border: none;
    white-space: nowrap;
  }
  .nav-link:hover { color: var(--text); background: var(--bg); }
  .nav-actions { display: flex; gap: .5rem; align-items: center; }
  .nav-btn-label { display: inline; }

  /* ─── BUTTONS ─── */
  .btn {
    font-family: var(--font-body); font-weight: 600; border: none; cursor: pointer;
    transition: all .15s cubic-bezier(.4,0,.2,1); display: inline-flex; align-items: center; gap: .45rem;
    border-radius: 100px; font-size: .9rem; text-decoration: none;
    white-space: nowrap; letter-spacing: -.01em;
  }
  .btn-ghost {
    background: transparent; color: var(--sub);
    padding: .5rem .9rem; border: 1px solid var(--border);
    font-size: .85rem; font-weight: 500;
  }
  .btn-ghost:hover {
    border-color: var(--text); color: var(--text);
    background: var(--deep);
  }
  .btn-accent {
    background: var(--accent); color: #fff; padding: .65rem 1.5rem;
    font-size: .9rem; font-weight: 600;
    box-shadow: 0 1px 2px rgba(168,128,58,.15), 0 4px 12px rgba(168,128,58,.18);
  }
  .btn-accent:hover {
    background: var(--accent-h);
    box-shadow: 0 2px 4px rgba(168,128,58,.2), 0 8px 24px rgba(168,128,58,.24);
    transform: translateY(-1px);
  }
  .btn-outline {
    background: transparent; color: var(--accent); padding: .6rem 1.4rem;
    font-size: .9rem; border: 1.5px solid var(--accent); font-weight: 600;
  }
  .btn-outline:hover { background: var(--accent-soft); }
  .btn-white {
    background: var(--card); color: var(--text); padding: .65rem 1.5rem;
    font-size: .9rem; border: 1px solid var(--border);
    box-shadow: var(--shadow-sm); font-weight: 500;
  }
  .btn-white:hover {
    border-color: var(--text); color: var(--text);
    box-shadow: var(--shadow);
    transform: translateY(-1px);
  }
  .btn-sm { padding: .42rem .9rem; font-size: .82rem; }
  .btn-green {
    background: var(--green); color: #fff;
    box-shadow: 0 1px 2px rgba(15,118,110,.15), 0 4px 12px rgba(15,118,110,.18);
  }
  .btn-green:hover { background: #0B5E58; transform: translateY(-1px); }
  .btn-disabled { opacity: .45; pointer-events: none; cursor: not-allowed; }

  /* ─── HERO CENTER ─── */
  .hero-center {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 4.5rem 2rem 4rem;
    background: linear-gradient(180deg, #F4F7FF 0%, #FFFFFF 100%);
    position: relative; overflow: hidden;
  }
  .hero-center::before {
    content: ''; position: absolute; inset: 0; z-index: 0;
    background:
      radial-gradient(ellipse 60% 40% at 30% 50%, rgba(168,128,58,.06) 0%, transparent 65%),
      radial-gradient(ellipse 50% 40% at 70% 50%, rgba(0,191,165,.05) 0%, transparent 65%);
  }
  .hero-inner { position: relative; z-index: 1; max-width: 800px; }

  /* ─── Hero 2 colonnes ─── */
  .hero-2col {
    position: relative; z-index: 1;
    width: 100%; max-width: 1180px;
    display: grid; grid-template-columns: 1.05fr .95fr;
    gap: 3.5rem; align-items: start;
  }
  .hero-col-right { position: sticky; top: 1.5rem; }
  .hero-col-left { text-align: left; }
  .hero-col-left .hero-badge { margin-bottom: 1.5rem; }
  .hero-col-left .hero-h1 { text-align: left; }
  .hero-col-left .hero-sub { margin: 0 0 2rem; max-width: 520px; }
  .hero-col-left .hero-service-links { justify-content: flex-start; }
  .hero-col-left .hero-trust { justify-content: flex-start; }
  .hero-cta-row {
    display: flex; gap: .9rem; flex-wrap: wrap; margin-bottom: 2rem;
  }
  .hero-cta-primary { padding: .9rem 1.7rem; font-size: .95rem; font-weight: 700; }
  .hero-cta-secondary { padding: .9rem 1.6rem; font-size: .95rem; font-weight: 600; }

  /* ─── Formulaire d'estimation inline (colonne droite) ─── */
  .hero-form-card {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 20px;
    padding: 2rem 1.9rem;
    box-shadow: 0 24px 60px rgba(20,30,55,.12), 0 2px 8px rgba(20,30,55,.04);
  }
  .hero-form-title {
    font-family: var(--font-head); font-size: 1.55rem; font-weight: 700;
    color: var(--text); letter-spacing: -.02em;
  }
  .hero-form-sub {
    font-size: .88rem; color: var(--muted); margin: .35rem 0 1.5rem;
  }
  .hero-form-fields { display: flex; flex-direction: column; gap: 1rem; }
  .hero-form-row2 { display: grid; grid-template-columns: 1fr 1fr; gap: .8rem; }
  .hero-form-card select, .hero-form-card input { transition: border-color .15s ease; }
  .hero-form-card select:focus, .hero-form-card input:focus { border-color: var(--accent); }
  .hero-form-btn {
    width: 100%; margin-top: 1.5rem; padding: .95rem;
    font-size: .98rem; font-weight: 700;
  }
  /* ─── Vidéo hero (colonne droite) ─── */
  .hero-video-card {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 20px;
    padding: 1.4rem;
    box-shadow: 0 24px 60px rgba(20,30,55,.12), 0 2px 8px rgba(20,30,55,.04);
  }
  .hero-video-frame {
    position: relative; padding-bottom: 56.25%; height: 0;
    border-radius: 14px; overflow: hidden; background: #0E1119;
  }
  .hero-video-frame iframe {
    position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;
  }
  .hero-video-placeholder {
    border-radius: 14px; background: linear-gradient(150deg, #14171F 0%, #1d2230 100%);
    aspect-ratio: 16/9; display: flex; flex-direction: column;
    align-items: center; justify-content: center; text-align: center;
    padding: 2rem; gap: .5rem;
  }
  .hero-video-play {
    width: 64px; height: 64px; border-radius: 50%;
    background: var(--accent); color: #fff;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.4rem; padding-left: 4px; margin-bottom: .5rem;
    box-shadow: 0 8px 24px rgba(168,128,58,.4);
  }
  .hero-video-ph-title {
    font-family: var(--font-head); font-size: 1.25rem; font-weight: 700; color: #fff;
  }
  .hero-video-ph-sub {
    font-size: .85rem; color: rgba(255,255,255,.6); max-width: 320px; line-height: 1.5;
  }
  .hero-video-card .hero-form-services { margin-top: 1.2rem; padding-top: 1.1rem; }

  /* ─── Vidéo hero (colonne droite) — fin ─── */
  .hero-form-note {
    text-align: center; font-size: .76rem; color: var(--muted);
    margin-top: .9rem;
  }
  .hero-form-services {
    display: flex; align-items: center; justify-content: center;
    flex-wrap: wrap; gap: .4rem .55rem;
    margin-top: 1.1rem; padding-top: 1rem;
    border-top: 1px solid var(--border);
  }
  .hero-form-services-label {
    font-size: .76rem; color: var(--muted); font-weight: 600;
    width: 100%; text-align: center; margin-bottom: .15rem;
  }
  .hero-form-service-link {
    background: none; border: none; cursor: pointer; padding: 0;
    font-family: var(--font-body); font-size: .82rem; font-weight: 600;
    color: var(--accent); transition: opacity .15s ease;
    text-decoration: underline; text-underline-offset: 3px;
    text-decoration-thickness: 1px;
  }
  .hero-form-service-link:hover { opacity: .68; }
  .hero-form-service-sep { color: var(--muted); font-size: .8rem; user-select: none; }

  @media (max-width: 960px) {
    .hero-2col { grid-template-columns: 1fr; gap: 2.5rem; max-width: 560px; }
    .hero-col-left { text-align: center; }
    .hero-col-left .hero-h1 { text-align: center; }
    .hero-col-left .hero-sub { margin: 0 auto 2rem; }
    .hero-col-left .hero-badge { margin-left: auto; margin-right: auto; }
    .hero-col-left .hero-service-links { justify-content: center; }
    .hero-col-left .hero-trust { justify-content: center; }
    .hero-cta-row { justify-content: center; }
  }
  .hero-badge {
    display: inline-flex; align-items: center; gap: .5rem;
    background: rgba(168,128,58,.08); color: var(--accent);
    border: 1px solid rgba(168,128,58,.2); border-radius: 50px;
    padding: .3rem 1rem; font-size: .78rem; font-weight: 600;
    letter-spacing: .3px; margin-bottom: 1.8rem;
  }
  .hero-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); }
  .hero-eyebrow { display: none; }
  .hero-h1 {
    /* +5% sur PC par rapport au clamp précédent (2.02/4.14/3.31) ; graisse renforcée */
    font-size: clamp(2.12rem, 4.35vw, 3.48rem);
    font-weight: 900; line-height: 1.13; letter-spacing: -.022em;
    color: var(--text); margin-bottom: 1.2rem;
  }
  .hero-h1 em { color: var(--accent); font-style: normal; }
  .hero-sub {
    /* Réduction de 8% : 1.1rem → 1.01rem */
    color: var(--sub); font-size: 1.01rem; max-width: 560px; margin: 0 auto 2.5rem;
    font-weight: 400; line-height: 1.75;
  }
  /* ─── HERO CTA : Barre de recherche ─── */
  .hero-search-wrap {
    max-width: 580px; width: 100%; margin: 0 auto 1.2rem;
    cursor: pointer;
  }
  .hero-search-bar {
    display: flex; align-items: center; gap: .65rem;
    background: var(--card); border: 1.5px solid var(--border);
    border-radius: 999px; padding: .4rem .4rem .4rem 1.2rem;
    box-shadow: 0 2px 6px rgba(26,26,31,.04), 0 8px 24px rgba(26,26,31,.06);
    transition: all .2s cubic-bezier(.4,0,.2,1);
  }
  .hero-search-wrap:hover .hero-search-bar {
    border-color: var(--accent);
    box-shadow: 0 4px 10px rgba(168,128,58,.10), 0 12px 32px rgba(168,128,58,.12);
    transform: translateY(-1px);
  }
  .hero-search-icon {
    color: var(--muted); flex-shrink: 0;
  }
  .hero-search-input {
    flex: 1; border: none; outline: none; background: transparent;
    font-family: var(--font-body); font-size: 1rem; color: var(--text);
    padding: .55rem 0; cursor: pointer;
    letter-spacing: -.005em;
  }
  .hero-search-input::placeholder { color: var(--muted); font-weight: 400; }
  .hero-search-btn {
    background: var(--accent); color: #fff; border: none; cursor: pointer;
    padding: .65rem 1.4rem; border-radius: 999px;
    font-family: var(--font-body); font-weight: 600; font-size: .9rem;
    letter-spacing: -.01em;
    box-shadow: 0 1px 2px rgba(168,128,58,.15), 0 4px 10px rgba(168,128,58,.18);
    transition: all .15s cubic-bezier(.4,0,.2,1);
    white-space: nowrap;
  }
  .hero-search-btn:hover {
    background: var(--accent-h);
    box-shadow: 0 2px 4px rgba(168,128,58,.22), 0 8px 18px rgba(168,128,58,.26);
  }

  /* ─── HERO Service Links ─── */
  .hero-service-links {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: .8rem; margin-bottom: 1.5rem; width: 100%;
  }
  .hero-service-link {
    display: flex; flex-direction: column; align-items: flex-start; gap: 3px;
    background: var(--white); border: 1.5px solid var(--border); cursor: pointer;
    font-family: var(--font-body); padding: .8rem 1rem; border-radius: var(--radius);
    transition: all .16s ease; text-align: left;
  }
  .hero-service-link:hover {
    border-color: var(--accent); transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(168,128,58,.12);
  }
  .hero-service-title {
    font-size: .92rem; font-weight: 700; color: var(--text); letter-spacing: -.01em;
  }
  .hero-service-link:hover .hero-service-title { color: var(--accent); }
  .hero-service-desc {
    font-size: .76rem; color: var(--muted); font-weight: 500; line-height: 1.3;
  }
  .hero-service-sep { display: none; }

  .hero-trust {
    font-size: .78rem; color: var(--muted); display: flex; align-items: center;
    justify-content: center; gap: .5rem; flex-wrap: wrap;
  }
  .hero-trust-sep { width: 3px; height: 3px; border-radius: 50%; background: var(--border); }

  /* ─── SERVICE CARDS (mode-grid) — conservé pour autres écrans qui l'utilisent ─── */
  .mode-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.2rem;
    max-width: 960px; width: 100%; margin: 3rem auto 0;
  }
  .mode-card {
    background: var(--white); border: 1.5px solid var(--border); border-radius: var(--radius-lg);
    padding: 2rem 1.8rem; cursor: pointer;
    transition: all .2s; position: relative; overflow: hidden;
    display: flex; flex-direction: column; gap: .8rem;
    box-shadow: var(--shadow-sm); text-align: left;
  }
  .mode-card:hover {
    border-color: var(--accent); box-shadow: var(--shadow);
    transform: translateY(-2px);
  }
  .mode-icon {
    width: 44px; height: 44px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.2rem; flex-shrink: 0;
  }
  .icon-check { background: rgba(168,128,58,.1); }
  .icon-buy { background: rgba(0,191,165,.1); }
  .icon-sell { background: rgba(245,158,11,.1); }
  .mode-label {
    font-size: 1rem; font-weight: 700; color: var(--text); letter-spacing: -.2px;
  }
  .mode-desc { color: var(--sub); font-size: .85rem; font-weight: 400; line-height: 1.6; flex: 1; }
  .mode-arrow {
    font-size: .82rem; color: var(--accent); font-weight: 600;
    margin-top: .4rem; display: flex; align-items: center; gap: .25rem;
    opacity: 0; transform: translateX(-4px); transition: all .2s;
  }
  .mode-card:hover .mode-arrow { opacity: 1; transform: translateX(0); }

  /* ─── STATS BAR ─── */
  .stats-bar {
    display: flex; align-items: center; justify-content: center; gap: 0;
    padding: 0; border-top: 1px solid var(--border);
    background: var(--white); flex-wrap: wrap;
  }
  .stat-item {
    text-align: center; padding: 1.5rem 3rem;
    border-right: 1px solid var(--border);
    flex: 1; min-width: 150px;
  }
  .stat-item:last-child { border-right: none; }
  .stat-val { font-size: 1.6rem; font-weight: 800; color: var(--accent); letter-spacing: -.02em; }
  .stat-label { font-size: .75rem; color: var(--sub); text-transform: uppercase; letter-spacing: .8px; margin-top: 2px; font-weight: 500; }

  /* ─── FREEMIUM BANNER ─── */
  .freemium-banner {
    background: var(--bg);
    border-top: 1px solid var(--border);
    padding: .8rem 2.5rem;
    display: flex; align-items: center; justify-content: center; gap: 1.5rem;
    font-size: .78rem; color: var(--sub); flex-wrap: wrap; text-align: center;
  }
  .freemium-item { display: flex; align-items: center; gap: .35rem; }
  /* Bloc notations en bas de page — format compact */
  .ratings-bottom {
    background: var(--bg);
    border-top: 1px solid var(--border);
    padding: 1.1rem 1.5rem 1.3rem;
    display: flex; flex-direction: column; align-items: center; gap: .5rem;
  }
  .ratings-bottom::before {
    content: "Vos avis sur nos services";
    font-size: .7rem; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--muted); font-weight: 700;
  }
  .ratings-bottom > div { transform: scale(.82); transform-origin: center; }
  @media (max-width: 640px) { .ratings-bottom > div { transform: scale(.92); } }
  .dot { width: 5px; height: 5px; border-radius: 50%; }
  .dot-green { background: var(--green); }
  .dot-orange { background: var(--orange); }
  .dot-red { background: var(--red); }

  /* ═══════════════════════════════════════════════════════════════════════
     HERO MODERNE — thème clair beige premium (même design, fond beige)
     Scopé sous .hero-modern pour ne pas impacter les autres pages.
     ═══════════════════════════════════════════════════════════════════════ */
  .hero-center.hero-modern {
    background: linear-gradient(180deg, #FBF8F1 0%, #F8F4EC 100%);
    padding: 4rem 2rem 0;
  }
  .hero-center.hero-modern::before {
    background:
      radial-gradient(ellipse 70% 50% at 25% 35%, rgba(168,128,58,.10) 0%, transparent 60%),
      radial-gradient(ellipse 55% 45% at 80% 60%, rgba(46,111,92,.07) 0%, transparent 60%);
  }
  /* Eyebrow / badge sur fond beige */
  .hero-modern .hero-badge {
    background: rgba(168,128,58,.10);
    border-color: rgba(168,128,58,.30);
    color: var(--accent-h);
  }
  .hero-modern .hero-badge-dot { background: var(--accent); }
  /* Titre — encre profonde, accent doré */
  .hero-modern .hero-h1 { color: var(--text); }
  .hero-modern .hero-h1 em { color: var(--accent); }
  /* Sous-titre — texte secondaire lisible sur beige */
  .hero-modern .hero-sub { color: var(--sub); }
  .hero-modern .hero-sub strong { color: var(--accent-h) !important; }
  /* CTA secondaire — bordure/texte dorés sur fond clair */
  .hero-modern .hero-cta-secondary {
    color: var(--accent); border-color: var(--accent); background: transparent;
  }
  .hero-modern .hero-cta-secondary:hover {
    background: var(--accent-soft); border-color: var(--accent-h);
  }
  /* ─── Bloc preuve sociale ─── */
  .hero-social {
    display: flex; align-items: center; gap: .85rem; margin-top: .5rem;
  }
  .hero-social-avatars { display: flex; }
  .hero-social-avatars span {
    width: 34px; height: 34px; border-radius: 50%;
    border: 2px solid #F8F4EC;
    margin-right: -10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: .7rem; font-weight: 700; color: #fff;
    font-family: var(--font-body);
  }
  .hero-social-avatars span:last-child { margin-right: 0; }
  .hero-social-stars { color: #E0A415; font-size: 1rem; letter-spacing: 1px; line-height: 1; }
  .hero-social-text { font-size: .82rem; color: var(--sub); margin-top: 3px; }
  .hero-social-text strong { color: var(--text); font-weight: 700; }

  /* Stats bar — intégrée au hero clair */
  .hero-modern-stats {
    width: 100%; max-width: 1180px; margin: 3.5rem auto 0;
    border-top: 1px solid var(--border);
    background: transparent;
  }
  .hero-modern-stats .stat-item { border-right-color: var(--border); }
  .hero-modern-stats .stat-val { color: var(--accent); }
  .hero-modern-stats .stat-label { color: var(--sub); }

  /* Bandeau confiance (types de clients) */
  .hero-trustbar {
    width: 100%; background: var(--deep);
    border-top: 1px solid var(--border);
    padding: 1.4rem 2rem 2.2rem; text-align: center;
  }
  .hero-trustbar-label {
    font-size: .68rem; letter-spacing: 2px; color: var(--muted);
    text-transform: uppercase; font-family: var(--font-mono); margin-bottom: .9rem;
  }
  .hero-trustbar-items {
    display: flex; align-items: center; justify-content: center;
    gap: 2.2rem; flex-wrap: wrap; opacity: .85;
  }
  .hero-trustbar-items span {
    font-size: .95rem; color: var(--sub); font-weight: 500;
  }
  @media (max-width: 768px) {
    .hero-center.hero-modern { padding: 2.5rem 1.2rem 0; }
    .hero-social { justify-content: center; }
    .hero-trustbar-items { gap: 1.2rem; }
    .cg-service-card { grid-template-columns: 1fr !important; gap: 1.3rem !important; padding: 1.4rem 1.3rem !important; }
  }

  /* ─── OVERLAY / PANEL ─── */
  .overlay {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(26,29,46,.5); backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center; padding: 1rem;
  }
  .panel {
    background: var(--white); border: 1px solid var(--border); border-radius: var(--radius-lg);
    width: 100%; max-width: 640px; max-height: 94vh; overflow-y: auto;
    position: relative; animation: slideUp .25s ease; box-shadow: var(--shadow-lg);
  }
  .panel.flex-panel { overflow: hidden; display: flex; flex-direction: column; }
  .panel.flex-panel .panel-body { overflow-y: auto; flex: 1; min-height: 0; }
  .panel-wide { max-width: 900px; }
  /* Grandes modales qui doivent occuper toute la fenêtre comme une vraie page :
     Mon espace, Apporteur d'affaires, Super Admin. Le contenu interne reste
     calé sur une largeur lisible via padding mais le modal lui-même prend
     toute la largeur disponible. */
  .panel-fullscreen {
    max-width: 1800px !important;
    width: 98vw !important;
    max-height: 96vh !important;
    height: 96vh;
    border-radius: 14px;
  }
  @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes viq-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes pulse-ring { 0%{box-shadow:0 0 0 0 rgba(168,128,58,.3)} 70%{box-shadow:0 0 0 8px rgba(168,128,58,0)} 100%{box-shadow:0 0 0 0 rgba(168,128,58,0)} }
  @keyframes fadeSlideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }
  @keyframes countUp { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }
  .panel-header {
    padding: 1.5rem 1.8rem 1.1rem;
    border-bottom: 1px solid var(--border);
    display: flex; align-items: flex-start; justify-content: space-between;
  }
  .panel-title { font-size: 1.35rem; font-weight: 800; letter-spacing: -.02em; line-height: 1.25; }
  .panel-sub { color: var(--sub); font-size: .83rem; margin-top: .2rem; }

  .close-btn {
    background: var(--bg); border: 1px solid var(--border); color: var(--sub);
    width: 30px; height: 30px; border-radius: var(--radius); cursor: pointer; font-size: .85rem;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all .15s;
  }
  .close-btn:hover { background: var(--border); color: var(--text); }
  .panel-body { padding: 1.5rem 1.8rem; }

  /* ─── FORM ─── */
  .field { margin-bottom: 1.2rem; }
  .field label {
    display: block; font-size: .75rem; font-weight: 600; letter-spacing: .4px;
    color: var(--sub); text-transform: uppercase; margin-bottom: .4rem;
  }
  .input {
    width: 100%; background: var(--white); border: 1.5px solid var(--border);
    color: var(--text); font-family: var(--font-body); font-size: .9rem;
    padding: .65rem 1rem; border-radius: var(--radius); transition: border-color .15s;
    outline: none;
  }
  .input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(168,128,58,.1); }
  .input::placeholder { color: var(--muted); }
  .input-vin { letter-spacing: 3px; font-size: .95rem; text-transform: uppercase; font-family: var(--font-mono); }
  select.input { cursor: pointer; }
  .radio-group { display: flex; gap: .5rem; flex-wrap: wrap; }
  .radio-btn {
    padding: .4rem .85rem; background: var(--white); border: 1.5px solid var(--border);
    border-radius: 50px; cursor: pointer; font-size: .83rem; color: var(--sub);
    transition: all .15s; user-select: none; font-weight: 500;
  }
  .radio-btn:hover { border-color: var(--accent); color: var(--accent); }
  .radio-btn.active { border-color: var(--accent); color: #fff; background: var(--accent); font-weight: 600; box-shadow: 0 2px 8px rgba(168,128,58,.35); }
  .range-row { display: flex; align-items: center; gap: 1rem; }
  input[type=range] { flex: 1; accent-color: var(--accent); height: 4px; cursor: pointer; }
  .range-val { font-family: var(--font-mono); font-size: .82rem; color: var(--accent); min-width: 80px; text-align: right; }

  /* ─── Champ numérique avec flèches haut/bas toujours visibles & agrandies ─── */
  /* Firefox : forcer l'affichage des spinners */
  input.num-spin { -moz-appearance: number-input; }
  /* WebKit/Blink : spinner toujours visible + agrandi */
  input.num-spin::-webkit-outer-spin-button,
  input.num-spin::-webkit-inner-spin-button {
    -webkit-appearance: inner-spin-button;
    appearance: auto;
    opacity: 1;            /* visible en permanence (pas seulement au survol) */
    height: 2.2rem;        /* zone cliquable agrandie */
    cursor: pointer;
  }
  .num-spin {
    font-size: 1rem !important;
    padding: .6rem .7rem !important;
    min-height: 2.6rem;
  }

  /* ─── Boutons d'action de l'Estimation détaillée — forme uniforme ─── */
  .estim-actions { display: flex; gap: .5rem; justify-content: flex-end; flex-wrap: wrap; }
  .estim-action-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: .4rem;
    padding: .55rem 1rem; border-radius: 10px;
    font-family: var(--font-body); font-size: .76rem; font-weight: 600; letter-spacing: .01em;
    cursor: pointer; transition: all .15s; white-space: nowrap;
    background: var(--card); border: 1.5px solid var(--accent); color: var(--accent);
    box-sizing: border-box; min-height: 2.4rem;
  }
  .estim-action-btn svg { flex-shrink: 0; width: 14px; height: 14px; }
  .estim-action-btn:hover { background: var(--accent-soft); transform: translateY(-1px); }
  .estim-action-btn.primary {
    background: var(--accent); color: #fff; border-color: var(--accent);
    box-shadow: 0 2px 6px rgba(168,128,58,.18);
  }
  .estim-action-btn.primary:hover { background: var(--accent-h); box-shadow: 0 4px 10px rgba(168,128,58,.28); }
  .estim-action-btn.whatsapp { background: #25D366; color: #fff; border-color: #25D366; }
  .estim-action-btn.whatsapp:hover { background: #1FB855; }

  /* ─── PROGRESS ─── */
  .progress-bar { height: 3px; background: var(--border); border-radius: 2px; margin-bottom: 2rem; }
  .progress-fill { height: 100%; background: var(--accent); border-radius: 2px; transition: width .4s ease; }
  .step-counter { font-size: .72rem; color: var(--muted); margin-bottom: .5rem; }
  .step-question { font-size: 1.1rem; font-weight: 600; margin-bottom: 1.4rem; line-height: 1.4; color: var(--text); }

  /* ─── VIN RESULT ─── */
  .vin-header {
    background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius);
    padding: 1.1rem 1.4rem; margin-bottom: 1.2rem;
    display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem;
  }
  .vin-code { font-family: var(--font-mono); font-size: .9rem; letter-spacing: 2px; color: var(--accent); }
  .vin-make { font-size: 1.3rem; font-weight: 700; color: var(--text); }
  .status-badge {
    font-size: .7rem; letter-spacing: .5px; font-weight: 600;
    padding: .22rem .65rem; border-radius: 50px; display: inline-flex; align-items: center; gap: .3rem;
  }
  .status-ok { background: rgba(0,191,165,.1); color: #00897B; border: 1px solid rgba(0,191,165,.3); }
  .status-warn { background: rgba(245,158,11,.1); color: var(--accent); border: 1px solid rgba(245,158,11,.3); }
  .status-bad { background: rgba(240,68,56,.1); color: var(--red); border: 1px solid rgba(240,68,56,.3); }

  .history-sections { display: flex; flex-direction: column; gap: .6rem; }
  .history-section { background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; }
  .history-section-head { padding: .8rem 1.1rem; display: flex; align-items: center; justify-content: space-between; cursor: pointer; }
  .history-section-head:hover { background: var(--bg); }
  .section-title { display: flex; align-items: center; gap: .6rem; font-weight: 600; font-size: .88rem; }
  .section-icon { font-size: .95rem; }
  .section-body { padding: 0 1.1rem 1.1rem; border-top: 1px solid var(--border); }
  .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: .5rem; padding-top: .7rem; }
  .data-item { display: flex; flex-direction: column; gap: .1rem; }
  .data-label { font-size: .68rem; color: var(--muted); text-transform: uppercase; letter-spacing: .5px; }
  .data-value { font-size: .88rem; font-family: var(--font-mono); color: var(--text); }
  .timeline { list-style: none; padding-top: .7rem; display: flex; flex-direction: column; gap: .45rem; }
  .timeline-item { display: flex; gap: 1rem; align-items: flex-start; padding-left: .5rem; border-left: 2px solid var(--border); position: relative; }
  .timeline-item::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: var(--accent); position: absolute; left: -4.5px; top: .3rem; }
  .timeline-date { font-family: var(--font-mono); font-size: .7rem; color: var(--muted); min-width: 70px; }
  .timeline-text { font-size: .83rem; color: var(--text); }

  /* ─── CAR CARDS ─── */
  .results-grid { display: flex; flex-direction: column; gap: .8rem; }
  .car-card { background: var(--white); border: 1.5px solid var(--border); border-radius: var(--radius); overflow: hidden; display: flex; transition: border-color .15s; box-shadow: var(--shadow-sm); }
  .car-card:hover { border-color: var(--accent); box-shadow: var(--shadow); }
  .car-img { width: 200px; flex-shrink: 0; background: var(--bg); display: flex; align-items: center; justify-content: center; font-size: 2.5rem; position: relative; overflow: hidden; }
  .car-img-placeholder { width: 100%; height: 140px; background: var(--bg); display: flex; align-items: center; justify-content: center; font-size: 2.5rem; flex-shrink: 0; }
  .car-info { padding: 1.1rem 1.3rem; flex: 1; display: flex; flex-direction: column; gap: .45rem; }
  .car-title { font-size: 1.1rem; font-weight: 700; color: var(--text); }
  .car-meta { display: flex; gap: 1rem; flex-wrap: wrap; }
  .car-meta-item { font-size: .73rem; color: var(--muted); font-family: var(--font-mono); }
  .car-price { font-size: 1.5rem; font-weight: 800; color: var(--green); letter-spacing: -.02em; }
  .car-tags { display: flex; gap: .35rem; flex-wrap: wrap; }
  .tag { font-size: .65rem; padding: .18rem .5rem; border-radius: 50px; font-weight: 600; letter-spacing: .2px; }
  .tag-platform { background: rgba(168,128,58,.08); color: var(--accent); }
  .tag-match { background: rgba(0,191,165,.08); color: var(--green); }
  .tag-premium { background: rgba(245,158,11,.1); color: var(--accent); }
  .car-actions { display: flex; gap: .4rem; margin-top: auto; }

  /* ─── SELL RESULT ─── */
  .sell-result { display: flex; flex-direction: column; gap: .8rem; }
  .sell-platform-card { background: var(--white); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 1.2rem 1.4rem; display: flex; align-items: center; gap: 1.2rem; }
  .sell-platform-card.recommended { border-color: var(--green); background: rgba(0,191,165,.03); }
  .platform-logo { width: 44px; height: 44px; border-radius: var(--radius); display: flex; align-items: center; justify-content: center; background: var(--bg); flex-shrink: 0; font-family: var(--font-mono); font-size: .65rem; font-weight: 700; text-align: center; line-height: 1.3; }
  .sell-info { flex: 1; }
  .sell-platform-name { font-weight: 700; margin-bottom: .15rem; font-size: .88rem; color: var(--text); }
  .sell-reason { font-size: .78rem; color: var(--sub); }
  .sell-price { font-size: 1.4rem; font-weight: 800; color: var(--orange); letter-spacing: -.02em; }

  /* ─── PAYWALL / PLANS ─── */
  .paywall { background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 1.8rem; text-align: center; margin-top: 1rem; }
  .paywall-title { font-size: 1.4rem; font-weight: 800; letter-spacing: -.3px; margin-bottom: .4rem; color: var(--text); }
  .paywall-sub { color: var(--sub); font-size: .85rem; margin-bottom: 1.4rem; }
  .plans-row { display: flex; gap: .8rem; flex-wrap: wrap; justify-content: center; margin-bottom: 1.4rem; }
  .plan-card { background: var(--white); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 1.1rem 1.3rem; min-width: 155px; text-align: left; cursor: pointer; transition: all .15s; position: relative; box-shadow: var(--shadow-sm); }
  .plan-card:hover, .plan-card.selected { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(168,128,58,.1); }
  .plan-card.selected { background: rgba(168,128,58,.03); }
  .plan-name { font-weight: 700; font-size: .85rem; margin-bottom: .2rem; color: var(--text); }
  .plan-price { font-size: 1.7rem; font-weight: 800; color: var(--accent); letter-spacing: -.03em; }
  .plan-price span { font-size: .78rem; color: var(--muted); font-weight: 400; }
  .plan-features { margin-top: .7rem; display: flex; flex-direction: column; gap: .28rem; }
  .plan-feature { font-size: .73rem; color: var(--sub); display: flex; gap: .35rem; align-items: flex-start; }
  .plan-feature::before { content: '✓'; color: var(--green); flex-shrink: 0; font-weight: 700; }
  .popular-badge { position: absolute; top: -.6rem; right: .7rem; font-size: .6rem; font-weight: 700; letter-spacing: .5px; background: var(--accent); color: #fff; padding: .15rem .5rem; border-radius: 50px; }

  /* ─── STRIPE ─── */
  .stripe-form { display: flex; flex-direction: column; gap: 1rem; }
  .stripe-logo { font-family: var(--font-mono); font-size: .72rem; color: var(--muted); display: flex; align-items: center; gap: .4rem; margin-bottom: .5rem; }
  .card-row { display: grid; grid-template-columns: 1fr 1fr; gap: .8rem; }

  /* ─── LOADING ─── */
  .loading-state { display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 2.5rem; text-align: center; }
  .spinner { width: 36px; height: 36px; border: 2.5px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin .7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100%{transform:scale(1);opacity:.5} 50%{transform:scale(1.4);opacity:1} }
  @keyframes chat-pulse { 0%,100%{box-shadow:0 4px 16px rgba(168,128,58,.4),0 0 0 3px rgba(168,128,58,.1)} 50%{box-shadow:0 8px 24px rgba(168,128,58,.55),0 0 0 6px rgba(168,128,58,.06)} }
  .loading-msg { color: var(--sub); font-size: .88rem; }
  .loading-steps { display: flex; flex-direction: column; gap: .35rem; text-align: left; }
  .loading-step { font-family: var(--font-mono); font-size: .72rem; color: var(--muted); display: flex; gap: .5rem; }
  .loading-step.done { color: var(--green); }
  .loading-step.active { color: var(--accent); }

  /* ─── UPSELL ─── */
  .upsell-row { background: rgba(168,128,58,.05); border: 1px solid rgba(168,128,58,.2); border-radius: var(--radius); padding: .9rem 1.2rem; display: flex; align-items: center; justify-content: space-between; margin-top: 1rem; flex-wrap: wrap; gap: .8rem; }
  .upsell-text { font-size: .83rem; color: var(--sub); }
  .upsell-text strong { color: var(--accent); }

  /* ─── FOOTER ─── */
  .site-footer { background: var(--white); border-top: 1px solid var(--border); padding: 2.5rem; }
  .footer-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 2rem; }
  .footer-brand .brand-mark { margin-bottom: .6rem; }
  .footer-brand p { color: var(--muted); font-size: .78rem; max-width: 220px; margin-top: .4rem; line-height: 1.6; }
  .footer-links { display: flex; gap: 1rem; flex-wrap: wrap; }
  .footer-col { display: flex; flex-direction: column; gap: .45rem; }
  .footer-col-title { font-size: .68rem; text-transform: uppercase; letter-spacing: 1.2px; color: var(--muted); font-weight: 700; margin-bottom: .3rem; }
  .footer-link { font-size: .82rem; color: var(--sub); background: none; border: none; cursor: pointer; text-align: left; padding: 0; transition: color .15s; }
  .footer-link:hover { color: var(--accent); }
  .footer-bottom { border-top: 1px solid var(--border); padding-top: 1.2rem; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
  .footer-copy { font-size: .72rem; color: var(--muted); }
  .footer-payments { display: flex; align-items: center; gap: .6rem; flex-wrap: wrap; }
  .footer-payments span { font-size: .7rem; color: var(--muted); }
  .footer-payments-data { display: flex; align-items: center; gap: .4rem; }

  /* ─── FOOTER EXTENDED ─── */
  .footer { background: var(--white); border-top: 1px solid var(--border); padding: 3rem 2.5rem 2rem; }
  .footer-grid { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 3rem; margin-bottom: 2.5rem; }
  .footer-brand .brand-mark { margin-bottom: .8rem; }
  .footer-brand p { font-size: .8rem; color: var(--muted); line-height: 1.7; max-width: 240px; }
  .footer-col h4 { font-size: .68rem; letter-spacing: 1.5px; text-transform: uppercase; color: var(--muted); margin-bottom: .9rem; font-weight: 700; }
  .footer-col a, .footer-col span { display: block; font-size: .83rem; color: var(--sub); text-decoration: none; margin-bottom: .5rem; cursor: pointer; transition: color .15s; }
  .footer-col a:hover, .footer-col span:hover { color: var(--accent); }
  .footer-legal-links { display: flex; gap: 1.5rem; }
  .footer-legal-links span { font-size: .72rem; color: var(--muted); cursor: pointer; transition: color .15s; }
  .footer-legal-links span:hover { color: var(--text); }

  /* ─── AUTH MODAL ─── */
  .auth-panel { max-width: 420px; }
  .auth-tabs { display: flex; border-bottom: 1px solid var(--border); margin-bottom: 1.4rem; }
  .auth-tab { flex: 1; padding: .7rem; background: none; border: none; cursor: pointer; font-size: .85rem; font-weight: 500; color: var(--muted); border-bottom: 2px solid transparent; transition: all .15s; margin-bottom: -1px; }
  .auth-tab.active { color: var(--accent); border-bottom-color: var(--accent); }
  .auth-methods { display: flex; flex-direction: column; gap: .7rem; margin-bottom: 1.1rem; }
  .auth-method-btn { display: flex; align-items: center; gap: .8rem; padding: .75rem 1rem; background: var(--white); border: 1.5px solid var(--border); border-radius: var(--radius); cursor: pointer; font-family: var(--font-body); font-size: .88rem; color: var(--text); transition: all .15s; width: 100%; font-weight: 500; }
  .auth-method-btn:hover { border-color: var(--accent); background: rgba(168,128,58,.03); }
  .auth-divider { display: flex; align-items: center; gap: .8rem; margin: .8rem 0; font-size: .72rem; color: var(--muted); }
  .auth-divider::before, .auth-divider::after { content: ''; flex: 1; height: 1px; background: var(--border); }
  .phone-row { display: flex; gap: .5rem; }
  .phone-prefix { background: var(--bg); border: 1.5px solid var(--border); border-radius: var(--radius); color: var(--text); font-family: var(--font-mono); font-size: .85rem; padding: .65rem .8rem; width: 80px; outline: none; }
  .otp-grid { display: flex; gap: .5rem; justify-content: center; margin: 1rem 0; }
  .otp-input { width: 44px; height: 52px; text-align: center; font-size: 1.3rem; font-family: var(--font-mono); background: var(--bg); border: 1.5px solid var(--border); border-radius: var(--radius); color: var(--text); outline: none; transition: border-color .15s; }
  .otp-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(168,128,58,.1); }
  .auth-success { text-align: center; padding: 1.5rem; }
  .auth-success-icon { font-size: 3rem; margin-bottom: .8rem; }

  /* ─── LEGAL MODAL ─── */
  .legal-panel { background: var(--white); border: 1px solid var(--border); border-radius: var(--radius-lg); width: 100%; max-width: 760px; max-height: 85vh; overflow-y: auto; position: relative; animation: slideUp .25s ease; box-shadow: var(--shadow-lg); }
  .legal-body { padding: 2rem; }
  .legal-body h2 { font-size: 1.5rem; font-weight: 800; margin-bottom: 1rem; color: var(--accent); }
  .legal-body h3 { font-size: .95rem; font-weight: 700; margin: 1.4rem 0 .45rem; color: var(--text); }
  .legal-body p { font-size: .83rem; color: var(--sub); line-height: 1.8; margin-bottom: .7rem; }
  .legal-body ul { padding-left: 1.2rem; margin-bottom: .8rem; }
  .legal-body ul li { font-size: .83rem; color: var(--sub); line-height: 1.8; }
  .legal-body a { color: var(--accent); text-decoration: none; }
  .legal-date { font-family: var(--font-mono); font-size: .68rem; color: var(--muted); margin-bottom: 1.4rem; }
  .legal-content { font-size: .83rem; line-height: 1.75; color: var(--sub); }
  .legal-content h2 { font-size: 1.2rem; font-weight: 700; color: var(--text); margin: 1.1rem 0 .4rem; }
  .legal-content h3 { font-size: .88rem; font-weight: 600; color: var(--text); margin: .8rem 0 .3rem; }
  .legal-content p { margin-bottom: .55rem; }
  .legal-content ul { padding-left: 1.2rem; margin-bottom: .6rem; }
  .legal-content li { margin-bottom: .2rem; }
  .legal-content a { color: var(--accent); text-decoration: none; }

  /* ─── SIRET BLOCK ─── */
  .siret-choice { display: flex; gap: .4rem; margin-bottom: 1.2rem; padding: 4px; background: var(--bg); border-radius: 10px; border: 1px solid var(--border); }
  .siret-choice-btn { flex: 1; padding: .5rem 1rem; background: transparent; border: none; border-radius: 8px; cursor: pointer; font-size: .85rem; color: var(--sub); font-family: var(--font-body); transition: all .15s; text-align: center; font-weight: 500; }
  .siret-choice-btn:hover { color: var(--text); }
  .siret-choice-btn.active { background: var(--white); color: var(--accent); font-weight: 600; box-shadow: 0 1px 4px rgba(0,0,0,.1); }
  .siret-result { background: rgba(0,191,165,.05); border: 1px solid rgba(0,191,165,.2); border-radius: var(--radius); padding: .6rem .9rem; margin-top: .5rem; font-size: .8rem; color: #00897B; }
  .siret-result strong { color: var(--text); display: block; font-size: .85rem; margin-bottom: .1rem; }
  .siret-searching { font-size: .78rem; color: var(--muted); font-family: var(--font-mono); margin-top: .4rem; }

  /* ─── WALLET ─── */
  .wallet-bar { display: flex; align-items: center; gap: .5rem; background: rgba(168,128,58,.08); border: 1px solid rgba(168,128,58,.2); border-radius: 50px; padding: .25rem .8rem; cursor: pointer; transition: background .15s; }
  .wallet-bar:hover { background: rgba(168,128,58,.14); }
  .wallet-bar .wallet-icon { font-size: .9rem; }
  .wallet-bar .wallet-amount { font-size: .95rem; font-weight: 800; color: var(--accent); }
  .wallet-bar .wallet-label { font-size: .68rem; color: var(--sub); font-family: var(--font-mono); }
  .wallet-pack-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: .6rem; margin-bottom: 1.2rem; } @media (max-width: 560px) { .wallet-pack-grid { grid-template-columns: 1fr; } }
  .wallet-pack { background: var(--white); border: 1.5px solid var(--border); border-radius: var(--radius); padding: 1rem; cursor: pointer; transition: all .18s ease; text-align: center; position: relative; }
  /* Pack Découverte déjà réclamé : grisé, non cliquable */
  .wallet-pack-claimed { opacity: .55; cursor: not-allowed !important; background: var(--card) !important; border-style: dashed !important; }
  .wallet-pack-claimed:hover { transform: none !important; border-color: var(--border) !important; }
  .wallet-pack:hover { border-color: var(--accent); transform: translateY(-1px); }
  .wallet-pack.selected {
    border-color: var(--accent-h);
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-h) 100%);
    box-shadow: 0 0 0 2px rgba(168,128,58,.25), 0 8px 22px rgba(168,128,58,.32);
    transform: translateY(-2px);
  }
  .wallet-pack.selected .pack-qty { color: #fff; text-shadow: 0 2px 4px rgba(0,0,0,.18); font-weight: 800; }
  .wallet-pack.selected .pack-price { color: #fff; font-weight: 700; }
  .wallet-pack.selected .pack-label { color: rgba(255,255,255,.95); font-weight: 600; letter-spacing: 1.6px; }
  .wallet-pack.selected .pack-unit { color: rgba(255,255,255,.85); }
  .wallet-pack.selected::after {
    content: "✓";
    position: absolute;
    top: 8px;
    right: 10px;
    width: 22px;
    height: 22px;
    background: #fff;
    color: var(--accent);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: .8rem;
    font-weight: 800;
    box-shadow: 0 2px 6px rgba(0,0,0,.2);
  }
  .wallet-pack .pack-qty { font-size: 1.7rem; font-weight: 800; color: var(--accent); line-height: 1; }
  .wallet-pack .pack-label { font-size: .7rem; color: var(--muted); margin: .2rem 0 .5rem; font-family: var(--font-mono); letter-spacing: .5px; text-transform: uppercase; }
  .wallet-pack .pack-price { font-size: .95rem; font-weight: 700; color: var(--text); }
  .wallet-pack .pack-unit { font-size: .68rem; color: var(--muted); }
  .wallet-pack .pack-best { position: absolute; top: -8px; left: 50%; transform: translateX(-50%); background: var(--accent); color: #fff; font-size: .58rem; font-weight: 700; padding: .15rem .5rem; border-radius: 10px; font-family: var(--font-mono); letter-spacing: .5px; white-space: nowrap; }
  .premium-cost { display: flex; align-items: center; justify-content: space-between; background: rgba(168,128,58,.05); border: 1px solid rgba(168,128,58,.15); border-radius: var(--radius); padding: .65rem 1rem; margin-bottom: 1rem; }
  .premium-cost .cost-label { font-size: .82rem; color: var(--sub); }
  .premium-cost .cost-value { display: flex; align-items: center; gap: .35rem; font-size: 1.05rem; font-weight: 800; color: var(--accent); }
  .cost-coin { width: 18px; height: 18px; background: var(--accent); border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: .6rem; font-weight: 700; color: #fff; }

  /* ─── FOOTER MODEL ─── */
  .footer-model-btn { display: flex; align-items: center; gap: .4rem; }
  .model-rank { font-size: .7rem; width: 12px; flex-shrink: 0; }
  .footer-col-services { min-width: 200px; }
  .footer-service-item { display: flex; flex-direction: column; gap: .1rem; margin-bottom: .6rem; }
  .footer-service-name { font-size: .82rem; color: var(--text); font-weight: 600; }
  .footer-service-desc { font-size: .72rem; color: var(--muted); line-height: 1.4; }

  /* ─── MODEL POPUP ─── */
  .model-popup { background: var(--white); border: 1px solid var(--border); border-radius: var(--radius-lg); width: 100%; max-width: 460px; animation: slideUp .2s ease; overflow: hidden; box-shadow: var(--shadow-lg); }
  .model-popup-body { padding: 1.3rem 1.5rem 1.5rem; }
  .model-popup-make { font-size: .65rem; color: var(--accent); letter-spacing: 2px; text-transform: uppercase; margin-bottom: .2rem; font-weight: 600; }
  .model-popup-name { font-size: 1.7rem; font-weight: 800; letter-spacing: -.03em; margin-bottom: .15rem; color: var(--text); }
  .model-popup-year { font-size: .75rem; color: var(--muted); margin-bottom: .9rem; }
  .model-popup-desc { font-size: .85rem; color: var(--sub); line-height: 1.7; margin-bottom: 1rem; }
  .model-popup-stats { display: flex; gap: .8rem; margin-bottom: 1rem; }
  .model-stat { flex: 1; display: flex; flex-direction: column; align-items: center; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius); padding: .7rem .5rem; }
  .model-stat-val { font-size: 1.2rem; font-weight: 800; color: var(--green); line-height: 1; margin-bottom: .2rem; }
  .model-stat-lbl { font-size: .62rem; color: var(--muted); text-transform: uppercase; letter-spacing: .5px; }

  /* ─── COOKIE BANNER ─── */
  .cookie-banner { position: fixed; bottom: 1.5rem; left: 50%; transform: translateX(-50%); z-index: 9999; width: calc(100% - 3rem); max-width: 720px; background: var(--white); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 1.2rem 1.5rem; display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; box-shadow: var(--shadow-lg); animation: slideUp .4s ease; }
  .cookie-text { flex: 1; font-size: .83rem; color: var(--sub); line-height: 1.6; }
  .cookie-text strong { color: var(--text); font-weight: 700; }
  .cookie-text a { color: var(--accent); cursor: pointer; text-decoration: underline; }
  .cookie-actions { display: flex; gap: .5rem; flex-shrink: 0; }
  .btn-cookie-refuse { padding: .45rem .9rem; background: var(--bg); border: 1px solid var(--border); border-radius: 100px; font-size: .82rem; cursor: pointer; color: var(--sub); font-weight: 500; transition: all .15s; }
  .btn-cookie-refuse:hover { border-color: var(--text); color: var(--text); }
  .btn-cookie-accept { padding: .45rem .9rem; background: var(--accent); border: none; border-radius: 100px; font-size: .82rem; cursor: pointer; color: #fff; font-weight: 600; transition: all .15s; }
  .btn-cookie-accept:hover { background: var(--accent-h); }

  /* ─── ONBOARDING ─── */
  .onboarding-overlay { position: fixed; inset: 0; z-index: 9998; background: rgba(26,29,46,.55); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 1rem; }
  .onboarding-panel { background: var(--white); border: 1px solid var(--border); border-radius: 16px; width: 100%; max-width: 540px; max-height: 90vh; overflow-y: auto; overflow-x: hidden; animation: slideUp .3s ease; box-shadow: var(--shadow-lg); }
  .onboarding-header { background: linear-gradient(135deg, #F4F7FF 0%, #EEF3FF 100%); padding: 2rem 2rem 1.5rem; text-align: center; position: sticky; top: 0; z-index: 2; border-bottom: 1px solid rgba(168,128,58,.1); }
  .onboarding-badge { display: inline-flex; align-items: center; gap: .4rem; font-size: .62rem; letter-spacing: 1.5px; text-transform: uppercase; color: var(--accent); background: rgba(168,128,58,.1); border: 1px solid rgba(168,128,58,.2); padding: .3rem .8rem; border-radius: 50px; margin-bottom: .9rem; font-weight: 700; }
  .onboarding-logo { font-family: var(--font-head); font-size: 2.2rem; font-weight: 800; letter-spacing: -.5px; margin-bottom: .35rem; color: var(--text); }
  .onboarding-logo span { color: var(--accent); }
  .onboarding-tagline { font-size: .88rem; color: var(--sub); font-weight: 400; }
  .onboarding-body { padding: 1.8rem 2rem; }
  .onboarding-steps { display: flex; flex-direction: column; gap: .8rem; margin-bottom: 1.4rem; }
  .onboarding-step { display: flex; align-items: flex-start; gap: .9rem; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius); padding: .9rem 1.1rem; transition: border-color .15s; }
  .onboarding-step:hover { border-color: rgba(168,128,58,.3); }
  .onboarding-step-icon { font-size: 1.1rem; flex-shrink: 0; margin-top: .1rem; }
  .onboarding-step-title { font-weight: 700; font-size: .87rem; margin-bottom: .12rem; color: var(--text); }
  .onboarding-step-desc { font-size: .78rem; color: var(--sub); line-height: 1.55; }
  .onboarding-disclaimer { background: rgba(245,158,11,.05); border: 1px solid rgba(245,158,11,.2); border-radius: var(--radius); padding: .9rem 1.1rem; margin-bottom: 1.3rem; font-size: .78rem; color: var(--sub); line-height: 1.6; }
  .onboarding-disclaimer strong { color: var(--accent); }
  .onboarding-footer { text-align: center; margin-top: .8rem; }
  .onboarding-skip { font-size: .75rem; color: var(--muted); cursor: pointer; background: none; border: none; text-decoration: underline; transition: color .15s; }
  .onboarding-skip:hover { color: var(--sub); }

  /* ─── RESPONSIVE ─── */
  @media (max-width: 900px) {
    nav { padding: 0 1.2rem; }
    .nav-center { display: none; }
    .hero-center { padding: 3rem 1.2rem 2.5rem; }
    .mode-grid { grid-template-columns: 1fr; gap: .8rem; }
    .stats-bar { gap: 0; }
    .stat-item { padding: 1.2rem 1.5rem; }
    .footer-grid { grid-template-columns: 1fr; gap: 2rem; }

    /* ─── Modales : alignées en haut sur mobile, overlay scrollable ─── */
    /* On utilise flex-start (pas center) : avec center, une modale plus haute
       que l'écran voit son HAUT poussé hors de la zone visible et inaccessible.
       Avec flex-start + overlay scrollable, le header reste toujours atteignable. */
    .overlay {
      align-items: flex-start;
      justify-content: center;
      padding: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
    /* Modales « app » (flex-panel) : l'overlay ne scrolle pas, c'est le body
       interne du panel qui défile (header + footer figés). */
    .overlay:has(.flex-panel) { overflow: hidden; align-items: stretch; }
    .panel {
      border-radius: 0 0 14px 14px;
      max-width: 100% !important;
      width: 100% !important;
      max-height: none;        /* laisser grandir, c'est l'overlay qui scrolle */
      min-height: 100vh;       /* occupe au moins tout l'écran pour un rendu page */
      margin: 0;
      position: relative;
      animation: slideDown .25s ease;
    }
    .panel.flex-panel { min-height: 0; height: 100vh; height: 100dvh; max-height: 100dvh; }
    .panel-wide { max-width: 100% !important; }
    .panel-fullscreen {
      max-width: 100vw !important;
      width: 100vw !important;
      height: 100vh !important; height: 100dvh !important;
      max-height: 100dvh !important;
      min-height: 0;
      border-radius: 0;
      margin: 0;
    }
    /* Header figé en haut (flex column : reste visible, le body scrolle). */
    .panel-header {
      padding: 1rem 1.1rem .8rem;
      flex-shrink: 0;
      background: var(--white);
      z-index: 10;
      border-bottom: 1px solid var(--border);
    }
    body.dark-mode .panel-header { background: #1C1C23; }
    .panel-title { font-size: 1.15rem; }
    .panel-sub { font-size: .76rem; }
    .panel-body { padding: 1rem 1.1rem 1.4rem; }

    /* Tableaux dans les modales : scroll horizontal si dépassement */
    .panel table { font-size: .76rem; }
    .panel table th, .panel table td { padding: .4rem .55rem !important; }

    /* Grilles tarification : forcer 1 colonne propre */
    .panel-wide [style*="grid-template-columns: repeat(auto-fit, minmax(240px"] {
      grid-template-columns: 1fr !important;
      gap: .8rem !important;
    }

    /* v70 — Dossiers VIQ : responsive mobile (cartes, lots) — desktop inchangé. */
    .viq-dossier-card { max-width: 100%; overflow: hidden; }
    .viq-dossier-card-header { flex-direction: column; align-items: stretch !important; gap: .5rem !important; }
    .viq-dossier-card-header > div:first-child { width: 100%; }
    .viq-dossier-score { align-self: flex-start; }
    .viq-dossier-afficher {
      align-self: flex-start; white-space: normal !important;
      padding: .5rem .9rem; border: 1.5px solid var(--accent); border-radius: 8px;
      text-decoration: none !important; text-align: center;
    }
    .viq-dossier-identifiers { font-size: .72rem !important; }
    .viq-dossier-identifiers span { word-break: break-all; }

    .viq-lot-header { flex-direction: column; align-items: stretch !important; }
    .viq-lot-header-name span:first-child { white-space: normal !important; }
    .viq-lot-header-actions { width: 100%; justify-content: flex-end; }

    /* Onboarding */
    .onboarding-overlay { padding: .5rem; align-items: flex-start; padding-top: 2rem; }
    .onboarding-panel { max-height: 94vh; overflow-y: auto; overflow-x: hidden; }
    .onboarding-header { padding: 1rem 1rem .8rem; }
    .onboarding-tagline { font-size: .78rem; }
    .onboarding-badge { font-size: .58rem; padding: .22rem .6rem; margin-bottom: .5rem; }
    .onboarding-body { padding: 1rem 1rem 1.2rem; }
    .onboarding-steps { gap: .5rem; margin-bottom: 1rem; }
    .onboarding-step { padding: .65rem .85rem; gap: .6rem; }
    .onboarding-logo { font-size: 1.8rem; }

    /* Onglets de modales (Mon espace, Dashboard Pro, Admin) : scroll horizontal */
    .saas-dash-tabs-bar { padding: 0 .5rem; }
    .saas-dash-tab { padding: .6rem .8rem; font-size: .82rem; white-space: nowrap; }
  }
  @media (max-width: 480px) {
    .hero-h1 { font-size: 2.1rem; font-weight: 900; }
    .nav-btn-label { display: none; }
    .mode-grid { margin-top: 2rem; }
    .panel-title { font-size: 1.05rem; }
    /* KPI cards Dashboard / Admin : 2 colonnes au lieu de 4+ */
    .panel [style*="grid-template-columns: repeat(auto-fit, minmax(200px"],
    .panel [style*="grid-template-columns: repeat(auto-fit, minmax(220px"] {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: .5rem !important;
    }
  }

  /* ─── Dark mode ─── */
  body.dark-mode {
    --white: #121217;
    --bg: #16161B;             /* Anthracite premium — aligné au hero */
    --card: #1C1C23;
    --deep: #101015;
    --border: #2A2A32;
    --border-strong: #3A3A44;
    --muted: #6E6E78;
    --text: #F2F0EA;           /* Off-white chaud */
    --sub: #B4B2BC;
    --accent: #C9A96E;         /* Ambre clair premium — aligné au hero */
    --accent-h: #B8965A;
    --accent-soft: rgba(201,169,110,.12);
    --accent2: #C9A96E;        /* Ambre (cohérence : plus de bleu) */
    --accent2-h: #B8965A;
    --accent2-soft: rgba(201,169,110,.12);
    --green: #4FB894;
    --green-soft: rgba(79,184,148,.14);
    --red: #F87171;
    --red-soft: rgba(248,113,113,.12);
    --orange: #D4A455;
    --gold: #C9A96E;
    --hero-dark: #16161B;
    --shadow-sm: 0 1px 3px rgba(0,0,0,.4), 0 1px 2px rgba(0,0,0,.3);
    --shadow:    0 4px 16px rgba(0,0,0,.4), 0 1px 4px rgba(0,0,0,.25);
    --shadow-lg: 0 20px 48px rgba(0,0,0,.5), 0 4px 16px rgba(0,0,0,.3);
    --shadow-xl: 0 28px 64px rgba(0,0,0,.6), 0 10px 24px rgba(0,0,0,.4);
  }
  body.dark-mode .hero-center { background: linear-gradient(180deg, #16161B 0%, #16161B 100%); }
  /* Le hero moderne porte déjà son propre thème sombre : on neutralise les
     surcharges dark-mode pour garder un rendu cohérent quel que soit le toggle. */
  body.dark-mode .hero-center.hero-modern { background: var(--hero-dark, #16161B); }
  body.dark-mode .hero-center.hero-modern::before {
    background:
      radial-gradient(ellipse 70% 50% at 25% 35%, rgba(168,128,58,.16) 0%, transparent 60%),
      radial-gradient(ellipse 55% 45% at 80% 60%, rgba(46,111,92,.12) 0%, transparent 60%);
  }
  body.dark-mode .hero-center.hero-modern .hero-badge { background: rgba(201,169,110,.1); border-color: rgba(201,169,110,.35); color: #C9A96E; }
  body.dark-mode .hero-modern .hero-h1 { color: #FFFFFF; }
  body.dark-mode .hero-modern .hero-h1 em { color: #C9A96E; }
  body.dark-mode .hero-modern .hero-sub { color: rgba(255,255,255,.66); }
  body.dark-mode .hero-modern .hero-sub strong { color: #C9A96E !important; }
  body.dark-mode .hero-modern .hero-cta-secondary { color: #fff; border-color: rgba(255,255,255,.4); }
  body.dark-mode .hero-modern .hero-cta-secondary:hover { background: rgba(255,255,255,.08); border-color: #fff; }
  body.dark-mode .hero-social-avatars span { border-color: var(--hero-dark, #16161B); }
  body.dark-mode .hero-social-text { color: rgba(255,255,255,.62); }
  body.dark-mode .hero-social-text strong { color: #fff; }
  body.dark-mode .hero-modern-stats { background: transparent; border-top-color: rgba(255,255,255,.1); }
  body.dark-mode .hero-modern-stats .stat-item { border-color: rgba(255,255,255,.1); }
  body.dark-mode .hero-modern-stats .stat-val { color: #C9A96E; }
  body.dark-mode .hero-modern-stats .stat-label { color: rgba(255,255,255,.5); }
  body.dark-mode .hero-trustbar { background: var(--hero-dark, #16161B); border-top-color: rgba(255,255,255,.08); }
  body.dark-mode .hero-trustbar-label { color: rgba(255,255,255,.4); }
  body.dark-mode .hero-trustbar-items span { color: rgba(255,255,255,.8); }
  body.dark-mode .hero-center::before {
    background:
      radial-gradient(ellipse 60% 40% at 30% 50%, rgba(201,169,110,.1) 0%, transparent 65%),
      radial-gradient(ellipse 50% 40% at 70% 50%, rgba(0,212,184,.08) 0%, transparent 65%);
  }
  body.dark-mode nav { background: rgba(28,28,35,.92); border-color: rgba(255,255,255,.08); }
  body.dark-mode .panel { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .panel-header { border-color: #2A2A32; }
  body.dark-mode .input { background: #101015; border-color: #2A2A32; color: #F2F0EA; }
  body.dark-mode .input:focus { border-color: #C9A96E; }
  body.dark-mode .mode-card { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .mode-card:hover { border-color: #C9A96E; background: #26262F; }
  body.dark-mode .stats-bar { background: #16161B; border-color: #2A2A32; }
  body.dark-mode .stat-item { border-color: #2A2A32; }
  body.dark-mode .freemium-banner { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .radio-btn { background: #101015; border-color: #2A2A32; color: #B4B2BC; }
  body.dark-mode .radio-btn.active { background: var(--accent); border-color: var(--accent); color: #fff; }
  body.dark-mode .car-card { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .sell-platform-card { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .plan-card { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .paywall { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .close-btn { background: #2A2A32; border-color: #3A3A44; color: #B4B2BC; }
  body.dark-mode .footer, body.dark-mode .site-footer { background: #16161B; border-color: #2A2A32; }
  body.dark-mode .footer-bottom { border-color: #2A2A32; }
  body.dark-mode .model-popup { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .model-stat { background: #101015; border-color: #2A2A32; }
  body.dark-mode .cookie-banner { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .onboarding-panel { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .onboarding-header { background: linear-gradient(135deg, #1C1C23 0%, #26262F 100%); border-color: rgba(201,169,110,.15); }
  body.dark-mode .onboarding-step { background: #101015; border-color: #2A2A32; }
  body.dark-mode .history-section { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .wallet-pack { background: #1C1C23; border-color: #2A2A32; }
  body.dark-mode .wallet-pack.selected {
    background: linear-gradient(135deg, var(--accent), #F59E0B);
    border-color: #F59E0B;
    box-shadow: 0 0 0 2px rgba(245,158,11,.35), 0 8px 22px rgba(168,128,58,.35);
  }
  body.dark-mode .wallet-pack.selected .pack-qty { color: #fff; }
  body.dark-mode .wallet-pack.selected .pack-price { color: #fff; }
  body.dark-mode .wallet-pack.selected .pack-label,
  body.dark-mode .wallet-pack.selected .pack-unit { color: rgba(255,255,255,.88); }
  body.dark-mode .auth-method-btn { background: #101015; border-color: #2A2A32; color: #F2F0EA; }
  body.dark-mode .siret-choice { background: #101015; border-color: #2A2A32; }
  body.dark-mode .siret-choice-btn.active { background: #1C1C23; }
  body.dark-mode .btn-white { background: #1C1C23; color: #F2F0EA; border-color: #2A2A32; }
  body.dark-mode .btn-ghost { border-color: #2A2A32; color: #B4B2BC; }
  body.dark-mode .btn-ghost:hover { border-color: #C9A96E; color: #C9A96E; background: rgba(201,169,110,.07); }
  body.dark-mode .hero-badge { background: rgba(201,169,110,.12); border-color: rgba(201,169,110,.25); }
  body.dark-mode .upsell-row { background: rgba(201,169,110,.08); border-color: rgba(201,169,110,.2); }

  /* ─── IQ Gauge & Market Charts Section ─── */
  .market-section {
    padding: 4rem 2rem;
    background: var(--bg);
    border-top: 1px solid var(--border);
  }
  .market-section-title {
    text-align: center; font-size: 1.7rem; font-weight: 900;
    letter-spacing: -.03em; color: var(--text); margin-bottom: .5rem;
  }
  .market-section-sub {
    text-align: center; font-size: .95rem; color: var(--sub);
    margin-bottom: 3rem;
  }
  .market-grid {
    max-width: 1100px; margin: 0 auto;
    display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem;
  }
  @media(max-width:900px){ .market-grid { grid-template-columns: 1fr; } }
  .iq-gauge-card {
    background: var(--card); border: 1px solid var(--border); border-radius: 16px;
    padding: 2rem; box-shadow: var(--shadow);
    display: flex; flex-direction: column; gap: 1.5rem;
  }
  .iq-gauge-header {
    display: flex; align-items: center; gap: 1rem;
  }
  .iq-gauge-ring {
    position: relative; width: 110px; height: 110px; flex-shrink: 0;
  }
  .iq-gauge-ring-fresh {
    animation: gaugeGlow 2s ease-in-out infinite;
  }
  @keyframes gaugeGlow {
    0%,100% { filter: drop-shadow(0 0 6px rgba(168,128,58,.4)); }
    50% { filter: drop-shadow(0 0 14px rgba(168,128,58,.8)); }
  }
  .iq-gauge-info { flex: 1; }
  .iq-gauge-price {
    font-size: 1.8rem; font-weight: 800; letter-spacing: -.04em;
    color: var(--text); line-height: 1.1;
  }
  .iq-gauge-range { font-size: .82rem; color: var(--sub); margin-top: .2rem; }
  .iq-gauge-freshness {
    display: flex; align-items: center; gap: .4rem;
    font-size: .72rem; font-weight: 600; margin-top: .6rem;
    font-family: var(--font-mono); letter-spacing: .5px;
  }
  .fresh-dot {
    width: 7px; height: 7px; border-radius: 50%; background: var(--green);
    animation: freshPulse 1.5s ease-in-out infinite;
    box-shadow: 0 0 6px var(--green);
  }
  @keyframes freshPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.6;transform:scale(1.3)} }
  .iq-sparkline {
    height: 60px; width: 100%; position: relative; overflow: hidden;
    border-radius: 8px; background: var(--bg);
  }
  .iq-sparkline svg { width: 100%; height: 100%; }
  .iq-sparkline-label {
    display: flex; justify-content: space-between;
    font-size: .68rem; color: var(--muted); margin-top: .4rem;
    font-family: var(--font-mono);
  }

  /* ─── Depreciation Chart ─── */
  .depreciation-card {
    background: var(--card); border: 1px solid var(--border); border-radius: 16px;
    padding: 2rem; box-shadow: var(--shadow);
    display: flex; flex-direction: column; gap: 1rem;
  }
  .dep-chart-title { font-size: 1rem; font-weight: 700; color: var(--text); }
  .dep-chart-sub { font-size: .78rem; color: var(--sub); margin-top: .15rem; }
  .dep-chart-wrap { position: relative; height: 180px; width: 100%; margin-top: .5rem; }
  .dep-chart-wrap svg { width: 100%; height: 100%; overflow: visible; }
  .dep-legend {
    display: flex; gap: 1.2rem; margin-top: .8rem; flex-wrap: wrap;
  }
  .dep-legend-item {
    display: flex; align-items: center; gap: .4rem;
    font-size: .75rem; color: var(--sub); font-weight: 500;
  }
  .dep-legend-dot { width: 10px; height: 3px; border-radius: 2px; }
  .dep-chart-note {
    font-size: .7rem; color: var(--muted); margin-top: .5rem;
    font-style: italic;
  }

  /* ══════ LUXURY PREMIUM — VehicleIQ ══════ */

  /* Gold palette */
  :root {
    --gold:      #C9A96E;
    --gold-l:    #E8D4A8;
    --gold-d:    #9A7A45;
    --obsidian:  #090A0F;
  }

  /* Grain texture on hero */
  .hero-center::after {
    content:''; position:absolute; inset:0; z-index:0; pointer-events:none;
    background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='.035'/%3E%3C/svg%3E");
    background-size:200px 200px; opacity:.7;
  }

  /* ─── TICKER ─── */
  .ticker-bar {
    background:var(--obsidian); height:36px; overflow:hidden;
    display:flex; align-items:center;
    border-bottom:1px solid rgba(201,169,110,.12);
    /* v5 : barre Live Marché fixée en haut, AU-DESSUS de la navbar (qui est en
       top:36px). Évite que la navbar fixe ne la recouvre. */
    position:fixed; top:0; left:0; right:0; z-index:201;
  }
  .ticker-label {
    background:var(--gold); color:#000; font-size:.6rem; font-weight:800;
    letter-spacing:2px; padding:0 1rem; height:100%; text-transform:uppercase;
    display:flex; align-items:center; flex-shrink:0; white-space:nowrap;
  }
  .ticker-track { overflow:hidden; flex:1; }
  .ticker-inner {
    display:flex; align-items:center;
    animation:tickerScroll 36s linear infinite;
    white-space:nowrap; width:max-content;
  }
  @keyframes tickerScroll { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  .ticker-inner:hover { animation-play-state:paused; }
  .ticker-item {
    display:inline-flex; align-items:center; gap:.6rem;
    padding:0 1.6rem; font-family:var(--font-mono); font-size:.68rem;
    color:rgba(255,255,255,.55); border-right:1px solid rgba(255,255,255,.06);
  }
  .ticker-car  { color:rgba(255,255,255,.85); font-weight:600; }
  .ticker-price { color:#fff; }
  .ticker-up   { color:#4ADE80; }
  .ticker-down { color:#F87171; }

  /* ─── CLIENT LOGOS MARQUEE ─── */
  .logos-strip {
    padding:2.2rem 0; background:var(--white);
    border-top:1px solid var(--border); border-bottom:1px solid var(--border);
    overflow:hidden;
  }
  .logos-label {
    text-align:center; font-size:.66rem; color:var(--muted);
    text-transform:uppercase; letter-spacing:2.5px; font-weight:700;
    margin-bottom:1.2rem;
  }
  .logos-track { overflow:hidden; }
  .logos-inner {
    display:flex; align-items:center; gap:0;
    animation:logoScroll 30s linear infinite; width:max-content;
  }
  @keyframes logoScroll { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  .logos-inner:hover { animation-play-state:paused; }
  .logo-pill {
    display:inline-flex; align-items:center; gap:.55rem;
    padding:.5rem 1.6rem; margin:0 .4rem;
    background:var(--bg); border:1px solid var(--border); border-radius:50px;
    font-size:.8rem; font-weight:700; color:var(--sub); white-space:nowrap;
    transition:all .2s; cursor:default;
  }
  .logo-pill:hover { border-color:rgba(201,169,110,.45); color:var(--text); }
  .logo-pip { width:7px; height:7px; border-radius:50%; flex-shrink:0; }

  /* ─── WHY US ─── */
  .why-section { padding:6rem 2rem; background:var(--white); }
  .why-inner   { max-width:1100px; margin:0 auto; }

  /* ─── Section Dashboard Mockup ─── */
  .dash-mock-section { padding: 5.5rem 2rem; background: var(--bg); }
  .dash-mock-inner { max-width: 1080px; margin: 0 auto; text-align: center; }
  .dash-mock-section .sec-eyebrow { color: var(--accent) !important; }
  .dash-mock-h2 {
    font-family: var(--font-head); font-size: 2.4rem; font-weight: 900;
    color: var(--text) !important; letter-spacing: -.025em; margin: .6rem 0 .8rem; line-height: 1.15;
  }
  .dash-mock-sub {
    font-size: 1.02rem; color: var(--sub) !important;
    max-width: 580px; margin: 0 auto 3rem; line-height: 1.65;
  }
  .dash-mock-window {
    background: #14171F !important; border: 1px solid rgba(255,255,255,.08);
    border-radius: 16px; overflow: hidden; text-align: left;
    box-shadow: 0 40px 90px rgba(0,0,0,.5);
  }
  .dash-mock-titlebar {
    display: flex; align-items: center; gap: .5rem;
    padding: .85rem 1.2rem; background: #1A1E27 !important;
    border-bottom: 1px solid rgba(255,255,255,.06);
  }
  .dash-dot { width: 11px; height: 11px; border-radius: 50%; }
  .dash-dot-r { background: #FF5F57; } .dash-dot-y { background: #FEBC2E; } .dash-dot-g { background: #28C840; }
  .dash-mock-url {
    margin-left: 1rem; font-family: var(--font-mono); font-size: .76rem;
    color: rgba(255,255,255,.4) !important;
  }
  .dash-mock-body { padding: 1.6rem; background: #14171F !important; }
  .dash-kpi-grid {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin-bottom: 1.2rem;
  }
  .dash-kpi {
    background: #1A1E27 !important; border: 1px solid rgba(255,255,255,.06);
    border-radius: 12px; padding: 1.1rem 1.2rem;
  }
  .dash-kpi-label { font-size: .76rem; color: rgba(255,255,255,.5) !important; margin-bottom: .5rem; }
  .dash-kpi-val { font-size: 1.5rem; font-weight: 800; color: #ffffff !important; letter-spacing: -.02em; }
  .dash-kpi-delta { font-size: .76rem; font-weight: 700; margin-top: .3rem; }
  .dash-kpi-delta.up, .dash-car-delta.up { color: #28C840 !important; }
  .dash-kpi-delta.down, .dash-car-delta.down { color: #FF5F57 !important; }
  .dash-chart-card {
    background: #1A1E27 !important; border: 1px solid rgba(255,255,255,.06);
    border-radius: 12px; padding: 1.3rem 1.4rem; margin-bottom: 1.2rem;
  }
  .dash-chart-head {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 1rem; flex-wrap: wrap; gap: .6rem;
  }
  .dash-chart-title { font-size: .9rem; font-weight: 700; color: #ffffff !important; }
  .dash-chart-tags { display: flex; gap: .4rem; }
  .dash-chart-tags span {
    font-size: .68rem; color: rgba(255,255,255,.55) !important;
    background: rgba(255,255,255,.07); border-radius: 6px; padding: .2rem .55rem;
  }
  .dash-chart-svg { width: 100%; height: 180px; display: block; }
  .dash-chart-axis {
    display: flex; justify-content: space-between; margin-top: .6rem;
    font-size: .68rem; color: rgba(255,255,255,.35) !important; font-family: var(--font-mono);
  }
  .dash-cars-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
  .dash-car {
    background: #1A1E27 !important; border: 1px solid rgba(255,255,255,.06);
    border-radius: 12px; padding: 1.1rem 1.2rem;
  }
  .dash-car-top {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: .6rem;
  }
  .dash-car-name { font-size: .86rem; font-weight: 700; color: #ffffff !important; }
  .dash-car-delta { font-size: .72rem; font-weight: 700;
    background: rgba(255,255,255,.06); border-radius: 6px; padding: .15rem .45rem; }
  .dash-car-price { font-size: 1.35rem; font-weight: 800; color: #ffffff !important; letter-spacing: -.02em; }
  .dash-car-km { font-size: .76rem; color: rgba(255,255,255,.45) !important; margin-top: .25rem; }

  /* ─── Mode light pour la fenêtre macOS — utilisé par le NOUVEAU mockup Pro ─── */
  .dash-mock-window-light {
    background: #FAFAF7 !important; border: 1px solid rgba(0,0,0,.08);
    border-radius: 14px; overflow: hidden; text-align: left;
    box-shadow: 0 30px 80px rgba(0,0,0,.45);
  }
  .dash-mock-titlebar-light {
    background: #F0EFEA !important; border-bottom: 1px solid rgba(0,0,0,.06);
  }
  .dash-mock-url-light { color: rgba(0,0,0,.42) !important; }
  .dash-mock-body-light { background: #FAFAF7 !important; padding: 1.3rem 1.4rem; color: #1A1A1F; }

  /* En-tête : Tableau de bord Pro */
  .dash-pro-header {
    display: flex; justify-content: space-between; align-items: center;
    background: linear-gradient(135deg, rgba(168,128,58,.07), rgba(168,128,58,0));
    border: 1px solid rgba(168,128,58,.18);
    border-radius: 10px; padding: .9rem 1.1rem; margin-bottom: .9rem;
    flex-wrap: wrap; gap: .6rem;
  }
  .dash-pro-title {
    font-family: var(--font-head); font-size: 1.05rem; font-weight: 800;
    color: #1A1A1F !important; letter-spacing: -.01em; line-height: 1.1;
  }
  .dash-pro-sub { font-size: .68rem; color: rgba(0,0,0,.5); font-family: var(--font-mono); margin-top: .2rem; }
  .dash-pro-badge {
    font-size: .58rem; font-weight: 800; color: var(--accent);
    background: rgba(168,128,58,.1); border: 1px solid rgba(168,128,58,.3);
    padding: .25rem .65rem; border-radius: 14px;
    font-family: var(--font-mono); letter-spacing: 1px;
  }

  /* 4 cartes KPI */
  .dash-pro-kpi-grid {
    display: grid; grid-template-columns: repeat(4, 1fr);
    gap: .6rem; margin-bottom: .9rem;
  }
  .dash-pro-kpi-card {
    background: #ffffff; border: 1px solid rgba(0,0,0,.07);
    border-radius: 10px; padding: .7rem .85rem;
  }
  .dash-pro-kpi-label {
    font-size: .54rem; color: rgba(0,0,0,.5);
    text-transform: uppercase; letter-spacing: 1px;
    font-family: var(--font-mono); margin-bottom: .3rem;
  }
  .dash-pro-kpi-val {
    font-family: var(--font-head); font-size: 1.2rem; font-weight: 800;
    letter-spacing: -.02em; line-height: 1.1;
  }
  .dash-pro-kpi-sub { font-size: .58rem; color: rgba(0,0,0,.55); margin-top: .25rem; }

  /* Grid analytics 3 colonnes */
  .dash-pro-analytics-grid {
    display: grid; grid-template-columns: 1.3fr 1fr 1fr;
    gap: .7rem;
  }
  .dash-pro-analytics-card {
    background: #ffffff; border: 1px solid rgba(0,0,0,.07);
    border-radius: 10px; padding: .8rem .95rem;
  }
  .dash-pro-analytics-title {
    font-size: .72rem; font-weight: 700; color: #1A1A1F;
    margin-bottom: .55rem; display: flex; align-items: center; gap: .35rem;
  }
  .dash-pro-analytics-body { display: flex; flex-direction: column; gap: .3rem; }

  /* Top 5 véhicules */
  .dash-pro-top5-row {
    display: grid; grid-template-columns: 22px 1fr auto;
    align-items: center; gap: .5rem;
    padding: .3rem .4rem; border-radius: 5px;
  }
  .dash-pro-top5-best { background: rgba(4,120,87,.05); }
  .dash-pro-top5-rank {
    font-family: var(--font-head); font-size: .68rem; font-weight: 800; color: rgba(0,0,0,.4);
  }
  .dash-pro-top5-best .dash-pro-top5-rank { color: var(--green); }
  .dash-pro-top5-name { font-size: .72rem; font-weight: 600; color: #1A1A1F;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .dash-pro-top5-detail { font-size: .58rem; color: rgba(0,0,0,.55); margin-top: .1rem; }
  .dash-pro-top5-margin { text-align: right; }
  .dash-pro-top5-margin-eur {
    font-family: var(--font-head); font-size: .76rem; font-weight: 800; color: var(--green);
  }
  .dash-pro-top5-margin-pct { font-size: .55rem; color: rgba(0,0,0,.55); }

  /* Plateformes */
  .dash-pro-plat-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: .3rem .35rem; gap: .4rem;
  }
  .dash-pro-plat-info { flex: 1; min-width: 0; }
  .dash-pro-plat-name { font-size: .72rem; font-weight: 600; color: #1A1A1F; }
  .dash-pro-plat-detail { font-size: .58rem; color: rgba(0,0,0,.55); margin-top: .1rem; }
  .dash-pro-plat-margin {
    font-family: var(--font-head); font-size: .74rem; font-weight: 800; color: var(--green);
  }

  /* Mix Pro / Particulier */
  .dash-pro-mix-bar {
    display: flex; height: 7px; border-radius: 3px; overflow: hidden;
    border: 1px solid rgba(0,0,0,.08); margin-bottom: .5rem;
  }
  .dash-pro-mix-row { display: flex; align-items: center; gap: .4rem; font-size: .68rem; padding: .15rem 0; }
  .dash-pro-mix-dot { width: 7px; height: 7px; border-radius: 2px; flex-shrink: 0; }
  .dash-pro-mix-label { flex: 1; color: #1A1A1F; }
  .dash-pro-mix-margin { font-size: .58rem; color: rgba(0,0,0,.55); font-family: var(--font-mono); }
  .dash-pro-segments-mini {
    margin-top: .55rem; padding-top: .55rem; border-top: 1px solid rgba(0,0,0,.07);
  }
  .dash-pro-segments-title {
    font-size: .54rem; color: rgba(0,0,0,.45);
    text-transform: uppercase; letter-spacing: 1px;
    font-family: var(--font-mono); margin-bottom: .3rem;
  }
  .dash-pro-seg-row {
    display: grid; grid-template-columns: 1fr auto auto;
    gap: .5rem; font-size: .65rem; padding: .12rem 0; color: #1A1A1F;
  }
  .dash-pro-seg-row .up { color: var(--green); font-weight: 700; }

  @media (max-width: 820px) {
    .dash-mock-h2 { font-size: 1.7rem; }
    .dash-kpi-grid { grid-template-columns: repeat(2, 1fr); }
    .dash-cars-grid { grid-template-columns: 1fr; }
    .dash-mock-body { padding: 1rem; }
    .dash-pro-kpi-grid { grid-template-columns: repeat(2, 1fr); }
    .dash-pro-analytics-grid { grid-template-columns: 1fr; }
    .dash-mock-body-light { padding: 1rem; }
  }
  @media (max-width: 1100px) and (min-width: 821px) {
    .dash-pro-analytics-grid { grid-template-columns: 1fr 1fr; }
    .dash-pro-analytics-grid > :last-child { grid-column: span 2; }
  }

  /* ─── Modale vidéo démo ─── */
  .demo-video-panel {
    position: relative; width: 100%; max-width: 900px;
    background: #0E1119; border-radius: 16px; overflow: hidden;
    box-shadow: 0 40px 90px rgba(0,0,0,.55);
  }
  .demo-video-close {
    position: absolute; top: .8rem; right: .8rem; z-index: 2;
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(0,0,0,.55); color: #fff; border: 1px solid rgba(255,255,255,.2);
    cursor: pointer; font-size: 1rem; display: flex; align-items: center; justify-content: center;
    transition: background .15s ease;
  }
  .demo-video-close:hover { background: rgba(0,0,0,.8); }
  .demo-video-frame { position: relative; padding-bottom: 56.25%; height: 0; }
  .demo-video-frame iframe {
    position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;
  }
  .demo-video-empty {
    padding: 4rem 2.5rem; text-align: center;
  }
  .demo-video-empty-icon { font-size: 3rem; margin-bottom: 1rem; }
  .demo-video-empty-title {
    font-family: var(--font-head); font-size: 1.4rem; font-weight: 700;
    color: #fff; margin-bottom: .6rem;
  }
  .demo-video-empty-sub {
    font-size: .92rem; color: rgba(255,255,255,.6);
    max-width: 420px; margin: 0 auto; line-height: 1.6;
  }

  /* ─── Section : 4 moteurs ─── */
  .engines-section { padding: 6rem 2rem; background: var(--bg, #F7F9FC); }
  .engines-inner { max-width: 1140px; margin: 0 auto; }
  .engines-sub {
    text-align: center; font-size: .98rem; color: var(--sub);
    max-width: 560px; margin: 0 auto 3rem; line-height: 1.7;
  }
  .engines-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.3rem; align-items: stretch;
  }
  .engine-card {
    background: var(--white); border: 1.5px solid var(--border);
    border-radius: 16px; padding: 1.8rem 1.5rem;
    display: flex; flex-direction: column; transition: all .18s ease;
    height: 100%;
  }
  .engine-card:hover {
    border-color: var(--accent); transform: translateY(-4px);
    box-shadow: 0 14px 36px rgba(168,128,58,.13);
  }
  .engine-icon {
    width: 46px; height: 46px; border-radius: 12px;
    background: var(--accent-soft, #FEF3E2); color: var(--accent);
    display: flex; align-items: center; justify-content: center;
    font-size: 1.4rem; margin-bottom: 1.1rem;
  }
  .engine-title {
    font-family: var(--font-head); font-size: 1.18rem; font-weight: 700;
    color: var(--text); margin-bottom: .6rem;
  }
  .engine-desc {
    font-size: .87rem; color: var(--sub); line-height: 1.6; flex: 1;
    margin-bottom: 1.2rem;
  }
  .engine-cta {
    background: none; border: none; cursor: pointer; text-align: left;
    font-family: var(--font-body); font-size: .85rem; font-weight: 700;
    color: var(--accent); padding: 0; transition: opacity .15s;
  }
  .engine-cta:hover { opacity: .7; }

  /* ─── Section : solutions par acteur ─── */
  .actors-section { padding: 6rem 2rem; background: var(--white); }
  .actors-inner { max-width: 1140px; margin: 0 auto; }
  .actors-sub {
    text-align: center; font-size: .98rem; color: var(--sub);
    max-width: 560px; margin: 0 auto 3rem; line-height: 1.7;
  }
  .actors-grid {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.1rem;
  }
  .actor-card {
    background: var(--bg, #F7F9FC); border: 1.5px solid var(--border);
    border-radius: 14px; padding: 1.5rem 1.4rem; cursor: pointer;
    text-align: left; transition: all .16s ease;
    display: flex; flex-direction: column; gap: .5rem;
  }
  .actor-card:hover {
    border-color: var(--accent); background: var(--white);
    transform: translateY(-3px); box-shadow: 0 10px 26px rgba(20,30,55,.08);
  }
  .actor-title {
    font-family: var(--font-head); font-size: 1.05rem; font-weight: 700;
    color: var(--text);
  }
  .actor-desc { font-size: .82rem; color: var(--sub); line-height: 1.5; flex: 1; }
  .actor-link { font-size: .8rem; font-weight: 700; color: var(--accent); }

  /* ─── Section : pages SEO par marque ─── */
  .seopg-section { padding: 6rem 2rem; background: var(--bg, #F7F9FC); }
  .seopg-inner { max-width: 1080px; margin: 0 auto; }
  .seopg-sub {
    text-align: center; font-size: .98rem; color: var(--sub);
    max-width: 540px; margin: 0 auto 3rem; line-height: 1.7;
  }
  .seopg-grid {
    display: grid; grid-template-columns: repeat(5, 1fr); gap: .9rem;
  }
  .seopg-card {
    background: var(--white); border: 1.5px solid var(--border);
    border-radius: 12px; padding: 1.1rem 1rem; cursor: pointer;
    display: flex; flex-direction: column; gap: .45rem;
    text-align: left; transition: all .15s ease;
  }
  .seopg-card:hover {
    border-color: var(--accent); transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(168,128,58,.1);
  }
  .seopg-tag {
    font-size: .62rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: .8px; color: var(--accent);
    font-family: var(--font-mono);
  }
  .seopg-label {
    font-size: .88rem; font-weight: 700; color: var(--text); line-height: 1.35; flex: 1;
  }
  .seopg-arrow { font-size: .76rem; font-weight: 700; color: var(--muted); }
  .seopg-card:hover .seopg-arrow { color: var(--accent); }

  @media (max-width: 960px) {
    .engines-grid { grid-template-columns: repeat(2, 1fr); }
    .actors-grid { grid-template-columns: repeat(2, 1fr); }
    .seopg-grid { grid-template-columns: repeat(3, 1fr); }
  }
  @media (max-width: 600px) {
    .engines-grid, .actors-grid { grid-template-columns: 1fr; }
    .seopg-grid { grid-template-columns: repeat(2, 1fr); }
  }
  body.dark-mode .engines-section, body.dark-mode .seopg-section { background: #16161B; }
  body.dark-mode .actors-section { background: #0B0D14; }
  body.dark-mode .engine-card, body.dark-mode .seopg-card { background: #161924; border-color: #2A2A32; }
  body.dark-mode .actor-card { background: #161924; border-color: #2A2A32; }

  /* ─── Section SEO alternative Argus ─── */
  .argus-alt-section { padding: 5rem 2rem; background: var(--accent-soft, #FEF3E2); }
  .argus-alt-inner { max-width: 920px; margin: 0 auto; }
  .argus-alt-h2 {
    font-family: var(--font-head); font-size: 2rem; font-weight: 700;
    color: var(--text); letter-spacing: -.02em; margin: 0 0 1rem; line-height: 1.2;
  }
  .argus-alt-seo {
    font-family: var(--font-body); font-size: 1rem; line-height: 1.7;
    color: var(--sub); margin: 0 0 2rem; max-width: 760px;
  }
  .argus-alt-table {
    background: var(--white); border: 1.5px solid var(--border);
    border-radius: var(--radius-lg, 14px); overflow: hidden;
  }
  .argus-alt-trow {
    display: grid; grid-template-columns: 1.4fr 1fr 1fr;
    border-bottom: 1px solid var(--border);
  }
  .compare-multi .argus-alt-trow {
    grid-template-columns: 1.3fr repeat(4, 1fr);
  }
  .compare-multi .argus-alt-cell,
  .compare-multi .argus-alt-crit { font-size: .78rem; padding: .7rem .55rem; }
  .compare-multi .argus-alt-thead > div { font-size: .68rem; padding: .7rem .55rem; }
  .argus-alt-viq-head { background: var(--accent); color: var(--white); }
  .argus-alt-trow:last-child { border-bottom: none; }
  .argus-alt-thead {
    background: var(--text); color: var(--white);
    font-family: var(--font-mono); font-size: .78rem;
    text-transform: uppercase; letter-spacing: .5px;
  }
  .argus-alt-thead > div { padding: .85rem 1.1rem; }
  .argus-alt-crit { padding: .9rem 1.1rem; font-size: .9rem; font-weight: 600; color: var(--text); }
  .argus-alt-cell { padding: .9rem 1.1rem; font-size: .9rem; color: var(--sub); display: flex; align-items: center; }
  .argus-alt-viq { background: rgba(168,128,58,.07); font-weight: 700; color: var(--text); }
  .argus-ok { color: var(--accent); font-weight: 800; font-size: 1.05rem; }
  .argus-x { color: var(--muted); }
  .argus-alt-cta { margin-top: 2rem; }
  @media (max-width: 900px) {
    .compare-multi .argus-alt-table { overflow-x: auto; }
    .compare-multi .argus-alt-trow { min-width: 620px; }
  }
  @media (max-width: 700px) {
    .argus-alt-h2 { font-size: 1.5rem; }
    .argus-alt-trow { grid-template-columns: 1.2fr .9fr .9fr; }
    .argus-alt-crit, .argus-alt-cell { padding: .7rem .6rem; font-size: .8rem; }
  }
  .sec-eyebrow {
    text-align:center; font-size:.66rem; font-weight:800;
    letter-spacing:3px; text-transform:uppercase; margin-bottom:.7rem;
  }
  .sec-title {
    text-align:center; font-size:clamp(1.8rem,3vw,2.6rem); font-weight:900;
    letter-spacing:-.04em; color:var(--text); margin-bottom:.7rem; line-height:1.2;
  }
  .sec-sub {
    text-align:center; font-size:.95rem; color:var(--sub);
    max-width:520px; margin:0 auto 3.5rem; line-height:1.75;
  }
  .why-grid {
    display:grid; grid-template-columns:repeat(3,1fr); gap:1.4rem;
  }
  @media(max-width:900px){ .why-grid{grid-template-columns:1fr;} }
  .why-card {
    background:var(--bg); border:1px solid var(--border); border-radius:16px;
    padding:2rem 1.8rem; position:relative; overflow:hidden; transition:all .22s;
  }
  .why-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:3px;
    background:linear-gradient(90deg,var(--gold) 0%,transparent 100%);
    opacity:0; transition:opacity .22s;
  }
  .why-card:hover { box-shadow:var(--shadow); transform:translateY(-3px); border-color:rgba(201,169,110,.28); }
  .why-card:hover::before { opacity:1; }
  .why-num {
    font-size:2.8rem; font-weight:800; color:var(--border);
    letter-spacing:-.06em; line-height:1; margin-bottom:.8rem;
    font-family:var(--font-mono);
  }
  .why-card-title { font-size:1rem; font-weight:700; color:var(--text); margin-bottom:.45rem; letter-spacing:-.02em; }
  .why-card-desc  { font-size:.85rem; color:var(--sub); line-height:1.72; }
  .why-metric {
    display:inline-flex; align-items:center; gap:.3rem; margin-top:1rem;
    background:rgba(201,169,110,.08); border:1px solid rgba(201,169,110,.22);
    border-radius:50px; padding:.28rem .7rem;
    font-size:.7rem; color:var(--gold); font-weight:700; letter-spacing:.3px;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     POLISH MODERNE GLOBAL — eyebrows raffinés + reveal au scroll
     ═══════════════════════════════════════════════════════════════════════ */
  /* Eyebrow : petit filet centré sous le label pour un rendu éditorial premium */
  .sec-eyebrow {
    display:flex; align-items:center; justify-content:center; gap:.6rem;
  }
  .sec-eyebrow::before, .sec-eyebrow::after {
    content:''; width:24px; height:1px; background:currentColor; opacity:.4;
  }
  /* Reveal au scroll — appliqué via .reveal-on-scroll + .is-visible (JS observer) */
  .reveal-on-scroll {
    opacity:0; transform:translateY(24px);
    transition:opacity .7s cubic-bezier(.16,1,.3,1), transform .7s cubic-bezier(.16,1,.3,1);
    will-change:opacity, transform;
  }
  .reveal-on-scroll.is-visible { opacity:1; transform:translateY(0); }
  /* Décalage progressif des cartes d'une même grille pour un effet cascade */
  .reveal-on-scroll.is-visible .why-card,
  .reveal-on-scroll.is-visible .testi-card { animation:viqCardIn .6s both; }
  .reveal-on-scroll.is-visible .why-card:nth-child(2),
  .reveal-on-scroll.is-visible .testi-card:nth-child(2) { animation-delay:.08s; }
  .reveal-on-scroll.is-visible .why-card:nth-child(3),
  .reveal-on-scroll.is-visible .testi-card:nth-child(3) { animation-delay:.16s; }
  .reveal-on-scroll.is-visible .why-card:nth-child(4) { animation-delay:.24s; }
  .reveal-on-scroll.is-visible .why-card:nth-child(5) { animation-delay:.32s; }
  .reveal-on-scroll.is-visible .why-card:nth-child(6) { animation-delay:.40s; }
  @keyframes viqCardIn { from{opacity:0; transform:translateY(16px);} to{opacity:1; transform:translateY(0);} }
  @keyframes viqLivePulse { 0%{transform:scale(1);opacity:.45;} 70%{transform:scale(2.4);opacity:0;} 100%{transform:scale(2.4);opacity:0;} }
  @media (prefers-reduced-motion: reduce) {
    .reveal-on-scroll { opacity:1 !important; transform:none !important; transition:none; }
    .reveal-on-scroll.is-visible .why-card,
    .reveal-on-scroll.is-visible .testi-card { animation:none; }
  }

  /* ─── TESTIMONIALS ─── */
  .testi-section { padding:6rem 2rem; background:var(--bg); border-top:1px solid var(--border); }
  .testi-inner   { max-width:1100px; margin:0 auto; }
  .testi-grid {
    display:grid; grid-template-columns:repeat(3,1fr); gap:1.2rem; margin-top:0;
  }
  @media(max-width:900px){ .testi-grid{grid-template-columns:1fr;} }
  .testi-card {
    background:var(--white); border:1px solid var(--border); border-radius:16px;
    padding:1.8rem; display:flex; flex-direction:column; gap:1.1rem;
    box-shadow:var(--shadow-sm); transition:all .2s;
  }
  .testi-card:hover { box-shadow:var(--shadow); transform:translateY(-2px); border-color:rgba(168,128,58,.18); }
  .testi-stars { display:flex; gap:4px; margin-bottom:.2rem; }
  .star { color:var(--gold); font-size:1.4rem; line-height:1; }
  .testi-quote {
    font-size:.87rem; color:var(--text); line-height:1.78; flex:1; font-style:italic;
  }
  .testi-quote::before { content:'\\201C'; font-size:1.6rem; color:var(--gold); line-height:0; vertical-align:-.45em; margin-right:.08em; font-style:normal; }
  .testi-author { display:flex; align-items:center; gap:.75rem; }
  .testi-avatar {
    width:38px; height:38px; border-radius:50%; flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
    font-size:.82rem; font-weight:800; color:#fff;
  }
  .testi-name { font-size:.85rem; font-weight:700; color:var(--text); }
  .testi-role { font-size:.7rem; color:var(--muted); margin-top:.05rem; }
  .testi-badge {
    display:inline-flex; align-items:center; gap:.3rem;
    font-size:.62rem; font-weight:700; letter-spacing:.4px;
    background:rgba(168,128,58,.07); color:var(--accent);
    border:1px solid rgba(168,128,58,.14); border-radius:50px;
    padding:.16rem .52rem; margin-top:.35rem;
  }

  /* ─── PROCESS ─── */
  .process-section { padding:6rem 2rem; background:var(--white); border-top:1px solid var(--border); }
  .process-inner   { max-width:860px; margin:0 auto; }
  .process-steps   { display:flex; flex-direction:column; gap:0; margin-top:3.5rem; }
  .process-step {
    display:flex; gap:1.8rem; align-items:flex-start;
    padding:2rem 0; border-bottom:1px solid var(--border);
  }
  .process-step:last-child { border-bottom:none; }
  .process-num {
    font-size:3.2rem; font-weight:800; color:var(--border); line-height:1;
    letter-spacing:-.06em; min-width:54px; text-align:center; flex-shrink:0;
    font-family:var(--font-mono); transition:color .2s;
  }
  .process-step:hover .process-num { color:var(--accent); }
  .process-content { flex:1; padding-top:.25rem; }
  .process-title { font-size:1.05rem; font-weight:700; color:var(--text); margin-bottom:.4rem; letter-spacing:-.02em; }
  .process-desc  { font-size:.86rem; color:var(--sub); line-height:1.75; }
  .process-tag {
    display:inline-flex; align-items:center; gap:.3rem; margin-top:.65rem;
    background:var(--bg); border:1px solid var(--border); border-radius:50px;
    font-size:.68rem; color:var(--muted); padding:.18rem .65rem; font-weight:500;
  }
  .process-icon { font-size:1.3rem; flex-shrink:0; margin-top:.3rem; }

  /* ─── TRUST STRIP ─── */
  .trust-strip {
    background:#080A0F;
    border-top:1px solid rgba(201,169,110,.1);
    padding:2.2rem 2rem;
  }
  .trust-inner {
    max-width:1100px; margin:0 auto;
    display:flex; align-items:center; justify-content:space-between;
    gap:1.5rem; flex-wrap:wrap;
  }
  .trust-label {
    font-size:.62rem; color:rgba(255,255,255,.35);
    letter-spacing:2px; text-transform:uppercase; font-weight:700; white-space:nowrap;
  }
  .trust-certs { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; }
  .cert-item {
    display:flex; align-items:center; gap:.55rem;
    background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.09);
    border-radius:10px; padding:.5rem .95rem; transition:all .2s;
  }
  .cert-item:hover { background:rgba(255,255,255,.08); border-color:rgba(201,169,110,.28); }
  .cert-icon { font-size:1rem; flex-shrink:0; }
  .cert-item-hl { background:rgba(201,169,110,.12); border-color:rgba(201,169,110,.4); }
  .cert-item-hl .cert-icon { font-size:1.5rem; }
  .cert-item-hl .cert-name { font-size:1rem; color:var(--gold); }
  .cert-name { font-size:.72rem; font-weight:700; color:rgba(255,255,255,.82); }
  .cert-desc { font-size:.6rem; color:rgba(255,255,255,.35); margin-top:1px; }

  /* ─── PRO CTA DARK ─── */
  .pro-cta-section {
    background:linear-gradient(135deg,#091525 0%,#0F1E38 50%,#07111E 100%);
    padding:5.5rem 2rem; text-align:center; position:relative; overflow:hidden;
    border-top:1px solid rgba(168,128,58,.12);
  }
  .pro-cta-section::before {
    content:''; position:absolute; inset:0; pointer-events:none;
    background:
      radial-gradient(ellipse 65% 55% at 50% 0%,rgba(168,128,58,.18) 0%,transparent 68%),
      radial-gradient(ellipse 40% 30% at 80% 90%,rgba(201,169,110,.09) 0%,transparent 55%);
  }
  .pro-cta-inner { position:relative; z-index:1; max-width:640px; margin:0 auto; }
  .pro-cta-badge {
    display:inline-flex; align-items:center; gap:.45rem; margin-bottom:1.5rem;
    background:rgba(201,169,110,.1); border:1px solid rgba(201,169,110,.25);
    border-radius:50px; padding:.3rem 1rem;
    font-size:.68rem; color:var(--gold); font-weight:700; letter-spacing:.8px;
  }
  .pro-cta-title {
    font-size:clamp(1.9rem,3.5vw,3rem); font-weight:900; color:#fff;
    letter-spacing:-.04em; line-height:1.15; margin-bottom:.9rem;
  }
  .pro-cta-sub { font-size:.95rem; color:rgba(255,255,255,.55); line-height:1.78; margin-bottom:2rem; }
  .pro-cta-btns { display:flex; justify-content:center; gap:.9rem; flex-wrap:wrap; }
  .btn-gold {
    background:linear-gradient(135deg,#C9A96E 0%,#E8C480 100%);
    color:#1A1200; font-weight:800; padding:.75rem 2rem; font-size:.92rem;
    box-shadow:0 4px 20px rgba(201,169,110,.4); border:none;
    /* v5 : forme pill pour les CTA pied de page */
    border-radius:999px; cursor:pointer; transition:all .2s;
    font-family:var(--font-body);
  }
  .btn-gold:hover { transform:translateY(-2px); box-shadow:0 8px 28px rgba(201,169,110,.55); }
  .btn-glass {
    background:rgba(255,255,255,.07); color:rgba(255,255,255,.82);
    border:1px solid rgba(255,255,255,.14); padding:.72rem 1.6rem; font-size:.88rem;
    /* v5 : forme pill pour les CTA pied de page */
    border-radius:999px; backdrop-filter:blur(6px); cursor:pointer;
    transition:all .2s; font-family:var(--font-body); font-weight:600;
  }
  .btn-glass:hover { background:rgba(255,255,255,.12); border-color:rgba(255,255,255,.26); }

  /* ─── Dark overrides for luxury sections ─── */
  body.dark-mode .why-section   { background:#16161B; }
  body.dark-mode .why-card      { background:#1C1C23; border-color:#2A2A32; }
  body.dark-mode .testi-section { background:#101015; border-color:#2A2A32; }
  body.dark-mode .testi-card    { background:#1C1C23; border-color:#2A2A32; }
  body.dark-mode .status-ok { color: #4FB894; }
  body.dark-mode .dash-mock-section { background: #101015; }
  body.dark-mode .dash-mock-h2 { color: #ffffff !important; }
  body.dark-mode .dash-mock-sub { color: rgba(255,255,255,.62) !important; }
  body.dark-mode .process-section { background:#16161B; border-color:#2A2A32; }
  body.dark-mode .process-step  { border-color:#2A2A32; }
  body.dark-mode .logos-strip   { background:#16161B; border-color:#2A2A32; }
  body.dark-mode .logo-pill     { background:#1C1C23; border-color:#2A2A32; }
  body.dark-mode .market-section { background:#101015; border-color:#2A2A32; }
  body.dark-mode .iq-gauge-card  { background:#1C1C23; border-color:#2A2A32; }
  body.dark-mode .depreciation-card { background:#1C1C23; border-color:#2A2A32; }
  body.dark-mode .iq-sparkline   { background:#101015; }

  /* ─── PRO DASHBOARD MODAL ─── */
  .pro-dash-overlay {
    position:fixed; inset:0; z-index:500;
    background:rgba(5,7,15,.82); backdrop-filter:blur(14px);
    display:flex; align-items:center; justify-content:center; padding:0.5rem;
  }
  .pro-dash-panel {
    background:var(--card);
    border:1px solid var(--border); border-radius:16px;
    /* Modal de taille fixe (comme le Super Admin) : 98vw × 96vh, plafonné à 1800px.
       Le panel ne scrolle PAS : header + barre d'onglets + footer restent figés,
       seul .saas-dash-content scrolle. */
    width:98vw; max-width:1800px;
    height:96vh; max-height:96vh;
    overflow:hidden;
    -webkit-overflow-scrolling:touch;
    display:flex; flex-direction:column;
    box-shadow:0 0 0 1px rgba(255,255,255,.04), 0 40px 100px rgba(0,0,0,.5), 0 8px 24px rgba(0,0,0,.3);
    animation:slideUp .3s cubic-bezier(.16,1,.3,1);
  }

  /* ═══ DESIGN SYSTEM DASHBOARDS — sidebar + content layout ═════════════════
     Architecture partagée par : Dashboard Pro, Admin, Mon espace, Apporteur.
     Inspiration : SaaS modernes (Linear, Notion, Vercel) avec navigation
     latérale gauche, contenu scrollable à droite, séparations nettes.
     ═════════════════════════════════════════════════════════════════════════ */
  /* Barre d'onglets horizontale, type onglets de navigation classiques d'app SaaS.
     Située sous le header, au-dessus du contenu. Soulignement orange sur l'actif. */
  .saas-dash-tabs-bar {
    display: flex;
    align-items: stretch;
    gap: .15rem;
    padding: 0 1.5rem;
    background: var(--bg);
    border-bottom: 1.5px solid var(--border);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    /* Reste figée sous le header, jamais par-dessus : le contenu scrolle dessous. */
    flex-shrink: 0;
    z-index: 2;
  }
  .saas-dash-tabs-bar::-webkit-scrollbar { height: 0; }
  /* Cache scrollbar des barres d'onglets Mon espace / Apporteur / Admin */
  .me-tabs-bar::-webkit-scrollbar { height: 0; }
  [class="panel-body"] > div[style*="overflowX"]::-webkit-scrollbar { height: 0; }
  .saas-dash-tab {
    position: relative;
    display: inline-flex; align-items: center; gap: .55rem;
    padding: .85rem 1.1rem;
    background: transparent; border: none; cursor: pointer;
    color: var(--sub);
    font-family: var(--font-body);
    font-size: .85rem; font-weight: 500;
    transition: all .15s cubic-bezier(.4,0,.2,1);
    letter-spacing: -.005em;
    white-space: nowrap;
    border-bottom: 2.5px solid transparent;
    margin-bottom: -1.5px;
  }
  .saas-dash-tab svg {
    width: 16px; height: 16px;
    flex-shrink: 0; opacity: .75;
  }
  .saas-dash-tab:hover {
    color: var(--text);
    background: rgba(0,0,0,.02);
  }
  .saas-dash-tab.active {
    color: var(--accent);
    font-weight: 600;
    border-bottom-color: var(--accent);
  }
  .saas-dash-tab.active svg { opacity: 1; }
  .saas-dash-tab-badge {
    font-size: .68rem; font-weight: 700; color: var(--accent);
    background: rgba(168,128,58,.1);
    padding: .12rem .45rem; border-radius: 100px;
    font-family: var(--font-mono);
    margin-left: .2rem;
  }
  .saas-dash-tab:not(.active) .saas-dash-tab-badge {
    color: var(--muted); background: var(--card);
  }
  .saas-dash-content {
    flex: 1; min-height: 0;
    /* Seule zone scrollable du modal : header + onglets restent figés au-dessus,
       footer figé en dessous. Pas de hauteur fixe → plus de chevauchement. */
    padding: 1.5rem 1.8rem;
    overflow-y: auto;
    display: flex; flex-direction: column;
    gap: 1rem;
    background: var(--card);
  }
  .saas-dash-content-header { margin-bottom: .6rem; }
  .saas-dash-content-title {
    font-family: var(--font-head);
    font-size: 1.5rem; font-weight: 700;
    color: var(--text); letter-spacing: -.015em;
    margin: 0 0 .25rem;
  }
  .saas-dash-content-sub {
    font-size: .82rem; color: var(--sub);
    line-height: 1.5;
  }
  /* Cards renforcées — séparation visible et nette */
  .saas-card {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 12px;
    padding: 1.2rem 1.4rem;
    transition: border-color .15s ease, box-shadow .15s ease;
  }
  .saas-card.hoverable:hover {
    border-color: var(--text);
    box-shadow: 0 4px 12px rgba(20,30,55,.06);
  }
  .saas-card-title {
    font-size: .85rem; font-weight: 700; color: var(--text);
    margin: 0 0 .8rem;
    display: flex; align-items: center; gap: .5rem;
  }
  .saas-card-eyebrow {
    font-size: .62rem; color: var(--muted);
    text-transform: uppercase; letter-spacing: 1.3px;
    font-family: var(--font-mono);
    margin-bottom: .3rem; font-weight: 700;
  }
  /* KPI Card — variante stat compacte */
  .saas-kpi {
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 12px;
    padding: 1rem 1.2rem;
    display: flex; flex-direction: column; gap: .25rem;
  }
  .saas-kpi-eyebrow {
    font-size: .6rem; color: var(--muted);
    text-transform: uppercase; letter-spacing: 1.3px;
    font-family: var(--font-mono); font-weight: 700;
  }
  .saas-kpi-value {
    font-family: var(--font-head);
    font-size: 1.5rem; font-weight: 800;
    color: var(--text); line-height: 1.1;
    letter-spacing: -.02em;
  }
  .saas-kpi-value.accent { color: var(--accent); }
  .saas-kpi-value.green { color: var(--green); }
  .saas-kpi-value.red { color: #9B2C2C; }
  .saas-kpi-sub {
    font-size: .72rem; color: var(--sub);
    margin-top: .1rem;
  }
  /* Responsive mobile */
  @media (max-width: 768px) {
    .saas-dash-tabs-bar {
      padding: 0 .8rem;
    }
    .saas-dash-tab {
      padding: .7rem .8rem;
      font-size: .8rem;
    }
    .saas-dash-tab span {
      /* Sur très petits écrans on garde le label mais l'icône suffit en visuel */
    }
    .saas-dash-content {
      padding: 1rem 1rem 5rem;
    }
    .saas-dash-content-title {
      font-size: 1.25rem;
    }
  }
  .pro-dash-header {
    display:flex; align-items:center; justify-content:space-between;
    padding:1.5rem 2rem; gap:1rem;
    /* v5 fix : NON wrap — sinon les boutons (◂ ▸ ✕) passent sur une 2e ligne
       et se retrouvent masqués par la barre d'onglets qui suit. */
    flex-wrap: nowrap;
    /* v5 fix : on retire le position:sticky qui causait un chevauchement avec
       la .saas-dash-tabs-bar quand le panel scrolle. Le scroll est désormais
       solidaire entre header + onglets + contenu (panel entier scrolle). */
    flex-shrink: 0;
    background:
      linear-gradient(135deg, rgba(168,128,58,.06) 0%, transparent 55%),
      var(--card);
    border-bottom:1px solid var(--border);
    /* Petit ombrage pour distinguer du contenu qui scrolle dessous */
    box-shadow:0 1px 0 var(--border);
  }
  .pro-dash-title {
    font-family:var(--font-head);
    font-size:1.35rem; font-weight:800; letter-spacing:-.035em; color:var(--text);
    display:flex; align-items:center; gap:.7rem;
  }
  .pro-dash-badge {
    font-size:.6rem; font-weight:800; letter-spacing:1.4px; text-transform:uppercase;
    background:linear-gradient(135deg, rgba(201,169,110,.18), rgba(201,169,110,.08));
    color:var(--gold); border:1px solid rgba(201,169,110,.35);
    padding:.28rem .7rem; border-radius:50px;
  }
  /* ── Barre d'actions services + partage WhatsApp ── */
  .pro-dash-actions {
    display: flex; align-items: center; justify-content: space-between;
    gap: 1rem; flex-wrap: wrap;
    padding: .75rem 1.8rem;
    background: var(--deep);
    border-bottom: 1px solid var(--border);
    /* Footer figé en bas du modal (le contenu scrolle au-dessus). */
    flex-shrink: 0;
  }
  .pro-dash-actions-left {
    display: flex; align-items: center; gap: .5rem; flex-wrap: wrap;
  }
  .pro-dash-actions-label {
    font-size: .72rem; color: var(--muted); font-weight: 500;
    margin-right: .2rem;
    text-transform: uppercase; letter-spacing: .8px;
  }
  .pro-dash-action-btn {
    display: inline-flex; align-items: center; gap: .5rem;
    background: var(--card); border: 1px solid var(--border);
    color: var(--text); cursor: pointer;
    padding: .62rem 1.15rem; border-radius: 9px;
    font-family: var(--font-body); font-size: .92rem; font-weight: 600;
    transition: all .15s cubic-bezier(.4,0,.2,1);
    white-space: nowrap;
  }
  .pro-dash-action-btn:hover {
    border-color: var(--accent); color: var(--accent);
    background: var(--accent-soft);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(168,128,58,.10);
  }
  .pro-dash-action-btn svg { flex-shrink: 0; width: 16px; height: 16px; }
  .pro-dash-share-btn {
    display: inline-flex; align-items: center; gap: .5rem;
    background: #25D366; border: none; color: #fff; cursor: pointer;
    padding: .62rem 1.25rem; border-radius: 9px;
    font-family: var(--font-body); font-size: .92rem; font-weight: 700;
    box-shadow: 0 1px 2px rgba(37,211,102,.18), 0 4px 10px rgba(37,211,102,.22);
    transition: all .15s cubic-bezier(.4,0,.2,1);
    white-space: nowrap;
  }
  .pro-dash-share-btn:hover {
    background: #1FBA59;
    box-shadow: 0 2px 4px rgba(37,211,102,.25), 0 8px 18px rgba(37,211,102,.32);
    transform: translateY(-1px);
  }
  @media (max-width: 720px) {
    /* Sur mobile : plein écran, MAIS on garde le modèle « app » comme sur PC —
       header + onglets figés en haut, footer d'actions figé en bas, seul le
       contenu central scrolle. */
    .pro-dash-overlay {
      padding: 0;
      align-items: stretch;
      background: var(--card);
      backdrop-filter: none;
    }
    .pro-dash-panel {
      max-width: 100%;
      width: 100%;
      height: 100vh; height: 100dvh;
      max-height: 100vh; max-height: 100dvh;
      border-radius: 0;
      border: none;
      box-shadow: none;
      /* Panel = colonne flex à hauteur fixe : il ne scrolle PAS lui-même. */
      overflow: hidden;
      display: flex; flex-direction: column;
    }
    /* Seule la zone de contenu scrolle. */
    .saas-dash-content {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }
    /* Header : agencement pleine largeur pour éviter le tassement.
       La 1re ligne = pager + thème + fermer (compacts, alignés à droite).
       La 2e ligne = titre + badge SIRET, sur toute la largeur.
       La 3e ligne = SIRET + nb estimations. → hauteur d'en-tête réduite. */
    .pro-dash-header {
      flex-shrink: 0; padding: .7rem .9rem; gap: .35rem;
      flex-wrap: wrap; align-items: center;
    }
    /* Bloc titre (1er enfant) : pleine largeur, ordonné après les contrôles */
    .pro-dash-header > div:first-child { order: 2; flex: 1 1 100%; min-width: 0; }
    /* Contrôles (2e enfant) : 1re ligne, alignés à droite */
    .pro-dash-header > div:last-child { order: 1; width: 100%; justify-content: flex-end; gap: .45rem; }
    .saas-dash-tabs-bar { flex-shrink: 0; }
    .pro-dash-actions { flex-shrink: 0; }
    .pro-dash-title { font-size: 1.05rem; gap: .45rem; flex-wrap: wrap; }
    .pro-dash-badge { font-size: .54rem; padding: .2rem .5rem; letter-spacing: 1px; }
    /* Barre d'actions : label masqué, défilement horizontal des boutons. */
    .pro-dash-actions {
      padding: .55rem .9rem; gap: .4rem;
      flex-direction: column; align-items: stretch;
    }
    .pro-dash-actions-label { display: none; }
    .pro-dash-actions-left { width: 100%; overflow-x: auto; flex-wrap: nowrap;
      padding-bottom: .2rem; -webkit-overflow-scrolling: touch;
      scrollbar-width: thin; }
    .pro-dash-action-btn { font-size: .8rem; padding: .5rem .8rem; flex-shrink: 0; }
    .pro-dash-action-btn svg { width: 15px; height: 15px; }
    .pro-dash-share-btn { font-size: .8rem; padding: .5rem .9rem; width: 100%; justify-content: center; }
    .pro-dash-body {
      padding: 1rem 1rem 1.4rem 1rem;
      gap: 1rem;
    }
  }

  /* Mobile compact : <= 480px */
  @media (max-width: 480px) {
    .pro-dash-header { padding: .8rem .9rem; }
    .pro-dash-title { font-size: .92rem; }
    .pro-dash-body { padding: .8rem .8rem 4rem .8rem; gap: .8rem; }
  }

  /* Grille comparaison de prix : 2 colonnes → 1 sur mobile */
  @media (max-width: 720px) {
    .pro-dash-price-grid { grid-template-columns: 1fr !important; gap: .8rem !important; }
    /* Tout grid inline 2-col du dashboard → 1 col en mobile */
    .pro-dash-body div[style*="gridTemplateColumns"][style*="1fr 1fr"] {
      grid-template-columns: 1fr !important;
    }
    .pro-dash-body div[style*="grid-template-columns"][style*="1fr 1fr"] {
      grid-template-columns: 1fr !important;
    }
    /* Tous les graphiques SVG du dashboard prennent toute la largeur */
    .pro-dash-body svg { width: 100% !important; height: auto !important; }
    /* Les courbes/charts doivent passer en pleine largeur */
    .pro-dash-body div[style*="flex"][style*="row"] {
      flex-direction: column !important;
    }
  }

  /* Bouton "Masquer/Afficher" la zone supérieure du dashboard */
  .pro-dash-collapse-btn {
    display: block; /* Visible sur tous les écrans */
    margin: .5rem 1.4rem 0;
    padding: .55rem 1rem;
    background: var(--bg);
    border: 1px dashed var(--border);
    color: var(--sub);
    border-radius: 8px;
    font-size: .78rem;
    cursor: pointer;
    text-align: center;
    transition: all .15s ease;
    width: calc(100% - 2.8rem);
  }
  .pro-dash-collapse-btn:hover {
    border-color: var(--accent);
    color: var(--accent);
    background: rgba(168,128,58,.04);
  }
  @media (max-width: 720px) {
    /* Sur mobile : marges réduites */
    .pro-dash-collapse-btn {
      margin: .4rem .8rem 0;
      padding: .45rem .8rem;
      font-size: .72rem;
      width: calc(100% - 1.6rem);
    }
  }

  /* Bloc Impact économique + Formulaire suivi de vente : margins adaptatives */
  .pro-dash-impact-bar, .pro-dash-platforms-block, .pro-dash-conseils-block, .pro-dash-followup-form {
    margin: .8rem 1.4rem 0;
    border-radius: 12px;
  }
  .pro-dash-followup-form {
    background: var(--bg);
    border: 1px solid var(--accent);
  }
  @media (max-width: 720px) {
    /* Estimation détaillée : boutons d'action uniformes sur mobile (grille 2 col) */
    .estim-actions {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: .5rem;
      justify-content: stretch;
      margin-top: .6rem;
    }
    /* Le bloc de droite (score/delta/actions) passe pleine largeur pour éviter le débordement */
    .estim-actions-wrap { width: 100%; flex: 1 1 100%; }
    .estim-actions { grid-column: 1 / -1; }
    .estim-action-btn {
      width: 100%;
      font-size: .72rem;
      padding: .6rem .4rem;
      min-height: 2.6rem;
      box-sizing: border-box;
    }
    .estim-action-btn span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .estim-action-btn svg { width: 15px !important; height: 15px !important; flex: 0 0 15px; }
    .pro-dash-impact-bar, .pro-dash-platforms-block, .pro-dash-conseils-block, .pro-dash-followup-form {
      margin: .6rem .8rem 0;
      padding: .8rem .9rem !important;
    }
    /* Conseils : grille 2 cols → 1 col sur mobile */
    .pro-dash-conseils-grid {
      grid-template-columns: 1fr !important;
    }
    /* Bandeau impact : passer en colonne (KPI les uns sous les autres) */
    .pro-dash-impact-bar {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: .55rem !important;
    }
    .pro-dash-impact-bar > div:last-child {
      width: 100%;
      justify-content: space-between !important;
      gap: .5rem !important;
    }
    /* Grille plateformes : adapter aux écrans étroits */
    .pro-dash-platforms-grid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: .45rem !important;
    }
    /* Formulaire : 1 colonne */
    .pro-dash-followup-form > div[style*="grid-template-columns"] {
      grid-template-columns: 1fr !important;
    }
  }
  @media (max-width: 480px) {
    .pro-dash-platforms-grid {
      grid-template-columns: 1fr !important;
    }
  }

  .pro-dash-body {
    /* Plus de scroll interne — le panel gère le scroll global */
    padding:1.5rem 1.8rem;
    display:flex; flex-direction:column; gap:1.5rem;
  }

  /* KPI row */
  .dash-kpi-row {
    display:grid; grid-template-columns:repeat(4,1fr); gap:1rem;
  }
  @media(max-width:700px){ .dash-kpi-row{grid-template-columns:repeat(2,1fr);} }
  .dash-kpi {
    background:
      linear-gradient(160deg, rgba(168,128,58,.045) 0%, transparent 60%),
      var(--bg);
    border:1px solid var(--border); border-radius:16px;
    padding:1.3rem 1.3rem; transition:all .22s cubic-bezier(.16,1,.3,1);
    position:relative; overflow:hidden;
  }
  .dash-kpi::after {
    content:''; position:absolute; top:0; left:0; right:0; height:2px;
    background:linear-gradient(90deg, var(--accent), transparent);
    opacity:0; transition:opacity .22s ease;
  }
  .dash-kpi:hover {
    border-color:rgba(168,128,58,.35);
    box-shadow:0 8px 24px rgba(168,128,58,.10);
    transform:translateY(-2px);
  }
  .dash-kpi:hover::after { opacity:1; }
  .dash-kpi-label { font-size:.64rem; color:var(--muted); text-transform:uppercase; letter-spacing:1.3px; font-weight:800; margin-bottom:.5rem; }
  .dash-kpi-value { font-family:var(--font-head); font-size:2rem; font-weight:800; color:var(--accent); letter-spacing:-.045em; line-height:1; }
  .dash-kpi-sub { font-size:.72rem; color:var(--sub); margin-top:.4rem; }
  .dash-kpi-trend { font-size:.7rem; font-weight:700; margin-top:.3rem; }
  .dash-kpi-trend.up   { color:#4ADE80; }
  .dash-kpi-trend.down { color:#F87171; }

  /* Charts row */
  .dash-charts-row {
    display:grid; grid-template-columns:1.6fr 1fr; gap:1.2rem;
  }
  @media(max-width:700px){ .dash-charts-row{grid-template-columns:1fr;} }
  .dash-chart-card {
    background:var(--bg); border:1px solid var(--border); border-radius:14px; padding:1.4rem;
  }
  .dash-chart-title { font-size:.85rem; font-weight:700; color:var(--text); margin-bottom:.2rem; }
  .dash-chart-sub   { font-size:.72rem; color:var(--muted); margin-bottom:1rem; }
  .dash-chart-area  { width:100%; position:relative; }
  .dash-chart-area svg { width:100%; overflow:visible; }

  /* Activity feed */
  .dash-activity-row {
    display:grid; grid-template-columns:1fr 1fr; gap:1.2rem;
  }
  @media(max-width:700px){ .dash-activity-row{grid-template-columns:1fr;} }
  .dash-feed-card {
    background:var(--bg); border:1px solid var(--border); border-radius:14px; padding:1.4rem;
  }
  .dash-feed-list { display:flex; flex-direction:column; gap:.6rem; margin-top:.9rem; }
  .dash-feed-item {
    display:flex; align-items:center; gap:.75rem;
    padding:.6rem .8rem; background:var(--card); border:1px solid var(--border);
    border-radius:8px; transition:border-color .15s;
  }
  .dash-feed-item:hover { border-color:rgba(168,128,58,.25); }
  .dash-feed-icon {
    width:32px; height:32px; border-radius:8px; flex-shrink:0;
    display:flex; align-items:center; justify-content:center; font-size:.95rem;
  }
  .dash-feed-info { flex:1; min-width:0; }
  .dash-feed-name { font-size:.82rem; font-weight:600; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .dash-feed-meta { font-size:.68rem; color:var(--muted); font-family:var(--font-mono); margin-top:1px; }
  .dash-feed-date { font-size:.65rem; color:var(--muted); font-family:var(--font-mono); flex-shrink:0; }

  /* Gate */
  .pro-dash-gate {
    display:flex; flex-direction:column; align-items:center; justify-content:center;
    gap:1.2rem; padding:4rem 2rem; text-align:center;
  }
  .pro-dash-gate-icon { font-size:3rem; opacity:.6; }
  .pro-dash-gate-title { font-size:1.3rem; font-weight:800; color:var(--text); letter-spacing:-.03em; }
  .pro-dash-gate-sub { font-size:.88rem; color:var(--sub); max-width:380px; line-height:1.7; }

  /* Dark overrides */
  body.dark-mode .pro-dash-panel  { background:#1C1C23; border-color:#2A2A32; }
  body.dark-mode .dash-kpi        { background:#101015; border-color:#2A2A32; }
  body.dark-mode .dash-chart-card { background:#101015; border-color:#2A2A32; }
  body.dark-mode .dash-feed-card  { background:#101015; border-color:#2A2A32; }
  body.dark-mode .dash-feed-item  { background:#1C1C23; border-color:#2A2A32; }

  /* ─── APP LOADER / SPLASH SCREEN ─── */
  .app-loader {
    position: fixed; inset: 0; z-index: 99999;
    background: var(--bg);
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 2rem;
    transition: opacity .4s ease, visibility .4s ease;
  }
  .app-loader.fade-out {
    opacity: 0; visibility: hidden;
    pointer-events: none;
  }
  .loader-logo {
    display: flex; flex-direction: column; align-items: center; gap: .8rem;
  }
  .loader-monogram {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 2.8rem; font-weight: 600; letter-spacing: -.02em;
    color: var(--text); position: relative;
    font-feature-settings: 'ss01';
  }
  .loader-monogram span { color: var(--accent); font-style: italic; }
  .loader-dot-pulse {
    position: absolute; top: 8px; right: -14px;
    width: 7px; height: 7px; border-radius: 50%;
    background: var(--accent); box-shadow: 0 0 12px rgba(168,128,58,.5);
    animation: loaderDotPulse 1.2s ease-in-out infinite;
  }
  @keyframes loaderDotPulse {
    0%,100% { opacity:1; transform:scale(1); box-shadow:0 0 12px rgba(168,128,58,.5); }
    50%      { opacity:.4; transform:scale(1.6); box-shadow:0 0 18px rgba(168,128,58,.3); }
  }
  .loader-tagline {
    font-family: 'JetBrains Mono', monospace; font-size: .6rem;
    letter-spacing: 4px; color: #9B9BA5;
    text-transform: uppercase;
  }
  .loader-bar-wrap {
    width: 200px; height: 2px; background: rgba(26,26,31,.08); border-radius: 2px; overflow: hidden;
  }
  .loader-bar {
    height: 100%; width: 0%; background: linear-gradient(90deg, var(--accent), #F59E0B);
    border-radius: 2px;
    animation: loaderBarFill 1.1s cubic-bezier(.4,0,.2,1) forwards;
  }
  @keyframes loaderBarFill {
    0%   { width: 0%; opacity: 1; }
    75%  { width: 85%; opacity: 1; }
    95%  { width: 100%; opacity: 1; }
    100% { width: 100%; opacity: 0; }
  }
  .loader-status {
    font-family: 'JetBrains Mono', monospace; font-size: .62rem;
    color: #9B9BA5; letter-spacing: 1.5px;
    animation: loaderStatusFade 1.1s ease forwards;
  }
  @keyframes loaderStatusFade {
    0%,60% { opacity: 1; }
    100%   { opacity: 0; }
  }
  body.dark-mode .app-loader { background: #131519; }
  body.dark-mode .loader-monogram { color: #ECEAE3; }
  body.dark-mode .loader-monogram span { color: #F59E0B; }
  body.dark-mode .loader-bar-wrap { background: rgba(255,255,255,.08); }

  /* ─── CONTACT MODAL ─── */
  .contact-modal { max-width: 520px; }
  .contact-form { display: flex; flex-direction: column; gap: 1rem; }
  .contact-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: .8rem; }
  .contact-textarea {
    width: 100%; min-height: 110px; resize: vertical;
    background: var(--white); border: 1.5px solid var(--border);
    color: var(--text); font-family: var(--font-body); font-size: .9rem;
    padding: .65rem 1rem; border-radius: var(--radius); outline: none;
    transition: border-color .15s; line-height: 1.6;
  }
  .contact-textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(168,128,58,.1); }
  .contact-send-btn {
    width: 100%; padding: .75rem; font-size: .95rem; font-weight: 700;
    background: var(--accent); color: #fff; border: none; border-radius: var(--radius);
    cursor: pointer; transition: all .18s; display: flex; align-items: center; justify-content: center; gap: .5rem;
  }
  .contact-send-btn:hover { background: var(--accent-h); transform: translateY(-1px); box-shadow: 0 4px 16px rgba(168,128,58,.35); }
  .contact-success {
    text-align: center; padding: 2rem 1rem;
    display: flex; flex-direction: column; align-items: center; gap: .9rem;
  }
  .contact-success-icon { font-size: 2.8rem; }
  .contact-success-title { font-size: 1.2rem; font-weight: 800; color: var(--text); }
  .contact-success-sub { font-size: .85rem; color: var(--sub); max-width: 320px; line-height: 1.7; }
  body.dark-mode .contact-textarea { background: #101015; border-color: #2A2A32; color: #F2F0EA; }
  body.dark-mode .contact-textarea:focus { border-color: #C9A96E; }

  /* ─── HERO ROTATING TEXT ─── */
  .hero-rotate-wrap {
    display: inline-block; position: relative; overflow: hidden;
    height: 1.2em; vertical-align: bottom;
  }
  .hero-rotate-word {
    display: block; color: var(--accent);
    animation: rotateWordIn .6s cubic-bezier(.4,0,.2,1) forwards;
  }
  .hero-rotate-word.out {
    animation: rotateWordOut .5s cubic-bezier(.4,0,.2,1) forwards;
  }
  @keyframes rotateWordIn  { from{opacity:0;transform:translateY(100%)} to{opacity:1;transform:translateY(0)} }
  @keyframes rotateWordOut { from{opacity:1;transform:translateY(0)}   to{opacity:0;transform:translateY(-100%)} }

  /* ─── BIGGER STARS & REVIEWS ─── */
  .star { color:var(--gold); font-size:1.15rem; }
  .testi-quote { font-size:.95rem; line-height:1.82; }
  .testi-name  { font-size:.95rem; }
  .testi-role  { font-size:.78rem; }
  .testi-card  { padding:2rem; }
  .testi-avatar { width:48px; height:48px; font-size:.95rem; }
  .testi-section { padding:6rem 2rem; }
  .testi-grid { gap:1.6rem; }

  /* ─── BLOG PAGE ─── */
  .blog-overlay {
    position:fixed; inset:0; z-index:300;
    background:var(--white); overflow-y:auto;
    animation:slideUp .25s ease;
  }
  body.dark-mode .blog-overlay { background:#16161B; }
  .blog-hero {
    padding:5rem 2rem 3.5rem;
    background:linear-gradient(180deg,var(--bg) 0%,var(--white) 100%);
    border-bottom:1px solid var(--border); text-align:center;
  }
  .blog-hero-tag {
    display:inline-flex; align-items:center; gap:.4rem;
    background:rgba(168,128,58,.08); border:1px solid rgba(168,128,58,.2);
    color:var(--accent); border-radius:50px; padding:.28rem .85rem;
    font-size:.72rem; font-weight:700; letter-spacing:.3px; margin-bottom:1.2rem;
  }
  .blog-hero-title {
    font-size:clamp(2rem,4vw,3rem); font-weight:800; letter-spacing:-.04em;
    color:var(--text); margin-bottom:.75rem; line-height:1.2;
  }
  .blog-hero-sub { font-size:.95rem; color:var(--sub); max-width:500px; margin:0 auto; }
  .blog-nav {
    display:flex; align-items:center; justify-content:space-between;
    padding:.9rem 2rem; border-bottom:1px solid var(--border);
    background:var(--white); position:sticky; top:0; z-index:10;
  }
  body.dark-mode .blog-nav { background:#16161B; }
  .blog-grid {
    max-width:1100px; margin:0 auto; padding:3rem 2rem;
    display:grid; grid-template-columns:repeat(3,1fr); gap:1.4rem;
  }
  @media(max-width:900px){ .blog-grid{ grid-template-columns:1fr; } }
  .blog-card {
    background:var(--card); border:1px solid var(--border); border-radius:14px;
    overflow:hidden; cursor:pointer; transition:all .2s; display:flex; flex-direction:column;
  }
  .blog-card:hover { transform:translateY(-3px); box-shadow:var(--shadow); border-color:rgba(168,128,58,.25); }
  .blog-card-img {
    height:180px; background:linear-gradient(135deg,var(--deep) 0%,var(--card) 100%);
    display:flex; align-items:center; justify-content:center;
    font-size:3rem; border-bottom:1px solid var(--border);
    position:relative; overflow:hidden;
  }
  .blog-card-cat {
    position:absolute; top:.75rem; left:.75rem;
    background:var(--accent); color:#fff; font-size:.6rem; font-weight:700;
    padding:.18rem .55rem; border-radius:50px; letter-spacing:.5px; text-transform:uppercase;
  }
  .blog-card-body { padding:1.3rem; flex:1; display:flex; flex-direction:column; gap:.6rem; }
  .blog-card-title { font-size:1rem; font-weight:700; color:var(--text); line-height:1.35; letter-spacing:-.02em; }
  .blog-card-excerpt { font-size:.82rem; color:var(--sub); line-height:1.7; flex:1; }
  .blog-card-meta { display:flex; align-items:center; gap:.6rem; flex-wrap:wrap; }
  .blog-card-date { font-size:.65rem; color:var(--muted); font-family:var(--font-mono); }
  .blog-card-read { font-size:.65rem; color:var(--muted); }
  /* Article full */
  .blog-article {
    max-width:760px; margin:0 auto; padding:3rem 2rem 5rem;
  }
  .blog-article-cat { font-size:.68rem; font-weight:700; color:var(--accent); letter-spacing:2px; text-transform:uppercase; margin-bottom:.8rem; }
  .blog-article-title { font-size:clamp(1.8rem,4vw,2.8rem); font-weight:800; letter-spacing:-.04em; color:var(--text); line-height:1.2; margin-bottom:1rem; }
  .blog-article-meta { display:flex; gap:1rem; align-items:center; flex-wrap:wrap; margin-bottom:2rem; padding-bottom:1.2rem; border-bottom:1px solid var(--border); font-size:.75rem; color:var(--muted); }
  .blog-article-body { font-size:.95rem; color:var(--sub); line-height:1.9; }
  .blog-article-body h2 { font-size:1.4rem; font-weight:800; color:var(--text); margin:2.5rem 0 .8rem; letter-spacing:-.03em; }
  .blog-article-body h3 { font-size:1.1rem; font-weight:700; color:var(--text); margin:1.8rem 0 .6rem; }
  .blog-article-body p { margin-bottom:1.2rem; }
  .blog-article-body ul { padding-left:1.4rem; margin-bottom:1.2rem; }
  .blog-article-body li { margin-bottom:.5rem; }
  .blog-article-body strong { color:var(--text); font-weight:700; }
  .blog-article-body .highlight {
    background:rgba(168,128,58,.07); border-left:3px solid var(--accent);
    padding:.9rem 1.2rem; border-radius:0 8px 8px 0; margin:1.5rem 0;
    font-style:italic; color:var(--text);
  }

  /* ─── RECENT ARTICLES (homepage) ─── */
  .recent-articles { padding:5rem 2rem; background:var(--white); border-top:1px solid var(--border); }
  body.dark-mode .recent-articles { background:#16161B; }
  .recent-art-grid {
    max-width:1100px; margin:2.5rem auto 0;
    display:grid; grid-template-columns:repeat(3,1fr); gap:1.2rem;
  }
  @media(max-width:900px){ .recent-art-grid{ grid-template-columns:1fr; } }

  /* ─── MOBILE NAV FIX ─── */
  /* Desktop par défaut : hide mobile-only buttons (sera écrasé en mobile via media query plus bas) */
  .nav-mobile-only { display: none !important; }

  @media(max-width:768px) {
    nav { padding:0 .9rem; height:56px; gap:.4rem; }
    .hero { padding-top: 56px; } /* compensation nav fixe mobile */
    .nav-center { display:none; }
    .nav-actions { gap:.3rem; flex-wrap:nowrap; overflow-x:auto; max-width:calc(100vw - 110px); }
    .nav-actions::-webkit-scrollbar { display:none; }
    .nav-actions button, .nav-actions div, .nav-actions a { flex-shrink:0; }
    .nav-btn-label { display:none; }
    /* On mobile, hide wallet pill (saves space, user voit ses crédits dans Mon espace) */
    .nav-wallet-pill { display:none !important; }
    /* On mobile, show hamburger menu instead of regular nav buttons */
    .nav-mobile-only { display: inline-flex !important; }
    .nav-desktop-only { display: none !important; }
  }

  /* ─── MOBILE MENU DRAWER ─────────────────────────────────────────────────── */
  .mobile-menu-overlay {
    position: fixed; inset: 0; z-index: 99999;
    background: rgba(0, 0, 0, .45);
    display: flex; justify-content: flex-end;
    animation: mobileMenuFadeIn .25s ease;
  }
  @keyframes mobileMenuFadeIn { from { opacity: 0; } to { opacity: 1; } }

  .mobile-menu-drawer {
    width: min(340px, 88vw); height: 100%;
    background: var(--card);
    box-shadow: -8px 0 32px rgba(26,26,31,.18);
    overflow-y: auto;
    display: flex; flex-direction: column;
    animation: mobileMenuSlideIn .3s cubic-bezier(.4,0,.2,1);
  }
  @keyframes mobileMenuSlideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }

  .mobile-menu-header {
    padding: 1.2rem 1.2rem 1rem;
    display: flex; align-items: center; justify-content: space-between;
    border-bottom: 1px solid var(--border);
  }

  .mobile-menu-wallet {
    margin: 1rem 1.2rem; padding: 1rem 1.1rem;
    background: var(--accent-soft);
    border: 1px solid rgba(168,128,58,.2);
    border-radius: var(--radius-lg);
    display: flex; align-items: center; justify-content: space-between;
    cursor: pointer; text-align: left;
    font-family: var(--font-body);
    transition: all .15s ease;
  }
  .mobile-menu-wallet:hover {
    border-color: var(--accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(168,128,58,.12);
  }
  .mobile-menu-wallet-cta {
    font-size: .78rem; color: var(--accent); font-weight: 600;
  }

  .mobile-menu-section {
    padding: .5rem 1.2rem 1rem;
    border-bottom: 1px solid var(--border);
  }
  .mobile-menu-section:last-of-type { border-bottom: none; }
  .mobile-menu-section-title {
    font-family: 'JetBrains Mono', monospace;
    font-size: .6rem; letter-spacing: 2px;
    color: var(--muted); text-transform: uppercase;
    font-weight: 500;
    margin: .8rem 0 .4rem;
  }
  .mobile-menu-link {
    display: flex; align-items: center; gap: .8rem;
    width: 100%; padding: .85rem .25rem;
    border: none; background: none; cursor: pointer;
    font-family: var(--font-body); font-size: .95rem; font-weight: 500;
    color: var(--text); text-align: left;
    border-radius: var(--radius);
    transition: all .12s ease;
  }
  .mobile-menu-link span:first-child {
    font-size: 1.1rem; width: 24px; text-align: center;
    flex-shrink: 0;
  }
  .mobile-menu-link:hover {
    background: var(--deep);
    padding-left: .6rem;
  }
  .mobile-menu-link:active { transform: scale(.98); }
  .mobile-menu-link-primary {
    background: var(--accent);
    color: #fff;
    margin-top: .3rem;
    padding: .9rem 1rem !important;
    border-radius: var(--radius) !important;
  }
  .mobile-menu-link-primary:hover {
    background: var(--accent-h);
    color: #fff;
    padding-left: 1rem !important;
  }
  .mobile-menu-link-admin {
    color: var(--accent);
    font-weight: 600;
  }

  .mobile-menu-footer {
    padding: 1.2rem; text-align: center;
    margin-top: auto;
    border-top: 1px solid var(--border);
  }

  body.dark-mode .mobile-menu-drawer { background: var(--card); }
  body.dark-mode .mobile-menu-overlay { background: rgba(0,0,0,.6); }

  @media(max-width:480px) {
    .hero-center { padding: 2rem 1.4rem 1.5rem; }
    .hero-inner { padding: 0 .3rem; }
    .hero-h1 { font-size: 1.52rem; letter-spacing: -.03em; padding: 0 .3rem; }
    .hero-sub { font-size: .88rem; padding: 0 .5rem; }
    .hero-badge { padding: 0 .3rem; }
    .hero-search-wrap { padding: 0 .2rem; }
    .hero-search-bar { padding: .35rem .35rem .35rem .9rem; }
    .hero-search-input { font-size: .85rem; min-width: 0; }
    .hero-search-input::placeholder { font-size: .8rem; }
    .hero-search-btn { padding: .5rem .85rem; font-size: .78rem; }
    .hero-service-links { font-size: .78rem; gap: .35rem; padding: 0 .5rem; }
    .hero-service-link { font-size: .78rem; }
    .hero-trust { padding: 0 .5rem; font-size: .72rem; gap: .35rem; }
    .mode-grid { gap:.6rem; margin-top:1.5rem; }
    .mode-card { padding:1.4rem 1.2rem; }
    .stats-bar { flex-wrap:wrap; }
    .stat-item { min-width:50%; border-right:none !important; border-bottom:1px solid var(--border); }
    .ticker-bar { display:none; }
    /* v42 — Carte d'illustration du Dossier VIQ (hero) : durcissement mobile.
       La carte suit déjà la largeur de sa colonne grid (1fr en dessous de
       960px), mais son contenu interne (ligne véhicule+prix, mini-timeline)
       utilisait des tailles pensées pour desktop — on les resserre ici. */
    .hero-sim-card { padding: 1.2rem 1.1rem; max-width: 100%; overflow: hidden; }
    .hero-sim-card * { min-width: 0; }
    /* Ticker masqué sur mobile → la navbar reprend le haut et la compensation hero repasse à 64px */
    nav { top: 0; }
    .hero { padding-top: 64px; }
    .testi-grid { grid-template-columns:1fr; }
    .recent-art-grid { grid-template-columns:1fr; }
  }

  /* ─── ADMIN BLOG & TESTIMONIALS TABS ─── */
  .admin-editor {
    background:var(--card); border:1px solid var(--border); border-radius:10px; padding:1.2rem;
  }
  .admin-img-upload {
    border:2px dashed var(--border); border-radius:8px; padding:1.5rem;
    text-align:center; cursor:pointer; transition:border-color .2s;
  }
  .admin-img-upload:hover { border-color:var(--accent); }


  /* ═══ HERO SIMULATOR — style iautos-like en mode clair ═══════════════════
     Carte claire qui suit le thème du site (var(--white) sur fond clair),
     toggle pilule avec icônes, input intégré avec drapeau européen "F" à
     gauche et bouton accent à droite. L'accent orange VIQ reste le seul
     point coloré fort. */
  .hero-sim-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 18px;
    padding: 1.7rem 1.6rem;
    box-shadow: 0 24px 60px rgba(20,30,55,.10), 0 2px 8px rgba(20,30,55,.04);
    color: var(--text);
  }
  .hero-sim-head { margin-bottom: 1.2rem; }
  .hero-sim-title {
    font-family: var(--font-head); font-size: 1.35rem; font-weight: 800;
    color: var(--text); letter-spacing: -.02em; line-height: 1.15;
  }
  .hero-sim-sub {
    font-size: .82rem; color: var(--sub); margin-top: .35rem; line-height: 1.5;
  }
  /* ─── Toggle en pilule arrondie ─── */
  .hero-sim-tabs {
    display: flex; gap: 0;
    background: var(--deep);
    border: 1px solid var(--border);
    border-radius: 100px;
    padding: .3rem;
    margin-bottom: 1.3rem;
  }
  .hero-sim-tab {
    flex: 1; padding: .65rem 1rem; border: none; cursor: pointer;
    background: none; border-radius: 100px;
    font-family: var(--font-body); font-size: .82rem; font-weight: 600;
    color: var(--sub);
    display: inline-flex; align-items: center; justify-content: center; gap: .45rem;
    transition: all .18s cubic-bezier(.4,0,.2,1);
  }
  .hero-sim-tab svg { width: 16px; height: 16px; flex-shrink: 0; }
  .hero-sim-tab:hover { color: var(--text); }
  .hero-sim-tab.active {
    background: var(--accent);
    color: #FFFFFF;
    box-shadow: 0 2px 8px rgba(168,128,58,.30);
  }
  /* ─── Label uppercase au-dessus du champ ─── */
  .hero-sim-field-label {
    font-size: .68rem; font-weight: 700; color: var(--muted);
    text-transform: uppercase; letter-spacing: 1.2px;
    margin-bottom: .55rem;
  }
  /* ─── Plaque : input intégré avec bouton à droite ─── */
  .hero-sim-plate-row {
    display: flex; align-items: center;
    background: var(--deep);
    border: 1px solid var(--border);
    border-radius: 100px;
    padding: .35rem .35rem .35rem .35rem;
    overflow: hidden;
    transition: border-color .15s ease;
  }
  .hero-sim-plate-row:focus-within { border-color: var(--accent); }
  /* Conteneur interne : bande UE + champ, pour évoquer une vraie plaque */
  .hero-sim-plate-input {
    flex: 1; display: flex; align-items: center; min-width: 0;
    gap: .5rem; padding-left: .35rem;
  }
  .hero-sim-euband {
    display: inline-flex; align-items: center; justify-content: center;
    flex-shrink: 0; line-height: 0;
  }
  .hero-sim-euband svg { display: block; border-radius: 3px; }
  .hero-sim-plate-row input {
    flex: 1; border: none; outline: none; background: transparent;
    color: var(--text); font-family: var(--font-mono);
    font-size: 1.05rem; font-weight: 700; letter-spacing: 4px;
    padding: 0 1rem 0 .25rem; text-transform: uppercase; min-width: 0;
    text-align: center;
  }
  .hero-sim-plate-row input::placeholder { color: var(--muted); font-weight: 700; }
  /* Bouton "Estimer" intégré dans la pilule */
  .hero-sim-go {
    background: var(--accent); color: #FFFFFF;
    border: none; border-radius: 100px;
    padding: .7rem 1.4rem; font-size: .88rem; font-weight: 700;
    cursor: pointer; white-space: nowrap; flex-shrink: 0;
    display: inline-flex; align-items: center; gap: .4rem;
    transition: transform .12s ease, box-shadow .15s ease, opacity .15s ease, background .15s ease;
  }
  .hero-sim-go:hover:not(:disabled) {
    background: #B8650A;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(168,128,58,.35);
  }
  .hero-sim-go:disabled { cursor: not-allowed; opacity: .45; }
  .hero-sim-go svg { width: 14px; height: 14px; }
  /* ─── Grille modèle (mode "Par marque / modèle") ───
     Layout en 2 colonnes égales. Marque et Kilométrage prennent toute la
     largeur. Modèle et Année sont côte à côte avec des largeurs égales.
     Tous les champs ont la même hauteur (44px) pour un alignement parfait. */
  .hero-sim-model-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: .8rem .7rem;
  }
  .hero-sim-model-grid > div { min-width: 0; }
  .hero-sim-model-grid .full { grid-column: 1 / -1; }
  .hero-sim-model-grid select,
  .hero-sim-model-grid input {
    width: 100%;
    background: var(--deep); border: 1px solid var(--border); color: var(--text);
    padding: 0 .9rem; height: 44px; border-radius: 10px;
    font-size: .88rem; font-family: var(--font-body); outline: none;
    transition: border-color .15s ease;
    box-sizing: border-box;
  }
  .hero-sim-model-grid select:focus,
  .hero-sim-model-grid input:focus { border-color: var(--accent); }
  .hero-sim-model-grid select { cursor: pointer; }
  .hero-sim-model-grid input::placeholder { color: var(--muted); }
  .hero-sim-model-grid label {
    font-size: .66rem; font-weight: 700; color: var(--muted);
    text-transform: uppercase; letter-spacing: 1px;
    margin-bottom: .35rem; display: block;
  }
  .hero-sim-model-grid .hero-sim-go {
    width: 100%; justify-content: center;
    padding: 0; height: 46px; font-size: .92rem;
  }
  .hero-sim-error {
    font-size: .76rem; color: #9B2C2C; margin-top: .8rem;
    background: rgba(220,38,38,.06); border: 1px solid rgba(220,38,38,.18);
    border-radius: 8px; padding: .55rem .75rem;
  }
  .hero-sim-result {
    margin-top: 1.3rem; padding-top: 1.3rem;
    border-top: 1px dashed var(--border);
    animation: slideUp .35s ease;
  }
  .hero-sim-vehicle {
    font-size: .82rem; color: var(--green); font-weight: 700;
    margin-bottom: .5rem;
  }
  .hero-sim-price-main {
    font-family: var(--font-mono); font-size: 2.4rem; font-weight: 800;
    color: var(--accent); letter-spacing: -.03em; line-height: 1.05;
  }
  .hero-sim-price-main span { font-size: 1.3rem; font-weight: 700; }
  .hero-sim-range {
    font-size: .85rem; color: var(--sub); margin: .45rem 0 1rem;
  }
  .hero-sim-range strong { color: var(--text); font-family: var(--font-mono); }
  .hero-sim-metrics {
    display: grid; grid-template-columns: 1fr 1fr; gap: .7rem;
    margin-bottom: 1.1rem;
  }
  .hero-sim-metric {
    background: var(--deep); border: 1px solid var(--border);
    border-radius: 12px; padding: .7rem .85rem; text-align: center;
  }
  .hero-sim-metric-v {
    font-family: var(--font-mono); font-size: 1rem; font-weight: 800;
    color: var(--text);
  }
  .hero-sim-metric-l {
    font-size: .67rem; color: var(--muted); margin-top: .2rem;
    text-transform: uppercase; letter-spacing: .5px; font-weight: 600;
  }
  .hero-sim-full { width: 100%; padding: .85rem; font-size: .95rem; font-weight: 700; }
  .hero-sim-note {
    text-align: center; font-size: .72rem; color: var(--muted);
    margin-top: .7rem;
  }
  .hero-sim-services {
    display: flex; align-items: center; justify-content: center;
    flex-wrap: wrap; gap: .4rem .55rem;
    margin-top: 1.2rem; padding-top: 1rem;
    border-top: 1px solid var(--border);
  }
  .hero-sim-services-label {
    font-size: .72rem; color: var(--muted); font-weight: 600;
    width: 100%; text-align: center; margin-bottom: .1rem;
  }
  .hero-sim-service-link {
    background: none; border: none; cursor: pointer; padding: 0;
    font-family: var(--font-body); font-size: .8rem; font-weight: 600;
    color: var(--accent); transition: opacity .15s ease;
    text-decoration: underline; text-underline-offset: 3px;
    text-decoration-thickness: 1px;
  }
  .hero-sim-service-link:hover { opacity: .68; }
  .hero-sim-service-sep { color: var(--muted); font-size: .8rem; user-select: none; }

  /* HeroSimulator v2 — confirmation sans prix */
  .hero-sim-vin {
    display: flex; align-items: center; gap: .5rem; margin-top: .5rem;
    font-size: .8rem; color: var(--sub);
  }
  .hero-sim-vin span {
    font-size: .62rem; color: var(--muted); text-transform: uppercase;
    letter-spacing: .5px; font-weight: 700;
  }

  /* ═══ Chatbot VIQ.AI flottant ════════════════════════════════════════════
     Bouton rond en bas à droite. Remonté automatiquement quand le FAB
     "Estimer un VO / Trouver un VO" est visible (classe .above-fab), pour
     éviter le chevauchement — particulièrement critique sur mobile où le
     FAB occupe presque toute la largeur. */
  .viq-chat-fab {
    position: fixed;
    bottom: 1.5rem;
    right: 1.5rem;
    width: 56px; height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent), var(--accent-h));
    border: none;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 16px rgba(168,128,58,.4), 0 0 0 3px rgba(168,128,58,.1);
    z-index: 95;
    transition: bottom .3s cubic-bezier(.16,1,.3,1),
                transform .25s ease, box-shadow .25s ease;
    animation: chat-pulse 3s ease-in-out infinite;
  }
  .viq-chat-fab:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 24px rgba(168,128,58,.55), 0 0 0 4px rgba(168,128,58,.15);
  }
  .viq-chat-fab-badge {
    position: absolute;
    top: -4px; right: -4px;
    background: var(--green); color: #000;
    font-size: .5rem; font-family: var(--font-mono);
    font-weight: 700; border-radius: 8px;
    padding: .1rem .3rem;
    border: 2px solid var(--bg);
    letter-spacing: .5px;
  }
  /* Quand le FAB "Estimer un VO" est visible, on remonte le chatbot pour
     ne pas qu'il chevauche le FAB (centré, ~bottom 1.5rem). Sur desktop
     il y a la place horizontalement ; sur mobile le FAB s'étend presque
     toute la largeur — on remonte donc plus franchement sur petit écran. */
  .viq-chat-fab.above-fab { bottom: 5.5rem; }
  @media (max-width: 768px) {
    .viq-chat-fab.above-fab { bottom: 5.8rem; }
  }
  @media (max-width: 480px) {
    .viq-chat-fab { width: 50px; height: 50px; right: 1rem; bottom: 1rem; }
    .viq-chat-fab.above-fab { bottom: 5.2rem; }
  }

  /* ═══ FAB flottant — apparaît au scroll, hors du hero ═══════════════════
     Pilule claire flottante en bas, contenant 2 actions : "Estimer un VO"
     (action principale, accent orange) et "Trouver un VO" (sub).
     Visible UNIQUEMENT quand le hero a quitté le viewport (révélation au
     scroll, géré côté JS via IntersectionObserver). */
  .viq-fab {
    position: fixed; bottom: 1.5rem; left: 50%;
    z-index: 90;
    display: flex; align-items: center; gap: .35rem;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 100px;
    padding: .4rem .45rem;
    box-shadow: 0 12px 40px rgba(20,30,55,.12), 0 4px 12px rgba(20,30,55,.06);

    /* État initial caché : opacity 0, translaté en bas, non-cliquable.
       Le hero étant visible au chargement, le FAB n'apparaît qu'au scroll. */
    opacity: 0;
    transform: translate(-50%, 24px);
    pointer-events: none;
    transition: opacity .35s cubic-bezier(.16,1,.3,1),
                transform .35s cubic-bezier(.16,1,.3,1);
  }
  .viq-fab.visible {
    opacity: 1;
    transform: translate(-50%, 0);
    pointer-events: auto;
  }
  .viq-fab-btn {
    display: inline-flex; align-items: center; gap: .5rem;
    padding: .65rem 1.15rem;
    border: none; cursor: pointer; border-radius: 100px;
    font-family: var(--font-body); font-size: .85rem; font-weight: 700;
    transition: all .18s cubic-bezier(.4,0,.2,1);
    white-space: nowrap;
  }
  .viq-fab-btn svg { width: 16px; height: 16px; flex-shrink: 0; }
  .viq-fab-btn.primary {
    background: var(--accent); color: #FFFFFF;
  }
  .viq-fab-btn.primary:hover {
    background: #B8650A; transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(168,128,58,.35);
  }
  .viq-fab-btn.secondary {
    background: transparent; color: var(--text);
  }
  .viq-fab-btn.secondary:hover { color: var(--accent); }
  @media (max-width: 480px) {
    .viq-fab { bottom: 1rem; padding: .35rem .4rem; }
    .viq-fab-btn { padding: .6rem .9rem; font-size: .8rem; }
    .viq-fab-btn svg { width: 14px; height: 14px; }
  }
  .hero-sim-vin code {
    font-family: var(--font-mono); font-size: .78rem; font-weight: 700;
    color: var(--accent); background: var(--accent-soft);
    padding: .15rem .5rem; border-radius: 4px; letter-spacing: 1px;
  }
  .hero-sim-opening {
    display: flex; align-items: center; gap: .5rem;
    margin-top: .8rem; font-size: .85rem; font-weight: 600;
    color: var(--accent-h);
  }
  .hero-sim-spinner {
    width: 13px; height: 13px; border-radius: 50%;
    border: 2px solid var(--accent-soft); border-top-color: var(--accent);
    display: inline-block; animation: spin .7s linear infinite;
  }
  .hero-sim-trustline {
    margin-top: 1rem; font-size: .74rem; line-height: 1.5;
    color: var(--muted); text-align: center;
  }
  .hero-sim-algo {
    display: block; margin-top: .45rem; font-size: .66rem; line-height: 1.45;
    color: var(--muted); opacity: .85; font-style: italic;
  }


  /* ═══ HOME FAQ (brief §10) ════════════════════════════════════════════ */
  .home-faq-section {
    padding: 5.5rem 2rem;
    background: var(--white);
    border-top: 1px solid var(--border);
  }
  .home-faq-inner { max-width: 820px; margin: 0 auto; text-align: center; }
  .home-faq-intro {
    color: var(--sub); font-size: 1.05rem; line-height: 1.7;
    max-width: 620px; margin: 1rem auto 2.8rem;
  }
  .home-faq-list { text-align: left; }
  .home-faq-item {
    border: 1.5px solid var(--border); border-radius: 14px;
    margin-bottom: .8rem; background: var(--white);
    transition: border-color .15s ease, box-shadow .15s ease;
  }
  .home-faq-item.open {
    border-color: rgba(168,128,58,.35);
    box-shadow: 0 4px 16px rgba(168,128,58,.06);
  }
  .home-faq-q {
    width: 100%; display: flex; align-items: center; justify-content: space-between;
    gap: 1rem; padding: 1.15rem 1.4rem; cursor: pointer;
    background: none; border: none; text-align: left;
    font-family: var(--font-head); font-size: 1.05rem; font-weight: 600;
    color: var(--text); letter-spacing: -.01em;
  }
  .home-faq-chevron {
    font-family: var(--font-mono); font-size: 1.4rem; color: var(--accent);
    flex-shrink: 0; line-height: 1; width: 22px; text-align: center;
  }
  .home-faq-a {
    padding: 0 1.4rem 1.3rem; color: var(--sub);
    font-size: .96rem; line-height: 1.75;
    animation: slideUp .25s ease;
  }
  .home-faq-cta {
    display: flex; gap: .9rem; justify-content: center; flex-wrap: wrap;
    margin-top: 2.8rem;
  }

  @media (max-width: 960px) {
    .hero-sim-card { padding: 1.5rem 1.3rem; }
    .hero-sim-price-main { font-size: 2.1rem; }
    .home-faq-section { padding: 4rem 1.3rem; }
    .home-faq-q { font-size: .98rem; padding: 1rem 1.1rem; }
  }
  @media (max-width: 560px) {
    .hero-sim-plate-row { flex-direction: column; }
    .hero-sim-go { width: 100%; }
    .hero-sim-model-grid { grid-template-columns: 1fr; }
  }


  /* ═══ PAGES SEO (piliers / métiers) ═══════════════════════════════════ */
  .seo-page { min-height: 100vh; background: var(--white); display: flex; flex-direction: column; }
  .seo-nav {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 2.5rem; height: 64px;
    border-bottom: 1px solid var(--border);
    background: var(--white); position: sticky; top: 0; z-index: 200;
  }
  .seo-nav-center { display: flex; gap: .3rem; }
  @media (max-width: 860px) { .seo-nav-center { display: none; } .seo-nav { padding: 0 1.2rem; } }
  .seo-main { flex: 1; max-width: 880px; margin: 0 auto; padding: 2.5rem 1.5rem 4rem; width: 100%; }

  .seo-breadcrumb { font-size: .82rem; color: var(--muted); margin-bottom: 1.8rem; display: flex; flex-wrap: wrap; gap: .3rem; align-items: center; }
  .seo-bc-link { background: none; border: none; cursor: pointer; color: var(--accent); font-size: .82rem; padding: 0; font-family: var(--font-body); }
  .seo-bc-link:hover { text-decoration: underline; }
  .seo-bc-current { color: var(--sub); font-weight: 600; }
  .seo-bc-sep { color: var(--border-strong); }

  .seo-article { animation: slideUp .3s ease; }
  .seo-h1 {
    font-family: var(--font-head); font-size: clamp(1.9rem, 3.6vw, 2.8rem);
    font-weight: 800; line-height: 1.18; letter-spacing: -.02em;
    color: var(--text); margin: .6rem 0 1.2rem;
  }
  .seo-intro {
    font-size: 1.12rem; line-height: 1.75; color: var(--sub);
    margin-bottom: 2rem; padding-bottom: 1.8rem;
    border-bottom: 1px solid var(--border);
  }
  .seo-section { margin: 2.6rem 0; }
  .seo-h2 {
    font-family: var(--font-head); font-size: 1.55rem; font-weight: 700;
    color: var(--text); letter-spacing: -.015em; margin-bottom: 1rem;
  }
  .seo-h3 {
    font-family: var(--font-head); font-size: 1.12rem; font-weight: 700;
    color: var(--text); margin: 1.4rem 0 .5rem;
  }
  .seo-p { font-size: 1.02rem; line-height: 1.8; color: var(--sub); margin-bottom: 1rem; }
  .seo-list { list-style: none; padding: 0; margin: 0; }
  .seo-list li {
    position: relative; padding: .55rem 0 .55rem 1.7rem;
    font-size: 1.02rem; line-height: 1.65; color: var(--sub);
    border-bottom: 1px solid var(--border);
  }
  .seo-list li::before {
    content: "→"; position: absolute; left: 0; top: .55rem;
    color: var(--accent); font-weight: 700;
  }
  .seo-list-num { counter-reset: seo; }
  .seo-list-num li::before {
    counter-increment: seo; content: counter(seo);
    font-family: var(--font-mono); font-size: .8rem; top: .7rem;
    background: var(--accent-soft); color: var(--accent-h);
    width: 20px; height: 20px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
  }
  .seo-list-num li { padding-left: 2rem; }

  .seo-inline-cta { padding: .85rem 1.8rem; font-size: .98rem; font-weight: 700; margin: .5rem 0 1rem; }

  .seo-stats {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem;
    margin: 1.6rem 0 2rem;
  }
  .seo-stat {
    background: var(--deep); border: 1px solid var(--border);
    border-radius: 14px; padding: 1.1rem; text-align: center;
  }
  .seo-stat-v {
    font-family: var(--font-mono); font-size: 1.5rem; font-weight: 800;
    color: var(--accent); letter-spacing: -.02em;
  }
  .seo-stat-l { font-size: .76rem; color: var(--muted); margin-top: .3rem; text-transform: uppercase; letter-spacing: .5px; font-weight: 600; }
  @media (max-width: 560px) { .seo-stats { grid-template-columns: 1fr; } }

  .seo-testimonial {
    background: var(--deep); border: 1px solid var(--border);
    border-left: 3px solid var(--accent); border-radius: 14px;
    padding: 1.6rem 1.8rem;
  }
  .seo-testimonial-stars { color: var(--accent); letter-spacing: 2px; margin-bottom: .7rem; font-size: .95rem; }
  .seo-testimonial-q {
    font-family: var(--font-head); font-size: 1.18rem; line-height: 1.6;
    color: var(--text); font-weight: 500; margin-bottom: 1rem;
  }
  .seo-testimonial-meta { font-size: .9rem; color: var(--sub); }
  .seo-testimonial-meta strong { color: var(--text); }

  .seo-faq-item { padding: 1rem 0; border-bottom: 1px solid var(--border); }
  .seo-faq-item:last-child { border-bottom: none; }
  .seo-faq-item .seo-h3 { margin-top: 0; }

  .seo-related {
    margin: 3rem 0 1rem; padding: 1.6rem 1.8rem;
    background: var(--accent-soft); border-radius: 16px;
  }
  .seo-related-title {
    font-size: .78rem; font-weight: 700; color: var(--accent-h);
    text-transform: uppercase; letter-spacing: 1px; margin-bottom: .9rem;
  }
  .seo-related-links { display: flex; flex-direction: column; gap: .55rem; align-items: flex-start; }
  .seo-related-link {
    background: none; border: none; cursor: pointer; padding: 0;
    font-family: var(--font-body); font-size: 1rem; font-weight: 600;
    color: var(--accent-h); text-align: left;
  }
  .seo-related-link:hover { text-decoration: underline; }

  /* CTA final pages SEO — fond accent */
  .seo-final-cta { background: var(--accent); padding: 4rem 1.5rem; }
  .seo-final-inner { max-width: 720px; margin: 0 auto; text-align: center; }
  .seo-final-h2 {
    font-family: var(--font-head); font-size: clamp(1.6rem, 3.2vw, 2.2rem);
    font-weight: 800; color: #fff; letter-spacing: -.02em; line-height: 1.25;
  }
  .seo-final-sub { color: rgba(255,255,255,.92); font-size: 1.05rem; margin: 1rem 0 2rem; line-height: 1.6; }
  .seo-final-btns { display: flex; gap: .9rem; justify-content: center; flex-wrap: wrap; }
  .seo-final-primary {
    background: #fff; color: var(--accent-h); padding: .9rem 1.9rem;
    font-size: .98rem; font-weight: 700; border: none;
  }
  .seo-final-primary:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(0,0,0,.18); }
  .seo-final-secondary {
    background: rgba(255,255,255,.14); color: #fff; padding: .9rem 1.8rem;
    font-size: .98rem; font-weight: 600; border: 1px solid rgba(255,255,255,.4);
  }
  .seo-final-secondary:hover { background: rgba(255,255,255,.22); }

  .seo-footer { background: var(--text); color: rgba(255,255,255,.7); padding: 3rem 1.5rem 1.5rem; }
  .seo-footer-inner {
    max-width: 1080px; margin: 0 auto;
    display: grid; grid-template-columns: 1.6fr 1fr 1fr 1fr; gap: 2rem;
  }
  .seo-footer .logo { color: #fff; }
  .seo-footer-col p { font-size: .88rem; line-height: 1.6; margin-top: .5rem; color: rgba(255,255,255,.55); }
  .seo-footer-title { color: #fff; font-weight: 700; font-size: .92rem; margin-bottom: .9rem; }
  .seo-footer-col button {
    display: block; background: none; border: none; cursor: pointer;
    color: rgba(255,255,255,.62); font-size: .88rem; padding: .28rem 0;
    font-family: var(--font-body); text-align: left;
  }
  .seo-footer-col button:hover { color: #fff; }
  .seo-footer-bottom {
    max-width: 1080px; margin: 2.5rem auto 0; padding-top: 1.5rem;
    border-top: 1px solid rgba(255,255,255,.12);
    font-size: .8rem; color: rgba(255,255,255,.45); text-align: center;
  }
  @media (max-width: 760px) { .seo-footer-inner { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 480px) { .seo-footer-inner { grid-template-columns: 1fr; } }


  /* ═══ PAGES COTES (/cote/...) ═════════════════════════════════════════ */
  .cote-grid {
    display: grid; grid-template-columns: repeat(2, 1fr);
    gap: .9rem; margin-top: 1.2rem;
  }
  .cote-card {
    display: flex; flex-direction: column; gap: .35rem;
    text-align: left; cursor: pointer;
    background: var(--white); border: 1.5px solid var(--border);
    border-radius: 14px; padding: 1.1rem 1.2rem;
    transition: border-color .15s ease, transform .15s ease, box-shadow .15s ease;
    font-family: var(--font-body);
  }
  .cote-card:hover {
    border-color: rgba(168,128,58,.4); transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(168,128,58,.08);
  }
  .cote-card-label {
    font-family: var(--font-head); font-size: 1.08rem; font-weight: 700;
    color: var(--text);
  }
  .cote-card-motors {
    font-size: .78rem; color: var(--muted); font-weight: 500;
  }
  .cote-card-arrow {
    font-size: .82rem; font-weight: 700; color: var(--accent);
    margin-top: .2rem;
  }
  @media (max-width: 560px) { .cote-grid { grid-template-columns: 1fr; } }

  .cote-motor-row { display: flex; flex-wrap: wrap; gap: .55rem; margin-top: .8rem; }
  .cote-motor-pill {
    background: var(--deep); border: 1.5px solid var(--border);
    color: var(--sub); border-radius: 50px; padding: .5rem 1.1rem;
    font-family: var(--font-body); font-size: .86rem; font-weight: 600;
    cursor: pointer; transition: all .15s ease;
  }
  .cote-motor-pill:hover { border-color: rgba(168,128,58,.4); color: var(--text); }
  .cote-motor-pill.active {
    background: var(--accent-soft); border-color: var(--accent);
    color: var(--accent-h);
  }

`;

// ─── Mock data helpers ────────────────────────────────────────────────────────
const mockVINResult = (vin) => ({
  vin: vin.toUpperCase(),
  fuel: "Essence",
  owners: 2,
  ctOk: true,
  make: "Volkswagen",
  model: "Golf VII GTI",
  year: 2019,
  engine: "2.0 TSI 245 ch",
  fuel: "Essence",
  transmission: "DSG 7",
  color: "Blanc Pur",
  mileage: 87400,
  owners: 2,
  score: 82,
  status: "ok",
  accidents: 0,
  stolen: false,
  mileageOk: true,
  ctOk: true,
  ctDate: "12/2025",
  lastService: "02/2024 – 85 000 km",
  registrations: [
    { date: "03/2019", text: "1ère mise en circulation – Allemagne (DE)" },
    { date: "09/2020", text: "Import France – Immatriculée en région IDF" },
    { date: "11/2022", text: "Changement de propriétaire – Paris 75" },
    { date: "02/2024", text: "Entretien complet concessionnaire agréé VW" },
  ],
  services: [
    { date: "03/2020 – 20 000 km", text: "Vidange + filtres" },
    { date: "09/2021 – 45 000 km", text: "Courroie de distribution + kit embrayage" },
    { date: "06/2022 – 65 000 km", text: "Plaquettes + disques AV/AR" },
    { date: "02/2024 – 85 000 km", text: "Vidange + bougies + inspection complète" },
  ],
  marketValue: "22 500 €",
  alternatives: [
    { make: "Renault Mégane RS", year: 2018, km: 92000, price: "18 900 €", platform: "AutoScout24", match: 94, img: "🚗" },
    { make: "Honda Civic Type R", year: 2017, km: 78000, price: "24 500 €", platform: "LeBonCoin", match: 88, img: "🏎️" },
    { make: "Ford Focus ST", year: 2019, km: 95000, price: "17 200 €", platform: "Marketplace", match: 79, img: "🚙" },
  ],
});

const mockBuyResults = (answers) => ([
  { make: "Renault Mégane IV", model: "1.3 TCe 140", year: 2021, km: 54000, price: "15 900 €", platform: "AutoScout24", match: 97, fuel: "Essence", tags: ["Pro certifié", "Garantie 6 mois"], img: "🚗" },
  { make: "Peugeot 308", model: "1.2 PureTech 130", year: 2020, km: 67000, price: "13 400 €", platform: "LeBonCoin", match: 91, fuel: "Essence", tags: ["Particulier", "CT OK"], img: "🚙" },
  { make: "Volkswagen Golf VIII", model: "1.5 eTSI 130", year: 2022, km: 38000, price: "19 800 €", platform: "Marketplace", match: 86, fuel: "Hybride légère", tags: ["Pro certifié", "0 accident"], img: "🏎️" },
]);

const mockSellResult = (answers) => ({
  vehicleEstimate: { low: "12 400 €", mid: "14 200 €", high: "15 800 €" },
  platforms: [
    {
      name: "LeBonCoin",
      code: "LBC",
      recommended: true,
      score: 9.2,
      reason: "Fort trafic particulier, idéal pour votre profil. Ventes rapides sur ce segment dans votre région.",
      price: "14 500 – 15 800 €",
      delay: "8 – 15 jours",
    },
    {
      name: "AutoScout24",
      code: "AS24",
      recommended: false,
      score: 7.8,
      reason: "Bonne visibilité auprès des professionnels. Prix souvent plus élevé mais délai de vente plus long.",
      price: "13 900 – 15 200 €",
      delay: "15 – 30 jours",
    },
    {
      name: "Marketplace Facebook",
      code: "FB",
      recommended: false,
      score: 6.5,
      reason: "Audience locale importante mais moins qualifiée. Idéal pour une vente rapide à prix bas.",
      price: "12 400 – 13 500 €",
      delay: "3 – 10 jours",
    },
  ],
  tip: "Avec de belles photos et un CT récent mentionné, vous pouvez viser la fourchette haute.",
});

// ─── QUESTIONS ────────────────────────────────────────────────────────────────
// Année en cours — calculée dynamiquement pour que les bornes max restent
// toujours à jour sans intervention (ex : passera à 2027 en janvier 2027).
const CURRENT_YEAR = new Date().getFullYear();

const BUY_QUESTIONS = [
  { id: "purpose",  text: "Type de recherche d'achat",                            type: "radio", options: ["Achat pour usage", "Achat pour revente"], default: "Achat pour usage",
    info: "« Achat pour usage » : vous cherchez un véhicule pour vous, le moteur optimise selon vos besoins. « Achat pour revente » (professionnels / marchands) : VehicleIQ calcule en plus, pour chaque véhicule proposé, la marge nette potentielle à la revente (cote de revente France − prix d'achat − malus − transport), comme un scan d'opportunité." },
  { id: "budget",   text: "Quel est votre budget maximum ?",                       type: "range", min: 2000, max: 300000, unit: " €", step: 500, default: 15000,
    info: "Indiquez le montant maximum que vous êtes prêt à dépenser, tout compris (véhicule + éventuels frais de carte grise). Un budget réaliste permet à VIQ.AI® de vous proposer des véhicules que vous pouvez réellement acquérir. Sous-estimer votre budget peut vous faire manquer de bonnes opportunités ; le surestimer peut orienter les recommandations vers des véhicules hors de portée." },
  { id: "fuel",     text: "Quelle motorisation recherchez-vous ?",                 type: "radio", options: ["Essence", "Diesel", "Hybride", "Hybride rechargeable", "Électrique", "Peu importe"],
    info: "La motorisation est l'un des critères les plus importants — elle détermine les coûts d'usage, la disponibilité des pièces et la valeur de revente. Diesel : économique sur longs trajets mais pénalisé en ZFE. Hybride : idéal en usage mixte. Électrique : 0 frais carburant mais nécessite une infrastructure de recharge. Soyez honnête sur vos habitudes de conduite réelles." },
  { id: "segment",  text: "Quel type de véhicule vous correspond ?",               type: "radio", options: ["Citadine", "Berline / Compacte", "Coupé", "SUV / Crossover", "Break / Familiale", "Utilitaire", "Peu importe"],
    info: "Choisissez le segment qui correspond à votre style de vie. Une citadine sera difficile à revendre en zone rurale ; un SUV consommera davantage en ville. Ne choisissez pas un segment par aspiration mais par besoin réel : nombre de passagers habituels, gabarit des routes que vous empruntez, facilité de stationnement." },
  { id: "brands",   text: "Entrez jusqu'à 3 marques et modèles",                  type: "text",  placeholder: "Ex : Porsche 911 GT3, Mercedes Classe E… — laisser vide = toutes marques",
    info: "Indiquez jusqu'à 3 marques et modèles précis qui vous intéressent (ex : Porsche 911 GT3, Mercedes Classe E). Plus vous êtes précis, plus les recommandations sont ciblées. Laisser ce champ vide permet à VIQ.AI® de vous recommander des véhicules indépendamment de la marque, ce qui peut révéler des opportunités inattendues." },
  { id: "city",     text: "Dans quelle ville / zone recherchez-vous ?",            type: "text",  placeholder: "Ex : Paris, Lyon, France entière…", optional: true,
    info: "Indiquez la ville ou la zone géographique où vous souhaitez acheter. VIQ.AI® priorise les véhicules proches de vous pour limiter les déplacements, mais peut élargir la recherche si vous laissez « France entière ». La proximité facilite l'essai, l'inspection et réduit les frais de convoyage." },
  { id: "year",     text: "Année minimum souhaitée ?",                             type: "range", min: 2006, max: CURRENT_YEAR, unit: "", default: 2019,
    info: "Plus le millésime minimum est récent, plus vous êtes protégé contre l'obsolescence technique et les dépenses de maintenance imprévues. Un véhicule post-2018 bénéficie généralement des dernières normes Euro 6d et d'une meilleure connectivité. En dessous de 2015, prévoyez un budget entretien plus conséquent." },
  { id: "km",       text: "Kilométrage maximum accepté ?",                         type: "range", min: 10000, max: 250000, unit: " km", step: 5000, default: 80000,
    info: "Le kilométrage est un indicateur d'usure, mais pas le seul. Un véhicule diesel à 150 000 km bien entretenu peut valoir mieux qu'un essence à 60 000 km mal entretenu. La moyenne française est de 15 000 km/an : ajustez ce seuil à l'âge du véhicule. Indiquez un chiffre réaliste et non idéalisé." },
  { id: "gearbox",  text: "Type de boîte de vitesses ?",                           type: "radio", options: ["Manuelle", "Automatique / CVT", "Peu importe"],
    info: "La boîte automatique est plus confortable en ville mais coûte 1 000 à 3 000 € de plus à l'achat et peut être plus onéreuse à réparer. La boîte manuelle est plus fiable mécaniquement et moins consommatrice. Soyez sincère : si vous n'êtes pas à l'aise avec la boîte manuelle, optez pour l'automatique — c'est un facteur de sécurité." },
  { id: "usage",    text: "Quel sera l'usage principal ?",                         type: "radio", options: ["Ville courtes distances", "Mixte ville / route", "Grands trajets / Autoroute", "Professionnel"], showIf: (a) => a.purpose !== "Achat pour revente",
    info: "Votre usage réel détermine la motorisation optimale et le type de véhicule adapté. Soyez précis : si vous faites 80% de trajets urbains mais que vous partez en vacances 2 fois par an, votre usage est urbain. Un mauvais choix ici peut entraîner des coûts d'exploitation 30 à 50% supérieurs." },
  { id: "sellers",  text: "Vous achetez chez ?",                                   type: "radio", options: ["Particulier uniquement", "Professionnel uniquement", "Les deux"],
    info: "Acheter chez un professionnel offre une garantie légale de 2 ans et un recours plus solide en cas de litige. Chez un particulier, le prix est souvent plus bas mais sans garantie. Si vous n'êtes pas mécanicien ou n'avez pas accès à un expert, privilégiez les professionnels ou exigez un rapport VehicleIQ avant tout achat particulier." },
  { id: "import",   text: "Véhicule importé (Allemagne, Belgique...) accepté ?",   type: "radio", options: ["Oui", "Non"],
    info: "Les véhicules importés (Allemagne, Belgique, Luxembourg) sont souvent moins chers de 10 à 20% pour le même modèle. Attention : la contre-visite CT peut révéler des divergences avec les normes françaises, et les délais de livraison de la carte grise peuvent être plus longs. Choisissez 'Oui' si vous êtes prêt à gérer ces démarches administratives supplémentaires." },
  // ── Questions SPÉCIFIQUES revente (affichées seulement si purpose = revente) ──
  { id: "resale_region", text: "Région d'immatriculation (pour le malus / carte grise)", type: "radio", options: ["Île-de-France", "Auvergne-Rhône-Alpes", "Hauts-de-France", "Nouvelle-Aquitaine", "Occitanie", "Grand Est", "Provence-Alpes-Côte d'Azur", "Autre"], showIf: (a) => a.purpose === "Achat pour revente", default: "Hauts-de-France",
    info: "La région détermine le tarif du cheval fiscal pour le calcul du coût d'immatriculation, qui entre dans le calcul de la marge nette de revente." },
  { id: "resale_transport", text: "Coût de transport estimé (€) — convoyage / import", type: "range", min: 0, max: 3000, unit: " €", step: 50, default: 500, showIf: (a) => a.purpose === "Achat pour revente",
    info: "Estimez le coût pour rapatrier le véhicule (convoyage France ou import UE). Il sera déduit de la marge nette calculée pour chaque véhicule proposé. Laissez 0 si vous récupérez le véhicule vous-même." },
];

// ─── Catalogue des finitions par marque/modèle ──────────────────────────────
// Le format : la clé est la marque normalisée (lowercase), la valeur est :
//   - soit un Array<string> de finitions génériques pour toute la marque
//   - soit un objet { default: [...], "modele1": [...], "modele2": [...] } pour finitions par modèle
// Helper getFinitions(makeStr) résout selon le contenu de la saisie.
const FINITION_CATALOG = {
  // Premium allemand
  "bmw": {
    default: ["Lounge", "Business", "M Sport", "M Performance", "Luxury", "xLine", "Edition Black Shadow", "M Competition", "Pack M"],
    "serie 1": ["Lounge", "Business", "M Sport", "Edition M", "M135i xDrive"],
    "serie 3": ["Lounge", "Business", "M Sport", "M340i xDrive", "M3 Competition"],
    "serie 5": ["Business", "M Sport", "Luxury", "Edition Touring", "M550i", "M5 Competition"],
    "x1": ["Business", "M Sport", "xLine", "Premiere Edition"],
    "x3": ["Business", "M Sport", "xLine", "Luxury", "M40i", "M Competition"],
    "x5": ["Business", "M Sport", "xLine", "Luxury", "M50i", "M Competition"],
    "i4": ["eDrive 35", "eDrive 40", "M50"],
    "i5": ["eDrive 40", "M60 xDrive"],
    "ix": ["xDrive 40", "xDrive 50", "M60"],
    "m8": ["Coupé", "Cabriolet", "Gran Coupé", "Competition"],
  },
  "mercedes": {
    default: ["Business Line", "Style", "AMG Line", "AMG", "AMG S", "Edition C", "Avantgarde", "Progressive", "Sport"],
    "classe a": ["Business Line", "Progressive Line", "AMG Line", "AMG 35", "AMG 45 S"],
    "classe c": ["Business Line", "Avantgarde", "AMG Line", "AMG 43", "AMG 63", "AMG 63 S"],
    "classe e": ["Business Line", "Avantgarde", "AMG Line", "AMG 53", "AMG 63 S"],
    "classe s": ["Avantgarde", "AMG Line", "AMG 63", "AMG 65", "Maybach"],
    "gla": ["Business Line", "Progressive", "AMG Line", "AMG 35", "AMG 45 S"],
    "glc": ["Business Line", "Avantgarde", "AMG Line", "AMG 43", "AMG 63 S"],
    "gle": ["Business Line", "Avantgarde", "AMG Line", "AMG 53", "AMG 63 S"],
    "eqa": ["Progressive", "AMG Line", "Edition 1"],
    "eqc": ["Edition 1886", "AMG Line"],
    "eqs": ["AMG Line", "Maybach"],
  },
  "audi": {
    default: ["Design", "S line", "Black Edition", "Business Line", "Avus", "Edition Plus", "RS", "Quattro"],
    "a1": ["Design", "S line", "Black Edition", "Citycarver"],
    "a3": ["Design", "S line", "Black Edition", "S3", "RS 3"],
    "a4": ["Design", "S line", "Black Edition", "Avant", "S4", "RS 4 Avant"],
    "a6": ["Design", "S line", "Black Edition", "Avant", "Allroad", "S6", "RS 6 Avant"],
    "q3": ["Design", "S line", "Black Edition", "Sportback", "RS Q3"],
    "q5": ["Design", "S line", "Black Edition", "Sportback", "SQ5", "RS Q5"],
    "q7": ["Design", "S line", "Black Edition", "SQ7", "RS Q7"],
    "q8": ["Design", "S line", "Black Edition", "SQ8", "RS Q8"],
    "e-tron": ["50 quattro", "55 quattro", "S quattro", "Sportback", "GT", "RS GT"],
  },
  "porsche": {
    default: ["Base", "T", "S", "GTS", "Turbo", "Turbo S", "Carrera", "Carrera S", "Carrera GTS", "Carrera 4", "Carrera 4S"],
    "911": ["Carrera", "Carrera T", "Carrera S", "Carrera 4", "Carrera 4S", "Carrera GTS", "Targa 4", "Targa 4S", "Targa 4 GTS", "Turbo", "Turbo S", "GT3", "GT3 RS", "GT3 Touring", "Sport Classic", "Dakar"],
    "cayenne": ["Base", "E-Hybrid", "S", "S E-Hybrid", "GTS", "Turbo", "Turbo S E-Hybrid", "Turbo GT"],
    "macan": ["Base", "T", "S", "GTS", "Turbo", "Electric"],
    "panamera": ["Base", "4", "4 E-Hybrid", "4S", "GTS", "Turbo S", "Turbo S E-Hybrid", "Turismo"],
    "taycan": ["Base", "4S", "GTS", "Turbo", "Turbo S", "Cross Turismo", "Sport Turismo"],
  },

  // Premium français
  "renault": {
    default: ["Life", "Evolution", "Equilibre", "Techno", "Iconic", "Esprit Alpine", "RS Line", "Initiale Paris"],
    "clio": ["Evolution", "Equilibre", "Techno", "Esprit Alpine", "RS Line"],
    "megane": ["Equilibre", "Techno", "Iconic", "Esprit Alpine"],
    "captur": ["Evolution", "Equilibre", "Techno", "Esprit Alpine", "Iconic"],
    "arkana": ["Evolution", "Equilibre", "Techno", "RS Line", "Esprit Alpine"],
    "austral": ["Equilibre", "Techno", "Iconic", "Esprit Alpine"],
    "espace": ["Techno", "Esprit Alpine", "Iconic"],
    "scenic": ["Techno", "Esprit Alpine", "Iconic"],
    "r5": ["Evolution", "Techno", "Iconic Cinq"],
    "zoe": ["Life", "Equilibre", "Techno", "Iconic"],
  },
  "peugeot": {
    default: ["Active", "Active Pack", "Allure", "Allure Pack", "GT", "GT Premium", "GT Line", "Style", "Roadtrip"],
    "208": ["Like", "Active", "Active Pack", "Allure", "Allure Pack", "GT"],
    "2008": ["Active", "Active Pack", "Allure", "Allure Pack", "GT"],
    "308": ["Active Pack", "Allure", "Allure Pack", "GT", "GT Hybride"],
    "3008": ["Allure", "Allure Pack", "GT", "GT Premium", "Hybrid4"],
    "5008": ["Active Pack", "Allure", "Allure Pack", "GT", "GT Premium"],
    "408": ["Allure", "Allure Pack", "GT"],
    "508": ["Active", "Allure", "Allure Pack", "GT", "GT Line", "Peugeot Sport Engineered"],
  },
  "citroen": {
    default: ["Live", "Live Pack", "Feel", "Shine", "Plus", "Max", "C-Series", "Carlinea"],
    "c3": ["Live", "Feel", "Shine", "Max", "Plus"],
    "c4": ["You", "Feel", "Shine", "Max"],
    "c5 aircross": ["Feel", "Shine", "C-Series", "Max"],
  },
  "ds": {
    default: ["So Chic", "Performance Line", "Performance Line+", "Rivoli", "Opera", "Bastille"],
    "ds 3": ["So Chic", "Performance Line", "Performance Line+", "Rivoli", "Opera"],
    "ds 4": ["Bastille+", "Rivoli", "Performance Line", "Performance Line+", "Opera"],
    "ds 7": ["Bastille", "Performance Line", "Rivoli", "Opera"],
    "ds 9": ["Performance Line", "Rivoli", "Opera", "Bastille"],
  },

  // Generaliste eu / asie
  "volkswagen": {
    default: ["Trendline", "Comfortline", "Highline", "Life", "Style", "Elegance", "R-Line", "R", "GTI", "GTD", "GTE", "GTX"],
    "polo": ["Life", "Style", "R-Line", "GTI"],
    "golf": ["Life", "Style", "R-Line", "GTI", "GTI Clubsport", "GTD", "GTE", "R"],
    "passat": ["Life", "Elegance", "R-Line"],
    "tiguan": ["Life", "Elegance", "R-Line", "R"],
    "id.3": ["Pro", "Pro S", "Tour", "GTX"],
    "id.4": ["Pro", "Pro Performance", "GTX"],
    "id.5": ["Pro Performance", "GTX"],
    "id.7": ["Pro", "GTX Tourer"],
  },
  "skoda": {
    default: ["Active", "Ambition", "Style", "L&K", "Selection", "RS", "Sportline"],
    "octavia": ["Active", "Ambition", "Style", "L&K", "RS", "Sportline"],
    "superb": ["Ambition", "Style", "L&K", "Sportline"],
    "kamiq": ["Active", "Ambition", "Style", "Sportline"],
    "karoq": ["Active", "Ambition", "Style", "Sportline"],
    "kodiaq": ["Active", "Ambition", "Style", "L&K", "Sportline", "RS"],
    "enyaq": ["Loft", "Lodge", "Suite", "Sportline", "RS"],
  },
  "seat": {
    default: ["Reference", "Style", "FR", "FR Black Edition", "Xcellence", "Cupra"],
    "ibiza": ["Reference", "Style", "FR", "Xcellence"],
    "leon": ["Style", "FR", "FR First Edition", "Xcellence", "Cupra"],
  },
  "cupra": ["Born", "Leon", "Formentor", "Ateca", "Tavascan", "VZ", "VZ1", "VZ2", "VZ3"],
  "toyota": {
    default: ["Active", "Dynamic", "Design", "Lounge", "Collection", "Premium", "GR Sport", "GR"],
    "yaris": ["Design", "Dynamic", "Active", "Collection", "Premiere Edition", "GR"],
    "yaris cross": ["Active", "Dynamic", "Collection", "GR Sport", "Premiere"],
    "corolla": ["Active", "Dynamic", "Design", "Collection", "GR Sport"],
    "rav4": ["Active", "Dynamic Business", "Collection", "Premiere", "GR Sport"],
    "c-hr": ["Active", "Dynamic", "Collection", "GR Sport", "Premiere"],
    "bz4x": ["Origin", "Pure", "Premiere Edition"],
  },
  "hyundai": {
    default: ["Pure", "Intuitive", "Creative", "Executive", "Initia", "N Line", "N"],
    "tucson": ["Intuitive", "Creative", "Executive", "N Line"],
    "kona": ["Intuitive", "Creative", "Executive", "N Line"],
    "ioniq 5": ["Intuitive", "Creative", "Executive", "N"],
    "ioniq 6": ["Intuitive", "Creative", "Executive"],
  },
  "kia": {
    default: ["Motion", "Active", "Active Business", "Design", "GT-Line", "GT-Line Premium", "GT"],
    "picanto": ["Motion", "Active", "GT-Line"],
    "ceed": ["Motion", "Active", "Design", "GT-Line", "GT"],
    "sportage": ["Motion", "Active Business", "Design", "GT-Line", "GT-Line Premium"],
    "niro": ["Motion", "Active", "Design", "GT-Line"],
    "ev6": ["Air", "Earth", "GT-Line", "GT"],
  },
  "nissan": {
    default: ["Visia", "Acenta", "N-Connecta", "Tekna", "Tekna+", "N-Design", "Nismo"],
    "juke": ["Acenta", "N-Connecta", "Tekna", "N-Design"],
    "qashqai": ["Visia", "Acenta", "N-Connecta", "Tekna", "Tekna+", "N-Design"],
    "leaf": ["Acenta", "N-Connecta", "Tekna", "Tekna Cuir"],
    "ariya": ["Engage", "Evolve", "Evolve+"],
  },
  "honda": {
    default: ["Comfort", "Elegance", "Executive", "Sport", "Advance"],
    "civic": ["Elegance", "Sport", "Advance", "Type R"],
    "hr-v": ["Elegance", "Advance", "Advance Style"],
    "cr-v": ["Elegance", "Advance", "Advance Tech"],
    "jazz": ["Executive", "Crosstar Advance"],
    "e": ["Advance"],
  },
  "mazda": {
    default: ["Prime-Line", "Centre-Line", "Selection", "Homura", "Exclusive-Line", "Takumi"],
    "cx-30": ["Prime-Line", "Centre-Line", "Homura", "Exclusive-Line", "Takumi"],
    "cx-5": ["Prime-Line", "Centre-Line", "Homura", "Exclusive-Line", "Takumi"],
    "mx-30": ["Prime-Line", "Exclusive-Line", "Takumi"],
    "mx-5": ["Prime-Line", "Selection", "Homura"],
  },
  "fiat": {
    default: ["Pop", "Easy", "Lounge", "Sport", "Cult", "La Prima", "RED"],
    "500": ["Pop", "Cult", "La Prima", "RED", "Icon"],
    "panda": ["Pop", "Easy", "City Life", "Cross"],
    "tipo": ["Pop", "Easy", "City Life", "Sport", "Cross"],
  },
  "alfa romeo": {
    default: ["Super", "Sprint", "Veloce", "Ti", "Competizione", "Quadrifoglio", "Estrema"],
    "giulia": ["Sprint", "Ti", "Veloce", "Competizione", "Quadrifoglio"],
    "stelvio": ["Sprint", "Ti", "Veloce", "Competizione", "Quadrifoglio"],
    "tonale": ["Sprint", "Ti", "Veloce", "Edizione Speciale"],
  },

  // Premium UK
  "jaguar": {
    default: ["S", "R-Dynamic S", "R-Dynamic SE", "R-Dynamic HSE", "300 Sport", "Black", "R", "SVR"],
    "f-pace": ["R-Dynamic S", "R-Dynamic SE", "R-Dynamic HSE", "300 Sport", "SVR"],
    "e-pace": ["S", "R-Dynamic S", "R-Dynamic SE", "R-Dynamic HSE", "300 Sport"],
    "i-pace": ["S", "SE", "HSE", "EV320 Black", "Black"],
    "xe": ["S", "R-Dynamic S", "R-Dynamic SE", "R-Dynamic HSE", "300 Sport"],
    "xf": ["R-Dynamic S", "R-Dynamic SE", "R-Dynamic HSE", "300 Sport"],
    "f-type": ["R-Dynamic", "P450", "R", "R75", "SVR"],
  },
  "land rover": {
    default: ["S", "SE", "HSE", "Autobiography", "First Edition", "Dynamic SE", "Dynamic HSE"],
    "range rover": ["S", "SE", "HSE", "Autobiography", "SV Autobiography", "SV"],
    "range rover sport": ["S", "SE", "HSE", "Dynamic SE", "Dynamic HSE", "Autobiography", "SV"],
    "range rover velar": ["S", "SE", "Dynamic SE", "Dynamic HSE", "Autobiography"],
    "range rover evoque": ["S", "SE", "Dynamic SE", "Dynamic HSE", "Autobiography"],
    "defender": ["S", "SE", "HSE", "X-Dynamic SE", "X-Dynamic HSE", "X", "OCTA", "V8"],
    "discovery": ["S", "SE", "HSE", "Dynamic SE", "Dynamic HSE", "Metropolitan"],
  },
  "mini": {
    default: ["Essential", "Classic", "Favoured", "Cooper", "Cooper S", "Cooper SE", "JCW", "Resolute"],
    "cooper": ["Essential", "Classic", "Favoured", "Resolute"],
    "countryman": ["Essential", "Classic", "Favoured", "Cooper S ALL4", "JCW"],
    "clubman": ["Essential", "Classic", "Favoured", "Cooper S", "JCW"],
  },
  "rolls-royce": ["Ghost", "Wraith", "Phantom", "Cullinan", "Spectre", "Black Badge"],
  "bentley": ["Continental GT", "Continental GTC", "Flying Spur", "Bentayga", "Mulliner", "Speed", "S"],
  "aston martin": ["Vantage", "DB12", "DBS", "DBX", "DBX707", "Vanquish"],

  // Premium italien
  "lamborghini": ["Huracan EVO", "Huracan STO", "Huracan Tecnica", "Urus", "Urus S", "Urus Performante", "Revuelto"],
  "ferrari": ["Roma", "Portofino M", "296 GTB", "296 GTS", "F8 Tributo", "SF90", "812 GTS", "Purosangue", "Daytona SP3", "12Cilindri"],
  "maserati": ["Modena", "Trofeo", "GranTurismo", "GranCabrio", "Levante", "Grecale", "MC20", "MCPura"],

  // EV
  "tesla": ["Standard Range", "Long Range", "Performance", "Plaid", "Plaid+"],
  "polestar": ["Standard", "Long Range Single Motor", "Long Range Dual Motor", "Performance Pack"],
  "byd": ["Active", "Boost", "Comfort", "Design"],
  "mg": ["Standard Range", "Comfort", "Luxury", "Trophy"],
  "lucid": ["Air Pure", "Air Touring", "Air Grand Touring", "Air Sapphire"],
  "rivian": ["Adventure", "Performance", "R1T", "R1S"],

  // Sport / niche
  "lotus": ["Sport", "Evora GT", "Emira V6", "Emira i4", "Eletre", "Eletre R", "Emeya"],
  "mclaren": ["GT", "Artura", "750S", "765LT", "Senna"],
  "alpine": ["Pure", "Légende", "S", "GT", "A110 R"],
  "abarth": ["595", "595 Competizione", "695", "695 Tributo Ferrari", "500e"],
  "genesis": ["Standard", "Luxury", "Sport", "Sport Plus"],

  // Utilitaires & US
  "ford": {
    default: ["Trend", "Titanium", "ST-Line", "Active", "Vignale", "ST"],
    "fiesta": ["Trend", "Titanium", "ST-Line", "Active", "Vignale", "ST"],
    "focus": ["Trend", "Titanium", "ST-Line", "Active", "Vignale", "ST"],
    "puma": ["Titanium", "ST-Line", "ST-Line X", "Vignale", "ST"],
    "kuga": ["Titanium", "ST-Line", "ST-Line X", "Vignale"],
    "mustang": ["EcoBoost", "GT", "Mach 1", "Mach-E", "Mach-E GT", "Dark Horse"],
  },
  "opel": {
    default: ["Edition", "Elegance", "GS Line", "GS", "Ultimate"],
    "corsa": ["Edition", "Elegance", "GS Line", "GS"],
    "astra": ["Edition", "Elegance", "GS Line", "GS", "Ultimate"],
    "mokka": ["Edition", "Elegance", "GS Line", "GS", "Ultimate"],
    "grandland": ["Edition", "Elegance", "GS Line", "GS", "Ultimate"],
  },
  "dacia": ["Essential", "Expression", "Extreme", "Journey", "Comfort", "SL Stepway"],
};

// Helper : retourne la liste de finitions pertinentes pour une saisie utilisateur "make"
function getFinitionsForMake(makeStr) {
  if (!makeStr || typeof makeStr !== "string") return [];
  const norm = makeStr.toLowerCase().trim();

  // 1) Détecter la marque dans la chaîne (recherche du préfixe le plus long qui matche)
  let matchedBrand = null;
  for (const brand of Object.keys(FINITION_CATALOG)) {
    if (norm.startsWith(brand) || norm.includes(" " + brand + " ") || norm.includes(brand + " ")) {
      if (!matchedBrand || brand.length > matchedBrand.length) matchedBrand = brand;
    }
  }
  if (!matchedBrand) return [];

  const entry = FINITION_CATALOG[matchedBrand];

  // Cas 1 : Array simple → finitions générales de la marque
  if (Array.isArray(entry)) return entry;

  // Cas 2 : Objet → chercher un modèle spécifique dans la saisie
  if (typeof entry === "object") {
    for (const modelKey of Object.keys(entry)) {
      if (modelKey === "default") continue;
      if (norm.includes(modelKey)) return entry[modelKey];
    }
    return entry.default || [];
  }
  return [];
}

// ─── Helper : extraire l'ID d'une vidéo YouTube depuis n'importe quel format ──
function extractYouTubeId(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  // formats : watch?v=ID, youtu.be/ID, embed/ID, shorts/ID, &v=ID
  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = u.match(p);
    if (m && m[1]) return m[1];
  }
  // cas où l'utilisateur colle juste l'ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(u)) return u;
  return null;
}

// ─── DemoVideoModal — modale vidéo "Découvrir la plateforme" ─────────────────
// ─── Helper universel : détecte la plateforme vidéo et retourne l'URL embed ──
// Supporte YouTube (id ou URL), HeyGen (embeds/UUID), URL d'embed directe.
function buildVideoEmbedUrl(rawUrl) {
  if (!rawUrl) return null;
  const url = String(rawUrl).trim();
  // 1. HeyGen — patterns : "https://app.heygen.com/embeds/{uuid}" ou "https://app.heygen.com/share/{uuid}"
  const heygenMatch = url.match(/heygen\.com\/(embeds|share)\/([a-f0-9]+)/i);
  if (heygenMatch) {
    return { src: `https://app.heygen.com/embeds/${heygenMatch[2]}`, platform: "heygen" };
  }
  // 2. URL d'embed déjà fournie (générique)
  if (/^https?:\/\/.+\/embed/i.test(url) || /^https?:\/\/.+\/embeds/i.test(url)) {
    return { src: url, platform: "embed" };
  }
  // 3. YouTube — réutilise extractYouTubeId existant
  const ytId = (typeof extractYouTubeId === "function") ? extractYouTubeId(url) : null;
  if (ytId) {
    return { src: `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`, platform: "youtube" };
  }
  // 4. Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return { src: `https://player.vimeo.com/video/${vimeoMatch[1]}`, platform: "vimeo" };
  }
  return null;
}

function DemoVideoModal({ onClose }) {
  // Source par défaut : la vidéo HeyGen de présentation VehicleIQ (peut être surchargée via admin localStorage)
  const DEFAULT_HEYGEN = "https://app.heygen.com/embeds/18e468947b5b474fb29b6582f4535134";
  const url = (() => {
    try { return localStorage.getItem("viq_demo_video") || DEFAULT_HEYGEN; }
    catch(e) { return DEFAULT_HEYGEN; }
  })();
  const embed = buildVideoEmbedUrl(url);
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="demo-video-panel">
        <button className="demo-video-close" onClick={onClose} aria-label="Fermer">✕</button>
        {embed ? (
          <div className="demo-video-frame">
            <iframe
              src={embed.src}
              title="Découvrir la plateforme VehicleIQ"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              frameBorder="0"
            />
          </div>
        ) : (
          <div className="demo-video-empty">
            <div className="demo-video-empty-icon">🎬</div>
            <div className="demo-video-empty-title">Vidéo bientôt disponible</div>
            <div className="demo-video-empty-sub">
              Notre démonstration vidéo est en cours de préparation. En attendant, réservez une démo personnalisée avec notre équipe.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── OrgManagementModal — Gestion multi-agent (Multi-Agents) ──────────
// Permet au propriétaire/admin d'une organisation de gérer ses agents :
// - Onglet "Membres" : liste avec rôle, possibilité de retirer/changer rôle
// - Onglet "Invitations" : inviter par email, copier le lien, révoquer
// - Onglet "Infos" : récap de l'organisation (SIRET, plan, agents actifs/max)
function OrgManagementModal({ onClose, user, refreshOrg }) {
  const [tab, setTab] = useState("members");
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // États du formulaire d'invitation
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("agent");
  const [inviting, setInviting] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState("");
  const [copied, setCopied] = useState(false);

  // Charger les données au mount + à chaque switch d'onglet
  const reload = async () => {
    setLoading(true); setError("");
    try {
      const [orgRes, membersRes, invitationsRes] = await Promise.all([
        apiGet("/api/org/my"),
        apiGet("/api/org/members").catch(() => ({ members: [] })),
        apiGet("/api/org/invitations?status=pending").catch(() => ({ invitations: [] })),
      ]);
      setOrganization(orgRes?.organization || null);
      setMembers(membersRes?.members || []);
      setInvitations(invitationsRes?.invitations || []);
    } catch (e) {
      setError("Impossible de charger les données. " + (e?.message || ""));
    }
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  // Mon rôle dans l'org
  const myRole = organization?.my_role || null;
  const canManage = myRole === "owner";
  const isOwner = myRole === "owner";

  // ─── Invitation : envoyer ───
  const handleInvite = async () => {
    setError(""); setInfo(""); setLastInviteUrl(""); setCopied(false);
    const cleanEmail = String(inviteEmail || "").trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError("Email invalide."); return;
    }
    setInviting(true);
    try {
      const res = await apiPost("/api/org/invite", { email: cleanEmail, role: inviteRole });
      setInfo("Invitation créée. Transmettez le lien ci-dessous à votre collaborateur par email ou messagerie :");
      setLastInviteUrl(res?.inviteUrl || "");
      setInviteEmail("");
      await reload();
    } catch (e) {
      const msg = e?.message || String(e);
      if (msg.includes("MAX_AGENTS_REACHED")) {
        setError("Limite d'agents atteinte sur votre plan. Contactez-nous pour augmenter la limite.");
      } else if (msg.includes("déjà en attente")) {
        setError("Une invitation est déjà en attente pour cet email.");
      } else {
        setError("Erreur : " + msg);
      }
    }
    setInviting(false);
  };

  // ─── Invitation : copier lien ───
  const copyInviteUrl = async () => {
    try {
      await navigator.clipboard.writeText(lastInviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* fallback : sélection auto */ }
  };

  // ─── Invitation : révoquer ───
  const handleRevoke = async (invId) => {
    if (!window.confirm("Révoquer cette invitation ? Le lien deviendra invalide.")) return;
    try {
      await apiPost(`/api/org/invitations/${invId}/revoke`, {});
      setInfo("Invitation révoquée.");
      await reload();
    } catch (e) {
      setError("Erreur révocation : " + (e?.message || e));
    }
  };

  // ─── Membre : changer rôle (owner only) ───
  const handleChangeRole = async (memberUid, newRole) => {
    setError(""); setInfo("");
    try {
      await apiPost(`/api/org/members/${memberUid}/role`, { role: newRole });
      setInfo("Rôle mis à jour.");
      await reload();
    } catch (e) {
      setError("Erreur : " + (e?.message || e));
    }
  };

  // ─── Membre : retirer (owner only) ───
  const handleRemoveMember = async (memberUid, memberName) => {
    if (!window.confirm(`Retirer ${memberName} de l'organisation ? Son accès au wallet partagé sera révoqué.`)) return;
    try {
      await apiDelete(`/api/org/members/${memberUid}`);
      setInfo("Membre retiré.");
      await reload();
    } catch (e) {
      setError("Erreur : " + (e?.message || e));
    }
  };

  // ─── Format date ───
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    } catch (e) { return "—"; }
  };

  // ─── Labels rôles (plus de rôle admin d'organisation) ───
  const roleLabel = (r) => {
    if (r === "owner") return { txt: "Propriétaire", color: "var(--accent)" };
    return { txt: "Agent", color: "var(--sub)" };
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel panel-wide" style={{ maxWidth: 920, maxHeight: "94vh", overflow: "auto" }}>
        <div className="panel-header">
          <div>
            <div className="panel-title">👥 Mon équipe</div>
            <div className="panel-sub">
              {organization
                ? `Organisation ${organization.company_name || "—"} · SIRET ${organization.siret} · Plan ${organization.plan_tier || "—"}`
                : "Aucune organisation associée à votre compte."}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
            <ThemeToggleButton size={32} />
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="panel-body">
          {/* Pas d'organisation : explication + CTA */}
          {!loading && !organization && (
            <div style={{
              padding: "2rem 1.5rem", textAlign: "center",
              background: "var(--card)", borderRadius: 8, border: "1px solid var(--border)"
            }}>
              <div style={{ fontSize: "2.5rem", marginBottom: ".7rem" }}>🏢</div>
              <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text)", marginBottom: ".4rem" }}>
                Aucune organisation associée
              </div>
              <div style={{ fontSize: ".85rem", color: "var(--sub)", lineHeight: 1.5, maxWidth: 520, margin: "0 auto" }}>
                La gestion multi-agent est réservée à l'offre <strong>Pro Multi-Agent</strong>.
                Pour en bénéficier, contactez notre équipe : nous vous activerons l'espace équipe et configurerons
                votre nombre maximum d'agents (3, 10 ou illimité selon votre palier).
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: "center", padding: "3rem" }}>
              <div className="spinner" style={{ margin: "0 auto" }} />
            </div>
          )}

          {/* Org présente : onglets + contenu */}
          {!loading && organization && (
            <>
              {/* Tabs */}
              <div style={{ display: "flex", gap: ".4rem", borderBottom: "1px solid var(--border)", marginBottom: "1rem" }}>
                {[
                  ["members",     `Membres (${members.length})`,         null],
                  ["invitations", `Invitations (${invitations.length})`, canManage],
                  ["info",        "Infos organisation",                  null],
                ].filter(([_, __, gate]) => gate === null || gate === true).map(([k, l]) => (
                  <button key={k} onClick={() => { setTab(k); setError(""); setInfo(""); setLastInviteUrl(""); }}
                    style={{
                      padding: ".6rem 1rem", border: "none",
                      borderBottom: "2px solid",
                      borderBottomColor: tab === k ? "var(--accent)" : "transparent",
                      background: "none",
                      color: tab === k ? "var(--accent)" : "var(--sub)",
                      fontWeight: tab === k ? 700 : 500,
                      cursor: "pointer", fontSize: ".88rem",
                      fontFamily: "var(--font-body)",
                      transition: "all .15s"
                    }}>{l}</button>
                ))}
              </div>

              {/* Messages info/erreur globaux */}
              {error && (
                <div style={{
                  padding: ".7rem .9rem", background: "rgba(185,28,28,.08)",
                  border: "1px solid rgba(185,28,28,.25)", borderRadius: 6,
                  fontSize: ".82rem", color: "#9B2C2C", marginBottom: ".8rem"
                }}>{error}</div>
              )}
              {info && (
                <div style={{
                  padding: ".7rem .9rem", background: "rgba(4,120,87,.08)",
                  border: "1px solid rgba(4,120,87,.25)", borderRadius: 6,
                  fontSize: ".82rem", color: "var(--green)", marginBottom: ".8rem"
                }}>{info}</div>
              )}

              {/* ─── Onglet MEMBRES ─── */}
              {tab === "members" && (
                <div>
                  <div style={{
                    fontSize: ".78rem", color: "var(--sub)", marginBottom: ".7rem",
                    display: "flex", justifyContent: "space-between", alignItems: "center"
                  }}>
                    <span>
                      {members.length} membre{members.length > 1 ? "s" : ""} actif{members.length > 1 ? "s" : ""}
                      {organization.max_agents > 0 && ` · Limite : ${organization.max_agents} agents max`}
                    </span>
                  </div>
                  {members.length === 0 ? (
                    <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--muted)" }}>
                      Aucun membre.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                      {members.map(m => {
                        const r = roleLabel(m.role);
                        const isMe = m.user_uid === user?.uid;
                        return (
                          <div key={m.id} style={{
                            display: "flex", alignItems: "center", gap: ".7rem",
                            padding: ".7rem .9rem", background: "var(--white)",
                            border: "1px solid var(--border)", borderRadius: 8
                          }}>
                            {/* Avatar circulaire */}
                            <div style={{
                              width: 36, height: 36, borderRadius: "50%",
                              background: r.color, color: "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: ".88rem", fontWeight: 700,
                              fontFamily: "var(--font-mono)",
                              flexShrink: 0
                            }}>
                              {(m.name || m.email || "?").charAt(0).toUpperCase()}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: ".88rem", color: "var(--text)" }}>
                                {m.name || m.email || "(inconnu)"} {isMe && <span style={{ fontSize: ".7rem", color: "var(--muted)", fontStyle: "italic" }}>(vous)</span>}
                              </div>
                              <div style={{ fontSize: ".72rem", color: "var(--sub)", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {m.email || "—"} · membre depuis {fmtDate(m.joined_at)}
                              </div>
                            </div>
                            <div style={{
                              fontSize: ".7rem", fontFamily: "var(--font-mono)",
                              padding: ".2rem .6rem", borderRadius: 4,
                              background: `${r.color}15`, color: r.color,
                              fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px"
                            }}>{r.txt}</div>
                            {/* Actions owner only — pas sur soi-même, pas sur le propriétaire */}
                            {isOwner && !isMe && m.role !== "owner" && (
                              <div style={{ display: "flex", gap: ".25rem" }}>
                                <select
                                  value={m.role}
                                  onChange={e => handleChangeRole(m.user_uid, e.target.value)}
                                  style={{
                                    padding: ".2rem .35rem", fontSize: ".72rem",
                                    border: "1px solid var(--border)", borderRadius: 4,
                                    fontFamily: "var(--font-body)", cursor: "pointer"
                                  }}>
                                  <option value="admin">Admin</option>
                                  <option value="agent">Agent</option>
                                </select>
                                <button
                                  onClick={() => handleRemoveMember(m.user_uid, m.name || m.email)}
                                  title="Retirer ce membre"
                                  style={{
                                    padding: ".2rem .45rem", fontSize: ".72rem",
                                    border: "1px solid #9B2C2C", borderRadius: 4,
                                    background: "transparent", color: "#9B2C2C",
                                    cursor: "pointer", fontFamily: "var(--font-body)"
                                  }}>✕</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ─── Onglet INVITATIONS ─── */}
              {tab === "invitations" && canManage && (
                <div>
                  {/* Formulaire d'invitation */}
                  <div style={{
                    padding: "1rem 1.1rem", background: "var(--card)",
                    border: "1px solid var(--border)", borderRadius: 8,
                    marginBottom: "1rem"
                  }}>
                    <div style={{ fontSize: ".85rem", fontWeight: 700, color: "var(--text)", marginBottom: ".5rem" }}>
                      Inviter un collaborateur
                    </div>
                    <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", alignItems: "stretch" }}>
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        placeholder="email@entreprise.fr"
                        style={{
                          flex: "1 1 220px", padding: ".55rem .7rem",
                          border: "1px solid var(--border)", borderRadius: 6,
                          fontSize: ".85rem", fontFamily: "var(--font-body)"
                        }}
                      />
                      <select
                        value={inviteRole}
                        onChange={e => setInviteRole(e.target.value)}
                        style={{
                          padding: ".55rem .5rem",
                          border: "1px solid var(--border)", borderRadius: 6,
                          fontSize: ".82rem", fontFamily: "var(--font-body)",
                          cursor: "pointer", background: "var(--white)"
                        }}>
                        <option value="agent">Agent</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button
                        onClick={handleInvite}
                        disabled={inviting}
                        className="btn btn-accent btn-sm"
                        style={{ fontSize: ".82rem" }}>
                        {inviting ? "Envoi…" : "Inviter →"}
                      </button>
                    </div>
                    <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: ".5rem", lineHeight: 1.4 }}>
                      <strong>Agent</strong> : peut estimer, contrôler des VIN et consulter le dashboard partagé.
                      <strong> Admin</strong> : tout cela + peut inviter et révoquer des agents.
                    </div>
                    {/* Lien d'invitation après création */}
                    {lastInviteUrl && (
                      <div style={{
                        marginTop: ".8rem", padding: ".6rem .8rem",
                        background: "rgba(168,128,58,.06)", border: "1.5px dashed var(--accent)",
                        borderRadius: 6
                      }}>
                        <div style={{ fontSize: ".72rem", color: "var(--sub)", marginBottom: ".3rem" }}>
                          Lien d'invitation (valide 14 jours) :
                        </div>
                        <div style={{ display: "flex", gap: ".4rem", alignItems: "center" }}>
                          <input readOnly value={lastInviteUrl}
                            onClick={e => e.target.select()}
                            style={{
                              flex: 1, padding: ".4rem .55rem",
                              fontSize: ".75rem", fontFamily: "var(--font-mono)",
                              border: "1px solid var(--border)", borderRadius: 4,
                              background: "var(--white)", color: "var(--text)"
                            }} />
                          <button onClick={copyInviteUrl}
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: ".75rem", whiteSpace: "nowrap" }}>
                            {copied ? "✓ Copié" : "📋 Copier"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Liste des invitations pending */}
                  <div style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: ".5rem" }}>
                    {invitations.length} invitation{invitations.length > 1 ? "s" : ""} en attente
                  </div>
                  {invitations.length === 0 ? (
                    <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--muted)", fontSize: ".82rem" }}>
                      Aucune invitation en attente.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
                      {invitations.map(inv => {
                        const r = roleLabel(inv.role);
                        const expired = inv.expires_at && new Date(inv.expires_at) < new Date();
                        return (
                          <div key={inv.id} style={{
                            display: "flex", alignItems: "center", gap: ".7rem",
                            padding: ".6rem .9rem", background: "var(--white)",
                            border: "1px solid var(--border)", borderRadius: 6
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: ".85rem", color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {inv.email}
                              </div>
                              <div style={{ fontSize: ".7rem", color: "var(--sub)" }}>
                                Invitée le {fmtDate(inv.created_at)} · expire le {fmtDate(inv.expires_at)}
                                {expired && <span style={{ color: "#9B2C2C", marginLeft: ".4rem" }}>⚠ expirée</span>}
                              </div>
                            </div>
                            <div style={{
                              fontSize: ".68rem", fontFamily: "var(--font-mono)",
                              padding: ".15rem .5rem", borderRadius: 3,
                              background: `${r.color}15`, color: r.color,
                              fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px"
                            }}>{r.txt}</div>
                            <button
                              onClick={() => handleRevoke(inv.id)}
                              title="Révoquer cette invitation"
                              style={{
                                padding: ".25rem .5rem", fontSize: ".7rem",
                                border: "1px solid #9B2C2C", borderRadius: 4,
                                background: "transparent", color: "#9B2C2C",
                                cursor: "pointer"
                              }}>Révoquer</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ─── Onglet INFOS ─── */}
              {tab === "info" && (
                <div style={{ display: "flex", flexDirection: "column", gap: ".7rem" }}>
                  {[
                    ["Raison sociale", organization.company_name || "—"],
                    ["SIRET",          organization.siret],
                    ["Plan",           organization.plan_tier || "volume_entreprise"],
                    ["Agents max",     organization.max_agents === -1 ? "Illimité" : organization.max_agents],
                    ["Membres actuels", members.length + " / " + (organization.max_agents === -1 ? "∞" : organization.max_agents)],
                    ["Votre rôle",     roleLabel(myRole).txt],
                    ["Créée le",       fmtDate(organization.created_at)],
                  ].map(([k, v]) => (
                    <div key={k} style={{
                      display: "flex", justifyContent: "space-between",
                      padding: ".6rem .8rem", background: "var(--card)",
                      border: "1px solid var(--border)", borderRadius: 6,
                      fontSize: ".85rem"
                    }}>
                      <span style={{ color: "var(--sub)" }}>{k}</span>
                      <span style={{ fontWeight: 600, color: "var(--text)" }}>{v}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: ".5rem", lineHeight: 1.5, fontStyle: "italic" }}>
                    Pour augmenter le nombre maximal d'agents ou modifier votre plan, contactez l'équipe VehicleIQ
                    via le bouton « Réserver une démo ».
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── OnboardingModal — Tutoriel interactif de première visite ──────────────
// Trigger : automatique à la première visite (localStorage.viq_onboarded)
// + bouton manuel "👋 Guide" dans la navbar pour rouvrir à volonté.
function OnboardingFirstVisitModal({ onClose, onStartEstimation, onStartCheck, onStartBuy }) {
  // Vidéo HeyGen par défaut, surchargeable via localStorage (admin)
  const DEFAULT_HEYGEN = "https://app.heygen.com/embeds/18e468947b5b474fb29b6582f4535134";
  const videoUrl = (() => {
    try { return localStorage.getItem("viq_demo_video") || DEFAULT_HEYGEN; }
    catch(e) { return DEFAULT_HEYGEN; }
  })();
  const embed = buildVideoEmbedUrl(videoUrl);

  // Marquer comme vu dans localStorage à la fermeture
  const handleClose = () => {
    try { localStorage.setItem("viq_onboarded", "1"); } catch(_) {}
    onClose();
  };

  // Lancer une action + marquer comme vu + fermer
  const handleAction = (action) => {
    try { localStorage.setItem("viq_onboarded", "1"); } catch(_) {}
    onClose();
    setTimeout(() => action && action(), 200);
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="panel" style={{
        maxWidth: 920, maxHeight: "94vh", overflow: "auto",
        padding: 0, position: "relative"
      }}>
        {/* Bouton fermer en haut à droite (absolu) */}
        <button
          onClick={handleClose}
          aria-label="Fermer le guide"
          style={{
            position: "absolute", top: 14, right: 14, zIndex: 10,
            width: 34, height: 34, borderRadius: "50%",
            border: "1px solid var(--border)", background: "var(--white)",
            cursor: "pointer", fontSize: "1rem", color: "var(--sub)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>✕</button>

        {/* En-tête : titre + sous-titre */}
        <div style={{
          padding: "1.7rem 1.8rem 1.2rem",
          borderBottom: "1px solid var(--border)",
          background: "linear-gradient(180deg, rgba(168,128,58,.04) 0%, rgba(168,128,58,0) 100%)"
        }}>
          <div style={{
            fontSize: ".7rem", fontFamily: "var(--font-mono)",
            letterSpacing: "2px", color: "var(--accent)",
            fontWeight: 600, marginBottom: ".4rem"
          }}>BIENVENUE SUR VEHICLEIQ</div>
          <div style={{
            fontFamily: "var(--font-head)", fontSize: "1.8rem",
            fontWeight: 700, color: "var(--text)", lineHeight: 1.15,
            marginBottom: ".4rem"
          }}>Découvrez la Cote auto pro <span style={{color:"var(--accent)"}}>en 90 secondes</span></div>
          <div style={{ fontSize: ".88rem", color: "var(--sub)", lineHeight: 1.5 }}>
            Regardez la vidéo de présentation, puis lancez votre première action.
            Vous pouvez aussi explorer librement la plateforme.
          </div>
        </div>

        {/* Vidéo HeyGen intégrée */}
        <div style={{ padding: "1.2rem 1.8rem 0" }}>
          {embed ? (
            <div style={{
              position: "relative", width: "100%", paddingBottom: "56.25%",
              borderRadius: 10, overflow: "hidden",
              border: "1px solid var(--border)",
              background: "#000"
            }}>
              <iframe
                src={embed.src}
                title="Présentation VehicleIQ"
                style={{
                  position: "absolute", top: 0, left: 0,
                  width: "100%", height: "100%", border: 0
                }}
                allow="encrypted-media; fullscreen"
                allowFullScreen
                frameBorder="0"
              />
            </div>
          ) : (
            <div style={{
              padding: "3rem", textAlign: "center",
              background: "var(--card)", borderRadius: 10,
              border: "1px dashed var(--border)"
            }}>
              <div style={{ fontSize: "2.5rem", marginBottom: ".5rem" }}>🎬</div>
              <div style={{ fontSize: ".95rem", fontWeight: 600, color: "var(--sub)" }}>
                Vidéo en préparation
              </div>
            </div>
          )}
        </div>

        {/* 3 CTAs d'action rapide */}
        <div style={{ padding: "1.4rem 1.8rem 1.2rem" }}>
          <div style={{
            fontSize: ".75rem", fontFamily: "var(--font-mono)",
            letterSpacing: "1.5px", color: "var(--sub)",
            fontWeight: 600, marginBottom: ".8rem", textTransform: "uppercase"
          }}>Lancez-vous</div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: ".7rem"
          }}>
            {[
              {
                icon: "💰", color: "var(--accent)",
                title: "Estimer un véhicule",
                sub: "Prix de vente recommandé en temps réel",
                onClick: () => handleAction(onStartEstimation)
              },
              {
                icon: "🔍", color: "#1F5AE6",
                title: "Contrôler un VIN",
                sub: "Historique SIV officiel · gratuit illimité",
                onClick: () => handleAction(onStartCheck)
              },
              {
                icon: "🎯", color: "var(--green)",
                title: "Recommandations d'achat",
                sub: "10 véhicules qualifiés selon votre profil",
                onClick: () => handleAction(onStartBuy)
              }
            ].map((cta, i) => (
              <button key={i} onClick={cta.onClick}
                style={{
                  padding: "1rem 1rem", border: "1.5px solid var(--border)",
                  borderRadius: 10, background: "var(--white)", cursor: "pointer",
                  textAlign: "left", transition: "all .15s ease",
                  display: "flex", flexDirection: "column", gap: ".3rem",
                  fontFamily: "var(--font-body)"
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = cta.color;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,.06)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = "var(--border)";
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}>
                <div style={{ fontSize: "1.6rem", lineHeight: 1 }}>{cta.icon}</div>
                <div style={{ fontWeight: 700, fontSize: ".95rem", color: "var(--text)" }}>
                  {cta.title}
                </div>
                <div style={{ fontSize: ".75rem", color: "var(--sub)", lineHeight: 1.4 }}>
                  {cta.sub}
                </div>
                <div style={{
                  fontSize: ".7rem", color: cta.color, fontWeight: 600,
                  marginTop: ".25rem"
                }}>Démarrer →</div>
              </button>
            ))}
          </div>
        </div>

        {/* Pied : explorer librement + petits avantages */}
        <div style={{
          padding: "1rem 1.8rem 1.4rem",
          borderTop: "1px solid var(--border)",
          background: "var(--card)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: "1rem", flexWrap: "wrap"
        }}>
          <div style={{ display: "flex", gap: "1.3rem", flexWrap: "wrap", fontSize: ".72rem", color: "var(--sub)" }}>
            <div>✓ Sans abonnement</div>
            <div>✓ Crédits sans expiration</div>
            <div>✓ 5 cotes offertes à l'inscription</div>
          </div>
          <button onClick={handleClose} className="btn btn-ghost btn-sm"
            style={{ fontSize: ".78rem" }}>
            Explorer librement →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── HeroSimulator — point d'entrée du hero (brief §1) ──────────────────────
// IMPORTANT : ce composant ne calcule AUCUN prix. La saisie plaque interroge
// le vrai backend (/api/check) uniquement pour identifier le véhicule, puis le
// modal d'estimation (SellPanel) s'ouvre pré-rempli — c'est lui, via VIQ.AI®,
// qui produit la véritable estimation. La saisie modèle ouvre directement le
// modal pré-rempli. Aucune valeur de prix n'est affichée ici.
// v41 — Illustration statique du Dossier VIQ pour le hero de la landing page.
// Remplace le mini-simulateur "Décoder une plaque française" (qui vendait un
// outil ponctuel) par un aperçu de ce que le client obtient RÉELLEMENT : une
// fiche véhicule consolidée qui vit dans le temps. Purement illustratif —
// données d'exemple fixes, aucun appel réseau. Le décodage de plaque reste
// disponible ailleurs (liens secondaires "Aussi disponible", page /decoder).
function HeroDossierIllustration() {
  const milestones = [
    ["HistoVec", true], ["CarVertical", true], ["Photos", true],
    ["Cote", true], ["Annonce", true], ["Vente", false],
  ];
  // v42 — Mini-timeline décorative pour occuper l'espace libéré par le
  // bouton CTA et les liens "Aussi disponible" (retirés : cette carte est
  // maintenant purement illustrative, les vrais CTA vivent dans la colonne
  // de gauche, à côté de "Demander une démo").
  const flow = [
    ["🔍", "Contrôle"], ["🛡", "HistoVec"], ["💰", "Cote"], ["📣", "Annonce"], ["✅", "Vente"],
  ];
  return (
    <div className="hero-sim-card" style={{ padding: "1.5rem 1.6rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <div style={{ fontSize: ".7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--accent)" }}>Aperçu</div>
        <div style={{ fontSize: ".7rem", color: "var(--muted)" }}>Dossier VIQ</div>
      </div>

      <div style={{ display: "flex", gap: ".9rem", alignItems: "center", marginBottom: "1.1rem" }}>
        <div style={{ width: 46, height: 46, borderRadius: 10, background: "var(--card, rgba(255,255,255,.06))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.35rem", flexShrink: 0 }}>🚗</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "var(--text)" }}>BMW Série 3 320d · 2019</div>
          {/* v42 — Plaque fictive (l'ancienne, GH-222-JQ, correspondait à un
              véhicule réel) : format volontairement générique, jamais utilisé
              en usage réel sur le site. */}
          <div style={{ fontSize: ".76rem", color: "var(--sub)" }}>AA-123-BB</div>
        </div>
        <div style={{ textAlign: "center", flexShrink: 0 }}>
          <div style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: "1.15rem", color: "var(--green, #2E6F5C)" }}>14 300 €</div>
          <div style={{ fontSize: ".6rem", color: "var(--muted)" }}>Prix conseillé</div>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginBottom: "1.1rem" }}>
        {milestones.map(([label, ok]) => (
          <span key={label} style={{ fontSize: ".68rem", fontWeight: 600, padding: ".22rem .6rem", borderRadius: 20,
            background: ok ? "rgba(46,111,92,.12)" : "rgba(255,255,255,.05)",
            color: ok ? "var(--green, #2E6F5C)" : "var(--muted)",
            border: "1px solid " + (ok ? "rgba(46,111,92,.3)" : "rgba(255,255,255,.12)") }}>
            {ok ? "✓" : "○"} {label}
          </span>
        ))}
      </div>

      <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1.2rem", background: "rgba(255,255,255,.04)", borderRadius: 8, padding: ".7rem .85rem" }}>
        Un seul dossier par véhicule : contrôle, HistoVec, cote, annonce et suivi de vente s'y ajoutent automatiquement — peu importe où vous avez commencé.
      </div>

      {/* Mini-timeline décorative — remplace le bouton + liens retirés */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 .2rem" }}>
        {flow.map(([icon, label], i) => (
          <Fragment key={label}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: ".3rem", minWidth: 0 }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(168,128,58,.12)", border: "1px solid rgba(168,128,58,.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".95rem" }}>{icon}</div>
              <div style={{ fontSize: ".62rem", color: "var(--muted)", whiteSpace: "nowrap" }}>{label}</div>
            </div>
            {i < flow.length - 1 && <div style={{ flex: 1, height: 1, background: "rgba(168,128,58,.25)", margin: "0 .2rem", marginBottom: "1.1rem" }} />}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function HeroSimulator({ onLaunch, onService, onCarteGrise }) {
  const MAKES = ["Peugeot","Renault","Citroën","DS","BMW","Mercedes","Audi","Volkswagen","Tesla","Toyota","Dacia","Ford","Opel","Fiat","Nissan","Hyundai","Kia","Seat","Skoda","Volvo","Mini","Mazda","Honda","Suzuki","Jeep","Land Rover","Porsche","Cupra","Alfa Romeo"];
  const [mode, setMode] = useState("plate"); // "plate" | "model"
  const [plate, setPlate] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [km, setKm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [identified, setIdentified] = useState(null); // { make, model, year, vin } — infos officielles, PAS de prix

  const YEARS = []; for (let y = (typeof CURRENT_YEAR !== "undefined" ? CURRENT_YEAR : new Date().getFullYear()); y >= 2005; y--) YEARS.push(y);

  const fmtPlate = (v) => {
    const c = v.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 9);
    const p1 = c.slice(0,2), p2 = c.slice(2,5), p3 = c.slice(5,7);
    if (c.length > 5) return `${p1}-${p2}-${p3}`;
    if (c.length > 2) return `${p1}-${p2}`;
    return p1;
  };

  // Plaque → identification réelle via backend, PUIS ouverture du modal pré-rempli.
  const runPlate = async () => {
    const clean = plate.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (clean.length < 7) { setError("Format invalide — ex : AB-123-CD"); return; }
    setLoading(true); setError(""); setIdentified(null);
    try {
      const data = await apiGet("/api/check/" + clean + "?is_pro=false&siret=");
      if (!data.success || !data.data) {
        // Pas d'identification : on ouvre quand même le modal, l'utilisateur saisira manuellement.
        setError("Véhicule non identifié — vous pourrez compléter dans la Cote.");
        setLoading(false);
        setTimeout(() => onLaunch({}), 600);
        return;
      }
      const info = data.data;
      const prefill = {};
      const mkStr = buildVehicleFullName(info);
      if (mkStr) prefill.make = mkStr;
      if (info.year) prefill.year_sell = parseInt(info.year);
      if (info.km) prefill.km_sell = parseInt(String(info.km).replace(/\D/g, "")) || undefined;
      if (info.fuel) prefill.fuel_sell = info.fuel;
      if (info.vin) prefill.vin = info.vin;
      setIdentified({
        label: mkStr,
        year: info.year || "",
        vin: info.vin || "",
      });
      setLoading(false);
      // Laisser une fraction de seconde pour montrer la confirmation, puis ouvrir le vrai modal.
      setTimeout(() => onLaunch(prefill), 700);
    } catch (e) {
      setError(e.code === "NOT_FOUND"
        ? "Plaque non reconnue — ouverture de la Cote manuelle."
        : "Service de décodage momentanément indisponible — ouverture de la Cote manuelle.");
      setLoading(false);
      setTimeout(() => onLaunch({}), 700);
    }
  };

  // Modèle → ouverture directe du modal pré-rempli (aucun calcul local).
  const runModel = () => {
    if (!make) { setError("Sélectionnez au moins une marque."); return; }
    setError("");
    const prefill = {};
    const mkStr = [make, model].filter(Boolean).join(" ").trim();
    if (mkStr) prefill.make = mkStr;
    if (year) prefill.year_sell = parseInt(year);
    if (km) prefill.km_sell = parseInt(String(km).replace(/\D/g, "")) || undefined;
    onLaunch(prefill);
  };

  const fld = { width:"100%", padding:".72rem .85rem", borderRadius:10, border:"1.5px solid var(--border)", background:"var(--white)", color:"var(--text)", fontSize:".9rem", fontFamily:"var(--font-body)", outline:"none", transition:"border-color .15s ease" };
  const lbl = { fontSize:".7rem", fontWeight:700, color:"var(--muted)", textTransform:"uppercase", letterSpacing:".6px", marginBottom:".3rem", display:"block" };

  return (
    <div className="hero-sim-card">
      <div className="hero-sim-head">
        <div className="hero-sim-title">Décoder une plaque française</div>
        <div className="hero-sim-sub">Entrez votre plaque d'immatriculation.</div>
      </div>

      <div className="hero-sim-field-label">Immatriculation</div>
      <div className="hero-sim-plate-row">
        <div className="hero-sim-plate-input">
          {/* Bande UE (bleu + 12 étoiles + F) comme sur une plaque française */}
          <span className="hero-sim-euband" aria-hidden="true">
            <svg viewBox="0 0 24 30" width="20" height="25">
              <rect width="24" height="30" rx="2" fill="#003399"/>
              {Array.from({ length: 12 }).map((_, i) => {
                const a = (i / 12) * 2 * Math.PI - Math.PI / 2;
                const cx = 12 + 7 * Math.cos(a), cy = 11 + 7 * Math.sin(a);
                return <circle key={i} cx={cx} cy={cy} r="1.1" fill="#FFCC00" />;
              })}
              <text x="12" y="28" textAnchor="middle" fill="#FFCC00" fontSize="6" fontWeight="700" fontFamily="Arial, sans-serif">F</text>
            </svg>
          </span>
          <input
            value={plate}
            onChange={e => { setPlate(fmtPlate(e.target.value)); setError(""); setIdentified(null); }}
            onKeyDown={e => e.key === "Enter" && runPlate()}
            placeholder="AA - 123 - BB"
            maxLength={9}
            aria-label="Plaque d'immatriculation"
          />
        </div>
        <button className="hero-sim-go"
          onClick={runPlate}
          disabled={loading || plate.replace(/[^a-zA-Z0-9]/g,"").length < 7}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          {loading ? "…" : "Estimer"}
        </button>
      </div>

      {error && <div className="hero-sim-error">⚠ {error}</div>}

      {/* Confirmation d'identification (PAS de prix) — affichée brièvement
          avant l'ouverture automatique du modal d'estimation réel. */}
      {identified && (
        <div className="hero-sim-result">
          <div className="hero-sim-vehicle">✓ Véhicule identifié{identified.label ? ` — ${identified.label}` : ""}{identified.year ? ` (${identified.year})` : ""}</div>
          {identified.vin && (
            <div className="hero-sim-vin">
              <span>VIN</span><code>{identified.vin}</code>
            </div>
          )}
          <div className="hero-sim-opening">
            <span className="hero-sim-spinner" />
            Ouverture de la Cote VIQ.AI®…
          </div>
        </div>
      )}

      <div className="hero-sim-trustline">
        Cote calculée par le moteur VIQ.AI® sur 100+ plateformes — résultat dans l'outil complet.
        <span className="hero-sim-algo">
          Le moteur compare en continu les annonces réelles du marché, pondère selon l'état, le kilométrage et la tendance récente, puis en déduit une fourchette de prix calibrée — sans cote figée.
        </span>
      </div>

      {/* Liens services secondaires */}
      <div className="hero-sim-services">
        <span className="hero-sim-services-label">Aussi disponible :</span>
        <button type="button" className="hero-sim-service-link" onClick={() => onService && onService("check")}>Décoder une plaque française</button>
        <span className="hero-sim-service-sep">·</span>
        <button type="button" className="hero-sim-service-link" onClick={() => onService && onService("buy")}>Aide à l'achat VO</button>
        <span className="hero-sim-service-sep">·</span>
        <button type="button" className="hero-sim-service-link" onClick={() => onCarteGrise && onCarteGrise()}>Carte grise &amp; malus</button>
      </div>
    </div>
  );
}


// ─── HomeFAQSection — FAQ SEO longue traîne (brief §10) ─────────────────────
function HomeFAQSection({ onLaunch, onContact }) {
  // v32 — FAQ recentrée sur les objections commerciales (brief conversion),
  // pas sur des mots-clés de recherche. Les questions longue traîne
  // (positionnement Argus, offre API, etc.) restent disponibles sur les
  // pages dédiées ; cette FAQ répond à ce qui bloque une décision d'achat.
  const QA = [
    {
      q: "Combien ça coûte vraiment ?",
      a: "4,99 € par véhicule analysé. Rien d'autre. Vos 5 premières analyses sont offertes à la création du compte, sans carte bancaire.",
    },
    {
      q: "Dois-je m'engager sur la durée ?",
      a: "Non. Pas d'abonnement obligatoire, pas d'engagement. Vous payez uniquement ce que vous utilisez, quand vous l'utilisez.",
    },
    {
      q: "Les données sont-elles fiables ?",
      a: "Oui. Historique officiel (API SIV), cote calibrée sur le marché réel — pas un barème mensuel figé. Estimation indicative, à confronter à l'état réel du véhicule.",
    },
    {
      q: "Combien de temps pour démarrer ?",
      a: "2 minutes. Créez votre compte, saisissez une plaque, obtenez votre premier rapport. Aucune formation nécessaire.",
    },
    {
      q: "Ça fonctionne pour toutes les marques ?",
      a: "Oui, toutes marques et modèles circulant en France, particuliers comme véhicules d'occasion professionnels.",
    },
    {
      q: "Mes données et celles de mes clients sont-elles protégées ?",
      a: "Oui. Conforme RGPD, hébergement en Europe, jamais partagées avec des tiers. Export ou suppression sur simple demande à contact@vehicleiq.fr.",
    },
  ];
  const [open, setOpen] = useState(0);

  return (
    <div className="home-faq-section">
      <div className="home-faq-inner">
        <div className="sec-eyebrow" style={{ color:"var(--accent)" }}>Questions fréquentes</div>
        <h2 className="sec-title">Ce que les marchands<br />nous demandent <em>avant de se lancer</em></h2>

        <div className="home-faq-list">
          {QA.map((item, i) => (
            <div key={i} className={`home-faq-item${open === i ? " open" : ""}`}>
              <button type="button" className="home-faq-q"
                aria-expanded={open === i}
                onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{item.q}</span>
                <span className="home-faq-chevron">{open === i ? "−" : "+"}</span>
              </button>
              {open === i && (
                <div className="home-faq-a">{item.a}</div>
              )}
            </div>
          ))}
        </div>

        <div className="home-faq-cta">
          <button className="btn btn-accent" style={{ padding:".8rem 1.9rem", fontSize:".95rem" }}
            onClick={onLaunch}>
            Essayer gratuitement →
          </button>
          <button className="btn btn-outline" style={{ padding:".8rem 1.7rem", fontSize:".95rem" }}
            onClick={onContact}>
            Parler à un expert
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── HeroVideo — vidéo de présentation (colonne droite du hero) ─────────────
// Lien YouTube modifiable depuis le dashboard admin (clé localStorage viq_demo_video).
function HeroVideo({ onService }) {
  const url = (() => { try { return localStorage.getItem("viq_demo_video") || ""; } catch(e) { return ""; } })();
  const id = extractYouTubeId(url);
  return (
    <div className="hero-video-card">
      {id ? (
        <div className="hero-video-frame">
          <iframe
            src={`https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`}
            title="VehicleIQ — présentation de la plateforme"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="hero-video-placeholder">
          <div className="hero-video-play">▶</div>
          <div className="hero-video-ph-title">Découvrez VehicleIQ en vidéo</div>
          <div className="hero-video-ph-sub">Présentation de la plateforme d'intelligence du véhicule d'occasion</div>
        </div>
      )}
      <div className="hero-form-services">
        <span className="hero-form-services-label">Nos services :</span>
        <button type="button" className="hero-form-service-link"
          onClick={() => onService && onService("check")}>
          Décoder une plaque française
        </button>
        <span className="hero-form-service-sep">·</span>
        <button type="button" className="hero-form-service-link"
          onClick={() => onService && onService("sell")}>
          Cote automobile
        </button>
        <span className="hero-form-service-sep">·</span>
        <button type="button" className="hero-form-service-link"
          onClick={() => onService && onService("buy")}>
          Outil d'aide à l'achat
        </button>
      </div>
    </div>
  );
}

// ─── HeroEstimateForm — formulaire d'estimation inline du hero ──────────────
// Champs réels (marque/modèle/année/km/énergie) → pré-remplit le modal Sell.
function HeroEstimateForm({ onLaunch, onService }) {
  const MAKES = ["Peugeot","Renault","Citroën","DS","BMW","Mercedes","Audi","Volkswagen","Tesla","Toyota","Dacia","Ford","Opel","Fiat","Nissan","Hyundai","Kia","Seat","Skoda","Volvo","Mini","Mazda","Honda","Suzuki","Jeep","Land Rover","Porsche","Cupra","Alfa Romeo"];
  const YEARS = []; for (let y = CURRENT_YEAR; y >= 2005; y--) YEARS.push(y);
  const FUELS = ["Essence","Diesel","Hybride","Hybride rechargeable","Électrique","GPL"];

  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [km, setKm] = useState("");
  const [fuel, setFuel] = useState("");

  const submit = () => {
    const makeStr = [make, model].filter(Boolean).join(" ").trim();
    const prefill = {};
    if (makeStr) prefill.make = makeStr;
    if (year) prefill.year_sell = parseInt(year);
    if (km) prefill.km_sell = parseInt(String(km).replace(/\D/g, "")) || undefined;
    if (fuel) prefill.fuel_sell = fuel;
    onLaunch(prefill);
  };

  const fld = { width: "100%", padding: ".8rem .9rem", borderRadius: 10, border: "1.5px solid var(--border)", background: "var(--white)", color: "var(--text)", fontSize: ".92rem", fontFamily: "var(--font-body)", outline: "none" };
  const lbl = { fontSize: ".72rem", fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: ".35rem", display: "block" };

  return (
    <div className="hero-form-card">
      <div className="hero-form-title">Estimer un véhicule</div>
      <div className="hero-form-sub">Obtenez une fourchette de prix juste et détaillée</div>

      <div className="hero-form-fields">
        <div>
          <label style={lbl}>Marque</label>
          <select style={fld} value={make} onChange={e => setMake(e.target.value)}>
            <option value="">Sélectionner la marque</option>
            {MAKES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Modèle</label>
          <input style={fld} type="text" value={model} onChange={e => setModel(e.target.value)}
            placeholder="Ex : 308, Clio, Série 3…" />
        </div>
        <div className="hero-form-row2">
          <div>
            <label style={lbl}>Année</label>
            <select style={fld} value={year} onChange={e => setYear(e.target.value)}>
              <option value="">Année</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Kilométrage</label>
            <input style={fld} type="text" inputMode="numeric" value={km}
              onChange={e => setKm(e.target.value.replace(/[^\d\s]/g, ""))}
              placeholder="Ex : 45 000 km" />
          </div>
        </div>
        <div>
          <label style={lbl}>Énergie</label>
          <select style={fld} value={fuel} onChange={e => setFuel(e.target.value)}>
            <option value="">Sélectionner</option>
            {FUELS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>

      <button className="btn btn-accent hero-form-btn" onClick={submit}>
        Obtenir mon cote →
      </button>
      <div className="hero-form-note">Cote calibrée sur 100+ plateformes · résultat en 30 s</div>

      <div className="hero-form-services">
        <span className="hero-form-services-label">Autres services :</span>
        <button type="button" className="hero-form-service-link"
          onClick={() => onService && onService("check")}>
          Décoder une plaque française
        </button>
        <span className="hero-form-service-sep">·</span>
        <button type="button" className="hero-form-service-link"
          onClick={() => onService && onService("sell")}>
          Cote automobile
        </button>
        <span className="hero-form-service-sep">·</span>
        <button type="button" className="hero-form-service-link"
          onClick={() => onService && onService("buy")}>
          Outil d'aide à l'achat
        </button>
      </div>
    </div>
  );
}

const SELL_QUESTIONS = [
  { id: "make",       text: "Marque et modèle exact du véhicule ?",                  type: "text",  placeholder: "Ex : Peugeot 308 SW 1.5 BlueHDi 130",
    info:"Saisissez la désignation commerciale complète de votre véhicule telle qu'elle figure sur la carte grise : marque, modèle, motorisation et puissance. Plus votre saisie est précise (ex : 'Renault Clio 5 E-Tech 140 Techno'), plus l'algorithme VIQ.AI® Engine pourra croiser les données avec les bonnes références de marché. Une désignation approximative peut entraîner une cote décalée de 10 à 20%." },
  { id: "year_sell",  text: "Année de 1ère mise en circulation ?",                   type: "range", min: 2005, max: CURRENT_YEAR, unit: "", default: 2019,
    info:"Il s'agit de l'année figurant sur la carte grise (rubrique B). Attention : l'année de fabrication peut différer de l'année d'immatriculation. C'est la date de 1ère mise en circulation qui compte pour la Cote car elle définit l'âge réel du véhicule et donc sa dépréciation. Une erreur d'un an peut faire varier la Cote de 5 à 8%." },
  { id: "km_sell",    text: "Kilométrage actuel ?",                                  type: "range", min: 0, max: 300000, unit: " km", step: 5000, default: 60000,
    info:"Indiquez le kilométrage exact affiché au compteur au moment de la saisie. N'arrondissez pas à la dizaine de milliers — chaque tranche de 10 000 km impacte la Cote. Le kilométrage est l'un des 3 facteurs les plus déterminants du prix de marché avec l'âge et l'état général. Une information fausse ne sert à rien : vous négocierez de toute façon sur la base du kilométrage réel." },
  { id: "fuel_sell",  text: "Motorisation ?",                                        type: "radio", options: ["Essence", "Diesel", "Hybride", "Hybride rechargeable", "Électrique", "GPL"],
    info:"La motorisation est intégrée dans l'algorithme de dépréciation. Les diesels subissent une pénalité croissante liée aux ZFE urbaines (-8% vs essence en 2025). Les hybrides rechargeables (PHEV) bénéficient d'une prime de marché de +12 à +15%. Sélectionnez la motorisation exacte de votre véhicule — une confusion entre hybride simple et hybride rechargeable peut décaler la Cote de plusieurs milliers d'euros." },
  { id: "gearbox_sell", text: "Boîte de vitesses ?",                                 type: "radio", options: ["Manuelle", "Automatique", "CVT / DSG / EDC"],
    info:"La boîte automatique (BVA, DSG, EDC, CVT) est valorisée à +6 à +9% sur le marché de l'occasion, notamment sur les véhicules premium. C'est un facteur de plus-value réel que l'algorithme intègre. Indiquez le type exact de boîte pour une cote précise." },
  { id: "state",      text: "État général du véhicule ?",                            type: "radio",
    options: [
      "Neuf — moins de 6 mois, aucun usage marqué",
      "Comme neuf — état concession, intérieur impeccable",
      "Excellent — aucun défaut visible",
      "Très bon — micro-rayures imperceptibles, entretien suivi",
      "Bon — quelques micro-rayures, intérieur propre",
      "Correct — usure visible, fonctionnement normal",
      "Médiocre — réparations cosmétiques ou mécaniques nécessaires"
    ],
    info: "Soyez absolument honnête sur cet item — c'est celui sur lequel les acheteurs négocient le plus agressivement. \"Neuf\" : véhicule de moins de 6 mois, kilométrage inférieur à 5 000 km, aucun usage marqué. \"Comme neuf\" : véhicule récent en état concession, aucune rayure, intérieur impeccable, carnet d'entretien complet. \"Excellent\" : aucun défaut visible, carrosserie sans rayure. \"Très bon\" : micro-rayures uniquement visibles à l'œil expert, entretien suivi avec factures. \"Bon\" : 1-2 micro-rayures superficielles, intérieur propre. \"Correct\" : usure normale visible mais sans dommage structurel. \"Médiocre\" : réparations à prévoir. Surestimer l'état vous donnera une cote trop haute et vous exposera à des déceptions lors des visites." },
  { id: "ct",         text: "Contrôle technique ?",                                  type: "radio", options: ["Véhicule neuf (< 4 ans, CT non requis)", "Valide (- de 2 ans)", "À refaire dans les 6 mois", "Non valide / Absent"],
    info: "Le contrôle technique est obligatoire pour toute vente entre particuliers en France (article L323-1 du Code de la route). Un CT non valide décote le véhicule de 10 à 14% car l'acheteur doit en payer le coût et prendre le risque de contre-visite. Pensez à faire votre CT avant la mise en vente si possible — l'investissement de 80€ se récupère souvent au centuple sur le prix final." },
  { id: "owners",     text: "Nombre de propriétaires précédents ?",                  type: "radio", options: ["1er propriétaire", "2ème propriétaire", "3 propriétaires et +"],
    info: "Le nombre de propriétaires est un facteur psychologique fort pour les acheteurs : un véhicule 1er propriétaire est perçu comme mieux entretenu et commande une prime de 5 à 8% sur le marché. Indiquez le nombre réel — les acheteurs vérifieront sur la carte grise. Un écart entre votre déclaration et la réalité peut anéantir une vente en cours de négociation." },
  { id: "options",    text: "Options & équipements présents sur le véhicule (sélectionnez tout ce qui s'applique)",
    type: "multi_select",
    options: [
      "GPS / Navigation intégrée",
      "Toit ouvrant / Toit panoramique",
      "Sièges en cuir / Alcantara",
      "Sièges chauffants / Ventilés / Massants",
      "Aide au stationnement / Caméra de recul",
      "Caméra 360° / Park Assist automatique",
      "Phares LED Matrix / Laser",
      "ADAS premium (régulateur adaptatif, lane assist, freinage auto)",
      "Système audio premium (Bose, B&O, Bowers&Wilkins, Meridian, Mark Levinson)",
      "Jantes alliage 18\"+ / Jantes spécifiques marque",
      "Attelage / Crochet d'attelage (fixe ou rétractable)",
      "Climatisation automatique bizone ou trizone"
    ],
    info: "Cochez chaque option présente sur votre véhicule. Plus vous sélectionnez d'options pertinentes, plus la Cote reflète la valeur réelle. Chaque équipement premium (GPS, toit ouvrant, cuir, sièges chauffants, ADAS, son premium, jantes spécifiques, attelage, clim auto) contribue à valoriser le véhicule de 0,5 à 2,5% par option. Au-delà de 6 options cochées, vous bénéficiez d'un bonus \"version bien équipée\" sur la Cote finale. L'attelage est particulièrement valorisé sur les SUV, breaks et utilitaires (caravane, remorque, porte-vélos). La climatisation automatique multi-zone est devenue un standard implicite : son absence pénalise la revente, sa présence est un argument de confort fort. Sois honnête : un acheteur vérifiera chaque option lors d'un essai." },
  { id: "options_detail", text: "Options spécifiques à très fort impact (facultatif — pour véhicules premium uniquement)", type: "text",
    placeholder: "Ex : lift system, pack carbone, Ad Personam, exhaust Akrapovic, couleur spéciale...",
    optional: true,
    info: "Sur les véhicules premium et ultra-premium, certaines options ajoutent bien plus que leur coût d'origine à la valeur de revente. Un lift system sur Lamborghini (+4%), un pack Ad Personam Ferrari (+8%), une couleur exclusive (+5-6%), des jantes forgées (+3%), un exhaust titane (+4%) ou une télémétrie (+4%) peuvent faire varier la Cote de plusieurs milliers d'euros. Laisser vide si votre véhicule n'a pas d'options à fort impact. Ce champ est facultatif." },
  { id: "urgency",    text: "Stratégie de vente souhaitée ?",                            type: "radio", options: ["Rapide (3-6 semaines)", "Normale (2-4 mois)"],
    info: "Votre stratégie de vente détermine le positionnement de prix recommandé : ‘Immédiate’ propose un prix 10% sous marché pour attirer un acheteur rapidement (idéal si vous avez besoin de liquider), ‘Rapide’ -4% pour une vente en quelques semaines à un particulier motivé, ‘Normale’ correspond au prix marché équilibré (vente sereine en 2-4 mois). Vous pourrez ajuster votre choix après le calcul." },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
// ─── LoadingExperience — écran de chargement plein espace avec visuels véhicule ─
// Remplace LoadingSteps pour l'estimation (SELL) et les recommandations (BUY).
// Affiche : images de modèles correspondants + "Algorithme VIQ.AI® Version X en
// cours d'exécution" + barre de progression + % d'avancement.
// Conçu pour exploiter tout l'espace de la modale (pas de cadre interne).
function LoadingExperience({ steps, activeIdx, make, year = "", version = "5", accent = "var(--accent)" }) {
  // Affiche le LOGO officiel de la marque (CDN fiable) + des faits qui défilent
  // (histoire / positionnement). Fallback sur une silhouette CSS si le logo ne
  // charge pas (marque inconnue, réseau, etc.).
  const makeClean = (make || "").trim();
  const makeWord = makeClean.split(/\s+/)[0] || "";
  const modelWords = makeClean.split(/\s+/).slice(1).join(" ");

  // Logo officiel de la marque (CDN fiable) + faits marque qui défilent.
  const logoUrl = useMemo(() => brandLogoUrl(makeWord), [makeWord]);
  const brandInfo = useMemo(() => getBrandInfo(makeWord), [makeWord]);
  const facts = useMemo(() => {
    if (!brandInfo) return [];
    return [brandInfo.history, brandInfo.now, `${brandInfo.country} · marque fondée en ${brandInfo.since}`].filter(Boolean);
  }, [brandInfo]);
  const [logoOk, setLogoOk] = useState(true);
  const [factIdx, setFactIdx] = useState(0);
  useEffect(() => {
    if (facts.length <= 1) return;
    const t = setInterval(() => setFactIdx(i => (i + 1) % facts.length), 3000);
    return () => clearInterval(t);
  }, [facts.length]);

  // Points de vigilance habituels du modèle concerné (problèmes connus).
  // Le backend met en cache (known_issues_cache) : l'appel ici alimente / réutilise
  // ce cache, donc pas de surcoût (l'enrichissement post-résultat sera un cache-hit).
  const [vigil, setVigil] = useState(null);
  useEffect(() => {
    if (!makeWord || !modelWords) return;
    let cancelled = false;
    apiPost("/api/vehicle/enrich", { report: { make: makeWord, model: modelWords, year: year || "" }, want_issues: true })
      .then(r => {
        if (cancelled) return;
        const ci = r && r.success && r.known_issues && Array.isArray(r.known_issues.common_issues) ? r.known_issues.common_issues : [];
        if (ci.length) setVigil(ci.slice(0, 5));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [makeWord, modelWords, year]);

  // % de progression — fluide, basé sur l'étape active
  const stepCount = steps.length || 1;
  const targetPct = Math.min(99, Math.round(((activeIdx + 1) / stepCount) * 100));
  const [displayPct, setDisplayPct] = useState(0);
  // Animer le % de façon fluide vers la cible (pas de saut brusque)
  useEffect(() => {
    let raf;
    const animate = () => {
      setDisplayPct(prev => {
        if (prev >= targetPct) return targetPct;
        const next = prev + Math.max(1, Math.ceil((targetPct - prev) / 8));
        if (next < targetPct) raf = requestAnimationFrame(animate);
        return Math.min(next, targetPct);
      });
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [targetPct]);

  const activeStep = steps[activeIdx] || {};
  const activeLabel = typeof activeStep === "string" ? activeStep : (activeStep.label || "");
  const activeDetail = typeof activeStep === "object" ? activeStep.detail : null;
  const activeIcon = typeof activeStep === "object" ? activeStep.icon : null;

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      width: "100%", minHeight: "min(72vh, 620px)",
      padding: "0",
    }}>
      {/* ── Zone visuelle véhicule — exploite toute la largeur ── */}
      <div style={{
        position: "relative", width: "100%", flex: "1 1 auto",
        minHeight: 240, maxHeight: 360,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        background: "radial-gradient(ellipse at center, rgba(168,128,58,.06), transparent 70%)",
        borderRadius: 16,
        marginBottom: "1.5rem",
      }}>
        {/* Halo lumineux animé en fond */}
        <div style={{
          position: "absolute", width: "60%", height: "60%", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(168,128,58,.12), transparent 65%)",
          filter: "blur(40px)", animation: "pulse-ring 3s ease-in-out infinite",
        }} />

        {logoUrl && logoOk ? (
          <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", maxWidth: "85%", animation: "fadeSlideIn .6s ease" }}>
            <div style={{ width: 130, height: 130, borderRadius: 20, background: "#fff", border: "1px solid var(--border)",
              display: "flex", alignItems: "center", justifyContent: "center", padding: 18, boxShadow: "0 18px 30px rgba(0,0,0,.12)" }}>
              <img src={logoUrl} alt={makeWord} onError={() => setLogoOk(false)}
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            </div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.5rem", color: "var(--text)", fontWeight: 500, letterSpacing: "-.01em", textAlign: "center" }}>
              {makeClean}
            </div>
            {facts.length > 0 && (
              <div key={factIdx} style={{ fontSize: ".82rem", color: "var(--sub)", textAlign: "center", lineHeight: 1.5, maxWidth: 460, minHeight: "2.6em", animation: "fadeSlideIn .5s ease" }}>
                {facts[factIdx]}
              </div>
            )}
          </div>
        ) : (
          // Fallback : silhouette automobile SVG stylisée + marque (logo indisponible)
          <div style={{ position: "relative", textAlign: "center", animation: "fadeSlideIn .5s ease" }}>
            <svg width="200" height="92" viewBox="0 0 200 92" fill="none" style={{ marginBottom: ".6rem", filter: "drop-shadow(0 8px 16px rgba(168,128,58,.18))" }}>
              <path d="M14 62 Q16 44 34 42 L58 40 Q70 26 92 25 L132 25 Q150 27 162 42 L182 47 Q190 49 190 58 L190 64 Q190 68 185 68 L168 68 M58 68 L128 68 M14 64 Q14 68 19 68 L34 68"
                stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="rgba(168,128,58,.05)" />
              <path d="M66 40 Q74 30 90 29 L120 29 Q136 30 146 41 Z" fill="rgba(168,128,58,.12)" stroke="var(--accent)" strokeWidth="1.5" strokeLinejoin="round" />
              <circle cx="50" cy="68" r="13" fill="var(--bg)" stroke="var(--accent)" strokeWidth="3" />
              <circle cx="150" cy="68" r="13" fill="var(--bg)" stroke="var(--accent)" strokeWidth="3" />
              <circle cx="50" cy="68" r="4" fill="var(--accent)" />
              <circle cx="150" cy="68" r="4" fill="var(--accent)" />
            </svg>
            {makeClean && (
              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.5rem", color: "var(--text)", letterSpacing: "-.01em", fontWeight: 500 }}>{makeClean}</div>
            )}
          </div>
        )}

      </div>

      {/* ── Texte algorithme en cours ── */}
      <div style={{ textAlign: "center", width: "100%", maxWidth: 540, padding: "0 1rem" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: ".55rem",
          padding: ".4rem .9rem", marginBottom: "1rem",
          background: "linear-gradient(135deg, rgba(168,128,58,.12), rgba(168,128,58,.04))",
          border: "1.5px solid rgba(168,128,58,.3)", borderRadius: 100,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent)", animation: "pulse-ring 1.4s infinite" }} />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: ".68rem", color: "var(--accent)", letterSpacing: "1.5px", fontWeight: 700, textTransform: "uppercase" }}>
            Algorithme VIQ.AI® Version {version}
          </span>
        </div>

        <div style={{
          fontFamily: "var(--font-head)", fontSize: "1.6rem", color: "var(--text)",
          letterSpacing: "-.015em", fontWeight: 500, lineHeight: 1.2, marginBottom: ".5rem",
        }}>
          en cours d'exécution…
        </div>

        {/* Étape active courante */}
        <div style={{
          fontSize: ".9rem", color: "var(--sub)", minHeight: "2.6rem",
          display: "flex", alignItems: "center", justifyContent: "center", gap: ".5rem",
          marginBottom: "1.3rem", transition: "all .35s ease",
        }} key={activeIdx}>
          {activeIcon && <span style={{ fontSize: "1.1rem" }}>{activeIcon}</span>}
          <span style={{ animation: "fadeSlideIn .4s ease" }}>{activeLabel}</span>
        </div>

        {/* Points de vigilance habituels sur le modèle concerné (pendant le calcul) */}
        {vigil && vigil.length > 0 && (
          <div style={{ textAlign: "left", maxWidth: 460, margin: "0 auto 1.2rem", background: "var(--card)", border: "1px solid var(--border)", borderLeft: "3px solid var(--accent)", borderRadius: 10, padding: ".7rem .9rem", animation: "fadeSlideIn .5s ease" }}>
            <div style={{ fontSize: ".68rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--accent)", marginBottom: ".4rem" }}>🛡 Points de vigilance habituels{makeClean ? " — " + makeClean : ""}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: ".25rem" }}>
              {vigil.map((it, i) => (
                <div key={i} style={{ fontSize: ".76rem", color: "var(--sub)", lineHeight: 1.4 }}>
                  • {it.titre}{it.zone ? <span style={{ color: "var(--muted)" }}> · {it.zone}</span> : null}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Barre de progression ── */}
        <div style={{
          height: 8, background: "var(--bg)", borderRadius: 5, overflow: "hidden",
          position: "relative", border: "1px solid var(--border)",
        }}>
          <div style={{
            height: "100%", width: `${displayPct}%`,
            background: "linear-gradient(90deg, var(--accent) 0%, #D4A455 100%)",
            borderRadius: 5,
            transition: "width .3s cubic-bezier(.4,0,.2,1)",
            boxShadow: "0 0 12px rgba(168,128,58,.5)",
          }} />
          <div style={{
            position: "absolute", top: 0, left: 0, height: "100%", width: "30%",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,.45), transparent)",
            animation: "shimmer 2s infinite", pointerEvents: "none",
          }} />
        </div>

        {/* ── % d'avancement sous le texte ── */}
        <div style={{
          marginTop: ".7rem",
          fontFamily: "var(--font-mono)", fontSize: "1.4rem", fontWeight: 700,
          color: "var(--accent)", letterSpacing: ".02em",
        }}>
          {displayPct}<span style={{ fontSize: ".9rem", color: "var(--muted)" }}>%</span>
        </div>

        {/* Détail technique discret + sécurité */}
        {activeDetail && (
          <div style={{
            fontSize: ".68rem", color: "var(--muted)", fontFamily: "var(--font-mono)",
            marginTop: ".4rem", lineHeight: 1.5, maxWidth: 460, margin: ".4rem auto 0",
            animation: "fadeSlideIn .35s ease",
          }} key={`d-${activeIdx}`}>
            {activeDetail}
          </div>
        )}
      </div>
    </div>
  );
}

// v14 — Affiche la cote 100% algo (calcul local, instantané) pendant que la
// fusion marché IA est encore en cours. Anime un "count-up" à l'arrivée de la
// valeur puis une légère pulsation pour signaler que le calcul final continue.
function PartialCoteAlgoCard({ cote }) {
  const target = Number(cote?.argus_mid) || 0;
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!target) return;
    setDisplay(0);
    const start = performance.now();
    const durationMs = 700;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round((target * eased) / 50) * 50);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [target]);
  if (!target) return null;
  return (
    <div style={{
      marginTop: "1rem", padding: ".9rem 1.1rem", background: "var(--card)",
      border: "1px solid var(--border)", borderRadius: 10, textAlign: "center",
      animation: "slideUp .35s ease",
    }}>
      <div style={{ fontSize: ".64rem", color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".8px", fontWeight: 800, marginBottom: ".3rem" }}>
        ✦ Notre Cote VIQ.AI <span style={{ color: "var(--muted)", fontWeight: 600 }}>(100% algo, déjà disponible)</span>
      </div>
      <div style={{ fontFamily: "var(--font-head)", fontSize: "1.7rem", fontWeight: 800, color: "var(--accent)" }}>
        {display.toLocaleString("fr-FR")} €
      </div>
      <div style={{ fontSize: ".7rem", color: "var(--sub)", marginTop: ".3rem", display: "flex", alignItems: "center", justifyContent: "center", gap: ".4rem" }}>
        <span className="loader-dot-pulse" style={{ width: 5, height: 5 }} />
        Fusion avec le marché temps réel en cours…
      </div>
    </div>
  );
}

function LoadingSteps({ steps, activeIdx }) {
  const sources = ["LeBonCoin", "AutoScout24", "La Centrale", "Facebook Marketplace", "Aramisauto", "ParuVendu"];
  const [srcIdx, setSrcIdx] = useState(0);
  // Compteurs animés qui défilent pour donner l'impression de "calcul en cours"
  const [tickCount, setTickCount] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setSrcIdx(i => (i+1) % sources.length);
      setTickCount(c => c + 1);
    }, 850);
    return () => clearInterval(t);
  }, []);

  // Détecter le type d'étape active pour adapter les animations
  const activeStep = steps[activeIdx] || {};
  const activeLabel = (typeof activeStep === "string" ? activeStep : activeStep.label || "").toLowerCase();
  const isSearchStep = activeLabel.includes("marché") || activeLabel.includes("prix") || activeLabel.includes("web") || activeLabel.includes("recherche");
  const isCalcStep   = activeLabel.includes("calcul") || activeLabel.includes("viq.ai") || activeLabel.includes("engine");
  const isCalibStep  = activeLabel.includes("calibration") || activeLabel.includes("bayé");
  const isZfeStep    = activeLabel.includes("zfe") || activeLabel.includes("crit'air");
  const isIssueStep  = activeLabel.includes("watchlist") || activeLabel.includes("défaut") || activeLabel.includes("mécanique");

  // Calcul du % de progression
  const pct = steps.length > 0 ? Math.round(((activeIdx + 1) / steps.length) * 100) : 0;

  return (
    <div className="loading-state" style={{ padding: "1.2rem 0" }}>
      {/* Header — badge engine + compteur étapes */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: ".55rem",
          padding: ".35rem .8rem",
          background: "linear-gradient(135deg, rgba(168,128,58,.12), rgba(168,128,58,.04))",
          border: "1.5px solid rgba(168,128,58,.3)",
          borderRadius: 100,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent)", animation: "pulse-ring 1.4s infinite" }} />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: ".68rem", color: "var(--accent)", letterSpacing: "1.5px", fontWeight: 700, textTransform: "uppercase" }}>
            VIQ.AI® Engine v5
          </span>
        </div>
        <div style={{ fontSize: ".7rem", color: "var(--sub)", fontFamily: "var(--font-mono)", fontWeight: 600 }}>
          Étape <span style={{ color: "var(--accent)", fontWeight: 700 }}>{Math.min(activeIdx + 1, steps.length)}</span> <span style={{ color: "var(--muted)" }}>/</span> <span style={{ color: "var(--text)" }}>{steps.length}</span>
        </div>
      </div>

      {/* Barre de progression */}
      <div style={{
        height: 4, background: "var(--bg)", borderRadius: 2, marginBottom: "1.2rem", overflow: "hidden", position: "relative"
      }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: "linear-gradient(90deg, var(--accent) 0%, #D4A455 100%)",
          borderRadius: 2,
          transition: "width .5s cubic-bezier(.4,0,.2,1)",
          boxShadow: "0 0 8px rgba(168,128,58,.4)",
        }} />
        {/* Glow animé qui se déplace */}
        <div style={{
          position: "absolute", top: 0, left: 0, height: "100%", width: "30%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,.4), transparent)",
          animation: "shimmer 2s infinite",
          pointerEvents: "none",
        }} />
      </div>

      {/* Liste des étapes */}
      <div className="loading-steps" style={{ maxHeight: 380, overflowY: "auto" }}>
        {steps.map((step, i) => {
          const label    = typeof step === "string" ? step : step.label;
          const detail   = typeof step === "object" ? step.detail : null;
          const icon     = typeof step === "object" ? step.icon : null;
          const isDone   = i < activeIdx;
          const isActive = i === activeIdx;
          const isFuture = i > activeIdx;
          const stepLabelLower = (label || "").toLowerCase();
          const showSources    = isActive && (stepLabelLower.includes("marché") || stepLabelLower.includes("web") || stepLabelLower.includes("temps réel"));
          const showZfeAnim    = isActive && (stepLabelLower.includes("zfe") || stepLabelLower.includes("crit"));
          const showIssueAnim  = isActive && (stepLabelLower.includes("watchlist") || stepLabelLower.includes("défaut") || stepLabelLower.includes("mécanique"));
          const showCalibAnim  = isActive && (stepLabelLower.includes("calibration") || stepLabelLower.includes("bayé"));

          return (
            <div key={i} style={{
              display: "flex", flexDirection: "column", gap: ".15rem", padding: ".4rem 0",
              opacity: isDone ? 0.4 : isActive ? 1 : 0.25,
              transition: "all .45s cubic-bezier(.4,0,.2,1)",
              transform: isActive ? "translateX(2px)" : "translateX(0)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: ".7rem" }}>
                {/* Cercle de statut */}
                <span style={{
                  width: 26, height: 26, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: isActive && icon ? ".95rem" : ".72rem",
                  flexShrink: 0,
                  background: isDone ? "var(--green)"
                            : isActive ? "linear-gradient(135deg, var(--accent), #C49654)"
                            : "transparent",
                  border: isDone ? "1px solid var(--green)"
                        : isActive ? "1.5px solid var(--accent)"
                        : "1px solid var(--border)",
                  color: isDone ? "#fff" : isActive ? "#fff" : "var(--muted)",
                  boxShadow: isActive
                    ? "0 0 0 5px rgba(168,128,58,.15), 0 0 14px rgba(168,128,58,.35), 0 2px 6px rgba(168,128,58,.2)"
                    : "none",
                  transition: "all .35s",
                  animation: isActive ? "pulse-ring 1.6s infinite" : "none",
                }}>
                  {isDone ? "✓" : isActive ? (icon || "◉") : (icon || "·")}
                </span>
                {/* Label */}
                <span style={{
                  color: isDone ? "var(--sub)" : isActive ? "var(--text)" : "var(--muted)",
                  fontWeight: isActive ? 600 : isDone ? 500 : 400,
                  fontSize: isActive ? ".88rem" : ".82rem",
                  transition: "all .3s",
                  letterSpacing: isActive ? "-.005em" : 0,
                }}>
                  {label}
                </span>
                {isActive && (
                  <span style={{
                    fontSize: ".7rem", color: "var(--accent)",
                    fontFamily: "var(--font-mono)",
                    animation: "blink 1.2s infinite",
                    marginLeft: "auto",
                  }}>●</span>
                )}
              </div>

              {/* Détail technique */}
              {detail && isActive && (
                <div style={{
                  fontSize: ".7rem", color: "var(--sub)",
                  fontFamily: "var(--font-mono)",
                  paddingLeft: "2.1rem", lineHeight: 1.5, marginTop: ".15rem",
                  animation: "fadeSlideIn .35s ease",
                }}>
                  {detail}
                </div>
              )}

              {/* Indicateur sources marché — étape recherche web */}
              {showSources && (
                <div style={{ display: "flex", gap: ".35rem", paddingLeft: "2.1rem", marginTop: ".4rem", flexWrap: "wrap" }}>
                  {sources.map((src, si) => (
                    <span key={src} style={{
                      fontSize: ".6rem", padding: "2px 8px", borderRadius: 50,
                      background: si === srcIdx ? "rgba(168,128,58,.14)" : "var(--card)",
                      border: si === srcIdx ? "1px solid rgba(168,128,58,.4)" : "1px solid var(--border)",
                      color: si === srcIdx ? "var(--accent)" : "var(--muted)",
                      fontWeight: si === srcIdx ? 700 : 400,
                      fontFamily: "var(--font-mono)",
                      transition: "all .25s",
                      transform: si === srcIdx ? "translateY(-1px)" : "none",
                    }}>{src}</span>
                  ))}
                  <span style={{ fontSize: ".6rem", color: "var(--green)", fontFamily: "var(--font-mono)", animation: "blink 1s step-end infinite", marginLeft: ".2rem" }}>●</span>
                </div>
              )}

              {/* Animation ZFE — défilement métropoles */}
              {showZfeAnim && (
                <div style={{ display: "flex", gap: ".3rem", paddingLeft: "2.1rem", marginTop: ".4rem", flexWrap: "wrap", fontFamily: "var(--font-mono)" }}>
                  {["Paris", "Lyon", "Marseille", "Toulouse", "Lille", "Strasbourg"].map((m, mi) => (
                    <span key={m} style={{
                      fontSize: ".6rem", padding: "2px 7px", borderRadius: 50,
                      background: mi === (tickCount % 6) ? "rgba(155,44,44,.1)" : "var(--card)",
                      border: mi === (tickCount % 6) ? "1px solid rgba(155,44,44,.35)" : "1px solid var(--border)",
                      color: mi === (tickCount % 6) ? "var(--red)" : "var(--muted)",
                      fontWeight: mi === (tickCount % 6) ? 700 : 400,
                      transition: "all .25s",
                    }}>{m}</span>
                  ))}
                </div>
              )}

              {/* Animation watchlist mécanique — défilement défauts */}
              {showIssueAnim && (
                <div style={{ display: "flex", gap: ".3rem", paddingLeft: "2.1rem", marginTop: ".4rem", flexWrap: "wrap", fontFamily: "var(--font-mono)" }}>
                  {["DSG7", "BMW N47", "EA189", "Puretech", "EcoBoost", "OM651"].map((d, di) => (
                    <span key={d} style={{
                      fontSize: ".6rem", padding: "2px 7px", borderRadius: 50,
                      background: di === (tickCount % 6) ? "rgba(168,128,58,.12)" : "var(--card)",
                      border: di === (tickCount % 6) ? "1px solid rgba(168,128,58,.35)" : "1px solid var(--border)",
                      color: di === (tickCount % 6) ? "var(--accent)" : "var(--muted)",
                      fontWeight: di === (tickCount % 6) ? 700 : 400,
                      transition: "all .25s",
                    }}>{d}</span>
                  ))}
                </div>
              )}

              {/* Animation calibration Bayésienne — compteur de ventes apprises */}
              {showCalibAnim && (
                <div style={{ paddingLeft: "2.1rem", marginTop: ".4rem", display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
                  <span style={{
                    fontSize: ".62rem", padding: "2px 8px", borderRadius: 4,
                    background: "rgba(46,111,92,.08)", border: "1px solid rgba(46,111,92,.25)",
                    color: "var(--green)", fontFamily: "var(--font-mono)", fontWeight: 600,
                  }}>
                    {(247 + tickCount * 13).toLocaleString("fr-FR")} ventes analysées
                  </span>
                  <span style={{ fontSize: ".6rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
                    · {Math.min(99, 60 + tickCount)}% confiance
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer — message rassurant + animation */}
      <div style={{
        marginTop: "1.2rem", paddingTop: ".9rem",
        borderTop: "1px dashed var(--border)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".6rem", flexWrap: "wrap",
      }}>
        <div style={{ fontSize: ".68rem", color: "var(--muted)", display: "inline-flex", alignItems: "center", gap: ".4rem" }}>
          <span style={{
            display: "inline-block", width: 6, height: 6, borderRadius: "50%",
            background: "var(--green)", animation: "blink 1.2s step-end infinite",
          }} />
          <span>Algorithme propriétaire VehicleIQ · Calcul en cours</span>
        </div>
        <div style={{ fontSize: ".62rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
          ~{Math.max(3, steps.length - activeIdx)} sec restantes
        </div>
      </div>
    </div>
  );
}

// ─── Rechargement wallet ─────────────────────────────────────────────────────
// ─── Politique tarifaire v2 — recalibrée sur CAC réel (cofondateur) ────────
// Vrais chiffres terrain : CAC ~946 €, marge brute cible 50 %, durée vie 40 mois
const PACKS = [
  // Particuliers : Découverte / Test / Activité régulière
  { qty: 5,  price: 0,    label: "Découverte",         per: "Offert",       best: false, badge: "1ère utilisation",  free: true,
    features: ["✓ Cote VIQ.AI®","✓ Contrôle VIN gratuit","✓ Sans CB requise"] },
  { qty: 10, price: 49.9, label: "Test",               per: "4,99 €/réq.",  best: false, badge: null,
    features: ["✓ Tout Découverte","✓ Recommandations achat","✓ Historique 30 jours"] },
  { qty: 30, price: 149.7, label: "Activité régulière", per: "4,99 €/réq.",  best: true,  badge: "⭐ Le plus choisi",
    features: ["✓ Tout Test","✓ Export PDF","✓ Historique illimité","✓ Suivi de vente"] },
];

const PACKS_PRO = [
  // Pros : Volume / Pro intensif / Intégration API
  { qty: 100, price: 499,    label: "Volume",       per: "4,99 €/réq.",  best: false, badge: "Marchands & Garages",
    features: ["✓ Tout Activité régulière","✓ Outil de suivi métier","✓ Comparatif 5 plateformes","✓ Stats d'usage"] },
  { qty: 300, price: 1497,   label: "Pro intensif", per: "4,99 €/réq.",  best: true,  badge: "⭐ Recommandé pros",
    features: ["✓ Tout Volume","✓ Impact économique cumulé","✓ Gains VIQ.AI® mesurés","🔜 Support sous 24h"] },
  // Pack API : flux différent → mailto contact@vehicleiq.fr (engagement volume, pas de paiement Stripe direct)
  { qty: 500,  price: 2500.00, label: "Intégration API", per: "5 €/crédit", best: false, badge: "Premium · Min. 2 500 € HT", type: "api", features: ["👤 Compte dédié","🛡️ SLA 99,5%","🧾 Facturation mensualisée"] },
];
const COSTS = { check: 2, buy: 2, sell: 3 };

// ─── PaymentSettingsTab — Onglet Paiement dans Mon espace ────────────────────
// Permet à l'utilisateur de :
//  • Voir sa carte enregistrée (brand · last4 · expiration)
//  • Mettre à jour sa carte (ouvre une mini-modale Stripe)
//  • Voir et annuler son abonnement Pro Solo (si actif)
//  • Reprendre un abonnement annulé (resume si cancel_at_period_end est true)
// ────────────────────────────────────────────────────────────────────────────
// v109 — Affichage prix avec promotion admin : ancien prix barré + nouveau
// prix réduit quand une promo est active pour ce plan/période, sinon le prix
// normal seul. Partagé par tous les emplacements qui affichent un tarif
// d'abonnement (page tarifs, paramètres de paiement, modales de souscription).
function PromoPriceLine({ promo, normalLabel, size = ".8rem" }) {
  if (!promo || !promo.discountPct) {
    return <span style={{ fontFamily: "var(--font-mono)", fontSize: size, color: "var(--muted)" }}>{normalLabel}</span>;
  }
  const fmt = (c) => `${(c / 100).toLocaleString("fr-FR", { minimumFractionDigits: (c % 100) ? 2 : 0 })} €`;
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: ".4rem", flexWrap: "wrap" }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: size, color: "var(--muted)", textDecoration: "line-through" }}>
        {fmt(promo.basePriceCents)}
      </span>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: size, color: "var(--red)", fontWeight: 800 }}>
        {fmt(promo.finalPriceCents)}
      </span>
      {promo.label && (
        <span style={{ fontSize: ".64rem", fontWeight: 700, color: "#fff", background: "var(--red)", borderRadius: 6, padding: ".1rem .45rem" }}>
          {promo.label}
        </span>
      )}
    </span>
  );
}

function PaymentSettingsTab({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showUpdateCard, setShowUpdateCard] = useState(false);
  const [showSubscribeUnlimited, setShowSubscribeUnlimited] = useState(false);
  const [showSubscribeMultiAgent, setShowSubscribeMultiAgent] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [actioning, setActioning] = useState(false);
  // v70 — Toggle Mensuel/Annuel : composant d'interface uniquement (règle 3),
  // n'affecte ni Stripe ni la logique de paiement — "Souscrire" reste la
  // souscription mensuelle existante quel que soit le réglage affiché ici.
  const [billingCycle, setBillingCycle] = useState("monthly"); // monthly | annual
  // v109 — Promotions admin actives sur les abonnements (ancien prix barré /
  // nouveau prix réduit), affichées ici comme sur la page tarifs publique.
  const [promotions, setPromotions] = useState(null);
  useEffect(() => { apiGet("/api/plans/promotions").then(r => { if (r?.success) setPromotions(r.promotions); }).catch(() => {}); }, []);

  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const h = await getAuthHeaders();
      if (!h) { setError("Connectez-vous"); setLoading(false); return; }
      const r = await fetch(`${API_URL}/api/billing/current`, { headers: h });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Erreur");
      setData(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) reload(); }, [user, reload]);

  // v5 : auto-déclenchement de la modale Multi-Agents si intent en session
  // (l'utilisateur a cliqué "Souscrire Multi-Agents" depuis la PricingModal)
  useEffect(() => {
    if (!data || data.subscription) return; // pas de trigger si déjà abonné
    try {
      const intent = sessionStorage.getItem("viq_subscribe_intent");
      if (intent === "multi_agent_3" && data.has_payment_method && user?.siret) {
        sessionStorage.removeItem("viq_subscribe_intent");
        setShowSubscribeMultiAgent(true);
      } else if (intent === "unlimited" && data.has_payment_method) {
        sessionStorage.removeItem("viq_subscribe_intent");
        setShowSubscribeUnlimited(true);
      } else if ((intent === "multi_agent_3" || intent === "unlimited") && !data.has_payment_method) {
        // Carte non encore enregistrée : on garde l'intent, l'utilisateur doit d'abord ajouter sa carte
        // L'intent sera consommé après ajout de carte (au prochain reload).
      }
    } catch (_) { /* sessionStorage indisponible */ }
  }, [data, user]);

  const handleCancel = async () => {
    setActioning(true); setError("");
    try {
      const h = await getAuthHeaders();
      const r = await fetch(`${API_URL}/api/billing/cancel-subscription`, { method: "POST", headers: { ...h, "Content-Type": "application/json" } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Annulation impossible");
      setConfirmCancel(false);
      await reload();
    } catch (e) { setError(e.message); }
    finally { setActioning(false); }
  };

  const handleResume = async () => {
    setActioning(true); setError("");
    try {
      const h = await getAuthHeaders();
      const r = await fetch(`${API_URL}/api/billing/resume-subscription`, { method: "POST", headers: { ...h, "Content-Type": "application/json" } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Reprise impossible");
      await reload();
    } catch (e) { setError(e.message); }
    finally { setActioning(false); }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: "3rem", color: "var(--sub)" }}><div className="spinner" style={{ margin: "0 auto .6rem" }} />Chargement…</div>;
  }

  const brandLabel = (b) => ({ visa: "Visa", mastercard: "Mastercard", amex: "American Express", cb: "Carte Bancaire" }[b?.toLowerCase()] || b || "Carte");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem" }}>
      {error && (
        <div style={{ background: "var(--red-soft)", border: "1px solid var(--red)", color: "var(--red)", padding: ".7rem 1rem", borderRadius: 10, fontSize: ".85rem" }}>
          {error}
        </div>
      )}

      {/* Moyen de paiement */}
      <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: ".9rem", flexWrap: "wrap", gap: ".5rem" }}>
          <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)" }}>
            💳 Moyen de paiement
          </div>
          {data?.has_payment_method && (
            <button
              onClick={() => setShowUpdateCard(true)}
              style={{ background: "transparent", border: "1.5px solid var(--border)", color: "var(--text)", padding: ".4rem .9rem", borderRadius: 8, fontSize: ".78rem", cursor: "pointer", fontWeight: 600 }}>
              Modifier
            </button>
          )}
        </div>
        {data?.has_payment_method && data.card ? (
          <div style={{ display: "flex", alignItems: "center", gap: ".9rem", padding: ".9rem 1.1rem", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10 }}>
            <div style={{
              fontSize: ".7rem", fontWeight: 700, color: "var(--text)",
              background: "var(--white)", border: "1.5px solid var(--border)",
              padding: ".25rem .6rem", borderRadius: 6, fontFamily: "var(--font-mono)", textTransform: "uppercase", letterSpacing: ".5px",
            }}>
              {brandLabel(data.card.brand)}
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: ".95rem", color: "var(--text)", fontWeight: 600 }}>
              •••• •••• •••• {data.card.last4}
            </div>
            <div style={{ marginLeft: "auto", fontSize: ".74rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>
              Expire {String(data.card.exp_month).padStart(2, "0")}/{String(data.card.exp_year).slice(-2)}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".8rem", padding: "1rem", background: "var(--bg)", border: "1px dashed var(--border)", borderRadius: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: ".84rem", color: "var(--sub)", lineHeight: 1.5 }}>
              Aucune carte enregistrée. Ajoutez votre moyen de paiement pour activer votre compte.
            </div>
            <button
              onClick={() => setShowUpdateCard(true)}
              className="btn btn-accent btn-sm">
              + Ajouter une carte
            </button>
          </div>
        )}
      </div>

      {/* Abonnement Pro Solo */}
      <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
        <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)", marginBottom: ".9rem" }}>
          🚀 Mon abonnement
        </div>
        {data?.subscription ? (
          <div>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem",
              padding: "1rem 1.2rem",
              background: data.subscription.cancel_at_period_end ? "rgba(155,44,44,.04)" : "rgba(46,111,92,.05)",
              border: `1.5px solid ${data.subscription.cancel_at_period_end ? "rgba(155,44,44,.2)" : "rgba(46,111,92,.2)"}`,
              borderRadius: 10, flexWrap: "wrap",
            }}>
              <div>
                <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".3rem" }}>
                  Plan actif
                </div>
                <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)", letterSpacing: "-.01em" }}>
                  Pro Solo — {(data.subscription.amount_cents / 100).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} € / mois
                </div>
                <div style={{ fontSize: ".75rem", color: "var(--sub)", marginTop: ".2rem" }}>
                  {data.subscription.cancel_at_period_end ? (
                    <>⚠️ Annulation programmée — accès jusqu'au {new Date(data.subscription.current_period_end_iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</>
                  ) : (
                    <>Prochain prélèvement le {new Date(data.subscription.current_period_end_iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</>
                  )}
                </div>
              </div>
              {data.subscription.cancel_at_period_end ? (
                <button
                  onClick={handleResume}
                  disabled={actioning}
                  className="btn btn-accent btn-sm"
                  style={{ minWidth: 140 }}>
                  {actioning ? "..." : "↻ Reprendre"}
                </button>
              ) : (
                <button
                  onClick={() => setConfirmCancel(true)}
                  disabled={actioning}
                  style={{ background: "transparent", border: "1.5px solid var(--red)", color: "var(--red)", padding: ".5rem 1rem", borderRadius: 8, fontSize: ".78rem", cursor: "pointer", fontWeight: 600 }}>
                  Annuler l'abonnement
                </button>
              )}
            </div>
            {confirmCancel && !data.subscription.cancel_at_period_end && (
              <div style={{ marginTop: ".9rem", padding: "1rem 1.2rem", background: "var(--red-soft)", border: "1.5px solid var(--red)", borderRadius: 10 }}>
                <div style={{ fontSize: ".82rem", color: "var(--text)", marginBottom: ".7rem", lineHeight: 1.5 }}>
                  <strong>Annuler votre abonnement Pro Solo ?</strong><br />
                  Vous garderez l'accès jusqu'au {new Date(data.subscription.current_period_end_iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}, puis vous reviendrez automatiquement au paiement à l'usage (4,99 € par dossier).
                </div>
                <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap" }}>
                  <button onClick={handleCancel} disabled={actioning} style={{ background: "var(--red)", border: "none", color: "#fff", padding: ".55rem 1.1rem", borderRadius: 8, fontSize: ".82rem", cursor: "pointer", fontWeight: 600 }}>
                    {actioning ? "Annulation..." : "Confirmer l'annulation"}
                  </button>
                  <button onClick={() => setConfirmCancel(false)} className="btn btn-ghost btn-sm">
                    Garder mon abonnement
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
          {/* v70 — Règle 3 : toggle Mensuel/Annuel, très visible, composant
              d'interface uniquement — n'affecte pas Stripe ni la souscription. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: ".8rem", margin: "0 0 1.1rem" }}>
            <span style={{ fontSize: ".84rem", fontWeight: 700, color: billingCycle === "monthly" ? "var(--text)" : "var(--muted)" }}>Mensuel</span>
            <button type="button" onClick={() => setBillingCycle(c => c === "monthly" ? "annual" : "monthly")}
              style={{ position: "relative", width: 52, height: 28, borderRadius: 20, border: "1.5px solid var(--accent)", background: billingCycle === "annual" ? "var(--accent)" : "var(--card)", cursor: "pointer", flexShrink: 0, transition: "background .15s" }}
              aria-label="Basculer entre facturation mensuelle et annuelle">
              <span style={{ position: "absolute", top: 2, left: billingCycle === "annual" ? 26 : 2, width: 22, height: 22, borderRadius: "50%", background: billingCycle === "annual" ? "#fff" : "var(--accent)", transition: "left .15s" }} />
            </button>
            <span style={{ fontSize: ".84rem", fontWeight: 700, color: billingCycle === "annual" ? "var(--text)" : "var(--muted)" }}>Annuel</span>
            {billingCycle === "annual" && (
              <span style={{ fontSize: ".68rem", fontWeight: 700, color: "var(--green)", background: "rgba(46,111,92,.1)", border: "1px solid rgba(46,111,92,.3)", borderRadius: 10, padding: ".15rem .55rem" }}>
                jusqu'à 48% d'économie
              </span>
            )}
          </div>
          <div style={{ background: "linear-gradient(135deg, rgba(46,111,92,.05), rgba(46,111,92,.01))", border: "1.5px solid rgba(46,111,92,.2)", borderRadius: 10, padding: "1.2rem 1.4rem" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: ".8rem" }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: ".84rem", color: "var(--sub)", lineHeight: 1.55, marginBottom: ".6rem" }}>
                  Vous êtes au tarif à l'usage <strong style={{ color: "var(--accent)" }}>(4,99 €/dossier, tarif unique)</strong>.
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: ".5rem", flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", color: "var(--green)", fontWeight: 600, letterSpacing: "-.01em" }}>
                    Passer à l'offre Pro Solo
                  </span>
                  <PromoPriceLine
                    promo={promotions?.unlimited?.[billingCycle]}
                    normalLabel={billingCycle === "annual" ? "2 999 €/an (soit ~250 €/mois) · cotes illimitées" : "399 €/mois · cotes illimitées"}
                  />
                </div>
                {billingCycle === "annual" && (
                  <div style={{ fontSize: ".72rem", color: "var(--green)", marginTop: ".3rem" }}>
                    Économie d'environ 37 % par rapport à la facturation mensuelle (399 € × 12 = 4 788 €/an).
                  </div>
                )}
                <div style={{ fontSize: ".74rem", color: "var(--sub)", marginTop: ".5rem", lineHeight: 1.5 }}>
                  Rentable dès <strong>82 cotes/mois</strong>. Résiliable à tout moment. Facturation {billingCycle === "annual" ? "annuelle" : "mensuelle"} automatique sur votre carte enregistrée.
                </div>
              </div>
              <button
                onClick={() => setShowSubscribeUnlimited(true)}
                disabled={!data?.has_payment_method}
                className="btn btn-accent"
                style={{ alignSelf: "flex-start", padding: ".7rem 1.4rem", whiteSpace: "nowrap" }}
                title={!data?.has_payment_method ? "Ajoutez d'abord une carte bancaire ci-dessus" : ""}>
                Souscrire →
              </button>
            </div>
            {!data?.has_payment_method && (
              <div style={{ fontSize: ".7rem", color: "var(--accent)", padding: ".4rem .6rem", background: "rgba(168,128,58,.05)", borderRadius: 6, border: "1px dashed rgba(168,128,58,.3)" }}>
                ⚠ Ajoutez d'abord une carte bancaire dans la section "Moyen de paiement" ci-dessus pour pouvoir souscrire.
              </div>
            )}
          </div>

          {/* v5 : Multi-Agents — visible uniquement pour les pros (SIRET) */}
          {user?.siret && (
            <div style={{ background: "linear-gradient(135deg, rgba(30,42,74,.04), rgba(30,42,74,.01))", border: "1.5px solid rgba(30,42,74,.2)", borderRadius: 10, padding: "1.2rem 1.4rem", marginTop: "1rem" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: ".8rem" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", fontSize: ".58rem", padding: ".18rem .55rem", background: "rgba(30,42,74,.1)", color: "var(--accent2)", border: "1px solid rgba(30,42,74,.25)", borderRadius: 100, fontFamily: "var(--font-mono)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".5rem" }}>
                    👥 Pour les organisations
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: ".5rem", flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", color: "var(--accent2)", fontWeight: 600, letterSpacing: "-.01em" }}>
                      Plan Pro Multi-Agent
                    </span>
                    <PromoPriceLine
                      promo={promotions?.multi_agent_3?.[billingCycle]}
                      normalLabel={billingCycle === "annual" ? "4 999 €/an (soit ~417 €/mois) · 3 agents inclus" : "799 €/mois · 3 agents inclus"}
                    />
                  </div>
                  {billingCycle === "annual" && (
                    <div style={{ fontSize: ".72rem", color: "var(--green)", marginTop: ".3rem" }}>
                      Économie d'environ 48 % par rapport à la facturation mensuelle (799 € × 12 = 9 588 €/an).
                    </div>
                  )}
                  <div style={{ fontSize: ".74rem", color: "var(--sub)", marginTop: ".5rem", lineHeight: 1.55 }}>
                    Tout le Pro Solo + <strong>10 cotes en parallèle</strong> (vs 3) + <strong>30 recos d'achat / requête</strong> (vs 10) + compte dédié + SLA 99,5 % + support prioritaire 24h. Organisation créée automatiquement.
                  </div>
                </div>
                <button
                  onClick={() => setShowSubscribeMultiAgent(true)}
                  disabled={!data?.has_payment_method}
                  className="btn btn-accent"
                  style={{ alignSelf: "flex-start", padding: ".7rem 1.4rem", whiteSpace: "nowrap", background: "var(--accent2)", border: "none", color: "#fff" }}
                  title={!data?.has_payment_method ? "Ajoutez d'abord une carte bancaire ci-dessus" : ""}>
                  Souscrire →
                </button>
              </div>
            </div>
          )}
          </>
        )}

        {/* v5 : lien CGV en bas de l'onglet paiement */}
        <div style={{
          marginTop: "1.5rem", padding: "1rem 0 0",
          borderTop: "1px dashed var(--border)",
          fontSize: ".72rem", color: "var(--muted)", lineHeight: 1.6, textAlign: "center",
        }}>
          En souscrivant, vous acceptez nos{" "}
          <button
            onClick={() => { try { window.dispatchEvent(new CustomEvent("viq-open-legal", { detail: "cgv" })); } catch(_) {} }}
            style={{ background: "none", border: "none", color: "var(--accent)", padding: 0, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit", fontSize: "inherit" }}>
            Conditions Générales de Vente
          </button>.
          {" "}TVA non applicable, art. 293 B du CGI · M.K.L Ingénierie et Conseil — SIRET 904 954 542 00026.
        </div>
      </div>

      {/* Sous-modale : souscription Pro Solo */}
      {showSubscribeUnlimited && (
        <SubscribeUnlimitedModal onClose={() => { setShowSubscribeUnlimited(false); reload(); }} user={user} card={data?.card} period={billingCycle} />
      )}

      {/* Sous-modale : souscription Pro Multi-Agent 799€/mois */}
      {showSubscribeMultiAgent && (
        <SubscribeMultiAgentModal onClose={() => { setShowSubscribeMultiAgent(false); reload(); }} user={user} card={data?.card} period={billingCycle} />
      )}

      {/* Sous-modale : update card */}
      {showUpdateCard && (
        <UpdateCardModal onClose={() => { setShowUpdateCard(false); reload(); }} user={user} />
      )}
    </div>
  );
}

// ─── UpdateCardModal — mini-modale pour changer/ajouter la carte enregistrée ──
function UpdateCardModal({ onClose, user }) {
  const STRIPE_PK = "pk_live_51TMxmrEGRqmRjda5bmVRtBgxOX9l5PU3KNeReDppBNj20MQsSVbAnFIPkKYvxALwGV9J4duNVdupLfllQBeQtSnk00aQbpXWsb";
  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const [stripeReady, setStripeReady] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Charger Stripe
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!window.Stripe) {
          await new Promise((resolve, reject) => {
            const existing = document.querySelector('script[src*="stripe.com/v3"]');
            if (existing) { existing.addEventListener("load", resolve); existing.addEventListener("error", reject); return; }
            const s = document.createElement("script");
            s.src = "https://js.stripe.com/v3/";
            s.onload = resolve; s.onerror = reject;
            document.head.appendChild(s);
          });
        }
        if (cancelled || !window.Stripe) return;
        stripeRef.current = window.Stripe(STRIPE_PK);
        setStripeReady(true);
      } catch (e) {
        if (!cancelled) setError("Module de paiement indisponible.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const cardMountCallback = useCallback((node) => {
    if (!node || !stripeRef.current || cardRef.current) return;
    const elements = stripeRef.current.elements();
    const card = elements.create("card", {
      style: {
        base: { fontSize: "16px", fontFamily: "Manrope, Inter, system-ui, sans-serif", color: "#15171C", lineHeight: "24px", "::placeholder": { color: "#8E939E" }, iconColor: "#A8803A" },
        invalid: { color: "#9B2C2C", iconColor: "#9B2C2C" },
      },
      hidePostalCode: true,
    });
    card.mount(node);
    cardRef.current = card;
    card.on("change", e => { setCardComplete(!!e.complete); if (e.error) setError(e.error.message); else setError(""); });
  }, [stripeReady]);

  useEffect(() => {
    return () => {
      if (cardRef.current) { try { cardRef.current.unmount(); cardRef.current.destroy(); } catch(_) {} cardRef.current = null; }
    };
  }, []);

  const handleSubmit = async () => {
    if (!cardComplete) return;
    setSaving(true); setError("");
    try {
      // 1) initier SetupIntent
      const h = await getAuthHeaders();
      const r = await fetch(`${API_URL}/api/billing/update-card-init`, { method: "POST", headers: { ...h, "Content-Type": "application/json" } });
      const initData = await r.json();
      if (!r.ok || !initData.clientSecret) throw new Error(initData.error || "Initialisation échouée");
      // 2) confirmer côté Stripe
      const { setupIntent, error: stripeErr } = await stripeRef.current.confirmCardSetup(initData.clientSecret, {
        payment_method: { card: cardRef.current, billing_details: { email: user?.email || undefined } }
      });
      if (stripeErr) throw new Error(stripeErr.message);
      if (setupIntent?.status !== "succeeded") throw new Error("Validation refusée : " + setupIntent?.status);
      // 3) confirmer côté backend
      const r2 = await fetch(`${API_URL}/api/billing/update-card-confirm`, {
        method: "POST",
        headers: { ...h, "Content-Type": "application/json" },
        body: JSON.stringify({ paymentMethodId: setupIntent.payment_method }),
      });
      const cd = await r2.json();
      if (!r2.ok) throw new Error(cd.error || "Erreur lors de l'enregistrement");
      setDone(true);
      setTimeout(() => onClose && onClose(), 1200);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 480, animation: "slideUp .25s ease" }}>
        <div className="panel-header">
          <div>
            <div className="panel-title">Mettre à jour ma carte</div>
            <div className="panel-sub">Votre nouvelle carte deviendra la méthode de paiement par défaut.</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {done ? (
            <div style={{ textAlign: "center", padding: "1.5rem 1rem" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: ".6rem" }}>✅</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.2rem", color: "var(--text)" }}>Carte mise à jour</div>
            </div>
          ) : (
            <>
              <div>
                <label style={{ fontSize: ".7rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, display: "block", marginBottom: ".4rem" }}>
                  Nouvelle carte
                </label>
                <div ref={cardMountCallback} style={{
                  padding: "14px 16px", background: "var(--white)",
                  border: "1.5px solid var(--border)", borderRadius: 10, minHeight: 52,
                }}>
                  {!stripeReady && (
                    <div style={{ display: "flex", alignItems: "center", gap: ".5rem", color: "var(--muted)", fontSize: ".82rem" }}>
                      <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      Chargement…
                    </div>
                  )}
                </div>
                <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".4rem" }}>
                  🔒 Aucun débit. Données chiffrées par Stripe (PCI DSS).
                </div>
              </div>
              {error && <div style={{ background: "var(--red-soft)", border: "1px solid var(--red)", color: "var(--red)", padding: ".5rem .8rem", borderRadius: 8, fontSize: ".8rem" }}>{error}</div>}
              <button className="btn btn-accent" disabled={saving || !cardComplete} onClick={handleSubmit} style={{ width: "100%", padding: ".7rem 1rem", justifyContent: "center" }}>
                {saving ? "Enregistrement…" : "Enregistrer la nouvelle carte"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── SubscribeUnlimitedModal — souscrire au plan Pro Solo 399€/mois ──────────
function SubscribeUnlimitedModal({ onClose, user, card, period = "monthly" }) {
  // v31 — Montant affiché selon la période choisie dans PricingModal.
  const isAnnual = period === "annual";
  const baseAmount = isAnnual ? 2999 : 399;
  const periodLabel = isAnnual ? "an" : "mois";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  // Acceptation des CGU/CGV — obligatoire avant souscription
  const [cgvAccepted, setCgvAccepted] = useState(false);
  // Code apporteur — pré-rempli si déjà rattaché lors de l'activation carte
  const [affiliateCode, setAffiliateCode] = useState(
    (user?.referred_by_affiliate || "").toUpperCase()
  );
  const alreadyReferred = !!(user?.referred_by_affiliate);
  const [affiliateStatus, setAffiliateStatus] = useState(alreadyReferred ? "valid" : null); // null | checking | valid | invalid
  const [affiliateName, setAffiliateName] = useState("");
  // v109 — Promotion admin éventuelle : réduit le prix de base AVANT le calcul
  // de la réduction apporteur — même ordre que côté serveur (subscribe-unlimited),
  // pour que le prix annoncé ici soit TOUJOURS celui réellement facturé.
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    apiGet("/api/plans/promotions").then(r => { if (r?.success) setPromo(r.promotions?.unlimited?.[period] || null); }).catch(() => {});
  }, [period]);
  const promoBaseAmount = promo?.discountPct ? Math.round(promo.finalPriceCents / 100) : baseAmount;
  // v81 — Règle : réduction réelle accordée sur présentation d'un code
  // apporteur d'affaires valide — affichée ici pour que le prix annoncé soit
  // TOUJOURS celui effectivement facturé (jamais une estimation à part).
  const [discountPct, setDiscountPct] = useState(0);
  const discountedAmount = discountPct > 0 ? Math.round(promoBaseAmount * (1 - discountPct / 100)) : promoBaseAmount;
  const amountLabel = discountedAmount.toLocaleString("fr-FR") + " €";
  // Vérification live du code apporteur (obligatoire) — identifie l'apporteur à chaque souscription.
  useEffect(() => {
    if (alreadyReferred) {
      // Le code est déjà rattaché : on relit quand même la réduction en vigueur.
      fetch(`${API_URL}/api/affiliate/check/${encodeURIComponent(affiliateCode)}`).then(r => r.json()).then(d => {
        if (d?.success) setDiscountPct(Number(isAnnual ? d.discount_pct_annual : d.discount_pct_monthly) || 0);
      }).catch(() => {});
      setAffiliateStatus("valid");
      return;
    }
    const code = affiliateCode.trim().toUpperCase();
    if (!code || code.length < 3) { setAffiliateStatus(null); setAffiliateName(""); setDiscountPct(0); return; }
    let cancelled = false; setAffiliateStatus("checking");
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/affiliate/check/${encodeURIComponent(code)}`);
        const d = await r.json();
        if (cancelled) return;
        if (d?.success) {
          setAffiliateStatus("valid"); setAffiliateName(d.name || "");
          setDiscountPct(Number(isAnnual ? d.discount_pct_annual : d.discount_pct_monthly) || 0);
        } else { setAffiliateStatus("invalid"); setAffiliateName(""); setDiscountPct(0); }
      } catch (e) { if (!cancelled) setAffiliateStatus(null); }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [affiliateCode, alreadyReferred]);
  const [showCgvViewer, setShowCgvViewer] = useState(false);

  const brandLabel = (b) => ({ visa: "Visa", mastercard: "Mastercard", amex: "American Express", cb: "Carte Bancaire" }[b?.toLowerCase()] || b || "Carte");

  const handleSubscribe = async () => {
    if (!cgvAccepted) { setError("Veuillez accepter les Conditions Générales d'Utilisation et de Vente."); return; }
    if (!alreadyReferred && affiliateStatus !== "valid") { setError("Un code apporteur d'affaires valide est obligatoire pour souscrire."); return; }
    setSaving(true); setError("");
    try {
      const h = await getAuthHeaders();
      const r = await fetch(`${API_URL}/api/billing/subscribe-unlimited`, {
        method: "POST",
        headers: { ...h, "Content-Type": "application/json" },
        body: JSON.stringify({ affiliate_code: affiliateCode.trim().toUpperCase() || null, period }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Souscription échouée");
      setShowCgvViewer(true);
      setDone(true);
      setTimeout(() => onClose && onClose(), 4000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
    {showCgvViewer && <CGVViewerModal onClose={() => setShowCgvViewer(false)} autoDownload={true} />}
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 520, animation: "slideUp .25s ease" }}>
        <div className="panel-header">
          <div>
            <div className="panel-title">Souscrire à l'offre Pro Solo</div>
            <div className="panel-sub">{amountLabel} / {periodLabel} · cotes illimitées · résiliable à tout moment</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {done ? (
            <div style={{ textAlign: "center", padding: "1.8rem 1rem" }}>
              <div style={{ fontSize: "3rem", marginBottom: ".6rem" }}>🚀</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", color: "var(--green)", letterSpacing: "-.01em" }}>Bienvenue dans l'offre Pro Solo</div>
              <div style={{ fontSize: ".85rem", color: "var(--sub)", marginTop: ".5rem" }}>Vos cotes sont désormais illimitées.</div>
            </div>
          ) : (
            <>
              {/* Récap offre */}
              <div style={{
                background: "linear-gradient(135deg, rgba(46,111,92,.06), rgba(46,111,92,.01))",
                border: "1.5px solid rgba(46,111,92,.2)",
                borderRadius: 12, padding: "1.1rem 1.3rem",
              }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: ".5rem", marginBottom: ".5rem" }}>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", color: "var(--text)", letterSpacing: "-.01em" }}>
                    Plan Pro Solo
                  </div>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: "1.6rem", color: "var(--green)", fontWeight: 600, textAlign: "right" }}>
                    {promo?.discountPct > 0 && (
                      <div style={{ fontSize: ".85rem", color: "var(--muted)", textDecoration: "line-through", fontWeight: 400 }}>{baseAmount.toLocaleString("fr-FR")} €</div>
                    )}
                    {amountLabel} <span style={{ fontSize: ".75rem", color: "var(--sub)" }}>/ {periodLabel}</span>
                  </div>
                </div>
                {promo?.discountPct > 0 && promo.label && (
                  <div style={{ fontSize: ".68rem", fontWeight: 700, color: "#fff", background: "var(--red)", borderRadius: 6, padding: ".15rem .5rem", display: "inline-block", marginBottom: ".5rem" }}>
                    {promo.label}
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: ".35rem", marginTop: ".6rem" }}>
                  {[
                    "Cotes illimitées",
                    "Outil de suivi métier complet",
                    "Comparatif 5 plateformes",
                    "Suivi de vente · marges trackées",
                    "Export PDF · rapport détaillé",
                    "Résiliable à tout moment",
                    "Accès au Service Photo IA (sur devis)",
                    "Accès à la Diffusion Multiplateformes (sur devis)",
                  ].map((f, i) => (
                    <div key={i} style={{ fontSize: ".78rem", color: "var(--sub)" }}>
                      <span style={{ color: "var(--green)", fontWeight: 700, marginRight: ".4rem" }}>✓</span>{f}
                    </div>
                  ))}
                </div>
              </div>

              {/* Carte qui sera débitée */}
              {card && (
                <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, padding: ".8rem 1.1rem" }}>
                  <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".4rem" }}>
                    Carte qui sera débitée
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: ".7rem" }}>
                    <div style={{
                      fontSize: ".7rem", fontWeight: 700, color: "var(--text)",
                      background: "var(--white)", border: "1.5px solid var(--border)",
                      padding: ".2rem .55rem", borderRadius: 6, fontFamily: "var(--font-mono)", textTransform: "uppercase",
                    }}>
                      {brandLabel(card.brand)}
                    </div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: ".9rem", color: "var(--text)" }}>
                      •••• {card.last4}
                    </div>
                    <div style={{ marginLeft: "auto", fontSize: ".7rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>
                      {String(card.exp_month).padStart(2, "0")}/{String(card.exp_year).slice(-2)}
                    </div>
                  </div>
                </div>
              )}

              {/* Récap conditions */}
              <div style={{ fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.6, padding: ".6rem .8rem", background: "var(--bg)", border: "1px dashed var(--border)", borderRadius: 8 }}>
                💡 Le premier prélèvement de <strong style={{ color: "var(--text)" }}>{amountLabel}</strong> sera effectué immédiatement.
                Ensuite, prélèvement mensuel automatique. Vous pouvez résilier à tout moment depuis cette page.
              </div>

              {/* Code apporteur d'affaires (obligatoire) — identifie l'apporteur, pré-rempli si déjà rattaché */}
              <div>
                <label style={{ fontSize: ".66rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700, display: "block", marginBottom: ".35rem" }}>
                  Code apporteur d'affaires <span style={{ textTransform: "none", letterSpacing: 0, color: "var(--accent)", fontWeight: 700 }}>(obligatoire)</span>
                </label>
                <input
                  type="text"
                  value={affiliateCode}
                  onChange={e => setAffiliateCode(e.target.value.toUpperCase())}
                  placeholder="EX : LMG26"
                  maxLength={12}
                  disabled={alreadyReferred}
                  style={{
                    width: "100%", padding: "10px 14px", boxSizing: "border-box",
                    background: alreadyReferred ? "var(--bg)" : "var(--white)",
                    border: "1.5px solid " + (affiliateStatus === "invalid" ? "var(--red)" : affiliateStatus === "valid" ? "var(--green)" : "var(--border)"), borderRadius: 10,
                    fontSize: ".9rem", fontFamily: "var(--font-mono)", letterSpacing: "1px",
                    textTransform: "uppercase", outline: "none",
                    color: alreadyReferred ? "var(--muted)" : "var(--text)",
                  }}
                />
                {alreadyReferred ? (
                  <div style={{ fontSize: ".68rem", color: "var(--green)", marginTop: ".3rem" }}>✓ Apporteur déjà rattaché à votre compte.</div>
                ) : affiliateStatus === "checking" ? (
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".3rem" }}>Vérification…</div>
                ) : affiliateStatus === "valid" ? (
                  <div style={{ fontSize: ".68rem", color: "var(--green)", marginTop: ".3rem" }}>✓ Apporteur identifié{affiliateName ? ` : ${affiliateName}` : ""} — vous lui serez rattaché.</div>
                ) : affiliateStatus === "invalid" ? (
                  <div style={{ fontSize: ".68rem", color: "var(--red)", marginTop: ".3rem" }}>Code apporteur inconnu. Un code valide est requis pour souscrire.</div>
                ) : (
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".3rem" }}>Saisissez le code de votre apporteur d'affaires (obligatoire).</div>
                )}
              </div>

              {/* Acceptation des CGU/CGV */}
              <label style={{ display: "flex", alignItems: "flex-start", gap: ".6rem", cursor: "pointer", fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.5 }}>
                <input type="checkbox" checked={cgvAccepted} onChange={e => setCgvAccepted(e.target.checked)}
                  style={{ marginTop: ".15rem", width: 16, height: 16, accentColor: "var(--green)", flexShrink: 0, cursor: "pointer" }} />
                <span>
                  J'ai lu et j'accepte les{" "}
                  <button type="button" onClick={(e) => { e.preventDefault(); window.open(CGV_PDF_URL, "_blank", "noopener"); }}
                    style={{ background: "none", border: "none", padding: 0, color: "var(--green)", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>
                    Conditions Générales d'Utilisation et de Vente
                  </button>{" "}de VehicleIQ.
                </span>
              </label>

              {error && (
                <div style={{ background: "var(--red-soft)", border: "1px solid var(--red)", color: "var(--red)", padding: ".55rem .8rem", borderRadius: 8, fontSize: ".8rem" }}>
                  {error}
                </div>
              )}

              <button
                className="btn btn-accent"
                disabled={saving || !cgvAccepted || (!alreadyReferred && affiliateStatus !== "valid")}
                onClick={handleSubscribe}
                style={{ width: "100%", padding: ".85rem 1rem", fontSize: ".95rem", justifyContent: "center", background: "var(--green)", border: "none", opacity: ((!cgvAccepted || (!alreadyReferred && affiliateStatus !== "valid")) && !saving) ? .6 : 1 }}>
                {saving ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, display: "inline-block", marginRight: ".5rem" }} />Souscription en cours…</> : `Confirmer la souscription · ${amountLabel} / ${periodLabel} →`}
              </button>

              <div style={{ fontSize: ".64rem", color: "var(--muted)", textAlign: "center", lineHeight: 1.5 }}>
                Aucun engagement de durée · Résiliable depuis votre espace · Données chiffrées par Stripe
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

// ─── SubscribeMultiAgentModal — souscrire au plan Pro Multi-Agent 799€/mois ─────
// Plan récurrent mensuel pour organisations 3 agents avec privilèges étendus :
//   • 10 estimations simultanées (vs 3)
//   • 12 recommandations d'achat par requête (vs 8)
//   • Compte dédié + SLA 99,5 % + support prioritaire 24h
function SubscribeMultiAgentModal({ onClose, user, card, period = "monthly" }) {
  // v31 — Montant affiché selon la période choisie dans PricingModal.
  const isAnnual = period === "annual";
  const baseAmount = isAnnual ? 4999 : 799;
  const periodLabel = isAnnual ? "an" : "mois";
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  // v5 : nom de l'organisation (préfilled depuis siret_name si dispo via /api/users/me)
  // L'utilisateur peut le surcharger avant souscription. Si vide à l'envoi, le backend
  // utilisera siret_name → name → fallback "Organisation Pro Multi-Agent".
  const [orgName, setOrgName] = useState("");
  const [orgNameSuggestion, setOrgNameSuggestion] = useState("");
  // Acceptation des CGU/CGV — obligatoire
  const [cgvAccepted, setCgvAccepted] = useState(false);
  // Code apporteur — pré-rempli si déjà rattaché lors de l'activation carte
  const [affiliateCode, setAffiliateCode] = useState(
    (user?.referred_by_affiliate || "").toUpperCase()
  );
  const [alreadyReferred, setAlreadyReferred] = useState(!!(user?.referred_by_affiliate));
  const [showCgvViewer, setShowCgvViewer] = useState(false);
  const [affiliateStatus, setAffiliateStatus] = useState(!!(user?.referred_by_affiliate) ? "valid" : null);
  const [affiliateName, setAffiliateName] = useState("");
  // v109 — Promotion admin éventuelle, appliquée avant la réduction apporteur
  // (voir SubscribeUnlimitedModal pour le détail — même ordre que le serveur).
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    apiGet("/api/plans/promotions").then(r => { if (r?.success) setPromo(r.promotions?.multi_agent_3?.[period] || null); }).catch(() => {});
  }, [period]);
  const promoBaseAmount = promo?.discountPct ? Math.round(promo.finalPriceCents / 100) : baseAmount;
  // v81 — Règle : réduction réelle accordée sur présentation d'un code
  // apporteur d'affaires valide — voir SubscribeUnlimitedModal pour le détail.
  const [discountPct, setDiscountPct] = useState(0);
  const discountedAmount = discountPct > 0 ? Math.round(promoBaseAmount * (1 - discountPct / 100)) : promoBaseAmount;
  const amountLabel = discountedAmount.toLocaleString("fr-FR") + " € HT";
  useEffect(() => {
    if (alreadyReferred) {
      fetch(`${API_URL}/api/affiliate/check/${encodeURIComponent(affiliateCode)}`).then(r => r.json()).then(d => {
        if (d?.success) setDiscountPct(Number(isAnnual ? d.discount_pct_annual : d.discount_pct_monthly) || 0);
      }).catch(() => {});
      setAffiliateStatus("valid");
      return;
    }
    const code = affiliateCode.trim().toUpperCase();
    if (!code || code.length < 3) { setAffiliateStatus(null); setAffiliateName(""); setDiscountPct(0); return; }
    let cancelled = false; setAffiliateStatus("checking");
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/affiliate/check/${encodeURIComponent(code)}`);
        const d = await r.json();
        if (cancelled) return;
        if (d?.success) {
          setAffiliateStatus("valid"); setAffiliateName(d.name || "");
          setDiscountPct(Number(isAnnual ? d.discount_pct_annual : d.discount_pct_monthly) || 0);
        } else { setAffiliateStatus("invalid"); setAffiliateName(""); setDiscountPct(0); }
      } catch (e) { if (!cancelled) setAffiliateStatus(null); }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [affiliateCode, alreadyReferred]);
  useEffect(() => {
    // Charger le nom suggéré depuis le profil utilisateur (siret_name ou name)
    (async () => {
      try {
        const h = await getAuthHeaders();
        if (!h) return;
        const r = await fetch(`${API_URL}/api/users/me`, { headers: h });
        if (!r.ok) return;
        const d = await r.json();
        const suggestion = d?.user?.siret_name || d?.user?.name || "";
        if (suggestion) {
          setOrgNameSuggestion(suggestion);
          setOrgName(suggestion);
        }
        // Pré-remplir le code apporteur depuis le profil si présent
        const ref = d?.user?.referred_by_affiliate;
        if (ref) { setAffiliateCode(String(ref).toUpperCase()); setAlreadyReferred(true); }
      } catch (e) { /* silencieux */ }
    })();
  }, []);
  const brandLabel = (b) => ({ visa: "Visa", mastercard: "Mastercard", amex: "American Express", cb: "Carte Bancaire" }[b?.toLowerCase()] || b || "Carte");

  const handleSubscribe = async () => {
    if (!cgvAccepted) { setError("Veuillez accepter les Conditions Générales d'Utilisation et de Vente."); return; }
    if (!alreadyReferred && affiliateStatus !== "valid") { setError("Un code apporteur d'affaires valide est obligatoire pour souscrire."); return; }
    setSaving(true); setError("");
    try {
      const h = await getAuthHeaders();
      const r = await fetch(`${API_URL}/api/billing/subscribe-multi-agent`, {
        method: "POST",
        headers: { ...h, "Content-Type": "application/json" },
        body: JSON.stringify({ company_name: orgName.trim() || null, affiliate_code: affiliateCode.trim().toUpperCase() || null, period }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Souscription échouée");
      setShowCgvViewer(true);
      setDone(true);
      setTimeout(() => onClose && onClose(), 4000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
    {showCgvViewer && <CGVViewerModal onClose={() => setShowCgvViewer(false)} autoDownload={true} />}
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 560, animation: "slideUp .25s ease" }}>
        <div className="panel-header">
          <div>
            <div className="panel-title">Souscrire à l'offre Pro Multi-Agent</div>
            <div className="panel-sub">{amountLabel} / {periodLabel} · 3 agents · cotes illimitées · résiliable à tout moment</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {done ? (
            <div style={{ textAlign: "center", padding: "1.8rem 1rem" }}>
              <div style={{ fontSize: "3rem", marginBottom: ".6rem" }}>👥</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", color: "var(--accent2)", letterSpacing: "-.01em" }}>Bienvenue dans Pro Multi-Agent</div>
              <div style={{ fontSize: ".85rem", color: "var(--sub)", marginTop: ".5rem" }}>
                Votre organisation est créée avec 3 places.<br />Invitez vos collaborateurs depuis « Mon espace ».
              </div>
            </div>
          ) : (
            <>
              {/* Récap offre */}
              <div style={{
                background: "linear-gradient(135deg, rgba(30,42,74,.05), rgba(30,42,74,.01))",
                border: "1.5px solid rgba(30,42,74,.2)",
                borderRadius: 12, padding: "1.1rem 1.3rem",
              }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: ".5rem", marginBottom: ".5rem" }}>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", color: "var(--text)", letterSpacing: "-.01em" }}>
                    Plan Pro Multi-Agent
                  </div>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: "1.6rem", color: "var(--accent2)", fontWeight: 600, textAlign: "right" }}>
                    {promo?.discountPct > 0 && (
                      <div style={{ fontSize: ".85rem", color: "var(--muted)", textDecoration: "line-through", fontWeight: 400 }}>{baseAmount.toLocaleString("fr-FR")} €</div>
                    )}
                    {amountLabel} <span style={{ fontSize: ".75rem", color: "var(--sub)" }}>/ {periodLabel}</span>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: ".35rem", marginTop: ".6rem" }}>
                  {[
                    { txt: "3 agents inclus (1 propriétaire + 2 collaborateurs)", strong: true },
                    { txt: "Cotes illimitées", strong: false },
                    { txt: "⚡ 10 cotes simultanées (vs 3)", strong: true },
                    { txt: "🎯 12 recommandations d'achat / requête (vs 8)", strong: true },
                    { txt: "Compte dédié · SLA 99,5 %", strong: false },
                    { txt: "Multi-utilisateurs avec rôles", strong: false },
                    { txt: "Support prioritaire sous 24h", strong: false },
                    { txt: "Facturation mensualisée · résiliable à tout moment", strong: false },
                    { txt: "📸 Accès au Service Photo IA (sur devis)", strong: true },
                    { txt: "📡 Accès à la Diffusion Multiplateformes (sur devis)", strong: true },
                  ].map((f, i) => (
                    <div key={i} style={{ fontSize: ".78rem", color: f.strong ? "var(--text)" : "var(--sub)", fontWeight: f.strong ? 600 : 400 }}>
                      <span style={{ color: "var(--accent2)", fontWeight: 700, marginRight: ".4rem" }}>✓</span>{f.txt}
                    </div>
                  ))}
                </div>
              </div>

              {/* v5 : Nom de l'organisation (modifiable, suggestion depuis siret_name) */}
              <div>
                <label style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700, display: "block", marginBottom: ".4rem" }}>
                  Nom de votre organisation
                </label>
                <input
                  type="text"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  placeholder={orgNameSuggestion || "Ex. : Garage Dupont SARL"}
                  maxLength={120}
                  style={{
                    width: "100%", padding: "11px 14px",
                    background: "var(--white)",
                    border: "1.5px solid var(--border)",
                    borderRadius: 10,
                    fontSize: ".9rem", color: "var(--text)",
                    outline: "none", transition: "border-color .15s",
                    boxSizing: "border-box",
                    fontFamily: "var(--font-body)",
                  }}
                  onFocus={e => e.target.style.borderColor = "var(--accent2)"}
                  onBlur={e => e.target.style.borderColor = "var(--border)"}
                />
                <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".35rem", lineHeight: 1.5 }}>
                  Ce nom sera visible par tous les agents de votre organisation dans « Mon espace ».
                  {orgNameSuggestion && orgName !== orgNameSuggestion && (
                    <> Suggestion depuis votre SIRET : <button onClick={() => setOrgName(orgNameSuggestion)} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: "inherit", textDecoration: "underline" }}>{orgNameSuggestion}</button>.</>
                  )}
                </div>
              </div>

              {/* Carte qui sera débitée */}
              {card && (
                <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, padding: ".8rem 1.1rem" }}>
                  <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".4rem" }}>
                    Carte qui sera débitée
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: ".7rem" }}>
                    <div style={{
                      fontSize: ".7rem", fontWeight: 700, color: "var(--text)",
                      background: "var(--white)", border: "1.5px solid var(--border)",
                      padding: ".2rem .55rem", borderRadius: 6, fontFamily: "var(--font-mono)", textTransform: "uppercase",
                    }}>
                      {brandLabel(card.brand)}
                    </div>
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: ".9rem", color: "var(--text)" }}>
                      •••• {card.last4}
                    </div>
                    <div style={{ marginLeft: "auto", fontSize: ".7rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>
                      {String(card.exp_month).padStart(2, "0")}/{String(card.exp_year).slice(-2)}
                    </div>
                  </div>
                </div>
              )}

              {/* Récap conditions */}
              <div style={{ fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.6, padding: ".6rem .8rem", background: "var(--bg)", border: "1px dashed var(--border)", borderRadius: 8 }}>
                💡 Le premier prélèvement de <strong style={{ color: "var(--text)" }}>{amountLabel}</strong> sera effectué immédiatement.
                Ensuite, prélèvement mensuel automatique. <strong>Un SIRET est requis</strong> (utilisé pour créer l'organisation).
                Vous pouvez résilier à tout moment depuis « Mon espace → Paiements & Abonnements ».
              </div>

              {/* Code apporteur d'affaires (obligatoire) — identifie l'apporteur, pré-rempli si déjà rattaché */}
              <div>
                <label style={{ fontSize: ".66rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700, display: "block", marginBottom: ".35rem" }}>
                  Code apporteur d'affaires <span style={{ textTransform: "none", letterSpacing: 0, color: "var(--accent)", fontWeight: 700 }}>(obligatoire)</span>
                </label>
                <input
                  type="text"
                  value={affiliateCode}
                  onChange={e => setAffiliateCode(e.target.value.toUpperCase())}
                  placeholder="EX : LMG26"
                  maxLength={12}
                  disabled={alreadyReferred}
                  style={{
                    width: "100%", padding: "10px 14px", boxSizing: "border-box",
                    background: alreadyReferred ? "var(--bg)" : "var(--white)",
                    border: "1.5px solid " + (affiliateStatus === "invalid" ? "var(--red)" : affiliateStatus === "valid" ? "var(--green)" : "var(--border)"), borderRadius: 10,
                    fontSize: ".9rem", fontFamily: "var(--font-mono)", letterSpacing: "1px",
                    textTransform: "uppercase", outline: "none",
                    color: alreadyReferred ? "var(--muted)" : "var(--text)",
                  }}
                />
                {alreadyReferred ? (
                  <div style={{ fontSize: ".68rem", color: "var(--green)", marginTop: ".3rem" }}>✓ Apporteur déjà rattaché à votre compte.</div>
                ) : affiliateStatus === "checking" ? (
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".3rem" }}>Vérification…</div>
                ) : affiliateStatus === "valid" ? (
                  <div style={{ fontSize: ".68rem", color: "var(--green)", marginTop: ".3rem" }}>✓ Apporteur identifié{affiliateName ? ` : ${affiliateName}` : ""} — vous lui serez rattaché.</div>
                ) : affiliateStatus === "invalid" ? (
                  <div style={{ fontSize: ".68rem", color: "var(--red)", marginTop: ".3rem" }}>Code apporteur inconnu. Un code valide est requis pour souscrire.</div>
                ) : (
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".3rem" }}>Saisissez le code de votre apporteur d'affaires (obligatoire).</div>
                )}
              </div>

              {/* Acceptation des CGU/CGV */}
              <label style={{ display: "flex", alignItems: "flex-start", gap: ".6rem", cursor: "pointer", fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.5 }}>
                <input type="checkbox" checked={cgvAccepted} onChange={e => setCgvAccepted(e.target.checked)}
                  style={{ marginTop: ".15rem", width: 16, height: 16, accentColor: "var(--accent2)", flexShrink: 0, cursor: "pointer" }} />
                <span>
                  J'ai lu et j'accepte les{" "}
                  <button type="button" onClick={(e) => { e.preventDefault(); window.open(CGV_PDF_URL, "_blank", "noopener"); }}
                    style={{ background: "none", border: "none", padding: 0, color: "var(--accent2)", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>
                    Conditions Générales d'Utilisation et de Vente
                  </button>{" "}de VehicleIQ.
                </span>
              </label>

              {error && (
                <div style={{ background: "var(--red-soft)", border: "1px solid var(--red)", color: "var(--red)", padding: ".55rem .8rem", borderRadius: 8, fontSize: ".8rem" }}>
                  {error}
                </div>
              )}

              <button
                className="btn btn-accent"
                disabled={saving || !cgvAccepted || (!alreadyReferred && affiliateStatus !== "valid")}
                onClick={handleSubscribe}
                style={{ width: "100%", padding: ".85rem 1rem", fontSize: ".95rem", justifyContent: "center", background: "var(--accent2)", border: "none", opacity: ((!cgvAccepted || (!alreadyReferred && affiliateStatus !== "valid")) && !saving) ? .6 : 1 }}>
                {saving ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, display: "inline-block", marginRight: ".5rem" }} />Souscription en cours…</> : `Confirmer la souscription · ${amountLabel} / ${periodLabel} →`}
              </button>

              <div style={{ fontSize: ".64rem", color: "var(--muted)", textAlign: "center", lineHeight: 1.5 }}>
                Aucun engagement de durée · Résiliable depuis votre espace · Organisation 3 agents créée automatiquement · Données chiffrées par Stripe
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

// ─── CGVViewerModal — liseuse PDF des CGU/CGV + téléchargement auto ───────────
// Affiche le PDF des Conditions Générales dans une liseuse intégrée (iframe) et
// déclenche automatiquement son téléchargement à l'ouverture. Utilisé après
// l'acceptation des CGV + validation de la carte dans RechargeModal, et
// réutilisable partout où l'on veut faire consulter le document.
function CGVViewerModal({ onClose, autoDownload = true }) {
  useEffect(() => {
    if (autoDownload) {
      // Léger différé pour laisser la modale s'afficher avant de lancer le download
      const t = setTimeout(() => downloadCGV(), 400);
      return () => clearTimeout(t);
    }
  }, [autoDownload]);

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()} style={{ zIndex: 4000 }}>
      <div className="panel" style={{ maxWidth: 880, width: "94vw", height: "92vh", display: "flex", flexDirection: "column", animation: "slideUp .25s ease" }}>
        <div className="panel-header" style={{ flexShrink: 0 }}>
          <div>
            <div className="panel-title">Conditions Générales d'Utilisation et de Vente</div>
            <div className="panel-sub">Document accepté lors de votre souscription — téléchargement en cours…</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, padding: "0 1.2rem 1rem", display: "flex", flexDirection: "column", gap: ".8rem" }}>
          <div style={{ flex: 1, minHeight: 0, border: "1.5px solid var(--border)", borderRadius: 10, overflow: "hidden", background: "var(--bg)" }}>
            <iframe
              src={CGV_PDF_URL + "#view=FitH"}
              title="Conditions Générales d'Utilisation et de Vente"
              style={{ width: "100%", height: "100%", border: "none", display: "block" }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".8rem", flexWrap: "wrap", flexShrink: 0 }}>
            <span style={{ fontSize: ".74rem", color: "var(--muted)" }}>
              Si la lecture ne s'affiche pas, utilisez le bouton de téléchargement.
            </span>
            <button className="btn btn-accent btn-sm" onClick={downloadCGV} style={{ whiteSpace: "nowrap" }}>
              ⬇ Télécharger le PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RechargeModal({ onClose, onRecharged, user, onShowAuth }) {
  // ── Nouveau flow : activation simple du compte par saisie de carte. ─────────
  // L'utilisateur ne voit JAMAIS de packs ni de crédits. On lui présente
  // uniquement le prix par dossier (4,99 €) et on enregistre sa carte via
  // Stripe pour des prélèvements mensuels automatiques selon usage.
  // ────────────────────────────────────────────────────────────────────────────
  const [step, setStep] = useState(user ? "form" : "auth_prompt");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [usage, setUsage] = useState(null);
  // Acceptation des CGU/CGV — obligatoire avant activation
  const [cgvAccepted, setCgvAccepted] = useState(false);
  // Affichage de la liseuse PDF des CGV après activation réussie
  const [showCgvViewer, setShowCgvViewer] = useState(false);
  // Code apporteur d'affaires (parrainage) — permet de rattacher ce client à un apporteur
  const [affiliateCode, setAffiliateCode] = useState("");
  const [affiliateStatus, setAffiliateStatus] = useState(null); // null | "checking" | "valid" | "invalid"
  const [affiliateName, setAffiliateName] = useState("");
  // Carte déjà enregistrée ? → le compte est déjà actif au paiement à l'usage :
  // on affiche un état informatif au lieu de redemander une carte (incohérent).
  const [existingCard, setExistingCard] = useState(null); // null=inconnu | false | {brand,last4}
  useEffect(() => {
    if (!user) { setExistingCard(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const h = await getAuthHeaders();
        if (!h) { if (!cancelled) setExistingCard(false); return; }
        const r = await fetch(`${API_URL}/api/billing/current`, { headers: h });
        const d = r.ok ? await r.json() : null;
        if (!cancelled) setExistingCard(d?.has_payment_method ? (d.card || {}) : false);
      } catch (e) { if (!cancelled) setExistingCard(false); }
    })();
    return () => { cancelled = true; };
  }, [user]);
  useEffect(() => {
    const code = affiliateCode.trim().toUpperCase();
    if (!code || code.length < 3) { setAffiliateStatus(null); setAffiliateName(""); return; }
    let cancelled = false;
    setAffiliateStatus("checking");
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/affiliate/check/${encodeURIComponent(code)}`);
        const d = await r.json();
        if (cancelled) return;
        if (d?.success) { setAffiliateStatus("valid"); setAffiliateName(d.name || ""); }
        else { setAffiliateStatus("invalid"); setAffiliateName(""); }
      } catch (e) {
        if (!cancelled) { setAffiliateStatus(null); }
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [affiliateCode]);

  // Charger l'usage du mois (pour afficher "il reste X cotes gratuites")
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const h = await getAuthHeaders();
        if (!h) return;
        const r = await fetch(`${API_URL}/api/pricing/usage`, { headers: h });
        if (!r.ok) return;
        const data = await r.json();
        if (data?.success) setUsage(data);
      } catch (e) { /* silencieux */ }
    })();
  }, [user]);

  // Init Stripe Elements (carte uniquement)
  // Note : la clé publishable est destinée à être exposée côté client (Stripe le précise),
  // c'est uniquement la secret_key qui doit rester côté serveur.
  const STRIPE_PK = "pk_live_51TMxmrEGRqmRjda5bmVRtBgxOX9l5PU3KNeReDppBNj20MQsSVbAnFIPkKYvxALwGV9J4duNVdupLfllQBeQtSnk00aQbpXWsb";
  const stripeRef = useRef(null);
  const cardRef = useRef(null);
  const cardContainerRef = useRef(null);
  const [stripeReady, setStripeReady] = useState(false);

  // Étape 1 : charger Stripe.js dès que la modale s'ouvre en mode "form"
  useEffect(() => {
    if (step !== "form" || stripeReady) return;
    let cancelled = false;
    const ensureStripe = async () => {
      if (window.Stripe) return window.Stripe(STRIPE_PK);
      // Charger le script si pas déjà présent
      const existing = document.querySelector('script[src*="stripe.com/v3"]');
      if (!existing) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://js.stripe.com/v3/";
          s.onload = resolve;
          s.onerror = () => reject(new Error("Stripe.js non chargé"));
          document.head.appendChild(s);
        });
      } else {
        // Attendre que le script existant finisse de charger
        await new Promise((resolve) => {
          if (window.Stripe) return resolve();
          const id = setInterval(() => { if (window.Stripe) { clearInterval(id); resolve(); } }, 50);
          setTimeout(() => { clearInterval(id); resolve(); }, 5000);
        });
      }
      if (!window.Stripe) throw new Error("Stripe SDK indisponible après chargement");
      return window.Stripe(STRIPE_PK);
    };
    (async () => {
      try {
        const stripe = await ensureStripe();
        if (cancelled) return;
        stripeRef.current = stripe;
        setStripeReady(true);
      } catch (e) {
        console.warn("[stripe load]", e);
        if (!cancelled) setError("Module de paiement indisponible. Rafraîchissez la page.");
      }
    })();
    return () => { cancelled = true; };
  }, [step, stripeReady]);

  // Étape 2 : monter le CardElement seulement quand Stripe est prêt ET que le DOM est rendu
  // On utilise un callback ref pour être sûr de monter dès que le DIV existe.
  const cardMountCallback = useCallback((node) => {
    cardContainerRef.current = node;
    if (!node || !stripeRef.current || cardRef.current) return;
    try {
      const elements = stripeRef.current.elements();
      const card = elements.create("card", {
        style: {
          base: {
            fontSize: "16px",
            fontFamily: "Manrope, Inter, system-ui, sans-serif",
            color: "#15171C",
            lineHeight: "24px",
            "::placeholder": { color: "#8E939E" },
            iconColor: "#A8803A",
          },
          invalid: { color: "#9B2C2C", iconColor: "#9B2C2C" },
        },
        hidePostalCode: true,
      });
      card.mount(node);
      cardRef.current = card;
      card.on("change", e => {
        setCardComplete(!!e.complete);
        if (e.error) setError(e.error.message); else setError("");
      });
      card.on("ready", () => {
        // Stripe a fini de monter son iframe — pas d'action requise mais utile en debug
        console.debug("[stripe] card element ready");
      });
    } catch (mountErr) {
      console.warn("[stripe mount]", mountErr);
      setError("Impossible d'initialiser le formulaire de paiement.");
    }
  }, [stripeReady]);

  // Cleanup à l'unmount du composant
  useEffect(() => {
    return () => {
      if (cardRef.current) {
        try { cardRef.current.unmount(); } catch(_) {}
        try { cardRef.current.destroy(); } catch(_) {}
        cardRef.current = null;
      }
    };
  }, []);

  const handleSubmit = async () => {
    if (!cardComplete) { setError("Renseignez vos informations bancaires."); return; }
    if (!cgvAccepted) { setError("Veuillez accepter les Conditions Générales d'Utilisation et de Vente."); return; }
    if (affiliateStatus !== "valid") { setError("Un code apporteur d'affaires valide est obligatoire pour activer votre compte."); return; }
    setSaving(true); setError("");
    try {
      // 1) Créer un SetupIntent côté serveur (mode monthly = enregistrer le moyen de paiement sans débit)
      // On utilise le tarif unique 4,99 € (499 cents) comme tarif de référence pour le price récurrent
      const data = await apiPost("/api/payment/create", {
        planId: "viq_pay_as_you_go",
        amount: 499,
        qty: 1,
        pack_label: "Activation compte VehicleIQ",
        email: user?.email,
        monthly: true,
      });
      if (!data?.success || !data.clientSecret) throw new Error(data?.error || "Initialisation du paiement échouée");
      // 2) Confirmer le SetupIntent avec la carte
      const billingDetails = { name: user?.displayName || user?.email || "VehicleIQ", email: user?.email || undefined };
      const { setupIntent, error: setupError } = await stripeRef.current.confirmCardSetup(
        data.clientSecret,
        { payment_method: { card: cardRef.current, billing_details: billingDetails } }
      );
      if (setupError) throw new Error(setupError.message || "Erreur lors de la validation de la carte.");
      if (setupIntent?.status !== "succeeded") throw new Error("Statut inattendu : " + (setupIntent?.status || "inconnu"));
      // 3) Enregistrer le moyen de paiement comme méthode par défaut côté backend (sans créditer)
      //    + rattacher le client à l'apporteur d'affaires si un code valide a été saisi.
      await apiPost("/api/payment/subscribe", {
        setupIntentId: data.setupIntentId,
        priceId: data.priceId,
        paymentMethodId: setupIntent.payment_method,
        uid: user?.uid,
        qty: 0, // 0 = pas de crédits ajoutés, c'est juste l'activation
        affiliate_code: affiliateStatus === "valid" ? affiliateCode.trim().toUpperCase() : null,
      }).catch(err => console.warn("[subscribe] confirmation backend échouée:", err.message));
      // 4) Succès — afficher la liseuse PDF des CGV (téléchargement auto) puis l'étape finale
      setShowCgvViewer(true);
      setStep("done");
      if (onRecharged) onRecharged(0);
    } catch (e) { setError(e.message || "Erreur inconnue"); }
    finally { setSaving(false); }
  };

  if (step === "auth_prompt") {
    return (
      <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="panel" style={{ maxWidth: 480, animation: "slideUp .25s ease" }}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Activer mon compte</div>
              <div className="panel-sub">Connectez-vous pour activer votre compte VehicleIQ.</div>
            </div>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="panel-body" style={{ textAlign: "center", padding: "2rem 1.5rem" }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🔐</div>
            <div style={{ fontSize: ".9rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1.4rem" }}>
              Pour activer votre compte et bénéficier de <strong style={{ color: "var(--accent)" }}>5 cotes offertes</strong>,
              créez votre compte en quelques secondes.
            </div>
            <button className="btn btn-accent" style={{ padding: ".7rem 1.8rem" }} onClick={() => { onClose(); if (onShowAuth) onShowAuth(); }}>
              Créer mon compte →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "done") {
    // v5 : si l'utilisateur a démarré le flow depuis une offre d'abonnement
    // (Pro Solo ou Pro Multi-Agent), on lui propose de finaliser en un clic.
    let subIntent = null;
    try { subIntent = sessionStorage.getItem("viq_subscribe_intent"); } catch(_) {}
    const multiAgentIntent = subIntent === "multi_agent_3";
    const unlimitedIntent = subIntent === "unlimited";
    const planLabel = multiAgentIntent ? "Pro Multi-Agent" : "Pro Solo";
    const planPrice = multiAgentIntent ? "799 €/mois" : "399 €/mois";

    return (
      <>
      {showCgvViewer && <CGVViewerModal onClose={() => setShowCgvViewer(false)} autoDownload={true} />}
      <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="panel" style={{ maxWidth: 520, animation: "slideUp .25s ease" }}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Compte activé ✓</div>
              <div className="panel-sub">Vous êtes prêt à estimer.</div>
            </div>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="panel-body" style={{ textAlign: "center", padding: "2rem 1.5rem" }}>
            <div style={{ fontSize: "3.5rem", marginBottom: ".8rem" }}>🎉</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.5rem", color: "var(--text)", marginBottom: ".4rem", letterSpacing: "-.01em" }}>
              Tout est prêt.
            </div>
            <div style={{ fontSize: ".88rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1.5rem", maxWidth: 360, margin: "0 auto 1.5rem" }}>
              Votre moyen de paiement est enregistré. Vous pouvez lancer vos cotes
              en toute sérénité — facturation mensuelle automatique sur votre carte.
            </div>

            {(multiAgentIntent || unlimitedIntent) && (
              <div style={{
                background: "linear-gradient(135deg, rgba(30,42,74,.06), rgba(30,42,74,.01))",
                border: "1.5px solid rgba(30,42,74,.25)",
                borderRadius: 12, padding: "1rem 1.2rem", marginBottom: "1.2rem", textAlign: "left",
              }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", fontSize: ".58rem", padding: ".18rem .55rem", background: "rgba(30,42,74,.1)", color: "var(--accent2)", border: "1px solid rgba(30,42,74,.25)", borderRadius: 100, fontFamily: "var(--font-mono)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".5rem" }}>
                  {multiAgentIntent ? "👥 Étape suivante" : "🚀 Étape suivante"}
                </div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: "1.1rem", color: "var(--text)", marginBottom: ".4rem", letterSpacing: "-.01em" }}>
                  Finaliser votre souscription {planLabel}
                </div>
                <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.55, marginBottom: ".7rem" }}>
                  Vous avez choisi le plan <strong>{planLabel} à {planPrice}</strong>. Cliquez ci-dessous pour finaliser votre abonnement.
                </div>
                <button
                  className="btn"
                  style={{ background: "var(--accent2)", color: "#fff", border: "none", width: "100%", padding: ".7rem 1rem", fontWeight: 600, justifyContent: "center" }}
                  onClick={() => {
                    // Conserver l'intent pour que PaymentSettingsTab auto-déclenche
                    // la modale de souscription correspondante au montage.
                    onClose();
                    try { window.dispatchEvent(new Event("viq-open-payment-tab")); } catch(_) {}
                  }}>
                  Continuer vers la souscription →
                </button>
              </div>
            )}

            <button className="btn btn-accent" style={{ padding: ".7rem 1.8rem" }} onClick={onClose}>
              {(multiAgentIntent || unlimitedIntent) ? "Plus tard" : "Commencer →"}
            </button>
          </div>
        </div>
      </div>
      </>
    );
  }

  // step === "form" : saisie de la carte
  // Quota gratuit à vie : used_total / free_total (clés héritées en secours).
  const usageUsed = usage ? (usage.used_total !== undefined ? usage.used_total : usage.used_this_month) : 0;
  const usageQuota = usage ? (usage.free_total || usage.free_per_month || 5) : 5;
  const isFreePhase = usage && usageUsed < usageQuota;
  const remainingFree = usage ? Math.max(0, usageQuota - usageUsed) : 5;

  // ── Carte déjà enregistrée : compte déjà actif au paiement à l'usage ──────
  // On n'affiche PAS le formulaire de carte (incohérent) : état informatif.
  if (existingCard && typeof existingCard === "object") {
    return (
      <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="panel" style={{ maxWidth: 480, animation: "slideUp .25s ease" }}>
          <div className="panel-header">
            <div>
              <div className="panel-title">Compte déjà actif</div>
              <div className="panel-sub">Paiement à l'usage · 4,99 € par dossier</div>
            </div>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div style={{ background: "rgba(46,111,92,.07)", border: "1.5px solid rgba(46,111,92,.3)", borderRadius: 12, padding: "1rem 1.1rem", fontSize: ".85rem", color: "var(--text)", lineHeight: 1.6 }}>
              <strong style={{ color: "var(--green)" }}>✓ Votre compte est déjà actif.</strong> Une carte est enregistrée
              {existingCard.brand || existingCard.last4 ? <> ({String(existingCard.brand || "carte").toUpperCase()}{existingCard.last4 ? " •••• " + existingCard.last4 : ""})</> : null}
              {" "}— vos cotes sont facturées automatiquement au tarif unique de <strong>4,99 €</strong> une fois vos cotes offertes épuisées.
              {isFreePhase && <> Il vous reste <strong style={{ color: "var(--accent)" }}>{remainingFree} cote{remainingFree > 1 ? "s" : ""} offerte{remainingFree > 1 ? "s" : ""}</strong>.</>}
            </div>
            <div style={{ fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.55 }}>
              Pour changer de carte ou passer à une offre mensuelle (Pro Solo, Pro Multi-Agent), rendez-vous dans <strong>Mon espace → Paiements & Abonnements</strong>.
            </div>
            <button className="btn btn-accent" onClick={onClose} style={{ width: "100%", justifyContent: "center", border: "none" }}>Compris →</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 520, animation: "slideUp .25s ease" }}>
        <div className="panel-header">
          <div>
            <div className="panel-title">Activer mon compte</div>
            <div className="panel-sub">Renseignez votre moyen de paiement. Facturation mensuelle à l'usage.</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="panel-body" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>

          {/* Bloc explicatif */}
          <div style={{
            background: "linear-gradient(135deg, rgba(168,128,58,.06), rgba(168,128,58,.01))",
            border: "1.5px solid rgba(168,128,58,.18)",
            borderRadius: 14,
            padding: "1.1rem 1.3rem",
          }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: ".5rem", marginBottom: ".4rem" }}>
              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", color: "var(--text)", letterSpacing: "-.01em" }}>
                <span style={{ color: "var(--accent)", fontWeight: 600 }}>4,99 €</span> / dossier — tarif unique
              </div>
            </div>
            <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.55 }}>
              {isFreePhase ? (
                <>Il vous reste <strong style={{ color: "var(--accent)" }}>{remainingFree} cote{remainingFree > 1 ? "s" : ""} offerte{remainingFree > 1 ? "s" : ""}</strong> (5 offertes et déjà actives à la création du compte).
                Enregistrez votre carte pour continuer sans interruption au-delà.</>
              ) : (
                <>Vos cotes offertes sont épuisées. Enregistrez votre carte pour continuer vos cotes
                au tarif unique de 4,99 € — sans abonnement, sans engagement.</>
              )}
            </div>
          </div>

          {/* Code apporteur d'affaires (parrainage) — obligatoire */}
          <div>
            <label style={{ fontSize: ".7rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, display: "block", marginBottom: ".4rem" }}>
              Code apporteur d'affaires <span style={{ textTransform: "none", letterSpacing: 0, color: "var(--accent)", fontWeight: 700 }}>(obligatoire)</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                value={affiliateCode}
                onChange={e => setAffiliateCode(e.target.value.toUpperCase())}
                placeholder="EX : LMG26"
                maxLength={12}
                style={{
                  width: "100%", padding: "12px 16px",
                  background: "var(--white)",
                  border: `1.5px solid ${affiliateStatus === "valid" ? "var(--green)" : affiliateStatus === "invalid" ? "var(--red)" : "var(--border)"}`,
                  borderRadius: 10,
                  fontSize: ".95rem", fontFamily: "var(--font-mono)",
                  letterSpacing: "1px", textTransform: "uppercase",
                  outline: "none", transition: "border-color .15s",
                  boxSizing: "border-box",
                }}
              />
              {affiliateStatus === "checking" && (
                <div className="spinner" style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, borderWidth: 2 }} />
              )}
              {affiliateStatus === "valid" && (
                <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: "var(--green)", fontSize: "1rem" }}>✓</span>
              )}
              {affiliateStatus === "invalid" && (
                <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: "var(--red)", fontSize: "1rem" }}>✕</span>
              )}
            </div>
            {affiliateStatus === "valid" && (
              <div style={{ fontSize: ".7rem", color: "var(--green)", marginTop: ".4rem", display: "flex", alignItems: "center", gap: ".3rem" }}>
                <span>✓</span> Apporteur identifié{affiliateName ? ` : ${affiliateName}` : ""} — vous lui serez rattaché.
              </div>
            )}
            {affiliateStatus === "invalid" && (
              <div style={{ fontSize: ".7rem", color: "var(--red)", marginTop: ".4rem" }}>
                Code apporteur inconnu. Un code valide est requis pour activer votre compte.
              </div>
            )}
            {!affiliateStatus && (
              <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: ".4rem" }}>
                Saisissez le code de votre apporteur d'affaires (obligatoire).
              </div>
            )}
          </div>

          {/* Saisie carte Stripe */}
          <div>
            <label style={{ fontSize: ".7rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, display: "block", marginBottom: ".4rem" }}>
              Coordonnées de paiement
            </label>
            {/* Conteneur Stripe : hauteur fixe nécessaire car Stripe injecte un iframe.
                Sans hauteur explicite, l'iframe s'affiche à 0px et le champ devient invisible. */}
            <div
              ref={cardMountCallback}
              style={{
                padding: "14px 16px",
                background: "var(--white)",
                border: "1.5px solid var(--border)",
                borderRadius: 10,
                minHeight: 52,
                transition: "border-color .15s",
              }}
            >
              {!stripeReady && (
                <div style={{ display: "flex", alignItems: "center", gap: ".5rem", color: "var(--muted)", fontSize: ".82rem" }}>
                  <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                  Chargement du module de paiement…
                </div>
              )}
            </div>
            <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".5rem", display: "flex", alignItems: "center", gap: ".4rem", flexWrap: "wrap" }}>
              <span>🔒</span>
              <span>Aucun débit immédiat. Données chiffrées par Stripe (PCI DSS).</span>
              <span style={{ marginLeft: "auto", display: "inline-flex", gap: ".25rem", alignItems: "center", fontFamily: "var(--font-mono)", fontSize: ".64rem", color: "var(--muted)" }}>
                <span style={{ background: "var(--white)", border: "1px solid var(--border)", padding: ".1rem .35rem", borderRadius: 3 }}>VISA</span>
                <span style={{ background: "var(--white)", border: "1px solid var(--border)", padding: ".1rem .35rem", borderRadius: 3 }}>MC</span>
                <span style={{ background: "var(--white)", border: "1px solid var(--border)", padding: ".1rem .35rem", borderRadius: 3 }}>CB</span>
              </span>
            </div>
          </div>

          {/* Acceptation des CGU/CGV — case à cocher obligatoire */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: ".6rem", cursor: "pointer", fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={cgvAccepted}
              onChange={e => setCgvAccepted(e.target.checked)}
              style={{ marginTop: ".15rem", width: 16, height: 16, accentColor: "var(--accent)", flexShrink: 0, cursor: "pointer" }}
            />
            <span>
              J'ai lu et j'accepte les{" "}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); window.open(CGV_PDF_URL, "_blank", "noopener"); }}
                style={{ background: "none", border: "none", padding: 0, color: "var(--accent)", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>
                Conditions Générales d'Utilisation et de Vente
              </button>{" "}
              de VehicleIQ.
            </span>
          </label>

          {error && (
            <div style={{ background: "var(--red-soft)", border: "1px solid var(--red)", color: "var(--red)", padding: ".55rem .8rem", borderRadius: 8, fontSize: ".8rem" }}>
              {error}
            </div>
          )}

          <button
            className="btn btn-accent"
            disabled={saving || !cardComplete || !cgvAccepted || affiliateStatus !== "valid"}
            onClick={handleSubmit}
            style={{ width: "100%", padding: ".8rem 1rem", fontSize: ".95rem", justifyContent: "center", opacity: ((!cardComplete || !cgvAccepted || affiliateStatus !== "valid") && !saving) ? .55 : 1, cursor: ((!cardComplete || !cgvAccepted || affiliateStatus !== "valid") && !saving) ? "not-allowed" : "pointer" }}>
            {saving ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, display: "inline-block", marginRight: ".5rem" }} />Activation…</> : "Activer mon compte →"}
          </button>

          <div style={{ fontSize: ".66rem", color: "var(--muted)", textAlign: "center", lineHeight: 1.5 }}>
            Aucun engagement · Résiliable à tout moment · Facturation mensuelle automatique<br />
            Données chiffrées par Stripe · Conforme PCI DSS · CB · Visa · Mastercard
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── WalletGate : vérifier le solde avant de débloquer un contenu premium ─────
function WalletGate({ onClose, onUnlocked, cost, label, user, onShowAuth, onRecharge }) {
  const balance = getWallet();
  const canSpend = balance >= cost;

  // ⚠️ Le débit a DÉJÀ été effectué "payé d'avance" par le handler appelant
  // (handleComplete / runCheck : spendWallet + /api/wallet/spend). WalletGate
  // ne doit donc PLUS débiter ici, sinon la requête est facturée deux fois.
  // Il se contente de débloquer (solde OK) ou d'orienter vers la recharge.
  const spend = () => {
    onUnlocked();
  };

  // Pas connecté → afficher la modale de connexion
  if (!user) return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 380 }}>
        <div className="panel-header"><div><div className="panel-title">Connexion requise</div></div><button className="close-btn" onClick={onClose}>✕</button></div>
        <div className="panel-body" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🔐</div>
          <p style={{ fontSize: ".87rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1.5rem" }}>Connectez-vous pour accéder aux fonctionnalités premium.</p>
          <button className="btn btn-accent" style={{ width: "100%" }} onClick={() => { onClose(); onShowAuth(); }}>Se connecter avec Google →</button>
        </div>
      </div>
    </div>
  );

  // Solde suffisant → débiter automatiquement sans afficher de modal
  if (canSpend) {
    spend();
    return null;
  }

  // Solde insuffisant → afficher la modal "limite atteinte" qui dirige vers tarifs
  // Aucune mention de crédit visible côté utilisateur.
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 420 }}>
        <div className="panel-header">
          <div><div className="panel-title">Contenu premium</div><div className="panel-sub">{label}</div></div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          {/* Message limite atteinte */}
          <div style={{
            padding: "1rem 1.2rem",
            background: "rgba(168,128,58,.05)",
            border: "1px solid rgba(168,128,58,.2)",
            borderRadius: 10,
            marginBottom: "1rem",
            textAlign: "center"
          }}>
            <div style={{ fontSize: "2rem", marginBottom: ".5rem" }}>🎁</div>
            <div style={{ fontSize: ".9rem", color: "var(--text)", lineHeight: 1.5, marginBottom: ".4rem" }}>
              Vous avez utilisé toutes vos cotes gratuites ce mois-ci.
            </div>
            <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.5 }}>
              Activez votre moyen de paiement pour continuer à <strong style={{ color: "var(--accent)" }}>4,99 € par dossier</strong> (tarif unique).
            </div>
          </div>
          <button className="btn btn-accent" style={{ width: "100%", marginBottom: ".5rem" }}
            onClick={() => { onClose(); onRecharge(); }}>
            Voir les tarifs →
          </button>
          <div style={{ textAlign: "center", marginTop: ".8rem", fontSize: ".7rem", color: "var(--muted)" }}>
            Pas d'abonnement · Pas d'engagement · Facturation mensuelle
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Auth Modal ───────────────────────────────────────────────────────────────
function AuthModal({ onClose, onAuth, defaultMode }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  // Email auth state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("particulier"); // particulier | entreprise
  // Champs Pro : SIRET + raison sociale (visible si accountType === "entreprise")
  const [siret, setSiret] = useState("");
  // siretCheck : { state, hint, name } — state: idle | checking | available | used | invalid
  const [siretCheck, setSiretCheck] = useState({ state: "idle", hint: "", name: "" });
  // Lookup gouv : infos officielles de l'entreprise (raison sociale, SIREN, activité)
  const [siretGouvInfo, setSiretGouvInfo] = useState(null);
  const [siretGouvSearching, setSiretGouvSearching] = useState(false);
  // emailMode = "signin" | "signup" (si defaultMode="signup") | "reset"
  const [emailMode, setEmailMode] = useState(defaultMode === "signup" ? "signup" : "signin");

  // Validation temps réel du SIRET (debounce 600ms)
  useEffect(() => {
    // Si pas en mode pro signup, on ne check rien
    if (emailMode !== "signup" || accountType !== "entreprise") {
      setSiretCheck({ state: "idle", hint: "", name: "" });
      return;
    }
    const clean = String(siret || "").replace(/\s/g, "");
    if (clean.length === 0) {
      setSiretCheck({ state: "idle", hint: "", name: "" });
      return;
    }
    if (clean.length < 9 || clean.length > 14) {
      setSiretCheck({ state: "invalid", hint: "Le SIRET doit faire 14 chiffres (ou SIREN 9 chiffres).", name: "" });
      return;
    }
    if (!/^\d+$/.test(clean)) {
      setSiretCheck({ state: "invalid", hint: "Le SIRET ne contient que des chiffres.", name: "" });
      return;
    }
    setSiretCheck({ state: "checking", hint: "Vérification…", name: "" });
    setSiretGouvInfo(null);
    setSiretGouvSearching(false);
    const t = setTimeout(async () => {
      // 1) Check unicité côté VehicleIQ
      let unique = true;
      try {
        const r = await apiGet("/api/users/check-siret?siret=" + encodeURIComponent(clean));
        if (r?.available === true) {
          setSiretCheck({ state: "available", hint: "SIRET disponible", name: "" });
        } else if (r?.reason === "already_used") {
          unique = false;
          setSiretCheck({
            state: "used",
            hint: "Ce SIRET est déjà associé à un autre compte (" + (r.hint || "compte existant") + "). " +
                  "Si vous êtes collaborateur, demandez à votre administrateur de vous inviter en tant qu'agent (offre Pro Multi-Agent).",
            name: ""
          });
        } else {
          setSiretCheck({ state: "idle", hint: "", name: "" });
        }
      } catch (e) {
        setSiretCheck({ state: "idle", hint: "", name: "" });
      }
      // 2) Lookup gouv en parallèle (uniquement si SIRET disponible)
      if (unique && clean.length >= 9) {
        setSiretGouvSearching(true);
        try {
          const res = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${clean}&page=1&per_page=1`);
          const data = await res.json();
          const result = data?.results?.[0];
          if (result) {
            const info = {
              nom: result.nom_complet || result.nom_raison_sociale || "—",
              siren: result.siren,
              activite: result.activite_principale_libelle || "—",
            };
            setSiretGouvInfo(info);
            // Pré-remplir le champ "name" si vide
            if (!name || name.trim() === "") {
              setName(info.nom);
            }
          } else {
            setSiretGouvInfo(null);
          }
        } catch (e) {
          setSiretGouvInfo(null);
        }
        setSiretGouvSearching(false);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [siret, accountType, emailMode]);

  // Formatage SIRET : insère des espaces tous les 3 chiffres pour la lisibilité
  const formatSiret = (v) => {
    const c = String(v || "").replace(/\D/g, "").slice(0, 14);
    return c.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
  };

  const handleGoogle = async () => {
    // SUJET 3 : Si on est en signup Pro, on doit avoir un SIRET valide AVANT d'ouvrir Google
    if (emailMode === "signup" && accountType === "entreprise") {
      const cleanSiret = String(siret || "").replace(/\s/g, "");
      if (!cleanSiret || cleanSiret.length < 9) {
        setError("Saisissez votre SIRET avant de continuer avec Google."); return;
      }
      if (siretCheck.state === "used") {
        setError("Ce SIRET est déjà utilisé. " + (siretCheck.hint || "")); return;
      }
      if (siretCheck.state === "invalid") {
        setError(siretCheck.hint || "SIRET invalide."); return;
      }
      if (siretCheck.state === "checking") {
        setError("Vérification du SIRET en cours, patientez quelques secondes…"); return;
      }
    }
    setLoading(true); setError(""); setInfo("");
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      setLoading(false); setSuccess(true);
      setTimeout(() => {
        onAuth({
          method: "google",
          email: result.user.email,
          name: result.user.displayName,
          uid: result.user.uid,
          accountType: emailMode === "signup" ? accountType : undefined,
          siret: emailMode === "signup" && accountType === "entreprise" ? String(siret || "").replace(/\s/g, "") : undefined,
          siretName: emailMode === "signup" && accountType === "entreprise" ? (result.user.displayName || "") : undefined,
        });
        onClose();
      }, 1200);
    } catch (err) {
      setLoading(false);
      if (err.code !== "auth/popup-closed-by-user") setError("Erreur Google : " + (err.message || "Réessayez"));
    }
  };

  // Mappe les codes Firebase vers des messages humains
  const emailErrorMsg = (err) => {
    const c = err.code || "";
    if (c === "auth/invalid-email") return "Adresse e-mail invalide.";
    if (c === "auth/user-not-found" || c === "auth/invalid-credential" || c === "auth/wrong-password") return "E-mail ou mot de passe incorrect.";
    if (c === "auth/email-already-in-use") return "Cet e-mail est déjà utilisé. Essayez de vous connecter.";
    if (c === "auth/weak-password") return "Le mot de passe doit faire au moins 6 caractères.";
    if (c === "auth/too-many-requests") return "Trop de tentatives. Réessayez dans quelques minutes.";
    if (c === "auth/network-request-failed") return "Connexion réseau interrompue. Vérifiez votre internet.";
    return "Erreur d'authentification : " + (err.message || c || "inconnue");
  };

  const handleEmailSubmit = async () => {
    setError(""); setInfo("");
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError("Adresse e-mail invalide."); return;
    }
    if (emailMode === "reset") {
      setLoading(true);
      try {
        await sendPasswordResetEmail(auth, cleanEmail);
        setLoading(false);
        setInfo("Un e-mail de réinitialisation vient d'être envoyé à " + cleanEmail + ". Vérifiez votre boîte de réception.");
      } catch (err) {
        setLoading(false);
        setError(emailErrorMsg(err));
      }
      return;
    }
    if (!password || password.length < 6) {
      setError("Le mot de passe doit faire au moins 6 caractères."); return;
    }
    if (emailMode === "signup" && (!name || name.trim().length < 2)) {
      setError("Veuillez indiquer votre prénom ou nom."); return;
    }
    // SUJET 3 : Bloquer le signup Pro si SIRET pas valide ou déjà pris
    if (emailMode === "signup" && accountType === "entreprise") {
      const cleanSiret = String(siret || "").replace(/\s/g, "");
      if (!cleanSiret || cleanSiret.length < 9) {
        setError("Le SIRET de votre entreprise est requis pour un compte professionnel.");
        return;
      }
      if (siretCheck.state === "used") {
        setError("Ce SIRET est déjà utilisé. " + (siretCheck.hint || ""));
        return;
      }
      if (siretCheck.state === "invalid") {
        setError(siretCheck.hint || "SIRET invalide.");
        return;
      }
      // Si checking en cours, attendre
      if (siretCheck.state === "checking") {
        setError("Vérification du SIRET en cours, patientez quelques secondes…");
        return;
      }
    }
    setLoading(true);
    try {
      let result;
      if (emailMode === "signup") {
        result = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        try { await updateProfile(result.user, { displayName: name.trim() }); } catch(e) {}
      } else {
        result = await signInWithEmailAndPassword(auth, cleanEmail, password);
      }
      setLoading(false); setSuccess(true);
      setTimeout(() => {
        onAuth({
          method: "email",
          email: result.user.email,
          name: result.user.displayName || name.trim() || cleanEmail.split("@")[0],
          uid: result.user.uid,
          accountType: emailMode === "signup" ? accountType : undefined,
          siret: emailMode === "signup" && accountType === "entreprise" ? String(siret || "").replace(/\s/g, "") : undefined,
          siretName: emailMode === "signup" && accountType === "entreprise" ? name.trim() : undefined,
        });
        onClose();
      }, 1200);
    } catch (err) {
      setLoading(false);
      if (emailMode === "signin" && (err.code === "auth/user-not-found" || err.code === "auth/invalid-credential")) {
        try {
          const methods = await fetchSignInMethodsForEmail(auth, cleanEmail);
          if (methods && methods.length > 0 && !methods.includes("password")) {
            setError("Ce compte existe mais utilise une autre méthode (" + methods.join(", ") + "). Connectez-vous avec Google.");
            return;
          }
        } catch(e) {}
      }
      setError(emailErrorMsg(err));
    }
  };

  if (success) return (
    <div className="overlay">
      <div className="panel" style={{ maxWidth: 540 }}>
        <div className="panel-body">
          <div className="auth-success">
            <div className="auth-success-icon">✅</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.8rem", marginBottom: ".4rem" }}>Connecté !</div>
            <div style={{ color: "var(--sub)", fontSize: ".85rem" }}>Bienvenue sur VehicleIQ</div>
          </div>
        </div>
      </div>
    </div>
  );

  const inputStyle = {
    width: "100%", boxSizing: "border-box", background: "var(--card)",
    border: "1px solid var(--border)", borderRadius: 6, padding: ".6rem .9rem",
    color: "var(--text)", fontSize: ".9rem", outline: "none",
    fontFamily: "var(--font-body)", marginBottom: ".6rem",
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 540 }}>
        <div className="panel-header" style={{
          flexDirection: "column", alignItems: "center", textAlign: "center", position: "relative",
          background: "linear-gradient(135deg, #F4F7FF 0%, #EEF3FF 100%)",
        }}>
          <button className="close-btn" onClick={onClose} style={{ position: "absolute", top: "1.1rem", right: "1.1rem" }}>✕</button>
          <div className="onboarding-badge">🔐 Accès sécurisé</div>
          <div className="onboarding-logo" style={{ fontSize: "2rem" }}>Mon <span>Compte</span></div>
          <div className="onboarding-tagline">Historique · Facturation · Préférences</div>
        </div>
        <div className="panel-body">
          <p style={{ fontSize: ".85rem", color: "var(--sub)", marginBottom: "1.2rem", lineHeight: 1.6 }}>
            {emailMode === "signup"
              ? "Créez votre compte gratuit pour accéder à vos estimations, votre historique et votre wallet de recharges."
              : "Connectez-vous pour accéder à vos estimations, votre historique et votre portefeuille de recharges."}
          </p>

          {/* Reset password mode */}
          {emailMode === "reset" ? (
            <>
              <div style={{ fontSize: ".82rem", color: "var(--sub)", marginBottom: ".4rem" }}>
                Entrez votre adresse e-mail pour recevoir un lien de réinitialisation.
              </div>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="vous@exemple.fr" autoComplete="email"
                onKeyDown={e => e.key === "Enter" && handleEmailSubmit()}
                style={inputStyle} />
              <button className="btn btn-accent" style={{ width: "100%" }} onClick={handleEmailSubmit} disabled={loading}>
                {loading ? "Envoi…" : "📩 Envoyer le lien"}
              </button>
              <button className="btn btn-ghost" style={{ width: "100%", marginTop: ".5rem", fontSize: ".8rem" }}
                onClick={() => { setEmailMode("signin"); setError(""); setInfo(""); }}>
                ← Retour à la connexion
              </button>
            </>
          ) : (
            <>
              {/* Toggle signin / signup */}
              <div style={{ display: "flex", gap: ".4rem", marginBottom: "1rem" }}>
                {[["signin","Se connecter"],["signup","Créer un compte"]].map(([m,l]) => (
                  <button key={m} onClick={() => { setEmailMode(m); setError(""); setInfo(""); }}
                    style={{ flex: 1, padding: ".5rem .5rem", borderRadius: 6, border: "1px solid",
                      borderColor: emailMode === m ? "var(--accent)" : "var(--border)",
                      background: emailMode === m ? "rgba(168,128,58,.12)" : "transparent",
                      color: emailMode === m ? "var(--accent)" : "var(--sub)",
                      fontSize: ".82rem", fontWeight: emailMode === m ? 700 : 400, cursor: "pointer",
                      fontFamily: "var(--font-body)", transition: "all .15s" }}>
                    {l}
                  </button>
                ))}
              </div>

              {emailMode === "signup" && (
                <>
                  <div style={{ fontSize: ".78rem", color: "var(--sub)", fontWeight: 600, marginBottom: ".4rem" }}>
                    Vous êtes :
                  </div>
                  <div style={{ display: "flex", gap: ".5rem", marginBottom: ".9rem" }}>
                    {[["particulier","🚗 Particulier","Estimer / vendre mon véhicule"],["entreprise","🏢 Professionnel","Garage VO, marchand, revendeur, mandataire"]].map(([v,l,d]) => (
                      <button key={v} type="button" onClick={() => setAccountType(v)}
                        style={{ flex: 1, padding: ".7rem .6rem", borderRadius: 8, border: "1.5px solid",
                          borderColor: accountType === v ? "var(--accent)" : "var(--border)",
                          background: accountType === v ? "rgba(168,128,58,.10)" : "var(--white)",
                          cursor: "pointer", textAlign: "left", transition: "all .15s",
                          fontFamily: "var(--font-body)" }}>
                        <div style={{ fontSize: ".85rem", fontWeight: 700,
                          color: accountType === v ? "var(--accent)" : "var(--text)" }}>{l}</div>
                        <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: 2, lineHeight: 1.25 }}>{d}</div>
                      </button>
                    ))}
                  </div>
                  <input type="text" value={name} onChange={e => setName(e.target.value)}
                    placeholder={accountType === "entreprise" ? "Nom du contact ou raison sociale" : "Prénom ou nom"} autoComplete="name"
                    style={inputStyle} />
                  {/* SUJET 3 : Champ SIRET obligatoire pour les Pros avec validation temps réel */}
                  {accountType === "entreprise" && (
                    <>
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          value={siret}
                          onChange={e => setSiret(formatSiret(e.target.value))}
                          placeholder="SIRET de votre entreprise (14 chiffres)"
                          inputMode="numeric"
                          autoComplete="off"
                          maxLength={17}
                          style={{
                            ...inputStyle,
                            paddingRight: 32,
                            borderColor:
                              siretCheck.state === "used"      ? "#9B2C2C"
                            : siretCheck.state === "invalid"   ? "#9B2C2C"
                            : siretCheck.state === "available" ? "var(--green)"
                            : (inputStyle && inputStyle.borderColor) || "var(--border)"
                          }}
                        />
                        {/* Indicateur visuel à droite de l'input */}
                        <div style={{
                          position: "absolute",
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "0.85rem",
                          pointerEvents: "none",
                          color:
                            siretCheck.state === "used"      ? "#9B2C2C"
                          : siretCheck.state === "invalid"   ? "#9B2C2C"
                          : siretCheck.state === "available" ? "var(--green)"
                          : "var(--muted)"
                        }}>
                          {siretCheck.state === "checking"  ? "…"
                         : siretCheck.state === "available" ? "✓"
                         : siretCheck.state === "used"      ? "✗"
                         : siretCheck.state === "invalid"   ? "!"
                         : ""}
                        </div>
                      </div>
                      {/* Hint sous le champ */}
                      {siretCheck.hint && (
                        <div style={{
                          fontSize: "0.72rem",
                          marginTop: "-0.4rem",
                          marginBottom: "0.6rem",
                          lineHeight: 1.4,
                          color:
                            siretCheck.state === "used"      ? "#9B2C2C"
                          : siretCheck.state === "invalid"   ? "#9B2C2C"
                          : siretCheck.state === "available" ? "var(--green)"
                          : "var(--sub)"
                        }}>
                          {siretCheck.hint}
                        </div>
                      )}
                      {/* Encart raison sociale officielle data.gouv */}
                      {siretGouvSearching && siretCheck.state !== "used" && (
                        <div style={{ fontSize: "0.7rem", color: "var(--muted)", marginTop: "-0.4rem", marginBottom: "0.5rem" }}>
                          Recherche entreprise sur data.gouv.fr…
                        </div>
                      )}
                      {siretGouvInfo && siretCheck.state === "available" && (
                        <div style={{
                          fontSize: "0.72rem", color: "var(--green)",
                          background: "rgba(4,120,87,.06)",
                          border: "1px solid rgba(4,120,87,.18)",
                          borderRadius: 4, padding: "0.5rem 0.7rem",
                          marginTop: "-0.4rem", marginBottom: "0.6rem",
                          lineHeight: 1.4
                        }}>
                          ✓ <strong>{siretGouvInfo.nom}</strong>
                          <div style={{ fontSize: "0.64rem", color: "var(--sub)", marginTop: 2 }}>
                            SIREN {siretGouvInfo.siren} · {siretGouvInfo.activite}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="vous@exemple.fr" autoComplete="email"
                style={inputStyle} />
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Mot de passe (6 caractères min)"
                autoComplete={emailMode === "signup" ? "new-password" : "current-password"}
                onKeyDown={e => e.key === "Enter" && handleEmailSubmit()}
                style={inputStyle} />

              <button className="btn btn-accent" style={{ width: "100%" }} onClick={handleEmailSubmit} disabled={loading}>
                {loading ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, display: "inline-block", marginRight: ".5rem" }} />{emailMode === "signup" ? "Création…" : "Connexion…"}</> : (emailMode === "signup" ? "Créer mon compte" : "Continuer")}
              </button>

              {emailMode === "signin" && (
                <button className="btn btn-ghost" style={{ width: "100%", marginTop: ".4rem", fontSize: ".75rem", color: "var(--muted)" }}
                  onClick={() => { setEmailMode("reset"); setError(""); setInfo(""); }}>
                  Mot de passe oublié ?
                </button>
              )}
            </>
          )}

          {/* Séparateur "ou" + Google (visible en signin et signup, pas en reset) */}
          {emailMode !== "reset" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: ".7rem", margin: "1.1rem 0", color: "var(--muted)", fontSize: ".72rem" }}>
                <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                <span>OU</span>
                <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
              </div>
              <button className="auth-method-btn" onClick={handleGoogle} disabled={loading}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: ".8rem" }}>
                {loading ? <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : <GoogleSVG />}
                <span>{loading ? "Connexion…" : (emailMode === "signup" ? "S'inscrire avec Google" : "Continuer avec Google")}</span>
              </button>
            </>
          )}

          {error && <div style={{ marginTop: ".8rem", padding: ".5rem .8rem", background: "rgba(255,60,92,.08)", border: "1px solid rgba(255,60,92,.25)", borderRadius: 4, fontSize: ".78rem", color: "var(--red)" }}>⚠ {error}</div>}
          {info && <div style={{ marginTop: ".8rem", padding: ".5rem .8rem", background: "rgba(0,200,100,.08)", border: "1px solid rgba(0,200,100,.25)", borderRadius: 4, fontSize: ".78rem", color: "var(--green)" }}>✓ {info}</div>}

          <div style={{ textAlign: "center", marginTop: "1.2rem", fontSize: ".7rem", color: "var(--muted)" }}>
            Authentification sécurisée Firebase · <span style={{ color: "var(--accent)" }}>VehicleIQ</span>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Legal Modal ──────────────────────────────────────────────────────────────
// ─── LeadFormPage — formulaire client public via lien personnalisé Pro ───────
// Accessible sans compte via https://vehicleiq.fr/?lead=TOKEN. Le client choisit
// un service (estimation / achat / carte grise), remplit le formulaire, laisse
// ses coordonnées et accepte les CGU/CGV/mentions légales. Le lead est stocké
// et apparaît dans l'onglet « Formulaires clients » du dashboard du Pro.
function LeadFormPage({ token }) {
  const [proName, setProName] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [step, setStep] = useState("form"); // v74 — plus de choix : uniquement la cotation, formulaire affiché directement
  const [type, setType] = useState("estimation");     // estimation | achat | cartegrise
  const [d, setD] = useState({});
  const [leadPhotos, setLeadPhotos] = useState([]); // data-URLs (estimation only)
  const leadFileRef = useRef(null);
  const leadCameraRef = useRef(null);
  const [plateBusy, setPlateBusy] = useState(false);   // décodage plaque en cours
  const [plateMsg, setPlateMsg] = useState("");        // retour du décodage
  const [contact, setContact] = useState({ name: "", email: "", phone: "", profile_type: "" });
  const [cgu, setCgu] = useState(false);
  const [legal, setLegal] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const [leadHistovec, setLeadHistovec] = useState(null);
  const MAX_LEAD_PHOTOS = 10;

  // v48 — Même logique de duplication que le Contrôle/Cotation simultanés du
  // pro, appliquée au formulaire client public : le plafond (3 ou 10) suit le
  // plan du compte pro derrière le lien.
  const [maxVehicles, setMaxVehicles] = useState(1);
  const [vehicleCount, setVehicleCount] = useState(1);
  const blankLeadVehicle = () => ({ immat: "", make: "", year: "", km: "", fuel: "", photos: [], hvData: null });
  const [leadVehicles, setLeadVehicles] = useState([blankLeadVehicle()]);
  const [treatAsLot, setTreatAsLot] = useState(false); // v62 — le client indique s'il préfère une vente groupée

  // v113 — TUNNEL DE CONVERSION : le visiteur commence par une estimation
  // gratuite immédiate (plaque seule, aucune coordonnée demandée), puis se
  // voit proposer l'analyse Premium à 4,99 €. Le formulaire complet reste
  // accessible en dessous pour ceux qui préfèrent la démarche classique.
  const [funnelPlate, setFunnelPlate] = useState("");
  const [funnelKm, setFunnelKm] = useState("");
  const [funnelBusy, setFunnelBusy] = useState(false);
  const [funnelResult, setFunnelResult] = useState(null); // { vehicle, estimation }
  const [funnelError, setFunnelError] = useState("");
  const [showPremiumForm, setShowPremiumForm] = useState(false);
  const [premiumContact, setPremiumContact] = useState({ name: "", email: "", phone: "" });
  const [premiumCgu, setPremiumCgu] = useState(false);
  const [premiumBusy, setPremiumBusy] = useState(false);
  const [premiumDone, setPremiumDone] = useState(false);
  const [premiumReport, setPremiumReport] = useState(null);
  const [premiumPdfError, setPremiumPdfError] = useState(false);
  // v115 — Le formulaire complet est replié par défaut (voir plus bas).
  const [formOpen, setFormOpen] = useState(false);

  const runFreeEstimate = async () => {
    const id = funnelPlate.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (id.length < 7 || funnelBusy) return;
    // v115 — Règle : le kilométrage devient obligatoire. Sans lui, la
    // fourchette est trop large pour être utile et l'indice de confiance
    // plafonne bas — mieux vaut l'exiger que livrer une estimation faible.
    if (!funnelKm || parseInt(funnelKm) <= 0) {
      setFunnelError("Merci d'indiquer le kilométrage : il est indispensable pour une estimation fiable.");
      return;
    }
    setFunnelBusy(true); setFunnelError(""); setFunnelResult(null);
    try {
      const r = await fetch(`${API_URL}/api/lead-form/${encodeURIComponent(token)}/free-estimate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: id, km: funnelKm || null }),
      }).then(x => x.json());
      if (r?.success && r.identified) setFunnelResult(r);
      else setFunnelError(r?.error || "Véhicule non identifié. Vérifiez la plaque saisie.");
    } catch (e) { setFunnelError("Service momentanément indisponible."); }
    finally { setFunnelBusy(false); }
  };

  const startPremiumCheckout = async () => {
    if (premiumBusy) return;
    if (!premiumContact.name.trim() || !premiumContact.email.trim() || !premiumContact.phone.trim() || !premiumCgu) return;
    setPremiumBusy(true); setFunnelError("");
    try {
      const r = await fetch(`${API_URL}/api/lead-form/${encodeURIComponent(token)}/premium-checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_name: premiumContact.name, client_email: premiumContact.email, client_phone: premiumContact.phone,
          cgu_accepted: premiumCgu, vehicle: funnelResult?.vehicle, estimation: funnelResult?.estimation,
        }),
      }).then(x => x.json());
      if (r?.success && r.checkout_url) {
        // v114 — Le retour de Stripe est une navigation complète : l'état
        // React est perdu. On conserve donc le véhicule/l'estimation pour
        // pouvoir générer le rapport PDF au retour, sans redemander quoi que
        // ce soit ni refaire un calcul.
        try {
          sessionStorage.setItem("viq_premium_pending", JSON.stringify({
            vehicle: funnelResult?.vehicle || {}, estimation: funnelResult?.estimation || {},
            client: { name: premiumContact.name, email: premiumContact.email }, proName,
          }));
        } catch (e) { /* mode privé : on retombera sur les données serveur */ }
        window.location.href = r.checkout_url;
      }
      else setFunnelError(r?.error || "Impossible d'initier le paiement.");
    } catch (e) { setFunnelError("Service de paiement momentanément indisponible."); }
    finally { setPremiumBusy(false); }
  };

  // Retour de Stripe : on confirme le paiement côté serveur (jamais sur la
  // seule foi du paramètre d'URL) — c'est ce qui déclenche la création du
  // lead qualifié, du Dossier VIQ et la notification du professionnel.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("premium") === "succes" && params.get("session_id")) {
      fetch(`${API_URL}/api/lead-form/${encodeURIComponent(token)}/premium-confirm`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: params.get("session_id") }),
      }).then(x => x.json()).then(r => {
        if (!r?.success) return;
        setPremiumDone(true);
        // v114 — Le paiement est confirmé côté serveur : on génère et
        // télécharge le rapport automatiquement. Les données proviennent de
        // la session (conservée avant la redirection Stripe) ou, à défaut,
        // de ce que le serveur a enregistré avec le lead.
        let payload = null;
        try { payload = JSON.parse(sessionStorage.getItem("viq_premium_pending") || "null"); } catch (e) {}
        if (!payload && r.lead) {
          payload = { vehicle: r.lead.vehicle || {}, estimation: r.lead.estimation || {}, client: {}, proName };
        }
        if (payload) {
          setPremiumReport(payload);
          generatePremiumReportPDF(payload)
            .then(() => { try { sessionStorage.removeItem("viq_premium_pending"); } catch (e) {} })
            .catch(err => { console.warn("[premium pdf]", err.message); setPremiumPdfError(true); });
        } else {
          setPremiumPdfError(true);
        }
      }).catch(() => {});
    }
  }, [token, proName]);

  useEffect(() => {
    fetch(`${API_URL}/api/lead-form/${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(j => { if (j?.success) { setProName(j.pro_name); setMaxVehicles(j.max_vehicles || 1); } else setInvalid(true); })
      .catch(() => setProName("votre professionnel automobile"));
  }, [token]);

  const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));

  // Décodeur de plaque — remplissage rapide (marque, modèle, finition) comme dans
  // le service estimation. Identifie le véhicule via le SIV et pré-remplit les champs.
  const decodePlate = async () => {
    const clean = String(d.immat || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (clean.length < 7) { setPlateMsg("Saisissez une plaque complète (ex : AA-123-BB)."); return; }
    setPlateBusy(true); setPlateMsg("");
    try {
      const data = await apiGet("/api/check/" + clean + "?is_pro=false&siret=");
      if (!data?.success || !data?.data) {
        setPlateMsg("Véhicule non identifié — complétez les champs manuellement.");
        setPlateBusy(false); return;
      }
      const info = data.data;
      const fullName = buildVehicleFullName(info);
      setD(prev => ({
        ...prev,
        make: fullName || prev.make,
        year: info.year ? String(info.year) : prev.year,
        fuel: info.fuel || prev.fuel,
        km: info.km ? String(info.km) : prev.km,
        vin: info.vin || prev.vin,
      }));
      setPlateMsg("✓ Véhicule identifié — vérifiez et complétez si besoin.");
    } catch (e) {
      setPlateMsg(e.code === "NOT_FOUND"
        ? "Plaque non reconnue — saisie manuelle possible."
        : "Service de décodage momentanément indisponible — saisie manuelle possible.");
    } finally { setPlateBusy(false); }
  };
  const lbl = { display: "block", fontSize: ".68rem", fontWeight: 700, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: ".35rem" };
  const fld = { width: "100%", padding: ".65rem .8rem", borderRadius: 10, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".92rem", outline: "none", boxSizing: "border-box" };
  const g2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".8rem", marginBottom: ".8rem" };
  const row = (children) => <div style={{ marginBottom: ".8rem" }}>{children}</div>;

  // v48 — Décodage plaque pour UNE carte véhicule du lot (formulaire multi-véhicules).
  const decodeBatchPlate = async (i) => {
    const clean = String(leadVehicles[i]?.immat || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (clean.length < 7) return;
    try {
      const data = await apiGet("/api/check/" + clean + "?is_pro=false&siret=");
      if (!data?.success || !data?.data) return;
      const info = data.data;
      const fullName = buildVehicleFullName(info);
      setLeadVehicles(prev => prev.map((v, idx) => idx === i ? {
        ...v,
        make: fullName || v.make,
        year: info.year ? String(info.year) : v.year,
        fuel: info.fuel || v.fuel,
        km: info.km ? String(info.km) : v.km,
      } : v));
    } catch (e) { /* saisie manuelle possible */ }
  };

  const SERVICES = [
    { id: "estimation", icon: "🚗", title: "Faire estimer mon véhicule", desc: "Obtenez la valeur de marché de votre véhicule, calculée par le moteur VIQ.AI®." },
    { id: "achat", icon: "🔎", title: "Être conseillé pour un achat", desc: "Décrivez le véhicule recherché : votre professionnel vous proposera les meilleures options." },
    { id: "cartegrise", icon: "🧾", title: "Estimer ma carte grise & malus", desc: "Coût d'immatriculation en France : taxe régionale, malus CO₂ et malus au poids, import compris." },
  ];
  const svcMeta = SERVICES.find(s => s.id === type);

  // Ajout de photos (estimation) — le pro lancera l'analyse vision depuis son dashboard.
  const addLeadPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    const room = Math.max(0, MAX_LEAD_PHOTOS - leadPhotos.length);
    if (room <= 0) return;
    const toDataUrl = (f) => compressImageFile(f).then(r => r || new Promise((resolve, reject) => {
      const rd = new FileReader(); rd.onload = () => resolve(rd.result); rd.onerror = reject; rd.readAsDataURL(f);
    }));
    try {
      const urls = (await Promise.all(files.slice(0, room).map(toDataUrl))).filter(Boolean);
      setLeadPhotos(prev => [...prev, ...urls].slice(0, MAX_LEAD_PHOTOS));
    } catch (e) {}
    if (leadFileRef.current) leadFileRef.current.value = "";
  };

  const submit = async () => {
    setError("");
    if (!contact.name.trim()) { setError("Merci de renseigner votre nom."); return; }
    const isBatch = type === "estimation" && vehicleCount > 1;
    // Immatriculation obligatoire pour une estimation (identifie le véhicule de façon fiable)
    if (type === "estimation" && !isBatch && !String(d.immat || "").trim()) {
      setError("L'immatriculation est obligatoire pour une estimation."); return;
    }
    if (isBatch && leadVehicles.some(v => !v.immat.trim())) {
      setError("Merci de renseigner la plaque de chaque véhicule."); return;
    }
    // Téléphone obligatoire (le professionnel doit pouvoir vous rappeler)
    if (!contact.phone.trim()) { setError("Merci de renseigner un numéro de téléphone."); return; }
    if (!contact.profile_type) { setError("Merci d'indiquer si vous êtes un professionnel de l'achat/revente, un particulier, ou autre."); return; }
    if (!cgu) { setError("Vous devez accepter les CGU, CGV et mentions légales pour envoyer votre demande."); return; }
    setSending(true);
    try {
      const body = {
        type, client_name: contact.name, client_email: contact.email, client_phone: contact.phone,
        client_profile_type: contact.profile_type, cgu_accepted: cgu,
      };
      if (isBatch) {
        body.vehicles = leadVehicles.map(v => ({
          data: { immat: v.immat, make: v.make, year: v.year, km: v.km, fuel: v.fuel, treat_as_lot: treatAsLot ? "oui" : "non" },
          photos: v.photos.length ? v.photos : undefined,
          histovec: v.hvData || undefined,
        }));
      } else {
        body.data = d;
        body.photos = type === "estimation" ? leadPhotos : undefined;
        body.histovec = type === "estimation" ? (leadHistovec || undefined) : undefined;
      }
      const r = await fetch(`${API_URL}/api/lead-form/${encodeURIComponent(token)}/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (j?.success) setStep("done");
      else setError(j?.error || "Envoi impossible. Réessayez.");
    } catch (e) { setError("Erreur réseau. Réessayez."); }
    finally { setSending(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, var(--accent-soft) 0%, var(--bg) 240px)", padding: "2.5rem 1.2rem 4rem", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ width: "100%", maxWidth: 680 }}>
        {/* En-tête */}
        <div style={{ textAlign: "center", marginBottom: "1.6rem" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", fontFamily: "var(--font-head)", fontWeight: 800, fontSize: "1.5rem", color: "var(--accent)", marginBottom: ".8rem" }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--accent)", display: "inline-block" }} />
            VehicleIQ
          </div>
          {invalid ? (
            <div style={{ color: "var(--red)", fontWeight: 600, background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.2rem" }}>Ce lien n'est plus valide. Contactez votre professionnel.</div>
          ) : (
            <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 16, padding: "1.1rem 1.3rem", display: "flex", alignItems: "center", gap: ".9rem", textAlign: "left", boxShadow: "0 2px 12px rgba(0,0,0,.04)" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "1.2rem", flexShrink: 0, fontFamily: "var(--font-head)" }}>
                {(proName || "?").trim().charAt(0).toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: ".76rem", color: "var(--muted)", marginBottom: ".15rem" }}>Bonjour 👋</div>
                <div style={{ color: "var(--text)", fontSize: ".98rem", fontWeight: 800, lineHeight: 1.3 }}>{proName || "…"}</div>
                <div style={{ fontSize: ".76rem", color: "var(--sub)", lineHeight: 1.4, marginTop: ".2rem" }}>
                  sera ravi(e) de vous répondre — merci de prendre un instant pour remplir ce court formulaire, nous revenons vers vous rapidement.
                </div>
              </div>
            </div>
          )}
          {!invalid && (
            <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: ".7rem", display: "flex", alignItems: "center", justifyContent: "center", gap: ".4rem" }}>
              <span>🔒</span> Vos informations sont transmises uniquement à ce professionnel.
            </div>
          )}
        </div>

        {/* Fil d'étapes retiré (peu clair pour le client) */}

        {!invalid && step === "choice" && (
          <div style={{ display: "grid", gap: ".9rem" }}>
            <div style={{ textAlign: "center", fontWeight: 800, color: "var(--text)", fontSize: "1.1rem", marginBottom: ".3rem" }}>Que souhaitez-vous faire ?</div>
            {SERVICES.map(s => (
              <button key={s.id} onClick={() => { setType(s.id); setStep("form"); }}
                style={{ textAlign: "left", display: "flex", gap: "1rem", alignItems: "center", background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.1rem 1.2rem", cursor: "pointer", transition: "all .15s", boxShadow: "0 1px 6px rgba(0,0,0,.03)" }}
                onMouseOver={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,.08)"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 1px 6px rgba(0,0,0,.03)"; }}>
                <span style={{ width: 46, height: 46, borderRadius: 12, background: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", lineHeight: 1, flexShrink: 0 }}>{s.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 800, color: "var(--text)", fontSize: ".98rem", marginBottom: ".2rem" }}>{s.title}</span>
                  <span style={{ display: "block", color: "var(--sub)", fontSize: ".82rem", lineHeight: 1.5 }}>{s.desc}</span>
                </span>
                <span style={{ color: "var(--accent)", fontSize: "1.3rem", flexShrink: 0, fontWeight: 700 }}>→</span>
              </button>
            ))}
          </div>
        )}

        {/* v113 — TUNNEL DE CONVERSION : estimation gratuite immédiate, sans
            aucune coordonnée demandée. La valeur d'abord, les coordonnées
            ensuite (et seulement pour l'offre Premium). */}
        {!invalid && step === "form" && premiumDone && (
          <div style={{ background: "rgba(46,111,92,.08)", border: "1.5px solid var(--green)", borderRadius: 16, padding: "1.4rem", marginBottom: "1.2rem", textAlign: "center" }}>
            <div style={{ fontSize: "1.6rem", marginBottom: ".4rem" }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "var(--green)", marginBottom: ".3rem" }}>Paiement confirmé — votre rapport est prêt</div>
            <div style={{ fontSize: ".85rem", color: "var(--sub)", lineHeight: 1.6 }}>
              Le téléchargement de votre rapport PDF démarre automatiquement. {proName} a été notifié et prendra contact avec vous rapidement.
            </div>
            {premiumReport && (
              <button className="btn btn-accent" style={{ marginTop: ".9rem", justifyContent: "center" }}
                onClick={() => generatePremiumReportPDF(premiumReport).catch(() => setPremiumPdfError(true))}>
                📄 Télécharger à nouveau le rapport
              </button>
            )}
            {premiumPdfError && (
              <div style={{ fontSize: ".78rem", color: "var(--red)", marginTop: ".6rem", lineHeight: 1.5 }}>
                Le téléchargement automatique n'a pas pu démarrer. Votre paiement est bien enregistré —
                contactez {proName} qui dispose de toutes vos données.
              </div>
            )}
          </div>
        )}

        {!invalid && step === "form" && !premiumDone && (
          <div style={{ background: "var(--white)", border: "1.5px solid var(--accent)", borderRadius: 16, padding: "1.4rem", marginBottom: "1.2rem", boxShadow: "0 4px 20px rgba(0,0,0,.06)" }}>
            <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "var(--text)", marginBottom: ".25rem" }}>
              💰 Estimez votre véhicule gratuitement
            </div>
            <div style={{ fontSize: ".82rem", color: "var(--sub)", marginBottom: "1rem", lineHeight: 1.55 }}>
              Saisissez simplement votre plaque : vous obtenez immédiatement une estimation, sans inscription ni coordonnées.
            </div>
            <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginBottom: ".6rem" }}>
              <input value={funnelPlate} onChange={e => setFunnelPlate(e.target.value.toUpperCase())}
                onKeyDown={e => { if (e.key === "Enter") runFreeEstimate(); }}
                placeholder="AA-123-BB ou VIN"
                style={{ ...fld, flex: "2 1 180px", fontFamily: "var(--font-mono)", letterSpacing: "1px", textTransform: "uppercase" }} />
              <input value={funnelKm} onChange={e => setFunnelKm(e.target.value.replace(/\D/g, ""))}
                onKeyDown={e => { if (e.key === "Enter") runFreeEstimate(); }}
                placeholder="Kilométrage *"
                style={{ ...fld, flex: "1 1 140px" }} />
              <button className="btn btn-accent" disabled={funnelBusy || funnelPlate.replace(/[^a-zA-Z0-9]/g, "").length < 7 || !funnelKm}
                onClick={runFreeEstimate}
                style={{ flex: "1 1 150px", justifyContent: "center", opacity: (funnelBusy || !funnelKm) ? .6 : 1 }}>
                {funnelBusy ? "Analyse…" : "Estimer gratuitement →"}
              </button>
            </div>
            {funnelError && <div style={{ fontSize: ".8rem", color: "var(--red)", marginTop: ".3rem" }}>⚠ {funnelError}</div>}

            {funnelResult && (
              <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid var(--border)" }}>
                <div style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: ".3rem" }}>
                  {[funnelResult.vehicle?.make, funnelResult.vehicle?.model, funnelResult.vehicle?.year].filter(Boolean).join(" ")}
                  {funnelResult.vehicle?.fuel ? ` · ${funnelResult.vehicle.fuel}` : ""}
                </div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: "2rem", fontWeight: 700, color: "var(--green)", lineHeight: 1.1 }}>
                  {Number(funnelResult.estimation?.prix_conseille || 0).toLocaleString("fr-FR")} €
                </div>
                <div style={{ fontSize: ".8rem", color: "var(--sub)", marginTop: ".2rem" }}>
                  Fourchette : {Number(funnelResult.estimation?.fourchette_basse || 0).toLocaleString("fr-FR")} € – {Number(funnelResult.estimation?.fourchette_haute || 0).toLocaleString("fr-FR")} €
                  {" · "}Indice de confiance : {funnelResult.estimation?.confiance}%
                </div>
                {!funnelResult.estimation?.km_utilise && (
                  <div style={{ fontSize: ".74rem", color: "var(--muted)", marginTop: ".35rem", fontStyle: "italic" }}>
                    Estimation calculée sans kilométrage — ajoutez-le ci-dessus pour la resserrer.
                  </div>
                )}

                {/* Upsell Premium */}
                <div style={{ marginTop: "1.1rem", background: "var(--card)", borderRadius: 12, padding: "1rem" }}>
                  <div style={{ fontWeight: 800, fontSize: ".95rem", marginBottom: ".4rem" }}>
                    🔍 Aller plus loin — Analyse Premium <span style={{ color: "var(--accent)" }}>4,99 €</span>
                  </div>
                  <ul style={{ fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.7, margin: 0, paddingLeft: "1.1rem" }}>
                    <li>Analyse Photo IA de l'état réel du véhicule</li>
                    <li>Rapport PDF complet + score VIQ.AI®</li>
                    <li>Comparaison avec les annonces similaires du marché</li>
                    <li>Recommandations pour vendre au meilleur prix</li>
                  </ul>
                  {!showPremiumForm ? (
                    <button className="btn btn-accent" style={{ width: "100%", justifyContent: "center", marginTop: ".8rem" }}
                      onClick={() => setShowPremiumForm(true)}>
                      Obtenir mon analyse Premium →
                    </button>
                  ) : (
                    <div style={{ marginTop: ".8rem", display: "flex", flexDirection: "column", gap: ".5rem" }}>
                      <input style={fld} placeholder="Nom et prénom *" value={premiumContact.name}
                        onChange={e => setPremiumContact(p => ({ ...p, name: e.target.value }))} />
                      <input style={fld} type="email" placeholder="Email *" value={premiumContact.email}
                        onChange={e => setPremiumContact(p => ({ ...p, email: e.target.value }))} />
                      <input style={fld} type="tel" placeholder="Téléphone *" value={premiumContact.phone}
                        onChange={e => setPremiumContact(p => ({ ...p, phone: e.target.value }))} />
                      <label style={{ display: "flex", gap: ".5rem", alignItems: "flex-start", fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.5 }}>
                        <input type="checkbox" checked={premiumCgu} onChange={e => setPremiumCgu(e.target.checked)} style={{ marginTop: ".15rem", accentColor: "var(--accent)" }} />
                        <span>J'accepte les CGU, CGV et mentions légales, et que mes informations soient transmises à {proName} pour traiter ma demande.</span>
                      </label>
                      <button className="btn btn-accent" disabled={premiumBusy || !premiumContact.name.trim() || !premiumContact.email.trim() || !premiumContact.phone.trim() || !premiumCgu}
                        onClick={startPremiumCheckout}
                        style={{ width: "100%", justifyContent: "center", opacity: (premiumBusy || !premiumCgu) ? .6 : 1 }}>
                        {premiumBusy ? "Redirection…" : "Payer 4,99 € et lancer l'analyse →"}
                      </button>
                      <div style={{ fontSize: ".7rem", color: "var(--muted)", textAlign: "center" }}>
                        Paiement sécurisé par Stripe · Aucun abonnement
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {!invalid && step === "form" && (
          <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 16, padding: "1.4rem 1.4rem 1.6rem", boxShadow: "0 4px 20px rgba(0,0,0,.05)" }}>
            {/* v115 — Repliée par défaut : l'estimation gratuite ci-dessus est
                le point d'entrée principal. Ce formulaire complet reste
                disponible pour qui souhaite une offre de reprise du pro. */}
            <button type="button" onClick={() => setFormOpen(o => !o)}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".6rem", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", color: "var(--text)" }}>
              <span style={{ fontWeight: 800, fontSize: "1.1rem" }}>{svcMeta?.icon} Faire estimer mon véhicule et recevoir une offre</span>
              <span style={{ fontSize: "1.1rem", color: "var(--accent)", flexShrink: 0, transform: formOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}>⌄</span>
            </button>
            {!formOpen && (
              <div style={{ fontSize: ".8rem", color: "var(--sub)", marginTop: ".5rem", lineHeight: 1.55 }}>
                Transmettez votre véhicule à {proName} pour recevoir une offre de reprise personnalisée.
              </div>
            )}
            {formOpen && (<>
            <div style={{ height: "1rem" }} />

            {type === "estimation" && (<>
              {/* v48 — Même logique que le Contrôle/Cotation simultanés du pro */}
              {maxVehicles > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: ".6rem", marginBottom: "1rem" }}>
                  <label style={{ fontSize: ".78rem", color: "var(--sub)", fontWeight: 600 }}>Nombre de véhicules :</label>
                  <select value={vehicleCount} onChange={e => {
                    const n = parseInt(e.target.value, 10);
                    setVehicleCount(n);
                    setLeadVehicles(prev => {
                      const next = prev.slice(0, n);
                      while (next.length < n) next.push(blankLeadVehicle());
                      return next;
                    });
                  }} style={{ padding: ".35rem .6rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".82rem", fontWeight: 600 }}>
                    {Array.from({ length: maxVehicles }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>{n === 1 ? "1 véhicule" : `${n} véhicules`}</option>
                    ))}
                  </select>
                </div>
              )}
              {vehicleCount > 1 && (
                <label style={{ display: "flex", alignItems: "center", gap: ".5rem", justifyContent: "center", marginBottom: "1rem", fontSize: ".78rem", color: "var(--sub)", cursor: "pointer" }}>
                  <input type="checkbox" checked={treatAsLot} onChange={e => setTreatAsLot(e.target.checked)} />
                  Je préfère vendre ces véhicules ensemble, en un seul lot (plutôt que séparément)
                </label>
              )}
              {vehicleCount > 1 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: ".9rem", marginBottom: ".8rem" }}>
                  {leadVehicles.map((v, i) => (
                    <div key={i} style={{ padding: ".9rem", border: "1.5px solid var(--border)", borderRadius: 12, background: "var(--card)" }}>
                      <div style={{ fontWeight: 800, fontSize: ".84rem", marginBottom: ".5rem" }}>Véhicule {i + 1}</div>
                      <div style={{ display: "flex", gap: ".5rem", alignItems: "stretch", marginBottom: ".6rem" }}>
                        <input style={{ ...fld, flex: 1, textTransform: "uppercase" }} value={v.immat}
                          onChange={e => setLeadVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, immat: e.target.value.toUpperCase() } : x))}
                          onKeyDown={e => e.key === "Enter" && decodeBatchPlate(i)}
                          placeholder="Plaque AA-123-BB" />
                        <button type="button" onClick={() => decodeBatchPlate(i)}
                          style={{ flexShrink: 0, padding: ".5rem 1rem", borderRadius: 8, border: "none", background: "var(--text)", color: "var(--bg)", fontSize: ".82rem", fontWeight: 800, cursor: "pointer" }}>
                          🔍 Décoder
                        </button>
                      </div>
                      <input style={{ ...fld, marginBottom: ".5rem" }} value={v.make}
                        onChange={e => setLeadVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, make: e.target.value } : x))}
                        placeholder="Marque et modèle exact" />
                      <div style={{ display: "flex", gap: ".5rem", marginBottom: ".6rem" }}>
                        <input style={fld} type="number" value={v.year} onChange={e => setLeadVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, year: e.target.value } : x))} placeholder="Année" />
                        <input style={fld} type="number" value={v.km} onChange={e => setLeadVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, km: e.target.value } : x))} placeholder="Kilométrage" />
                      </div>
                      <HistovecImport onImported={hv => setLeadVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, hvData: hv } : x))} compact />
                      {v.hvData && <HistovecSummary hv={v.hvData} />}
                      <div style={{ marginTop: ".6rem", padding: ".6rem .7rem", background: "rgba(46,111,92,.05)", border: "1px solid rgba(46,111,92,.2)", borderRadius: 8 }}>
                        <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--green)", marginBottom: ".3rem" }}>📸 Photos ({v.photos.length}/{MAX_LEAD_PHOTOS})</div>
                        {v.photos.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: ".35rem", marginBottom: ".45rem" }}>
                            {v.photos.map((p, pi) => (
                              <div key={pi} style={{ position: "relative", width: 54, height: 54, borderRadius: 6, overflow: "hidden", border: "1.5px solid var(--border)" }}>
                                <img src={p} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <button type="button" onClick={() => setLeadVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, photos: x.photos.filter((_, ppi) => ppi !== pi) } : x))}
                                  style={{ position: "absolute", top: 1, right: 1, background: "rgba(0,0,0,.6)", color: "#fff", border: "none", borderRadius: "50%", width: 16, height: 16, fontSize: ".62rem", cursor: "pointer", lineHeight: 1, padding: 0 }}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {v.photos.length < MAX_LEAD_PHOTOS && (
                          <div style={{ display: "flex", gap: ".5rem", alignItems: "center", flexWrap: "wrap" }}>
                            <button type="button" onClick={() => document.getElementById(`lead-batch-gallery-${i}`)?.click()}
                              style={{ padding: ".35rem .75rem", borderRadius: 8, border: "1px solid var(--green)", background: "var(--white)", color: "var(--green)", fontSize: ".76rem", fontWeight: 700, cursor: "pointer" }}>
                              🖼 Galerie
                            </button>
                            <button type="button" onClick={() => document.getElementById(`lead-batch-camera-${i}`)?.click()}
                              style={{ padding: ".35rem .75rem", borderRadius: 8, border: "1px solid var(--green)", background: "var(--green)", color: "#fff", fontSize: ".76rem", fontWeight: 700, cursor: "pointer" }}>
                              📷 Appareil photo
                            </button>
                            {/* Galerie : sans 'capture' → ouvre la photothèque */}
                            <input type="file" accept="image/*" multiple id={`lead-batch-gallery-${i}`} style={{ display: "none" }}
                              onChange={async e => {
                                const files = Array.from(e.target.files || []).slice(0, MAX_LEAD_PHOTOS - v.photos.length);
                                const urls = (await Promise.all(files.map(f => compressImageFile(f)))).filter(Boolean);
                                setLeadVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, photos: [...x.photos, ...urls].slice(0, MAX_LEAD_PHOTOS) } : x));
                                e.target.value = "";
                              }} />
                            {/* Appareil photo : 'capture' → ouvre directement la caméra arrière */}
                            <input type="file" accept="image/*" capture="environment" id={`lead-batch-camera-${i}`} style={{ display: "none" }}
                              onChange={async e => {
                                const files = Array.from(e.target.files || []).slice(0, MAX_LEAD_PHOTOS - v.photos.length);
                                const urls = (await Promise.all(files.map(f => compressImageFile(f)))).filter(Boolean);
                                setLeadVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, photos: [...x.photos, ...urls].slice(0, MAX_LEAD_PHOTOS) } : x));
                                e.target.value = "";
                              }} />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", textAlign: "center" }}>
                    Votre professionnel recevra une demande distincte pour chaque véhicule.
                  </div>
                </div>
              ) : (<>
              {/* Immatriculation (obligatoire) + décodeur de plaque pour remplissage rapide */}
              <div style={{ marginBottom: ".8rem", padding: ".75rem .9rem", background: "var(--accent-soft)", border: "1.5px solid rgba(168,128,58,.35)", borderRadius: 10 }}>
                <label style={{ ...lbl, color: "var(--accent)" }}>Immatriculation * — remplissage rapide</label>
                <div style={{ display: "flex", gap: ".5rem", alignItems: "stretch" }}>
                  <input style={{ ...fld, flex: 1, textTransform: "uppercase" }} value={d.immat || ""}
                    onChange={e => { set("immat", e.target.value.toUpperCase()); setPlateMsg(""); }}
                    onKeyDown={e => e.key === "Enter" && decodePlate()}
                    placeholder="AA-123-BB" />
                  <button type="button" onClick={decodePlate} disabled={plateBusy}
                    style={{ flexShrink: 0, padding: ".5rem 1.1rem", borderRadius: 8, border: "none", background: "var(--text)", color: "var(--bg)", fontSize: ".85rem", fontWeight: 800, cursor: plateBusy ? "wait" : "pointer", opacity: plateBusy ? .6 : 1, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: ".35rem" }}>
                    {plateBusy ? "…" : "🔍 Décoder"}
                  </button>
                </div>
                {plateMsg && <div style={{ fontSize: ".74rem", marginTop: ".4rem", color: plateMsg.startsWith("✓") ? "var(--green)" : "var(--sub)", fontWeight: 600 }}>{plateMsg}</div>}
                <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: ".35rem", lineHeight: 1.4 }}>
                  Saisissez votre plaque et cliquez sur « Décoder » : la marque, le modèle et la finition se remplissent automatiquement.
                </div>
              </div>
              <div style={{ marginBottom: ".8rem" }}>
                <HistovecImport onImported={setLeadHistovec} compact />
                <HistovecSummary hv={leadHistovec} />
              </div>
              {/* v116 — Rapport d'historique international CarVertical. Le VIN
                  décodé (ou la plaque saisie) est transmis dans le lien pour
                  pré-remplir la recherche côté CarVertical. */}
              <div style={{ marginBottom: ".9rem" }}>
                <div style={{ fontSize: ".74rem", color: "var(--sub)", marginBottom: ".4rem", lineHeight: 1.5 }}>
                  Vérifiez l'historique international de votre véhicule (compteur, sinistres, import/export) :
                </div>
                <CarVerticalBanner vin={d.vin} plaque={d.immat} style={{ maxWidth: 380 }} />
              </div>
              {row(<><label style={lbl}>Marque et modèle exact *</label><input style={fld} value={d.make || ""} onChange={e => set("make", e.target.value)} placeholder="Ex : Peugeot 308 SW 1.5 BlueHDi 130" /></>)}
              <div style={g2}>
                <div><label style={lbl}>Année de 1ère immat.</label><input style={fld} type="number" min="1990" max="2026" value={d.year || ""} onChange={e => set("year", e.target.value)} placeholder="Ex : 2019" /></div>
                <div><label style={lbl}>Kilométrage</label><input style={fld} type="number" value={d.km || ""} onChange={e => set("km", e.target.value)} placeholder="Ex : 85 000" /></div>
              </div>
              <div style={g2}>
                <div><label style={lbl}>Motorisation</label>
                  <select style={{ ...fld, cursor: "pointer" }} value={d.fuel || ""} onChange={e => set("fuel", e.target.value)}>
                    <option value="">—</option>{["Essence","Diesel","Hybride","Hybride rechargeable","Électrique","GPL"].map(o => <option key={o}>{o}</option>)}
                  </select></div>
                <div><label style={lbl}>Boîte de vitesses</label>
                  <select style={{ ...fld, cursor: "pointer" }} value={d.gearbox || ""} onChange={e => set("gearbox", e.target.value)}>
                    <option value="">—</option>{["Manuelle","Automatique","CVT / DSG / EDC"].map(o => <option key={o}>{o}</option>)}
                  </select></div>
              </div>
              {/* v101 — Ces champs (état, CT, propriétaires, délai, options) ne sont
                  plus affichés côté client : ils restent utiles pour la cote
                  affinée mais uniquement dans le flux professionnel. Le client
                  garde plaque/marque/année/km/motorisation/boîte + photos +
                  import HistoVec, largement suffisant pour une 1ère estimation. */}
              <div style={{ marginBottom: ".8rem", padding: ".75rem .9rem", background: "rgba(46,111,92,.05)", border: "1.5px solid rgba(46,111,92,.25)", borderRadius: 10 }}>
                <label style={{ ...lbl, color: "var(--green)" }}>📸 Photos du véhicule (recommandé · {MAX_LEAD_PHOTOS} max)</label>
                <div style={{ fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.45, marginBottom: ".5rem" }}>
                  Des photos nettes permettent à votre professionnel d'affiner la Cote. Idéalement :
                </div>
                <div style={{ fontSize: ".72rem", color: "var(--text)", lineHeight: 1.55, marginBottom: ".6rem", paddingLeft: ".2rem" }}>
                  • 3/4 avant <strong>avec la plaque visible</strong><br />
                  • 3/4 arrière<br />
                  • Tableau de bord (compteur kilométrique lisible)<br />
                  • Siège conducteur<br />
                  • Siège passager<br />
                  • Et toute autre vue utile (jantes, défauts, coffre, motorisation…)
                </div>
                {leadPhotos.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginBottom: ".6rem" }}>
                    {leadPhotos.map((src, i) => (
                      <div key={i} style={{ position: "relative", width: 70, height: 70, borderRadius: 8, overflow: "hidden", border: "1.5px solid var(--border)" }}>
                        <img src={src} alt={"photo " + (i + 1)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        <button type="button" onClick={() => setLeadPhotos(prev => prev.filter((_, j) => j !== i))}
                          style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: "50%", border: "none", background: "rgba(0,0,0,.6)", color: "#fff", fontSize: ".7rem", cursor: "pointer", lineHeight: 1, padding: 0 }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {leadPhotos.length < MAX_LEAD_PHOTOS && (
                  <>
                    <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                      <button type="button" onClick={() => leadFileRef.current && leadFileRef.current.click()}
                        style={{ padding: ".5rem 1rem", borderRadius: 8, border: "1px solid var(--green)", background: "var(--card)", color: "var(--green)", fontSize: ".8rem", fontWeight: 700, cursor: "pointer" }}>
                        🖼 Galerie
                      </button>
                      <button type="button" onClick={() => leadCameraRef.current && leadCameraRef.current.click()}
                        style={{ padding: ".5rem 1rem", borderRadius: 8, border: "1px solid var(--green)", background: "var(--green)", color: "#fff", fontSize: ".8rem", fontWeight: 700, cursor: "pointer" }}>
                        📷 Appareil photo
                      </button>
                      <span style={{ alignSelf: "center", fontSize: ".75rem", color: "var(--muted)" }}>{leadPhotos.length}/{MAX_LEAD_PHOTOS}</span>
                    </div>
                    {/* Galerie : sans 'capture' → ouvre la photothèque */}
                    <input ref={leadFileRef} type="file" accept="image/*" multiple
                      style={{ display: "none" }} onChange={e => addLeadPhotos(e.target.files)} />
                    {/* Appareil photo : 'capture' → ouvre directement la caméra arrière */}
                    <input ref={leadCameraRef} type="file" accept="image/*" capture="environment"
                      style={{ display: "none" }} onChange={e => addLeadPhotos(e.target.files)} />
                    <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".5rem" }}>
                      Choisissez vos photos depuis la galerie, ou prenez-les directement avec l'appareil photo.
                    </div>
                  </>
                )}
              </div>
              </>)}
            </>)}

            {type === "achat" && (<>
              <div style={g2}>
                <div><label style={lbl}>Budget maximum (€) *</label><input style={fld} type="number" value={d.budget || ""} onChange={e => set("budget", e.target.value)} placeholder="Ex : 25 000" /></div>
                <div><label style={lbl}>Type de véhicule</label>
                  <select style={{ ...fld, cursor: "pointer" }} value={d.segment || ""} onChange={e => set("segment", e.target.value)}>
                    <option value="">—</option>{["Citadine","Berline / Compacte","Coupé","SUV / Crossover","Break / Familiale","Utilitaire","Peu importe"].map(o => <option key={o}>{o}</option>)}
                  </select></div>
              </div>
              {row(<><label style={lbl}>Marques préférées</label><input style={fld} value={d.brands || ""} onChange={e => set("brands", e.target.value)} placeholder="Ex : Renault, Peugeot — vide = toutes" /></>)}
              <div style={g2}>
                <div><label style={lbl}>Motorisation souhaitée</label>
                  <select style={{ ...fld, cursor: "pointer" }} value={d.fuel || ""} onChange={e => set("fuel", e.target.value)}>
                    <option value="">Peu importe</option>{["Essence","Diesel","Hybride","Hybride rechargeable","Électrique"].map(o => <option key={o}>{o}</option>)}
                  </select></div>
                <div><label style={lbl}>Kilométrage max</label><input style={fld} type="number" value={d.km || ""} onChange={e => set("km", e.target.value)} placeholder="Ex : 100 000" /></div>
              </div>
              <div style={g2}>
                <div><label style={lbl}>Année minimum</label><input style={fld} type="number" min="2000" max="2026" value={d.year || ""} onChange={e => set("year", e.target.value)} placeholder="Ex : 2018" /></div>
                <div><label style={lbl}>Ville / zone de recherche</label><input style={fld} value={d.city || ""} onChange={e => set("city", e.target.value)} placeholder="Ex : Lille, France entière…" /></div>
              </div>
            </>)}

            {type === "cartegrise" && (<>
              <div style={g2}>
                <div><label style={lbl}>Immatriculation</label><input style={fld} value={d.immat || ""} onChange={e => set("immat", e.target.value.toUpperCase())} placeholder="AA-123-BB (si déjà immatriculé)" /></div>
                <div><label style={lbl}>Marque et modèle *</label><input style={fld} value={d.makeModel || ""} onChange={e => set("makeModel", e.target.value)} placeholder="Ex : BMW X3 xDrive20d" /></div>
              </div>
              <div style={g2}>
                <div><label style={lbl}>Énergie</label>
                  <select style={{ ...fld, cursor: "pointer" }} value={d.energie || ""} onChange={e => set("energie", e.target.value)}>
                    <option value="">—</option>{["Essence","Diesel","Hybride","Hybride rechargeable","Électrique","GPL"].map(o => <option key={o}>{o}</option>)}
                  </select></div>
                <div><label style={lbl}>Puissance (ch)</label><input style={fld} type="number" value={d.ch || ""} onChange={e => set("ch", e.target.value)} placeholder="Ex : 190" /></div>
              </div>
              <div style={g2}>
                <div><label style={lbl}>Émissions CO₂ (g/km, si connues)</label><input style={fld} type="number" value={d.co2 || ""} onChange={e => set("co2", e.target.value)} placeholder="Ex : 150" /></div>
                <div><label style={lbl}>Date de 1ʳᵉ immat. (mois/année)</label><input style={fld} type="month" value={d.dateImmat || ""} onChange={e => set("dateImmat", e.target.value)} /></div>
              </div>
              <div style={g2}>
                <div><label style={lbl}>Véhicule importé ?</label>
                  <select style={{ ...fld, cursor: "pointer" }} value={d.isImport || ""} onChange={e => set("isImport", e.target.value)}>
                    <option value="">—</option><option value="oui">Oui (1ʳᵉ immatriculation en France)</option><option value="non">Non (déjà immatriculé en France)</option>
                  </select></div>
                <div><label style={lbl}>Pays d'achat (si import)</label>
                  <select style={{ ...fld, cursor: "pointer" }} value={d.paysAchat || ""} onChange={e => set("paysAchat", e.target.value)}>
                    <option value="">—</option>{["Allemagne","Belgique","Luxembourg","Espagne","Italie","Autre UE"].map(o => <option key={o}>{o}</option>)}
                  </select></div>
              </div>
            </>)}

            {/* Coordonnées */}
            <div style={{ borderTop: "1.5px dashed var(--border)", margin: "1.1rem 0 .9rem", paddingTop: "1rem", fontWeight: 800, color: "var(--text)", fontSize: ".92rem" }}>Vos coordonnées</div>
            {row(<><label style={lbl}>Nom et prénom *</label><input style={fld} value={contact.name} onChange={e => setContact(c => ({ ...c, name: e.target.value }))} placeholder="Ex : Marie Dupont" /></>)}
            {row(<>
              <label style={lbl}>Vous êtes *</label>
              <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                {[["pro", "Pro de l'achat/revente"], ["particulier", "Particulier"], ["autre", "Autre"]].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setContact(c => ({ ...c, profile_type: val }))}
                    style={{
                      flex: "1 1 140px", padding: ".55rem .7rem", borderRadius: 10, cursor: "pointer", fontSize: ".82rem", fontWeight: 600,
                      border: "1.5px solid " + (contact.profile_type === val ? "var(--accent)" : "var(--border)"),
                      background: contact.profile_type === val ? "var(--accent-soft)" : "var(--card)",
                      color: contact.profile_type === val ? "var(--accent)" : "var(--sub)",
                    }}>
                    {contact.profile_type === val ? "✓ " : ""}{label}
                  </button>
                ))}
              </div>
            </>)}
            <div style={g2}>
              <div><label style={lbl}>Email</label><input style={fld} type="email" value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))} placeholder="vous@exemple.fr" /></div>
              <div><label style={lbl}>Téléphone *</label><input style={fld} type="tel" value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))} placeholder="06 12 34 56 78" /></div>
            </div>

            {/* Consentement légal */}
            <label style={{ display: "flex", gap: ".6rem", alignItems: "flex-start", cursor: "pointer", fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.55, marginTop: ".4rem" }}>
              <input type="checkbox" checked={cgu} onChange={e => setCgu(e.target.checked)} style={{ marginTop: ".2rem", width: 16, height: 16, accentColor: "var(--accent)", flexShrink: 0 }} />
              <span>
                J'ai lu et j'accepte les{" "}
                <button type="button" onClick={() => setLegal("cgu")} style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "var(--accent)", textDecoration: "underline", cursor: "pointer" }}>Conditions Générales d'Utilisation</button>,{" "}
                les <button type="button" onClick={() => setLegal("cgv")} style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "var(--accent)", textDecoration: "underline", cursor: "pointer" }}>Conditions Générales de Vente</button>{" "}
                et les <button type="button" onClick={() => setLegal("mentions")} style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "var(--accent)", textDecoration: "underline", cursor: "pointer" }}>mentions légales</button> de VehicleIQ,
                et j'accepte que mes informations soient transmises à {proName || "ce professionnel"} pour traiter ma demande (RGPD — droits d'accès et d'effacement : contact@vehicleiq.fr).
              </span>
            </label>

            {error && <div style={{ marginTop: ".8rem", color: "var(--red)", fontSize: ".82rem", fontWeight: 600, background: "rgba(176,90,58,.08)", border: "1px solid rgba(176,90,58,.25)", borderRadius: 8, padding: ".6rem .8rem" }}>⚠ {error}</div>}
            <button className="btn btn-accent" disabled={sending} onClick={submit}
              style={{ width: "100%", marginTop: "1rem", padding: "1rem", fontSize: "1rem", fontWeight: 700, justifyContent: "center", opacity: sending ? .6 : 1, cursor: sending ? "wait" : "pointer", border: "none", borderRadius: 12 }}>
              {sending ? "Envoi en cours…" : "Envoyer ma demande →"}
            </button>
            <div style={{ textAlign: "center", fontSize: ".7rem", color: "var(--muted)", marginTop: ".6rem" }}>
              Réponse rapide · sans engagement de votre part
            </div>
            </>)}
          </div>
        )}

        {!invalid && step === "done" && (
          <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 16, padding: "2.6rem 1.6rem", textAlign: "center", boxShadow: "0 4px 20px rgba(0,0,0,.05)" }}>
            <div style={{ width: 72, height: 72, borderRadius: "50%", background: "rgba(46,111,92,.12)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem", fontSize: "2.2rem" }}>✅</div>
            <div style={{ fontWeight: 800, fontSize: "1.3rem", color: "var(--text)", marginBottom: ".6rem", fontFamily: "var(--font-head)" }}>Merci ! Votre demande est bien envoyée.</div>
            <div style={{ color: "var(--sub)", fontSize: ".92rem", lineHeight: 1.6, maxWidth: 420, margin: "0 auto" }}>
              <strong style={{ color: "var(--text)" }}>{proName || "Votre professionnel"}</strong> a bien reçu votre demande et reviendra vers vous rapidement avec {type === "estimation" ? "l'estimation de votre véhicule" : type === "achat" ? "des propositions de véhicules" : "l'estimation de votre carte grise et malus"}.
            </div>
            <div style={{ marginTop: "1.4rem", padding: ".8rem 1rem", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, fontSize: ".78rem", color: "var(--muted)", display: "inline-block" }}>
              💡 Pensez à garder votre téléphone à portée — le professionnel peut vous rappeler.
            </div>
            <div style={{ marginTop: "1rem", fontSize: ".8rem", color: "var(--sub)" }}>À très bientôt !</div>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "1.6rem", fontSize: ".7rem", color: "var(--muted)" }}>
          vehicleiq.fr — M.K.L Ingénierie et Conseil · SIRET 904 954 542 00026
        </div>
      </div>
      {legal && <LegalModal type={legal} onClose={() => setLegal(null)} />}
    </div>
  );
}

function LegalModal({ type, onClose }) {
  const isMentions = type === "mentions";
  const isCgv = type === "cgv";
  const title = isMentions ? "Mentions légales"
              : isCgv ? "Conditions Générales de Vente"
              : "CGU & Politique de confidentialité";
  return (
    <div className="overlay">
      <div className="panel panel-wide">
        <div className="panel-header">
          <div>
            <div className="panel-title">{title}</div>
            <div className="panel-sub">VehicleIQ — vehicleiq.fr</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          <div className="legal-content">
            {isCgv ? (
              <>
                <h2>Conditions Générales de Vente</h2>
                <p style={{ fontSize: ".85rem", color: "var(--sub)", fontStyle: "italic" }}>
                  Applicables à compter du {new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} — Version 1.0
                </p>
                <button className="btn btn-accent btn-sm" onClick={downloadCGV} style={{ marginBottom: "1rem" }}>
                  ⬇ Télécharger les Conditions Générales d'Utilisation et de Vente (PDF)
                </button>

                <h2>Préambule</h2>
                <p>Les présentes Conditions Générales de Vente (« CGV ») régissent l'ensemble des relations contractuelles entre <strong>M.K.L Ingénierie et Conseil</strong>, exploitant la plateforme VehicleIQ accessible à l'adresse vehicleiq.fr (ci-après « VehicleIQ ») et toute personne physique ou morale, professionnelle ou particulière, souscrivant aux services proposés (ci-après le « Client »).</p>
                <p>Toute commande ou souscription implique l'acceptation pleine et entière des présentes CGV. Elles prévalent sur tout autre document du Client (conditions générales d'achat notamment), sauf accord écrit préalable de M.K.L Ingénierie et Conseil.</p>

                <h2>1. Identification du vendeur</h2>
                <p>
                  <strong>Dénomination :</strong> M.K.L Ingénierie et Conseil<br/>
                  <strong>Nom commercial :</strong> VehicleIQ<br/>
                  <strong>Forme juridique :</strong> Auto-entreprise (micro-entrepreneur)<br/>
                  <strong>SIRET :</strong> 904 954 542 00026<br/>
                  <strong>Siège social :</strong> 2 Avenue de la République, 02100 Saint-Quentin, France<br/>
                  <strong>Représentant légal :</strong> Lorentz Mboumba Bong<br/>
                  <strong>Contact :</strong> <a href="mailto:contact@vehicleiq.fr">contact@vehicleiq.fr</a><br/>
                  <strong>Régime fiscal :</strong> Franchise en base de TVA — art. 293 B du Code Général des Impôts. TVA non applicable.
                </p>

                <h2>2. Services proposés</h2>
                <p>VehicleIQ propose les services suivants :</p>
                <ul>
                  <li><strong>Contrôle plaque / VIN</strong> : décodage gratuit et illimité d'un numéro d'immatriculation ou VIN avec récupération des données techniques du véhicule.</li>
                  <li><strong>Cote VO (vendre)</strong> : analyse algorithmique du prix marché d'un véhicule d'occasion à partir des caractéristiques saisies. Estimation indicative calibrée sur le marché français, non contractuelle.</li>
                  <li><strong>Recommandations d'achat (acheter)</strong> : suggestions personnalisées de véhicules basées sur le profil et le budget du Client, croisées avec les annonces actives du marché.</li>
                  <li><strong>Rapports PDF</strong> : export téléchargeable de chaque cote (3 pages).</li>
                  <li><strong>Forfaits abonnement</strong> : Pro Solo (399 €/mois) et Pro Multi-Agent (799 €/mois pour 3 agents).</li>
                </ul>
                <p>Les services sont fournis « en l'état » et constituent une <strong>aide à la décision</strong>. Ils ne se substituent en aucun cas à un contrôle technique officiel, une expertise automobile agréée, un acte de cession notarié, ou tout autre document officiel.</p>

                <h2>3. Tarifs</h2>
                <p>Les prix sont indiqués en euros, hors taxes (HT). Conformément à l'article 293 B du CGI, M.K.L Ingénierie et Conseil bénéficie de la franchise en base de TVA : <strong>aucune TVA n'est applicable</strong>.</p>
                <p><strong>Tarification à l'usage (pay-as-you-go) :</strong></p>
                <ul>
                  <li>Tarif unique : <strong>4,99 €</strong> par dossier, quel que soit le volume.</li>
                  <li>5 cotes offertes et déjà actives à la création du compte, sans activation nécessaire. Ce quota gratuit est attribué une fois pour toutes (il n'est pas renouvelé mensuellement).</li>
                  <li>Le contrôle plaque / VIN reste gratuit et illimité.</li>
                </ul>
                <p><strong>Forfaits mensuels (récurrents) :</strong></p>
                <ul>
                  <li><strong>Illimité</strong> : 399 € HT/mois — cotes illimitées, résiliable à tout moment.</li>
                  <li><strong>Pro Multi-Agent</strong> : 799 € HT/mois — 3 agents inclus, 10 cotes simultanées, 12 recommandations par requête, SLA 99,5 %, support prioritaire 24h.</li>
                </ul>
                <p>VehicleIQ se réserve le droit de modifier ses tarifs à tout moment. Les nouveaux tarifs ne s'appliquent qu'aux commandes postérieures à leur publication.</p>

                <h2>4. Modalités de paiement</h2>
                <p>Les paiements sont effectués <strong>comptant à la commande</strong> par carte bancaire via Stripe (paiement sécurisé conforme PCI-DSS). Aucune donnée bancaire n'est stockée par M.K.L Ingénierie et Conseil.</p>
                <p>Pour les forfaits mensuels, le premier prélèvement est effectué immédiatement à la souscription, puis automatiquement chaque mois à la date anniversaire de la souscription, sur le moyen de paiement enregistré, jusqu'à résiliation par le Client.</p>
                <p><strong>Aucun escompte</strong> pour paiement anticipé.</p>
                <p>En cas de retard ou de défaut de paiement, conformément à l'article L441-10 du Code de commerce :</p>
                <ul>
                  <li>Pénalités de retard au taux d'intérêt légal en vigueur, majoré de 10 points (10 % minimum).</li>
                  <li>Indemnité forfaitaire de <strong>40 € HT</strong> par facture pour frais de recouvrement.</li>
                  <li>Suspension immédiate du service jusqu'à régularisation.</li>
                </ul>

                <h2>5. Facturation</h2>
                <p>Une facture est émise pour chaque paiement et mise à disposition du Client dans son espace personnel (rubrique « Facturation »), téléchargeable au format PDF. Les factures portent les mentions obligatoires prévues par les articles L441-9 et suivants du Code de commerce.</p>

                <h2>6. Droit de rétractation</h2>
                <p><strong>Important :</strong> les services VehicleIQ étant des prestations numériques exécutées immédiatement après le paiement et avec l'accord exprès du Client, ils relèvent de l'<strong>article L221-28, 13°</strong> du Code de la consommation. Le droit de rétractation prévu à l'article L221-18 <strong>ne s'applique pas</strong>.</p>
                <p>En souscrivant un service, le Client reconnaît avoir expressément demandé l'exécution immédiate de la prestation et renonce à son droit de rétractation.</p>

                <h2>7. Résiliation d'un abonnement</h2>
                <p>Les forfaits mensuels (Pro Solo, Pro Multi-Agent) sont sans engagement de durée. Le Client peut résilier à tout moment depuis son espace personnel (rubrique « Paiements & Abonnements »). La résiliation prend effet à la fin de la période en cours déjà payée ; aucun remboursement prorata n'est effectué.</p>

                <h2>8. Disponibilité du service</h2>
                <p>VehicleIQ s'efforce d'assurer la disponibilité du service 24h/24, 7j/7, sans toutefois pouvoir garantir une disponibilité ininterrompue. Des opérations de maintenance peuvent occasionner des interruptions, notifiées dans la mesure du possible.</p>
                <p>Pour les souscripteurs au forfait Pro Multi-Agent, un <strong>SLA de 99,5 %</strong> mensuel est appliqué. En cas de non-respect avéré sur un mois civil, un crédit proportionnel est appliqué sur la facture suivante, sur demande écrite du Client.</p>

                <h2>9. Limitation de responsabilité</h2>
                <p>M.K.L Ingénierie et Conseil ne saurait être tenu responsable :</p>
                <ul>
                  <li>De l'inexactitude des données fournies par les bases tierces (sources d'annonces, données SIV, etc.) ;</li>
                  <li>Des décisions d'achat ou de vente prises par le Client sur la base des cotes fournies — les rapports VehicleIQ sont une aide à la décision et n'engagent en aucun cas la responsabilité de M.K.L Ingénierie et Conseil ;</li>
                  <li>De toute perte indirecte (manque à gagner, perte de marge, perte de clientèle) ;</li>
                  <li>Des indisponibilités liées à des cas de force majeure (panne réseau Internet du Client, intervention de tiers, etc.).</li>
                </ul>
                <p>En tout état de cause, la responsabilité de M.K.L Ingénierie et Conseil est limitée au montant des sommes effectivement versées par le Client sur les <strong>12 derniers mois</strong>.</p>

                <h2>10. Propriété intellectuelle</h2>
                <p>L'ensemble des éléments de la plateforme VehicleIQ (interface, algorithmes, code source, marque déposée à l'INPI, rapports PDF, charte graphique) est la propriété exclusive de M.K.L Ingénierie et Conseil. Le Client bénéficie d'un droit d'usage personnel et non exclusif des rapports générés, qu'il peut transmettre à ses propres clients. Toute autre exploitation (reproduction, revente du service, ingénierie inverse) est strictement interdite.</p>

                <h2>10 bis. Module de prospection (CRM) — responsabilité du Client</h2>
                <p>Le module de prospection par email et SMS ainsi que l'import de coordonnées professionnelles sont fournis à titre d'outil technique. Le Client demeure seul responsable, en qualité de responsable de traitement au sens du RGPD, du respect de l'<strong>article L.34-5 du Code des postes et des communications électroniques</strong> et de la réglementation applicable à la prospection commerciale (contenu des campagnes, origine licite des coordonnées importées, traitement des oppositions reçues). M.K.L Ingénierie et Conseil agit en qualité de sous-traitant pour la fourniture de l'infrastructure d'envoi, conformément à l'article 28 du RGPD, et se réserve le droit de suspendre l'accès au module en cas d'usage manifestement abusif ou non conforme.</p>

                <h2>11. Données personnelles — RGPD</h2>
                <p>Les données personnelles collectées sont traitées conformément à la Politique de confidentialité accessible depuis le site. Le Client dispose d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition en écrivant à <a href="mailto:contact@vehicleiq.fr">contact@vehicleiq.fr</a>.</p>

                <h2>12. Réclamations</h2>
                <p>Toute réclamation doit être formulée par écrit à <a href="mailto:contact@vehicleiq.fr">contact@vehicleiq.fr</a> dans un délai de <strong>30 jours</strong> suivant la facturation. Passé ce délai, la prestation est réputée acceptée.</p>

                <h2>13. Médiation</h2>
                <p>Conformément aux articles L611-1 et suivants du Code de la consommation, en cas de litige n'ayant pu être résolu amiablement, le Client consommateur peut recourir gratuitement au médiateur de la consommation. Le médiateur compétent est <strong>Medicys</strong> — <a href="https://www.medicys.fr" target="_blank" rel="noreferrer">medicys.fr</a> — 73 boulevard de Clichy, 75009 Paris.</p>

                <h2>14. Droit applicable et juridiction</h2>
                <p>Les présentes CGV sont soumises au <strong>droit français</strong>. En cas de litige et après échec d'une résolution amiable, les tribunaux français seront seuls compétents. Pour les litiges avec un Client professionnel, attribution exclusive de juridiction au <strong>Tribunal de Commerce du ressort du siège social de M.K.L Ingénierie et Conseil</strong> (Saint-Quentin), même en cas de pluralité de défendeurs ou d'appel en garantie.</p>

                <h2>15. Modification des CGV</h2>
                <p>M.K.L Ingénierie et Conseil se réserve le droit de modifier les présentes CGV à tout moment. Les CGV applicables sont celles en vigueur au moment de la commande. Les Clients abonnés sont informés des modifications substantielles avec un préavis raisonnable.</p>

                <h2>16. Contact</h2>
                <p>
                  M.K.L Ingénierie et Conseil — VehicleIQ<br/>
                  2 Avenue de la République, 02100 Saint-Quentin, France<br/>
                  <a href="mailto:contact@vehicleiq.fr">contact@vehicleiq.fr</a><br/>
                  <em>Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}</em>
                </p>
              </>
            ) : isMentions ? (
              <>
                <h2>Mentions légales</h2>
                <p>Conformément aux dispositions de la loi n°2004-575 du 21 juin 2004 pour la confiance en l'économie numérique (LCEN), les présentes mentions légales sont portées à la connaissance des utilisateurs du site vehicleiq.fr.</p>

                <h2>Éditeur du site</h2>
                <p>
                  <strong>Dénomination sociale :</strong> M.K.L Ingénierie et Conseil<br/>
                  <strong>Nom commercial :</strong> VehicleIQ<br/>
                  <strong>Forme juridique :</strong> Auto-entreprise (micro-entrepreneur)<br/>
                  <strong>Siège social :</strong> 2 Avenue de la République, 02100 Saint-Quentin, France<br/>
                  <strong>SIRET :</strong> 904 954 542 00026<br/>
                  <strong>Représentant légal :</strong> Lorentz Mboumba Bong<br/>
                  <strong>Email :</strong> <a href="mailto:contact@vehicleiq.fr">contact@vehicleiq.fr</a><br/>
                  <strong>Site web :</strong> <a href="https://vehicleiq.fr" target="_blank" rel="noreferrer">vehicleiq.fr</a>
                </p>

                <h2>Hébergement</h2>
                <p>
                  <strong>Hébergeur frontend :</strong> Infomaniak Network SA — Rue Eugène-Marziano 25, 1227 Genève, Suisse — <a href="https://www.infomaniak.com" target="_blank" rel="noreferrer">infomaniak.com</a><br/>
                  <strong>Hébergeur backend / base de données :</strong> Railway Infrastructure (via Hetzner) — Datacenter Amsterdam, Pays-Bas (Union Européenne) — <a href="https://railway.app" target="_blank" rel="noreferrer">railway.app</a><br/>
                  Les serveurs backend et la base de données PostgreSQL sont hébergés au sein de l'Union Européenne, conformément au RGPD.
                </p>

                <h2>Propriété intellectuelle</h2>
                <p>L'ensemble du contenu du site vehicleiq.fr (textes, interface, algorithmes, code source, marque VEHICLEIQ déposée à l'INPI) est la propriété exclusive de M.K.L Ingénierie et Conseil. Toute reproduction, représentation, modification ou exploitation, même partielle, est strictement interdite sans autorisation écrite préalable, sous peine de poursuites judiciaires sur le fondement des articles L.335-2 et suivants du Code de la Propriété Intellectuelle.</p>

                <h2>Services tiers intégrés</h2>
                <ul>
                  <li><strong>API Plaque immatriculation UAB</strong> (Lituanie, UE) : données historiques de véhicules — usage B2B exclusif conformément aux CGU API Plaque immatriculation — <a href="https://www.apiplaqueimmatriculation.com/fr/terms" target="_blank" rel="noreferrer">apiplaqueimmatriculation.com/terms</a></li>
                  <li><strong>Stripe Technology Europe Ltd</strong> (Dublin, Irlande, UE) : traitement sécurisé des paiements — certifié PCI-DSS niveau 1 — <a href="https://stripe.com/fr/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a></li>
                  <li><strong>Google Firebase (Google Ireland Ltd)</strong> (Dublin, Irlande, UE) : authentification utilisateurs via OAuth 2.0 — <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">policies.google.com/privacy</a></li>
                  <li><strong>Ministère de l'Intérieur français — HistoVec</strong> : données d'immatriculation officielles via API gouvernementale — données.gouv.fr</li>
                  <li><strong>API Entreprises (DINUM)</strong> : vérification des numéros SIRET — <a href="https://api.gouv.fr" target="_blank" rel="noreferrer">api.gouv.fr</a></li>
                </ul>

                <h2>Cadre légal applicable à la consultation des données de véhicules</h2>
                <p>La consultation des données relatives à l'historique des véhicules est encadrée par :</p>
                <ul>
                  <li>La <strong>loi n°78-17 du 6 janvier 1978</strong> relative à l'informatique, aux fichiers et aux libertés (loi Informatique et Libertés), modifiée</li>
                  <li>Le <strong>Règlement (UE) 2016/679</strong> du Parlement européen et du Conseil du 27 avril 2016 (RGPD)</li>
                  <li>L'<strong>arrêté du 9 février 2009</strong> relatif aux modalités d'immatriculation des véhicules (SIV)</li>
                  <li>Le <strong>décret n°2009-727 du 19 juin 2009</strong> relatif au système d'immatriculation des véhicules</li>
                  <li>La <strong>délibération CNIL n°2019-137</strong> portant sur la communication des données du SIV aux prestataires agréés</li>
                  <li>Les <strong>conditions générales d'utilisation de API Plaque immatriculation UAB</strong>, qui réservent l'accès API à un usage strictement B2B — les rapports générés via l'API API Plaque immatriculation ne peuvent être transmis qu'à des clients professionnels (entreprises identifiées par leur SIRET sur la plateforme VehicleIQ)</li>
                </ul>
                <p>Les données de véhicules affichées dans les rapports VehicleIQ sont fournies à titre informatif. Elles ne constituent pas un document officiel et ne sauraient engager la responsabilité de M.K.L Ingénierie et Conseil en cas d'inexactitude.</p>

                <h2>Limitation de responsabilité</h2>
                <p>M.K.L Ingénierie et Conseil ne saurait être tenu responsable des dommages directs ou indirects résultant de l'utilisation des informations fournies par la plateforme VehicleIQ, notamment en cas d'inexactitude des données issues de bases tierces (API Plaque immatriculation, HistoVec). Les rapports sont produits à titre d'aide à la décision et ne se substituent pas à un contrôle technique officiel ni à une expertise professionnelle.</p>

                <h2>Médiation et litiges</h2>
                <p>En cas de litige non résolu à l'amiable, l'utilisateur peut recourir à la médiation de la consommation. Les présentes mentions légales sont soumises au droit français. Le tribunal compétent est celui du ressort du siège social de M.K.L Ingénierie et Conseil (Tribunal judiciaire de Saint-Quentin / Laon).</p>
              </>
            ) : (
              <>
                <h2>Conditions Générales d'Utilisation (CGU)</h2>
                <p>Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») régissent l'accès et l'utilisation de la plateforme VehicleIQ, éditée par M.K.L Ingénierie et Conseil (SIRET 904 954 542 00026), dont le siège social est situé 2 Avenue de la République, 02100 Saint-Quentin. En accédant à vehicleiq.fr, l'utilisateur accepte sans réserve les présentes CGU.</p>

                <h2>1. Objet et description du service</h2>
                <p>VehicleIQ est une plateforme SaaS d'intelligence automobile organisée autour du <strong>Dossier VIQ</strong> : pour chaque véhicule traité, un dossier unique centralise automatiquement contrôle d'historique, import HistoVec et CarVertical, photos, analyse par intelligence artificielle, cotation, annonce et suivi de vente. VehicleIQ propose notamment :</p>
                <ul>
                  <li><strong>Contrôle véhicule</strong> : consultation de l'historique d'un véhicule via son numéro VIN ou sa plaque d'immatriculation, alimentée par les données API Plaque immatriculation et HistoVec</li>
                  <li><strong>Aide à l'achat</strong> : recommandations personnalisées par algorithme d'intelligence artificielle</li>
                  <li><strong>Optimisation de vente</strong> : cote de valeur et génération d'annonce, organisées au sein du Dossier VIQ du véhicule concerné</li>
                  <li><strong>Prospection client (CRM)</strong> : mise en relation par email et SMS avec un répertoire de professionnels ou avec les leads reçus via formulaire, ainsi qu'import de coordonnées propres à l'utilisateur professionnel — voir l'article dédié ci-dessous</li>
                </ul>
                <p>La plateforme est actuellement en version actuelle. Les données affichées sont partiellement issues de jeux de données illustratifs dans l'attente de l'activation complète des intégrations API.</p>

                <h2>2. Accès au service et modèle freemium</h2>
                <p>La consultation des données HistoVec est gratuite et illimitée. L'accès aux fonctionnalités premium (score de fiabilité, rapport complet, résultats IA personnalisés, fiches de vente) est conditionné à un paiement via Stripe. L'accès aux rapports API Plaque immatriculation est réservé aux utilisateurs professionnels (B2B) ayant fourni un numéro SIRET valide, conformément aux conditions d'utilisation de API Plaque immatriculation UAB.</p>

                <h2>3. Obligations de l'utilisateur</h2>
                <p>L'utilisateur s'engage à :</p>
                <ul>
                  <li>Ne pas utiliser la plateforme à des fins illicites ou contraires aux présentes CGU</li>
                  <li>Ne pas tenter de contourner les mesures de sécurité ou d'accès</li>
                  <li>Fournir des informations exactes lors de l'inscription et du renseignement de son profil (notamment le numéro SIRET pour les professionnels)</li>
                  <li>Ne pas revendre ou redistribuer les rapports générés à des tiers sans autorisation</li>
                  <li>Respecter les droits de propriété intellectuelle de M.K.L Ingénierie et Conseil et de ses partenaires (API Plaque immatriculation, HistoVec, Stripe)</li>
                </ul>

                <h2>4. Conditions spécifiques à l'utilisation des données API Plaque immatriculation</h2>
                <p>Conformément aux <strong>Terms of Service de API Plaque immatriculation UAB</strong> (disponibles sur <a href="https://www.apiplaqueimmatriculation.com/fr/terms" target="_blank" rel="noreferrer">apiplaqueimmatriculation.com/terms</a>), l'accès aux rapports d'historique véhicule via l'API API Plaque immatriculation est soumis aux conditions suivantes :</p>
                <ul>
                  <li>Les rapports API Plaque immatriculation ne peuvent être distribués qu'à des <strong>clients professionnels (B2B)</strong> — toute distribution à des consommateurs particuliers (B2C) est interdite sans accord spécifique avec API Plaque immatriculation</li>
                  <li>Les données contenues dans les rapports sont la propriété de API Plaque immatriculation UAB et de ses fournisseurs de données</li>
                  <li>L'utilisateur professionnel s'engage à ne pas extraire, copier ou redistribuer massivement les données des rapports</li>
                  <li>VehicleIQ agit en qualité de revendeur agréé des données API Plaque immatriculation dans le cadre de son accord commercial B2B</li>
                  <li>En cas d'inexactitude dans un rapport API Plaque immatriculation, le recours doit être exercé directement auprès de API Plaque immatriculation UAB, VehicleIQ n'étant que l'intermédiaire technique</li>
                </ul>

                <h2>5. Prospection commerciale par email et SMS (module CRM)</h2>
                <p>VehicleIQ met à disposition des utilisateurs professionnels un outil de prospection permettant l'envoi d'emails et de SMS à des fins commerciales, à destination d'un répertoire de professionnels de l'automobile ou des leads reçus via le formulaire client de l'utilisateur. Cette activité de prospection directe par voie électronique est encadrée par l'<strong>article L.34-5 du Code des postes et des communications électroniques (CPCE)</strong> et par le <strong>Règlement (UE) 2016/679 (RGPD)</strong>.</p>
                <ul>
                  <li>L'utilisateur professionnel est seul <strong>responsable du traitement</strong> au sens du RGPD pour les campagnes qu'il initie : le choix des destinataires, le contenu des messages et le respect des règles applicables à la prospection B2B lui incombent. VehicleIQ agit en tant que <strong>sous-traitant</strong> au sens de l'article 28 du RGPD pour la fourniture de l'infrastructure technique d'envoi.</li>
                  <li>Chaque email de prospection comporte l'identité de l'expéditeur et un mécanisme de désinscription simple et gratuit (réponse « STOP »), conformément à l'article L.34-5 du CPCE.</li>
                  <li>L'utilisateur s'engage à ne pas utiliser le module de prospection à l'égard de personnes ayant exprimé leur opposition, et à respecter les principes de minimisation et de finalité du RGPD.</li>
                  <li>Des limites quotidiennes d'envoi (campagnes, emails, SMS) sont appliquées par VehicleIQ afin de prévenir tout usage abusif ou assimilable à du spam.</li>
                  <li>Le canal SMS, lorsqu'il est activé, est soumis aux mêmes règles ; l'envoi peut être opéré via un prestataire tiers d'envoi en masse, auquel cas ce dernier agit également en qualité de sous-traitant pour le compte de l'utilisateur.</li>
                </ul>

                <h2>6. Import de coordonnées professionnelles</h2>
                <p>L'utilisateur peut importer ses propres listes de contacts professionnels (fichier CSV/XLSX) ou en saisir manuellement dans le module CRM. L'utilisateur garantit disposer d'une base légale et d'un droit d'usage sur les coordonnées ainsi importées, conformément à la <strong>loi n°78-17 du 6 janvier 1978</strong> modifiée et au RGPD. VehicleIQ n'exerce aucun contrôle a priori sur l'origine de ces données et agit, pour leur traitement, en qualité de sous-traitant au sens de l'<strong>article 28 du RGPD</strong> ; l'utilisateur demeure responsable du traitement pour les données qu'il importe. Ces contacts restent strictement privés à l'utilisateur qui les a importés et ne sont jamais partagés avec d'autres utilisateurs de la plateforme.</p>

                <h2>7. Paiements et remboursements</h2>
                <p>Les paiements sont traités par <strong>Stripe Technology Europe Ltd</strong>, certifié PCI-DSS. Les prix sont indiqués en euros TTC. En tant qu'auto-entrepreneur, M.K.L Ingénierie et Conseil n'est pas assujetti à la TVA (article 293B du CGI). Tout paiement est définitif, sauf demande de remboursement formulée dans les 7 jours suivant l'achat auprès de contact@vehicleiq.fr, si le service n'a pas pu être rendu.</p>

                <h2>8. Données personnelles et RGPD</h2>
                <p>Le traitement des données personnelles est régi par notre Politique de confidentialité accessible depuis cette même page. Conformément au <strong>Règlement (UE) 2016/679 (RGPD)</strong> et à la <strong>loi Informatique et Libertés n°78-17</strong>, l'utilisateur dispose d'un droit d'accès, de rectification, d'effacement et de portabilité de ses données. Pour exercer ces droits : <a href="mailto:contact@vehicleiq.fr">contact@vehicleiq.fr</a>. Autorité de contrôle : <a href="https://www.cnil.fr" target="_blank" rel="noreferrer">CNIL</a>.</p>

                <h2>9. Propriété intellectuelle</h2>
                <p>La marque VEHICLEIQ, l'interface, les algorithmes et le code source sont la propriété exclusive de M.K.L Ingénierie et Conseil, protégés par le <strong>Code de la Propriété Intellectuelle</strong> (articles L.111-1 et suivants pour le droit d'auteur, articles L.711-1 et suivants pour les marques). Toute reproduction non autorisée est passible de poursuites.</p>

                <h2>10. Limitation de responsabilité</h2>
                <p>M.K.L Ingénierie et Conseil décline toute responsabilité quant à l'exactitude des données issues de bases tierces. Les rapports VehicleIQ constituent une aide à la décision et ne se substituent pas à un contrôle technique officiel (CT), une expertise automobile agréée, ou tout autre document officiel. L'utilisateur reste seul responsable des décisions d'achat ou de vente prises sur la base des informations fournies.</p>

                <h2>11. Disponibilité du service</h2>
                <p>M.K.L Ingénierie et Conseil s'efforce d'assurer la disponibilité du service 24h/24, 7j/7, mais ne peut garantir une disponibilité sans interruption. Des maintenances peuvent être effectuées avec notification préalable.</p>

                <h2>12. Modification des CGU</h2>
                <p>M.K.L Ingénierie et Conseil se réserve le droit de modifier les présentes CGU à tout moment. Les modifications prennent effet dès leur publication sur le site. L'utilisation continue du service après modification vaut acceptation des nouvelles CGU.</p>

                <h2>13. Droit applicable et juridiction</h2>
                <p>Les présentes CGU sont soumises au <strong>droit français</strong>. En cas de litige, les parties s'engagent à rechercher une solution amiable avant tout recours judiciaire. À défaut, le tribunal compétent sera celui du ressort du siège social de M.K.L Ingénierie et Conseil.</p>

                <h2>14. Contact</h2>
                <p>M.K.L Ingénierie et Conseil — VehicleIQ<br/>
                2 Avenue de la République, 02100 Saint-Quentin<br/>
                <a href="mailto:contact@vehicleiq.fr">contact@vehicleiq.fr</a><br/>
                Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}</p>

                <h2>Politique de confidentialité</h2>
                <p>M.K.L Ingénierie et Conseil accorde une importance capitale à la protection de vos données personnelles, conformément au <strong>RGPD (Règlement UE 2016/679)</strong> et à la <strong>loi Informatique et Libertés n°78-17</strong>.</p>

                <h2>Responsable du traitement</h2>
                <p>M.K.L Ingénierie et Conseil — Lorentz Mboumba Bong — contact@vehicleiq.fr — 2 Avenue de la République, 02100 Saint-Quentin</p>

                <h2>Données collectées</h2>
                <ul>
                  <li><strong>Lors de la connexion :</strong> adresse email et nom (Google OAuth 2.0)</li>
                  <li><strong>Lors de l'utilisation :</strong> numéros VIN/plaques recherchés, préférences d'achat/vente, historique des rapports, données de profil (téléphone, adresse, SIRET si pro)</li>
                  <li><strong>Dossier VIQ :</strong> pour chaque véhicule traité par un utilisateur professionnel — historique du véhicule, photos, analyses IA, cotations, annonces et informations de vente, ainsi que les coordonnées du client final lorsqu'elles sont renseignées (nom, email, téléphone) via le formulaire de demande</li>
                  <li><strong>Prospection (CRM) :</strong> coordonnées professionnelles importées ou saisies par l'utilisateur (répertoire privé), historique des campagnes email/SMS envoyées, statut de suivi commercial (contacté, relancé, etc.)</li>
                  <li><strong>Paiements :</strong> traités exclusivement par Stripe — aucune donnée bancaire n'est stockée par VehicleIQ</li>
                  <li><strong>Données techniques :</strong> adresse IP hashée (anonymisée), type de navigateur</li>
                </ul>

                <h2>Bases légales et durées de conservation</h2>
                <ul>
                  <li>Données de compte : exécution du contrat — durée d'activité + 3 ans</li>
                  <li>Historique des recherches : intérêt légitime — 3 ans</li>
                  <li>Dossier VIQ (dont coordonnées du client final saisies par le professionnel) : exécution du contrat conclu entre le professionnel et son client / intérêt légitime du professionnel à assurer son suivi commercial — durée d'activité du Dossier + 3 ans</li>
                  <li>Coordonnées importées ou utilisées à des fins de prospection (CRM) : intérêt légitime de l'utilisateur professionnel à des fins de prospection B2B (article 6.1.f du RGPD), sous sa propre responsabilité de responsable de traitement — conservées jusqu'à suppression par l'utilisateur ou opposition du destinataire</li>
                  <li>Données de paiement : obligation légale comptable — 10 ans</li>
                  <li>Logs techniques (IP hashée) : sécurité — 12 mois</li>
                </ul>

                <h2>Vos droits</h2>
                <p>Accès, rectification, effacement, portabilité, opposition, limitation : <a href="mailto:contact@vehicleiq.fr">contact@vehicleiq.fr</a>. Réclamation auprès de la <a href="https://www.cnil.fr" target="_blank" rel="noreferrer">CNIL</a>.</p>
                <p>Si vous recevez un email ou SMS de prospection via VehicleIQ, vous pouvez vous y opposer à tout moment et sans frais en répondant « STOP » au message reçu, conformément à l'article L.34-5 du Code des postes et des communications électroniques.</p>

                <h2>Cookies</h2>
                <p>Cookies strictement nécessaires uniquement (session, préférences). Aucun cookie publicitaire ou de tracking tiers.</p>

                <h2>Hébergement des données</h2>
                <p>Backend et base de données hébergés aux Pays-Bas (Amsterdam, UE) via Railway/Hetzner. Frontend hébergé en Suisse via Infomaniak. Authentification via Google Firebase Ireland (UE). Tous les transferts sont encadrés par des clauses contractuelles types conformes au RGPD.</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}




// ─── Pro Multi-Agent — Detail Modal ───────────────────────────────────
function B2BModal({ onClose, user }) {
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 520 }}>
        <div className="panel-header">
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: ".65rem", color: "var(--accent2)", letterSpacing: "2px", textTransform: "uppercase", marginBottom: ".3rem" }}>Devis personnalisé · mensualité sans engagement</div>
            <div className="panel-title">Offre Pro Multi-Agent VehicleIQ</div>
            <div className="panel-sub">Une équipe, un compte, une facturation</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          <div style={{ background: "linear-gradient(135deg, rgba(168,128,58,.06), rgba(168,128,58,.02))", border: "1px solid rgba(168,128,58,.2)", borderRadius: 10, padding: "1.2rem 1.4rem", marginBottom: "1.4rem" }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.15rem", marginBottom: ".8rem", color: "var(--accent2)", letterSpacing: "-.01em" }}>Ce que l'offre Pro Multi-Agent inclut :</div>
            {[
              { icon: "✦", text: "Tout l'écosystème Pro (Tableau de bord, Comparatif plateformes, Suivi de vente, Gains VIQ.AI® mesurés)" },
              { icon: "👥", text: "Multi-utilisateurs avec rôles — propriétaire et agents sous un même SIRET, facturation consolidée" },
              { icon: "🛡️", text: "SLA garanti 99,5 % de disponibilité, support prioritaire en cas d'incident" },
              { icon: "🧾", text: "Facturation mensualisée par virement à 30 jours (alternative à Stripe CB)" },
              { icon: "💎", text: "Tarif sur devis selon volume d'appels (vs 4,99 € en pay-as-you-go classique)" },
              { icon: "📞", text: "Compte dédié avec interlocuteur direct + réponse à votre demande sous 48h ouvrées" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: ".7rem", marginBottom: ".55rem" }}>
                <span style={{ fontSize: ".95rem", color: "var(--accent)", flexShrink: 0, paddingTop: ".05rem" }}>{item.icon}</span>
                <span style={{ fontSize: ".83rem", color: "var(--sub)", lineHeight: 1.55 }}>{item.text}</span>
              </div>
            ))}
          </div>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", marginBottom: ".6rem", letterSpacing: "-.01em" }}>Vous êtes intéressé ?</div>
          <p style={{ fontSize: ".82rem", color: "var(--sub)", marginBottom: "1.2rem", lineHeight: 1.6 }}>
            L'offre Pro Multi-Agent s'adresse aux garages, marchands, revendeurs et mandataires VO indépendants qui souhaitent partager un même compte entre plusieurs collaborateurs sous le même SIRET (799 € HT/mois, 3 agents inclus). Laissez-nous vos coordonnées pour en savoir plus ou recevoir un devis adapté sous 48h.
          </p>
          <button className="btn btn-accent" style={{ width: "100%", marginBottom: ".8rem" }}
            onClick={() => {
              const subject = "Demande de devis — Pro Multi-Agent VehicleIQ";
              const lines = [];
              lines.push("Bonjour,");
              lines.push("");
              lines.push("Je suis intéressé(e) par l'offre Pro Multi-Agent VehicleIQ.");
              lines.push("");
              lines.push("──── Mes coordonnées ────");
              if (user) {
                if (user.name)      lines.push("Nom : " + user.name);
                if (user.email)     lines.push("Email : " + user.email);
                if (user.siretName) lines.push("Société : " + user.siretName);
                if (user.siret)     lines.push("SIRET : " + user.siret);
              } else {
                lines.push("Nom : ");
                lines.push("Société : ");
                lines.push("SIRET : ");
                lines.push("Email : ");
                lines.push("Téléphone : ");
              }
              lines.push("");
              lines.push("──── Mes besoins ────");
              lines.push("Volume estimé : ____ estimations / mois");
              lines.push("Nombre d'agents : ____");
              lines.push("Activité : (garage VO / marchand VO / revendeur VO / mandataire VO / autre)");
              lines.push("");
              lines.push("──── Offre demandée ────");
              lines.push("Pro Multi-Agent — 799 € HT / mois, 3 agents inclus");
              lines.push("Agents : illimités sous le même SIRET");
              lines.push("Facturation : mensualisée par virement à 30 jours");
              lines.push("");
              lines.push("Merci de me transmettre un devis adapté.");
              lines.push("");
              lines.push("Cordialement");
              const body = encodeURIComponent(lines.join("\n"));
              window.dispatchEvent(new CustomEvent("viq-open-contact", { detail: { mailto: "mailto:contact@vehicleiq.fr?subject=" + encodeURIComponent(subject) + "&body=" + body } }));
            }}>
            ✉ Nous contacter : contact@vehicleiq.fr
          </button>
          <div style={{ textAlign: "center", fontFamily: "var(--font-mono)", fontSize: ".68rem", color: "var(--muted)" }}>
            Réponse sous 48h ouvrées
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Intégration API VIQ.AI® — offre développeurs / éditeurs ───────────────
// Offre distincte du Pro Multi-Agent (abonnement). Vente de volumes d'appels API
// avec personnalisation (marque blanche, nom de domaine). Pas de paiement Stripe
// direct : prise de contact par email (engagement volume, devis).
const API_TIERS = [
  { calls: "1 000 appels API / mois", price: "Sur devis", note: "Idéal intégration ponctuelle / POC", best: false },
  { calls: "2 500 appels API / mois", price: "Sur devis", note: "Volume intermédiaire", best: true },
  { calls: "7 000 appels API / mois", price: "Sur devis", note: "Fort volume", best: false },
];

// Modules de l'Outil de suivi métier (onglets du dashboard pro) proposés comme
// produits/services DISTINCTS dans le devis API. Chaque module correspond à un
// ou plusieurs endpoints, avec une estimation indicative du volume d'appels API
// MENSUEL qu'il génère (à calibrer selon l'activité réelle du client).
const API_MODULES = [
  { icon: "💰", name: "Cote véhicule (estimation)",        endpoint: "/api/estimate", calls: "≈ 1 appel / cote", basis: "1 appel par véhicule estimé" },
  { icon: "📸", name: "Cote par photos (vision IA)",        endpoint: "/api/estimate/from-photos", calls: "≈ 1 appel / analyse", basis: "1 appel par lot de photos analysé" },
  { icon: "🛒", name: "Scan d'opportunité (achat)",         endpoint: "/api/buy", calls: "≈ 1 appel / scan", basis: "1 appel par recherche de véhicule" },
  { icon: "🔍", name: "Décodage plaque / SIV",              endpoint: "/api/check", calls: "≈ 1 appel / plaque", basis: "1 appel par identification (gratuit sur la plateforme)" },
  { icon: "🚗", name: "Carte grise & malus",                endpoint: "/api/cartegrise", calls: "≈ 1 appel / calcul", basis: "1 appel par simulation de coût" },
  { icon: "🌐", name: "Comparatif plateformes",             endpoint: "/api/platforms", calls: "≈ 1 appel / comparaison", basis: "1 appel par véhicule comparé" },
  { icon: "📈", name: "Live Marché (cotations directes)",   endpoint: "/api/live-market", calls: "≈ 30–90 appels / mois", basis: "rafraîchissement périodique (1–3 / jour)" },
  { icon: "📄", name: "Génération de documents (mandat, CERFA cession)", endpoint: "/api/documents", calls: "≈ 1 appel / document", basis: "1 appel par document généré" },
  { icon: "📊", name: "Analyses & tableau de bord",         endpoint: "/api/analytics", calls: "≈ 1 appel / consultation", basis: "agrégation des données à l'ouverture" },
  { icon: "📥", name: "Formulaires clients (leads)",        endpoint: "/api/pro-leads", calls: "≈ 1–2 appels / lead", basis: "réception + analyse photos éventuelle" },
];

function APIIntegrationModal({ onClose, user }) {
  const handleContact = () => {
    const subject = "Demande — Intégration API VIQ.AI®";
    const lines = [];
    lines.push("Bonjour l'équipe VehicleIQ,");
    lines.push("");
    lines.push("Je souhaite intégrer l'API VIQ.AI® et recevoir un devis.");
    lines.push("");
    lines.push("──── Mes coordonnées ────");
    if (user) {
      if (user.name)      lines.push("Nom : " + user.name);
      if (user.email)     lines.push("Email : " + user.email);
      if (user.siretName) lines.push("Société : " + user.siretName);
      if (user.siret)     lines.push("SIRET : " + user.siret);
    } else {
      lines.push("Nom : ");
      lines.push("Société : ");
      lines.push("Email : ");
      lines.push("Téléphone : ");
    }
    lines.push("");
    lines.push("──── Mon projet ────");
    lines.push("Volume d'appels API estimé / mois : ____");
    lines.push("Personnalisation souhaitée : (marque blanche / nom de domaine dédié / aucune)");
    lines.push("Cas d'usage : ____");
    lines.push("");
    lines.push("──── Modules souhaités (cochez) ────");
    lines.push("[ ] Cote véhicule (estimation) — ~1 appel/cote/mois");
    lines.push("[ ] Cote par photos (vision IA) — ~1 appel/analyse");
    lines.push("[ ] Scan d'opportunité (achat) — ~1 appel/scan");
    lines.push("[ ] Décodage plaque / SIV — ~1 appel/plaque");
    lines.push("[ ] Carte grise & malus — ~1 appel/calcul");
    lines.push("[ ] Comparatif plateformes — ~1 appel/comparaison");
    lines.push("[ ] Live Marché — ~30–90 appels/mois");
    lines.push("[ ] Génération de documents (mandat, cession) — ~1 appel/document");
    lines.push("[ ] Analyses & tableau de bord — ~1 appel/consultation");
    lines.push("[ ] Formulaires clients (leads) — ~1–2 appels/lead");
    lines.push("");
    lines.push("──── Offre envisagée ────");
    lines.push("[ ] 1 000 appels API / mois");
    lines.push("[ ] 2 500 appels API / mois");
    lines.push("[ ] 7 000 appels API / mois");
    lines.push("");
    lines.push("Merci de me transmettre un devis adapté.");
    lines.push("");
    lines.push("Cordialement");
    const body = encodeURIComponent(lines.join("\n"));
    window.dispatchEvent(new CustomEvent("viq-open-contact", { detail: { mailto: "mailto:contact@vehicleiq.fr?subject=" + encodeURIComponent(subject) + "&body=" + body } }));
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 560 }}>
        <div className="panel-header">
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: ".65rem", color: "var(--accent2)", letterSpacing: "2px", textTransform: "uppercase", marginBottom: ".3rem" }}>Marque blanche · sur mesure · sans abonnement</div>
            <div className="panel-title">Intégration API VIQ.AI®</div>
            <div className="panel-sub">Intégrez la puissance de Cote VIQ.AI® dans vos propres outils</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          {/* Ce que comprend l'offre */}
          <div style={{ background: "linear-gradient(135deg, rgba(168,128,58,.06), rgba(168,128,58,.02))", border: "1px solid rgba(168,128,58,.2)", borderRadius: 10, padding: "1.2rem 1.4rem", marginBottom: "1.4rem" }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.15rem", marginBottom: ".8rem", color: "var(--accent2)", letterSpacing: "-.01em" }}>Ce que l'offre API inclut :</div>
            {[
              { icon: "🔌", text:"Accès complet à l'API VIQ.AI® : cote, cote, recommandations d'achat, comparatif plateformes" },
              { icon: "🎨", text: "Personnalisation poussée — marque blanche, intégration dans votre interface, rapports à vos couleurs" },
              { icon: "🌐", text: "Nom de domaine dédié possible (api.votre-marque.fr) pour une intégration transparente" },
              { icon: "📊", text: "Mêmes données que la plateforme VehicleIQ, en temps réel" },
              { icon: "🔑", text: "Clés API, documentation technique et environnement de test (sandbox)" },
              { icon: "📞", text: "Accompagnement technique à l'intégration + interlocuteur dédié" },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: ".7rem", marginBottom: ".55rem" }}>
                <span style={{ fontSize: ".95rem", color: "var(--accent)", flexShrink: 0, paddingTop: ".05rem" }}>{item.icon}</span>
                <span style={{ fontSize: ".83rem", color: "var(--sub)", lineHeight: 1.55 }}>{item.text}</span>
              </div>
            ))}
          </div>

          {/* Grille tarifaire API */}
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", marginBottom: ".7rem", letterSpacing: "-.01em" }}>Nos forfaits API</div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".55rem", marginBottom: "1.3rem" }}>
            {API_TIERS.map((t, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: ".8rem", padding: ".8rem 1rem",
                border: "1.5px solid " + (t.best ? "var(--accent)" : "var(--border)"),
                borderRadius: 10,
                background: t.best ? "rgba(168,128,58,.05)" : "var(--white)",
                position: "relative",
              }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: ".92rem", color: "var(--text)" }}>
                    {t.calls}
                    {t.best && <span style={{ marginLeft: ".5rem", fontSize: ".6rem", fontWeight: 700, color: "#fff", background: "var(--accent)", padding: ".1rem .4rem", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".5px", verticalAlign: "middle" }}>Populaire</span>}
                  </div>
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: ".15rem" }}>{t.note}</div>
                </div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: "1rem", fontWeight: 800, color: t.best ? "var(--accent)" : "var(--text)", whiteSpace: "nowrap" }}>
                  {t.price}
                </div>
              </div>
            ))}
          </div>

          {/* Modules activables — produits distincts entrant dans le devis */}
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", marginBottom: ".4rem", letterSpacing: "-.01em" }}>Modules activables (au choix dans le devis)</div>
          <p style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: ".7rem", lineHeight: 1.55 }}>
            Chaque module de l'Outil de suivi métier peut être intégré à votre solution comme un produit distinct. Le volume d'appels API est <strong>mensuel</strong> et estimé selon votre activité : votre devis additionne les modules retenus.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", marginBottom: "1.3rem" }}>
            {API_MODULES.map((m, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".7rem", padding: ".6rem .8rem", border: "1px solid var(--border)", borderRadius: 8, background: "var(--white)" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: ".6rem", minWidth: 0 }}>
                  <span style={{ fontSize: ".95rem", flexShrink: 0 }}>{m.icon}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: ".82rem", fontWeight: 700, color: "var(--text)", lineHeight: 1.3 }}>{m.name}</div>
                    <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".1rem", fontFamily: "var(--font-mono)" }}>{m.endpoint} · {m.basis}</div>
                  </div>
                </div>
                <div style={{ fontSize: ".74rem", fontWeight: 700, color: "var(--accent)", whiteSpace: "nowrap", flexShrink: 0, textAlign: "right" }}>
                  {m.calls}<div style={{ fontSize: ".6rem", color: "var(--muted)", fontWeight: 600 }}>/ mois</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: ".7rem", color: "var(--muted)", marginBottom: "1.3rem", lineHeight: 1.5, fontStyle: "italic" }}>
            Volumes d'appels indicatifs, facturés par mois et à calibrer selon votre activité réelle. Le devis final consolide les modules choisis et le palier de volume mensuel.
          </div>

          <p style={{ fontSize: ".82rem", color: "var(--sub)", marginBottom: "1.2rem", lineHeight: 1.6 }}>
            L'API VIQ.AI® s'adresse aux éditeurs de logiciels, plateformes automobiles et professionnels souhaitant intégrer notre moteur de Cote directement dans leurs propres outils. Personnalisation sur mesure, marque blanche et nom de domaine dédié disponibles. Contactez notre équipe pour un devis adapté à votre volume et à vos besoins d'intégration.
          </p>
          <button className="btn btn-accent" style={{ width: "100%", marginBottom: ".8rem" }} onClick={handleContact}>
            ✉ Contacter l'équipe : contact@vehicleiq.fr
          </button>
          <div style={{ textAlign: "center", fontFamily: "var(--font-mono)", fontSize: ".68rem", color: "var(--muted)" }}>
            Réponse sous 48h ouvrées
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FAQ Modal ────────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  { q: "Comment fonctionne la vérification VIN ?", a: "Vous saisissez le numéro VIN (17 caractères) ou la plaque d'immatriculation de votre véhicule. Vous saisissez la plaque d'immatriculation. Notre système interroge l'API Plaque immatriculation (source : Système d'Immatriculation des Véhicules) qui retourne instantanément le VIN, les données constructeur, la couleur et l'historique du véhicule." },
  { q: "Les données API Plaque immatriculation sont-elles fiables ?", a: "L'API Plaque immatriculation interroge directement le Système d'Immatriculation des Véhicules (SIV) français. Les données sont celles des autorités officielles — précision maximale sur le sol français." },
  { q: "Peut-on faire une estimation voiture avec l'immatriculation uniquement ?", a: "Oui — il suffit de saisir la plaque d'immatriculation pour que VehicleIQ retrouve automatiquement le VIN et les caractéristiques du véhicule, puis calcule sa cote. Aucune autre information n'est obligatoire pour obtenir une première estimation." },
  { q: "Comment rechercher un professionnel de l'automobile par SIRET ?", a: "Le répertoire de prospection de VehicleIQ permet aux professionnels (marchands, garages, concessions) de retrouver un confrère ou un partenaire par SIRET, nom d'entreprise, catégorie ou ville, pour l'ajouter à leurs contacts ou lui envoyer une proposition commerciale." },
  { q: "Qu'est-ce qu'une IA de recherche de voiture d'occasion ?", a: "L'aide à l'achat de VehicleIQ analyse budget, usage et préférences pour recommander des véhicules d'occasion correspondants, en croisant les annonces disponibles sur les principales plateformes — chaque suggestion reçoit un score de correspondance." },
  { q: "Qu'est-ce qui est gratuit sur VehicleIQ ?", a: "La consultation des données officielles (HistoVec : identité du véhicule, contrôle technique, propriétaires) est gratuite. Vos 5 premières cotes sont offertes et déjà actives à la création du compte, sans CB. Au-delà, chaque dossier coûte à partir de 4,99 € (tarif unique), facturé à l'usage — sans abonnement ni engagement. Vous ne payez que quand vous utilisez." },
  { q: "Comment sont sécurisés mes paiements ?", a: "Tous les paiements sont traités par Stripe, le leader mondial du paiement en ligne. Vos coordonnées bancaires ne transitent jamais par nos serveurs et sont chiffrées selon les normes PCI-DSS. Nous acceptons Visa, Mastercard et la plupart des cartes bancaires européennes." },
  { q: "Puis-je obtenir un remboursement ?", a: "Oui, nous offrons une garantie satisfait ou remboursé de 7 jours pour tout rapport acheté. Si les données obtenues ne correspondent pas à ce que vous attendiez, contactez-nous à contact@vehicleiq.fr et nous vous remboursons sans question." },
  { q: "Comment fonctionne la recommandation d'achat ?", a: "Notre algorithme analyse vos réponses (âge, ville, budget, usage, préférences de marques, kilométrage accepté) et les croise avec les annonces disponibles sur AutoScout24, LeBonCoin et Marketplace. Chaque suggestion reçoit un score de correspondance de 0 à 100%." },
  { q: "Mes données personnelles sont-elles protégées ?", a: "Oui, conformément au RGPD. Nous ne collectons que les données nécessaires au service (numéro de téléphone ou email pour l'authentification, VIN pour les rapports). Vos données ne sont jamais revendues à des tiers. Consultez notre politique de confidentialité pour plus de détails." },
  { q: "VehicleIQ est-il disponible pour les professionnels ?", a: "Oui. VehicleIQ propose une gamme Pro (packs Volume et Pro intensif) accessible directement depuis la plateforme, et une offre Pro Multi-Agent (799 € HT/mois, 3 agents) et une offre Intégration API sur devis pour les besoins à fort volume (à partir de 2 500 € HT) avec compte dédié, SLA et facturation mensualisée. Contactez-nous à contact@vehicleiq.fr pour discuter de votre cas d'usage." },
  { q: "Comment publier mon annonce directement ?", a: "Après avoir complété le formulaire Vendre, VehicleIQ génère une annonce optimisée prête à copier-coller sur AutoScout24, LeBonCoin ou Marketplace. Nous fournissons également un lien direct vers le formulaire de dépôt de chaque plateforme. La publication directe automatisée est disponible avec le Pack Publication à 14,99 €." },
  { q: "L'application fonctionne-t-elle sur mobile ?", a: "Oui, VehicleIQ est entièrement responsive et fonctionne sur tous les appareils (smartphone, tablette, ordinateur). L'interface s'adapte automatiquement à la taille de votre écran. Une application mobile native est en cours de développement." },
];

function FAQModal({ onClose }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 640, maxHeight: "85vh", overflowY: "auto" }}>
        <div className="panel-header" style={{ position: "sticky", top: 0, background: "var(--deep)", zIndex: 1 }}>
          <div>
            <div className="panel-title">FAQ – Questions fréquentes</div>
            <div className="panel-sub">Tout ce que vous devez savoir sur VehicleIQ</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} style={{ borderBottom: "1px solid var(--border)", paddingBottom: ".8rem", marginBottom: ".8rem" }}>
              <button onClick={() => setOpen(open === i ? null : i)}
                style={{ width: "100%", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", textAlign: "left", padding: ".2rem 0" }}>
                <span style={{ fontSize: ".88rem", fontWeight: 600, color: "var(--text)" }}>{item.q}</span>
                <span style={{ color: "var(--accent)", flexShrink: 0, transition: "transform .2s", transform: open === i ? "rotate(45deg)" : "rotate(0)", fontSize: "1rem" }}>+</span>
              </button>
              {open === i && (
                <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.7, marginTop: ".6rem", paddingLeft: ".5rem", borderLeft: "2px solid var(--accent)" }}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
          <div style={{ textAlign: "center", padding: "1rem 0 .5rem", fontSize: ".8rem", color: "var(--sub)" }}>
            Une autre question ? <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("viq-open-contact"))} style={{ color: "var(--accent)", background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer", textDecoration: "underline" }}>contact@vehicleiq.fr</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Footer Services Accordion ────────────────────────────────────────────────
function FooterServices({ onService, onDashTab, user, onShowAuth }) {
  return (
    <div className="fcol-services" style={{ minWidth: "200px", display: "flex", flexDirection: "column", gap: ".4rem" }}>
      <div className="footer-col-title">Services</div>
      {[
        { id: "check", label: "🔍 Contrôler un véhicule", kind: "service" },
        { id: "buy",   label: "🛒 Acheter un véhicule", kind: "service" },
        { id: "sell",  label: "💰 Estimer un véhicule", kind: "service" },
        { id: "sale", label: "📊 Suivi de vente", kind: "dash" },
        { id: "leads", label: "📥 Formulaires clients", kind: "dash" },
        { id: "cartegrise", label: "🧾 Carte grise & malus", kind: "dash" },
        { id: "documents",  label: "📄 Documents (mandats & cessions)", kind: "dash" },
      ].map(item => (
        <button key={item.id} className="footer-link"
          onClick={() => item.kind === "dash" ? (onDashTab && onDashTab(item.id)) : onService(item.id)}>
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ─── Service Modal ────────────────────────────────────────────────────────────
const SERVICE_INFO = {
  check: {
    title: "Contrôler un véhicule",
    icon: "🔍",
    color: "var(--blue)",
    desc: "Obtenez un rapport complet sur n'importe quel véhicule grâce à son numéro VIN ou sa plaque d'immatriculation.",
    features: [
      { icon: "📋", title: "Historique complet", desc: "Propriétaires successifs, pays d'immatriculation, dates de mise en circulation." },
      { icon: "⚠️", title: "Sinistres & accidents", desc: "Accidents déclarés, véhicule volé, anomalie de kilométrage détectée." },
      { icon: "🔧", title: "Entretien & révisions", desc: "Historique des entretiens, dernière révision, contrôle technique." },
      { icon: "💰", title: "Valeur marché", desc:"Cote du prix de revente basée sur les données marché temps réel." },
      { icon: "🚗", title: "Véhicules alternatifs", desc: "3 véhicules similaires recommandés selon le profil du véhicule contrôlé." },
    ],
    cta: "Contrôler un véhicule",
    pricing: "Gratuit et illimité · Contrôle plaque : 0 crédit · Sans abonnement",
  },
  buy: {
    title: "Acheter un véhicule",
    icon: "🛒",
    color: "var(--green)",
    desc: "Répondez à 10 questions sur votre profil et votre budget. Notre IA sélectionne les meilleures annonces pour vous.",
    features: [
      { icon: "🎯", title: "Matching personnalisé", desc: "Algorithme IA qui croise vos préférences, usage quotidien et budget maximal." },
      { icon: "🔎", title: "Multi-plateformes", desc: "Recherche simultanée sur AutoScout24, LeBonCoin et Facebook Marketplace." },
      { icon: "📊", title: "Score de correspondance", desc: "Chaque annonce reçoit un score de 0 à 100 selon votre profil." },
      { icon: "🔗", title: "Liens directs", desc: "Accès direct aux annonces sélectionnées sur les plateformes partenaires." },
      { icon: "🔔", title: "Alertes prix (Premium)", desc: "Soyez notifié quand le prix d'une annonce baisse." },
    ],
    cta: "Trouver mon véhicule",
    pricing: "1ère utilisation gratuite · Résultats personnalisés : 1 crédit par requête · Packs à partir de 59 €",
  },
  sell: {
    title: "Estimer un véhicule",
    icon: "💰",
    color: "var(--accent2)",
    desc: "Estimez le prix de vente optimal de votre véhicule et découvrez sur quelle plateforme vendre au meilleur prix.",
    features: [
      { icon: "📈", title:"Cote prix", desc: "Fourchette basse / recommandée / haute basée sur les ventes réelles du marché." },
      { icon: "🏆", title: "Meilleure plateforme", desc: "Recommandation de la plateforme la plus adaptée à votre véhicule et votre région." },
      { icon: "📝", title: "Annonce optimisée", desc: "Génération automatique d'une annonce prête à publier avec les mots-clés les plus recherchés." },
      { icon: "🔗", title: "Publication directe", desc: "Liens directs vers les formulaires de dépôt d'annonce sur chaque plateforme." },
      { icon: "💼", title: "Réseau pro (Premium)", desc: "Diffusion auprès de notre réseau de revendeurs professionnels agréés." },
    ],
    cta: "Estimer mon véhicule",
    pricing: "1ère utilisation gratuite · Fiche cote + plateformes : 1 crédit par requête · Packs à partir de 59 €",
  },
};

function ServiceModal({ serviceId, onClose, onLaunch }) {
  const s = SERVICE_INFO[serviceId];
  if (!s) return null;
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 560 }}>
        <div className="panel-header">
          <div>
            <div style={{ fontSize: "1.5rem", marginBottom: ".2rem" }}>{s.icon}</div>
            <div className="panel-title" style={{ color: s.color }}>{s.title}</div>
            <div className="panel-sub">{s.desc}</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          <div style={{ display: "flex", flexDirection: "column", gap: ".8rem", marginBottom: "1.5rem" }}>
            {s.features.map((f, i) => (
              <div key={i} style={{ display: "flex", gap: ".9rem", alignItems: "flex-start" }}>
                <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>{f.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: ".87rem", marginBottom: ".15rem" }}>{f.title}</div>
                  <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.5 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 4, padding: ".7rem 1rem", fontSize: ".75rem", color: "var(--sub)", fontFamily: "var(--font-mono)", marginBottom: "1rem" }}>
            💎 {s.pricing}
          </div>
          <button className="btn btn-accent" style={{ width: "100%" }} onClick={() => { onClose(); if (onLaunch) onLaunch(serviceId); }}>
            {s.cta} →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function StarRating({ value, onChange, readonly = false, size = 18 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: "flex", gap: 2, cursor: readonly ? "default" : "pointer" }}>
      {[1,2,3,4,5].map(star => (
        <svg key={star} width={size} height={size} viewBox="0 0 24 24"
          fill={(hovered || value) >= star ? "#FFB800" : "none"}
          stroke={(hovered || value) >= star ? "#FFB800" : "#555"}
          strokeWidth="1.5"
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(0)}
          onClick={() => !readonly && onChange && onChange(star)}
          style={{ flexShrink: 0, transition: "all .15s" }}>
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      ))}
    </div>
  );
}

function ServiceRatings({ user, onShowAuth, inline = false }) {
  const [ratings, setRatings] = useState({ check: {avg:0,count:0}, buy: {avg:0,count:0}, sell: {avg:0,count:0} });
  const [myRatings, setMyRatings] = useState({});
  const [saved, setSaved] = useState({});

  useEffect(() => {
    apiGet("/api/ratings").then(r => { if (r.success) setRatings(r.ratings); }).catch(()=>{});
    if (user?.uid) {
      apiGet("/api/ratings/me").then(r => { if (r.success) setMyRatings(r.ratings); }).catch(()=>{});
    }
  }, [user?.uid]);

  const handleRate = async (service, star) => {
    if (!user) { onShowAuth && onShowAuth(); return; }
    setMyRatings(prev => ({ ...prev, [service]: star }));
    try {
      await apiPost("/api/ratings", { service, rating: star });
      setSaved(prev => ({ ...prev, [service]: true }));
      apiGet("/api/ratings").then(r => { if (r.success) setRatings(r.ratings); }).catch(()=>{});
      setTimeout(() => setSaved(prev => ({ ...prev, [service]: false })), 2000);
    } catch(e) {}
  };

  const services = [
    { key: "check", label: "🔍 Contrôle plaque", tarif: "Gratuit" },
    { key: "buy",   label: "🛒 Achat IA",       tarif: "inclus" },
    { key: "sell",  label:"💰 Cote",     tarif: "inclus" },
  ];

  // Mode inline (dans la freemium-banner du hero)
  if (inline) return (
    <div style={{ display: "flex", gap: "2rem", flexWrap: "wrap", alignItems: "center", justifyContent: "center", width: "100%" }}>
      {services.map(({ key, label, tarif }) => {
        const avg = ratings[key]?.avg || 0;
        const count = ratings[key]?.count || 0;
        const myNote = myRatings[key] || 0;
        return (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: ".9rem", flexWrap: "wrap" }}>
            {/* Label + tarif */}
            <div style={{ display: "flex", flexDirection: "column", gap: ".15rem" }}>
              <span style={{ fontSize: ".95rem", color: "var(--sub)", fontWeight: 700 }}>{label}</span>
              <span style={{ fontSize: ".78rem", color: "var(--accent2)", fontFamily: "var(--font-mono)" }}>{tarif}/rech.</span>
            </div>
            {/* Séparateur */}
            <div style={{ width: 1, height: 40, background: "var(--border)", flexShrink: 0 }} />
            {/* Moyenne globale */}
            <div style={{ display: "flex", flexDirection: "column", gap: ".25rem", alignItems: "center" }}>
              <StarRating value={Math.round(avg)} readonly size={22} />
              <span style={{ fontSize: ".82rem", color: "var(--muted)", fontWeight: 600 }}>
                {avg > 0 ? `${avg.toFixed(1)} (${count})` : "—"}
              </span>
            </div>
            {/* Note utilisateur */}
            {user ? (
              <div style={{ display: "flex", flexDirection: "column", gap: ".25rem", alignItems: "center" }}>
                <StarRating value={myNote} onChange={star => handleRate(key, star)} size={24} />
                <span style={{ fontSize: ".78rem", color: saved[key] ? "var(--green)" : "var(--muted)", fontWeight: 600 }}>
                  {saved[key] ? "✓ Noté" : myNote > 0 ? "Votre note" : "Notez"}
                </span>
              </div>
            ) : (
              <button onClick={() => onShowAuth && onShowAuth()}
                style={{ fontSize: ".82rem", color: "var(--accent)", background: "none", border: "1px solid rgba(168,128,58,.3)", borderRadius: 6, padding: ".35rem .7rem", cursor: "pointer", fontFamily: "var(--font-body)", whiteSpace: "nowrap", fontWeight: 600 }}>
                ★ Notez
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  // Mode footer (colonne)
  return (
    <div className="footer-col" style={{ minWidth: 200 }}>
      <div className="footer-col-title">⭐ Avis utilisateurs</div>
      {services.map(({ key, label }) => {
        const avg = ratings[key]?.avg || 0;
        const count = ratings[key]?.count || 0;
        const myNote = myRatings[key] || 0;
        return (
          <div key={key} style={{ marginBottom: ".9rem" }}>
            <div style={{ fontSize: ".75rem", color: "var(--sub)", marginBottom: ".2rem", fontWeight: 600 }}>{label}</div>
            <div style={{ display: "flex", alignItems: "center", gap: ".4rem", marginBottom: ".25rem" }}>
              <StarRating value={Math.round(avg)} readonly size={13} />
              <span style={{ fontSize: ".68rem", color: "var(--accent2)", fontWeight: 600 }}>
                {avg > 0 ? avg.toFixed(1) : "—"}
              </span>
              {count > 0 && <span style={{ fontSize: ".62rem", color: "var(--muted)" }}>({count})</span>}
            </div>
            {user ? (
              <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                <StarRating value={myNote} onChange={star => handleRate(key, star)} size={14} />
                {saved[key] && <span style={{ fontSize: ".62rem", color: "var(--green)" }}>✓ Noté</span>}
              </div>
            ) : (
              <button onClick={() => onShowAuth && onShowAuth()}
                style={{ fontSize: ".62rem", color: "var(--accent)", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "var(--font-body)" }}>
                + Donner mon avis
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── CessionModal — Certificat de cession CERFA 15776 + Histovec ─────────────
// Mini-module de décodage de plaque réutilisable (cession + mandat).
// Saisit une plaque → interroge l'API officielle → renvoie {immat, vin, make, model, year, fuel, gearbox}.
function PlateLookupInline({ onResult, defaultPlate = "" }) {
  const [plate, setPlate] = useState(defaultPlate || "");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null); // {ok, text}
  const fmt = (v) => {
    const c = v.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 7);
    return c.length > 5 ? `${c.slice(0, 2)}-${c.slice(2, 5)}-${c.slice(5)}` : c.length > 2 ? `${c.slice(0, 2)}-${c.slice(2)}` : c;
  };
  const lookup = async () => {
    const clean = plate.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (clean.length < 7) { setMsg({ ok: false, text: "Format invalide — ex : AB-123-CD" }); return; }
    setLoading(true); setMsg(null);
    try {
      const data = await apiGet("/api/check/" + clean + "?is_pro=false&siret=");
      if (!data.success || !data.data) { setMsg({ ok: false, text: "Véhicule non trouvé — saisie manuelle possible." }); }
      else {
        const d = data.data;
        const num = (v) => { if (v == null) return ""; const n = parseInt(String(v).replace(/[^\d]/g, ""), 10); return isNaN(n) ? "" : n; };
        // Date 1re immatriculation → "YYYY-MM" pour l'input mois/année
        const toMonth = (s) => {
          if (!s) return ""; const str = String(s).trim(); let m;
          if ((m = str.match(/^(\d{4})[-\/](\d{1,2})/))) return `${m[1]}-${String(m[2]).padStart(2, "0")}`;                 // YYYY-MM(-DD)
          if ((m = str.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/))) return `${m[3]}-${String(m[2]).padStart(2, "0")}`;   // DD/MM/YYYY
          if ((m = str.match(/^(\d{1,2})[-\/](\d{4})$/))) return `${m[2]}-${String(m[1]).padStart(2, "0")}`;                 // MM/YYYY
          if ((m = str.match(/(\d{4})/))) return `${m[1]}-01`;
          return "";
        };
        const makeModel = buildVehicleFullName(d);
        onResult && onResult({
          immat: fmt(plate), vin: d.vin || "",
          make: d.make || "", model: d.model || "", makeModel,
          year: d.year || "", fuel: d.fuel || "", gearbox: d.gearbox || "",
          co2: num(d.co2), power_kw: num(d.power_kw), power_cv: num(d.power_cv),
          weight: num(d.weight), date_month: toMonth(d.date_immat),
        });
        setMsg({ ok: true, text: `✓ ${makeModel || ""}${d.vin ? " · VIN récupéré" : ""}`.trim() });
      }
    } catch (e) { setMsg({ ok: false, text: "Erreur de connexion — saisie manuelle possible." }); }
    setLoading(false);
  };
  return (
    <div style={{ background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", borderRadius: 10, padding: ".8rem .9rem", marginBottom: "1rem" }}>
      <div style={{ fontSize: ".74rem", color: "var(--sub)", marginBottom: ".5rem", fontWeight: 600 }}>
        🔍 Décoder une plaque pour préremplir immatriculation, VIN et véhicule
      </div>
      <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", alignItems: "center" }}>
        <input value={plate} onChange={e => { setPlate(fmt(e.target.value)); setMsg(null); }}
          placeholder="AB-123-CD"
          style={{ flex: "1 1 160px", minWidth: 140, padding: ".55rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontFamily: "var(--font-mono)", fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase", outline: "none" }} />
        <button type="button" className="btn btn-accent btn-sm" onClick={lookup} disabled={loading}
          style={{ opacity: loading ? .6 : 1, cursor: loading ? "wait" : "pointer", whiteSpace: "nowrap" }}>
          {loading ? "Décodage…" : "Décoder"}
        </button>
      </div>
      {msg && <div style={{ marginTop: ".5rem", fontSize: ".76rem", fontWeight: 600, color: msg.ok ? "var(--green)" : "var(--red)" }}>{msg.text}</div>}
    </div>
  );
}

function CessionModal({ onClose, prefill, user }) {
  const p = prefill || {};
  const full = p._full || {}; // ligne DB si on édite une cession existante

  // Construit un objet de coordonnées à partir du compte connecté (best-effort).
  // L'adresse compte est une chaîne libre : on tente d'en extraire n°/voie/CP/commune.
  const meAsParty = () => {
    const u = user || (typeof window !== "undefined" ? window.__viq_user : null) || {};
    const nom = u.siret_name || u.name || (u.email ? u.email.split("@")[0] : "") || "";
    const isPro = !!u.siret;
    let numVoie = "", typeVoie = "", nomVoie = "", cp = "", commune = "";
    const addr = (u.address || "").trim();
    if (addr) {
      // CP (5 chiffres) + commune
      const cpM = addr.match(/\b(\d{5})\b\s*([^,\d][^,]*)?/);
      if (cpM) { cp = cpM[1]; commune = (cpM[2] || "").trim(); }
      // 1re ligne avant le CP = voie
      const voiePart = addr.split(/\b\d{5}\b/)[0].replace(/[,\s]+$/, "").trim();
      const numM = voiePart.match(/^\s*(\d+)\s*(bis|ter)?\s+(.*)$/i);
      if (numM) {
        numVoie = numM[1] + (numM[2] ? " " + numM[2] : "");
        const rest = numM[3].trim().split(/\s+/);
        typeVoie = rest.shift() || "";
        nomVoie = rest.join(" ");
      } else {
        nomVoie = voiePart;
      }
    }
    return { nom, isPro, siret: u.siret || "", numVoie, typeVoie, nomVoie, cp, commune };
  };

  // Préremplit le vendeur OU l'acheteur avec les coordonnées du compte.
  const fillSelf = (who) => {
    const me = meAsParty();
    setF(prev => who === "vendeur" ? {
      ...prev,
      vendeurType: me.isPro ? "morale" : "physique",
      vendeurNom: me.nom, vendeurSiret: me.siret,
      vendeurNumVoie: me.numVoie, vendeurTypeVoie: me.typeVoie, vendeurNomVoie: me.nomVoie,
      vendeurCP: me.cp, vendeurCommune: me.commune,
    } : {
      ...prev,
      acheteurType: me.isPro ? "morale" : "physique",
      acheteurNom: me.nom, acheteurSiret: me.siret,
      acheteurNumVoie: me.numVoie, acheteurTypeVoie: me.typeVoie, acheteurNomVoie: me.nomVoie,
      acheteurCP: me.cp, acheteurCommune: me.commune,
    });
  };
  const [f, setF] = useState({
    immat: full.immat || p.immat || "", vin: full.vin || p.vin || "", date1ere: "", marque: full.marque || p.makeModel || "",
    type: "", genre: "", denomination: "", km: full.km || p.km || "", numFormule: "",
    vendeurType: full.vendeur_type || "physique", vendeurSexe: "M", vendeurNom: full.vendeur_nom || "", vendeurSiret: full.vendeur_siret || "",
    vendeurNumVoie: "", vendeurTypeVoie: "", vendeurNomVoie: "", vendeurCP: full.vendeur_cp || "", vendeurCommune: full.vendeur_commune || "",
    dateVente: full.date_vente || "", heureVente: "",
    acheteurType: full.acheteur_type || "physique", acheteurSexe: "M", acheteurNom: full.acheteur_nom || "", acheteurSiret: full.acheteur_siret || "",
    acheteurNaissance: "", acheteurLieuNaissance: "",
    acheteurNumVoie: "", acheteurTypeVoie: "", acheteurNomVoie: "", acheteurCP: full.acheteur_cp || "", acheteurCommune: full.acheteur_commune || "",
    lieu: full.lieu || "Saint-Quentin", viqPrice: p.viqPrice || "", year: p.year || "", fuel: p.fuel || "", gearbox: p.gearbox || "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const canGen = f.immat.trim() && f.marque.trim() && f.vendeurNom.trim() && f.acheteurNom.trim();

  const genCerfa = async () => {
    if (!canGen || busy) return;
    setBusy(true); setMsg("");
    try {
      await exportCessionCerfa(f);
      // Enregistrement en base (best-effort)
      try {
        await apiPost("/api/cessions", {
          immat: f.immat, vin: f.vin, marque: f.marque,
          vehicule: [f.marque, f.type].filter(Boolean).join(" "),
          km: f.km ? Number(f.km) : null, date_vente: f.dateVente, lieu: f.lieu,
          vendeur_type: f.vendeurType, vendeur_nom: f.vendeurNom, vendeur_siret: f.vendeurSiret,
          vendeur_cp: f.vendeurCP, vendeur_commune: f.vendeurCommune,
          acheteur_type: f.acheteurType, acheteur_nom: f.acheteurNom, acheteur_siret: f.acheteurSiret,
          acheteur_cp: f.acheteurCP, acheteur_commune: f.acheteurCommune,
        });
        setMsg("✓ Certificat de cession généré et enregistré.");
      } catch (e) {
        setMsg("✓ Certificat généré (PDF téléchargé). ⚠ Enregistrement en base indisponible.");
      }
    }
    catch (e) { setMsg("⚠ " + (e.message || "Erreur lors de la génération. Le CERFA doit être déposé sur le serveur.")); }
    finally { setBusy(false); }
  };
  const genHisto = async () => {
    setBusy(true); setMsg("");
    try { await exportHistoriqueReport(f); setMsg("✓ Rapport d'historique VIQ.AI® généré."); }
    catch (e) { setMsg("⚠ " + (e.message || "Erreur.")); }
    finally { setBusy(false); }
  };
  const histovecUrl = "https://histovec.interieur.gouv.fr/histovec/accueil";

  const lbl = { fontSize: ".7rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem", display: "block" };
  const fld = { width: "100%", padding: ".55rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".88rem", fontFamily: "var(--font-body)", outline: "none" };
  const g2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".7rem", marginBottom: ".7rem" };
  const g3 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: ".7rem", marginBottom: ".7rem" };
  const st = (t) => <div style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", fontWeight: 800, color: "var(--text)", margin: "1.3rem 0 .7rem" }}>{t}</div>;
  const typeToggle = (key, sexKey) => (
    <div style={{ display: "flex", gap: ".5rem", marginBottom: ".7rem", flexWrap: "wrap", alignItems: "center" }}>
      {[["physique", "Personne physique"], ["morale", "Personne morale"]].map(([v, t]) => (
        <button key={v} type="button" onClick={() => set(key, v)}
          style={{ padding: ".45rem .8rem", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: ".82rem",
            border: "1.5px solid " + (f[key] === v ? "var(--accent)" : "var(--border)"),
            background: f[key] === v ? "var(--accent-soft)" : "var(--card)", color: f[key] === v ? "var(--accent)" : "var(--text)" }}>{t}</button>
      ))}
      {f[key] === "physique" && (
        <div style={{ display: "flex", gap: ".3rem", marginLeft: ".4rem" }}>
          {[["M", "M"], ["F", "F"]].map(([v, t]) => (
            <button key={v} type="button" onClick={() => set(sexKey, v)}
              style={{ width: 34, padding: ".4rem", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: ".82rem",
                border: "1.5px solid " + (f[sexKey] === v ? "var(--accent)" : "var(--border)"),
                background: f[sexKey] === v ? "var(--accent-soft)" : "var(--card)", color: f[sexKey] === v ? "var(--accent)" : "var(--text)" }}>{t}</button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel panel-wide flex-panel" style={{ maxWidth: 740 }}>
        <div className="panel-header" style={{ flexShrink: 0 }}>
          <div>
            <div className="panel-title">📄 Certificat de cession & Historique</div>
            <div className="panel-sub">CERFA 15776 officiel · pré-rempli depuis la Cote</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="panel-body" style={{ overflowY: "auto" }}>
          {st("Le véhicule")}
          {!(f.immat && f.vin) && (
            <PlateLookupInline defaultPlate={f.immat}
              onResult={(d) => setF(prev => ({
                ...prev,
                immat: d.immat || prev.immat, vin: d.vin || prev.vin,
                marque: prev.marque || d.make || "",
              }))} />
          )}
          <div style={g2}>
            <div><label style={lbl}>Immatriculation *</label><input style={fld} value={f.immat} onChange={e => set("immat", e.target.value.toUpperCase())} placeholder="AB-123-CD" /></div>
            <div><label style={lbl}>N° de série (VIN)</label><input style={fld} value={f.vin} onChange={e => set("vin", e.target.value.toUpperCase())} placeholder="VF..." /></div>
          </div>
          <div style={g2}>
            <div><label style={lbl}>Marque *</label><input style={fld} value={f.marque} onChange={e => set("marque", e.target.value)} placeholder="BMW" /></div>
            <div><label style={lbl}>Type / variante / version</label><input style={fld} value={f.type} onChange={e => set("type", e.target.value)} /></div>
          </div>
          <div style={g3}>
            <div><label style={lbl}>Genre national</label><input style={fld} value={f.genre} onChange={e => set("genre", e.target.value)} placeholder="VP" /></div>
            <div><label style={lbl}>Kilométrage</label><input className="num-spin" style={fld} type="number" value={f.km} onChange={e => set("km", e.target.value)} /></div>
            <div><label style={lbl}>1re mise en circ. (JJ/MM/AAAA)</label><input style={fld} value={f.date1ere} onChange={e => set("date1ere", e.target.value)} placeholder="01/01/2020" /></div>
          </div>
          <div style={g2}>
            <div><label style={lbl}>Dénomination commerciale</label><input style={fld} value={f.denomination} onChange={e => set("denomination", e.target.value)} /></div>
            <div><label style={lbl}>N° de formule (carte grise)</label><input style={fld} value={f.numFormule} onChange={e => set("numFormule", e.target.value)} placeholder="2020..." /></div>
          </div>

          {st("Ancien propriétaire (vendeur)")}
          <button type="button" onClick={() => fillSelf("vendeur")}
            title="Remplir avec les coordonnées de mon compte"
            style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", marginBottom: ".7rem",
              background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.3)", color: "var(--accent)",
              borderRadius: 8, padding: ".4rem .8rem", fontSize: ".76rem", fontWeight: 600, cursor: "pointer" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Préremplir avec mes coordonnées
          </button>
          {typeToggle("vendeurType", "vendeurSexe")}
          <div style={g2}>
            <div><label style={lbl}>Nom & prénom / Raison sociale *</label><input style={fld} value={f.vendeurNom} onChange={e => set("vendeurNom", e.target.value)} /></div>
            <div><label style={lbl}>SIRET (le cas échéant)</label><input style={fld} value={f.vendeurSiret} onChange={e => set("vendeurSiret", e.target.value)} /></div>
          </div>
          <div style={g3}>
            <div><label style={lbl}>N° voie</label><input style={fld} value={f.vendeurNumVoie} onChange={e => set("vendeurNumVoie", e.target.value)} /></div>
            <div><label style={lbl}>Type de voie</label><input style={fld} value={f.vendeurTypeVoie} onChange={e => set("vendeurTypeVoie", e.target.value)} placeholder="Avenue" /></div>
            <div><label style={lbl}>Nom de la voie</label><input style={fld} value={f.vendeurNomVoie} onChange={e => set("vendeurNomVoie", e.target.value)} /></div>
          </div>
          <div style={g2}>
            <div><label style={lbl}>Code postal</label><input style={fld} value={f.vendeurCP} onChange={e => set("vendeurCP", e.target.value)} /></div>
            <div><label style={lbl}>Commune</label><input style={fld} value={f.vendeurCommune} onChange={e => set("vendeurCommune", e.target.value)} /></div>
          </div>
          <div style={g2}>
            <div><label style={lbl}>Date de vente (JJ/MM/AAAA)</label><input style={fld} value={f.dateVente} onChange={e => set("dateVente", e.target.value)} placeholder="06/06/2026" /></div>
            <div><label style={lbl}>Heure de vente</label><input style={fld} value={f.heureVente} onChange={e => set("heureVente", e.target.value)} placeholder="14h30" /></div>
          </div>

          {st("Nouveau propriétaire (acheteur)")}
          <button type="button" onClick={() => fillSelf("acheteur")}
            title="Remplir avec les coordonnées de mon compte"
            style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", marginBottom: ".7rem",
              background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.3)", color: "var(--accent)",
              borderRadius: 8, padding: ".4rem .8rem", fontSize: ".76rem", fontWeight: 600, cursor: "pointer" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            Préremplir avec mes coordonnées
          </button>
          {typeToggle("acheteurType", "acheteurSexe")}
          <div style={g2}>
            <div><label style={lbl}>Nom & prénom / Raison sociale *</label><input style={fld} value={f.acheteurNom} onChange={e => set("acheteurNom", e.target.value)} /></div>
            <div><label style={lbl}>SIRET (le cas échéant)</label><input style={fld} value={f.acheteurSiret} onChange={e => set("acheteurSiret", e.target.value)} /></div>
          </div>
          {f.acheteurType === "physique" && (
            <div style={g2}>
              <div><label style={lbl}>Date de naissance (JJ/MM/AAAA)</label><input style={fld} value={f.acheteurNaissance} onChange={e => set("acheteurNaissance", e.target.value)} /></div>
              <div><label style={lbl}>Lieu de naissance</label><input style={fld} value={f.acheteurLieuNaissance} onChange={e => set("acheteurLieuNaissance", e.target.value)} /></div>
            </div>
          )}
          <div style={g3}>
            <div><label style={lbl}>N° voie</label><input style={fld} value={f.acheteurNumVoie} onChange={e => set("acheteurNumVoie", e.target.value)} /></div>
            <div><label style={lbl}>Type de voie</label><input style={fld} value={f.acheteurTypeVoie} onChange={e => set("acheteurTypeVoie", e.target.value)} /></div>
            <div><label style={lbl}>Nom de la voie</label><input style={fld} value={f.acheteurNomVoie} onChange={e => set("acheteurNomVoie", e.target.value)} /></div>
          </div>
          <div style={g2}>
            <div><label style={lbl}>Code postal</label><input style={fld} value={f.acheteurCP} onChange={e => set("acheteurCP", e.target.value)} /></div>
            <div><label style={lbl}>Commune</label><input style={fld} value={f.acheteurCommune} onChange={e => set("acheteurCommune", e.target.value)} /></div>
          </div>
          <div style={{ marginBottom: ".7rem" }}>
            <label style={lbl}>Lieu de signature</label><input style={{ ...fld, maxWidth: 280 }} value={f.lieu} onChange={e => set("lieu", e.target.value)} />
          </div>

          {/* Histovec */}
          {st("Historique du véhicule")}
          <div style={{ padding: "1rem 1.2rem", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", borderRadius: 10, marginBottom: ".8rem" }}>
            <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: ".4rem", fontSize: ".9rem" }}>📋 Histovec officiel (recommandé)</div>
            <div style={{ fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.5, marginBottom: ".7rem" }}>
              L'historique administratif officiel (sinistres, gages, oppositions, propriétaires) est délivré gratuitement par l'État. Seul le titulaire de la carte grise peut l'éditer via FranceConnect.
            </div>
            <a className="btn btn-accent btn-sm" href={histovecUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "inline-flex" }}>
              Obtenir l'Histovec officiel →
            </a>
          </div>

          <div style={{ marginTop: "1rem", padding: ".7rem .9rem", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", borderRadius: 8, fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.5 }}>
            ⚖️ Le certificat de cession généré reprend le formulaire officiel CERFA 15776. Vérifiez chaque champ avant signature. Les deux parties doivent signer (un exemplaire chacune).
          </div>
          {msg && <div style={{ marginTop: ".9rem", fontSize: ".85rem", fontWeight: 600, color: msg.startsWith("✓") ? "var(--green)" : "var(--red)" }}>{msg}</div>}

          <div style={{ display: "flex", gap: ".7rem", marginTop: "1.3rem", justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button className="btn btn-outline" onClick={onClose}>Fermer</button>
            <button className="btn btn-accent" onClick={genCerfa} disabled={!canGen || busy}
              style={{ opacity: (!canGen || busy) ? .55 : 1, cursor: (!canGen || busy) ? "not-allowed" : "pointer" }}>
              {busy ? "Génération…" : "📄 Générer le certificat de cession"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MandatModal — Création / édition d'un mandat de vente ───────────────────
// Pré-remplit le véhicule depuis une estimation, collecte les infos du mandant
// (client), génère le PDF (exportMandatPDF) et enregistre le mandat en base.
function MandatModal({ onClose, user, siret, prefill }) {
  const p = prefill || {};
  const full = p._full || {}; // ligne DB si on édite un mandat existant
  const [f, setF] = useState({
    mandantStatut: full.mandant_statut || "particulier",
    mandantNom: full.mandant_nom || "", mandantRepresentant: full.mandant_representant || "", mandantAdresse: full.mandant_adresse || "",
    mandantTel: full.mandant_tel || "", mandantEmail: full.mandant_email || "", mandantSiret: full.mandant_siret || "", mandantTva: full.mandant_tva || "",
    vehMakeModel: full.vehicule || p.makeModel || "", vehYear: full.vehicule_annee || p.year || "", vehKm: full.vehicule_km || p.km || "",
    vehFuel: p.fuel || "", vehGearbox: p.gearbox || "", vehVin: full.vehicule_vin || p.vin || "", vehImmat: full.vehicule_immat || p.immat || "",
    cote: full.cote || p.viqPrice || "", prixSouhaite: full.prix_souhaite || p.viqPrice || "", prixMin: full.prix_min || "",
    prixSouhaiteHt: full.prix_souhaite_ht || "", prixMinHt: full.prix_min_ht || "",
    commType: full.commission_type || "pourcentage", commValue: full.commission_value || "",
    duree: full.duree || "trois (3) mois", dureePost: "trois (3) mois", preavis: "quinze (15) jours",
    lieu: full.lieu || "Saint-Quentin", exclusif: !!full.exclusif,
    type: full.type || "standard",
  });
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [importMsg, setImportMsg] = useState("");
  const [importing, setImporting] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  // Bascule le type de mandat. Le type "online_exclusive" force exclusif + durée 1 mois.
  const setType = (tp) => setF(prev => ({
    ...prev, type: tp,
    ...(tp === "online_exclusive" ? { exclusif: true, duree: "un (1) mois" } : {}),
  }));
  const round925 = (v) => v ? Math.round(Number(v) * 0.925) : "";
  // Import d'une fiche annonce PDF → pré-remplit véhicule + prix + planchers (−7,5 %).
  const importPdf = async (file) => {
    if (!file || importing) return;
    setImporting(true); setImportMsg("Lecture du PDF…");
    try {
      const text = await extractPdfText(file);
      const d = parseListingFields(text);
      setF(prev => ({
        ...prev,
        vehMakeModel: d.makeModel || prev.vehMakeModel,
        vehYear: d.year || prev.vehYear,
        vehKm: d.km || prev.vehKm,
        vehFuel: d.fuel || prev.vehFuel,
        vehGearbox: d.gearbox || prev.vehGearbox,
        vehVin: d.vin || prev.vehVin,
        prixSouhaite: d.priceTtc || prev.prixSouhaite,
        prixSouhaiteHt: d.priceHt || prev.prixSouhaiteHt,
        prixMin: d.priceTtc ? round925(d.priceTtc) : prev.prixMin,
        prixMinHt: d.priceHt ? round925(d.priceHt) : prev.prixMinHt,
      }));
      const bits = [d.makeModel && "véhicule", d.priceTtc && "prix TTC", d.priceHt && "prix HT", d.km && "km"].filter(Boolean);
      setImportMsg(bits.length ? `✓ Importé : ${bits.join(", ")}. Planchers calculés à −7,5 %. Vérifiez les champs.` : "⚠ Aucun champ reconnu — saisie manuelle.");
    } catch (e) {
      setImportMsg("⚠ Impossible de lire ce PDF. Saisissez les champs manuellement.");
    } finally { setImporting(false); }
  };

  const canGenerate = f.mandantNom.trim() && f.vehMakeModel.trim();

  const handleGenerate = async () => {
    if (!canGenerate || saving) return;
    setSaving(true); setSavedMsg("");
    const ref = "VIQ-MDT-" + Date.now();
    const mandat = { ...f, ref, estimation_id: p.estimation_id || null };
    try {
      // 1) Génération du PDF (toujours, même si la sauvegarde échoue)
      await exportMandatPDF(mandat);
      // 2) Enregistrement en base (best-effort)
      try {
        await apiPost("/api/mandats", {
          ref,
          estimation_id: mandat.estimation_id,
          mandant_statut: f.mandantStatut,
          mandant_nom: f.mandantNom,
          mandant_representant: f.mandantRepresentant,
          mandant_adresse: f.mandantAdresse,
          mandant_tel: f.mandantTel,
          mandant_email: f.mandantEmail,
          mandant_siret: f.mandantSiret,
          mandant_tva: f.mandantTva,
          vehicule: f.vehMakeModel,
          vehicule_annee: String(f.vehYear || ""),
          vehicule_km: f.vehKm ? Number(f.vehKm) : null,
          vehicule_vin: f.vehVin,
          vehicule_immat: f.vehImmat,
          estimation: f.estimation ? Number(f.estimation) : null,
          prix_souhaite: f.prixSouhaite ? Number(f.prixSouhaite) : null,
          prix_min: f.prixMin ? Number(f.prixMin) : null,
          prix_souhaite_ht: f.prixSouhaiteHt ? Number(f.prixSouhaiteHt) : null,
          prix_min_ht: f.prixMinHt ? Number(f.prixMinHt) : null,
          commission_type: f.commType,
          commission_value: f.commValue ? Number(f.commValue) : null,
          duree: f.duree, exclusif: f.exclusif, lieu: f.lieu, type: f.type,
        });
        setSavedMsg("✓ Mandat généré et enregistré.");
      } catch (e) {
        setSavedMsg("✓ Mandat généré (PDF téléchargé). ⚠ Enregistrement en base indisponible.");
      }
    } catch (e) {
      setSavedMsg("⚠ Erreur lors de la génération du PDF.");
    } finally {
      setSaving(false);
    }
  };

  const lbl = { fontSize: ".72rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem", display: "block" };
  const fld = { width: "100%", padding: ".6rem .75rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".9rem", fontFamily: "var(--font-body)", outline: "none" };
  const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".8rem", marginBottom: ".8rem" };
  const sectionTitle = (t) => <div style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", fontWeight: 800, color: "var(--text)", margin: "1.4rem 0 .7rem" }}>{t}</div>;

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel panel-wide flex-panel" style={{ maxWidth: 720 }}>
        <div className="panel-header" style={{ flexShrink: 0 }}>
          <div>
            <div className="panel-title">📝 Générer un mandat de vente</div>
            <div className="panel-sub">Mandat d'intermédiation · pré-rempli depuis la Cote</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="panel-body" style={{ overflowY: "auto" }}>
          {sectionTitle("Type de mandat")}
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
            {[["standard", "Standard (intermédiation)"], ["online_exclusive", "Vente en ligne exclusive + enchères (1 mois)"]].map(([v, t]) => (
              <button key={v} type="button" onClick={() => setType(v)}
                style={{ flex: "1 1 220px", padding: ".6rem .7rem", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: ".82rem",
                  border: "1.5px solid " + (f.type === v ? "var(--accent)" : "var(--border)"),
                  background: f.type === v ? "var(--accent-soft)" : "var(--card)",
                  color: f.type === v ? "var(--accent)" : "var(--text)" }}>
                {t}
              </button>
            ))}
          </div>

          {f.type === "online_exclusive" && (
            <div style={{ marginTop: ".9rem", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", borderRadius: 10, padding: ".9rem 1rem" }}>
              <div style={{ fontSize: ".82rem", fontWeight: 700, color: "var(--text)", marginBottom: ".3rem" }}>📄 Importer une fiche annonce (PDF)</div>
              <div style={{ fontSize: ".74rem", color: "var(--sub)", marginBottom: ".6rem", lineHeight: 1.5 }}>
                Importez la fiche du véhicule (mobile.de, AutoScout…). Le véhicule et les prix sont pré-remplis, et les prix planchers calculés automatiquement à <strong>−7,5 %</strong> (HT et TTC). Mandat exclusif, 1 mois, avec mise en pause des annonces existantes 2 semaines.
              </div>
              <label className="btn btn-accent btn-sm" style={{ cursor: importing ? "wait" : "pointer", opacity: importing ? .6 : 1, display: "inline-flex", alignItems: "center", gap: ".4rem" }}>
                {importing ? "Lecture…" : "Choisir un PDF à importer"}
                <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }} disabled={importing}
                  onChange={(e) => { const f0 = e.target.files && e.target.files[0]; if (f0) importPdf(f0); e.target.value = ""; }} />
              </label>
              {importMsg && <div style={{ marginTop: ".5rem", fontSize: ".76rem", fontWeight: 600, color: importMsg.startsWith("✓") ? "var(--green)" : "var(--sub)" }}>{importMsg}</div>}
            </div>
          )}

          {sectionTitle("Le mandant (client)")}
          <div style={{ marginBottom: ".8rem" }}>
            <label style={lbl}>Qualité</label>
            <div style={{ display: "flex", gap: ".5rem" }}>
              {[["particulier", "Particulier"], ["pro", "Professionnel"]].map(([v, t]) => (
                <button key={v} type="button" onClick={() => set("mandantStatut", v)}
                  style={{ flex: 1, padding: ".55rem", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: ".85rem",
                    border: "1.5px solid " + (f.mandantStatut === v ? "var(--accent)" : "var(--border)"),
                    background: f.mandantStatut === v ? "var(--accent-soft)" : "var(--card)",
                    color: f.mandantStatut === v ? "var(--accent)" : "var(--text)" }}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: ".8rem" }}>
            <label style={lbl}>Nom / Raison sociale *</label>
            <input style={fld} value={f.mandantNom} onChange={e => set("mandantNom", e.target.value)} placeholder="Ex : Jean Dupont / Garage Dupont SARL" />
          </div>
          {f.mandantStatut === "pro" && (
            <div style={{ marginBottom: ".8rem" }}>
              <label style={lbl}>Représenté par (le cas échéant)</label>
              <input style={fld} value={f.mandantRepresentant} onChange={e => set("mandantRepresentant", e.target.value)} placeholder="Nom du représentant légal" />
            </div>
          )}
          <div style={{ marginBottom: ".8rem" }}>
            <label style={lbl}>Adresse</label>
            <input style={fld} value={f.mandantAdresse} onChange={e => set("mandantAdresse", e.target.value)} placeholder="N°, rue, code postal, ville" />
          </div>
          <div style={grid2}>
            <div><label style={lbl}>Téléphone</label><input style={fld} value={f.mandantTel} onChange={e => set("mandantTel", e.target.value)} placeholder="06 12 34 56 78" /></div>
            <div><label style={lbl}>E-mail</label><input style={fld} type="email" value={f.mandantEmail} onChange={e => set("mandantEmail", e.target.value)} placeholder="client@email.fr" /></div>
          </div>
          {f.mandantStatut === "pro" && (
            <div style={grid2}>
              <div><label style={lbl}>SIRET</label><input style={fld} value={f.mandantSiret} onChange={e => set("mandantSiret", e.target.value)} placeholder="123 456 789 00012" /></div>
              <div><label style={lbl}>N° TVA intracom.</label><input style={fld} value={f.mandantTva} onChange={e => set("mandantTva", e.target.value)} placeholder="FR..." /></div>
            </div>
          )}

          {sectionTitle("Le véhicule")}
          {!(f.vehImmat && f.vehVin) && (
            <PlateLookupInline defaultPlate={f.vehImmat}
              onResult={(d) => setF(prev => ({
                ...prev,
                vehImmat: d.immat || prev.vehImmat, vehVin: d.vin || prev.vehVin,
                vehMakeModel: prev.vehMakeModel || [d.make, d.model].filter(Boolean).join(" "),
                vehYear: prev.vehYear || d.year || "",
                vehFuel: prev.vehFuel || d.fuel || "", vehGearbox: prev.vehGearbox || d.gearbox || "",
              }))} />
          )}
          <div style={{ marginBottom: ".8rem" }}>
            <label style={lbl}>Marque / Modèle *</label>
            <input style={fld} value={f.vehMakeModel} onChange={e => set("vehMakeModel", e.target.value)} placeholder="Ex : BMW Série 3 320d" />
          </div>
          <div style={grid2}>
            <div><label style={lbl}>Année</label><input className="num-spin" style={fld} type="number" value={f.vehYear} onChange={e => set("vehYear", e.target.value)} placeholder="2021" /></div>
            <div><label style={lbl}>Kilométrage</label><input className="num-spin" style={fld} type="number" value={f.vehKm} onChange={e => set("vehKm", e.target.value)} placeholder="62000" /></div>
          </div>
          <div style={grid2}>
            <div><label style={lbl}>Énergie</label><input style={fld} value={f.vehFuel} onChange={e => set("vehFuel", e.target.value)} placeholder="Diesel" /></div>
            <div><label style={lbl}>Boîte</label><input style={fld} value={f.vehGearbox} onChange={e => set("vehGearbox", e.target.value)} placeholder="Automatique" /></div>
          </div>
          <div style={grid2}>
            <div><label style={lbl}>VIN (n° de série)</label><input style={fld} value={f.vehVin} onChange={e => set("vehVin", e.target.value)} placeholder="VF..." /></div>
            <div><label style={lbl}>Immatriculation</label><input style={fld} value={f.vehImmat} onChange={e => set("vehImmat", e.target.value)} placeholder="AA-123-BB" /></div>
          </div>

          {sectionTitle("Prix & commission")}
          <div style={grid2}>
            <div><label style={lbl}>Cote VIQ.AI® (indicative)</label><input className="num-spin" style={fld} type="number" value={f.cote} onChange={e => set("estimation", e.target.value)} placeholder="€" /></div>
            <div><label style={lbl}>Prix souhaité (TTC)</label><input className="num-spin" style={fld} type="number" value={f.prixSouhaite} onChange={e => set("prixSouhaite", e.target.value)} placeholder="€" /></div>
          </div>
          <div style={grid2}>
            <div><label style={lbl}>{f.type === "online_exclusive" ? "Prix minimum / plancher (TTC)" : "Prix minimum accepté (TTC)"}</label><input className="num-spin" style={fld} type="number" value={f.prixMin} onChange={e => set("prixMin", e.target.value)} placeholder="€" /></div>
            <div>
              <label style={lbl}>Type de commission</label>
              <select style={{ ...fld, cursor: "pointer" }} value={f.commType} onChange={e => set("commType", e.target.value)}>
                <option value="pourcentage">Pourcentage du prix de vente</option>
                <option value="forfait">Forfait fixe</option>
                <option value="net_vendeur">Net vendeur</option>
              </select>
            </div>
          </div>
          {f.type === "online_exclusive" && (
            <div style={grid2}>
              <div><label style={lbl}>Prix souhaité (HT)</label><input className="num-spin" style={fld} type="number" value={f.prixSouhaiteHt} onChange={e => set("prixSouhaiteHt", e.target.value)} placeholder="€ HT" /></div>
              <div><label style={lbl}>Prix minimum / plancher (HT)</label><input className="num-spin" style={fld} type="number" value={f.prixMinHt} onChange={e => set("prixMinHt", e.target.value)} placeholder="€ HT" /></div>
            </div>
          )}
          <div style={{ marginBottom: ".8rem" }}>
            <label style={lbl}>{f.commType === "pourcentage" ? "Commission (%)" : f.commType === "forfait" ? "Montant du forfait (€)" : "Prix net vendeur (€)"}</label>
            <input className="num-spin" style={fld} type="number" value={f.commValue} onChange={e => set("commValue", e.target.value)} placeholder={f.commType === "pourcentage" ? "Ex : 5" : "€"} />
          </div>

          {sectionTitle("Conditions")}
          <div style={grid2}>
            <div><label style={lbl}>Durée du mandat</label><input style={fld} value={f.duree} onChange={e => set("duree", e.target.value)} /></div>
            <div><label style={lbl}>Lieu de signature</label><input style={fld} value={f.lieu} onChange={e => set("lieu", e.target.value)} /></div>
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", cursor: "pointer", fontSize: ".88rem", color: "var(--sub)", marginTop: ".3rem" }}>
            <input type="checkbox" checked={f.exclusif} onChange={e => set("exclusif", e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--accent)", cursor: "pointer" }} />
            <span>Mandat <strong>exclusif</strong> (sinon non exclusif)</span>
          </label>

          <div style={{ marginTop: "1rem", padding: ".7rem .9rem", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", borderRadius: 8, fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.5 }}>
            ⚖️ Document type généré automatiquement. Il est recommandé de le faire vérifier par un conseil juridique et de l'adapter à votre situation avant signature.
          </div>

          {savedMsg && (
            <div style={{ marginTop: ".9rem", fontSize: ".85rem", fontWeight: 600, color: savedMsg.startsWith("✓") ? "var(--green)" : "var(--red)" }}>{savedMsg}</div>
          )}

          <div style={{ display: "flex", gap: ".7rem", marginTop: "1.3rem", justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button className="btn btn-outline" onClick={onClose}>Fermer</button>
            <button className="btn btn-accent" onClick={handleGenerate} disabled={!canGenerate || saving}
              style={{ opacity: (!canGenerate || saving) ? .55 : 1, cursor: (!canGenerate || saving) ? "not-allowed" : "pointer" }}>
              {saving ? "Génération…" : "📄 Générer le mandat (PDF)"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CharteModal — Charte graphique publique ────────────────────────────────
function CharteModal({ onClose }) {
  const Sw = ({ c, n, h, r }) => (
    <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "var(--card)" }}>
      <div style={{ height: 64, background: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.04)" }} />
      <div style={{ padding: ".6rem .7rem" }}>
        <div style={{ fontWeight: 700, fontSize: ".8rem", color: "var(--text)" }}>{n}</div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: ".7rem", color: "var(--muted)" }}>{h}</div>
        <div style={{ fontSize: ".68rem", color: "var(--sub)", marginTop: ".3rem", lineHeight: 1.4 }}>{r}</div>
      </div>
    </div>
  );
  const Eyebrow = ({ children }) => (
    <div style={{ fontFamily: "var(--font-mono)", fontSize: ".66rem", letterSpacing: "2px", textTransform: "uppercase", color: "var(--accent)", margin: "1.6rem 0 .8rem" }}>{children}</div>
  );
  const SecTitle = ({ n, children }) => (
    <div style={{ display: "flex", alignItems: "baseline", gap: ".8rem", margin: "2.4rem 0 1rem" }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: ".8rem", fontWeight: 700, color: "var(--accent)" }}>{n}</span>
      <span style={{ fontFamily: "var(--font-head)", fontSize: "1.7rem", color: "var(--text)", letterSpacing: "-.01em" }}>{children}</span>
    </div>
  );
  const lead = { fontSize: ".95rem", color: "var(--sub)", maxWidth: 640, lineHeight: 1.6, marginBottom: "1rem" };
  const swGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: ".8rem" };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel panel-wide panel-fullscreen flex-panel">
        <div className="panel-header" style={{ flexShrink: 0 }}>
          <div>
            <div className="panel-title">Charte graphique</div>
            <div className="panel-sub">L'identité visuelle de VehicleIQ · v1.0</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
            <a className="btn btn-accent btn-sm" href={CHARTE_PDF_URL} download
              style={{ textDecoration: "none", whiteSpace: "nowrap" }}>
              ⬇ Télécharger le PDF
            </a>
            <ThemeToggleButton size={32} />
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="panel-body" style={{ overflowY: "auto" }}>
          <div style={{ textAlign: "center", padding: "2rem 1rem", background: "linear-gradient(160deg, var(--deep), var(--bg))", borderRadius: 16, marginBottom: "1.5rem", border: "1px solid var(--border)" }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "3rem", lineHeight: 1, color: "var(--text)" }}>
              <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: "var(--accent2)", verticalAlign: ".55em", marginRight: ".4rem" }} />
              Vehicle<span style={{ fontStyle: "italic", color: "var(--accent)" }}>IQ</span>
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: ".6rem", letterSpacing: "4px", color: "var(--muted)", marginTop: ".6rem" }}>VIQ.AI® ENGINE</div>
            <a className="btn btn-outline btn-sm" href={CHARTE_PDF_URL} download
              style={{ textDecoration: "none", marginTop: "1.1rem", display: "inline-flex" }}>
              ⬇ Télécharger la charte (PDF)
            </a>
          </div>

          <SecTitle n="01">La marque</SecTitle>
          <p style={lead}>VehicleIQ est une plateforme française d'intelligence automobile : cotation, contrôle et aide à la décision pour les professionnels du véhicule d'occasion. Identité premium, sobre et crédible. Codes : éditorial (sérif), chaleureux (beige), précis (monospace).</p>
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
            {["Précision", "Confiance", "Rapidité"].map(t => (
              <span key={t} style={{ fontFamily: "var(--font-mono)", fontSize: ".68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 999, padding: ".3rem .8rem" }}>{t}</span>
            ))}
          </div>

          <SecTitle n="02">Couleurs</SecTitle>
          <p style={lead}>Fond blanc cassé chaud, encre profonde, accent doré antique signature. Bleu nuit pour la gravitas, vert forêt pour le sérieux business.</p>
          <Eyebrow>Accent &amp; signature</Eyebrow>
          <div style={swGrid}>
            <Sw c="#A8803A" n="Doré antique" h="#A8803A" r="Accent primaire, boutons, « IQ »." />
            <Sw c="#8A6829" n="Doré profond" h="#8A6829" r="Survol / pressé." />
            <Sw c="#1E2A4A" n="Bleu nuit" h="#1E2A4A" r="Accent secondaire, gravitas." />
            <Sw c="#F7EFDC" n="Ivoire doux" h="#F7EFDC" r="Fond des pills." />
          </div>
          <Eyebrow>Surfaces &amp; texte</Eyebrow>
          <div style={swGrid}>
            <Sw c="#F8F4EC" n="Fond page" h="#F8F4EC" r="Blanc cassé chaud." />
            <Sw c="#FDFBF6" n="Cartes" h="#FDFBF6" r="Crème clair." />
            <Sw c="#15171C" n="Encre" h="#15171C" r="Titres & texte." />
            <Sw c="#4E525B" n="Secondaire" h="#4E525B" r="Texte secondaire." />
          </div>
          <Eyebrow>Sémantique</Eyebrow>
          <div style={swGrid}>
            <Sw c="#2E6F5C" n="Vert forêt" h="#2E6F5C" r="Succès, tendance +." />
            <Sw c="#9B2C2C" n="Bordeaux" h="#9B2C2C" r="Erreur, tendance −." />
            <Sw c="#C9A96E" n="Or clair" h="#C9A96E" r="Accent mode sombre." />
            <Sw c="#16161B" n="Anthracite" h="#16161B" r="Fond mode sombre." />
          </div>

          <SecTitle n="03">Typographie</SecTitle>
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "1.4rem", marginBottom: ".8rem", background: "var(--card)" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: ".64rem", letterSpacing: "1px", textTransform: "uppercase", color: "var(--muted)", marginBottom: ".6rem" }}>Titres — Instrument Serif</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "2rem", color: "var(--text)" }}>Cotez, positionnez, <span style={{ fontStyle: "italic", color: "var(--accent)" }}>décidez.</span></div>
          </div>
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "1.4rem", marginBottom: ".8rem", background: "var(--card)" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: ".64rem", letterSpacing: "1px", textTransform: "uppercase", color: "var(--muted)", marginBottom: ".6rem" }}>Texte — Manrope (400 à 800)</div>
            <div style={{ fontSize: "1.05rem", color: "var(--text)" }}>L'analyse temps réel pour une <strong>cote calibrée</strong> sur le marché du moment.</div>
          </div>
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "1.4rem", background: "var(--card)" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: ".64rem", letterSpacing: "1px", textTransform: "uppercase", color: "var(--muted)", marginBottom: ".6rem" }}>Données — JetBrains Mono</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "1.05rem", color: "var(--accent)", fontWeight: 700 }}>AA-123-BB · 32.400 € · fourchette · ▲ 1,2 %</div>
          </div>

          <SecTitle n="04">Iconographie &amp; photographie</SecTitle>
          <p style={lead}>L'imagerie renforce la crédibilité professionnelle et la chaleur premium, sans cliché « stock photo ».</p>
          <ul style={{ margin: "0 0 1rem 1.1rem", color: "var(--sub)", fontSize: ".9rem", lineHeight: 1.7 }}>
            <li>Véhicules en 3/4 avant, propres, fond neutre clair ou showroom sobre — esprit annonce premium.</li>
            <li>Lumière naturelle douce, dominante chaude cohérente avec le fond beige.</li>
            <li>Icônes linéaires (trait ~2 px), encre ou doré, jamais multicolores.</li>
            <li>À éviter : sur-saturation, clichés corporate, imagerie « IA » (cerveaux, circuits bleus).</li>
          </ul>

          <SecTitle n="05">Ton &amp; voix</SecTitle>
          <p style={lead}>Direct, factuel, sans jargon. Vouvoiement. On valorise le gain de temps et la fiabilité.</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".8rem" }}>
            <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", background: "var(--card)" }}>
              <div style={{ fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".1em", color: "var(--green)", fontWeight: 700, marginBottom: ".6rem" }}>À faire</div>
              <div style={{ fontSize: ".85rem", color: "var(--sub)", lineHeight: 1.7 }}>Bénéfice concret · chiffres (&lt;5 min) · vocabulaire métier · mentions honnêtes</div>
            </div>
            <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", background: "var(--card)" }}>
              <div style={{ fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".1em", color: "var(--red)", fontWeight: 700, marginBottom: ".6rem" }}>À éviter</div>
              <div style={{ fontSize: ".85rem", color: "var(--sub)", lineHeight: 1.7 }}>Superlatifs vagues · jargon IA · prix garanti · tutoiement · excès d'emojis</div>
            </div>
          </div>

          <div style={{ marginTop: "2rem", paddingTop: "1.2rem", borderTop: "1px solid var(--border)", fontSize: ".72rem", color: "var(--muted)", fontStyle: "italic" }}>
            Charte graphique VehicleIQ v1.0 — M.K.L Ingénierie et Conseil · SIRET 904 954 542 00026 · vehicleiq.fr
          </div>
        </div>
      </div>
    </div>
  );
}

function Footer({ onLegal, onModel, onService, onDashTab, onFAQ, onB2B, onAPI, onContact, onBlog, onCharte, user, onShowAuth }) {
  return (
    <footer className="site-footer">
      <div className="footer-top">
        <div className="footer-brand">
          <div className="brand-mark brand-footer">
            <div className="logo">
              <div className="logo-dot" />
              Vehicle<span>IQ</span>
            </div>
            <div className="brand-tagline">VIQ.AI Engine</div>
          </div>
          <p>Intelligence automobile. Vérifiez, achetez et vendez avec la puissance de la data.</p>
        </div>
        <div className="footer-links">
          {CAR_BRANDS.map(brand => (
            <div key={brand.name} className="footer-col">
              <div className="footer-col-title">{brand.name}</div>
              {brand.models.map((m) => (
                <button key={m.name} className="footer-link footer-model-btn"
                  onClick={() => onModel(brand.name, m)}>
                  {m.name}
                </button>
              ))}
            </div>
          ))}
          <FooterServices onService={onService} onDashTab={onDashTab} />
          <div className="footer-col">
            <div className="footer-col-title">Légal</div>
            <button className="footer-link" onClick={() => onLegal("mentions")}>Mentions légales</button>
            <button className="footer-link" onClick={() => onLegal("privacy")}>Politique de confidentialité</button>
            <button className="footer-link" onClick={() => onLegal("cgu")}>CGU / Confidentialité</button>
            <button className="footer-link" onClick={() => onLegal("cgv")}>Conditions Générales de Vente</button>
          </div>
          <div className="footer-col">
            <div className="footer-col-title">Contact</div>
            <button className="footer-link" onClick={onContact}>Nous contacter</button>
            <button className="footer-link" onClick={onFAQ}>Support & FAQ</button>
            <button className="footer-link" onClick={() => (onAPI ? onAPI() : onB2B && onB2B())}>Intégration API</button>
            <button className="footer-link" onClick={() => onBlog && onBlog()}>Blog</button>
            <button className="footer-link" onClick={() => onCharte && onCharte()}>Charte graphique</button>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
          <div className="footer-copy">© {new Date().getFullYear()} VehicleIQ — M.K.L Ingénierie et Conseil · Tous droits réservés · <span style={{ color: "var(--accent2)", fontWeight: 600 }}>VIQ.AI® Engine</span></div>
          <div style={{ fontSize: ".72rem", color: "var(--sub)", display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <span>🇫🇷 Conçu en France</span>
            <span>🇪🇺 Hébergé en Union Européenne</span>
            <span style={{ color: "var(--muted)", fontSize: ".68rem" }}>SIRET 904 954 542 00026</span>
          </div>
          <div style={{ fontSize: ".65rem", color: "var(--muted)", lineHeight: 1.4, maxWidth: 600 }}>
            ⚖ <strong style={{ color: "var(--sub)" }}>*</strong> Les cotes, données et suggestions affichées sur VehicleIQ sont des estimations algorithmiques et statistiques, fournies à titre purement indicatif et consultatif. Elles ne constituent ni une expertise, ni une offre, ni un engagement contractuel, conformément à l'article 1112-1 du Code civil et aux articles L.111-1 et L.221-5 du Code de la consommation. Le prix de transaction final relève de la seule appréciation de l'utilisateur.
          </div>
          <div style={{ fontSize: ".62rem", color: "var(--muted)", lineHeight: 1.5, maxWidth: 620, marginTop: ".4rem", paddingTop: ".4rem", borderTop: "1px solid var(--border)" }}>
            🔒 <strong style={{ color: "var(--sub)" }}>VehicleIQ n'est pas un établissement financier.</strong> Les recharges sont traitées exclusivement par <strong style={{ color: "var(--sub)" }}>Stripe Inc.</strong>, plateforme certifiée PCI DSS. Conformément au RGPD et à la directive DSP2.
          </div>
        </div>
        <div className="footer-payments">
          <span>Paiement sécurisé</span>
          <CarteBleueIcon />
          <VisaSVG />
          <MastercardSVG />
          <PCIDSSIcon />
          <div className="footer-payments-data">
            <span>Données</span>
            <APIPlaqueSVG />
          </div>
        </div>
      </div>
    </footer>
  );
}


// ─── SIRET lookup via API Entreprises (data.gouv.fr) ─────────────────────────
const VIQ_SIRET = "90495454200026"; // M.K.L Ingénierie et Conseil

function SiretBlock({ isPro, setIsPro, siret, setSiret, siretInfo, setSiretInfo }) {
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  const lookupSiret = async (val) => {
    const clean = val.replace(/\s/g, "");
    setSiret(clean);
    setSiretInfo(null);
    setError("");
    if (clean.length < 9) return;
    setSearching(true);
    try {
      // API Annuaire des Entreprises (data.gouv.fr) — open data, no auth required
      const res = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${clean}&page=1&per_page=1`);
      const data = await res.json();
      const result = data.results?.[0];
      if (result) {
        setSiretInfo({
          nom: result.nom_complet || result.nom_raison_sociale || "—",
          siren: result.siren,
          activite: result.activite_principale_libelle || "—",
          adresse: result.siege?.adresse || "—",
        });
      } else {
        setError("Aucune entreprise trouvée pour ce numéro.");
      }
    } catch (e) {
      setError("Erreur de recherche. Vérifiez le numéro.");
    }
    setSearching(false);
  };

  return (
    <div style={{ marginBottom: "1.2rem" }}>
      <div style={{ fontSize: ".8rem", fontWeight: 600, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".6rem" }}>
        Vous êtes ?
      </div>
      <div className="siret-choice">
        <button className={`siret-choice-btn ${isPro === false ? "active" : ""}`} onClick={() => { setIsPro(false); setSiret(VIQ_SIRET); setSiretInfo(null); setError(""); }}>
          👤 Particulier
        </button>
        <button className={`siret-choice-btn ${isPro === true ? "active" : ""}`} onClick={() => { setIsPro(true); setSiret(""); setSiretInfo(null); setError(""); }}>
          🏢 Professionnel
        </button>
      </div>

      {isPro === false && (
        <div style={{ fontSize: ".75rem", color: "var(--sub)", padding: ".5rem .8rem", background: "rgba(61,158,255,.05)", border: "1px solid rgba(61,158,255,.15)", borderRadius: 4 }}>
          ℹ️ En tant que particulier, le rapport sera émis sous notre numéro d'entreprise conformément aux CGU API Plaque immatriculation.
        </div>
      )}

      {isPro === true && (
        <div className="field" style={{ marginBottom: 0 }}>
          <label>SIRET <span style={{ color: "var(--accent)" }}>*</span></label>
          <input className="input" placeholder="Ex : 123 456 789 00012"
            value={siret} maxLength={50}
            onChange={e => lookupSiret(e.target.value)} />
          {searching && <div className="siret-searching">🔍 Recherche en cours…</div>}
          {error && <div style={{ fontSize: ".75rem", color: "var(--red)", marginTop: ".3rem" }}>⚠ {error}</div>}
          {siretInfo && (
            <div className="siret-result">
              <strong>{siretInfo.nom}</strong>
              SIREN : {siretInfo.siren} · {siretInfo.activite}
              {siretInfo.adresse !== "—" && <div style={{ color: "var(--sub)", marginTop: ".15rem" }}>{siretInfo.adresse}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Check Panel (Plaque d'immatriculation) ───────────────────────────────────

// v49 — Résolution robuste du plafond de simultanéité (Contrôle/Cotation).
// Avant, seul `user.plan_limits.parallel_*` était lu — si ce champ n'était
// pas (encore) présent sur l'objet `user` côté client (ex. objet utilisateur
// partiellement hydraté), le plafond retombait systématiquement à 3, même
// pour un compte Pro Multi-Agent (10). Le formulaire lead, lui, fonctionnait
// car il vérifie `subscription_plan` directement en base côté serveur — on
// applique donc ici le même repli, en plus de `plan_limits` en priorité.
function resolveMaxSimul(user) {
  if (user?.plan_limits?.parallel_checks) return user.plan_limits.parallel_checks;
  if (user?.plan_limits?.parallel_estimations) return user.plan_limits.parallel_estimations;
  const plan = user?.effective_plan || user?.subscription_plan;
  if (plan === "multi_agent_3") return 10;
  return 3;
}

function VINPanel({ onClose, user, onShowAuth, onWalletChange, onShowRecharge, affiliateData, onLaunchBatchSell, onOpenDossiers }) {
  // v58 — Résolution SYNCHRONE du prefill, avant tout hook, appliquée via des
  // initialiseurs paresseux (useState(() => ...)) plutôt qu'un effet
  // post-rendu. Un effet à deps vides s'exécute APRÈS le premier rendu ; en
  // cas de doute sur l'ordre de montage/rendu, cette approche est strictement
  // plus fiable puisque la valeur est déjà là dès la première peinture — plus
  // aucune fenêtre de temps où le champ pourrait apparaître vide avant de se
  // remplir. Consommé une seule fois (la variable globale est aussitôt vidée).
  const _prefillRaw = window.__viq_vin_prefill;
  if (_prefillRaw) window.__viq_vin_prefill = null;
  const _prefillObj = _prefillRaw && typeof _prefillRaw === "object" ? _prefillRaw : null;
  const _prefillPlate = _prefillObj ? _prefillObj.plaqueOrVin : _prefillRaw;
  const _prefillCleaned = _prefillPlate ? String(_prefillPlate).replace(/[^a-zA-Z0-9-]/g, "").toUpperCase() : "";

  const [immat, setImmat] = useState(_prefillCleaned);
  const [inputMode, setInputMode] = useState(_prefillCleaned.replace(/-/g, "").length === 17 ? "vin" : "plaque");
  // Multi-plaque pour apporteurs d'affaires
  const [multiMode, setMultiMode] = useState(false);
  const [multiVins, setMultiVins] = useState([""]);
  // Auto-fill from saved profile
  const savedProfile = (() => { try { return JSON.parse(localStorage.getItem("viq_profile") || "{}"); } catch(e) { return {}; } })();
  const [paid, setPaid] = useState(false);
  const [openingDossier, setOpeningDossier] = useState(false); // v104 — bouton "Ouvrir un dossier VIQ" du contrôle
  const [isPro, setIsPro] = useState(savedProfile.isPro !== undefined ? savedProfile.isPro : null);
  const [siret, setSiret] = useState(savedProfile.siret || "");
  const [siretInfo, setSiretInfo] = useState(savedProfile.siretName ? { nom: savedProfile.siretName, siren: savedProfile.siret } : null);
  const [step, setStep] = useState("input"); // input | loading | result | batchResult | paywall
  // v45 — Contrôle simultané NATIF : plus de modale séparée. Un menu déroulant
  // choisit le nombre de véhicules (1 à maxSimul selon l'abonnement), et le
  // même champ plaque/VIN se duplique en autant de champs dans CE formulaire.
  const maxSimul = resolveMaxSimul(user);
  const [vehicleCount, setVehicleCount] = useState(1);
  // v48 — Chaque véhicule du lot porte désormais ses PROPRES HistoVec/photos
  // (avant, seul le champ d'identification était dupliqué — HistoVec et
  // photos restaient uniques pour tout le lot, ce qui n'avait pas de sens
  // dès que 2 véhicules ou plus étaient saisis).
  const blankBatchVehicle = () => ({ identifier: "", hvData: null, cvData: null, photos: [] });
  const [multiVehicles, setMultiVehicles] = useState([blankBatchVehicle()]);
  const [batchResults, setBatchResults] = useState(null);
  const [batchResultIndex, setBatchResultIndex] = useState(0); // v48 — carrousel de résultats
  const [lotMode, setLotMode] = useState("individual"); // v62 — individual | lot
  const [batchRunning, setBatchRunning] = useState(false);
  const [loadStep, setLoadStep] = useState(0);
  const [result, setResult] = useState(null);
  const [openSection, setOpenSection] = useState("identity");
  const [showGate, setShowGate] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);

  const [saved, setSaved] = useState(false);

  const [hvData, setHvData] = useState(_prefillObj?.histovec?.found ? _prefillObj.histovec : null);
  const [cvData, setCvData] = useState(_prefillObj?.carvertical?.found ? _prefillObj.carvertical : null); // v9 : rapport CarVertical importé
  const [knownIssues, setKnownIssues] = useState(null);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [enrichDone, setEnrichDone] = useState(false); // score affiché seulement après l'analyse de risque
  const [checkError, setCheckError] = useState(null); // { text, retryable } — v7 : message inline au lieu d'alert()
  // v25 — Inspection visuelle IA (Pro) : photos → état réel par zone + image de synthèse.
  const [inspectionPhotos, setInspectionPhotos] = useState(_prefillObj?.photos?.length ? _prefillObj.photos.slice(0, 10) : []);
  const [visualInspection, setVisualInspection] = useState(null);
  const [inspectionLoading, setInspectionLoading] = useState(false);
  const [inspectionError, setInspectionError] = useState(null);
  // v27 — ID de la ligne `searches` en base : permet de synchroniser la DB
  // (source de vérité de l'historique, cf. /api/users/me) à chaque
  // enrichissement, au lieu de ne l'écrire qu'une fois au contrôle initial.
  const [searchId, setSearchId] = useState(null);
  // v37 — Dossier VIQ : résolu systématiquement via find-or-create, jamais créé à la main.
  const [dossierId, setDossierId] = useState(_prefillObj?.dossier_id || null);
  // v55 — Bouton "Importer les données du dossier existant" : disponible dès
  // que le Contrôle est ouvert normalement (pas seulement via "Continuer"
  // depuis un Dossier VIQ), pour ne jamais repartir de zéro sur un véhicule
  // déjà connu (plaque déjà saisie manuellement, photos/HistoVec déjà là).
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupMsg, setLookupMsg] = useState(null);
  const importExistingDossier = async () => {
    const cleaned = immat.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (!cleaned) return;
    setLookupBusy(true); setLookupMsg(null);
    try {
      const params = inputMode === "vin" ? `vin=${encodeURIComponent(cleaned)}` : `plaque=${encodeURIComponent(cleaned)}`;
      const r = await apiGet(`/api/dossiers/lookup?${params}`);
      if (!r?.success || !r.found) { setLookupMsg("Aucun Dossier VIQ existant pour cette identification."); return; }
      setDossierId(r.dossier.id);
      if (r.photos && r.photos.length) setInspectionPhotos(r.photos.slice(0, 10));
      if (r.currentFiles?.histovec) setHvData({ found: true, ...r.currentFiles.histovec.payload });
      if (r.currentFiles?.carvertical) setCvData({ found: true, ...r.currentFiles.carvertical.payload });
      const parts = [r.dossier.make, r.dossier.model].filter(Boolean).join(" ");
      setLookupMsg(`✓ Dossier existant importé${parts ? ` — ${parts}` : ""}${r.photos?.length ? ` · ${r.photos.length} photo(s)` : ""}${r.currentFiles?.histovec ? " · HistoVec" : ""}${r.currentFiles?.carvertical ? " · CarVertical" : ""}.`);
    } catch (e) { setLookupMsg("Import impossible : " + (e?.message || "erreur réseau")); }
    finally { setLookupBusy(false); }
  };

  // v45 — Contrôle simultané NATIF : même logique par véhicule que le flux
  // simple (find-or-create + rattachement de l'opération), mais lancée en
  // parallèle (Promise.all) sur tous les identifiants saisis dans le même
  // formulaire. RÈGLE : "il faut que ce soit fait en simultané" — pas une
  // boucle qui revient au formulaire à chaque véhicule.
  const handleBatchCheck = async () => {
    const valid = multiVehicles.filter(v => v.identifier.trim());
    if (!valid.length) return;
    if (isPro === null) { alert("Veuillez indiquer si vous êtes particulier ou professionnel."); return; }
    if (isPro === true && siret.replace(/\s/g, "").length < 9) { alert("Veuillez renseigner votre numéro SIRET."); return; }

    // v62 — "Traiter en lot" : crée le lot AVANT le traitement pour tagger
    // chaque dossier dès sa création — la Cotation simultanée qui suit
    // (si l'utilisateur enchaîne) retrouvera le même lot_id.
    let currentLotId = null;
    if (lotMode === "lot") {
      try {
        const lr = await apiPost("/api/dossier-lots", { vehicle_count: valid.length });
        if (lr?.success) currentLotId = lr.lot.id;
      } catch (e) { /* non bloquant */ }
    }

    const runOne = async (v) => {
      const cleaned = v.identifier.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
      try {
        const proParams = `?is_pro=${isPro}&siret=${encodeURIComponent(siret)}&siret_name=${encodeURIComponent(siretInfo?.nom || "")}`;
        const data = await apiGet("/api/check/" + cleaned + proParams);
        if (!data.success || !data.data) {
          return { identifier: cleaned, ok: false, error: data.error || "Aucune correspondance trouvée." };
        }
        const vehicleResult = data.data;

        // v65 — Analyse photo IA par véhicule : absente du mode groupé jusqu'ici
        // (les photos étaient collectées par carte mais jamais envoyées à
        // l'analyse), contrairement au Contrôle individuel. Même logique :
        // l'ajustement de score est appliqué AVANT la sauvegarde, pour que
        // l'historique reflète directement le score final.
        if (v.photos?.length > 0) {
          try {
            const insp = await apiPost("/api/vehicle/visual-inspection", {
              images: v.photos,
              make: vehicleResult.make, model: vehicleResult.model, year: vehicleResult.year, fuel: vehicleResult.fuel,
              km: vehicleResult.km,
            });
            if (insp?.success) {
              if (typeof insp.score_delta === "number" && insp.score_delta !== 0 && vehicleResult.score != null) {
                vehicleResult.score = Math.max(0, Math.min(100, vehicleResult.score + insp.score_delta));
              }
              vehicleResult.visual_inspection = insp;
            }
          } catch (e) { /* inspection non bloquante — le contrôle reste valable sans elle */ }
        }

        let dossierId = null;
        try {
          const dres = await apiPost("/api/dossiers/find-or-create", {
            vin: vehicleResult.vin || (cleaned.length === 17 ? cleaned : undefined),
            plaque: cleaned.length !== 17 ? cleaned : undefined,
            make: vehicleResult.make, model: vehicleResult.model, year: vehicleResult.year, fuel: vehicleResult.fuel,
            lot_id: currentLotId || undefined,
          });
          if (dres?.success) {
            dossierId = dres.dossier.id;
            apiPost(`/api/dossiers/${dossierId}/operations`, { type: "check", payload: vehicleResult }).catch(() => {});
            // v48/v65 — HistoVec, CarVertical et photos propres à CE véhicule,
            // désormais saisis par carte dans le formulaire (voir plus bas) —
            // rattachés au bon dossier, pas partagés entre tous les véhicules du lot.
            if (v.hvData) apiPost(`/api/dossiers/${dossierId}/files`, { type: "histovec", payload: v.hvData }).catch(() => {});
            if (v.cvData) apiPost(`/api/dossiers/${dossierId}/files`, { type: "carvertical", payload: v.cvData }).catch(() => {});
            if (v.photos?.length) apiPost(`/api/dossiers/${dossierId}/files`, { type: "photo", payload: { images: v.photos, count: v.photos.length } }).catch(() => {});
          }
        } catch (e) { /* non bloquant */ }
        return { identifier: cleaned, ok: true, vehicleResult, dossierId, hvData: v.hvData, cvData: v.cvData, photos: v.photos };
      } catch (e) {
        return { identifier: cleaned, ok: false, error: e?.message || "Erreur réseau" };
      }
    };

    setBatchRunning(true);
    const settled = await Promise.all(valid.map(runOne));
    setBatchRunning(false);
    setBatchResults(settled);
    setBatchResultIndex(0);
    setStep("batchResult");

    // v65 — Passerelle vers la Cotation simultanée (même principe que
    // window.__viq_vin_prefill, déjà utilisé ailleurs dans ce fichier).
    // AVANT : seuls vin/plaque/make/model/year/fuel/dossierId passaient —
    // km, boîte, finition, puissance, HistoVec, CarVertical, photos et
    // l'analyse IA étaient perdus, forçant une ressaisie complète à
    // l'Estimation. Tout ce qui est déjà connu doit être repris.
    const okOnes = settled.filter(r => r.ok).map(r => ({
      vin: r.vehicleResult.vin, plaque: r.identifier.length !== 17 ? r.identifier : null,
      make: r.vehicleResult.make, model: r.vehicleResult.model, year: r.vehicleResult.year, fuel: r.vehicleResult.fuel,
      km: r.vehicleResult.km || r.vehicleResult.mileage || null,
      gearbox: r.vehicleResult.gearbox || null,
      finition: r.vehicleResult.finition || null,
      power_cv: r.vehicleResult.power_cv || null,
      dossierId: r.dossierId,
      hvData: r.hvData || null, cvData: r.cvData || null, photos: r.photos || [],
      visualInspection: r.vehicleResult.visual_inspection || null,
    }));
    if (okOnes.length) window.__viq_batch_check_results = okOnes;
  };

  const handleCheck = async () => {
    const minLen = inputMode === "vin" ? 17 : 7;
    if (immat.length < minLen) return;
    if (isPro === null) { alert("Veuillez indiquer si vous êtes particulier ou professionnel."); return; }
    if (isPro === true && siret.replace(/\s/g,"").length < 9) { alert("Veuillez renseigner votre numéro SIRET."); return; }

    setCheckError(null);
    // ── Contrôle VIN/plaque : GRATUIT (acquisition / aimant d'audience) ──
    // Le coût marginal pour VehicleIQ (~0,15 € : SIV + tokens) est absorbé,
    // c'est un investissement délibéré pour la captation d'utilisateurs.
    const SERVICE_COST = 0;
    // Pas de vérification de solde, pas de débit : on débloque directement.
    setPaid(true);
    if (onWalletChange) onWalletChange();

    setStep("loading");
    let i = 0;
    const t = setInterval(() => { setLoadStep(prev => Math.min(prev + 1, 4)); }, 3000);
    const cleanPlaque = immat.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    try {
      const proParams = isPro !== null ? `?is_pro=${isPro}&siret=${encodeURIComponent(siret)}&siret_name=${encodeURIComponent(siretInfo?.nom||"")}` : "";
      const data = await apiGet("/api/check/" + cleanPlaque + proParams);
      clearInterval(t); setLoadStep(4);
      if (!data.success || !data.data) {
        setStep("input");
        setCheckError({
          text: data.code === "SERVICE_UNAVAILABLE"
            ? "Le service de décodage est momentanément indisponible. Réessayez dans quelques minutes."
            : (data.error || `Aucune correspondance trouvée pour cette ${inputMode === "vin" ? "VIN" : "plaque"}. Vérifiez la saisie.`),
          retryable: data.code === "SERVICE_UNAVAILABLE",
        });
        return;
      }
      const vehicleResult = data.data;
      setSearchId(data.search_id || null);

      // v37 — RÈGLE 3/13 : le dossier est résolu AVANT toute autre opération,
      // jamais reconstruit après coup. Sans VIN ni plaque exploitable
      // (théoriquement impossible ici puisqu'on vient de décoder), on ne
      // bloque pas le contrôle lui-même — seule la persistance en dossier
      // est sautée (règle 4 : reste alors une simple recherche, pas un dossier).
      let resolvedDossierId = null;
      try {
        const dres = await apiPost("/api/dossiers/find-or-create", {
          vin: vehicleResult.vin, plaque: cleanPlaque,
          make: vehicleResult.make, model: vehicleResult.model, year: vehicleResult.year, fuel: vehicleResult.fuel,
        });
        if (dres?.success) { resolvedDossierId = dres.dossier.id; setDossierId(resolvedDossierId); }
      } catch (e) { /* non bloquant — le contrôle reste valable sans dossier persistant */ }

      // v26 — Si des photos ont été ajoutées AVANT de cliquer "Analyser ce
      // véhicule", l'inspection visuelle se déclenche maintenant dans la même
      // action (au lieu d'une étape séparée après le résultat) — son ajustement
      // de score est appliqué AVANT de sauvegarder l'historique, pour que
      // l'historique reflète directement le score final et non un score
      // intermédiaire qui serait ensuite corrigé silencieusement.
      let inspectionResult = null;
      if (inspectionPhotos.length > 0) {
        try {
          const insp = await apiPost("/api/vehicle/visual-inspection", {
            images: inspectionPhotos,
            make: vehicleResult.make, model: vehicleResult.model, year: vehicleResult.year, fuel: vehicleResult.fuel,
            km: vehicleResult.km,
          });
          if (insp?.success) {
            inspectionResult = insp;
            if (typeof insp.score_delta === "number" && insp.score_delta !== 0 && vehicleResult.score != null) {
              vehicleResult.score = Math.max(0, Math.min(100, vehicleResult.score + insp.score_delta));
            }
            // v27 — L'inspection visuelle fait désormais PARTIE du rapport
            // (une seule vérité), pas une donnée à côté qui pourrait se perdre
            // entre l'écran, l'historique local et la base de données.
            vehicleResult.visual_inspection = insp;
          }
        } catch (e) { /* inspection non bloquante — le contrôle reste valable sans elle */ }
      }

      // v37 — RÈGLE 5/9 : le contrôle s'enregistre comme un ÉVÉNEMENT rattaché
      // au dossier (jamais l'inverse). Fait après la finalisation complète du
      // rapport (inspection visuelle incluse) pour que l'opération porte la
      // vérité définitive, pas un état intermédiaire.
      if (resolvedDossierId) {
        apiPost(`/api/dossiers/${resolvedDossierId}/operations`, { type: "check", payload: vehicleResult }).catch(() => {});
        // v38 FIX — RÈGLE 6 : les photos appartiennent au dossier, jamais
        // seulement au payload d'une opération. Sans ça, "Continuer" ne
        // pouvait jamais les retrouver pour les proposer à l'étape suivante.
        if (inspectionPhotos.length > 0) {
          apiPost(`/api/dossiers/${resolvedDossierId}/files`, {
            type: "photo",
            payload: { images: inspectionPhotos, count: inspectionPhotos.length, visual_inspection: inspectionResult || undefined },
          }).catch(() => {});
        }
      }

      // v27 — Synchroniser la base (source de vérité de l'historique) avec le
      // rapport final AVANT même de l'afficher : le score/la vérité stockée en
      // base ne doit jamais être en retard sur ce qui est réellement calculé.
      if (data.search_id) {
        apiPatch(`/api/search/${data.search_id}`, { result: vehicleResult }).catch(() => {});
      }

      // Sauvegarder le check complet dans l'historique local
      try {
        const existing = JSON.parse(localStorage.getItem("viq_history") || "[]");
        const inputData = {
          plaque: inputMode === "plaque" ? cleanPlaque : "",
          vin:    inputMode === "vin"    ? cleanPlaque : "",
        };
        const entry = {
          // v28 FIX : utiliser l'ID de la base (search_id) quand il est
          // disponible, PAS Date.now(). Avant ce correctif, l'entrée locale et
          // l'entrée distante (même contrôle) avaient des ID totalement
          // différents (timestamp local vs ID serial DB) — la déduplication ne
          // pouvait alors compter que sur une empreinte "type + date arrondie à
          // la minute", fragile par nature. Or l'inspection visuelle peut
          // prendre 10 à 30 secondes AVANT cette sauvegarde : largement de quoi
          // déborder sur la minute suivante et casser l'empreinte → le même
          // contrôle apparaissait deux fois dans l'historique (score avant/après
          // inspection visuelle, ex. 52/100 et 47/100 pour le même véhicule).
          id: data.search_id || Date.now(),
          date: new Date().toISOString(),
          type: "check",
          answers: inputData,
          data: inputData,
          results: vehicleResult,
          result: vehicleResult,
        };
        // Dédupliquer (8s)
        const last = existing[0];
        if (!last || last.type !== "check" || (Date.now() - new Date(last.date).getTime()) >= 8000) {
          localStorage.setItem("viq_history", JSON.stringify([entry, ...existing].slice(0, 50)));
        }
      } catch(e) {}
      setKnownIssues(null); setEnrichDone(false);
      if (inspectionResult) setVisualInspection(inspectionResult);
      setTimeout(() => {
        setResult(vehicleResult);
        setStep("result");
        // v28 FIX : passer le delta visuel directement en paramètre plutôt que
        // de compter sur le state React `visualInspection` — ce state n'est pas
        // garanti à jour au moment de cet appel (mise à jour asynchrone, closure
        // figée au moment où ce setTimeout a été programmé), ce qui pouvait
        // faire recalculer enrichVehicle un score SANS le bon ajustement visuel,
        // écrasant silencieusement le score correct par un score différent.
        enrichVehicle(vehicleResult, null, undefined, inspectionResult?.score_delta);
        if (!paid) setTimeout(() => setShowGate(true), 400);

      }, 400);
    } catch (err) {
      clearInterval(t); setStep("input");
      // v7 : message inline clair (plus d'alert() brut avec un code HTTP) —
      // distingue panne fournisseur (réessayer plus tard) de VIN/plaque non
      // reconnue (vérifier la saisie).
      setCheckError({
        text: err.code === "SERVICE_UNAVAILABLE"
          ? "Le service de décodage est momentanément indisponible. Réessayez dans quelques minutes."
          : err.code === "NOT_FOUND"
            ? `Aucune correspondance trouvée pour cette ${inputMode === "vin" ? "VIN" : "plaque"}. Vérifiez la saisie.`
            : (err.message || "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez."),
        retryable: true,
      });
    }
  };

  // Enrichissement : fusion HistoVec (optionnel) + synthèse des problèmes connus
  // (recherche web) + recalcul du score VIQ.AI. Appelé après le contrôle, puis à
  // chaque import HistoVec. Non bloquant : un échec n'altère pas le rapport.
  const enrichVehicle = async (baseReport, hvObj, cvObj, visualDeltaOverride) => {
    const rep = baseReport || result;
    if (!rep || !rep.make || rep.make === "—") { setEnrichDone(true); return; }
    setIssuesLoading(true);
    try {
      const r = await apiPost("/api/vehicle/enrich", {
        report: rep,
        histovec: hvObj || hvData || undefined,
        carvertical: cvObj || cvData || undefined,
        mileage: rep.mileage || rep.km_reported || undefined,
        want_issues: true,
        // v28 FIX : accepte le delta en paramètre explicite (prioritaire) plutôt
        // que de compter uniquement sur le state React `visualInspection`, qui
        // n'est pas garanti à jour au moment de l'appel (mise à jour
        // asynchrone + closure figée quand enrichVehicle est invoqué juste
        // après une inspection visuelle dans le même enchaînement d'actions).
        visual_score_delta: visualDeltaOverride ?? (rep.visual_inspection?.score_delta ?? visualInspection?.score_delta) ?? undefined,
      });
      if (r && r.success) {
        if (r.report) setResult(prev => ({ ...(prev || {}), ...r.report }));
        if (r.known_issues && r.known_issues.found) setKnownIssues(r.known_issues);
        // Synchroniser l'entrée d'historique avec le score enrichi (intègre le
        // risque) pour que l'historique affiche le même score strict.
        // v9 : on identifie l'entrée à mettre à jour par VIN/plaque — l'ancienne
        // logique supposait à tort que c'était toujours hist[0] (la plus
        // récente), ce qui cassait dès qu'un autre contrôle avait eu lieu entre
        // temps : l'historique continuait d'afficher un score générique/ancien
        // au lieu du score enrichi (cas observé : Peugeot 508, 80/100 affiché
        // contre 47/100 réel après import HistoVec).
        try {
          const hist = JSON.parse(localStorage.getItem("viq_history") || "[]");
          if (r.report && r.report.score != null) {
            const targetVin = String(rep.vin || r.report.vin || "").toUpperCase().replace(/\s/g, "");
            const targetPlaque = String(rep.plaque || "").toUpperCase().replace(/\s/g, "");
            let touched = false;
            let mergedForDb = null;
            const next = hist.map(h => {
              if (h.type !== "check") return h;
              const hVin = String((h.results && h.results.vin) || (h.data && h.data.vin) || "").toUpperCase().replace(/\s/g, "");
              const hPlaque = String((h.data && h.data.plaque) || "").toUpperCase().replace(/\s/g, "");
              const matches = (targetVin && hVin && hVin === targetVin) || (targetPlaque && hPlaque && hPlaque === targetPlaque);
              if (!matches) return h;
              touched = true;
              // v27 : préserver l'inspection visuelle déjà présente (rep peut la
              // contenir si elle a eu lieu dans le même passage de handleCheck) —
              // le rapport enrichi ne doit jamais faire "oublier" une donnée déjà
              // acquise, une seule vérité fusionnée progressivement.
              const mergedResult = { ...(h.results || {}), ...rep, ...r.report };
              mergedForDb = mergedResult;
              return { ...h, results: mergedResult, result: mergedResult };
            });
            if (touched) localStorage.setItem("viq_history", JSON.stringify(next));
            // v27 — Synchroniser la base : sans ça, /api/users/me continue de
            // renvoyer le score INITIAL pour toujours (la base n'est jamais
            // réécrite après le contrôle initial), et ce score périmé écrase le
            // bon score local dès qu'on ouvre l'historique ("le serveur est
            // source de vérité" dans la logique de fusion) — c'était la cause
            // réelle du score erroné dans l'historique.
            if (searchId && mergedForDb) {
              apiPatch(`/api/search/${searchId}`, { result: mergedForDb }).catch(() => {});
            }
          }
        } catch (e) {}
      }
    } catch (e) { /* enrichissement non bloquant */ }
    finally { setIssuesLoading(false); setEnrichDone(true); }
  };

  // v41 — Avant, si `dossierId` n'était pas encore résolu (find-or-create en
  // cours, ou ayant silencieusement échoué), l'import HistoVec/CarVertical
  // n'était JAMAIS écrit dans `vehicle_files` : il ne vivait que dans l'état
  // local de ce composant, perdu à la fermeture de la page. On retente ici la
  // résolution avec les identifiants déjà disponibles sur cette page avant
  // d'abandonner — l'import ne doit jamais silencieusement disparaître.
  const persistDossierFile = async (type, payload) => {
    let id = dossierId;
    if (!id) {
      try {
        const cleanPlaqueNow = inputMode === "plaque" ? immat.replace(/[^a-zA-Z0-9]/g, "").toUpperCase() : null;
        const vinNow = result?.vin || (inputMode === "vin" ? immat.replace(/\s/g, "").toUpperCase() : null);
        if (vinNow || cleanPlaqueNow) {
          const dres = await apiPost("/api/dossiers/find-or-create", {
            vin: vinNow, plaque: cleanPlaqueNow, make: result?.make, model: result?.model, year: result?.year, fuel: result?.fuel,
          });
          if (dres?.success) { id = dres.dossier.id; setDossierId(id); }
        }
      } catch (e) { console.warn(`[Dossier VIQ] résolution dossier échouée pour "${type}" :`, e.message); }
    }
    if (id) apiPost(`/api/dossiers/${id}/files`, { type, payload }).catch(e => console.warn(`[Dossier VIQ] échec enregistrement "${type}" :`, e.message));
    else console.warn(`[Dossier VIQ] "${type}" non enregistré — aucun dossier résolu (VIN/plaque manquant).`);
  };
  const onHistovecImported = (hv) => {
    setHvData(hv);
    enrichVehicle(result, hv, cvData);
    persistDossierFile("histovec", hv);
  };
  const onCarVerticalImported = (cv) => {
    setCvData(cv);
    enrichVehicle(result, hvData, cv);
    persistDossierFile("carvertical", cv);
  };

  // v25 — Ajout de photos (galerie ou appareil), converties en base64 pour l'envoi.
  const handleInspectionPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10 - inspectionPhotos.length);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => setInspectionPhotos(prev => prev.length < 10 ? [...prev, reader.result] : prev);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const runVisualInspection = async () => {
    if (!inspectionPhotos.length || inspectionLoading) return;
    setInspectionLoading(true); setInspectionError(null);
    try {
      const data = await apiPost("/api/vehicle/visual-inspection", {
        images: inspectionPhotos,
        make: result?.make, model: result?.model, year: result?.year, fuel: result?.fuel,
        km: hvData?.km || cvData?.km,
      });
      if (data?.success) {
        setVisualInspection(data);
        // Le score affiché intègre l'ajustement visuel — cohérent avec le
        // reste de l'app (un seul score VIQ.AI®, jamais deux chiffres concurrents).
        if (typeof data.score_delta === "number" && data.score_delta !== 0 && result?.score != null) {
          const newScore = Math.max(0, Math.min(100, result.score + data.score_delta));
          setResult(prev => ({ ...prev, score: newScore, visual_inspection: data }));
          // v26/v27 — Synchroniser l'historique local ET la base (source de
          // vérité de /api/users/me) avec le score ajusté ET le détail complet
          // de l'inspection (SVG + texte) — sans quoi, historique et PDF ne
          // montrent que l'ancien score, sans jamais l'inspection visuelle.
          try {
            const hist = JSON.parse(localStorage.getItem("viq_history") || "[]");
            const targetVin = String(result.vin || "").toUpperCase().replace(/\s/g, "");
            const targetPlaque = String(result.plaque || "").toUpperCase().replace(/\s/g, "");
            let touched = false;
            let mergedForDb = null;
            const next = hist.map(h => {
              if (h.type !== "check") return h;
              const hVin = String((h.results && h.results.vin) || (h.data && h.data.vin) || "").toUpperCase().replace(/\s/g, "");
              const hPlaque = String((h.data && h.data.plaque) || "").toUpperCase().replace(/\s/g, "");
              const matches = (targetVin && hVin && hVin === targetVin) || (targetPlaque && hPlaque && hPlaque === targetPlaque);
              if (!matches) return h;
              touched = true;
              const mergedResult = { ...(h.results || {}), score: newScore, visual_inspection: data };
              mergedForDb = mergedResult;
              return { ...h, results: mergedResult, result: mergedResult };
            });
            if (touched) localStorage.setItem("viq_history", JSON.stringify(next));
            if (searchId && mergedForDb) {
              apiPatch(`/api/search/${searchId}`, { result: mergedForDb }).catch(() => {});
            } else if (searchId) {
              // Pas d'entrée locale correspondante trouvée (rare) — on met quand
              // même à jour la base avec ce qu'on a de plus complet.
              apiPatch(`/api/search/${searchId}`, { result: { ...result, score: newScore, visual_inspection: data } }).catch(() => {});
            }
          } catch (e) {}
        }
      } else {
        setInspectionError(data?.message || data?.error || "Analyse impossible pour le moment.");
      }
    } catch (e) {
      setInspectionError(e?.message || "Erreur réseau — réessayez dans quelques instants.");
    } finally {
      setInspectionLoading(false);
    }
  };

  const sections = result ? [
    { id: "identity", title: "Identité du véhicule", icon: "🪪", content: (
      <div className="data-grid">
        {[["Marque / Modèle", `${result.make||"—"} ${result.model||""}`], ["Motorisation", result.engine||result.fuel||"—"], ["Carburant", result.fuel||"—"],
          ["Boîte de vitesses", result.transmission||result.gearbox||"—"], ["Couleur", result.color||"—"], ["Année", result.year||"—"],
          ["Kilomètres", result.mileage ? result.mileage.toLocaleString()+" km" : (result.km_reported ? Number(result.km_reported).toLocaleString()+" km" : "—")], ["Propriétaires", result.owners||result.nb_owners||"—"],
        ].map(([l, v]) => <div key={l} className="data-item"><span className="data-label">{l}</span><span className="data-value">{v||"—"}</span></div>)}
      </div>
    )},
    { id: "accidents", title: "Données techniques complémentaires", icon: "🔧", content: (
      <div style={{ paddingTop: ".8rem", display: "flex", flexDirection: "column", gap: ".6rem" }}>
        {[
          { label: "Code moteur", val: result.engine_code || "—", ok: true },
          { label: "Type mine (CNIT)", val: result.type_mine || "—", ok: true },
          { label: "Cylindres", val: result.cylinders ? result.cylinders + " cylindres" : "—", ok: true },
          { label: "Poids total (PTAC)", val: result.weight || "—", ok: true },
          { label: "Puissance réelle", val: result.power_kw || "—", ok: true },
        ].filter(i => i.val !== "—").map(item => (
          <div key={item.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: ".87rem" }}>{item.label}</span>
            <span style={{ fontSize: ".85rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>{item.val}</span>
          </div>
        ))}
        <div style={{ marginTop: ".5rem", padding: ".6rem .8rem", background: "rgba(30,136,229,.06)", border: "1px solid rgba(30,136,229,.2)", borderRadius: 6, fontSize: ".75rem", color: "var(--sub)", lineHeight: 1.6 }}>
          💡 Pour consulter l'historique des sinistres, CT et kilométrages déclarés, visitez{" "}
          <a href="https://histovec.interieur.gouv.fr" target="_blank" rel="noreferrer" style={{ color: "#90CAF9" }}>HistoVec.gouv.fr</a> (gratuit, officiel).
        </div>
      </div>
    )},
    { id: "history", title: "Historique de propriété", icon: "📋", content: (
      <ul className="timeline">
        {(result.registrations||[]).length > 0 ? (result.registrations||[]).map((r, i) => (
          <li key={i} className="timeline-item">
            <span className="timeline-date">{r.date}</span>
            <span className="timeline-text">{r.text}</span>
          </li>
        )) : <li className="timeline-item"><span className="timeline-text" style={{color:"var(--sub)"}}>Données d'historique non disponibles pour ce véhicule.</span></li>}
      </ul>
    )},
    { id: "service", title: "Entretien & Réparations", icon: "🔧", content: (
      <ul className="timeline">
        {(result.services||[]).length > 0 ? (result.services||[]).map((s, i) => (
          <li key={i} className="timeline-item">
            <span className="timeline-date">{s.date?.split("–")[0] || ""}</span>
            <span className="timeline-text">{s.text}</span>
          </li>
        )) : <li className="timeline-item"><span className="timeline-text" style={{color:"var(--sub)"}}>Aucun historique d'entretien disponible.</span></li>}
      </ul>
    )},
  ] : [];

  return (
    <>
      <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="panel panel-wide" style={{ maxHeight:"92vh", display:"flex", flexDirection:"column" }}>
          <div className="panel-header" style={{ flexShrink:0 }}>
            <div>
              <div className="panel-title">Contrôler un véhicule</div>
              <div className="panel-sub">Décodage plaque · API Plaque immatriculation</div>
            </div>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="panel-body" style={{ overflowY:"auto", flex:1, minHeight:0 }}>

            {step === "input" && (
              <>
                <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", background: "rgba(30,136,229,.08)", border: "1px solid rgba(30,136,229,.25)", borderRadius: 8, padding: ".5rem 1rem", fontSize: ".75rem", color: "var(--sub)" }}>
                    <APIPlaqueSVG />
                  </div>
                </div>
                {/* v45 — Contrôle simultané NATIF : un menu déroulant choisit le
                    nombre de véhicules, le champ d'identification se duplique
                    directement ici — plus de modale séparée. */}
                {maxSimul > 1 && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: ".6rem", marginBottom: "1rem" }}>
                    <label style={{ fontSize: ".78rem", color: "var(--sub)", fontWeight: 600 }}>Nombre de véhicules :</label>
                    <select
                      value={vehicleCount}
                      onChange={e => {
                        const n = parseInt(e.target.value, 10);
                        setVehicleCount(n);
                        setMultiVehicles(prev => {
                          const next = prev.slice(0, n);
                          while (next.length < n) next.push(blankBatchVehicle());
                          return next;
                        });
                        setCheckError(null);
                      }}
                      style={{ padding: ".35rem .6rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".82rem", fontWeight: 600 }}>
                      {Array.from({ length: maxSimul }, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>{n === 1 ? "1 (simple)" : `${n} en simultané`}</option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Toggle Plaque / VIN */}
                <div style={{ display:"flex", gap:".4rem", marginBottom:"1rem", background:"var(--card)", borderRadius:8, padding:3 }}>
                  {[["plaque","🔡 Plaque"],["vin","🔢 VIN"]].map(([t,l]) => (
                    <button key={t} onClick={() => { setInputMode(t); setImmat(""); setMultiVehicles(Array.from({ length: vehicleCount }, blankBatchVehicle)); setHvData(null); setKnownIssues(null); setCheckError(null); }}
                      style={{ flex:1, padding:".4rem .6rem", borderRadius:6, border:"none", cursor:"pointer",
                        background: inputMode===t ? "var(--deep)" : "transparent",
                        color: inputMode===t ? "var(--text)" : "var(--sub)",
                        fontSize:".82rem", fontWeight: inputMode===t ? 600 : 400,
                        fontFamily:"var(--font-body)", transition:"all .2s" }}>
                      {l}
                    </button>
                  ))}
                </div>
                {vehicleCount === 1 ? (
                  <>
                  {inputMode === "vin" ? (
                    <div className="field">
                      <label style={{ fontWeight: 600 }}>Numéro VIN (17 caractères)</label>
                      <input className="input" maxLength={17} placeholder="VF1DZ0N0641118804"
                        value={immat} onChange={e => { setImmat(e.target.value.replace(/\s/g,"").toUpperCase()); setLookupMsg(null); }}
                        onKeyDown={e => e.key === "Enter" && immat.length === 17 && handleCheck()}
                        style={{ fontSize: "1rem", textAlign: "center", letterSpacing: "2px", fontFamily: "var(--font-mono)" }} />
                      <div style={{ fontSize: ".73rem", color: "var(--sub)", marginTop: ".4rem", textAlign: "center" }}>
                        17 caractères · Visible sur le pare-brise ou la carte grise (case E)
                      </div>
                    </div>
                  ) : (
                    <div className="field">
                      <label style={{ fontWeight: 600 }}>Plaque d'immatriculation</label>
                      <input className="input input-immat" maxLength={9} placeholder="AB-123-CD"
                        value={immat} onChange={e => { setImmat(e.target.value.replace(/[^a-zA-Z0-9-]/g, "").toUpperCase()); setLookupMsg(null); }}
                        onKeyDown={e => e.key === "Enter" && immat.length >= 7 && handleCheck()}
                        style={{ fontSize: "1.3rem", textAlign: "center", letterSpacing: "4px", fontFamily: "var(--font-mono)", fontWeight: 700 }} />
                      <div style={{ fontSize: ".73rem", color: "var(--sub)", marginTop: ".4rem", textAlign: "center" }}>
                        Format SIV · Ex : AB-123-CD · Le VIN est récupéré automatiquement
                      </div>
                    </div>
                  )}
                  {/* v56 — Note explicite : sans elle, la fonctionnalité passait
                      inaperçue (remonté comme un bug de perte de données alors
                      que l'import existe mais n'est pas assez visible). */}
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", textAlign: "center", marginTop: ".5rem", fontStyle: "italic" }}>
                    💡 Véhicule déjà traité ? Saisissez la plaque d'un Dossier VIQ existant pour en importer les photos et le HistoVec.
                  </div>
                  {/* v55 — Reprendre un véhicule déjà connu (photos, HistoVec)
                      sans passer par le Dossier VIQ lui-même. */}
                  {immat.replace(/[^a-zA-Z0-9]/g, "").length >= 6 && (
                    <div style={{ textAlign: "center", marginTop: ".5rem", marginBottom: "1rem" }}>
                      <button type="button" onClick={importExistingDossier} disabled={lookupBusy}
                        style={{ background: "none", border: "none", cursor: lookupBusy ? "wait" : "pointer", fontSize: ".76rem", color: "var(--accent)", fontWeight: 600, textDecoration: "underline", opacity: lookupBusy ? .6 : 1 }}>
                        {lookupBusy ? "Recherche…" : "📂 Importer les données du dossier existant"}
                      </button>
                      {lookupMsg && <div style={{ fontSize: ".72rem", color: lookupMsg.startsWith("✓") ? "var(--green)" : "var(--muted)", marginTop: ".3rem" }}>{lookupMsg}</div>}
                    </div>
                  )}
                  </>
                ) : (
                  <div className="field">
                    <label style={{ fontWeight: 600 }}>{inputMode === "vin" ? "Véhicules (VIN)" : "Véhicules (plaque)"} ({vehicleCount})</label>
                    {/* v62 — Lot vs individuel */}
                    <div style={{ display: "flex", gap: ".5rem", margin: ".5rem 0" }}>
                      {[["individual", "🔢 Individuel", "Chaque véhicule reste indépendant."], ["lot", "📦 En lot", "Les dossiers créés seront reliés (prix global à définir à l'Estimation)."]].map(([id, label, hint]) => (
                        <button key={id} type="button" onClick={() => setLotMode(id)}
                          style={{ flex: 1, textAlign: "left", padding: ".5rem .7rem", borderRadius: 8, cursor: "pointer",
                            border: "1.5px solid " + (lotMode === id ? "var(--accent)" : "var(--border)"),
                            background: lotMode === id ? "var(--accent-soft)" : "var(--card)" }}>
                          <div style={{ fontSize: ".76rem", fontWeight: 700, color: lotMode === id ? "var(--accent)" : "var(--text)" }}>{label}</div>
                          <div style={{ fontSize: ".64rem", color: "var(--muted)", marginTop: ".1rem" }}>{hint}</div>
                        </button>
                      ))}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: ".8rem", marginTop: ".4rem" }}>
                      {multiVehicles.map((v, i) => (
                        <div key={i} style={{ padding: ".8rem", border: "1.5px solid var(--border)", borderRadius: 10, background: "var(--card)" }}>
                          <input className="input" maxLength={inputMode === "vin" ? 17 : 9}
                            placeholder={inputMode === "vin" ? `VIN #${i + 1}` : `Plaque #${i + 1} (ex : AB-123-CD)`}
                            value={v.identifier}
                            onChange={e => {
                              const cleaned = inputMode === "vin" ? e.target.value.replace(/\s/g, "").toUpperCase() : e.target.value.replace(/[^a-zA-Z0-9-]/g, "").toUpperCase();
                              setMultiVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, identifier: cleaned } : x));
                            }}
                            style={{ fontSize: "1rem", textAlign: "center", letterSpacing: "2px", fontFamily: "var(--font-mono)", fontWeight: 600, width: "100%", boxSizing: "border-box" }} />

                          {/* v48/v65 — HistoVec, CarVertical et photos propres à CE véhicule */}
                          <div style={{ marginTop: ".6rem" }}>
                            <HistovecImport onImported={hv => setMultiVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, hvData: hv } : x))} compact />
                            {v.hvData && <HistovecSummary hv={v.hvData} />}
                          </div>
                          <div style={{ marginTop: ".6rem" }}>
                            <CarVerticalImport onImported={cv => setMultiVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, cvData: cv } : x))} compact plaque={v.identifier} />
                            {v.cvData && <CarVerticalSummary cv={v.cvData} />}
                          </div>
                          <div style={{ marginTop: ".5rem", display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
                            <input type="file" accept="image/*" multiple id={`batch-photo-${i}`} style={{ display: "none" }}
                              onChange={async e => {
                                const files = Array.from(e.target.files || []).slice(0, 10 - v.photos.length);
                                const urls = (await Promise.all(files.map(f => compressImageFile(f)))).filter(Boolean);
                                setMultiVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, photos: [...x.photos, ...urls].slice(0, 10) } : x));
                                e.target.value = "";
                              }} />
                            <label htmlFor={`batch-photo-${i}`} style={{ padding: ".35rem .8rem", borderRadius: 8, border: "1px solid var(--border)", cursor: "pointer", background: "var(--white)", color: "var(--sub)", fontSize: ".76rem", fontWeight: 600 }}>
                              🖼 Photos ({v.photos.length}/10)
                            </label>
                            {v.photos.map((p, pi) => (
                              <div key={pi} style={{ position: "relative", width: 36, height: 36, borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)" }}>
                                <img src={p} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                <button type="button" onClick={() => setMultiVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, photos: x.photos.filter((_, ppi) => ppi !== pi) } : x))}
                                  style={{ position: "absolute", top: 0, right: 0, background: "rgba(0,0,0,.6)", color: "#fff", border: "none", width: 14, height: 14, fontSize: ".6rem", cursor: "pointer", lineHeight: 1 }}>✕</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: ".73rem", color: "var(--sub)", marginTop: ".5rem", textAlign: "center" }}>
                      Analysés en parallèle · chaque véhicule reçoit son propre Dossier VIQ
                    </div>
                  </div>
                )}

                {checkError && (
                  <div style={{
                    display: "flex", alignItems: "flex-start", gap: ".5rem",
                    background: "rgba(255,60,92,.06)", border: "1px solid rgba(255,60,92,.25)",
                    borderRadius: 8, padding: ".65rem .8rem", marginTop: ".7rem", marginBottom: ".2rem",
                  }}>
                    <span style={{ fontSize: "1rem", lineHeight: 1 }}>{checkError.retryable ? "⏳" : "⚠️"}</span>
                    <div style={{ fontSize: ".8rem", color: "var(--text)" }}>
                      {checkError.text}
                      {checkError.retryable && <div style={{ fontSize: ".72rem", color: "var(--sub)", marginTop: ".2rem" }}>Vous pouvez réessayer directement ci-dessus.</div>}
                    </div>
                  </div>
                )}

                <SiretBlock isPro={isPro} setIsPro={setIsPro} siret={siret} setSiret={setSiret} siretInfo={siretInfo} setSiretInfo={setSiretInfo} />
                {vehicleCount === 1 && (
                  <>
                    <div style={{ margin: ".9rem 0" }}>
                      <HistovecImport onImported={onHistovecImported} compact />
                      <HistovecSummary hv={hvData} />
                    </div>
                    <div style={{ margin: ".9rem 0" }}>
                      <CarVerticalImport onImported={onCarVerticalImported} compact vin={result?.vin} plaque={immat} />
                      <CarVerticalSummary cv={cvData} />
                    </div>

                    {/* v26 — Photos déplacées en amont : l'inspection visuelle se déclenche
                        désormais avec "Analyser ce véhicule", au lieu d'être une étape
                        séparée après le résultat. Réservé aux comptes Pro. */}
                    {((user?.siret && user.siret.length > 3) || user?.is_pro) && (
                      <div style={{ margin: ".9rem 0", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: ".8rem 1rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".4rem" }}>
                          <span style={{ fontWeight: 700, fontSize: ".82rem" }}>📷 Photos du véhicule (optionnel)</span>
                          <span style={{ fontSize: ".6rem", fontWeight: 700, color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", padding: ".1rem .4rem", borderRadius: 4, textTransform: "uppercase" }}>Pro</span>
                        </div>
                        <div style={{ fontSize: ".74rem", color: "var(--sub)", marginBottom: ".55rem" }}>
                          Ajoutez des photos pour que « Analyser ce véhicule » évalue aussi l'état réel du véhicule (zone par zone) et affine le score VIQ.AI® en conséquence.
                        </div>
                        <div style={{ display: "flex", gap: ".5rem", alignItems: "center", flexWrap: "wrap", marginBottom: ".5rem" }}>
                          <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                            🖼 Galerie
                            <input type="file" accept="image/*" multiple hidden onChange={handleInspectionPhotos} />
                          </label>
                          <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                            📷 Appareil photo
                            <input type="file" accept="image/*" capture="environment" hidden onChange={handleInspectionPhotos} />
                          </label>
                          <span style={{ fontSize: ".72rem", color: "var(--muted)" }}>{inspectionPhotos.length}/10</span>
                        </div>
                        {inspectionPhotos.length > 0 && (
                          <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
                            {inspectionPhotos.map((p, i) => (
                              <div key={i} style={{ position: "relative" }}>
                                <img src={p} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border)" }} />
                                <button onClick={() => setInspectionPhotos(prev => prev.filter((_, j) => j !== i))}
                                  style={{ position: "absolute", top: -6, right: -6, width: 16, height: 16, borderRadius: "50%", background: "var(--text)", color: "var(--bg)", border: "none", fontSize: ".6rem", cursor: "pointer", lineHeight: 1 }}>×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                <button
                  className={`btn btn-accent ${
                    (vehicleCount === 1
                      ? (inputMode === "vin" ? immat.length !== 17 : immat.length < 7)
                      : multiVehicles.filter(x => x.identifier.trim()).length === 0
                    ) || isPro === null || (isPro && siret.replace(/\s/g,"").length < 9) || batchRunning ? "btn-disabled" : ""
                  }`}
                  onClick={vehicleCount === 1 ? handleCheck : handleBatchCheck}>
                  {vehicleCount === 1 ? "🔍 Analyser ce véhicule →" : batchRunning ? "Analyse en cours…" : `🔍 Analyser ces ${multiVehicles.filter(x => x.identifier.trim()).length || vehicleCount} véhicules →`}
                </button>
                <div style={{ textAlign: "center", marginTop: ".8rem", fontSize: ".7rem", color: "var(--muted)" }}>
                  Données officielles SIV · Résultat en 0,4 secondes{inspectionPhotos.length > 0 ? " · Inspection visuelle incluse" : ""}
                </div>
              </>
            )}
            {step === "batchResult" && (() => {
              const results = batchResults || [];
              const r = results[batchResultIndex];
              const okCount = results.filter(x => x.ok).length;
              return (
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                    <div style={{ fontWeight: 700, fontSize: ".9rem" }}>{okCount}/{results.length} véhicule(s) analysé(s) avec succès</div>
                    {results.length > 1 && (
                      <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                        <button className="btn-icon" disabled={batchResultIndex <= 0} onClick={() => setBatchResultIndex(i => i - 1)} style={{ opacity: batchResultIndex <= 0 ? .35 : 1 }}>‹</button>
                        <span style={{ fontSize: ".76rem", color: "var(--muted)" }}>{batchResultIndex + 1}/{results.length}</span>
                        <button className="btn-icon" disabled={batchResultIndex >= results.length - 1} onClick={() => setBatchResultIndex(i => i + 1)} style={{ opacity: batchResultIndex >= results.length - 1 ? .35 : 1 }}>›</button>
                      </div>
                    )}
                  </div>

                  {r && !r.ok && (
                    <div style={{ padding: "1.2rem", borderRadius: 10, border: "1px solid rgba(176,90,58,.4)", background: "rgba(176,90,58,.05)" }}>
                      <div style={{ fontWeight: 700, fontSize: ".9rem" }}>{r.identifier}</div>
                      <div style={{ fontSize: ".82rem", color: "var(--red, #b03a3a)", marginTop: ".3rem" }}>⚠ {r.error}</div>
                    </div>
                  )}

                  {r && r.ok && (
                    <>
                      {/* En-tête façon fiche standard : nom, plaque, score */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", padding: "0 .2rem" }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: "1.1rem" }}>{r.vehicleResult.make} {r.vehicleResult.model}{r.vehicleResult.year ? ` · ${r.vehicleResult.year}` : ""}</div>
                          <div style={{ fontSize: ".78rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{r.identifier}</div>
                        </div>
                        {r.vehicleResult.score != null && (
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: "1.4rem", color: "var(--green)" }}>{r.vehicleResult.score}<span style={{ fontSize: ".7rem", color: "var(--muted)" }}>/100</span></div>
                            <div style={{ fontSize: ".62rem", color: "var(--muted)" }}>Score VIQ.AI®</div>
                          </div>
                        )}
                      </div>

                      {/* Détail identité + caractéristiques — même composant que l'onglet Véhicule du Dossier VIQ */}
                      <DossierVehicleTab detail={{ latestCheck: r.vehicleResult, dossier: { vin: r.vehicleResult.vin, plaque: r.identifier.length !== 17 ? r.identifier : null } }} />

                      {r.photos?.length > 0 && (
                        <div style={{ marginTop: "1rem" }}>
                          <div style={{ fontWeight: 700, fontSize: ".82rem", marginBottom: ".5rem" }}>📷 Photos ({r.photos.length})</div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))", gap: ".4rem" }}>
                            {r.photos.map((p, pi) => <img key={pi} src={p} alt="" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 6, border: "1px solid var(--border)" }} />)}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div style={{ display: "flex", gap: ".6rem", marginTop: "1.2rem" }}>
                    <button className="btn btn-outline" style={{ flex: 1, justifyContent: "center" }}
                      onClick={() => { setStep("input"); setBatchResults(null); setMultiVehicles(Array.from({ length: vehicleCount }, blankBatchVehicle)); }}>
                      Nouveau contrôle
                    </button>
                    <button className="btn btn-accent" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>
                      Fermer
                    </button>
                  </div>
                  {okCount > 0 && (
                    <button className="btn btn-accent" style={{ width: "100%", justifyContent: "center", marginTop: ".6rem", background: "var(--green)" }}
                      onClick={() => { if (onLaunchBatchSell) onLaunchBatchSell(); else onClose(); }}>
                      🔢 Coter {okCount > 1 ? "ces véhicules" : "ce véhicule"} maintenant →
                    </button>
                  )}
                </div>
              );
            })()}
            {step === "loading" && (
              <LoadingSteps
                steps={[
                  { label: inputMode==="vin" ? "Décodage du VIN…" : "Décodage de la plaque…", detail: "api.apiplaqueimmatriculation.com · SIV officiel France" },
                  { label: "Identification du véhicule…", detail: "Marque · Modèle · Date mise en circulation · Motorisation" },
                  { label: "Récupération des données techniques…", detail: "Couleur · Puissance · Boîte · CO₂ · Carrosserie · Poids" },
                  { label: "Calcul du score VIQ.AI®…", detail: "Score de fiabilité · Analyse du profil véhicule" },
                  { label: "Génération du rapport…", detail: "Mise en forme des données · Rapport complet prêt" },
                ]}
                activeIdx={loadStep}
              />
            )}
            {step === "result" && result && (
              <div className="vin-result">

                {/* ── En-tête véhicule — toujours visible ── */}
                <div className="vin-header">
                  <div>
                    <div className="vin-code">{result.vin}</div>
                    <div className="vin-make">{result.make} {result.model} · {result.year}</div>
                    <div style={{ marginTop: ".3rem" }}><span style={{ display: "inline-flex", alignItems: "center", gap: ".3rem", fontSize: ".62rem", fontFamily: "var(--font-mono)", color: "var(--accent2)", background: "rgba(255,184,0,.08)", border: "1px solid rgba(255,184,0,.2)", borderRadius: 10, padding: ".1rem .45rem", letterSpacing: ".3px", flexShrink: 0 }}>✦ VIQ.AI® Engine</span></div>
                  </div>
                  {paid ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", alignItems: "flex-end" }}>
                      {enrichDone ? (
                        <ScoreGauge score={result.score || 0} size={76} />
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: ".25rem", minWidth: 110 }}>
                          <span className="status-badge" style={{ background: "rgba(168,128,58,.1)", color: "var(--accent2)", border: "1px solid rgba(168,128,58,.3)", fontSize: ".66rem" }}>⏳ Analyse de risque…</span>
                          <span style={{ fontSize: ".64rem", color: "var(--muted)", textAlign: "right", lineHeight: 1.4 }}>Score calculé après<br/>l'analyse de fiabilité</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", alignItems: "flex-end" }}>
                      <span className="status-badge" style={{ background: "rgba(255,184,0,.1)", color: "var(--accent2)", border: "1px solid rgba(255,184,0,.3)" }}>🔒 Score masqué</span>
                      <div style={{ fontSize: ".75rem", color: "var(--sub)" }}>Débloquer · gratuit</div>
                    </div>
                  )}
                </div>

                {/* ── Bloc données carte grise — source officielle SIV ── */}
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: "1rem 1.2rem", marginBottom: ".8rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: ".7rem", marginBottom: ".8rem" }}>
                    <div style={{ background: "#1565C0", borderRadius: 4, padding: "3px 8px", display: "flex", alignItems: "center", gap: ".4rem", flexShrink: 0 }}>
                      <span style={{ fontSize: "14px" }}>🇫🇷</span>
                      <span style={{ color: "#fff", fontWeight: 700, fontSize: ".75rem", fontFamily: "var(--font-mono)", letterSpacing: 1 }}>SIV</span>
                    </div>
                    <div style={{ fontSize: ".75rem", color: "var(--sub)" }}>Données officielles Système d'Immatriculation des Véhicules</div>
                    {result.logo && <img src={result.logo} alt={result.make} style={{ height: 22, marginLeft: "auto", opacity: .8 }} />}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem" }}>
                    {[
                      { label: "Marque", value: result.make },
                      { label: "Modèle / Version", value: result.version !== "—" ? result.version : result.model },
                      { label: "1ère mise en circulation", value: result.date_immat },
                      { label: "Carburant", value: result.fuel },
                      { label: "Couleur", value: result.color },
                      { label: "Boîte de vitesses", value: result.gearbox },
                      { label: "Puissance", value: result.power_cv },
                      { label: "CO₂", value: result.co2 },
                      { label: "Nb de portes", value: result.doors },
                      { label: "Places assises", value: result.seats },
                      { label: "Carrosserie", value: result.body },
                      { label: "Poids", value: result.weight },
                    ].filter(i => i.value && i.value !== "—").map((item, i) => (
                      <div key={i} style={{ background: "var(--deep)", borderRadius: 4, padding: ".5rem .7rem" }}>
                        <div style={{ fontSize: ".68rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>{item.label}</div>
                        <div style={{ fontSize: ".85rem", fontWeight: 600, marginTop: ".1rem" }}>{item.value}</div>
                      </div>
                    ))}
                  </div>
                  {result.vin && result.vin !== "—" && (
                    <div style={{ marginTop: ".7rem", padding: ".4rem .7rem", background: "var(--deep)", borderRadius: 4, fontSize: ".72rem", fontFamily: "var(--font-mono)", color: "var(--muted)", letterSpacing: ".5px" }}>
                      VIN : <strong style={{ color: "var(--sub)" }}>{result.vin}</strong>
                    </div>
                  )}
                  <div style={{ marginTop: ".6rem", fontSize: ".68rem", color: "var(--muted)", fontStyle: "italic", lineHeight: 1.5 }}>
                    ℹ Les données de sinistres, kilométrage déclaré et historique CT ne sont pas disponibles via cette source. Pour un historique complet, consultez HistoVec.gouv.fr (gratuit) avec le numéro de plaque.
                  </div>
                </div>

                {/* ── Contenu premium — caché jusqu'au paiement ── */}
                {!paid ? (
                  <div style={{ border: "1px solid rgba(168,128,58,.25)", borderRadius: 6, overflow: "hidden" }}>
                    {/* Preview floutée */}
                    <div style={{ padding: "1rem 1.2rem", filter: "blur(4px)", opacity: .4, pointerEvents: "none", userSelect: "none" }}>
                      <div style={{ height: 12, background: "var(--border)", borderRadius: 6, marginBottom: ".5rem", width: "70%" }} />
                      <div style={{ height: 10, background: "var(--border)", borderRadius: 6, marginBottom: ".4rem", width: "90%" }} />
                      <div style={{ height: 10, background: "var(--border)", borderRadius: 6, width: "55%" }} />
                    </div>
                    {/* CTA paiement */}
                    <div style={{ background: "rgba(168,128,58,.04)", borderTop: "1px solid rgba(168,128,58,.15)", padding: "1rem 1.2rem", textAlign: "center" }}>
                      <div style={{ fontSize: ".85rem", fontWeight: 600, marginBottom: ".3rem" }}>🔒 Score VehicleIQ + rapport complet</div>
                      <div style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: "1rem", lineHeight: 1.5 }}>
                        Score de fiabilité sur 100, sinistres, kilométrage historique,<br/>valeur marché estimée et véhicules alternatifs
                      </div>
                      
                      <button className="btn btn-accent" style={{ width: "100%", fontSize: ".95rem" }} onClick={() => setShowGate(true)}>
                        🔓 Débloquer le rapport · gratuit
                      </button>
                      
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Sections accordéon — visibles après paiement */}
                    <div className="history-sections">
                      {sections.map(s => (
                        <div key={s.id} className="history-section">
                          <div className="history-section-head" onClick={() => setOpenSection(openSection === s.id ? null : s.id)}>
                            <div className="section-title"><span className="section-icon">{s.icon}</span>{s.title}</div>
                            <span style={{ color: "var(--sub)", fontSize: ".8rem" }}>{openSection === s.id ? "▲" : "▼"}</span>
                          </div>
                          {openSection === s.id && <div className="section-body">{s.content}</div>}
                        </div>
                      ))}
                    </div>
                    {/* HistoVec + CarVertical (optionnels, recommandés) + Fiabilité & risques (analyse VIQ.AI®) */}
                    <div style={{ marginTop: "1rem" }}>
                      <HistovecImport onImported={onHistovecImported} />
                      <HistovecSummary hv={hvData} />
                      <div style={{ marginTop: ".6rem" }}>
                        <CarVerticalImport onImported={onCarVerticalImported} vin={result?.vin} plaque={immat} />
                        <CarVerticalSummary cv={cvData} />
                      </div>
                      <KnownIssuesPanel data={knownIssues} loading={issuesLoading && !knownIssues} />

                      {/* v25 — Inspection visuelle IA (Pro) : photos → état réel par zone + image de synthèse */}
                      <div style={{ marginTop: ".6rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "1rem 1.1rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".4rem" }}>
                          <span style={{ fontWeight: 700, fontSize: ".85rem" }}>📷 Inspection visuelle IA</span>
                          <span style={{ fontSize: ".6rem", fontWeight: 700, color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)", padding: ".1rem .4rem", borderRadius: 4, textTransform: "uppercase" }}>Pro</span>
                        </div>
                        {!((user?.siret && user.siret.length > 3) || user?.is_pro) ? (
                          <div style={{ fontSize: ".78rem", color: "var(--sub)" }}>
                            Ajoutez des photos du véhicule pour une analyse de l'état réel, zone par zone, avec image de synthèse — réservé aux comptes Pro.{" "}
                            <button className="link-btn" style={{ color: "var(--accent)", fontWeight: 700 }}
                              onClick={() => window.dispatchEvent(new CustomEvent("viq-open-pricing"))}>
                              Voir les forfaits Pro →
                            </button>
                          </div>
                        ) : !visualInspection ? (
                          <>
                            <div style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: ".6rem" }}>
                              Ajoutez jusqu'à 10 photos (extérieur, jantes, intérieur...) : l'IA évalue l'état réel de chaque zone visible et affine le score VIQ.AI® en conséquence.
                            </div>
                            <div style={{ display: "flex", gap: ".5rem", alignItems: "center", flexWrap: "wrap", marginBottom: ".6rem" }}>
                              <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                                🖼 Galerie
                                <input type="file" accept="image/*" multiple hidden onChange={handleInspectionPhotos} />
                              </label>
                              <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                                📷 Appareil photo
                                <input type="file" accept="image/*" capture="environment" hidden onChange={handleInspectionPhotos} />
                              </label>
                              <span style={{ fontSize: ".72rem", color: "var(--muted)" }}>{inspectionPhotos.length}/10</span>
                            </div>
                            {inspectionPhotos.length > 0 && (
                              <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap", marginBottom: ".7rem" }}>
                                {inspectionPhotos.map((p, i) => (
                                  <div key={i} style={{ position: "relative" }}>
                                    <img src={p} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border)" }} />
                                    <button onClick={() => setInspectionPhotos(prev => prev.filter((_, j) => j !== i))}
                                      style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: "var(--text)", color: "var(--bg)", border: "none", fontSize: ".65rem", cursor: "pointer", lineHeight: 1 }}>×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {inspectionError && <div style={{ fontSize: ".74rem", color: "var(--red)", marginBottom: ".6rem" }}>{inspectionError}</div>}
                            <button className="btn btn-accent btn-sm" disabled={!inspectionPhotos.length || inspectionLoading} onClick={runVisualInspection}
                              style={{ opacity: !inspectionPhotos.length ? .5 : 1 }}>
                              {inspectionLoading ? "Analyse en cours…" : "🔍 Lancer l'inspection visuelle"}
                            </button>
                          </>
                        ) : (
                          <div>
                            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-start" }}>
                              <div style={{ width: 160, flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: visualInspection.synthesis_svg }} />
                              <div style={{ flex: 1, minWidth: 200 }}>
                                {visualInspection.synthese && <div style={{ fontSize: ".8rem", color: "var(--text)", marginBottom: ".6rem", fontStyle: "italic" }}>« {visualInspection.synthese} »</div>}
                                {visualInspection.vigilance_points?.length > 0 && (
                                  <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.6 }}>
                                    {visualInspection.vigilance_points.map((v, i) => <li key={i}>{v}</li>)}
                                  </ul>
                                )}
                                {typeof visualInspection.score_delta === "number" && (
                                  <div style={{ marginTop: ".6rem", fontSize: ".74rem", color: visualInspection.score_delta < 0 ? "var(--red)" : "var(--green)" }}>
                                    Ajustement du score selon l'état visuel réel : {visualInspection.score_delta > 0 ? "+" : ""}{visualInspection.score_delta} points
                                  </div>
                                )}
                              </div>
                            </div>
                            <button className="btn btn-ghost btn-sm" style={{ marginTop: ".7rem" }}
                              onClick={() => { setVisualInspection(null); setInspectionPhotos([]); setInspectionError(null); }}>
                              🔄 Nouvelle inspection
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ marginTop: "1rem", padding: "1rem 1.2rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontSize: ".75rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>Valeur marché estimée</div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "2rem", color: "var(--green)" }}>{result.marketValue}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: ".75rem", color: "var(--sub)" }}>Dernière révision</div>
                        <div style={{ fontSize: ".85rem", fontFamily: "var(--font-mono)" }}>{result.lastService}</div>
                      </div>
                    </div>
                    {/* Véhicules alternatifs — inclus dans le rapport payant */}
                    {result.alternatives?.length > 0 && (
                      <div style={{ marginTop: "1rem" }}>
                        <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".6rem" }}>
                          {result.alternatives.length} véhicules alternatifs recommandés
                        </div>
                        {result.alternatives.map((alt, i) => {
                          const PLATFORM_URLS = {"AutoScout24":"https://www.autoscout24.fr","LeBonCoin":"https://www.leboncoin.fr/voitures","La Centrale":"https://www.lacentrale.fr","Marketplace":"https://www.facebook.com/marketplace"};
                          return (
                            <div key={i} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: ".7rem 1rem", marginBottom: ".4rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: ".88rem" }}>{alt.make}</div>
                                <div style={{ fontSize: ".75rem", color: "var(--sub)" }}>{alt.year} · {alt.km?.toLocaleString?.("fr-FR") || alt.km} km</div>
                                <div style={{ fontSize: ".7rem", color: "var(--muted)" }}>🔍 {alt.platform}</div>
                              </div>
                              <div style={{ textAlign: "right", flexShrink: 0 }}>
                                <div style={{ fontFamily: "var(--font-head)", fontSize: "1.1rem", color: "var(--green)" }}>{alt.price}</div>
                                {alt.match && <div style={{ fontSize: ".68rem", color: "var(--accent2)" }}>{alt.match}% match</div>}
                                <button className="btn btn-ghost btn-sm" style={{ marginTop: ".3rem", fontSize: ".72rem" }} onClick={() => window.open(PLATFORM_URLS[alt.platform] || "https://www.leboncoin.fr", "_blank")}>
                                  Voir →
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                {/* Disclaimer légal */}
                <div style={{ marginTop: "1rem", padding: ".6rem .9rem", background: "rgba(100,100,120,.06)", border: "1px solid rgba(100,100,120,.15)", borderRadius: 4, fontSize: ".68rem", color: "var(--muted)", lineHeight: 1.5 }}>
                  ⚖ Les données et cotes affichées sont générées à titre purement indicatif et ne constituent pas un engagement contractuel (art. 1112-1 C. civ. · art. L.111-1 C. conso.). VehicleIQ ne saurait être tenu responsable des décisions prises sur la base de ces informations.
                </div>
                    {/* v104 — Règle : les 4 CTA (Exporter, Partager, Lancer
                        une cote, Lancer une recherche) sont retirés de ce
                        modale et remplacés par un unique CTA qui ouvre (ou
                        crée) le Dossier VIQ de ce véhicule — c'est là que
                        vivent désormais l'export, le partage, la cote et la
                        recherche marché. */}
                    {result?.make && result.make !== "—" && (
                      <button className="btn btn-accent" disabled={openingDossier}
                        style={{ width: "100%", justifyContent: "center", marginTop: ".6rem", opacity: openingDossier ? .6 : 1 }}
                        onClick={async () => {
                          if (openingDossier || !onOpenDossiers) return;
                          setOpeningDossier(true);
                          try {
                            const r = await apiPost("/api/dossiers/find-or-create", {
                              vin: result.vin || undefined,
                              plaque: !result.vin ? immat : undefined,
                              make: result.make, model: result.model, year: result.year, fuel: result.fuel,
                            });
                            if (r?.success && r.dossier?.id) onOpenDossiers(r.dossier.id);
                          } catch (e) { console.warn("[VINPanel ouvrir dossier]", e.message); }
                          finally { setOpeningDossier(false); }
                        }}>
                        {openingDossier ? "…" : "📁 Ouvrir un dossier VIQ →"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showGate && <WalletGate cost={0} label="Rapport plaque · Score VIQ.AI® (gratuit)" onClose={() => setShowGate(false)} onUnlocked={() => { setShowGate(false); setPaid(true); if(onWalletChange) onWalletChange(); }} user={user} onShowAuth={onShowAuth} onRecharge={() => { setShowGate(false); setShowRecharge(true); }} />}
        {showRecharge && <RechargeModal onClose={() => setShowRecharge(false)} onRecharged={(qty) => {
          setShowRecharge(false);
          if (onWalletChange) onWalletChange();
          // Si l'utilisateur avait déjà saisi sa plaque/VIN avant le blocage, relancer le check
          if (window.__viq_pending_check_action) {
            window.__viq_pending_check_action = false;
            setTimeout(() => handleCheck(), 300);
          }
        }} user={user} onShowAuth={onShowAuth} />}
    </>
  );
}


// ─── Car brands data ──────────────────────────────────────────────────────────
const CAR_BRANDS = [
  { name: "Renault", models: [
    { name: "Clio", year: "2023", rank: 1, desc: "La Clio est la voiture la plus vendue en France depuis des décennies. Compacte, économique et fiable, elle s'adresse aussi bien aux jeunes conducteurs qu'aux familles urbaines. La génération actuelle propose des finitions modernes et une version hybride.", ventes: "120 000", fiabilite: "92%" },
    { name: "Mégane", year: "2022", rank: 2, desc: "La Mégane incarne le segment C chez Renault. Spacieuse, bien équipée et disponible en version électrique (Mégane E-Tech), elle séduit les familles et les professionnels à la recherche d'un véhicule polyvalent.", ventes: "74 000", fiabilite: "88%" },
    { name: "Captur", year: "2023", rank: 3, desc: "Le Captur est le SUV urbain phare de Renault. Son gabarit compact, sa hauteur de caisse et son design dynamique en font l'un des crossovers les plus populaires du marché européen.", ventes: "68 000", fiabilite: "87%" },
  ]},
  { name: "Peugeot", models: [
    { name: "208", year: "2023", rank: 1, desc: "La Peugeot 208 est la citadine premium par excellence. Son i-Cockpit innovant, son design affûté et sa disponibilité en version 100% électrique (e-208) en font un best-seller de la catégorie.", ventes: "98 000", fiabilite: "90%" },
    { name: "308", year: "2022", rank: 2, desc: "Élue voiture de l'année 2022, la 308 combine un design audacieux à une technologie de pointe. Disponible en hybride rechargeable, elle cible une clientèle exigeante et soucieuse de l'environnement.", ventes: "61 000", fiabilite: "89%" },
    { name: "3008", year: "2023", rank: 3, desc: "Le 3008 est le SUV familial premium de Peugeot. Spacieux, confortable et bien équipé, il trône régulièrement parmi les 10 véhicules les plus vendus en Europe.", ventes: "57 000", fiabilite: "86%" },
  ]},
  { name: "Citroën", models: [
    { name: "C3", year: "2023", rank: 1, desc: "La Citroën C3 est la citadine accessible et confortable par excellence. Avec ses amortisseurs hydrauliques progressifs et son habitacle spacieux, elle redéfinit le confort dans sa catégorie à un prix imbattable.", ventes: "72 000", fiabilite: "88%" },
    { name: "C5 Aircross", year: "2023", rank: 2, desc: "Le C5 Aircross est le SUV familial phare de Citroën. Son confort de suspension exceptionnel, son habitacle généreux et sa disponibilité en hybride rechargeable en font un choix idéal pour les longs trajets.", ventes: "49 000", fiabilite: "85%" },
    { name: "ë-C4", year: "2023", rank: 3, desc: "La ë-C4 est la berline électrique accessible de Citroën. Avec jusqu'à 350 km d'autonomie et le confort légendaire de la marque, elle représente une porte d'entrée idéale vers la mobilité électrique.", ventes: "31 000", fiabilite: "87%" },
  ]},
  { name: "Volkswagen", models: [
    { name: "Golf", year: "2023", rank: 1, desc: "La Golf est une référence mondiale dans le segment compact. Robuste, polyvalente et technologiquement avancée, elle est le choix de ceux qui recherchent un équilibre parfait entre confort, performance et fiabilité.", ventes: "89 000", fiabilite: "93%" },
    { name: "Polo", year: "2022", rank: 2, desc: "La Polo est la citadine premium de Volkswagen. Elle offre une qualité de fabrication supérieure à sa catégorie, un habitacle bien fini et une dynamique de conduite plaisante.", ventes: "54 000", fiabilite: "91%" },
    { name: "Tiguan", year: "2023", rank: 3, desc: "Le Tiguan est le SUV familial de référence chez VW. Spacieux, modulable et disponible en version hybride rechargeable, il combine praticité et plaisir de conduite.", ventes: "48 000", fiabilite: "88%" },
  ]},
  { name: "BMW", models: [
    { name: "Série 3", year: "2023", rank: 1, desc: "La Série 3 est la berline sport de référence mondiale. Dynamique, premium et technologique, elle associe plaisir de conduite et confort quotidien dans un équilibre rarement atteint.", ventes: "42 000", fiabilite: "89%" },
    { name: "Série 5", year: "2023", rank: 2, desc: "La Série 5 représente l'excellence de la berline executive. Spacieuse, luxueuse et disponible en version hybride rechargeable, elle est le choix des dirigeants et des grands voyageurs.", ventes: "31 000", fiabilite: "88%" },
    { name: "X5", year: "2023", rank: 3, desc: "Le BMW X5 est le SUV premium par excellence. Puissant, spacieux et doté d'équipements haut de gamme, il s'impose comme le véhicule de prestige pour les familles actives.", ventes: "27 000", fiabilite: "87%" },
  ]},
  { name: "Mercedes", models: [
    { name: "Classe A", year: "2023", rank: 1, desc: "La Classe A est la porte d'entrée dans l'univers Mercedes. Compacte mais premium, elle embarque l'interface MBUX et offre un niveau de finition supérieur à la concurrence.", ventes: "38 000", fiabilite: "88%" },
    { name: "Classe C", year: "2022", rank: 2, desc: "La Classe C est la berline medium premium de Mercedes. Dotée d'un intérieur inspiré de la grande Classe S, elle propose une expérience luxueuse dans un format accessible.", ventes: "29 000", fiabilite: "87%" },
    { name: "GLC", year: "2023", rank: 3, desc: "Le GLC est le SUV premium phare de Mercedes. Confortable, bien équipé et disponible en hybride rechargeable, il séduit une clientèle aisée à la recherche d'espace et de prestige.", ventes: "24 000", fiabilite: "86%" },
  ]},
  { name: "Nissan", models: [
    { name: "Qashqai", year: "2023", rank: 1, desc: "Le Qashqai est le pionnier du segment SUV compact en Europe. Fiable, confortable et doté d'une motorisation hybride légère e-POWER, il reste une valeur sûre du marché.", ventes: "52 000", fiabilite: "89%" },
    { name: "Juke", year: "2023", rank: 2, desc: "Le Juke est le crossover urbain au design polarisant de Nissan. Compact, dynamique et personnalisable, il cible une clientèle jeune et branchée.", ventes: "38 000", fiabilite: "86%" },
    { name: "Leaf", year: "2022", rank: 3, desc: "La Leaf est l'une des voitures électriques les plus vendues au monde. Pionnière du segment, elle offre une autonomie compétitive et un coût d'usage parmi les plus bas du marché.", ventes: "21 000", fiabilite: "91%" },
  ]},
  { name: "Tesla", models: [
    { name: "Model Y", year: "2023", rank: 1, desc: "Le Model Y est le véhicule électrique le plus vendu au monde en 2023. SUV compact polyvalent avec une autonomie allant jusqu'à 533 km, il domine le marché européen de l'électrique grâce à son réseau Supercharger imbattable.", ventes: "1 200 000", fiabilite: "91%" },
    { name: "Model 3", year: "2024", rank: 2, desc: "La Model 3 restylée (Project Highland) est la berline électrique de référence. Intérieur épuré, performances exceptionnelles et autonomie jusqu'à 629 km en font l'alternative électrique la plus plébiscitée en France.", ventes: "650 000", fiabilite: "90%" },
    { name: "Model X", year: "2023", rank: 3, desc: "Le Model X est le SUV électrique premium de Tesla, reconnaissable à ses portes Falcon Wing. Avec 7 places et une autonomie de 580 km, il allie espace familial et performances de supercar.", ventes: "95 000", fiabilite: "88%" },
  ]},
];

// ── Système d'identité de marque (remplace les rendus ImagInStudio) ───────────
// Logos officiels via CDN public fiable (jsDelivr / car-logos-dataset) + base de
// connaissances éditoriale par marque (histoire + positionnement actuel).
// Plus de watermark, plus de modèle 3D incohérent : on affiche le logo de la
// marque et un court texte qui valorise le contexte du véhicule.
function brandSlug(make) {
  const m = String(make || "").trim().toLowerCase().split(/\s+/)[0]
    .replace("citroën", "citroen").replace("citroen", "citroen");
  const map = {
    "mercedes": "mercedes-benz", "mercedes-benz": "mercedes-benz", "vw": "volkswagen",
    "range": "land-rover", "land": "land-rover", "rolls": "rolls-royce", "alfa": "alfa-romeo",
    "ds": "ds-automobiles", "mini": "mini", "byd": "byd", "mg": "mg-motor",
  };
  return map[m] || m;
}
function brandLogoUrl(make) {
  const slug = brandSlug(make);
  if (!slug) return null;
  // car-logos-dataset (optimized PNG, fond transparent) servi par jsDelivr
  return `https://cdn.jsdelivr.net/gh/filippofilip95/car-logos-dataset@master/logos/optimized/${slug}.png`;
}
// Histoire + positionnement par marque. Affiché en encart sous le logo.
const BRAND_INFO = {
  "mercedes-benz": { country: "🇩🇪 Allemagne", since: 1926, history: "Pionnier de l'automobile depuis l'invention de la voiture par Carl Benz en 1886, Mercedes-Benz incarne le luxe et l'innovation technique allemande.", now: "La marque à l'étoile mise aujourd'hui sur l'électrique haut de gamme (gamme EQ) tout en conservant ses berlines et SUV de référence." },
  "bmw": { country: "🇩🇪 Allemagne", since: 1916, history: "Né de la construction de moteurs d'avion, BMW a bâti sa réputation sur le plaisir de conduite et la propulsion.", now: "BMW déploie une large gamme électrique (i4, iX) tout en gardant l'ADN sportif de ses séries M." },
  "audi": { country: "🇩🇪 Allemagne", since: 1909, history: "Les quatre anneaux symbolisent la fusion de quatre constructeurs. Audi s'est imposée par sa transmission quattro et son design.", now: "La marque accélère sur l'électrique (e-tron) et le numérique embarqué, en montée en gamme face à Mercedes et BMW." },
  "volkswagen": { country: "🇩🇪 Allemagne", since: 1937, history: "La « voiture du peuple » a démocratisé l'automobile en Europe avec la Coccinelle puis la Golf, best-seller mondial.", now: "Volkswagen opère sa bascule électrique avec la gamme ID., produite en grand volume pour rester accessible." },
  "peugeot": { country: "🇫🇷 France", since: 1810, history: "Plus ancien constructeur automobile au monde encore en activité, Peugeot a forgé son identité sur le lion et un savoir-faire généraliste.", now: "Au sein de Stellantis, Peugeot électrifie toute sa gamme (e-208, e-3008) avec son i-Cockpit signature." },
  "renault": { country: "🇫🇷 France", since: 1899, history: "Acteur historique de l'industrie française, Renault a marqué l'histoire avec la 4L, la Clio et l'Espace.", now: "Renault renoue avec son patrimoine via des icônes électriques (R5 E-Tech) et une stratégie de montée en gamme." },
  "citroen": { country: "🇫🇷 France", since: 1919, history: "Marque de l'audace technique (Traction Avant, DS, 2CV), Citroën a toujours cultivé le confort et l'originalité.", now: "Citroën se repositionne sur l'accessibilité et le confort (ë-C3 électrique abordable) au sein de Stellantis." },
  "toyota": { country: "🇯🇵 Japon", since: 1937, history: "Premier constructeur mondial, Toyota a imposé l'hybride au grand public dès 1997 avec la Prius.", now: "Leader de l'hybride, Toyota élargit prudemment son offre 100% électrique tout en pariant sur l'hydrogène." },
  "land-rover": { country: "🇬🇧 Royaume-Uni", since: 1948, history: "Référence mondiale du tout-terrain de luxe, Land Rover et sa gamme Range Rover allient capacités hors route et raffinement.", now: "La marque (groupe JLR) électrifie ses modèles premium tout en cultivant son image d'aventure haut de gamme." },
  "bentley": { country: "🇬🇧 Royaume-Uni", since: 1919, history: "Symbole du grand luxe britannique et de la performance, Bentley a marqué l'histoire au Mans avant de devenir une référence des GT d'exception.", now: "Bentley engage sa transition vers l'électrique haut de gamme tout en perpétuant l'artisanat de ses ateliers de Crewe." },
  "porsche": { country: "🇩🇪 Allemagne", since: 1931, history: "Icône de la voiture de sport, Porsche a construit sa légende autour de la 911, référence intemporelle.", now: "Porsche réussit sa transition électrique avec le Taycan et électrifie le Macan, sans renier sa sportivité." },
  "ferrari": { country: "🇮🇹 Italie", since: 1939, history: "Mythe absolu de l'automobile, Ferrari incarne la passion italienne, la course et l'exclusivité.", now: "Ferrari dévoile ses premiers modèles hybrides et électriques tout en préservant la rareté qui fait sa valeur." },
  "fiat": { country: "🇮🇹 Italie", since: 1899, history: "Constructeur populaire italien, Fiat a motorisé l'Italie avec la 500 et la Panda.", now: "La 500 électrique relance la marque sur le segment urbain au sein de Stellantis." },
  "volvo": { country: "🇸🇪 Suède", since: 1927, history: "Pionnier mondial de la sécurité automobile (ceinture 3 points offerte au monde en 1959), Volvo rime avec robustesse scandinave.", now: "Volvo vise le 100% électrique et le design épuré, en s'appuyant sur sa réputation de sécurité." },
  "tesla": { country: "🇺🇸 États-Unis", since: 2003, history: "Pionnier de la voiture électrique grand public, Tesla a bouleversé l'industrie avec la Model S puis la Model 3.", now: "Tesla reste la référence de l'électrique connecté, avec son réseau Supercharger et ses mises à jour logicielles." },
  "nissan": { country: "🇯🇵 Japon", since: 1933, history: "Constructeur japonais majeur, Nissan a démocratisé l'électrique avec la Leaf, première électrique grand public.", now: "Nissan poursuit l'électrification (Ariya) et entretient son image avec des modèles comme le Qashqai." },
  "hyundai": { country: "🇰🇷 Corée du Sud", since: 1967, history: "Montée en gamme spectaculaire en deux décennies, Hyundai s'est imposé par son rapport qualité-prix et ses garanties.", now: "Hyundai est salué pour sa gamme électrique Ioniq, primée internationalement." },
  "kia": { country: "🇰🇷 Corée du Sud", since: 1944, history: "Marque coréenne au design audacieux, Kia a opéré une transformation remarquable de son image.", now: "Kia séduit avec sa gamme électrique EV et un design distinctif récompensé." },
  "ford": { country: "🇺🇸 États-Unis", since: 1903, history: "Inventeur de la production de masse avec la Ford T, Ford a rendu l'automobile accessible au monde entier.", now: "Ford électrifie ses icônes (Mustang Mach-E) et se recentre sur les SUV et utilitaires en Europe." },
};
function getBrandInfo(make) {
  return BRAND_INFO[brandSlug(make)] || null;
}

// ── Carte d'identité de marque (logo + histoire + positionnement) ─────────────
function BrandIdentityCard({ make, model, year, color, compact = false }) {
  const logo = useMemo(() => brandLogoUrl(make), [make]);
  const info = useMemo(() => getBrandInfo(make), [make]);
  const [logoOk, setLogoOk] = useState(true);
  if (!make || make === "—") return null;
  const brandName = String(make).trim().split(/\s+/)[0];
  return (
    <div style={{ width: "100%", borderRadius: compact ? 12 : "8px 8px 0 0", overflow: "hidden", marginBottom: compact ? 0 : ".8rem",
      background: "linear-gradient(135deg, var(--card), var(--bg))", border: "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: compact ? ".7rem .9rem" : "1rem 1.1rem" }}>
        <div style={{ flexShrink: 0, width: compact ? 54 : 72, height: compact ? 54 : 72, borderRadius: 12, background: "#fff",
          border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", padding: 8 }}>
          {logo && logoOk ? (
            <img src={logo} alt={brandName} onError={() => setLogoOk(false)}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
          ) : (
            <span style={{ fontFamily: "var(--font-head)", fontSize: compact ? ".8rem" : "1rem", fontWeight: 700, color: "var(--accent)", textAlign: "center", lineHeight: 1 }}>
              {brandName.slice(0, 3).toUpperCase()}
            </span>
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: compact ? "1rem" : "1.2rem", fontWeight: 600, color: "var(--text)", lineHeight: 1.1 }}>
            {make} {model && model !== "—" ? model : ""} {year && year !== "—" ? year : ""}
          </div>
          {info && (
            <div style={{ fontSize: ".7rem", color: "var(--sub)", marginTop: ".2rem", display: "flex", gap: ".6rem", flexWrap: "wrap" }}>
              <span>{info.country}</span>
              <span>· Depuis {info.since}</span>
              {color && color !== "—" && <span>· {color}</span>}
            </div>
          )}
        </div>
      </div>
      {info && !compact && (
        <div style={{ padding: "0 1.1rem 1rem", display: "flex", flexDirection: "column", gap: ".5rem" }}>
          <div style={{ fontSize: ".76rem", color: "var(--sub)", lineHeight: 1.5 }}>
            <span style={{ fontWeight: 700, color: "var(--accent)" }}>Histoire · </span>{info.history}
          </div>
          <div style={{ fontSize: ".76rem", color: "var(--sub)", lineHeight: 1.5 }}>
            <span style={{ fontWeight: 700, color: "var(--green)" }}>Aujourd'hui · </span>{info.now}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Bandeau photo véhicule (rendu ImagInStudio) ───────────────────────────────
function VehiclePhotoBanner({ make, model, year, color, fuel }) {
  if (!make || make === "—") return null;
  return <BrandIdentityCard make={make} model={model} year={year} color={color} />;
}

// ── Model Popup ──────────────────────────────────────────────────────────────
function ModelPopup({ brand, model, onClose, onStart }) {
  const rankColor = model.rank === 1 ? "var(--green)" : model.rank === 2 ? "var(--accent2)" : "var(--accent)";
  const rankLabel = model.rank === 1 ? "▲ Modèle le plus vendu" : model.rank === 2 ? "— 2ème du segment" : "▼ 3ème du segment";
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="model-popup">
        <div className="panel-header">
          <div>
            <div className="model-popup-make">{brand}</div>
            <div className="model-popup-name">{model.name}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: ".7rem", color: rankColor }}>{rankLabel}</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        {/* Identité de marque (logo + contexte) */}
        <div style={{ padding: ".8rem 1rem 0" }}>
          <BrandIdentityCard make={brand} model={model.name} year={model.year} compact />
        </div>
        <div className="model-popup-body">
          <div className="model-popup-year">Millésime phare : {model.year}</div>
          <div className="model-popup-desc">{model.desc}</div>
          <div className="model-popup-stats">
            <div className="model-stat"><span className="model-stat-val">{model.ventes}</span><span className="model-stat-lbl">Ventes/an</span></div>
            <div className="model-stat"><span className="model-stat-val">{model.fiabilite}</span><span className="model-stat-lbl">Fiabilité</span></div>
          </div>
          <button className="btn btn-accent" style={{ width: "100%", fontSize: ".9rem", padding: ".7rem 1rem" }}
            onClick={() => { if (onStart) onStart(); else onClose(); }}>
            Commencer maintenant →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cookie Banner ────────────────────────────────────────────────────────────
function CookieBanner({ onAccept, onRefuse, onLegal }) {
  return (
    <div className="cookie-banner">
      <div className="cookie-text">
        <strong>🍪 Cookies & confidentialité</strong><br/>
        Nous utilisons des cookies pour améliorer votre expérience, mesurer l'audience et personnaliser le contenu.
        En cliquant sur "Accepter", vous consentez à l'utilisation de cookies de tracking.{" "}
        <a onClick={() => onLegal("privacy")}>Politique de confidentialité</a>
      </div>
      <div className="cookie-actions">
        <button className="btn-cookie-refuse" onClick={onRefuse}>Refuser</button>
        <button className="btn-cookie-accept" onClick={onAccept}>Accepter</button>
      </div>
    </div>
  );
}

// ─── Question Tooltip Modal ───────────────────────────────────────────────────
function QuestionTooltip({ info }) {
  const [open, setOpen] = useState(false);
  if (!info) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="En savoir plus sur cette question"
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 18, height: 18, borderRadius: "50%",
          background: "rgba(255,184,0,.15)", border: "1.5px solid rgba(255,184,0,.4)",
          color: "var(--accent2)", fontSize: ".65rem", fontWeight: 700,
          cursor: "pointer", fontFamily: "var(--font-body)",
          marginLeft: ".4rem", flexShrink: 0, verticalAlign: "middle",
          lineHeight: 1, padding: 0, transition: "all .15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.background="rgba(255,184,0,.3)"; }}
        onMouseLeave={e => { e.currentTarget.style.background="rgba(255,184,0,.15)"; }}
      >ℹ</button>
      {open && (
        <div className="overlay" style={{ zIndex: 9999 }} onClick={() => setOpen(false)}>
          <div className="panel" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="panel-header">
              <div>
                <div className="panel-title" style={{ fontSize: "1rem" }}>ℹ Pourquoi cette question ?</div>
              </div>
              <button className="close-btn" onClick={() => setOpen(false)}>✕</button>
            </div>
            <div className="panel-body">
              <div style={{ fontSize: ".85rem", color: "var(--sub)", lineHeight: 1.75 }}>{info}</div>
              <div style={{ marginTop: "1rem", padding: ".6rem .9rem", background: "rgba(168,128,58,.05)", border: "1px solid rgba(168,128,58,.15)", borderRadius: 6, fontSize: ".75rem", color: "var(--sub)" }}>
                💡 <strong style={{ color: "var(--text)" }}>Important :</strong> Plus vos informations sont précises et factuelles, plus la Cote VIQ.AI® Engine sera fiable. Des données inexactes peuvent décaler la Cote de 10 à 25%.
              </div>
              <button className="btn btn-accent" style={{ width: "100%", marginTop: "1rem" }} onClick={() => setOpen(false)}>
                J'ai compris ✓
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Questionnaire component ──────────────────────────────────────────────────
// ─── Catalogue Marque + Modèles populaires (autocomplétion) ──────────────
// Couvre ~95% du marché français. Format : { "Marque": ["Modèle1", "Modèle2", ...] }
const VEHICLE_MAKES_MODELS = {
  "Renault":    ["Clio","Captur","Megane","Megane E-Tech","Scenic","Kadjar","Koleos","Talisman","Twingo","Zoe","Arkana","Austral","Espace","Trafic","Master","Kangoo"],
  "Peugeot":    ["108","208","2008","308","308 SW","3008","508","508 SW","5008","Rifter","Partner","Expert","Boxer","e-208","e-2008","e-308"],
  "Citroen":    ["C1","C3","C3 Aircross","C4","C4 Cactus","C4 X","C5 X","C5 Aircross","Berlingo","Jumpy","Jumper","DS3","DS4","DS5","ë-C4"],
  "DS":         ["DS3","DS3 Crossback","DS4","DS7 Crossback","DS9"],
  "Dacia":      ["Sandero","Sandero Stepway","Logan","Duster","Lodgy","Dokker","Jogger","Spring"],
  "Volkswagen": ["Polo","Golf","Golf GTI","Passat","T-Cross","T-Roc","Tiguan","Touareg","Touran","Sharan","Caddy","Up","ID.3","ID.4","ID.5","ID.7","Arteon"],
  "Audi":       ["A1","A3","A4","A4 Avant","A5","A6","A6 Avant","A7","A8","Q2","Q3","Q4 e-tron","Q5","Q7","Q8","e-tron","TT","RS3","RS4","RS6","S3","S4"],
  "BMW":        ["Serie 1","Serie 2","Serie 2 Gran Coupe","Serie 3","Serie 4","Serie 5","Serie 6","Serie 7","X1","X2","X3","X4","X5","X6","X7","Z4","i3","i4","iX","iX3","M2","M3","M4","M5"],
  "Mercedes":   ["Classe A","Classe B","Classe C","Classe E","Classe S","CLA","CLS","GLA","GLB","GLC","GLE","GLS","Classe G","EQA","EQB","EQC","EQE","EQS","Vito","Sprinter"],
  "Mini":       ["Cooper","Cooper S","Cooper SE","One","Countryman","Clubman","Cabrio"],
  "Smart":      ["Fortwo","Forfour","#1","#3"],
  "Toyota":     ["Aygo","Aygo X","Yaris","Yaris Cross","Corolla","Corolla Cross","C-HR","RAV4","Highlander","Camry","Prius","Land Cruiser","Hilux","Proace","bZ4X"],
  "Lexus":      ["CT","IS","ES","LS","UX","NX","RX","LX","LC","RZ"],
  "Honda":      ["Jazz","Civic","HR-V","CR-V","e","ZR-V"],
  "Mazda":      ["Mazda 2","Mazda 3","Mazda 6","CX-3","CX-30","CX-5","CX-60","MX-5","MX-30"],
  "Nissan":     ["Micra","Juke","Qashqai","X-Trail","Leaf","Ariya","Note","Pulsar","370Z","GT-R"],
  "Hyundai":    ["i10","i20","i30","Bayon","Kona","Tucson","Santa Fe","Ioniq","Ioniq 5","Ioniq 6","Nexo"],
  "Kia":        ["Picanto","Rio","Ceed","ProCeed","XCeed","Stonic","Niro","Sportage","Sorento","Soul","EV6","EV9","e-Niro","e-Soul"],
  "Ford":       ["Fiesta","Focus","Mondeo","Puma","Kuga","EcoSport","Edge","Mustang","Mustang Mach-E","Ranger","Transit","Tourneo"],
  "Opel":       ["Corsa","Corsa Electric","Astra","Astra Sports Tourer","Insignia","Crossland","Mokka","Mokka Electric","Grandland","Combo","Vivaro","Movano"],
  "Fiat":       ["500","500e","500X","500L","Panda","Tipo","600","Doblo","Ducato","124 Spider"],
  "Alfa Romeo": ["Giulia","Stelvio","Tonale","MiTo","Giulietta","4C"],
  "Lancia":     ["Ypsilon","Delta"],
  "Jeep":       ["Renegade","Compass","Cherokee","Grand Cherokee","Wrangler","Avenger"],
  "Volvo":      ["XC40","XC60","XC90","V40","V60","V90","S60","S90","C40","EX30","EX90"],
  "Polestar":   ["Polestar 2","Polestar 3","Polestar 4"],
  "Skoda":      ["Fabia","Scala","Octavia","Superb","Kamiq","Karoq","Kodiaq","Enyaq","Citigo"],
  "Seat":       ["Ibiza","Leon","Arona","Ateca","Tarraco","Mii"],
  "Cupra":      ["Born","Formentor","Leon","Ateca","Tavascan"],
  "Tesla":      ["Model 3","Model Y","Model S","Model X","Cybertruck","Roadster"],
  "Porsche":    ["911","718 Cayman","718 Boxster","Cayenne","Macan","Panamera","Taycan"],
  "Jaguar":     ["XE","XF","F-Type","E-Pace","F-Pace","I-Pace"],
  "Land Rover": ["Defender","Discovery","Discovery Sport","Range Rover","Range Rover Sport","Range Rover Evoque","Range Rover Velar"],
  "MG":         ["MG3","MG4","MG5","ZS","ZS EV","HS","Marvel R","Cyberster"],
  "Suzuki":     ["Swift","Ignis","Vitara","S-Cross","Jimny","Across","Baleno"],
  "Subaru":     ["Impreza","XV","Forester","Outback","Solterra","BRZ"],
  "Mitsubishi": ["Space Star","ASX","Eclipse Cross","Outlander","L200"],
  "BYD":        ["Atto 3","Dolphin","Seal","Han","Tang"],
  "Aiways":     ["U5","U6"],
  "Lotus":      ["Emira","Eletre","Evija"],
  "Bentley":    ["Continental","Bentayga","Flying Spur"],
  "Ferrari":    ["Roma","Portofino","SF90","296","812","F8","Purosangue"],
  "Lamborghini":["Huracan","Urus","Revuelto"],
  "Maserati":   ["Ghibli","Quattroporte","Levante","Grecale","MC20","GranTurismo"],
  "Rolls Royce":["Phantom","Ghost","Wraith","Cullinan","Spectre"],
  "Aston Martin":["Vantage","DB11","DBX","DBS","Valkyrie"],
  "McLaren":    ["570S","720S","Artura","GT","Senna"],
};

// ─── Motorisations courantes par marque (utilisé pour suggestions niveau 2)
// Quand l'utilisateur a sélectionné "Marque Modèle", on propose les motorisations
// les plus courantes de la marque. Format : array de strings courtes.
const ENGINES_BY_MAKE = {
  "Renault": [
    "1.0 TCe 100","1.3 TCe 140","1.3 TCe 160","1.5 dCi 95","1.5 dCi 115","1.6 E-Tech 145","1.6 E-Tech 160 hybride rechargeable","E-Tech 100% électrique 130","E-Tech 100% électrique 220","1.6 dCi 130","2.0 dCi 160"
  ],
  "Peugeot": [
    "1.2 PureTech 110","1.2 PureTech 130","1.2 PureTech 130 EAT8","1.5 BlueHDi 130","1.5 BlueHDi 130 EAT8","1.6 PureTech 180","Hybrid 136","Hybrid 180 e-EAT8","Hybrid4 300","Electric 136","Electric 156","2.0 BlueHDi 180","GT-Line 1.6 THP"
  ],
  "Citroen": [
    "1.2 PureTech 110","1.2 PureTech 130","1.5 BlueHDi 130","1.6 PureTech 180","Hybrid 136","Electric 100","Electric 136"
  ],
  "DS": [
    "1.2 PureTech 130","1.5 BlueHDi 130","1.6 PureTech 180","1.6 PureTech 225","E-Tense 225","E-Tense 300","E-Tense 4x4 360"
  ],
  "Dacia": [
    "TCe 90","TCe 100 LPG","TCe 130","Blue dCi 95","Blue dCi 115","ECO-G 100","Electric 45 kW","Electric 65 kW","Hybrid 140"
  ],
  "Volkswagen": [
    "1.0 TSI 95","1.0 TSI 110","1.5 TSI 130","1.5 TSI 150","1.5 eTSI 150 DSG","2.0 TSI 190","2.0 TSI 245 GTI","2.0 TDI 115","2.0 TDI 150","2.0 TDI 200","eHybrid 245","GTE 245","R 320","Electric 58 kWh","Electric 77 kWh"
  ],
  "Audi": [
    "30 TFSI 110","35 TFSI 150","40 TFSI 200","45 TFSI 245 quattro","50 TFSI 299 quattro","S3 310","RS3 400","30 TDI 116","35 TDI 163","40 TDI 204","50 TDI 286 quattro","TFSI e 204","TFSI e 245","e-tron 50 313 ch","e-tron 55 408 ch"
  ],
  "BMW": [
    "116d 116","118d 150","118i 136","120d xDrive 190","M135i xDrive 306","320d 190","330d 286","330e 292 hybride rechargeable","M340i xDrive 374","M3 Competition 510","520d 190","530d 286","530e 292 hybride rechargeable","xDrive30d 286","xDrive40i 380","M50i 530","i4 eDrive40 340","iX xDrive40 326","iX xDrive50 523"
  ],
  "Mercedes": [
    "A 180","A 180 d","A 200","A 200 d","A 220","A 250 e EQ Power","A 35 AMG 4Matic 306","A 45 S AMG 4Matic+ 421","C 180","C 200","C 220 d","C 300","C 300 e","C 300 d","C 43 AMG 4Matic 408","E 200","E 220 d","E 300 e","E 53 AMG 4Matic+ 435","GLA 200","GLA 250e","GLC 220 d 4Matic","GLC 300 4Matic","GLC 300 e 4Matic","GLE 350 de 4Matic","GLE 450 d 4Matic"
  ],
  "Mini": [
    "Cooper 136","Cooper S 178","Cooper SE 184 électrique","One 102","JCW 231","Cooper D 116","Cooper SD 170"
  ],
  "Toyota": [
    "Hybrid 116","Hybrid 130","Hybrid 140","Hybrid 196","Hybrid 218","Hybrid AWD-i 222","PHEV 306","GR Sport 1.6 261"
  ],
  "Lexus": [
    "Hybrid 184","Hybrid 220","Hybrid 250","Hybrid F Sport 220","Hybrid 350h","Hybrid 450h+","F 500"
  ],
  "Honda": [
    "1.5 i-MMD Hybrid 109","1.5 i-MMD Hybrid 122","2.0 i-MMD Hybrid 184","Type R 320","e Advance 154 électrique"
  ],
  "Mazda": [
    "e-Skyactiv-G 122","e-Skyactiv-G 150","e-Skyactiv-G 165","e-Skyactiv-X 186","e-Skyactiv-D 150","e-Skyactiv-D 200","PHEV 327","e-Skyactiv électrique 145"
  ],
  "Nissan": [
    "1.0 DIG-T 92","1.3 DIG-T 140","1.3 DIG-T 158","e-Power 190","Hybrid 140","Electric 39 kWh","Electric 62 kWh","Electric 87 kWh"
  ],
  "Hyundai": [
    "1.0 T-GDi 100","1.2 75","1.4 100","1.4 T-GDi 138","Hybrid 141","Hybrid 230","PHEV 265","N Performance 280","Electric 39 kWh","Electric 64 kWh","Electric 77 kWh","Electric 84 kWh"
  ],
  "Kia": [
    "1.0 T-GDi 100","1.4 100","1.6 T-GDi 140","Hybrid 141","Hybrid 230","PHEV 265","Electric 39 kWh","Electric 64 kWh","Electric 77 kWh","GT 585"
  ],
  "Ford": [
    "1.0 EcoBoost 100","1.0 EcoBoost 125","1.0 EcoBoost mHEV 125","1.5 EcoBoost 150","2.3 EcoBoost ST 280","Hybrid 155","PHEV 225","Electric 198","Electric 487 GT"
  ],
  "Opel": [
    "1.2 Turbo 100","1.2 Turbo 130","1.5 Diesel 130","Hybrid 180","Electric 136","Electric 156"
  ],
  "Fiat": [
    "1.0 Hybrid 70","1.0 GSE Hybrid 100","1.2 Firefly 75","1.3 MultiJet 95","Electric 95","Electric 118"
  ],
  "Alfa Romeo": [
    "1.5 Hybrid 160","1.5 Hybrid VGT 180","2.0 Turbo 280","2.9 V6 Bi-Turbo Quadrifoglio 510","2.2 Diesel 160","2.2 Diesel 210","PHEV Q4 280"
  ],
  "Jeep": [
    "1.5 Turbo Hybrid 130","2.0 Turbo 272","PHEV 240 4xe","PHEV 380 4xe","Electric 156"
  ],
  "Volvo": [
    "B3 163","B4 197","B5 250 AWD","T6 AWD 350 hybride rechargeable","T8 AWD 455 hybride rechargeable","D3 150","D4 197 AWD","Recharge 231","Recharge Twin 408","Recharge Twin Performance 517"
  ],
  "Polestar": [
    "Single Motor 272","Long Range Single 272","Long Range Dual Motor 408","Performance Dual Motor 476"
  ],
  "Skoda": [
    "1.0 TSI 95","1.0 TSI 110","1.5 TSI 150","2.0 TSI 245 RS","1.6 TDI 115","2.0 TDI 150","2.0 TDI 200 RS","iV 204","iV 265","iV 286"
  ],
  "Seat": [
    "1.0 TSI 95","1.0 TSI 110","1.5 TSI 150","2.0 TSI 245 Cupra","1.6 TDI 115","2.0 TDI 150","eHybrid 245","e-Hybrid 245"
  ],
  "Cupra": [
    "1.5 TSI 150","2.0 TSI 245","2.0 TSI 300","2.0 TSI 310 VZ","eHybrid 204","eHybrid 245","Electric 204","Electric 231","Electric 326","Performance 340"
  ],
  "Tesla": [
    "Standard Range","Long Range","Long Range Dual Motor","Performance","Plaid"
  ],
  "Porsche": [
    "Carrera 385","Carrera S 450","Carrera GTS 480","Turbo S 650","GT3 510","Macan 265","Macan S 380","Macan GTS 440","Macan Turbo 440","Cayenne 340","Cayenne S 440","Cayenne Turbo GT 640","E-Hybrid 470","Taycan 408","Taycan 4S 530","Taycan Turbo 680","Taycan Turbo S 761"
  ],
  "Jaguar": [
    "P250 250","P300 300","P400e 404 hybride rechargeable","D165 163","D200 204","EV400 400 électrique"
  ],
  "Land Rover": [
    "P250 250","P300 300","P400 400","P530 530","P400e 404 hybride rechargeable","D200 204","D300 300","D350 350"
  ],
  "MG": [
    "1.5 T-GDi 162","Hybrid+ 196","Electric 130","Electric 156","Electric 245","Electric 313 Trophy"
  ],
  "Suzuki": [
    "1.2 Dualjet Hybrid 83","1.4 Boosterjet Hybrid 129","1.5 Strong Hybrid 116","1.5 Strong Hybrid AllGrip 116"
  ],
  "BYD": [
    "Single Motor 204","Dual Motor 313","Long Range 313","Performance 530"
  ],
  "Mitsubishi": [
    "1.0 71","1.6 PHEV 188","2.4 PHEV 224"
  ],
  "Lancia": ["1.0 GSE Hybrid 70","Electric 156"],
  "Smart": ["EQ 82 électrique","Brabus 90 électrique","#1 Pro 272 électrique","#1 Pro+ 272 électrique","#3 Pro 272 électrique"],
  "Subaru": ["e-BOXER 150","Hybrid 150","2.0i 150","2.5i 184","WRX STI 300"],
};

// Helper : à partir d'une saisie utilisateur, propose jusqu'à 8 suggestions.
// Algorithme à 2 niveaux :
//   Niveau 1 (query = marque ou début de marque) → suggère "Marque Modèle"
//   Niveau 2 (query = marque + modèle déjà complet) → suggère "Marque Modèle + Motorisation"
function getMakeModelSuggestions(query) {
  if (!query || query.length < 3) return [];
  const q = query.toLowerCase().trim();
  const results = [];
  const seen = new Set();
  const qTokens = q.split(/\s+/).filter(Boolean);
  const allMatch = (haystack) => qTokens.every(t => haystack.includes(t));

  // ─── Détection NIVEAU 2 : la query correspond exactement à "Marque Modèle"
  // (sans motorisation supplémentaire) → on propose les motorisations
  for (const make of Object.keys(VEHICLE_MAKES_MODELS)) {
    const makeL = make.toLowerCase();
    for (const model of VEHICLE_MAKES_MODELS[make]) {
      const combo = (make + " " + model).toLowerCase();
      // Match exact (query == "marque modele") → niveau 2
      if (q === combo) {
        const engines = ENGINES_BY_MAKE[make] || [];
        for (const engine of engines) {
          const full = make + " " + model + " " + engine;
          results.push(full);
          if (results.length >= 8) return results;
        }
        // Si pas assez de motorisations, complète avec des modèles voisins de la même marque
        if (results.length < 8) {
          for (const otherModel of VEHICLE_MAKES_MODELS[make]) {
            if (otherModel === model) continue;
            const sug = make + " " + otherModel;
            if (!results.includes(sug)) {
              results.push(sug);
              if (results.length >= 8) return results;
            }
          }
        }
        return results;
      }
    }
  }

  // ─── Détection NIVEAU 2b : query commence par "Marque Modèle " avec quelque chose après
  // → on propose des motorisations qui matchent ce qui est tapé après
  for (const make of Object.keys(VEHICLE_MAKES_MODELS)) {
    for (const model of VEHICLE_MAKES_MODELS[make]) {
      const baseL = (make + " " + model).toLowerCase();
      if (q.startsWith(baseL + " ")) {
        // L'utilisateur a déjà "Marque Modèle " et tape la motorisation
        const remainder = q.substring(baseL.length + 1).trim();
        const engines = ENGINES_BY_MAKE[make] || [];
        for (const engine of engines) {
          const engineL = engine.toLowerCase();
          if (engineL.includes(remainder) || remainder.split(/\s+/).every(t => engineL.includes(t))) {
            const full = make + " " + model + " " + engine;
            if (!seen.has(full.toLowerCase())) {
              results.push(full);
              seen.add(full.toLowerCase());
              if (results.length >= 8) return results;
            }
          }
        }
        if (results.length > 0) return results;
      }
    }
  }

  // ─── NIVEAU 1 (comportement original) : query = marque ou début de marque/modèle
  for (const make of Object.keys(VEHICLE_MAKES_MODELS)) {
    const makeL = make.toLowerCase();
    if (makeL.startsWith(q) || makeL.includes(q)) {
      for (const model of VEHICLE_MAKES_MODELS[make]) {
        const key = make + " " + model;
        if (!seen.has(key.toLowerCase())) {
          results.push(key);
          seen.add(key.toLowerCase());
        }
      }
    } else {
      for (const model of VEHICLE_MAKES_MODELS[make]) {
        const combo = make + " " + model;
        const comboL = combo.toLowerCase();
        if (allMatch(comboL) || comboL.includes(q)) {
          const key = combo;
          if (!seen.has(key.toLowerCase())) {
            results.push(key);
            seen.add(key.toLowerCase());
          }
        }
      }
    }
    if (results.length >= 20) break;
  }
  return results.slice(0, 8);
}

// Composant : Input texte avec dropdown d'autocomplétion Marque/Modèle
function MakeModelAutocomplete({ value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [touched, setTouched] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const suggestions = touched ? getMakeModelSuggestions(value || "") : [];
  const showDropdown = open && suggestions.length > 0;

  // Fermer si clic en dehors
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDropdown]);

  const pick = (sug) => {
    onChange(sug);
    setOpen(false);
    setHighlighted(-1);
    // Garder le focus pour pouvoir continuer à taper (préciser motorisation, par ex.)
    setTimeout(() => { try { inputRef.current?.focus(); } catch(_) {} }, 0);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        className="input"
        placeholder={placeholder}
        value={value || ""}
        onChange={e => { onChange(e.target.value); setOpen(true); setHighlighted(-1); setTouched(true); }}
        onFocus={() => { setTouched(true); setOpen(true); }}
        onKeyDown={(e) => {
          if (!showDropdown) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlighted(h => Math.min(suggestions.length - 1, h + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlighted(h => Math.max(-1, h - 1));
          } else if (e.key === "Enter" && highlighted >= 0) {
            e.preventDefault();
            pick(suggestions[highlighted]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        autoComplete="off"
      />
      {showDropdown && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          marginTop: 4, background: "var(--white)",
          border: "1px solid var(--border)", borderRadius: 6,
          boxShadow: "0 8px 24px rgba(0,0,0,.12)",
          zIndex: 50, maxHeight: 360, overflowY: "auto"
        }}>
          {suggestions.map((sug, i) => (
            <div key={sug}
              onMouseDown={(e) => { e.preventDefault(); pick(sug); }}
              onMouseEnter={() => setHighlighted(i)}
              style={{
                padding: ".55rem .8rem",
                background: highlighted === i ? "rgba(168,128,58,.08)" : "transparent",
                color: "var(--text)", fontSize: ".88rem", cursor: "pointer",
                borderBottom: i < suggestions.length - 1 ? "1px solid var(--border)" : "none",
                fontFamily: "var(--font-body)"
              }}>
              {sug}
            </div>
          ))}
          <div style={{
            padding: ".4rem .8rem", fontSize: ".66rem", color: "var(--muted)",
            background: "var(--card)", borderTop: "1px solid var(--border)",
            fontStyle: "italic"
          }}>
            Sélectionnez le modèle → la motorisation sera proposée à l'étape suivante
          </div>
        </div>
      )}
    </div>
  );
}

function Questionnaire({ questions, onComplete, allowSkip, required, prefillAnswers }) {
  const [answers, setAnswers] = useState(prefillAnswers || {});
  // Filtrage conditionnel : une question peut définir showIf(answers) → bool.
  // Recalculé à chaque rendu selon les réponses courantes (ex : questions revente).
  const visibleQuestions = questions.filter(q => typeof q.showIf !== "function" || q.showIf(answers));
  // Si un prefill est fourni, démarrer à la 1ère question non encore renseignée
  // (les champs saisis dans le formulaire du hero ne sont pas re-demandés).
  const firstUnanswered = (() => {
    if (!prefillAnswers || typeof prefillAnswers !== "object") return 0;
    for (let i = 0; i < visibleQuestions.length; i++) {
      const id = visibleQuestions[i].id;
      const v = prefillAnswers[id];
      const filled = !(v === undefined || v === null || v === "");
      if (!filled) return i;
    }
    return 0;
  })();
  // ── Regroupement des questions par écran (2 à 3 par groupe) ──────────────
  // On chunk la liste en groupes : 3 questions max par écran, mais on limite à
  // 2 si le groupe contient un slider (range) pour éviter des écrans trop hauts.
  const groups = (() => {
    const res = [];
    let cur = [];
    let curHasRange = false;
    for (const qq of visibleQuestions) {
      const isRange = qq.type === "range";
      const cap = (curHasRange || isRange) ? 2 : 3;
      if (cur.length >= cap) { res.push(cur); cur = []; curHasRange = false; }
      cur.push(qq);
      if (isRange) curHasRange = true;
    }
    if (cur.length) res.push(cur);
    return res;
  })();
  // Index du groupe contenant la 1ère question non répondue (pour le prefill).
  const groupOfQuestion = (qi) => {
    let count = 0;
    for (let g = 0; g < groups.length; g++) {
      if (qi < count + groups[g].length) return g;
      count += groups[g].length;
    }
    return 0;
  };
  const [currentGroup, setCurrentGroup] = useState(groupOfQuestion(firstUnanswered));
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!prefillAnswers || typeof prefillAnswers !== "object") return;
    setAnswers(prev => {
      const next = { ...prev };
      let changed = false;
      for (const k in prefillAnswers) {
        const incoming = prefillAnswers[k];
        if (incoming === undefined || incoming === null || incoming === "") continue;
        const existing = prev[k];
        const isEmpty = existing === undefined || existing === null || existing === "";
        if (isEmpty) { next[k] = incoming; changed = true; }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillAnswers]);

  const safeGroup = Math.min(currentGroup, Math.max(0, groups.length - 1));
  const groupQuestions = groups[safeGroup] || [];
  const progress = (safeGroup / groups.length) * 100;
  const [error, setError] = useState("");

  const valOf = (qq) => answers[qq.id] ?? (qq.type === "range" ? qq.default : "");
  const setValOf = (qq, v) => { setError(""); setAnswers(a => ({ ...a, [qq.id]: v })); };

  const safeComplete = (merged) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    onComplete(merged);
  };

  const isLastGroup = safeGroup >= groups.length - 1;

  const next = () => {
    if (submittingRef.current) return;
    // Construire les réponses du groupe + valider les champs requis non optionnels.
    const merged = { ...answers };
    for (const qq of groupQuestions) {
      const cv = answers[qq.id] ?? (qq.type === "range" ? qq.default : "");
      merged[qq.id] = cv;
      if (required && !isLastGroup && !qq.optional) {
        const isEmpty = qq.type === "range" ? false : !cv || String(cv).trim() === "";
        if (isEmpty) { setError("Merci de répondre aux questions de cette étape avant de continuer."); return; }
      }
    }
    setError("");
    setAnswers(merged);
    if (!isLastGroup) setCurrentGroup(safeGroup + 1);
    else safeComplete(merged);
  };
  const prev = () => { setError(""); if (safeGroup > 0) setCurrentGroup(safeGroup - 1); };
  const skipAll = () => { if (!submittingRef.current) safeComplete({}); };

  // Rendu d'un champ unique (réutilisé pour chaque question du groupe).
  const renderField = (q) => {
    const val = valOf(q);
    const setVal = (v) => setValOf(q, v);
    return (
      <div key={q.id} style={{ marginBottom: "1.6rem" }}>
        <div className="step-question" style={{ display:"flex", alignItems:"flex-start", gap:".3rem", fontSize:"1rem", marginBottom:".9rem" }}>
          <span>{q.text}</span>
          <QuestionTooltip info={q.info} />
          {q.optional && <span style={{ fontSize:".7rem", color:"var(--muted)", fontWeight:400, marginLeft:".3rem" }}>(facultatif)</span>}
        </div>
        {renderFieldInput(q, val, setVal, answers)}
      </div>
    );
  };

  return (
    <div>
      <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>

      {groupQuestions.map(q => renderField(q))}

      {error && (
        <div style={{ marginTop: ".4rem", padding: ".5rem .8rem", background: "rgba(255,60,92,.08)", border: "1px solid rgba(255,60,92,.25)", borderRadius: 4, fontSize: ".78rem", color: "var(--red)" }}>
          ⚠ {error}
        </div>
      )}
      <div style={{ display: "flex", gap: ".8rem", marginTop: "1rem", alignItems:"center" }}>
        {currentGroup > 0 && <button className="btn btn-ghost" onClick={prev}>← Retour</button>}
        <button
          className={`btn btn-accent${submitting ? " btn-disabled" : ""}`}
          onClick={next}
          disabled={submitting}
          style={{ marginLeft: "auto", opacity: submitting ? .6 : 1, cursor: submitting ? "not-allowed" : "pointer" }}>
          {submitting && isLastGroup
            ? "Analyse en cours…"
            : (isLastGroup ? "Voir les résultats →" : "Suivant →")}
        </button>
      </div>
    </div>
  );
}

// ── Champ « range » avec saisie directe agrandie + option Budget ultra premium ──
function RangeField({ q, val, setVal }) {
  const isBudget = q.id === "budget";
  // Limite haute étendue pour les véhicules ultra-premium (au-delà de 300 000 €).
  const ULTRA_MAX = 2000000;
  const [ultra, setUltra] = useState(isBudget && Number(val) > q.max);
  const effMax = isBudget && ultra ? ULTRA_MAX : q.max;

  const clamp = (v) => Math.max(q.min, Math.min(effMax, Number(v) || q.min));

  return (
    <div>
      <div className="range-row">
        <input type="range" min={q.min} max={effMax} step={q.step || 1}
          value={Math.min(val, effMax)} onChange={e => setVal(Number(e.target.value))} />
        <div className="range-val">{Number(val).toLocaleString("fr-FR")}{q.unit}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: ".7rem", marginTop: ".6rem", flexWrap: "wrap" }}>
        <span style={{ fontSize: ".72rem", color: "var(--muted)" }}>Ou saisir directement :</span>
        <input
          className="num-spin"
          type="number" min={q.min} max={effMax} step={q.step || 1}
          value={val}
          onChange={e => setVal(clamp(e.target.value))}
          style={{
            width: 150, background: "var(--card)", border: "1px solid var(--border)",
            borderRadius: 8, color: "var(--text)",
            fontFamily: "var(--font-mono)", outline: "none",
          }}
          onFocus={e => e.target.style.borderColor = "var(--accent)"}
          onBlur={e => e.target.style.borderColor = "var(--border)"}
        />
        <span style={{ fontSize: ".72rem", color: "var(--muted)" }}>{q.unit}</span>
      </div>
      {isBudget && (
        <label style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", marginTop: ".8rem", cursor: "pointer", fontSize: ".82rem", color: "var(--sub)" }}>
          <input
            type="checkbox"
            checked={ultra}
            onChange={e => {
              const on = e.target.checked;
              setUltra(on);
              if (!on && Number(val) > q.max) setVal(q.max); // re-borne si on désactive
            }}
            style={{ width: 16, height: 16, accentColor: "var(--accent)", cursor: "pointer" }}
          />
          <span>💎 <strong>Budget ultra premium</strong> — dépasser 300 000 € (véhicules d'exception)</span>
        </label>
      )}
    </div>
  );
}

// ── Rendu d'un champ de questionnaire selon son type (extrait pour réutilisation) ──
function renderFieldInput(q, val, setVal, answers) {
  return (<>
      {q.type === "radio" && (
        <div className="radio-group">
          {q.options.map(o => (
            <div key={o} className={`radio-btn ${val === o ? "active" : ""}`} onClick={() => setVal(o)}>{o}</div>
          ))}
        </div>
      )}
      {q.type === "text" && q.id === "make" && (
        <MakeModelAutocomplete value={val} onChange={setVal} placeholder={q.placeholder} />
      )}
      {q.type === "text" && q.id !== "make" && (
        <input className="input" placeholder={q.placeholder} value={val}
          onChange={e => setVal(e.target.value)} />
      )}
      {q.type === "range" && (
        <RangeField q={q} val={val} setVal={setVal} />
      )}

      {q.type === "select_dynamic" && (() => {
        const source = (answers && answers[q.sourceField]) || "";
        const finitions = getFinitionsForMake(source);
        if (!source) {
          return (
            <div style={{ padding: "1rem 1.2rem", background: "var(--deep)", border: "1px dashed var(--border)", borderRadius: 8, color: "var(--muted)", fontSize: ".85rem", fontStyle: "italic" }}>
              {q.placeholderEmpty || "Saisissez d'abord la marque/modèle"}
            </div>
          );
        }
        if (finitions.length === 0) {
          return (
            <div>
              <div style={{ padding: ".7rem 1rem", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.2)", borderRadius: 8, fontSize: ".82rem", color: "var(--sub)", marginBottom: ".6rem" }}>
                💡 Marque/modèle non reconnu dans le catalogue de finitions. Vous pouvez saisir manuellement le nom de votre finition ci-dessous.
              </div>
              <input
                className="input" placeholder="Ex : GT-Line, Sport, Premium..."
                value={val}
                onChange={e => setVal(e.target.value)}
              />
            </div>
          );
        }
        return (
          <div>
            <div className="radio-group" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: ".5rem" }}>
              {finitions.map(o => (
                <div key={o} className={`radio-btn ${val === o ? "active" : ""}`}
                  style={{ fontSize: ".88rem", padding: ".55rem .8rem", textAlign: "left" }}
                  onClick={() => setVal(o)}>{o}</div>
              ))}
              <div className={`radio-btn ${val === "Autre / Inconnue" ? "active" : ""}`}
                style={{ fontSize: ".88rem", padding: ".55rem .8rem", textAlign: "left", fontStyle: "italic", opacity: .8 }}
                onClick={() => setVal("Autre / Inconnue")}>Autre / Je ne sais pas</div>
            </div>
            <div style={{ fontSize: ".7rem", color: "var(--muted)", marginTop: ".6rem", fontStyle: "italic" }}>
              💡 {finitions.length} finition{finitions.length > 1 ? "s" : ""} suggérée{finitions.length > 1 ? "s" : ""} pour « {source.split(" ").slice(0, 3).join(" ")} »
            </div>
          </div>
        );
      })()}

      {q.type === "multi_select" && (() => {
        const selected = (val && typeof val === "string") ? val.split(" · ").filter(Boolean) : [];
        const isSelected = (opt) => selected.includes(opt);
        const toggle = (opt) => {
          const next = isSelected(opt) ? selected.filter(x => x !== opt) : [...selected, opt];
          setVal(next.join(" · "));
        };
        return (
          <div>
            <div className="radio-group" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: ".5rem" }}>
              {q.options.map(o => (
                <div key={o}
                  className={`radio-btn ${isSelected(o) ? "active" : ""}`}
                  style={{
                    fontSize: ".82rem", padding: ".6rem .8rem", textAlign: "left",
                    display: "flex", alignItems: "center", gap: ".55rem",
                  }}
                  onClick={() => toggle(o)}>
                  <span style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: `1.5px solid ${isSelected(o) ? "var(--accent)" : "var(--border-strong)"}`,
                    background: isSelected(o) ? "var(--accent)" : "transparent",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontSize: ".7rem", fontWeight: 700,
                    transition: "all .15s ease"
                  }}>
                    {isSelected(o) && "✓"}
                  </span>
                  <span style={{ flex: 1, lineHeight: 1.35 }}>{o}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: ".7rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: ".4rem" }}>
              <span>{selected.length === 0 ? "💡 Aucune option sélectionnée" : `✓ ${selected.length} option${selected.length > 1 ? "s" : ""} sélectionnée${selected.length > 1 ? "s" : ""}`}</span>
              {selected.length >= 6 && (
                <span style={{ color: "var(--accent)", fontWeight: 600 }}>🏆 Bonus version bien équipée</span>
              )}
            </div>
          </div>
        );
      })()}
  </>);
}

// ─── Buy Panel ────────────────────────────────────────────────────────────────

// ─── Fallback suggestions sans IA ────────────────────────────────────────────
// ⚠ DEPRECATED — Cette fonction génère des modèles avec prix calculés en pourcentages du budget
// (ex: Mégane à 187k€ si budget 199k€). Elle a été retirée du flow normal car elle affichait
// des données trompeuses quand l'API Claude échouait. Conservée en cas de besoin futur, mais
// NE PAS L'APPELER depuis runSearch — préférer afficher un message d'erreur clair à l'utilisateur.
function generateFallbackSuggestions(ans) {
  const budget = parseInt(ans?.budget) || 15000;
  const anneeMin = parseInt(ans?.year) || 2018;
  const kmMax = parseInt(ans?.km) || 100000;
  const marques = (ans?.brands || "").toLowerCase();
  // Adapter la sélection au profil
  const prefRenault = !marques || marques.includes("renault") || marques.includes("toutes");
  const prefPeugeot = !marques || marques.includes("peugeot") || marques.includes("toutes");
  const prefVW = !marques || marques.includes("volkswagen") || marques.includes("vw") || marques.includes("toutes");
  const importOk = ans?.import === "Oui" || ans?.import === "Peu importe";
  const suggestions = [
    { make: prefRenault ? "Renault" : "Toyota", model: prefRenault ? "Mégane IV 1.3 TCe 140" : "Corolla 1.8 HSD", year: Math.max(anneeMin, 2021), km: Math.min(54000, kmMax), price: Math.floor(budget*0.94), fuel: "Essence", gearbox: "Automatique", color: "Gris Titanium", platform: "AutoScout24", match: 97, why_match: `Budget parfaitement respecté, ${anneeMin <= 2021 ? "année compatible" : "modèle récent"}`, tags: ["Pro certifié", "Garantie 6 mois"], pros: ["Faible consommation", "Confort excellent", "Garantie incluse"], cons: ["Prix légèrement au-dessus marché"], argus_low: Math.floor(budget*0.90), argus_high: Math.floor(budget*0.98), photo_query: prefRenault ? "Renault Megane 2021 grey" : "Toyota Corolla 2021" },
    { make: prefPeugeot ? "Peugeot" : "Honda", model: prefPeugeot ? "308 1.2 PureTech 130" : "Civic 1.0 VTEC", year: Math.max(anneeMin, 2020), km: Math.min(67000, kmMax), price: Math.floor(budget*0.81), fuel: "Essence", gearbox: "Manuelle", color: "Blanc Nacré", platform: "LeBonCoin", match: 91, why_match: "Prix 19% sous budget — marge de négociation possible", tags: ["Particulier", "CT OK"], pros: ["Prix négociable", "Entretien récent", "Bonne fiabilité"], cons: ["Kilométrage élevé"], argus_low: Math.floor(budget*0.78), argus_high: Math.floor(budget*0.85), photo_query: prefPeugeot ? "Peugeot 308 2020 white" : "Honda Civic 2020" },
    { make: prefVW ? "Volkswagen" : "Seat", model: prefVW ? "Golf VIII 1.5 eTSI" : "Leon 1.5 TSI FR", year: Math.max(anneeMin, 2022), km: Math.min(38000, kmMax), price: Math.floor(budget*1.05), fuel: "Hybride légère", gearbox: "DSG7", color: "Bleu Atlantic", platform: "La Centrale", match: 88, why_match: "Légèrement au-dessus du budget mais kilométrage très faible", tags: ["Pro certifié", "0 accident"], pros: ["Très récent", "Technologie hybride", "Réseau garage dense"], cons: ["Légèrement hors budget"], argus_low: Math.floor(budget*1.0), argus_high: Math.floor(budget*1.12), photo_query: prefVW ? "Volkswagen Golf 2022 blue" : "Seat Leon FR 2022" },
    { make: "Toyota", model: "Yaris 1.5 HSD", year: Math.max(anneeMin, 2021), km: Math.min(42000, kmMax), price: Math.floor(budget*0.87), fuel: "Hybride", gearbox: "CVT", color: "Rouge Bordeaux", platform: "Aramisauto", match: 84, why_match: `Hybride — zéro malus ${ans?.malus !== "Non" ? "et économies carburant" : ""}`, tags: ["Label occasion", "Hybride économique"], pros: ["Fiabilité Toyota #1 mondial", "Très économique à l'usage", "Pas de malus"], cons: ["Coffre limité"], argus_low: Math.floor(budget*0.84), argus_high: Math.floor(budget*0.91), photo_query: "Toyota Yaris hybrid 2021 red" },
    { make: "Ford", model: "Focus 1.0 EcoBoost 125", year: Math.max(anneeMin, 2020), km: Math.min(58000, kmMax), price: Math.floor(budget*0.76), fuel: "Essence", gearbox: "Manuelle", color: "Gris Magnétique", platform: "ParuVendu", match: 79, why_match: "Meilleur rapport espace/prix de la sélection", tags: ["Particulier", "Révision récente"], pros: ["Très spacieux pour ce prix", "Révision récente", "Consommation maîtrisée"], cons: ["Moins valorisé à la revente"], argus_low: Math.floor(budget*0.73), argus_high: Math.floor(budget*0.80), photo_query: "Ford Focus 2020 grey" },
  ];
  // Pad to 10 with variations
  const more = [
    { make: importOk ? "Opel" : "Citroën", model: importOk ? "Astra 1.2 Turbo 110" : "C4 1.2 PureTech 130", year: Math.max(anneeMin, 2021), km: Math.min(49000, kmMax), price: Math.floor(budget*0.72), fuel: "Essence", gearbox: "Manuelle", color: "Gris Artense", platform: "Vroom", match: 77, why_match: importOk ? "Import Allemagne — prix 28% sous budget" : "Citroën fiable, budget maîtrisé", tags: [importOk ? "Import EU" : "Origine France", "CT OK"], pros: ["Excellent rapport Q/P", "Peu concurrencé", "Fiable"], cons: ["Réseau moins dense"], argus_low: Math.floor(budget*0.70), argus_high: Math.floor(budget*0.76), photo_query: importOk ? "Opel Astra 2021 grey" : "Citroen C4 2021 grey" },
    { make: "Skoda", model: "Octavia 1.0 TSI 110", year: Math.max(anneeMin, 2020), km: Math.min(72000, kmMax), price: Math.floor(budget*0.78), fuel: "Essence", gearbox: "Manuelle", color: "Gris Quartz", platform: "Motorway", match: 80, why_match: "Coffre 600L — espace premium pour ce budget", tags: ["Espace familial", "Groupe VW"], pros: ["Coffre 600L", "Fiabilité Groupe VW", "Prix imbattable"], cons: ["Kilométrage élevé"], argus_low: Math.floor(budget*0.75), argus_high: Math.floor(budget*0.82), photo_query: "Skoda Octavia 2020 grey" },
    { make: "Hyundai", model: "i30 1.0 T-GDI 120", year: Math.max(anneeMin, 2021), km: Math.min(44000, kmMax), price: Math.floor(budget*0.79), fuel: "Essence", gearbox: "Manuelle", color: "Blanc Polar", platform: "Mandataire Auto", match: 76, why_match: "Garantie constructeur potentiellement active", tags: ["Garantie 5 ans", "Très fiable"], pros: ["Garantie Hyundai restante", "Fiabilité reconnue", "Prix négociable"], cons: ["Moins valorisé à la revente"], argus_low: Math.floor(budget*0.76), argus_high: Math.floor(budget*0.83), photo_query: "Hyundai i30 2021 white" },
    { make: "Mazda", model: "3 2.0 Skyactiv-G 122", year: Math.max(anneeMin, 2020), km: Math.min(61000, kmMax), price: Math.floor(budget*0.83), fuel: "Essence", gearbox: "Automatique", color: "Rouge Soul", platform: "Carflow", match: 78, why_match: "Finition intérieure premium pour ce segment", tags: ["Design premium", "Rare sur le marché"], pros: ["Finition intérieure luxueuse", "Moteur très fiable", "Rare = décotée"], cons: ["Marque moins prisée à la revente"], argus_low: Math.floor(budget*0.80), argus_high: Math.floor(budget*0.87), photo_query: "Mazda 3 2020 red" },
    { make: "Dacia", model: "Sandero Stepway TCe 90", year: Math.max(anneeMin, 2022), km: Math.min(35000, kmMax), price: Math.floor(budget*0.62), fuel: "Essence", gearbox: "Manuelle", color: "Vert Mistral", platform: "Promoneuve", match: 74, why_match: `Budget libéré de ${Math.floor(budget*0.38).toLocaleString("fr-FR")} € — idéal premier véhicule`, tags: ["Neuf décote", "Budget économique"], pros: ["Très peu kilométré", "Fiabilité Dacia prouvée", "Coût entretien minimal"], cons: ["Finitions basiques"], argus_low: Math.floor(budget*0.59), argus_high: Math.floor(budget*0.66), photo_query: "Dacia Sandero Stepway 2022 green" },
  ];
  return [...suggestions, ...more].slice(0, 10).map(s => ({ ...s, priceFormatted: s.price.toLocaleString("fr-FR") + " €", img: null }));
}


function CallbackModal({ onClose, user, answers, type, onShowRecharge, onWalletChange }) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Détecte particulier vs pro pour proposer le bon parcours
  const isPro = !!(user?.is_pro);
  // On ne propose le rapport premium que pour le type "sell" et pour les particuliers
  const offerPremiumReport = type === "sell" && !isPro;

  const details = useMemo(() => {
    if (!answers) return "";
    if (type === "buy") {
      return [
        answers.budget && "Budget: " + Number(answers.budget).toLocaleString("fr-FR") + " €",
        answers.fuel && "Motorisation: " + answers.fuel,
        answers.segment && "Segment: " + answers.segment,
        answers.brands && "Marques: " + answers.brands,
        answers.year && "Année min: " + answers.year,
        answers.km && "Km max: " + Number(answers.km).toLocaleString("fr-FR") + " km",
        answers.gearbox && "Boîte: " + answers.gearbox,
        answers.usage && "Usage: " + answers.usage,
      ].filter(Boolean).join("\n");
    }
    if (type === "sell") {
      return [
        answers.make && "Véhicule: " + answers.make,
        answers.year_sell && "Année: " + answers.year_sell,
        answers.km_sell && "Km: " + Number(answers.km_sell).toLocaleString("fr-FR") + " km",
        answers.fuel_sell && "Motorisation: " + answers.fuel_sell,
        answers.gearbox_sell && "Boîte: " + answers.gearbox_sell,
        answers.state && "État: " + answers.state,
        answers.ct && "CT: " + answers.ct,
        answers.urgency && "Urgence: " + answers.urgency,
      ].filter(Boolean).join("\n");
    }
    return "";
  }, [answers, type]);

  // Persiste le choix "ne plus afficher" si coché
  const persistDontShow = () => {
    if (dontShowAgain) {
      try { localStorage.setItem("viq_callback_optout", "1"); } catch(e) {}
    }
  };

  // ─── BRANCHE 1 : Particulier Sell → Rapport premium à 7,99 € (10 recharges) ──
  const handleYesPremiumReport = async () => {
    persistDontShow();
    const COST = 10;
    const ok = await chargeForAction({
      cost: COST, label: "rapport_premium_sell",
      onSpent: ({ newBalance }) => {
        if (onWalletChange) onWalletChange();
      },
      onInsufficient: ({ missing }) => {
        // Limite mensuelle atteinte → ouvrir tarifs
        alert("Limite mensuelle atteinte. Le rapport premium est facturé 7,99 €. Activez votre moyen de paiement dans Mon espace pour continuer.");
        if (onShowRecharge) onShowRecharge();
        onClose();
      },
      action: () => {
        // Le débit a réussi → on envoie l'email à VehicleIQ pour produire le rapport
        const subject = encodeURIComponent("Rapport premium VehicleIQ — Commande");
        const body = encodeURIComponent(
          "Bonjour l'équipe VehicleIQ,\n\n" +
          "Un utilisateur a commandé un RAPPORT PREMIUM (7,99 € / 10 recharges débitées).\n\n" +
          "=== COORDONNÉES CLIENT ===\n" +
          "Nom : " + (user?.name || "Non renseigné") + "\n" +
          "Email : " + (user?.email || "Non renseigné") + "\n" +
          "UID : " + (user?.uid || "") + "\n\n" +
          "=== DÉTAILS DU VÉHICULE ===\n" + details + "\n\n" +
          "À traiter dans les 24 ouvrées.\n\n" +
          "Cordialement,\nVehicleIQ"
        );
        window.location.href = "mailto:bonglorentz@gmail.com?cc=contact@vehicleiq.fr&subject=" + subject + "&body=" + body;
        // Toast de confirmation
        setTimeout(() => {
          alert("✅ Commande enregistrée !\n\n10 recharges ont été débitées de votre wallet.\nVous recevrez votre rapport premium par email dans les 24h ouvrées.");
          onClose();
        }, 500);
      },
    });
    // Si chargeForAction a redirigé vers la recharge ou affiché l'alerte, on a déjà géré
    if (!ok) return;
  };

  // ─── BRANCHE 2 : Tous autres cas (Buy, Pro Sell) → Demande de rappel téléphonique ──
  const handleYesCallback = () => {
    persistDontShow();
    const subject = encodeURIComponent("Demande de rappel VehicleIQ — " + (type === "buy" ? "Achat" : "Vente"));
    const body = encodeURIComponent(
      "Bonjour,\n\nUn utilisateur souhaite être rappelé concernant son " +
      (type === "buy" ? "projet d'achat" : "projet d'estimation") + ".\n\n" +
      "=== COORDONNÉES ===\n" +
      "Nom : " + (user?.name || "Non renseigné") + "\n" +
      "Email : " + (user?.email || "Non renseigné") + "\n" +
      "UID : " + (user?.uid || "") + "\n\n" +
      "=== DÉTAILS DE LA REQUÊTE ===\n" + details + "\n\n" +
      "Cordialement,\nVehicleIQ"
    );
    window.location.href = "mailto:bonglorentz@gmail.com?cc=contact@vehicleiq.fr&subject=" + subject + "&body=" + body;
    onClose();
  };

  const handleYes = offerPremiumReport ? handleYesPremiumReport : handleYesCallback;

  const handleNo = () => {
    persistDontShow();
    onClose();
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && handleNo()}>
      <div style={{
        background: "var(--card)", border: "1px solid var(--border)", borderRadius: 16,
        padding: "2rem 2rem 1.4rem", maxWidth: 460, width: "90%",
        boxShadow: "var(--shadow-xl)",
        textAlign: "center",
        animation: "fadeInUp .4s ease"
      }}>
        {offerPremiumReport ? (
          <>
            <div style={{ fontSize: "2.5rem", marginBottom: ".8rem" }}>✨</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", fontWeight: 600, marginBottom: ".6rem", color: "var(--text)" }}>
              Rapport premium personnalisé
            </div>
            <div style={{ fontSize: ".88rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1rem" }}>
              Souhaitez-vous obtenir un <strong style={{ color: "var(--text)" }}>rapport premium détaillé</strong> sur votre véhicule, rédigé par notre équipe d'experts ?
            </div>

            {/* Encart valeur ajoutée */}
            <div style={{
              background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.25)",
              borderRadius: 10, padding: "1rem", marginBottom: "1rem",
              textAlign: "left", fontSize: ".82rem", lineHeight: 1.7
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".5rem" }}>
                <span style={{ fontWeight: 600, color: "var(--text)" }}>📊 Ce que vous recevez</span>
                <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "var(--accent)" }}>7,99 €</span>
              </div>
              <ul style={{ margin: 0, paddingLeft: "1rem", color: "var(--sub)" }}>
                <li>Analyse marché détaillée (24 mois historique)</li>
                <li>Stratégie de vente personnalisée par marque/segment</li>
                <li>Annonce optimisée prête à publier</li>
                <li>Top 10 plateformes recommandées avec scoring</li>
                <li>Livraison sous 24h ouvrées par email</li>
              </ul>
              <div style={{ marginTop: ".7rem", paddingTop: ".5rem", borderTop: "1px dashed rgba(168,128,58,.3)", fontSize: ".74rem", color: "var(--muted)", textAlign: "center" }}>
                Paiement unique · 7,99 € · facturé sur votre carte
              </div>
            </div>

            <div style={{ display: "flex", gap: ".6rem", justifyContent: "center", marginBottom: "1.1rem", flexDirection: "column" }}>
              <button className="btn btn-accent" style={{ width: "100%", fontSize: ".95rem", justifyContent: "center", padding: ".7rem" }} onClick={handleYes}>
                ✅ Oui, je commande pour 7,99 €
              </button>
              <button className="btn btn-ghost" style={{ width: "100%", fontSize: ".82rem", justifyContent: "center" }} onClick={handleNo}>
                Non merci, je continue seul
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: "2.5rem", marginBottom: ".8rem" }}>📞</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", fontWeight: 600, marginBottom: ".6rem", color: "var(--text)" }}>
              Être rappelé par VehicleIQ
            </div>
            <div style={{ fontSize: ".88rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
              Un conseiller VehicleIQ peut vous accompagner personnellement dans votre projet {type === "buy" ? "d'achat" : "d'estimation"}.
            </div>
            <div style={{ display: "flex", gap: ".8rem", justifyContent: "center", marginBottom: "1.1rem" }}>
              <button className="btn btn-accent" style={{ flex: 1, fontSize: ".9rem", justifyContent: "center" }} onClick={handleYes}>
                ✅ Oui, me rappeler
              </button>
              <button className="btn btn-ghost" style={{ flex: 1, fontSize: ".9rem", justifyContent: "center" }} onClick={handleNo}>
                Non merci
              </button>
            </div>
          </>
        )}

        {/* Option "Ne plus afficher" */}
        <label style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", cursor: "pointer", userSelect: "none",
          fontSize: ".75rem", color: "var(--muted)", padding: ".25rem .5rem", borderRadius: 6 }}>
          <input type="checkbox" checked={dontShowAgain} onChange={e => setDontShowAgain(e.target.checked)}
            style={{ cursor: "pointer", accentColor: "var(--accent)" }} />
          Ne plus afficher ce message
        </label>

        <div style={{ fontSize: ".65rem", color: "var(--muted)", marginTop: ".9rem" }}>
          vehicleiq.fr · contact@vehicleiq.fr
        </div>
      </div>
    </div>
  );
}

function BuyPanel({ onClose, user, onShowAuth, onWalletChange, onShowRecharge }) {
  // Lire le prefill depuis le replay historique
  const prefill = (() => {
    try { const p = window.__viq_buy_prefill; if (p) { setTimeout(() => { try { window.__viq_buy_prefill = null; } catch (e) {} }, 500); return p; } } catch(e) {}
    return null;
  })();
  const [step, setStep] = useState("questions");
  const [loading, setLoading] = useState(false);
  const [loadIdx, setLoadIdx] = useState(0);
  const [results, setResults] = useState(null);
  // State d'erreur (option A : on N'AFFICHE PAS de fallback bidon, on AVERTIT l'utilisateur)
  const [searchError, setSearchError] = useState(null);
  const [showGate, setShowGate] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [paid, setPaid] = useState(false);
  const paidRef = useRef(false); // lecture synchrone — empêche le double-débit (closure stale de runSearch)
  const [answers, setAnswers] = useState(null);
  const [saved, setSaved] = useState(false);
  const [showScreenshotComingSoon, setShowScreenshotComingSoon] = useState(false);
  const [showCallback, setShowCallback] = useState(false);
  const [callbackAnswers, setCallbackAnswers] = useState(null);

  const handleComplete = (ans) => {
    // ── Vérifier le solde AVANT de lancer le service (économie tokens IA) ──
    // Côté UI : invisible — limite mensuelle plutôt que crédits.
    const SERVICE_COST = 1;
    const currentBalance = getWallet();
    if (currentBalance < SERVICE_COST) {
      // Limite atteinte : rediriger vers la page tarification
      setAnswers(ans);
      setCallbackAnswers(ans);
      try { window.dispatchEvent(new CustomEvent("viq-open-pricing")); } catch(_) {}
      return;
    }
    // Débiter immédiatement et débloquer (le service est payé d'avance)
    if (spendWallet(SERVICE_COST)) {
      if (window.__viq_uid) {
        fetch(`${window.__viq_api_url||"https://vehicleiq-backend-production.up.railway.app"}/api/wallet/spend`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${window.__viq_uid}` },
          body: JSON.stringify({ cost: SERVICE_COST, label: "Résultats personnalisés + liens annonces" })
        }).catch(() => {});
      }
      paidRef.current = true; // synchrone — runSearch verra la bonne valeur
      setPaid(true);
      if (onWalletChange) onWalletChange();
    }
    setAnswers(ans);
    setCallbackAnswers(ans);
    runSearch(ans);
  };

  const BUY_LOAD_STEPS = [
    { icon: "👤", label: "Analyse de votre profil d'acheteur…", detail: "Budget · Marques · Motorisation · Kilomètrage cible · Localisation" },
    { icon: "📚", label: "Interrogation de la base catalogue…", detail: "950+ modèles · Prix constructeurs France 2025-2026 · Versions et finitions" },
    { icon: "🌐", label: "Recherche web IA temps réel…", detail: "LeBonCoin · AutoScout24 · La Centrale · Facebook Marketplace · Aramisauto" },
    { icon: "📊", label: "Calcul des médianes marché…", detail: "Annonces actives 30j · Filtrage outliers · Pondération par récence" },
    { icon: "📅", label: "Application du coefficient saisonnier…", detail: "Variation par mois × type de carrosserie · 8 segments couverts" },
    { icon: "📍", label: "Ajustement régional…", detail: "100+ codes postaux · Tension marché · Île-de-France, métropoles, rural" },
    { icon: "🛡️", label: "Croisement watchlist mécanique…", detail: "11 défauts modèles documentés · DSG7, N47, EA189, Puretech, EcoBoost…" },
    { icon: "📈", label: "Application du momentum marché…", detail: "Tendance 30j/90j par segment · EV, hybride, diesel, premium" },
    { icon: "🤖", label: "Calcul VIQ.AI® Engine v5…", detail: "Multiplicateur pays · Dépréciation · Options · Calibration Bayésienne" },
    { icon: "🎯", label: "Génération des 10 recommandations…", detail: "Classement par score · Vérification budget · Tri plateforme optimale" },
  ];

  const runSearch = async (ans) => {
    setLoading(true); setStep("loading");
    setLoadIdx(0);
    setSearchError(null); // reset erreur précédente

    // Option A : pas de fallback frontend. Si Claude échoue, on affiche un message clair.
    let results = null;
    let errorDetail = null;
    let noResultsMsg = null;
    try {
      results = await runSteppedLoader({
        steps: BUY_LOAD_STEPS,
        setIdx: setLoadIdx,
        tickMs: 1200,
        minTotalMs: 7000,
        work: async () => {
          // v24 : recherche 100% locale (catalogue + cache marché), plus d'appel
          // IA en direct — mais on garde le mode asynchrone par cohérence avec
          // le reste de l'app (job + polling).
          const data = await apiPostAsync("/api/buy/suggest", ans);
          if (!data || !data.success) {
            throw new Error(data?.error || "Réponse backend invalide");
          }
          if (!data.data || !data.data.length) {
            // v24 : un résultat vide n'est plus une erreur — c'est une réponse
            // honnête ("aucun véhicule du catalogue ne correspond"), distincte
            // d'une vraie panne. On ne lève pas d'exception dans ce cas.
            if (data.no_results) {
              noResultsMsg = data.message || "Aucun véhicule du catalogue ne correspond à ces critères.";
              return [];
            }
            throw new Error("Aucune recommandation disponible pour le moment.");
          }
          return data.data;
        },
      });
    } catch(e) {
      console.error("[buy/suggest] error:", e.status, e.message, e.detail);
      if (isPaymentRequiredError(e)) {
        // Paiement requis (quota gratuit épuisé) → message + ouverture onglet Paiement
        handlePaymentRequired(e);
        setLoading(false);
        setStep("form");
        return;
      }
      errorDetail = {
        message: e.message || "Erreur inconnue",
        status: e.status || null,
        // Diagnostic technique pour l'utilisateur (utile à transmettre au support)
        timestamp: new Date().toISOString(),
      };
    }

    setLoading(false);

    if (noResultsMsg) {
      // v24 : résultat vide légitime — écran distinct, ton neutre (pas une panne).
      setSearchError({ message: noResultsMsg, isNoResults: true, timestamp: new Date().toISOString() });
      setStep("error");
      return;
    }

    if (errorDetail || !results) {
      // Pas de résultat : on affiche l'erreur, on ne stocke RIEN dans l'historique
      setSearchError(errorDetail || { message: "Aucun résultat", timestamp: new Date().toISOString() });
      setStep("error");
      return;
    }

    setResults(results);
    setStep("result");
    // Sauvegarder les 10 résultats dans l'historique local (uniquement si succès)
    try {
      const existing = JSON.parse(localStorage.getItem("viq_history") || "[]");
      const entry = { id: Date.now(), date: new Date().toISOString(), type: "buy", answers: ans, results: { count: (results||[]).length, models: results||[], suggestions: results||[] }, data: ans };
      localStorage.setItem("viq_history", JSON.stringify([entry, ...existing].slice(0, 50)));
    } catch(e) {}
    if (!paidRef.current) setTimeout(() => setShowGate(true), 400);
  };

  return (
    <>
      <div className="overlay">
        <div className="panel panel-wide">
          <div className="panel-header">
            <div>
              <div className="panel-title" style={{ color: "var(--green)" }}>Acheter un véhicule</div>
              <div className="panel-sub">Trouvez le véhicule parfait selon votre profil</div>
            </div>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="panel-body">
            {!((user?.siret && user.siret.length > 3) || user?.is_pro) ? (
              <ProFeatureGate title="Aide à l'achat — réservé aux comptes Pro"
                onUpgrade={() => window.dispatchEvent(new CustomEvent("viq-open-pricing"))} />
            ) : (
            <>
            {step === "questions" && (
              <div>
                <Questionnaire questions={BUY_QUESTIONS} onComplete={handleComplete} prefillAnswers={prefill} />
              </div>
            )}
            {step === "loading" && (
              <LoadingExperience
                steps={BUY_LOAD_STEPS}
                activeIdx={loadIdx}
                make={(answers?.brands && answers.brands !== "toutes marques" ? answers.brands : "") || callbackAnswers?.brands || ""}
                version="5"
              />
            )}
            {step === "error" && searchError && (
              <div style={{ padding: "2rem 1rem", textAlign: "center" }}>
                <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>{searchError.isNoResults ? "🔍" : "⚠️"}</div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", fontWeight: 700, marginBottom: ".5rem", color: "var(--text)" }}>
                  {searchError.isNoResults ? "Aucun véhicule ne correspond à ces critères" : "Service temporairement indisponible"}
                </div>
                <div style={{ fontSize: ".88rem", color: "var(--sub)", marginBottom: "1.5rem", lineHeight: 1.6, maxWidth: 520, margin: "0 auto 1.5rem" }}>
                  {searchError.isNoResults
                    ? (searchError.message || "Essayez d'élargir la marque, le budget ou le kilométrage pour élargir la recherche.")
                    : "Le moteur de recherche n'a pas pu traiter votre demande à l'instant. Plutôt que de vous afficher des cotes approximatives qui pourraient vous induire en erreur, nous préférons vous demander de réessayer dans quelques instants."}
                </div>
                {!searchError.isNoResults && (
                <div style={{ background: "rgba(168,128,58,.06)", border: "1px solid rgba(168,128,58,.18)", borderRadius: 8, padding: ".7rem 1rem", fontSize: ".74rem", color: "var(--sub)", marginBottom: "1.5rem", maxWidth: 520, margin: "0 auto 1.5rem", textAlign: "left" }}>
                  <div style={{ fontWeight: 700, color: "var(--accent)", marginBottom: ".3rem" }}>Détails techniques</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: ".7rem" }}>
                    {searchError.message}
                    {searchError.status && <span> · code {searchError.status}</span>}
                  </div>
                  <div style={{ fontSize: ".65rem", color: "var(--muted)", marginTop: ".3rem" }}>
                    {new Date(searchError.timestamp).toLocaleString("fr-FR")} · Si le problème persiste, écrivez à contact@vehicleiq.fr
                  </div>
                </div>
                )}
                <div style={{ display: "flex", gap: ".6rem", justifyContent: "center", flexWrap: "wrap" }}>
                  {!searchError.isNoResults && (
                  <button className="btn btn-accent" onClick={() => {
                    setSearchError(null);
                    if (answers) runSearch(answers);
                    else setStep("questions");
                  }}>
                    🔄 Réessayer
                  </button>
                  )}
                  <button className={searchError.isNoResults ? "btn btn-accent" : "btn btn-ghost"} onClick={() => {
                    setSearchError(null);
                    setStep("questions");
                  }}>
                    ← Modifier les critères
                  </button>
                </div>
              </div>
            )}
            {step === "result" && results && (
              <div>
                {/* Header résultats */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                  <div>
                    <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem" }}>{results.length} résultats</div>
                    <div style={{ fontSize: ".75rem", color: "var(--sub)" }}>Classés par score de correspondance décroissant</div>
                    <div style={{ marginTop: ".3rem" }}><span style={{ display: "inline-flex", alignItems: "center", gap: ".3rem", fontSize: ".62rem", fontFamily: "var(--font-mono)", color: "var(--accent2)", background: "rgba(255,184,0,.08)", border: "1px solid rgba(255,184,0,.2)", borderRadius: 10, padding: ".1rem .45rem", letterSpacing: ".3px", flexShrink: 0 }}>✦ VIQ.AI® Engine</span></div>
                  </div>
                  {!paid && (
                    <button className="btn btn-accent btn-sm" onClick={() => setShowGate(true)}>
                      🔓 Débloquer la Cote
                    </button>
                  )}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: ".7rem" }}>
                  {results.map((car, i) => {
                    const isBlurred = !paid;
                    const PLATFORM_URLS = {"AutoScout24":"https://www.autoscout24.fr","LeBonCoin":"https://www.leboncoin.fr/voitures","La Centrale":"https://www.lacentrale.fr","Aramisauto":"https://www.aramisauto.com","ParuVendu":"https://www.paruvendu.fr","Vroom":"https://www.vroom.com","Motorway":"https://motorway.co.uk","Promoneuve":"https://www.promoneuve.fr","Mandataire Auto":"https://www.mandataire-auto.fr","Carflow":"https://www.carflow.fr"};
                    return (
                      <div key={i} style={{ background: "var(--card)", border: `1px solid ${isBlurred ? "var(--border)" : "var(--border)"}`, borderRadius: 8, overflow: "hidden", position: "relative", filter: isBlurred ? "blur(4px)" : "none", userSelect: isBlurred ? "none" : "auto", pointerEvents: isBlurred ? "none" : "auto" }}>
                        {/* Photo */}
                        {/* Bandeau identité marque (logo officiel) — remplace les photos imprécises */}
                        <div style={{ height: 110, overflow: "hidden", position: "relative", background: "linear-gradient(135deg, #fff 0%, var(--card) 100%)", display: "flex", alignItems: "center", justifyContent: "center", borderBottom: "1px solid var(--border)" }}>
                          {brandLogoUrl(car.make) ? (
                            <img
                              src={brandLogoUrl(car.make)}
                              alt={car.make}
                              style={{ maxWidth: "46%", maxHeight: "58%", objectFit: "contain" }}
                              onError={e => {
                                // Repli : si le logo ne charge pas, afficher le badge marque (initiales colorées)
                                const holder = e.target.parentNode.querySelector(".brand-fallback");
                                e.target.style.display = "none";
                                if (holder) holder.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div className="brand-fallback" style={{ display: brandLogoUrl(car.make) ? "none" : "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: ".35rem" }}>
                            <MakeBadge make={car.make} size={46} />
                            <span style={{ fontSize: ".74rem", fontWeight: 700, color: "var(--text)" }}>{car.make}</span>
                          </div>
                          {/* Score badge */}
                          <div style={{ position: "absolute", top: 8, right: 8, background: car.match >= 90 ? "rgba(0,200,100,.92)" : car.match >= 80 ? "rgba(255,184,0,.92)" : "rgba(100,100,120,.85)", borderRadius: 20, padding: ".2rem .7rem", fontWeight: 700, fontSize: ".78rem", color: car.match >= 90 ? "#000" : "#fff", display: "flex", alignItems: "center", gap: ".3rem" }}>
                            <span>{car.match}%</span>
                            <div style={{ height: 14, width: 14, borderRadius: "50%", background: "rgba(255,255,255,.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".55rem" }}>✓</div>
                          </div>
                          {/* Rank */}
                          <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,.75)", borderRadius: 4, padding: ".15rem .5rem", fontSize: ".7rem", color: "#fff", fontFamily: "var(--font-mono)" }}>#{i+1}</div>
                          {/* Rank */}
                          <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,.75)", borderRadius: 4, padding: ".15rem .5rem", fontSize: ".7rem", color: "#fff", fontFamily: "var(--font-mono)" }}>#{i+1}</div>
                        </div>
                        <div style={{ padding: ".75rem 1rem" }}>
                          {/* Titre + Prix */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: ".35rem" }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: ".93rem" }}>{car.make} {car.model}</div>
                              <div style={{ fontSize: ".72rem", color: "var(--sub)" }}>{car.year} · {Number(car.km||0).toLocaleString("fr-FR")} km · {car.fuel}{car.gearbox?" · "+car.gearbox:""}</div>
                              {car.color && <div style={{ fontSize: ".68rem", color: "var(--muted)" }}>🎨 {car.color}</div>}
                              {car.why_match && <div style={{ fontSize: ".7rem", color: "var(--accent2)", marginTop: ".2rem" }}>✦ {car.why_match}</div>}
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0, marginLeft: ".5rem" }}>
                              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.2rem", color: "var(--green)" }}>{car.priceFormatted||(Number(car.price||0).toLocaleString("fr-FR")+" €")}</div>
                              {(car.argus_low||car.argus_value) && <div style={{ fontSize: ".63rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>Cote {Number(car.argus_low||car.argus_value).toLocaleString("fr-FR")}–{Number(car.argus_high||car.argus_value).toLocaleString("fr-FR")} €</div>}
                              {car.prixNeufFormatted && <div style={{ fontSize: ".63rem", color: "var(--muted)" }}>Neuf : {car.prixNeufFormatted}</div>}
                            </div>
                          </div>
                          {/* Pros/cons */}
                          {car.pros?.length > 0 && <div style={{ marginBottom: ".35rem" }}>
                            {car.pros.slice(0,2).map((p,j)=><div key={j} style={{ fontSize: ".7rem", color: "var(--green)" }}>✓ {p}</div>)}
                            {car.cons?.slice(0,1).map((c,j)=><div key={j} style={{ fontSize: ".7rem", color: "var(--sub)" }}>⚠ {c}</div>)}
                          </div>}
                          {/* Résumé d'opportunité (mode revente) */}
                          {car.opportunity && (() => {
                            const o = car.opportunity;
                            const vs = {
                              bonne:    { color: "var(--green)",  bg: "rgba(46,111,92,.1)",  label: "🟢 Bonne marge" },
                              moyenne:  { color: "var(--accent)", bg: "rgba(168,128,58,.12)", label: "🟡 Marge correcte" },
                              faible:   { color: "var(--accent)", bg: "rgba(168,128,58,.08)", label: "🟠 Marge faible" },
                              negative: { color: "var(--red)",    bg: "rgba(176,90,58,.1)",  label: "🔴 À perte" },
                            }[o.verdict] || {};
                            const eur = (v) => (v || v === 0) ? Math.round(v).toLocaleString("fr-FR") + " €" : "—";
                            return (
                              <div style={{ marginTop: ".4rem", marginBottom: ".4rem", border: "1px solid " + vs.color, borderRadius: 8, overflow: "hidden" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: ".4rem .6rem", background: vs.bg }}>
                                  <span style={{ fontSize: ".72rem", fontWeight: 800, color: vs.color }}>{vs.label}</span>
                                  <span style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", fontWeight: 700, color: vs.color }}>
                                    {o.margeMid >= 0 ? "+" : ""}{eur(o.margeMid)}
                                  </span>
                                </div>
                                <div style={{ padding: ".45rem .6rem", fontSize: ".66rem", color: "var(--sub)", lineHeight: 1.5 }}>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>Prix affiché</span><strong style={{ color: "var(--text)" }}>{eur(o.prixAffiche)}</strong></div>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>+ Malus / carte grise</span><strong style={{ color: "var(--text)" }}>{eur(o.coutImmat)}</strong></div>
                                  {o.transport > 0 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>+ Transport{o.isImport ? " (import)" : ""}</span><strong style={{ color: "var(--text)" }}>{eur(o.transport)}</strong></div>}
                                  <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--border)", marginTop: ".2rem", paddingTop: ".2rem" }}><span>Coût de revient</span><strong style={{ color: "var(--text)" }}>{eur(o.coutTotal)}</strong></div>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}><span>Cote revente France</span><strong style={{ color: "var(--green)" }}>{eur(o.reventeMid)}</strong></div>
                                  <div style={{ fontSize: ".62rem", color: "var(--muted)", marginTop: ".25rem" }}>
                                    Marge {o.margePct >= 0 ? "+" : ""}{o.margePct}% · fourchette {eur(o.margeLow)} → {eur(o.margeHigh)}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          {/* Tags + CTA */}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: ".4rem", borderTop: "1px solid var(--border)" }}>
                            <div style={{ display: "flex", gap: ".3rem", flexWrap: "wrap" }}>
                              {(car.tags||[]).slice(0,2).map((t,j)=><span key={j} className="tag" style={{ fontSize: ".65rem" }}>{t}</span>)}
                            </div>
                            <button className="btn btn-accent btn-sm" onClick={() => {
                              const subject = encodeURIComponent("Intérêt achat — " + car.make + " " + (car.model||"") + " " + car.year);
                              const body = encodeURIComponent(
                                "Bonjour,\n\nUn utilisateur VehicleIQ souhaite acheter ce véhicule.\n\n" +
                                "=== VÉHICULE ===\n" +
                                car.make + " " + (car.model||"") + " " + car.year + " — " + car.km + " km — " + car.fuel + "\n" +
                                "Cote VIQ.AI® : " + car.priceFormatted + " · Match : " + car.match + "%\n\n" +
                                "=== UTILISATEUR ===\n" +
                                "Email : " + (user?.email || "Non connecté") + "\n" +
                                "Nom : " + (user?.name || "") + "\n\n" +
                                "Cordialement,\nVehicleIQ"
                              );
                              window.location.href = "mailto:bonglorentz@gmail.com?cc=contact@vehicleiq.fr&subject=" + subject + "&body=" + body;
                            }}>
                              Je suis intéressé →
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Unlock banner */}
                {!paid && (
                  <div style={{ marginTop: ".8rem", background: "rgba(168,128,58,.05)", border: "1px solid rgba(168,128,58,.25)", borderRadius: 8, padding: "1rem 1.2rem", textAlign: "center" }}>
                    <div style={{ fontSize: ".85rem", fontWeight: 600, marginBottom: ".4rem" }}>🔒 {results.length} véhicules personnalisés — contenu flouté</div>
                    <div style={{ fontSize: ".75rem", color: "var(--sub)", marginBottom: ".8rem" }}>Débloquez les 10 résultats avec scores, argus, pros/cons et accès directs</div>
                    <button className="btn btn-accent" onClick={() => setShowGate(true)}>🔓 Débloquer la Cote</button>
                  </div>
                )}
                {/* Export PDF + Partage WhatsApp après déblocage */}
                {paid && (
                  <div style={{ display: "flex", gap: ".5rem", marginTop: ".8rem" }}>
                    <button
                      className="btn btn-ghost"
                      style={{ flex: 1, fontSize: ".82rem", display: "flex", alignItems: "center", justifyContent: "center", gap: ".5rem" }}
                      onClick={() => chargeForAction({
                        cost: 1, label: "export_pdf_buy",
                        action: () => exportBuyPDF(results, answers),
                        onSpent: () => { if (onWalletChange) onWalletChange(); },
                        onInsufficient: () => { if (onShowRecharge) onShowRecharge(); },
                      })}
                    >
                      📄 Exporter la sélection en PDF 
                    </button>
                    <button
                      type="button"
                      style={{
                        flex: 1, fontFamily: "var(--font-body)", fontSize: ".82rem", fontWeight: 600,
                        background: "#25D366", color: "#fff", border: "none", borderRadius: 6,
                        padding: ".55rem .9rem", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: ".4rem",
                        boxShadow: "0 1px 2px rgba(37,211,102,.18), 0 4px 10px rgba(37,211,102,.22)",
                        transition: "all .15s cubic-bezier(.4,0,.2,1)"
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "#1FBA59"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "#25D366"; e.currentTarget.style.transform = "translateY(0)"; }}
                      onClick={() => chargeForAction({
                        cost: 0, label: "share_whatsapp_buy",
                        action: () => {
                          const top3 = (results || []).slice(0, 3);
                          const lines = [
                            `🚗 *Top recommandations VehicleIQ*`,
                            answers?.budget && `💰 Budget : ${Number(answers.budget).toLocaleString("fr-FR")} €`,
                            answers?.fuel && `⛽ Motorisation : ${answers.fuel}`,
                            "",
                            ...top3.map((c, i) => {
                              const lbl = [c.make, c.model, c.year].filter(Boolean).join(" ");
                              const km = c.km ? `${Number(c.km).toLocaleString("fr-FR")} km` : "";
                              const prix = c.priceFormatted || (c.price ? `${Number(c.price).toLocaleString("fr-FR")} €` : "");
                              return `${i+1}. *${lbl}*${km ? ` — ${km}` : ""}${prix ? ` — ${prix}` : ""}${c.match ? ` (match ${c.match}%)` : ""}`;
                            }),
                            "",
                            "Sélection propulsée par VehicleIQ — vehicleiq.fr"
                          ].filter(Boolean);
                          window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener,noreferrer");
                        },
                        onSpent: () => { if (onWalletChange) onWalletChange(); },
                        onInsufficient: () => { if (onShowRecharge) onShowRecharge(); },
                      })}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                      WhatsApp
                    </button>
                  </div>
                )}
                {/* Disclaimer légal */}
                <div style={{ marginTop: ".8rem", padding: ".6rem .9rem", background: "rgba(100,100,120,.06)", border: "1px solid rgba(100,100,120,.15)", borderRadius: 4, fontSize: ".68rem", color: "var(--muted)", lineHeight: 1.5 }}>
                  ⚖ Les suggestions générées par IA sont à titre indicatif et ne constituent pas une offre contractuelle (art. 1112-1 C. civ.). Les prix sont des cotes de marché — vérifiez toujours les annonces directement sur les plateformes.
                </div>
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>
      {showGate && <WalletGate cost={1} label="Résultats personnalisés + liens annonces" onClose={() => setShowGate(false)} onUnlocked={() => { setShowGate(false); setPaid(true); if(onWalletChange) onWalletChange(); setTimeout(() => { if (localStorage.getItem("viq_callback_optout") !== "1") setShowCallback(true); }, 30000); }} user={user} onShowAuth={onShowAuth} onRecharge={() => { setShowGate(false); setShowRecharge(true); }} />}
        {showRecharge && <RechargeModal onClose={() => setShowRecharge(false)} onRecharged={(qty) => {
          setShowRecharge(false);
          if (onWalletChange) onWalletChange();
          // Si l'utilisateur avait validé un questionnaire avant le blocage, lancer la recherche
          if (answers && step !== "loading" && step !== "results") {
            setTimeout(() => runSearch(answers), 300);
          }
        }} user={user} onShowAuth={onShowAuth} />}
    </>
  );
}

// ─── Sell Panel ───────────────────────────────────────────────────────────────
// ─── MultiEstimateComparator ─────────────────────────────────────────────────
function MultiEstimateComparator({ multiResults }) {
  const [carIdx, setCarIdx] = useState(0);
  const prices = multiResults.map(r => r.prix_viq || Math.round(((r.argus_mid||r.argus_low)||0)*0.95/100)*100);
  const active = multiResults[carIdx] || {};
  const activePrice = prices[carIdx] || 0;

  return (
    <div style={{ marginBottom:"1rem" }}>
      {/* ── Bandeau comparatif — une seule fois ── */}
      <div style={{ padding:".8rem 1rem", background:"rgba(255,184,0,.06)", border:"1px solid rgba(255,184,0,.2)", borderRadius:8, marginBottom:".8rem" }}>
        <div style={{ fontSize:".7rem", color:"var(--accent2)", fontWeight:700, textTransform:"uppercase", letterSpacing:".5px", marginBottom:".6rem" }}>
          ⚡ Comparatif {multiResults.length} véhicules — cliquez pour naviguer
        </div>
        <div style={{ display:"flex", gap:".5rem", flexWrap:"wrap" }}>
          {multiResults.map((r,i) => {
            const prix = prices[i];
            const diff = i === carIdx ? null : prix - activePrice;
            const isActive = i === carIdx;
            return (
              <div key={i} onClick={() => setCarIdx(i)}
                style={{ flex:"1 1 110px", minWidth:110, background: isActive?"rgba(255,184,0,.15)":"var(--card)",
                  border: isActive?"1px solid rgba(255,184,0,.6)":"1px solid var(--border)",
                  borderRadius:6, padding:".5rem .8rem", cursor:"pointer", transition:"all .2s" }}>
                <div style={{ fontSize:".68rem", color:"var(--sub)", marginBottom:".15rem" }}>Véhicule #{i+1}</div>
                <div style={{ fontWeight:700, fontSize:".82rem", color:"var(--text)", marginBottom:".1rem" }}>{r.make} {r.year}</div>
                <div style={{ fontFamily:"var(--font-head)", fontSize:".95rem", color:"var(--green)", fontWeight:700 }}>{prix.toLocaleString("fr-FR")} €</div>
                {diff !== null && (
                  <div style={{ fontSize:".68rem", fontWeight:700, color: diff > 0 ? "#F87171" : "#4ADE80", marginTop:".1rem" }}>
                    {diff > 0 ? "▲ +" : "▼ "}{Math.abs(diff).toLocaleString("fr-FR")} €
                  </div>
                )}
                <div style={{ fontSize:".62rem", color:"var(--muted)", marginTop:".1rem" }}>Score {r.score_vente||"—"}%</div>
              </div>
            );
          })}
        </div>
        {/* Navigation flèches */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:".7rem" }}>
          <button onClick={() => setCarIdx(i => Math.max(0, i-1))} disabled={carIdx===0}
            style={{ background:"var(--deep)", border:"1px solid var(--border)", borderRadius:6, padding:".3rem .8rem", cursor:"pointer", color:"var(--sub)", fontSize:".78rem", opacity: carIdx===0 ? .4 : 1 }}>← Véhicule précédent</button>
          <span style={{ fontSize:".72rem", color:"var(--muted)", fontFamily:"var(--font-mono)" }}>{carIdx+1} / {multiResults.length}</span>
          <button onClick={() => setCarIdx(i => Math.min(multiResults.length-1, i+1))} disabled={carIdx===multiResults.length-1}
            style={{ background:"var(--deep)", border:"1px solid var(--border)", borderRadius:6, padding:".3rem .8rem", cursor:"pointer", color:"var(--sub)", fontSize:".78rem", opacity: carIdx===multiResults.length-1 ? .4 : 1 }}>Véhicule suivant →</button>
        </div>
      </div>

      {/* ── Résultats du véhicule sélectionné ── */}
      <div style={{ border:"1px solid var(--border)", borderRadius:8, padding:".8rem 1rem", background:"var(--card)" }}>
        <div style={{ fontSize:".7rem", color:"var(--accent2)", fontWeight:700, marginBottom:".5rem" }}>
          Véhicule #{carIdx+1} — {active.make} {active.model} {active.year}
        </div>
        {/* Prix VIQ.AI® — un seul prix, épuré */}
        {activePrice > 0 && (
          <div style={{ textAlign:"center", padding:"1.1rem", background:"rgba(46,111,92,.06)", border:"1px solid rgba(46,111,92,.2)", borderRadius:10, marginBottom:".6rem" }}>
            <div style={{ fontSize:".6rem", color:"var(--green)", textTransform:"uppercase", letterSpacing:"1.2px", marginBottom:".3rem", fontWeight:700, fontFamily:"var(--font-mono)" }}>✦ Cote VIQ.AI®</div>
            <div style={{ fontFamily:"var(--font-head)", fontSize:"2.1rem", color:"var(--green)", fontWeight:600, letterSpacing:"-.01em" }}>{activePrice.toLocaleString("fr-FR")} €</div>
          </div>
        )}
        {/* Conseil prix */}
        {active.conseil_prix && (
          <div style={{ fontSize:".78rem", color:"var(--sub)", lineHeight:1.5, marginBottom:".5rem" }}>💡 {active.conseil_prix}</div>
        )}
        {/* Plateformes top 3 */}
        {active.plateformes?.length > 0 && (
          <div>
            <div style={{ fontSize:".65rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:".4px", marginBottom:".3rem" }}>Plateformes recommandées</div>
            {active.plateformes.slice(0,3).map((pl,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:".6rem", padding:".35rem .6rem", background:"var(--deep)", borderRadius:4, marginBottom:".25rem", fontSize:".75rem" }}>
                <span style={{ color:"var(--accent2)", fontWeight:700 }}>#{i+1}</span>
                <span style={{ fontWeight:600 }}>{pl.name}</span>
                <span style={{ color:"var(--muted)", marginLeft:"auto" }}>{pl.commission}</span>
                <span style={{ color:"var(--sub)", fontSize:".68rem" }}>{pl.delai_moyen}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function SellPanel({ onClose, user, onShowAuth, onWalletChange, onShowRecharge, sellPrefill }) {
  // Préremplissage : la prop React (fiable) prime sur la variable globale (secours).
  // IMPORTANT : on lit le global SANS l'effacer ici — le layout monte parfois
  // plusieurs instances de ce panneau (piles de modales dupliquées) et la 1ʳᵉ
  // instance viderait le prefill avant que l'instance visible ne le lise.
  // L'effacement se fait dans un effet, après que toutes les instances ont lu.
  // v28 — `_photos` (base64, potentiellement plusieurs Mo) est retiré du
  // prefill utilisé pour les réponses du formulaire : il ne doit JAMAIS finir
  // dupliqué dans l'historique local ou l'input envoyé au serveur — seul
  // `sellPhotos` (ci-dessous) doit le porter, une fois consommé.
  const [prefill, setPrefill] = useState(() => {
    const raw = (sellPrefill && typeof sellPrefill === "object" && Object.keys(sellPrefill).length)
      ? sellPrefill
      : (() => { try { return window.__viq_sell_prefill || null; } catch (e) { return null; } })();
    if (!raw) return null;
    if (raw._photos || raw._histovec || raw._dossier_id) { const { _photos, _histovec, _dossier_id, ...rest } = raw; return rest; }
    return raw;
  });
  useEffect(() => {
    try { if (window.__viq_sell_prefill) setTimeout(() => { window.__viq_sell_prefill = null; }, 500); } catch (e) {}
    try { if (window.__viq_sell_photos_prefill) setTimeout(() => { window.__viq_sell_photos_prefill = null; }, 500); } catch (e) {}
  }, []);
  // Si la prop arrive/maj après le montage (flux d'auth asynchrone), on la prend en compte.
  useEffect(() => {
    if (sellPrefill && typeof sellPrefill === "object" && Object.keys(sellPrefill).length) {
      setPrefill(prev => (prev && Object.keys(prev).length ? prev : sellPrefill));
    }
  }, [sellPrefill]);

  const [step, setStep] = useState("questions");
  const [loadIdx, setLoadIdx] = useState(0);
  const [result, setResult] = useState(null);
  // Enrichissement HistoVec + CarVertical (optionnels) + synthèse fiabilité/risques modèle-année.
  // v36 — Reprend le HistoVec déjà connu (transmis depuis un Dossier VIQ) pour
  // ne pas le redemander alors qu'il a déjà été importé lors d'un Contrôle.
  // v37 — Dossier VIQ : si on arrive depuis un dossier existant, l'ID est
  // transmis dans le prefill (_dossier_id) ; sinon il sera résolu au premier
  // find-or-create (dès qu'un VIN/plaque est connu).
  const [dossierId, setDossierId] = useState(() => {
    try { return (sellPrefill && sellPrefill._dossier_id) || window.__viq_sell_prefill?._dossier_id || null; }
    catch (e) { return null; }
  });
  const [hvDataSell, setHvDataSell] = useState(() => {
    try {
      const raw = (sellPrefill && typeof sellPrefill === "object" && sellPrefill._histovec) || window.__viq_sell_prefill?._histovec;
      return raw && raw.found ? raw : null;
    } catch (e) { return null; }
  });
  const [cvDataSell, setCvDataSell] = useState(null); // v9 : rapport CarVertical importé
  const [knownIssuesSell, setKnownIssuesSell] = useState(null);
  const [reliabilityScoreSell, setReliabilityScoreSell] = useState(null); // score de fiabilité VIQ.AI® (0-100)
  const [issuesLoadingSell, setIssuesLoadingSell] = useState(false);
  const lastSellAnsRef = useRef(null);
  const lastSellHistoryIdRef = useRef(null); // v13 : id de l'entrée d'historique à synchroniser après enrichissement
  const enrichSell = async (ansObj, hvObj, cvObj) => {
    const full = String((ansObj && ansObj.make) || "").trim();
    if (!full) return;
    const sp = full.indexOf(" ");
    const report = {
      make: sp > 0 ? full.slice(0, sp) : full,
      model: sp > 0 ? full.slice(sp + 1) : "",
      version: "",
      year: (ansObj && (ansObj.year_sell || ansObj.year)) || "",
      fuel: (ansObj && ansObj.fuel_sell) || "",
      engine_code: "",
    };
    setIssuesLoadingSell(true);
    try {
      const r = await apiPost("/api/vehicle/enrich", {
        report,
        histovec: hvObj || hvDataSell || undefined,
        carvertical: cvObj || cvDataSell || undefined,
        mileage: parseInt(ansObj && ansObj.km_sell) || undefined,
        want_issues: true,
      });
      if (r && r.success) {
        if (r.known_issues && r.known_issues.found) setKnownIssuesSell(r.known_issues);
        if (typeof r.score === "number") setReliabilityScoreSell(r.score);
        // v13 : synchroniser l'historique avec le score de fiabilité + les
        // points de vigilance — même principe que enrichVehicle (Contrôler),
        // pour que "Estimer une cote" ait aussi ces éléments présents dans
        // l'historique, pas seulement à l'écran au moment du calcul.
        if (lastSellHistoryIdRef.current != null) {
          try {
            const hist = JSON.parse(localStorage.getItem("viq_history") || "[]");
            const next = hist.map(h => {
              if (h.type !== "sell" || h.id !== lastSellHistoryIdRef.current) return h;
              const mergedResult = {
                ...(h.results || {}),
                ...(typeof r.score === "number" ? { reliability_score: r.score } : {}),
                ...(r.known_issues && r.known_issues.found ? { known_issues: r.known_issues } : {}),
              };
              return { ...h, results: mergedResult, result: mergedResult };
            });
            localStorage.setItem("viq_history", JSON.stringify(next));
          } catch (e) {}
        }
      }
    } catch (e) { /* non bloquant */ }
    finally { setIssuesLoadingSell(false); }
  };
  // v41 — Même correctif que Contrôler : ne jamais perdre un import HistoVec
  // si le dossier n'est pas encore résolu à cet instant précis.
  const persistDossierFileSell = async (type, payload) => {
    let id = dossierId;
    if (!id) {
      try {
        const ans = lastSellAnsRef.current || {};
        // v42 — tente aussi via marque+modèle+année seuls (repli tier 3),
        // plus seulement si un VIN/plaque est connu : une Estimation manuelle
        // (cas le plus courant) n'en a jamais.
        if (ans.vin || ans.plaque || ans.make) {
          const dres = await apiPost("/api/dossiers/find-or-create", {
            vin: ans.vin, plaque: ans.plaque, make: ans.make, year: ans.year_sell || ans.year, fuel: ans.fuel_sell,
          });
          if (dres?.success) { id = dres.dossier.id; setDossierId(id); }
        }
      } catch (e) { console.warn(`[Dossier VIQ] résolution dossier échouée pour "${type}" :`, e.message); }
    }
    if (id) apiPost(`/api/dossiers/${id}/files`, { type, payload }).catch(e => console.warn(`[Dossier VIQ] échec enregistrement "${type}" :`, e.message));
    else console.warn(`[Dossier VIQ] "${type}" non enregistré — aucun dossier résolu.`);
  };
  const onHistovecImportedSell = (hv) => {
    setHvDataSell(hv);
    enrichSell(lastSellAnsRef.current || {}, hv, cvDataSell);
    persistDossierFileSell("histovec", hv);
  };
  // v9 : CarVertical importé → recalcule le score de fiabilité immédiatement.
  // L'ajustement du PRIX de vente (accidents/compteur trafiqué, déjà câblé
  // côté serveur dans estimateVehiclePrice) s'appliquera à la prochaine
  // estimation lancée — pas de re-débit automatique d'un crédit ici.
  const onCarVerticalImportedSell = (cv) => {
    setCvDataSell(cv);
    enrichSell(lastSellAnsRef.current || {}, hvDataSell, cv);
    persistDossierFileSell("carvertical", cv);
  };
  // v5.2 : stratégie de vente sélectionnée — détermine quel prix s'affiche en gros
  // (Immédiate / Rapide / Normale). Préselectionnée depuis answers.urgency dans
  // le payload de calcul, modifiable via les 3 toggles sur la carte résultat.
  const [sellStrategy, setSellStrategy] = useState("normale");
  const [multiResults, setMultiResults] = useState([]); // résultats multi-estimation Pro
  const [multiRound, setMultiRound] = useState(0); // 0=normal, 1=1er véhicule, 2=2e, 3=3e
  const [multiMode, setMultiMode] = useState(false);
  // v45 — Cotation simultanée NATIVE (remplace l'usage de multiMode
  // ci-dessus, laissé inerte, et l'ancienne modale séparée BatchSellPanel).
  const [vehicleCount, setVehicleCount] = useState(1);
  const [batchVehicles, setBatchVehicles] = useState([{ make: "", year: "", km: "", fuel: "Essence", state: "Bon", vin: null, plaque: null, dossierId: null, gearbox: null, hvData: null, cvData: null, photos: [], visualInspection: null }]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchPayErr, setBatchPayErr] = useState(null);
  const [batchResults, setBatchResults] = useState(null);
  // v62 — "Traiter en lot" : un prix global pour tout le lot au lieu d'un
  // prix par véhicule (chaque véhicule garde quand même sa cote de
  // référence individuelle, affichée à titre indicatif).
  const [lotMode, setLotMode] = useState("individual"); // individual | lot
  const [lotId, setLotId] = useState(null);
  const [lotPrice, setLotPrice] = useState("");
  const [lotSaved, setLotSaved] = useState(false);

  // v45 — Reprise depuis un Contrôle simultané (même principe que
  // window.__viq_vin_prefill, déjà utilisé ailleurs) : si des véhicules
  // viennent d'être contrôlés ensemble, on les propose ici pré-remplis.
  useEffect(() => {
    if (typeof window === "undefined" || !window.__viq_batch_check_results) return;
    const handoff = window.__viq_batch_check_results;
    window.__viq_batch_check_results = null;
    // v48 — Lancement direct depuis le Contrôle simultané (bouton "Coter ces
    // véhicules maintenant") : fixer vehicleCount > 1 ici suffit à afficher
    // directement le formulaire simultané — il n'y a plus de bascule séparée
    // (l'ancien état booléen batchSellMode a été retiré, vehicleCount est
    // l'unique source de vérité pour savoir si on est en mode simultané).
    if (typeof window !== "undefined") window.__viq_batch_sell_autolaunch = false;
    if (Array.isArray(handoff) && handoff.length) {
      setVehicleCount(Math.min(handoff.length, maxSimul));
      setBatchVehicles(handoff.slice(0, maxSimul).map(v => {
        // v65 — même logique que handleComplete (single) : la finition
        // enrichit le libellé "make" plutôt que d'être un champ séparé,
        // pour rester compatible avec l'API existante (ans.make).
        const finitionOk = v.finition && v.finition !== "—" && v.finition !== "Autre / Inconnue";
        const makeLabel = [v.make, v.model].filter(Boolean).join(" ")
          + (finitionOk && !String(v.make || "").toLowerCase().includes(String(v.finition).toLowerCase()) ? ` ${v.finition}` : "");
        return {
          make: makeLabel,
          year: v.year ? String(v.year) : "", km: v.km ? String(v.km) : "", fuel: v.fuel || "Essence", state: "Bon",
          vin: v.vin || null, plaque: v.plaque || null, dossierId: v.dossierId || null,
          gearbox: v.gearbox || null,
          hvData: v.hvData || null, cvData: v.cvData || null, photos: v.photos || [],
          visualInspection: v.visualInspection || null,
        };
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showGate, setShowGate] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [paid, setPaid] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showCallbackSell, setShowCallbackSell] = useState(false);
  const [showPlatformsList, setShowPlatformsList] = useState(false); // v14 : liste des 10 plateformes masquée par défaut
  const [partialCoteAlgo, setPartialCoteAlgo] = useState(null); // v14 : cote 100% algo, affichée pendant le chargement
  const [callbackAnswersSell, setCallbackAnswersSell] = useState(null);
  // Pour le bouton "Relancer" : on stocke le snapshot des réponses précédentes
  // et on incrémente un compteur pour forcer le remount du Questionnaire (re-init avec les valeurs)
  const [relaunchPrefill, setRelaunchPrefill] = useState(null);
  const [relaunchKey, setRelaunchKey] = useState(0);
  // Remplissage par photos (jusqu'à 10 images) — analyse vision + décodage plaque
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoMsg, setPhotoMsg] = useState(null); // { ok, text }
  // v28 — Reprend les photos déjà transmises par le client via un formulaire
  // lead (voir launchLead / analyzeLeadPhotos) : avant ce correctif, lancer
  // une estimation depuis un tel lead laissait le champ photo vide alors que
  // des photos existaient déjà pour ce véhicule.
  const [sellPhotos, setSellPhotos] = useState(() => {
    try {
      const rawPhotos = (sellPrefill && typeof sellPrefill === "object" && sellPrefill._photos)
        || window.__viq_sell_prefill?._photos
        || window.__viq_sell_photos_prefill;
      if (Array.isArray(rawPhotos) && rawPhotos.length) return rawPhotos.slice(0, 10);
    } catch (e) {}
    return [];
  }); // data-URLs accumulées (max 10)
  const fileInputRef = useRef(null);
  const sellCameraRef = useRef(null);
  // Ajoute des photos à la sélection (sans lancer l'analyse) — comme le formulaire lead.
  // Sur mobile l'appareil ne renvoie qu'une image à la fois : on accumule jusqu'à 10.
  const addSellPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    const room = Math.max(0, 10 - sellPhotos.length);
    if (room <= 0) return;
    setPhotoMsg(null);
    const toDataUrl = (f) => compressImageFile(f).then(r => r || new Promise((resolve, reject) => {
      const rd = new FileReader(); rd.onload = () => resolve(rd.result); rd.onerror = reject; rd.readAsDataURL(f);
    }));
    try {
      const urls = (await Promise.all(files.slice(0, room).map(toDataUrl))).filter(Boolean);
      setSellPhotos(prev => [...prev, ...urls].slice(0, 10));
    } catch (e) {}
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  // Lance l'analyse vision sur les photos accumulées (facturée comme 1 estimation).
  const analyzePhotos = async () => {
    if (!sellPhotos.length || photoBusy) return;
    setPhotoBusy(true); setPhotoMsg(null);
    try {
      const r = await apiPost("/api/estimate/from-photos", { images: sellPhotos });
      if (r?.success && r.prefill) {
        // Normalise les champs courts de la vision (ex: state "Bon") vers les
        // options EXACTES du questionnaire (« Bon — quelques micro-rayures… »).
        const pf = normalizeSellAnswers(r.prefill);
        setRelaunchPrefill(null);
        setPrefill(prev => ({ ...prev, ...pf }));
        setRelaunchKey(k => k + 1); // force le remount du questionnaire avec le nouveau prefill
        const filled = Object.keys(pf).filter(k => !["plaque", "immat"].includes(k)).length;
        setPhotoMsg({ ok: true, text: `${filled} champ${filled > 1 ? "s" : ""} pré-rempli${filled > 1 ? "s" : ""}${r.plate_decoded ? " (plaque décodée ✓)" : ""}. Vérifiez puis validez.` });
      } else {
        setPhotoMsg({ ok: false, text: r?.message || r?.error || "Analyse impossible." });
      }
    } catch (e) {
      if (isPaymentRequiredError(e)) { handlePaymentRequired(e); setPhotoMsg({ ok: false, text: "Paiement requis pour l'analyse photo." }); }
      else setPhotoMsg({ ok: false, text: (e && e.message) ? e.message : "Analyse impossible. Réessayez ou saisissez manuellement." });
    } finally {
      setPhotoBusy(false);
    }
  };
  const [estimOk, setEstimOk] = useState(null);
  const [userTargetPrice, setUserTargetPrice] = useState("");
  // Marge revente (pour les pros qui achètent pour revendre)
  const [margeMode, setMargeMode] = useState("eur"); // "eur" ou "pct"
  const [margeValue, setMargeValue] = useState("");
  const [feedbackPrice, setFeedbackPrice] = useState("");
  const [feedbackReason, setFeedbackReason] = useState("");
  // Cote ajustée (suite au feedback "non réaliste" + prix saisi)
  const [adjustedEstimation, setAdjustedEstimation] = useState(null);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const isPro = (user?.siret && user.siret.length > 3) || user?.is_pro;
  // Abonné illimité (Pro Solo / Multi-Agent) → analyse photo incluse, sans débit à l'usage
  const isUnlimitedPlan = ["unlimited", "multi_agent_3"].includes(user?.subscription_plan) || !!(user?.plan_limits?.is_multi_agent);
  // Multi-Agents → 10 estimations en parallèle (vs 3 pour les autres pros).
  // Source de vérité = backend (user.plan_limits.parallel_estimations).
  const maxSimul = resolveMaxSimul(user);
  const isMultiAgent = !!(user?.plan_limits?.is_multi_agent);
  const [sellLoadSteps, setSellLoadSteps] = useState([
    { icon: "🔍", label: "Identification du véhicule…", detail: "Marque · Modèle · Millésime · Motorisation · Boîte · Kilométrage" },
    { icon: "📚", label: "Interrogation base catalogue constructeur…", detail: "Prix neuf officiel France · +8000 modèles · Segment détecté" },
    { icon: "📊", label: "Lecture du cache marché…", detail: "Prix observés, mis à jour en continu" },
    { icon: "📅", label: "Application du coefficient saisonnier…", detail: "Cabriolet, sportive, SUV, familiale · Mois courant" },
    { icon: "📍", label: "Ajustement régional…", detail: "Tension marché par département · 100+ codes postaux" },
    { icon: "🏙️", label: "Vérification zone ZFE projetée…", detail: "Crit'Air · Métropoles actives & 2026-2028" },
    { icon: "🛡️", label: "Croisement watchlist mécanique…", detail: "11 défauts modèles documentés" },
    { icon: "📈", label: "Application du momentum marché 30j/90j…", detail: "Tendance segment temps réel" },
    { icon: "🧠", label: "Calibration Bayésienne sale_followup…", detail: "Apprentissage continu · Avantage VehicleIQ" },
    { icon: "🤖", label: "Calcul VIQ.AI® Engine v5…", detail: "Fusion 70/30 marché × algo · Plafonds cohérence · Prix TTC & HT" },
    { icon: "🎯", label: "Génération annonce & plateformes…", detail: "10 plateformes recommandées · Score d'adéquation" },
  ]);
  const isUnlocked = paid;

  // v45 — Cotation simultanée NATIVE : même logique par véhicule que le flux
  // simple (appel /api/annonce/generate + rattachement Dossier VIQ), lancée
  // en parallèle (Promise.all) sur toutes les fiches dupliquées dans CE
  // formulaire. RÈGLE : "il faut que ce soit fait en simultané".
  const handleBatchSell = async () => {
    setBatchPayErr(null);
    const valid = batchVehicles.filter(v => v.make.trim());
    if (!valid.length) return;
    const cost = valid.length;
    if (!isUnlimitedPlan) {
      if (!spendWallet(cost)) { setBatchPayErr(`Solde insuffisant pour ${cost} cote${cost > 1 ? "s" : ""} (4,99 € chacune). Rechargez votre compte.`); return; }
      if (window.__viq_uid) {
        fetch(`${window.__viq_api_url || "https://vehicleiq-backend-production.up.railway.app"}/api/wallet/spend`, {
          method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${window.__viq_uid}` },
          body: JSON.stringify({ cost, label: `Cote x${cost} simultanées` }),
        }).catch(() => {});
      }
    }

    // v62 — En mode lot, on crée le lot AVANT de traiter les véhicules pour
    // pouvoir tagger chaque dossier avec le même lot_id dès sa création.
    let currentLotId = null;
    if (lotMode === "lot") {
      try {
        const lr = await apiPost("/api/dossier-lots", { vehicle_count: valid.length });
        if (lr?.success) { currentLotId = lr.lot.id; setLotId(lr.lot.id); }
      } catch (e) { /* non bloquant — le lot pourra être créé plus tard si besoin */ }
    }

    const runOne = async (v) => {
      try {
        const ans = {
          make: v.make.trim(), year_sell: v.year || "2020", km_sell: v.km || "80000",
          fuel_sell: v.fuel, gearbox_sell: v.gearbox || "Manuelle", state: v.state, ct: "Oui",
          urgency: "Normale", seller_type: "Professionnel", city_sell: "France",
          vin: v.vin || undefined, plaque: v.plaque || undefined,
          // v66 — Le moteur de cotation sait ajuster le prix à partir de
          // CarVertical (accidents/compteur trafiqué) côté serveur, mais rien
          // ne le lui transmettait jamais jusqu'ici — ni en individuel, ni en
          // lot. Corrigé ici pour le traitement en lot (le point remonté).
          carvertical: v.cvData || undefined,
        };
        const raw = await apiPost("/api/annonce/generate", ans);
        const resp = (raw?.success && raw?.data) ? raw.data : raw;
        if (!resp || (!resp.argus_mid && !resp.prix_viq && !resp.argus_low)) {
          return { ...v, ok: false, error: "Cote non calculée — précisez le modèle exact." };
        }
        let dossierId = v.dossierId || null;
        try {
          if (!dossierId && (v.vin || v.plaque || v.make)) {
            const dres = await apiPost("/api/dossiers/find-or-create", { vin: v.vin, plaque: v.plaque, make: v.make, year: v.year, fuel: v.fuel, lot_id: currentLotId || undefined });
            if (dres?.success) dossierId = dres.dossier.id;
          } else if (dossierId && currentLotId) {
            // Dossier déjà connu (repris d'un Contrôle simultané) : le tagger quand même dans le lot.
            apiPost("/api/dossiers/find-or-create", { vin: v.vin, plaque: v.plaque, make: v.make, year: v.year, fuel: v.fuel, lot_id: currentLotId }).catch(() => {});
          }
          if (dossierId) {
            apiPost(`/api/dossiers/${dossierId}/operations`, { type: "estimation", payload: resp }).catch(() => {});
            // v65 — Filet de sécurité : HistoVec/CarVertical/photos reprises du
            // Contrôle simultané ont normalement déjà été persistées à cette
            // étape-là, mais on les réécrit ici aussi (idempotent) pour ne rien
            // perdre si ce dossier vient d'ailleurs ou si cet appel avait échoué.
            if (v.hvData) apiPost(`/api/dossiers/${dossierId}/files`, { type: "histovec", payload: v.hvData }).catch(() => {});
            if (v.cvData) apiPost(`/api/dossiers/${dossierId}/files`, { type: "carvertical", payload: v.cvData }).catch(() => {});
            if (v.photos?.length) apiPost(`/api/dossiers/${dossierId}/files`, { type: "photo", payload: { images: v.photos, count: v.photos.length } }).catch(() => {});
          }
        } catch (e) { /* non bloquant */ }
        return { ...v, ok: true, result: resp, dossierId };
      } catch (e) {
        return { ...v, ok: false, error: e?.message || "Erreur réseau" };
      }
    };

    setBatchRunning(true);
    const settled = await Promise.all(valid.map(runOne));
    setBatchRunning(false);
    setBatchResults(settled);
    if (lotMode === "lot" && currentLotId) {
      const suggested = settled.filter(r => r.ok).reduce((sum, r) => sum + (Number(r.result?.prix_viq || r.result?.argus_mid) || 0), 0);
      if (suggested > 0) setLotPrice(String(Math.round(suggested)));
    }
    setStep("result");
  };

  const handleComplete = async (ans) => {
    if (!ans.make || String(ans.make).trim() === "") { alert("Veuillez indiquer la marque et le modèle du véhicule."); return; }

    // Enrichir make avec la finition saisie (si présente et ≠ "Autre / Inconnue")
    if (ans.finition && ans.finition !== "Autre / Inconnue" && !String(ans.make).toLowerCase().includes(String(ans.finition).toLowerCase())) {
      ans.make = String(ans.make).trim() + " " + String(ans.finition).trim();
    }

    // v42 — RÈGLE 3/13 : résoudre (ou mettre à jour) le dossier dès que
    // possible. Avant, on n'essayait QUE si un VIN/plaque était connu — toute
    // estimation manuelle (marque/modèle saisis à la main, sans décoder de
    // plaque) ne créait donc jamais aucun Dossier VIQ, silencieusement. Le
    // serveur sait maintenant créer un dossier via marque+modèle+année seuls
    // (repli tier 3) quand aucun identifiant officiel n'est disponible.
    // `resolvedDossierIdForThisRun` — et non le state `dossierId` — sert pour
    // le reste de cette fonction : setDossierId() ne met pas à jour la
    // variable locale dans cette même exécution (closure figée), donc lire
    // `dossierId` juste après l'avoir "set" renverrait encore l'ancienne
    // valeur (souvent null) au moment de rattacher l'estimation/les photos
    // plus bas — le dossier venait d'être créé mais restait vide.
    let resolvedDossierIdForThisRun = dossierId;
    if (!resolvedDossierIdForThisRun) {
      try {
        const dres = await apiPost("/api/dossiers/find-or-create", {
          vin: ans.vin, plaque: ans.plaque, make: ans.make, year: ans.year_sell, fuel: ans.fuel_sell,
        });
        if (dres?.success) { resolvedDossierIdForThisRun = dres.dossier.id; setDossierId(resolvedDossierIdForThisRun); }
      } catch (e) { /* non bloquant */ }
    }

    // ── Vérifier le solde AVANT de lancer le service (économie tokens IA) ──
    // Côté UI : invisible — on parle de "limite mensuelle" pas de crédits.
    const SERVICE_COST = multiMode ? 3 : 1;
    const currentBalance = getWallet();
    if (currentBalance < SERVICE_COST) {
      // Solde insuffisant : rediriger vers la page tarification (qui invite à
      // renseigner un moyen de paiement pour continuer au-delà des 5 estim.
      // gratuites mensuelles).
      setCallbackAnswersSell(ans);
      window.__viq_pending_sell_answers = ans;
      try { window.dispatchEvent(new CustomEvent("viq-open-pricing")); } catch(_) {}
      return;
    }
    // Débiter immédiatement et débloquer (le service est payé d'avance)
    if (spendWallet(SERVICE_COST)) {
      if (window.__viq_uid) {
        fetch(`${window.__viq_api_url||"https://vehicleiq-backend-production.up.railway.app"}/api/wallet/spend`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${window.__viq_uid}` },
          body: JSON.stringify({ cost: SERVICE_COST, label: multiMode ? "Cote x3 + 10 plateformes" : "Cote VIQ.AI® + 10 plateformes" })
        }).catch(() => {});
      }
      setPaid(true);
      if (onWalletChange) onWalletChange();
    }

    setCallbackAnswersSell(ans);
    setStep("loading");
    setLoadIdx(0);
    const makeName = (ans.make || "").split(" ")[0];
    const SELL_LOAD_STEPS = [
      { icon: "🔍", label: "Identification du véhicule…", detail: "Marque · Modèle · Millésime · Motorisation · Boîte · Kilométrage" },
      { icon: "📚", label: "Interrogation base catalogue constructeur…", detail: "Prix neuf officiel France · +8000 modèles · Segment VIQ.AI® v5 détecté" },
      { icon: "📊", label: "Lecture du cache marché…", detail: "Prix observés, mis à jour en continu" },
      { icon: "📅", label: "Application du coefficient saisonnier…", detail: "Cabriolet, sportive, SUV, familiale, citadine, utilitaire · Mois courant" },
      { icon: "📍", label: "Ajustement régional…", detail: "Tension marché par département · 100+ codes postaux référencés" },
      { icon: "🏙️", label: "Vérification zone ZFE projetée…", detail: "Crit'Air · Métropoles actives & 2026-2028 · Décote diesel/essence ancien" },
      { icon: "🛡️", label: "Croisement watchlist mécanique…", detail: "11 défauts modèles documentés · DSG7, N47, EA189, Puretech, EcoBoost…" },
      { icon: "📈", label: "Application du momentum marché 30j/90j…", detail: "Tendance segment · EV en baisse, hybride en hausse, diesel ancien chute" },
      { icon: "🧠", label: "Calibration Bayésienne sale_followup…", detail: "Apprentissage continu depuis prix vendus réels · Avantage VehicleIQ" },
      { icon: multiMode ? "⚡" : "🤖", label: multiMode ? `${maxSimul} cotes simultanées VIQ.AI® Engine v5…` : "Calcul VIQ.AI® Engine v5…", detail: multiMode ? `${maxSimul} cotes en parallèle · Comparaison de fourchettes` : "Fusion 70/30 marché × algo · Plafonds cohérence · Prix TTC & HT" },
      { icon: "🎯", label: "Génération annonce & plateformes…", detail: "10 plateformes recommandées · Score d'adéquation · Conseil prix vente rapide" },
    ];
    setSellLoadSteps(SELL_LOAD_STEPS);
    setKnownIssuesSell(null);
    setReliabilityScoreSell(null);
    setPartialCoteAlgo(null); // v14 : réinitialiser la cote algo affichée pendant le chargement

    // v101 — Règle utilisateur : plus aucun écran de chargement artificiel
    // pour la cotation. Le calcul réel (API + fallback ci-dessous) est déjà
    // quasi instantané (v95) ; on ne fait plus attendre l'utilisateur derrière
    // une animation d'étapes cosmétique de plusieurs secondes — le prix
    // s'affiche dès que le calcul est terminé, point.
    let sellResult = null;

    // IDs réels du formulaire SELL_QUESTIONS :
    // make, year_sell, km_sell, state, ct, price_expect, urgency, seller_type, pro_open, city_sell
    const make = ans.make || "";
    const year = parseInt(ans.year_sell) || 2020;
    const km = parseInt(ans.km_sell) || 80000;
    const state = ans.state || "Bon";
    const ct = ans.ct || "Oui";
    const priceExpect = parseInt(ans.price_expect) || 0;
    const urgency = ans.urgency || "Pas pressé";
    const sellerType = ans.seller_type || "Particulier";
    const proOpen = ans.pro_open || "Peu importe";
    const city = ans.city_sell || "France";

    // 1. Appel backend principal (VIQ.AI® Engine v5 — 100% algo + calibration
    // DB, plus d'appel IA/web en direct depuis useAI=false). Appel direct
    // (synchrone) plutôt que le mode job+polling : celui-ci imposait un
    // délai plancher d'au moins 1,5s avant même le premier sondage, ce qui
    // n'a plus lieu d'être pour une réponse désormais quasi instantanée.
    let paymentBlocked = false;
    let sellSearchId = null;
    try {
      const raw = await apiPost("/api/annonce/generate", ans);
      // Le backend peut retourner { success, data } ou directement l'objet
      const resp = (raw?.success && raw?.data) ? raw.data : raw;
      sellSearchId = raw?.search_id || null;
      if (resp && (resp.argus_low || resp.argus_mid || resp.annonce_titre)) {
        sellResult = resp;
      }
    } catch(e) {
      if (isPaymentRequiredError(e)) {
        // Paiement requis : NE PAS générer de fallback local (ce serait une estimation gratuite non voulue)
        paymentBlocked = true;
        handlePaymentRequired(e);
      } else {
        console.warn("[SellPanel] Backend unavailable:", e.message);
      }
    }

    // Si le paiement a été refusé, on arrête tout : pas de fallback, pas de résultat.
    if (paymentBlocked) {
      setStep("form");
      return;
    }

    // 3. Fallback — calcul argus basé sur les vraies données
    if (!sellResult) {
      const age = 2026 - year;
      // Mapping des 7 nouvelles options d'état → coefficient
      // (on matche par préfixe parce que les labels contiennent un tiret + description)
      const stateRaw = String(state || "").trim();
      const stateCoeffMap = {
        "Neuf": 1.18,                // bonus +18%
        "Comme neuf": 1.12,          // +12%
        "Excellent": 1.08,           // +8%
        "Très bon": 1.04,            // +4%
        "Bon": 1.0,                  // neutre
        "Correct": 0.88,             // -12%
        "Médiocre": 0.72,            // -28%
      };
      const stateKey = Object.keys(stateCoeffMap).find(k => stateRaw.startsWith(k)) || "Bon";
      const stateCoeff = stateCoeffMap[stateKey];
      const kmPenalty = Math.max(0, (km - 50000) * 0.04);
      const ctCoeff = ct === "Oui" ? 1.0 : ct === "À refaire" ? 0.94 : 0.88;
      const urgCoeff = urgency === "Immédiate" ? 0.90 : urgency === "1 mois" ? 0.95 : 1.0;
      const baseMid = Math.max(2000, Math.floor((20000 - (age * 1400) - kmPenalty) * stateCoeff * ctCoeff));
      sellResult = {
        make, model: "", year, km, fuel: "—",
        conseil_prix: priceExpect && priceExpect > baseMid * 1.1 ? `Votre prix espéré (${priceExpect.toLocaleString("fr-FR")} €) est au-dessus du marché — nous recommandons ${baseMid.toLocaleString("fr-FR")} €` : `Valeur marchande actuelle estimée alignée avec le marché actuel pour un ${make} de ${year}`,
        argus_low: Math.max(1500, Math.floor(baseMid * 0.87)),
        argus_mid: Math.max(2000, baseMid),
        argus_high: Math.max(2500, Math.floor(baseMid * urgCoeff * 1.13)),
        prix_viq: Math.round(Math.max(1500, Math.floor(baseMid * 0.87)) * 0.95 / 100) * 100,
        market_trend: "stable", score_vente: Math.floor(68 + stateCoeff * 10),
        plateformes: [
          { name: "LeBonCoin", url: "https://www.leboncoin.fr", score_vente: 94, type: "généraliste", commission: "Gratuit", delai_moyen: "2-3 semaines", profil_acheteurs: "Particuliers actifs, acheteurs locaux toutes catégories", description: "Plateforme n°1 en France avec 36M de visiteurs/mois — idéale pour les ventes rapides entre particuliers" },
          { name: "AutoScout24", url: "https://www.autoscout24.fr", score_vente: 90, type: "spécialisée", commission: "Gratuit / Pro", delai_moyen: "1-2 semaines", profil_acheteurs: "Acheteurs informés, professionnels et particuliers européens", description: "Forte visibilité internationale, idéal pour les véhicules premium ou avec historique clean" },
          { name: "La Centrale", url: "https://www.lacentrale.fr", score_vente: 87, type: "spécialisée", commission: "Gratuit", delai_moyen: "2-3 semaines", profil_acheteurs: "Acheteurs sérieux qui comparent les prix avant d'acheter", description: "Référence pour les cotes argus — attire des acheteurs préparés et décidés" },
          { name: "Facebook Marketplace", url: "https://www.facebook.com/marketplace", score_vente: 85, type: "généraliste", commission: "Gratuit", delai_moyen: "1-2 semaines", profil_acheteurs: "Particuliers locaux, acheteurs impulsifs 25-45 ans", description: "Vente très rapide en local — efficace pour les petits budgets et véhicules citadins" },
          { name: "ParuVendu", url: "https://www.paruvendu.fr", score_vente: 78, type: "généraliste", commission: "Gratuit", delai_moyen: "3-4 semaines", profil_acheteurs: "Acheteurs régionaux prudents, souvent premiers acheteurs", description: "Bon complément régional avec une audience moins concurrentielle" },
          { name: "Aramisauto", url: "https://www.aramisauto.com", score_vente: 75, type: "rachat professionnel", commission: "0% (rachat direct)", delai_moyen: "24-48h", profil_acheteurs: "Professionnel — vente immédiate garantie", description: "Rachat garanti en 24h pour une vente ultra-rapide, prix légèrement en dessous du marché" },
          { name: "Vroom", url: "https://www.vroom.com", score_vente: 72, type: "spécialisée", commission: "Gratuit", delai_moyen: "2-3 semaines", profil_acheteurs: "Acheteurs tech-savvy, recherche en ligne active", description: "Plateforme digitale moderne appréciée pour sa transparence et ses outils de financement" },
          { name: "Motorway", url: "https://motorway.co.uk", score_vente: 70, type: "enchères pro", commission: "Gratuit particulier", delai_moyen: "1 semaine", profil_acheteurs: "Réseau de 5000 revendeurs professionnels", description: "Mise aux enchères auprès de pros — idéal si vous souhaitez céder rapidement sans négociation" },
          { name: "Promoneuve", url: "https://www.promoneuve.fr", score_vente: 67, type: "mandataire", commission: "Gratuit", delai_moyen: "3-5 semaines", profil_acheteurs: "Acheteurs à la recherche de bonnes affaires récentes", description: "Moins de concurrence que LBC, clientèle qualifiée et sérieuse" },
          { name: "Mandataire Auto", url: "https://www.mandataire-auto.fr", score_vente: 64, type: "mandataire", commission: "Gratuit", delai_moyen: "4-6 semaines", profil_acheteurs: "Acheteurs cherchant remise sur véhicules récents", description: "Niche spécialisée avec une audience restreinte mais très ciblée" },
        ],
        annonce_titre: `${make} ${year} — ${km.toLocaleString("fr-FR")} km · ${callbackAnswersSell?.fuel_sell || "Essence"} · ${callbackAnswersSell?.gearbox_sell || ""}`,
        annonce_texte: `${make} ${year}, ${km.toLocaleString("fr-FR")} km, ${callbackAnswersSell?.fuel_sell || ""}, boîte ${callbackAnswersSell?.gearbox_sell || ""}. État ${state}.${ct === "Oui" || (ct||"").includes("Valide") ? " CT valide." : ""} ${callbackAnswersSell?.owners ? callbackAnswersSell.owners + "." : ""}${callbackAnswersSell?.options_detail?.trim() ? " Options : " + callbackAnswersSell.options_detail.trim() + "." : (callbackAnswersSell?.options && !callbackAnswersSell.options.includes("base") ? " Bien équipé." : "")} Contact sérieux uniquement.`,
        points_forts: [`Kilométrage de ${km.toLocaleString("fr-FR")} km cohérent pour un ${year}`, `CT ${ct === "Oui" ? "valide — argument de vente fort" : "à prévoir — prix ajusté en conséquence"}`, `État ${state} — ${stateCoeff >= 1 ? "prime au marché" : "reflété dans le prix"}`],
        points_attention: ["Joindre les factures d'entretien pour rassurer l'acheteur", "Prendre des photos en lumière naturelle pour maximiser les clics"],
      };
    }

    // v27 — Si des photos ont été ajoutées (déjà utilisées pour le préremplissage
    // ci-dessus), l'inspection visuelle s'exécute aussi ici : elle affine à la
    // fois la cote (prix) ET le score de fiabilité, avec la même image de
    // synthèse et les mêmes points de vigilance que pour "Contrôler un véhicule".
    if (sellPhotos.length > 0 && sellResult) {
      try {
        const insp = await apiPost("/api/vehicle/visual-inspection", {
          images: sellPhotos, make, model: sellResult.model || "", year, fuel: callbackAnswersSell?.fuel_sell, km,
        });
        if (insp?.success) {
          // Ajustement de prix proportionnel au score_delta (-30..+5 / 100) —
          // borné pour qu'une seule inspection visuelle ne puisse pas faire
          // dériver la cote de façon disproportionnée.
          const priceMultiplier = 1 + (insp.score_delta || 0) / 100 * 0.6;
          ["argus_low", "argus_mid", "argus_high", "prix_viq"].forEach(k => {
            if (sellResult[k]) sellResult[k] = Math.max(500, Math.round(sellResult[k] * priceMultiplier / 100) * 100);
          });
          if (typeof insp.score_delta === "number" && sellResult.score_vente != null) {
            sellResult.score_vente = Math.max(0, Math.min(100, sellResult.score_vente + insp.score_delta));
          }
          sellResult.visual_inspection = insp;
        }
      } catch (e) { /* inspection non bloquante — la cote reste valable sans elle */ }
      // v27 — Synchroniser la base : sans ça, /api/users/me continuerait de
      // renvoyer la cote/score INITIALE (celle loggée côté serveur avant que
      // l'inspection visuelle n'ait pu s'exécuter), et cette donnée périmée
      // écraserait la bonne valeur dès l'ouverture de l'historique — même bug
      // que celui déjà corrigé pour le Contrôle véhicule.
      if (sellSearchId) {
        apiPatch(`/api/search/${sellSearchId}`, { result: sellResult }).catch(() => {});
      }
    }

    // v37 — RÈGLE 5/9 : l'estimation s'enregistre comme événement rattaché au
    // dossier, avec le résultat définitif (inspection visuelle incluse si elle
    // a eu lieu juste au-dessus).
    if (resolvedDossierIdForThisRun && sellResult) {
      apiPost(`/api/dossiers/${resolvedDossierIdForThisRun}/operations`, { type: "estimation", payload: sellResult }).catch(() => {});
      // v38 FIX — RÈGLE 6 : les photos appartiennent au dossier.
      if (sellPhotos.length > 0) {
        apiPost(`/api/dossiers/${resolvedDossierIdForThisRun}/files`, {
          type: "photo",
          payload: { images: sellPhotos, count: sellPhotos.length, visual_inspection: sellResult.visual_inspection || undefined },
        }).catch(() => {});
      }
    }

    // v101 — Calcul terminé : on affiche le résultat immédiatement, plus
    // d'attente artificielle derrière une animation d'étapes.
    {
      if (multiMode) {
        const newResults = [...multiResults, sellResult];
        setMultiResults(newResults);
        if (multiRound < maxSimul) {
          // Prochain véhicule
          setMultiRound(r => r + 1);
          setStep("questions");
          setLoadIdx(0);
        } else {
          // Tous les véhicules complétés → afficher le comparatif
          setResult(sellResult);
          setStep("result");
          setMultiMode(false);
          setMultiRound(0);
          if (!paid) setTimeout(() => setShowGate(true), 600);
        }
      } else {
        // Ensure prix_viq is always present
      if (sellResult && sellResult.argus_low && !sellResult.prix_viq) {
        sellResult.prix_viq = Math.round((sellResult.argus_mid || sellResult.argus_low) * 0.95 / 100) * 100;
      }
      // Sauvegarder l'estimation dans l'historique local
      try {
        const existing = JSON.parse(localStorage.getItem("viq_history") || "[]");
        const last = existing[0];
        if (!last || last.type !== "sell" || (Date.now() - new Date(last.date).getTime()) >= 8000) {
          const entry = {
            // v28 FIX : même correctif que pour le Contrôle véhicule — utiliser
            // l'ID de la base (sellSearchId) quand disponible, pas Date.now(),
            // pour que la déduplication locale/distante fonctionne même quand
            // l'inspection visuelle (10-30s) a fait déborder la sauvegarde sur
            // la minute suivante.
            id: sellSearchId || Date.now(),
            date: new Date().toISOString(),
            type: "sell",
            answers: ans,
            data: ans,
            results: sellResult,
            result: sellResult,
          };
          lastSellHistoryIdRef.current = entry.id; // v13 : pour que enrichSell mette à jour LA bonne entrée
          localStorage.setItem("viq_history", JSON.stringify([entry, ...existing].slice(0, 50)));
        }
      } catch(e) {}
      // v5.2 : pré-sélectionner la stratégie de vente d'après l'urgency saisie
      // dans le formulaire. Le label peut commencer par "Immédiate", "Rapide" ou
      // "Normale". L'utilisateur pourra toujours switcher via les toggles.
      const urgLabel = String(ans.urgency || "").toLowerCase();
      if (urgLabel.startsWith("immédiate") || urgLabel.startsWith("immediate")) setSellStrategy("immediate");
      else if (urgLabel.startsWith("rapide")) setSellStrategy("rapide");
      else setSellStrategy("normale");
      setResult(sellResult);
        setStep("result");
        lastSellAnsRef.current = ans;
        // v14 : on ne calcule PLUS automatiquement le score de fiabilité ici —
        // avec seulement marque/modèle/année/carburant (aucun km, CT,
        // propriétaires réels), le calcul retombait systématiquement sur des
        // valeurs par défaut optimistes et produisait un score incohérent
        // avec celui, plus fiable, calculé ailleurs à partir de données
        // réelles (VIN décodé + HistoVec). Le score ne se calcule maintenant
        // que si un rapport HistoVec ou CarVertical est réellement importé
        // (voir onHistovecImportedSell / onCarVerticalImportedSell).
        if (!paid) setTimeout(() => setShowGate(true), 600);
      }
    }
  };

  return (
    <>
      <div className="overlay">
        <div className="panel panel-wide">
          <div className="panel-header">
            <div>
              <div className="panel-title" style={{ color: "var(--accent2)" }}>Estimer mon véhicule</div>
              <div className="panel-sub">Obtenez la Cote de votre véhicule</div>
            </div>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="panel-body">
            {step === "questions" && (
              <div>
                {/* v45 — Cotation simultanée NATIVE : un menu déroulant choisit le
                    nombre de véhicules ; au-delà de 1, ce même écran affiche des
                    champs dupliqués (essentiels) au lieu du questionnaire complet
                    à étapes — plus de modale séparée. */}
                {maxSimul > 1 && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: ".6rem", marginBottom: "1rem" }}>
                    <label style={{ fontSize: ".78rem", color: "var(--sub)", fontWeight: 600 }}>Nombre de véhicules à coter :</label>
                    <select
                      value={vehicleCount}
                      onChange={e => {
                        const n = parseInt(e.target.value, 10);
                        setVehicleCount(n);
                        setBatchVehicles(prev => {
                          const next = prev.slice(0, n);
                          while (next.length < n) next.push({ make: "", year: "", km: "", fuel: "Essence", state: "Bon", vin: null, plaque: null, dossierId: null, gearbox: null, hvData: null, cvData: null, photos: [], visualInspection: null });
                          return next;
                        });
                      }}
                      style={{ padding: ".35rem .6rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".82rem", fontWeight: 600 }}>
                      {Array.from({ length: maxSimul }, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>{n === 1 ? "1 (simple)" : `${n} en simultané`}</option>
                      ))}
                    </select>
                  </div>
                )}
                {vehicleCount > 1 ? (
                  <div>
                    <div style={{ fontSize: ".84rem", color: "var(--sub)", marginBottom: "1rem" }}>
                      Jusqu'à {maxSimul} véhicules, cotés en parallèle — pas les uns après les autres.
                      {batchVehicles.some(v => v.vin || v.plaque) ? " Pré-rempli depuis votre Contrôle simultané." : ""}
                    </div>
                    {/* v62 — Lot vs individuel */}
                    <div style={{ display: "flex", gap: ".5rem", marginBottom: "1rem" }}>
                      {[["individual", "🔢 Prix individuel", "Chaque véhicule garde sa propre cote."], ["lot", "📦 Traiter en lot", "Un seul prix global pour tout le lot (vente groupée)."]].map(([id, label, hint]) => (
                        <button key={id} type="button" onClick={() => setLotMode(id)}
                          style={{ flex: 1, textAlign: "left", padding: ".6rem .8rem", borderRadius: 10, cursor: "pointer",
                            border: "1.5px solid " + (lotMode === id ? "var(--accent)" : "var(--border)"),
                            background: lotMode === id ? "var(--accent-soft)" : "var(--card)" }}>
                          <div style={{ fontSize: ".8rem", fontWeight: 700, color: lotMode === id ? "var(--accent)" : "var(--text)" }}>{label}</div>
                          <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".1rem" }}>{hint}</div>
                        </button>
                      ))}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: ".7rem", marginBottom: "1rem" }}>
                      {batchVehicles.map((v, i) => (
                        <div key={i} style={{ padding: ".7rem", border: "1.5px solid var(--border)", borderRadius: 10, background: "var(--card)" }}>
                          <div style={{ display: "flex", gap: ".5rem", marginBottom: ".5rem", flexWrap: "wrap" }}>
                            <input style={{ flex: "2 1 200px", padding: ".5rem .6rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--white)", color: "var(--text)", fontSize: ".82rem", boxSizing: "border-box" }}
                              placeholder="Marque & modèle (ex : Peugeot 308)" value={v.make}
                              onChange={e => setBatchVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, make: e.target.value } : x))} />
                          </div>
                          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                            <input style={{ flex: 1, minWidth: 90, padding: ".5rem .6rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--white)", color: "var(--text)", fontSize: ".82rem", boxSizing: "border-box" }}
                              placeholder="Année" value={v.year}
                              onChange={e => setBatchVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, year: e.target.value.replace(/\D/g, "") } : x))} />
                            <input style={{ flex: 1, minWidth: 90, padding: ".5rem .6rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--white)", color: "var(--text)", fontSize: ".82rem", boxSizing: "border-box" }}
                              placeholder="Kilométrage" value={v.km}
                              onChange={e => setBatchVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, km: e.target.value.replace(/\D/g, "") } : x))} />
                            <select style={{ flex: 1, minWidth: 90, padding: ".5rem .6rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--white)", color: "var(--text)", fontSize: ".82rem" }}
                              value={v.fuel} onChange={e => setBatchVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, fuel: e.target.value } : x))}>
                              {["Essence", "Diesel", "Hybride", "Hybride rechargeable", "Électrique"].map(o => <option key={o}>{o}</option>)}
                            </select>
                            <select style={{ flex: 1, minWidth: 90, padding: ".5rem .6rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--white)", color: "var(--text)", fontSize: ".82rem" }}
                              value={v.state} onChange={e => setBatchVehicles(prev => prev.map((x, idx) => idx === i ? { ...x, state: e.target.value } : x))}>
                              {["Très bon", "Bon", "Moyen", "À rénover"].map(o => <option key={o}>{o}</option>)}
                            </select>
                          </div>
                          {(v.vin || v.plaque) && <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".4rem" }}>🔗 {v.vin || v.plaque} — rattaché à son Dossier VIQ</div>}
                          {(v.hvData || v.cvData || v.photos?.length > 0 || v.visualInspection) && (
                            <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap", marginTop: ".4rem" }}>
                              {v.hvData && <span style={{ fontSize: ".66rem", fontWeight: 700, color: "var(--green)", background: "rgba(46,111,92,.1)", borderRadius: 10, padding: ".1rem .5rem" }}>✓ HistoVec repris</span>}
                              {v.cvData && <span style={{ fontSize: ".66rem", fontWeight: 700, color: "var(--green)", background: "rgba(46,111,92,.1)", borderRadius: 10, padding: ".1rem .5rem" }}>✓ CarVertical repris</span>}
                              {v.photos?.length > 0 && <span style={{ fontSize: ".66rem", fontWeight: 700, color: "var(--green)", background: "rgba(46,111,92,.1)", borderRadius: 10, padding: ".1rem .5rem" }}>✓ {v.photos.length} photo(s) reprise(s)</span>}
                              {v.visualInspection && <span style={{ fontSize: ".66rem", fontWeight: 700, color: "var(--green)", background: "rgba(46,111,92,.1)", borderRadius: 10, padding: ".1rem .5rem" }}>✓ Analyse IA reprise</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <button className={`btn btn-accent ${batchRunning ? "btn-disabled" : ""}`} disabled={batchRunning} onClick={handleBatchSell} style={{ width: "100%", justifyContent: "center" }}>
                      {batchRunning ? "Cotation en cours…" : `Coter ces véhicules${isUnlimitedPlan ? "" : ` (${batchVehicles.filter(v => v.make.trim()).length || 1} × 4,99 €)`}`}
                    </button>
                    {batchPayErr && <div style={{ marginTop: ".6rem", fontSize: ".8rem", color: "var(--red, #c0392b)" }}>⚠ {batchPayErr}</div>}
                  </div>
                ) : (
                <>
                {/* Remplissage automatique par photos (jusqu'à 10) */}
                <div style={{ marginBottom: ".8rem", padding: ".75rem 1rem", background: "rgba(46,111,92,.05)", border: "1.5px solid rgba(46,111,92,.25)", borderRadius: 10 }}>
                  <div style={{ fontSize: ".82rem", fontWeight: 700, color: "var(--green)" }}>📸 Remplir à partir de photos (recommandé)</div>
                  <div style={{ fontSize: ".7rem", color: "var(--sub)", marginTop: ".15rem", lineHeight: 1.45, marginBottom: ".5rem" }}>
                    Ajoutez jusqu'à 10 photos, puis lancez l'analyse — les champs se pré-remplissent automatiquement. Idéalement : 3/4 avant <strong>avec plaque visible</strong>, 3/4 arrière, tableau de bord, siège conducteur, siège passager, et toute autre vue utile.
                  </div>
                  {/* Aperçus des photos sélectionnées */}
                  {sellPhotos.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginBottom: ".6rem" }}>
                      {sellPhotos.map((src, i) => (
                        <div key={i} style={{ position: "relative", width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: "1.5px solid var(--border)" }}>
                          <img src={src} alt={"photo " + (i + 1)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <button type="button" disabled={photoBusy} onClick={() => setSellPhotos(prev => prev.filter((_, j) => j !== i))}
                            style={{ position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: "50%", border: "none", background: "rgba(0,0,0,.6)", color: "#fff", fontSize: ".7rem", cursor: "pointer", lineHeight: 1, padding: 0 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", alignItems: "center" }}>
                    {sellPhotos.length < 10 && (
                      <button type="button" disabled={photoBusy}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        style={{ padding: ".5rem 1rem", borderRadius: 8, border: "1px solid var(--green)", cursor: photoBusy ? "not-allowed" : "pointer", background: "var(--card)", color: "var(--green)", fontSize: ".8rem", fontWeight: 700, fontFamily: "var(--font-body)", opacity: photoBusy ? .5 : 1 }}>
                        🖼 Galerie
                      </button>
                    )}
                    {sellPhotos.length < 10 && (
                      <button type="button" disabled={photoBusy}
                        onClick={() => sellCameraRef.current && sellCameraRef.current.click()}
                        style={{ padding: ".5rem 1rem", borderRadius: 8, border: "1px solid var(--green)", cursor: photoBusy ? "not-allowed" : "pointer", background: "var(--card)", color: "var(--green)", fontSize: ".8rem", fontWeight: 700, fontFamily: "var(--font-body)", opacity: photoBusy ? .5 : 1 }}>
                        📷 Appareil photo
                      </button>
                    )}
                    {sellPhotos.length < 10 && <span style={{ fontSize: ".75rem", color: "var(--muted)" }}>{sellPhotos.length}/10</span>}
                    {sellPhotos.length > 0 && (
                      <button type="button" disabled={photoBusy}
                        onClick={analyzePhotos}
                        style={{ padding: ".5rem 1.1rem", borderRadius: 8, border: "none", cursor: photoBusy ? "wait" : "pointer", background: photoBusy ? "var(--bg)" : "var(--green)", color: photoBusy ? "var(--sub)" : "#fff", fontSize: ".8rem", fontWeight: 700, fontFamily: "var(--font-body)", whiteSpace: "nowrap" }}>
                        {photoBusy ? "Analyse en cours…" : `✨ Analyser ${sellPhotos.length} photo${sellPhotos.length > 1 ? "s" : ""} →`}
                      </button>
                    )}
                    {/* Galerie (sans capture) + Appareil photo (capture) — les deux proposés */}
                    <input ref={fileInputRef} type="file" accept="image/*" multiple
                      style={{ display: "none" }} onChange={(e) => addSellPhotos(e.target.files)} />
                    <input ref={sellCameraRef} type="file" accept="image/*" capture="environment"
                      style={{ display: "none" }} onChange={(e) => addSellPhotos(e.target.files)} />
                  </div>
                  {photoBusy && (
                    <div style={{ fontSize: ".72rem", color: "var(--sub)", marginTop: ".5rem", fontStyle: "italic" }}>
                      Lecture des photos (plaque, compteur, modèle, état…) — quelques secondes.
                    </div>
                  )}
                  {photoMsg && (
                    <div style={{ fontSize: ".74rem", fontWeight: 600, marginTop: ".5rem", color: photoMsg.ok ? "var(--green)" : "var(--red)" }}>
                      {photoMsg.ok ? "✓ " : "⚠ "}{photoMsg.text}
                    </div>
                  )}
                  <div style={{ fontSize: ".64rem", color: "var(--muted)", marginTop: ".4rem" }}>
                    {isUnlimitedPlan
                      ? "Inclus dans votre abonnement Pro (illimité). Photos non conservées."
                      : "Analyse facturée comme une cote (4,99 € si vos essais offerts sont épuisés). Photos non conservées."}
                  </div>
                </div>
                <PlaqueAutoFill onFill={(data) => {
                  // Normalise la motorisation vers les options exactes du radio
                  const normFuel = (raw) => {
                    if (!raw || raw === "—") return null;
                    const s = String(raw).toLowerCase();
                    if (s.includes("hybride") && (s.includes("recharge") || s.includes("plug") || s.includes("phev"))) return "Hybride rechargeable";
                    if (s.includes("hybride") || s.includes("hev") || s.includes("hybrid")) return "Hybride";
                    if (s.includes("électr") || s.includes("electr") || s.includes("ev")) return "Électrique";
                    if (s.includes("gpl") || s.includes("lpg")) return "GPL";
                    if (s.includes("diesel") || s.includes("gazole") || s.includes("go") || s.includes("hdi") || s.includes("tdi") || s.includes("dci") || s.includes("bluehdi")) return "Diesel";
                    if (s.includes("essence") || s.includes("eg") || s.includes("sp") || s.includes("petrol") || s.includes("gasoline")) return "Essence";
                    return null;
                  };
                  // Normalise la boîte vers les options exactes du radio
                  const normGearbox = (raw) => {
                    if (!raw || raw === "—") return null;
                    const s = String(raw).toLowerCase();
                    if (s.includes("dsg") || s.includes("edc") || s.includes("cvt") || s.includes("dct") || s.includes("pdk") || s.includes("s tronic") || s.includes("eat")) return "CVT / DSG / EDC";
                    if (s.includes("auto") || s.includes("at") || s.includes("bva") || s.includes("tiptronic")) return "Automatique";
                    if (s.includes("manu") || s.includes("mt") || s.includes("bvm")) return "Manuelle";
                    return null;
                  };
                  // Normalise un nombre (year, km) en gérant les chaînes "55 000" ou "55000"
                  const toNum = (raw) => {
                    if (raw === undefined || raw === null || raw === "" || raw === "—") return null;
                    if (typeof raw === "number") return raw > 0 ? raw : null;
                    const n = parseInt(String(raw).replace(/\D/g, ""), 10);
                    return isNaN(n) || n <= 0 ? null : n;
                  };
                  // Pioche la première valeur numérique valide parmi plusieurs champs candidats
                  const pickNum = (...candidates) => {
                    for (const c of candidates) {
                      const n = toNum(c);
                      if (n !== null) return n;
                    }
                    return null;
                  };
                  const fuel = normFuel(data.fuel);
                  const gearbox = normGearbox(data.transmission || data.gearbox);
                  const year = pickNum(data.year, data.first_registration_year, data.annee, data.first_circulation, data.date_premiere_circulation);
                  const km = pickNum(
                    data.mileage, data.km_reported, data.km, data.kilometrage,
                    data.last_mileage, data.last_km, data.km_ct, data.ct_km,
                    data.compteur, data.compteur_km
                  );
                  const fullName = buildVehicleFullName(data);
                  setPrefill(prev => ({
                    ...prev,
                    make:         fullName || prev?.make,
                    year_sell:    year !== null ? year : prev?.year_sell,
                    km_sell:      km !== null ? km : prev?.km_sell,
                    fuel_sell:    fuel || prev?.fuel_sell,
                    gearbox_sell: gearbox || prev?.gearbox_sell,
                    // v36 FIX — cause racine du bug "plusieurs dossiers pour le même
                    // véhicule" : le VIN et la plaque, pourtant présents dans la
                    // réponse de décodage, n'étaient JAMAIS conservés. Sans eux, le
                    // Dossier VIQ ne pouvait jamais relier cette estimation au
                    // Contrôle fait sur le même véhicule — une nouvelle fiche était
                    // créée à chaque fois.
                    vin:          data.vin || prev?.vin,
                    plaque:       data.plaque || data.immat || prev?.plaque,
                  }));
                }}
                onImportDossier={async (plaque) => {
                  try {
                    const r = await apiGet(`/api/dossiers/lookup?plaque=${encodeURIComponent(plaque)}`);
                    if (!r?.success || !r.found) return null;
                    setDossierId(r.dossier.id);
                    if (r.photos && r.photos.length) setSellPhotos(r.photos.slice(0, 10));
                    if (r.currentFiles?.histovec) setHvDataSell({ found: true, ...r.currentFiles.histovec.payload });
                    if (r.currentFiles?.carvertical) setCvDataSell({ found: true, ...r.currentFiles.carvertical.payload });
                    const parts = [r.dossier.make, r.dossier.model].filter(Boolean).join(" ");
                    return `✓ Dossier existant importé${parts ? ` — ${parts}` : ""}${r.photos?.length ? ` · ${r.photos.length} photo(s)` : ""}${r.currentFiles?.histovec ? " · HistoVec" : ""}${r.currentFiles?.carvertical ? " · CarVertical" : ""}.`;
                  } catch (e) { return "Import impossible : " + (e?.message || "erreur réseau"); }
                }} />
                <div style={{ margin: ".8rem 0" }}>
                  <HistovecImport onImported={setHvDataSell} compact />
                  <HistovecSummary hv={hvDataSell} />
                </div>
                <div style={{ margin: ".8rem 0" }}>
                  <CarVerticalImport onImported={setCvDataSell} compact vin={prefill?.vin} plaque={prefill?.plaque || prefill?.immat} />
                  <CarVerticalSummary cv={cvDataSell} />
                </div>
                <Questionnaire
                  key={`sell-q-${relaunchKey}`}
                  questions={SELL_QUESTIONS}
                  onComplete={handleComplete}
                  required
                  prefillAnswers={relaunchPrefill || prefill} />
                </>
                )}
              </div>
            )}
            {step === "loading" && (
              <>
                <LoadingExperience
                  steps={sellLoadSteps}
                  activeIdx={loadIdx}
                  make={callbackAnswersSell?.make || ""}
                  year={callbackAnswersSell?.year_sell || callbackAnswersSell?.year || ""}
                  version="5"
                />
                {/* v14 : cote 100% algo affichée dès qu'elle est disponible (calcul
                    local instantané), pendant que la fusion marché IA est encore
                    en cours — évite l'écran de chargement "muet" sur toute sa durée. */}
                {partialCoteAlgo && <PartialCoteAlgoCard cote={partialCoteAlgo} />}
              </>
            )}
            {step === "result" && batchResults && (
              <div>
                <div style={{ fontWeight: 700, fontSize: ".9rem", marginBottom: "1rem" }}>
                  {batchResults.filter(r => r.ok).length}/{batchResults.length} cotation(s) réussie(s)
                  {lotMode === "lot" && <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: ".78rem" }}> · traitement en lot</span>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: ".6rem" }}>
                  {batchResults.map((r, i) => {
                    const eur = (v) => (v || v === 0) ? Math.round(Number(v)).toLocaleString("fr-FR") + " €" : "—";
                    return (
                      <div key={i} style={{ padding: ".8rem 1rem", borderRadius: 10, border: "1px solid " + (r.ok ? "var(--border)" : "rgba(176,90,58,.4)"), background: r.ok ? "var(--card)" : "rgba(176,90,58,.05)" }}>
                        <div style={{ fontWeight: 700, fontSize: ".84rem" }}>{r.make}{r.year ? ` · ${r.year}` : ""}</div>
                        {r.ok ? (
                          <div style={{ fontSize: ".82rem", color: "var(--green)", fontWeight: 700, marginTop: ".2rem" }}>
                            {lotMode === "lot" && <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: ".7rem" }}>Réf. individuelle : </span>}
                            {eur(r.result.prix_viq || r.result.argus_mid)}
                            <span style={{ fontWeight: 400, color: "var(--sub)", fontSize: ".74rem" }}> · fourchette {eur(r.result.argus_low)} – {eur(r.result.argus_high)}</span>
                          </div>
                        ) : (
                          <div style={{ fontSize: ".78rem", color: "var(--red, #b03a3a)", marginTop: ".2rem" }}>⚠ {r.error}</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {lotMode === "lot" && lotId && (
                  <div style={{ marginTop: "1.2rem", padding: ".9rem 1rem", background: "var(--accent-soft)", border: "1.5px solid var(--accent)", borderRadius: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: ".84rem", marginBottom: ".5rem" }}>📦 Prix global du lot</div>
                    <div style={{ fontSize: ".74rem", color: "var(--sub)", marginBottom: ".6rem" }}>
                      Pré-rempli avec la somme des cotes individuelles — ajustez selon votre négociation.
                    </div>
                    <div style={{ display: "flex", gap: ".6rem", alignItems: "center" }}>
                      <input type="number" value={lotPrice} onChange={e => { setLotPrice(e.target.value); setLotSaved(false); }}
                        placeholder="Prix total du lot (€)"
                        style={{ flex: 1, padding: ".6rem .8rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--white)", color: "var(--text)", fontSize: ".9rem", fontWeight: 700 }} />
                      <button className="btn btn-accent" disabled={!lotPrice || lotSaved} onClick={async () => {
                        try {
                          const r = await apiPatch(`/api/dossier-lots/${lotId}`, { total_price: Number(lotPrice) });
                          if (r?.success) setLotSaved(true);
                        } catch (e) {}
                      }}>
                        {lotSaved ? "✓ Enregistré" : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", gap: ".6rem", marginTop: "1.2rem" }}>
                  <button className="btn btn-outline" style={{ flex: 1, justifyContent: "center" }}
                    onClick={() => { setStep("questions"); setBatchResults(null); setLotId(null); setLotPrice(""); setLotSaved(false); }}>
                    Nouvelle cotation
                  </button>
                  <button className="btn btn-accent" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>
                    Fermer
                  </button>
                </div>
              </div>
            )}
            {step === "result" && result && (
              <div className="sell-result">
                {/* Comparatif multi-estimation + carousel */}
                {multiResults.length > 1 && (
                  <MultiEstimateComparator multiResults={multiResults} />
                )}
                {/* Comparatif multi-estimation si disponible */}
                {multiResults.length > 1 && (
                  <div style={{ marginBottom:"1rem", padding:".8rem 1rem", background:"rgba(255,184,0,.06)", border:"1px solid rgba(255,184,0,.2)", borderRadius:8 }}>
                    <div style={{ fontSize:".72rem", color:"var(--accent2)", fontWeight:700, textTransform:"uppercase", letterSpacing:".5px", marginBottom:".6rem" }}>⚡ Comparatif {multiResults.length} véhicules</div>
                    <div style={{ display:"flex", gap:".6rem", overflowX:"auto" }}>
                      {multiResults.map((r,i) => {
                        const prix = r.prix_viq || Math.round(((r.argus_mid||r.argus_low)||0)*0.95/100)*100;
                        return (
                          <div key={i} style={{ minWidth:140, background:"var(--card)", border:"1px solid var(--border)", borderRadius:6, padding:".6rem .8rem", flexShrink:0 }}>
                            <div style={{ fontSize:".72rem", fontWeight:700, marginBottom:".3rem" }}>#{i+1} {r.make} {r.year}</div>
                            <div style={{ fontSize:".85rem", color:"var(--green)", fontWeight:700 }}>{prix.toLocaleString("fr-FR")} €</div>
                            <div style={{ fontSize:".65rem", color:"var(--muted)" }}>Score {r.score_vente||"—"}/100</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Bandeau photo du modèle */}
                <VehiclePhotoBanner make={result.make} model={result.model} year={result.year} color={result.color} fuel={result.fuel_sell || callbackAnswersSell?.fuel_sell} />

                {/* Véhicule header */}
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: "1rem 1.2rem", marginBottom: ".8rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{result.make || "—"} {result.model || ""} {result.year || ""}</div>
                    <div style={{ fontSize: ".78rem", color: "var(--sub)" }}>{result.km ? Number(result.km).toLocaleString("fr-FR") + " km · " : ""}{result.fuel || ""}</div>
                    <div style={{ marginTop: ".3rem" }}><span style={{ display: "inline-flex", alignItems: "center", gap: ".3rem", fontSize: ".62rem", fontFamily: "var(--font-mono)", color: "var(--accent2)", background: "rgba(255,184,0,.08)", border: "1px solid rgba(255,184,0,.2)", borderRadius: 10, padding: ".1rem .45rem", letterSpacing: ".3px", flexShrink: 0 }}>✦ VIQ.AI® Engine</span></div>
                  </div>
                  {result.aiGenerated && <span style={{ fontSize: ".65rem", background: "rgba(255,184,0,.1)", border: "1px solid rgba(255,184,0,.3)", color: "var(--accent2)", borderRadius: 10, padding: ".2rem .6rem", fontFamily: "var(--font-mono)" }}>✦ IA</span>}
                </div>

  
              {/* Résultat partiellement flouté si non payé — même style que Buy */}
              <div style={{ position:"relative", overflow:"hidden" }}>
                {!isUnlocked && (
                  <div style={{
                    position:"absolute", inset:0, zIndex:10,
                    background:"linear-gradient(to bottom, rgba(17,17,17,0) 0%, rgba(17,17,17,0) 30%, rgba(17,17,17,.85) 60%, rgba(17,17,17,.98) 100%)",
                    borderRadius:8,
                    display:"flex", flexDirection:"column",
                    alignItems:"center", justifyContent:"flex-end",
                    paddingBottom:"1.5rem", gap:".6rem"
                  }}>
                    <div style={{ fontSize:".82rem", color:"var(--sub)", textAlign:"center" }}>
                      🔒 Cote verrouillée
                    </div>
                  </div>
                )}
                {!isUnlocked && (
                  <div style={{ position:"absolute", inset:0, zIndex:10, backdropFilter:"blur(6px) brightness(0.4)", WebkitBackdropFilter:"blur(6px) brightness(0.4)", borderRadius:8, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:".5rem", cursor:"pointer" }}
                    onClick={() => setShowGate(true)}>
                    <div style={{ fontSize:"1.5rem" }}>🔒</div>
                    <div style={{ fontSize:".78rem", fontWeight:700, color:"rgba(255,255,255,.9)", textShadow:"0 1px 3px rgba(0,0,0,.8)" }}>Résultats verrouillés</div>
                    <div style={{ fontSize:".7rem", color:"rgba(255,255,255,.6)", textShadow:"0 1px 3px rgba(0,0,0,.8)" }}>Cliquez pour déverrouiller</div>
                  </div>
                )}
              {/* v15 : Cote actuelle estimée + Cote 100% algo affichées côte à côte
                  (au lieu d'empilées), et "Notre Cote VIQ.AI" ne désigne plus QUE
                  la cote algo — l'ancienne étiquette de repli sur le prix principal
                  était source de confusion entre les deux cotes.
                  v29 : le flou "Réservé aux comptes Pro" est retiré — la cote est
                  désormais visible pour tous les comptes, avec ou sans SIRET. */}
                {(result.argus_low || result.argus_mid) && (() => {
                  const prixVIQ = result.prix_viq || Math.round(((result.argus_mid || result.argus_low) || 0) * 0.95 / 100) * 100;
                  return (
                    <div style={{ display: "flex", gap: ".7rem", marginBottom: "1rem", flexWrap: "wrap", animation:"slideUp .4s ease" }}>
                      <div style={{ flex: "1 1 220px", background: "rgba(0,180,90,.07)", border: "2px solid rgba(0,200,100,.3)", borderRadius: 10, padding: "1.2rem 1.5rem", textAlign: "center" }}>
                        <div style={{ fontSize: ".65rem", color: "var(--green)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: ".4rem", fontWeight: 700 }}>
                          ✦ Cote actuelle estimée
                        </div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "2.8rem", color: "var(--green)", fontWeight: 600, lineHeight: 1, letterSpacing: "-.01em", animation:"countUp .6s ease" }}>
                          {prixVIQ.toLocaleString("fr-FR")} €
                        </div>
                      </div>

                      {/* Cote 100% algo — toujours affichée juste à côté de la cote principale */}
                      {result.cote_algo && (
                        <div style={{ flex: "1 1 220px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, padding: "1.2rem 1.5rem", textAlign: "center" }}>
                          <div style={{ fontSize: ".65rem", color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: ".4rem", fontWeight: 700 }}>
                            ✦ Notre Cote VIQ.AI
                          </div>
                          <div style={{ fontFamily: "var(--font-head)", fontSize: "2rem", fontWeight: 700, color: "var(--accent)", lineHeight: 1 }}>
                            {Number(result.cote_algo.argus_mid).toLocaleString("fr-FR")} €
                          </div>
                          <div style={{ fontSize: ".72rem", color: "var(--sub)", marginTop: ".3rem" }}>
                            {Number(result.cote_algo.argus_low).toLocaleString("fr-FR")} – {Number(result.cote_algo.argus_high).toLocaleString("fr-FR")} €
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Fourchette de marché — toujours affichée (bas / haut),
                    même en cote 100% algorithmique sans données marché. */}
                {(() => {
                  const mid = Number(result.argus_mid || result.prix_viq || result.argus_low) || null;
                  let low = Number(result.argus_low || result.cote_algo?.argus_low) || (mid ? Math.round(mid * 0.92 / 100) * 100 : null);
                  let high = Number(result.argus_high || result.cote_algo?.argus_high) || (mid ? Math.round(mid * 1.08 / 100) * 100 : null);
                  if (!low || !high) return null;
                  if (high < low) { const t = low; low = high; high = t; }
                  return (
                    <div style={{ display: "flex", gap: ".7rem", marginBottom: ".8rem" }}>
                      <div style={{ flex: 1, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: ".6rem .9rem", textAlign: "center" }}>
                        <div style={{ fontSize: ".62rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 700 }}>Bas du marché</div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.25rem", fontWeight: 700, color: "var(--text)" }}>{low.toLocaleString("fr-FR")} €</div>
                      </div>
                      <div style={{ flex: 1, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: ".6rem .9rem", textAlign: "center" }}>
                        <div style={{ fontSize: ".62rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".6px", fontWeight: 700 }}>Haut du marché</div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.25rem", fontWeight: 700, color: "var(--text)" }}>{high.toLocaleString("fr-FR")} €</div>
                      </div>
                    </div>
                  );
                })()}

                <div style={{ fontSize: ".68rem", color: "var(--muted)", marginBottom: ".8rem", lineHeight: 1.45, textAlign: "center" }}>
                  La Cote actuelle estimée fusionne notre calcul algorithmique avec les données marché temps réel. La Cote VIQ.AI (100% algo) est un calcul stable et reproductible (prix neuf, dépréciation, kilométrage, état) — indépendant du marché du moment. Estimations indicatives, non contractuelles.
                </div>

                {/* v15 : import HistoVec / CarVertical retiré du flux d'estimation —
                    ces rapports (et le score de fiabilité qui en découle) sont
                    désormais exclusifs au Contrôle plaque/VIN (VINPanel), où de
                    vraies données véhicule (VIN décodé) sont disponibles. */}

                {/* Conseil prix + Prix vente rapide */}
                {result.conseil_prix && (
                  <div style={{ background: "rgba(255,184,0,.06)", border: "1px solid rgba(255,184,0,.2)", borderRadius: 6, padding: ".7rem 1rem", marginBottom: ".8rem" }}>
                    <div style={{ fontSize: ".82rem", marginBottom: ".4rem" }}>💡 <strong>Conseil pratique :</strong> {result.conseil_prix}</div>
                    <div style={{ paddingTop: ".45rem", borderTop: "1px solid rgba(255,184,0,.15)", fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.55 }}>
                      🚀 Pour maximiser vos chances de vente, <strong style={{ color: "var(--accent)" }}>publiez votre annonce dès maintenant</strong> sur les plateformes recommandées ci-dessous, avec des photos de qualité et une description complète. Un prix cohérent avec le marché et une annonce soignée réduisent le délai de vente de 40%.
                    </div>
                  </div>
                )}

                {/* Stratégies de prix par cible : Pro vs Particuliers + stratégie choisie */}
                {(result.strategie_pro || result.strategie_particuliers) && (
                  <div style={{ marginBottom: ".8rem" }}>
                    {(result.urgency_choisie || result.urgency) && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", fontSize: ".72rem", fontWeight: 800, background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 20, padding: ".25rem .7rem", marginBottom: ".55rem" }}>
                        🎯 Votre stratégie : {result.urgency_choisie || result.urgency}
                      </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: ".7rem" }}>
                      {result.strategie_particuliers && (
                        <div style={{ background: "rgba(46,111,92,.05)", border: "1.5px solid rgba(46,111,92,.3)", borderRadius: 8, padding: ".7rem .9rem" }}>
                          <div style={{ fontSize: ".62rem", color: "var(--green)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".35rem" }}>👤 Cible Particuliers — vente directe</div>
                          {result.strategie_particuliers.fourchette_low && (
                            <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--green)", fontFamily: "var(--font-head)", marginBottom: ".3rem" }}>
                              {Number(result.strategie_particuliers.fourchette_low).toLocaleString("fr-FR")} – {Number(result.strategie_particuliers.fourchette_high).toLocaleString("fr-FR")} €
                            </div>
                          )}
                          {result.strategie_particuliers.conseil && <div style={{ fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.5 }}>{result.strategie_particuliers.conseil}</div>}
                        </div>
                      )}
                      {result.strategie_pro && (
                        <div style={{ background: "rgba(30,42,74,.04)", border: "1.5px solid rgba(30,42,74,.25)", borderRadius: 8, padding: ".7rem .9rem" }}>
                          <div style={{ fontSize: ".62rem", color: "var(--accent2)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".35rem" }}>🏢 Cible Pro — reprise / marchand</div>
                          {result.strategie_pro.fourchette_low && (
                            <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "var(--accent2)", fontFamily: "var(--font-head)", marginBottom: ".3rem" }}>
                              {Number(result.strategie_pro.fourchette_low).toLocaleString("fr-FR")} – {Number(result.strategie_pro.fourchette_high).toLocaleString("fr-FR")} €
                            </div>
                          )}
                          {result.strategie_pro.conseil && <div style={{ fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.5 }}>{result.strategie_pro.conseil}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Badge VIQ.AI® certifié exportable */}
                {paid && result.make && (
                  <CertifiedBadgeSVG
                    make={result.make} model={result.model || ""}
                    year={result.year}
                    prix={result.prix_viq || Math.round(((result.argus_mid||result.argus_low)||0)*0.95/100)*100}
                    score={result.score_vente}
                  />
                )}

                {/* v27 — Inspection visuelle IA (photos déjà utilisées pour le préremplissage) */}
                {result.visual_inspection?.zones && (
                  <div style={{ marginBottom: "1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "1rem 1.1rem" }}>
                    <div style={{ fontWeight: 700, fontSize: ".85rem", marginBottom: ".5rem" }}>📷 Inspection visuelle IA</div>
                    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-start" }}>
                      <div style={{ width: 140, flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: result.visual_inspection.synthesis_svg }} />
                      <div style={{ flex: 1, minWidth: 200 }}>
                        {result.visual_inspection.synthese && <div style={{ fontSize: ".78rem", color: "var(--text)", marginBottom: ".5rem", fontStyle: "italic" }}>« {result.visual_inspection.synthese} »</div>}
                        {result.visual_inspection.vigilance_points?.length > 0 && (
                          <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: ".76rem", color: "var(--sub)", lineHeight: 1.6 }}>
                            {result.visual_inspection.vigilance_points.map((v, i) => <li key={i}>{v}</li>)}
                          </ul>
                        )}
                        {typeof result.visual_inspection.score_delta === "number" && result.visual_inspection.score_delta !== 0 && (
                          <div style={{ marginTop: ".5rem", fontSize: ".73rem", color: result.visual_inspection.score_delta < 0 ? "var(--red)" : "var(--green)" }}>
                            Cote et score ajustés selon l'état visuel réel ({result.visual_inspection.score_delta > 0 ? "+" : ""}{result.visual_inspection.score_delta} points de score)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Points forts + Conseils vente — enrichis */}
                {result.points_forts?.length > 0 && (
                  <div style={{ marginBottom: "1rem" }}>
                    <div style={{ display: "flex", gap: ".5rem", marginBottom: ".5rem", flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 200, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: ".7rem 1rem" }}>
                        <div style={{ fontSize: ".68rem", color: "var(--green)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".4rem", fontWeight: 700 }}>✦ Points forts</div>
                        {(result.points_forts||[]).slice(0,5).map((p,i) => (
                          <div key={i} style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: ".25rem" }}>✓ {p}</div>
                        ))}
                      </div>
                      {(result.conseils_vente||result.points_attention)?.length > 0 && (
                        <div style={{ flex: 1, minWidth: 200, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: ".7rem 1rem" }}>
                          <div style={{ fontSize: ".68rem", color: "var(--accent2)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".4rem", fontWeight: 700 }}>💡 Conseils vente</div>
                          {(result.conseils_vente||result.points_attention||[]).slice(0,5).map((p,i) => (
                            <div key={i} style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: ".25rem" }}>→ {p}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Options typiques du modèle */}
                    {result.options_typiques?.length > 0 && (
                      <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: ".7rem 1rem" }}>
                        <div style={{ fontSize: ".68rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".5rem" }}>⚙️ Équipements typiques de ce modèle</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: ".3rem" }}>
                          {result.options_typiques.map((o,i) => (
                            <span key={i} style={{ fontSize: ".72rem", background: "var(--deep)", border: "1px solid var(--border)", borderRadius: 10, padding: ".15rem .55rem", color: "var(--sub)" }}>{o}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Badge "Cote ajustée" — affiché après feedback utilisateur */}
                {isUnlocked && adjustedEstimation && (
                  <div style={{
                    background: "linear-gradient(135deg, rgba(168,128,58,.08), rgba(4,120,87,.05))",
                    border: "1.5px solid var(--accent)", borderRadius: 10,
                    padding: "1rem 1.2rem", marginBottom: ".9rem"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".5rem", flexWrap: "wrap", marginBottom: ".7rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: ".4rem" }}>
                        <span style={{ fontSize: "1.3rem" }}>🎯</span>
                        <span style={{ fontSize: ".88rem", fontWeight: 700, color: "var(--accent)" }}>
                          Cote ajustée avec votre feedback
                        </span>
                      </div>
                      <div style={{ fontSize: ".62rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>
                        Mise à jour : {new Date(adjustedEstimation.date).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                    </div>
                    {/* Trois prix en parallèle pour transparence */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: ".55rem", marginBottom: ".7rem" }}>
                      <div style={{ background: "var(--white)", border: "1px solid var(--border)", borderRadius: 6, padding: ".55rem .7rem", textAlign: "center" }}>
                        <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontFamily: "var(--font-mono)" }}>VIQ.AI® initial</div>
                        <div style={{ fontSize: "1rem", fontWeight: 800, color: "var(--sub)", fontFamily: "var(--font-head)", marginTop: ".15rem" }}>
                          {Number(adjustedEstimation.explanation.prix_viq_initial).toLocaleString("fr-FR")} €
                        </div>
                      </div>
                      <div style={{ background: "var(--white)", border: "1px solid var(--border)", borderRadius: 6, padding: ".55rem .7rem", textAlign: "center" }}>
                        <div style={{ fontSize: ".58rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", fontFamily: "var(--font-mono)" }}>Votre saisie</div>
                        <div style={{ fontSize: "1rem", fontWeight: 800, color: "var(--sub)", fontFamily: "var(--font-head)", marginTop: ".15rem" }}>
                          {Number(adjustedEstimation.explanation.prix_user_saisi).toLocaleString("fr-FR")} €
                        </div>
                        {adjustedEstimation.explanation.was_bounded && (
                          <div style={{ fontSize: ".55rem", color: "var(--accent2)", marginTop: ".1rem", fontStyle: "italic" }}>
                            (borné à {Number(adjustedEstimation.explanation.prix_user_borne).toLocaleString("fr-FR")} €)
                          </div>
                        )}
                      </div>
                      <div style={{ background: "linear-gradient(135deg, var(--accent) 0%, var(--accent2) 100%)", borderRadius: 6, padding: ".55rem .7rem", textAlign: "center", color: "#fff" }}>
                        <div style={{ fontSize: ".58rem", opacity: .9, textTransform: "uppercase", letterSpacing: ".5px", fontFamily: "var(--font-mono)" }}>Ajustée</div>
                        <div style={{ fontSize: "1.1rem", fontWeight: 800, fontFamily: "var(--font-head)", marginTop: ".15rem" }}>
                          {Number(adjustedEstimation.prix_ajuste).toLocaleString("fr-FR")} €
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.45 }}>
                      <strong style={{ color: "var(--text)" }}>Méthode :</strong> {adjustedEstimation.explanation.methode}.
                      {adjustedEstimation.explanation.raison_utilisateur && (
                        <> Raison invoquée : <em>« {(() => {
                          const labels = { too_low: "La Cote est trop basse", too_high: "La Cote est trop haute", missing_options: "Options non prises en compte", state: "L'état réel diffère", local_market: "Le marché local est différent", other: "Autre" };
                          return labels[adjustedEstimation.explanation.raison_utilisateur] || adjustedEstimation.explanation.raison_utilisateur;
                        })()} »</em>.</>
                      )}
                      {adjustedEstimation.explanation.was_bounded && (
                        <> ⚠ Votre prix a été <strong>borné</strong> car il dépassait ±25% de la fourchette Argus initiale, pour préserver la rigueur de la Cote.</>
                      )}
                    </div>
                  </div>
                )}
                {isUnlocked && estimOk === null && !adjustedEstimation && (
                  <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: ".9rem 1.1rem", marginBottom: ".8rem", textAlign: "center" }}>
                    <div style={{ fontSize: ".85rem", fontWeight: 600, marginBottom: ".4rem" }}>
                      Cette cote est-elle réaliste selon vous ?
                    </div>
                    <div style={{ fontSize: ".72rem", color: "var(--sub)", marginBottom: ".7rem" }}>
                      Votre feedback nous aide à affiner les cotes futures
                    </div>
                    <div style={{ display: "flex", gap: ".6rem", justifyContent: "center" }}>
                      <button className="btn btn-ghost" style={{ flex: 1, fontSize: ".85rem", borderColor: "var(--green)", color: "var(--green)" }}
                        onClick={() => setEstimOk(true)}>
                        ✅ Oui, c'est réaliste
                      </button>
                      <button className="btn btn-ghost" style={{ flex: 1, fontSize: ".85rem", borderColor: "var(--accent)", color: "var(--accent)" }}
                        onClick={() => setEstimOk(false)}>
                        ❌ Non, je ne pense pas
                      </button>
                    </div>
                  </div>
                )}
                {isUnlocked && estimOk === true && (
                  <div style={{ background: "rgba(0,200,100,.06)", border: "1px solid rgba(0,200,100,.2)", borderRadius: 8, padding: ".9rem 1.1rem", marginBottom: ".8rem" }}>
                    <div style={{ fontSize: ".82rem", fontWeight: 600, color: "var(--green)", marginBottom: ".5rem", textAlign: "center" }}>
                      ✅ Merci pour votre retour !
                    </div>
                    <div style={{ fontSize: ".78rem", color: "var(--text)", marginBottom: ".5rem" }}>
                      À quel prix comptez-vous proposer ce véhicule ?
                    </div>
                    <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                      <input type="number" placeholder="ex : 12 500" value={userTargetPrice || ""}
                        onChange={e => setUserTargetPrice(e.target.value)}
                        style={{ flex: 1, padding: ".55rem .7rem", border: "1px solid var(--border)", borderRadius: 6, fontSize: ".85rem", background: "var(--white)" }} />
                      <span style={{ fontSize: ".82rem", color: "var(--sub)" }}>€</span>
                      <button className="btn btn-accent btn-sm" style={{ fontSize: ".78rem" }}
                        onClick={() => {
                          // Stockage 100% en DB via API (le localStorage n'est plus utilisé : les feedbacks sont la donnée propriétaire — ils DOIVENT être en base)
                          const estimId = (result?.estimation_id) || `est_${Date.now()}`;
                          apiPost("/api/feedback", {
                            estimation_id: estimId,
                            vehicle_data: callbackAnswersSell || {},
                            estimation_data: result,
                            is_realistic: true,
                            user_price: userTargetPrice ? parseInt(userTargetPrice, 10) : null,
                            reason: null
                          }).catch(err => console.warn("[feedback] envoi backend échoué:", err.message));
                          alert("Merci ! Votre prix cible a été enregistré.");
                        }}>
                        Enregistrer
                      </button>
                    </div>
                    <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".4rem", textAlign: "center" }}>
                      Cette donnée nous aide à affiner les cotes marché en France
                    </div>
                  </div>
                )}
                {isUnlocked && estimOk === false && (
                  <div style={{ background: "rgba(168,128,58,.04)", border: "1px solid rgba(168,128,58,.15)", borderRadius: 8, padding: ".9rem 1.1rem", marginBottom: ".8rem" }}>
                    <div style={{ fontSize: ".82rem", fontWeight: 600, color: "var(--text)", marginBottom: ".5rem" }}>
                      Aidez-nous à comprendre
                    </div>
                    <div style={{ fontSize: ".78rem", color: "var(--text)", marginBottom: ".3rem" }}>
                      Quel prix avez-vous en tête pour ce véhicule ?
                    </div>
                    <div style={{ display: "flex", gap: ".5rem", alignItems: "center", marginBottom: ".7rem" }}>
                      <input type="number" placeholder="ex : 14 000" value={feedbackPrice || ""}
                        onChange={e => setFeedbackPrice(e.target.value)}
                        style={{ flex: 1, padding: ".55rem .7rem", border: "1px solid var(--border)", borderRadius: 6, fontSize: ".85rem", background: "var(--white)" }} />
                      <span style={{ fontSize: ".82rem", color: "var(--sub)" }}>€</span>
                    </div>
                    <div style={{ fontSize: ".78rem", color: "var(--text)", marginBottom: ".3rem" }}>
                      Raison principale ?
                    </div>
                    <select value={feedbackReason || ""}
                      onChange={e => setFeedbackReason(e.target.value)}
                      style={{ width: "100%", padding: ".55rem .7rem", border: "1px solid var(--border)", borderRadius: 6, fontSize: ".82rem", background: "var(--white)", marginBottom: ".7rem" }}>
                      <option value="">— Sélectionnez —</option>
                      <option value="too_low">La Cote est trop basse</option>
                      <option value="too_high">La Cote est trop haute</option>
                      <option value="missing_options">Des options/équipements non pris en compte</option>
                      <option value="state">L'état réel diffère</option>
                      <option value="local_market">Le marché local est différent</option>
                      <option value="other">Autre raison</option>
                    </select>
                    <div style={{ display: "flex", gap: ".5rem" }}>
                      <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: ".76rem" }}
                        disabled={feedbackSaving}
                        onClick={async () => {
                          // Validation : prix + raison requis pour calculer l'ajustement
                          if (!feedbackPrice || !feedbackReason) {
                            alert("Merci de renseigner votre prix et la raison pour que nous puissions ajuster la Cote.");
                            return;
                          }
                          setFeedbackSaving(true);
                          const estimId = (result?.estimation_id) || `est_${Date.now()}`;
                          try {
                            // Envoi à /api/feedback : le backend calcule prix_ajuste et le renvoie
                            const resp = await apiPost("/api/feedback", {
                              estimation_id: estimId,
                              vehicle_data: callbackAnswersSell || {},
                              estimation_data: result,
                              is_realistic: false,
                              user_price: parseInt(feedbackPrice, 10),
                              reason: feedbackReason
                            });
                            if (resp.prix_ajuste && resp.prix_ajuste_explanation) {
                              // Stocker pour affichage dans la zone résultat
                              setAdjustedEstimation({
                                prix_ajuste: resp.prix_ajuste,
                                explanation: resp.prix_ajuste_explanation,
                                date: new Date().toISOString()
                              });
                              // Masquer le formulaire feedback (le résultat est ajusté maintenant)
                              setEstimOk(null);
                            } else {
                              alert("Merci pour votre retour, il a été enregistré.");
                            }
                          } catch(err) {
                            console.warn("[feedback] erreur:", err.message);
                            alert("Erreur lors de l'enregistrement. Merci de réessayer.");
                          } finally {
                            setFeedbackSaving(false);
                          }
                        }}>
                        {feedbackSaving ? "Calcul en cours…" : "💾 Ajuster la Cote"}
                      </button>
                      <button className="btn btn-accent btn-sm" style={{ flex: 1, fontSize: ".76rem" }}
                        onClick={() => setShowCallbackSell(true)}>
                        📞 Être rappelé
                      </button>
                    </div>
                  </div>
                )}

                {/* Tendance marché + score */}
                {(result.market_trend || result.score_vente) && (
                  <div style={{ display: "flex", gap: ".5rem", marginBottom: "1rem" }}>
                    {result.market_trend && <div style={{ flex: 1, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: ".5rem .8rem", fontSize: ".8rem" }}>
                      Tendance : <strong style={{ color: result.market_trend === "hausse" ? "var(--green)" : result.market_trend === "baisse" ? "var(--red)" : "var(--sub)" }}>
                        {result.market_trend === "hausse" ? "↗ En hausse" : result.market_trend === "baisse" ? "↘ En baisse" : "→ Stable"}
                      </strong>
                    </div>}
                    {result.score_vente && <div style={{ flex: 1, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: ".5rem .8rem", fontSize: ".8rem", textAlign: "center" }} title="Attractivité de l'annonce sur le marché — distinct du score de fiabilité du véhicule (voir plus bas)">
                      📈 Score de vente <strong style={{ color: "var(--accent2)" }}>{result.score_vente}%</strong>
                    </div>}
                  </div>
                )}

                {/* Annonce générée */}
                {result.annonce_titre && (
                  <div style={{ background: "rgba(61,158,255,.06)", border: "1px solid rgba(61,158,255,.2)", borderRadius: 6, padding: "1rem 1.2rem", marginBottom: "1rem" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: ".5rem" }}>
                      <div style={{ fontSize: ".7rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>📝 Annonce générée</div>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize:".68rem" }}
                        onClick={() => {
                          const txt = result.annonce_titre + "\n\n" + result.annonce_texte;
                          navigator.clipboard.writeText(txt).then(() => alert("✅ Annonce copiée !")).catch(() => {});
                        }}>📋 Copier</button>
                    </div>
                    <div style={{ fontWeight: 700, marginBottom: ".5rem", fontSize:".95rem" }}>{result.annonce_titre}</div>
                    <div style={{ fontSize: ".83rem", color: "var(--sub)", lineHeight: 1.65, whiteSpace:"pre-wrap" }}>{result.annonce_texte}</div>
                    {/* Options détail si renseigné */}
                    {(result.options_detail || (callbackAnswersSell?.options_detail?.trim())) && (
                      <div style={{ marginTop:".6rem", padding:".4rem .7rem", background:"rgba(255,184,0,.06)", border:"1px solid rgba(255,184,0,.2)", borderRadius:4, fontSize:".75rem", color:"var(--accent2)" }}>
                        ⭐ Options spécifiques : {result.options_detail || callbackAnswersSell?.options_detail}
                      </div>
                    )}
                  </div>
                )}

                {/* Widget satisfaction estimation */}
                <div style={{ marginTop: ".5rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".7rem", flexWrap: "wrap", gap: ".5rem" }}>
                    <div>
                      <div style={{ fontSize: ".72rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>
                        {(result.plateformes||result.platforms||[]).length} plateformes classées par score
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                      {isUnlocked && (result.plateformes||result.platforms||[]).length > 0 && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setShowPlatformsList(v => !v)}>
                          {showPlatformsList ? "Masquer" : "Afficher"} les plateformes
                        </button>
                      )}
                      {!isUnlocked && (
                        <button className="btn btn-accent btn-sm" onClick={() => setShowGate(true)}>🔓 Débloquer la Cote</button>
                      )}
                    </div>
                  </div>

                  {(isUnlocked ? showPlatformsList : true) && (result.plateformes || result.platforms || []).map((p, i) => {
                    const isBlurred = !isUnlocked;
                    const scoreColor = p.score_vente >= 88 ? "var(--green)" : p.score_vente >= 75 ? "var(--accent2)" : "var(--sub)";
                    return (
                      <div key={i} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: ".8rem 1rem", marginBottom: ".4rem", filter: isBlurred ? "blur(4px)" : "none", userSelect: isBlurred ? "none" : "auto", pointerEvents: isBlurred ? "none" : "auto", position: "relative" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: ".4rem" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".2rem" }}>
                              {i === 0 && <span style={{ fontSize: ".65rem", background: "rgba(255,184,0,.15)", border: "1px solid rgba(255,184,0,.4)", color: "var(--accent2)", borderRadius: 10, padding: ".1rem .45rem", fontFamily: "var(--font-mono)" }}>⭐ TOP</span>}
                              <span style={{ fontWeight: 700, fontSize: ".88rem" }}>#{i+1} {p.name}</span>
                              <span style={{ fontSize: ".63rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{p.type}</span>
                            </div>
                            <div style={{ fontSize: ".72rem", color: "var(--sub)", marginBottom: ".2rem" }}>
                              💰 {p.commission} · ⏱ {p.delai_moyen||p.delay}
                            </div>
                          </div>
                          {/* Score vente */}
                          <div style={{ textAlign: "right", flexShrink: 0, marginLeft: ".8rem" }}>
                            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", color: scoreColor }}>{p.score_vente}%</div>
                            <div style={{ fontSize: ".6rem", color: "var(--muted)" }}>score vente</div>
                          </div>
                        </div>
                        {/* Profil acheteurs */}
                        {p.profil_acheteurs && <div style={{ fontSize: ".72rem", color: "var(--sub)", background: "var(--deep)", borderRadius: 4, padding: ".4rem .6rem", marginBottom: ".3rem" }}>
                          👥 {p.profil_acheteurs}
                        </div>}
                        {/* Description */}
                        {(p.description||p.conseil) && <div style={{ fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.5, marginBottom: ".4rem" }}>
                          💡 {p.description||p.conseil}
                        </div>}
                        {!isBlurred && (
                          <div style={{ paddingTop: ".4rem", borderTop: "1px solid var(--border)" }}>
                            <button className="btn btn-accent btn-sm" onClick={() => {
                              const veh = `${result?.make || callbackAnswersSell?.make || ""} ${result?.year || callbackAnswersSell?.year_sell || ""}`.trim();
                              const subject = encodeURIComponent("Publication d'annonce VehicleIQ — " + p.name);
                              const body = encodeURIComponent(
                                "Bonjour,\n\nJe souhaite être accompagné(e) pour publier mon annonce sur " + p.name + ".\n\n" +
                                "=== VÉHICULE ===\n" + (veh || "Non renseigné") + "\n" +
                                (result?.cote ? "Prix de vente recommandé : " + result.cote + "\n" : "") +
                                "\n=== COORDONNÉES ===\n" +
                                "Nom : " + (user?.name || "Non renseigné") + "\n" +
                                "Email : " + (user?.email || "Non renseigné") + "\n" +
                                "UID : " + (user?.uid || "") + "\n\n" +
                                "Plateforme cible : " + p.name + (p.url ? " (" + p.url + ")" : "") + "\n\n" +
                                "Cordialement,\nVehicleIQ"
                              );
                              window.location.href = "mailto:bonglorentz@gmail.com?cc=contact@vehicleiq.fr&subject=" + subject + "&body=" + body;
                            }}>
                              Publier sur {p.name} →
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Bouton débloquer en bas — même style que Buy */}
                  {!isUnlocked && (
                    <div style={{ marginTop:"1.2rem", textAlign:"center" }}>
                      <button className="btn btn-accent btn-lg" style={{ width:"100%", fontSize:".95rem" }} onClick={() => setShowGate(true)}>
                        🔓 Débloquer la Cote complète
                      </button>
                      <div style={{ fontSize:".7rem", color:"var(--muted)", marginTop:".4rem" }}>
                        Prix de vente recommandé · Conseil stratégique · 10 plateformes classées
                      </div>
                    </div>
                  )}
                  {/* Export PDF + Partage WhatsApp après déblocage */}
                  {paid && (
                    <div style={{ display: "flex", gap: ".5rem", marginTop: ".8rem" }}>
                      <button
                        className="btn btn-ghost"
                        style={{ flex: 1, fontSize: ".82rem", display: "flex", alignItems: "center", justifyContent: "center", gap: ".5rem" }}
                        onClick={() => chargeForAction({
                          cost: 1, label: "export_pdf_sell",
                          action: () => exportSellPDF({ ...result, reliability_score: reliabilityScoreSell, known_issues: knownIssuesSell }, {make: result.make, year: result.year, km: result.km}),
                          onSpent: () => { if (onWalletChange) onWalletChange(); },
                          onInsufficient: () => { if (onShowRecharge) onShowRecharge(); },
                        })}
                      >
                        📄 Exporter en PDF 
                      </button>
                      <button
                        type="button"
                        style={{
                          flex: 1, fontFamily: "var(--font-body)", fontSize: ".82rem", fontWeight: 600,
                          background: "#25D366", color: "#fff", border: "none", borderRadius: 6,
                          padding: ".55rem .9rem", cursor: "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center", gap: ".4rem",
                          boxShadow: "0 1px 2px rgba(37,211,102,.18), 0 4px 10px rgba(37,211,102,.22)",
                          transition: "all .15s cubic-bezier(.4,0,.2,1)"
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#1FBA59"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "#25D366"; e.currentTarget.style.transform = "translateY(0)"; }}
                        onClick={() => chargeForAction({
                          cost: 0, label: "share_whatsapp_sell",
                          action: () => {
                            const argLow = result?.argus_low;
                            const argMid = result?.argus_mid;
                            const argHigh = result?.argus_high;
                            const prix = result?.prix_viq;
                            const lines = [
                              `🚗 *Estimation VehicleIQ*`,
                              result?.make && `Véhicule : ${result.make}`,
                              result?.year && `Année : ${result.year}`,
                              result?.km && `Kilométrage : ${Number(result.km).toLocaleString("fr-FR")} km`,
                              "",
                              prix && `💰 *Prix VIQ.AI® recommandé : ${Number(prix).toLocaleString("fr-FR")} €*`,
                              argLow && argHigh && `📊 Côte Argus : ${Number(argLow).toLocaleString("fr-FR")} € → ${Number(argHigh).toLocaleString("fr-FR")} €`,
                              argMid && `📈 Marché médian : ${Number(argMid).toLocaleString("fr-FR")} €`,
                              "",
                              "Cote propulsée par VehicleIQ — vehicleiq.fr"
                            ].filter(Boolean);
                            window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener,noreferrer");
                          },
                          onSpent: () => { if (onWalletChange) onWalletChange(); },
                          onInsufficient: () => { if (onShowRecharge) onShowRecharge(); },
                        })}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                        WhatsApp
                      </button>
                    </div>
                  )}
                </div>
                {/* Disclaimer légal */}
                <div style={{ marginTop: ".8rem", padding: ".6rem .9rem", background: "rgba(100,100,120,.06)", border: "1px solid rgba(100,100,120,.15)", borderRadius: 4, fontSize: ".68rem", color: "var(--muted)", lineHeight: 1.5 }}>
                  ⚖ La Cote de prix et le classement des plateformes sont générés à titre indicatif (art. 1112-1 C. civ. · art. L.221-5 C. conso.). La cote Argus réelle peut varier selon l'état exact du véhicule et les conditions du marché local.
                </div>

              </div>
            </div>
            )}
          </div>
        </div>
      </div>
      {showGate && <WalletGate cost={1} label="Cote VIQ.AI® + 10 plateformes" onClose={() => setShowGate(false)} onUnlocked={() => { setShowGate(false); setPaid(true); if(onWalletChange) onWalletChange(); setTimeout(() => { if (localStorage.getItem("viq_callback_optout") !== "1") setShowCallbackSell(true); }, 30000); }} user={user} onShowAuth={onShowAuth} onRecharge={() => { setShowGate(false); setShowRecharge(true); }} />}
        {showRecharge && <RechargeModal onClose={() => setShowRecharge(false)} onRecharged={(qty) => {
          setShowRecharge(false);
          if (onWalletChange) onWalletChange();
          // Si l'utilisateur avait validé un questionnaire avant le blocage, lancer l'estimation
          const pendingAnswers = window.__viq_pending_sell_answers;
          if (pendingAnswers && step !== "loading" && step !== "results") {
            window.__viq_pending_sell_answers = null;
            setTimeout(() => handleComplete(pendingAnswers), 300);
          }
        }} user={user} onShowAuth={onShowAuth} />}
        {showCallbackSell && <CallbackModal onClose={() => setShowCallbackSell(false)} user={user} answers={callbackAnswersSell} type="sell" onShowRecharge={onShowRecharge} onWalletChange={onWalletChange} />}
    </>
  );
}





// ─── VIQ.AI Chat Agent ───────────────────────────────────────────────────────
const CHAT_SYSTEM_PROMPT = `Tu es VIQ.AI, l'assistant intelligent de VehicleIQ.fr — plateforme d'estimation et d'achat/vente de véhicules d'occasion en France, à destination des professionnels (garages, marchands VO, mandataires, indépendants) et des particuliers.

Tu peux aider l'utilisateur à :
1. ACHETER un véhicule — tu poses les questions du formulaire achat de façon conversationnelle (budget, motorisation, type de véhicule, marques, kilométrage max, année min, boîte, usage, vendeur, import). Une fois toutes les infos collectées, génère 5 suggestions personnalisées avec prix, fourchette marché, plateformes.
2. VENDRE un véhicule — tu poses les questions du formulaire vente (marque/modèle, année, km, motorisation, boîte, état, CT, nb propriétaires, options, urgence). Une fois collecté, donne une estimation complète avec fourchette basse/mid/haute et 5 plateformes de vente recommandées.
3. CONTRÔLER un véhicule — demande le numéro VIN ou la plaque, explique ce que l'analyse révèle (HistoVec, sinistres, vol, gage, contrôle technique).
4. IDENTIFIER un véhicule depuis une photo — analyse les images uploadées et identifie la marque, le modèle, l'année approximative, les caractéristiques.
5. ANALYSER des factures — lis les documents uploadés (factures d'entretien, CT, etc.) et donne ton avis sur l'historique.
6. SAV et guide — réponds aux questions sur les fonctionnalités de VehicleIQ, les tarifs, les modules disponibles.

═══════════════════════════════════════════════════════════════════════
TARIFICATION VEHICLEIQ (à jour mai 2026) — utilise UNIQUEMENT ces chiffres :
═══════════════════════════════════════════════════════════════════════

🆓 GRATUIT :
- Contrôle plaque / VIN : illimité (rapport HistoVec)
- 5 estimations offertes et déjà actives à la création du compte (quota à vie, non mensuel, sans activation nécessaire)
- Inscription sans carte bancaire

💎 PAY-AS-YOU-GO (estimations à l'usage) :
- Tarif unique : 4,99 € par dossier, quel que soit le volume.
- Il n'y a PLUS de grille dégressive ni de remise au volume.
- Le contrôle plaque/VIN reste gratuit et illimité.

🏆 ABONNEMENTS MENSUELS (récurrents, sans engagement) :
- ILLIMITÉ : 399 € HT/mois — estimations illimitées, résiliable à tout moment
- MULTI-AGENTS : 799 € HT/mois — 3 agents inclus sous le même SIRET, 10 estimations simultanées, 12 recommandations par requête, SLA 99,5 %, support prioritaire 24h

🆕 SERVICE INCLUS DANS LES OFFRES PRO : Lien client leads personnalisé — chaque compte Pro dispose d'un lien partageable permettant à ses clients de demander une estimation, des recommandations d'achat ou un calcul carte grise & malus ; les demandes arrivent dans l'onglet « Formulaires clients » du dashboard.
- Intégration API : à partir de 2 500 € HT (sur devis, volumes d'appels API, marque blanche)

⚠️ NE JAMAIS mentionner de packs de crédits ("Test 10 cr / 59 €", "Activité régulière 30 cr / 149 €", "Volume 100 cr / 390 €", "Pro intensif 300 cr / 990 €") — ces packs n'existent plus. Le seul modèle à l'usage est 4,99 € par dossier.
⚠️ Toujours préciser "HT" pour les prix Pro (M.K.L Ingénierie est en franchise TVA, art. 293 B du CGI).
⚠️ La concurrence vend des abonnements 250-600 €/mois en engagement annuel — VehicleIQ est sans engagement.

═══════════════════════════════════════════════════════════════════════
ROUTAGE VERS LES MODALES (très important) :
═══════════════════════════════════════════════════════════════════════

Quand l'utilisateur veut concrètement accéder à un formulaire ou une modale, insère à la fin de ta réponse une balise spéciale sur sa propre ligne. Le front parse ces balises et affiche un bouton d'action cliquable. NE PAS commenter la balise, ne pas l'expliquer — juste l'écrire.

Balises disponibles :
- [OPEN:sell] — pour ouvrir le formulaire d'estimation de vente
- [OPEN:buy] — pour ouvrir le formulaire de recherche d'achat / recommandations
- [OPEN:check] — pour ouvrir le contrôle VIN/plaque (gratuit)
- [OPEN:pricing] — pour ouvrir la modale tarification complète
- [OPEN:profile] — pour ouvrir l'espace utilisateur (profil, historique, facturation)

Exemples d'usage approprié :
- "Tu veux estimer ta Peugeot ?" → fin de message : [OPEN:sell]
- "Je peux te trouver une Audi A3 à ce budget" → fin de message : [OPEN:buy]
- "Tu peux vérifier l'historique avec le VIN, gratuitement" → fin de message : [OPEN:check]
- "Voici nos tarifs Pro complets" → fin de message : [OPEN:pricing]

N'insère JAMAIS de balise dans une phrase. Toujours sur une nouvelle ligne à la fin. Une seule balise par message maximum (la plus pertinente).

═══════════════════════════════════════════════════════════════════════
RÈGLES DE STYLE :
═══════════════════════════════════════════════════════════════════════
- Tu parles français, ton ton est professionnel mais accessible et chaleureux
- Tu utilises des emojis sobrement pour structurer tes réponses
- Quand tu génères des estimations, sois précis avec des chiffres réels basés sur le marché français 2026
- Tu mentionnes toujours "Cote VIQ.AI® — à titre indicatif" quand tu donnes des prix
- Tu n'inventes jamais de données VIN ou d'immatriculation officielles (HistoVec)
- Si une image t'est fournie, analyse-la vraiment et donne des informations détaillées
- Si l'utilisateur demande explicitement à parler à un humain, indique-lui : contact@vehicleiq.fr ou via le formulaire "Réserver une démo" du site

VehicleIQ est édité par M.K.L Ingénierie et Conseil, SIRET 904 954 542 00026, 2 Avenue de la République, 02100 Saint-Quentin. En franchise TVA (art. 293 B CGI).`;

function VIQAIChat({ user, onClose, onShowAuth }) {
  const FREE_LIMIT = 5;
  const WELCOME_MSG = "Bonjour ! Je suis **VIQ.AI**, l'assistant d'aide de VehicleIQ. 🚗\n\nJe suis là pour **répondre à vos questions** et vous **guider** sur la plateforme :\n• Comment **fonctionne** VehicleIQ\n• Les **tarifs** et le fonctionnement sans abonnement\n• Les **offres Pro** (Solo, Multi-Agent, API)\n• Où trouver l'**cote**, le **contrôle plaque/VIN** ou l'**aide à l'achat**\n\nℹ️ Pour estimer un véhicule ou obtenir des recommandations d'achat, utilisez les outils dédiés de la plateforme — je vous y oriente avec plaisir.\n\nComment puis-je vous aider ?";
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: "assistant",
      content: WELCOME_MSG,
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]); // base64 images
  const [imagePreviews, setImagePreviews] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [userMsgCount, setUserMsgCount] = useState(0); // requêtes envoyées par l'utilisateur
  const [isUnlimited, setIsUnlimited] = useState(false); // Pro Solo / Multi-Agent → pas de limite
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Les comptes Pro Solo / Pro Multi-Agent (abonnement actif) ne sont pas limités.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiGet("/api/billing/current");
        if (!cancelled && data && data.subscription) setIsUnlimited(true);
      } catch (e) { /* compte sans abonnement : limite appliquée */ }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const remainingFree = Math.max(0, FREE_LIMIT - userMsgCount);
  const limitReached = !isUnlimited && userMsgCount >= FREE_LIMIT;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // ── Gestion images ──────────────────────────────────────────────────────────
  const handleFiles = (files) => {
    Array.from(files).slice(0, 3).forEach(file => {
      if (!file.type.startsWith("image/") && file.type !== "application/pdf") return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(",")[1];
        const mediaType = file.type;
        setImages(prev => [...prev.slice(-2), { base64, mediaType, name: file.name }]);
        setImagePreviews(prev => [...prev.slice(-2), { url: e.target.result, name: file.name, type: file.type }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Envoi message ───────────────────────────────────────────────────────────
  const sendMessage = async () => {
    const text = input.trim();
    if (!text && images.length === 0) return;
    if (loading) return;

    // Limite de requêtes gratuites (sauf Pro Solo / Multi-Agent).
    if (!isUnlimited && userMsgCount >= FREE_LIMIT) {
      setMessages(prev => [...prev, {
        id: Date.now() + 2,
        role: "assistant",
        content: "Vous avez atteint la limite de **5 questions gratuites** avec l'assistant. 🙏\n\nLes comptes **Pro Solo** et **Pro Multi-Agent** bénéficient de l'assistant **sans limite**. Vous pouvez aussi retrouver toutes les réponses dans notre **FAQ** complète.\n\n[OPEN:pricing]",
        timestamp: new Date(),
      }]);
      return;
    }

    const userMsg = {
      id: Date.now(),
      role: "user",
      content: text,
      images: imagePreviews.map(p => p.url),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setUserMsgCount(c => c + 1);
    setInput("");
    const currentImages = [...images];
    const currentPreviews = [...imagePreviews];
    setImages([]);
    setImagePreviews([]);
    setLoading(true);

    try {
      // Construire l'historique pour Claude
      const history = messages.slice(-8).map(m => {
        if (m.role === "user" && m.images?.length) {
          return {
            role: "user",
            content: [
              ...m.images.map((_, i) => ({
                type: "image",
                source: { type: "base64", media_type: "image/jpeg", data: images[i]?.base64 || "" }
              })).filter(img => img.source.data),
              { type: "text", text: m.content || "Analyse cette image" }
            ]
          };
        }
        return { role: m.role, content: m.content };
      });

      // Message actuel avec images si présentes
      let userContent;
      if (currentImages.length > 0) {
        userContent = [
          ...currentImages.map(img => ({
            type: img.mediaType === "application/pdf" ? "document" : "image",
            source: { type: "base64", media_type: img.mediaType, data: img.base64 }
          })),
          { type: "text", text: text || "Analyse ce document" }
        ];
      } else {
        userContent = text;
      }

      const authH = await getAuthHeaders();
      const resp = await fetch(API_URL + "/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authH },
        body: JSON.stringify({
          messages: [...history, { role: "user", content: userContent }],
          userName: user?.name || user?.email || null,
        })
      });

      const data = await resp.json();
      const reply = data.reply || data.content?.[0]?.text || "Je n'ai pas pu traiter votre demande. Veuillez réessayer.";

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: "assistant",
        content: reply,
        timestamp: new Date(),
      }]);
    } catch(err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: "assistant",
        content: "❌ Une erreur s'est produite. Vérifiez votre connexion et réessayez.",
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // ── Rendu message avec markdown simple ─────────────────────────────────────
  // ── Mapping des balises [OPEN:xxx] vers les actions modales ──────────────
  // Le chatbot insère ces balises en fin de réponse pour proposer une action
  // directe à l'utilisateur. Le front les remplace par des boutons cliquables.
  const ACTION_TARGETS = {
    check:   { label: "🔍 Contrôler une plaque / un VIN",     panel: "check" },
    pricing: { label: "💎 Voir tous les tarifs",               event: "viq-open-pricing" },
    profile: { label: "👤 Mon espace VehicleIQ",               event: "viq-open-profile" },
  };

  const handleAction = (key) => {
    const target = ACTION_TARGETS[key];
    if (!target) return;
    onClose && onClose();
    setTimeout(() => {
      if (target.panel) {
        window.dispatchEvent(new CustomEvent("viq-open-panel", { detail: target.panel }));
      } else if (target.event) {
        window.dispatchEvent(new CustomEvent(target.event));
      }
    }, 80);
  };

  const renderContent = (text) => {
    if (!text) return null;
    // Extraire et retirer les balises [OPEN:xxx] du texte (pour rendre des boutons)
    const actionKeys = [];
    const ACTION_RE = /\[OPEN:([a-z_]+)\]/gi;
    let cleanText = text.replace(ACTION_RE, (_, k) => {
      const key = String(k).toLowerCase();
      if (ACTION_TARGETS[key] && !actionKeys.includes(key)) actionKeys.push(key);
      return ""; // retirer la balise du texte affiché
    }).replace(/\n{3,}/g, "\n\n").trimEnd();

    // Découpage du texte nettoyé pour gras / sauts de ligne / puces
    const parts = cleanText.split(/(\*\*.*?\*\*|\n|•)/g);
    const textNodes = parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i} style={{ color: "var(--text)" }}>{part.slice(2,-2)}</strong>;
      }
      if (part === "\n") return <br key={i} />;
      if (part === "•") return <span key={i} style={{ color: "var(--accent2)" }}>•</span>;
      return <span key={i}>{part}</span>;
    });

    // Si une action a été détectée, ajouter un bouton à la fin
    return (
      <>
        {textNodes}
        {actionKeys.map((key) => {
          const target = ACTION_TARGETS[key];
          return (
            <button
              key={`action-${key}`}
              onClick={() => handleAction(key)}
              style={{
                display: "block",
                marginTop: ".7rem",
                background: "linear-gradient(135deg, var(--accent), var(--accent2))",
                border: "none", color: "#fff",
                padding: ".5rem .9rem",
                borderRadius: 8, fontSize: ".75rem", fontWeight: 600,
                cursor: "pointer", fontFamily: "var(--font-body)",
                boxShadow: "0 4px 10px rgba(168,128,58,.25)",
                transition: "transform .15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; }}>
              {target.label} →
            </button>
          );
        })}
      </>
    );
  };

  // ── Suggestions rapides ─────────────────────────────────────────────────────
  const QUICK = [
    { label: "📋 Comment ça marche ?", msg: "Comment fonctionne VehicleIQ ?" },
    { label: "💳 Tarifs et fonctionnement", msg: "Quels sont les tarifs et comment fonctionne le paiement à l'usage ?" },
    { label: "🏢 Les offres Pro", msg: "Quelles sont les offres Pro Solo, Multi-Agent et API ?" },
    { label: "🔍 Contrôle plaque / VIN", msg: "Comment fonctionne le contrôle plaque / VIN gratuit ?" },
  ];

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: "min(520px, 95vw)",
        height: "min(700px, 90vh)",
        background: "var(--deep)",
        border: "1px solid rgba(168,128,58,.25)",
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,.6), 0 0 0 1px rgba(168,128,58,.1)",
        position: "relative",
      }}>

        {/* Header */}
        <div style={{
          padding: "1rem 1.2rem",
          borderBottom: "1px solid var(--border)",
          background: "linear-gradient(135deg, rgba(168,128,58,.08), transparent)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".8rem" }}>
            {/* Logo animé */}
            <div style={{
              width: 38, height: 38,
              background: "linear-gradient(135deg, var(--accent), var(--accent2))",
              borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 0 16px rgba(168,128,58,.4)",
              flexShrink: 0,
            }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="4" fill="rgba(255,255,255,.9)"/>
                  <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="rgba(255,255,255,.9)" strokeWidth="2" strokeLinecap="round"/>
                </svg></div>
            <div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.1rem", display: "flex", alignItems: "center", gap: ".5rem" }}>
                VIQ.AI
                <span style={{ fontSize: ".6rem", background: "rgba(0,200,100,.15)", border: "1px solid rgba(0,200,100,.3)", color: "var(--green)", borderRadius: 8, padding: ".1rem .4rem", fontFamily: "var(--font-mono)" }}>EN LIGNE</span>
              </div>
              <div style={{ fontSize: ".72rem", color: "var(--sub)" }}>Assistant FAQ & guidage</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: ".6rem" }}>
            {!isUnlimited && (
              <span title={`Questions gratuites restantes : ${remainingFree}/${FREE_LIMIT}`}
                style={{ fontSize: ".62rem", fontFamily: "var(--font-mono)", color: remainingFree > 0 ? "var(--sub)" : "var(--red)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 50, padding: ".15rem .55rem", whiteSpace: "nowrap" }}>
                {remainingFree}/{FREE_LIMIT} gratuites
              </span>
            )}
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1rem", display: "flex", flexDirection: "column", gap: ".8rem" }}>

          {messages.map(msg => (
            <div key={msg.id} style={{
              display: "flex",
              flexDirection: msg.role === "user" ? "row-reverse" : "row",
              alignItems: "flex-start",
              gap: ".5rem",
            }}>
              {/* Avatar */}
              {msg.role === "assistant" && (
                <div style={{ width: 28, height: 28, background: "linear-gradient(135deg, var(--accent), var(--accent2))", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" fill="rgba(255,255,255,.9)"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="rgba(255,255,255,.9)" strokeWidth="2" strokeLinecap="round"/></svg></div>
              )}

              <div style={{ maxWidth: "80%", display: "flex", flexDirection: "column", gap: ".3rem", alignItems: msg.role === "user" ? "flex-end" : "flex-start" }}>
                {/* Images uploadées */}
                {msg.images?.map((img, i) => (
                  <img key={i} src={img} alt="upload" style={{ maxWidth: 180, maxHeight: 120, borderRadius: 8, objectFit: "cover", border: "1px solid var(--border)" }} />
                ))}
                {/* Bulle */}
                <div style={{
                  background: msg.role === "user"
                    ? "linear-gradient(135deg, var(--accent), #cc3300)"
                    : "var(--card)",
                  border: msg.role === "user" ? "none" : "1px solid var(--border)",
                  borderRadius: msg.role === "user" ? "12px 12px 4px 12px" : "4px 12px 12px 12px",
                  padding: ".65rem .9rem",
                  fontSize: ".84rem",
                  lineHeight: 1.55,
                  color: msg.role === "user" ? "#fff" : "var(--text)",
                  boxShadow: msg.role === "user" ? "0 4px 12px rgba(168,128,58,.25)" : "none",
                }}>
                  {renderContent(msg.content)}
                </div>
                <div style={{ fontSize: ".6rem", color: "var(--muted)" }}>
                  {msg.timestamp.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
              <div style={{ width: 28, height: 28, background: "linear-gradient(135deg, var(--accent), var(--accent2))", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" fill="rgba(255,255,255,.9)"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="rgba(255,255,255,.9)" strokeWidth="2" strokeLinecap="round"/></svg></div>
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "4px 12px 12px 12px", padding: ".65rem .9rem", display: "flex", gap: ".3rem", alignItems: "center" }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: "var(--accent)",
                    animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                    opacity: .7,
                  }} />
                ))}
              </div>
            </div>
          )}

          {/* Suggestions rapides (seulement sur le premier message) */}
          {messages.length === 1 && !loading && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginTop: ".3rem" }}>
              {QUICK.map((q, i) => (
                <button key={i} onClick={() => { setInput(q.msg); setTimeout(() => inputRef.current?.focus(), 50); }}
                  style={{
                    background: "var(--card)", border: "1px solid var(--border)",
                    borderRadius: 20, padding: ".35rem .8rem",
                    fontSize: ".75rem", color: "var(--sub)", cursor: "pointer",
                    fontFamily: "var(--font-body)",
                    transition: "all .2s",
                  }}
                  onMouseEnter={e => { e.target.style.borderColor = "var(--accent)"; e.target.style.color = "var(--accent)"; }}
                  onMouseLeave={e => { e.target.style.borderColor = "var(--border)"; e.target.style.color = "var(--sub)"; }}
                >{q.label}</button>
              ))}
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Previews images */}
        {imagePreviews.length > 0 && (
          <div style={{ padding: ".5rem 1rem", borderTop: "1px solid var(--border)", display: "flex", gap: ".5rem", flexWrap: "wrap", background: "var(--deep)", flexShrink: 0 }}>
            {imagePreviews.map((p, i) => (
              <div key={i} style={{ position: "relative" }}>
                {p.type?.startsWith("image") ? (
                  <img src={p.url} alt={p.name} style={{ height: 52, width: 52, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border)" }} />
                ) : (
                  <div style={{ height: 52, width: 52, background: "var(--card)", borderRadius: 6, border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem" }}>📄</div>
                )}
                <button onClick={() => removeImage(i)} style={{
                  position: "absolute", top: -6, right: -6,
                  width: 18, height: 18, borderRadius: "50%",
                  background: "var(--accent)", border: "none",
                  color: "#fff", fontSize: ".65rem", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Input zone */}
        <div style={{
          padding: ".75rem 1rem",
          borderTop: "1px solid var(--border)",
          background: "var(--deep)",
          flexShrink: 0,
        }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        >
          {dragOver && (
            <div style={{ position: "absolute", inset: 0, background: "rgba(168,128,58,.08)", border: "2px dashed var(--accent)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, fontSize: ".9rem", color: "var(--accent)" }}>
              Déposez vos images ici
            </div>
          )}
          <div style={{ display: "flex", alignItems: "flex-end", gap: ".5rem" }}>
            {/* Upload image */}
            <button onClick={() => fileInputRef.current?.click()} title="Ajouter une image ou facture"
              style={{ width: 36, height: 36, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem", flexShrink: 0, color: "var(--sub)", transition: "all .2s" }}
              onMouseEnter={e => { e.target.style.borderColor = "var(--accent)"; e.target.style.color = "var(--accent)"; }}
              onMouseLeave={e => { e.target.style.borderColor = "var(--border)"; e.target.style.color = "var(--sub)"; }}
            >📎</button>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple style={{ display: "none" }} onChange={e => handleFiles(e.target.files)} />

            {/* Textarea */}
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Posez votre question sur VehicleIQ…"
              rows={1}
              style={{
                flex: 1,
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: ".5rem .8rem",
                color: "var(--text)",
                fontFamily: "var(--font-body)",
                fontSize: ".84rem",
                resize: "none",
                outline: "none",
                lineHeight: 1.5,
                maxHeight: 100,
                overflowY: "auto",
                transition: "border-color .2s",
              }}
              onFocus={e => e.target.style.borderColor = "var(--accent)"}
              onBlur={e => e.target.style.borderColor = "var(--border)"}
            />

            {/* Send */}
            <button onClick={sendMessage} disabled={loading || (!input.trim() && images.length === 0)}
              style={{
                width: 36, height: 36,
                background: input.trim() || images.length > 0 ? "linear-gradient(135deg, var(--accent), #cc3300)" : "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 8, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: ".9rem", flexShrink: 0,
                opacity: loading ? .5 : 1,
                transition: "all .2s",
                boxShadow: input.trim() || images.length > 0 ? "0 4px 12px rgba(168,128,58,.3)" : "none",
              }}
            >➤</button>
          </div>
          <div style={{ fontSize: ".62rem", color: "var(--muted)", marginTop: ".4rem", textAlign: "center" }}>
            ✦ VIQ.AI® · Entrée pour envoyer · Maj+Entrée pour saut de ligne · Glissez des images
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Affiliate Dashboard ─────────────────────────────────────────────────────
function AffiliateDashboard({ user, affiliateData, onClose }) {
  const [stats, setStats] = useState(affiliateData ? { ...affiliateData, sales_count: 0, ca_total_cents: 0, total_commission: 0 } : null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  // Blog management
  const [blogDrafts, setBlogDrafts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("viq_blog_drafts") || "[]"); } catch(e) { return []; }
  });
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title:"", cat:"Marché", emoji:"📊", excerpt:"", body:"" });
  const [payouts, setPayouts] = useState(null); // { months, pending_cents, paid_cents }
  const [rib, setRib] = useState({ titulaire: "", iban: "", bic: "" });
  const [ribSaving, setRibSaving] = useState(false);
  const [ribSaved, setRibSaved] = useState(false);
  const [ribErr, setRibErr] = useState("");
  const saveRib = async () => {
    setRibSaving(true); setRibErr(""); setRibSaved(false);
    try {
      const r = await apiPost("/api/affiliate/rib", rib);
      if (r && r.success) { setRib(r.rib); setRibSaved(true); setTimeout(() => setRibSaved(false), 3000); }
      else setRibErr((r && r.error) || "Enregistrement impossible.");
    } catch (e) { setRibErr((e && e.message) || "Erreur."); }
    finally { setRibSaving(false); }
  };
  useEffect(() => {
    if (!user?.uid) return;
    apiGet("/api/affiliate/payouts").then(r => { if (r?.success) setPayouts(r); }).catch(() => {});
  }, [user?.uid]);
  // Testimonials management
  const [testiDrafts, setTestiDrafts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("viq_testimonials") || "[]"); } catch(e) { return []; }
  });
  const [testiForm, setTestiForm] = useState({ name:"", role:"", badge:"", quote:"", stars:5, bg:"var(--accent)", photo:"" });

  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    apiGet("/api/affiliate/me")
      .then(res => {
        if (res.success && res.stats) {
          setStats(prev => ({ ...(prev||{}), ...res.stats }));
          setClients(res.clients || []);
          if (res.stats.rib) setRib({ titulaire: res.stats.rib.titulaire || "", iban: res.stats.rib.iban || "", bic: res.stats.rib.bic || "" });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.uid]);


  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel panel-wide panel-fullscreen" style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div className="panel-header" style={{ flexShrink: 0, borderBottom: "2px solid var(--green)", background: "linear-gradient(135deg, rgba(0,200,100,.06), transparent)" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: ".8rem" }}>
              <div className="panel-title">🤝 Mon espace apporteur</div>
              {stats?.code && <span style={{ fontSize: ".65rem", background: "rgba(0,200,100,.12)", border: "1px solid rgba(0,200,100,.3)", color: "var(--green)", borderRadius: 10, padding: ".15rem .6rem", fontFamily: "var(--font-mono)", letterSpacing: "2px" }}>{stats.code}</span>}
            </div>
            <div className="panel-sub">{user?.email} — Espace apporteur d'affaires</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
            <ThemeToggleButton size={32} />
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Tabs — style harmonisé avec scroll horizontal */}
        <div style={{
          display: "flex", borderBottom: "1.5px solid var(--border)",
          flexShrink: 0, background: "var(--bg)",
          padding: "0 1rem", gap: ".2rem",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
        }}>
          {[["overview","📊 Vue globale"],["onboarding","🎓 Onboarding"],["clients","👥 Mes clients"],["payouts","💸 Mes versements"]].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: ".75rem 1.1rem",
              background: tab===t ? "rgba(15,118,110,.08)" : "transparent",
              border: "none", cursor: "pointer",
              fontSize: ".84rem", fontWeight: tab===t ? 600 : 500,
              color: tab===t ? "var(--green)" : "var(--sub)",
              borderBottom: tab===t ? "2.5px solid var(--green)" : "2.5px solid transparent",
              marginBottom: -1.5, fontFamily: "var(--font-body)",
              whiteSpace: "nowrap",
              transition: "all .15s cubic-bezier(.4,0,.2,1)",
              borderRadius: "8px 8px 0 0"
            }}>{l}</button>
          ))}
        </div>

        <div className="panel-body" style={{ overflowY: "auto", flex: 1 }}>
          {loading && <div style={{ textAlign: "center", padding: "3rem" }}><div className="spinner" style={{ margin: "0 auto 1rem" }} />Chargement…</div>}

          {/* Pas encore apporteur */}
          {!loading && !stats && (
            <div style={{ textAlign: "center", padding: "3rem 2rem" }}>
              <div style={{ fontSize: "3.5rem", marginBottom: "1.2rem" }}>🤝</div>
              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.6rem", marginBottom: ".8rem" }}>Espace apporteur d'affaires</div>
              <div style={{ color: "var(--sub)", fontSize: ".88rem", lineHeight: 1.7, marginBottom: "1.8rem", maxWidth: 460, margin: "0 auto 1.8rem" }}>
                Votre espace n'est pas encore activé.<br/>Les comptes apporteurs sont créés <strong style={{ color: "var(--text)" }}>manuellement par l'équipe VehicleIQ</strong> sous 24h après réception de votre demande.
              </div>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("viq-open-contact", { detail: { mailto: "mailto:contact@vehicleiq.fr?subject=" + encodeURIComponent("Demande espace Apporteur d'Affaires VehicleIQ") + "&body=" + encodeURIComponent("Bonjour,\n\nJe souhaite rejoindre le réseau d'apporteurs d'affaires VehicleIQ.\n\nMon nom : " + (user?.name||"") + "\nMon email : " + (user?.email||"") + "\nMon UID Firebase : " + (user?.uid||"") + "\n\nCordialement.") } }))}
                className="btn btn-accent"
                style={{ fontSize: ".9rem", padding: ".75rem 1.8rem", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: ".5rem", marginBottom: "1.5rem" }}
              >
                ✉ Envoyer ma demande à VehicleIQ
              </button>
              <div style={{ marginTop: "1.2rem", fontSize: ".75rem", color: "var(--muted)", lineHeight: 1.6 }}>
                💎 Commissions : <strong style={{ color: "var(--green)" }}>30 %</strong> sur la première inscription de votre filleul · <strong style={{ color: "var(--accent)" }}>20 %</strong> sur chaque renouvellement mensuel · <strong style={{ color: "var(--accent2)" }}>+5 %</strong> de bonus dès 25 clients signés/mois — pour les 3 offres payantes (Starter à l'usage, Pro Solo, Pro Multi-Agent), sans plafond
              </div>
            </div>
          )}

          {/* Vue globale */}
          {!loading && stats && tab === "overview" && (
            <div>
              {/* KPIs */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: ".8rem", marginBottom: "1.5rem" }}>
                {[
                  { label: "Mon code", value: stats.code, icon: "🔑", color: "var(--accent)", mono: true },
                  { label: "Ventes apportées", value: stats.sales_count || 0, icon: "🛒", color: "var(--green)" },
                  { label: "CA apporté", value: ((stats.ca_total_cents||0)/100).toFixed(2)+" €", icon: "💰", color: "var(--accent2)" },
                  { label: "Mes commissions", value: ((stats.total_commission||0)/100).toFixed(2)+" €", icon: "💎", color: "var(--green)" },
                ].map((c,i) => (
                  <div key={i} style={{
                    background: "var(--white)",
                    border: "1.5px solid var(--border)",
                    borderRadius: 12,
                    padding: "1rem 1.2rem",
                    borderLeft: "3px solid "+c.color,
                    transition: "border-color .15s ease"
                  }}>
                    <div style={{ fontSize: "1.2rem", marginBottom: ".3rem" }}>{c.icon}</div>
                    <div style={{ fontFamily: c.mono?"var(--font-mono)":"var(--font-head)", fontSize: c.mono?"1.4rem":"1.8rem", fontWeight: 800, color: c.color, letterSpacing: c.mono?"3px":"-.02em", lineHeight: 1.1 }}>{c.value}</div>
                    <div style={{ fontSize: ".7rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.3px", marginTop: ".25rem", fontFamily: "var(--font-mono)", fontWeight: 600 }}>{c.label}</div>
                  </div>
                ))}
              </div>

              {/* Comment ça marche */}
              <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: "1.2rem", marginBottom: "1rem" }}>
                <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: "1rem" }}>Comment ça fonctionne</div>
                {[
                  ["1. Partagez votre code", `Transmettez votre code ${stats.code} à vos prospects — ils le saisissent lors de leur premier achat de recharges (ou vous l'attribuez via le CRM)`],
                  ["2. Vente automatiquement attribuée", "Chaque achat avec votre code est enregistré dans votre portefeuille avec calcul du % applicable"],
                  ["3. Commission versée à l'encaissement", "30 % sur la première inscription, puis 20 % à chaque renouvellement mensuel (+5 % de bonus dès 25 clients signés/mois) — crédité une fois le paiement client effectivement reçu"],
                ].map(([t,d],i)=>(
                  <div key={i} style={{ display: "flex", gap: "1rem", padding: ".7rem 0", borderBottom: i<2?"1px solid var(--border)":"none" }}>
                    <div style={{ fontFamily: "var(--font-head)", fontSize: "1.2rem", color: "var(--green)", flexShrink: 0 }}>{i+1}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: ".85rem", marginBottom: ".2rem" }}>{t}</div>
                      <div style={{ fontSize: ".78rem", color: "var(--sub)" }}>{d}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Barème de commissions détaillé */}
              <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: "1.2rem", marginBottom: "1rem" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: ".5rem" }}>
                  <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>💎 Barème de commissions</div>
                  <div style={{ fontSize: ".66rem", color: "var(--muted)", fontStyle: "italic" }}>Versement à l'encaissement client · sans plafond</div>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".78rem" }}>
                  <thead>
                    <tr style={{ background: "var(--deep)" }}>
                      {["Offre payante","1ʳᵉ inscription","Renouvellement mensuel","Exemple sur 12 mois"].map(h => (
                        <th key={h} style={{ padding: ".55rem .7rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".3px" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Offre 1 — Pro (à l'usage) */}
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: ".55rem .7rem", fontWeight: 600 }}>Pro — à l'usage (4,99 €/dossier)</td>
                      <td style={{ padding: ".55rem .7rem", color: "var(--green)", fontWeight: 800, fontSize: ".95rem", fontFamily: "var(--font-head)" }}>30 %</td>
                      <td style={{ padding: ".55rem .7rem", color: "var(--accent)", fontWeight: 800, fontSize: ".95rem", fontFamily: "var(--font-head)" }}>20 %</td>
                      <td style={{ padding: ".55rem .7rem", color: "var(--text)", fontWeight: 600 }}>≈ 40,50 € le 1ᵉʳ mois · puis ≈ 27 €/mois</td>
                    </tr>
                    {/* Offre 2 — Abonnement Illimité */}
                    <tr style={{ background: "var(--card)", borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: ".55rem .7rem", fontWeight: 600 }}>Abonnement Pro Solo (399 €/mois)</td>
                      <td style={{ padding: ".55rem .7rem", color: "var(--green)", fontWeight: 800, fontSize: ".95rem", fontFamily: "var(--font-head)" }}>30 %</td>
                      <td style={{ padding: ".55rem .7rem", color: "var(--accent)", fontWeight: 800, fontSize: ".95rem", fontFamily: "var(--font-head)" }}>20 %</td>
                      <td style={{ padding: ".55rem .7rem", color: "var(--text)", fontWeight: 600 }}>119,70 € puis 79,80 €/mois · ≈ 998 €</td>
                    </tr>
                    {/* Offre 3 — Abonnement Multi-Agents */}
                    <tr>
                      <td style={{ padding: ".55rem .7rem", fontWeight: 600, color: "var(--accent2)" }}>Abonnement Pro Multi-Agent (799 €/mois)</td>
                      <td style={{ padding: ".55rem .7rem", color: "var(--green)", fontWeight: 800, fontSize: ".95rem", fontFamily: "var(--font-head)" }}>30 %</td>
                      <td style={{ padding: ".55rem .7rem", color: "var(--accent)", fontWeight: 800, fontSize: ".95rem", fontFamily: "var(--font-head)" }}>20 %</td>
                      <td style={{ padding: ".55rem .7rem", color: "var(--text)", fontWeight: 600 }}>239,70 € puis 159,80 €/mois · ≈ 1 998 €</td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ marginTop: ".8rem", padding: ".7rem .9rem", background: "rgba(168,128,58,.06)", border: "1px solid rgba(168,128,58,.18)", borderRadius: 6, fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.55 }}>
                  <strong style={{ color: "var(--accent)" }}>💡 À retenir :</strong> un barème simple et unique pour les <strong>3 offres payantes</strong> — vous touchez <strong>30 % sur la première inscription</strong> de votre filleul, puis <strong>20 % à chaque renouvellement mensuel</strong> tant qu'il reste client, et <strong style={{ color: "var(--accent2)" }}>+5 % de bonus sur l'ensemble de vos commissions du mois dès 25 clients signés dans le mois</strong>. Les abonnements (Pro Solo 399 €/mois et Pro Multi-Agent 799 €/mois) génèrent donc un revenu récurrent mois après mois, tandis que l'offre Starter à l'usage vous rapporte sur chaque mois facturé à votre filleul.
                </div>
                {/* v31 — Pro Solo et Pro Multi-Agent sont désormais aussi disponibles en
                    facturation annuelle (2 999 €/an et 4 999 €/an) — le calcul de commission
                    reste inchangé : même pourcentage, même logique (30 % à la souscription,
                    20 % au renouvellement), simplement appliqué au montant réellement facturé. */}
                <div style={{ marginTop: ".6rem", padding: ".7rem .9rem", background: "rgba(46,111,92,.06)", border: "1px solid rgba(46,111,92,.18)", borderRadius: 6, fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.55 }}>
                  <strong style={{ color: "var(--green)" }}>📅 Offres annuelles :</strong> vos filleuls peuvent aussi souscrire Pro Solo ou Pro Multi-Agent en facturation <strong>annuelle</strong> (2 999 €/an ou 4 999 €/an) plutôt que mensuelle. Le calcul de votre commission ne change pas : même pourcentage, même logique (30 % à la souscription, 20 % au renouvellement) — simplement appliqué au montant réellement facturé par Stripe. Un filleul en Pro Solo annuel vous rapporte donc <strong>899,70 €</strong> à la souscription (30 % de 2 999 €) et <strong>599,80 €</strong> à chaque renouvellement annuel (20 %), au lieu de commissions mensuelles plus petites mais plus fréquentes.
                </div>
              </div>

              {/* Code partageable */}
              <div style={{ background: "rgba(0,200,100,.06)", border: "1px solid rgba(0,200,100,.2)", borderRadius: 8, padding: "1rem 1.2rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: ".72rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem" }}>Votre code parrainage</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: "2rem", fontWeight: 700, color: "var(--green)", letterSpacing: "6px" }}>{stats.code}</div>
                </div>
                <button className="btn btn-ghost" onClick={() => { navigator.clipboard.writeText(stats.code); alert("Code copié !"); }}>
                  📋 Copier
                </button>
              </div>
            </div>
          )}

          {/* Mes clients */}
          {/* Onboarding — kit complet de l'apporteur */}
          {!loading && stats && tab === "onboarding" && (() => {
            const Section = ({ title, children, icon }) => (
              <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.1rem 1.3rem", marginBottom: "1rem" }}>
                <div style={{ fontSize: ".82rem", fontWeight: 800, color: "var(--text)", marginBottom: ".7rem", display: "flex", alignItems: "center", gap: ".5rem" }}>{icon && <span>{icon}</span>}{title}</div>
                {children}
              </div>
            );
            const Li = ({ children }) => <li style={{ fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: ".25rem" }}>{children}</li>;
            const offers = [
              ["Essai gratuit", "0 €", "5 estimations offertes et déjà actives à la création du compte, sans CB. La porte d'entrée pour faire essayer."],
              ["Starter (à l'usage)", "4,99 € / dossier", "Tarif unique, sans abonnement ni engagement. L'argument massue : on paie ce qu'on utilise."],
              ["Pro Solo", "399 € / mois", "Estimations illimitées. Rentable dès 82 estim./mois. Mandats, CERFA, lien leads, photos inclus."],
              ["Pro Multi-Agent", "799 € HT / mois", "3 agents, 10 estim. simultanées, SLA 99,5 %. Équipes multi-collaborateurs."],
            ];
            const objections = [
              ["« J'ai déjà l'Argus / un outil. »", "« Vous payez le vôtre tous les mois, même sans l'utiliser. Nous, c'est à l'usage, et vos premières estimations sont offertes. On compare sur un véhicule ? »"],
              ["« C'est trop cher. »", "« À 4,99 € le dossier, une seule reprise mieux négociée la rentabilise des dizaines de fois. Et vous commencez gratuitement. »"],
              ["« Je n'ai pas le temps. »", "« C'est exactement pour ça que VehicleIQ existe : de 30 minutes à moins de 5 minutes par véhicule — voire quelques photos suffisent. »"],
              ["« Je vais réfléchir. »", "« Créons votre compte gratuit maintenant : vous testez sur vos vrais véhicules cette semaine, et on se rappelle vendredi. »"],
              ["« Est-ce fiable ? »", "« On croise des données marché en temps réel avec notre moteur. Vous gardez la décision finale — c'est une aide à la décision, pas un couperet. »"],
            ];
            return (
              <div>
                {/* Bienvenue + code */}
                <div style={{ background: "linear-gradient(135deg, rgba(0,200,100,.06), transparent)", border: "1.5px solid rgba(0,200,100,.25)", borderRadius: 12, padding: "1.1rem 1.3rem", marginBottom: "1rem" }}>
                  <div style={{ fontSize: ".95rem", fontWeight: 800, color: "var(--text)", marginBottom: ".3rem" }}>🎓 Bienvenue dans l'équipe commerciale VehicleIQ</div>
                  <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.6 }}>
                    Votre mission : faire signer les garages, marchands et mandataires VO de votre secteur. Vous prospectez, vous démontrez, vous concluez jusqu'à la première souscription payante. Le support et l'administratif produit, c'est nous.
                  </div>
                  <div style={{ marginTop: ".7rem", display: "flex", alignItems: "center", gap: ".6rem", flexWrap: "wrap" }}>
                    <span style={{ fontSize: ".72rem", color: "var(--sub)" }}>Votre code apporteur (à communiquer à chaque client) :</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontWeight: 800, letterSpacing: "3px", color: "var(--green)", background: "rgba(0,200,100,.1)", border: "1px solid rgba(0,200,100,.3)", borderRadius: 8, padding: ".2rem .7rem" }}>{stats.code}</span>
                    <span style={{ fontSize: ".68rem", color: "var(--muted)" }}>généré automatiquement — c'est lui qui garantit vos commissions</span>
                  </div>
                </div>

                {/* ATOUT N°1 : le formulaire lead */}
                <div style={{ background: "rgba(168,128,58,.06)", border: "2px solid rgba(168,128,58,.3)", borderRadius: 12, padding: "1.1rem 1.3rem", marginBottom: "1rem" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", fontSize: ".62rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "1px", color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 20, padding: ".2rem .7rem", marginBottom: ".6rem" }}>⭐ Votre atout n°1 à mettre en avant</div>
                  <div style={{ fontSize: ".9rem", fontWeight: 800, color: "var(--text)", marginBottom: ".4rem" }}>Le formulaire lead d'entrée — l'argument qui fait signer</div>
                  <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.65 }}>
                    Chaque compte pro dispose d'un <strong style={{ color: "var(--text)" }}>formulaire lead personnalisé</strong> (un simple lien à partager) qui lui permet de <strong style={{ color: "var(--text)" }}>collecter en continu des véhicules à estimer et des besoins de conseil</strong>. Toutes les entrées (avec photos et import HistoVec) remontent automatiquement dans son tableau de bord, prêtes à être lancées en un clic.
                  </div>
                  <ul style={{ margin: ".6rem 0 0", paddingLeft: "1.1rem" }}>
                    <Li><strong style={{ color: "var(--text)" }}>Collecte à froid sans connexion :</strong> le pro — ou ses propres apporteurs — peut récolter des véhicules sans avoir à se connecter ni à partager d'identifiants. Le lien suffit.</Li>
                    <Li><strong style={{ color: "var(--text)" }}>Tout centralisé :</strong> chaque demande client (estimation, achat, carte grise) arrive horodatée dans le dashboard, avec le bouton « analyser la fiabilité ».</Li>
                    <Li><strong style={{ color: "var(--text)" }}>L'angle de vente :</strong> « Vous partagez UN lien — sur votre vitrine, vos réseaux, vos cartes — et les véhicules à estimer tombent tout seuls dans votre tableau de bord. »</Li>
                  </ul>
                  <div style={{ marginTop: ".6rem", fontSize: ".7rem", color: "var(--muted)", fontStyle: "italic" }}>
                    Le lien client a la forme <span style={{ fontFamily: "var(--font-mono)" }}>vehicleiq.fr/?lead=…</span> — chaque pro récupère le sien dans son espace ; un QR code est généré pour l'affichage en atelier.
                  </div>
                </div>

                {/* Le produit en bref */}
                <Section title="Le produit en 10 minutes" icon="🚗">
                  <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                    <Li><strong style={{ color: "var(--text)" }}>Estimation VIQ.AI® :</strong> cote instantanée, deux cibles de prix (particulier / reprise pro), fourchette et conseil de prix.</Li>
                    <Li><strong style={{ color: "var(--text)" }}>Pré-remplissage par photos :</strong> jusqu'à 5 photos et le formulaire se remplit seul (effet « waouh » en RDV).</Li>
                    <Li><strong style={{ color: "var(--text)" }}>Contrôle VIN / plaque :</strong> identification + fiche détaillée en quelques secondes — gratuit et illimité.</Li>
                    <Li><strong style={{ color: "var(--text)" }}>Comparatif multi-plateformes, carte grise &amp; malus, documents de vente</strong> (mandat, CERFA), suivi de vente &amp; marges.</Li>
                  </ul>
                </Section>

                {/* Offres */}
                <Section title="Les offres (à connaître par cœur)" icon="💼">
                  <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                    {offers.map(([nom, prix, arg], i) => (
                      <div key={i} style={{ display: "flex", gap: ".8rem", alignItems: "baseline", flexWrap: "wrap", padding: ".5rem .7rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}>
                        <span style={{ fontWeight: 800, fontSize: ".82rem", color: "var(--text)", minWidth: 130 }}>{nom}</span>
                        <span style={{ fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-head)", minWidth: 110 }}>{prix}</span>
                        <span style={{ fontSize: ".76rem", color: "var(--sub)", flex: 1, lineHeight: 1.5 }}>{arg}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: ".6rem", fontSize: ".76rem", color: "var(--sub)", background: "rgba(168,128,58,.05)", border: "1px solid rgba(168,128,58,.18)", borderRadius: 8, padding: ".6rem .8rem", lineHeight: 1.55 }}>
                    <strong style={{ color: "var(--accent)" }}>À retenir :</strong> notre force, c'est le <strong>sans engagement et le prix à l'usage</strong>. Là où la concurrence impose 3 000 – 10 000 €/an, on commence à 4,99 € le dossier.
                  </div>
                </Section>

                {/* Pitch */}
                <Section title="Le pitch commercial" icon="🎤">
                  <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem" }}>Accroche 30 secondes</div>
                  <div style={{ fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.6, fontStyle: "italic", marginBottom: ".7rem", paddingLeft: ".7rem", borderLeft: "3px solid var(--accent)" }}>
                    « Bonjour, [Prénom] de VehicleIQ. On aide les garages comme le vôtre à coter un véhicule d'occasion et à savoir où il se situe sur le marché en moins de 5 minutes, sans abonnement ni engagement — vous payez à l'estimation, et les premières sont offertes. Je vous montre en 2 minutes sur un véhicule de votre stock ? »
                  </div>
                  <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem" }}>La démo qui convertit</div>
                  <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                    <Li>Faites l'estimation sur LEUR véhicule, pas un exemple théorique.</Li>
                    <Li>Chronométrez à voix haute : « Voilà, 40 secondes. »</Li>
                    <Li>Montrez le pré-remplissage par photos et les deux cibles de prix (particulier vs reprise).</Li>
                    <Li>Donnez-leur leur <strong style={{ color: "var(--text)" }}>lien formulaire lead</strong> à partager et terminez par une action datée.</Li>
                  </ul>
                </Section>

                {/* Objections */}
                <Section title="Traiter les objections" icon="🛡">
                  <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                    {objections.map(([o, r], i) => (
                      <div key={i} style={{ padding: ".5rem .7rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: ".78rem", color: "var(--text)", marginBottom: ".2rem" }}>{o}</div>
                        <div style={{ fontSize: ".76rem", color: "var(--sub)", lineHeight: 1.55 }}>{r}</div>
                      </div>
                    ))}
                  </div>
                </Section>

                {/* Rémunération */}
                <Section title="Votre rémunération" icon="💎">
                  <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                    <Li><strong style={{ color: "var(--green)" }}>30 % HT</strong> sur la première souscription du client (versée une fois, à l'encaissement).</Li>
                    <Li><strong style={{ color: "var(--accent)" }}>20 % HT</strong> à chaque renouvellement (selon votre mandat).</Li>
                    <Li><strong style={{ color: "var(--accent2)" }}>+5 % de bonus</strong> dès 25 clients signés dans le mois.</Li>
                    <Li><strong style={{ color: "var(--text)" }}>Versement :</strong> les commissions d'un mois sont versées <strong>au début du mois suivant</strong>, par virement (pensez à renseigner votre RIB dans l'onglet « Mes versements »).</Li>
                  </ul>
                </Section>

                {/* Checklist RDV */}
                <Section title="Checklist du parfait RDV" icon="✅">
                  <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
                    <Li>J'ai repéré 1–2 véhicules de leur stock avant le RDV (plaques notées).</Li>
                    <Li>Mon accès démo fonctionne (connexion testée).</Li>
                    <Li>Je connais leur volume de VO et leur outil actuel.</Li>
                    <Li>J'ai mon code apporteur prêt à communiquer.</Li>
                    <Li>J'ai préparé une démo photo (plaque + compteur).</Li>
                    <Li>J'ai fixé le prochain pas AVANT de partir (date précise).</Li>
                  </ul>
                </Section>

                {/* Scripts */}
                <Section title="Scripts prêts à l'emploi" icon="✍️">
                  <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem" }}>E-mail de prospection à froid</div>
                  <div style={{ fontSize: ".77rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: ".7rem", whiteSpace: "pre-line", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: ".7rem .8rem" }}>
{`Objet : Coter vos véhicules d'occasion en 2 minutes (estimations offertes)

Bonjour [Nom], je suis [Prénom] de VehicleIQ. Nous aidons les garages et marchands VO à coter un véhicule et à situer son prix face au marché en moins de 5 minutes — sans abonnement ni engagement, avec des estimations offertes pour démarrer.
Auriez-vous 2 minutes cette semaine pour que je vous le montre sur un véhicule de votre stock ?
Bien à vous, [Prénom] — [téléphone] — contact@vehicleiq.fr`}
                  </div>
                  <div style={{ fontSize: ".72rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem" }}>Phrase de closing</div>
                  <div style={{ fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.6, fontStyle: "italic", paddingLeft: ".7rem", borderLeft: "3px solid var(--green)" }}>
                    « On crée votre accès maintenant : vous testez sur vos prochains véhicules cette semaine, et on fait le point vendredi. Ça vous va ? »
                  </div>
                </Section>

                <div style={{ fontSize: ".72rem", color: "var(--muted)", textAlign: "center", padding: ".5rem", lineHeight: 1.5 }}>
                  Question produit, technique ou commerciale ? <strong style={{ color: "var(--accent)" }}>contact@vehicleiq.fr</strong>
                </div>
              </div>
            );
          })()}

          {!loading && stats && tab === "clients" && (
            <div>
              <div style={{ fontSize: ".85rem", color: "var(--sub)", marginBottom: ".6rem" }}>{clients.length} client{clients.length>1?"s":""} dans votre portefeuille</div>
              {/* Recherche simple clients */}
              <div style={{ display:"flex", gap:".4rem", marginBottom:".8rem" }}>
                <input placeholder="🔍 Rechercher un client…" id="aff-search"
                  style={{ flex:1, background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:6, padding:".35rem .7rem", color:"var(--text)", fontSize:".78rem", outline:"none", fontFamily:"var(--font-body)" }}
                  onChange={e => {
                    const s = e.target.value.toLowerCase();
                    document.querySelectorAll(".aff-client-row").forEach(row => {
                      row.style.display = row.textContent.toLowerCase().includes(s) ? "" : "none";
                    });
                  }} />
              </div>
              {clients.length === 0 ? (
                <div style={{ textAlign: "center", padding: "3rem", color: "var(--sub)" }}>
                  <div style={{ fontSize: "2rem", marginBottom: ".8rem" }}>👥</div>
                  <div>Aucun client encore</div>
                  <div style={{ fontSize: ".78rem", marginTop: ".4rem", color: "var(--muted)" }}>Partagez votre code {stats.code} pour démarrer</div>
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".78rem" }}>
                  <thead>
                    <tr style={{ background: "var(--deep)" }}>
                      {["Nom","Email","Pack","CA généré","Commission","Statut","Client depuis"].map(h => (
                        <th key={h} style={{ padding: ".5rem .8rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".3px", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c,i) => {
                      const isSale = c.source === "sale";
                      return (
                        <tr key={c.uid + "_" + i} style={{ background: i%2===0?"var(--card)":"transparent", borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: ".5rem .8rem", fontWeight: 600 }}>{c.name||"—"}</td>
                          <td style={{ padding: ".5rem .8rem", fontFamily: "var(--font-mono)", fontSize: ".7rem", color: "var(--sub)" }}>{c.email}</td>
                          <td style={{ padding: ".5rem .8rem", fontSize: ".7rem", color: "var(--sub)" }}>{c.pack || "—"}</td>
                          <td style={{ padding: ".5rem .8rem", color: isSale ? "var(--green)" : "var(--muted)", fontWeight: 700 }}>
                            {((c.amount_cents||0)/100).toFixed(2)} €
                          </td>
                          <td style={{ padding: ".5rem .8rem", color: isSale ? "var(--accent2)" : "var(--muted)", fontWeight: 700 }}>
                            {((c.commission_cents||0)/100).toFixed(2)} €
                          </td>
                          <td style={{ padding: ".5rem .8rem" }}>
                            <span style={{
                              fontSize: ".62rem", fontFamily: "var(--font-mono)",
                              padding: ".15rem .45rem", borderRadius: 4,
                              background: isSale ? "rgba(4,120,87,.10)" : "rgba(168,128,58,.08)",
                              color: isSale ? "var(--green)" : "var(--accent)",
                              border: "1px solid " + (isSale ? "rgba(4,120,87,.25)" : "rgba(168,128,58,.25)"),
                              textTransform: "uppercase", letterSpacing: ".5px"
                            }}>
                              {isSale ? "✓ Encaissé" : "En attente"}
                            </span>
                          </td>
                          <td style={{ padding: ".5rem .8rem", color: "var(--muted)", fontSize: ".7rem" }}>{c.created_at?new Date(c.created_at).toLocaleDateString("fr-FR"):"—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Mes versements de commissions */}
          {!loading && stats && tab === "payouts" && (
            <div>
              {/* Coordonnées bancaires (RIB) — nécessaires au versement */}
              <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.1rem 1.3rem", marginBottom: "1.2rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".6rem", flexWrap: "wrap", marginBottom: ".7rem" }}>
                  <div style={{ fontSize: ".8rem", fontWeight: 700, color: "var(--text)" }}>🏦 Mes coordonnées bancaires (RIB)</div>
                  {rib.iban
                    ? <span style={{ fontSize: ".66rem", fontWeight: 700, background: "rgba(46,111,92,.1)", color: "var(--green)", border: "1px solid rgba(46,111,92,.3)", borderRadius: 20, padding: ".15rem .6rem" }}>✓ Enregistré</span>
                    : <span style={{ fontSize: ".66rem", fontWeight: 700, background: "rgba(245,158,11,.1)", color: "var(--accent2)", border: "1px solid rgba(245,158,11,.3)", borderRadius: 20, padding: ".15rem .6rem" }}>⚠ À renseigner</span>}
                </div>
                <div style={{ fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.5, marginBottom: ".8rem" }}>
                  Renseignez l'IBAN sur lequel vos commissions seront virées. Ces informations sont transmises de façon sécurisée à l'équipe VehicleIQ et ne servent qu'au versement de vos commissions.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: ".7rem" }}>
                  <div>
                    <label style={{ fontSize: ".68rem", color: "var(--sub)", fontWeight: 600, display: "block", marginBottom: ".25rem" }}>Titulaire du compte *</label>
                    <input className="input" value={rib.titulaire} onChange={e => setRib(r => ({ ...r, titulaire: e.target.value }))} placeholder="Nom / raison sociale" maxLength={255} />
                  </div>
                  <div>
                    <label style={{ fontSize: ".68rem", color: "var(--sub)", fontWeight: 600, display: "block", marginBottom: ".25rem" }}>IBAN *</label>
                    <input className="input" value={rib.iban} onChange={e => setRib(r => ({ ...r, iban: e.target.value.toUpperCase() }))} placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" maxLength={40} style={{ fontFamily: "var(--font-mono)" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: ".68rem", color: "var(--sub)", fontWeight: 600, display: "block", marginBottom: ".25rem" }}>BIC (facultatif)</label>
                    <input className="input" value={rib.bic} onChange={e => setRib(r => ({ ...r, bic: e.target.value.toUpperCase() }))} placeholder="BNPAFRPPXXX" maxLength={15} style={{ fontFamily: "var(--font-mono)" }} />
                  </div>
                </div>
                {ribErr && <div style={{ marginTop: ".5rem", fontSize: ".74rem", color: "var(--red, #E08A6A)" }}>{ribErr}</div>}
                <div style={{ display: "flex", alignItems: "center", gap: ".7rem", marginTop: ".8rem" }}>
                  <button className="btn btn-accent btn-sm" disabled={ribSaving || !rib.titulaire || rib.iban.replace(/\s/g,"").length < 14} onClick={saveRib}>
                    {ribSaving ? "Enregistrement…" : "Enregistrer mon RIB"}
                  </button>
                  {ribSaved && <span style={{ fontSize: ".76rem", color: "var(--green)", fontWeight: 600 }}>✓ RIB enregistré</span>}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".8rem", marginBottom: "1.2rem" }}>
                {[
                  { label: "En attente de versement", value: ((payouts?.pending_cents||0)/100).toFixed(2)+" €", icon: "⏳", color: "var(--accent)" },
                  { label: "Déjà versé", value: ((payouts?.paid_cents||0)/100).toFixed(2)+" €", icon: "✅", color: "var(--green)" },
                ].map((c,i) => (
                  <div key={i} style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem", borderLeft: "3px solid "+c.color }}>
                    <div style={{ fontSize: "1.2rem", marginBottom: ".3rem" }}>{c.icon}</div>
                    <div style={{ fontFamily: "var(--font-head)", fontSize: "1.8rem", fontWeight: 800, color: c.color, lineHeight: 1.1 }}>{c.value}</div>
                    <div style={{ fontSize: ".7rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.3px", marginTop: ".25rem", fontFamily: "var(--font-mono)", fontWeight: 600 }}>{c.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: "rgba(168,128,58,.06)", border: "1px solid rgba(168,128,58,.18)", borderRadius: 8, padding: ".8rem 1rem", marginBottom: "1rem", fontSize: ".76rem", color: "var(--sub)", lineHeight: 1.55 }}>
                <strong style={{ color: "var(--accent)" }}>💸 Comment ça marche :</strong> vos commissions sont cumulées par mois civil. Le versement d'un mois est effectué <strong>au début du mois suivant</strong> (par virement). Le statut passe à « Versé » dès que le paiement est émis.
              </div>

              {!payouts || !payouts.months ? (
                <div style={{ color: "var(--sub)", fontSize: ".85rem", padding: "1rem" }}>Chargement…</div>
              ) : payouts.months.length === 0 ? (
                <div style={{ background: "var(--card)", border: "1.5px dashed var(--border)", borderRadius: 10, padding: "1.6rem", textAlign: "center", color: "var(--sub)", fontSize: ".85rem" }}>
                  Aucune commission pour l'instant. Vos versements apparaîtront ici dès vos premières ventes apportées.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem" }}>
                  <thead>
                    <tr style={{ background: "var(--deep)" }}>
                      {["Mois","Ventes","Commissions","Bonus","Total dû","Statut"].map(h => (
                        <th key={h} style={{ padding: ".55rem .7rem", textAlign: h==="Mois"||h==="Statut"?"left":"right", color: "var(--sub)", fontWeight: 600, fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".3px" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payouts.months.map((m,i) => {
                      const paid = m.status === "paid";
                      const [y,mo] = m.period.split("-");
                      const label = new Date(parseInt(y), parseInt(mo)-1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
                      return (
                        <tr key={m.period} style={{ borderBottom: "1px solid var(--border)", background: i%2?"var(--card)":"transparent" }}>
                          <td style={{ padding: ".55rem .7rem", fontWeight: 600, textTransform: "capitalize" }}>{label}</td>
                          <td style={{ padding: ".55rem .7rem", textAlign: "right", color: "var(--sub)" }}>{m.sales_count}</td>
                          <td style={{ padding: ".55rem .7rem", textAlign: "right" }}>{(m.commission_cents/100).toFixed(2)} €</td>
                          <td style={{ padding: ".55rem .7rem", textAlign: "right", color: m.bonus_cents>0?"var(--green)":"var(--muted)" }}>{m.bonus_cents>0?"+"+(m.bonus_cents/100).toFixed(2)+" €":"—"}</td>
                          <td style={{ padding: ".55rem .7rem", textAlign: "right", fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-head)" }}>{(m.due_cents/100).toFixed(2)} €</td>
                          <td style={{ padding: ".55rem .7rem" }}>
                            {paid
                              ? <span style={{ fontSize: ".68rem", fontWeight: 700, background: "rgba(46,111,92,.12)", color: "var(--green)", borderRadius: 20, padding: ".15rem .6rem" }}>✓ Versé{m.paid_at?" · "+new Date(m.paid_at).toLocaleDateString("fr-FR",{day:"2-digit",month:"2-digit"}):""}</span>
                              : <span style={{ fontSize: ".68rem", fontWeight: 700, background: "rgba(168,128,58,.12)", color: "var(--accent)", borderRadius: 20, padding: ".15rem .6rem" }}>⏳ En attente</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
// ─── Admin Dashboard ─────────────────────────────────────────────────────────

function VehicleRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    apiGet("/api/admin/vehicles").then(res => {
      if (res.success) setRequests(res.requests || []);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const filtered = filter === "all" ? requests : requests.filter(r => r.type === filter);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
        <div style={{ fontSize:".85rem", color:"var(--sub)" }}>{requests.length} requête{requests.length > 1 ? "s" : ""}</div>
        <div style={{ display:"flex", gap:".5rem" }}>
          {["all","buy","sell"].map(f => (
            <button key={f} className={`btn btn-ghost btn-sm`} style={{ fontSize:".72rem", ...(filter===f?{borderColor:"var(--accent)",color:"var(--accent)"}:{}) }}
              onClick={()=>setFilter(f)}>{f==="all"?"Tous":f==="buy"?"🛒 Achats":"💰 Ventes"}</button>
          ))}
        </div>
      </div>
      {loading && <div style={{ color:"var(--sub)", fontSize:".85rem" }}>Chargement...</div>}
      {filtered.map((req, i) => {
        const ans = req.answers || {};
        const photos = req.photos || [];
        // Construire entry au format attendu par exportEntryToPDF
        const pdfEntry = {
          id: req.id || i,
          date: req.created_at || req.date,
          type: req.type,
          answers: ans,
          data: ans,
          results: req.results || req.result || {},
          result: req.results || req.result || {},
        };
        return (
          <div key={i} style={{ background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:8, padding:".9rem 1.1rem", marginBottom:".6rem" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:".4rem" }}>
              <div>
                <span style={{ color:req.type==="buy"?"var(--green)":"var(--accent2)", fontWeight:700, fontSize:".88rem" }}>
                  {req.type==="buy"?"🛒 Achat":req.type==="check"?"🔍 Contrôle":"💰 Vente"}
                </span>
                <span style={{ fontSize:".72rem", color:"var(--muted)", fontFamily:"var(--font-mono)", marginLeft:".8rem" }}>
                  {new Date(req.created_at).toLocaleString("fr-FR")}
                </span>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:".5rem" }}>
                <button
                  onClick={() => exportEntryToPDF(pdfEntry).catch(err => alert("Erreur export PDF : " + err.message))}
                  title="Exporter ce véhicule en PDF"
                  style={{ background:"rgba(168,128,58,.08)", border:"1px solid rgba(168,128,58,.3)", color:"var(--accent)",
                           padding:".25rem .55rem", borderRadius:5, fontSize:".7rem", cursor:"pointer",
                           fontWeight:600, display:"inline-flex", alignItems:"center", gap:".3rem" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(168,128,58,.18)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(168,128,58,.08)"; }}>
                  📄 PDF
                </button>
                <span style={{ fontSize:".7rem", color:"var(--sub)" }}>{req.user_uid?.slice(0,12)}...</span>
              </div>
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:".3rem", marginBottom:".4rem" }}>
              {Object.entries(ans).filter(([k,v])=>v&&!k.startsWith("_")).slice(0,6).map(([k,v])=>(
                <span key={k} style={{ fontSize:".68rem", background:"var(--deep)", border:"1.5px solid var(--border)", borderRadius:10, padding:".1rem .5rem", color:"var(--sub)" }}>
                  <span style={{color:"var(--muted)"}}>{k}:</span> {String(v)}
                </span>
              ))}
            </div>
            {photos.length > 0 && (
              <div style={{ display:"flex", gap:".4rem", flexWrap:"wrap" }}>
                {photos.slice(0,4).map((p,j)=>(
                  <img key={j} src={p} alt="" style={{ width:60, height:60, objectFit:"cover", borderRadius:4, border:"1.5px solid var(--border)" }} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AdminDashboard({ user, onClose }) {
  const [tab, _setTab] = useState("overview");
  // Suivi des visiteurs (vue globale)
  const [visits, setVisits] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => apiGet("/api/admin/visits").then(j => { if (alive && j?.success) setVisits(j); }).catch(() => {});
    load();
    const t = setInterval(load, 30000); // rafraîchit les visiteurs en ligne toutes les 30 s
    return () => { alive = false; clearInterval(t); };
  }, []);
  // Versements de commissions apporteurs
  const [adminPayouts, setAdminPayouts] = useState(null);
  const [payoutBusy, setPayoutBusy] = useState(null); // `${code}:${period}`
  const loadAdminPayouts = () => apiGet("/api/admin/payouts").then(r => { if (r?.success) setAdminPayouts(r.affiliates || []); }).catch(() => setAdminPayouts([]));
  const markPayout = async (code, period, status) => {
    const key = code + ":" + period;
    setPayoutBusy(key);
    try {
      await apiPost("/api/admin/payouts/mark", { affiliate_code: code, period, status });
      await loadAdminPayouts();
    } catch (e) { alert("Action impossible : " + (e?.message || "erreur")); }
    finally { setPayoutBusy(null); }
  };
  // Blog management — v73 : persistance réelle côté serveur (le "Publier"
  // n'écrivait jusqu'ici que dans le localStorage du navigateur, jamais
  // visible pour personne d'autre — remplacé par de vrais appels API).
  const [blogArticles, setBlogArticles] = useState(null);
  const loadBlogArticles = () => apiGet("/api/admin/blog/articles").then(r => { if (r?.success) setBlogArticles(r.articles || []); });
  useEffect(() => { loadBlogArticles(); }, []);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title: "", cat: "Marché", emoji: "📊", excerpt: "", body: "", slug: "", metaTitle: "", metaDescription: "", coverImage: "" });
  const [articleSaving, setArticleSaving] = useState(false);
  const [articleError, setArticleError] = useState(null);
  // Testimonials management
  const [testiDrafts, setTestiDrafts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("viq_testimonials") || "[]"); } catch(e) { return []; }
  });
  const [testiForm, setTestiForm] = useState({ name:"", role:"", badge:"", quote:"", stars:5, bg:"var(--accent)", photo:"" });
  // Vidéo démo (lien YouTube modifiable)
  const [demoVideoUrl, setDemoVideoUrl] = useState(() => {
    try { return localStorage.getItem("viq_demo_video") || ""; } catch(e) { return ""; }
  });
  const [demoVideoSaved, setDemoVideoSaved] = useState(false);
  const [stats, setStats] = useState(null);
  // Météo super admin (cohérent avec Dashboard Pro) — basé sur localStorage.viq_profile.address
  const [adminWeather, setAdminWeather] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const loadW = async () => {
      try {
        const saved = JSON.parse(localStorage.getItem("viq_profile") || "{}");
        const addr = saved.address;
        if (!addr) return;
        const cached = JSON.parse(localStorage.getItem("viq_weather") || "null");
        if (cached && cached.addr === addr && (Date.now() - cached.ts) < 6 * 3600 * 1000) {
          if (!cancelled) setAdminWeather(cached.data);
          return;
        }
        const g = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(addr)}&limit=1`);
        if (!g.ok) return;
        const gd = await g.json();
        const f = (gd.features || [])[0];
        if (!f) return;
        const [lng, lat] = f.geometry.coordinates;
        const city = f.properties.city || f.properties.name || "";
        const w = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current_weather=true&timezone=Europe%2FParis`);
        if (!w.ok) return;
        const wd = await w.json();
        if (!wd.current_weather) return;
        const data = { temp: Math.round(wd.current_weather.temperature), code: wd.current_weather.weathercode, city };
        if (!cancelled) setAdminWeather(data);
        localStorage.setItem("viq_weather", JSON.stringify({ addr, ts: Date.now(), data }));
      } catch (e) { /* silencieux */ }
    };
    loadW();
    return () => { cancelled = true; };
  }, []);
  const [users, setUsers] = useState([]);
  // v73 — Règle 5 : modification du forfait d'un utilisateur par le Super Admin.
  const [editingPlanUid, setEditingPlanUid] = useState(null);
  const [planDraft, setPlanDraft] = useState({ plan: "", billing_period: "monthly" });
  const [planSaving, setPlanSaving] = useState(false);
  const [quoteTarget, setQuoteTarget] = useState(null); // v73 — Règle 6 : { email, name } du destinataire du devis
  const changePlan = async (uid) => {
    setPlanSaving(true);
    try {
      const r = await apiPost(`/api/admin/users/${uid}/plan`, { plan: planDraft.plan || null, billing_period: planDraft.billing_period });
      if (r?.success) { setEditingPlanUid(null); await refreshUsers(); }
      else alert("Erreur : " + (r?.error || "modification échouée"));
    } catch (e) { alert("Erreur réseau : " + e.message); }
    finally { setPlanSaving(false); }
  };
  const [searches, setSearches] = useState([]);
  const [payments, setPayments] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  // Piste 1 : Dataset propriétaire
  const [datasetStats, setDatasetStats] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // v5 : privilèges du super admin courant (chargés depuis /api/admin/me)
  // canExportCSV est FALSE pour les super admins promus, TRUE pour l'owner uniquement.
  // Défaut conservateur : on attend la réponse de /api/admin/me avant de présumer les droits.
  const [adminPrivileges, setAdminPrivileges] = useState({ isOwner: false, isSuperAdmin: false, canExportCSV: false, sensitiveAccess: false });;
  useEffect(() => {
    (async () => {
      try {
        const h = await getAuthHeaders();
        if (!h) return;
        const r = await fetch(`${API_URL}/api/admin/me`, { headers: h });
        if (!r.ok) return;
        const d = await r.json();
        if (d?.success) setAdminPrivileges({
          isOwner: !!d.isOwner,
          isSuperAdmin: !!d.isSuperAdmin,
          canExportCSV: !!d.canExportCSV,
          sensitiveAccess: !!d.sensitiveAccess,
        });
      } catch (e) { /* silencieux : si l'endpoint n'existe pas, on garde les défauts */ }
    })();
  }, []);
  // Filtres & recherche par onglet
  const [adminSearch, setAdminSearch] = useState("");
  const [adminFilter, setAdminFilter] = useState("all");
  const [adminSort, setAdminSort] = useState("desc");
  // Reset filtres au changement d'onglet — sinon "Pro" sélectionné dans Utilisateurs
  // fuit vers Requêtes et masque tous les résultats non-pro.
  const setTab = (t) => {
    _setTab(t);
    setAdminSearch("");
    setAdminFilter("all");
    if (t === "payouts" && adminPayouts === null) loadAdminPayouts();
  };

  const filterAndSort = (list, fields) => {
    let filtered = list;
    if (adminSearch) {
      const s = adminSearch.toLowerCase();
      filtered = list.filter(item => fields.some(f => String(item[f]||"").toLowerCase().includes(s)));
    }
    if (adminFilter !== "all") {
      // Bug corrigé : avant, la condition `item.is_pro === (adminFilter==="pro")`
      // était toujours évaluée et matchait tous les is_pro=false dès qu'on filtrait
      // par type ("check", "sell", "buy") — d'où l'affichage de Cotes alors
      // qu'on avait cliqué "Contrôle". On dispatche désormais selon le filtre.
      if (adminFilter === "pro") {
        filtered = filtered.filter(item => item.is_pro === true);
      } else if (adminFilter === "particulier") {
        filtered = filtered.filter(item => item.is_pro === false || item.is_pro === null || item.is_pro === undefined);
      } else {
        // Filtres de type d'objet (sell, buy, check) ou de plan
        filtered = filtered.filter(item => item.type === adminFilter || item.plan_id === adminFilter || item.plan === adminFilter);
      }
    }
    return adminSort === "desc" ? filtered : [...filtered].reverse();
  };

  const FilterBar = ({ fields, filterOptions }) => (
    <div style={{ display:"flex", gap:".4rem", marginBottom:".8rem", flexWrap:"wrap" }}>
      <input placeholder="🔍 Rechercher…" value={adminSearch} onChange={e=>setAdminSearch(e.target.value)}
        style={{ flex:1, minWidth:130, background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:6, padding:".35rem .7rem", color:"var(--text)", fontSize:".78rem", outline:"none", fontFamily:"var(--font-body)" }} />
      {filterOptions?.map(({val,label}) => (
        <button key={val} onClick={()=>setAdminFilter(val)}
          style={{ padding:".3rem .6rem", borderRadius:6, border:"1.5px solid var(--border)", background:adminFilter===val?"var(--accent)":"var(--card)", color:adminFilter===val?"#fff":"var(--sub)", fontSize:".7rem", cursor:"pointer", fontFamily:"var(--font-body)" }}>
          {label}
        </button>
      ))}
      <button onClick={()=>setAdminSort(s=>s==="desc"?"asc":"desc")}
        style={{ padding:".3rem .6rem", borderRadius:6, border:"1.5px solid var(--border)", background:"var(--card)", color:"var(--sub)", fontSize:".7rem", cursor:"pointer", fontFamily:"var(--font-body)" }}>
        {adminSort==="desc"?"↓ Récent":"↑ Ancien"}
      </button>
    </div>
  );

  const ADMIN_UIDS = ["7j1CjoqPJreotdckYtSQZoCYPRw1"]; // UID Firebase super-admin propriétaire

  // v5 : isAdmin true si dans la liste env OU promu super admin en base (vérifié via /api/admin/me)
  const hardCodedAdmin = ADMIN_UIDS.includes(user?.uid);
  const isAdmin = hardCodedAdmin || adminPrivileges.isSuperAdmin;
  // v6 : accès aux sections sensibles (Vue globale, Paiements, Apporteurs,
  // Versements, Analyse consolidée, Dataset). L'owner (compte historique) y a
  // toujours accès ; un super admin promu doit être autorisé explicitement
  // (adminPrivileges.sensitiveAccess, renvoyé par /api/admin/me).
  const sensitiveAccess = adminPrivileges.isOwner || adminPrivileges.sensitiveAccess;

  // Placeholder flouté affiché à la place d'un onglet sensible tant que le
  // super admin courant n'a pas été autorisé par le propriétaire. Reprend le
  // même langage visuel que le verrouillage des résultats (blur + cadenas).
  const RestrictedTabPlaceholder = ({ label, icon }) => (
    <div style={{ position: "relative", minHeight: 260, borderRadius: 10, overflow: "hidden", border: "1px solid var(--border)" }}>
      <div aria-hidden="true" style={{ filter: "blur(9px)", opacity: .55, padding: "1.2rem", userSelect: "none", pointerEvents: "none" }}>
        <div style={{ display: "flex", gap: ".8rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ flex: "1 1 160px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "1rem", minHeight: 70 }} />
          ))}
        </div>
        <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, height: 160 }} />
      </div>
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: ".6rem", textAlign: "center", padding: "1.5rem",
        background: "linear-gradient(180deg, rgba(17,17,17,.05), rgba(17,17,17,.25))",
      }}>
        <div style={{ fontSize: "1.8rem" }}>🔒</div>
        <div style={{ fontSize: ".92rem", fontWeight: 800, color: "var(--text)" }}>Accès réservé au super admin</div>
        <div style={{ fontSize: ".78rem", color: "var(--sub)", maxWidth: 360 }}>
          L'onglet {icon} {label} contient des données sensibles (financières / stratégiques). Il n'est visible qu'après autorisation explicite du super admin propriétaire.
        </div>
      </div>
    </div>
  );

  const [adminRefreshing, setAdminRefreshing] = useState(false);
  const loadAdminData = useCallback(() => {
    if (!isAdmin) return Promise.resolve();
    return Promise.all([
      // v6 : stats/payments/affiliates sont désormais protégés côté serveur par
      // requireSensitiveAdmin — un admin non autorisé reçoit un 403 dessus. On
      // catch individuellement pour ne jamais faire échouer tout le Promise.all
      // (sinon Utilisateurs/Requêtes, non restreints, cesseraient de charger).
      apiGet("/api/admin/stats").catch(() => null),
      apiGet("/api/admin/users"),
      apiGet("/api/admin/searches"),
      apiGet("/api/admin/payments").catch(() => null),
      apiGet("/api/admin/affiliates").catch(() => null),
      apiGet("/api/admin/dataset-stats").catch(() => null),
      apiGet("/api/admin/feedback").catch(() => ({ feedbacks: [] })),
      apiGet("/api/admin/sale-followup").catch(() => ({ followups: [] })),
    ]).then(([s, u, se, p, aff, ds, fb, fl]) => {
      if (s?.success) setStats(s.stats);
      if (u.success) setUsers(u.users || []);
      if (se.success) setSearches(se.searches || []);
      if (p?.success) setPayments(p.payments || []);
      if (aff?.success) setAffiliates(aff.affiliates || []);
      if (ds) setDatasetStats(ds);
      setFeedbacks((fb && fb.feedbacks) || []);
      setFollowups((fl && fl.followups) || []);
    }).catch(e => setError("Erreur chargement données : " + e.message))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  useEffect(() => { loadAdminData(); }, [loadAdminData]);

  // Actualisation manuelle de toutes les données admin (bouton « Actualiser »).
  const refreshAllAdmin = async () => {
    if (adminRefreshing) return;
    setAdminRefreshing(true);
    try {
      const tasks = [loadAdminData()];
      tasks.push(apiGet("/api/admin/visits").then(j => { if (j?.success) setVisits(j); }).catch(() => {}));
      if (adminPayouts !== null) tasks.push(loadAdminPayouts());
      await Promise.all(tasks);
    } finally {
      setTimeout(() => setAdminRefreshing(false), 350);
    }
  };

  // v5 : recharger uniquement la liste users (après promotion/révocation)
  const refreshUsers = async () => {
    try {
      const u = await apiGet("/api/admin/users");
      if (u?.success) setUsers(u.users || []);
    } catch (e) { /* silencieux */ }
  };

  const downloadCSV = async (type) => {
    try {
      // getAuthHeaders est async — sans await, on passait une Promise comme headers,
      // qui était sérialisée en "[object Object]" → 401 "Non authentifié" → Excel
      // recevait le JSON d'erreur et l'affichait dans la cellule A1.
      const headers = await getAuthHeaders();
      const res = await fetch(API_URL + "/api/admin/" + type + "/export", { headers });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? " — " + txt.slice(0, 200) : ""}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "viq_" + type + "_" + Date.now() + ".csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Erreur export CSV : " + e.message);
    }
  };

  if (!isAdmin) return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 380, textAlign: "center", padding: "3rem 2rem" }}>
        <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🚫</div>
        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.5rem", marginBottom: ".5rem" }}>Accès refusé</div>
        <div style={{ color: "var(--sub)", marginBottom: "1.5rem" }}>Réservé aux super-administrateurs VehicleIQ.</div>
        <button className="btn btn-ghost" onClick={onClose}>Fermer</button>
      </div>
    </div>
  );

  const STAT_CARDS = stats ? [
    { label: "Utilisateurs", value: stats.total_users || 0, icon: "👥", color: "var(--blue)" },
    { label: "Requêtes", value: stats.total_searches || 0, icon: "🔍", color: "var(--green)" },
    { label: "Paiements", value: stats.total_payments || 0, icon: "💳", color: "var(--accent2)" },
    { label: "CA total", value: (((stats.total_revenue_cents || 0) / 100).toFixed(2)) + " €", icon: "💰", color: "var(--accent)" },
    { label: "Actifs 7j", value: stats.active_7d || 0, icon: "📈", color: "var(--green)" },
    { label: "Requêtes aujourd'hui", value: stats.searches_today || 0, icon: "⚡", color: "var(--accent)" },
  ] : [];

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel panel-wide panel-fullscreen" style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div className="panel-header" style={{ flexShrink: 0, borderBottom: "2px solid var(--accent)", background: "linear-gradient(135deg, rgba(168,128,58,.08), transparent)" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: ".8rem" }}>
              <div className="panel-title">⚡ Super Admin</div>
              <span style={{ fontSize: ".65rem", background: "rgba(168,128,58,.15)", border: "1px solid rgba(168,128,58,.3)", color: "var(--accent)", borderRadius: 10, padding: ".15rem .5rem", fontFamily: "var(--font-mono)" }}>VEHICLEIQ</span>
            </div>
            <div className="panel-sub">{user?.email} — Dashboard opérationnel</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
            <button onClick={refreshAllAdmin} disabled={adminRefreshing} title="Actualiser les données" aria-label="Actualiser"
              style={{width:32,height:32,borderRadius:8,border:"1.5px solid var(--border)",background:"var(--bg)",cursor:adminRefreshing?"wait":"pointer",color:"var(--text)",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: adminRefreshing ? "viq-spin .8s linear infinite" : "none" }}>
                <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
            <ThemeToggleButton size={32} />
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Tabs — style harmonisé avec le design system SaaS */}
        <div style={{
          display: "flex", borderBottom: "1.5px solid var(--border)", flexShrink: 0,
          background: "var(--bg)", padding: "0 1rem", gap: ".2rem",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
        }}>
          {[["overview","📊 Vue globale"],["users","👥 Utilisateurs"],["searches","🔍 Requêtes"],["payments","💳 Paiements"],["affiliates","🤝 Apporteurs"],["payouts","💸 Versements"],["consolidated","📈 Analyse consolidée"],["dataset","🗂 Dataset"],["blog","✍️ Blog"],["temoignages","⭐ Témoignages"],["demovideo","🎬 Vidéo démo"],["settings","⚙️ Paramètres"]].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: ".75rem 1.1rem",
              background: tab===t ? "rgba(168,128,58,.08)" : "transparent",
              border: "none", cursor: "pointer",
              fontSize: ".84rem", fontWeight: tab===t ? 600 : 500,
              color: tab===t ? "var(--accent)" : "var(--sub)",
              borderBottom: tab===t ? "2.5px solid var(--accent)" : "2.5px solid transparent",
              marginBottom: -1.5, fontFamily: "var(--font-body)",
              whiteSpace: "nowrap",
              transition: "all .15s cubic-bezier(.4,0,.2,1)",
              borderRadius: "8px 8px 0 0"
            }}>{l}</button>
          ))}
        </div>

        <div className="panel-body" style={{ overflowY: "auto", flex: 1 }}>
          {loading && <div style={{ textAlign: "center", padding: "3rem" }}><div className="spinner" style={{ margin: "0 auto 1rem" }} />Chargement des données…</div>}
          {error && <div style={{ padding: "1rem", color: "var(--red)", background: "rgba(255,60,92,.08)", borderRadius: 6 }}>⚠ {error}</div>}

          {/* ── Overview enrichie ── */}
          {!loading && tab === "overview" && !sensitiveAccess && <RestrictedTabPlaceholder label="Vue globale" icon="📊" />}
          {!loading && tab === "overview" && sensitiveAccess && (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

              {/* ─── Bandeau de bienvenue + météo (cohérent avec Dashboard Pro) ─── */}
              {(() => {
                const h = new Date().getHours();
                const salut = h < 6 ? "Bonne nuit" : h < 12 ? "Bonjour" : h < 18 ? "Bel après-midi" : "Bonne soirée";
                const fullName = user?.displayName || user?.email?.split("@")[0] || "";
                const firstName = fullName.split(/[\s.]/)[0].replace(/^./, c => c.toUpperCase());
                const weatherIcon = (code) => {
                  if (code === 0) return "☀️";
                  if (code <= 2) return "🌤️";
                  if (code === 3) return "☁️";
                  if (code <= 49) return "🌫️";
                  if (code <= 67) return "🌧️";
                  if (code <= 77) return "❄️";
                  if (code <= 86) return "🌦️";
                  if (code <= 99) return "⛈️";
                  return "🌤️";
                };
                return (
                  <div style={{
                    background: "linear-gradient(135deg, rgba(168,128,58,.08), rgba(168,128,58,.02) 60%, transparent)",
                    border: "1.5px solid var(--border)",
                    borderRadius: 14,
                    padding: "1.1rem 1.4rem",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: "1rem"
                  }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: ".15rem", minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)", letterSpacing: "-.01em" }}>
                        {salut}{firstName ? `, ${firstName}` : ""} 👋
                      </div>
                      <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.5 }}>
                        Bienvenue dans le centre de contrôle VehicleIQ. Voici l'activité de la plateforme à jour.
                      </div>
                    </div>
                    {adminWeather && (
                      <div style={{
                        display: "flex", alignItems: "center", gap: ".7rem",
                        background: "var(--white)", border: "1.5px solid var(--border)",
                        borderRadius: 100, padding: ".5rem 1rem .5rem .8rem",
                        boxShadow: "0 2px 8px rgba(20,30,55,.04)"
                      }}>
                        <span style={{ fontSize: "1.5rem", lineHeight: 1 }}>{weatherIcon(adminWeather.code)}</span>
                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                          <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-head)" }}>
                            {adminWeather.temp}°C
                          </span>
                          {adminWeather.city && (
                            <span style={{ fontSize: ".66rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)" }}>
                              {adminWeather.city}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ─── Suivi des visiteurs ─── */}
              <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1.1rem 1.3rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: ".9rem", flexWrap: "wrap", gap: ".5rem" }}>
                  <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>📍 Trafic & visiteurs</div>
                  {visits && (
                    <div style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", fontSize: ".74rem", color: "var(--green)", fontWeight: 700 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--green)", animation: "pulse-ring 1.6s infinite", display: "inline-block" }} />
                      {visits.online} en ligne
                    </div>
                  )}
                </div>
                {!visits ? (
                  <div style={{ color: "var(--sub)", fontSize: ".82rem" }}>Chargement…</div>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: ".7rem" }}>
                      <AdminMetricCard icon="🟢" label="En ligne" value={visits.online} accent="var(--green)" />
                      <AdminMetricCard icon="📅" label="Aujourd'hui" value={visits.today} accent="var(--accent)" />
                      <AdminMetricCard icon="📈" label="7 derniers jours" value={visits.last7} accent="var(--accent2)" />
                      <AdminMetricCard icon="🗓️" label="Mois écoulé (30 j)" value={visits.last30} accent="var(--text)" />
                    </div>
                    {/* Répartition par appareil (30 j) */}
                    {visits.devices && (() => {
                      const d = visits.devices;
                      const total = (d.smartphone || 0) + (d.ordinateur || 0) + (d.tablette || 0) + (d.inconnu || 0);
                      const pct = (n) => total ? Math.round((n / total) * 100) + " %" : "0 %";
                      return (
                        <div style={{ marginTop: ".7rem" }}>
                          <div style={{ fontSize: ".62rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".5rem" }}>Visiteurs par appareil (30 j)</div>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: ".7rem" }}>
                            <AdminMetricCard icon="📱" label="Smartphone" value={d.smartphone || 0} sub={pct(d.smartphone || 0) + " du trafic"} accent="#3B82F6" />
                            <AdminMetricCard icon="💻" label="Ordinateur" value={d.ordinateur || 0} sub={pct(d.ordinateur || 0) + " du trafic"} accent="var(--accent)" />
                            <AdminMetricCard icon="📟" label="Tablette" value={d.tablette || 0} sub={pct(d.tablette || 0) + " du trafic"} accent="var(--accent2)" />
                            {(d.inconnu || 0) > 0 && (
                              <AdminMetricCard icon="❔" label="Inconnu" value={d.inconnu || 0} sub={pct(d.inconnu || 0) + " du trafic"} accent="var(--muted)" />
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>

              {/* ─── 6 KPI cards principales ─── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: ".8rem" }}>
                {STAT_CARDS.map((c, i) => (
                  <AdminMetricCard key={i} icon={c.icon} label={c.label} value={c.value} accent={c.color} />
                ))}
              </div>

              {/* ─── Métriques additionnelles : 4 sub-KPIs ─── */}
              {stats && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: ".8rem" }}>
                  <AdminMetricCard label="Conversion" accent="var(--accent)"
                    value={`${(stats.conversion_pct || 0).toFixed(1)} %`}
                    sub={`${stats.paying_users || 0} payants / ${stats.total_users || 0} inscrits`} />
                  <AdminMetricCard label="ARPU" accent="var(--green)"
                    value={`${((stats.avg_revenue_per_user_cents || 0) / 100).toFixed(0)} €`}
                    sub="par utilisateur payant" />
                  <AdminMetricCard label="CA 30j" accent="var(--accent)"
                    value={`${(((stats.revenue_30d_cents || 0) / 100)).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`}
                    sub={`↗ ${(((stats.revenue_7d_cents || 0) / 100)).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} € sur 7j`} />
                  <AdminMetricCard label="Pro Multi-Agent" accent="#3B82F6"
                    value={stats.total_organizations || 0}
                    sub={`organisations · ${stats.total_agents || 0} agents`} />
                </div>
              )}

              {/* v78 — Règle : chiffres CRM et Dossiers VIQ, plateforme entière. */}
              {stats && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: ".8rem" }}>
                  <AdminMetricCard label="Dossiers VIQ" accent="var(--accent)"
                    value={stats.total_dossiers_viq || 0}
                    sub={`${stats.total_lots_viq || 0} en lot · ${stats.total_sales_recorded || 0} ventes renseignées`} />
                  <AdminMetricCard label="Campagnes CRM" accent="#9333ea"
                    value={stats.total_campaigns || 0}
                    sub={`${stats.total_crm_recipients || 0} destinataires cumulés`} />
                  <AdminMetricCard label="Emails envoyés (CRM)" accent="#2563eb"
                    value={stats.total_emails_sent || 0}
                    sub="toutes campagnes confondues" />
                  <AdminMetricCard label="Contacts CRM" accent="#0d9488"
                    value={stats.total_contacted || 0}
                    sub="prospects/leads déjà contactés" />
                </div>
              )}

              {/* ─── Évolution mensuelle (12 derniers mois) : barres SVG ─── */}
              {stats && (stats.monthly || []).length > 0 && (() => {
                const months = stats.monthly || [];
                const maxRevenue = Math.max(...months.map(m => m.revenue_cents || 0), 1);
                const maxUsers = Math.max(...months.map(m => m.users || 0), 1);
                const maxSearches = Math.max(...months.map(m => m.searches || 0), 1);
                return (
                  <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "1.2rem", flexWrap: "wrap", gap: ".5rem" }}>
                      <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)" }}>
                        📊 Évolution mensuelle · 12 derniers mois
                      </div>
                      <div style={{ display: "flex", gap: ".8rem", fontSize: ".7rem", color: "var(--sub)" }}>
                        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "var(--accent)", borderRadius: 2, marginRight: ".3rem", verticalAlign: "middle" }} />CA (€)</span>
                        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#3B82F6", borderRadius: 2, marginRight: ".3rem", verticalAlign: "middle" }} />Inscrits</span>
                        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#10B981", borderRadius: 2, marginRight: ".3rem", verticalAlign: "middle" }} />Requêtes</span>
                      </div>
                    </div>
                    {/* Graphique en barres groupées */}
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${months.length}, 1fr)`, gap: ".25rem", alignItems: "end", height: 180, marginBottom: ".5rem" }}>
                      {months.map((m, i) => {
                        const revHeight = (m.revenue_cents / maxRevenue) * 160;
                        const userHeight = (m.users / maxUsers) * 160;
                        const searchHeight = (m.searches / maxSearches) * 160;
                        return (
                          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, height: "100%", justifyContent: "flex-end", position: "relative" }}>
                            <div style={{ display: "flex", alignItems: "flex-end", gap: 1, height: "100%", width: "100%", justifyContent: "center" }}>
                              <div title={`CA: ${(m.revenue_cents/100).toLocaleString("fr-FR")} €`} style={{ width: 8, height: Math.max(2, revHeight), background: "var(--accent)", borderRadius: "2px 2px 0 0", transition: "height .4s" }} />
                              <div title={`${m.users} inscrits`} style={{ width: 8, height: Math.max(2, userHeight), background: "#3B82F6", borderRadius: "2px 2px 0 0", transition: "height .4s" }} />
                              <div title={`${m.searches} requêtes`} style={{ width: 8, height: Math.max(2, searchHeight), background: "#10B981", borderRadius: "2px 2px 0 0", transition: "height .4s" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Labels mois */}
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${months.length}, 1fr)`, gap: ".25rem" }}>
                      {months.map((m, i) => (
                        <div key={i} style={{ fontSize: ".62rem", color: "var(--sub)", textAlign: "center", fontFamily: "var(--font-mono)", textTransform: "uppercase", letterSpacing: ".3px" }}>
                          {m.label || m.month}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ─── Mix Pro / Particulier ─── */}
              {stats && (
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
                  <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)", marginBottom: "1rem" }}>
                    👥 Répartition de la base utilisateurs
                  </div>
                  {(() => {
                    const pro = stats.pro_users || 0;
                    const part = stats.particulier_users || 0;
                    const total = pro + part;
                    const proPct = total > 0 ? Math.round((pro / total) * 100) : 0;
                    const partPct = 100 - proPct;
                    return (
                      <>
                        <div style={{ display: "flex", height: 12, background: "var(--deep)", borderRadius: 100, overflow: "hidden", marginBottom: ".7rem" }}>
                          <div style={{ width: `${proPct}%`, background: "#3B82F6", transition: "width .4s" }} />
                          <div style={{ width: `${partPct}%`, background: "var(--accent)", transition: "width .4s" }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".82rem", color: "var(--text)" }}>
                          <div>
                            <span style={{ color: "#3B82F6", marginRight: ".3rem" }}>●</span>
                            <strong>Pro</strong> · {pro.toLocaleString("fr-FR")} utilisateur{pro > 1 ? "s" : ""} ({proPct} %)
                          </div>
                          <div>
                            <span style={{ color: "var(--accent)", marginRight: ".3rem" }}>●</span>
                            <strong>Particulier</strong> · {part.toLocaleString("fr-FR")} utilisateur{part > 1 ? "s" : ""} ({partPct} %)
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* ─── Top 5 utilisateurs par CA ─── */}
              {stats && (stats.top_users || []).length > 0 && (
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
                  <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)", marginBottom: "1rem" }}>
                    🏆 Top 5 utilisateurs · classement par CA payé
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".82rem" }}>
                    <thead>
                      <tr style={{ background: "var(--deep)" }}>
                        {["#", "Utilisateur", "Type", "Paiements", "Recherches", "CA total"].map(h => (
                          <th key={h} style={{ padding: ".55rem .8rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(stats.top_users || []).map((u, i) => (
                        <tr key={u.uid} style={{ borderBottom: i < stats.top_users.length - 1 ? "1px solid var(--border)" : "none" }}>
                          <td style={{ padding: ".55rem .8rem", fontFamily: "var(--font-head)", fontWeight: 800, color: i === 0 ? "var(--accent)" : "var(--muted)" }}>#{i + 1}</td>
                          <td style={{ padding: ".55rem .8rem" }}>
                            <div style={{ fontWeight: 600, color: "var(--text)" }}>{u.siret_name || u.name || u.email?.split("@")[0] || "—"}</div>
                            <div style={{ fontSize: ".7rem", color: "var(--sub)" }}>{u.email}</div>
                          </td>
                          <td style={{ padding: ".55rem .8rem" }}>
                            <span style={{ fontSize: ".7rem", padding: ".15rem .5rem", borderRadius: 100, background: u.is_pro ? "rgba(59,130,246,.1)" : "rgba(168,128,58,.1)", color: u.is_pro ? "#3B82F6" : "var(--accent)", fontWeight: 600 }}>
                              {u.is_pro ? "Pro" : "Particulier"}
                            </span>
                          </td>
                          <td style={{ padding: ".55rem .8rem", color: "var(--sub)" }}>{u.payment_count}</td>
                          <td style={{ padding: ".55rem .8rem", color: "var(--sub)" }}>{u.search_count || 0}</td>
                          <td style={{ padding: ".55rem .8rem", fontFamily: "var(--font-head)", fontWeight: 700, color: "var(--green)" }}>
                            {((u.total_spent_cents || 0) / 100).toFixed(2)} €
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ─── Répartition requêtes (existant) ─── */}
              {stats && (
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem" }}>
                  <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)", marginBottom: "1rem" }}>
                    🎯 Répartition des requêtes par service
                  </div>
                  {[["check","🔍 Contrôle VIN","var(--blue)",stats.searches_by_type?.check||0],
                    ["buy","🛒 Acheter","var(--green)",stats.searches_by_type?.buy||0],
                    ["sell","💰 Estimer","var(--accent2)",stats.searches_by_type?.sell||0]].map(([type,label,color,count]) => {
                    const total = stats.total_searches || 1;
                    const pct = Math.round((count/total)*100);
                    return (
                      <div key={type} style={{ marginBottom: ".8rem" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: ".25rem" }}>
                          <span style={{ fontSize: ".82rem", fontWeight: 600 }}>{label}</span>
                          <span style={{ fontSize: ".82rem", color, fontWeight: 700 }}>{count.toLocaleString("fr-FR")} ({pct}%)</span>
                        </div>
                        <div style={{ height: 8, background: "var(--deep)", borderRadius: 4, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: pct+"%", background: color, borderRadius: 4, transition: "width .5s" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ─── Indicateurs récents (today/7j) ─── */}
              {stats && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: ".8rem" }}>
                  <div style={{ background: "linear-gradient(135deg, rgba(168,128,58,.05), rgba(168,128,58,0))", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem" }}>
                    <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".25rem" }}>📈 Activité aujourd'hui</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem", marginTop: ".4rem" }}>
                      <div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", fontWeight: 800, color: "var(--accent)", lineHeight: 1 }}>{stats.searches_today || 0}</div>
                        <div style={{ fontSize: ".62rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: ".15rem" }}>Requêtes</div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", fontWeight: 800, color: "var(--green)", lineHeight: 1 }}>{stats.new_users_today || 0}</div>
                        <div style={{ fontSize: ".62rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: ".15rem" }}>Inscrits</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ background: "linear-gradient(135deg, rgba(16,185,129,.05), rgba(16,185,129,0))", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem" }}>
                    <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.3px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".25rem" }}>📊 Activité 7 derniers jours</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: ".5rem", marginTop: ".4rem" }}>
                      <div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.2rem", fontWeight: 800, color: "var(--green)", lineHeight: 1 }}>{stats.searches_7d || 0}</div>
                        <div style={{ fontSize: ".58rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: ".15rem" }}>Requêtes</div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.2rem", fontWeight: 800, color: "#3B82F6", lineHeight: 1 }}>{stats.new_users_7d || 0}</div>
                        <div style={{ fontSize: ".58rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: ".15rem" }}>Inscrits</div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.2rem", fontWeight: 800, color: "var(--accent)", lineHeight: 1 }}>{stats.payments_7d || 0}</div>
                        <div style={{ fontSize: ".58rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: ".15rem" }}>Paiements</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Users ── */}
          {!loading && tab === "users" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".6rem" }}>
                <div style={{ fontSize: ".85rem", color: "var(--sub)" }}>{users.length} utilisateurs enregistrés</div>
                {adminPrivileges.canExportCSV && <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV("users")}>⬇ CSV</button>}
              </div>
              {FilterBar({ fields: ["name","email","siret"], filterOptions: [{val:"all",label:"Tous"},{val:"pro",label:"Pro"},{val:"particulier",label:"Particulier"}] })}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".78rem" }}>
                  <thead>
                    <tr style={{ background: "var(--deep)" }}>
                      {["Nom","Email","Wallet 💎","Type","Offre","SIRET","Rôle","Apporteur","Affilié via","Organisation","Inscrit le","Facturation"].map(h => (
                        <th key={h} style={{ padding: ".5rem .8rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".3px", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filterAndSort(users, ["name","email","siret"]).map((u, i) => {
                      const aff = affiliates.find(a => a.uid === u.uid);
                      const promoteUser = async () => {
                        if (!window.confirm(`Promouvoir ${u.name || u.email} en apporteur d'affaires ?\n\nUn code unique de 5 caractères sera généré automatiquement.`)) return;
                        try {
                          const r = await apiPost("/api/admin/affiliates/create", { uid: u.uid, name: u.name || u.email });
                          if (r && r.success) {
                            alert(`✓ ${u.name || u.email} est désormais apporteur d'affaires.\nCode : ${r.affiliate?.code || "généré"}`);
                            // Refresh la liste des affiliates
                            apiGet("/api/admin/affiliates").then(aff => { if (aff.success) setAffiliates(aff.affiliates || []); }).catch(() => {});
                          } else {
                            alert("Erreur : " + (r?.error || "promotion échouée"));
                          }
                        } catch(e) { alert("Erreur réseau : " + e.message); }
                      };
                      const createOrgForUser = async () => {
                        if (!u.siret) {
                          alert("Cet utilisateur n'a pas de SIRET enregistré. Demandez-lui de compléter son profil d'abord.");
                          return;
                        }
                        // v5 : permettre de choisir un nom personnalisé pour l'organisation
                        // (par défaut on suggère siret_name | name, mais l'admin peut le surcharger).
                        const defaultName = u.siret_name || u.name || u.email || "Organisation";
                        const orgName = window.prompt(
                          "Nom de l'organisation Pro Multi-Agent :\n(visible par tous les membres dans Mon espace)",
                          defaultName
                        );
                        if (orgName === null) return; // annulation
                        const finalName = String(orgName).trim() || defaultName;
                        const maxAgentsStr = window.prompt(
                          `Organisation "${finalName}"\nSIRET : ${u.siret}\n\nNombre max d'agents (3 / 10 / illimité=-1) :`,
                          "3"
                        );
                        if (maxAgentsStr === null) return;
                        const maxAgents = parseInt(maxAgentsStr) || 3;
                        try {
                          const r = await apiPost("/api/admin/org/create-for-user", {
                            user_uid: u.uid,
                            siret: u.siret,
                            company_name: finalName,
                            max_agents: maxAgents
                          });
                          if (r && r.success) {
                            alert("✓ Organisation \"" + finalName + "\" créée pour " + (u.name || u.email) + ".\nMax agents : " + (maxAgents === -1 ? "illimité" : maxAgents));
                            // Si on a créé pour soi-même, rafraîchir userOrg
                            try { window.dispatchEvent(new Event("viq-org-changed")); } catch(_) {}
                            // Rafraîchir la liste users pour mettre à jour la colonne Organisation
                            try { refreshUsers(); } catch(_) {}
                          } else {
                            alert("Erreur : " + (r?.error || "création échouée"));
                          }
                        } catch(e) { alert("Erreur réseau : " + e.message); }
                      };
                      return (
                        <tr key={u.uid} style={{ background: i%2===0?"var(--card)":"transparent", borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: ".5rem .8rem" }}>{u.name || "—"}</td>
                          <td style={{ padding: ".5rem .8rem", fontFamily: "var(--font-mono)", fontSize: ".72rem", color: "var(--sub)" }}>{u.email}</td>
                          <td style={{ padding: ".5rem .8rem", textAlign: "center", color: "var(--accent2)", fontWeight: 700 }}>{u.wallet_balance || 0}</td>
                          <td style={{ padding: ".5rem .8rem" }}><span style={{ fontSize: ".65rem", background: u.is_pro?"rgba(255,184,0,.1)":"rgba(100,100,120,.1)", color: u.is_pro?"var(--accent2)":"var(--sub)", borderRadius: 8, padding: ".1rem .4rem" }}>{u.is_pro?"Pro":"Particulier"}</span></td>
                          {/* Offre tarifaire actuelle (subscription_plan ; défaut = à l'usage) */}
                          <td style={{ padding: ".5rem .8rem", whiteSpace: "nowrap" }}>
                            {editingPlanUid === u.uid ? (
                              <div style={{ display: "flex", gap: ".3rem", alignItems: "center" }}>
                                <select value={planDraft.plan} onChange={e => setPlanDraft(d => ({ ...d, plan: e.target.value }))}
                                  style={{ fontSize: ".68rem", padding: ".2rem .4rem", borderRadius: 6, border: "1px solid var(--border)" }}>
                                  <option value="">À l'usage</option>
                                  <option value="unlimited">Pro Solo</option>
                                  <option value="multi_agent_3">Pro Multi-Agent</option>
                                </select>
                                {planDraft.plan && (
                                  <select value={planDraft.billing_period} onChange={e => setPlanDraft(d => ({ ...d, billing_period: e.target.value }))}
                                    style={{ fontSize: ".68rem", padding: ".2rem .4rem", borderRadius: 6, border: "1px solid var(--border)" }}>
                                    <option value="monthly">Mensuel</option>
                                    <option value="annual">Annuel</option>
                                  </select>
                                )}
                                <button disabled={planSaving} onClick={() => changePlan(u.uid)} style={{ fontSize: ".65rem", fontWeight: 700, background: "var(--accent)", color: "#fff", border: "none", borderRadius: 6, padding: ".2rem .5rem", cursor: "pointer" }}>✓</button>
                                <button disabled={planSaving} onClick={() => setEditingPlanUid(null)} style={{ fontSize: ".65rem", background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: ".2rem .5rem", cursor: "pointer" }}>✕</button>
                              </div>
                            ) : (() => {
                              const plan = String(u.subscription_plan || "").toLowerCase();
                              const isMulti = plan.includes("multi");
                              const isSolo = !isMulti && (plan.includes("solo") || plan.includes("unlimited") || plan.includes("illimit"));
                              const label = isMulti ? "Pro Multi-Agent" : isSolo ? "Pro Solo" : "À l'usage";
                              const col = isMulti ? "var(--accent2)" : isSolo ? "var(--accent)" : "var(--sub)";
                              const bg = isMulti ? "rgba(30,42,74,.10)" : isSolo ? "rgba(168,128,58,.12)" : "var(--bg)";
                              return (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: ".3rem" }}>
                                  <span title={u.subscription_plan || "Paiement à l'usage (pay-as-you-go)"} style={{ fontSize: ".65rem", background: bg, color: col, border: "1px solid var(--border)", borderRadius: 8, padding: ".12rem .45rem", fontWeight: 700 }}>{label}</span>
                                  <button onClick={() => { setPlanDraft({ plan: isMulti ? "multi_agent_3" : isSolo ? "unlimited" : "", billing_period: u.billing_period || "monthly" }); setEditingPlanUid(u.uid); }}
                                    title="Modifier le forfait" style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".68rem", color: "var(--muted)" }}>✏️</button>
                                </span>
                              );
                            })()}
                          </td>
                          <td style={{ padding: ".5rem .8rem", fontFamily: "var(--font-mono)", fontSize: ".7rem", color: "var(--muted)" }}>{u.siret || "—"}</td>
                          {/* v5 : Rôle — seul l'owner peut promouvoir / révoquer */}
                          <td style={{ padding: ".5rem .8rem" }}>
                            {u.is_super_admin ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: ".3rem", alignItems: "flex-start" }}>
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: ".25rem",
                                fontSize: ".62rem",
                                background: "rgba(168,128,58,.12)",
                                color: "var(--accent)",
                                border: "1px solid rgba(168,128,58,.35)",
                                borderRadius: 100, padding: ".18rem .55rem",
                                fontFamily: "var(--font-mono)", fontWeight: 700, letterSpacing: ".5px",
                              }}>
                                👑 Admin
                                {adminPrivileges.isOwner && (
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm(`Révoquer le statut super admin de ${u.name || u.email} ?`)) return;
                                      try {
                                        const r = await apiPost("/api/admin/demote-super-admin", { uid: u.uid });
                                        if (r?.success) { alert("✓ Statut révoqué."); refreshUsers(); }
                                        else alert("Erreur : " + (r?.error || "—"));
                                      } catch(e) { alert("Erreur réseau : " + e.message); }
                                    }}
                                    title="Révoquer le statut super admin"
                                    style={{ background: "transparent", border: "none", color: "var(--accent)", cursor: "pointer", padding: "0 0 0 .2rem", fontSize: ".7rem", lineHeight: 1 }}>
                                    ✕
                                  </button>
                                )}
                              </span>
                              {/* v6 : accès aux sections admin sensibles (Vue globale, Paiements,
                                  Apporteurs, Versements, Analyse consolidée, Dataset) — accordé au
                                  cas par cas par l'owner, indépendamment du statut super admin. */}
                              {adminPrivileges.isOwner ? (
                                <button
                                  onClick={async () => {
                                    const grant = !u.sensitive_admin_access;
                                    const verb = grant ? "accorder" : "révoquer";
                                    if (!window.confirm(`${grant ? "Accorder" : "Révoquer"} l'accès aux sections sensibles (Vue globale, Paiements, Apporteurs, Versements, Analyse consolidée, Dataset) pour ${u.name || u.email} ?`)) return;
                                    try {
                                      const r = await apiPost(`/api/admin/${grant ? "grant" : "revoke"}-sensitive-access`, { uid: u.uid });
                                      if (r?.success) { alert(`✓ Accès sections sensibles ${grant ? "accordé" : "révoqué"}.`); refreshUsers(); }
                                      else alert("Erreur : " + (r?.error || "—"));
                                    } catch(e) { alert("Erreur réseau : " + e.message); }
                                  }}
                                  title={u.sensitive_admin_access ? "Cliquer pour révoquer l'accès aux sections sensibles" : "Cliquer pour accorder l'accès aux sections sensibles"}
                                  style={{
                                    fontSize: ".6rem", fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-body)",
                                    borderRadius: 6, padding: ".18rem .5rem",
                                    background: u.sensitive_admin_access ? "rgba(46,111,92,.1)" : "var(--card)",
                                    color: u.sensitive_admin_access ? "var(--green)" : "var(--sub)",
                                    border: u.sensitive_admin_access ? "1px solid rgba(46,111,92,.35)" : "1px solid var(--border)",
                                  }}>
                                  {u.sensitive_admin_access ? "🔓 Sections sensibles" : "🔒 Sections sensibles"}
                                </button>
                              ) : u.sensitive_admin_access ? (
                                <span style={{ fontSize: ".6rem", color: "var(--green)" }}>🔓 Sections sensibles</span>
                              ) : null}
                              </div>
                            ) : adminPrivileges.isOwner ? (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Promouvoir ${u.name || u.email} en super admin ?\n\nIl aura accès au dashboard admin MAIS pas aux exports CSV (réservés au propriétaire).`)) return;
                                  try {
                                    const r = await apiPost("/api/admin/promote-super-admin", { uid: u.uid });
                                    if (r?.success) { alert("✓ " + u.email + " est désormais super admin."); refreshUsers(); }
                                    else alert("Erreur : " + (r?.error || "—"));
                                  } catch(e) { alert("Erreur réseau : " + e.message); }
                                }}
                                title="Promouvoir en super admin (sans accès export CSV)"
                                style={{ fontSize: ".62rem", background: "rgba(168,128,58,.08)", color: "var(--accent)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 6, padding: ".2rem .55rem", cursor: "pointer", fontWeight: 600, fontFamily: "var(--font-body)" }}
                                onMouseEnter={e => { e.currentTarget.style.background = "rgba(168,128,58,.18)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "rgba(168,128,58,.08)"; }}>
                                👑 Promouvoir
                              </button>
                            ) : (
                              <span style={{ fontSize: ".65rem", color: "var(--muted)", fontStyle: "italic" }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: ".5rem .8rem" }}>
                            {aff ? (
                              <span title={`Code apporteur : ${aff.code}\nVentes : ${aff.sales_count || 0}\nCA : ${((aff.ca_total_cents||0)/100).toFixed(2)} €`}
                                style={{ fontSize: ".65rem", background: "rgba(0,200,100,.12)", color: "var(--green)", border: "1px solid rgba(0,200,100,.3)", borderRadius: 8, padding: ".15rem .5rem", fontFamily: "var(--font-mono)", letterSpacing: "1px", fontWeight: 700 }}>
                                🤝 {aff.code}
                              </span>
                            ) : sensitiveAccess ? (
                              <button onClick={promoteUser} title="Attribuer le rôle d'apporteur d'affaires"
                                style={{ fontSize: ".62rem", background: "rgba(168,128,58,.08)", color: "var(--accent)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 6, padding: ".2rem .55rem", cursor: "pointer", fontWeight: 600, fontFamily: "var(--font-body)" }}
                                onMouseEnter={e => { e.currentTarget.style.background = "rgba(168,128,58,.18)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "rgba(168,128,58,.08)"; }}>
                                + Promouvoir
                              </button>
                            ) : (
                              <span title="Accès réservé au super admin" style={{ fontSize: ".65rem", color: "var(--muted)", fontStyle: "italic" }}>🔒 —</span>
                            )}
                          </td>
                          {/* Affilié via — code de l'apporteur d'affaires auquel ce client est rattaché */}
                          <td style={{ padding: ".5rem .8rem" }}>
                            {u.referred_by_affiliate ? (() => {
                              const refAff = affiliates.find(a => String(a.code).toUpperCase() === String(u.referred_by_affiliate).toUpperCase());
                              return (
                                <span title={refAff ? `Apporteur : ${refAff.user_name || refAff.email || refAff.code}` : "Code apporteur de rattachement"}
                                  style={{ fontSize: ".65rem", background: "rgba(168,128,58,.10)", color: "var(--accent)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 8, padding: ".15rem .5rem", fontFamily: "var(--font-mono)", letterSpacing: "1px", fontWeight: 700 }}>
                                  🔗 {u.referred_by_affiliate}
                                </span>
                              );
                            })() : <span style={{ color: "var(--muted)" }}>—</span>}
                          </td>
                          {/* v5 : colonne Organisation — distinguer "+Activer org" (bleu) vs "Active" (vert) */}
                          <td style={{ padding: ".5rem .8rem" }}>
                            {u.is_org_owner ? (
                              <span title={u.organization_name ? `Organisation : ${u.organization_name}` : "Propriétaire d'une organisation"}
                                style={{ fontSize: ".65rem", background: "rgba(46,111,92,.12)", color: "var(--green)", border: "1px solid rgba(46,111,92,.35)", borderRadius: 8, padding: ".15rem .5rem", fontFamily: "var(--font-mono)", fontWeight: 700, letterSpacing: ".3px", display: "inline-flex", alignItems: "center", gap: ".3rem" }}>
                                ✓ Organisation active
                              </span>
                            ) : u.is_org_member ? (
                              <span title="Membre d'une organisation"
                                style={{ fontSize: ".65rem", background: "rgba(46,111,92,.06)", color: "var(--green)", border: "1px solid rgba(46,111,92,.25)", borderRadius: 8, padding: ".15rem .5rem", fontFamily: "var(--font-mono)", fontWeight: 600 }}>
                                ◇ Membre d'org
                              </span>
                            ) : u.siret ? (
                              <button onClick={createOrgForUser} title="Convertir en organisation (Pro Multi-Agent)"
                                style={{ fontSize: ".62rem", background: "var(--accent2-soft)", color: "var(--accent2)", border: "1px solid var(--accent2)", borderRadius: 6, padding: ".2rem .55rem", cursor: "pointer", fontWeight: 600, fontFamily: "var(--font-body)" }}>
                                + Activer org
                              </button>
                            ) : <span style={{ fontSize: ".65rem", color: "var(--muted)", fontStyle: "italic" }}>—</span>}
                          </td>
                          <td style={{ padding: ".5rem .8rem", color: "var(--muted)", fontSize: ".7rem" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                          <td style={{ padding: ".5rem .8rem" }}>
                            <button onClick={() => setQuoteTarget({ email: u.email, name: u.name })}
                              style={{ fontSize: ".68rem", fontWeight: 700, background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: ".2rem .5rem", cursor: "pointer", color: "var(--accent)", whiteSpace: "nowrap" }}>
                              📧 Devis
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Searches ── */}
          {!loading && tab === "searches" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".8rem", flexWrap:"wrap", gap:".6rem" }}>
                <div style={{ fontSize: ".85rem", color: "var(--sub)" }}>{searches.length} requête{searches.length>1?"s":""} enregistrée{searches.length>1?"s":""}</div>
                {adminPrivileges.canExportCSV && <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV("searches")}>⬇ Export CSV</button>}
              </div>
              {FilterBar({ fields: ["user_uid","type","email","name"], filterOptions: [
                {val:"all",label:"Tout"},{val:"sell",label:"💰 Cote"},{val:"buy",label:"🛒 Achat"},{val:"check",label:"🔍 Contrôle"}
              ] })}
              {(() => {
                // Helpers de présentation — rendre chaque requête lisible & actionnable
                const usersByUid = {};
                (users||[]).forEach(u => { if (u.uid) usersByUid[u.uid] = u; });
                const TYPE_META = {
                  sell:  { icon:"💰", label:"Cote", color:"var(--accent2)" },
                  buy:   { icon:"🛒", label:"Recommandation achat", color:"var(--green)" },
                  check: { icon:"🔍", label:"Contrôle véhicule", color:"var(--blue)" },
                };
                const fmtDate = (d) => d ? new Date(d).toLocaleString("fr-FR",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—";
                // Extrait les champs clés d'une requête en libellés lisibles
                const summarize = (inp, type) => {
                  if (!inp || typeof inp !== "object") return [];
                  const rows = [];
                  const push = (k,v) => { if (v !== undefined && v !== null && String(v).trim() !== "") rows.push([k, String(v)]); };
                  const veh = [inp.make, inp.model].filter(Boolean).join(" ").trim();
                  push("Véhicule", veh);
                  push("Plaque / VIN", inp.plaque || inp.vin);
                  push("Année", inp.year_sell || inp.year);
                  push("Kilométrage", (inp.km_sell || inp.km) ? Number(String(inp.km_sell||inp.km).replace(/\D/g,"")).toLocaleString("fr-FR")+" km" : "");
                  push("Motorisation", inp.fuel_sell || inp.fuel);
                  push("Boîte", inp.gearbox_sell || inp.gearbox);
                  push("État", inp.state);
                  push("Budget", inp.budget ? Number(inp.budget).toLocaleString("fr-FR")+" €" : "");
                  push("Usage", inp.usage);
                  push("Ville", inp.city || inp.ville);
                  return rows;
                };
                const list = filterAndSort(searches, ["user_uid","type","email","name"]).slice(0, 150);
                if (list.length === 0) return (
                  <div style={{ textAlign:"center", padding:"2.5rem", color:"var(--sub)" }}>
                    <div style={{ fontSize:"2rem", marginBottom:".6rem" }}>🔍</div>
                    <div>Aucune requête ne correspond au filtre</div>
                  </div>
                );
                return (
                  <div style={{ display:"flex", flexDirection:"column", gap:".7rem" }}>
                    {list.map((s) => {
                      const meta = TYPE_META[s.type] || { icon:"•", label:s.type, color:"var(--sub)" };
                      const inp = typeof s.input === "object" ? s.input : (() => { try { return JSON.parse(s.input||"{}"); } catch(e) { return {}; } })();
                      const u = s.user_uid ? usersByUid[s.user_uid] : null;
                      const email = u?.email || s.user_email || s.email || "";
                      const name = u?.name || s.name || "";
                      const phone = (u?.phone || s.phone || "").trim();
                      const rows = summarize(inp, s.type);
                      // Détails de prix de l'estimation (result loggé côté backend pour type=sell)
                      const resObj = typeof s.result === "object" && s.result ? s.result : (() => { try { return JSON.parse(s.result || "{}"); } catch (e) { return {}; } })();
                      const eurFmt = (v) => (v || v === 0) ? Number(v).toLocaleString("fr-FR") + " €" : null;
                      const priceRows = [];
                      if (resObj && (resObj.prix_viq || resObj.argus_low || resObj.argus_high)) {
                        if (resObj.prix_viq) priceRows.push(["Prix VIQ.AI®", eurFmt(resObj.prix_viq)]);
                        if (resObj.argus_low && resObj.argus_high) priceRows.push(["Fourchette marché", eurFmt(resObj.argus_low) + " – " + eurFmt(resObj.argus_high)]);
                        else if (resObj.argus_mid) priceRows.push(["Valeur marché", eurFmt(resObj.argus_mid)]);
                        if (resObj.conseil_prix) priceRows.push(["Conseil prix", eurFmt(resObj.conseil_prix)]);
                        if (resObj.score_vente) priceRows.push(["Score vente", resObj.score_vente + "/100"]);
                      }
                      const veh = [inp.make, inp.model].filter(Boolean).join(" ").trim() || "véhicule";
                      const contact = () => {
                        if (!email) { alert("Aucun email connu pour cet utilisateur (UID : " + (s.user_uid||"inconnu") + ")."); return; }
                        const subject = encodeURIComponent("VehicleIQ — Votre " + meta.label.toLowerCase() + (veh!=="véhicule" ? " : " + veh : ""));
                        const body = encodeURIComponent(
                          "Bonjour" + (name ? " " + name : "") + ",\n\n" +
                          "Suite à votre requête \"" + meta.label + "\" du " + fmtDate(s.created_at) + " sur VehicleIQ, nous revenons vers vous.\n\n" +
                          "=== RÉCAPITULATIF DE VOTRE REQUÊTE ===\n" +
                          (rows.length ? rows.map(([k,v]) => k + " : " + v).join("\n") : "—") +
                          "\n\n" +
                          "Notre équipe reste à votre disposition.\n\nCordialement,\nL'équipe VehicleIQ\ncontact@vehicleiq.fr"
                        );
                        window.location.href = "mailto:" + email + "?cc=contact@vehicleiq.fr&subject=" + subject + "&body=" + body;
                      };
                      return (
                        <div key={s.id} style={{ background:"var(--card)", border:"1.5px solid var(--border)", borderLeft:"3px solid "+meta.color, borderRadius:8, padding:".85rem 1rem" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:".8rem", flexWrap:"wrap", marginBottom:".5rem" }}>
                            <div style={{ minWidth:0 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:".5rem", flexWrap:"wrap" }}>
                                <span style={{ fontSize:".82rem", fontWeight:700, color:meta.color }}>{meta.icon} {meta.label}</span>
                                <span style={{ fontSize:".7rem", color:"var(--muted)", fontFamily:"var(--font-mono)" }}>#{String(s.id).slice(0,8)}</span>
                              </div>
                              <div style={{ fontSize:".72rem", color:"var(--sub)", marginTop:".25rem" }}>
                                {name && <span style={{ fontWeight:600, color:"var(--text)" }}>{name} · </span>}
                                {email
                                  ? <span style={{ fontFamily:"var(--font-mono)" }}>{email}</span>
                                  : <span style={{ color:"var(--muted)", fontStyle:"italic" }}>email inconnu</span>}
                                <span style={{ color:"var(--muted)" }}> · {fmtDate(s.created_at)}</span>
                              </div>
                            </div>
                            <div style={{ display:"flex", gap:".4rem", flexShrink:0 }}>
                              {phone && (
                                <a href={"tel:" + phone.replace(/[\s.]/g, "")} title={"Appeler " + phone}
                                  className="btn btn-ghost btn-sm"
                                  style={{ fontSize:".72rem", whiteSpace:"nowrap", textDecoration:"none", display:"inline-flex", alignItems:"center", gap:".3rem" }}>
                                  📞 {phone}
                                </a>
                              )}
                              <button className="btn btn-ghost btn-sm" style={{ fontSize:".72rem", whiteSpace:"nowrap" }}
                                onClick={() => exportEntryToPDF({ type: s.type, results: resObj, result: resObj, data: inp, answers: inp })
                                  .catch(err => alert("Export PDF échoué : " + err.message))}
                                title="Télécharger le PDF de cette requête — identique à celui généré pour l'utilisateur">
                                📄 PDF
                              </button>
                              <button className="btn btn-accent btn-sm" style={{ fontSize:".72rem", whiteSpace:"nowrap" }}
                                onClick={contact} disabled={!email} title={email ? "Envoyer un email à "+email : "Email inconnu"}>
                                ✉️ Contacter
                              </button>
                            </div>
                          </div>
                          {rows.length > 0 && (
                            <div style={{ display:"flex", flexWrap:"wrap", gap:".3rem .5rem", marginTop:".4rem", paddingTop:".5rem", borderTop:"1px dashed var(--border)" }}>
                              {rows.map(([k,v],idx) => (
                                <span key={idx} style={{ fontSize:".68rem", background:"var(--deep)", border:"1.5px solid var(--border)", borderRadius:5, padding:".18rem .5rem", color:"var(--sub)" }}>
                                  <span style={{ color:"var(--muted)" }}>{k}</span> <span style={{ color:"var(--text)", fontWeight:600 }}>{v}</span>
                                </span>
                              ))}
                            </div>
                          )}
                          {/* Détails de prix de l'estimation (résultat VIQ.AI® loggé) */}
                          {priceRows.length > 0 && (
                            <div style={{ display:"flex", flexWrap:"wrap", gap:".3rem .5rem", marginTop:".45rem" }}>
                              {priceRows.map(([k,v],idx) => (
                                <span key={"pr"+idx} style={{ fontSize:".68rem", background:"rgba(46,111,92,.08)", border:"1.5px solid rgba(46,111,92,.3)", borderRadius:5, padding:".18rem .5rem" }}>
                                  <span style={{ color:"var(--muted)" }}>{k}</span> <span style={{ color:"var(--green)", fontWeight:700 }}>{v}</span>
                                </span>
                              ))}
                            </div>
                          )}
                          {s.user_uid && (
                            <div style={{ fontSize:".62rem", color:"var(--muted)", fontFamily:"var(--font-mono)", marginTop:".45rem" }}>
                              UID : {s.user_uid}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {searches.length > 150 && (
                      <div style={{ textAlign:"center", fontSize:".72rem", color:"var(--muted)", padding:".6rem" }}>
                        150 requêtes les plus récentes affichées · {searches.length} au total · export CSV pour la liste complète
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Affiliates ── */}
          {!loading && tab === "affiliates" && !sensitiveAccess && <RestrictedTabPlaceholder label="Apporteurs" icon="🤝" />}
          {!loading && tab === "affiliates" && sensitiveAccess && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <div style={{ fontSize: ".85rem", color: "var(--sub)" }}>{affiliates.length} apporteur{affiliates.length > 1?"s":""} d'affaires</div>

              </div>
              {affiliates.length === 0 ? (
                <div style={{ textAlign: "center", padding: "2rem", color: "var(--sub)" }}>
                  <div style={{ fontSize: "2rem", marginBottom: ".8rem" }}>🤝</div>
                  <div>Aucun apporteur d'affaires enregistré</div>
                  <div style={{ fontSize: ".78rem", color: "var(--muted)", marginTop: ".5rem" }}>Les apporteurs s'inscrivent via leur espace Mon profil</div>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".78rem" }}>
                    <thead>
                      <tr style={{ background: "var(--deep)" }}>
                        {["Code","Nom","Email","Ventes","CA apporté","Commission","RIB","Créé le"].map(h => (
                          <th key={h} style={{ padding: ".5rem .8rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".3px", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {affiliates.map((a, i) => (
                        <tr key={a.code} style={{ background: i%2===0?"var(--card)":"transparent", borderBottom: "1px solid var(--border)" }}>
                          <td style={{ padding: ".5rem .8rem", fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--accent)", letterSpacing: "2px" }}>{a.code}</td>
                          <td style={{ padding: ".5rem .8rem", fontWeight: 600 }}>{a.user_name || "—"}</td>
                          <td style={{ padding: ".5rem .8rem", fontFamily: "var(--font-mono)", fontSize: ".7rem", color: "var(--sub)" }}>{a.email}</td>
                          <td style={{ padding: ".5rem .8rem", textAlign: "center", color: "var(--accent2)", fontWeight: 700 }}>{a.sales_count || 0}</td>
                          <td style={{ padding: ".5rem .8rem", color: "var(--green)", fontWeight: 700 }}>{((a.ca_total_cents||0)/100).toFixed(2)} €</td>
                          <td style={{ padding: ".5rem .8rem", color: "var(--accent2)", fontWeight: 700 }}>{((a.commission_total||0)/100).toFixed(2)} €</td>
                          <td style={{ padding: ".5rem .8rem", fontSize: ".7rem", whiteSpace: "nowrap" }}>
                            {a.rib_iban ? (
                              <span title={`Titulaire : ${a.rib_titulaire || "—"}\nIBAN : ${a.rib_iban}\nBIC : ${a.rib_bic || "—"}`}
                                style={{ fontFamily: "var(--font-mono)", color: "var(--text)", cursor: "help" }}>
                                💳 {a.rib_iban.length > 12 ? a.rib_iban.slice(0, 8) + "…" + a.rib_iban.slice(-4) : a.rib_iban}
                              </span>
                            ) : <span style={{ color: "var(--muted)" }}>—</span>}
                          </td>
                          <td style={{ padding: ".5rem .8rem", color: "var(--muted)", fontSize: ".7rem" }}>{a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Payments ── */}
          {!loading && tab === "payments" && !sensitiveAccess && <RestrictedTabPlaceholder label="Paiements" icon="💳" />}
          {!loading && tab === "payments" && sensitiveAccess && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".6rem" }}>
                <div style={{ fontSize: ".85rem", color: "var(--sub)" }}>{payments.length} paiements · CA total : <strong style={{ color: "var(--green)" }}>{(payments.reduce((a,p) => a + (p.amount_cents||0), 0)/100).toFixed(2)} €</strong></div>
                {adminPrivileges.canExportCSV && <button className="btn btn-ghost btn-sm" onClick={() => downloadCSV("payments")}>⬇ CSV</button>}
              </div>
              {FilterBar({ fields: ["user_uid","plan_id","email","name"], filterOptions: [] })}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".78rem" }}>
                  <thead>
                    <tr style={{ background: "var(--deep)" }}>
                      {["Utilisateur","Type","Plan","Montant","Statut","Date"].map(h => (
                        <th key={h} style={{ padding: ".5rem .8rem", textAlign: "left", color: "var(--sub)", fontWeight: 600, fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".3px" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filterAndSort(payments, ["user_uid","plan_id","email","name"]).map((p, i) => (
                      <tr key={p.id} style={{ background: i%2===0?"var(--card)":"transparent", borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: ".5rem .8rem" }} title={"UID : " + (p.user_uid || "—") + (p.phone ? "\nTél : " + p.phone : "") + (p.siret ? "\nSIRET : " + p.siret : "")}>
                          <div style={{ fontWeight: 600, color: "var(--text)", fontSize: ".75rem" }}>{p.name || "—"}</div>
                          <div style={{ fontFamily: "var(--font-mono)", fontSize: ".66rem", color: "var(--sub)" }}>{p.email || (p.user_uid ? p.user_uid.slice(0,12)+"…" : "—")}</div>
                        </td>
                        <td style={{ padding: ".5rem .8rem" }}>
                          <span style={{ fontSize: ".63rem", background: p.is_pro ? "rgba(255,184,0,.1)" : "rgba(100,100,120,.1)", color: p.is_pro ? "var(--accent2)" : "var(--sub)", borderRadius: 8, padding: ".1rem .4rem" }}>{p.is_pro ? "Pro" : "Part."}</span>
                        </td>
                        <td style={{ padding: ".5rem .8rem", fontSize: ".75rem" }}>{p.plan_id || "—"}</td>
                        <td style={{ padding: ".5rem .8rem", fontWeight: 700, color: p.amount_cents > 0 ? "var(--green)" : "var(--red)" }}>{p.amount_cents ? (p.amount_cents/100).toFixed(2)+" €" : "—"}</td>
                        <td style={{ padding: ".5rem .8rem" }}><span style={{ fontSize: ".65rem", background: "rgba(0,200,100,.1)", color: "var(--green)", borderRadius: 8, padding: ".1rem .4rem" }}>Confirmé</span></td>
                        <td style={{ padding: ".5rem .8rem", color: "var(--muted)", fontSize: ".7rem", whiteSpace: "nowrap" }}>{p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR", {day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ─── VERSEMENTS DE COMMISSIONS (admin) ─── */}
          {!loading && tab === "payouts" && !sensitiveAccess && <RestrictedTabPlaceholder label="Versements" icon="💸" />}
          {!loading && tab === "payouts" && sensitiveAccess && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: ".8rem", flexWrap: "wrap", gap: ".5rem" }}>
                <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>💸 Versements de commissions</div>
                <div style={{ fontSize: ".7rem", color: "var(--muted)", fontStyle: "italic" }}>Versement en début de mois suivant · marquez chaque mois payé une fois le virement émis</div>
              </div>

              {adminPayouts === null ? (
                <div style={{ color: "var(--sub)", fontSize: ".85rem", padding: "1rem" }}>Chargement…</div>
              ) : adminPayouts.length === 0 ? (
                <div style={{ background: "var(--card)", border: "1.5px dashed var(--border)", borderRadius: 10, padding: "1.6rem", textAlign: "center", color: "var(--sub)", fontSize: ".85rem" }}>
                  Aucun apporteur avec des ventes pour l'instant.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem" }}>
                  {adminPayouts.map(aff => (
                    <div key={aff.code} style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1rem 1.2rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: ".5rem", marginBottom: ".7rem" }}>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: ".95rem", color: "var(--text)" }}>{aff.name}</span>
                          <span style={{ marginLeft: ".5rem", fontSize: ".68rem", fontFamily: "var(--font-mono)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 6, padding: ".1rem .45rem", color: "var(--accent)", letterSpacing: "1px" }}>{aff.code}</span>
                        </div>
                        <div style={{ display: "flex", gap: "1rem", fontSize: ".74rem" }}>
                          <span style={{ color: "var(--accent)" }}>⏳ {(aff.pending_cents/100).toFixed(2)} € en attente</span>
                          <span style={{ color: "var(--green)" }}>✅ {(aff.paid_cents/100).toFixed(2)} € versé</span>
                        </div>
                      </div>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".78rem" }}>
                        <thead>
                          <tr style={{ background: "var(--deep)" }}>
                            {["Mois","Ventes","Commissions","Bonus","Total dû","Statut","Action"].map(h => (
                              <th key={h} style={{ padding: ".45rem .6rem", textAlign: h==="Mois"||h==="Statut"||h==="Action"?"left":"right", color: "var(--sub)", fontWeight: 600, fontSize: ".66rem", textTransform: "uppercase", letterSpacing: ".3px" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {aff.months.length === 0 ? (
                            <tr><td colSpan={7} style={{ padding: ".6rem", color: "var(--muted)", fontStyle: "italic" }}>Aucune commission.</td></tr>
                          ) : aff.months.map((m,i) => {
                            const paid = m.status === "paid";
                            const [y,mo] = m.period.split("-");
                            const label = new Date(parseInt(y), parseInt(mo)-1, 1).toLocaleDateString("fr-FR", { month: "short", year: "numeric" });
                            const key = aff.code + ":" + m.period;
                            return (
                              <tr key={m.period} style={{ borderBottom: "1px solid var(--border)", background: i%2?"var(--card)":"transparent" }}>
                                <td style={{ padding: ".45rem .6rem", fontWeight: 600, textTransform: "capitalize" }}>{label}</td>
                                <td style={{ padding: ".45rem .6rem", textAlign: "right", color: "var(--sub)" }}>{m.sales_count}</td>
                                <td style={{ padding: ".45rem .6rem", textAlign: "right" }}>{(m.commission_cents/100).toFixed(2)} €</td>
                                <td style={{ padding: ".45rem .6rem", textAlign: "right", color: m.bonus_cents>0?"var(--green)":"var(--muted)" }}>{m.bonus_cents>0?"+"+(m.bonus_cents/100).toFixed(2):"—"}</td>
                                <td style={{ padding: ".45rem .6rem", textAlign: "right", fontWeight: 800, fontFamily: "var(--font-head)" }}>{(m.due_cents/100).toFixed(2)} €</td>
                                <td style={{ padding: ".45rem .6rem" }}>
                                  {paid
                                    ? <span style={{ fontSize: ".66rem", fontWeight: 700, color: "var(--green)" }}>✓ Versé{m.paid_at?" "+new Date(m.paid_at).toLocaleDateString("fr-FR",{day:"2-digit",month:"2-digit"}):""}</span>
                                    : <span style={{ fontSize: ".66rem", fontWeight: 700, color: "var(--accent)" }}>⏳ En attente</span>}
                                </td>
                                <td style={{ padding: ".45rem .6rem" }}>
                                  <button disabled={payoutBusy===key} onClick={() => markPayout(aff.code, m.period, paid ? "pending" : "paid")}
                                    style={{ fontSize: ".66rem", fontWeight: 700, padding: ".2rem .6rem", borderRadius: 6, cursor: payoutBusy===key?"wait":"pointer",
                                      border: "1px solid " + (paid ? "var(--border)" : "var(--green)"),
                                      background: paid ? "var(--card)" : "var(--green)", color: paid ? "var(--sub)" : "#fff", opacity: payoutBusy===key?.6:1 }}>
                                    {payoutBusy===key ? "…" : paid ? "Annuler" : "Marquer versé"}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ─── ANALYSE CONSOLIDÉE (admin / plateforme) ─── */}
          {!loading && tab === "consolidated" && !sensitiveAccess && <RestrictedTabPlaceholder label="Analyse consolidée" icon="📈" />}
          {!loading && tab === "consolidated" && sensitiveAccess && (() => {
            const followupList = (followups || []).filter(f => f && f.sold_price && f.sold_date);
            if (followupList.length === 0) {
              return (
                <div style={{ padding: "3rem 1.5rem", textAlign: "center", background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 12 }}>
                  <div style={{ fontSize: "2.5rem", marginBottom: ".5rem" }}>📊</div>
                  <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "var(--text)", marginBottom: ".4rem" }}>Aucune vente enregistrée sur la plateforme</div>
                  <div style={{ fontSize: ".82rem", color: "var(--sub)", maxWidth: 480, margin: "0 auto", lineHeight: 1.5 }}>
                    Les analyses consolidées apparaîtront ici dès que les utilisateurs Pro auront enregistré des prix de vente réels dans leur suivi de vente.
                  </div>
                </div>
              );
            }

            // 12 derniers mois
            const now = new Date();
            const months = [];
            for (let i = 11; i >= 0; i--) {
              const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
              months.push({
                key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
                label: d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" }).replace(".", ""),
                date: d,
              });
            }

            // ── Segments de prix : 4 tranches (une barre par tranche, chaque mois) ──
            const PRICE_TIERS = [
              { label: "< 15 k€",  test: p => p < 15000 },
              { label: "15–35 k€", test: p => p >= 15000 && p < 35000 },
              { label: "35–80 k€", test: p => p >= 35000 && p < 80000 },
              { label: "80–150 k€ · Premium",       test: p => p >= 80000 && p < 150000 },
              { label: "150–225 k€ · Ultra premium", test: p => p >= 150000 && p < 225000 },
              { label: "> 225 k€ · Rares",           test: p => p >= 225000 },
            ];
            const priceTierIdx = (p) => {
              const v = Number(p) || 0;
              for (let i = 0; i < PRICE_TIERS.length; i++) if (PRICE_TIERS[i].test(v)) return i;
              return PRICE_TIERS.length - 1;
            };

            const clientSeg = f => {
              const ct = f.client_type || "unknown";
              if (ct === "pro") return "Pro";
              if (ct === "particulier") return "Particulier";
              return "Non précisé";
            };
            const pickupSeg = f => {
              if (f.pickup_method === "concession") return "En concession";
              if (f.pickup_method === "expedition") return "Expédition / Livraison";
              return "Non précisé";
            };
            const platformSeg = f => {
              const p = (f.platform || "").trim();
              return p || "Non précisé";
            };

            // Agrégation par mois × segment × tranche de prix : { seg: { monthKey: [t0,t1,t2,t3] } }
            const aggregateByPrice = (segmentFn) => {
              const data = {};
              followupList.forEach(f => {
                const d = new Date(f.sold_date);
                if (isNaN(d.getTime())) return;
                const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                const seg = segmentFn(f);
                if (!seg) return;
                const ti = priceTierIdx(f.sold_price);
                if (!data[seg]) data[seg] = {};
                if (!data[seg][mk]) data[seg][mk] = new Array(PRICE_TIERS.length).fill(0);
                data[seg][mk][ti] += 1;
              });
              return data;
            };

            const byClient = aggregateByPrice(clientSeg);
            const byPickup = aggregateByPrice(pickupSeg);
            const byPlatform = aggregateByPrice(platformSeg);

            const platformTotals = Object.entries(byPlatform).map(([k, mm]) => {
              let t = 0; Object.values(mm).forEach(arr => arr.forEach(n => t += n)); return [k, t];
            });
            platformTotals.sort((a, b) => b[1] - a[1]);
            const topPlatforms = platformTotals.slice(0, 6).map(([k]) => k);
            const byPlatformTop = {};
            Object.entries(byPlatform).forEach(([seg, monthMap]) => {
              const key = topPlatforms.includes(seg) ? seg : "Autres";
              if (!byPlatformTop[key]) byPlatformTop[key] = {};
              Object.entries(monthMap).forEach(([mk, arr]) => {
                if (!byPlatformTop[key][mk]) byPlatformTop[key][mk] = new Array(PRICE_TIERS.length).fill(0);
                for (let i = 0; i < PRICE_TIERS.length; i++) byPlatformTop[key][mk][i] += arr[i] || 0;
              });
            });

            // KPIs plateforme
            const totalVentes = followupList.length;
            const last3MonthKeys = months.slice(-3).map(m => m.key);
            const ventesRecentes = followupList.filter(f => {
              const d = new Date(f.sold_date);
              const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
              return last3MonthKeys.includes(mk);
            }).length;
            const totalCA = followupList.reduce((s, f) => s + (Number(f.sold_price) || 0), 0);
            const uniqueUsers = new Set(followupList.map(f => f.user_uid).filter(Boolean)).size;

            const COLORS = ["#A8803A", "#1F5AE6", "#2E6F5C", "#9B2C2C", "#6E6E78", "#C9A96E", "#5A5A66", "#3A3A45"];

            // ── une barre par tranche de prix et par mois, empilée par segment ──
            const GroupedPriceBarChart = ({ title, sub, data, height = 220 }) => {
              const segments = Object.keys(data);
              const chartH = height;
              const N = PRICE_TIERS.length;
              const GROUP_W = 64;
              const barW = 7, gap = 2;
              const groupInner = N * barW + (N - 1) * gap;
              const padLeft = (GROUP_W - groupInner) / 2;
              const vbW = months.length * GROUP_W;

              let maxVal = 0;
              months.forEach(m => {
                for (let t = 0; t < N; t++) {
                  let sum = 0;
                  segments.forEach(seg => { sum += (data[seg]?.[m.key]?.[t] || 0); });
                  if (sum > maxVal) maxVal = sum;
                }
              });
              maxVal = Math.max(1, maxVal);

              return (
                <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 12, padding: "1.2rem 1.4rem", marginBottom: "1rem" }}>
                  <div style={{ marginBottom: ".4rem" }}>
                    <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-head)" }}>{title}</div>
                    <div style={{ fontSize: ".72rem", color: "var(--sub)", marginTop: ".15rem" }}>{sub}</div>
                  </div>
                  {/* Légende segments */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem .9rem", margin: ".5rem 0 .4rem", fontSize: ".7rem" }}>
                    {segments.map((seg, i) => (
                      <div key={seg} style={{ display: "flex", alignItems: "center", gap: ".3rem", color: "var(--sub)" }}>
                        <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
                        <span>{seg}</span>
                      </div>
                    ))}
                  </div>
                  {/* Légende tranches de prix */}
                  <div style={{ fontSize: ".66rem", color: "var(--muted)", marginBottom: ".7rem" }}>
                    ${PRICE_TIERS.length} barres / mois (gauche → droite) : {PRICE_TIERS.map((pt, i) => `${i + 1}·${pt.label}`).join("   ")}
                  </div>
                  <div style={{ width: "100%", height: chartH + 44, position: "relative" }}>
                    <svg viewBox={`0 0 ${vbW} ${chartH + 40}`} preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
                      {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
                        <line key={i} x1="0" y1={chartH * (1 - t)} x2={vbW} y2={chartH * (1 - t)}
                          stroke="#E5E5EA" strokeWidth="0.5" strokeDasharray="2,3" />
                      ))}
                      {months.map((m, mi) => {
                        const gx = mi * GROUP_W;
                        return (
                          <g key={m.key}>
                            {PRICE_TIERS.map((pt, ti) => {
                              const bx = gx + padLeft + ti * (barW + gap);
                              let yOffset = chartH;
                              const stack = segments.map((seg, si) => {
                                const v = data[seg]?.[m.key]?.[ti] || 0;
                                const h = (v / maxVal) * chartH;
                                yOffset -= h;
                                return { seg, v, h, y: yOffset, color: COLORS[si % COLORS.length] };
                              });
                              const tierTotal = stack.reduce((s, b) => s + b.v, 0);
                              const stackH = stack.reduce((s, b) => s + b.h, 0);
                              return (
                                <g key={ti}>
                                  {stack.map((b, bi) => b.h > 0 && (
                                    <rect key={bi} x={bx} y={b.y} width={barW} height={b.h} fill={b.color} opacity={0.9}>
                                      <title>{b.seg} · {pt.label} — {m.label} : {b.v}</title>
                                    </rect>
                                  ))}
                                  {tierTotal > 0 && (
                                    <text x={bx + barW / 2} y={chartH - stackH - 3}
                                      textAnchor="middle" fontSize="7" fill="var(--text)" fontWeight="700">{tierTotal}</text>
                                  )}
                                  <text x={bx + barW / 2} y={chartH + 10}
                                    textAnchor="middle" fontSize="6" fill="var(--muted)">{ti + 1}</text>
                                </g>
                              );
                            })}
                            <text x={gx + GROUP_W / 2} y={chartH + 24}
                              textAnchor="middle" fontSize="9" fill="var(--sub)">{m.label}</text>
                          </g>
                        );
                      })}
                      <line x1="0" y1={chartH} x2={vbW} y2={chartH} stroke="var(--border)" strokeWidth="1" />
                    </svg>
                  </div>
                </div>
              );
            };

            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {/* En-tête */}
                <div>
                  <h2 style={{ fontSize: "1.1rem", margin: 0, fontFamily: "var(--font-head)", fontWeight: 700, color: "var(--text)" }}>Analyse consolidée plateforme</h2>
                  <div style={{ fontSize: ".78rem", color: "var(--sub)", marginTop: ".2rem" }}>
                    Vue à l'échelle de la plateforme — 12 derniers mois, croisée par client × méthode de retrait × plateforme.
                  </div>
                </div>

                {/* KPIs plateforme */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: ".7rem" }}>
                  {[
                    { label: "Ventes plateforme", value: totalVentes, sub: "Toutes périodes" },
                    { label: "Ventes 3 derniers mois", value: ventesRecentes, sub: "Tendance récente" },
                    { label: "CA cumulé Pros", value: totalCA.toLocaleString("fr-FR") + " €", sub: "Volume transactionnel" },
                    { label: "Pros actifs (avec ventes)", value: uniqueUsers, sub: "Utilisateurs qui loggent" },
                  ].map((k, i) => (
                    <div key={i} style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".9rem 1.1rem" }}>
                      <div style={{ fontSize: ".62rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", fontWeight: 700, marginBottom: ".25rem" }}>{k.label}</div>
                      <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", color: "var(--text)", fontWeight: 700, lineHeight: 1.1 }}>{k.value}</div>
                      <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".2rem" }}>{k.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Graphiques mensualisés — une barre par tranche de prix, chaque mois */}
                <GroupedPriceBarChart
                  title="Ventes par type de client (plateforme)"
                  sub="Pour chaque mois, une barre par tranche de prix ; la couleur ventile Pro / Particulier."
                  data={byClient}
                />
                <GroupedPriceBarChart
                  title="Ventes par méthode de retrait (plateforme)"
                  sub="Pour chaque mois, une barre par tranche de prix ; la couleur ventile concession vs expédition."
                  data={byPickup}
                />
                <GroupedPriceBarChart
                  title="Ventes par plateforme (top 6 + Autres)"
                  sub="Pour chaque mois, une barre par tranche de prix ; la couleur ventile par canal."
                  data={byPlatformTop}
                />

                <div style={{ background: "rgba(168,128,58,.05)", border: "1px solid rgba(168,128,58,.18)", borderRadius: 8, padding: ".8rem 1rem", fontSize: ".72rem", color: "var(--sub)", lineHeight: 1.55 }}>
                  <strong style={{ color: "var(--accent)" }}>📊 Lecture :</strong> chaque mois affiche <strong>une barre par tranche de prix</strong> (1·&lt;15 k€, 2·15–35 k€, 3·35–80 k€, 4·80–150 k€ Premium, 5·150–225 k€ Ultra premium, 6·&gt;225 k€) ; à l'intérieur d'une barre, les couleurs ventilent par segment. Ces graphiques agrègent les suivis de vente de l'ensemble des utilisateurs Pro. Le segment « Non précisé » correspond aux entrées non renseignées — incitez vos Pros à compléter ces champs pour fiabiliser les analyses.
                </div>
              </div>
            );
          })()}

          {/* ─── BLOG TAB ─── */}
          {/* Piste 1 : Dataset propriétaire — feedbacks + suivis de vente */}
          {!loading && tab === "dataset" && !sensitiveAccess && <RestrictedTabPlaceholder label="Dataset" icon="🗂" />}
          {!loading && tab === "dataset" && sensitiveAccess && (
            <div>
              {/* Stats globales */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: ".7rem", marginBottom: "1.2rem" }}>
                <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".9rem 1rem" }}>
                  <div style={{ fontSize: ".68rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>Feedbacks totaux</div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "var(--accent)", marginTop: ".3rem" }}>
                    {(datasetStats?.feedback?.total_feedback) || feedbacks.length || 0}
                  </div>
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".3rem" }}>
                    {datasetStats?.feedback?.positive || 0} positifs · {datasetStats?.feedback?.negative || 0} négatifs
                  </div>
                </div>
                <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".9rem 1rem" }}>
                  <div style={{ fontSize: ".68rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>Ventes confirmées</div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "var(--green, var(--green))", marginTop: ".3rem" }}>
                    {(datasetStats?.followup?.total_followup) || followups.length || 0}
                  </div>
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".3rem" }}>
                    {datasetStats?.followup?.pro_clients || 0} pro · {datasetStats?.followup?.particulier_clients || 0} particulier
                  </div>
                </div>
                <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".9rem 1rem" }}>
                  <div style={{ fontSize: ".68rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>Prix moyen vente</div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "var(--accent2)", marginTop: ".3rem" }}>
                    {datasetStats?.followup?.avg_sold_price
                      ? Number(datasetStats.followup.avg_sold_price).toLocaleString("fr-FR") + " €"
                      : "—"}
                  </div>
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".3rem" }}>
                    sur les ventes confirmées
                  </div>
                </div>
                <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".9rem 1rem" }}>
                  <div style={{ fontSize: ".68rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px" }}>Précision perçue</div>
                  <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "var(--accent)", marginTop: ".3rem" }}>
                    {datasetStats?.feedback?.total_feedback > 0
                      ? Math.round((datasetStats.feedback.positive / datasetStats.feedback.total_feedback) * 100) + "%"
                      : "—"}
                  </div>
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".3rem" }}>
                    cotes jugées réalistes
                  </div>
                </div>
              </div>

              {/* Section : Top 5 modèles les plus estimés / les plus recommandés */}
              {(() => {
                // Calcule en local depuis les feedbacks et suivis disponibles
                const allEstims = [
                  ...(feedbacks || []).map(f => ({
                    label: (f.vehicle_data?.make || "—").trim() + " " + (f.vehicle_data?.year_sell || ""),
                    price: f.estimation_data?.prix_viq || f.estimation_data?.argus_mid || 0,
                    source: "feedback"
                  })),
                  ...(followups || []).map(s => ({
                    label: (s.vehicle_data?.make || "—").trim() + " " + (s.vehicle_data?.year_sell || ""),
                    price: parseInt(s.sold_price) || 0,
                    source: "followup"
                  }))
                ].filter(e => e.label && e.label.trim() !== "" && e.label !== "—");

                // Top 5 modèles les plus estimés (par occurrences)
                const countByModel = {};
                allEstims.forEach(e => {
                  const k = e.label.trim();
                  countByModel[k] = (countByModel[k] || 0) + 1;
                });
                const topEstimes = Object.entries(countByModel)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5);

                // Top 5 modèles les plus recommandés = ceux avec score_vente le plus élevé
                const recoByModel = {};
                (feedbacks || []).forEach(f => {
                  const k = ((f.vehicle_data?.make || "") + " " + (f.vehicle_data?.year_sell || "")).trim();
                  const score = f.estimation_data?.score_vente || 0;
                  if (!k || k === "" || !score) return;
                  if (!recoByModel[k]) recoByModel[k] = { total: 0, count: 0 };
                  recoByModel[k].total += score;
                  recoByModel[k].count += 1;
                });
                const topRecommandes = Object.entries(recoByModel)
                  .map(([k, v]) => [k, Math.round(v.total / v.count), v.count])
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 5);

                // Moyennes
                const allPrices = allEstims.map(e => e.price).filter(p => p > 0);
                const avgPrice = allPrices.length > 0 ? Math.round(allPrices.reduce((a, b) => a + b, 0) / allPrices.length) : 0;

                // Moyenne de budget — utilise prix d'achat espéré ou prix vendu
                const budgets = (followups || []).map(s => parseInt(s.sold_price) || 0).filter(p => p > 0);
                const avgBudget = budgets.length > 0 ? Math.round(budgets.reduce((a, b) => a + b, 0) / budgets.length) : 0;

                if (allEstims.length === 0) return null;
                return (
                  <div style={{ marginBottom: "1.5rem" }}>
                    {/* Deux cartes de moyennes en haut */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".7rem", marginBottom: ".9rem" }}>
                      <div style={{ background: "linear-gradient(135deg, rgba(31,90,230,.06), rgba(31,90,230,.02))", border: "1px solid rgba(31,90,230,.2)", borderRadius: 8, padding: "1rem 1.2rem" }}>
                        <div style={{ fontSize: ".66rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", fontFamily: "var(--font-mono)" }}>📊 Moyenne de prix estimé</div>
                        <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "var(--accent2)", fontFamily: "var(--font-head)", marginTop: ".3rem" }}>
                          {avgPrice > 0 ? avgPrice.toLocaleString("fr-FR") + " €" : "—"}
                        </div>
                        <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".2rem" }}>
                          sur {allPrices.length} estimations enregistrées
                        </div>
                      </div>
                      <div style={{ background: "linear-gradient(135deg, rgba(168,128,58,.06), rgba(168,128,58,.02))", border: "1px solid rgba(168,128,58,.2)", borderRadius: 8, padding: "1rem 1.2rem" }}>
                        <div style={{ fontSize: ".66rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", fontFamily: "var(--font-mono)" }}>💼 Moyenne de budget acheteur</div>
                        <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-head)", marginTop: ".3rem" }}>
                          {avgBudget > 0 ? avgBudget.toLocaleString("fr-FR") + " €" : "—"}
                        </div>
                        <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".2rem" }}>
                          sur {budgets.length} ventes confirmées
                        </div>
                      </div>
                    </div>

                    {/* Deux colonnes : Top 5 estimés / Top 5 recommandés */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".7rem" }}>
                      <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".9rem 1.1rem" }}>
                        <div style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--text)", marginBottom: ".6rem", display: "flex", alignItems: "center", gap: ".4rem" }}>
                          🔝 Top 5 modèles les plus estimés
                        </div>
                        {topEstimes.length === 0 ? (
                          <div style={{ fontSize: ".74rem", color: "var(--muted)", fontStyle: "italic" }}>Pas encore de données</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: ".35rem" }}>
                            {topEstimes.map(([model, count], i) => (
                              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: ".35rem .55rem", background: i === 0 ? "rgba(168,128,58,.08)" : "var(--white)", border: "1.5px solid var(--border)", borderRadius: 5, fontSize: ".76rem" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                                  <span style={{ width: 18, height: 18, borderRadius: 9, background: i === 0 ? "var(--accent)" : "var(--border)", color: i === 0 ? "#fff" : "var(--sub)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: ".64rem", fontWeight: 700, fontFamily: "var(--font-mono)" }}>{i + 1}</span>
                                  <span style={{ fontWeight: 600 }}>{model}</span>
                                </div>
                                <div style={{ color: "var(--sub)", fontSize: ".7rem", fontFamily: "var(--font-mono)" }}>
                                  {count} estim.
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".9rem 1.1rem" }}>
                        <div style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--text)", marginBottom: ".6rem", display: "flex", alignItems: "center", gap: ".4rem" }}>
                          ⭐ Top 5 modèles les plus recommandés
                        </div>
                        <div style={{ fontSize: ".62rem", color: "var(--muted)", marginBottom: ".5rem", fontStyle: "italic", marginTop: "-.4rem" }}>
                          (score_vente moyen ≥ 80)
                        </div>
                        {topRecommandes.length === 0 ? (
                          <div style={{ fontSize: ".74rem", color: "var(--muted)", fontStyle: "italic" }}>Pas encore de données</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: ".35rem" }}>
                            {topRecommandes.map(([model, scoreAvg, count], i) => (
                              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: ".35rem .55rem", background: i === 0 ? "rgba(4,120,87,.08)" : "var(--white)", border: "1.5px solid var(--border)", borderRadius: 5, fontSize: ".76rem" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                                  <span style={{ width: 18, height: 18, borderRadius: 9, background: i === 0 ? "var(--green, var(--green))" : "var(--border)", color: i === 0 ? "#fff" : "var(--sub)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: ".64rem", fontWeight: 700, fontFamily: "var(--font-mono)" }}>{i + 1}</span>
                                  <span style={{ fontWeight: 600 }}>{model}</span>
                                </div>
                                <div style={{ color: "var(--green, var(--green))", fontSize: ".7rem", fontWeight: 700, fontFamily: "var(--font-mono)" }}>
                                  {scoreAvg}/100
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Section : Suivis de vente */}
              <div style={{ fontSize: ".88rem", fontWeight: 700, color: "var(--text)", marginBottom: ".5rem" }}>
                💰 Ventes confirmées (paires cote → prix de vente réel)
              </div>
              <div style={{ fontSize: ".7rem", color: "var(--sub)", marginBottom: ".7rem", fontStyle: "italic" }}>
                Donnée propriétaire : ce que les concurrents n'ont pas. À mesure que cette table grandit, la valeur de VehicleIQ augmente.
              </div>
              {followups.length === 0 ? (
                <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--sub)", background: "var(--card)", borderRadius: 8 }}>
                  Aucun suivi de vente pour l'instant. Première donnée propriétaire à venir avec les premiers clients qui déclarent leurs reprises.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", marginBottom: "1.5rem" }}>
                  {followups.slice(0, 50).map((f, idx) => {
                    const v = f.vehicle_data || {};
                    const e = f.estimation_data || {};
                    const estimVIQ = e.prix_viq || Math.round((e.argus_mid || e.argus_low || 0) * 0.95 / 100) * 100;
                    const ecart = estimVIQ ? (f.sold_price - estimVIQ) : null;
                    const ecartPct = estimVIQ ? Math.round((ecart / estimVIQ) * 100) : null;
                    return (
                      <div key={f.id || idx} style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 6, padding: ".6rem .8rem", display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr", gap: ".5rem", alignItems: "center", fontSize: ".75rem" }}>
                        <div>
                          <div style={{ fontWeight: 700, color: "var(--text)" }}>
                            {v.make || "?"} {v.year_sell || v.year || ""}
                          </div>
                          <div style={{ fontSize: ".66rem", color: "var(--muted)" }}>
                            {f.user_email || f.user_uid?.slice(0, 8) || "?"} · {f.sold_date ? new Date(f.sold_date).toLocaleDateString("fr-FR") : "—"}
                          </div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: ".64rem", color: "var(--sub)" }}>Cote VIQ</div>
                          <div style={{ fontWeight: 600 }}>{estimVIQ ? Number(estimVIQ).toLocaleString("fr-FR") + " €" : "—"}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: ".64rem", color: "var(--sub)" }}>Prix vendu</div>
                          <div style={{ fontWeight: 700, color: "var(--green, var(--green))" }}>{Number(f.sold_price).toLocaleString("fr-FR")} €</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: ".64rem", color: "var(--sub)" }}>Écart</div>
                          <div style={{ fontWeight: 600, color: ecart === null ? "var(--muted)" : (Math.abs(ecartPct) <= 5 ? "var(--green, var(--green))" : "var(--accent)") }}>
                            {ecart !== null ? (ecart > 0 ? "+" : "") + ecartPct + "%" : "—"}
                          </div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: ".64rem", color: "var(--sub)" }}>Client</div>
                          <div style={{ fontWeight: 600, textTransform: "capitalize" }}>{f.client_type === "particulier" ? "Particulier" : f.client_type === "pro" ? "Pro" : "?"}</div>
                        </div>
                      </div>
                    );
                  })}
                  {followups.length > 50 && (
                    <div style={{ fontSize: ".7rem", color: "var(--muted)", textAlign: "center", marginTop: ".4rem" }}>
                      Affichage limité aux 50 plus récents · {followups.length} au total
                    </div>
                  )}
                </div>
              )}

              {/* Section : Feedbacks d'estimation */}
              <div style={{ fontSize: ".88rem", fontWeight: 700, color: "var(--text)", marginBottom: ".7rem", fontFamily: "var(--font-head)", letterSpacing: "-.01em" }}>
                💬 Feedbacks utilisateurs — perception immédiate de la Cote
              </div>
              {feedbacks.length === 0 ? (
                <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--sub)", background: "var(--card)", borderRadius: 8 }}>
                  Aucun feedback pour l'instant.
                </div>
              ) : (() => {
                // Statistiques globales pour donner du contexte
                const total = feedbacks.length;
                const realisticCount = feedbacks.filter(f => f.is_realistic).length;
                const unrealisticCount = total - realisticCount;
                const realisticPct = total > 0 ? Math.round((realisticCount / total) * 100) : 0;
                // Analyse des raisons d'écart
                const reasonsCount = {};
                feedbacks.filter(f => !f.is_realistic && f.reason).forEach(f => {
                  reasonsCount[f.reason] = (reasonsCount[f.reason] || 0) + 1;
                });
                const topReasons = Object.entries(reasonsCount).sort((a,b) => b[1] - a[1]).slice(0, 4);
                const reasonLabel = (r) => ({
                  too_high: "Trop élevée",
                  too_low: "Trop basse",
                  state: "État non pris en compte",
                  km: "Kilométrage atypique",
                  market: "Hors marché",
                  options: "Options non valorisées",
                  region: "Spécificité régionale",
                  rare: "Modèle rare",
                  other: "Autre",
                }[r] || r);

                return (
                  <>
                    {/* Bandeau de stats globales */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: ".6rem", marginBottom: "1rem" }}>
                      <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".8rem 1rem" }}>
                        <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".25rem" }}>Total feedbacks</div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.6rem", fontWeight: 800, color: "var(--text)", lineHeight: 1.1, letterSpacing: "-.02em" }}>{total}</div>
                      </div>
                      <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".8rem 1rem", borderLeft: "3px solid var(--green)" }}>
                        <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".25rem" }}>Cotes validées</div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.6rem", fontWeight: 800, color: "var(--green)", lineHeight: 1.1, letterSpacing: "-.02em" }}>{realisticCount} <span style={{ fontSize: ".85rem", color: "var(--sub)", fontWeight: 500 }}>({realisticPct}%)</span></div>
                      </div>
                      <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".8rem 1rem", borderLeft: "3px solid var(--red)" }}>
                        <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.2px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".25rem" }}>Cotes contestées</div>
                        <div style={{ fontFamily: "var(--font-head)", fontSize: "1.6rem", fontWeight: 800, color: "var(--red)", lineHeight: 1.1, letterSpacing: "-.02em" }}>{unrealisticCount} <span style={{ fontSize: ".85rem", color: "var(--sub)", fontWeight: 500 }}>({100 - realisticPct}%)</span></div>
                      </div>
                    </div>

                    {/* Top raisons de contestation */}
                    {topReasons.length > 0 && (
                      <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".9rem 1.1rem", marginBottom: "1rem" }}>
                        <div style={{ fontSize: ".7rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".6rem" }}>
                          Principales raisons d'écart
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem" }}>
                          {topReasons.map(([r, count]) => (
                            <div key={r} style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", padding: ".3rem .7rem", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 100, fontSize: ".74rem" }}>
                              <span style={{ color: "var(--text)", fontWeight: 600 }}>{reasonLabel(r)}</span>
                              <span style={{ color: "var(--red)", fontWeight: 700, fontFamily: "var(--font-mono)" }}>{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Liste détaillée des feedbacks */}
                    <div style={{ fontSize: ".72rem", color: "var(--sub)", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)", fontWeight: 700, marginBottom: ".5rem" }}>
                      Détail des feedbacks
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: ".55rem" }}>
                      {feedbacks.slice(0, 50).map((fb, idx) => {
                        // Extraire les détails véhicule depuis JSONB
                        const vd = fb.vehicle_data || {};
                        const ed = fb.estimation_data || {};
                        const make = vd.make || vd.makeModel?.split(" ")[0] || "";
                        const model = vd.model || (vd.makeModel ? vd.makeModel.split(" ").slice(1).join(" ") : "");
                        const year = vd.year || vd.year_sell;
                        const km = vd.km || vd.km_sell || vd.km_buy;
                        const fuel = vd.fuel || vd.fuel_sell;
                        const state = vd.state ? String(vd.state).split("—")[0].trim() : null;
                        const ct = vd.ct;
                        // Prix VIQ estimé
                        const prixVIQ = ed.prix_viq || ed.argus_mid || null;
                        const prixUser = fb.user_price;
                        // Calcul de l'écart
                        let ecart = null, ecartPct = null;
                        if (prixVIQ && prixUser) {
                          ecart = prixUser - prixVIQ;
                          ecartPct = Math.round((ecart / prixVIQ) * 1000) / 10;
                        }
                        const ecartColor = !ecart ? "var(--muted)" : Math.abs(ecartPct) < 5 ? "var(--green)" : Math.abs(ecartPct) < 15 ? "var(--accent)" : "var(--red)";

                        return (
                          <div key={fb.id || idx} style={{
                            background: "var(--white)",
                            border: "1.5px solid var(--border)",
                            borderLeft: `3px solid ${fb.is_realistic ? "var(--green)" : "var(--red)"}`,
                            borderRadius: 10,
                            padding: ".9rem 1.1rem",
                          }}>
                            {/* Header : statut + utilisateur + date */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".5rem", marginBottom: ".55rem", flexWrap: "wrap" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: ".3rem",
                                  padding: ".2rem .55rem", borderRadius: 100,
                                  background: fb.is_realistic ? "rgba(46,111,92,.08)" : "rgba(155,44,44,.06)",
                                  color: fb.is_realistic ? "var(--green)" : "var(--red)",
                                  fontSize: ".65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px",
                                  fontFamily: "var(--font-mono)",
                                }}>
                                  {fb.is_realistic ? "✓ Réaliste" : "✗ Contestée"}
                                </span>
                                <span style={{ fontSize: ".78rem", fontWeight: 600, color: "var(--text)" }}>
                                  {fb.user_email || (fb.user_uid?.slice(0, 10) || "anonyme")}
                                </span>
                                {fb.user_role && fb.user_role !== "user" && (
                                  <span style={{ fontSize: ".62rem", padding: ".1rem .45rem", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--sub)", fontFamily: "var(--font-mono)" }}>{fb.user_role}</span>
                                )}
                              </div>
                              <div style={{ fontSize: ".64rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
                                {new Date(fb.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                              </div>
                            </div>

                            {/* Véhicule estimé */}
                            {(make || model || year) && (
                              <div style={{ marginBottom: ".55rem" }}>
                                <div style={{ fontSize: ".88rem", fontWeight: 600, color: "var(--text)", marginBottom: ".25rem" }}>
                                  🚗 {make} {model}{year ? ` · ${year}` : ""}
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: ".35rem" }}>
                                  {km && <span style={{ fontSize: ".66rem", padding: ".15rem .5rem", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 100, color: "var(--sub)", fontFamily: "var(--font-mono)" }}>{Number(km).toLocaleString("fr-FR")} km</span>}
                                  {fuel && <span style={{ fontSize: ".66rem", padding: ".15rem .5rem", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 100, color: "var(--sub)" }}>⛽ {fuel}</span>}
                                  {state && <span style={{ fontSize: ".66rem", padding: ".15rem .5rem", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 100, color: "var(--sub)" }}>🔧 {state}</span>}
                                  {ct && <span style={{ fontSize: ".66rem", padding: ".15rem .5rem", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 100, color: "var(--sub)" }}>📋 {ct}</span>}
                                </div>
                              </div>
                            )}

                            {/* Comparaison prix VIQ vs prix utilisateur */}
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: ".5rem", padding: ".55rem .7rem", background: "var(--bg)", borderRadius: 8, marginBottom: fb.reason ? ".55rem" : 0 }}>
                              {prixVIQ && (
                                <div>
                                  <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", fontWeight: 700 }}>Cote VIQ.AI®</div>
                                  <div style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", fontWeight: 600, color: "var(--accent)", marginTop: ".15rem" }}>
                                    {Number(prixVIQ).toLocaleString("fr-FR")} €
                                  </div>
                                </div>
                              )}
                              {prixUser && (
                                <div>
                                  <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", fontWeight: 700 }}>Prix utilisateur</div>
                                  <div style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", fontWeight: 600, color: "var(--text)", marginTop: ".15rem" }}>
                                    {Number(prixUser).toLocaleString("fr-FR")} €
                                  </div>
                                </div>
                              )}
                              {ecart !== null && (
                                <div>
                                  <div style={{ fontSize: ".6rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".8px", fontFamily: "var(--font-mono)", fontWeight: 700 }}>Écart</div>
                                  <div style={{ fontFamily: "var(--font-head)", fontSize: "1.05rem", fontWeight: 600, color: ecartColor, marginTop: ".15rem" }}>
                                    {ecart >= 0 ? "+" : ""}{Math.round(ecart).toLocaleString("fr-FR")} € <span style={{ fontSize: ".72rem", color: ecartColor }}>({ecart >= 0 ? "+" : ""}{ecartPct}%)</span>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Raison de contestation */}
                            {fb.reason && (
                              <div style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".74rem", color: "var(--sub)", padding: ".4rem .6rem", background: "rgba(155,44,44,.05)", border: "1px solid rgba(155,44,44,.15)", borderRadius: 6 }}>
                                <span style={{ color: "var(--red)" }}>⚠</span>
                                <span><strong style={{ color: "var(--text)" }}>Raison :</strong> {reasonLabel(fb.reason)}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {feedbacks.length > 50 && (
                        <div style={{ fontSize: ".7rem", color: "var(--muted)", textAlign: "center", marginTop: ".4rem", padding: ".6rem", background: "var(--bg)", borderRadius: 6 }}>
                          Affichage limité aux 50 plus récents · <strong>{feedbacks.length}</strong> au total
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {tab === "blog" && (
            <div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem" }}>
                <div style={{ fontWeight:700, fontSize:"1rem" }}>Gestion du Blog</div>
                <button className="btn btn-accent btn-sm" onClick={() => {
                  setArticleForm({ title:"", cat:"Marché", emoji:"📊", excerpt:"", body:"", slug:"", metaTitle:"", metaDescription:"", coverImage:"" });
                  setArticleError(null);
                  setEditingArticle("new");
                }}>+ Nouvel article</button>
              </div>
              {editingArticle && (
                <div className="admin-editor" style={{ marginBottom:"1.2rem" }}>
                  <div style={{ fontSize:".82rem", fontWeight:700, marginBottom:".8rem", color:"var(--accent)" }}>
                    {editingArticle === "new" ? "Nouvel article" : "Modifier"}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:".7rem", marginBottom:".7rem" }}>
                    <div className="field" style={{ margin:0 }}><label>Titre *</label><input className="input" value={articleForm.title} onChange={e=>setArticleForm(f=>({...f,title:e.target.value}))} placeholder="Titre de l'article"/></div>
                    <div className="field" style={{ margin:0 }}><label>Catégorie</label><select className="input" value={articleForm.cat} onChange={e=>setArticleForm(f=>({...f,cat:e.target.value}))}>{["Marché","Concurrence","VehicleIQ","Services","Tendances","Réglementation","Gestion"].map(c=><option key={c}>{c}</option>)}</select></div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"80px 1fr", gap:".7rem", marginBottom:".7rem" }}>
                    <div className="field" style={{ margin:0 }}><label>Emoji</label><input className="input" value={articleForm.emoji} onChange={e=>setArticleForm(f=>({...f,emoji:e.target.value}))}/></div>
                    <div className="field" style={{ margin:0 }}><label>Extrait</label><input className="input" value={articleForm.excerpt} onChange={e=>setArticleForm(f=>({...f,excerpt:e.target.value}))} placeholder="Description courte..."/></div>
                  </div>
                  {/* v73 — Règle : slug/meta title/meta description ajoutés (SEO/GEO). */}
                  <div className="field" style={{ margin:"0 0 .7rem" }}>
                    <label>Slug (URL) — laisser vide pour générer automatiquement depuis le titre</label>
                    <input className="input" value={articleForm.slug} onChange={e=>setArticleForm(f=>({...f,slug:e.target.value}))} placeholder="mon-article-seo"/>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:".7rem", marginBottom:".7rem" }}>
                    <div className="field" style={{ margin:0 }}><label>Meta title</label><input className="input" value={articleForm.metaTitle} onChange={e=>setArticleForm(f=>({...f,metaTitle:e.target.value}))} placeholder="Titre affiché dans les résultats Google (60 car. max conseillé)"/></div>
                    <div className="field" style={{ margin:0 }}><label>Meta description</label><input className="input" value={articleForm.metaDescription} onChange={e=>setArticleForm(f=>({...f,metaDescription:e.target.value}))} placeholder="Description affichée dans les résultats Google (155 car. max conseillé)"/></div>
                  </div>
                  <div className="field" style={{ margin:"0 0 .7rem" }}><label>Corps (HTML supporté)</label>
                    <textarea className="contact-textarea" style={{ minHeight:160 }} value={articleForm.body} onChange={e=>setArticleForm(f=>({...f,body:e.target.value}))} placeholder="<h2>Section</h2><p>Contenu...</p>"/>
                  </div>
                  {/* v73 — Règle : l'upload d'image ne faisait rien (aucun onchange
                      sur l'input file) — corrigé, avec compression avant stockage. */}
                  <div className="admin-img-upload" style={{ marginBottom:".7rem" }} onClick={() => {
                    const i = document.createElement("input"); i.type = "file"; i.accept = "image/*";
                    i.onchange = async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try {
                        const dataUrl = await compressImageFile(f, 1200, 0.8);
                        setArticleForm(fm => ({ ...fm, coverImage: dataUrl }));
                      } catch (err) { setArticleError("Image invalide."); }
                    };
                    i.click();
                  }}>
                    {articleForm.coverImage ? (
                      <img src={articleForm.coverImage} alt="" style={{ maxWidth: "100%", maxHeight: 140, borderRadius: 8 }} />
                    ) : (
                      <><div style={{ fontSize:"1.3rem" }}>🖼️</div><div style={{ fontSize:".75rem", color:"var(--sub)" }}>Insérer une image (PNG, JPG, WEBP)</div></>
                    )}
                  </div>
                  {articleForm.coverImage && (
                    <button className="btn btn-ghost btn-sm" style={{ marginBottom: ".7rem" }} onClick={() => setArticleForm(f => ({ ...f, coverImage: "" }))}>Supprimer l'image</button>
                  )}
                  {articleError && <div style={{ fontSize: ".78rem", color: "var(--red, #c0392b)", marginBottom: ".6rem" }}>⚠ {articleError}</div>}
                  <div style={{ display:"flex", gap:".5rem" }}>
                    <button className="btn btn-accent btn-sm" disabled={articleSaving} onClick={async () => {
                      if (!articleForm.title) return;
                      setArticleSaving(true); setArticleError(null);
                      try {
                        const payload = { ...articleForm };
                        const r = editingArticle === "new"
                          ? await apiPost("/api/admin/blog/articles", payload)
                          : await apiPut(`/api/admin/blog/articles/${editingArticle}`, payload);
                        if (r?.success) { await loadBlogArticles(); setEditingArticle(null); }
                        else setArticleError(r?.error || "Publication impossible.");
                      } catch (e) { setArticleError(e?.message || "Publication impossible."); }
                      finally { setArticleSaving(false); }
                    }}>{articleSaving ? "Publication…" : "💾 Publier"}</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setEditingArticle(null); setArticleError(null);}}>Annuler</button>
                  </div>
                </div>
              )}
              <div style={{ display:"flex", flexDirection:"column", gap:".5rem" }}>
                {blogArticles === null ? (
                  <div style={{ fontSize: ".82rem", color: "var(--muted)" }}>Chargement…</div>
                ) : [
                  ...BLOG_ARTICLES.map(a => ({ ...a, _source: "static" })),
                  ...blogArticles.map(a => ({ ...a, cat: a.category, _source: "backend" })),
                ].map(art => (
                  <div key={art._source + "-" + art.id} style={{ background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:8, padding:".7rem 1rem", display:"flex", alignItems:"center", gap:".8rem" }}>
                    <span style={{ fontSize:"1.1rem" }}>{art.emoji}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:".83rem", fontWeight:600, color:"var(--text)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                        {art.title}{art._source === "backend" && !art.published && <span style={{ marginLeft: ".4rem", fontSize: ".62rem", fontWeight: 700, color: "var(--muted)" }}>(brouillon)</span>}
                      </div>
                      <div style={{ fontSize:".65rem", color:"var(--muted)", fontFamily:"var(--font-mono)" }}>{art.cat} · {art._source === "backend" ? new Date(art.created_at).toLocaleDateString("fr-FR") : art.date} {art._source === "backend" ? `· /blog/${art.slug}` : ""}</div>
                    </div>
                    <span style={{ fontSize:".6rem", background:art._source==="backend"?"rgba(0,191,165,.1)":"rgba(168,128,58,.1)", color:art._source==="backend"?"var(--green)":"var(--accent)", border:`1px solid ${art._source==="backend"?"rgba(0,191,165,.2)":"rgba(168,128,58,.2)"}`, padding:".1rem .4rem", borderRadius:50, fontWeight:700, flexShrink:0 }}>
                      {art._source === "backend" ? "PERSO" : "SEO"}
                    </span>
                    {art._source === "backend" && (
                      <>
                        <button onClick={()=>{
                          setArticleForm({ title: art.title, cat: art.category || "Marché", emoji: art.emoji || "📊", excerpt: art.excerpt || "", body: art.body || "", slug: art.slug || "", metaTitle: art.meta_title || "", metaDescription: art.meta_description || "", coverImage: art.cover_image || "" });
                          setArticleError(null);
                          setEditingArticle(art.id);
                        }} style={{ background:"none", border:"1.5px solid var(--border)", borderRadius:6, padding:".22rem .5rem", cursor:"pointer", fontSize:".7rem", color:"var(--sub)", flexShrink:0 }}>✏️</button>
                        <button onClick={async () => { if (window.confirm("Supprimer définitivement cet article ?")) { await apiDelete(`/api/admin/blog/articles/${art.id}`); loadBlogArticles(); } }}
                          style={{ background:"none", border:"1.5px solid var(--border)", borderRadius:6, padding:".22rem .5rem", cursor:"pointer", fontSize:".7rem", color:"var(--red, #c0392b)", flexShrink:0 }}>🗑️</button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── TÉMOIGNAGES TAB ─── */}
          {tab === "temoignages" && (
            <div>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1rem" }}>
                <div style={{ fontWeight:700, fontSize:"1rem" }}>Gestion des Témoignages</div>
                <button className="btn btn-accent btn-sm" onClick={()=>{setTestiForm({name:"",role:"",badge:"",quote:"",stars:5,bg:"var(--accent)",photo:""});setEditingArticle("new-testi");}}>+ Nouveau</button>
              </div>
              {editingArticle === "new-testi" && (
                <div className="admin-editor" style={{ marginBottom:"1.2rem" }}>
                  <div style={{ fontSize:".82rem", fontWeight:700, marginBottom:".8rem", color:"var(--accent)" }}>Nouveau témoignage</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:".7rem", marginBottom:".7rem" }}>
                    <div className="field" style={{ margin:0 }}><label>Prénom Nom *</label><input className="input" value={testiForm.name} onChange={e=>setTestiForm(f=>({...f,name:e.target.value}))} placeholder="Jean Dupont"/></div>
                    <div className="field" style={{ margin:0 }}><label>Rôle & entreprise *</label><input className="input" value={testiForm.role} onChange={e=>setTestiForm(f=>({...f,role:e.target.value}))} placeholder="Directeur VO — Groupe X"/></div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 80px", gap:".7rem", marginBottom:".7rem" }}>
                    <div className="field" style={{ margin:0 }}><label>Badge activité</label><input className="input" value={testiForm.badge} onChange={e=>setTestiForm(f=>({...f,badge:e.target.value}))} placeholder="Mandataire"/></div>
                    <div className="field" style={{ margin:0 }}><label>Couleur avatar</label><input type="color" className="input" value={testiForm.bg} onChange={e=>setTestiForm(f=>({...f,bg:e.target.value}))} style={{ height:42, padding:".2rem" }}/></div>
                    <div className="field" style={{ margin:0 }}><label>Étoiles</label><select className="input" value={testiForm.stars} onChange={e=>setTestiForm(f=>({...f,stars:Number(e.target.value)}))}>{[5,4,3].map(n=><option key={n}>{n}</option>)}</select></div>
                  </div>
                  <div style={{ display:"flex", gap:".7rem", alignItems:"center", marginBottom:".7rem" }}>
                    <div className="admin-img-upload" style={{ flex:1 }} onClick={()=>{const i=document.createElement("input");i.type="file";i.accept="image/*";i.onchange=(e)=>{const f=e.target.files[0];if(!f)return;const r=new FileReader();r.onload=(ev)=>setTestiForm(f=>({...f,photo:ev.target.result}));r.readAsDataURL(f);};i.click();}}>
                      {testiForm.photo ? <img src={testiForm.photo} alt="" style={{ width:52,height:52,borderRadius:"50%",objectFit:"cover" }}/> : <><div style={{ fontSize:"1.2rem" }}>👤</div><div style={{ fontSize:".7rem", color:"var(--sub)" }}>Photo (optionnel)</div></>}
                    </div>
                    {testiForm.photo && <button onClick={()=>setTestiForm(f=>({...f,photo:""}))} style={{ background:"none", border:"1.5px solid var(--border)", borderRadius:6, padding:".3rem .6rem", cursor:"pointer", fontSize:".7rem", color:"var(--sub)" }}>Supprimer</button>}
                  </div>
                  <div className="field" style={{ margin:"0 0 .7rem" }}><label>Témoignage *</label>
                    <textarea className="contact-textarea" style={{ minHeight:90 }} value={testiForm.quote} onChange={e=>setTestiForm(f=>({...f,quote:e.target.value}))} placeholder="Rédigez le témoignage ici..."/>
                  </div>
                  <div style={{ display:"flex", gap:".5rem" }}>
                    <button className="btn btn-accent btn-sm" onClick={()=>{
                      if(!testiForm.name||!testiForm.quote)return;
                      const av=testiForm.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
                      const t={...testiForm,avatar:av,id:"testi-"+Date.now()};
                      const up=[t,...testiDrafts];setTestiDrafts(up);localStorage.setItem("viq_testimonials",JSON.stringify(up));setEditingArticle(null);
                    }}>💾 Sauvegarder</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setEditingArticle(null)}>Annuler</button>
                  </div>
                </div>
              )}
              <div style={{ display:"flex", flexDirection:"column", gap:".6rem" }}>
                {testiDrafts.map((t,i)=>(
                  <div key={t.id||i} style={{ background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:10, padding:".9rem 1rem", display:"flex", gap:".8rem", alignItems:"flex-start" }}>
                    {t.photo ? <img src={t.photo} alt={t.name} style={{ width:42,height:42,borderRadius:"50%",objectFit:"cover",flexShrink:0 }}/> : <div style={{ width:42,height:42,borderRadius:"50%",background:t.bg||"var(--accent)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:".8rem",fontWeight:800,color:"#fff",flexShrink:0 }}>{t.avatar}</div>}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:".83rem" }}>{t.name}</div>
                      <div style={{ fontSize:".7rem", color:"var(--muted)", marginBottom:".25rem" }}>{t.role}</div>
                      <div style={{ fontSize:".78rem", color:"var(--sub)", fontStyle:"italic" }}>"{t.quote.slice(0,100)}{t.quote.length>100?"…":""}"</div>
                    </div>
                    <button onClick={()=>{const u=testiDrafts.filter((_,j)=>j!==i);setTestiDrafts(u);localStorage.setItem("viq_testimonials",JSON.stringify(u));}} style={{ background:"none", border:"1.5px solid var(--border)", borderRadius:6, padding:".22rem .45rem", cursor:"pointer", fontSize:".7rem", color:"var(--sub)", flexShrink:0 }}>🗑️</button>
                  </div>
                ))}
                {testiDrafts.length===0 && <div style={{ color:"var(--muted)", fontSize:".82rem", textAlign:"center", padding:"1.5rem" }}>Aucun témoignage personnalisé.</div>}
              </div>
            </div>
          )}

          {tab === "demovideo" && (
            <div style={{ padding:"1.2rem 0", maxWidth:620 }}>
              <div style={{ fontSize:"1rem", fontWeight:700, color:"var(--text)", marginBottom:".4rem", fontFamily:"var(--font-head)" }}>
                🎬 Vidéo de démonstration
              </div>
              <p style={{ fontSize:".82rem", color:"var(--sub)", lineHeight:1.6, marginBottom:"1.2rem" }}>
                Cette vidéo s'affiche quand un visiteur clique sur « Découvrir la plateforme » sur la page d'accueil.
                Collez un lien YouTube standard (ex&nbsp;: <code style={{ fontSize:".76rem", background:"var(--card)", padding:".1rem .3rem", borderRadius:3 }}>https://www.youtube.com/watch?v=XXXXXXXXX</code>) ou un lien court <code style={{ fontSize:".76rem", background:"var(--card)", padding:".1rem .3rem", borderRadius:3 }}>https://youtu.be/XXXXXXXXX</code>.
              </p>
              <label style={{ fontSize:".74rem", fontWeight:700, color:"var(--muted)", textTransform:"uppercase", letterSpacing:".6px", display:"block", marginBottom:".4rem" }}>
                Lien YouTube
              </label>
              <input
                type="text"
                value={demoVideoUrl}
                onChange={e => { setDemoVideoUrl(e.target.value); setDemoVideoSaved(false); }}
                placeholder="https://www.youtube.com/watch?v=…"
                style={{ width:"100%", padding:".8rem .9rem", borderRadius:8, border:"1.5px solid var(--border)", background:"var(--card)", color:"var(--text)", fontSize:".88rem", fontFamily:"var(--font-body)", outline:"none", marginBottom:"1rem" }}
              />
              <div style={{ display:"flex", gap:".7rem", alignItems:"center" }}>
                <button
                  onClick={() => {
                    try { localStorage.setItem("viq_demo_video", demoVideoUrl.trim()); } catch(e) {}
                    setDemoVideoSaved(true);
                  }}
                  style={{ padding:".7rem 1.5rem", borderRadius:8, border:"none", background:"var(--accent)", color:"#fff", fontWeight:700, fontSize:".85rem", cursor:"pointer", fontFamily:"var(--font-body)" }}>
                  Enregistrer le lien
                </button>
                {demoVideoUrl && (
                  <button
                    onClick={() => { setDemoVideoUrl(""); try { localStorage.removeItem("viq_demo_video"); } catch(e) {} setDemoVideoSaved(false); }}
                    style={{ padding:".7rem 1.2rem", borderRadius:8, border:"1.5px solid var(--border)", background:"none", color:"var(--sub)", fontWeight:600, fontSize:".82rem", cursor:"pointer", fontFamily:"var(--font-body)" }}>
                    Effacer
                  </button>
                )}
                {demoVideoSaved && <span style={{ color:"var(--green,#16a34a)", fontSize:".82rem", fontWeight:600 }}>✓ Enregistré</span>}
              </div>
              {(() => {
                const id = extractYouTubeId(demoVideoUrl);
                return id ? (
                  <div style={{ marginTop:"1.6rem" }}>
                    <div style={{ fontSize:".74rem", fontWeight:700, color:"var(--muted)", textTransform:"uppercase", letterSpacing:".6px", marginBottom:".5rem" }}>Aperçu</div>
                    <div style={{ position:"relative", paddingBottom:"56.25%", height:0, borderRadius:10, overflow:"hidden", border:"1.5px solid var(--border)" }}>
                      <iframe
                        src={`https://www.youtube.com/embed/${id}`}
                        title="Aperçu vidéo démo"
                        style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", border:"none" }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                ) : demoVideoUrl ? (
                  <div style={{ marginTop:"1rem", fontSize:".8rem", color:"var(--red,#dc2626)" }}>
                    ⚠ Lien YouTube non reconnu. Vérifiez le format.
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* v66 — Paramètres Super Admin : limites de prospection modifiables
              sans toucher au code. */}
          {tab === "settings" && (
            <div style={{ padding: "1.5rem", maxWidth: 560 }}>
              <div style={{ fontFamily: "var(--font-head)", fontSize: "1.15rem", fontWeight: 700, marginBottom: ".3rem" }}>
                ⚙️ Paramètres de prospection
              </div>
              <div style={{ fontSize: ".82rem", color: "var(--sub)", marginBottom: "1.4rem" }}>
                Limites quotidiennes appliquées à tous les comptes Pro. Modifiables ici, sans déploiement.
              </div>
              <AdminSettingsPanel />
            </div>
          )}
        </div>
      </div>
      {quoteTarget && <QuoteModal target={quoteTarget} onClose={() => setQuoteTarget(null)} />}
    </div>
  );
}

// v66 — Panneau de paramètres Super Admin (limites de prospection), extrait
// en composant séparé pour garder AdminDashboard lisible.
// v73 — Règle 6 : génération de devis + lien de paiement Stripe pour une
// offre Pro (mensuelle ou annuelle), envoyé par email via facturation@vehicleiq.fr.
function QuoteModal({ target, onClose }) {
  const [email, setEmail] = useState(target.email || "");
  const [name, setName] = useState(target.name || "");
  const [plan, setPlan] = useState("unlimited");
  const [period, setPeriod] = useState("monthly");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // v110 — Le devis doit refléter la même promotion admin que la page tarifs
  // et le montant réellement facturé (voir /api/admin/billing/quote).
  const [promotions, setPromotions] = useState(null);
  useEffect(() => { apiGet("/api/plans/promotions").then(r => { if (r?.success) setPromotions(r.promotions); }).catch(() => {}); }, []);
  const activePromo = promotions?.[plan]?.[period];

  const send = async () => {
    setSending(true); setError(null); setResult(null);
    try {
      const r = await apiPost("/api/admin/billing/quote", { email, recipient_name: name, plan, billing_period: period });
      if (r?.success) setResult(r.payment_url);
      else setError(r?.error || "Envoi impossible.");
    } catch (e) { setError(e?.message || "Envoi impossible."); }
    finally { setSending(false); }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 480 }}>
        <div className="panel-header">
          <div className="panel-title">📧 Générer un devis</div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: "1.2rem", display: "flex", flexDirection: "column", gap: ".8rem" }}>
          <div className="field" style={{ margin: 0 }}><label>Email destinataire *</label><input className="input" value={email} onChange={e => setEmail(e.target.value)} placeholder="client@exemple.fr" /></div>
          <div className="field" style={{ margin: 0 }}><label>Nom (optionnel)</label><input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Jean Dupont" /></div>
          <div style={{ display: "flex", gap: ".7rem" }}>
            <div className="field" style={{ margin: 0, flex: 1 }}>
              <label>Offre</label>
              <select className="input" value={plan} onChange={e => setPlan(e.target.value)}>
                <option value="unlimited">Pro Solo</option>
                <option value="multi_agent_3">Pro Multi-Agent</option>
              </select>
            </div>
            <div className="field" style={{ margin: 0, flex: 1 }}>
              <label>Facturation</label>
              <select className="input" value={period} onChange={e => setPeriod(e.target.value)}>
                <option value="monthly">Mensuelle</option>
                <option value="annual">Annuelle</option>
              </select>
            </div>
          </div>
          <div style={{ fontSize: ".76rem", color: "var(--sub)" }}>
            {activePromo?.discountPct > 0 ? (
              <PromoPriceLine
                promo={activePromo}
                normalLabel={plan === "unlimited"
                  ? (period === "annual" ? "2 999 €/an (~250 €/mois)" : "399 €/mois")
                  : (period === "annual" ? "4 999 €/an (~417 €/mois)" : "799 €/mois")}
              />
            ) : (
              plan === "unlimited"
                ? (period === "annual" ? "2 999 €/an (~250 €/mois)" : "399 €/mois")
                : (period === "annual" ? "4 999 €/an (~417 €/mois)" : "799 €/mois")
            )}
          </div>
          {error && <div style={{ fontSize: ".8rem", color: "var(--red, #c0392b)" }}>⚠ {error}</div>}
          {result && (
            <div style={{ fontSize: ".8rem", color: "var(--green)", background: "rgba(46,111,92,.08)", border: "1px solid rgba(46,111,92,.3)", borderRadius: 8, padding: ".7rem" }}>
              ✓ Devis envoyé à {email}.<br />
              Lien de paiement : <a href={result} target="_blank" rel="noreferrer" style={{ color: "var(--green)", wordBreak: "break-all" }}>{result}</a>
            </div>
          )}
          <button className="btn btn-accent" disabled={sending || !email} onClick={send}>
            {sending ? "Envoi en cours…" : "Envoyer le devis →"}
          </button>
        </div>
      </div>
    </div>
  );
}

// v109 — Promotions sur les abonnements : ancien prix barré / nouveau prix
// réduit, affiché partout où le tarif apparaît (page tarifs, paramètres de
// paiement, modales de souscription) — et appliqué au montant réellement
// facturé par Stripe (voir subscribe-unlimited / subscribe-multi-agent).
function PromotionsAdminPanel() {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);

  const PLANS = [["unlimited", "Pro Solo"], ["multi_agent_3", "Pro Multi-Agent"]];
  const PERIODS = [["monthly", "Mensuel"], ["annual", "Annuel"]];

  const load = () => {
    apiGet("/api/admin/plan-promotions").then(r => {
      if (r?.success) {
        setRows(r.promotions || []);
        const d = {};
        for (const p of PLANS) for (const per of PERIODS) {
          const existing = (r.promotions || []).find(x => x.plan_code === p[0] && x.period === per[0]);
          d[`${p[0]}_${per[0]}`] = {
            discountPct: existing?.discount_pct ?? 0,
            label: existing?.label ?? "",
            active: !!existing?.active,
          };
        }
        setDraft(d);
      }
    }).catch(() => setError("Impossible de charger les promotions."));
  };
  useEffect(load, []);

  const save = async (planCode, period) => {
    const key = `${planCode}_${period}`;
    setSaving(key); setError(null); setSaved(null);
    try {
      const { discountPct, label, active } = draft[key];
      const r = await apiPatch("/api/admin/plan-promotions", { planCode, period, discountPct: parseInt(discountPct) || 0, label: label || null, active });
      if (!r?.success) { setError(r?.error || "Enregistrement impossible."); setSaving(null); return; }
      setSaved(key); setTimeout(() => setSaved(null), 2000);
      load();
    } catch (e) { setError(e?.message || "Enregistrement impossible."); }
    finally { setSaving(null); }
  };

  if (rows === null) return <div style={{ fontSize: ".84rem", color: "var(--muted)" }}>Chargement…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {PLANS.map(([planCode, planLabel]) => (
        <div key={planCode} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: "1rem" }}>
          <div style={{ fontWeight: 700, fontSize: ".9rem", marginBottom: ".7rem" }}>{planLabel}</div>
          {PERIODS.map(([period, periodLabel]) => {
            const key = `${planCode}_${period}`;
            const d = draft[key] || { discountPct: 0, label: "", active: false };
            return (
              <div key={period} style={{ display: "flex", gap: ".6rem", alignItems: "center", flexWrap: "wrap", marginBottom: ".6rem", padding: ".5rem", background: "var(--card)", borderRadius: 8 }}>
                <span style={{ fontSize: ".78rem", fontWeight: 600, width: 70 }}>{periodLabel}</span>
                <label style={{ display: "flex", alignItems: "center", gap: ".3rem", fontSize: ".76rem" }}>
                  <input type="checkbox" checked={d.active} onChange={e => setDraft(prev => ({ ...prev, [key]: { ...d, active: e.target.checked } }))} />
                  Active
                </label>
                <input type="number" min={0} max={100} value={d.discountPct} onChange={e => setDraft(prev => ({ ...prev, [key]: { ...d, discountPct: e.target.value } }))}
                  style={{ width: 70, padding: ".4rem .5rem", borderRadius: 6, border: "1.5px solid var(--border)", background: "var(--white)", color: "var(--text)", fontSize: ".84rem" }} />
                <span style={{ fontSize: ".78rem", color: "var(--muted)" }}>%</span>
                <input type="text" placeholder="Libellé (ex: -20% lancement)" value={d.label} onChange={e => setDraft(prev => ({ ...prev, [key]: { ...d, label: e.target.value } }))}
                  style={{ flex: 1, minWidth: 140, padding: ".4rem .6rem", borderRadius: 6, border: "1.5px solid var(--border)", background: "var(--white)", color: "var(--text)", fontSize: ".82rem" }} />
                <button className="btn btn-accent btn-sm" disabled={saving === key} onClick={() => save(planCode, period)}>
                  {saving === key ? "…" : saved === key ? "✓" : "Enregistrer"}
                </button>
              </div>
            );
          })}
        </div>
      ))}
      {error && <div style={{ fontSize: ".82rem", color: "var(--red, #c0392b)" }}>⚠ {error}</div>}
    </div>
  );
}

// v111 — Déclenchement manuel des tâches cron catalogue, désormais le SEUL
// moyen de les exécuter tant que l'automatisation reste désactivée
// (catalogue_crons_auto_enabled=0 par défaut, voir plus bas dans ce panneau).
function CronManualTriggersPanel() {
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState({});

  const JOBS = [
    ["/api/admin/cron/weekly-price-update", "Prix marché catalogue — hebdomadaire"],
    ["/api/admin/cron/market-prices", "Prix marché catalogue — mensuel"],
    ["/api/admin/cron/known-issues", "Known issues catalogue — mensuel"],
    ["/api/admin/cron/prospect-directory", "Répertoire prospects — mensuel"],
  ];

  const trigger = async (path, label) => {
    setBusy(path); setMsg(m => ({ ...m, [path]: null }));
    try {
      const r = await apiPost(path, {});
      setMsg(m => ({ ...m, [path]: r?.success ? (r.message || "Lancé.") : (r?.error || "Échec.") }));
    } catch (e) { setMsg(m => ({ ...m, [path]: e?.message || "Échec." })); }
    finally { setBusy(null); }
  };

  return (
    <div style={{ border: "1.5px solid var(--border)", borderRadius: 10, padding: "1rem" }}>
      <div style={{ fontWeight: 800, fontSize: ".92rem", marginBottom: ".2rem" }}>▶️ Lancer une mise à jour maintenant</div>
      <div style={{ fontSize: ".76rem", color: "var(--sub)", marginBottom: ".8rem" }}>
        Ces tâches consomment des appels IA/web facturés — à lancer uniquement quand nécessaire.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
        {JOBS.map(([path, label]) => (
          <div key={path} style={{ display: "flex", gap: ".6rem", alignItems: "center", flexWrap: "wrap", padding: ".5rem", background: "var(--card)", borderRadius: 8 }}>
            <span style={{ fontSize: ".8rem", flex: 1, minWidth: 200 }}>{label}</span>
            <button className="btn btn-outline btn-sm" disabled={busy === path} onClick={() => trigger(path, label)}>
              {busy === path ? "…" : "Lancer maintenant"}
            </button>
            {msg[path] && <div style={{ fontSize: ".72rem", color: "var(--muted)", width: "100%" }}>{msg[path]}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null); // clé en cours de sauvegarde
  const [saved, setSaved] = useState(null); // clé venant d'être sauvegardée
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet("/api/admin/settings").then(r => {
      if (r?.success) { setSettings(r.settings); setDraft(r.settings); }
    }).catch(() => setError("Impossible de charger les paramètres."));
  }, []);

  const FIELDS = [
    ["prospection_max_campaigns_per_day", "Campagnes", "Nombre maximal de campagnes par jour", 1, 10000],
    ["prospection_max_emails_per_day", "Emails", "Nombre maximal d'emails par jour", 1, 10000],
    ["prospection_max_sms_per_day", "SMS", "Nombre maximal de SMS par jour", 1, 10000],
    ["prospection_max_whatsapp_per_day", "WhatsApp", "Nombre maximal de messages WhatsApp par jour", 1, 10000],
    // v81 — Règle : réduction accordée au client sur présentation d'un code
    // apporteur d'affaires valide. N'affecte jamais la commission de
    // l'apporteur, toujours calculée sur le tarif plein.
    ["affiliate_discount_pct_monthly", "Réduction apporteur — Mensuel", "Pourcentage de réduction client sur les forfaits mensuels (0 = aucune réduction)", 0, 100],
    ["affiliate_discount_pct_annual", "Réduction apporteur — Annuel", "Pourcentage de réduction client sur les forfaits annuels (0 = aucune réduction)", 0, 100],
    // v83 — Règle : agent IA de prospection téléphonique — coupure d'urgence
    // + plafonds de budget/volume, modifiables sans déploiement.
    ["voice_agent_enabled", "Agent vocal IA — Activation", "0 = désactivé (coupure d'urgence), 1 = activé", 0, 1],
    ["voice_agent_max_calls_per_day", "Agent vocal IA — Appels/jour max", "Nombre maximal d'appels par pro et par 24h (maîtrise du budget)", 1, 500],
    ["voice_agent_max_call_duration_seconds", "Agent vocal IA — Durée max (secondes)", "Coupure automatique de l'appel au-delà de cette durée", 30, 600],
    ["voice_agent_max_concurrent_calls", "Agent vocal IA — Appels simultanés max", "Nombre d'appels que l'agent peut mener en même temps (protège le budget)", 1, 20],
    ["voice_agent_hours_start", "Agent vocal IA — Heure de début", "Heure locale (Europe/Paris) à partir de laquelle l'appel est autorisé", 0, 23],
    ["voice_agent_hours_end", "Agent vocal IA — Heure de fin", "Heure locale (Europe/Paris) après laquelle l'appel n'est plus autorisé", 1, 24],
    // v111 — Coupure d'urgence suite à un incident de coût : les tâches cron
    // mensuelles (prix marché, known issues, répertoire prospects) sont
    // désactivées par défaut. Les relancer manuellement ci-dessous, ou
    // remettre ce paramètre à 1 pour restaurer l'automatisation.
    ["catalogue_crons_auto_enabled", "Crons catalogue — Automatisation", "0 = désactivé (déclenchement manuel uniquement, par défaut), 1 = automatisation mensuelle réactivée", 0, 1],
  ];

  const save = async (key, min) => {
    setSaving(key); setError(null); setSaved(null);
    try {
      const value = parseInt(draft[key], 10);
      if (!Number.isFinite(value) || value < min) { setError("Valeur invalide."); setSaving(null); return; }
      const r = await apiPost("/api/admin/settings", { key, value });
      if (!r?.success) { setError(r?.error || "Enregistrement impossible."); setSaving(null); return; }
      // v73 — Vérification après écriture : on ne fait plus confiance à la
      // seule réponse "success" — on relit la valeur réellement en base pour
      // détecter un échec silencieux de persistance.
      const check = await apiGet("/api/admin/settings");
      if (check?.success && String(check.settings[key]) === String(value)) {
        setSettings(check.settings); setDraft(check.settings);
        setSaved(key); setTimeout(() => setSaved(null), 2000);
      } else {
        setError("La valeur ne semble pas avoir été enregistrée en base — réessayez ou contactez le support.");
      }
    } catch (e) { setError(e?.message || "Enregistrement impossible."); }
    finally { setSaving(null); }
  };

  if (!settings) return <div style={{ fontSize: ".84rem", color: "var(--muted)" }}>Chargement…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* v111 — Déclenchement manuel des crons catalogue, désormais désactivés
          par défaut suite à un incident de coût. */}
      <CronManualTriggersPanel />
      {/* v109 — Promotions sur les abonnements : ancien prix barré / nouveau
          prix réduit, affiché sur les tarifs et facturé réellement. */}
      <div style={{ border: "1.5px solid var(--accent)", borderRadius: 10, padding: "1rem" }}>
        <div style={{ fontWeight: 800, fontSize: ".92rem", marginBottom: ".2rem" }}>🏷️ Promotions abonnements</div>
        <div style={{ fontSize: ".76rem", color: "var(--sub)", marginBottom: ".8rem" }}>
          Réduction appliquée à l'affichage (ancien prix barré) ET au montant réellement facturé par Stripe.
        </div>
        <PromotionsAdminPanel />
      </div>
      {FIELDS.map(([key, label, hint, min, max]) => (
        <div key={key} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: "1rem" }}>
          <div style={{ fontWeight: 700, fontSize: ".88rem", marginBottom: ".15rem" }}>{label}</div>
          <div style={{ fontSize: ".76rem", color: "var(--sub)", marginBottom: ".7rem" }}>{hint}</div>
          <div style={{ display: "flex", gap: ".6rem", alignItems: "center" }}>
            <input type="number" min={min} max={max} value={draft[key] ?? ""} onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
              style={{ width: 120, padding: ".5rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".9rem", fontWeight: 700 }} />
            <button className="btn btn-accent btn-sm" disabled={saving === key || String(draft[key]) === String(settings[key])} onClick={() => save(key, min)}>
              {saving === key ? "…" : saved === key ? "✓ Enregistré" : "Enregistrer"}
            </button>
          </div>
        </div>
      ))}
      {error && <div style={{ fontSize: ".82rem", color: "var(--red, #c0392b)" }}>⚠ {error}</div>}
    </div>
  );
}
// ─── Onboarding Modal ─────────────────────────────────────────────────────────
function OnboardingModal({ onClose }) {
  const [step, setStep] = useState(0); // 0=intro 1=disclaimer 2=signup
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authInfo, setAuthInfo] = useState("");
  // Onboarding signup : type de compte + SIRET (option B)
  const [onbAccountType, setOnbAccountType] = useState("particulier"); // particulier | entreprise
  const [onbSiret, setOnbSiret] = useState("");
  const [onbSiretInfo, setOnbSiretInfo] = useState(null);
  const [onbSiretError, setOnbSiretError] = useState("");
  const [onbSiretSearching, setOnbSiretSearching] = useState(false);
  // Lookup SIRET via API publique data.gouv (réutilise la même logique que SiretBlock)
  const lookupOnbSiret = async (val) => {
    const clean = val.replace(/\s/g, "");
    setOnbSiret(clean);
    setOnbSiretInfo(null);
    setOnbSiretError("");
    if (clean.length < 9) return;
    setOnbSiretSearching(true);
    try {
      const res = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${clean}&page=1&per_page=1`);
      const data = await res.json();
      const result = data.results?.[0];
      if (result) {
        setOnbSiretInfo({
          nom: result.nom_complet || result.nom_raison_sociale || "—",
          siren: result.siren,
          activite: result.activite_principale_libelle || "—",
        });
      } else {
        setOnbSiretError("Aucune entreprise trouvée pour ce numéro.");
      }
    } catch (e) {
      setOnbSiretError("Erreur de recherche. Vérifiez le numéro.");
    }
    setOnbSiretSearching(false);
  };
  // Email auth state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [emailMode, setEmailMode] = useState("signup"); // "signin" | "signup" | "reset" — onboarding défaut signup

  const handleGoogle = async () => {
    setAuthLoading(true); setAuthError(""); setAuthInfo("");
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      onClose({ method: "google", email: result.user.email, name: result.user.displayName, uid: result.user.uid });
    } catch (err) {
      setAuthLoading(false);
      if (err.code !== "auth/popup-closed-by-user") setAuthError("Erreur Google. Réessayez.");
    }
  };

  // Mappe les codes Firebase vers des messages humains
  const emailErrorMsg = (err) => {
    const c = err.code || "";
    if (c === "auth/invalid-email") return "Adresse e-mail invalide.";
    if (c === "auth/user-not-found" || c === "auth/invalid-credential" || c === "auth/wrong-password") return "E-mail ou mot de passe incorrect.";
    if (c === "auth/email-already-in-use") return "Cet e-mail est déjà utilisé. Essayez de vous connecter.";
    if (c === "auth/weak-password") return "Le mot de passe doit faire au moins 6 caractères.";
    if (c === "auth/too-many-requests") return "Trop de tentatives. Réessayez dans quelques minutes.";
    if (c === "auth/network-request-failed") return "Connexion réseau interrompue.";
    return "Erreur d'authentification : " + (err.message || c || "inconnue");
  };

  const handleEmailSubmit = async () => {
    setAuthError(""); setAuthInfo("");
    // Validation SIRET pour les Pros : doit être renseigné et valide
    if (emailMode === "signup" && onbAccountType === "entreprise") {
      const cleanSiret = (onbSiret || "").replace(/\s/g, "");
      if (cleanSiret.length < 9) {
        setAuthError("Veuillez saisir un numéro de SIRET valide (au moins 9 chiffres).");
        return;
      }
    }
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setAuthError("Adresse e-mail invalide."); return;
    }
    if (emailMode === "reset") {
      setAuthLoading(true);
      try {
        await sendPasswordResetEmail(auth, cleanEmail);
        setAuthLoading(false);
        setAuthInfo("Un e-mail de réinitialisation vient d'être envoyé à " + cleanEmail + ".");
      } catch (err) {
        setAuthLoading(false);
        setAuthError(emailErrorMsg(err));
      }
      return;
    }
    if (!password || password.length < 6) {
      setAuthError("Le mot de passe doit faire au moins 6 caractères."); return;
    }
    if (emailMode === "signup" && (!name || name.trim().length < 2)) {
      setAuthError("Veuillez indiquer votre prénom ou nom."); return;
    }
    setAuthLoading(true);
    try {
      let result;
      if (emailMode === "signup") {
        result = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        try { await updateProfile(result.user, { displayName: name.trim() }); } catch(e) {}
        // Persister type compte + SIRET dès la création (option B onboarding)
        if (onbAccountType === "entreprise" && onbSiret) {
          try {
            const cleanSiret = (onbSiret || "").replace(/\s/g, "");
            localStorage.setItem("viq_account_type", "entreprise");
            localStorage.setItem("viq_siret", cleanSiret);
            if (onbSiretInfo?.nom) localStorage.setItem("viq_company_name", onbSiretInfo.nom);
            // Synchroniser au backend (best-effort, ne bloque pas le flow)
            apiPost("/api/users/sync", { siret: cleanSiret, company_name: onbSiretInfo?.nom || null, account_type: "entreprise" }).catch(() => {});
          } catch(_) {}
        } else {
          try { localStorage.setItem("viq_account_type", "particulier"); } catch(_) {}
        }
      } else {
        result = await signInWithEmailAndPassword(auth, cleanEmail, password);
      }
      onClose({
        method: "email",
        email: result.user.email,
        name: result.user.displayName || name.trim() || cleanEmail.split("@")[0],
        uid: result.user.uid,
      });
    } catch (err) {
      setAuthLoading(false);
      if (emailMode === "signin" && (err.code === "auth/user-not-found" || err.code === "auth/invalid-credential")) {
        try {
          const methods = await fetchSignInMethodsForEmail(auth, cleanEmail);
          if (methods && methods.length > 0 && !methods.includes("password")) {
            setAuthError("Ce compte existe mais utilise une autre méthode (" + methods.join(", ") + "). Connectez-vous avec Google.");
            return;
          }
        } catch(e) {}
      }
      setAuthError(emailErrorMsg(err));
    }
  };

  const onbInputStyle = {
    width: "100%", boxSizing: "border-box", background: "var(--card)",
    border: "1.5px solid var(--border)", borderRadius: 6, padding: ".55rem .85rem",
    color: "var(--text)", fontSize: ".88rem", outline: "none",
    fontFamily: "var(--font-body)", marginBottom: ".55rem",
  };

  const SignupScreen = (
    <div>
      <p style={{ fontSize: ".83rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1.2rem", textAlign: "center" }}>
        Créez votre compte gratuit pour accéder à votre historique et vos résultats.
      </p>

      <div style={{ marginBottom: ".8rem" }}>
        {emailMode === "reset" ? (
          <>
            <div style={{ fontSize: ".78rem", color: "var(--sub)", marginBottom: ".4rem" }}>
              Entrez votre e-mail pour recevoir un lien de réinitialisation.
            </div>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="vous@exemple.fr" autoComplete="email"
              onKeyDown={e => e.key === "Enter" && handleEmailSubmit()}
              style={onbInputStyle} />
            <button className="btn btn-accent" style={{ width: "100%" }} onClick={handleEmailSubmit} disabled={authLoading}>
              {authLoading ? "Envoi…" : "📩 Envoyer le lien"}
            </button>
            <button className="btn btn-ghost" style={{ width: "100%", marginTop: ".4rem", fontSize: ".78rem" }}
              onClick={() => { setEmailMode("signin"); setAuthError(""); setAuthInfo(""); }}>
              ← Retour
            </button>
          </>
        ) : (
          <>
            {/* Toggle signin / signup */}
            <div style={{ display: "flex", gap: ".4rem", marginBottom: ".9rem" }}>
              {[["signin","Se connecter"],["signup","Créer un compte"]].map(([m,l]) => (
                <button key={m} onClick={() => { setEmailMode(m); setAuthError(""); setAuthInfo(""); }}
                  style={{ flex: 1, padding: ".4rem .5rem", borderRadius: 6, border: "1px solid",
                    borderColor: emailMode === m ? "var(--accent)" : "var(--border)",
                    background: emailMode === m ? "rgba(168,128,58,.12)" : "transparent",
                    color: emailMode === m ? "var(--accent)" : "var(--sub)",
                    fontSize: ".78rem", fontWeight: emailMode === m ? 700 : 400, cursor: "pointer",
                    fontFamily: "var(--font-body)" }}>
                  {l}
                </button>
              ))}
            </div>

            {emailMode === "signup" && (
              <>
                {/* Toggle Particulier / Professionnel */}
                <div style={{ fontSize: ".78rem", color: "var(--sub)", fontWeight: 600, marginBottom: ".4rem" }}>
                  Vous êtes :
                </div>
                <div style={{ display: "flex", gap: ".5rem", marginBottom: ".9rem" }}>
                  {[["particulier","🚗 Particulier","Estimer / vendre mon véhicule"],["entreprise","🏢 Professionnel","Garage VO, marchand, revendeur, mandataire"]].map(([v,l,d]) => (
                    <button key={v} type="button" onClick={() => setOnbAccountType(v)}
                      style={{ flex: 1, padding: ".7rem .6rem", borderRadius: 8, border: "1.5px solid",
                        borderColor: onbAccountType === v ? "var(--accent)" : "var(--border)",
                        background: onbAccountType === v ? "rgba(168,128,58,.10)" : "var(--white)",
                        cursor: "pointer", textAlign: "left", transition: "all .15s",
                        fontFamily: "var(--font-body)" }}>
                      <div style={{ fontSize: ".85rem", fontWeight: 700,
                        color: onbAccountType === v ? "var(--accent)" : "var(--text)" }}>{l}</div>
                      <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: 2, lineHeight: 1.25 }}>{d}</div>
                    </button>
                  ))}
                </div>
                <input type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder={onbAccountType === "entreprise" ? "Nom du contact ou raison sociale" : "Prénom ou nom"} autoComplete="name"
                  style={onbInputStyle} />
                {/* SIRET — visible uniquement si Professionnel */}
                {onbAccountType === "entreprise" && (
                  <>
                    <input type="text" value={onbSiret}
                      onChange={e => lookupOnbSiret(e.target.value)}
                      placeholder="Numéro de SIRET (14 chiffres)"
                      autoComplete="off"
                      style={onbInputStyle} />
                    {onbSiretSearching && (
                      <div style={{ fontSize: ".7rem", color: "var(--muted)", marginBottom: ".5rem" }}>
                        Recherche en cours…
                      </div>
                    )}
                    {onbSiretInfo && (
                      <div style={{ fontSize: ".72rem", color: "var(--green, var(--green))", background: "rgba(4,120,87,.06)", border: "1px solid rgba(4,120,87,.18)", borderRadius: 4, padding: ".5rem .7rem", marginBottom: ".55rem" }}>
                        ✓ <strong>{onbSiretInfo.nom}</strong>
                        <div style={{ fontSize: ".66rem", color: "var(--sub)", marginTop: ".15rem" }}>
                          SIREN {onbSiretInfo.siren} · {onbSiretInfo.activite}
                        </div>
                      </div>
                    )}
                    {onbSiretError && (
                      <div style={{ fontSize: ".7rem", color: "var(--accent2)", marginBottom: ".5rem" }}>
                        ⚠ {onbSiretError}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="vous@exemple.fr" autoComplete="email"
              style={onbInputStyle} />
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Mot de passe (6 caractères min)"
              autoComplete={emailMode === "signup" ? "new-password" : "current-password"}
              onKeyDown={e => e.key === "Enter" && handleEmailSubmit()}
              style={onbInputStyle} />

            <button className="btn btn-accent" style={{ width: "100%" }} onClick={handleEmailSubmit} disabled={authLoading}>
              {authLoading ? <><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2, display: "inline-block", marginRight: ".4rem" }} />{emailMode === "signup" ? "Création…" : "Connexion…"}</> : (emailMode === "signup" ? "Créer mon compte" : "Continuer")}
            </button>

            {emailMode === "signin" && (
              <button className="btn btn-ghost" style={{ width: "100%", marginTop: ".3rem", fontSize: ".72rem", color: "var(--muted)" }}
                onClick={() => { setEmailMode("reset"); setAuthError(""); setAuthInfo(""); }}>
                Mot de passe oublié ?
              </button>
            )}
          </>
        )}

        {/* Séparateur "ou" + Google (visible en signin et signup, pas en reset) */}
        {emailMode !== "reset" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: ".7rem", margin: "1rem 0", color: "var(--muted)", fontSize: ".7rem" }}>
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
              <span>OU</span>
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            </div>
            <button className="auth-method-btn" onClick={handleGoogle} disabled={authLoading}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: ".8rem" }}>
              {authLoading ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : <GoogleSVG />}
              <span>{authLoading ? "Connexion…" : (emailMode === "signup" ? "S'inscrire avec Google" : "Continuer avec Google")}</span>
            </button>
          </>
        )}
      </div>

      {authError && (
        <div style={{ padding: ".5rem .8rem", background: "rgba(255,60,92,.08)", border: "1px solid rgba(255,60,92,.25)", borderRadius: 4, fontSize: ".75rem", color: "var(--red)", marginBottom: ".8rem" }}>
          ⚠ {authError}
        </div>
      )}
      {authInfo && (
        <div style={{ padding: ".5rem .8rem", background: "rgba(0,200,100,.08)", border: "1px solid rgba(0,200,100,.25)", borderRadius: 4, fontSize: ".75rem", color: "var(--green)", marginBottom: ".8rem" }}>
          ✓ {authInfo}
        </div>
      )}
      <div style={{ textAlign: "center", fontSize: ".68rem", color: "var(--muted)" }}>
        Authentification sécurisée Firebase
      </div>
    </div>
  );

  const SCREENS = [
    {
      // ── Écran 1 : Présentation ──────────────────────────────────────────
      header: (
        <>
          <div className="onboarding-badge">✦ Plateforme B2B — Données SIV officielles</div>
          <div className="onboarding-logo">Vehicle<span>IQ</span></div>
          <div className="onboarding-tagline">Intelligence automobile · AI & API Plaque immatriculation</div>
        </>
      ),
      body: (
        <>
          <p style={{ fontSize: ".85rem", color: "var(--sub)", lineHeight: 1.7, marginBottom: "1.2rem" }}>
            VehicleIQ est la plateforme d'intelligence automobile qui vous donne accès à l'information dont vous avez besoin pour prendre les meilleures décisions sur le marché de l'occasion.
          </p>
          <div className="onboarding-steps">
            {[
              { icon: "🔍", title: "Contrôler un véhicule", desc: "Rapport complet via VIN ou plaque : sinistres, kilométrage, propriétaires, valeur marché et score de fiabilité." },
              { icon: "🛒", title: "Acheter intelligemment", desc: "Notre IA analyse votre profil et sélectionne les meilleures annonces sur AutoScout24, LeBonCoin et Marketplace." },
              { icon: "💰", title: "Vendre au meilleur prix", desc:"Cote du prix optimal, recommandation de plateforme et génération automatique de votre annonce." },
            ].map((s, i) => (
              <div key={i} className="onboarding-step">
                <span className="onboarding-step-icon">{s.icon}</span>
                <div>
                  <div className="onboarding-step-title">{s.title}</div>
                  <div className="onboarding-step-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      ),
      cta: "Suivant →",
    },
    {
      // ── Écran 2 : Plateforme en production ──────────────────────────────
      header: (
        <>
          <div className="onboarding-badge">✦ VehicleIQ</div>
          <div className="onboarding-logo" style={{ fontSize: "2rem" }}>Vehicle<span>IQ</span></div>
          <div className="onboarding-tagline">3 services · IA · API Plaque immatriculation</div>
        </>
      ),
      body: (
        <>
          <div style={{ background: "rgba(0,200,100,.06)", border: "1px solid rgba(0,200,100,.2)", borderRadius: 6, padding: ".9rem 1.1rem", marginBottom: "1.1rem", fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.7 }}>
            <strong style={{ color: "var(--green)" }}>🚀 Disponible dès maintenant</strong><br/>
            VehicleIQ est en production. Toutes les fonctionnalités sont actives avec des données réelles. Créez votre compte et commencez à utiliser la plateforme en quelques secondes.
          </div>
          <div className="onboarding-steps">
            {[
              { icon: "🔍", title: "Contrôle par plaque ", desc: "Saisissez n'importe quelle plaque française. Données officielles SIV via API Plaque immatriculation : VIN, couleur, motorisation, CT, propriétaires." },
              { icon: "🛒", title: "Recommandations achat ", desc: "VIQ.AI® Engine analyse votre profil et vous propose 10 véhicules correspondants en 30 secondes, avec prix marché réel." },
              { icon: "💰", title:"Cote prix ", desc: "Algorithme propriétaire INPI. Calibrage temps réel LeBonCoin · AutoScout24 · La Centrale. Estimation indicative." },
              { icon: "💳", title: "Paiement sécurisé ", desc: "Stripe, paiement sécurisé. Achetez vos recharges en toute sécurité. 1 recharge = accès à un service. Valable sans limite de durée." },
            ].map((s, i) => (
              <div key={i} className="onboarding-step">
                <span className="onboarding-step-icon">{s.icon}</span>
                <div>
                  <div className="onboarding-step-title">{s.title}</div>
                  <div className="onboarding-step-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      ),
      cta: "Accéder à la plateforme →",
    },
    {
      // ── Écran 3 : Inscription Google + Téléphone ─────────────────────
      header: (
        <>
          <div className="onboarding-badge">🔐 Accès sécurisé</div>
          <div className="onboarding-logo" style={{ fontSize: "2rem" }}>Mon <span>Compte</span></div>
          <div className="onboarding-tagline">Historique · Facturation · Préférences</div>
        </>
      ),
      body: SignupScreen,
      cta: null,
    },
  ];

  const screen = SCREENS[step];
  const isSignup = step === 2;

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-panel">
        <div className="onboarding-header">
          {screen.header}
          <div style={{ display: "flex", justifyContent: "center", gap: ".4rem", marginTop: "1rem" }}>
            {SCREENS.map((_, i) => (
              <div key={i} style={{ width: i === step ? 20 : 6, height: 6, borderRadius: 3, background: i === step ? "var(--accent)" : "var(--muted)", transition: "all .3s" }} />
            ))}
          </div>
        </div>
        <div className="onboarding-body">
          {screen.body}
          {screen.cta && (
            <button className="btn btn-accent" style={{ width: "100%", marginTop: ".4rem" }}
              onClick={() => setStep(s => s + 1)}>
              {screen.cta}
            </button>
          )}
          <div className="onboarding-footer">
            <button className="onboarding-skip" onClick={() => {
              // Explicitly pass no user — ensures no ghost account
              if (typeof onClose === "function") onClose(null);
            }}>
              {isSignup ? "Explorer sans compte →" : "Passer l'introduction"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── PDF Export (jsPDF + jspdf-autotable) ────────────────────────────────────
// ─── Coordonnées légales VehicleIQ (utilisées pour factures, CGV, etc.) ────
const VEHICLEIQ_LEGAL = {
  legal_name: "M.K.L Ingénierie et Conseil",
  trade_name: "VehicleIQ",
  address_line1: "2 Avenue de la République",
  address_line2: "02100 Saint-Quentin, France",
  siret: "904 954 542 00026",
  legal_rep: "Lorentz Mboumba Bong",
  email: "contact@vehicleiq.fr",
  website: "vehicleiq.fr",
  // v8 : franchise en base de TVA — pas de TVA collectée (mention conservée,
  // seule la mention du statut juridique "auto-entreprise" a été retirée des
  // documents hors mentions légales/CGU/CGV, cf. VEHICLEIQ_LEGAL usages).
  vat_status: "TVA non applicable, art. 293 B du CGI",
};

// ─── Formatage des nombres pour les PDF (jsPDF) ──────────────────────────────
// IMPORTANT : toLocaleString("fr-FR") insère une espace fine insécable (U+202F)
// comme séparateur de milliers. La police Helvetica par défaut de jsPDF ne
// possède pas ce glyphe et l'affiche comme un "/" (ex. "37/600"). On formate
// donc nous-mêmes avec un POINT comme séparateur de milliers (37.600), supporté
// par la police. À n'utiliser QUE pour le rendu PDF, pas pour l'UI web.
function pdfNum(value) {
  if (value === null || value === undefined || value === "" || isNaN(Number(value))) return "—";
  const n = Math.round(Number(value));
  const neg = n < 0;
  const s = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return (neg ? "-" : "") + s;
}

// Récupère les coordonnées du client connecté pour l'en-tête des PDF.
// Source : window.__viq_user (tenu à jour par l'app). Retourne null si déconnecté.
function getPdfClient(override) {
  const u = override || (typeof window !== "undefined" ? window.__viq_user : null);
  if (!u) return null;
  const name = u.siret_name || u.name || (u.email ? u.email.split("@")[0] : "") || "Client";
  const lines = [];
  if (u.address) lines.push(u.address);
  if (u.siret) lines.push(`SIRET : ${u.siret}`);
  if (u.email) lines.push(u.email);
  if (u.phone) lines.push(`Tél. ${u.phone}`);
  return { name, lines };
}

// v114 — Rapport Premium client (tunnel de conversion, 4,99 €). Généré et
// téléchargé côté navigateur juste après confirmation du paiement, en
// réutilisant le même chargeur jsPDF et la même charte que la facture.
// Contenu strictement basé sur ce qui a réellement été calculé — aucune
// donnée inventée : les sections sans donnée disponible sont annoncées comme
// telles plutôt que remplies de texte générique.
async function generatePremiumReportPDF({ vehicle = {}, estimation = {}, client = {}, proName = "" }) {
  const jsPDFClass = await loadJsPDF();
  const doc = new jsPDFClass({ unit: "mm", format: "a4" });
  const W = 210, M = 15;
  const ACCENT = [168, 128, 58];
  const TEXT = [30, 30, 35];
  const SUB = [110, 110, 120];
  const GREEN = [46, 111, 92];
  let y = 20;

  // En-tête
  doc.setFont("helvetica", "bold"); doc.setFontSize(22); doc.setTextColor(...ACCENT);
  doc.text("VehicleIQ", M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...SUB);
  doc.text("VIQ.AI Engine — Rapport d'estimation Premium", M, y + 5);
  doc.text(new Date().toLocaleDateString("fr-FR"), W - M, y, { align: "right" });
  y += 14;
  doc.setDrawColor(...ACCENT); doc.setLineWidth(0.8); doc.line(M, y, W - M, y);
  y += 10;

  // Véhicule
  doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...TEXT);
  const vLabel = [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(" ") || "Véhicule";
  doc.text(vLabel, M, y); y += 7;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(...SUB);
  const idLine = [
    vehicle.plaque ? `Plaque : ${vehicle.plaque}` : null,
    vehicle.vin ? `VIN : ${vehicle.vin}` : null,
    vehicle.fuel ? `Carburant : ${vehicle.fuel}` : null,
    estimation.km_utilise ? `Kilométrage : ${pdfNum(estimation.km_utilise)} km` : null,
  ].filter(Boolean).join("   ·   ");
  if (idLine) { doc.text(idLine, M, y); y += 9; }

  // Estimation
  doc.setFillColor(250, 247, 240);
  doc.roundedRect(M, y, W - 2 * M, 34, 3, 3, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...SUB);
  doc.text("PRIX DE VENTE CONSEILLÉ", M + 6, y + 8);
  doc.setFontSize(24); doc.setTextColor(...GREEN);
  doc.text(`${pdfNum(estimation.prix_conseille || 0)} EUR`, M + 6, y + 20);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...SUB);
  doc.text(
    `Fourchette : ${pdfNum(estimation.fourchette_basse || 0)} – ${pdfNum(estimation.fourchette_haute || 0)} EUR    ·    Indice de confiance : ${estimation.confiance ?? "—"}%`,
    M + 6, y + 28
  );
  y += 44;

  // Méthodologie — transparente, pas de promesse excessive
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...TEXT);
  doc.text("Comment cette estimation est calculée", M, y); y += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...SUB);
  const method = doc.splitTextToSize(
    "L'estimation combine l'identification officielle du véhicule (Système d'Immatriculation des Véhicules) "
    + "et l'algorithme VIQ.AI Engine, qui pondère l'âge, le kilométrage, la motorisation et la dynamique du "
    + "marché de l'occasion. L'indice de confiance reflète la quantité d'informations réellement disponibles "
    + "sur ce véhicule : il augmente lorsque le kilométrage et l'historique sont renseignés.",
    W - 2 * M
  );
  doc.text(method, M, y); y += method.length * 4.6 + 6;

  if (!estimation.km_utilise) {
    doc.setTextColor(...ACCENT); doc.setFont("helvetica", "bold"); doc.setFontSize(9.5);
    const warn = doc.splitTextToSize(
      "Note : cette estimation a été calculée sans kilométrage renseigné. Le kilométrage est le facteur "
      + "le plus influent après l'âge : communiquez-le à votre professionnel pour affiner la valeur.",
      W - 2 * M
    );
    doc.text(warn, M, y); y += warn.length * 4.6 + 6;
  }

  // Conseils de vente
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...TEXT);
  doc.text("Recommandations pour vendre au meilleur prix", M, y); y += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...SUB);
  [
    "Rassemblez le carnet d'entretien et les factures : un historique documenté rassure et se paie.",
    "Photographiez le véhicule en extérieur, de jour : 3/4 avant, 3/4 arrière, intérieur, compteur.",
    "Présentez un contrôle technique récent : c'est la première question de tout acheteur.",
    "Positionnez votre prix dans le haut de la fourchette si l'état et l'historique sont irréprochables.",
    "Un véhicule propre et détaillé se vend plus vite, souvent au-dessus de la cote moyenne.",
  ].forEach(t => {
    const lines = doc.splitTextToSize("•  " + t, W - 2 * M);
    doc.text(lines, M, y); y += lines.length * 4.6 + 1.5;
  });
  y += 6;

  // Contact pro
  if (proName) {
    doc.setFillColor(246, 243, 236);
    doc.roundedRect(M, y, W - 2 * M, 20, 3, 3, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...TEXT);
    doc.text("Votre interlocuteur", M + 6, y + 8);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...SUB);
    doc.text(`${proName} dispose de ce rapport et vous recontactera pour finaliser votre projet.`, M + 6, y + 15);
    y += 28;
  }

  // Pied de page légal
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(...SUB);
  const legal = doc.splitTextToSize(
    "Les données et cotes affichées sont générées à titre purement indicatif et ne constituent pas un engagement "
    + "contractuel (art. 1112-1 C. civ. · art. L.111-1 C. conso.). VehicleIQ ne saurait être tenu responsable des "
    + "décisions prises sur la base de ces informations.  ·  vehicleiq.fr — M.K.L Ingénierie et Conseil",
    W - 2 * M
  );
  doc.text(legal, M, 285);

  const slug = (vLabel || "vehicule").replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
  doc.save(`VehicleIQ-rapport-premium-${slug}.pdf`);
}

// ─── Génération PDF facture client ─────────────────────────────────────────
// Format A4, charte VehicleIQ (doré antique #A8803A), mentions CGV obligatoires.
// `billingItem` : { date, plan, price, mode, id? }
// `userInfo`    : { email, name, siret, siret_name, address, phone }
async function generateInvoicePDF(billingItem, userInfo) {
  const jsPDFClass = await loadJsPDF();
  const doc = new jsPDFClass({ unit: "mm", format: "a4" });
  const W = 210, H = 297;
  const M = 15; // marge

  // Couleurs charte
  const ACCENT = [168, 128, 58];   // doré antique
  const TEXT = [30, 30, 35];
  const SUB = [110, 110, 120];
  const BORDER = [220, 220, 225];

  // ── En-tête : logo + numéro facture
  const invoiceDate = new Date(billingItem.date);
  const invoiceNum = "VIQ-" + invoiceDate.getFullYear()
    + String(invoiceDate.getMonth() + 1).padStart(2, "0")
    + "-" + (billingItem.id ? String(billingItem.id).slice(-6).toUpperCase() : Math.random().toString(36).slice(2, 8).toUpperCase());

  // Logo + nom
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...ACCENT);
  doc.text("VehicleIQ", M, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SUB);
  doc.text("Cote VO professionnelle", M, 27);

  // Bandeau "FACTURE" à droite
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...TEXT);
  doc.text("FACTURE", W - M, 22, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SUB);
  doc.text(`N° ${invoiceNum}`, W - M, 28, { align: "right" });
  doc.text(`Émise le ${invoiceDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}`, W - M, 32, { align: "right" });

  // Ligne de séparation
  doc.setDrawColor(...ACCENT);
  doc.setLineWidth(0.6);
  doc.line(M, 38, W - M, 38);

  // ── Bloc émetteur (gauche) et client (droite)
  let y = 48;
  doc.setFontSize(8);
  doc.setTextColor(...SUB);
  doc.setFont("helvetica", "bold");
  doc.text("ÉMETTEUR", M, y);
  doc.text("CLIENT", W / 2 + 5, y);

  y += 6;
  doc.setFontSize(9.5);
  doc.setTextColor(...TEXT);
  doc.setFont("helvetica", "bold");
  doc.text(VEHICLEIQ_LEGAL.legal_name, M, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const emitterLines = [
    VEHICLEIQ_LEGAL.trade_name,
    VEHICLEIQ_LEGAL.address_line1,
    VEHICLEIQ_LEGAL.address_line2,
    `SIRET : ${VEHICLEIQ_LEGAL.siret}`,
    VEHICLEIQ_LEGAL.email,
    VEHICLEIQ_LEGAL.website,
  ];
  let ey = y + 4;
  emitterLines.forEach(line => { doc.text(line, M, ey); ey += 4; });

  // Bloc client (droite)
  doc.setFontSize(9.5);
  doc.setFont("helvetica", "bold");
  const clientName = userInfo?.siret_name || userInfo?.name || (userInfo?.email ? userInfo.email.split("@")[0] : "Client");
  doc.text(clientName, W / 2 + 5, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const clientLines = [];
  if (userInfo?.address) clientLines.push(userInfo.address);
  if (userInfo?.siret) clientLines.push(`SIRET : ${userInfo.siret}`);
  if (userInfo?.email) clientLines.push(userInfo.email);
  if (userInfo?.phone) clientLines.push(`Tél. ${userInfo.phone}`);
  if (clientLines.length === 0) clientLines.push("(coordonnées non renseignées)");
  let cy = y + 4;
  clientLines.forEach(line => { doc.text(line, W / 2 + 5, cy); cy += 4; });

  // ── Tableau des prestations
  y = Math.max(ey, cy) + 10;

  // Parse du prix (peut être "29,90 €" ou "3,90 €")
  const priceStr = String(billingItem.price || "0").replace(/[^\d,\.]/g, "").replace(",", ".");
  const priceNum = parseFloat(priceStr) || 0;

  // Désignation lisible
  const planLabel = (() => {
    const p = String(billingItem.plan || "").toLowerCase();
    if (p.includes("subscription_unlimited")) return "Abonnement Illimité — Estimations illimitées (forfait mensuel)";
    if (p.includes("subscription_multi_agent")) return "Abonnement Pro Multi-Agent — 3 agents, estimations illimitées (forfait mensuel)";
    if (p.includes("renewal")) return "Renouvellement d'abonnement mensuel";
    if (p.includes("pack") || p.includes("crédit") || p.includes("estimation")) return billingItem.plan;
    return billingItem.plan || "Service VehicleIQ";
  })();

  // En-tête tableau
  const tableX = M;
  const tableW = W - 2 * M;
  doc.setFillColor(245, 240, 230);
  doc.rect(tableX, y, tableW, 9, "F");
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...TEXT);
  doc.text("DÉSIGNATION", tableX + 3, y + 6);
  doc.text("QTÉ", tableX + tableW - 60, y + 6);
  doc.text("PU HT", tableX + tableW - 40, y + 6);
  doc.text("MONTANT", tableX + tableW - 18, y + 6, { align: "left" });

  y += 13;
  // Ligne unique de prestation
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  // Découper le label en plusieurs lignes si trop long
  const labelLines = doc.splitTextToSize(planLabel, tableW - 65);
  labelLines.forEach((line, i) => {
    doc.text(line, tableX + 3, y + (i * 4.5));
  });
  doc.text("1", tableX + tableW - 60, y);
  doc.text(`${priceNum.toFixed(2).replace(".", ",")} €`, tableX + tableW - 40, y);
  doc.setFont("helvetica", "bold");
  doc.text(`${priceNum.toFixed(2).replace(".", ",")} €`, tableX + tableW - 3, y, { align: "right" });

  y += Math.max(9, labelLines.length * 4.5 + 4);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.line(tableX, y, tableX + tableW, y);

  // ── Totaux (alignés à droite)
  y += 7;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...SUB);
  doc.text("Total HT", tableX + tableW - 50, y);
  doc.setTextColor(...TEXT);
  doc.text(`${priceNum.toFixed(2).replace(".", ",")} €`, tableX + tableW - 3, y, { align: "right" });

  y += 5;
  doc.setTextColor(...SUB);
  doc.text(`TVA (${VEHICLEIQ_LEGAL.vat_status})`, tableX + tableW - 90, y);
  doc.setTextColor(...TEXT);
  doc.text("0,00 €", tableX + tableW - 3, y, { align: "right" });

  y += 8;
  doc.setFillColor(245, 240, 230);
  doc.rect(tableX + tableW - 65, y - 5, 65, 10, "F");
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...ACCENT);
  doc.text("TOTAL TTC", tableX + tableW - 60, y + 1);
  doc.text(`${priceNum.toFixed(2).replace(".", ",")} €`, tableX + tableW - 3, y + 1, { align: "right" });

  // ── Statut paiement
  y += 18;
  doc.setFillColor(46, 111, 92);  // vert
  doc.roundedRect(M, y, 50, 8, 1.5, 1.5, "F");
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("✓ PAYÉE", M + 4, y + 5.5);

  doc.setTextColor(...SUB);
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  const modeLabel = billingItem.mode === "wallet" ? "Crédits wallet" : "Carte bancaire (Stripe)";
  doc.text(`Mode de paiement : ${modeLabel} — ${invoiceDate.toLocaleDateString("fr-FR")}`, M + 55, y + 5.5);

  // ── Mentions CGV (bas de page)
  const footerY = H - 50;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.line(M, footerY, W - M, footerY);

  doc.setFontSize(7);
  doc.setTextColor(...SUB);
  doc.setFont("helvetica", "bold");
  doc.text("CONDITIONS GÉNÉRALES DE VENTE", M, footerY + 5);
  doc.setFont("helvetica", "normal");

  const cgvText = [
    "Paiement comptant à la commande par carte bancaire via Stripe. Aucun escompte pour paiement anticipé.",
    "Les prestations VehicleIQ (cotes, recommandations, analyses) sont des services numériques exécutés immédiatement —",
    "le droit de rétractation prévu à l'article L221-18 du Code de la consommation ne s'applique pas (article L221-28, 13°).",
    "Toute contestation devra être formulée par écrit à contact@vehicleiq.fr dans les 30 jours suivant la facturation.",
    "Les conditions complètes sont consultables sur vehicleiq.fr (rubrique « Conditions Générales de Vente »).",
    "TVA non applicable, art. 293 B du CGI (régime de la franchise en base de TVA).",
    "En cas de retard de paiement : pénalités au taux d'intérêt légal majoré + indemnité forfaitaire de 40 € (art. L441-10 du Code de commerce).",
  ];
  let fy = footerY + 10;
  cgvText.forEach(line => { doc.text(line, M, fy); fy += 3.2; });

  // Pied de page
  doc.setFontSize(6.5);
  doc.setTextColor(...SUB);
  doc.text(
    `${VEHICLEIQ_LEGAL.legal_name} · SIRET ${VEHICLEIQ_LEGAL.siret} · ${VEHICLEIQ_LEGAL.address_line2} · ${VEHICLEIQ_LEGAL.email}`,
    W / 2, H - 8, { align: "center" }
  );

  // ── Sauvegarde
  const safeName = clientName.replace(/[^\w\d-]/g, "_").slice(0, 30);
  doc.save(`Facture_${invoiceNum}_${safeName}.pdf`);
}

// v26 — Convertit un SVG (chaîne) en PNG (data URL) via un <canvas> navigateur,
// pour l'intégrer dans un PDF jsPDF (qui ne sait pas dessiner du SVG
// directement). Résout avec null en cas d'échec — non bloquant pour l'export.
function svgToPngDataUrl(svgString, width = 320, height = 520) {
  return new Promise((resolve) => {
    try {
      const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = width; canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/png"));
        } catch (e) { URL.revokeObjectURL(url); resolve(null); }
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    } catch (e) { resolve(null); }
  });
}

async function loadJsPDF() {
  if (window.jspdf && window.jspdf.jsPDF && window.jspdf.jsPDF.API.autoTable) {
    return window.jspdf.jsPDF;
  }
  // Charger jsPDF
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  // Charger jspdf-autotable (extension qui ajoute doc.autoTable())
  if (!window.jspdf.jsPDF.API.autoTable) {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return window.jspdf.jsPDF;
}

// v54 — Chargeur SheetJS (import CSV/XLSX de prospects), même schéma que
// loadJsPDF ci-dessus : script CDN chargé une seule fois, mis en cache sur window.
async function loadXLSX() {
  if (window.XLSX) return window.XLSX;
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.XLSX;
}

// Parseur CSV minimal mais robuste aux champs entre guillemets (virgules ou
// points-virgules internes, guillemets échappés en "") — pas de dépendance
// nécessaire pour un usage aussi simple (import de contacts).
function parseCSVText(text) {
  const delim = (text.split("\n")[0].split(";").length > text.split("\n")[0].split(",").length) ? ";" : ",";
  const rows = [];
  let row = [], field = "", inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], next = text[i + 1];
    if (inQuotes) {
      if (c === '"' && next === '"') { field += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === delim) { row.push(field); field = ""; }
      else if (c === "\n" || c === "\r") {
        if (c === "\r" && next === "\n") i++;
        row.push(field); field = "";
        if (row.some(f => f.trim() !== "")) rows.push(row);
        row = [];
      } else field += c;
    }
  }
  if (field || row.length) { row.push(field); if (row.some(f => f.trim() !== "")) rows.push(row); }
  if (!rows.length) return [];
  // v60 — Garde le texte ORIGINAL des en-têtes (juste .trim(), pas de
  // minuscule/renommage) : la nouvelle étape de correspondance de colonnes a
  // besoin d'afficher les en-têtes tels que l'utilisateur les reconnaît.
  const header = rows[0].map(h => h.trim());
  return rows.slice(1).map(r => {
    const obj = {};
    header.forEach((h, i) => { obj[h] = (r[i] || "").trim(); });
    return obj;
  });
}

// v60 — Suggestion de correspondance automatique (best-effort) entre les
// en-têtes du fichier importé et les champs attendus — toujours CONFIRMÉE
// par l'utilisateur avant import (voir l'étape "mapping" de ProspectionAgent),
// jamais appliquée silencieusement : c'était exactement le bug remonté
// (lignes ignorées sans explication quand l'en-tête ne correspondait à
// aucun des alias connus).
const IMPORT_COLUMN_ALIASES = {
  name: ["nom", "name", "société", "societe", "entreprise", "company", "nom complet", "raison sociale", "client"],
  email: ["email", "e-mail", "mail", "courriel", "adresse email", "adresse e-mail"],
  phone: ["téléphone", "telephone", "phone", "tél", "tel", "numéro", "numero", "mobile", "portable"],
  city: ["ville", "city", "localité", "localite", "commune"],
  region: ["région", "region", "département", "departement"],
  website: ["site", "website", "site web", "url", "lien"],
};
function guessImportColumnMap(headers) {
  const map = {};
  for (const [key, aliases] of Object.entries(IMPORT_COLUMN_ALIASES)) {
    const found = headers.find(h => aliases.includes(h.trim().toLowerCase()));
    if (found) map[key] = found;
  }
  return map;
}


// Fait correspondre les en-têtes de colonnes usuels (FR/EN) aux champs
// attendus par /api/prospection/import — tolérant aux variations courantes.


// Chargeur dynamique de pdf-lib (pour remplir le CERFA AcroForm officiel).
async function loadPdfLib() {
  if (window.PDFLib) return window.PDFLib;
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf-lib/1.17.1/pdf-lib.min.js";
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.PDFLib;
}

// Chargeur dynamique de pdf.js (extraction de texte d'une fiche annonce importée).
let __viqPdfjsPromise = null;
function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (__viqPdfjsPromise) return __viqPdfjsPromise;
  __viqPdfjsPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    s.onload = () => {
      try { if (window.pdfjsLib) window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js"; } catch (_) {}
      resolve(window.pdfjsLib);
    };
    s.onerror = () => reject(new Error("pdf.js load failed"));
    document.head.appendChild(s);
  });
  return __viqPdfjsPromise;
}

// Extrait le texte brut d'un fichier PDF (toutes pages) côté client.
async function extractPdfText(file) {
  const pdfjs = await loadPdfJs();
  const buf = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: buf }).promise;
  let text = "";
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map(it => it.str).join(" ") + "\n";
  }
  return text;
}

// Parse une fiche annonce (type mobile.de / AutoScout) → champs véhicule + prix.
// Best-effort : les champs sont pré-remplis puis vérifiables/éditables par l'utilisateur.
function parseListingFields(text) {
  const t = (text || "").replace(/\u00A0/g, " ");
  const numFR = (s) => Number(String(s).replace(/[^\d]/g, "")) || null;
  const out = { makeModel: "", year: "", km: "", fuel: "", gearbox: "", vin: "", immat: "", priceTtc: null, priceHt: null };

  // Prix net (HT) explicite : "€164,529 (net)" ou "164 529 € (net)"
  let mNet = t.match(/€?\s*([\d.,\s]{4,})\s*€?\s*\(net\)/i);
  if (mNet) out.priceHt = numFR(mNet[1]);

  // Prix affiché (TTC) : on prend le plus grand montant € plausible (hors mensualité "per month").
  const cleaned = t.replace(/from\s*€?\s*[\d.,]+\s*(per month|\/\s*mois|par mois)/gi, " ");
  const euros = [];
  const re = /€\s*([\d][\d.,\s]{2,})|([\d][\d.,\s]{2,})\s*€/g; let mm;
  while ((mm = re.exec(cleaned)) !== null) { const v = numFR(mm[1] || mm[2]); if (v && v >= 3000 && v <= 5000000) euros.push(v); }
  if (euros.length) out.priceTtc = Math.max(...euros);
  if (out.priceHt && out.priceTtc && out.priceHt > out.priceTtc) { const x = out.priceHt; out.priceHt = out.priceTtc; out.priceTtc = x; }

  // Kilométrage
  let mKm = t.match(/Mileage[^\d]{0,12}([\d.,\s]{2,})\s*km/i) || t.match(/([\d.,\s]{3,})\s*km\b/i);
  if (mKm) out.km = numFR(mKm[1]);

  // Énergie
  let mFuel = t.match(/Fuel\s*type[^A-Za-z]{0,8}([A-Za-zéè]+)/i) || t.match(/\b(petrol|diesel|electric|hybrid|essence|gpl)\b/i);
  if (mFuel) {
    const f = mFuel[1].toLowerCase();
    out.fuel = f.startsWith("petrol") || f.startsWith("essence") ? "Essence"
      : f.startsWith("diesel") ? "Diesel"
      : f.startsWith("electric") ? "Électrique"
      : f.startsWith("hybrid") ? "Hybride"
      : f.startsWith("gpl") ? "GPL" : mFuel[1];
  }

  // Boîte
  let mG = t.match(/transmission[^A-Za-z]{0,8}([A-Za-z]+)/i) || t.match(/\b(Automatic|Manual|Automatique|Manuelle)\b/i);
  if (mG) { const g = mG[1].toLowerCase(); out.gearbox = g.startsWith("auto") ? "Automatique" : g.startsWith("man") ? "Manuelle" : mG[1]; }

  // 1re immatriculation MM/AAAA → année
  let mReg = t.match(/First\s*registration[^\d]{0,8}(\d{1,2}\/\d{4})/i) || t.match(/\b(\d{2}\/\d{4})\b/);
  if (mReg) { const y = mReg[1].match(/(\d{4})/); if (y) out.year = y[1]; out.firstReg = mReg[1]; }

  // VIN (17 caractères) si présent
  let mVin = t.match(/\b([A-HJ-NPR-Z0-9]{17})\b/);
  if (mVin) out.vin = mVin[1];

  // Marque / modèle — heuristique par préfixe de marque connue
  const BRANDS = "Porsche|BMW|Mercedes-Benz|Mercedes|Audi|Volkswagen|VW|Renault|Peugeot|Citro[eë]n|DS|Tesla|Toyota|Dacia|Ford|Opel|Fiat|Nissan|Hyundai|Kia|Seat|Skoda|Volvo|Mini|Mazda|Honda|Suzuki|Jeep|Land\\s*Rover|Range\\s*Rover|Cupra|Alfa\\s*Romeo|Ferrari|Lamborghini|Maserati|Bentley|Aston\\s*Martin|McLaren|Jaguar";
  let mMk = t.match(new RegExp("\\b(" + BRANDS + ")\\b[^\\n€]{0,70}", "i"));
  if (mMk) out.makeModel = mMk[0].replace(/[*]+/g, " ").replace(/\s{2,}/g, " ").replace(/\s+(Mileage|Performance|Fuel|from)\b.*$/i, "").trim();

  return out;
}


// URL du CERFA 15776 en version AcroForm pure (XFA neutralisé), déposé à la
// racine du frontend (comme le PDF des CGV). Modifier ici si le chemin change.
const CERFA_CESSION_URL = "/cerfa_15776_acroform.pdf";

// Remplit le CERFA de cession officiel et déclenche le téléchargement.
// `d` : objet plat avec les valeurs du formulaire (voir CessionModal).
async function exportCessionCerfa(d) {
  const PDFLib = await loadPdfLib();
  const resp = await fetch(CERFA_CESSION_URL);
  if (!resp.ok) throw new Error("CERFA introuvable (à déposer sur le serveur)");
  const bytes = await resp.arrayBuffer();
  const pdf = await PDFLib.PDFDocument.load(bytes);
  const form = pdf.getForm();
  // Le CERFA a 2 pages (exemplaire vendeur + exemplaire acheteur) avec les MÊMES
  // champs sous Page1[0]. et Page2[0]. — on remplit les deux.
  const PAGES = ["topmostSubform[0].Page1[0].", "topmostSubform[0].Page2[0]."];
  const setT = (name, val) => {
    if (val == null || val === "") return;
    PAGES.forEach(P => { try { form.getTextField(P + name).setText(String(val)); } catch (e) {} });
  };
  const setR = (name, val) => { PAGES.forEach(P => { try { form.getRadioGroup(P + name).select(String(val)); } catch (e) {} }); };
  const setC = (name, on) => { PAGES.forEach(P => { try { const c = form.getCheckBox(P + name); on ? c.check() : c.uncheck(); } catch (e) {} }); };

  // Découpage date "JJ/MM/AAAA" → [J, M, AAAA]
  const splitDate = (s) => {
    if (!s) return ["", "", ""];
    const m = String(s).match(/(\d{2})\D(\d{2})\D(\d{2,4})/);
    return m ? [m[1], m[2], m[3].slice(-2)] : ["", "", ""];
  };

  // ── Véhicule ──
  setT("num_Immatriculation[0]", d.immat);
  setT("num_Identification[0]", d.vin);
  const [bj, bm, ba] = splitDate(d.date1ere);
  setT("num_DateImmatriculationJour[0]", bj);
  setT("num_DateImmatriculationMois[0]", bm);
  setT("num_DateImmatriculationAnnée[0]", ba);
  setT("txt_MarqueVéhicule[0]", d.marque);
  setT("txt_TypeVarianteVersionVéhicule[0]", d.type);
  setT("txt_GenreNational[0]", d.genre);
  setT("txt_DénominationCommerciale[0]", d.denomination);
  setT("num_KilométrageCompteur[0]", d.km);
  setT("num_Formule[0]", d.numFormule);

  // ── Vendeur (ancien propriétaire) ──
  setR("Groupe_de_boutons_radio1[0]", d.vendeurType === "morale" ? "2" : "1");
  if (d.vendeurType !== "morale") setR("Groupe_de_boutons_radio2[0]", d.vendeurSexe === "F" ? "2" : "1");
  setT("txt_IdentitéVendeur[0]", d.vendeurNom);
  setT("Num_Siret[0]", d.vendeurSiret);
  setT("num_VoieAdresse[0]", d.vendeurNumVoie);
  setT("txt_TypeVoieAdresse[0]", d.vendeurTypeVoie);
  setT("txt_NomVoie[0]", d.vendeurNomVoie);
  setT("num_CodePostalAdresse[0]", d.vendeurCP);
  setT("txt_CommuneAdresse[0]", d.vendeurCommune);
  setR("Groupe_de_boutons_radio3[0]", "1"); // céder (pas pour destruction)
  const [vj, vm, va] = splitDate(d.dateVente);
  setT("num_DateVenteJour[0]", vj);
  setT("num_DateVenteMois[0]", vm);
  setT("num_DateVenteAnnée[0]", va);
  if (d.heureVente) {
    const hh = String(d.heureVente).split(/[:hH]/);
    setT("num_HoraireVente1[0]", (hh[0] || "").padStart(2, "0"));
    setT("num_HoraireVente2[0]", (hh[1] || "00").padStart(2, "0"));
  }
  setC("ckb_ValidationDéclaration1[0]", true);  // certificat situation administrative remis
  setC("ckb_ValidationDéclaration2[0]", true);  // pas de transformation notable
  setT("txt_LieuDéclaration1[0]", d.lieu);
  setT("num_DateDéclaration[0]", d.dateVente);

  // ── Acheteur (nouveau propriétaire) ──
  setR("Groupe_de_boutons_radio4[0]", d.acheteurType === "morale" ? "2" : "1");
  if (d.acheteurType !== "morale") setR("Groupe_de_boutons_radio5[0]", d.acheteurSexe === "F" ? "2" : "1");
  setT("txt_IdentitéAcheteur[0]", d.acheteurNom);
  setT("num_SiretAcheteur[0]", d.acheteurSiret);
  const [nj, nm, na] = splitDate(d.acheteurNaissance);
  setT("num_DateNaissanceAcheteurJ[0]", nj);
  setT("num_DateNaissanceAcheteurM[0]", nm);
  setT("num_DateNaissanceAcheteurA[0]", na);
  setT("txt_LieuNaissanceAcheteur[0]", d.acheteurLieuNaissance);
  setT("num_VoieAdresseAcheteur[0]", d.acheteurNumVoie);
  setT("txt_TypeVoieAdresseAcheteur[0]", d.acheteurTypeVoie);
  setT("txt_NomVoieAdresseAcheteur[0]", d.acheteurNomVoie);
  setT("num_CodePostalAdresseAcheteur[0]", d.acheteurCP);
  setT("txt_CommuneAdresseAcheteur[0]", d.acheteurCommune);
  setC("ckb_ValidationDéclarationA1[0]", true); // acquérir le véhicule
  setC("ckb_ValidationDéclarationA2[0]", true); // informé de la situation administrative
  setT("txt_LieuDéclaration2[0]", d.lieu);
  setT("txt_dateDéclaration[0]", d.dateVente);

  // Forcer le rendu des champs par les lecteurs PDF
  try { form.acroForm.dict.set(PDFLib.PDFName.of("NeedAppearances"), PDFLib.PDFBool.True); } catch (e) {}

  const out = await pdf.save();
  const blob = new Blob([out], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Certificat_cession_${(d.immat || "vehicule").replace(/[^a-z0-9]/gi, "_")}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// ═══════════════════════════════════════════════════════════════════════
// VehicleIQ PDF Design System (v3 — 29/05/2026)
// Style harmonisé sur la facture : fond blanc, en-tête sobre avec logo
// doré + titre majuscule, bandes émetteur/client, tableaux clairs avec
// header beige, palette charte (doré antique #A8803A).
// ═══════════════════════════════════════════════════════════════════════

const PDF3 = {
  // Palette charte (RGB)
  COL: {
    ACCENT:    [168, 128, 58],   // doré antique #A8803A
    ACCENT_LT: [245, 240, 230],  // beige clair (fond entêtes tableaux)
    TEXT:      [30, 30, 35],     // texte principal #1E1E23
    SUB:       [110, 110, 120],  // texte secondaire #6E6E78
    MUTED:     [160, 160, 168],  // texte très discret
    BORDER:    [220, 220, 225],  // séparateurs
    WHITE:     [255, 255, 255],
    GREEN:     [46, 111, 92],    // var(--green)
    RED:       [155, 44, 44],    // var(--red)
    BG:        [250, 250, 247],  // fond crème optionnel
  },
  // Layout A4 portrait
  L: 15,   // marge gauche
  R: 15,   // marge droite
  T: 20,   // marge haute
  B: 20,   // marge basse
  W: 210,  // largeur A4
  H: 297,  // hauteur A4
  CW: 180, // largeur contenu (W - 2*L)
  MONTHS_FR: ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"],
};

// ─── Date courante formatée FR ───────────────────────────────────────────
function pdf3DateFr(d = new Date()) {
  return `${d.getDate()} ${PDF3.MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

// ─── Référence document formatée VIQ-XXX-YYYYMM-XXXXXX ───────────────────
function pdf3Ref(prefix = "DOC") {
  const d = new Date();
  return `VIQ-${prefix}-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

// ─── En-tête de document (style facture) ────────────────────────────────
// Logo "VehicleIQ" doré à gauche + tagline, gros titre majuscule à droite,
// numéro de référence + date, ligne de séparation dorée.
// Retourne le Y après le header pour continuer le contenu.
function pdf3Header(doc, opts) {
  const { docTitle = "RAPPORT", tagline = "Estimation VO professionnelle", ref = "", date = pdf3DateFr() } = opts || {};
  const W = PDF3.W, L = PDF3.L, R = PDF3.R;

  // Logo VehicleIQ doré
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("VehicleIQ", L, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(tagline, L, 27);

  // Titre document à droite (majuscules)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(docTitle, W - R, 22, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PDF3.COL.SUB);
  if (ref) doc.text(`N° ${ref}`, W - R, 28, { align: "right" });
  doc.text(`Émis le ${date}`, W - R, 32, { align: "right" });

  // Ligne de séparation dorée
  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.6);
  doc.line(L, 38, W - R, 38);

  return 48; // Y de départ pour le contenu
}

// ─── Bloc émetteur/client (côte à côte) ──────────────────────────────────
// Émetteur (gauche) = VehicleIQ ; Client (droite) = données passées.
// Retourne le Y après le bloc.
function pdf3PartiesBlock(doc, y, opts) {
  const { client = {}, showEmitter = true } = opts || {};
  const W = PDF3.W, L = PDF3.L;
  const colRight = W / 2 + 5;

  // Labels ÉMETTEUR / CLIENT
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.setFont("helvetica", "bold");
  if (showEmitter) doc.text("ÉMETTEUR", L, y);
  doc.text("CLIENT", colRight, y);

  y += 6;
  let leftY = y, rightY = y;

  if (showEmitter) {
    // Bloc émetteur (VehicleIQ)
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...PDF3.COL.TEXT);
    doc.text(VEHICLEIQ_LEGAL.legal_name, L, leftY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    const lines = [
      VEHICLEIQ_LEGAL.trade_name,
      VEHICLEIQ_LEGAL.address_line1,
      VEHICLEIQ_LEGAL.address_line2,
      `SIRET : ${VEHICLEIQ_LEGAL.siret}`,
      VEHICLEIQ_LEGAL.email,
      VEHICLEIQ_LEGAL.website,
    ];
    leftY += 4;
    lines.forEach(l => { doc.text(l, L, leftY); leftY += 4; });
  }

  // Bloc client (droite)
  doc.setFontSize(9.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF3.COL.TEXT);
  const clientName = client.siret_name || client.name || (client.email ? client.email.split("@")[0] : "Client");
  doc.text(clientName, colRight, rightY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const clientLines = [];
  if (client.address) clientLines.push(client.address);
  if (client.siret) clientLines.push(`SIRET : ${client.siret}`);
  if (client.email) clientLines.push(client.email);
  if (client.phone) clientLines.push(`Tél. ${client.phone}`);
  if (clientLines.length === 0) clientLines.push("(coordonnées non renseignées)");
  rightY += 4;
  clientLines.forEach(l => { doc.text(l, colRight, rightY); rightY += 4; });

  return Math.max(leftY, rightY) + 8;
}

// ─── Titre de section (style facture, sobre) ─────────────────────────────
// Texte majuscule, taille moyenne, sur une fine ligne dorée.
function pdf3SectionTitle(doc, y, title) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(String(title).toUpperCase(), PDF3.L, y);
  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.5);
  doc.line(PDF3.L, y + 2, PDF3.L + 30, y + 2);
  return y + 9;
}

// ─── Tableau de données (style facture) ─────────────────────────────────
// Header beige clair avec libellés en gras, lignes blanches séparées par
// un filet gris très fin, alignement texte gauche / nombres droite.
//   columns : [{ key, label, w (mm), align ("left"|"right"|"center"), bold }]
//   rows    : [{ key1: val1, key2: val2, ... }]
// Retourne le Y après la dernière ligne.
function pdf3Table(doc, y, columns, rows, opts = {}) {
  const { L = PDF3.L, CW = PDF3.CW } = opts;
  const totalW = columns.reduce((s, c) => s + (c.w || 0), 0);
  // Normalisation des largeurs si elles ne couvrent pas CW
  const scale = totalW > 0 ? CW / totalW : 1;
  const cols = columns.map(c => ({ ...c, w: (c.w || 0) * scale }));

  // En-tête (fond beige clair)
  doc.setFillColor(...PDF3.COL.ACCENT_LT);
  doc.rect(L, y, CW, 8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(...PDF3.COL.TEXT);
  let cx = L;
  cols.forEach(c => {
    const align = c.align || "left";
    const tx = align === "right" ? cx + c.w - 2 : align === "center" ? cx + c.w / 2 : cx + 2;
    doc.text(String(c.label || "").toUpperCase(), tx, y + 5.5, { align });
    cx += c.w;
  });

  y += 11;

  // Lignes
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  rows.forEach((row, ri) => {
    // Saut de page si débordement (laisser 30mm pour pied)
    if (y > PDF3.H - 30) {
      pdf3Footer(doc);
      doc.addPage();
      y = pdf3Header(doc, { docTitle: "SUITE", ref: "", date: pdf3DateFr() });
      // ré-imprimer l'en-tête tableau
      doc.setFillColor(...PDF3.COL.ACCENT_LT);
      doc.rect(L, y, CW, 8, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...PDF3.COL.TEXT);
      cx = L;
      cols.forEach(c => {
        const align = c.align || "left";
        const tx = align === "right" ? cx + c.w - 2 : align === "center" ? cx + c.w / 2 : cx + 2;
        doc.text(String(c.label || "").toUpperCase(), tx, y + 5.5, { align });
        cx += c.w;
      });
      y += 11;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
    }

    // Alternance discrète : fond très léger une ligne sur deux (optionnel)
    if (ri % 2 === 1) {
      doc.setFillColor(252, 250, 247);
      doc.rect(L, y - 4, CW, 7, "F");
    }

    cx = L;
    cols.forEach(c => {
      const align = c.align || "left";
      const tx = align === "right" ? cx + c.w - 2 : align === "center" ? cx + c.w / 2 : cx + 2;
      const val = row[c.key];
      let txt = val === null || val === undefined ? "—" : String(val);
      // Tronquer si trop long
      doc.setFont("helvetica", c.bold ? "bold" : "normal");
      while (doc.getTextWidth(txt) > c.w - 4 && txt.length > 4) {
        txt = txt.slice(0, -2);
      }
      if (txt !== String(val ?? "")) txt = txt.replace(/.{3}$/, "...");
      doc.setTextColor(...(c.bold ? PDF3.COL.TEXT : PDF3.COL.TEXT));
      doc.text(txt, tx, y, { align });
      cx += c.w;
    });

    // Filet de séparation
    doc.setDrawColor(...PDF3.COL.BORDER);
    doc.setLineWidth(0.15);
    doc.line(L, y + 2.5, L + CW, y + 2.5);

    y += 7;
  });

  return y + 4;
}

// ─── KPI Cards (3 ou 4 chiffres clés alignés horizontalement) ────────────
// Bloc utilisé sur les rapports d'estimation/recommandation pour mettre
// en avant les indicateurs principaux (prix VIQ, score, nb véhicules, etc.)
function pdf3Kpis(doc, y, kpis) {
  const L = PDF3.L, CW = PDF3.CW;
  const n = kpis.length;
  const gap = 4;
  const cardW = (CW - (n - 1) * gap) / n;
  const cardH = 22;

  kpis.forEach((k, i) => {
    const x = L + i * (cardW + gap);
    // Fond blanc + bordure beige
    doc.setFillColor(...PDF3.COL.WHITE);
    doc.setDrawColor(...PDF3.COL.BORDER);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, cardW, cardH, 1.5, 1.5, "FD");
    // Trait doré en haut
    doc.setFillColor(...PDF3.COL.ACCENT);
    doc.rect(x, y, cardW, 1.2, "F");

    // Label
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...PDF3.COL.SUB);
    doc.text(String(k.label || "").toUpperCase(), x + 3, y + 6);

    // Valeur (grosse)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...(k.highlight ? PDF3.COL.ACCENT : PDF3.COL.TEXT));
    doc.text(String(k.value || "—"), x + 3, y + 14);

    // Sub-label
    if (k.sub) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...PDF3.COL.MUTED);
      doc.text(String(k.sub), x + 3, y + 19);
    }
  });

  return y + cardH + 6;
}

// ─── Encart d'information (citation / alerte / explication) ─────────────
function pdf3Note(doc, y, text, opts = {}) {
  const { color = PDF3.COL.ACCENT, bgColor = PDF3.COL.ACCENT_LT } = opts;
  const L = PDF3.L, CW = PDF3.CW;
  // Wrap du texte sur la largeur du contenu - padding
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...PDF3.COL.TEXT);
  const lines = doc.splitTextToSize(text, CW - 12);
  const h = lines.length * 4.5 + 8;
  // Fond
  doc.setFillColor(...bgColor);
  doc.rect(L, y, CW, h, "F");
  // Filet vertical gauche coloré
  doc.setFillColor(...color);
  doc.rect(L, y, 2.5, h, "F");
  // Texte
  lines.forEach((ln, i) => doc.text(ln, L + 7, y + 6 + i * 4.5));
  return y + h + 5;
}

// ─── Pied de page minimaliste avec coordonnées légales + CGV ─────────────
function pdf3Footer(doc, opts = {}) {
  const { showCgv = false } = opts;
  const W = PDF3.W, L = PDF3.L, H = PDF3.H;
  const y = H - 18;

  doc.setDrawColor(...PDF3.COL.BORDER);
  doc.setLineWidth(0.2);
  doc.line(L, y, W - L, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(
    `${VEHICLEIQ_LEGAL.legal_name} · SIRET ${VEHICLEIQ_LEGAL.siret} · ${VEHICLEIQ_LEGAL.address_line2} · ${VEHICLEIQ_LEGAL.email}`,
    W / 2, y + 4, { align: "center" }
  );

  if (showCgv) {
    doc.setFontSize(6);
    doc.setTextColor(...PDF3.COL.MUTED);
    doc.text(
      "Document à valeur informative — Conditions Générales de Vente disponibles sur vehicleiq.fr (rubrique Légal).",
      W / 2, y + 8, { align: "center" }
    );
  }

  // Numéro de page (en bas à droite)
  const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
  const totalPages = doc.internal.getNumberOfPages();
  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.MUTED);
  doc.text(`${pageNum} / ${totalPages}`, W - L, y + 4, { align: "right" });

  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.MUTED);
  doc.text("vehicleiq.fr", L, y + 4);
}

// ─── Saut de page propre : footer page actuelle + nouveau header ────────
function pdf3NextPage(doc, opts) {
  pdf3Footer(doc);
  doc.addPage();
  return pdf3Header(doc, opts);
}

// ─── Wrap texte simple ───────────────────────────────────────────────────
function pdf3WrappedText(doc, y, txt, opts = {}) {
  const { L = PDF3.L, CW = PDF3.CW, size = 9, color = PDF3.COL.TEXT, leading = 4.5, style = "normal" } = opts;
  doc.setFont("helvetica", style);
  doc.setFontSize(size);
  doc.setTextColor(...color);
  const lines = doc.splitTextToSize(String(txt || ""), CW);
  lines.forEach((ln, i) => doc.text(ln, L, y + i * leading));
  return y + lines.length * leading + 2;
}

// ═══════════════════════════════════════════════════════════════════════
// VehicleIQ PDF Design System (v2 — 23/05/2026) — LEGACY (utilisé encore
// par certains rapports en attendant migration v3).
// ═══════════════════════════════════════════════════════════════════════

// Palette officielle — v3 harmonisée sur la charte VehicleIQ (doré antique).
// Ces couleurs sont héritées par toutes les fonctions PDF v2 legacy
// (pdfHeader, pdfFooter, pdfSectionTitle, pdfKpiCard, etc.), garantissant
// un rendu visuellement cohérent avec la facture (style v3).
const VIQ_COLORS = {
  ambre:    [168, 128, 58],   // doré antique #A8803A — accent principal (anciennement [217,119,6] orange)
  ambreLt:  [245, 240, 230],  // beige clair — fond entêtes tableaux (anciennement #FEF3C7)
  anthra:   [30, 30, 35],     // texte principal #1E1E23 (anciennement #1A1A1F — quasi identique)
  charcoal: [60, 60, 70],     // texte corps
  greyMd:   [110, 110, 120],  // texte secondaire #6E6E78
  greyLt:   [220, 220, 225],  // séparateurs (anciennement #D1D5DB — quasi identique)
  paper:    [252, 250, 247],  // fond crème très subtil (anciennement #FAFAF7)
  white:    [255, 255, 255],
  green:    [46, 111, 92],    // var(--green) — aligné avec PDF3
  red:      [155, 44, 44],    // var(--red)
  blue:     [31, 90, 230],    // #1F5AE6
};

// Constantes layout (A4 portrait par défaut, override en paysage si besoin)
const VIQ_LAYOUT = {
  marginL: 18, marginR: 18, marginT: 22, marginB: 18,  // tous en mm
  contentW: 174,  // 210 - 18 - 18
};

// Mois français pour formatage date
const VIQ_MONTHS_FR = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
function viqDateFr() {
  const d = new Date();
  return `${d.getDate()} ${VIQ_MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

// ─── Helper : dessine le logo "VehicleIQ" (serif noir + ambre) ─────────
function pdfLogo(doc, x, y, size = 10, white = false) {
  const baseColor = white ? VIQ_COLORS.white : VIQ_COLORS.anthra;
  doc.setFont("times", "bold");
  doc.setFontSize(size);
  doc.setTextColor(...baseColor);
  doc.text("Vehicle", x, y);
  const w = doc.getTextWidth("Vehicle");
  doc.setTextColor(...VIQ_COLORS.ambre);
  doc.text("IQ", x + w, y);
}

// ─── Texte avec police, taille, couleur, alignement ─────────────────────
function pdfText(doc, x, y, txt, opts = {}) {
  const {
    font = "helvetica", style = "normal", size = 9,
    color = VIQ_COLORS.charcoal, align = "left"
  } = opts;
  doc.setFont(font, style);
  doc.setFontSize(size);
  doc.setTextColor(...color);
  doc.text(String(txt ?? ""), x, y, { align });
}

// ─── Label uppercase tracké (style monospace) ───────────────────────────
function pdfLabel(doc, x, y, txt, opts = {}) {
  const { color = VIQ_COLORS.greyMd, size = 6.5, align = "left" } = opts;
  const spaced = String(txt || "").toUpperCase().split("").join(" ");
  doc.setFont("courier", "normal");
  doc.setFontSize(size);
  doc.setTextColor(...color);
  doc.text(spaced, x, y, { align });
}

// ─── Filet horizontal fin ──────────────────────────────────────────────
function pdfRule(doc, x, y, w, color = VIQ_COLORS.greyLt, weight = 0.3) {
  doc.setDrawColor(...color);
  doc.setLineWidth(weight);
  doc.line(x, y, x + w, y);
}

// ─── Texte multi-lignes avec wrap automatique ───────────────────────────
function pdfWrappedText(doc, x, y, txt, maxW, opts = {}) {
  const {
    font = "helvetica", style = "normal", size = 9,
    color = VIQ_COLORS.charcoal, leading = 4.5
  } = opts;
  doc.setFont(font, style);
  doc.setFontSize(size);
  doc.setTextColor(...color);
  const words = String(txt || "").split(/\s+/);
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = (line + " " + word).trim();
    if (doc.getTextWidth(test) <= maxW) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  for (let i = 0; i < lines.length; i++) {
    doc.text(lines[i], x, y + i * leading);
  }
  return y + (lines.length - 1) * leading; // dernière ligne y
}

// ─── Page de garde universelle — refondue v3 (style facture harmonisé) ──
// Plus de bandeau anthracite plein écran : on adopte le header sobre de la
// facture avec logo doré + titre majuscule, puis un bloc émetteur/client.
// La valeur centrale (prix, score, etc.) est mise en avant dans une carte
// claire avec accent doré, comme le total TTC de la facture.
function pdfCover(doc, opts) {
  const {
    docType = "RAPPORT",
    title = "Document",
    subtitle = "",
    valueLabel = null,
    valueText = null,
    valueSub = null,
    clientLine1 = "",
    clientLine2 = "",
    ref = "",
    pageH = 297, pageW = 210,
  } = opts;

  // ── Header style facture (logo doré gauche + titre maj droite + ligne dorée)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("VehicleIQ", PDF3.L, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text("Cote VO professionnelle", PDF3.L, 27);

  // Titre type document (en majuscules) à droite
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(String(docType).toUpperCase(), pageW - PDF3.R, 22, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PDF3.COL.SUB);
  if (ref) doc.text(`N° ${ref}`, pageW - PDF3.R, 28, { align: "right" });
  doc.text(`Émis le ${pdf3DateFr()}`, pageW - PDF3.R, 32, { align: "right" });

  // Ligne dorée de séparation
  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.6);
  doc.line(PDF3.L, 38, pageW - PDF3.R, 38);

  // ── Bloc émetteur / client (côte à côte, style facture)
  let y = 48;
  const colRight = pageW / 2 + 5;

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text("ÉMETTEUR", PDF3.L, y);
  doc.text("CLIENT", colRight, y);
  y += 6;

  // Émetteur (VehicleIQ)
  doc.setFontSize(9.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(VEHICLEIQ_LEGAL.legal_name, PDF3.L, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const eLines = [
    VEHICLEIQ_LEGAL.trade_name,
    VEHICLEIQ_LEGAL.address_line1,
    VEHICLEIQ_LEGAL.address_line2,
    `SIRET : ${VEHICLEIQ_LEGAL.siret}`,
    VEHICLEIQ_LEGAL.email,
    VEHICLEIQ_LEGAL.website,
  ];
  let leftY = y + 4;
  eLines.forEach(l => { doc.text(l, PDF3.L, leftY); leftY += 4; });

  // Client (à droite)
  doc.setFontSize(9.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(clientLine1 || "Client", colRight, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  let rightY = y + 4;
  if (clientLine2) {
    // Découpe sur " · " si plusieurs infos
    String(clientLine2).split(/\s*·\s*/).forEach(l => {
      if (l.trim()) { doc.text(l.trim(), colRight, rightY); rightY += 4; }
    });
  }

  y = Math.max(leftY, rightY) + 12;

  // ── Titre du véhicule / document (gros, centré)
  doc.setFont("helvetica", "bold");
  let titleSize = 20;
  while (titleSize >= 12) {
    doc.setFontSize(titleSize);
    if (doc.getTextWidth(title) <= PDF3.CW - 6) break;
    titleSize -= 1;
  }
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(title, pageW / 2, y, { align: "center" });
  if (subtitle) {
    y += 7;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...PDF3.COL.SUB);
    doc.text(subtitle, pageW / 2, y, { align: "center" });
  }
  y += 12;

  // ── Bloc valeur centrale (style total TTC de la facture)
  if (valueText !== null && valueText !== undefined) {
    const boxW = 130, boxH = 36;
    const boxX = (pageW - boxW) / 2;

    // Fond beige clair, bordure dorée
    doc.setFillColor(...PDF3.COL.ACCENT_LT);
    doc.setDrawColor(...PDF3.COL.ACCENT);
    doc.setLineWidth(0.6);
    doc.roundedRect(boxX, y, boxW, boxH, 2, 2, "FD");

    if (valueLabel) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...PDF3.COL.ACCENT);
      doc.text(String(valueLabel).toUpperCase(), pageW / 2, y + 8, { align: "center" });
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(...PDF3.COL.TEXT);
    doc.text(String(valueText), pageW / 2, y + 22, { align: "center" });
    if (valueSub) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...PDF3.COL.SUB);
      doc.text(valueSub, pageW / 2, y + 30, { align: "center" });
    }
    y += boxH + 8;
  }

  // ── Pied de page (logo + coordonnées légales, comme la facture)
  const footY = pageH - 22;
  doc.setDrawColor(...PDF3.COL.BORDER);
  doc.setLineWidth(0.2);
  doc.line(PDF3.L, footY, pageW - PDF3.R, footY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(
    `${VEHICLEIQ_LEGAL.legal_name} · SIRET ${VEHICLEIQ_LEGAL.siret} · ${VEHICLEIQ_LEGAL.address_line2} · ${VEHICLEIQ_LEGAL.email}`,
    pageW / 2, footY + 4, { align: "center" }
  );
  doc.text("Document à valeur informative — Conditions Générales de Vente disponibles sur vehicleiq.fr (rubrique Légal).",
    pageW / 2, footY + 8, { align: "center" });
}

// ─── Header fin pour pages de contenu (post-couverture) ─────────────────
function pdfHeader(doc, title, sub) {
  // v3 harmonisé : adopte le style facture pour cohérence avec generateInvoicePDF
  const pageW = doc.internal.pageSize.width;

  // Logo VehicleIQ doré à gauche
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("VehicleIQ", PDF3.L, 14);

  // Titre document à droite (sobre)
  if (title) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...PDF3.COL.SUB);
    doc.text(title, pageW - PDF3.R, 14, { align: "right" });
  }
  if (sub) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...PDF3.COL.SUB);
    doc.text(sub, PDF3.L, 21);
  }
  // Ligne dorée fine de séparation
  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.4);
  doc.line(PDF3.L, 17, pageW - PDF3.R, 17);
}

// ─── Footer fin pour pages de contenu (harmonisé v3) ────────────────────
function pdfFooter(doc, pageNum, proQrDataUrl) {
  const pageW = doc.internal.pageSize.width;
  const pageH = doc.internal.pageSize.height;
  const y = pageH - 18;

  doc.setDrawColor(...PDF3.COL.BORDER);
  doc.setLineWidth(0.2);
  doc.line(PDF3.L, y, pageW - PDF3.R, y);

  // Coordonnées légales complètes (comme la facture)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(
    `${VEHICLEIQ_LEGAL.legal_name} · SIRET ${VEHICLEIQ_LEGAL.siret} · ${VEHICLEIQ_LEGAL.address_line2} · ${VEHICLEIQ_LEGAL.email}`,
    pageW / 2, y + 4, { align: "center" }
  );

  // Numéro de page à droite
  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.MUTED);
  doc.text(`Page ${pageNum || ""}`, pageW - PDF3.R, y + 4, { align: "right" });
  doc.text("vehicleiq.fr", PDF3.L, y + 4);

  // v50 — QR code du professionnel (lien vers SON formulaire client), sur
  // chaque page générée depuis son compte. Placé dans la marge tampon
  // au-dessus du bandeau pied de page (jamais un point de collision observé
  // dans ce document : les seuils de saut de page ci-dessus, ex. "y > H-60",
  // laissent toujours cette zone libre). Fond blanc défensif au cas où.
  if (proQrDataUrl) {
    try {
      const qrSize = 12;
      const qrX = pageW - PDF3.R - qrSize;
      const qrY = y - qrSize - 3;
      doc.setFillColor(255, 255, 255);
      doc.rect(qrX - 1, qrY - 4, qrSize + 2, qrSize + 5, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.5);
      doc.setTextColor(...PDF3.COL.MUTED);
      doc.text("Nous contacter", qrX + qrSize / 2, qrY - 1, { align: "center" });
      doc.addImage(proQrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
    } catch (e) { /* non bloquant — le PDF reste valide sans QR */ }
  }
}

// v50 — Récupère (et met en cache pour la durée de la session) le QR code du
// lien de formulaire client du PRO CONNECTÉ, pour l'intégrer dans les PDF
// générés depuis son compte. Un visiteur non connecté (rapport public) ou un
// pro sans lien actif n'a simplement pas de QR — le PDF reste valide.
let _proLeadQrCache = null;
async function getProLeadQRDataUrl() {
  try {
    if (_proLeadQrCache !== null) return _proLeadQrCache || null;
    if (typeof window === "undefined" || !window.__viq_uid) { _proLeadQrCache = false; return null; }
    const r = await apiGet("/api/pro-leads/link");
    if (!r?.success || !r.url) { _proLeadQrCache = false; return null; }
    const dataUrl = await QRCode.toDataURL(r.url, { margin: 1, width: 240, errorCorrectionLevel: "M", color: { dark: "#1A1A1F", light: "#FFFFFF" } });
    _proLeadQrCache = dataUrl;
    return dataUrl;
  } catch (e) { _proLeadQrCache = false; return null; }
}

// ─── v59 — Traitement photo IA maison (détourage + habillage VIQ.AI) ──────
// Détourage 100% en local dans le navigateur (@imgly/background-removal,
// WASM, licence libre) — pas d'API payante tierce, ce qui correspond
// exactement à "on propose ça nous-même". new Function(...) cache l'import()
// à webpack (sinon il tente de le résoudre au build et échoue) : c'est le
// contournement standard pour charger un module ESM distant dans une app
// bundlée. Chargé une seule fois, mis en cache sur window comme jsPDF/XLSX.
const _dynamicImport = new Function("specifier", "return import(specifier)");
async function loadBgRemoval() {
  if (window.__viqBgRemoval) return window.__viqBgRemoval;
  const mod = await _dynamicImport("https://esm.sh/@imgly/background-removal@1.5.5");
  window.__viqBgRemoval = mod;
  return mod;
}

function _loadImageEl(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Image illisible"));
    img.src = src;
  });
}

// Détoure une photo, la recompose sur un fond studio propre, ajoute le
// filigrane VIQ.AI et le QR code du pro connecté (coin bas-droit). Retourne
// une data URL JPEG prête à télécharger/publier. `onProgress(step)` optionnel
// pour piloter un indicateur d'avancement (le détourage prend plusieurs
// secondes, surtout au premier appel — chargement du modèle WASM).
// v77 — Règle : 5 arrière-plans proposés (2 couleur + 3 showroom stylisés en
// canvas — aucune photo de studio réelle n'est disponible dans cet
// environnement, ce sont des compositions colorées/dégradés qui évoquent un
// showroom, pas des photos). Le traitement se limite désormais à retirer
// l'arrière-plan et apposer le QR code, sans filigrane ni bandeau.
const LISTING_BACKGROUNDS = {
  beige:    { label: "Beige studio",     kind: "color", draw: (ctx, W, H) => { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, "#f6f3ec"); g.addColorStop(1, "#e9e4d8"); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); } },
  gris:     { label: "Gris studio",      kind: "color", draw: (ctx, W, H) => { const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, "#e4e6e9"); g.addColorStop(1, "#c9cdd3"); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H); } },
  showroom1: { label: "Showroom clair",  kind: "showroom", draw: (ctx, W, H) => {
    const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, "#dfe3e6"); g.addColorStop(.55, "#f4f5f6"); g.addColorStop(1, "#b8bcc2"); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.save(); ctx.globalAlpha = .35; ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 2;
    for (let x = -H; x < W + H; x += 60) { ctx.beginPath(); ctx.moveTo(x, H); ctx.lineTo(x + H * .4, H * .55); ctx.stroke(); }
    ctx.restore();
  } },
  showroom2: { label: "Showroom sombre", kind: "showroom", draw: (ctx, W, H) => {
    const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, "#1c2128"); g.addColorStop(.5, "#2a313b"); g.addColorStop(1, "#0f1216"); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    const spot = ctx.createRadialGradient(W / 2, H * .4, H * .05, W / 2, H * .4, H * .7);
    spot.addColorStop(0, "rgba(255,255,255,.18)"); spot.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = spot; ctx.fillRect(0, 0, W, H);
  } },
  showroom3: { label: "Showroom doré",   kind: "showroom", draw: (ctx, W, H) => {
    const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, "#eee6d3"); g.addColorStop(.6, "#d9c9a0"); g.addColorStop(1, "#a8803a"); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    const spot = ctx.createRadialGradient(W / 2, H * .35, H * .04, W / 2, H * .35, H * .65);
    spot.addColorStop(0, "rgba(255,255,255,.3)"); spot.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = spot; ctx.fillRect(0, 0, W, H);
  } },
};

async function processListingPhoto(rawDataUrl, { proQrDataUrl, background = "beige", onProgress } = {}) {
  onProgress?.("Chargement du moteur de détourage…");
  const { removeBackground } = await loadBgRemoval();
  onProgress?.("Détourage du véhicule…");
  const cutoutBlob = await removeBackground(rawDataUrl);
  const cutoutUrl = URL.createObjectURL(cutoutBlob);
  try {
    onProgress?.("Composition de la photo…");
    const img = await _loadImageEl(cutoutUrl);
    // v77 — Règle : conserver les proportions de la photo d'origine plutôt
    // que de forcer un cadre 4:3 fixe — le canvas épouse le ratio de la
        // photo source (plafonné pour rester raisonnable côté poids/mémoire).
    const MAX_DIM = 1400;
    const scaleToMax = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
    const W = Math.round(img.width * scaleToMax), H = Math.round(img.height * scaleToMax);
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    const bgDef = LISTING_BACKGROUNDS[background] || LISTING_BACKGROUNDS.beige;
    bgDef.draw(ctx, W, H);

    // Véhicule détouré, centré et mis à l'échelle DANS le cadre qui respecte
    // déjà les proportions d'origine — pas de recadrage forcé.
    const scale = Math.min((W * 0.92) / img.width, (H * 0.88) / img.height);
    const dw = img.width * scale, dh = img.height * scale;
    const dx = (W - dw) / 2, dy = (H - dh) / 2;
    ctx.drawImage(img, dx, dy, dw, dh);

    // QR code du pro (coin bas-droit) — mène à son formulaire client. Seul
    // élément ajouté en plus du détourage, comme demandé.
    if (proQrDataUrl) {
      try {
        const qrImg = await _loadImageEl(proQrDataUrl);
        const qs = Math.round(Math.min(W, H) * 0.11);
        ctx.fillStyle = "#fff";
        ctx.fillRect(W - qs - 22, H - qs - 22, qs + 12, qs + 12);
        ctx.drawImage(qrImg, W - qs - 16, H - qs - 16, qs, qs);
      } catch (e) { /* non bloquant — la photo reste valide sans QR */ }
    }

    onProgress?.("Terminé");
    return canvas.toDataURL("image/jpeg", 0.9);
  } finally {
    URL.revokeObjectURL(cutoutUrl);
  }
}



// ─── Titre de section (harmonisé v3 : uppercase + ligne dorée) ─────────
function pdfSectionTitle(doc, y, txt, ruleW = 30) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(String(txt).toUpperCase(), PDF3.L, y);
  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.5);
  doc.line(PDF3.L, y + 2, PDF3.L + ruleW, y + 2);
  return y + 10;
}

// ─── Carte KPI (boîte arrondie + filet accent + label + valeur) ────────
function pdfKpiCard(doc, x, y, w, h, labelTxt, value, sub, accent) {
  accent = accent || VIQ_COLORS.ambre;
  doc.setFillColor(...VIQ_COLORS.white);
  doc.setDrawColor(...VIQ_COLORS.greyLt);
  doc.setLineWidth(0.4);
  doc.roundedRect(x, y, w, h, 2, 2, "FD");
  // Filet accent en haut
  doc.setFillColor(...accent);
  doc.rect(x, y, w, 1.2, "F");
  // Label
  pdfLabel(doc, x + 4, y + 7, labelTxt, { color: VIQ_COLORS.greyMd, size: 6 });
  // Valeur
  pdfText(doc, x + 4, y + 16, String(value),
    { font: "times", style: "bold", size: 17, color: VIQ_COLORS.anthra });
  // Sub
  if (sub) {
    pdfText(doc, x + 4, y + 21, sub, { size: 7.5, color: VIQ_COLORS.greyMd });
  }
}

// ─── Ligne clé/valeur deux colonnes ────────────────────────────────────
function pdfKvRow(doc, x, y, key, value, keyW) {
  keyW = keyW || 32;
  pdfText(doc, x, y, key, { size: 8.5, color: VIQ_COLORS.greyMd });
  // Tronquer/réduire la valeur si trop large pour la colonne
  let valStr = String(value || "—");
  let valSize = 9;
  // Estimation grossière de la zone dispo
  const maxW = 60;
  doc.setFont("helvetica", "bold");
  while (valSize > 7 && doc.getFontSize() && true) {
    doc.setFontSize(valSize);
    if (doc.getTextWidth(valStr) <= maxW) break;
    valSize -= 0.5;
  }
  pdfText(doc, x + keyW, y, valStr,
    { font: "helvetica", style: "bold", size: valSize, color: VIQ_COLORS.anthra });
}

// ─── Glyphe flèche (utilisé pour tendance marché) ──────────────────────
function pdfGlyphArrow(doc, x, y, direction, size, color) {
  size = size || 4;
  color = color || VIQ_COLORS.greyMd;
  doc.setDrawColor(...color);
  doc.setFillColor(...color);
  doc.setLineWidth(0.6);
  // direction : "up", "down", "right"
  if (direction === "up") {
    // flèche montante (45° haut-droite)
    doc.line(x, y, x + size, y - size);
    doc.line(x + size, y - size, x + size, y - size + 2);
    doc.line(x + size, y - size, x + size - 2, y - size);
  } else if (direction === "down") {
    doc.line(x, y, x + size, y + size);
    doc.line(x + size, y + size, x + size, y + size - 2);
    doc.line(x + size, y + size, x + size - 2, y + size);
  } else {
    // flèche horizontale
    doc.line(x, y, x + size, y);
    doc.line(x + size, y, x + size - 2, y - 1);
    doc.line(x + size, y, x + size - 2, y + 1);
  }
}

function pdfSection(doc, y, title, rgb=[217,119,6]) {
  doc.setFillColor(rgb[0],rgb[1],rgb[2]);
  doc.rect(14,y,3,6,"F");
  doc.setFontSize(9); doc.setFont("helvetica","bold");
  doc.setTextColor(26,26,31);
  doc.text(title.toUpperCase(),20,y+5);
  doc.setDrawColor(232,229,221); doc.line(14,y+7,196,y+7);
  return y+11;
}

// ─── PDF helpers avancés ────────────────────────────────────────────────────

/** Rectangle arrondi avec gradient simulé (deux passes de couleur) */
function pdfGradientBox(doc, x, y, w, h, rgb1, rgb2, radius=2) {
  // Couleur principale
  doc.setFillColor(rgb1[0],rgb1[1],rgb1[2]);
  doc.roundedRect(x, y, w, h, radius, radius, "F");
  // Bande supérieure plus claire pour effet de relief
  doc.setFillColor(rgb2[0],rgb2[1],rgb2[2]);
  doc.roundedRect(x, y, w, h*0.4, radius, radius, "F");
}

/** Score donut SVG-like (cercle avec arc coloré) */
function pdfDonut(doc, cx, cy, score, radius=15) {
  const col = score >= 80 ? [0,200,100] : score >= 60 ? [255,184,0] : [255,77,77];
  // Cercle de fond
  doc.setDrawColor(232,229,221); doc.setLineWidth(2);
  doc.circle(cx, cy, radius, "S");
  // Arc proportionnel au score : on simule avec un cercle entier coloré + masquage par segments
  // jsPDF ne fait pas d'arcs, donc on simule via plusieurs petits triangles
  const pct = Math.max(0, Math.min(100, score)) / 100;
  doc.setDrawColor(col[0], col[1], col[2]); doc.setLineWidth(2.5);
  const segments = Math.round(40 * pct);
  for (let i = 0; i < segments; i++) {
    const a1 = -Math.PI/2 + (i / 40) * 2 * Math.PI;
    const a2 = -Math.PI/2 + ((i + 1) / 40) * 2 * Math.PI;
    doc.line(cx + Math.cos(a1)*radius, cy + Math.sin(a1)*radius,
             cx + Math.cos(a2)*radius, cy + Math.sin(a2)*radius);
  }
  doc.setLineWidth(0.2);
  // Score au centre
  doc.setFontSize(11); doc.setFont("helvetica","bold");
  doc.setTextColor(col[0], col[1], col[2]);
  doc.text(String(score || "—"), cx, cy + 1.5, { align: "center" });
  doc.setFontSize(5.5); doc.setFont("helvetica","normal");
  doc.setTextColor(86,86,96);
  doc.text("/100", cx, cy + 5.5, { align: "center" });
}

/** Barre de progression colorée */
function pdfProgressBar(doc, x, y, w, h, pct, rgb=[0,200,100]) {
  pct = Math.max(0, Math.min(100, pct));
  doc.setFillColor(244,242,236);
  doc.roundedRect(x, y, w, h, h/2, h/2, "F");
  doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  doc.roundedRect(x, y, (w * pct / 100), h, h/2, h/2, "F");
}

/** Champ clé/valeur sur 1 ligne */
function pdfKV(doc, x, y, label, value, opts={}) {
  doc.setFontSize(opts.labelSize || 7);
  doc.setFont("helvetica","normal");
  doc.setTextColor(86,86,96);
  doc.text((label || "").toUpperCase(), x, y);
  doc.setFontSize(opts.valueSize || 9);
  doc.setFont("helvetica","bold");
  doc.setTextColor(...(opts.valueColor || [26,26,31]));
  doc.text(String(value || "—"), x, y + 4);
}

/**
 * Export PDF d'une entrée d'historique (check, sell ou buy).
 * Utilise jspdf-autotable pour les tableaux propres et helpers maison pour
 * la mise en page premium.
 */
async function exportEntryToPDF(entry, opts) {
  // v27 — Refonte : ce PDF doit être IDENTIQUE à celui généré en direct
  // (contrôle véhicule, estimation), pas une reconstruction séparée qui finit
  // par diverger (styles legacy différents, champs manquants...). On délègue
  // désormais aux MÊMES fonctions que les exports "live" (exportVINPDF,
  // exportSellPDF, exportBuyPDF), à partir des données stockées dans
  // l'historique — désormais le rapport COMPLET (voir /api/check, /api/vin,
  // /api/annonce/generate), plus la vérité que des champs choisis à la main.
  const r = entry.results || entry.result || {};
  const d = entry.data || entry.answers || {};
  const type = entry.type || "check";

  if (type === "check") {
    const vin = r.vin || d.vin || d.plaque || "";
    return exportVINPDF(r, vin, opts);
  }

  if (type === "sell" || type === "estimation") {
    // Mapping le plus robuste possible : les noms de champs de l'historique
    // (sauvegardés côté formulaire "Estimer") peuvent différer légèrement des
    // noms utilisés par exportSellPDF — on couvre les variantes connues.
    const mappedAnswers = {
      make: r.make || d.make || undefined,
      model: d.model || r.model || undefined,
      year_sell: d.year_sell || d.year || r.year || undefined,
      km_sell: d.km_sell || d.km || r.km || undefined,
      fuel_sell: d.fuel_sell || d.fuel || r.fuel || undefined,
      gearbox_sell: d.gearbox_sell || d.gearbox || undefined,
      state: d.state || undefined,
      ct: d.ct || undefined,
      owners: d.owners || undefined,
      color: d.color || undefined,
      client_name: d.client_name || d.user_name || undefined,
      client_company: d.client_company || undefined,
      client_email: d.client_email || undefined,
    };
    return exportSellPDF(r, mappedAnswers, opts);
  }

  if (type === "buy") {
    // Harmonisation : le PDF d'achat depuis l'historique réutilise EXACTEMENT
    // le même générateur premium que celui produit juste après une recherche
    // (exportBuyPDF), pour un design identique. On mappe les données de
    // l'entrée d'historique vers le format attendu (results[], answers).
    const suggestions = r.models || r.suggestions || r.data || (Array.isArray(r) ? r : []);
    const results = (Array.isArray(suggestions) ? suggestions : []).map(v => ({
      make: v.make || "",
      model: v.model || "",
      year: v.year || "",
      km: v.km != null ? Number(v.km) : undefined,
      price: v.price != null ? Number(v.price) : 0,
      priceFormatted: v.priceFormatted,
      prix_neuf: v.prix_neuf, prixNeufFormatted: v.prixNeufFormatted,
      match: v.match != null ? Number(String(v.match).replace(/[^\d.]/g, "")) : undefined,
      argus_low: v.argus_low != null ? Number(v.argus_low) : undefined,
      argus_high: v.argus_high != null ? Number(v.argus_high) : undefined,
      why_match: v.why_match || v.reason || "",
      pros: Array.isArray(v.pros) ? v.pros : undefined,
      cons: Array.isArray(v.cons) ? v.cons : undefined,
    }));
    const mappedAnswers = {
      budget: d.budget || d.budget_max || undefined,
      km: d.km_max || d.km || undefined,
      year: d.year_min || d.annee_min || d.year || undefined,
      brands: d.brands || d.marques || undefined,
      city: d.city || d.ville || undefined,
      fuel: d.fuel_buy || d.fuel || undefined,
      seller: d.seller || d.vendeur || undefined,
      import: d.import || undefined,
      make: [d.make, d.model].filter(Boolean).join(" ").trim() || undefined,
    };
    return exportBuyPDF(results, mappedAnswers);
  }

  // Type non reconnu (ancienne entrée, format inattendu) : repli minimal sur
  // l'export contrôle, le plus générique des trois.
  return exportVINPDF(r, r.vin || d.vin || d.plaque || "");
}

/**
 * Export PDF complet de l'analyse d'un véhicule du dashboard pro.
 * Multi-pages :
 *  - Page 1 : Identité véhicule + Indice VIQ + Comparaison de prix
 *  - Page 2 : Courbe de dépréciation 8 ans
 *  - Page 3 : 10 plateformes recommandées
 * entry : objet véhicule complet (makeModel, year, km, fuel, gearbox, viqPrice,
 *         argusLow, argusMid, argusHigh, tam, marche, score, platforms)
 */
async function exportVehicleAnalysisToPDF(entry) {
  // Refonte v3 : style facture cohérent — portrait A4, sections claires,
  // tableaux pdf3Table lisibles, fini les fonds noirs/dégradés sombres.
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = PDF3.W, L = PDF3.L, R = PDF3.R, CW = PDF3.CW, H = PDF3.H;
  const proQrDataUrl = await getProLeadQRDataUrl();

  const dd = new Date();
  const ref = pdf3Ref("PRO");
  const makeModel = entry?.makeModel || "Véhicule";

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 1 — COUVERTURE (style facture)
  // ═══════════════════════════════════════════════════════════════════════
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("VehicleIQ", L, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text("Analyse Pro VehicleIQ", L, 27);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text("ANALYSE VÉHICULE", W - R, 22, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(`N° ${ref}`, W - R, 28, { align: "right" });
  doc.text(`Émise le ${pdf3DateFr()}`, W - R, 32, { align: "right" });

  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.6);
  doc.line(L, 38, W - R, 38);

  // Bloc parties : émetteur (gauche) + client connecté (droite)
  let y = 48;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text("ÉMETTEUR", L, y);
  const pdfClient = getPdfClient();
  if (pdfClient) doc.text("CLIENT", W / 2 + 5, y);
  y += 6;
  doc.setFontSize(9.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(VEHICLEIQ_LEGAL.legal_name, L, y);
  // Client (droite) — même ligne de base que le nom de l'émetteur
  if (pdfClient) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...PDF3.COL.TEXT);
    doc.text(pdfClient.name, W / 2 + 5, y);
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const eLines = [
    // v7 : mention "Auto-entreprise (micro-entrepreneur)" retirée sur ce
    // rapport spécifiquement (image plus professionnelle) — conservée sur les
    // documents à portée légale/contractuelle (facture, mandat, cession).
    VEHICLEIQ_LEGAL.trade_name,
    VEHICLEIQ_LEGAL.address_line1,
    VEHICLEIQ_LEGAL.address_line2,
    `SIRET : ${VEHICLEIQ_LEGAL.siret}`,
    VEHICLEIQ_LEGAL.email,
  ];
  let ey = y;
  eLines.forEach(l => { ey += 4; doc.text(l, L, ey); });
  // Lignes client à droite
  if (pdfClient) {
    let cy = y;
    doc.setTextColor(...PDF3.COL.SUB);
    pdfClient.lines.forEach(l => { cy += 4; doc.text(l, W / 2 + 5, cy); });
    doc.setTextColor(...PDF3.COL.TEXT);
  }
  // y avance sous le plus long des deux blocs
  y = ey + 12;

  // Titre véhicule (centré, grand)
  doc.setFont("helvetica", "bold");
  let titleSize = 24;
  while (titleSize > 14) {
    doc.setFontSize(titleSize);
    if (doc.getTextWidth(makeModel) <= CW - 6) break;
    titleSize -= 1;
  }
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(makeModel, W / 2, y + 4, { align: "center" });

  // Sous-titre caractéristiques
  const subBits = [];
  if (entry?.year) subBits.push(String(entry.year));
  if (entry?.km) subBits.push(pdfNum(Number(entry.km)) + " km");
  if (entry?.fuel) subBits.push(entry.fuel);
  if (entry?.gearbox) subBits.push(entry.gearbox);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(subBits.filter(Boolean).join("  ·  "), W / 2, y + 11, { align: "center" });

  y += 22;

  // Carte centrale : prix VIQ si disponible
  if (entry?.viqPrice) {
    const boxW = 150, boxH = 48;
    const boxX = (W - boxW) / 2;
    doc.setFillColor(...PDF3.COL.ACCENT_LT);
    doc.setDrawColor(...PDF3.COL.ACCENT);
    doc.setLineWidth(0.8);
    doc.roundedRect(boxX, y, boxW, boxH, 3, 3, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...PDF3.COL.ACCENT);
    doc.text("VALEUR ESTIMÉE VIQ.AI® ENGINE", W / 2, y + 9, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(32);
    doc.setTextColor(...PDF3.COL.TEXT);
    doc.text(pdfNum(Number(entry.viqPrice)) + " €", W / 2, y + 28, { align: "center" });
    if (entry.argusLow && entry.argusHigh) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...PDF3.COL.SUB);
      doc.text(
        `Fourchette ${pdfNum(Number(entry.argusLow))} – ${pdfNum(Number(entry.argusHigh))} €`,
        W / 2, y + 38, { align: "center" }
      );
    }
    y += boxH + 12;
  }

  // KPIs : score de fiabilité (VIQ.AI®) + tendance si disponibles
  // v13 : "Indice VIQ" était ambigu (mélangeait score de vente et score de
  // fiabilité selon le type d'entrée) — on utilise désormais le champ
  // explicitement désambiguïsé (entry.reliabilityScore) quand disponible.
  const kpis = [];
  const relScoreForPdf = entry?.reliabilityScore != null ? entry.reliabilityScore : entry?.score;
  if (relScoreForPdf != null) {
    kpis.push({
      label: "Score de fiabilité VIQ.AI®",
      value: `${relScoreForPdf}/100`,
      sub: relScoreForPdf >= 80 ? "Très bon profil" : relScoreForPdf >= 60 ? "Bon profil" : "À vérifier",
      highlight: relScoreForPdf >= 80,
    });
  }
  if (entry?.market_trend) {
    const lbl = entry.market_trend === "hausse" ? "↗ Hausse" : entry.market_trend === "baisse" ? "↘ Baisse" : "→ Stable";
    kpis.push({ label: "Tendance marché", value: lbl, sub: "30 derniers jours" });
  }
  if (entry?.ct) {
    kpis.push({ label: "Contrôle technique", value: entry.ct === "Oui" || entry.ct === "Valide" ? "Valide" : "À vérifier", sub: "Statut déclaré" });
  }
  if (kpis.length > 0) {
    y = pdf3Kpis(doc, y, kpis);
  }

  // ─── Section : Points de vigilance (top 5) ───
  const analysisVigilance = getVigilanceTop5(entry?.knownIssuesData);
  if (analysisVigilance.length) {
    y += 4;
    y = pdf3SectionTitle(doc, y, "Top 5 points de vigilance majeurs");
    analysisVigilance.forEach((pt) => {
      doc.setFillColor(224, 138, 106);
      doc.circle(L + 2, y - 1, 1.4, "F");
      y = pdf3WrappedText(doc, y, pt, {
        L: L + 7, CW: CW - 7, size: 9.5, color: PDF3.COL.TEXT, leading: 4.5,
      }) + 3;
    });
    y += 2;
  }

  // Pied de couverture
  const footY = H - 22;
  doc.setDrawColor(...PDF3.COL.BORDER);
  doc.setLineWidth(0.2);
  doc.line(L, footY, W - R, footY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(
    `${VEHICLEIQ_LEGAL.legal_name} · SIRET ${VEHICLEIQ_LEGAL.siret} · ${VEHICLEIQ_LEGAL.address_line2} · ${VEHICLEIQ_LEGAL.email}`,
    W / 2, footY + 4, { align: "center" }
  );
  doc.setFontSize(6);
  doc.setTextColor(...PDF3.COL.MUTED);
  doc.text(
    "Document à valeur informative — Conditions Générales de Vente disponibles sur vehicleiq.fr (rubrique Légal).",
    W / 2, footY + 8, { align: "center" }
  );

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 2 — Détails véhicule (tableau)
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  pdfHeader(doc, "Analyse véhicule");
  y = 30;

  y = pdf3SectionTitle(doc, y, "Identification du véhicule");
  const idRows = [
    { critere: "Marque / Modèle",     valeur: makeModel },
    { critere: "Année",               valeur: entry?.year ? String(entry.year) : "—" },
    { critere: "Kilométrage",         valeur: entry?.km ? pdfNum(Number(entry.km)) + " km" : "—" },
    { critere: "Motorisation",        valeur: entry?.fuel || "—" },
    { critere: "Boîte de vitesses",   valeur: entry?.gearbox || "—" },
    { critere: "État général",        valeur: entry?.state || "—" },
    { critere: "Contrôle technique",  valeur: entry?.ct || "—" },
    { critere: "Plaque / VIN",        valeur: entry?.plaque || entry?.vin || "—" },
  ];
  y = pdf3Table(doc, y, [
    { key: "critere", label: "Critère", w: 70 },
    { key: "valeur",  label: "Valeur",  w: 110, bold: true },
  ], idRows);

  // ─── Valorisation (si disponible) ───
  if (entry?.viqPrice || entry?.argusLow) {
    y += 4;
    y = pdf3SectionTitle(doc, y, "Valorisation marché");
    const valRows = [];
    if (entry.argusLow) valRows.push({ critere: "Bas marché",    valeur: pdfNum(Number(entry.argusLow)) + " €" });
    if (entry.viqPrice) valRows.push({ critere: "Prix VIQ.AI® Engine", valeur: pdfNum(Number(entry.viqPrice)) + " €" });
    if (entry.argusHigh) valRows.push({ critere: "Haut marché",  valeur: pdfNum(Number(entry.argusHigh)) + " €" });
    if (entry.argus_mid) valRows.push({ critere: "Côte Argus (mid)", valeur: pdfNum(Number(entry.argus_mid)) + " €" });
    y = pdf3Table(doc, y, [
      { key: "critere", label: "Niveau", w: 70 },
      { key: "valeur",  label: "Montant", w: 110, align: "right", bold: true },
    ], valRows);
  }

  // ─── Indicateurs de fiabilité ───
  const indicators = [];
  if (entry?.ct != null) indicators.push({ critere: "Contrôle technique", valeur: (entry.ct === "Oui" || entry.ct === "Valide") ? "✓ Valide" : "À vérifier" });
  if (entry?.accidents != null) indicators.push({ critere: "Sinistres déclarés", valeur: entry.accidents === 0 ? "Aucun" : `${entry.accidents} déclaré(s)` });
  if (entry?.stolen != null) indicators.push({ critere: "Statut vol", valeur: entry.stolen ? "Signalé volé" : "✓ Non signalé" });
  if (entry?.gage != null) indicators.push({ critere: "Gage / opposition", valeur: entry.gage ? "Sous gage" : "Aucun gage" });
  if (indicators.length > 0) {
    y += 4;
    y = pdf3SectionTitle(doc, y, "Indicateurs de fiabilité");
    y = pdf3Table(doc, y, [
      { key: "critere", label: "Critère", w: 90 },
      { key: "valeur",  label: "Statut",  w: 90, bold: true },
    ], indicators);
  }

  // ─── Note méthodologique ───
  if (y < H - 60) {
    y += 4;
    y = pdf3Note(doc, y,
      "Analyse calculée par l'algorithme VIQ.AI® Engine v5 : fusion 70/30 entre données marché temps réel et catalogue constructeur, ajustée par les coefficients saisonnier, régional, ZFE, watchlist mécanique et momentum. Variance attendue ±2 à 8 %.",
      { color: PDF3.COL.ACCENT, bgColor: PDF3.COL.ACCENT_LT }
    );
  }

  // ─── Note légale en bas ───
  const noteY = H - 30;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(...PDF3.COL.MUTED);
  const legalLines = doc.splitTextToSize(
    "Cette analyse est indicative et ne constitue ni un engagement de rachat, ni une cote opposable, ni un avis d'expert agréé. Elle ne se substitue pas à un contrôle technique officiel ni à une inspection physique du véhicule par un professionnel.",
    CW
  );
  legalLines.forEach((line, i) => doc.text(line, L, noteY + i * 3.5));

  pdfFooter(doc, 2, proQrDataUrl);

  // Sauvegarde
  const slug = makeModel.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  const dateSlug = new Date().toISOString().slice(0, 10);
  doc.save(`viq_analyse_${slug}_${dateSlug}.pdf`);
}

async function exportVINPDF(result, vin, opts) {
  // Refonte v3 : style facture cohérent — couverture sobre, tableaux pdf3Table,
  // bandeau source officielle en beige (au lieu du bleu République qui détonne).
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = PDF3.W, L = PDF3.L, R = PDF3.R, CW = PDF3.CW, H = PDF3.H;
  const proQrDataUrl = await getProLeadQRDataUrl();

  const sc = Number(result.score || 0);
  const scLabel = sc >= 80 ? "Excellent" : sc >= 60 ? "Correct" : "À vérifier";
  const scColor = sc >= 80 ? PDF3.COL.GREEN : sc >= 60 ? PDF3.COL.ACCENT : PDF3.COL.RED;
  const ref = pdf3Ref("VIN");
  const makeModel = ((result.make || "") + " " + (result.model || "")).trim() || "Véhicule";

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 1 — COUVERTURE (style facture)
  // ═══════════════════════════════════════════════════════════════════════
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("VehicleIQ", L, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text("Rapport de contrôle véhicule", L, 27);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text("CONTRÔLE VIN", W - R, 22, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(`N° ${ref}`, W - R, 28, { align: "right" });
  doc.text(`Émis le ${pdf3DateFr()}`, W - R, 32, { align: "right" });

  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.6);
  doc.line(L, 38, W - R, 38);

  // Bloc émetteur uniquement (l'analyse est interne)
  let y = 48;
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text("ÉMETTEUR", L, y);
  doc.text("DONNÉES OFFICIELLES", W / 2 + 5, y);
  y += 6;
  doc.setFontSize(9.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(VEHICLEIQ_LEGAL.legal_name, L, y);
  doc.text("HistoVec", W / 2 + 5, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const eLines = [
    VEHICLEIQ_LEGAL.trade_name,
    VEHICLEIQ_LEGAL.address_line2,
    `SIRET : ${VEHICLEIQ_LEGAL.siret}`,
    VEHICLEIQ_LEGAL.email,
  ];
  let leftY = y + 4;
  eLines.forEach(l => { doc.text(l, L, leftY); leftY += 4; });

  const sLines = [
    "Ministère de l'Intérieur",
    "api.histovec.interieur.gouv.fr",
    `VIN : ${(vin || "").toUpperCase()}`,
  ];
  let rightY = y + 4;
  sLines.forEach(l => { doc.text(l, W / 2 + 5, rightY); rightY += 4; });

  y = Math.max(leftY, rightY) + 10;

  // Titre véhicule
  doc.setFont("helvetica", "bold");
  let titleSize = 22;
  while (titleSize > 12) {
    doc.setFontSize(titleSize);
    if (doc.getTextWidth(makeModel) <= CW - 6) break;
    titleSize -= 1;
  }
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(makeModel, W / 2, y + 4, { align: "center" });

  // Sous-titre
  const subBits = [];
  if (result.year) subBits.push(String(result.year));
  if (result.fuel) subBits.push(result.fuel);
  if (result.km) subBits.push(pdfNum(Number(result.km)) + " km");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(subBits.filter(Boolean).join("  ·  "), W / 2, y + 11, { align: "center" });

  y += 22;

  // Carte centrale : score de fiabilité
  const boxW = 150, boxH = 48;
  const boxX = (W - boxW) / 2;
  doc.setFillColor(...PDF3.COL.ACCENT_LT);
  doc.setDrawColor(...scColor);
  doc.setLineWidth(0.8);
  doc.roundedRect(boxX, y, boxW, boxH, 3, 3, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...scColor);
  doc.text(`SCORE FIABILITÉ — ${scLabel.toUpperCase()}`, W / 2, y + 9, { align: "center" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(32);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(`${sc} / 100`, W / 2, y + 28, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(`VIN ${(vin || "").toUpperCase()}`, W / 2, y + 38, { align: "center" });

  y += boxH + 8;

  // Pied de couverture (légal)
  const footY = H - 22;
  doc.setDrawColor(...PDF3.COL.BORDER);
  doc.setLineWidth(0.2);
  doc.line(L, footY, W - R, footY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(
    `${VEHICLEIQ_LEGAL.legal_name} · SIRET ${VEHICLEIQ_LEGAL.siret} · ${VEHICLEIQ_LEGAL.address_line2} · ${VEHICLEIQ_LEGAL.email}`,
    W / 2, footY + 4, { align: "center" }
  );
  doc.setFontSize(6);
  doc.setTextColor(...PDF3.COL.MUTED);
  doc.text(
    "Document à valeur informative — Conditions Générales de Vente disponibles sur vehicleiq.fr (rubrique Légal).",
    W / 2, footY + 8, { align: "center" }
  );

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 2 — Identité officielle + Alertes + Estimation marché
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  pdfHeader(doc, "Rapport de contrôle véhicule");
  y = 30;

  // ─── Identité officielle (HistoVec) ───
  y = pdf3SectionTitle(doc, y, "Identité officielle (HistoVec)");
  const idRows = [
    { critere: "Marque / Modèle",            valeur: makeModel },
    { critere: "Année de mise en circulation", valeur: String(result.year || "—") },
    { critere: "Énergie",                    valeur: result.fuel || "—" },
    { critere: "Kilométrage déclaré",        valeur: result.km ? pdfNum(Number(result.km)) + " km" : "—" },
    { critere: "Nombre de propriétaires",    valeur: String(result.owners || "—") },
    { critere: "Contrôle technique",         valeur: result.ctOk ? "✓ Valide" : "À vérifier" },
    { critere: "VIN",                        valeur: (vin || "").toUpperCase() },
  ];
  y = pdf3Table(doc, y, [
    { key: "critere", label: "Critère", w: 70 },
    { key: "valeur",  label: "Valeur",  w: 110, bold: true },
  ], idRows);

  // ─── Sinistres & alertes ───
  y += 4;
  y = pdf3SectionTitle(doc, y, "Sinistres & alertes");
  const alertsRows = [
    {
      critere: "Sinistres déclarés",
      statut: result.sinistres_declares > 0 ? `${result.sinistres_declares} sinistre(s) détecté(s)` : "✓ Aucun sinistre",
      ok: !(result.sinistres_declares > 0),
    },
    {
      critere: "Statut volé",
      statut: result.stolen ? "⚠ ALERTE — véhicule signalé volé" : "✓ Aucune alerte",
      ok: !result.stolen,
    },
    {
      critere: "Gage / Opposition",
      statut: result.gage ? "⚠ Gage détecté" : "✓ Aucun gage ni opposition",
      ok: !result.gage,
    },
  ];
  y = pdf3Table(doc, y, [
    { key: "critere", label: "Critère", w: 70 },
    { key: "statut",  label: "Statut",  w: 110, bold: true },
  ], alertsRows);

  // ─── Estimation valeur marché ───
  if (result.marketValue) {
    y += 4;
    y = pdf3SectionTitle(doc, y, "Cote valeur marché");

    // Carte beige doré avec valeur centrale
    const valueBoxH = 22;
    doc.setFillColor(...PDF3.COL.ACCENT_LT);
    doc.setDrawColor(...PDF3.COL.ACCENT);
    doc.setLineWidth(0.5);
    doc.roundedRect(L, y, CW, valueBoxH, 2, 2, "FD");
    // Filet doré gauche
    doc.setFillColor(...PDF3.COL.ACCENT);
    doc.rect(L, y, 2.5, valueBoxH, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF3.COL.ACCENT);
    doc.text("VALEUR MARCHÉ ESTIMÉE", L + 7, y + 6);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...PDF3.COL.TEXT);
    doc.text(String(result.marketValue), L + 7, y + 16);

    if (result.lastService) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...PDF3.COL.SUB);
      doc.text(`Dernière révision : ${result.lastService}`, L + CW - 4, y + 7, { align: "right" });
    }
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF3.COL.MUTED);
    doc.text("À titre indicatif", L + CW - 4, y + 17, { align: "right" });
    y += valueBoxH + 6;
  }

  // ─── Bandeau source officielle (style facture, beige clair) ───
  doc.setFillColor(...PDF3.COL.ACCENT_LT);
  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.4);
  doc.roundedRect(L, y, CW, 14, 2, 2, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("Données officielles HistoVec", L + 4, y + 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text("Ministère de l'Intérieur — api.histovec.interieur.gouv.fr", L + 4, y + 11);
  y += 20;

  // ─── Section : Top 5 points de vigilance (v13) ───
  const vinVigilance = getVigilanceTop5(result.known_issues);
  if (vinVigilance.length) {
    if (y > H - 60) { pdfFooter(doc, 2, proQrDataUrl); doc.addPage(); y = 24; }
    y = pdf3SectionTitle(doc, y, "Top 5 points de vigilance majeurs (fiabilité VIQ.AI®)");
    vinVigilance.forEach((pt) => {
      doc.setFillColor(224, 138, 106);
      doc.circle(L + 2, y - 1, 1.4, "F");
      y = pdf3WrappedText(doc, y, pt, {
        L: L + 7, CW: CW - 7, size: 9.5, color: PDF3.COL.TEXT, leading: 4.5,
      }) + 3;
    });
    y += 3;
  }

  // ─── Section : Inspection visuelle IA (v26) ───
  const vi = result.visual_inspection;
  if (vi && vi.zones) {
    if (y > H - 90) { pdfFooter(doc, 2, proQrDataUrl); doc.addPage(); y = 24; }
    y = pdf3SectionTitle(doc, y, "Inspection visuelle IA (photos du véhicule)");

    const ZONE_LABELS = { avant: "Avant", toit: "Toit", flanc_gauche: "Flanc gauche", flanc_droit: "Flanc droit", arriere: "Arrière", jantes_pneus: "Jantes / pneus", interieur: "Intérieur" };
    const ZONE_COLORS = { ok: [46, 111, 92], attention: [217, 119, 6], probleme: [184, 73, 58], non_visible: [180, 176, 165] };
    const ZONE_TEXT = { ok: "OK", attention: "Vigilance", probleme: "Problème", non_visible: "Non visible" };

    let pngUrl = null;
    try { pngUrl = await svgToPngDataUrl(vi.synthesis_svg, 320, 520); } catch (e) {}

    const imgW = 34, imgH = 55;
    const textX = pngUrl ? L + imgW + 8 : L;
    const textW = pngUrl ? CW - imgW - 8 : CW;

    if (pngUrl) {
      try { doc.addImage(pngUrl, "PNG", L, y, imgW, imgH); } catch (e) {}
    }

    let zy = y + 2;
    Object.keys(ZONE_LABELS).forEach((z) => {
      const status = vi.zones[z] || "non_visible";
      const [r, g, b] = ZONE_COLORS[status];
      doc.setFillColor(r, g, b);
      doc.circle(textX + 2, zy - 1.3, 1.3, "F");
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...PDF3.COL.TEXT);
      doc.text(`${ZONE_LABELS[z]} : ${ZONE_TEXT[status]}`, textX + 6, zy);
      zy += 5.2;
    });
    y = Math.max(y + imgH, zy) + 4;

    if (vi.synthese) {
      y = pdf3WrappedText(doc, y, `« ${vi.synthese} »`, { L, CW, size: 9, color: PDF3.COL.SUB, leading: 4.5, style: "italic" }) + 3;
    }
    if (typeof vi.score_delta === "number" && vi.score_delta !== 0) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(9);
      doc.setTextColor(...(vi.score_delta < 0 ? PDF3.COL.ACCENT : PDF3.COL.GREEN));
      doc.text(`Ajustement du score selon l'état visuel réel : ${vi.score_delta > 0 ? "+" : ""}${vi.score_delta} points`, L, y);
      y += 6;
    }
    if (Array.isArray(vi.vigilance_points) && vi.vigilance_points.length) {
      vi.vigilance_points.forEach((pt) => {
        doc.setFillColor(224, 138, 106);
        doc.circle(L + 2, y - 1, 1.4, "F");
        y = pdf3WrappedText(doc, y, pt, { L: L + 7, CW: CW - 7, size: 9, color: PDF3.COL.TEXT, leading: 4.5 }) + 3;
      });
    }
    y += 3;
  }

  // ─── Note légale ───
  if (y < H - 40) {
    y = pdf3Note(doc, y,
      "Ce rapport est produit à titre informatif à partir des données officielles HistoVec. Il ne constitue pas un document officiel et ne se substitue pas à un contrôle technique agréé. VehicleIQ (M.K.L Ingénierie et Conseil) décline toute responsabilité en cas d'inexactitude des données issues de bases tierces. Toute décision d'achat doit être confirmée par une inspection physique du véhicule et la vérification des documents originaux.",
      { color: PDF3.COL.ACCENT, bgColor: PDF3.COL.ACCENT_LT }
    );
  }

  pdfFooter(doc, 2, proQrDataUrl);

  // Sauvegarde
  const vinSlug = (vin || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 17);
  if (opts && opts.asDataUri) return doc.output("datauristring");
  doc.save(`VehicleIQ_Rapport_VIN_${vinSlug || "VEHICULE"}_${Date.now()}.pdf`);
}

async function exportBuyPDF(results, answers) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = PDF3.W, L = PDF3.L, R = PDF3.R, CW = PDF3.CW, H = PDF3.H;
  const proQrDataUrl = await getProLeadQRDataUrl();

  const budget = Number(answers?.budget || 0);
  const ref = pdf3Ref("BUY");

  // Client (recherche personnalisée — coordonnées éventuelles depuis answers)
  const connectedBuy = (typeof window !== "undefined" && window.__viq_user) ? window.__viq_user : null;
  const client = {
    name: answers?.client_name || answers?.user_name || connectedBuy?.name || connectedBuy?.siret_name || "Recherche personnalisée",
    siret_name: answers?.client_company || connectedBuy?.siret_name || "",
    email: answers?.client_email || connectedBuy?.email || "",
    siret: connectedBuy?.siret || "",
    address: answers?.city || connectedBuy?.address || "",
    phone: answers?.client_phone || connectedBuy?.phone || "",
  };

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 1 — COUVERTURE
  // ═══════════════════════════════════════════════════════════════════════

  // En-tête
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("VehicleIQ", L, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text("Cote VO professionnelle", L, 27);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text("SÉLECTION D'ACHAT", W - R, 22, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(`N° ${ref}`, W - R, 28, { align: "right" });
  doc.text(`Émise le ${pdf3DateFr()}`, W - R, 32, { align: "right" });

  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.6);
  doc.line(L, 38, W - R, 38);

  // Bloc émetteur / client
  let y = pdf3PartiesBlock(doc, 48, { client });

  // Titre central
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(`${results.length} véhicules recommandés`, W / 2, y + 4, { align: "center" });

  // Sous-titre marques / ville
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...PDF3.COL.SUB);
  const subBits = [];
  if (answers?.brands && answers.brands !== "toutes marques") subBits.push(answers.brands);
  else subBits.push("Toutes marques");
  if (answers?.city) subBits.push(answers.city);
  doc.text(subBits.join("  ·  "), W / 2, y + 11, { align: "center" });

  y += 22;

  // Carte valeur centrale = Budget
  const boxW = 150, boxH = 48;
  const boxX = (W - boxW) / 2;
  doc.setFillColor(...PDF3.COL.ACCENT_LT);
  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.8);
  doc.roundedRect(boxX, y, boxW, boxH, 3, 3, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("BUDGET MAXIMUM", W / 2, y + 9, { align: "center" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(32);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(pdfNum(budget) + " €", W / 2, y + 28, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PDF3.COL.SUB);
  const constraints = [];
  if (answers?.km) constraints.push(`≤ ${pdfNum(Number(answers.km))} km`);
  if (answers?.year) constraints.push(`À partir de ${answers.year}`);
  if (constraints.length) doc.text(constraints.join("  ·  "), W / 2, y + 38, { align: "center" });

  y += boxH + 12;

  // KPIs : nombre de marques, % top score, budget moyen sélection
  const avgPrice = results.length > 0
    ? Math.round(results.reduce((s, r) => s + (Number(r.price) || 0), 0) / results.length)
    : 0;
  const topMatch = results.length > 0
    ? Math.max(...results.map(r => Number(r.match) || 0))
    : 0;
  y = pdf3Kpis(doc, y, [
    { label: "Véhicules trouvés",  value: String(results.length), sub: "Annonces actives" },
    { label: "Top score",          value: `${topMatch} %`,         sub: "Meilleur match", highlight: true },
    { label: "Prix médian sélection", value: pdfNum(avgPrice) + " €", sub: "Sur les résultats" },
  ]);

  // Pied de couverture (légal)
  const footY = H - 22;
  doc.setDrawColor(...PDF3.COL.BORDER);
  doc.setLineWidth(0.2);
  doc.line(L, footY, W - R, footY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(
    `${VEHICLEIQ_LEGAL.legal_name} · SIRET ${VEHICLEIQ_LEGAL.siret} · ${VEHICLEIQ_LEGAL.address_line2} · ${VEHICLEIQ_LEGAL.email}`,
    W / 2, footY + 4, { align: "center" }
  );
  doc.setFontSize(6);
  doc.setTextColor(...PDF3.COL.MUTED);
  doc.text(
    "Document à valeur informative — Conditions Générales de Vente disponibles sur vehicleiq.fr (rubrique Légal).",
    W / 2, footY + 8, { align: "center" }
  );

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 2 — Profil d'achat + Tableau récap
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  pdfHeader(doc, "Sélection d'achat");
  y = 30;

  // ─── Section 1 : Profil d'achat (récap des critères) ───
  y = pdf3SectionTitle(doc, y, "Profil d'achat");
  const criteres = [
    { critere: "Budget maximum",     valeur: pdfNum(budget) + " €" },
    { critere: "Marques souhaitées", valeur: answers?.brands || "Toutes marques" },
    { critere: "Ville",              valeur: answers?.city || "France" },
    { critere: "Kilométrage max",    valeur: pdfNum(Number(answers?.km || 100000)) + " km" },
    { critere: "Année minimum",      valeur: String(answers?.year || 2018) },
    { critere: "Carburant",          valeur: answers?.fuel || "Peu importe" },
    { critere: "Vendeur préféré",    valeur: answers?.seller || "Les deux" },
    { critere: "Import accepté",     valeur: answers?.import || "Peu importe" },
  ];
  y = pdf3Table(doc, y, [
    { key: "critere", label: "Critère", w: 70 },
    { key: "valeur",  label: "Valeur",  w: 110, bold: true },
  ], criteres);

  // ─── Section 2 : Tableau récap des véhicules ───
  y += 4;
  y = pdf3SectionTitle(doc, y, `${results.length} véhicules recommandés`);

  const carsRows = results.map((car, i) => ({
    rang:     `#${i + 1}`,
    marque:   `${car.make || ""} ${car.model || ""}`.trim() || "—",
    annee:    String(car.year || "—"),
    km:       car.km ? pdfNum(Number(car.km)) + " km" : "—",
    prixNeuf: car.prixNeufFormatted || (car.prix_neuf ? pdfNum(Number(car.prix_neuf)) + " €" : "—"),
    prix:     (pdfNum(Number(car.price || 0)) + " €"),
    match:    car.match ? `${car.match} %` : "—",
  }));
  y = pdf3Table(doc, y, [
    { key: "rang",     label: "#",         w: 10, align: "center" },
    { key: "marque",   label: "Marque / Modèle", w: 58, bold: true },
    { key: "annee",    label: "Année",     w: 14, align: "center" },
    { key: "km",       label: "Kilométrage", w: 26, align: "right" },
    { key: "prixNeuf", label: "Prix neuf", w: 26, align: "right" },
    { key: "prix",     label: "Cote VIQ.AI", w: 28, align: "right", bold: true },
    { key: "match",    label: "Score",     w: 18, align: "right" },
  ], carsRows);

  pdfFooter(doc, 2, proQrDataUrl);

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 3+ — Fiches détaillées (1 par véhicule, organisées par cartes)
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  pdfHeader(doc, "Sélection d'achat — Fiches détaillées");
  y = 30;
  let pg = 3;
  y = pdf3SectionTitle(doc, y, "Détail des véhicules recommandés");

  results.forEach((car, i) => {
    const cardH = 36;
    // Saut de page si nécessaire (laisser 30mm pour pied)
    if (y + cardH > H - 30) {
      pdfFooter(doc, pg, proQrDataUrl);
      doc.addPage();
      pg++;
      pdfHeader(doc, "Sélection d'achat — suite");
      y = 30;
    }

    // Carte véhicule
    doc.setFillColor(...PDF3.COL.WHITE);
    doc.setDrawColor(...PDF3.COL.BORDER);
    doc.setLineWidth(0.4);
    doc.roundedRect(L, y, CW, cardH, 2, 2, "FD");

    // Bande dorée gauche
    const matchCol = car.match >= 90 ? PDF3.COL.GREEN
      : car.match >= 80 ? PDF3.COL.ACCENT
      : PDF3.COL.MUTED;
    doc.setFillColor(...matchCol);
    doc.rect(L, y, 3, cardH, "F");

    // Numéro de rang
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...PDF3.COL.WHITE);
    doc.text(`#${i + 1}`, L + 1.5, y + cardH / 2 + 1, { align: "center" });

    // Marque + modèle
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...PDF3.COL.TEXT);
    doc.text((car.make || "") + " " + (car.model || ""), L + 8, y + 7);

    // Caractéristiques en sous-titre
    const caracs = [
      car.year,
      car.km ? pdfNum(Number(car.km)) + " km" : null,
      car.fuel,
      car.gearbox,
    ].filter(Boolean).join("  ·  ");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...PDF3.COL.SUB);
    doc.text(caracs, L + 8, y + 12);

    // Pourquoi ce match
    if (car.why_match) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(7.5);
      doc.setTextColor(...PDF3.COL.ACCENT);
      const txt = "→ " + String(car.why_match).slice(0, 100);
      doc.text(txt, L + 8, y + 18);
    }

    // Pros (2 max)
    if (car.pros && car.pros.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...PDF3.COL.GREEN);
      doc.text("✓ " + car.pros.slice(0, 2).join("  ·  ✓ "), L + 8, y + 24);
    }

    // Prix neuf catalogue (repère utile à côté de la cote)
    if (car.prix_neuf || car.prixNeufFormatted) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...PDF3.COL.MUTED);
      doc.text(`Prix neuf catalogue : ${car.prixNeufFormatted || (pdfNum(Number(car.prix_neuf)) + " €")}`, L + 8, y + 30);
    }

    // Prix à droite (gros, doré)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(...PDF3.COL.TEXT);
    doc.text(
      (pdfNum(Number(car.price || 0)) + " €"),
      L + CW - 4, y + 10, { align: "right" }
    );

    // Match % + couleur
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...matchCol);
    doc.text(`${car.match || 0} % match`, L + CW - 4, y + 17, { align: "right" });

    // Fourchette argus
    if (car.argus_low) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...PDF3.COL.MUTED);
      doc.text(
        `Argus ${pdfNum(Number(car.argus_low))}–${pdfNum(Number(car.argus_high || car.argus_low))} €`,
        L + CW - 4, y + 23, { align: "right" }
      );
    }

    y += cardH + 4;
  });

  // ─── Note légale en bas de la dernière page ───
  if (y < H - 30) {
    y += 4;
    y = pdf3Note(doc, y,
      "Cette sélection est générée à titre indicatif par l'algorithme VIQ.AI® Engine à partir des critères saisis. Les prix et disponibilités peuvent évoluer rapidement. VehicleIQ ne se substitue pas à un essai physique du véhicule ni à la vérification des documents originaux avant achat.",
      { color: PDF3.COL.ACCENT, bgColor: PDF3.COL.ACCENT_LT }
    );
  } else {
    pdfFooter(doc, pg, proQrDataUrl);
    doc.addPage();
    pg++;
    pdfHeader(doc, "Sélection d'achat — mentions");
    pdf3Note(doc, 40,
      "Cette sélection est générée à titre indicatif par l'algorithme VIQ.AI® Engine à partir des critères saisis. Les prix et disponibilités peuvent évoluer rapidement. VehicleIQ ne se substitue pas à un essai physique du véhicule ni à la vérification des documents originaux avant achat.",
      { color: PDF3.COL.ACCENT, bgColor: PDF3.COL.ACCENT_LT }
    );
  }

  pdfFooter(doc, pg, proQrDataUrl);

  doc.save(`VehicleIQ_Selection_Achat_${Date.now()}.pdf`);
}

// ─── Mandat de vente — génération PDF (format aligné au modèle Word) ──────────
// `m` : objet mandat complet (mandant + véhicule + prix + commission + options).
async function exportMandatPDF(m) {
  const online = m.type === "online_exclusive";
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const W = 210, L = 18, R = 192, CW = R - L;
  const ACCENT = [168, 128, 58], INK = [26, 26, 31], SUB = [78, 82, 91], MUTED = [142, 147, 158];
  let y = 18;

  const ensure = (need) => { if (y + need > 282) { doc.addPage(); y = 18; } };
  const euro = (v) => (v || v === 0) ? (pdfNum(Number(v)) + " €") : "—";

  // En-tête
  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...ACCENT);
  doc.text("VehicleIQ", L, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...MUTED);
  doc.text("M.K.L Ingénierie et Conseil — vehicleiq.fr", L, y + 5);
  doc.setDrawColor(...ACCENT); doc.setLineWidth(0.6); doc.line(L, y + 8, R, y + 8);
  y += 18;

  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...INK);
  doc.text(online ? "MANDAT DE VENTE EXCLUSIF — VENTE EN LIGNE & ENCHÈRES" : "MANDAT DE VENTE DE VÉHICULE D'OCCASION", W / 2, y, { align: "center" });
  doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...SUB);
  doc.text(online ? "Vente en ligne et enchères en ligne — durée 1 mois — exclusif" : "Mandat d'intermédiation — applicable aux particuliers et aux professionnels", W / 2, y + 6, { align: "center" });
  y += 14;

  // Réf
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...SUB);
  const ref = m.ref || ("VIQ-MDT-" + Date.now());
  const dateStr = new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(`Référence : ${ref}`, L, y);
  doc.text(`Date : ${dateStr}`, R, y, { align: "right" });
  y += 5; doc.text(`Lieu : ${m.lieu || "Saint-Quentin"}`, L, y);
  y += 9;

  // Helpers de section
  const secTitle = (t) => {
    ensure(14); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...ACCENT);
    doc.text(t, L, y); y += 6;
  };
  const para = (t, opt = {}) => {
    doc.setFont("helvetica", opt.bold ? "bold" : (opt.italic ? "italic" : "normal"));
    doc.setFontSize(opt.size || 9); doc.setTextColor(...(opt.color || INK));
    const lines = doc.splitTextToSize(t, CW);
    ensure(lines.length * 4.4 + 2);
    doc.text(lines, L, y); y += lines.length * 4.4 + (opt.after != null ? opt.after : 2);
  };
  const kv = (k, v) => {
    ensure(6); doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...SUB);
    doc.text(k + " : ", L, y);
    const kw = doc.getTextWidth(k + " : ");
    doc.setFont("helvetica", "normal"); doc.setTextColor(...INK);
    const lines = doc.splitTextToSize(String(v || "—"), CW - kw);
    doc.text(lines, L + kw, y); y += Math.max(5, lines.length * 4.4);
  };

  // Parties
  secTitle("Entre les soussignés");
  para("Le Mandataire", { bold: true });
  para("M.K.L Ingénierie et Conseil, exploitant la plateforme VehicleIQ (vehicleiq.fr), siège : 2 Avenue de la République, 02100 Saint-Quentin, SIRET 904 954 542 00026, représentée par son dirigeant dûment habilité (« le Mandataire »).");
  para("Le Mandant", { bold: true });
  const statut = m.mandantStatut === "pro" ? "Professionnel" : "Particulier";
  kv("Qualité", statut);
  kv("Nom / Raison sociale", m.mandantNom);
  if (m.mandantRepresentant) kv("Représenté par", m.mandantRepresentant);
  kv("Adresse", m.mandantAdresse);
  kv("Téléphone / E-mail", [m.mandantTel, m.mandantEmail].filter(Boolean).join(" · "));
  if (m.mandantStatut === "pro") { kv("SIRET", m.mandantSiret); if (m.mandantTva) kv("N° TVA intracom.", m.mandantTva); }
  para("(« le Mandant »). Le Mandataire et le Mandant sont ensemble « les Parties ».", { italic: true, color: SUB, size: 8.5, after: 4 });

  secTitle("Article 1 — Objet du mandat");
  para(online
    ? "Le Mandant confie au Mandataire un mandat EXCLUSIF d'intermédiation ayant pour objet la commercialisation et la recherche d'un acquéreur pour le(s) véhicule(s) désigné(s) à l'article 2, par VENTE EN LIGNE et par ENCHÈRES EN LIGNE, en particulier à destination du marché français. Le Mandataire agit en qualité d'intermédiaire : il n'achète pas le Véhicule, n'en devient pas propriétaire, n'encaisse pas le prix et ne signe pas l'acte de vente, lequel est conclu directement entre le Mandant et l'acquéreur."
    : "Le Mandant confie au Mandataire un mandat d'intermédiation ayant pour objet la recherche d'un acquéreur et la facilitation de la vente du véhicule désigné à l'article 2 (« le Véhicule »). Mandat d'entremise : le Mandataire met en œuvre des moyens (cote, mise en valeur, diffusion, mise en relation, accompagnement de la négociation). Il n'achète pas le Véhicule et n'en devient pas propriétaire. Sauf stipulation expresse, il ne signe pas l'acte de vente et n'encaisse pas le prix : la vente est conclue directement entre le Mandant et l'acquéreur.");

  secTitle("Article 2 — Désignation du véhicule");
  kv("Marque / Modèle", m.vehMakeModel);
  kv("Année / 1re mise en circulation", m.vehYear);
  kv("Kilométrage", m.vehKm ? (pdfNum(Number(m.vehKm)) + " km") : "—");
  kv("Énergie / Boîte", [m.vehFuel, m.vehGearbox].filter(Boolean).join(" / "));
  if (m.vehVin) kv("N° de série (VIN)", m.vehVin);
  if (m.vehImmat) kv("Immatriculation", m.vehImmat);
  para("Le Mandant déclare être propriétaire du Véhicule ou disposer du pouvoir de le faire vendre, et garantit l'exactitude des informations. Le Véhicule est réputé vendu en l'état décrit.", { italic: true, color: SUB, size: 8.5 });

  secTitle("Article 3 — Prix de vente et cote");
  para("Le Mandataire communique une cote de la valeur de marché établie via son moteur VIQ.AI®, fournie à titre indicatif (ni garantie de prix, ni offre d'achat).");
  if (m.estimation) kv("Cote VIQ.AI® (indicative)", euro(m.estimation));
  kv("Prix de vente souhaité (TTC)", euro(m.prixSouhaite));
  if (online && m.prixSouhaiteHt) kv("Prix de vente souhaité (HT)", euro(m.prixSouhaiteHt));
  kv("Prix de vente minimum / plancher (TTC)", euro(m.prixMin));
  if (online && m.prixMinHt) kv("Prix de vente minimum / plancher (HT)", euro(m.prixMinHt));
  para(online
    ? "Les prix planchers sont fixés à 7,5 % en dessous du prix actuellement affiché (en HT et en TTC). Le Mandataire ne pourra accepter, conclure ni recommander aucune offre — y compris une enchère adjugée — inférieure au prix plancher correspondant sans l'accord écrit et préalable du Mandant."
    : "Le Mandataire ne pourra accepter une offre inférieure au prix minimum sans accord exprès et préalable du Mandant.", { size: 8.5, color: SUB });

  secTitle("Article 4 — Rémunération du Mandataire");
  let commTxt = "";
  if (m.commType === "forfait") commTxt = `Forfait de ${euro(m.commValue)}`;
  else if (m.commType === "pourcentage") commTxt = `${m.commValue || "—"} % du prix de vente effectif`;
  else if (m.commType === "net_vendeur") commTxt = `Différence entre le prix de vente effectif et le prix net vendeur de ${euro(m.commValue)}`;
  para(`La commission d'intermédiation n'est due qu'en cas de vente effective à un acquéreur présenté par le Mandataire, après encaissement du prix. Modalité retenue : ${commTxt}. ` + (m.mandantStatut === "pro" ? "Montant HT, TVA en sus le cas échéant." : "Montant TTC."));
  para("Aucune commission n'est due si le Véhicule n'est pas vendu pendant la durée du mandat, sous réserve de l'article 8.", { size: 8.5, color: SUB, italic: true });

  secTitle("Article 5 — Durée du mandat");
  para(`Le mandat est conclu pour une durée de ${m.duree || "trois (3) mois"} à compter de sa signature, renouvelable par accord écrit. À défaut de vente et de renouvellement, il prend fin à l'échéance sans indemnité.`);

  secTitle("Article 6 — Nature du mandat" + (online ? " et diffusion" : ""));
  para(online
    ? "Mandat EXCLUSIF : pendant toute la durée du mandat, le Mandant confie la commercialisation en ligne et aux enchères en ligne du / des Véhicule(s) au seul Mandataire, et s'interdit de les confier à un autre intermédiaire pour une diffusion en ligne ou aux enchères. Le Mandataire diffuse via ses canaux de vente en ligne (places de marché, portails spécialisés, mise en relation directe) et d'enchères en ligne, en ciblant en priorité le marché français."
    : (m.exclusif
      ? "Mandat EXCLUSIF : le Mandant confie la vente au seul Mandataire pendant la durée du mandat. Pour un Mandant particulier, l'exclusivité ne prive pas du droit de rétractation (art. 12)."
      : "Mandat NON EXCLUSIF : le Mandant conserve la faculté de vendre lui-même ou de recourir à d'autres intermédiaires."));
  if (online) {
    secTitle("Article 6 bis — Mise en pause des annonces déjà en ligne");
    para("Afin de préserver la cohérence de prix et l'exclusivité, le Mandant s'engage à mettre en pause (dépublier ou rendre non visibles) l'ensemble des annonces du / des Véhicule(s) déjà en ligne — quel que soit le portail — pendant une durée de deux (2) semaines à compter de la signature. Durant cette période, seul le Mandataire assure la diffusion. À l'issue des deux semaines, toute remise en ligne par le Mandant devra respecter les prix planchers (art. 3) et l'exclusivité du présent article.");
  }

  secTitle("Article 7 — Obligations des Parties");
  para("Le Mandataire : estimer, mettre en valeur et diffuser l'annonce, rechercher des acquéreurs, rendre compte, transmettre les offres sérieuses, agir avec diligence et loyauté, respecter le RGPD. Le Mandant : fournir des informations exactes, remettre les documents (CI, contrôle technique valide, carnet d'entretien), garantir un véhicule libre de gage/opposition, le maintenir disponible et en l'état, informer de toute vente directe, signer l'acte et accomplir les formalités de cession au prix convenu.");

  secTitle("Article 8 — Acquéreur présenté par le Mandataire");
  para(`La commission reste due si, pendant la durée du mandat ou dans un délai de ${m.dureePost || "trois (3) mois"} après son expiration, le Véhicule est vendu à un acquéreur présenté par le Mandataire ou ayant pris contact grâce à son action.`);

  secTitle("Article 9 — Résiliation");
  para(`Chaque Partie peut résilier par lettre recommandée AR ou e-mail avec AR, moyennant un préavis de ${m.preavis || "quinze (15) jours"}. La résiliation ne dispense pas du paiement de la commission due au titre d'une vente déjà conclue ou relevant de l'article 8.`);

  secTitle("Article 10 — Responsabilité et garanties");
  para("Le Mandataire est tenu d'une obligation de moyens, non de résultat ; il ne garantit ni la vente ni un prix déterminé. Le Mandant demeure seul vendeur et assume les obligations légales du vendeur (vices cachés ; pour le vendeur professionnel, garantie légale de conformité). Les cotes VIQ.AI® sont indicatives.");

  secTitle("Article 11 — Protection des données (RGPD)");
  para("Les données sont traitées par le Mandataire (responsable de traitement) pour l'exécution du mandat, conformément au RGPD. Droits d'accès, rectification, effacement et opposition : contact@vehicleiq.fr.");

  if (m.mandantStatut !== "pro") {
    secTitle("Article 12 — Droit de rétractation (Mandant particulier)");
    para("Pour un mandat conclu à distance ou hors établissement avec un consommateur, celui-ci dispose de quatorze (14) jours pour se rétracter sans motif (Code de la consommation), par notification à contact@vehicleiq.fr. En cas d'exécution anticipée à sa demande, il peut être redevable des prestations déjà fournies au prorata.");
  }

  secTitle("Article 13 — Loi applicable et litiges");
  para("Droit français. Recherche d'une solution amiable ; le consommateur peut recourir gratuitement à un médiateur de la consommation. À défaut, juridictions compétentes de droit commun.");

  secTitle("Article 14 — Dispositions diverses");
  para("Le présent mandat constitue l'intégralité de l'accord des Parties. Toute modification fait l'objet d'un avenant écrit. Établi en deux (2) exemplaires originaux.");

  // Signatures
  ensure(40);
  y += 4;
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...ACCENT);
  doc.text("Signatures", L, y); y += 7;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...INK);
  doc.text(`Fait à ${m.lieu || "Saint-Quentin"}, le ${dateStr}, en deux exemplaires.`, L, y); y += 10;
  const colW = CW / 2;
  doc.setFont("helvetica", "bold"); doc.text("Le Mandant", L, y); doc.text("Le Mandataire", L + colW + 6, y); y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...SUB);
  doc.text(`Nom : ${m.mandantNom || "______________________"}`, L, y);
  doc.text("M.K.L Ingénierie et Conseil — VehicleIQ", L + colW + 6, y); y += 5;
  doc.setFontSize(8); doc.setTextColor(...MUTED);
  doc.text("Mention « Bon pour mandat de vente »", L, y);
  doc.text("Mention « Bon pour acceptation »", L + colW + 6, y); y += 12;
  doc.setTextColor(...SUB); doc.text("Signature :", L, y); doc.text("Signature :", L + colW + 6, y);

  // Pied de page sur toutes les pages
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...MUTED);
    doc.text("M.K.L Ingénierie et Conseil · SIRET 904 954 542 00026 · vehicleiq.fr", L, 290);
    doc.text(`Page ${i}/${pages}`, R, 290, { align: "right" });
  }

  const fname = `Mandat_${(m.mandantNom || "client").replace(/[^a-z0-9]/gi, "_")}_${Date.now()}.pdf`;
  doc.save(fname);
  return fname;
}

// ─── Rapport d'historique VIQ.AI® (à partir des données SIV de la plateforme) ──
// IMPORTANT : ce n'est PAS l'Histovec officiel (réservé à l'État / au titulaire
// de la carte grise). C'est un rapport maison compilant les données connues, qui
// invite à obtenir l'Histovec officiel pour l'historique complet.
async function exportHistoriqueReport(v) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const W = 210, L = 18, R = 192, CW = R - L;
  const ACCENT = [168, 128, 58], INK = [26, 26, 31], SUB = [78, 82, 91], MUTED = [142, 147, 158];
  let y = 18;

  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...ACCENT);
  doc.text("VehicleIQ", L, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...MUTED);
  doc.text("Rapport d'historique VIQ.AI® — édité le " + new Date().toLocaleDateString("fr-FR"), L, y + 5);
  doc.setDrawColor(...ACCENT); doc.setLineWidth(0.6); doc.line(L, y + 8, R, y + 8);
  y += 18;

  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...INK);
  doc.text("RAPPORT D'HISTORIQUE VÉHICULE", W / 2, y, { align: "center" });
  doc.setFont("helvetica", "italic"); doc.setFontSize(8.5); doc.setTextColor(...SUB);
  doc.text("Synthèse des données techniques disponibles via VIQ.AI®", W / 2, y + 6, { align: "center" });
  y += 16;

  const kv = (k, val) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(...SUB);
    doc.text(k, L, y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(...INK);
    doc.text(String(val || "—"), L + 60, y);
    y += 7;
  };
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...ACCENT);
  doc.text("Identité du véhicule", L, y); y += 8;
  kv("Marque / Modèle", v.makeModel);
  kv("Immatriculation", v.immat);
  kv("N° de série (VIN)", v.vin);
  kv("Année / 1re mise en circ.", v.year);
  kv("Kilométrage", v.km ? (pdfNum(Number(v.km)) + " km") : "—");
  kv("Énergie / Boîte", [v.fuel, v.gearbox].filter(Boolean).join(" / "));
  if (v.viqPrice) kv("Estimation VIQ.AI®", pdfNum(Number(v.viqPrice)) + " €");
  y += 4;

  doc.setFillColor(247, 239, 220); doc.setDrawColor(...ACCENT); doc.setLineWidth(0.4);
  doc.roundedRect(L, y, CW, 34, 3, 3, "FD");
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...INK);
  doc.text("Pour l'historique officiel complet : Histovec", L + 5, y + 8);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...SUB);
  const note = doc.splitTextToSize("L'historique administratif officiel (sinistres, gages, oppositions, changements de propriétaire) est délivré gratuitement par l'État via Histovec. Seul le titulaire de la carte grise peut l'éditer, sur histovec.interieur.gouv.fr (connexion FranceConnect).", CW - 10);
  doc.text(note, L + 5, y + 15);
  y += 42;

  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  const disc = doc.splitTextToSize("Ce rapport est établi à partir des données techniques disponibles via VIQ.AI® et ne se substitue pas au rapport Histovec officiel de l'État. Données fournies à titre indicatif.", CW);
  doc.text(disc, L, y);

  doc.setFontSize(7); doc.setTextColor(...MUTED);
  doc.text("M.K.L Ingénierie et Conseil · SIRET 904 954 542 00026 · vehicleiq.fr", L, 290);

  doc.save(`Rapport_historique_${(v.immat || v.makeModel || "vehicule").replace(/[^a-z0-9]/gi, "_")}_${Date.now()}.pdf`);
}

// ─── PDF du calcul carte grise / malus écologique ────────────────────────────
async function exportCarteGrisePDF(f, r) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const W = 210, L = 18, R = 192, CW = R - L;
  const ACCENT = [168, 128, 58], INK = [26, 26, 31], SUB = [78, 82, 91], MUTED = [142, 147, 158];
  let y = 18;
  const eur = (v) => (v || v === 0) ? (pdfNum(Number(v)) + " €") : "—";

  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...ACCENT);
  doc.text("VehicleIQ", L, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...MUTED);
  doc.text("Estimation carte grise & malus — éditée le " + new Date().toLocaleDateString("fr-FR"), L, y + 5);
  doc.setDrawColor(...ACCENT); doc.setLineWidth(0.6); doc.line(L, y + 8, R, y + 8);
  y += 18;

  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...INK);
  doc.text("ESTIMATION DU COÛT D'IMMATRICULATION", W / 2, y, { align: "center" });
  y += 14;

  const kv = (k, v) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(...SUB);
    doc.text(k, L, y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(...INK);
    doc.text(String(v || "—"), L + 70, y); y += 6.5;
  };
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...ACCENT);
  doc.text("Caractéristiques du véhicule", L, y); y += 8;
  if (f.makeModel) kv("Véhicule", f.makeModel);
  if (f.immat) kv("Immatriculation", f.immat);
  if (f.vin) kv("VIN", f.vin);
  kv("Énergie", f.energie);
  kv("Puissance", (f.ch ? f.ch + " ch" : "—") + (f.ch ? " (" + Math.round(Number(f.ch) * 0.7355) + " kW)" : "") + " · " + (f.cv || "—") + " CV fiscaux");
  kv("Émissions CO₂", (f.co2 || "—") + " g/km");
  kv("Masse en ordre de marche", (f.poids || "—") + " kg");
  kv("1ʳᵉ immatriculation", f.dateImmat || "—");
  kv("Région d'immatriculation", f.region);
  kv("Véhicule importé", f.isImport ? ("Oui · " + (f.paysAchat || "UE")) : "Non");
  y += 4;

  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...ACCENT);
  doc.text("Détail du coût", L, y); y += 8;
  const line = (k, sub, v) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(...INK);
    doc.text(k, L, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...SUB);
    if (sub) doc.text(sub, L, y + 4);
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...INK);
    doc.text(eur(v), R, y, { align: "right" });
    doc.setDrawColor(230, 225, 212); doc.setLineWidth(0.2); doc.line(L, y + 6, R, y + 6);
    y += sub ? 11 : 8;
  };
  line("Taxe régionale (Y.1)", `${f.cv || 0} CV × ${eur(r.tarifCV)}${f.region === "Île-de-France" ? " + majoration IDFM" : ""}${r.plus10 ? " · réduite de 50 % (>10 ans)" : ""}`, r.taxeReg);
  if (f.isImport) {
    line("Malus CO₂", r.plus15 ? "Exonéré (véhicule de plus de 15 ans)" : `Barème ${r.annee} · décote import ${Math.round(r.dec * 100)} %`, r.mCO2);
    line("Malus au poids (TMOM)", r.plus15 ? "Exonéré (>15 ans)" : `Barème ${r.annee} · décote ${Math.round(r.dec * 100)} %`, r.mPoids);
  }
  line("Taxes fixes", "Gestion (11 €) + acheminement (2,76 €)", r.fixes);

  y += 2;
  doc.setFillColor(247, 239, 220); doc.setDrawColor(...ACCENT); doc.setLineWidth(0.5);
  doc.roundedRect(L, y, CW, 16, 2, 2, "FD");
  doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...INK);
  doc.text("TOTAL ESTIMÉ", L + 5, y + 10);
  doc.setFontSize(15); doc.setTextColor(...ACCENT);
  doc.text(eur(r.total), R - 5, y + 10, { align: "right" });
  y += 24;

  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  const disc = doc.splitTextToSize("Estimation indicative fondée sur les barèmes 2026 (taxe régionale par région, malus CO₂ WLTP, malus au poids dès 1 500 kg, décote import par ancienneté — art. L.421-7-2 du CIBS). Le montant officiel est calculé lors de la démarche sur le service public. Les barèmes évoluent chaque année.", CW);
  doc.text(disc, L, y);

  doc.setFontSize(7); doc.setTextColor(...MUTED);
  doc.text("M.K.L Ingénierie et Conseil · SIRET 904 954 542 00026 · vehicleiq.fr", L, 290);

  doc.save(`Carte_grise_${(f.immat || "vehicule").replace(/[^a-z0-9]/gi, "_")}_${Date.now()}.pdf`);
}

// ─── Rapport d'analyse détaillée (PDF) — reprend les blocs de l'onglet ────────
async function exportAnalyseDetailleePDF(stats, siret) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  const W = 210, L = 18, R = 192, CW = R - L;
  const ACCENT = [168, 128, 58], INK = [26, 26, 31], SUB = [78, 82, 91], MUTED = [142, 147, 158], GREEN = [46, 111, 92], RED = [155, 44, 44];
  let y = 18;
  const s = stats || {};
  const k = s.kpis || {};
  const eur = (v) => (v || v === 0) ? (pdfNum(Number(v)) + " €") : "—";
  const ensure = (need) => { if (y + need > 282) { doc.addPage(); y = 18; } };

  doc.setFont("helvetica", "bold"); doc.setFontSize(20); doc.setTextColor(...ACCENT);
  doc.text("VehicleIQ", L, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...MUTED);
  doc.text("Rapport d'analyse détaillée — édité le " + new Date().toLocaleDateString("fr-FR"), L, y + 5);
  if (siret) doc.text("SIRET " + siret, R, y + 5, { align: "right" });
  doc.setDrawColor(...ACCENT); doc.setLineWidth(0.6); doc.line(L, y + 8, R, y + 8);
  y += 16;

  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...INK);
  doc.text("ANALYSE DÉTAILLÉE DE VOTRE ACTIVITÉ", W / 2, y, { align: "center" });
  y += 12;

  const sec = (t) => { ensure(12); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...ACCENT); doc.text(t, L, y); y += 6; };

  sec("Indicateurs clés");
  const kpis = [
    ["Marge totale tracée", eur(k.total_margin)],
    ["Marge moyenne / véhicule", eur(k.avg_margin)],
    ["Volume vendu", eur(k.total_sold_volume)],
    ["Ventes avec marge", String(k.nb_sales_with_margin || 0)],
    ["Précision VIQ.AI®", k.precision_pct != null ? ("±" + k.precision_pct + " %") : "—"],
    ["Conversion estim. → vente", k.conversion_rate != null ? (k.conversion_rate + " %") : "—"],
  ];
  doc.setFontSize(9);
  kpis.forEach((row, i) => {
    const col = i % 2; const x = L + col * (CW / 2);
    if (col === 0) ensure(7);
    doc.setFont("helvetica", "normal"); doc.setTextColor(...SUB); doc.text(row[0] + " :", x, y);
    doc.setFont("helvetica", "bold"); doc.setTextColor(...INK);
    doc.text(row[1], x + 55, y);
    if (col === 1 || i === kpis.length - 1) y += 6;
  });
  y += 4;

  sec("Top véhicules les plus rentables");
  const top = s.top5 || [];
  if (!top.length) { doc.setFont("helvetica", "italic"); doc.setFontSize(8.5); doc.setTextColor(...MUTED); doc.text("Aucune vente avec marge renseignée.", L, y); y += 6; }
  else top.slice(0, 7).forEach((t, i) => {
    ensure(6);
    const vd = t.vehicle || {};
    const label = vd.make_model || vd.makeModel || [vd.make, vd.model].filter(Boolean).join(" ") || ("Véhicule #" + t.id);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...INK);
    doc.text(`${i + 1}. ${label}`, L, y);
    doc.setFont("helvetica", "bold"); doc.setTextColor(...(t.margin >= 0 ? GREEN : RED));
    doc.text(`${t.margin >= 0 ? "+" : ""}${pdfNum(t.margin)} €`, R, y, { align: "right" });
    y += 5.5;
  });
  y += 3;

  sec("Performance par plateforme");
  const plats = s.platforms || [];
  if (!plats.length) { doc.setFont("helvetica", "italic"); doc.setFontSize(8.5); doc.setTextColor(...MUTED); doc.text("Aucune vente enregistrée.", L, y); y += 6; }
  else {
    ensure(7);
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
    doc.text("PLATEFORME", L, y); doc.text("VENTES", L + 70, y); doc.text("PRO / PART.", L + 95, y);
    doc.text("PRIX MOY.", L + 130, y); doc.text("MARGE MOY.", R, y, { align: "right" });
    y += 4.5;
    plats.slice(0, 12).forEach(p => {
      ensure(5.5);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...INK);
      doc.text(String(p.name).slice(0, 28), L, y);
      doc.text(String(p.count), L + 70, y);
      doc.setTextColor(...SUB);
      doc.text(`${p.pro || 0} / ${p.particulier || 0}`, L + 95, y);
      doc.text(p.avg_sold ? pdfNum(p.avg_sold) + " €" : "—", L + 130, y);
      doc.setFont("helvetica", "bold"); doc.setTextColor(...(p.avg_margin == null ? MUTED : p.avg_margin >= 0 ? GREEN : RED));
      doc.text(p.avg_margin == null ? "n/a" : `${p.avg_margin >= 0 ? "+" : ""}${pdfNum(p.avg_margin)} €`, R, y, { align: "right" });
      y += 5;
    });
  }
  y += 3;

  const mix = s.client_mix || {};
  if (mix.pro || mix.particulier) {
    sec("Mix Pro / Particulier");
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...INK);
    ensure(6);
    doc.text(`Pro : ${mix.pro || 0} vente(s)` + (mix.pro_avg != null ? ` · marge moy. ${pdfNum(mix.pro_avg)} €` : ""), L, y); y += 5.5;
    doc.text(`Particulier : ${mix.particulier || 0} vente(s)` + (mix.particulier_avg != null ? ` · marge moy. ${pdfNum(mix.particulier_avg)} €` : ""), L, y); y += 7;
  }

  const segs = s.price_segments || [];
  if (segs.length) {
    sec("Rotation par segment de prix");
    segs.forEach(seg => {
      ensure(5.5);
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...INK);
      doc.text(seg.label || "—", L, y);
      doc.setTextColor(...SUB);
      doc.text(`${seg.count || 0} vente(s)`, L + 70, y);
      if (seg.total_margin != null) {
        doc.setFont("helvetica", "bold"); doc.setTextColor(...(seg.total_margin >= 0 ? GREEN : RED));
        doc.text(`${seg.total_margin >= 0 ? "+" : ""}${pdfNum(seg.total_margin)} €`, R, y, { align: "right" });
      }
      y += 5.5;
    });
    y += 2;
  }

  const agents = s.top_agents || [];
  if (agents.length) {
    sec("Top Suivi Ventes");
    agents.slice(0, 5).forEach((a, i) => {
      ensure(6);
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...INK);
      doc.text(`${i + 1}. ${a.name || a.email || "—"}`, L, y);
      doc.setTextColor(...SUB); doc.setFontSize(7.5);
      doc.text(`${a.sales || 0} vente(s)`, L + 90, y);
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...GREEN);
      doc.text(`${(a.total_margin || 0) >= 0 ? "+" : ""}${pdfNum(a.total_margin || 0)} €`, R, y, { align: "right" });
      y += 5.5;
    });
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...MUTED);
    doc.text("Rapport VehicleIQ · données indicatives issues de votre suivi de vente", L, 290);
    doc.text(`Page ${i}/${pages}`, R, 290, { align: "right" });
  }
  doc.save(`Rapport_analyse_VehicleIQ_${Date.now()}.pdf`);
}

async function exportSellPDF(result, answers, opts) {
  const JsPDF = await loadJsPDF();
  const doc = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = PDF3.W, L = PDF3.L, R = PDF3.R, CW = PDF3.CW, H = PDF3.H;
  const proQrDataUrl = await getProLeadQRDataUrl();

  // ── Extraction des données ──
  const v = {
    make:    result.make || answers?.make || "Véhicule",
    model:   answers?.model || "",
    year:    result.year || answers?.year_sell || "—",
    km:      result.km || answers?.km_sell || 0,
    fuel:    result.fuel || answers?.fuel_sell || "",
    gearbox: answers?.gearbox_sell || "",
    state:   answers?.state || "",
    ct:      answers?.ct || "",
    owners:  answers?.owners || "",
    color:   answers?.color || "",
  };
  const prixViq = result.prix_viq || Math.round(((result.argus_mid || result.argus_low) || 0) * 0.95 / 100) * 100;
  const argusLow  = result.argus_low  || Math.round(prixViq * 0.94);
  const argusHigh = result.argus_high || Math.round(prixViq * 1.05);
  const ref = pdf3Ref("EST");

  // Client : priorité aux champs saisis dans answers, sinon repli sur
  // l'utilisateur connecté (window.__viq_user via getPdfClient).
  const connected = (typeof window !== "undefined" && window.__viq_user) ? window.__viq_user : null;
  const client = {
    name: answers?.client_name || answers?.user_name || connected?.name || "",
    siret_name: answers?.client_company || connected?.siret_name || "",
    email: answers?.client_email || connected?.email || "",
    siret: answers?.client_siret || connected?.siret || "",
    address: answers?.client_address || connected?.address || "",
    phone: answers?.client_phone || connected?.phone || "",
  };

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 1 — COUVERTURE (style facture)
  // ═══════════════════════════════════════════════════════════════════════

  // En-tête : logo doré + RAPPORT D'ESTIMATION + N° + date + ligne dorée
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("VehicleIQ", L, 22);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text("Cote VO professionnelle", L, 27);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text("RAPPORT D'ESTIMATION", W - R, 22, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(`N° ${ref}`, W - R, 28, { align: "right" });
  doc.text(`Émis le ${pdf3DateFr()}`, W - R, 32, { align: "right" });

  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.6);
  doc.line(L, 38, W - R, 38);

  // Bloc émetteur / client
  let y = pdf3PartiesBlock(doc, 48, { client });

  // Titre véhicule en grand (centré)
  const vehicleTitle = (v.make + (v.model ? " " + v.model : "")).trim();
  doc.setFont("helvetica", "bold");
  let titleSize = 24;
  while (titleSize > 14) {
    doc.setFontSize(titleSize);
    if (doc.getTextWidth(vehicleTitle) <= CW - 6) break;
    titleSize -= 1;
  }
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(vehicleTitle, W / 2, y + 4, { align: "center" });

  // Sous-titre (caractéristiques principales)
  const subBits = [v.year];
  if (v.km) subBits.push(pdfNum(Number(v.km)) + " km");
  if (v.fuel) subBits.push(v.fuel);
  if (v.gearbox) subBits.push(v.gearbox);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(subBits.filter(Boolean).join("  ·  "), W / 2, y + 11, { align: "center" });

  y += 22;

  // Carte valeur centrale (style "Total TTC" de la facture, plus grande)
  const boxW = 150, boxH = 48;
  const boxX = (W - boxW) / 2;
  doc.setFillColor(...PDF3.COL.ACCENT_LT);
  doc.setDrawColor(...PDF3.COL.ACCENT);
  doc.setLineWidth(0.8);
  doc.roundedRect(boxX, y, boxW, boxH, 3, 3, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...PDF3.COL.ACCENT);
  doc.text("VALEUR ESTIMÉE VIQ.AI® ENGINE", W / 2, y + 9, { align: "center" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(32);
  doc.setTextColor(...PDF3.COL.TEXT);
  doc.text(pdfNum(Number(prixViq)) + " €", W / 2, y + 28, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(
    `Fourchette ${pdfNum(Number(argusLow))} – ${pdfNum(Number(argusHigh))} €`,
    W / 2, y + 38, { align: "center" }
  );

  y += boxH + 12;

  // Indicateurs rapides (3 KPIs)
  const kpis = [];
  if (result.score_vente != null) kpis.push({ label: "Score vente", value: `${result.score_vente}/100`, sub: result.score_vente >= 70 ? "Forte appétence marché" : "Marché plus tendu", highlight: result.score_vente >= 70 });
  if (result.reliability_score != null) kpis.push({ label: "Score fiabilité VIQ.AI®", value: `${result.reliability_score}/100`, sub: "État mécanique/administratif — distinct du score vente", highlight: result.reliability_score >= 70 });
  if (result.market_trend) {
    const trendLbl = result.market_trend === "hausse" ? "↗ En hausse" : result.market_trend === "baisse" ? "↘ En baisse" : "→ Stable";
    kpis.push({ label: "Tendance marché", value: trendLbl, sub: "30 derniers jours" });
  }
  if (result.delai_vente_jours || result.delai_vente) {
    kpis.push({ label: "Délai de vente", value: `${result.delai_vente_jours || result.delai_vente} j`, sub: "Cote médiane" });
  }
  if (kpis.length > 0) {
    y = pdf3Kpis(doc, y, kpis);
  }

  // Pied de couverture (légal)
  const footY = H - 22;
  doc.setDrawColor(...PDF3.COL.BORDER);
  doc.setLineWidth(0.2);
  doc.line(L, footY, W - R, footY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...PDF3.COL.SUB);
  doc.text(
    `${VEHICLEIQ_LEGAL.legal_name} · SIRET ${VEHICLEIQ_LEGAL.siret} · ${VEHICLEIQ_LEGAL.address_line2} · ${VEHICLEIQ_LEGAL.email}`,
    W / 2, footY + 4, { align: "center" }
  );
  doc.setFontSize(6);
  doc.setTextColor(...PDF3.COL.MUTED);
  doc.text(
    "Document à valeur informative — Conditions Générales de Vente disponibles sur vehicleiq.fr (rubrique Légal).",
    W / 2, footY + 8, { align: "center" }
  );

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 2 — Fiche véhicule + Fourchette + Plateformes
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  pdfHeader(doc, "Rapport de Cote");
  y = 30;

  // ─── Section 1 : Fiche véhicule (tableau clair) ───
  y = pdf3SectionTitle(doc, y, "Fiche véhicule");

  const vehicleRows = [
    { critere: "Marque",         valeur: v.make },
    { critere: "Modèle",         valeur: v.model || "—" },
    { critere: "Année",          valeur: String(v.year) },
    { critere: "Kilométrage",    valeur: v.km ? pdfNum(Number(v.km)) + " km" : "—" },
    { critere: "Motorisation",   valeur: v.fuel || "—" },
    { critere: "Boîte",          valeur: v.gearbox || "—" },
    { critere: "Couleur",        valeur: v.color || "—" },
    { critere: "État général",   valeur: v.state || "—" },
    { critere: "Contrôle technique", valeur: v.ct || "—" },
    { critere: "Propriétaires",  valeur: v.owners || "—" },
  ];
  y = pdf3Table(doc, y, [
    { key: "critere", label: "Critère",  w: 60, bold: false },
    { key: "valeur",  label: "Valeur",   w: 120, bold: true },
  ], vehicleRows);

  // ─── Section 2 : Fourchette d'estimation (3 KPIs) ───
  y += 4;
  y = pdf3SectionTitle(doc, y, "Fourchette de Cote");
  y = pdf3Kpis(doc, y, [
    { label: "Plancher",      value: pdfNum(Number(argusLow)) + " €", sub: "Vente rapide" },
    { label: "Recommandé",    value: pdfNum(Number(prixViq)) + " €", sub: "Valeur marché alignée", highlight: true },
    { label: "Haut de gamme", value: pdfNum(Number(argusHigh)) + " €", sub: "1er propriétaire premium" },
  ]);

  // Note informative
  y = pdf3Note(doc, y,
    "La fourchette plancher–haut représente l'amplitude observée pour des véhicules comparables sur les principales places de marché françaises. La valeur recommandée correspond à un positionnement médian alignant temps de vente et marge de négociation.",
  );

  // ─── Section 3 : Plateformes recommandées ───
  if (result.plateformes && result.plateformes.length > 0) {
    // Saut de page si nécessaire
    if (y > H - 80) {
      pdfFooter(doc, 2, proQrDataUrl);
      doc.addPage();
      pdfHeader(doc, "Rapport de Cote");
      y = 30;
    }
    y = pdf3SectionTitle(doc, y, "Plateformes recommandées");

    const platsRows = result.plateformes.slice(0, 8).map((p, i) => ({
      rang:  String(i + 1),
      nom:   p.name || "—",
      prix:  pdfNum(Number(p.prix_indicatif || prixViq)) + " €",
      score: p.score_vente ? `${p.score_vente} %` : "—",
      type:  (p.type || "").slice(0, 40),
    }));
    y = pdf3Table(doc, y, [
      { key: "rang",  label: "#",         w: 12,  align: "center" },
      { key: "nom",   label: "Plateforme", w: 50, bold: true },
      { key: "prix",  label: "Prix conseillé", w: 40, align: "right" },
      { key: "score", label: "Score",     w: 25, align: "right" },
      { key: "type",  label: "Audience",  w: 53 },
    ], platsRows);
  }

  pdfFooter(doc, 2, proQrDataUrl);

  // ═══════════════════════════════════════════════════════════════════════
  // PAGE 3 — Points forts + Stratégie + Méthodologie
  // ═══════════════════════════════════════════════════════════════════════
  doc.addPage();
  pdfHeader(doc, "Rapport de Cote");
  y = 30;

  // ─── Section : Fiabilité VIQ.AI® — Points de vigilance ───
  // v13 : le score de fiabilité et le top 5 des points de vigilance doivent
  // être présents partout (écran, historique, export PDF) — jamais seulement
  // à l'écran au moment du calcul.
  const sellVigilance = getVigilanceTop5(result.known_issues);
  if (result.reliability_score != null || sellVigilance.length) {
    y = pdf3SectionTitle(doc, y, "Fiabilité VIQ.AI® — Points de vigilance");
    if (result.reliability_score != null) {
      y = pdf3WrappedText(doc, y, `Score de fiabilité : ${result.reliability_score}/100${result.known_issues?.reliability_label ? ` (${result.known_issues.reliability_label})` : ""} — évalue l'état mécanique et administratif du véhicule, à ne pas confondre avec le score de vente ci-dessus.`, {
        L, CW, size: 9, color: PDF3.COL.SUB, leading: 4.5,
      }) + 3;
    }
    if (sellVigilance.length) {
      sellVigilance.forEach((pt) => {
        doc.setFillColor(224, 138, 106); // rouge/orange d'alerte, distinct des pastilles dorées "points forts"
        doc.circle(L + 2, y - 1, 1.4, "F");
        y = pdf3WrappedText(doc, y, pt, {
          L: L + 7, CW: CW - 7, size: 9.5, color: PDF3.COL.TEXT, leading: 4.5,
        }) + 3;
      });
    }
    y += 4;
  }

  // ─── Section : Inspection visuelle IA (v27) ───
  const sellVi = result.visual_inspection;
  if (sellVi && sellVi.zones) {
    if (y > H - 90) { pdfFooter(doc, 3, proQrDataUrl); doc.addPage(); pdfHeader(doc, "Rapport de Cote"); y = 30; }
    y = pdf3SectionTitle(doc, y, "Inspection visuelle IA (photos du véhicule)");

    const ZONE_LABELS = { avant: "Avant", toit: "Toit", flanc_gauche: "Flanc gauche", flanc_droit: "Flanc droit", arriere: "Arrière", jantes_pneus: "Jantes / pneus", interieur: "Intérieur" };
    const ZONE_COLORS = { ok: [46, 111, 92], attention: [217, 119, 6], probleme: [184, 73, 58], non_visible: [180, 176, 165] };
    const ZONE_TEXT = { ok: "OK", attention: "Vigilance", probleme: "Problème", non_visible: "Non visible" };

    let sellPngUrl = null;
    try { sellPngUrl = await svgToPngDataUrl(sellVi.synthesis_svg, 320, 520); } catch (e) {}

    const imgW = 34, imgH = 55;
    const textX = sellPngUrl ? L + imgW + 8 : L;

    if (sellPngUrl) {
      try { doc.addImage(sellPngUrl, "PNG", L, y, imgW, imgH); } catch (e) {}
    }

    let zy = y + 2;
    Object.keys(ZONE_LABELS).forEach((z) => {
      const status = sellVi.zones[z] || "non_visible";
      const [r2, g2, b2] = ZONE_COLORS[status];
      doc.setFillColor(r2, g2, b2);
      doc.circle(textX + 2, zy - 1.3, 1.3, "F");
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...PDF3.COL.TEXT);
      doc.text(`${ZONE_LABELS[z]} : ${ZONE_TEXT[status]}`, textX + 6, zy);
      zy += 5.2;
    });
    y = Math.max(y + imgH, zy) + 4;

    if (sellVi.synthese) {
      y = pdf3WrappedText(doc, y, `« ${sellVi.synthese} »`, { L, CW, size: 9, color: PDF3.COL.SUB, leading: 4.5, style: "italic" }) + 3;
    }
    if (typeof sellVi.score_delta === "number" && sellVi.score_delta !== 0) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(9);
      doc.setTextColor(...(sellVi.score_delta < 0 ? PDF3.COL.ACCENT : PDF3.COL.GREEN));
      doc.text(`Cote et score ajustés selon l'état visuel réel : ${sellVi.score_delta > 0 ? "+" : ""}${sellVi.score_delta} points`, L, y);
      y += 6;
    }
    if (Array.isArray(sellVi.vigilance_points) && sellVi.vigilance_points.length) {
      sellVi.vigilance_points.forEach((pt) => {
        doc.setFillColor(224, 138, 106);
        doc.circle(L + 2, y - 1, 1.4, "F");
        y = pdf3WrappedText(doc, y, pt, { L: L + 7, CW: CW - 7, size: 9, color: PDF3.COL.TEXT, leading: 4.5 }) + 3;
      });
    }
    y += 3;
  }

  // ─── Section : Points forts ───
  if (result.points_forts && result.points_forts.length) {
    y = pdf3SectionTitle(doc, y, "Points forts du véhicule");
    result.points_forts.forEach((pt) => {
      // Pastille dorée
      doc.setFillColor(...PDF3.COL.ACCENT);
      doc.circle(L + 2, y - 1, 1.4, "F");
      // Texte wrap
      y = pdf3WrappedText(doc, y, pt, {
        L: L + 7, CW: CW - 7, size: 9.5, color: PDF3.COL.TEXT, leading: 4.5,
      }) + 3;
    });
    y += 4;
  }

  // ─── Section : Stratégie de mise en vente ───
  if (result.conseils_vente && result.conseils_vente.length) {
    if (y > H - 60) {
      pdfFooter(doc, 3, proQrDataUrl);
      doc.addPage();
      pdfHeader(doc, "Rapport de Cote");
      y = 30;
    }
    y = pdf3SectionTitle(doc, y, "Stratégie de mise en vente");
    result.conseils_vente.forEach((conseil, i) => {
      // Pastille numérotée dorée claire
      doc.setFillColor(...PDF3.COL.ACCENT_LT);
      doc.setDrawColor(...PDF3.COL.ACCENT);
      doc.setLineWidth(0.4);
      doc.circle(L + 3, y - 1, 3, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...PDF3.COL.ACCENT);
      doc.text(String(i + 1), L + 3, y + 0.5, { align: "center" });
      // Conseil wrap
      y = pdf3WrappedText(doc, y, conseil, {
        L: L + 9, CW: CW - 9, size: 9, color: PDF3.COL.TEXT, leading: 4.5,
      }) + 6;
    });
  }

  // ─── Annonce auto-générée ───
  if (result.annonce_titre && y < 220) {
    y += 4;
    y = pdf3SectionTitle(doc, y, "Annonce auto-générée");
    // Bloc encadré beige clair
    doc.setFillColor(...PDF3.COL.ACCENT_LT);
    doc.setDrawColor(...PDF3.COL.ACCENT);
    doc.setLineWidth(0.4);
    const titleLines = doc.splitTextToSize(result.annonce_titre, CW - 8);
    const textLines = doc.splitTextToSize(result.annonce_texte || "", CW - 8).slice(0, 10);
    const boxH = 6 + titleLines.length * 5 + textLines.length * 4 + 6;
    doc.roundedRect(L, y, CW, boxH, 2, 2, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(...PDF3.COL.TEXT);
    titleLines.forEach((line, i) => doc.text(line, L + 4, y + 7 + i * 5));
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...PDF3.COL.SUB);
    textLines.forEach((line, i) => doc.text(line, L + 4, y + 7 + titleLines.length * 5 + 3 + i * 4));
    y += boxH + 6;
  }

  // ─── Bloc méthodologie ───
  if (y < 240) {
    y = pdf3Note(doc,
      y,
      "Cote calculée par fusion 70/30 : 70 % données de marché temps réel (annonces actives sur les principales places de marché françaises) + 30 % algorithme VIQ.AI® Engine v5 (catalogue constructeur, dépréciation segmentée, coefficient saisonnier, ajustement régional, ZFE, watchlist mécanique, momentum 30j/90j, calibration Bayésienne sur ventes réelles). Variance attendue ±2 à 8 % selon positionnement plateforme.",
      { color: PDF3.COL.ACCENT, bgColor: PDF3.COL.ACCENT_LT }
    );
  }

  // ─── Note légale en bas de page ───
  const noteY = H - 30;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(...PDF3.COL.MUTED);
  const legalLines = doc.splitTextToSize(
    "Cette cote est indicative et ne constitue ni un engagement de rachat, ni une cote opposable. Le prix de transaction final dépend de l'état effectif du véhicule, du marché local et de la négociation entre vendeur et acheteur.",
    CW
  );
  legalLines.forEach((line, i) => doc.text(line, L, noteY + i * 3.5));

  pdfFooter(doc, 3, proQrDataUrl);

  // Sauvegarde
  const safeName = vehicleTitle.replace(/[^\w\d-]/g, "_").slice(0, 40);
  if (opts && opts.asDataUri) return doc.output("datauristring");
  doc.save(`VehicleIQ_Estimation_${safeName}_${Date.now()}.pdf`);
}

// ─── Save Result Helper — localStorage + backend sync ────────────────────────

// Nettoie toutes les données locales liées à un compte utilisateur
function clearUserLocalStorage() {
  const keys = [
    "viq_history", "viq_billing", "viq_profile",
    "viq_session", "viq_free_buy_used", "viq_free_sell_used",
    "viq_org_cache"
  ];
  keys.forEach(k => localStorage.removeItem(k));
}

async function saveToHistory(type, answers, results = null) {
  try {
    const existing = JSON.parse(localStorage.getItem("viq_history") || "[]");
    // Dédupliquer — ne pas sauvegarder si même type dans les 8 dernières secondes
    const last = existing[0];
    if (last && last.type === type && (Date.now() - new Date(last.date).getTime()) < 8000) return true;
    const entry = { id: Date.now(), date: new Date().toISOString(), type, answers, results };
    localStorage.setItem("viq_history", JSON.stringify([entry, ...existing].slice(0, 50)));
    if (window.__viq_uid) {
      apiPost("/api/history/save", { type, input: answers }).catch(() => {});
    }
    return true;
  } catch(e) { return false; }
}

function SaveBanner({ onSave, saved }) {
  return (
    <div style={{ background: saved ? "rgba(0,232,122,.06)" : "rgba(61,158,255,.06)", border: `1px solid ${saved ? "rgba(0,232,122,.2)" : "rgba(61,158,255,.2)"}`, borderRadius: 6, padding: ".8rem 1.2rem", display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "1rem", gap: "1rem", flexWrap: "wrap" }}>
      <div style={{ fontSize: ".82rem", color: "var(--sub)" }}>
        {saved ? <span style={{ color: "var(--green)" }}>✓ Résultat sauvegardé</span> : "💾 Sauvegarder ce résultat dans votre historique ?"}
      </div>
      {!saved && <button className="btn btn-ghost btn-sm" onClick={onSave}>Sauvegarder</button>}
    </div>
  );
}


// ─── CertifiedBadge VIQ.AI® — exportable SVG ────────────────────────────────
function CertifiedBadgeSVG({ make, model, year, prix, score }) {
  const date = new Date().toLocaleDateString("fr-FR");
  const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="340" height="100" viewBox="0 0 340 100">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="#111111"/>
      <stop offset="1" stop-color="#1A1A2E"/>
    </linearGradient>
  </defs>
  <rect width="340" height="100" rx="8" fill="url(#bg)"/>
  <rect width="4" height="100" rx="2" fill="var(--accent)"/>
  <text x="18" y="22" font-family="Arial Black" font-size="10" fill="var(--accent)" letter-spacing="1">VEHICLE</text>
  <text x="76" y="22" font-family="Arial Black" font-size="10" fill="var(--accent)">IQ</text>
  <text x="103" y="22" font-family="Arial" font-size="8" fill="#666666">® Engine</text>
  <text x="18" y="42" font-family="Arial Black" font-size="13" fill="#FFFFFF">${make} ${model} ${year}</text>
  <text x="18" y="60" font-family="Arial Black" font-size="18" fill="#00C864">${prix ? pdfNum(prix)+" €" : "—"}</text>
  <text x="18" y="75" font-family="Arial" font-size="8" fill="#888888">Valeur marchande estimée • VIQ.AI® Engine</text>
  <text x="18" y="90" font-family="Arial" font-size="7" fill="#555555">vehicleiq.fr • ${date} • INPI déposé</text>
  ${score ? `<rect x="285" y="12" width="44" height="44" rx="22" fill="none" stroke="${score>=80?"#00C864":score>=60?"#FFB800":"var(--accent)"}" stroke-width="3"/>
  <text x="307" y="32" font-family="Arial Black" font-size="13" fill="${score>=80?"#00C864":score>=60?"#FFB800":"var(--accent)"}" text-anchor="middle">${score}</text>
  <text x="307" y="47" font-family="Arial" font-size="7" fill="#666" text-anchor="middle">/100</text>` : ""}
</svg>`;
  const download = () => {
    const blob = new Blob([svgContent], {type:"image/svg+xml"});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `VehicleIQ_Badge_${make}_${year}.svg`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div style={{ marginTop:".8rem" }}>
      <div dangerouslySetInnerHTML={{ __html: svgContent }}
        style={{ width:"100%", maxWidth:340, borderRadius:8, overflow:"hidden" }} />
      <button className="btn btn-ghost" style={{ width:"100%", marginTop:".4rem", fontSize:".75rem" }} onClick={download}>
        ⬇ Télécharger le badge VIQ.AI® (SVG)
      </button>
    </div>
  );
}

const calcTAM = (argLow, argMid) => {
  if (!argLow && !argMid) return null;
  const mid = argMid || argLow;
  const algo = argLow || mid;
  return Math.round(((mid * 0.70) + (algo * 0.30)) * 0.95 / 100) * 100;
};

// ─── OrgAgentsPanel — Phase 2 Multi-agent : gestion des collaborateurs ────
// Visible dans "Mon espace" uniquement pour les owner/admin d'une organisation.
function OrgAgentsPanel({ user, organization, members, invitations, onReload }) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("agent");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  // État éphémère pour l'animation "Copié !" — clef = identifiant unique de la copie
  const [copiedKey, setCopiedKey] = useState(null);
  const flashCopied = (key) => {
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(prev => prev === key ? null : prev), 1800);
  };

  const myRole = organization?.my_role || "agent";
  const canManage = myRole === "owner";
  const isOwner = myRole === "owner";

  const handleInvite = async () => {
    setInviteError(""); setInviteSuccess(null);
    const cleanEmail = String(inviteEmail || "").trim().toLowerCase();
    if (!cleanEmail.includes("@")) {
      setInviteError("Email invalide");
      return;
    }
    setInviteLoading(true);
    try {
      const r = await apiPost("/api/org/invite", { email: cleanEmail, role: inviteRole });
      if (r?.success && r.inviteUrl) {
        setInviteSuccess({ url: r.inviteUrl, email: cleanEmail });
        setInviteEmail("");
        if (onReload) onReload();
      } else {
        setInviteError(r?.message || r?.error || "Erreur lors de l'invitation");
      }
    } catch (err) {
      const msg = err?.message || "";
      if (msg.includes("MAX_AGENTS_REACHED")) {
        setInviteError("Limite atteinte : " + organization.max_agents + " agents maximum sur votre plan. Contactez-nous pour augmenter la limite.");
      } else if (msg.includes("409") || msg.toLowerCase().includes("déjà")) {
        setInviteError("Une invitation est déjà en attente pour cet email.");
      } else {
        setInviteError(msg || "Erreur réseau. Réessayez.");
      }
    }
    setInviteLoading(false);
  };

  const handleRevoke = async (invitationId) => {
    try {
      await apiPost("/api/org/invitations/" + invitationId + "/revoke", {});
      if (onReload) onReload();
    } catch (e) { /* silent */ }
  };

  const handleChangeRole = async (memberUid, newRole) => {
    try {
      await apiPost("/api/org/members/" + memberUid + "/role", { role: newRole });
      if (onReload) onReload();
    } catch (e) { /* silent */ }
  };

  const handleRemove = async (memberUid) => {
    try {
      await apiDelete("/api/org/members/" + memberUid);
      setConfirmRemove(null);
      if (onReload) onReload();
    } catch (e) { /* silent */ }
  };

  const copyToClipboard = async (text, key) => {
    if (!text || String(text).includes("undefined")) {
      console.warn("[copy] URL invalide :", text);
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback pour navigateurs anciens ou contextes non sécurisés
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      flashCopied(key || text);
    } catch(e) { console.warn("[copy] failed:", e); }
  };

  const roleLabel = (r) => r === "owner" ? "Propriétaire" : "Agent";
  const roleColor = (r) => r === "owner" ? "var(--accent)" : "var(--sub)";

  // Comptage aligné avec le backend : membres non-owner + invitations pending
  const currentAgentsCount = members.filter(m => m.role !== "owner").length;
  const pendingInvitesCount = invitations.length;
  const totalSlotsUsed = currentAgentsCount + pendingInvitesCount;
  const maxAgents = organization.max_agents;
  const isUnlimited = maxAgents === -1;
  const limitReached = !isUnlimited && totalSlotsUsed >= maxAgents;

  return (
    <div style={{ padding: "0.5rem 0" }}>
      {/* En-tête organisation */}
      <div style={{
        background: "linear-gradient(135deg, rgba(168,128,58,.04), rgba(168,128,58,0))",
        border: "1px solid rgba(168,128,58,.18)",
        borderRadius: 10, padding: "1.1rem 1.3rem", marginBottom: "1.2rem"
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: ".65rem", fontFamily: "var(--font-mono)", letterSpacing: "1.5px", color: "var(--accent)", fontWeight: 700, marginBottom: ".25rem" }}>ORGANISATION</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.2rem", fontWeight: 700, color: "var(--text)", lineHeight: 1.2 }}>
              {organization.company_name || "Entreprise"}
            </div>
            <div style={{ fontSize: ".72rem", color: "var(--sub)", marginTop: ".25rem" }}>
              SIRET {organization.siret}  ·  Plan {organization.plan_tier === "volume_entreprise" ? "Pro Multi-Agent" : organization.plan_tier}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: ".65rem", color: "var(--muted)", fontFamily: "var(--font-mono)", letterSpacing: "1px" }}>AGENTS</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.4rem", fontWeight: 700, color: "var(--text)" }}>
              {totalSlotsUsed} / {isUnlimited ? "∞" : maxAgents}
            </div>
            {pendingInvitesCount > 0 && (
              <div style={{ fontSize: ".62rem", color: "var(--muted)" }}>
                {currentAgentsCount} actif{currentAgentsCount > 1 ? "s" : ""} + {pendingInvitesCount} en attente
              </div>
            )}
            <div style={{ fontSize: ".64rem", color: limitReached ? "#9B2C2C" : "var(--muted)" }}>
              Votre rôle : <strong style={{ color: roleColor(myRole) }}>{roleLabel(myRole)}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Formulaire d'invitation (visible uniquement pour owner/admin) */}
      {canManage && (
        <div style={{
          background: "var(--card)", border: "1.5px solid var(--border)",
          borderRadius: 8, padding: "1rem 1.2rem", marginBottom: "1.2rem"
        }}>
          <div style={{ fontSize: ".82rem", fontWeight: 700, color: "var(--text)", marginBottom: ".6rem" }}>
            Inviter un collaborateur
          </div>
          <div style={{ fontSize: ".72rem", color: "var(--sub)", marginBottom: ".8rem", lineHeight: 1.5 }}>
            Saisissez l'email de votre collaborateur. Un lien d'invitation sera généré : transmettez-le-lui par email ou messagerie. L'invitation expire au bout de 14 jours.
          </div>
          {limitReached ? (
            <div style={{
              background: "rgba(185,28,28,.06)", border: "1px solid rgba(185,28,28,.18)",
              padding: ".7rem .9rem", borderRadius: 6, color: "#9B2C2C", fontSize: ".75rem"
            }}>
              ⚠ Limite atteinte : {maxAgents} places ({currentAgentsCount} actif{currentAgentsCount > 1 ? "s" : ""} + {pendingInvitesCount} invitation{pendingInvitesCount > 1 ? "s" : ""} en attente). Pour ajouter un agent, soit révoquer une invitation pending, soit contacter le support pour augmenter votre plan.
            </div>
          ) : (
            <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
              <input type="email" value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !inviteLoading && handleInvite()}
                placeholder="email@collaborateur.fr"
                style={{
                  flex: 1, minWidth: 220, padding: ".55rem .8rem",
                  border: "1.5px solid var(--border)", borderRadius: 6,
                  fontSize: ".88rem", fontFamily: "var(--font-body)"
                }} />
              {/* Le rôle est désormais toujours "agent". Plus de sélecteur — seul
                  le propriétaire administre l'organisation. */}
              <button onClick={handleInvite} disabled={inviteLoading}
                className="btn btn-accent"
                style={{ fontSize: ".82rem", padding: ".55rem 1.1rem" }}>
                {inviteLoading ? "Envoi…" : "Inviter →"}
              </button>
            </div>
          )}
          {inviteError && (
            <div style={{
              marginTop: ".7rem", fontSize: ".74rem", color: "#9B2C2C",
              background: "rgba(185,28,28,.05)", padding: ".55rem .75rem",
              borderRadius: 5, lineHeight: 1.45
            }}>{inviteError}</div>
          )}
          {inviteSuccess && (
            <div style={{
              marginTop: ".7rem", padding: ".8rem 1rem",
              background: "rgba(4,120,87,.05)", border: "1px solid rgba(4,120,87,.2)",
              borderRadius: 6
            }}>
              <div style={{ fontSize: ".78rem", color: "var(--green)", fontWeight: 600, marginBottom: ".4rem" }}>
                ✓ Invitation créée pour <strong>{inviteSuccess.email}</strong>
              </div>
              <div style={{ fontSize: ".7rem", color: "var(--sub)", marginBottom: ".5rem" }}>
                Transmettez ce lien à votre collaborateur par email ou messagerie :
              </div>
              <div style={{
                display: "flex", gap: ".4rem", alignItems: "center",
                background: "var(--white)", padding: ".4rem .5rem",
                borderRadius: 4, border: "1.5px solid var(--border)",
                fontSize: ".7rem", fontFamily: "var(--font-mono)"
              }}>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {inviteSuccess.url}
                </span>
                <button onClick={() => copyToClipboard(inviteSuccess.url, "success-" + inviteSuccess.email)}
                  style={{
                    padding: ".25rem .55rem", fontSize: ".68rem",
                    border: "1px solid " + (copiedKey === "success-" + inviteSuccess.email ? "var(--green)" : "var(--accent)"),
                    background: copiedKey === "success-" + inviteSuccess.email ? "var(--green)" : "var(--white)",
                    color: copiedKey === "success-" + inviteSuccess.email ? "#fff" : "var(--accent)",
                    borderRadius: 4, cursor: "pointer",
                    fontFamily: "var(--font-body)", fontWeight: 600,
                    minWidth: 64, transition: "all .15s ease"
                  }}>{copiedKey === "success-" + inviteSuccess.email ? "✓ Copié !" : "Copier"}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Liste des membres */}
      <div style={{ marginBottom: "1.2rem" }}>
        <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", marginBottom: ".5rem", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)" }}>
          Membres actifs ({members.length})
        </div>
        <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
          {members.length === 0 ? (
            <div style={{ padding: "1rem", color: "var(--muted)", fontSize: ".82rem", textAlign: "center" }}>
              Aucun membre
            </div>
          ) : members.map((m, i) => (
            <div key={m.id}
              style={{
                display: "flex", alignItems: "center", padding: ".7rem 1rem",
                borderBottom: i < members.length - 1 ? "1px solid var(--border)" : "none",
                gap: ".7rem", flexWrap: "wrap"
              }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: ".84rem", fontWeight: 600, color: "var(--text)" }}>
                  {m.name || m.email || m.user_uid.slice(0, 8)}
                  {m.user_uid === user?.uid && (
                    <span style={{ marginLeft: ".4rem", fontSize: ".62rem", color: "var(--muted)" }}>(vous)</span>
                  )}
                </div>
                <div style={{ fontSize: ".68rem", color: "var(--sub)" }}>{m.email}</div>
              </div>
              <div style={{
                fontSize: ".68rem", fontWeight: 700, padding: ".15rem .55rem",
                background: m.role === "owner" ? "rgba(168,128,58,.10)"
                          : "rgba(107,114,128,.10)",
                color: roleColor(m.role), borderRadius: 4,
                textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)"
              }}>{roleLabel(m.role)}</div>
              {isOwner && m.role !== "owner" && (
                <div style={{ display: "flex", gap: ".35rem" }}>
                  {/* Plus de bouton de promotion/rétrogradation : tous les membres
                      non-owner sont des agents, point. Seule la suppression reste. */}
                  {confirmRemove === m.user_uid ? (
                    <>
                      <button onClick={() => handleRemove(m.user_uid)}
                        style={{
                          padding: ".25rem .55rem", fontSize: ".68rem",
                          border: "1px solid #9B2C2C", background: "#9B2C2C",
                          color: "var(--white)", borderRadius: 4, cursor: "pointer",
                          fontFamily: "var(--font-body)", fontWeight: 600
                        }}>Confirmer</button>
                      <button onClick={() => setConfirmRemove(null)}
                        style={{
                          padding: ".25rem .55rem", fontSize: ".68rem",
                          border: "1.5px solid var(--border)", background: "var(--white)",
                          color: "var(--sub)", borderRadius: 4, cursor: "pointer",
                          fontFamily: "var(--font-body)"
                        }}>Annuler</button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmRemove(m.user_uid)}
                      title="Retirer ce membre"
                      style={{
                        padding: ".25rem .55rem", fontSize: ".68rem",
                        border: "1.5px solid var(--border)", background: "var(--white)",
                        color: "#9B2C2C", borderRadius: 4, cursor: "pointer",
                        fontFamily: "var(--font-body)"
                      }}>✕</button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Invitations en attente */}
      {canManage && invitations.length > 0 && (
        <div>
          <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", marginBottom: ".5rem", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "var(--font-mono)" }}>
            Invitations en attente ({invitations.length})
          </div>
          <div style={{ background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
            {invitations.map((inv, i) => {
              // Guard : token peut être absent si le backend ne l'expose pas (anciens enregistrements)
              const hasToken = !!inv.token && inv.token !== "undefined";
              const inviteUrl = hasToken
                ? (window.location.origin + "/invitation?token=" + inv.token)
                : null;
              const copyKey = "inv-" + inv.id;
              const isCopied = copiedKey === copyKey;
              return (
                <div key={inv.id}
                  style={{
                    display: "flex", alignItems: "center", padding: ".7rem 1rem",
                    borderBottom: i < invitations.length - 1 ? "1px solid var(--border)" : "none",
                    gap: ".7rem", flexWrap: "wrap"
                  }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: ".82rem", fontWeight: 600, color: "var(--text)" }}>
                      {inv.email}
                    </div>
                    <div style={{ fontSize: ".66rem", color: "var(--sub)" }}>
                      {roleLabel(inv.role)}  ·  Envoyée le {new Date(inv.created_at).toLocaleDateString("fr-FR")}
                      {inv.expires_at && (
                        <span>  ·  Expire le {new Date(inv.expires_at).toLocaleDateString("fr-FR")}</span>
                      )}
                    </div>
                  </div>
                  {hasToken ? (
                    <button onClick={() => copyToClipboard(inviteUrl, copyKey)}
                      style={{
                        padding: ".25rem .55rem", fontSize: ".68rem",
                        border: "1px solid " + (isCopied ? "var(--green)" : "var(--accent)"),
                        background: isCopied ? "var(--green)" : "var(--white)",
                        color: isCopied ? "#fff" : "var(--accent)",
                        borderRadius: 4, cursor: "pointer",
                        fontFamily: "var(--font-body)", fontWeight: 600,
                        minWidth: 110, transition: "all .15s ease"
                      }}>{isCopied ? "✓ Copié !" : "Copier le lien"}</button>
                  ) : (
                    <span title="Token indisponible — l'invitation est trop ancienne ou corrompue. Révoquez-la et créez-en une nouvelle."
                      style={{
                        padding: ".25rem .55rem", fontSize: ".68rem",
                        color: "var(--muted)", fontStyle: "italic"
                      }}>Lien indisponible</span>
                  )}
                  <button onClick={() => handleRevoke(inv.id)}
                    title="Révoquer l'invitation"
                    style={{
                      padding: ".25rem .55rem", fontSize: ".68rem",
                      border: "1.5px solid var(--border)", background: "var(--white)",
                      color: "#9B2C2C", borderRadius: 4, cursor: "pointer",
                      fontFamily: "var(--font-body)"
                    }}>Révoquer</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!canManage && (
        <div style={{
          background: "var(--card)", border: "1px dashed var(--border)",
          borderRadius: 8, padding: "1rem 1.2rem", textAlign: "center",
          fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.5
        }}>
          Vous êtes membre <strong>agent</strong> de cette organisation.<br/>
          La gestion des collaborateurs est réservée au propriétaire du compte.
        </div>
      )}
    </div>
  );
}

function HistoryModal({ onClose, user, onProfileSaved, onRecharge, pricingUsage, onOpenPricing, initialTab }) {
  const [tab, setTab] = useState(initialTab || "requests");
  // Phase 2 multi-agent : état de l'organisation (chargée à l'ouverture si user connecté)
  const [orgData, setOrgData] = useState({ organization: null, members: [], invitations: [], loaded: false });
  const reloadOrg = async () => {
    if (!user?.uid) return;
    try {
      const r = await apiGet("/api/org/my");
      if (!r?.success || !r.organization) {
        setOrgData({ organization: null, members: [], invitations: [], loaded: true });
        return;
      }
      const org = r.organization;
      const [memRes, invRes] = await Promise.all([
        apiGet("/api/org/members").catch(() => null),
        org.my_role === "owner"
          ? apiGet("/api/org/invitations?status=pending").catch(() => null)
          : Promise.resolve({ invitations: [] })
      ]);
      setOrgData({
        organization: org,
        members: memRes?.members || [],
        invitations: invRes?.invitations || [],
        loaded: true
      });
    } catch (e) {
      setOrgData({ organization: null, members: [], invitations: [], loaded: true });
    }
  };
  useEffect(() => {
    if (user?.uid) reloadOrg();
  }, [user?.uid]);
  const [reqLog, setReqLog] = useState(() => { try { return JSON.parse(localStorage.getItem("viq_history") || "[]"); } catch(e) { return []; } });
  const [historyShared, setHistoryShared] = useState(false); // true si historique consolidé de l'org
  const [billing, setBilling] = useState(() => { try { return JSON.parse(localStorage.getItem("viq_billing") || "[]"); } catch(e) { return []; } });
  const [loadingData, setLoadingData] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  // Filtre par agent : seulement utile en vue consolidée d'organisation.
  // "all" = tous les agents · sinon = filtrer sur ce user_uid spécifique.
  const [filterAgent, setFilterAgent] = useState("all");
  // ─── Piste 1 : Suivi de vente réel + feedback utilisateur ───────────────
  const [saleFollowups, setSaleFollowups] = useState({});
  const [feedbacks, setFeedbacks] = useState({});
  const [followupOpen, setFollowupOpen] = useState(null);
  const [followupDraft, setFollowupDraft] = useState({ sold_price: "", sold_date: "", client_type: "", notes: "", platform: "", platform_custom: "", purchase_price: "", pickup_method: "" });
  useEffect(() => {
    if (!user?.uid) return;
    apiGet("/api/sale-followup/mine").then(d => {
      const map = {};
      (d.followups || []).forEach(f => { if (f.estimation_id) map[f.estimation_id] = f; });
      setSaleFollowups(map);
    }).catch(err => console.warn("[sale-followup] chargement échoué:", err.message));
    apiGet("/api/feedback/mine").then(d => {
      const map = {};
      (d.feedbacks || []).forEach(f => {
        if (f.estimation_id && (!map[f.estimation_id] || new Date(f.created_at) > new Date(map[f.estimation_id].created_at))) {
          map[f.estimation_id] = f;
        }
      });
      setFeedbacks(map);
    }).catch(err => console.warn("[feedback/mine] chargement échoué:", err.message));
  }, [user?.uid]);
  const openFollowup = (idx, existing) => {
    setFollowupOpen(idx);
    if (existing) {
      const platformValue = existing.platform || "";
      // L'entry n'est pas accessible ici directement — on stocke en "Autre" par défaut si plateforme renseignée
      // (le dropdown affichera quand même la liste correcte au rendu, et on bascule en "Autre" si pas dans la liste)
      setFollowupDraft({
        sold_price: existing.sold_price || "",
        sold_date: existing.sold_date ? String(existing.sold_date).slice(0, 10) : "",
        client_type: existing.client_type || "",
        notes: existing.notes || "",
        platform: platformValue,
        platform_custom: platformValue,
        purchase_price: existing.purchase_price ? String(existing.purchase_price) : ""
      });
    } else {
      setFollowupDraft({ sold_price: "", sold_date: new Date().toISOString().slice(0, 10), client_type: "", notes: "", platform: "", platform_custom: "", purchase_price: "", pickup_method: "" });
    }
  };
  const saveFollowup = async (entry) => {
    const estimId = entry.estimation_id || entry.id || `est_${entry.date || Date.now()}`;
    if (!followupDraft.sold_price || isNaN(parseInt(followupDraft.sold_price, 10))) {
      alert("Veuillez saisir le prix de vente réel (en €).");
      return;
    }
    try {
      // Plateforme : si "Autre", utilise platform_custom
      const platformValue = followupDraft.platform === "Autre"
        ? (followupDraft.platform_custom || "Autre").trim().slice(0, 80)
        : (followupDraft.platform || "").trim().slice(0, 80) || null;
      const ppStr = String(followupDraft.purchase_price || "").trim();
      const purchasePrice = ppStr && !isNaN(parseInt(ppStr, 10)) && parseInt(ppStr, 10) > 0
        ? parseInt(ppStr, 10) : null;

      const payload = {
        estimation_id: estimId,
        vehicle_data: entry.answers || entry.data || {},
        estimation_data: entry.results || entry.result || {},
        sold_price: parseInt(followupDraft.sold_price, 10),
        sold_date: followupDraft.sold_date || null,
        client_type: followupDraft.client_type || "unknown",
        notes: followupDraft.notes || null,
        platform: platformValue,
        purchase_price: purchasePrice,
        pickup_method: followupDraft.pickup_method || null
      };
      const resp = await apiPost("/api/sale-followup", payload);
      setSaleFollowups(prev => ({ ...prev, [estimId]: { ...payload, id: resp.id } }));
      setFollowupOpen(null);
      alert("✅ Prix de vente enregistré. Merci, cette donnée nous est précieuse !");
    } catch (err) {
      console.error("[sale-followup] save error:", err.status, err.message, err.detail);
      const detailMsg = err.detail && err.detail.pg_code
        ? `\n\nCode technique : ${err.detail.pg_code}`
        : "";
      const userMsg = err.message && !err.message.startsWith("Erreur API:")
        ? err.message
        : "Le serveur n'a pas pu enregistrer ce prix de vente.";
      alert("❌ " + userMsg + detailMsg + "\n\nSi le problème persiste, écrivez à contact@vehicleiq.fr.");
    }
  };

  // Écouter l'event pour switcher vers l'onglet profil depuis l'extérieur
  useEffect(() => {
    const handler = (e) => setTab(e.detail || "profile");
    window.addEventListener("viq-history-tab", handler);
    return () => window.removeEventListener("viq-history-tab", handler);
  }, []);

  // Export XLSX de tout l'historique
  const exportHistoryXLSX = async () => {
    try {
      const XLSX = await import("https://cdn.sheetjs.com/xlsx-0.20.3/package/xlsx.mjs");
      const rows = reqLog.map(e => {
        const d = e.answers || e.data || {};
        const r = e.results || e.result || {};
        return {
          Date: new Date(e.date).toLocaleDateString("fr-FR"),
          Type: e.type === "check" ? "Contrôle" : e.type === "sell" ? "Estimation" : "Achat",
          "Marque/Modèle": d.make || r.make || d.plaque || "—",
          Année: d.year_sell || d.year || r.year || "—",
          Kilométrage: d.km_sell || d.km || "—",
          Motorisation: d.fuel_sell || d.fuel || r.fuel || "—",
          "Prix estimé (TTC)": (r.prix_viq || r.argus_mid || r.argus_low) ? (r.prix_viq || Math.round((r.argus_mid || r.argus_low) * 0.95 / 100) * 100).toLocaleString("fr-FR") + " €" : "—",
          "Score VIQ.AI®": r.score || "—",
          Conseil: r.conseil_prix || "—",
        };
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Historique VehicleIQ");
      XLSX.writeFile(wb, `VehicleIQ_Historique_${new Date().toLocaleDateString("fr-FR").replace(/\//g,"-")}.xlsx`);
    } catch(e) { alert("Erreur export : " + e.message); }
  };

  const shareEntry = (e) => {
    const d = e.answers || e.data || {};
    const r = e.results || e.result || {};
    const date = new Date(e.date).toLocaleDateString("fr-FR");
    const lines = [`🚗 VehicleIQ — ${typeLabel[e.type]}`, `📅 ${date}`, ``];
    if (d.plaque) lines.push(`🔡 Plaque : ${d.plaque}`);
    if (d.make || r.make) lines.push(`🚗 Véhicule : ${r.make||d.make||""} ${r.model||d.model||""} ${r.year||d.year_sell||d.year||""}`.trim());
    if (e.type === "sell") {
      if (d.km_sell) lines.push(`🛣 Km : ${Number(d.km_sell).toLocaleString("fr-FR")} km`);
      if (d.fuel_sell) lines.push(`⛽ Carburant : ${d.fuel_sell}`);
      if (d.gearbox_sell) lines.push(`⚙️ Boîte : ${d.gearbox_sell}`);
      if (d.state) lines.push(`🔧 État : ${d.state.split("—")[0].trim()}`);
      if (d.ct) lines.push(`📋 CT : ${d.ct}`);
      if (d.owners) lines.push(`👤 Propriétaires : ${d.owners}`);
      if (r.argus_low || r.argus_mid || r.prix_viq) {
        const prix = r.prix_viq || Math.round((r.argus_mid || r.argus_low) * 0.95 / 100) * 100;
        lines.push(``, `💰 Valeur marchande estimée (VIQ.AI®) : ${prix.toLocaleString("fr-FR")} € TTC`);
        lines.push(`💶 Prix HT : ${Math.round(prix/1.20/100)*100} €`);
        if (r.conseil_prix) lines.push(`💡 Conseil : ${r.conseil_prix}`);
        if (r.score_vente) lines.push(`📊 Score de vente : ${r.score_vente}%`);
        const tamS = calcTAM(r.argus_low, r.argus_mid);
        if (tamS) lines.push(`📈 Tendances Actuelles Marché : ${tamS.toLocaleString('fr-FR')} € TTC (${Math.round(tamS/1.20/100)*100} € HT)`);
      }
      if (r.plateformes?.length) lines.push(``, `📢 Plateformes recommandées : ${r.plateformes.slice(0,3).map(p=>p.name||p).join(", ")}...`);
    }
    if (e.type === "buy") {
      if (d.budget) lines.push(`💶 Budget max : ${Number(d.budget).toLocaleString("fr-FR")} €`);
      if (d.fuel) lines.push(`⛽ Motorisation : ${d.fuel}`);
      if (d.segment) lines.push(`🚙 Segment : ${d.segment}`);
      if (d.year) lines.push(`📅 Année min : ${d.year}`);
      if (d.km) lines.push(`🛣 Km max : ${Number(d.km).toLocaleString("fr-FR")} km`);
      // 10 résultats
      const models = r.models || r.suggestions || [];
      if (models.length) {
        lines.push(``, `🔟 Recommandations VIQ.AI® :`);
        models.forEach((m, i) => {
          lines.push(`  #${i+1} ${m.make} ${m.model||""} ${m.year||""} — ${m.price ? Number(m.price).toLocaleString("fr-FR")+" €" : "—"} · ${m.km ? Number(m.km).toLocaleString("fr-FR")+" km" : ""} · ${m.fuel||""} · ${m.platform||""} · Score ${m.match||"—"}%`);
        });
      }
    }
    if (e.type === "check") {
      if (r.vin) lines.push(`🔢 VIN : ${r.vin}`);
      if (r.fuel) lines.push(`⛽ Carburant : ${r.fuel}`);
      if (r.color) lines.push(`🎨 Couleur : ${r.color}`);
      if (r.power_cv) lines.push(`⚡ Puissance : ${r.power_cv}`);
      if (r.gearbox) lines.push(`⚙️ Boîte : ${r.gearbox}`);
      if (r.date_immat) lines.push(`📅 1ère mise en circulation : ${r.date_immat}`);
      if (r.score != null) lines.push(``, `✦ Score VIQ.AI® : ${r.score}/100`);
    }
    lines.push(``, `🔗 vehicleiq.fr`);
    const text = lines.join("\n");
    if (navigator.share) {
      navigator.share({ title: "VehicleIQ", text }).catch(() => {
        navigator.clipboard.writeText(text).then(() => alert("✅ Copié dans le presse-papiers !")).catch(() => {});
      });
    } else {
      navigator.clipboard.writeText(text).then(() => alert("✅ Copié dans le presse-papiers !")).catch(() => {});
    }
  };

  const downloadEntry = (e) => {
    const d = e.answers || e.data || {};
    const r = e.results || e.result || {};
    const lines = [
      "VehicleIQ — Rapport",
      `Date : ${new Date(e.date).toLocaleDateString("fr-FR")}`,
      `Type : ${e.type === "check" ? "Contrôle" : e.type === "sell" ? "Estimation" : "Achat"}`,
      `Véhicule : ${d.make || r.make || d.plaque || "—"} ${d.year_sell || r.year || ""}`,
      `Kilométrage : ${d.km_sell || d.km || "—"}`,
      `Motorisation : ${d.fuel_sell || d.fuel || r.fuel || "—"}`,
      (r.prix_viq || r.argus_mid || r.argus_low) ? `Prix estimé (VIQ.AI®) : ${(r.prix_viq || Math.round((r.argus_mid || r.argus_low) * 0.95 / 100) * 100).toLocaleString("fr-FR")} € TTC` : "",
      (() => { const tam = calcTAM(r.argus_low, r.argus_mid); return tam ? `Tendances Actuelles Marché : ${tam} € TTC (${Math.round(tam/1.20/100)*100} € HT)` : ""; })(),
      r.score ? `Score VIQ.AI® : ${r.score}/100` : "",
      r.conseil_prix ? `Conseil : ${r.conseil_prix}` : "",
      "", "vehicleiq.fr — VIQ.AI® Engine — INPI déposé",
    ].filter(Boolean).join("\n");
    const blob = new Blob([lines], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `VehicleIQ_${e.type}_${new Date(e.date).toLocaleDateString("fr-FR").replace(/\//g,"-")}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!user?.uid) return;
    // Show local data instantly (no loading delay)
    setLoadingData(false);
    // Then fetch from server in background
    apiGet("/api/users/me").then(res => {
      if (!res?.success) return;
      // Merge server history with local (server is source of truth)
      if (res.searches?.length) {
        const remote = res.searches.map(s => {
          const input  = typeof s.input  === "object" ? s.input  : (JSON.parse(s.input  || "{}") || {});
          const result = typeof s.result === "object" ? s.result : (JSON.parse(s.result || "{}") || {});
          if (s.type === "buy" && result.suggestions?.length && !result.models) {
            result.models = result.suggestions;
          }
          return {
            id: s.id, date: s.created_at, type: s.type,
            answers: input, results: result, data: input,
            // Métadonnées org (partagé) — pour affichage de l'auteur
            user_uid: s.user_uid, user_name: s.user_name, user_email: s.user_email, user_role: s.user_role,
          };
        });
        setHistoryShared(!!res.historyShared);
        // Merge: server est source de vérité, déduplication par ID ET par empreinte type+date
        const remoteIds = new Set(remote.map(r => String(r.id)));
        // Créer une empreinte pour chaque entrée distante (type + date arrondie à la minute)
        const remoteFingerprints = new Set(remote.map(r => {
          const d = new Date(r.date); d.setSeconds(0, 0);
          return r.type + "_" + d.toISOString();
        }));
        const localOnly = reqLog.filter(h => {
          if (remoteIds.has(String(h.id))) return false;
          const d = new Date(h.date); d.setSeconds(0, 0);
          const fp = h.type + "_" + d.toISOString();
          return !remoteFingerprints.has(fp);
        });
        const merged = [...remote, ...localOnly].sort((a,b) => new Date(b.date) - new Date(a.date));
        setReqLog(merged);
        localStorage.setItem("viq_history", JSON.stringify(merged));
      }
      if (res.payments?.length) {
        const remoteBilling = res.payments.map(p => ({
          date: p.created_at,
          plan: p.plan_id || p.plan || "—",
          price: p.amount_cents ? `${(p.amount_cents / 100).toFixed(2).replace(".", ",")} €` : "—",
          mode: "stripe",
        }));
        // Merge with local wallet purchases (not in backend)
        const localBilling = (() => { try { return JSON.parse(localStorage.getItem("viq_billing") || "[]"); } catch(e) { return []; } })();
        const localWallet = localBilling.filter(b => b.mode === "wallet");
        const merged = [...remoteBilling, ...localWallet].sort((a,b) => new Date(b.date) - new Date(a.date));
        setBilling(merged);
        localStorage.setItem("viq_billing", JSON.stringify(merged));
      }
      if (res.user) {
        const p = { phone: res.user.phone||"", address: res.user.address||"", siret: res.user.siret||"", siretName: res.user.siret_name||"", isPro: res.user.is_pro||false };
        localStorage.setItem("viq_profile", JSON.stringify(p));
      }
    }).catch(() => {}); // silently fail — local data still shown
  }, [user?.uid]);
  const typeLabel = { check: "🔍 Contrôle VIN", buy: "🛒 Recherche achat", sell: "💰 Cote" };
  const typeColor = { check: "var(--blue)", buy: "var(--green)", sell: "var(--accent2)" };

  // Profile tab state — load from localStorage (populated from backend above)
  const savedProfile = (() => { try { return JSON.parse(localStorage.getItem("viq_profile") || "{}"); } catch(e) { return {}; } })();
  const [phone, setPhone] = useState(savedProfile.phone || "");
  const [address, setAddress] = useState(savedProfile.address || "");
  // Autocomplete adresse via api-adresse.data.gouv.fr
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  // Quand des suggestions arrivent et que le champ est rempli, on les montre
  useEffect(() => { if (addressSuggestions.length > 0) setShowAddressSuggestions(true); }, [addressSuggestions]);
  const [profileSiret, setProfileSiret] = useState(savedProfile.siret || "");
  const [profileSiretName, setProfileSiretName] = useState(savedProfile.siretName || "");
  const [profileIsPro, setProfileIsPro] = useState(savedProfile.isPro || false);
  const [profileSearching, setProfileSearching] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState("");

  const lookupProfileSiret = async (val) => {
    setProfileSiret(val.replace(/\s/g,""));
    setProfileSiretName(""); setProfileError("");
    if (val.replace(/\s/g,"").length < 9) return;
    setProfileSearching(true);
    try {
      const res = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${val.replace(/\s/g,"")}&page=1&per_page=1`);
      const data = await res.json();
      const r = data.results?.[0];
      if (r) setProfileSiretName(r.nom_complet || r.nom_raison_sociale || "");
      else setProfileError("Entreprise non trouvée");
    } catch(e) { setProfileError("Erreur de recherche"); }
    setProfileSearching(false);
  };

  const saveProfile = async () => {
    // SUJET 3 : pré-check SIRET avant de sauver (UX) — si SIRET utilisé par un autre compte, bloquer
    setProfileError("");
    if (user?.uid && profileIsPro && profileSiret) {
      try {
        const check = await apiGet("/api/users/check-siret?siret=" + encodeURIComponent(profileSiret));
        if (check && check.available === false && check.reason === "already_used") {
          setProfileError(
            "Ce SIRET est déjà associé à un autre compte VehicleIQ (compte " + (check.hint || "existant") + "). " +
            "Si vous êtes collaborateur de cette entreprise, demandez à votre administrateur de vous inviter en tant qu'agent " +
            "depuis son espace. La fonctionnalité multi-agent est incluse dans l'offre Pro Multi-Agent."
          );
          return;
        }
      } catch(e) { /* silencieux : on laisse le save tenter */ }
    }
    const profile = { phone, address, siret: profileSiret, siretName: profileSiretName, isPro: profileIsPro };
    localStorage.setItem("viq_profile", JSON.stringify(profile));
    // Sync to backend if connected
    if (user?.uid && profileIsPro && profileSiret) {
      try {
        await apiPost("/api/users/pro", { is_pro: profileIsPro, siret: profileSiret, siret_name: profileSiretName });
      } catch(err) {
        // Erreur 409 SIRET_ALREADY_USED (au cas où le pré-check a été contourné, ex: race condition)
        const msg = err?.message || "";
        if (msg.includes("SIRET_ALREADY_USED") || msg.includes("409")) {
          setProfileError(
            "Ce SIRET est déjà associé à un autre compte VehicleIQ. " +
            "Si vous êtes collaborateur, demandez à votre administrateur de vous inviter (fonctionnalité Pro Multi-Agent)."
          );
          return;
        }
        setProfileError("Erreur de synchronisation. Réessayez dans quelques instants.");
        return;
      }
    }
    setProfileSaved(true);
    if (onProfileSaved) onProfileSaved(profile);
    setTimeout(() => setProfileSaved(false), 2000);
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel panel-wide panel-fullscreen flex-panel">
        <div className="panel-header" style={{ flexShrink: 0 }}>
          <div>
            <div className="panel-title">Mon espace</div>
            <div className="panel-sub">{user ? (user.email || user.name) : "Historique local"}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
            <ThemeToggleButton size={32} />
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        </div>
        {/* Barre d'onglets fixée — hors de la zone scrollable (comme le dashboard Super Admin) */}
        <div className="me-tabs-bar" style={{
          display: "flex", borderBottom: "1.5px solid var(--border)", flexShrink: 0,
          gap: ".2rem",
          background: "var(--bg)",
          padding: "0 1rem",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
        }}>
            {[
              ["requests", `📋 Requêtes (${reqLog.length})`],
              ["billing", `🧾 Facturation (${billing.length})`],
              ["payment", "💳 Paiements & Abonnements"],
              ...(orgData.organization ? [["agents", `👥 Mes agents (${orgData.members.length})`]] : []),
              ["profile", "👤 Mon profil"]
            ].map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)} style={{
                padding: ".75rem 1.1rem",
                background: tab === t ? "rgba(168,128,58,.08)" : "transparent",
                border: "none", cursor: "pointer",
                fontSize: ".84rem", fontWeight: tab === t ? 600 : 500,
                color: tab === t ? "var(--accent)" : "var(--sub)",
                borderBottom: tab === t ? "2.5px solid var(--accent)" : "2.5px solid transparent",
                marginBottom: -1.5, fontFamily: "var(--font-body)",
                whiteSpace: "nowrap",
                transition: "all .15s cubic-bezier(.4,0,.2,1)",
                borderRadius: "8px 8px 0 0"
              }}>{label}</button>
            ))}
          </div>

        <div className="panel-body">
          {tab === "requests" && (loadingData
            ? <div style={{ textAlign: "center", padding: "2rem", color: "var(--sub)" }}><div className="spinner" style={{ margin: "0 auto 1rem" }} />Chargement depuis le serveur…</div>
            : <div>
                {/* Bannière "Historique consolidé de l'org" — visible pour owner/admin */}
                {historyShared && orgData.organization && (
                  <div style={{
                    background: "linear-gradient(135deg, rgba(168,128,58,.06), rgba(168,128,58,0))",
                    border: "1px solid rgba(168,128,58,.2)",
                    borderRadius: 8, padding: ".7rem 1rem", marginBottom: "1rem",
                    fontSize: ".78rem", color: "var(--text)", display: "flex",
                    alignItems: "center", gap: ".6rem", flexWrap: "wrap"
                  }}>
                    <span style={{ fontSize: "1.1rem" }}>👥</span>
                    <span>
                      <strong>Vue consolidée de l'organisation</strong> · {orgData.organization.company_name || orgData.organization.siret}
                      <br/>
                      <span style={{ fontSize: ".7rem", color: "var(--sub)" }}>
                        Vous voyez ici l'activité de tous vos collaborateurs (recherches signées avec leur nom).
                      </span>
                    </span>
                  </div>
                )}
                {/* Barre recherche + filtres + export */}
                <div style={{ display:"flex", gap:".5rem", marginBottom:"1rem", flexWrap:"wrap" }}>
                  <input placeholder="🔍 Rechercher…" value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}
                    style={{ flex:1, minWidth:120, background:"var(--card)", border:"1.5px solid var(--border)", borderRadius:6, padding:".4rem .8rem", color:"var(--text)", fontSize:".82rem", outline:"none", fontFamily:"var(--font-body)" }} />
                  {["all","check","sell","buy"].map(f=>(
                    <button key={f} onClick={()=>setFilterType(f)}
                      style={{ padding:".35rem .7rem", borderRadius:6, border:"1.5px solid var(--border)", background:filterType===f?"var(--accent)":"var(--card)", color:filterType===f?"#fff":"var(--sub)", fontSize:".72rem", cursor:"pointer", fontFamily:"var(--font-body)" }}>
                      {f==="all"?"Tous":f==="check"?"🔍 Contrôle":f==="sell"?"💰 Cote":"🛒 Achat"}
                    </button>
                  ))}
                  {/* Filtre par agent — visible seulement en vue consolidée d'org */}
                  {historyShared && orgData.members && orgData.members.length > 1 && (
                    <select value={filterAgent} onChange={e=>setFilterAgent(e.target.value)}
                      title="Filtrer par agent"
                      style={{ padding:".35rem .7rem", borderRadius:6, border:"1.5px solid var(--border)", background:filterAgent!=="all"?"rgba(168,128,58,.08)":"var(--card)", color:filterAgent!=="all"?"var(--accent)":"var(--sub)", fontSize:".72rem", cursor:"pointer", fontFamily:"var(--font-body)", fontWeight:600 }}>
                      <option value="all">👥 Tous les agents</option>
                      {orgData.members.map(m => (
                        <option key={m.user_uid} value={m.user_uid}>
                          {m.role === "owner" ? "👑 " : "👤 "}{m.name || m.email || m.user_uid.slice(0, 8)}
                          {m.user_uid === user?.uid ? " (vous)" : ""}
                        </option>
                      ))}
                    </select>
                  )}
                  <button onClick={()=>setSortOrder(o=>o==="desc"?"asc":"desc")}
                    style={{ padding:".35rem .7rem", borderRadius:6, border:"1.5px solid var(--border)", background:"var(--card)", color:"var(--sub)", fontSize:".72rem", cursor:"pointer", fontFamily:"var(--font-body)" }}>
                    {sortOrder==="desc"?"↓ Récent":"↑ Ancien"}
                  </button>
                  {(user?.siret && user.siret.length > 3) || user?.is_pro ? (
                    <button onClick={exportHistoryXLSX}
                      style={{ padding:".35rem .7rem", borderRadius:6, border:"1px solid rgba(0,200,100,.3)", background:"rgba(0,200,100,.06)", color:"var(--green)", fontSize:".72rem", cursor:"pointer", fontFamily:"var(--font-body)", fontWeight:600 }}>
                      ⬇ XLSX
                    </button>
                  ) : (
                    <div style={{ position:"relative", display:"inline-block" }}>
                      <button style={{ padding:".35rem .7rem", borderRadius:6, border:"1px solid rgba(0,200,100,.3)", background:"rgba(0,200,100,.06)", color:"var(--green)", fontSize:".72rem", fontFamily:"var(--font-body)", fontWeight:600, filter:"blur(3px)", pointerEvents:"none" }}>⬇ XLSX</button>
                      <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}
                        onClick={() => window.dispatchEvent(new CustomEvent("viq-open-panel", { detail:"profile" }))}>
                        <span style={{ fontSize:".6rem", color:"var(--accent)", background:"var(--deep)", padding:"1px 5px", borderRadius:3 }}>🔒 Pro</span>
                      </div>
                    </div>
                  )}
                </div>
                {(() => {
                  const filtered = reqLog
                    .filter(e => filterType==="all" || e.type===filterType)
                    .filter(e => filterAgent==="all" || e.user_uid===filterAgent)
                    .filter(e => {
                      if (!searchTerm) return true;
                      const s = searchTerm.toLowerCase();
                      const d = e.answers||e.data||{};
                      return Object.values(d).some(v=>String(v).toLowerCase().includes(s));
                    })
                    .sort((a,b)=>sortOrder==="desc"?new Date(b.date)-new Date(a.date):new Date(a.date)-new Date(b.date));
                  if (filtered.length===0) return <div style={{textAlign:"center",padding:"2rem",color:"var(--sub)"}}>Aucune requête trouvée</div>;
                  return (
                    <div style={{ display:"flex", flexDirection:"column", gap:".6rem" }}>
                    {filtered.map(e => {
                  const d = e.answers || e.data || {};
                  const r = e.results || e.result || {};
                  return (
                    <div key={e.id} style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".9rem 1.1rem", marginBottom: ".5rem" }}>
                      {/* Badge auteur — visible seulement si historique consolidé de l'org et l'auteur n'est pas l'user courant */}
                      {historyShared && e.user_uid && e.user_uid !== user?.uid && (
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: ".3rem",
                          fontSize: ".62rem", color: "var(--sub)",
                          background: "rgba(0,0,0,.04)", border: "1.5px solid var(--border)",
                          padding: ".15rem .5rem", borderRadius: 4,
                          marginBottom: ".4rem", fontFamily: "var(--font-mono)"
                        }}>
                          <span>👤</span>
                          <span>Par <strong style={{ color: "var(--text)" }}>{e.user_name || e.user_email || "agent"}</strong></span>
                          {e.user_role && e.user_role !== "owner" && (
                            <span style={{ color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px" }}>· {e.user_role}</span>
                          )}
                        </div>
                      )}
                      {/* Header */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".5rem", gap: ".4rem" }}>
                        <div style={{ fontWeight: 700, fontSize: ".88rem", color: typeColor[e.type] }}>{typeLabel[e.type]}</div>
                        <div style={{ display:"flex", alignItems:"center", gap:".5rem" }}>
                          <button
                            onClick={() => exportEntryToPDF(e).catch(err => alert("Erreur export PDF : " + err.message))}
                            title="Exporter ce véhicule en PDF"
                            style={{ background:"rgba(168,128,58,.08)", border:"1px solid rgba(168,128,58,.3)", color:"var(--accent)",
                                     padding:".2rem .5rem", borderRadius:5, fontSize:".68rem", cursor:"pointer",
                                     fontWeight:600, display:"inline-flex", alignItems:"center", gap:".25rem" }}
                            onMouseEnter={ev => { ev.currentTarget.style.background = "rgba(168,128,58,.18)"; }}
                            onMouseLeave={ev => { ev.currentTarget.style.background = "rgba(168,128,58,.08)"; }}>
                            📄 PDF
                          </button>
                          <div style={{ fontSize: ".65rem", color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
                            {new Date(e.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                      </div>

                      {/* Détails buy */}
                      {e.type === "buy" && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: ".25rem .7rem", fontSize: ".73rem", color: "var(--sub)", marginBottom: ".5rem" }}>
                          {d.budget && <span>💶 <strong style={{ color: "var(--text)" }}>{Number(d.budget).toLocaleString("fr-FR")} €</strong></span>}
                          {d.fuel && d.fuel !== "Peu importe" && <span>⛽ {d.fuel}</span>}
                          {d.segment && d.segment !== "Peu importe" && <span>🚗 {d.segment}</span>}
                          {d.brands && <span>🏷 {d.brands}</span>}
                          {d.year && <span>📅 depuis {d.year}</span>}
                          {d.km && <span>🛣 max {Number(d.km).toLocaleString("fr-FR")} km</span>}
                          {d.gearbox && d.gearbox !== "Peu importe" && <span>⚙️ {d.gearbox}</span>}
                          {d.city && <span>📍 {d.city}</span>}
                          {r.count && <span style={{ color: "var(--green)", fontWeight: 600 }}>→ {r.count} résultats</span>}
                        </div>
                      )}

                      {/* Détails sell */}
                      {e.type === "sell" && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: ".25rem .7rem", fontSize: ".73rem", color: "var(--sub)", marginBottom: ".5rem" }}>
                          {d.make && <span>🚗 <strong style={{ color: "var(--text)" }}>{d.make}</strong></span>}
                          {(d.year_sell||d.year) && <span>📅 {d.year_sell||d.year}</span>}
                          {(d.km_sell||d.km) && <span>🛣 {Number(d.km_sell||d.km||0).toLocaleString("fr-FR")} km</span>}
                          {(d.fuel_sell||d.fuel) && <span>⛽ {d.fuel_sell||d.fuel}</span>}
                          {d.gearbox_sell && <span>⚙️ {d.gearbox_sell}</span>}
                          {d.state && <span>🔧 {d.state.split("—")[0]?.trim()}</span>}
                          {d.ct && <span>📋 CT {d.ct}</span>}
                          {/* Prix estimés VIQ.AI® — réservés aux comptes Pro (SIRET) */}
                          {(r.argus_low || r.prix_viq) && (() => {
                            const prixReco = r.prix_viq || Math.round(((r.argus_mid || r.argus_low) || 0) * 0.95 / 100) * 100;
                            return (
                              <div style={{ width:"100%", marginTop:".4rem" }}>
                                <div style={{ padding:".5rem .8rem", background:"rgba(46,111,92,.06)", border:"1px solid rgba(46,111,92,.2)", borderRadius:8, display:"flex", alignItems:"center", gap:".8rem", flexWrap:"wrap", marginBottom:".3rem", position:"relative" }}>
                                  <span style={{ fontSize:".64rem", color:"var(--muted)", whiteSpace:"nowrap", textTransform:"uppercase", letterSpacing:".8px", fontFamily:"var(--font-mono)", fontWeight:700 }}>Cote VIQ.AI®</span>
                                  <span style={{ color:"var(--green)", fontWeight:600, fontSize:".95rem", fontFamily:"var(--font-head)" }}>
                                    {prixReco.toLocaleString("fr-FR")} €
                                  </span>
                                  {(r.argus_low && r.argus_high) && (
                                    <span style={{ fontSize:".7rem", color:"var(--sub)", whiteSpace:"nowrap" }}>
                                      Fourchette : <strong style={{ color:"var(--text)" }}>{Number(r.argus_low).toLocaleString("fr-FR")} – {Number(r.argus_high).toLocaleString("fr-FR")} €</strong>
                                    </span>
                                  )}
                                  {r.score_vente && <span style={{ fontSize:".68rem", color:"var(--sub)", marginLeft:"auto" }}>Score : <strong style={{color:"var(--accent2)"}}>{r.score_vente}%</strong></span>}
                                </div>
                                {r.is_fallback && (
                                  <div style={{ fontSize:".66rem", fontWeight:800, color:"var(--accent)", padding:".25rem .5rem" }}>
                                    ✦ Notre Cote VIQ.AI
                                  </div>
                                )}
                                {r.conseil_prix && (
                                  <div style={{ fontSize:".72rem", color:"var(--sub)", padding:".3rem .5rem", borderTop:"1px solid var(--border)", marginTop:".2rem" }}>
                                    💡 {r.conseil_prix}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Détails check — STRUCTURE DÉTAILLÉE PAR SECTIONS */}
                      {e.type === "check" && (
                        <div style={{ display:"flex", flexDirection:"column", gap:".5rem", marginBottom:".5rem" }}>

                          {/* Bandeau identité haute : plaque/VIN + véhicule */}
                          <div style={{ display:"flex", flexWrap:"wrap", gap:".4rem .7rem", alignItems:"center" }}>
                            {(d.plaque||d.vin||r.vin) && (
                              <span style={{ fontFamily:"var(--font-mono)", background:"rgba(30,136,229,.1)", border:"1px solid rgba(30,136,229,.25)", borderRadius:4, padding:".15rem .55rem", color:"#90CAF9", letterSpacing:"1.5px", fontWeight:700, fontSize:".82rem" }}>
                                {d.plaque || d.vin || r.vin}
                              </span>
                            )}
                            {(r.make || r.model) && (
                              <span style={{ fontSize:".88rem", fontWeight:600, color:"var(--text)" }}>
                                🚗 {r.make||""} {r.model||""}
                                {(r.version && r.version !== "—") && <span style={{ color:"var(--sub)", fontWeight:400, marginLeft:".3rem" }}>· {r.version}</span>}
                              </span>
                            )}
                            {r.year && <span style={{ fontSize:".76rem", color:"var(--sub)" }}>📅 {r.first_registration_date || r.year}</span>}
                          </div>

                          {/* Section 1 — Identité officielle */}
                          <div style={{ background:"var(--deep)", border:"1.5px solid var(--border)", borderRadius:8, padding:".55rem .7rem" }}>
                            <div style={{ fontSize:".62rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:".35rem", fontWeight:600 }}>
                              📋 Identité officielle
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))", gap:".3rem .9rem", fontSize:".74rem" }}>
                              {r.make && <div><span style={{color:"var(--muted)"}}>Marque :</span> <strong style={{color:"var(--text)"}}>{r.make}</strong></div>}
                              {r.model && <div><span style={{color:"var(--muted)"}}>Modèle :</span> <strong style={{color:"var(--text)"}}>{r.model}</strong></div>}
                              {r.version && r.version !== "—" && <div><span style={{color:"var(--muted)"}}>Version :</span> <strong style={{color:"var(--text)"}}>{r.version}</strong></div>}
                              {r.year && <div><span style={{color:"var(--muted)"}}>Année :</span> <strong style={{color:"var(--text)"}}>{r.year}</strong></div>}
                              {r.first_registration_date && <div><span style={{color:"var(--muted)"}}>1ère immat. :</span> <strong style={{color:"var(--text)"}}>{r.first_registration_date}</strong></div>}
                              {r.color && <div><span style={{color:"var(--muted)"}}>Couleur :</span> <strong style={{color:"var(--text)"}}>🎨 {r.color}</strong></div>}
                              {(r.vin && r.vin !== (d.plaque||d.vin)) && (
                                <div style={{ gridColumn:"1 / -1" }}><span style={{color:"var(--muted)"}}>VIN :</span> <strong style={{fontFamily:"var(--font-mono)", color:"var(--text)", fontSize:".7rem", letterSpacing:".5px"}}>{r.vin}</strong></div>
                              )}
                              {r.type_mine && <div style={{ gridColumn:"1 / -1" }}><span style={{color:"var(--muted)"}}>Type Mine (CNIT) :</span> <strong style={{fontFamily:"var(--font-mono)", color:"var(--text)", fontSize:".7rem"}}>{r.type_mine}</strong></div>}
                              {r.genre && <div><span style={{color:"var(--muted)"}}>Genre :</span> <strong style={{color:"var(--text)"}}>{r.genre}</strong></div>}
                              {r.carrosserie && <div><span style={{color:"var(--muted)"}}>Carrosserie :</span> <strong style={{color:"var(--text)"}}>{r.carrosserie}</strong></div>}
                            </div>
                          </div>

                          {/* Section 2 — Caractéristiques techniques */}
                          <div style={{ background:"var(--deep)", border:"1.5px solid var(--border)", borderRadius:8, padding:".55rem .7rem" }}>
                            <div style={{ fontSize:".62rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:".35rem", fontWeight:600 }}>
                              ⚙️ Caractéristiques techniques
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))", gap:".3rem .9rem", fontSize:".74rem" }}>
                              {(r.fuel || r.engine) && <div><span style={{color:"var(--muted)"}}>Carburant :</span> <strong style={{color:"var(--text)"}}>⛽ {r.fuel || r.engine}</strong></div>}
                              {(r.transmission || r.gearbox) && <div><span style={{color:"var(--muted)"}}>Boîte :</span> <strong style={{color:"var(--text)"}}>⚙️ {r.transmission || r.gearbox}</strong></div>}
                              {(r.power_cv || r.power_kw) && <div><span style={{color:"var(--muted)"}}>Puissance :</span> <strong style={{color:"var(--text)"}}>⚡ {r.power_cv ? r.power_cv + " cv" : r.power_kw + " kW"}</strong></div>}
                              {r.power_fiscal && <div><span style={{color:"var(--muted)"}}>Puiss. fiscale :</span> <strong style={{color:"var(--text)"}}>{r.power_fiscal} CV</strong></div>}
                              {r.engine_code && <div><span style={{color:"var(--muted)"}}>Code moteur :</span> <strong style={{fontFamily:"var(--font-mono)", color:"var(--text)", fontSize:".7rem"}}>🔧 {r.engine_code}</strong></div>}
                              {r.cylinders && <div><span style={{color:"var(--muted)"}}>Cylindres :</span> <strong style={{color:"var(--text)"}}>{r.cylinders}</strong></div>}
                              {r.displacement && <div><span style={{color:"var(--muted)"}}>Cylindrée :</span> <strong style={{color:"var(--text)"}}>{r.displacement} cm³</strong></div>}
                              {r.weight && <div><span style={{color:"var(--muted)"}}>Poids :</span> <strong style={{color:"var(--text)"}}>⚖️ {r.weight}</strong></div>}
                              {r.co2 && <div><span style={{color:"var(--muted)"}}>CO₂ :</span> <strong style={{color:"var(--text)"}}>🌿 {r.co2} g/km</strong></div>}
                              {r.crit_air && <div><span style={{color:"var(--muted)"}}>Crit'Air :</span> <strong style={{color:"var(--accent)"}}>🏷️ Crit'Air {r.crit_air}</strong></div>}
                              {r.places && <div><span style={{color:"var(--muted)"}}>Places :</span> <strong style={{color:"var(--text)"}}>👥 {r.places}</strong></div>}
                              {r.doors && <div><span style={{color:"var(--muted)"}}>Portes :</span> <strong style={{color:"var(--text)"}}>🚪 {r.doors}</strong></div>}
                            </div>
                          </div>

                          {/* Section 3 — Historique d'usage */}
                          {((r.mileage || r.km_reported) || (r.owners || r.nb_owners) || r.marketValue || r.lastService || r.ctDate) && (
                            <div style={{ background:"var(--deep)", border:"1.5px solid var(--border)", borderRadius:8, padding:".55rem .7rem" }}>
                              <div style={{ fontSize:".62rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:".35rem", fontWeight:600 }}>
                                🕒 Historique & usage
                              </div>
                              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))", gap:".3rem .9rem", fontSize:".74rem" }}>
                                {(r.mileage || r.km_reported) && <div><span style={{color:"var(--muted)"}}>Kilométrage :</span> <strong style={{color:"var(--text)"}}>🛣 {Number(r.mileage || r.km_reported).toLocaleString("fr-FR")} km</strong></div>}
                                {(r.owners || r.nb_owners) && <div><span style={{color:"var(--muted)"}}>Propriétaires :</span> <strong style={{color:"var(--text)"}}>👤 {r.owners || r.nb_owners}</strong></div>}
                                {r.marketValue && <div><span style={{color:"var(--muted)"}}>Valeur marché :</span> <strong style={{color:"var(--green)"}}>💶 {r.marketValue}</strong></div>}
                                {r.lastService && <div><span style={{color:"var(--muted)"}}>Dern. révision :</span> <strong style={{color:"var(--text)"}}>🔧 {r.lastService}</strong></div>}
                                {r.ctDate && <div><span style={{color:"var(--muted)"}}>CT valide jusqu'à :</span> <strong style={{color:"var(--text)"}}>📋 {r.ctDate}</strong></div>}
                                {r.region && <div><span style={{color:"var(--muted)"}}>Région :</span> <strong style={{color:"var(--text)"}}>📍 {r.region}</strong></div>}
                              </div>
                            </div>
                          )}

                          {/* Section 4 — Historique d'immatriculations (timeline) */}
                          {Array.isArray(r.registrations) && r.registrations.length > 0 && (
                            <div style={{ background:"var(--deep)", border:"1.5px solid var(--border)", borderRadius:8, padding:".55rem .7rem" }}>
                              <div style={{ fontSize:".62rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:".4rem", fontWeight:600 }}>
                                📜 Historique d'immatriculations <span style={{ color:"var(--accent)", fontWeight:700 }}>({r.registrations.length})</span>
                              </div>
                              <div style={{ display:"flex", flexDirection:"column", gap:".25rem", paddingLeft:".5rem", borderLeft:"2px solid var(--accent)" }}>
                                {r.registrations.slice(0, 8).map((reg, i) => (
                                  <div key={i} style={{ fontSize:".72rem", display:"flex", gap:".7rem", padding:".15rem 0" }}>
                                    <span style={{ fontFamily:"var(--font-mono)", color:"var(--muted)", flexShrink:0, minWidth:80, fontWeight:600 }}>{reg.date}</span>
                                    <span style={{ color:"var(--text)" }}>{reg.text}</span>
                                  </div>
                                ))}
                                {r.registrations.length > 8 && (
                                  <div style={{ fontSize:".68rem", color:"var(--muted)", fontStyle:"italic", marginTop:".15rem" }}>
                                    … et {r.registrations.length - 8} autres événements
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Section 5 — Entretien & réparations (timeline) */}
                          {Array.isArray(r.services) && r.services.length > 0 && (
                            <div style={{ background:"var(--deep)", border:"1.5px solid var(--border)", borderRadius:8, padding:".55rem .7rem" }}>
                              <div style={{ fontSize:".62rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:".4rem", fontWeight:600 }}>
                                🔧 Entretien & réparations <span style={{ color:"var(--green)", fontWeight:700 }}>({r.services.length})</span>
                              </div>
                              <div style={{ display:"flex", flexDirection:"column", gap:".25rem", paddingLeft:".5rem", borderLeft:"2px solid var(--green)" }}>
                                {r.services.slice(0, 8).map((srv, i) => (
                                  <div key={i} style={{ fontSize:".72rem", display:"flex", gap:".7rem", padding:".15rem 0" }}>
                                    <span style={{ fontFamily:"var(--font-mono)", color:"var(--muted)", flexShrink:0, minWidth:90, fontWeight:600 }}>{srv.date?.split("–")[0]?.trim() || srv.date}</span>
                                    <span style={{ color:"var(--text)" }}>{srv.text}</span>
                                  </div>
                                ))}
                                {r.services.length > 8 && (
                                  <div style={{ fontSize:".68rem", color:"var(--muted)", fontStyle:"italic", marginTop:".15rem" }}>
                                    … et {r.services.length - 8} interventions supplémentaires
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Section 6 — Sinistres & alertes */}
                          {(r.accidents != null || r.stolen != null || r.gage != null || r.sinistres_declares != null) && (
                            <div style={{ background: (r.stolen || r.gage || (r.accidents && r.accidents > 0) || (r.sinistres_declares && r.sinistres_declares > 0)) ? "rgba(220,60,60,.06)" : "rgba(0,180,90,.06)", border: `1px solid ${(r.stolen || r.gage || (r.accidents && r.accidents > 0) || (r.sinistres_declares && r.sinistres_declares > 0)) ? "rgba(220,60,60,.25)" : "rgba(0,180,90,.25)"}`, borderRadius:8, padding:".55rem .7rem" }}>
                              <div style={{ fontSize:".62rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:".35rem", fontWeight:600 }}>
                                🛡️ Sinistres & alertes
                              </div>
                              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px, 1fr))", gap:".3rem .9rem", fontSize:".74rem" }}>
                                {(r.accidents != null || r.sinistres_declares != null) && (
                                  <div>
                                    <span style={{color:"var(--muted)"}}>Sinistres :</span>{" "}
                                    <strong style={{color: (r.accidents===0 && (!r.sinistres_declares || r.sinistres_declares===0))?"var(--green)":"var(--red)"}}>
                                      {(r.accidents===0 && (!r.sinistres_declares || r.sinistres_declares===0))?"✓ Aucun":((r.sinistres_declares ?? r.accidents) + " déclaré(s)")}
                                    </strong>
                                  </div>
                                )}
                                {r.stolen != null && (
                                  <div>
                                    <span style={{color:"var(--muted)"}}>Statut volé :</span>{" "}
                                    <strong style={{color: !r.stolen?"var(--green)":"var(--red)"}}>{r.stolen?"🚨 ALERTE VOLÉ":"✓ Non signalé"}</strong>
                                  </div>
                                )}
                                {r.gage != null && (
                                  <div>
                                    <span style={{color:"var(--muted)"}}>Gage :</span>{" "}
                                    <strong style={{color: !r.gage?"var(--green)":"var(--accent)"}}>{r.gage?"⚠ Gage détecté":"✓ Aucun gage"}</strong>
                                  </div>
                                )}
                                {(r.ct_valid != null || r.ctOk != null) && (
                                  <div>
                                    <span style={{color:"var(--muted)"}}>CT :</span>{" "}
                                    <strong style={{color: (r.ct_valid||r.ctOk)?"var(--green)":"var(--accent)"}}>{(r.ct_valid||r.ctOk)?"✓ Valide":"⚠ À vérifier"}</strong>
                                  </div>
                                )}
                                {r.mileageOk != null && (
                                  <div>
                                    <span style={{color:"var(--muted)"}}>Kilométrage :</span>{" "}
                                    <strong style={{color: r.mileageOk?"var(--green)":"var(--accent)"}}>{r.mileageOk?"✓ Cohérent":"⚠ Anomalie"}</strong>
                                  </div>
                                )}
                                {r.opposition != null && (
                                  <div>
                                    <span style={{color:"var(--muted)"}}>Opposition :</span>{" "}
                                    <strong style={{color: !r.opposition?"var(--green)":"var(--red)"}}>{r.opposition?"⚠ Opposition active":"✓ Aucune"}</strong>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Section 7 — Score VIQ.AI® (encart final) */}
                          {r.score != null && (
                            <div style={{ background: r.score >= 80 ? "rgba(0,200,100,.06)" : r.score >= 60 ? "rgba(255,184,0,.06)" : "rgba(168,128,58,.06)", border:`1px solid ${r.score>=80?"rgba(0,200,100,.25)":r.score>=60?"rgba(255,184,0,.25)":"rgba(168,128,58,.25)"}`, borderRadius:8, padding:".7rem .9rem", display:"flex", alignItems:"center", gap:"1rem", flexWrap:"wrap" }}>
                              <div style={{ display:"flex", flexDirection:"column", gap:".15rem" }}>
                                <span style={{fontSize:".62rem", color:"var(--muted)", textTransform:"uppercase", letterSpacing:"1.5px", fontWeight:600}}>✦ Score VIQ.AI®</span>
                                <span style={{fontSize:"1.6rem", fontWeight:700, fontFamily:"var(--font-head)", color: r.score>=80?"var(--green)":r.score>=60?"var(--accent2)":"var(--accent)", lineHeight:1}}>{r.score}<span style={{fontSize:".85rem", color:"var(--muted)", fontWeight:500}}>/100</span></span>
                              </div>
                              <div style={{ flex:1, minWidth:200 }}>
                                <div style={{ fontSize:".82rem", fontWeight:600, color: r.score>=80?"var(--green)":r.score>=60?"var(--accent2)":"var(--accent)" }}>
                                  {r.score>=80?"✅ Véhicule sain — Achat recommandé":r.score>=60?"⚠ Points à vérifier — Inspection conseillée":"🔴 Vérifications recommandées — Prudence"}
                                </div>
                                {r.score_comment && (
                                  <div style={{ fontSize:".7rem", color:"var(--sub)", marginTop:".25rem" }}>{r.score_comment}</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Plateformes sell */}
                      {e.type === "sell" && r.plateformes?.length > 0 && (
                        <div style={{ marginTop:".4rem", marginBottom:".4rem" }}>
                          <div style={{ fontSize:".68rem", color:"var(--muted)", marginBottom:".3rem", textTransform:"uppercase", letterSpacing:".4px" }}>
                            Plateformes recommandées <span style={{ textTransform: "none", fontWeight: 400, fontSize: ".64rem", color: "var(--muted)" }}>— score d'adéquation au véhicule</span>
                          </div>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:".3rem" }}>
                            {r.plateformes.slice(0,10).map((p,i)=>(
                              <div key={i} style={{ fontSize:".68rem", background:"var(--deep)", border:"1.5px solid var(--border)", borderRadius:6, padding:".2rem .55rem", display:"flex", gap:".3rem", alignItems:"center" }}>
                                <span style={{ color:"var(--accent2)", fontWeight:700 }}>#{i+1}</span>
                                <span style={{ color:"var(--text)" }}>{p.name||p}</span>
                                {p.score_vente && <span style={{ color:"var(--muted)", fontFamily: "var(--font-mono)", fontSize: ".62rem" }} title="Score d'adéquation : pertinence de cette plateforme pour vendre votre véhicule (cible audience, prix moyen, vitesse de rotation). Ce n'est pas un pourcentage du prix.">⚙ {p.score_vente}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Modèles recommandés buy */}
                      {e.type === "buy" && r.models?.length > 0 && (
                        <div style={{ marginTop:".4rem", marginBottom:".4rem" }}>
                          <div style={{ fontSize:".68rem", color:"var(--muted)", marginBottom:".3rem", textTransform:"uppercase", letterSpacing:".4px" }}>{r.models.length} Modèle{r.models.length > 1 ? "s" : ""} recommandé{r.models.length > 1 ? "s" : ""}</div>
                          <div style={{ display:"flex", flexDirection:"column", gap:".25rem" }}>
                            {r.models.map((m,i)=>(
                              <div key={i} style={{ fontSize:".72rem", background:"var(--deep)", border:"1.5px solid var(--border)", borderRadius:6, padding:".3rem .7rem", display:"flex", gap:".6rem", alignItems:"center", flexWrap:"wrap" }}>
                                <span style={{ color:"var(--accent2)", fontWeight:700, minWidth:16 }}>#{i+1}</span>
                                <span style={{ color:"var(--text)", fontWeight:600 }}>{m.make} {m.model}</span>
                                <span style={{ color:"var(--sub)" }}>{m.year}</span>
                                {m.km && <span style={{ color:"var(--sub)" }}>· {Number(m.km).toLocaleString("fr-FR")} km</span>}
                                {m.fuel && <span style={{ color:"var(--sub)" }}>· {m.fuel}</span>}
                                {m.price && <span style={{ color:"var(--green)", fontWeight:700 }}>{typeof m.price==="number"?m.price.toLocaleString("fr-FR")+" €":m.price}</span>}
                                {m.match && <span style={{ color:"var(--muted)", fontSize:".65rem" }}>{m.match}%</span>}
                                {m.platform && <span style={{ color:"var(--accent2)", fontSize:".65rem" }}>{m.platform}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Boutons actions */}
                      <div style={{ display: "flex", gap: ".4rem", paddingTop: ".5rem", borderTop: "1px solid var(--border)", flexWrap:"wrap" }}>
                        {/* Relancer : ré-ouvre le formulaire correspondant avec les champs
                            pré-remplis depuis les valeurs stockées en DB pour cette estimation */}
                        {(e.type === "sell" || e.type === "buy" || e.type === "check") && (
                          <button className="btn btn-ghost btn-sm"
                            style={{ fontSize: ".7rem", color: "var(--accent)", borderColor: "var(--accent)", fontWeight: 700 }}
                            title="Relancer cette estimation : le formulaire s'ouvre pré-rempli avec les données de ce véhicule"
                            onClick={() => triggerReplay(e, onClose)}>
                            🔄 Relancer
                          </button>
                        )}
                        {(e.type === "buy" || e.type === "sell" || e.type === "check") && (
                          <button className="btn btn-ghost btn-sm" style={{ fontSize: ".7rem" }}
                            onClick={() => {
                              const typeLabel = e.type === "buy" ? "Achat" : e.type === "check" ? "Contrôle véhicule" : "Estimation";
                              const veh = [d.make, d.model, d.year_sell || d.year].filter(Boolean).join(" ").trim();
                              const subject = encodeURIComponent("Demande de rappel VehicleIQ — " + typeLabel);
                              const body = encodeURIComponent(
                                "Bonjour,\n\nJe souhaite être rappelé(e) concernant une requête de mon historique.\n\n" +
                                "=== TYPE ===\n" + typeLabel + "\n\n" +
                                "=== VÉHICULE / REQUÊTE ===\n" +
                                (veh ? veh + "\n" : "") +
                                (d.plaque || d.vin ? "Plaque/VIN : " + (d.plaque || d.vin) + "\n" : "") +
                                (d.km_sell || d.km ? "Kilométrage : " + (d.km_sell || d.km) + "\n" : "") +
                                (d.fuel_sell || d.fuel ? "Motorisation : " + (d.fuel_sell || d.fuel) + "\n" : "") +
                                (d.budget ? "Budget : " + d.budget + "\n" : "") +
                                "\n=== COORDONNÉES ===\n" +
                                "Nom : " + (user?.name || "Non renseigné") + "\n" +
                                "Email : " + (user?.email || "Non renseigné") + "\n" +
                                "UID : " + (user?.uid || "") + "\n\n" +
                                "Cordialement,\nVehicleIQ"
                              );
                              window.location.href = "mailto:bonglorentz@gmail.com?cc=contact@vehicleiq.fr&subject=" + subject + "&body=" + body;
                            }}>
                            📞 Être rappelé
                          </button>
                        )}
                        {/* Télécharger / WhatsApp / Partager — Pro uniquement */}
                        {((user?.siret && user.siret.length > 3) || user?.is_pro) ? (<>
                        {/* Bouton Estimation détaillée → ouvre le Dashboard Pro centré sur ce véhicule */}
                        {e.type === "sell" && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: ".7rem", color: "var(--accent2)", borderColor: "var(--accent2)" }}
                            onClick={() => {
                              const estId = e.id || e.estimation_id || e.uuid;
                              window.dispatchEvent(new CustomEvent("viq-open-prodash", { detail: { coteId: estId, tab: "vehicle" } }));
                              if (onClose) onClose();
                            }}
                            title="Voir l'analyse complète : courbe de dépréciation, évolution marché, comparaison plateformes…">
                            📊 Cote détaillée
                          </button>
                        )}
                        {/* Piste 1 : afficher le prix prévu par l'utilisateur (si feedback existe) */}
                        {e.type === "sell" && (() => {
                          const eId = e.estimation_id || e.id || `est_${e.date}`;
                          const fb = feedbacks[eId];
                          if (!fb || !fb.user_price) return null;
                          return (
                            <div style={{ fontSize: ".68rem", color: "var(--sub)", padding: ".2rem .55rem", background: "rgba(168,128,58,.06)", borderRadius: 4, border: "1px solid rgba(168,128,58,.15)", display: "flex", alignItems: "center" }}>
                              💭 Prix prévu : <strong style={{ color: "var(--accent)", marginLeft: ".25rem" }}>{Number(fb.user_price).toLocaleString("fr-FR")} €</strong>
                              {fb.estimation_data?.prix_ajuste && (
                                <span style={{ marginLeft: ".5rem", fontSize: ".66rem" }}>
                                  · 🎯 <strong style={{ color: "var(--green, var(--green))" }}>{Number(fb.estimation_data.prix_ajuste).toLocaleString("fr-FR")} €</strong>
                                  <span style={{ color: "var(--muted)", marginLeft: ".15rem", fontStyle: "italic" }}>ajustée</span>
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        {/* Piste 1 : bouton suivi de vente réel pour les estimations */}
                        {e.type === "sell" && (() => {
                          const eId = e.estimation_id || e.id || `est_${e.date}`;
                          const existing = saleFollowups[eId];
                          return (
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ fontSize: ".7rem", color: existing ? "var(--green, var(--green))" : "var(--accent)", borderColor: existing ? "var(--green, var(--green))" : "var(--accent)" }}
                              onClick={() => openFollowup(e.id, existing)}
                              title={existing ? `Vendu ${existing.sold_price} € (${existing.client_type || "?"})` : "Saisir le prix de vente réel"}>
                              {existing ? `✓ Vendu ${Number(existing.sold_price).toLocaleString("fr-FR")} €` : "💰 Saisir prix de vente"}
                            </button>
                          );
                        })()}
                        <button className="btn btn-ghost btn-sm" style={{ fontSize: ".7rem" }} onClick={() => {
                          const d2 = e.answers || e.data || {};
                          const r2 = e.results || e.result || {};
                          const date = new Date(e.date).toLocaleDateString("fr-FR");
                          const lines = [`🚗 VehicleIQ — ${typeLabel[e.type]}`, `📅 ${date}`, ``];
                          if (d2.plaque) lines.push(`🔡 Plaque : ${d2.plaque}`);
                          if (d2.make || r2.make) lines.push(`🚗 Véhicule : ${r2.make||d2.make||""} ${r2.model||d2.model||""} ${r2.year||d2.year_sell||d2.year||""}`.trim());
                          if (e.type === "sell") {
                            if (d2.km_sell) lines.push(`🛣 Km : ${Number(d2.km_sell).toLocaleString("fr-FR")} km`);
                            if (d2.fuel_sell) lines.push(`⛽ Carburant : ${d2.fuel_sell}`);
                            if (d2.gearbox_sell) lines.push(`⚙️ Boîte : ${d2.gearbox_sell}`);
                            if (d2.state) lines.push(`🔧 État : ${d2.state.split("—")[0].trim()}`);
                            if (d2.ct) lines.push(`📋 CT : ${d2.ct}`);
                            if (d2.owners) lines.push(`👤 Propriétaires : ${d2.owners}`);
                            if (r2.argus_low || r2.argus_mid || r2.prix_viq) {
                              const prix = r2.prix_viq || Math.round((r2.argus_mid || r2.argus_low) * 0.95 / 100) * 100;
                              lines.push(``, `💰 Valeur marchande estimée (VIQ.AI®) : ${prix.toLocaleString("fr-FR")} € TTC`);
                              lines.push(`💶 Prix HT : ${Math.round(prix/1.20/100)*100} €`);
                              if (r2.conseil_prix) lines.push(`💡 Conseil : ${r2.conseil_prix}`);
                              if (r2.score_vente) lines.push(`📊 Score de vente : ${r2.score_vente}%`);
                              const tamWA = calcTAM(r2.argus_low, r2.argus_mid);
                              if (tamWA) lines.push(`📈 Tendances Actuelles Marché : ${tamWA.toLocaleString("fr-FR")} € TTC (${Math.round(tamWA/1.20/100)*100} € HT)`);
                            }
                          }
                          if (e.type === "buy") {
                            if (d2.budget) lines.push(`💶 Budget max : ${Number(d2.budget).toLocaleString("fr-FR")} €`);
                            if (d2.fuel) lines.push(`⛽ Motorisation : ${d2.fuel}`);
                            if (d2.segment) lines.push(`🚙 Segment : ${d2.segment}`);
                            if (d2.year) lines.push(`📅 Année min : ${d2.year}`);
                            if (d2.km) lines.push(`🛣 Km max : ${Number(d2.km).toLocaleString("fr-FR")} km`);
                            const models2 = r2.models || r2.suggestions || [];
                            if (models2.length) {
                              lines.push(``, `🔟 Recommandations VIQ.AI® :`);
                              models2.forEach((m,i) => {
                                lines.push(`  #${i+1} ${m.make} ${m.model||""} ${m.year||""} — ${m.price ? Number(m.price).toLocaleString("fr-FR")+" €" : "—"} · ${m.km ? Number(m.km).toLocaleString("fr-FR")+" km" : ""} · ${m.fuel||""} (${m.match||"—"}%)`);
                              });
                            }
                          }
                          if (e.type === "check") {
                            if (r2.vin) lines.push(`🔢 VIN : ${r2.vin}`);
                            if (r2.fuel) lines.push(`⛽ Carburant : ${r2.fuel}`);
                            if (r2.color) lines.push(`🎨 Couleur : ${r2.color}`);
                            if (r2.power_cv) lines.push(`⚡ Puissance : ${r2.power_cv}`);
                            if (r2.score != null) lines.push(`✦ Score VIQ.AI® : ${r2.score}/100`);
                          }
                          lines.push(``, `🔗 Généré par vehicleiq.fr`);
                          window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
                        }}>
                          📲 WhatsApp
                        </button>
                        </>) : null}
                      </div>
                      {/* Piste 1 : Formulaire inline de suivi de vente réel */}
                      {followupOpen === e.id && (
                        <div style={{ marginTop: ".7rem", padding: ".9rem 1rem", background: "var(--card, #FAFAF7)", border: "1.5px solid var(--border)", borderRadius: 8 }}>
                          <div style={{ fontSize: ".82rem", fontWeight: 700, marginBottom: ".5rem", color: "var(--text)" }}>
                            💰 Suivi de vente réel
                          </div>
                          <div style={{ fontSize: ".7rem", color: "var(--sub)", marginBottom: ".7rem", lineHeight: 1.5 }}>
                            Cette information nous aide à affiner les cotes et reste strictement confidentielle.
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem", marginBottom: ".5rem" }}>
                            <div>
                              <label style={{ fontSize: ".68rem", color: "var(--sub)", display: "block", marginBottom: ".2rem" }}>Prix de vente réel (€) *</label>
                              <input
                                type="number"
                                placeholder="ex : 12 500"
                                value={followupDraft.sold_price}
                                onChange={ev => setFollowupDraft(d => ({ ...d, sold_price: ev.target.value }))}
                                style={{ width: "100%", padding: ".4rem .55rem", border: "1.5px solid var(--border)", borderRadius: 5, fontSize: ".8rem", background: "var(--white)" }}
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: ".68rem", color: "var(--sub)", display: "block", marginBottom: ".2rem" }}>Date de vente</label>
                              <input
                                type="date"
                                value={followupDraft.sold_date}
                                onChange={ev => setFollowupDraft(d => ({ ...d, sold_date: ev.target.value }))}
                                style={{ width: "100%", padding: ".4rem .55rem", border: "1.5px solid var(--border)", borderRadius: 5, fontSize: ".8rem", background: "var(--white)" }}
                              />
                            </div>
                          </div>
                          <div style={{ marginBottom: ".5rem" }}>
                            <label style={{ fontSize: ".68rem", color: "var(--sub)", display: "block", marginBottom: ".2rem" }}>Type de client</label>
                            <div style={{ display: "flex", gap: ".4rem" }}>
                              {[["pro", "Pro"], ["particulier", "Particulier"]].map(([val, lab]) => (
                                <button
                                  key={val}
                                  type="button"
                                  onClick={() => setFollowupDraft(d => ({ ...d, client_type: val }))}
                                  className={`btn btn-ghost btn-sm`}
                                  style={{
                                    flex: 1, fontSize: ".75rem",
                                    background: followupDraft.client_type === val ? "var(--accent)" : "transparent",
                                    color: followupDraft.client_type === val ? "#fff" : "var(--text)",
                                    borderColor: followupDraft.client_type === val ? "var(--accent)" : "var(--border)"
                                  }}>
                                  {lab}
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Plateforme de vente : dropdown des 10 plateformes recommandées par VIQ.AI */}
                          <div style={{ marginBottom: ".5rem" }}>
                            <label style={{ fontSize: ".68rem", color: "var(--sub)", display: "block", marginBottom: ".2rem" }}>
                              Plateforme de vente
                            </label>
                            {(() => {
                              const recommended = ((e?.results?.plateformes) || (e?.result?.plateformes) || []).slice(0, 10);
                              return (
                                <select
                                  value={followupDraft.platform}
                                  onChange={ev => setFollowupDraft(d => ({ ...d, platform: ev.target.value, platform_custom: ev.target.value === "Autre" ? d.platform_custom : "" }))}
                                  style={{ width: "100%", padding: ".4rem .55rem", border: "1.5px solid var(--border)", borderRadius: 5, fontSize: ".8rem", background: "var(--white)", color: followupDraft.platform ? "var(--text)" : "var(--muted)" }}>
                                  <option value="">— Sélectionner —</option>
                                  {recommended.map((p, i) => {
                                    const name = (typeof p === "string") ? p : (p?.name || p?.platform || "");
                                    if (!name) return null;
                                    return <option key={i} value={name}>{name}</option>;
                                  })}
                                  <option value="Autre">Autre…</option>
                                </select>
                              );
                            })()}
                            {followupDraft.platform === "Autre" && (
                              <input type="text" placeholder="Nom de la plateforme"
                                value={followupDraft.platform_custom}
                                onChange={ev => setFollowupDraft(d => ({ ...d, platform_custom: ev.target.value }))}
                                maxLength={80}
                                style={{ width: "100%", marginTop: ".3rem", padding: ".35rem .55rem", border: "1.5px solid var(--border)", borderRadius: 5, fontSize: ".78rem", background: "var(--white)" }} />
                            )}
                          </div>
                          {/* ── Méthode de retrait du véhicule (aligné avec le dashboard Pro) ── */}
                          <div style={{ marginBottom: ".5rem" }}>
                            <label style={{ fontSize: ".68rem", color: "var(--sub)", display: "block", marginBottom: ".2rem" }}>
                              Méthode de retrait du véhicule
                            </label>
                            <div style={{ display: "flex", gap: ".4rem" }}>
                              {[
                                ["concession", "🏢 En concession", "L'acheteur vient récupérer le véhicule sur place"],
                                ["expedition", "🚚 Expédition / Livraison", "Le véhicule est livré chez l'acheteur"],
                              ].map(([val, lab, tooltip]) => (
                                <button key={val} type="button" title={tooltip}
                                  onClick={() => setFollowupDraft(d => ({ ...d, pickup_method: val }))}
                                  className="btn btn-ghost btn-sm"
                                  style={{
                                    flex: 1, fontSize: ".75rem",
                                    background: followupDraft.pickup_method === val ? "var(--accent)" : "transparent",
                                    color: followupDraft.pickup_method === val ? "#fff" : "var(--text)",
                                    borderColor: followupDraft.pickup_method === val ? "var(--accent)" : "var(--border)"
                                  }}>
                                  {lab}
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Prix d'achat + Marge calculée */}
                          <div style={{ marginBottom: ".5rem" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem" }}>
                              <div>
                                <label style={{ fontSize: ".68rem", color: "var(--sub)", display: "block", marginBottom: ".2rem" }}>
                                  Prix d'achat (€) <span style={{ color: "var(--muted)", fontWeight: 400 }}>· facultatif</span>
                                </label>
                                <input type="number" placeholder="ex : 10 000"
                                  value={followupDraft.purchase_price}
                                  onChange={ev => setFollowupDraft(d => ({ ...d, purchase_price: ev.target.value }))}
                                  style={{ width: "100%", padding: ".4rem .55rem", border: "1.5px solid var(--border)", borderRadius: 5, fontSize: ".8rem", background: "var(--white)" }} />
                              </div>
                              <div>
                                <label style={{ fontSize: ".68rem", color: "var(--sub)", display: "block", marginBottom: ".2rem" }}>
                                  Marge brute (€) <span style={{ color: "var(--muted)", fontWeight: 400 }}>· calculée</span>
                                </label>
                                {(() => {
                                  const sp = parseInt(followupDraft.sold_price, 10);
                                  const pp = parseInt(followupDraft.purchase_price, 10);
                                  const validSp = !isNaN(sp) && sp > 0;
                                  const validPp = !isNaN(pp) && pp > 0;
                                  if (!validSp || !validPp) {
                                    return (
                                      <div style={{ width: "100%", padding: ".4rem .55rem", border: "1.5px solid var(--border)", borderRadius: 5, fontSize: ".8rem", background: "var(--card)", color: "var(--muted)", fontStyle: "italic" }}>—</div>
                                    );
                                  }
                                  const margin = sp - pp;
                                  const marginPct = pp > 0 ? Math.round((margin / pp) * 100) : 0;
                                  const isPositive = margin >= 0;
                                  return (
                                    <div style={{
                                      width: "100%", padding: ".4rem .55rem",
                                      border: "1px solid " + (isPositive ? "var(--green)" : "#9B2C2C"),
                                      borderRadius: 5, fontSize: ".8rem",
                                      background: isPositive ? "rgba(4,120,87,.06)" : "rgba(185,28,28,.06)",
                                      color: isPositive ? "var(--green)" : "#9B2C2C", fontWeight: 700,
                                      display: "flex", justifyContent: "space-between", alignItems: "center"
                                    }}>
                                      <span>{isPositive ? "+" : ""}{margin.toLocaleString("fr-FR")} €</span>
                                      <span style={{ fontSize: ".66rem", fontWeight: 400 }}>{isPositive ? "+" : ""}{marginPct} %</span>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                          <div style={{ marginBottom: ".7rem" }}>
                            <label style={{ fontSize: ".68rem", color: "var(--sub)", display: "block", marginBottom: ".2rem" }}>Notes (facultatif)</label>
                            <input
                                type="text"
                                placeholder="ex : Reprise sous garantie, négocié de 200 € à la baisse..."
                                value={followupDraft.notes}
                                onChange={ev => setFollowupDraft(d => ({ ...d, notes: ev.target.value }))}
                                style={{ width: "100%", padding: ".4rem .55rem", border: "1.5px solid var(--border)", borderRadius: 5, fontSize: ".78rem", background: "var(--white)" }}
                              />
                          </div>
                          <div style={{ display: "flex", gap: ".5rem" }}>
                            <button className="btn btn-accent btn-sm" style={{ flex: 2, fontSize: ".78rem" }} onClick={() => saveFollowup(e)}>
                              💾 Enregistrer
                            </button>
                            <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: ".78rem" }} onClick={() => setFollowupOpen(null)}>
                              Annuler
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
                  );
                })()}
                <button onClick={() => { localStorage.removeItem("viq_history"); onClose(); }} style={{ background: "none", border: "1.5px solid var(--border)", color: "var(--sub)", padding: ".4rem .8rem", borderRadius: 4, cursor: "pointer", fontSize: ".75rem", fontFamily: "var(--font-body)", marginTop:".5rem" }}>Effacer l'historique</button>
              </div>
          )}

          {tab === "billing" && (loadingData
            ? <div style={{ textAlign: "center", padding: "2rem", color: "var(--sub)" }}><div className="spinner" style={{ margin: "0 auto .6rem" }} />Chargement…</div>
            : billing.length === 0
            ? <div style={{ textAlign: "center", padding: "3rem", color: "var(--sub)" }}>Aucun paiement enregistré</div>
            : <div style={{ display: "flex", flexDirection: "column", gap: ".6rem" }}>
                {pricingUsage?.is_subscriber && (
                  <div style={{ background: "rgba(46,111,92,.08)", border: "1.5px solid rgba(46,111,92,.25)", borderRadius: 8, padding: ".8rem 1rem", fontSize: ".82rem", color: "var(--text)", lineHeight: 1.5 }}>
                    <strong style={{ color: "var(--green)" }}>✓ Abonnement actif</strong> — vos estimations sont incluses dans votre forfait. Vous n'êtes facturé que de votre <strong>abonnement mensuel</strong> ci-dessous, jamais par service.
                  </div>
                )}
                {billing.map((b, i) => (
                  <div key={i} style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 6, padding: ".9rem 1.2rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".8rem", flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontWeight: 600, fontSize: ".87rem" }}>{b.plan}</div>
                      <div style={{ fontSize: ".75rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>{new Date(b.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: ".8rem" }}>
                      <div style={{ fontFamily: "var(--font-head)", fontSize: "1.2rem", color: "var(--green)" }}>{b.price}</div>
                      <button
                        onClick={async () => {
                          try {
                            await generateInvoicePDF(b, {
                              email: user?.email,
                              name: user?.name || user?.displayName,
                              siret: profileSiret,
                              siret_name: profileSiretName,
                              address,
                              phone,
                            });
                          } catch (err) {
                            alert("Téléchargement échoué : " + (err?.message || "erreur inconnue"));
                          }
                        }}
                        title="Télécharger la facture au format PDF"
                        style={{
                          background: "transparent",
                          border: "1.5px solid rgba(168,128,58,.4)",
                          color: "var(--accent)",
                          fontSize: ".72rem", fontWeight: 600,
                          padding: ".4rem .7rem", borderRadius: 6,
                          cursor: "pointer", fontFamily: "var(--font-body)",
                          display: "inline-flex", alignItems: "center", gap: ".3rem",
                          transition: "all .15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(168,128,58,.08)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                        ⬇ PDF
                      </button>
                    </div>
                  </div>
                ))}
                {/* Note bas de liste : rappel CGV */}
                <div style={{ fontSize: ".7rem", color: "var(--muted)", padding: ".6rem .9rem", marginTop: ".4rem", lineHeight: 1.5, fontStyle: "italic" }}>
                  Les factures contiennent les coordonnées du vendeur, du client et un extrait des Conditions Générales de Vente.
                  Pour les CGV complètes, consultez le bas de page du site.
                </div>
              </div>
          )}

          {tab === "payment" && <PaymentSettingsTab user={user} />}

          {tab === "agents" && orgData.organization && (
            <OrgAgentsPanel
              user={user}
              organization={orgData.organization}
              members={orgData.members}
              invitations={orgData.invitations}
              onReload={reloadOrg}
            />
          )}

          {tab === "profile" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {user && profileIsPro && profileSiret && !orgData.organization && orgData.loaded && (
                <div style={{
                  background: "linear-gradient(135deg, rgba(31,90,230,.05), rgba(31,90,230,.01))",
                  border: "1.5px solid rgba(31,90,230,.25)",
                  borderRadius: 10, padding: "1.1rem 1.3rem", marginBottom: "1.2rem"
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: ".8rem" }}>
                    <div style={{ fontSize: "1.8rem", lineHeight: 1 }}>👥</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", marginBottom: ".3rem" }}>
                        Espace multi-agent disponible
                      </div>
                      <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.55, marginBottom: ".8rem" }}>
                        Invitez vos collaborateurs à rejoindre votre compte VehicleIQ. Chaque agent accède aux outils sous votre organisation. Réservé à l'offre Pro Multi-Agent (3 agents inclus).
                      </div>
                      <button onClick={async () => {
                        if (!window.confirm("Activer votre espace multi-agent ?\n\nVotre SIRET : " + profileSiret + "\nNombre max d'agents par défaut : 3\n\nVous pourrez ensuite inviter vos collaborateurs depuis l'onglet 'Mes agents'.")) return;
                        try {
                          const r = await apiPost("/api/org/create", {
                            siret: profileSiret,
                            company_name: profileSiretName || ""
                          });
                          if (r?.success) {
                            await reloadOrg();
                            setTab("agents");
                            // Notifier App pour rafraîchir userOrg → affichage bouton "Mon équipe"
                            try { window.dispatchEvent(new Event("viq-org-changed")); } catch(_) {}
                          } else {
                            alert("Erreur : " + (r?.error || "activation échouée"));
                          }
                        } catch(e) {
                          const msg = e?.message || "";
                          if (msg.includes("SIRET_MISMATCH")) {
                            alert("Le SIRET de votre profil ne correspond pas. Vérifiez votre profil pro.");
                          } else {
                            alert("Erreur réseau : " + msg);
                          }
                        }
                      }}
                        className="btn btn-accent"
                        style={{ fontSize: ".82rem", padding: ".55rem 1.1rem" }}>
                        Activer mon espace multi-agent →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Infos compte */}
              <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 6, padding: "1rem 1.2rem" }}>
                <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".8rem" }}>Compte connecté</div>
                <div style={{ fontSize: ".88rem", marginBottom: ".2rem" }}>{user?.name || "—"}</div>
                <div style={{ fontSize: ".82rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>{user?.email || "Non connecté"}</div>
              </div>
              {/* Infos personnelles */}
              <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 6, padding: "1rem 1.2rem" }}>
                <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".8rem" }}>Informations personnelles</div>
                <div className="field" style={{ marginBottom: ".8rem" }}>
                  <label style={{ fontSize: ".78rem" }}>Téléphone</label>
                  <input className="input" placeholder="06 XX XX XX XX" value={phone} onChange={e => setPhone(e.target.value)} />
                </div>
                <div className="field" style={{ marginBottom: 0, position: "relative" }}>
                  <label style={{ fontSize: ".78rem" }}>Adresse postale</label>
                  <input className="input" placeholder="Tapez quelques caractères pour la suggestion automatique…"
                    value={address}
                    onChange={e => {
                      const v = e.target.value;
                      setAddress(v);
                      // Auto-complétion via l'API gouvernementale data.gouv.fr
                      if (v.length >= 3) {
                        fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(v)}&limit=5&autocomplete=1`)
                          .then(r => r.ok ? r.json() : null)
                          .then(d => {
                            if (d && d.features) {
                              setAddressSuggestions(d.features.map(f => f.properties.label));
                            }
                          })
                          .catch(() => setAddressSuggestions([]));
                      } else {
                        setAddressSuggestions([]);
                      }
                    }}
                    onFocus={() => { if (address.length >= 3 && addressSuggestions.length > 0) setShowAddressSuggestions(true); }}
                    onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 200)}
                  />
                  {showAddressSuggestions && addressSuggestions.length > 0 && (
                    <div style={{
                      position: "absolute", top: "100%", left: 0, right: 0,
                      background: "var(--white)", border: "1.5px solid var(--border)",
                      borderRadius: 8, marginTop: ".3rem", zIndex: 10,
                      boxShadow: "0 8px 24px rgba(20,30,55,.12)",
                      maxHeight: 220, overflowY: "auto"
                    }}>
                      {addressSuggestions.map((s, i) => (
                        <div key={i}
                          onClick={() => { setAddress(s); setAddressSuggestions([]); setShowAddressSuggestions(false); }}
                          style={{
                            padding: ".55rem .8rem", cursor: "pointer", fontSize: ".82rem",
                            color: "var(--text)", borderBottom: i < addressSuggestions.length - 1 ? "1px solid var(--border)" : "none",
                            transition: "background .12s"
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--bg)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          📍 {s}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Annonce du déclencheur de l'autocomplete */}
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".25rem", fontStyle: "italic" }}>
                    Source : api-adresse.data.gouv.fr (officiel)
                  </div>
                </div>
              </div>
              {/* SIRET / Profil pro */}
              <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 6, padding: "1rem 1.2rem" }}>
                <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".8rem" }}>Profil professionnel</div>
                {orgData.organization ? (
                  // Membre d'une organisation : SIRET hérité, non modifiable.
                  // Le propriétaire comme les agents partagent le même SIRET (celui de l'org).
                  <div>
                    <div style={{
                      background: "rgba(168,128,58,.06)",
                      border: "1px solid rgba(168,128,58,.2)",
                      borderRadius: 6, padding: ".75rem .9rem", marginBottom: ".6rem",
                      fontSize: ".74rem", color: "var(--text)", lineHeight: 1.5
                    }}>
                      🏢 Vous êtes <strong>{orgData.organization.my_role === "owner" ? "propriétaire" : "agent"}</strong> de l'organisation. Le SIRET est hérité automatiquement.
                    </div>
                    {orgData.organization.company_name && (
                      <div className="field">
                        <label style={{ fontSize: ".78rem" }}>Raison sociale</label>
                        <input className="input"
                          value={orgData.organization.company_name}
                          readOnly disabled
                          style={{
                            background: "var(--card)",
                            color: "var(--text)",
                            fontWeight: 600,
                            cursor: "not-allowed"
                          }} />
                      </div>
                    )}
                    <div className="field" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: ".78rem" }}>SIRET de l'organisation</label>
                      <input className="input"
                        value={orgData.organization.siret}
                        readOnly
                        disabled
                        style={{
                          background: "var(--card)",
                          color: "var(--text)",
                          fontFamily: "var(--font-mono)",
                          cursor: "not-allowed"
                        }} />
                    </div>
                  </div>
                ) : (
                  // Compte solo : choix Particulier / Professionnel libre
                  <>
                    <div className="siret-choice" style={{ marginBottom: ".8rem" }}>
                      <button className={`siret-choice-btn ${!profileIsPro ? "active" : ""}`} onClick={() => { setProfileIsPro(false); setProfileSiret(""); setProfileSiretName(""); }}>👤 Particulier</button>
                      <button className={`siret-choice-btn ${profileIsPro ? "active" : ""}`} onClick={() => setProfileIsPro(true)}>🏢 Professionnel</button>
                    </div>
                    {profileIsPro && (
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: ".78rem" }}>SIRET <span style={{ color: "var(--accent)" }}>*</span></label>
                        <input className="input" placeholder="123 456 789 00012"
                          value={profileSiret} maxLength={50}
                          onChange={e => lookupProfileSiret(e.target.value)} />
                        {profileSearching && <div className="siret-searching">🔍 Recherche en cours…</div>}
                        {profileError && <div style={{ fontSize: ".75rem", color: "var(--red)", marginTop: ".3rem" }}>⚠ {profileError}</div>}
                        {profileSiretName && (
                          <div className="siret-result">
                            <strong>{profileSiretName}</strong>
                            SIRET : {profileSiret}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              <button className="btn btn-accent" style={{ width: "100%" }} onClick={saveProfile}>
                {profileSaved ? "✓ Profil sauvegardé !" : "Sauvegarder mon profil →"}
              </button>

              {/* Documents légaux */}
              <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 6, padding: "1rem 1.2rem" }}>
                <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--sub)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".6rem" }}>Documents légaux</div>
                <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.5, marginBottom: ".8rem" }}>
                  Retrouvez les Conditions Générales d'Utilisation et de Vente que vous avez acceptées lors de votre souscription.
                </div>
                <button className="btn btn-ghost btn-sm" onClick={downloadCGV} style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>
                  ⬇ Télécharger les Conditions Générales d'Utilisation et de Vente (PDF)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ─── Apporteurs d'affaires — map UID → données (gestion manuelle) ─────────────
const AFFILIATES_MAP = {
  "9I0MnNdPZ3VWr0jgQ4qVJVACXYS2": { code: "EKF26", name: "Eunice KOFFI" },
  "7j1CjoqPJreotdckYtSQZoCYPRw1": { code: "LMG26", name: "Lorentz MBOUMBA BONG" },
};
// ─── Wallet helpers ───────────────────────────────────────────────────────────
// Note : prix unitaire moyen d'un crédit ≈ 4,99 € (pack Activité régulière 30cr/149,70€).
// Voir RechargeModal pour la grille complète. Aucune constante hardcodée ici pour éviter les divergences.

function getWallet() {
  try { return parseInt(localStorage.getItem("viq_wallet") || "0", 10); } catch(e) { return 0; }
}
function setWallet(n) {
  try { localStorage.setItem("viq_wallet", String(Math.max(0, n))); } catch(e) {}
}

// ─── runSteppedLoader — barre d'étapes au tempo régulier ─────────────────────
// Problème résolu : avant, les étapes 1→(N-1) défilaient à intervalle fixe puis
// la DERNIÈRE étape restait bloquée le temps de l'appel API (5 à 30 s), créant
// une impression de plantage et incitant l'utilisateur à quitter.
//
// Cette fonction garantit que CHAQUE étape — y compris la dernière — a une durée
// perçue homogène :
//   • Les étapes avancent à `tickMs` d'intervalle au début.
//   • On NE termine PAS automatiquement sur l'avant-dernière étape : la
//     progression est volontairement freinée pour ne jamais "rattraper" la fin
//     avant que la promesse `work` (l'appel API) soit résolue.
//   • Dès que `work` est résolu, on déroule les étapes restantes à un tempo
//     accéléré (60% de tickMs) pour rattraper sans donner l'impression de saut.
//   • Bonus v5 : variation légère du tempo (-/+200ms) pour rendre l'attente
//     moins mécanique et plus humaine.
//
// Retourne le résultat de `work()`.
async function runSteppedLoader({ steps, setIdx, work, tickMs = 1400, minTotalMs = 0 }) {
  const N = steps.length;
  let idx = 0;
  let workDone = false;
  let workResult = null;
  let workError = null;
  const startedAt = Date.now();

  // Lance le travail réel (API) en parallèle
  const workPromise = Promise.resolve()
    .then(() => work())
    .then(r => { workResult = r; workDone = true; })
    .catch(e => { workError = e; workDone = true; });

  // Variation pseudo-aléatoire mais déterministe par index pour rendre le
  // défilement moins mécanique (l'œil détecte les intervalles parfaits)
  const jitter = (i) => 0.85 + 0.3 * ((i * 1664525 + 1013904223) % 1000) / 1000;

  await new Promise((resolve) => {
    const tick = () => {
      const ceiling = workDone ? N - 1 : Math.max(0, N - 2);
      if (idx < ceiling) {
        idx += 1;
        setIdx(idx);
      }
      const elapsed = Date.now() - startedAt;
      if (workDone && idx >= N - 1 && elapsed >= minTotalMs) {
        // Petit délai final pour laisser voir la dernière étape "achevée"
        setTimeout(resolve, 600);
        return;
      }
      // Tempo adaptatif : plus rapide si workDone (accélération naturelle)
      // + jitter pour humaniser le rythme
      const baseMs = workDone ? tickMs * 0.6 : tickMs;
      const nextMs = Math.round(baseMs * jitter(idx));
      setTimeout(tick, nextMs);
    };
    setTimeout(tick, tickMs);
  });

  await workPromise; // garantie que workResult/workError sont fixés
  if (workError) throw workError;
  return workResult;
}

function spendWallet(cost) {
  const bal = getWallet();
  if (bal < cost) return false;
  setWallet(bal - cost);
  return true;
}
function addWallet(qty) {
  setWallet(getWallet() + qty);
}

// ─── chargeForAction — facture 1+ crédits, exécute l'action si OK ─────────────
// Utilisée pour : export PDF, téléchargement, partage simple, partage WhatsApp.
// • Si solde insuffisant : déclenche onInsufficient (typiquement showRecharge modal).
// • Si OK : débite localement + backend en arrière-plan + exécute action(),
//   et déclenche onSpent({ newBalance }) pour rafraîchir le wallet pill / UI.
async function chargeForAction({ cost = 1, label = "action", action, onSpent, onInsufficient }) {
  const bal = getWallet();
  if (bal < cost) {
    if (typeof onInsufficient === "function") onInsufficient({ missing: cost - bal });
    else alert("Vos estimations offertes sont épuisées. Activez votre moyen de paiement dans Mon espace pour continuer (4,99 € par dossier).");
    return false;
  }
  // Débiter localement (effet immédiat)
  setWallet(bal - cost);
  // Débit backend en arrière-plan (best-effort, non bloquant)
  try {
    const apiUrl = window.__viq_api_url || "https://vehicleiq-backend-production.up.railway.app";
    fetch(`${apiUrl}/api/wallet/spend`, {
      method: "POST", headers: { "Content-Type":"application/json" },
      body: JSON.stringify({ cost, label, uid: window.__viq_uid })
    }).catch(()=>{});
  } catch(e) {}
  // Notifier l'UI
  if (typeof onSpent === "function") onSpent({ newBalance: bal - cost });
  // Exécuter l'action
  try { await action(); } catch(e) { console.warn("[chargeForAction]", e); }
  return true;
}

// ─── MobileMenuDrawer — Menu hamburger pour mobile ───────────────────────────
function MobileMenuDrawer({ onClose, user, isAdmin, pricingUsage, actions }) {
  return (
    <div
      className="mobile-menu-overlay"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="mobile-menu-drawer">

        {/* Header drawer */}
        <div className="mobile-menu-header">
          <div className="brand-mark">
            <div className="logo">
              <div className="logo-dot" />
              Vehicle<span>IQ</span>
            </div>
            <div className="brand-tagline">VIQ.AI Engine</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Sections de liens */}
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Découvrir</div>
          <button className="mobile-menu-link" onClick={() => { onClose(); (actions.openAPI ? actions.openAPI() : actions.openB2B()); }}>
            <span>🔌</span> Intégration API
          </button>
          <button className="mobile-menu-link" onClick={() => { onClose(); actions.openPricing(); }}>
            <span>💰</span> Tarification
          </button>
          <button className="mobile-menu-link" onClick={() => { onClose(); actions.openBlog(); }}>
            <span>📖</span> Blog
          </button>
          <button className="mobile-menu-link" onClick={() => { onClose(); actions.openFAQ(); }}>
            <span>📚</span> Ressources & FAQ
          </button>
        </div>

        {/* Section connecté */}
        {user && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Mon compte</div>
            <button className="mobile-menu-link" onClick={() => { onClose(); actions.openHistory(); }}>
              <span>👤</span> Mon espace
            </button>
            <button className="mobile-menu-link" onClick={() => { onClose(); actions.openPayment(); }}>
              <span>💳</span> Paiements et Abonnements
            </button>
            <button className="mobile-menu-link" onClick={() => { onClose(); actions.openDashboard(); }}>
              <span>📊</span> Dashboard Pro
            </button>
            <button className="mobile-menu-link" onClick={() => { onClose(); actions.openAffiliate(); }}>
              <span>🤝</span> Programme Partenaires
            </button>
            {/* v112 — Déplacé ici depuis la barre de navigation supérieure,
                désormais supprimée. */}
            {actions.openOrgManagement && (
              <button className="mobile-menu-link" onClick={() => { onClose(); actions.openOrgManagement(); }}>
                <span>👥</span> Mon équipe
              </button>
            )}
            {isAdmin && (
              <button className="mobile-menu-link mobile-menu-link-admin"
                onClick={() => { onClose(); actions.openAdmin(); }}>
                <span>⚡</span> Admin
              </button>
            )}
          </div>
        )}

        {/* Section non-connecté */}
        {!user && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Compte</div>
            <button className="mobile-menu-link"
              onClick={() => { onClose(); actions.openLogin(); }}>
              <span>🔑</span> Connexion
            </button>
            <button className="mobile-menu-link mobile-menu-link-primary"
              onClick={() => { onClose(); actions.openSignup(); }}>
              <span>✨</span> Créer un compte
            </button>
          </div>
        )}

        {/* Lancer un service */}
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Lancer un service</div>
          <button className="mobile-menu-link"
            onClick={() => { onClose(); actions.launchService("check"); }}>
            <span>🔍</span> Contrôle VIN / Plaque
          </button>
          <button className="mobile-menu-link"
            onClick={() => { onClose(); actions.launchService("sell"); }}>
            <span>💰</span> Estimer un véhicule
          </button>
          <button className="mobile-menu-link"
            onClick={() => { onClose(); actions.launchService("buy"); }}>
            <span>✓</span> Recommandations achat
          </button>
        </div>

        {/* Footer drawer */}
        <div className="mobile-menu-footer">
          <a href="https://vehicleiq.fr" style={{ color: "var(--muted)", fontSize: ".75rem", textDecoration: "none" }}>
            vehicleiq.fr
          </a>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
//  ROUTING SEO + PAGES PILIERS (Livrable 3) + PAGES MÉTIERS (Livrable 4)
// ════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════
//  MICRO-ROUTEUR SEO — sans dépendance externe
//  Gère les URLs propres pour les pages piliers / métiers / cotes.
//  Prérequis indexation : SSR ou prerender côté hébergement (voir note infra).
// ════════════════════════════════════════════════════════════════════════
function viqGetPath() {
  try { return window.location.pathname.replace(/\/+$/, "") || "/"; }
  catch (e) { return "/"; }
}

function viqNavigate(path) {
  try {
    if (viqGetPath() === path.replace(/\/+$/, "")) { window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    window.history.pushState({}, "", path);
    window.dispatchEvent(new Event("viq-route"));
    window.scrollTo({ top: 0, behavior: "auto" });
  } catch (e) {
    window.location.href = path;
  }
}

function useViqRoute() {
  const [path, setPath] = useState(viqGetPath());
  useEffect(() => {
    const sync = () => setPath(viqGetPath());
    window.addEventListener("popstate", sync);
    window.addEventListener("viq-route", sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener("viq-route", sync);
    };
  }, []);
  return path;
}

// ─── AcceptInvitationPage — Page d'acceptation d'invitation multi-agent ──
// Route : /invitation?token=xxx
// Comportement :
//   - Token absent → message d'erreur
//   - Token présent + user non connecté → invite à se connecter
//   - Token présent + user connecté → affiche les infos de l'org + bouton Accepter
function AcceptInvitationPage({ user, onShowAuth, onAccepted }) {
  const token = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("token") || "";
    } catch (e) { return ""; }
  })();

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token || token === "undefined") {
      setError("Lien d'invitation invalide ou expiré.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        // IMPORTANT : on fait un fetch DIRECT sans header Authorization car cet endpoint
        // est public. Si on passait par apiGet, on enverrait "Authorization: Bearer null"
        // ou un uid stale post-signin → le backend peut traiter ça comme un Bearer invalide.
        // Fetch direct = pas de header parasite, pas de race condition avec l'auth Firebase.
        const res = await fetch(`${API_URL}/api/org/invitations/by-token/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (res.status === 404) {
          setError("Cette invitation n'existe pas ou a été révoquée.");
        } else if (!res.ok) {
          setError("Impossible de charger l'invitation. Réessayez plus tard.");
        } else {
          const r = await res.json();
          if (r?.success && r.invitation) {
            setInvitation(r.invitation);
          } else {
            setError(r?.error || "Invitation introuvable");
          }
        }
      } catch (e) {
        if (cancelled) return;
        setError("Impossible de charger l'invitation. Vérifiez votre connexion réseau et réessayez.");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleAccept = async () => {
    if (!user?.uid) {
      onShowAuth();
      return;
    }
    setAccepting(true);
    setError("");
    try {
      const r = await apiPost("/api/org/invitations/" + encodeURIComponent(token) + "/accept", {});
      if (r?.success) {
        setAccepted(true);
        if (onAccepted) onAccepted();
        // Notifier App pour rafraîchir userOrg → affichage bouton "Mon équipe"
        try { window.dispatchEvent(new Event("viq-org-changed")); } catch(_) {}
        // Rediriger vers l'accueil après 3 secondes
        setTimeout(() => { viqNavigate("/"); }, 3000);
      } else {
        setError(r?.error || "Erreur lors de l'acceptation");
      }
    } catch (e) {
      const msg = e?.message || "";
      if (msg.toLowerCase().includes("expirée")) {
        setError("Cette invitation a expiré. Demandez à votre administrateur d'en créer une nouvelle.");
      } else if (msg.toLowerCase().includes("déjà")) {
        setError("Vous faites déjà partie de cette organisation.");
      } else {
        setError(msg || "Erreur lors de l'acceptation");
      }
    }
    setAccepting(false);
  };

  const roleLabel = (r) => r === "owner" ? "Propriétaire" : "Agent";

  // Style commun de la page (full-screen centré)
  const pageStyle = {
    minHeight: "100vh",
    background: "linear-gradient(180deg, var(--card) 0%, var(--white) 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "2rem 1rem"
  };

  return (
    <div style={pageStyle}>
      <div style={{
        maxWidth: 540, width: "100%",
        background: "var(--white)",
        border: "1.5px solid var(--border)", borderRadius: 14,
        padding: "2.5rem 2.2rem",
        boxShadow: "0 8px 32px rgba(0,0,0,.06)"
      }}>
        {/* En-tête : logo VehicleIQ */}
        <div style={{ textAlign: "center", marginBottom: "1.8rem" }}>
          <div style={{
            fontFamily: "var(--font-head)", fontSize: "1.5rem", fontWeight: 700, color: "var(--text)"
          }}>
            Vehicle<span style={{ color: "var(--accent)" }}>IQ</span>
          </div>
          <div style={{ fontSize: ".68rem", color: "var(--sub)", fontFamily: "var(--font-mono)", letterSpacing: "1px", marginTop: ".2rem" }}>
            INVITATION COLLABORATEUR
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: "center", color: "var(--sub)", fontSize: ".88rem", padding: "2rem 0" }}>
            <div className="spinner" style={{ margin: "0 auto 1rem" }} />
            Chargement de l'invitation…
          </div>
        )}

        {!loading && error && (
          <div style={{
            background: "rgba(185,28,28,.05)",
            border: "1px solid rgba(185,28,28,.2)",
            borderRadius: 8, padding: "1.2rem", textAlign: "center"
          }}>
            <div style={{ fontSize: "1.8rem", marginBottom: ".5rem" }}>⚠</div>
            <div style={{ color: "#9B2C2C", fontSize: ".88rem", fontWeight: 600, lineHeight: 1.5 }}>
              {error}
            </div>
            <button onClick={() => viqNavigate("/")}
              className="btn btn-ghost"
              style={{ marginTop: "1.2rem", fontSize: ".82rem" }}>
              Retour à l'accueil
            </button>
          </div>
        )}

        {!loading && !error && accepted && (
          <div style={{
            background: "rgba(4,120,87,.05)",
            border: "1px solid rgba(4,120,87,.2)",
            borderRadius: 8, padding: "1.5rem", textAlign: "center"
          }}>
            <div style={{ fontSize: "2.2rem", marginBottom: ".5rem", color: "var(--green)" }}>✓</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.2rem", fontWeight: 700, color: "var(--text)", marginBottom: ".5rem" }}>
              Bienvenue dans l'organisation  !
            </div>
            <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.5 }}>
              Vous êtes maintenant membre de <strong>{invitation?.company_name || "l'organisation"}</strong>.<br/>
              Redirection vers l'accueil dans 3 secondes…
            </div>
          </div>
        )}

        {!loading && !error && !accepted && invitation && (
          <>
            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ fontSize: ".95rem", color: "var(--text)", marginBottom: ".5rem", lineHeight: 1.5 }}>
                Vous êtes invité à rejoindre l'organisation :
              </div>
              <div style={{
                background: "linear-gradient(135deg, rgba(168,128,58,.06), rgba(168,128,58,0))",
                border: "1px solid rgba(168,128,58,.2)",
                borderRadius: 10, padding: "1.1rem 1.3rem"
              }}>
                <div style={{
                  fontFamily: "var(--font-head)", fontSize: "1.3rem", fontWeight: 700, color: "var(--text)",
                  marginBottom: ".25rem"
                }}>
                  {invitation.company_name || "Entreprise"}
                </div>
                <div style={{ fontSize: ".74rem", color: "var(--sub)", fontFamily: "var(--font-mono)" }}>
                  SIRET {invitation.siret}
                </div>
                <div style={{
                  marginTop: ".7rem", fontSize: ".78rem", color: "var(--sub)",
                  display: "flex", gap: "1rem", flexWrap: "wrap"
                }}>
                  <span>
                    Rôle proposé :
                    <strong style={{ marginLeft: ".3rem", color: "var(--text)" }}>{roleLabel(invitation.role)}</strong>
                  </span>
                  <span>
                    Invitation envoyée à :
                    <strong style={{ marginLeft: ".3rem", color: "var(--text)" }}>{invitation.email}</strong>
                  </span>
                </div>
              </div>
            </div>

            {/* Avantages */}
            <div style={{
              background: "var(--card)", border: "1.5px solid var(--border)",
              borderRadius: 8, padding: "1rem 1.2rem", marginBottom: "1.5rem"
            }}>
              <div style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--text)", marginBottom: ".5rem", fontFamily: "var(--font-mono)", letterSpacing: "1px", textTransform: "uppercase" }}>
                Ce que vous débloquez
              </div>
              <ul style={{ margin: 0, paddingLeft: "1.2rem", fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.6 }}>
                <li>Accès aux outils de Cote VIQ.AI®</li>
                <li>Contrôle VIN / Plaque illimité</li>
                <li>Recommandations d'achat sur mesure</li>
                <li>Espace collaboratif partagé avec votre équipe</li>
              </ul>
            </div>

            {/* CTA */}
            {user?.uid ? (
              <>
                <button onClick={handleAccept} disabled={accepting}
                  className="btn btn-accent"
                  style={{
                    width: "100%", padding: ".95rem", fontSize: ".95rem",
                    fontWeight: 700
                  }}>
                  {accepting ? "Acceptation en cours…" : "Accepter l'invitation →"}
                </button>
                <div style={{ textAlign: "center", marginTop: ".7rem", fontSize: ".68rem", color: "var(--muted)" }}>
                  Connecté en tant que <strong>{user.email}</strong>
                </div>
              </>
            ) : (
              <>
                <button onClick={onShowAuth}
                  className="btn btn-accent"
                  style={{
                    width: "100%", padding: ".95rem", fontSize: ".95rem",
                    fontWeight: 700
                  }}>
                  Se connecter ou créer un compte pour rejoindre
                </button>
                <div style={{ textAlign: "center", marginTop: ".7rem", fontSize: ".7rem", color: "var(--sub)", lineHeight: 1.5 }}>
                  Après connexion, vous serez automatiquement ajouté à l'organisation.<br/>
                  L'email <strong>{invitation.email}</strong> sera utilisé si vous créez un nouveau compte.
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Maj <title> + meta description + canonical + JSON-LD pour une page SEO
function viqSetMeta({ title, description, path, jsonLd, image, type }) {
  try {
    if (title) document.title = title;
    const up = (sel, attr, key, content) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    if (description) {
      up('meta[name="description"]', "name", "description", description);
      up('meta[property="og:description"]', "property", "og:description", description);
      up('meta[name="twitter:description"]', "name", "twitter:description", description);
    }
    if (title) {
      up('meta[property="og:title"]', "property", "og:title", title);
      up('meta[name="twitter:title"]', "name", "twitter:title", title);
    }
    const canonicalUrl = "https://vehicleiq.fr" + (path || "/");
    // v82 — Règle SEO/GEO : og:type/og:url/og:image et Twitter Card
    // manquaient entièrement — ajoutés ici, une seule fois, pour que toutes
    // les pages utilisant viqSetMeta en bénéficient d'un coup.
    up('meta[property="og:type"]', "property", "og:type", type || "website");
    up('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    up('meta[property="og:site_name"]', "property", "og:site_name", "VehicleIQ");
    up('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    const ogImage = image || "https://vehicleiq.fr/og-image.png";
    up('meta[property="og:image"]', "property", "og:image", ogImage);
    up('meta[name="twitter:image"]', "name", "twitter:image", ogImage);
    // Canonical
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", canonicalUrl);
    // JSON-LD (Schema.org) — un seul bloc piloté par data-viq
    let s = document.head.querySelector('script[data-viq-jsonld]');
    if (jsonLd) {
      if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.setAttribute("data-viq-jsonld", "1"); document.head.appendChild(s); }
      s.textContent = JSON.stringify(jsonLd);
    } else if (s) {
      s.remove();
    }
  } catch (e) { /* SSR-safe : ignore si document indisponible */ }
}

// ─── Breadcrumb réutilisable ────────────────────────────────────────────────
function SeoBreadcrumb({ items }) {
  return (
    <nav className="seo-breadcrumb" aria-label="Fil d'Ariane">
      {items.map((it, i) => (
        <span key={i}>
          {it.path ? (
            <button className="seo-bc-link" onClick={() => viqNavigate(it.path)}>{it.label}</button>
          ) : (
            <span className="seo-bc-current">{it.label}</span>
          )}
          {i < items.length - 1 && <span className="seo-bc-sep">/</span>}
        </span>
      ))}
    </nav>
  );
}

// ─── Layout commun des pages SEO (nav simplifiée + footer + CTA) ────────────
function SeoPageLayout({ children, breadcrumb, onCta, onContact, onAPI, onHome }) {
  // v82 — Règle SEO/GEO : le fil d'Ariane n'existait que visuellement —
  // aucune donnée structurée BreadcrumbList associée. Script JSON-LD séparé
  // de celui de viqSetMeta (une page a souvent déjà un FAQPage/Article en
  // parallèle — Google accepte plusieurs blocs JSON-LD sur une même page).
  useEffect(() => {
    if (!breadcrumb || !breadcrumb.length) return;
    let s = document.head.querySelector('script[data-viq-breadcrumb]');
    if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.setAttribute("data-viq-breadcrumb", "1"); document.head.appendChild(s); }
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumb.map((it, i) => ({
        "@type": "ListItem", "position": i + 1, "name": it.label,
        ...(it.path ? { "item": "https://vehicleiq.fr" + it.path } : {}),
      })),
    });
    return () => { s?.remove(); };
  }, [breadcrumb]);

  return (
    <div className="app seo-page">
      <nav className="seo-nav">
        <div className="brand-mark brand-mark-clickable" role="button" tabIndex={0}
          onClick={onHome}
          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onHome(); }}
          aria-label="Retour à l'accueil VehicleIQ">
          <div className="logo"><div className="logo-dot" />Vehicle<span>IQ</span></div>
          <div className="brand-tagline">VIQ.AI Engine</div>
        </div>
        <div className="nav-center seo-nav-center">
          <button className="nav-link" onClick={() => viqNavigate("/estimation-automobile-professionnelle")}>Cote</button>
          <button className="nav-link" onClick={() => viqNavigate("/outil-pricing-vo-professionnel")}>Pricing VO</button>
          <button className="nav-link" onClick={() => viqNavigate("/tendances-marche-automobile")}>Marché</button>
          <button className="nav-link" onClick={() => (onAPI ? onAPI() : viqNavigate("/solutions/marchands-automobiles"))}>Intégration API</button>
        </div>
        <div className="nav-actions">
          <button className="btn btn-accent btn-sm" onClick={onCta}>Espace Pro</button>
        </div>
      </nav>

      <main className="seo-main">
        {breadcrumb && <SeoBreadcrumb items={breadcrumb} />}
        {children}
      </main>

      {/* CTA final commun — fond accent */}
      <div className="seo-final-cta">
        <div className="seo-final-inner">
          <h2 className="seo-final-h2">Estimez votre premier véhicule maintenant — gratuitement</h2>
          <p className="seo-final-sub">5 cotes offertes et déjà actives à l'inscription, sans carte bancaire. Le tableau de bord 360° des professionnels de l'achat-revente VO — cote, contrôle, aide à l'achat et suivi de vente réunis (estimations indicatives).</p>
          <div className="seo-final-btns">
            <button className="btn seo-final-primary" onClick={onCta}>Démarrer la Cote →</button>
            <button className="btn seo-final-secondary" onClick={onContact}>Parler à un expert</button>
          </div>
        </div>
      </div>

      <footer className="seo-footer">
        <div className="seo-footer-inner">
          <div className="seo-footer-col">
            <div className="logo" style={{ marginBottom: ".7rem" }}><div className="logo-dot" />Vehicle<span>IQ</span></div>
            <p>Plateforme d'intelligence du véhicule d'occasion. Cote, contrôle et pricing VO pour les professionnels de l'automobile.</p>
          </div>
          <div className="seo-footer-col">
            <div className="seo-footer-title">Plateforme</div>
            <button onClick={() => viqNavigate("/estimation-automobile-professionnelle")}>Cote professionnelle</button>
            <button onClick={() => viqNavigate("/controle-vehicule-vin-plaque")}>Contrôle VIN / Plaque</button>
            <button onClick={() => viqNavigate("/outil-pricing-vo-professionnel")}>Outil de pricing VO</button>
            <button onClick={() => viqNavigate("/tendances-marche-automobile")}>Tendances marché</button>
          </div>
          <div className="seo-footer-col">
            <div className="seo-footer-title">Solutions métiers</div>
            <button onClick={() => viqNavigate("/solutions/marchands-automobiles")}>Marchands VO</button>
            <button onClick={() => viqNavigate("/solutions/garages")}>Garages VO indépendants</button>
          </div>
          <div className="seo-footer-col">
            <div className="seo-footer-title">Entreprise</div>
            <button onClick={onHome}>Accueil</button>
            <button onClick={onContact}>Contact</button>
          </div>
        </div>
        <div className="seo-footer-bottom">© {new Date().getFullYear()} VehicleIQ — VIQ.AI® Engine · Plateforme d'intelligence automobile</div>
      </footer>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
//  LIVRABLE 3 — PAGES PILIERS SEO (×4)
// ════════════════════════════════════════════════════════════════════════
const PILLAR_PAGES = {
  "/estimation-automobile-professionnelle": {
    metaTitle: "Estimation automobile professionnelle en temps réel | VehicleIQ",
    metaDesc: "Estimation VO sans abonnement, calibrée sur le marché réel (indicative). Conçu pour les indépendants et marchands VO : 30 estimations pour 99 € HT (vs 250-600 €/mois en abonnement chez la concurrence). Pay-as-you-go.",
    eyebrow: "Page pilier · Estimation",
    h1: "Outil d'estimation automobile professionnelle en temps réel (IA + données marché)",
    intro: "VehicleIQ est une plateforme d'intelligence automobile qui estime la valeur réelle d'un véhicule d'occasion en temps réel. Le moteur VIQ.AI® croise les prix de plus de 100 plateformes pour produire une fourchette indicative calibrée sur le marché réel. Les professionnels du VO fiabilisent ainsi chaque prix d'achat et de revente, sans dépendre d'un barème figé. Le résultat est disponible en moins de 30 secondes, dans un rapport PDF exportable.",
    sections: [
      {
        h2: "1. Pourquoi estimer un véhicule comme un professionnel ?",
        body: [
          "Un particulier estime au feeling ; un professionnel engage sa marge. Sur un VO à 25 000 €, une erreur de 5 % représente 1 250 € de marge perdue, ou pire, une reprise surévaluée. La précision de la Cote devient donc un avantage compétitif direct dans un marché où les prix bougent chaque semaine.",
          "La Cote professionnelle repose sur des données marché vivantes, pas sur une intuition. Elle intègre le kilométrage exact, la motorisation, l'état réel et la pression de l'offre locale. C'est cette rigueur qui distingue un achat rentable d'un stock qui dort.",
          "Avec une plateforme d'intelligence automobile, vous standardisez cette rigueur sur l'ensemble de vos dossiers. Chaque acheteur de votre équipe applique la même méthode, traçable et défendable face à un client.",
        ],
      },
      {
        h2: "2. Les méthodes de Cote : Argus, cote marché, data, IA",
        body: [
          "La cote barème type Argus reste une référence historique reconnue, mais elle est lissée sur trente jours et issue d'un panel fermé. Elle décrit le marché d'hier, pas celui d'aujourd'hui.",
          "La cote marché (médiane des annonces) reflète mieux le réel, mais inclut les annonces aberrantes qui biaisent la médiane. La data brute multi-sources corrige ce biais en filtrant et en pondérant des milliers de points de prix.",
          "L'IA ajoute une couche de modélisation : elle apprend les coefficients de dépréciation par segment, anticipe la saisonnalité et ajuste selon la géographie. VIQ.AI® combine data temps réel et algorithme propriétaire pour produire une estimation indicative au plus proche du marché.",
        ],
      },
      {
        h2: "3. VehicleIQ et VIQ.AI® : la cote marché expliquée",
        body: [
          "Le moteur VIQ.AI® scanne en continu LeBonCoin, La Centrale, AutoScout24 et d'autres plateformes pour reconstituer le prix de marché instantané d'un modèle donné. Il pondère les annonces selon la cohérence du kilométrage, de l'état et de la finition.",
          "L'algorithme applique ensuite des coefficients de dépréciation calibrés par âge, énergie et segment, puis ajuste selon la pression de l'offre locale. La fourchette basse/haute encadre l'incertitude résiduelle de façon transparente.",
          "Chaque cote s'accompagne d'un indice de confiance et de l'historique SIV officiel, ce qui sécurise la décision avant tout engagement financier. La plateforme reste un outil d'aide à la décision : le professionnel garde la main.",
        ],
      },
      {
        h2: "4. Comment utiliser l'outil pas à pas",
        body: [
          "Saisissez une plaque d'immatriculation au format SIV : VehicleIQ récupère automatiquement le VIN, la marque, la motorisation et la date de mise en circulation via l'API officielle. Vous pouvez aussi partir d'un modèle.",
          "Complétez le kilométrage, l'état et les options : chaque critère affine la fourchette. L'outil produit alors un prix central, une fourchette basse/haute et la tendance de marché sur trente jours.",
          "Exportez un rapport professionnel à transmettre au client ou à archiver. Répétez l'opération sur chaque dossier : la méthode est identique, rapide et défendable.",
        ],
      },
    ],
    ctaLabel: "Essayer gratuitement →",
    related: [
      { label: "Contrôle de véhicule par VIN et plaque", path: "/controle-vehicule-vin-plaque" },
      { label: "Outil de pricing VO professionnel", path: "/outil-pricing-vo-professionnel" },
    ],
    faq: [
      { q: "Le rapport VehicleIQ peut-il être présenté à un client ?", a: "Oui. Le rapport est structuré, daté et traçable : il documente les sources et la méthode utilisées. C'est un support de négociation solide à présenter à un client. Il s'agit d'une cote indicative basée sur le marché en temps réel, pas d'une cote officielle." },
      { q: "Faut-il un abonnement pour estimer ?", a: "Non. VehicleIQ fonctionne en paiement à l'usage, sans engagement. Les premières cotes sont offertes pour tester la plateforme." },
    ],
  },

  "/controle-vehicule-vin-plaque": {
    metaTitle: "Contrôle véhicule professionnel par VIN et plaque | VehicleIQ",
    metaDesc: "Contrôlez l'historique d'un véhicule par VIN ou plaque : kilométrage, sinistres, entretien. Multi-sources, pour marchands, assureurs et flottes.",
    eyebrow: "Page pilier · Contrôle véhicule",
    h1: "Contrôle de véhicule professionnel par VIN et plaque d'immatriculation",
    intro: "Avant toute reprise ou achat, le contrôle du véhicule sécurise la décision. VehicleIQ interroge l'API SIV officielle et croise plusieurs sources pour reconstituer l'historique d'un véhicule à partir de sa plaque ou de son VIN. Vous identifiez en quelques secondes les incohérences de kilométrage, les sinistres et les zones de risque. C'est la couche de fiabilisation indispensable d'une plateforme d'intelligence automobile.",
    sections: [
      {
        h2: "1. Pourquoi vérifier un véhicule avant toute reprise ou achat ?",
        body: [
          "Une reprise mal contrôlée peut transformer une marge en perte sèche : compteur trafiqué, sinistre masqué, entretien fantôme. Le contrôle systématique élimine ces angles morts avant l'engagement financier.",
          "Pour un marchand, vérifier un véhicule en amont évite d'immobiliser du stock invendable. Pour un assureur, c'est la base d'une indemnisation juste. Le contrôle n'est pas une option, c'est une assurance.",
          "Automatiser ce contrôle le rend systématique. Chaque dossier passe par la même grille de vérification, sans dépendre de la vigilance individuelle d'un acheteur pressé.",
        ],
      },
      {
        h2: "2. Données collectées : kilométrage, sinistres, historique d'entretien",
        body: [
          "À partir de la plaque ou du VIN, VehicleIQ récupère les caractéristiques officielles : marque, modèle, motorisation, date de mise en circulation. Ces données servent de socle au croisement.",
          "L'analyse met en évidence les incohérences de kilométrage entre relevés successifs, signal majeur de compteur manipulé. Les éléments d'historique connus (sinistres, entretien) complètent le profil de risque.",
          "Chaque information est horodatée et rattachée à sa source, ce qui rend le rapport exploitable face à un client ou en interne pour justifier une décision d'achat.",
        ],
      },
      {
        h2: "3. Contrôle automatisé multi-sources par VehicleIQ",
        body: [
          "Le moteur VIQ.AI® consolide les données officielles et les signaux de marché en une seule requête, sans naviguer entre dix outils. Le gain de temps est immédiat sur un volume élevé de dossiers.",
          "Le contrôle s'intègre naturellement au flux de Cote : un véhicule contrôlé puis estimé donne un prix fiabilisé, pas une simple cote théorique.",
          "Cette automatisation rend la vérification systématique et homogène sur toute votre activité, quelle que soit la personne qui traite le dossier.",
        ],
      },
      {
        h2: "4. Cas d'usage : marchand, assureur, gestionnaire de flotte",
        body: [
          "Le marchand contrôle chaque véhicule avant reprise pour ne jamais surpayer un stock à risque. L'assureur fiabilise l'évaluation d'un sinistre et lutte contre la fraude documentaire.",
          "Le gestionnaire de flotte suit l'historique de chaque véhicule sur tout son cycle de vie, ce qui sécurise la valeur résiduelle à la sortie de parc.",
          "Dans les trois cas, le contrôle alimente directement la décision de prix : c'est le préalable d'une cote crédible.",
        ],
      },
    ],
    ctaLabel: "Analyser un véhicule maintenant →",
    related: [
      { label: "Cote automobile professionnelle", path: "/estimation-automobile-professionnelle" },
      { label: "Outil de pricing VO professionnel", path: "/outil-pricing-vo-professionnel" },
    ],
    faq: [
      { q: "Le contrôle fonctionne-t-il avec une plaque étrangère ?", a: "Le contrôle est optimisé pour le format SIV français. Pour les véhicules importés, le VIN reste la clé d'identification la plus fiable." },
      { q: "Le contrôle remplace-t-il un contrôle technique ?", a: "Non. Il documente l'historique et les incohérences, mais ne se substitue pas au contrôle technique réglementaire réalisé en centre agréé." },
    ],
  },

  "/outil-pricing-vo-professionnel": {
    metaTitle: "Outil de pricing VO pour garages et marchands | VehicleIQ",
    metaDesc: "Outil de pricing VO en temps réel pour garages et marchands indépendants : prix marché indicatifs calibrés sur le réel, alternative moderne à l'Argus.",
    eyebrow: "Page pilier · Pricing VO",
    h1: "Outil de pricing VO pour garages et marchands indépendants",
    intro: "Le pricing VO décide de la rentabilité d'un garage ou d'un marchand indépendant. VehicleIQ calcule un prix de marché indicatif en temps réel à partir de plus de 100 plateformes. Vous achetez au bon prix, vous affichez au prix juste et vous accélérez la rotation du stock. La plateforme d'intelligence automobile remplace les barèmes figés par une lecture vivante du marché.",
    sections: [
      {
        h2: "1. Les enjeux du pricing VO en 2026",
        body: [
          "Les marges VO se compriment et les prix deviennent plus volatils. Un stock acheté trop cher ou affiché à côté du marché dort, immobilise de la trésorerie et finit en perte. Le pricing n'est plus un détail, c'est le cœur du métier.",
          "Les acheteurs comparent en ligne avant de se déplacer : un prix décalé de quelques centaines d'euros tue la visibilité d'une annonce. Le bon prix doit être trouvé du premier coup.",
          "Dans ce contexte, s'appuyer sur un barème mensuel revient à piloter en regardant dans le rétroviseur. Le pricing temps réel devient la norme des professionnels performants.",
        ],
      },
      {
        h2: "2. Comment VehicleIQ calcule les prix marché en temps réel",
        body: [
          "Le moteur VIQ.AI® agrège en continu les annonces de plus de 100 plateformes, filtre les valeurs aberrantes et pondère selon la cohérence du véhicule. Il reconstitue ainsi le prix réel d'un modèle à l'instant T.",
          "Des coefficients de dépréciation par âge, énergie et segment ajustent le résultat, puis une correction géographique tient compte de la pression locale de l'offre. La sortie est une fourchette claire, pas un chiffre opaque.",
          "Cette transparence permet de défendre un prix d'achat face à un vendeur et un prix d'affichage face à un client, données à l'appui.",
        ],
      },
      {
        h2: "3. Comparatif VIQ.AI® vs Argus : ce qui change concrètement",
        body: [
          "Un barème type Argus est mis à jour mensuellement et issu d'un panel restreint. VIQ.AI® lit le marché en temps réel sur 100+ plateformes : la différence se mesure en fraîcheur de donnée.",
          "Concrètement, sur un segment qui recule de 3 % en dix jours, un barème mensuel l'ignore tandis que VIQ.AI® l'intègre immédiatement. Sur un volume de dossiers, cet écart se chiffre en milliers d'euros.",
          "Là où le barème reste théorique, le pricing temps réel est défendable et actionnable au quotidien. Les deux logiques sont complémentaires, mais seul le temps réel protège la marge au quotidien.",
        ],
      },
      {
        h2: "4. Intégration dans votre processus d'achat / revente",
        body: [
          "À la reprise, contrôlez puis estimez le véhicule : vous fixez un prix d'achat plafond fondé sur le marché réel, pas sur une intuition. À la mise en vente, alignez l'affichage sur la fourchette pour maximiser la visibilité.",
          "En suivi de stock, surveillez la dérive de prix d'un véhicule qui ne se vend pas et ajustez avant qu'il ne devienne une perte. Le pricing devient un processus continu, pas un acte ponctuel.",
          "Une intégration API permet d'appeler le pricing depuis votre DMS ou CRM, pour que la donnée vive là où vos équipes travaillent déjà.",
        ],
      },
    ],
    ctaLabel: "Découvrir le pricing intelligent →",
    related: [
      { label: "Cote automobile professionnelle", path: "/estimation-automobile-professionnelle" },
      { label: "Tendances du marché automobile", path: "/tendances-marche-automobile" },
    ],
    faq: [
      { q: "Le pricing s'intègre-t-il à mon DMS ?", a: "Oui, via une intégration API. Contactez l'équipe pour évaluer la connexion à votre système et obtenir la documentation technique." },
      { q: "Le pricing fonctionne-t-il sur les véhicules rares ?", a: "Plus un modèle a d'annonces actives, plus la fourchette est resserrée. Sur les véhicules rares, l'indice de confiance signale l'incertitude pour que vous gardiez la main." },
    ],
  },

  "/tendances-marche-automobile": {
    metaTitle: "Tendances du marché automobile 2026 : data et IA | VehicleIQ",
    metaDesc: "Tendances du marché automobile 2026 : décote, segments porteurs, saisonnalité. Insights data et IA pour anticiper la valorisation VO.",
    eyebrow: "Page pilier · Marché",
    h1: "Tendances du marché automobile 2026 : data, IA et comportement d'achat",
    intro: "Comprendre les tendances du marché automobile, c'est anticiper la valeur de demain. VehicleIQ observe en continu des milliers de prix pour décrypter la décote par segment, la saisonnalité et les ruptures de marché. Ces insights aident les professionnels du VO à décider quand acheter, quand revendre et où se positionner. Une plateforme d'intelligence automobile transforme la donnée brute en avantage stratégique.",
    sections: [
      {
        h2: "1. Évolution du marché du véhicule d'occasion en France",
        body: [
          "Le marché du VO français reste le moteur du secteur automobile, porté par un ratio occasion/neuf historiquement élevé. La contrainte budgétaire des acheteurs renforce durablement la demande de seconde main.",
          "La compression des prix s'accélère sur les segments généralistes tandis que certaines motorisations résistent mieux. Lire ces écarts par segment évite de généraliser une tendance qui ne vaut pas partout.",
          "La digitalisation de la recherche concentre le volume sur quelques grandes plateformes, ce qui rend l'observation multi-sources d'autant plus pertinente pour reconstituer le prix réel.",
        ],
      },
      {
        h2: "2. L'impact de la data et de l'IA sur la valorisation VO",
        body: [
          "La data temps réel remplace progressivement le barème mensuel comme référence opérationnelle. L'IA y ajoute la capacité d'anticiper plutôt que de seulement constater.",
          "Modéliser la dépréciation par segment permet d'identifier les véhicules qui décotent vite et ceux qui tiennent. C'est la base d'une politique d'achat sélective et rentable.",
          "L'IA détecte aussi les signaux faibles : une bascule réglementaire ou fiscale qui pèse sur un segment se voit dans les prix avant d'apparaître dans les barèmes.",
        ],
      },
      {
        h2: "3. Insights exclusifs VehicleIQ : décote, segments porteurs, saisonnalité",
        body: [
          "L'analyse de la décote met en évidence les fenêtres où un véhicule perd brutalement de la valeur, à éviter à l'achat et à anticiper à la revente. Le timing devient un levier de marge.",
          "Certains segments restent porteurs malgré la pression générale sur les prix : les repérer tôt oriente la politique de stock vers ce qui se vend vite et bien.",
          "La saisonnalité influence fortement la demande de certaines catégories. Acheter à contre-saison et vendre au pic est une stratégie que la data rend exploitable concrètement.",
        ],
      },
      {
        h2: "4. Comment anticiper le marché avec un outil data",
        body: [
          "Suivre la tendance à trente jours d'un modèle indique s'il faut accélérer ou différer une décision. La donnée transforme l'incertitude en arbitrage rationnel.",
          "Comparer la dynamique de plusieurs segments aide à réallouer le budget d'achat vers ce qui performe, sans pari aveugle. C'est du pilotage, pas de l'intuition.",
          "Intégrée à la Cote et au pricing, cette lecture marché ferme la boucle : on contrôle, on estime, on price et on décide au bon moment.",
        ],
      },
    ],
    ctaLabel: "Accéder aux insights marché →",
    related: [
      { label: "Outil de pricing VO professionnel", path: "/outil-pricing-vo-professionnel" },
      { label:"Cote automobile professionnelle", path: "/estimation-automobile-professionnelle" },
    ],
    faq: [
      { q: "À quelle fréquence les tendances sont-elles actualisées ?", a: "Le moteur VIQ.AI® observe le marché en continu. Les tendances reflètent les annonces actives les plus récentes, pas un instantané mensuel." },
      { q: "Ces insights sont-ils spécifiques à ma région ?", a: "L'analyse intègre une correction géographique. La pression locale de l'offre est prise en compte dans la lecture des prix." },
    ],
  },
};

function PillarPage({ data, path, onCta, onContact, onHome, onAPI }) {
  useEffect(() => {
    viqSetMeta({
      title: data.metaTitle,
      description: data.metaDesc,
      path,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": (data.faq || []).map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
    });
  }, [path]);

  return (
    <SeoPageLayout
      onCta={onCta} onContact={onContact} onHome={onHome} onAPI={onAPI}
      breadcrumb={[
        { label: "Accueil", path: "/" },
        { label: data.eyebrow.split("· ")[1] || "Ressources" },
      ]}
    >
      <article className="seo-article">
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>{data.eyebrow}</div>
        <h1 className="seo-h1">{data.h1}</h1>
        <p className="seo-intro">{data.intro}</p>

        <button className="btn btn-accent seo-inline-cta" onClick={onCta}>{data.ctaLabel}</button>

        {data.sections.map((sec, i) => (
          <section className="seo-section" key={i}>
            <h2 className="seo-h2">{sec.h2}</h2>
            {sec.body.map((p, j) => <p className="seo-p" key={j}>{p}</p>)}
          </section>
        ))}

        {/* Maillage interne */}
        <div className="seo-related">
          <div className="seo-related-title">À lire aussi</div>
          <div className="seo-related-links">
            {data.related.map((r, i) => (
              <button key={i} className="seo-related-link" onClick={() => viqNavigate(r.path)}>
                {r.label} →
              </button>
            ))}
          </div>
        </div>

        {/* FAQ */}
        {data.faq && data.faq.length > 0 && (
          <section className="seo-section">
            <h2 className="seo-h2">Questions fréquentes</h2>
            {data.faq.map((f, i) => (
              <div className="seo-faq-item" key={i}>
                <h3 className="seo-h3">{f.q}</h3>
                <p className="seo-p">{f.a}</p>
              </div>
            ))}
          </section>
        )}
      </article>
    </SeoPageLayout>
  );
}


// ════════════════════════════════════════════════════════════════════════
//  LIVRABLE 4 — PAGES B2B MÉTIERS (×7)  /solutions/[metier]
// ════════════════════════════════════════════════════════════════════════
const METIER_PAGES = {
  "marchands-automobiles": {
    metaTitle: "VehicleIQ pour marchands automobiles : pricing VO en temps réel",
    metaDesc: "Estimation et pricing VO temps réel pour marchands automobiles. Achetez au bon prix, accélérez la rotation du stock. Alternative à l'Argus.",
    label: "Marchands automobiles",
    h1: "VehicleIQ pour marchands automobiles : achetez au bon prix, vendez plus vite",
    intro: "Pour un marchand automobile, chaque reprise est un pari sur la marge. VehicleIQ remplace l'intuition par une estimation marché indicative, pour acheter juste et faire tourner le stock. La plateforme d'intelligence automobile sécurise l'achat et fiabilise le prix d'affichage.",
    enjeux: [
      "Surpayer une reprise et bloquer la marge dès l'achat.",
      "Stock qui dort faute d'un prix d'affichage aligné sur le marché réel.",
      "Barème mensuel déconnecté d'un marché qui bouge chaque semaine.",
      "Décisions d'achat non homogènes entre les acheteurs de l'équipe.",
    ],
    resout: [
      "Cote marché temps réel indicative sur 100+ plateformes.",
      "Prix d'achat plafond objectivé, défendable face au vendeur.",
      "Prix d'affichage aligné pour maximiser la visibilité des annonces.",
      "Méthode homogène et traçable sur tous les dossiers.",
    ],
    cas: [
      "Reprise client : contrôle VIN puis cote en 30 s, prix plafond fixé sur place.",
      "Sourcing pro : comparaison rapide de plusieurs lots pour arbitrer le budget d'achat.",
      "Déstockage : détection du véhicule qui dérive et réajustement avant la perte.",
    ],
    benefices: [
      "Marge protégée dès l'achat grâce à un prix objectivé.",
      "Rotation du stock accélérée par un affichage au prix juste.",
      "Temps gagné : un dossier complet en moins d'une minute.",
    ],
    temoignage: {
      verbatim: "On a arrêté d'acheter au feeling. La fourchette VIQ.AI® nous sert de plafond à la reprise — notre marge moyenne s'est stabilisée.",
      nom: "Karim B.", poste: "Gérant", entreprise: "Négoce auto multimarque", ville: "Lyon",
    },
    stats: [
      ["temps réel", "Cote marché"],
      ["100+", "Plateformes analysées"],
      ["< 30 s", "Par dossier de reprise"],
    ],
    faq: [
      { q: "Combien coûte une cote ?", a: "VehicleIQ fonctionne en paiement à l'usage, sans abonnement. Les premières cotes sont offertes pour tester la plateforme." },
      { q: "L'outil gère-t-il les véhicules importés ?", a: "Oui, via le VIN. Le format SIV optimise l'identification des véhicules immatriculés en France ; le VIN reste la clé pour les imports." },
      { q: "Puis-je transmettre le rapport à un client ?", a: "Le rapport est structuré et daté, conçu pour servir de support de négociation transparent." },
      { q: "Est-ce une alternative à l'Argus ?", a: "VehicleIQ lit le marché en temps réel sur 100+ plateformes, là où un barème est mensuel. Les deux logiques sont complémentaires." },
    ],
  },

  "garages": {
    metaTitle: "VehicleIQ pour garages : estimation et reprise VO fiable",
    metaDesc: "Estimation auto et reprise VO pour garages indépendants. Logiciel d'estimation automobile en temps réel, estimation indicative. Sans abonnement.",
    label: "Garages",
    h1: "VehicleIQ pour garages : transformez chaque reprise en opportunité",
    intro: "Le garage indépendant capte des reprises mais manque souvent d'un outil de pricing fiable. VehicleIQ apporte une estimation automobile professionnelle en temps réel, sans abonnement, pour sécuriser chaque reprise et créer une activité VO rentable à côté de l'atelier.",
    enjeux: [
      "Reprise refusée ou sous-évaluée par manque d'outil fiable.",
      "Pas le temps de comparer manuellement des dizaines d'annonces.",
      "Activité VO marginale faute de méthode de pricing.",
      "Risque de surpayer un véhicule à l'historique incertain.",
    ],
    resout: [
      "Cote marché en 30 s, sans expertise pricing préalable.",
      "Contrôle VIN/plaque intégré pour fiabiliser l'historique.",
      "Pricing temps réel transformant la reprise en marge réelle.",
      "Aucun abonnement : on consomme à l'usage.",
    ],
    cas: [
      "Client en révision qui propose son ancien véhicule en reprise : cote immédiate au comptoir.",
      "Évaluation rapide avant de proposer un prix de rachat ferme.",
      "Aide à l'achat pour conseiller un client cherchant un VO sain.",
    ],
    benefices: [
      "Nouvelle source de marge sans investissement lourd.",
      "Décision de reprise sécurisée et rapide.",
      "Crédibilité renforcée auprès du client avec un rapport clair.",
    ],
    temoignage: {
      verbatim: "Avant, on déclinait les reprises faute d'outil. Aujourd'hui on estime en direct au comptoir et on capte la vente derrière.",
      nom: "Sébastien M.", poste: "Garagiste", entreprise: "Garage indépendant", ville: "Tours",
    },
    stats: [
      ["temps réel", "Cote marché"],
      ["0 €", "Abonnement"],
      ["30 s", "Estimation au comptoir"],
    ],
    faq: [
      { q: "Faut-il être expert en pricing ?", a: "Non. L'outil produit la fourchette ; vous gardez la décision finale. Aucune compétence pricing préalable n'est requise." },
      { q: "Est-ce adapté à un petit volume ?", a: "Oui. Le paiement à l'usage rend l'outil rentable même pour quelques reprises par mois." },
      { q: "Le contrôle d'historique est-il inclus ?", a: "Le contrôle VIN/plaque s'intègre au flux de Cote pour fiabiliser la décision." },
      { q: "Puis-je l'utiliser pour conseiller un achat ?", a: "Oui, l'aide à l'achat VO permet d'orienter un client vers un véhicule sain au bon prix." },
    ],
  },

  "concessions": {
    metaTitle: "VehicleIQ pour concessions : valorisation VO multi-marques",
    metaDesc: "Valorisation VO et reprise pour concessions automobiles. Estimation temps réel (indicatif), homogène sur tout le réseau. Intégration API DMS.",
    label: "Concessions",
    h1: "VehicleIQ pour concessions : une valorisation VO homogène et défendable",
    intro: "En concession, la reprise VO conditionne la vente du véhicule neuf. La méthode VIQ.AI® calibrée sur le marché réel (estimation indicative) apporte une lecture marché objectivée. À noter : VehicleIQ s'adresse aujourd'hui en priorité aux garages, marchands, revendeurs et mandataires VO indépendants — les besoins spécifiques des réseaux multi-sites peuvent être discutés sur demande.",
    enjeux: [
      "Cotes hétérogènes selon le conseiller, source de litiges.",
      "Reprise surévaluée pour sauver une vente VN, marge VO sacrifiée.",
      "Barème interne lent à refléter le marché réel.",
      "Difficulté à justifier un prix de reprise face au client.",
    ],
    resout: [
      "Méthode unique et traçable pour tous les conseillers.",
      "Fourchette marché objectivée, indépendante de la pression VN.",
      "Lecture temps réel du marché VO multimarque.",
      "Rapport structuré servant de support de négociation.",
    ],
    cas: [
      "Reprise lors d'une vente VN : cote objective présentée au client.",
      "Audit du stock VO pour repérer les véhicules surévalués à l'achat.",
      "Harmonisation des pratiques entre plusieurs points de vente du groupe.",
    ],
    benefices: [
      "Marge VO préservée même sous pression commerciale VN.",
      "Moins de litiges grâce à une cote défendable.",
      "Pilotage homogène du réseau sur une donnée commune.",
    ],
    temoignage: {
      verbatim: "Tous nos conseillers parlent désormais le même langage prix. La reprise n'est plus un point de friction avec le client.",
      nom: "Nathalie R.", poste: "Responsable VO", entreprise: "Concession multimarque", ville: "Nantes",
    },
    stats: [
      ["temps réel", "Cote marché"],
      ["100+", "Plateformes analysées"],
      ["API", "Intégration DMS possible"],
    ],
    faq: [
      { q: "L'outil s'intègre-t-il au DMS du groupe ?", a: "Une intégration API permet d'appeler cote et contrôle depuis votre DMS. Contactez l'équipe pour la documentation." },
      { q: "Peut-on harmoniser plusieurs sites ?", a: "Oui. La méthode est identique partout, ce qui garantit l'homogénéité sur tout le réseau." },
      { q: "Le prix dépend-il de la pression VN ?", a: "Non. La fourchette reflète le marché VO réel, indépendamment de l'objectif de vente neuf." },
      { q: "Le rapport est-il transmissible au client ?", a: "Oui. Il est structuré, daté et traçable : un support de négociation transparent." },
    ],
  },

  "assureurs": {
    metaTitle: "VehicleIQ pour assureurs : valeur de remplacement VO",
    metaDesc: "Évaluation VO pour assureurs : valeur de remplacement fiable, lutte anti-fraude, contrôle d'historique multi-sources (estimations indicatives).",
    label: "Assureurs",
    h1: "VehicleIQ pour assureurs : une valeur de remplacement juste et traçable",
    intro: "Pour un assureur, la valeur d'un véhicule sinistré doit être juste, rapide et défendable. La méthode VIQ.AI® calibrée sur le marché réel (estimation indicative) et le contrôle d'historique multi-sources offrent une lecture marché objectivée. À noter : VehicleIQ s'adresse aujourd'hui en priorité aux garages, marchands, revendeurs et mandataires VO indépendants — les besoins assurantiels peuvent être discutés sur demande.",
    enjeux: [
      "Contestation fréquente de la valeur proposée à l'assuré.",
      "Fraude documentaire difficile à détecter manuellement.",
      "Délais d'évaluation qui allongent le règlement du sinistre.",
      "Hétérogénéité des évaluations entre gestionnaires.",
    ],
    resout: [
      "Valeur de remplacement objectivée sur le marché réel.",
      "Contrôle d'historique pour repérer les incohérences.",
      "Évaluation en quelques secondes, traçable et documentée.",
      "Méthode homogène sur l'ensemble des dossiers.",
    ],
    cas: [
      "Sinistre total : détermination rapide de la valeur de remplacement.",
      "Vérification d'historique pour détecter un kilométrage incohérent.",
      "Contre-expertise : fourchette marché documentée et traçable, présentable face à un expert adverse.",
    ],
    benefices: [
      "Moins de contestations grâce à une valeur défendable.",
      "Réduction de l'exposition à la fraude.",
      "Délais de règlement raccourcis.",
    ],
    temoignage: {
      verbatim: "La fourchette marché horodatée a fait baisser nettement nos contestations sur la valeur de remplacement.",
      nom: "Olivier D.", poste: "Responsable indemnisation", entreprise: "Cabinet d'assurance", ville: "Lille",
    },
    stats: [
      ["temps réel", "Cote marché"],
      ["Multi-sources", "Contrôle d'historique"],
      ["Horodaté", "Rapport traçable"],
    ],
    faq: [
      { q: "Le rapport est-il utilisable en contre-expertise ?", a: "Le rapport documente sources et méthode avec horodatage : un support documenté qu'un expert pourra examiner et discuter contradictoirement." },
      { q: "L'outil détecte-t-il la fraude au compteur ?", a: "Le contrôle met en évidence les incohérences de kilométrage, signal majeur de manipulation." },
      { q: "Peut-on l'intégrer à notre SI ?", a: "Une intégration API est possible. Contactez l'équipe pour évaluer la connexion." },
      { q: "Quel délai pour une évaluation ?", a: "Une cote marché est produite en moins de 30 secondes." },
    ],
  },

  "gestionnaires-flotte": {
    metaTitle: "VehicleIQ pour gestionnaires de flotte : valorisation parc",
    metaDesc: "Valorisation de flotte VO en temps réel : suivi de dépréciation, fenêtre de revente optimale, estimation indicative. Pour loueurs et gestionnaires.",
    label: "Gestionnaires de flotte",
    h1: "VehicleIQ pour gestionnaires de flotte : maximisez la valeur résiduelle",
    intro: "Sortir un parc au mauvais moment ou au mauvais prix coûte cher. La méthode VIQ.AI® apporte une lecture marché temps réel utile à l'arbitrage de valeur résiduelle. À noter : VehicleIQ s'adresse aujourd'hui en priorité aux garages, marchands, revendeurs et mandataires VO indépendants — les besoins spécifiques de gestion de flotte peuvent être discutés sur demande.",
    enjeux: [
      "Sous-valorisation des véhicules à la sortie de parc.",
      "Décisions de revente prises sans visibilité sur le marché réel.",
      "Dépréciation subie plutôt qu'anticipée.",
      "Pas de vue consolidée de la valeur du parc.",
    ],
    resout: [
      "Valorisation temps réel de chaque véhicule du parc.",
      "Suivi de dépréciation par segment et anticipation.",
      "Identification des fenêtres de revente optimales.",
      "Vision consolidée de la valeur résiduelle.",
    ],
    cas: [
      "Sortie de parc : prix de cession aligné sur le marché à l'instant T.",
      "Arbitrage de renouvellement selon la dynamique de décote.",
      "Reporting de valeur résiduelle pour la direction financière.",
    ],
    benefices: [
      "Valeur de revente maximisée par le bon timing.",
      "Décisions de renouvellement objectivées.",
      "Reporting financier fiabilisé.",
    ],
    temoignage: {
      verbatim: "On ne subit plus la décote : on planifie les sorties de parc selon la tendance marché. L'impact sur la valeur résiduelle est net.",
      nom: "Claire F.", poste: "Fleet manager", entreprise: "Société de location longue durée", ville: "Paris",
    },
    stats: [
      ["temps réel", "Cote marché"],
      ["Temps réel", "Suivi de dépréciation"],
      ["Parc complet", "Valorisation consolidée"],
    ],
    faq: [
      { q: "Peut-on valoriser tout un parc d'un coup ?", a: "Oui, la plateforme permet une valorisation consolidée et un suivi par segment." },
      { q: "Comment est déterminée la fenêtre de revente ?", a: "Par le suivi de la tendance de décote du segment : l'outil signale les phases de perte accélérée." },
      { q: "Y a-t-il un reporting financier ?", a: "La valeur résiduelle consolidée alimente votre reporting. Une intégration API est possible." },
      { q: "Est-ce adapté à la LLD ?", a: "Oui, loueurs et gestionnaires de flotte sont une cible directe de la plateforme." },
    ],
  },

  "reseaux-automobiles": {
    metaTitle: "VehicleIQ pour réseaux automobiles : pricing VO unifié",
    metaDesc: "Pricing VO unifié pour réseaux automobiles : méthode homogène multi-sites, données temps réel (indicatif), pilotage central. Intégration API.",
    label: "Réseaux automobiles",
    h1: "VehicleIQ pour réseaux automobiles : un pricing VO unifié sur tout le réseau",
    intro: "Un réseau automobile performant parle un seul langage prix. La méthode VIQ.AI® calibrée sur le marché réel (estimation indicative) apporte une référence marché commune et lisible. À noter : VehicleIQ s'adresse aujourd'hui en priorité aux garages, marchands, revendeurs et mandataires VO indépendants — les déploiements multi-sites peuvent être discutés sur demande.",
    enjeux: [
      "Pratiques de pricing divergentes entre sites du réseau.",
      "Difficulté de pilotage central faute de donnée commune.",
      "Barèmes internes lents et hétérogènes.",
      "Marge VO non maîtrisée à l'échelle du groupe.",
    ],
    resout: [
      "Méthode de Cote unique sur tous les sites.",
      "Donnée marché commune et temps réel.",
      "Pilotage central de la performance VO.",
      "Marge VO maîtrisée à l'échelle du réseau.",
    ],
    cas: [
      "Déploiement d'une politique de reprise homogène sur N sites.",
      "Benchmark interne des performances VO par point de vente.",
      "Consolidation de la valeur de stock à l'échelle groupe.",
    ],
    benefices: [
      "Cohérence de prix sur tout le réseau.",
      "Pilotage data-driven de l'activité VO.",
      "Marge consolidée protégée.",
    ],
    temoignage: {
      verbatim: "Le déploiement multi-sites a aligné nos pratiques de reprise en quelques semaines. Le pilotage central est enfin possible.",
      nom: "Marc T.", poste: "Directeur réseau", entreprise: "Groupe de distribution automobile", ville: "Bordeaux",
    },
    stats: [
      ["temps réel", "Cote marché"],
      ["Multi-sites", "Méthode unifiée"],
      ["API", "Pilotage central"],
    ],
    faq: [
      { q: "Peut-on déployer sur plusieurs sites ?", a: "Oui, la méthode est identique partout. Une intégration API facilite le déploiement réseau." },
      { q: "Y a-t-il un pilotage central ?", a: "La donnée commune permet un benchmark interne et un pilotage consolidé de la performance VO." },
      { q: "Comment se fait l'intégration ?", a: "Via API, à connecter à votre DMS/CRM groupe. Contactez l'équipe pour la documentation." },
      { q: "Quel délai de mise en place ?", a: "Le déploiement dépend de votre SI ; l'usage de la plateforme est immédiat dès l'accès." },
    ],
  },

  "import-export-automobile": {
    metaTitle: "VehicleIQ pour import-export auto : arbitrage prix marché",
    metaDesc: "Import-export automobile : comparez les prix de marché, arbitrez les opportunités, contrôlez l'historique par VIN (estimations indicatives).",
    label: "Import-export automobile",
    h1: "VehicleIQ pour l'import-export automobile : arbitrez les opportunités au bon prix",
    intro: "L'import-export vit d'écarts de prix entre marchés. VehicleIQ objective le prix de marché d'un véhicule et contrôle son historique par VIN, pour sécuriser chaque arbitrage. La plateforme d'intelligence automobile fiabilise des décisions d'achat à distance.",
    enjeux: [
      "Arbitrage d'opportunité sans référence de prix fiable.",
      "Historique incertain sur des véhicules achetés à distance.",
      "Volatilité des prix entre marchés et dans le temps.",
      "Risque de surpayer un lot sans données objectives.",
    ],
    resout: [
      "Prix de marché objectivé pour comparer les opportunités.",
      "Contrôle d'historique par VIN avant engagement.",
      "Lecture temps réel pour saisir la fenêtre d'arbitrage.",
      "Décision d'achat à distance sécurisée.",
    ],
    cas: [
      "Évaluation d'un lot avant achat à distance.",
      "Comparaison du prix cible avec le marché de revente visé.",
      "Contrôle VIN pour écarter les véhicules à risque.",
    ],
    benefices: [
      "Marge d'arbitrage protégée par une donnée objective.",
      "Risque réduit sur les achats à distance.",
      "Décisions plus rapides sur des fenêtres courtes.",
    ],
    temoignage: {
      verbatim: "Sur des achats à distance, le contrôle VIN couplé à la fourchette marché a fait disparaître nos mauvaises surprises.",
      nom: "Driss A.", poste: "Négociant import-export", entreprise: "Trading automobile", ville: "Marseille",
    },
    stats: [
      ["temps réel", "Cote marché"],
      ["VIN", "Contrôle d'historique"],
      ["Temps réel", "Lecture de marché"],
    ],
    faq: [
      { q: "L'outil gère-t-il les véhicules étrangers ?", a: "Le VIN est la clé d'identification universelle utilisée pour le contrôle ; le format SIV optimise les véhicules français." },
      { q: "Peut-on comparer deux marchés ?", a: "L'outil objective le prix de marché de référence ; vous arbitrez ensuite selon votre marché de revente cible." },
      { q: "Le contrôle d'historique est-il fiable à distance ?", a: "Il met en évidence les incohérences connues : un filtre de risque essentiel avant un achat non vu." },
      { q: "Y a-t-il un engagement ?", a: "Non, paiement à l'usage sans abonnement." },
    ],
  },
};

function MetierPage({ slug, onCta, onContact, onHome, onAPI }) {
  const data = METIER_PAGES[slug];
  const path = "/solutions/" + slug;
  useEffect(() => {
    if (!data) return;
    viqSetMeta({
      title: data.metaTitle, description: data.metaDesc, path,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": (data.faq || []).map(f => ({
          "@type": "Question", "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
    });
  }, [slug]);

  if (!data) return null;

  const Block = ({ title, items, ordered }) => (
    <section className="seo-section">
      <h2 className="seo-h2">{title}</h2>
      <ul className={`seo-list${ordered ? " seo-list-num" : ""}`}>
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </section>
  );

  return (
    <SeoPageLayout
      onCta={onCta} onContact={onContact} onHome={onHome} onAPI={onAPI}
      breadcrumb={[
        { label: "Accueil", path: "/" },
        { label: "Solutions", path: "/solutions/marchands-automobiles" },
        { label: data.label },
      ]}
    >
      <article className="seo-article">
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Solution métier · {data.label}</div>
        <h1 className="seo-h1">{data.h1}</h1>
        <p className="seo-intro">{data.intro}</p>

        {/* Statistiques sectorielles */}
        <div className="seo-stats">
          {data.stats.map(([v, l], i) => (
            <div className="seo-stat" key={i}>
              <div className="seo-stat-v">{v}</div>
              <div className="seo-stat-l">{l}</div>
            </div>
          ))}
        </div>

        <button className="btn btn-accent seo-inline-cta" onClick={onCta}>Réserver une démo →</button>

        <Block title="Vos enjeux métier" items={data.enjeux} />
        <Block title="Ce que VehicleIQ résout" items={data.resout} />
        <Block title="Cas d'usage concrets" items={data.cas} ordered />
        <Block title="Bénéfices business" items={data.benefices} />

        {/* Témoignage */}
        <section className="seo-section">
          <div className="seo-testimonial">
            <div className="seo-testimonial-stars">★★★★★</div>
            <p className="seo-testimonial-q">« {data.temoignage.verbatim} »</p>
            <div className="seo-testimonial-meta">
              <strong>{data.temoignage.nom}</strong> · {data.temoignage.poste}, {data.temoignage.entreprise} — {data.temoignage.ville}
            </div>
          </div>
        </section>

        {/* FAQ métier */}
        <section className="seo-section">
          <h2 className="seo-h2">Questions fréquentes — {data.label}</h2>
          {data.faq.map((f, i) => (
            <div className="seo-faq-item" key={i}>
              <h3 className="seo-h3">{f.q}</h3>
              <p className="seo-p">{f.a}</p>
            </div>
          ))}
        </section>

        {/* Maillage : autres métiers + piliers */}
        <div className="seo-related">
          <div className="seo-related-title">Autres solutions</div>
          <div className="seo-related-links">
            <button className="seo-related-link" onClick={() => viqNavigate("/estimation-automobile-professionnelle")}>Cote automobile professionnelle →</button>
            <button className="seo-related-link" onClick={() => viqNavigate("/outil-pricing-vo-professionnel")}>Outil de pricing VO →</button>
            <button className="seo-related-link" onClick={() => viqNavigate("/controle-vehicule-vin-plaque")}>Contrôle VIN / Plaque →</button>
          </div>
        </div>
      </article>
    </SeoPageLayout>
  );
}


// ════════════════════════════════════════════════════════════════════════
//  LIVRABLE 5 — PAGES COTES (générées depuis COTE_CATALOG)
// ════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════
//  LIVRABLE 5 — PAGES COTES  /cote/[marque]  et  /cote/[marque]/[modele]/[motorisation]
// ════════════════════════════════════════════════════════════════════════
// Catalogue : marque → { label, models: [{ slug, label, motors: [slug...] }] }
const COTE_CATALOG = {
  "peugeot":   { label: "Peugeot",   models: [
    { slug: "208", label: "208", motors: ["essence","diesel","electrique"] },
    { slug: "308", label: "308", motors: ["essence","diesel","hybride"] },
    { slug: "3008", label: "3008", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "2008", label: "2008", motors: ["essence","diesel","electrique"] },
    { slug: "5008", label: "5008", motors: ["essence","diesel"] },
  ]},
  "renault":   { label: "Renault",   models: [
    { slug: "clio", label: "Clio", motors: ["essence","diesel","hybride"] },
    { slug: "captur", label: "Captur", motors: ["essence","hybride","hybride-rechargeable"] },
    { slug: "megane", label: "Mégane", motors: ["essence","diesel","electrique"] },
    { slug: "austral", label: "Austral", motors: ["essence","hybride"] },
    { slug: "scenic", label: "Scénic", motors: ["electrique"] },
  ]},
  "citroen":   { label: "Citroën",   models: [
    { slug: "c3", label: "C3", motors: ["essence","diesel","electrique"] },
    { slug: "c4", label: "C4", motors: ["essence","diesel","electrique"] },
    { slug: "c5-aircross", label: "C5 Aircross", motors: ["essence","diesel","hybride-rechargeable"] },
  ]},
  "ds":        { label: "DS",        models: [
    { slug: "ds3", label: "DS 3", motors: ["essence","diesel","electrique"] },
    { slug: "ds4", label: "DS 4", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "ds7", label: "DS 7", motors: ["essence","diesel","hybride-rechargeable"] },
  ]},
  "volkswagen":{ label: "Volkswagen",models: [
    { slug: "golf", label: "Golf", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "polo", label: "Polo", motors: ["essence","diesel"] },
    { slug: "tiguan", label: "Tiguan", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "id3", label: "ID.3", motors: ["electrique"] },
    { slug: "id4", label: "ID.4", motors: ["electrique"] },
  ]},
  "bmw":       { label: "BMW",       models: [
    { slug: "serie-1", label: "Série 1", motors: ["essence","diesel"] },
    { slug: "serie-3", label: "Série 3", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "x1", label: "X1", motors: ["essence","diesel","electrique"] },
    { slug: "x3", label: "X3", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "i4", label: "i4", motors: ["electrique"] },
  ]},
  "mercedes":  { label: "Mercedes",  models: [
    { slug: "classe-a", label: "Classe A", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "classe-c", label: "Classe C", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "gla", label: "GLA", motors: ["essence","diesel"] },
    { slug: "eqa", label: "EQA", motors: ["electrique"] },
  ]},
  "audi":      { label: "Audi",      models: [
    { slug: "a1", label: "A1", motors: ["essence"] },
    { slug: "a3", label: "A3", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "q3", label: "Q3", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "q5", label: "Q5", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "e-tron", label: "e-tron", motors: ["electrique"] },
  ]},
  "tesla":     { label: "Tesla",     models: [
    { slug: "model-3", label: "Model 3", motors: ["electrique"] },
    { slug: "model-y", label: "Model Y", motors: ["electrique"] },
    { slug: "model-s", label: "Model S", motors: ["electrique"] },
  ]},
  "toyota":    { label: "Toyota",    models: [
    { slug: "yaris", label: "Yaris", motors: ["essence","hybride"] },
    { slug: "corolla", label: "Corolla", motors: ["essence","hybride"] },
    { slug: "rav4", label: "RAV4", motors: ["hybride","hybride-rechargeable"] },
    { slug: "c-hr", label: "C-HR", motors: ["hybride"] },
  ]},
  "dacia":     { label: "Dacia",     models: [
    { slug: "sandero", label: "Sandero", motors: ["essence","gpl"] },
    { slug: "duster", label: "Duster", motors: ["essence","diesel","gpl"] },
    { slug: "spring", label: "Spring", motors: ["electrique"] },
  ]},
  "ford":      { label: "Ford",      models: [
    { slug: "fiesta", label: "Fiesta", motors: ["essence","diesel"] },
    { slug: "puma", label: "Puma", motors: ["essence","hybride"] },
    { slug: "kuga", label: "Kuga", motors: ["essence","diesel","hybride-rechargeable"] },
  ]},
  "opel":      { label: "Opel",      models: [
    { slug: "corsa", label: "Corsa", motors: ["essence","diesel","electrique"] },
    { slug: "astra", label: "Astra", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "mokka", label: "Mokka", motors: ["essence","electrique"] },
  ]},
  "fiat":      { label: "Fiat",      models: [
    { slug: "500", label: "500", motors: ["essence","electrique"] },
    { slug: "panda", label: "Panda", motors: ["essence","hybride"] },
  ]},
  "nissan":    { label: "Nissan",    models: [
    { slug: "juke", label: "Juke", motors: ["essence","hybride"] },
    { slug: "qashqai", label: "Qashqai", motors: ["essence","hybride"] },
    { slug: "leaf", label: "Leaf", motors: ["electrique"] },
  ]},
  "hyundai":   { label: "Hyundai",   models: [
    { slug: "i20", label: "i20", motors: ["essence"] },
    { slug: "tucson", label: "Tucson", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "kona", label: "Kona", motors: ["essence","electrique"] },
  ]},
  "kia":       { label: "Kia",       models: [
    { slug: "picanto", label: "Picanto", motors: ["essence"] },
    { slug: "sportage", label: "Sportage", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "niro", label: "Niro", motors: ["hybride","electrique"] },
  ]},
  "seat":      { label: "Seat",      models: [
    { slug: "ibiza", label: "Ibiza", motors: ["essence"] },
    { slug: "leon", label: "Leon", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "arona", label: "Arona", motors: ["essence"] },
  ]},
  "skoda":     { label: "Skoda",     models: [
    { slug: "fabia", label: "Fabia", motors: ["essence"] },
    { slug: "octavia", label: "Octavia", motors: ["essence","diesel","hybride-rechargeable"] },
    { slug: "kodiaq", label: "Kodiaq", motors: ["essence","diesel"] },
  ]},
  "volvo":     { label: "Volvo",     models: [
    { slug: "xc40", label: "XC40", motors: ["essence","hybride-rechargeable","electrique"] },
    { slug: "xc60", label: "XC60", motors: ["diesel","hybride-rechargeable"] },
  ]},
  "mini":      { label: "Mini",      models: [
    { slug: "cooper", label: "Cooper", motors: ["essence","electrique"] },
    { slug: "countryman", label: "Countryman", motors: ["essence","hybride-rechargeable"] },
  ]},
  "porsche":   { label: "Porsche",   models: [
    { slug: "macan", label: "Macan", motors: ["essence","electrique"] },
    { slug: "cayenne", label: "Cayenne", motors: ["essence","hybride-rechargeable"] },
    { slug: "taycan", label: "Taycan", motors: ["electrique"] },
  ]},
  "cupra":     { label: "Cupra",     models: [
    { slug: "formentor", label: "Formentor", motors: ["essence","hybride-rechargeable"] },
    { slug: "born", label: "Born", motors: ["electrique"] },
  ]},
  "land-rover":{ label: "Land Rover",models: [
    { slug: "evoque", label: "Range Rover Evoque", motors: ["diesel","hybride-rechargeable"] },
    { slug: "discovery-sport", label: "Discovery Sport", motors: ["diesel","hybride-rechargeable"] },
  ]},
};

const MOTOR_LABELS = {
  "essence": "essence",
  "diesel": "diesel",
  "hybride": "hybride",
  "hybride-rechargeable": "hybride rechargeable",
  "electrique": "électrique",
  "gpl": "GPL",
};

// ─── Page COTE MARQUE : /cote/[marque] ──────────────────────────────────────
function CoteBrandPage({ brandKey, onCta, onContact, onHome, onAPI }) {
  const brand = COTE_CATALOG[brandKey];
  const path = "/cote/" + brandKey;
  useEffect(() => {
    if (!brand) return;
    viqSetMeta({
      title: `Cote ${brand.label} : estimation prix en temps réel | VehicleIQ`,
      description: `Cote ${brand.label} en temps réel : estimez le prix d'un ${brand.label} d'occasion sur 100+ plateformes, estimation indicative. Alternative à l'Argus.`,
      path,
      jsonLd: {
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": `Comment estimer la cote d'un ${brand.label} ?`,
            "acceptedAnswer": { "@type": "Answer", "text": `VehicleIQ analyse les prix réels du marché sur 100+ plateformes et calibre la cote ${brand.label} sur le marché réel selon l'année, le kilométrage et la motorisation.` } },
        ],
      },
    });
  }, [brandKey]);
  if (!brand) return null;

  return (
    <SeoPageLayout
      onCta={onCta} onContact={onContact} onHome={onHome} onAPI={onAPI}
      breadcrumb={[{ label: "Accueil", path: "/" }, { label: "Cote auto", path: "/cote/peugeot" }, { label: brand.label }]}
    >
      <article className="seo-article">
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Cote {brand.label}</div>
        <h1 className="seo-h1">Cote {brand.label} : cote prix en temps réel</h1>
        <p className="seo-intro">
          Estimez la valeur d'un {brand.label} d'occasion à partir des prix réels du marché.
          Le moteur VIQ.AI® analyse en continu plus de 100 plateformes pour produire une cote {brand.label} calibrée
          sur le marché réel, selon l'année, le kilométrage et la motorisation — une estimation indicative. Une lecture vivante du marché,
          là où un barème reste lissé sur trente jours.
        </p>

        <button className="btn btn-accent seo-inline-cta" onClick={onCta}>Estimer un {brand.label} →</button>

        <section className="seo-section">
          <h2 className="seo-h2">Cote {brand.label} par modèle</h2>
          <p className="seo-p">
            Sélectionnez un modèle pour accéder à son cote détaillée par motorisation.
            Chaque cote intègre la décote spécifique au modèle et la pression de l'offre du moment.
          </p>
          <div className="cote-grid">
            {brand.models.map(m => (
              <button key={m.slug} className="cote-card"
                onClick={() => viqNavigate(`/cote/${brandKey}/${m.slug}/${m.motors[0]}`)}>
                <span className="cote-card-label">{brand.label} {m.label}</span>
                <span className="cote-card-motors">{m.motors.map(x => MOTOR_LABELS[x]).join(" · ")}</span>
                <span className="cote-card-arrow">Voir la cote →</span>
              </button>
            ))}
          </div>
        </section>

        <section className="seo-section">
          <h2 className="seo-h2">Pourquoi la cote {brand.label} VehicleIQ est plus précise</h2>
          <p className="seo-p">
            Une cote {brand.label} fiable ne se lit pas dans un barème figé. Elle se calcule à partir des annonces
            actives, filtrées des valeurs aberrantes et pondérées selon la cohérence du véhicule. VIQ.AI® applique
            ensuite des coefficients de dépréciation propres au segment {brand.label}.
          </p>
          <p className="seo-p">
            Le résultat est une fourchette basse/haute transparente, accompagnée d'un indice de confiance.
            Vous fiabilisez ainsi un prix d'achat ou de revente {brand.label} en moins de 30 secondes,
            avec un rapport détaillé à un client.
          </p>
        </section>

        <div className="seo-related">
          <div className="seo-related-title">Cote & pricing</div>
          <div className="seo-related-links">
            <button className="seo-related-link" onClick={() => viqNavigate("/estimation-automobile-professionnelle")}>Cote automobile professionnelle →</button>
            <button className="seo-related-link" onClick={() => viqNavigate("/outil-pricing-vo-professionnel")}>Outil de pricing VO →</button>
            <button className="seo-related-link" onClick={() => viqNavigate("/tendances-marche-automobile")}>Tendances du marché automobile →</button>
          </div>
        </div>
      </article>
    </SeoPageLayout>
  );
}

// ─── Page COTE MODÈLE+MOTORISATION : /cote/[marque]/[modele]/[motorisation] ──
function CoteModelPage({ brandKey, modelSlug, motorSlug, onCta, onContact, onHome, onAPI }) {
  const brand = COTE_CATALOG[brandKey];
  const model = brand && brand.models.find(m => m.slug === modelSlug);
  const motorOk = model && model.motors.includes(motorSlug);
  const path = `/cote/${brandKey}/${modelSlug}/${motorSlug}`;
  const motorLabel = MOTOR_LABELS[motorSlug] || motorSlug;

  useEffect(() => {
    if (!brand || !model || !motorOk) return;
    viqSetMeta({
      title: `Cote ${brand.label} ${model.label} ${motorLabel} | VehicleIQ`,
      description: `Cote ${brand.label} ${model.label} ${motorLabel} d'occasion : estimation temps réel (indicatif) sur 100+ plateformes. Prix juste pour les pros.`,
      path,
      jsonLd: {
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": `Quel est le prix d'un ${brand.label} ${model.label} ${motorLabel} d'occasion ?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Le prix dépend de l'année, du kilométrage et de l'état. VehicleIQ calibre la cote ${brand.label} ${model.label} ${motorLabel} sur les prix réels du marché (estimation indicative).` } },
        ],
      },
    });
  }, [brandKey, modelSlug, motorSlug]);

  if (!brand || !model || !motorOk) return null;

  return (
    <SeoPageLayout
      onCta={onCta} onContact={onContact} onHome={onHome} onAPI={onAPI}
      breadcrumb={[
        { label: "Accueil", path: "/" },
        { label: brand.label, path: "/cote/" + brandKey },
        { label: `${model.label} ${motorLabel}` },
      ]}
    >
      <article className="seo-article">
        <div className="sec-eyebrow" style={{ color: "var(--accent)" }}>Cote {brand.label} {model.label}</div>
        <h1 className="seo-h1">Cote {brand.label} {model.label} {motorLabel} : cote prix en temps réel</h1>
        <p className="seo-intro">
          Estimez la valeur d'un {brand.label} {model.label} {motorLabel} d'occasion à partir des prix réels du marché.
          VIQ.AI® analyse les annonces actives sur 100+ plateformes et calibre la cote sur le marché réel selon l'année,
          le kilométrage et l'état. Une cote vivante pour acheter et revendre au prix juste.
        </p>

        <button className="btn btn-accent seo-inline-cta" onClick={onCta}>
          Estimer ce {brand.label} {model.label} →
        </button>

        <section className="seo-section">
          <h2 className="seo-h2">Ce qui fait varier la cote d'un {model.label} {motorLabel}</h2>
          <p className="seo-p">
            Le kilométrage et l'année de mise en circulation pèsent le plus lourd dans la cote.
            La motorisation {motorLabel} a sa propre dynamique de marché : elle peut être valorisée
            ou pénalisée selon les tendances et la réglementation en vigueur.
          </p>
          <p className="seo-p">
            L'état général, l'historique d'entretien et la finition ajustent ensuite la fourchette.
            VehicleIQ intègre tous ces critères pour produire une cote {model.label} défendable,
            pas une moyenne théorique.
          </p>
        </section>

        <section className="seo-section">
          <h2 className="seo-h2">Autres motorisations du {brand.label} {model.label}</h2>
          <div className="cote-motor-row">
            {model.motors.map(mo => (
              <button key={mo}
                className={`cote-motor-pill${mo === motorSlug ? " active" : ""}`}
                onClick={() => viqNavigate(`/cote/${brandKey}/${modelSlug}/${mo}`)}>
                {MOTOR_LABELS[mo]}
              </button>
            ))}
          </div>
        </section>

        <section className="seo-section">
          <h2 className="seo-h2">Questions fréquentes</h2>
          <div className="seo-faq-item">
            <h3 className="seo-h3">La cote {model.label} {motorLabel} est-elle fiable pour une reprise ?</h3>
            <p className="seo-p">
              Oui. La cote s'appuie sur les prix réels du marché et s'accompagne d'un indice de confiance
              et d'une fourchette basse/haute, ce qui sécurise un prix de reprise.
            </p>
          </div>
          <div className="seo-faq-item">
            <h3 className="seo-h3">À quelle fréquence la cote est-elle mise à jour ?</h3>
            <p className="seo-p">
              En continu. Le moteur VIQ.AI® observe les annonces actives en temps réel,
              contrairement à un barème mensuel.
            </p>
          </div>
        </section>

        <div className="seo-related">
          <div className="seo-related-title">Aller plus loin</div>
          <div className="seo-related-links">
            <button className="seo-related-link" onClick={() => viqNavigate("/cote/" + brandKey)}>Toute la cote {brand.label} →</button>
            <button className="seo-related-link" onClick={() => viqNavigate("/estimation-automobile-professionnelle")}>Cote automobile professionnelle →</button>
            <button className="seo-related-link" onClick={() => viqNavigate("/controle-vehicule-vin-plaque")}>Contrôle VIN / Plaque →</button>
          </div>
        </div>
      </article>
    </SeoPageLayout>
  );
}


// ─── Météo compacte (réutilise le pipeline adresse→data.gouv→open-meteo) ──────
function wmoIcon(code) {
  if (code === 0) return "☀️";
  if (code <= 2) return "🌤️";
  if (code === 3) return "☁️";
  if (code <= 48) return "🌫️";
  if (code <= 67) return "🌧️";
  if (code <= 77) return "🌨️";
  if (code <= 82) return "🌦️";
  if (code <= 86) return "🌨️";
  return "⛈️";
}
function WeatherMini() {
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = JSON.parse(localStorage.getItem("viq_profile") || "{}");
        const addr = saved.address || "";
        const cached = JSON.parse(localStorage.getItem("viq_weather") || "null");
        if (cached && cached.addr === addr && (Date.now() - cached.ts) < 6 * 3600 * 1000) {
          if (!cancelled) setWeather(cached.data); return;
        }
        let lat = null, lng = null, city = "";
        if (addr) {
          const g = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(addr)}&limit=1`);
          if (g.ok) { const gd = await g.json(); const f = (gd.features || [])[0]; if (f) { [lng, lat] = f.geometry.coordinates; city = f.properties.city || f.properties.name || ""; } }
        }
        if (lat == null) { lat = 49.8489; lng = 3.2876; city = "Saint-Quentin"; } // repli
        const w = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current_weather=true&timezone=Europe%2FParis`);
        if (!w.ok) return; const wd = await w.json(); if (!wd.current_weather) return;
        const data = { temp: Math.round(wd.current_weather.temperature), code: wd.current_weather.weathercode, city };
        if (!cancelled) setWeather(data);
        localStorage.setItem("viq_weather", JSON.stringify({ addr, ts: Date.now(), data }));
      } catch (e) { /* la météo est décorative */ }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!weather) return null;
  return (
    <div title={weather.city ? `Météo · ${weather.city}` : "Météo"} style={{ display: "flex", alignItems: "center", gap: ".4rem", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 12, padding: ".5rem .8rem", whiteSpace: "nowrap" }}>
      <span style={{ fontSize: "1.05rem", lineHeight: 1 }}>{wmoIcon(weather.code)}</span>
      <span style={{ fontWeight: 800, color: "var(--text)", fontFamily: "var(--font-head)" }}>{weather.temp}°</span>
      {weather.city && <span style={{ fontSize: ".72rem", color: "var(--muted)" }}>{weather.city}</span>}
    </div>
  );
}

// ─── AppHome — page d'accueil dédiée de l'application (user.vehicleiq.fr) ─────
// Remplace la landing marketing sur le sous-domaine applicatif : hub d'actions
// pour l'utilisateur connecté, écran de connexion ciblé sinon.
// ═══════════════════════════════════════════════════════════════════════════
// v33 — DOSSIER VIQ : le concept central de la refonte user.vehicleiq.fr.
// Un dossier = un véhicule, identifié par VIN (repli sur la plaque). Regroupe
// TOUTES les entrées d'historique (Contrôle, Estimation) concernant ce même
// véhicule en une seule fiche, avec timeline et prochaine action recommandée.
// Construit entièrement côté client à partir de l'historique existant
// (searches/viq_history) — aucune table, route ou API nouvelle. Les entrées
// "achat" (recherches multi-véhicules, pas un véhicule précis) ne sont jamais
// regroupées. Les entrées sans VIN ni plaque exploitable deviennent des
// dossiers "orphelins" à une seule entrée (repli demandé et validé).
function _normVin(v) { return String(v || "").toUpperCase().replace(/\s/g, ""); }
function _normPlaque(p) { return String(p || "").toUpperCase().replace(/[\s-]/g, ""); }

function _dossierKeyOf(h) {
  const r = h.results || h.result || {};
  const d = h.data || h.answers || {};
  const vin = _normVin(r.vin || d.vin);
  const plaque = _normPlaque(d.plaque || r.plaque);
  return vin || plaque || null;
}

function _leadKeyOf(lead) {
  const d = (typeof lead.data === "object" && lead.data) || {};
  const vin = _normVin(d.vin);
  const plaque = _normPlaque(d.plaque || d.immat);
  return vin || plaque || null;
}

// v34 — CORRECTIF MAJEUR : le Dossier VIQ ignorait complètement les
// formulaires clients (pro_leads) — un véhicule reçu via lead, avec photos,
// score et HistoVec déjà transmis par le client, se voyait proposer les mêmes
// actions "à faire" que si rien n'existait. Les leads sont désormais LA
// source prioritaire (score, photos, HistoVec, points de vigilance) : ce que
// le client a déjà fourni ne doit jamais être redemandé.
function _finalizeDossier(key, entries, leadsForVehicle) {
  const sorted = entries.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
  const checkEntries = sorted.filter(e => e.type === "check");
  const sellEntries = sorted.filter(e => e.type === "sell");
  const checkEntry = checkEntries[checkEntries.length - 1] || null;
  const sellEntry = sellEntries[sellEntries.length - 1] || null;
  const latest = sorted[sorted.length - 1];
  // Lead le plus récent pour ce véhicule (une demande "cartegrise" n'a pas de
  // score/photos exploitables ici, on privilégie estimation/achat).
  const leads = (leadsForVehicle || []).slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const lead = leads.filter(l => l.type === "estimation" || l.type === "achat").pop() || leads[leads.length - 1] || null;

  // Fusion progressive : une donnée acquise dans une entrée ne doit jamais
  // disparaître si une entrée suivante ne la reporte pas (ex. HistoVec importé
  // lors du Contrôle doit rester visible après une Estimation séparée).
  let merged = {};
  sorted.forEach(e => { merged = { ...merged, ...(e.results || e.result || {}) }; });
  let mergedData = {};
  sorted.forEach(e => { mergedData = { ...mergedData, ...(e.data || e.answers || {}) }; });
  const leadData = lead ? ((typeof lead.data === "object" && lead.data) || {}) : {};

  const hasHistovec = !!(lead?.histovec?.found || merged.hv_found || merged.histovec_applied || merged.nb_proprietaires !== undefined || merged.ct_valide !== undefined);
  const hasCarVertical = !!(merged.cv_found || merged.carvertical_applied);
  const hasVisualInspection = !!(merged.visual_inspection && merged.visual_inspection.zones);
  const hasPhotos = !!(lead?.has_photos) || hasVisualInspection;
  const photoCount = lead?.photo_count || 0;
  const hasPriceEstimate = !!sellEntry;
  const hasAnnonce = !!(sellEntry && (sellEntry.results || sellEntry.result || {}).annonce_texte);

  // Score : priorité au lead (déjà calculé, gratuit, cf. analyse automatique
  // des formulaires clients), repli sur le merged des entrées de recherche.
  const score = typeof lead?.viq_score === "number" ? lead.viq_score
    : (typeof merged.score === "number" ? merged.score : null);
  const knownIssues = (lead?.known_issues?.found) ? lead.known_issues : (merged.known_issues?.found ? merged.known_issues : null);

  // Statut "à traiter / traité" : calqué sur le statut du lead s'il existe
  // (source de vérité identique aux Formulaires clients) ; à défaut, un
  // dossier sans demande client est "traité" dès qu'une cote a été estimée,
  // sinon "à traiter".
  const status = lead ? (lead.status || "nouveau") : (hasPriceEstimate ? "traite" : "nouveau");

  let nextAction;
  if (!checkEntry && !lead) nextAction = { label: "Identifier le véhicule", type: "check" };
  else if (!hasHistovec) nextAction = { label: "Importer le rapport HistoVec", type: "histovec" };
  else if (!hasPriceEstimate) nextAction = { label: lead ? "Lancer la Cote" : "Estimer la cote de reprise et de revente", type: "sell" };
  else if (!hasAnnonce) nextAction = { label: "Générer l'annonce de revente", type: "annonce" };
  else nextAction = { label: "Suivre la vente", type: "sale" };

  const make = merged.make || mergedData.make || leadData.make || leadData.marque || "";
  const model = merged.model || mergedData.model || leadData.model || "";
  const year = merged.year || mergedData.year_sell || mergedData.year || leadData.year || "";

  return {
    key,
    isOrphan: key.startsWith("orphan_") || key.startsWith("lead_"),
    vin: merged.vin || mergedData.vin || leadData.vin || null,
    plaque: mergedData.plaque || merged.plaque || leadData.plaque || leadData.immat || null,
    make, model, year,
    label: `${make || "Véhicule"}${model ? " " + model : ""}${year ? " · " + year : ""}`,
    score, knownIssues, hasPhotos, photoCount, status,
    prixReprise: sellEntry ? (merged.argus_low || null) : null,
    prixVente: sellEntry ? (merged.prix_viq || merged.argus_mid || null) : null,
    marge: (merged.argus_mid && merged.argus_low) ? Math.round(merged.argus_mid - merged.argus_low) : null,
    hasHistovec, hasCarVertical, hasVisualInspection, hasPriceEstimate, hasAnnonce,
    nextAction,
    entries: sorted,
    lead, // référence complète pour les actions (Lancer la Cote, Voir les photos…)
    clientName: lead?.client_name || null,
    clientEmail: lead?.client_email || null,
    clientPhone: lead?.client_phone || null,
    lastActivity: lead?.created_at && (!latest || new Date(lead.created_at) > new Date(latest.date)) ? lead.created_at : (latest ? latest.date : (lead ? lead.created_at : null)),
    merged, mergedData,
  };
}

function groupIntoDossiers(historyEntries, leads) {
  const buckets = new Map();
  const list = [];
  // Les leads d'abord : ils définissent souvent la clé du dossier (VIN/plaque
  // transmis par le client), avant même qu'un Contrôle/Estimation existe.
  (leads || []).forEach(lead => {
    if (lead.type !== "estimation" && lead.type !== "achat") return; // cartegrise : pas un dossier véhicule
    const key = _leadKeyOf(lead) || ("lead_" + lead.id);
    if (!buckets.has(key)) buckets.set(key, { entries: [], leads: [] });
    buckets.get(key).leads.push(lead);
  });
  (historyEntries || []).forEach(h => {
    if (h.type === "buy") return; // recherche multi-véhicules, jamais un dossier
    const key = _dossierKeyOf(h) || ("orphan_" + h.id);
    if (!buckets.has(key)) buckets.set(key, { entries: [], leads: [] });
    buckets.get(key).entries.push(h);
  });
  buckets.forEach((bucket, key) => list.push(_finalizeDossier(key, bucket.entries, bucket.leads)));
  list.sort((a, b) => new Date(b.lastActivity || 0) - new Date(a.lastActivity || 0));
  return list;
}

// ═══════════════════════════════════════════════════════════════════════════
// v33 — DossierVIQPage : page centrale de la nouvelle architecture. Remplace
// la navigation éclatée (Contrôler / Estimer / Mes cotes / Suivi de vente
// séparés) par UNE fiche par véhicule. Réutilise les panneaux existants
// (VINPanel, SellPanel) pour l'action elle-même — cette page organise et
// présente, elle ne réimplémente pas les appels API.
// ─── v43 — Onglet "Véhicule" du Dossier VIQ ────────────────────────────────
// Règle demandée : "depuis un dossier viq, le professionnel puisse... retrouver
// toutes les informations du véhicule". Réutilise exactement les mêmes noms de
// champs que l'affichage détaillé d'un Contrôle (voir plus haut dans ce
// fichier) pour rester cohérent visuellement avec le reste de l'app.
function DossierVehicleTab({ detail }) {
  const r = detail?.latestCheck || detail?.latestEstimation || {};
  const hasIdentity = r.make || r.model || r.vin;
  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      await exportEntryToPDF({ type: "check", results: r, data: { vin: detail?.dossier?.vin, plaque: detail?.dossier?.plaque } });
    } catch (e) { alert("Erreur export PDF : " + e.message); }
    finally { setPdfBusy(false); }
  };
  if (!hasIdentity) {
    return (
      <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: "var(--sub)" }}>
        <div style={{ fontSize: "1.6rem", marginBottom: ".5rem" }}>🚗</div>
        Aucune fiche technique disponible pour l'instant.<br />
        <span style={{ fontSize: ".82rem", color: "var(--muted)" }}>Lancez un Contrôle VIN/plaque pour la récupérer ici.</span>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ".6rem" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button className="btn btn-ghost btn-sm" disabled={pdfBusy} onClick={exportPdf}>
          {pdfBusy ? "Génération…" : "📄 Exporter en PDF"}
        </button>
      </div>
      <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".7rem .8rem" }}>
        <div style={{ fontSize: ".64rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: ".4rem", fontWeight: 600 }}>📋 Identité officielle</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: ".35rem 1rem", fontSize: ".82rem" }}>
          {r.make && <div><span style={{ color: "var(--muted)" }}>Marque : </span><strong>{r.make}</strong></div>}
          {r.model && <div><span style={{ color: "var(--muted)" }}>Modèle : </span><strong>{r.model}</strong></div>}
          {r.version && r.version !== "—" && <div><span style={{ color: "var(--muted)" }}>Version : </span><strong>{r.version}</strong></div>}
          {r.year && <div><span style={{ color: "var(--muted)" }}>Année : </span><strong>{r.year}</strong></div>}
          {r.first_registration_date && <div><span style={{ color: "var(--muted)" }}>1ère immat. : </span><strong>{r.first_registration_date}</strong></div>}
          {r.color && <div><span style={{ color: "var(--muted)" }}>Couleur : </span><strong>🎨 {r.color}</strong></div>}
          {r.vin && <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "var(--muted)" }}>VIN : </span><strong style={{ fontFamily: "var(--font-mono)", fontSize: ".76rem" }}>{r.vin}</strong></div>}
          {r.type_mine && <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "var(--muted)" }}>Type Mine (CNIT) : </span><strong style={{ fontFamily: "var(--font-mono)", fontSize: ".76rem" }}>{r.type_mine}</strong></div>}
          {r.genre && <div><span style={{ color: "var(--muted)" }}>Genre : </span><strong>{r.genre}</strong></div>}
          {r.carrosserie && <div><span style={{ color: "var(--muted)" }}>Carrosserie : </span><strong>{r.carrosserie}</strong></div>}
        </div>
      </div>

      <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".7rem .8rem" }}>
        <div style={{ fontSize: ".64rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: ".4rem", fontWeight: 600 }}>⚙️ Caractéristiques techniques</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: ".35rem 1rem", fontSize: ".82rem" }}>
          {(r.fuel || r.engine) && <div><span style={{ color: "var(--muted)" }}>Carburant : </span><strong>⛽ {r.fuel || r.engine}</strong></div>}
          {(r.transmission || r.gearbox) && <div><span style={{ color: "var(--muted)" }}>Boîte : </span><strong>⚙️ {r.transmission || r.gearbox}</strong></div>}
          {(r.power_cv || r.power_kw) && <div><span style={{ color: "var(--muted)" }}>Puissance : </span><strong>⚡ {r.power_cv ? r.power_cv + " cv" : r.power_kw + " kW"}</strong></div>}
          {r.engine_code && <div><span style={{ color: "var(--muted)" }}>Code moteur : </span><strong style={{ fontFamily: "var(--font-mono)", fontSize: ".76rem" }}>🔧 {r.engine_code}</strong></div>}
          {r.cylinders && <div><span style={{ color: "var(--muted)" }}>Cylindres : </span><strong>{r.cylinders}</strong></div>}
          {r.weight && <div><span style={{ color: "var(--muted)" }}>Poids : </span><strong>⚖️ {r.weight}</strong></div>}
          {r.co2 && <div><span style={{ color: "var(--muted)" }}>CO₂ : </span><strong>🌿 {r.co2} g/km</strong></div>}
          {r.doors && <div><span style={{ color: "var(--muted)" }}>Portes : </span><strong>🚪 {r.doors}</strong></div>}
        </div>
      </div>

      {((r.mileage || r.km_reported) || (r.owners || r.nb_owners) || r.lastService || r.ctDate) && (
        <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".7rem .8rem" }}>
          <div style={{ fontSize: ".64rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: ".4rem", fontWeight: 600 }}>🕒 Historique & usage</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: ".35rem 1rem", fontSize: ".82rem" }}>
            {(r.mileage || r.km_reported) && <div><span style={{ color: "var(--muted)" }}>Kilométrage : </span><strong>🛣 {Number(r.mileage || r.km_reported).toLocaleString("fr-FR")} km</strong></div>}
            {(r.owners || r.nb_owners) && <div><span style={{ color: "var(--muted)" }}>Propriétaires : </span><strong>👤 {r.owners || r.nb_owners}</strong></div>}
            {r.lastService && <div><span style={{ color: "var(--muted)" }}>Dern. révision : </span><strong>🔧 {r.lastService}</strong></div>}
            {r.ctDate && <div><span style={{ color: "var(--muted)" }}>CT valide jusqu'à : </span><strong>📋 {r.ctDate}</strong></div>}
          </div>
        </div>
      )}

      {Array.isArray(r.registrations) && r.registrations.length > 0 && (
        <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 8, padding: ".7rem .8rem" }}>
          <div style={{ fontSize: ".64rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: ".4rem", fontWeight: 600 }}>
            📜 Historique d'immatriculations ({r.registrations.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".25rem", paddingLeft: ".5rem", borderLeft: "2px solid var(--accent)" }}>
            {r.registrations.slice(0, 8).map((reg, i) => (
              <div key={i} style={{ fontSize: ".76rem", display: "flex", gap: ".7rem" }}>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--muted)", flexShrink: 0, minWidth: 80, fontWeight: 600 }}>{reg.date}</span>
                <span>{reg.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── v43 — Onglet "Marché" du Dossier VIQ ──────────────────────────────────
// Règle demandée : "situer son véhicule par rapport au marché". Affiche la
// fourchette Argus + les plateformes recommandées de la dernière cotation, et
// intègre un simulateur de rachat compact qui réutilise la cote DÉJÀ CONNUE
// de ce dossier (pas de nouvel appel réseau) — utile pour évaluer un rachat
// similaire sans quitter le dossier.
// v53 — Assistant compact dans le Dossier VIQ : navigation en langage naturel
// entre les onglets (HistoVec, CarVertical, Marché, Prospecter, Suivi de
// vente, Comptabilité) ou reprise directe de la prochaine action suggérée.
// Portée volontairement locale (mots-clés, pas d'appel serveur) — le dossier
// n'a que 7 destinations possibles, un classement IA serait disproportionné.
function DossierAssistantBar({ onNavigate, onContinue }) {
  const [q, setQ] = useState("");
  const run = () => {
    const s = q.trim().toLowerCase();
    if (!s) return;
    if (/(continue|continuer|suivant|prochaine action)/.test(s)) { onContinue(); setQ(""); return; }
    const map = [
      [/histovec/, "vehicle"],
      [/carvertical/, "carvertical"],
      [/(march[ée]|cote|prix|argus|rachat)/, "market"],
      [/(prospect|propos|client|lead|contact)/, "propose"],
      [/(vente|vendu|vendre|annonce)/, "sale"],
      [/(compt|tva|marge)/, "accounting"],
    ];
    const hit = map.find(([re]) => re.test(s));
    onNavigate(hit ? hit[1] : "overview");
    setQ("");
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: ".5rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 24, padding: ".25rem .25rem .25rem 1rem", marginBottom: "1rem" }}>
      <span style={{ fontSize: ".95rem" }}>🤖</span>
      <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && run()}
        placeholder="Demandez-moi : « importer le HistoVec », « générer l'annonce », « continuer »…"
        style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontSize: ".8rem", padding: ".5rem 0", color: "var(--text)" }} />
      <button onClick={run} disabled={!q.trim()}
        style={{ flexShrink: 0, border: "none", borderRadius: 20, padding: ".45rem .9rem", background: "var(--accent)", color: "#fff", fontWeight: 700, fontSize: ".78rem", cursor: q.trim() ? "pointer" : "default", opacity: q.trim() ? 1 : .5 }}>
        →
      </button>
    </div>
  );
}

function DossierMarketTab({ detail }) {
  const rapport = detail?.cotation?.rapport || {};
  const platforms = (rapport.plateformes || rapport.platforms || []).slice(0, 5);
  const { argus_low, argus_high, prix_conseille } = detail?.cotation || {};
  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const d = detail?.dossier || {};
      await exportEntryToPDF({ type: "sell", results: rapport, data: { make: d.make, model: d.model, year_sell: d.year, fuel_sell: d.fuel } });
    } catch (e) { alert("Erreur export PDF : " + e.message); }
    finally { setPdfBusy(false); }
  };

  const [prixAchat, setPrixAchat] = useState("");
  const [transport, setTransport] = useState("");
  const eur = (v) => (v || v === 0) ? Math.round(Number(v)).toLocaleString("fr-FR") + " €" : "—";

  if (!detail?.cotation?.realisee) {
    return (
      <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: "var(--sub)" }}>
        <div style={{ fontSize: "1.6rem", marginBottom: ".5rem" }}>📊</div>
        Pas encore de cotation pour ce véhicule.<br />
        <span style={{ fontSize: ".82rem", color: "var(--muted)" }}>Estimez la cote pour situer ce véhicule par rapport au marché.</span>
      </div>
    );
  }

  const margeMid = prixAchat ? Math.round((prix_conseille || 0) - Number(prixAchat) - (Number(transport) || 0)) : null;

  // v93 — Règle : reconstruire un objet "cur" équivalent à celui utilisé côté
  // Dashboard Pro, à partir des données déjà disponibles dans ce Dossier VIQ
  // — MarketIntelligenceSection ne dépend que de cette forme d'objet.
  const d = detail?.dossier || {};
  const marketCur = {
    makeModel: [d.make, d.model].filter(Boolean).join(" ") || undefined,
    viqPrice: prix_conseille, marche: prix_conseille,
    argusLow: argus_low, argusHigh: argus_high, argusMid: prix_conseille,
    km: rapport.km || d.km, score: rapport.score || detail?.score,
    year: d.year,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button className="btn btn-ghost btn-sm" disabled={pdfBusy} onClick={exportPdf}>
          {pdfBusy ? "Génération…" : "📄 Exporter la cotation en PDF"}
        </button>
      </div>
      {(argus_low != null && argus_high != null) && (
        <div style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".9rem 1rem" }}>
          <div style={{ fontSize: ".7rem", color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".5rem", fontWeight: 700 }}>Positionnement marché</div>
          <div style={{ position: "relative", height: 6, borderRadius: 4, background: "linear-gradient(90deg, var(--border), var(--green), var(--border))", marginBottom: ".4rem" }}>
            <div style={{ position: "absolute", top: -4, left: "50%", transform: "translateX(-50%)", width: 14, height: 14, borderRadius: "50%", background: "var(--accent)", border: "2px solid var(--white)", boxShadow: "0 1px 4px rgba(0,0,0,.3)" }} title="Prix conseillé" />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".76rem", color: "var(--sub)" }}>
            <span>{eur(argus_low)}</span>
            <strong style={{ color: "var(--accent)" }}>{eur(prix_conseille)} conseillé</strong>
            <span>{eur(argus_high)}</span>
          </div>
        </div>
      )}

      {platforms.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, fontSize: ".85rem", marginBottom: ".5rem" }}>🎯 Plateformes recommandées</div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
            {platforms.map((p, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: ".55rem .8rem", fontSize: ".82rem" }}>
                <span><strong>#{i + 1} {p.name}</strong>{p.type && <span style={{ color: "var(--muted)", fontSize: ".72rem", marginLeft: ".4rem" }}>{p.type}</span>}</span>
                {p.score_vente != null && <span style={{ fontWeight: 700, color: p.score_vente >= 88 ? "var(--green)" : "var(--accent)" }}>{p.score_vente}%</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* v93 — Règle : reprend Évolution marché web / Signal Marché Live /
          Analyse de Risque Acheteur du Dashboard Pro, ici dans le Dossier VIQ. */}
      <MarketIntelligenceSection cur={marketCur} idx={detail?.dossier?.id || 0} />

      {/* Simulateur de rachat compact — réutilise la cote déjà connue du dossier */}
      <div style={{ background: "var(--accent-soft)", border: "1px solid var(--accent)", borderRadius: 10, padding: ".9rem 1rem" }}>
        <div style={{ fontWeight: 700, fontSize: ".82rem", color: "var(--accent)", marginBottom: ".5rem" }}>🎯 Simuler un rachat similaire</div>
        <div style={{ fontSize: ".76rem", color: "var(--sub)", marginBottom: ".6rem" }}>
          À quel prix d'achat un véhicule comparable resterait-il rentable, sur la base de la revente France déjà estimée ici ({eur(prix_conseille)}) ?
        </div>
        <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginBottom: ".6rem" }}>
          <input type="number" placeholder="Prix d'achat envisagé" value={prixAchat} onChange={e => setPrixAchat(e.target.value)}
            style={{ flex: "1 1 160px", padding: ".55rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--white)", fontSize: ".84rem" }} />
          <input type="number" placeholder="Transport (€, optionnel)" value={transport} onChange={e => setTransport(e.target.value)}
            style={{ flex: "1 1 160px", padding: ".55rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--white)", fontSize: ".84rem" }} />
        </div>
        {margeMid !== null && (
          <div style={{ textAlign: "center", padding: ".6rem", background: "var(--white)", borderRadius: 8 }}>
            <div style={{ fontSize: ".68rem", color: "var(--muted)", textTransform: "uppercase" }}>Marge nette estimée</div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.5rem", fontWeight: 700, color: margeMid >= 0 ? "var(--green)" : "var(--red, #b03a3a)" }}>
              {margeMid >= 0 ? "+" : ""}{eur(margeMid)}
            </div>
          </div>
        )}
        <div style={{ fontSize: ".64rem", color: "var(--muted)", marginTop: ".5rem" }}>
          Estimation indicative — n'inclut pas le malus/coût d'immatriculation. Pour un calcul complet avec malus CO₂, utilisez le Scan d'opportunité depuis le tableau de bord.
        </div>
      </div>

      {/* v53 — Recherche achat : accès direct à l'outil de recommandations
          d'achat depuis le Marché du dossier, pour comparer avec ce que
          pourrait proposer un véhicule similaire actuellement en vente. */}
      <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("viq-open-panel", { detail: "buy" }))}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: ".5rem", padding: ".8rem", borderRadius: 10, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontWeight: 700, fontSize: ".84rem", cursor: "pointer" }}>
        🔍 Rechercher des véhicules similaires à l'achat
      </button>
    </div>
  );
}

// ─── v43 — Onglet "Vente" du Dossier VIQ ───────────────────────────────────
// Règle demandée : "suivre la vente et toutes les décisions qui vont avec"
// DEPUIS le dossier. Formulaire réel (pas un simple affichage) — pré-rempli
// si une vente existe déjà (mode édition), sinon suggère le prix conseillé.
function DossierSaleTab({ detail, dossierId, onSaved }) {
  const existing = detail?.saleInfo;
  const suggested = detail?.cotation?.prix_conseille;
  const [f, setF] = useState({
    sold_price: existing?.sold_price ? String(existing.sold_price) : (suggested ? String(Math.round(suggested)) : ""),
    sold_date: existing?.sold_date ? String(existing.sold_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
    platform: existing?.platform || "",
    purchase_price: existing?.purchase_price ? String(existing.purchase_price) : "",
    client_type: existing?.client_type || "unknown",
    pickup_method: existing?.pickup_method || "",
    notes: existing?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => { setF(p => ({ ...p, [k]: v })); setSaved(false); };

  const lbl = { fontSize: ".7rem", fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".3rem", display: "block" };
  const fld = { width: "100%", padding: ".55rem .7rem", borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".86rem", boxSizing: "border-box" };

  const margin = (f.sold_price && f.purchase_price) ? (Number(f.sold_price) - Number(f.purchase_price)) : null;

  const save = async () => {
    setErr(null);
    if (!f.sold_price || isNaN(parseInt(f.sold_price, 10))) { setErr("Indiquez le prix de vente."); return; }
    setSaving(true);
    try {
      const d = detail.dossier;
      const r = await apiPost("/api/sale-followup", {
        dossier_id: dossierId,
        vehicle_data: { make: d.make, model: d.model, year: d.year, vin: d.vin, plaque: d.plaque },
        estimation_data: detail.latestEstimation || {},
        sold_price: parseInt(f.sold_price, 10),
        sold_date: f.sold_date || null,
        client_type: f.client_type,
        notes: f.notes || null,
        platform: f.platform || null,
        purchase_price: f.purchase_price || null,
        pickup_method: f.pickup_method || null,
      });
      if (r?.success) { setSaved(true); onSaved && onSaved(); }
      else setErr(r?.error || "Échec de l'enregistrement.");
    } catch (e) { setErr(e?.message || "Échec de l'enregistrement."); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ".9rem" }}>
      {!existing && suggested && (
        <div style={{ fontSize: ".78rem", color: "var(--sub)", background: "var(--card)", borderRadius: 8, padding: ".6rem .8rem" }}>
          💡 Prix conseillé au moment de la cotation : <strong>{Math.round(suggested).toLocaleString("fr-FR")} €</strong> (pré-rempli ci-dessous, modifiable).
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".7rem" }}>
        <div><label style={lbl}>Prix de vente réel *</label><input type="number" style={fld} value={f.sold_price} onChange={e => set("sold_price", e.target.value)} /></div>
        <div><label style={lbl}>Date de vente</label><input type="date" style={fld} value={f.sold_date} onChange={e => set("sold_date", e.target.value)} /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".7rem" }}>
        <div><label style={lbl}>Prix d'achat (pour marge)</label><input type="number" style={fld} value={f.purchase_price} onChange={e => set("purchase_price", e.target.value)} /></div>
        <div><label style={lbl}>Plateforme</label><input style={fld} value={f.platform} onChange={e => set("platform", e.target.value)} placeholder="LeBonCoin, AutoScout24…" /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".7rem" }}>
        <div><label style={lbl}>Type de client</label>
          <select style={fld} value={f.client_type} onChange={e => set("client_type", e.target.value)}>
            <option value="unknown">Non précisé</option><option value="particulier">Particulier</option><option value="pro">Professionnel</option>
          </select>
        </div>
        <div><label style={lbl}>Retrait</label>
          <select style={fld} value={f.pickup_method} onChange={e => set("pickup_method", e.target.value)}>
            <option value="">Non précisé</option><option value="concession">Sur place</option><option value="expedition">Expédition</option>
          </select>
        </div>
      </div>
      <div><label style={lbl}>Notes</label><textarea style={{ ...fld, minHeight: 60, resize: "vertical" }} value={f.notes} onChange={e => set("notes", e.target.value)} /></div>

      {margin !== null && (
        <div style={{ textAlign: "center", padding: ".7rem", background: margin >= 0 ? "rgba(46,111,92,.08)" : "rgba(176,90,58,.08)", borderRadius: 8 }}>
          <span style={{ fontSize: ".72rem", color: "var(--muted)" }}>Marge : </span>
          <strong style={{ color: margin >= 0 ? "var(--green)" : "var(--red, #b03a3a)", fontSize: "1.1rem" }}>{margin >= 0 ? "+" : ""}{margin.toLocaleString("fr-FR")} €</strong>
        </div>
      )}

      {err && <div style={{ fontSize: ".8rem", color: "var(--red, #c0392b)" }}>⚠ {err}</div>}
      {saved && <div style={{ fontSize: ".8rem", color: "var(--green)" }}>✓ Enregistré.</div>}
      <button className="btn btn-accent" disabled={saving} onClick={save} style={{ justifyContent: "center" }}>
        {saving ? "Enregistrement…" : existing ? "Mettre à jour la vente" : "Enregistrer la vente"}
      </button>
    </div>
  );
}

// v59 — Studio photo IA : détourage + fond studio + filigrane VIQ.AI + QR du
// pro, traité intégralement dans le navigateur (voir processListingPhoto
// plus haut). "On propose ça nous-même" — pas de service tiers payant.
function ListingPhotoStudio({ photos, dossierId, onSaved }) {
  // v79 — Règle : détourage IA + arrière-plans désactivés (rendu jugé pas
  // assez professionnel) — remplacé par une simple mention "Sur demande".
  // Le traitement lui-même (processListingPhoto/LISTING_BACKGROUNDS) n'est
  // plus déclenché depuis cet écran, mais reste défini plus haut dans le
  // fichier au cas où il faudrait le réactiver plus tard.
  if (!photos || !photos.length) return null;

  return (
    <div style={{ margin: "1rem 0", padding: ".9rem 1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10 }}>
      <div style={{ fontWeight: 700, fontSize: ".82rem", marginBottom: ".3rem" }}>✨ Photos pour l'annonce</div>
      <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.55 }}>
        Retouche professionnelle des photos (détourage, arrière-plan) — <strong>disponible sur demande</strong>. Contactez-nous pour en bénéficier.
      </div>
    </div>
  );
}

function DisabledListingPhotoStudioUnused({ photos, dossierId, onSaved }) {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState("");
  const [results, setResults] = useState(null); // [{ original, processed, error }]
  const [selected, setSelected] = useState({});
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [background, setBackground] = useState("beige"); // v77 — 5 arrière-plans au choix
  const MAX_BATCH = 6;

  const toggle = (i) => setSelected(p => ({ ...p, [i]: !p[i] }));
  const selectedIdx = photos.map((_, i) => i).filter(i => selected[i]);

  const run = async () => {
    const idxs = (selectedIdx.length ? selectedIdx : photos.map((_, i) => i)).slice(0, MAX_BATCH);
    if (!idxs.length) return;
    setProcessing(true); setError(null); setResults(null); setSaved(false);
    try {
      const qr = await getProLeadQRDataUrl();
      const out = [];
      for (const i of idxs) {
        setProgress(`Photo ${out.length + 1}/${idxs.length}…`);
        try {
          const processed = await processListingPhoto(photos[i], {
            proQrDataUrl: qr, background,
            onProgress: (step) => setProgress(`Photo ${out.length + 1}/${idxs.length} — ${step}`),
          });
          out.push({ original: photos[i], processed });
        } catch (e) {
          out.push({ original: photos[i], processed: null, error: e?.message || "échec" });
        }
        setResults([...out]);
      }
    } catch (e) {
      setError(e?.message || "Traitement impossible — réessayez dans un instant.");
    } finally { setProcessing(false); setProgress(""); }
  };

  const saveAll = async () => {
    if (!results) return;
    const images = results.filter(r => r.processed).map(r => r.processed);
    if (!images.length || !dossierId) return;
    try {
      await apiPost(`/api/dossiers/${dossierId}/files`, { type: "photo_annonce", payload: { images, count: images.length } });
      setSaved(true);
      onSaved && onSaved();
    } catch (e) { setError("Enregistrement impossible : " + (e?.message || "erreur réseau")); }
  };

  if (!photos || !photos.length) return null;

  return (
    <div style={{ margin: "1rem 0", padding: ".9rem 1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10 }}>
      <div style={{ fontWeight: 700, fontSize: ".82rem", marginBottom: ".2rem" }}>✨ Photos pour l'annonce (IA)</div>
      <div style={{ fontSize: ".74rem", color: "var(--sub)", lineHeight: 1.5, marginBottom: ".7rem" }}>
        Détourage automatique, fond neutre, filigrane VIQ.AI et votre QR code — traité ici même, aucune photo envoyée à un service tiers.
      </div>

      {!results && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", marginBottom: ".6rem" }}>
            {photos.map((p, i) => (
              <div key={i} onClick={() => toggle(i)} style={{ position: "relative", cursor: "pointer", width: 60, height: 60, borderRadius: 8, overflow: "hidden", border: "2px solid " + (selected[i] ? "var(--accent)" : "transparent") }}>
                <img src={p} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                {selected[i] && (
                  <div style={{ position: "absolute", top: 2, right: 2, background: "var(--accent)", color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: ".6rem", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>✓</div>
                )}
              </div>
            ))}
          </div>
          <div style={{ fontSize: ".7rem", color: "var(--muted)", marginBottom: ".7rem" }}>
            {selectedIdx.length ? `${selectedIdx.length} photo(s) sélectionnée(s)` : `Aucune sélection = les ${Math.min(photos.length, MAX_BATCH)} premières`} · {MAX_BATCH} maximum par lot (traitement local, prend quelques secondes par photo)
          </div>
          {/* v77 — Règle : 5 arrière-plans au choix (2 couleur + 3 showroom). */}
          <div style={{ marginBottom: ".7rem" }}>
            <div style={{ fontSize: ".7rem", color: "var(--sub)", marginBottom: ".35rem" }}>Arrière-plan :</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".4rem" }}>
              {Object.entries(LISTING_BACKGROUNDS).map(([key, bg]) => (
                <button key={key} type="button" onClick={() => setBackground(key)}
                  style={{ fontSize: ".72rem", fontWeight: 600, padding: ".3rem .65rem", borderRadius: 20, cursor: "pointer",
                    border: "1.5px solid " + (background === key ? "var(--accent)" : "var(--border)"),
                    background: background === key ? "var(--accent-soft)" : "var(--card)",
                    color: background === key ? "var(--accent)" : "var(--sub)" }}>
                  {background === key ? "✓ " : ""}{bg.label}
                </button>
              ))}
            </div>
          </div>
          <button className="btn btn-accent" disabled={processing} onClick={run} style={{ justifyContent: "center" }}>
            {processing ? (progress || "Traitement…") : "✨ Traiter ces photos"}
          </button>
          {error && <div style={{ marginTop: ".5rem", fontSize: ".78rem", color: "var(--red, #c0392b)" }}>{error}</div>}
        </>
      )}

      {results && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: ".6rem", marginBottom: ".8rem" }}>
            {results.map((r, i) => (
              <div key={i} style={{ borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)" }}>
                {r.processed ? (
                  <>
                    <img src={r.processed} alt="" style={{ width: "100%", display: "block", background: "var(--bg)" }} />
                    <a href={r.processed} download={`viq-annonce-${i + 1}.jpg`} style={{ display: "block", textAlign: "center", fontSize: ".72rem", padding: ".3rem", color: "var(--accent)", textDecoration: "none", background: "var(--white)" }}>
                      ⬇ Télécharger
                    </a>
                  </>
                ) : (
                  <div style={{ aspectRatio: "4/3", display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".7rem", color: "var(--muted)", padding: ".5rem", textAlign: "center" }}>
                    Échec{r.error ? ` : ${r.error}` : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: ".6rem" }}>
            <button className="btn btn-outline" style={{ flex: 1, justifyContent: "center" }} onClick={() => { setResults(null); setSaved(false); }}>
              Recommencer
            </button>
            <button className="btn btn-accent" style={{ flex: 1, justifyContent: "center" }} disabled={saved} onClick={saveAll}>
              {saved ? "✓ Enregistré dans le dossier" : "Enregistrer dans le dossier"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// v58 — Miroir client de DOSSIER_WORKFLOW_STEPS (database.js) : permet à
// "Précédent"/"Suivant" de parcourir les étapes SANS toucher au skip serveur
// (une étape déjà terminée, pas seulement ignorée, doit aussi être
// consultable en arrière — un mécanisme basé sur skip/unskip n'aurait aucun
// effet visible dans ce cas).
const WORKFLOW_STEPS_CLIENT = [
  { type: "check", label: "Identifier le véhicule", hint: "Renseignez le VIN ou la plaque d'immatriculation pour rattacher automatiquement l'historique officiel à ce Dossier VIQ." },
  { type: "histovec", label: "Importer le rapport HistoVec" },
  { type: "carvertical", label: "Importer le rapport CarVertical" },
  { type: "photos", label: "Ajouter des photos du véhicule", hint: "Des photos nettes (extérieur, intérieur, compteur) permettent à l'analyse IA d'évaluer l'état réel du véhicule et d'ajuster le score en conséquence." },
  { type: "sell", label: "Estimer la cote", hint: "VIQ.AI® calcule une cote en temps réel à partir des annonces comparables du marché — la base indispensable pour fixer un prix de vente cohérent." },
  { type: "annonce", label: "Générer l'annonce de revente", hint: "Une annonce rédigée automatiquement à partir des données déjà collectées, prête à publier sur vos plateformes habituelles." },
  { type: "sale", label: "Renseigner le prix de vente", hint: "Indiquez le prix de vente réel une fois le véhicule cédé — cela alimente le suivi de marge et affine la calibration de VIQ.AI® pour vos prochaines cotations." },
];

// v67 — Regroupement visuel des Dossiers VIQ d'un même lot (règle 5) : les
// cartes individuelles (déjà construites, inchangées) sont simplement
// enveloppées ici, avec un résumé dépliable.
function LotDossierGroup({ items, cards, onDissolve, onRename, onRemoveMember, busy }) {
  const [open, setOpen] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(items[0]?.lot_name || "");
  const names = items.map(d => d.label).join(" · ");
  const lotId = items[0]?.lot_id;
  return (
    <div style={{ border: "2px solid var(--accent)", borderRadius: 14, padding: ".8rem", background: "rgba(168,128,58,.04)", maxWidth: "100%", boxSizing: "border-box" }}>
      <div className="viq-lot-header" style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".3rem", flexWrap: "wrap" }}>
        <div className="viq-lot-header-name" onClick={() => setOpen(o => !o)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: ".5rem", minWidth: 0 }}>
          <span style={{ fontSize: ".82rem", fontWeight: 800, color: "var(--accent)", whiteSpace: "nowrap" }}>
            📦 {items[0]?.lot_name || `Lot de ${items.length} véhicules`}
          </span>
          <span style={{ fontSize: ".8rem", color: "var(--muted)" }}>{open ? "▲" : "▼"}</span>
        </div>
        {/* v69 — Règle 2 : Renommer (optionnel) + Dissocier — agissent uniquement sur la relation lot_id/dossier_lots. */}
        <div className="viq-lot-header-actions" style={{ display: "flex", gap: ".4rem", flexShrink: 0, flexWrap: "wrap", marginLeft: "auto" }}>
          {editingName ? (
            <>
              <input autoFocus value={nameDraft} onChange={e => setNameDraft(e.target.value)} placeholder="Nom du lot"
                style={{ fontSize: ".76rem", padding: ".25rem .5rem", borderRadius: 6, border: "1px solid var(--border)", width: 140 }}
                onClick={e => e.stopPropagation()} />
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={e => { e.stopPropagation(); onRename(lotId, nameDraft); setEditingName(false); }}>✓</button>
            </>
          ) : (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: ".7rem" }} disabled={busy} onClick={e => { e.stopPropagation(); setEditingName(true); }}>✏️ Renommer</button>
          )}
          <button className="btn btn-ghost btn-sm" style={{ fontSize: ".7rem", color: "var(--red, #c0392b)" }} disabled={busy}
            onClick={e => { e.stopPropagation(); if (window.confirm(`Dissocier ce lot ? Les ${items.length} véhicules redeviennent des dossiers individuels (aucune donnée véhicule n'est modifiée).`)) onDissolve(lotId); }}>
            🔓 Dissocier le lot
          </button>
        </div>
      </div>
      {/* v71 — La liste des véhicules doit passer à la ligne (pas de troncature/
          ellipsis, pas de débordement) : bloc pleine largeur séparé de la ligne
          d'actions, qui contenait le débordement horizontal signalé. */}
      <div onClick={() => setOpen(o => !o)} style={{ cursor: "pointer", fontSize: ".76rem", color: "var(--sub)", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.5, marginBottom: open ? ".6rem" : 0 }}>
        {names}
      </div>
      {open && (
        <div style={{ display: "grid", gap: ".7rem" }}>
          {cards.map((card, i) => (
            <div key={items[i]?.id || i}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "-.4rem" }}>
                <button className="btn btn-ghost btn-sm" style={{ fontSize: ".66rem", color: "var(--muted)" }} disabled={busy}
                  onClick={() => onRemoveMember(items[i].id)}>
                  ✕ Retirer du lot
                </button>
              </div>
              {card}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// v67 — LotProcessingModal : module de traitement en LOT, INDÉPENDANT.
// Ne réutilise AUCUN composant du Contrôle/Estimation individuels
// (VINPanel/SellPanel) — nouvelle UI dédiée, branchée sur les routes du
// VehicleLotEngine (server.js) qui, elles, réutilisent en interne les MÊMES
// routes que le traitement individuel. Le traitement individuel n'est
// touché nulle part par ce composant.
// ═══════════════════════════════════════════════════════════════════════
function LotProcessingModal({ lotId, onClose, onSaved }) {
  const [lot, setLot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(null); // "estimate" | null — v69 : contrôle/analyse retirés (règle 4), traitements exclusivement individuels désormais
  const [lastResults, setLastResults] = useState(null);
  const [history, setHistory] = useState(null); // v68 — règle 7 : historique groupé du lot
  // v69 — Règle 5 : contexte de valorisation du lot (recherche métier — pas de
  // pourcentage unique universel documenté ; la pratique varie selon le
  // canal de vente, d'où le choix proposé à l'utilisateur plutôt qu'une
  // règle inventée). Reprise en lot pro-à-pro est le contexte le plus
  // fréquent pour un usage VehicleIQ, donc le défaut.
  const [savingValuations, setSavingValuations] = useState(false);
  const [valuationsSaved, setValuationsSaved] = useState(false);

  const loadState = () => {
    setLoading(true); setError(null);
    return apiGet(`/api/lot-engine/${lotId}/state`).then(r => {
      if (r?.success) setLot(r.lot);
      else setError(r?.error || "Lot introuvable.");
    }).catch(e => setError(e?.message || "Erreur réseau.")).finally(() => setLoading(false));
  };
  const loadHistory = () => {
    apiGet(`/api/lot-engine/${lotId}/history`).then(r => { if (r?.success) setHistory(r.log); }).catch(() => setHistory([]));
  };

  useEffect(() => { loadState(); loadHistory(); }, [lotId]);

  const runAction = async (action, path, extraBody) => {
    setRunning(action); setLastResults(null); setError(null);
    try {
      const r = await apiPost(`/api/lot-engine/${lotId}/${path}`, extraBody || {});
      if (r?.success) {
        setLastResults({ action, results: r.results });
        await loadState(); // v67 — Règle 4 : toujours recharger depuis le VehicleDossier après traitement.
        loadHistory();
        onSaved && onSaved();
      } else setError(r?.error || "Traitement impossible.");
    } catch (e) { setError(e?.message || "Traitement impossible."); }
    finally { setRunning(null); }
  };

  const eur = (v) => (v || v === 0) ? Math.round(Number(v)).toLocaleString("fr-FR") + " €" : "—";

  // v69 — Règle 5 : contextes identifiés par la recherche métier (remarketing
  // de flottes, enchères pro, reprise marchand, liquidation) — la pratique
  // constante dans tous les cas est qu'un lot se négocie avec une décote par
  // rapport à la somme des valeurs individuelles (jamais une prime), mais le
  // TAUX dépend du canal. Aucune de ces valeurs n'est une norme officielle :
  // ce sont des ordres de grandeur couramment observés, modifiables ici même.
  const VALUATION_CONTEXTS = [
    { id: "simple_sum", label: "Somme simple", hint: "Véhicules vendus séparément, juste regroupés pour le suivi — pas de négociation groupée réelle.", discount: 0 },
    { id: "pro_to_pro", label: "Reprise en lot par un professionnel", hint: "Cas le plus courant : un marchand ou négociant reprend le lot en une fois.", discount: 0.10 },
    { id: "auction", label: "Vente aux enchères professionnelles", hint: "Lot proposé en bloc sur une plateforme d'enchères B2B (VO reconditionnées, flottes).", discount: 0.20 },
    { id: "urgent_liquidation", label: "Liquidation rapide / déstockage", hint: "Besoin de liquidités immédiat, vente accélérée sans négociation véhicule par véhicule.", discount: 0.30 },
  ];

  return (
    <div className="overlay" onClick={onClose}>
      <div className="panel panel-wide" onClick={e => e.stopPropagation()}>
        <div className="panel-header">
          <div>
            <div className="panel-title">📦 Traitement du lot{lot ? ` — ${lot.members.length} véhicule(s)` : ""}</div>
            <div className="panel-sub">Chaque véhicule est traité individuellement, exactement comme un Dossier VIQ seul.</div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: "1.2rem", maxHeight: "70vh", overflowY: "auto" }}>
          {loading && !lot && <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>Chargement du lot…</div>}
          {error && <div style={{ fontSize: ".84rem", color: "var(--red, #c0392b)", marginBottom: "1rem" }}>⚠ {error}</div>}

          {lot && (
            <>
              {(() => {
                const priced = lot.members.filter(m => m.cotation?.realisee && m.cotation.prix_conseille != null);
                if (!priced.length) return null;
                const total = priced.reduce((sum, m) => sum + Number(m.cotation.prix_conseille || 0), 0);
                const allValuations = VALUATION_CONTEXTS.map(c => ({ ...c, amount: total * (1 - c.discount) }));
                return (
                  <div style={{ marginBottom: "1.2rem", padding: "1rem", background: "linear-gradient(135deg, var(--accent-soft), var(--card))", border: "1.5px solid var(--accent)", borderRadius: 12 }}>
                    <div style={{ fontSize: ".7rem", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: ".7rem" }}>Valeur du lot selon le contexte de vente</div>
                    {/* v91 — Règle : les 4 contextes affichés en une fois (au lieu d'un
                        sélecteur ne montrant qu'un seul prix à la fois), avec un bouton
                        pour sauvegarder ces montants sur le lot. */}
                    <div style={{ display: "flex", flexDirection: "column", gap: ".6rem", marginBottom: ".8rem" }}>
                      {allValuations.map(c => (
                        <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".8rem", padding: ".6rem .8rem", background: "var(--white)", borderRadius: 8, border: "1px solid var(--border)" }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: ".82rem", fontWeight: 700, color: "var(--text)" }}>{c.label}{c.discount > 0 ? ` (−${Math.round(c.discount * 100)}%)` : ""}</div>
                            <div style={{ fontSize: ".72rem", color: "var(--sub)" }}>{c.hint}</div>
                          </div>
                          <div style={{ fontFamily: "var(--font-head)", fontSize: "1.15rem", fontWeight: 800, color: "var(--text)", whiteSpace: "nowrap" }}>{eur(c.amount)}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: ".72rem", color: "var(--sub)" }}>
                      Somme des {priced.length} cote(s) individuelle(s) : {eur(total)}.
                      {priced.length < lot.members.length ? ` ${lot.members.length - priced.length} véhicule(s) sans cote pas inclus dans ce total.` : ""}
                    </div>
                    <div style={{ fontSize: ".64rem", color: "var(--muted)", marginTop: ".5rem", marginBottom: ".8rem", fontStyle: "italic" }}>
                      Ordres de grandeur indicatifs basés sur les pratiques courantes du marché — ajustez selon votre négociation réelle.
                    </div>
                    <button className="btn btn-outline btn-sm" disabled={savingValuations}
                      onClick={async () => {
                        setSavingValuations(true);
                        try {
                          const r = await apiPatch(`/api/dossier-lots/${lot.id}`, {
                            total_price: total,
                            valuations: allValuations.map(c => ({ id: c.id, label: c.label, discount: c.discount, amount: Math.round(c.amount) })),
                          });
                          if (r?.success) { setValuationsSaved(true); setTimeout(() => setValuationsSaved(false), 2500); }
                        } finally { setSavingValuations(false); }
                      }}>
                      {savingValuations ? "Enregistrement…" : valuationsSaved ? "✓ Enregistré" : "💾 Sauvegarder ces montants"}
                    </button>
                  </div>
                );
              })()}
              <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap", marginBottom: "1.2rem" }}>
                <button className="btn btn-accent" disabled={!!running} onClick={() => runAction("estimate", "estimate")}>
                  {running === "estimate" ? "Estimation en cours…" : "💰 Estimer le lot"}
                </button>
              </div>

              {lastResults && (
                <div style={{ marginBottom: "1.2rem", padding: ".8rem 1rem", background: "var(--accent-soft)", border: "1px solid var(--accent)", borderRadius: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: ".84rem", marginBottom: ".4rem" }}>
                    Résultat — {lastResults.results.filter(r => r.ok).length}/{lastResults.results.length} véhicule(s) traité(s)
                  </div>
                  {lastResults.results.filter(r => !r.ok).map((r, i) => (
                    <div key={i} style={{ fontSize: ".78rem", color: "var(--red, #c0392b)" }}>⚠ Dossier #{r.dossierId || "?"} : {r.error}</div>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: ".7rem" }}>
                {lot.members.map(m => {
                  const lc = m.latestCheck || {};
                  const label = [m.dossier?.make || lc.make, m.dossier?.model || lc.model].filter(Boolean).join(" ") || "Véhicule non identifié";
                  const est = m.cotation?.realisee ? m.cotation : null;
                  return (
                    <div key={m.dossier.id} style={{ border: "1.5px solid var(--border)", borderRadius: 10, padding: ".9rem 1rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: ".5rem" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: ".9rem" }}>{label}{m.dossier?.year ? ` · ${m.dossier.year}` : ""}</div>
                          <div style={{ fontSize: ".76rem", color: "var(--muted)" }}>{m.dossier?.plaque || m.dossier?.vin || "Sans identifiant"}</div>
                        </div>
                        {typeof lc.score === "number" && (
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: "1.15rem", color: "var(--accent)" }}>{lc.score}<span style={{ fontSize: ".65rem", color: "var(--muted)" }}>/100</span></div>
                            <div style={{ fontSize: ".64rem", color: "var(--muted)" }}>Score VIQ.AI®</div>
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap", marginTop: ".6rem" }}>
                        {[
                          [!!m.hasHistovec, "HistoVec"],
                          [!!m.hasCarVertical, "CarVertical"],
                          [!!(m.photos && m.photos.length), m.photos?.length ? `Photos (${m.photos.length})` : "Photos"],
                          [!!lc.visual_inspection, "Analyse IA"],
                          [!!est, "Cote estimée"],
                        ].map(([ok, label2]) => (
                          <span key={label2} style={{
                            fontSize: ".68rem", fontWeight: 700, borderRadius: 10, padding: ".15rem .55rem",
                            color: ok ? "var(--green)" : "var(--muted)",
                            background: ok ? "rgba(46,111,92,.1)" : "var(--card)",
                            border: "1px solid " + (ok ? "rgba(46,111,92,.3)" : "var(--border)"),
                          }}>
                            {ok ? "✓" : "○"} {label2}
                          </span>
                        ))}
                      </div>
                      {est && (
                        <div style={{ marginTop: ".6rem", fontSize: ".82rem" }}>
                          <span style={{ fontSize: ".68rem", color: "var(--muted)" }}>Prix estimé (référence individuelle) : </span>
                          <span style={{ fontWeight: 700, color: "var(--green)" }}>{eur(est.prix_conseille)}</span>
                          <span style={{ fontWeight: 400, color: "var(--sub)", fontSize: ".74rem" }}> · fourchette {eur(est.argus_low)} – {eur(est.argus_high)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* v68 — Règle 7 : historique groupé du lot */}
              {history && history.length > 0 && (
                <div style={{ marginTop: "1.4rem", paddingTop: "1.2rem", borderTop: "1px solid var(--border)" }}>
                  <div style={{ fontWeight: 700, fontSize: ".84rem", marginBottom: ".7rem" }}>🕐 Historique du lot</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                    {history.map((h, i) => {
                      const ACTION_LABEL = { control: "🔍 Contrôle du lot", analyze_photos: "📸 Analyse photos du lot", estimate: "💰 Estimation du lot" };
                      return (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: ".55rem .8rem", background: "var(--card)", borderRadius: 8, fontSize: ".8rem" }}>
                          <span>{ACTION_LABEL[h.action] || h.action}</span>
                          <span style={{ color: "var(--muted)", fontSize: ".72rem" }}>
                            {h.success_count}/{h.vehicle_count} véhicule(s) mis à jour · {new Date(h.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// v93 — Règle : nouvel onglet Documents dans le Dossier VIQ, entre
// Prospecter et Suivi de vente — réunit mandat de vente, certificat de
// cession (réutilise les générateurs existants du Dashboard Pro via
// callback, pré-remplis avec CE véhicule) et les exports PDF déjà présents
// ailleurs dans le dossier (jamais dupliqués, juste liés).
function DossierDocumentsTab({ s, detail, onCreateMandat, onCreateCession, onNavigate }) {
  const eur = (v) => (v || v === 0) ? Math.round(Number(v)).toLocaleString("fr-FR") + " €" : "—";
  const makeModel = [s?.make, s?.model].filter(Boolean).join(" ");
  const price = detail?.cotation?.prix_conseille;
  const mandatPrefill = { makeModel, vin: s?.vin, immat: s?.plaque, year: s?.year, viqPrice: price };
  const cessionPrefill = { makeModel, vin: s?.vin, immat: s?.plaque };
  const cardStyle = { background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: "1.1rem", display: "flex", flexDirection: "column", gap: ".6rem" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ fontSize: ".82rem", color: "var(--sub)" }}>
        Générez les documents liés à ce véhicule — pré-remplis automatiquement avec les informations déjà connues du Dossier VIQ.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1rem" }}>
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: ".9rem" }}>📝 Mandat de vente</div>
          <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.5 }}>
            Mandat d'intermédiation conforme (14 articles), pré-rempli avec {makeModel || "ce véhicule"}{price ? ` et la cote estimée (${eur(price)})` : ""}.
          </div>
          <button className="btn btn-accent btn-sm" style={{ justifyContent: "center" }} onClick={() => onCreateMandat && onCreateMandat(mandatPrefill)}>
            Générer le mandat de vente
          </button>
        </div>
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: ".9rem" }}>📄 Certificat de cession</div>
          <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.5 }}>
            Certificat officiel CERFA 15776 (les deux exemplaires), pré-rempli avec l'immatriculation et le VIN de ce véhicule.
          </div>
          <button className="btn btn-accent btn-sm" style={{ justifyContent: "center" }} onClick={() => onCreateCession && onCreateCession(cessionPrefill)}>
            Créer le certificat de cession
          </button>
        </div>
      </div>

      <div style={{ fontWeight: 700, fontSize: ".85rem", marginTop: ".5rem" }}>📤 Exports PDF disponibles</div>
      <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
        <button onClick={() => onNavigate("vehicle")}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, padding: ".7rem 1rem", cursor: "pointer", textAlign: "left" }}>
          <span style={{ fontSize: ".84rem", color: "var(--text)" }}>Fiche véhicule & HistoVec (PDF)</span>
          <span style={{ fontSize: ".78rem", color: "var(--accent)", fontWeight: 700 }}>Ouvrir l'onglet HistoVec →</span>
        </button>
        <button onClick={() => onNavigate("market")}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, padding: ".7rem 1rem", cursor: "pointer", textAlign: "left" }}>
          <span style={{ fontSize: ".84rem", color: "var(--text)" }}>Cotation & positionnement marché (PDF)</span>
          <span style={{ fontSize: ".78rem", color: "var(--accent)", fontWeight: 700 }}>Ouvrir l'onglet Marché →</span>
        </button>
      </div>
    </div>
  );
}

function DossierVIQPage({ user, onClose, onOpenCheck, onOpenSell, onOpenSaleTracking, onCreateMandat, onCreateCession, onOpenLeads, onOpenProspection, initialKey, onSelectedChange }) {
  // v37 — Réécriture sur le vrai modèle Dossier VIQ (vehicle_dossiers /
  // vehicle_operations / vehicle_files côté serveur), remplace la
  // reconstruction côté client par regroupement a posteriori — cause
  // structurelle des bugs précédents (doublons, données non synchronisées).
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(["all"]); // v93 — multisélection au lieu d'un choix unique
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [showMarketModal, setShowMarketModal] = useState(false); // v104 — Vérifier le marché, depuis ce dossier
  const [selectedId, setSelectedId] = useState(() => {
    // v86 — Règle : initialKey doit être un identifiant de dossier valide
    // (entier positif) — jamais autre chose (ex. un objet événement passé
    // par erreur via un onClick mal câblé, comme le bug corrigé sur le
    // bouton "Ouvrir mes dossiers"). Toute valeur invalide retombe sur la
    // liste plutôt que d'afficher un "dossier introuvable".
    const n = Number(initialKey);
    return Number.isInteger(n) && n > 0 ? n : null;
  });
  // v71 — Règle 5 (suite) : rapporte le dossier ouvert au parent, pour que
  // l'URL de user.vehicleiq.fr reflète le dossier précis affiché (lien
  // direct partageable), sans changer le fonctionnement de cette page.
  useEffect(() => { onSelectedChange && onSelectedChange(selectedId); }, [selectedId]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null); // v65 — distingue "en cours" de "a échoué", évite un spinner bloqué indéfiniment
  const [annonceOpen, setAnnonceOpen] = useState(false); // v40 — repli fermé à chaque changement de dossier
  const [improvingAnnonce, setImprovingAnnonce] = useState(false);
  const [annonceError, setAnnonceError] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null); // v42 — visionneuse photo plein écran
  const [dossierTab, setDossierTab] = useState("overview"); // v43 — Dossier VIQ = hub complet (Vue d'ensemble / Véhicule / Marché / Vente)
  // v58 — index de parcours manuel de la Prochaine action (null = suivre
  // l'étape auto-suggérée). Remis à null à chaque changement de dossier
  // sélectionné (voir l'effet un peu plus bas).
  const [browseIndex, setBrowseIndex] = useState(null);
  const [showLotModal, setShowLotModal] = useState(false); // v67 — LotProcessingModal
  // v69 — Règle 2 : sélection multiple pour créer/rejoindre un lot depuis la
  // liste des Dossiers VIQ. N'agit que sur la relation lot_id.
  const [lotSelection, setLotSelection] = useState({}); // dossierId -> true
  const [lotActionBusy, setLotActionBusy] = useState(false);
  const [lotActionMsg, setLotActionMsg] = useState(null);
  const toggleLotSelection = (id) => setLotSelection(prev => { const n = { ...prev }; if (n[id]) delete n[id]; else n[id] = true; return n; });
  const clearLotSelection = () => setLotSelection({});

  const createLotFromSelection = async () => {
    const ids = Object.keys(lotSelection).map(Number);
    if (ids.length < 2) { setLotActionMsg("Sélectionnez au moins 2 véhicules."); return; }
    setLotActionBusy(true); setLotActionMsg(null);
    try {
      const r = await apiPost("/api/dossier-lots/create-from-dossiers", { dossier_ids: ids });
      if (r?.success) { clearLotSelection(); await refresh(); } else setLotActionMsg(r?.error || "Création impossible.");
    } catch (e) { setLotActionMsg(e?.message || "Création impossible."); }
    finally { setLotActionBusy(false); }
  };
  const addSelectionToLot = async (lotId) => {
    const ids = Object.keys(lotSelection).map(Number);
    if (!ids.length || !lotId) return;
    setLotActionBusy(true); setLotActionMsg(null);
    try {
      for (const id of ids) await apiPost(`/api/dossier-lots/${lotId}/add-dossier`, { dossier_id: id });
      clearLotSelection(); await refresh();
    } catch (e) { setLotActionMsg(e?.message || "Ajout au lot impossible."); }
    finally { setLotActionBusy(false); }
  };
  const removeDossierFromLot = async (dossierId) => {
    setLotActionBusy(true); setLotActionMsg(null);
    try {
      const r = await apiPost(`/api/dossiers/${dossierId}/remove-from-lot`, {});
      if (r?.success) await refresh(); else setLotActionMsg(r?.error || "Retrait impossible.");
    } catch (e) { setLotActionMsg(e?.message || "Retrait impossible."); }
    finally { setLotActionBusy(false); }
  };
  const dissolveLot = async (lotId) => {
    setLotActionBusy(true); setLotActionMsg(null);
    try {
      const r = await apiPost(`/api/dossier-lots/${lotId}/dissolve`, {});
      if (r?.success) await refresh(); else setLotActionMsg(r?.error || "Dissociation impossible.");
    } catch (e) { setLotActionMsg(e?.message || "Dissociation impossible."); }
    finally { setLotActionBusy(false); }
  };
  const renameLot = async (lotId, name) => {
    setLotActionBusy(true); setLotActionMsg(null);
    try {
      const r = await apiPatch(`/api/dossier-lots/${lotId}`, { name });
      if (r?.success) await refresh(); else setLotActionMsg(r?.error || "Renommage impossible.");
    } catch (e) { setLotActionMsg(e?.message || "Renommage impossible."); }
    finally { setLotActionBusy(false); }
  };
  // v68 — Imports directs (règle 10) : plus besoin d'ouvrir le module
  // Contrôler pour HistoVec/CarVertical/Photos depuis un Dossier VIQ.
  const [directImportBusy, setDirectImportBusy] = useState(false);
  const [directImportMsg, setDirectImportMsg] = useState(null);
  const [analyzingPhotos, setAnalyzingPhotos] = useState(false);

  const persistDirectFile = async (dossierId, type, payload) => {
    setDirectImportBusy(true); setDirectImportMsg(null);
    try {
      const r = await apiPost(`/api/dossiers/${dossierId}/files`, { type, payload });
      if (r?.success) {
        setDirectImportMsg(`✓ ${type === "histovec" ? "HistoVec" : type === "carvertical" ? "CarVertical" : "Photos"} enregistré(es).`);
        await fetchDossierDetail(dossierId); await refresh();
      } else setDirectImportMsg(r?.error || "Import impossible.");
    } catch (e) { setDirectImportMsg(e?.message || "Import impossible."); }
    finally { setDirectImportBusy(false); }
  };

  const analyzePhotosDirect = async (dossierId) => {
    setAnalyzingPhotos(true); setDirectImportMsg(null);
    try {
      const r = await apiPost(`/api/dossiers/${dossierId}/analyze-photos`, {});
      if (r?.success) { setDirectImportMsg("✓ Analyse IA terminée — score mis à jour."); await fetchDossierDetail(dossierId); await refresh(); }
      else setDirectImportMsg(r?.error || "Analyse impossible.");
    } catch (e) { setDirectImportMsg(e?.message || "Analyse impossible."); }
    finally { setAnalyzingPhotos(false); }
  };

  const uploadPhotosDirect = async (dossierId, files) => {
    setDirectImportBusy(true); setDirectImportMsg(null);
    try {
      const images = [];
      for (const f of Array.from(files || []).slice(0, 10)) images.push(await compressImageFile(f));
      if (!images.length) { setDirectImportBusy(false); return; }
      const r = await apiPost(`/api/dossiers/${dossierId}/files`, { type: "photo", payload: { images, count: images.length } });
      if (r?.success) { setDirectImportMsg(`✓ ${images.length} photo(s) enregistrée(s).`); await fetchDossierDetail(dossierId); await refresh(); }
      else setDirectImportMsg(r?.error || "Import impossible.");
    } catch (e) { setDirectImportMsg(e?.message || "Import impossible."); }
    finally { setDirectImportBusy(false); }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await apiGet("/api/dossiers");
      if (r?.success) setDossiers(r.dossiers || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [user?.uid]);

  const fetchDossierDetail = (id, { resetTab } = {}) => {
    if (!id) { setDetail(null); return; }
    setDetailLoading(true);
    setDetailError(null);
    if (resetTab) { setAnnonceOpen(false); setAnnonceError(null); setDossierTab("overview"); setBrowseIndex(null); }
    return apiGet(`/api/dossiers/${id}`).then(r => {
      if (r?.success) setDetail(r);
      else setDetailError(r?.error || "Dossier introuvable ou inaccessible.");
    }).catch(e => { setDetailError(e?.message || "Erreur réseau."); })
      .finally(() => { setDetailLoading(false); });
  };

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setAnnonceOpen(false);
    setAnnonceError(null);
    setDossierTab("overview");
    setBrowseIndex(null);
    apiGet(`/api/dossiers/${selectedId}`).then(r => {
      if (cancelled) return;
      if (r?.success) setDetail(r);
      else setDetailError(r?.error || "Dossier introuvable ou inaccessible.");
    }).catch(e => { if (!cancelled) setDetailError(e?.message || "Erreur réseau."); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  // v41 — "Améliorer l'annonce" : réécriture ponctuelle via Claude Haiku
  // (cheap), le résultat écrase l'affichage local sans attendre un rechargement
  // complet du dossier, puis se propage via l'opération enregistrée côté serveur.
  const improveAnnonce = async (dossierIdArg) => {
    if (improvingAnnonce) return;
    setImprovingAnnonce(true); setAnnonceError(null);
    try {
      const r = await apiPost(`/api/dossiers/${dossierIdArg}/improve-annonce`, {});
      if (r?.success) {
        setDetail(prev => prev ? { ...prev, annonce: { ...prev.annonce, texte: r.annonce_texte } } : prev);
        setAnnonceOpen(true);
      } else setAnnonceError(r?.error || "Réécriture impossible.");
    } catch (e) { setAnnonceError(e?.message || "Réécriture impossible."); }
    finally { setImprovingAnnonce(false); }
  };

  // v40 — Plus AUCUN recalcul ici : hasHistovec / hasCarVertical / hasPhotos /
  // hasPriceEstimate / hasAnnonce / hasVente / nextAction / score viennent
  // tels quels de /api/dossiers (listDossiers → computeNextAction), la MÊME
  // fonction que celle utilisée par /api/dossiers/:id (buildVehicleDossier).
  // Recalculer ces règles ici — même "à l'identique" — est exactement ce qui
  // a permis à la liste et à la fiche détail de diverger auparavant (ex. la
  // liste ignorait CarVertical/Photos dans son propre nextAction). Ce
  // composant ne fait plus qu'afficher l'objet consolidé reçu du serveur.
  const rows = dossiers.map(d => {
    const label = `${d.make || "Véhicule"}${d.model ? " " + d.model : ""}${d.year ? " · " + d.year : ""}`;
    const latestEst = d.latest_estimation;
    return {
      ...d, label,
      knownIssues: (d.latest_check?.known_issues?.found ? d.latest_check.known_issues : null) || (latestEst?.known_issues?.found ? latestEst.known_issues : null),
    };
  });

  const countMissing = {
    histovec: rows.filter(d => !d.hasHistovec).length,
    carvertical: rows.filter(d => !d.hasCarVertical).length,
    photos: rows.filter(d => !d.hasPhotos).length,
    cote: rows.filter(d => !d.hasPriceEstimate).length,
    annonce: rows.filter(d => !d.hasAnnonce).length,
    vente: rows.filter(d => !d.hasVente).length,
    lot: rows.filter(d => d.lot_id).length,
    complete: rows.filter(d => d.hasHistovec && d.hasCarVertical && d.hasPhotos && d.hasPriceEstimate && d.hasAnnonce && d.hasVente).length,
    crm: rows.filter(d => d.sentViaCRM).length,
  };
  const FILTER_TEST = {
    all: () => true,
    histovec: d => !d.hasHistovec, carvertical: d => !d.hasCarVertical,
    photos: d => !d.hasPhotos, cote: d => !d.hasPriceEstimate, annonce: d => !d.hasAnnonce,
    vente: d => !d.hasVente,
    lot: d => !!d.lot_id,
    complete: d => d.hasHistovec && d.hasCarVertical && d.hasPhotos && d.hasPriceEstimate && d.hasAnnonce && d.hasVente,
    crm: d => !!d.sentViaCRM,
  };
  const filtered = rows.filter(d => {
    if (!filters.includes("all") && !filters.some(f => FILTER_TEST[f](d))) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (d.label || "").toLowerCase().includes(q) || (d.vin || "").toLowerCase().includes(q) || (d.plaque || "").toLowerCase().includes(q) || (d.customer_name || "").toLowerCase().includes(q);
  });

  const MilestoneBadge = ({ ok, label }) => (
    <span style={{ fontSize: ".7rem", fontWeight: 600, padding: ".22rem .6rem", borderRadius: 20, background: ok ? "rgba(46,111,92,.1)" : "var(--card)", color: ok ? "var(--green)" : "var(--muted)", border: "1px solid " + (ok ? "rgba(46,111,92,.25)" : "var(--border)") }}>
      {ok ? "✓" : "○"} {label}
    </span>
  );

  // v37 — RÈGLE 14/15 : "Continuer" recharge TOUT ce qui est déjà connu
  // (HistoVec, CarVertical, dernière cotation) — jamais repartir de zéro.
  // v58 — overrideType : utilisé quand l'utilisateur a navigué manuellement
  // (Précédent/Suivant) vers une étape différente de celle auto-suggérée.
  const continueDossier = (d, overrideType) => {
    const type = overrideType || d.nextAction.type;
    if (["check", "histovec", "carvertical", "photos"].includes(type)) {
      // v52 — Reprise enrichie : le Contrôle relancé depuis "Continuer" doit
      // récupérer tout ce que le dossier connaît déjà (photos, HistoVec),
      // pas seulement la plaque — et doit écrire sur CE dossier, pas en
      // recréer un autre via find-or-create (d'où dossier_id explicite).
      const src = detail && detail.dossier.id === d.id ? detail : null;
      onOpenCheck({
        plaqueOrVin: d.vin || d.plaque,
        dossier_id: d.id,
        photos: src?.photos && src.photos.length ? src.photos : undefined,
        histovec: src?.currentFiles?.histovec ? { found: true, ...src.currentFiles.histovec.payload } : undefined,
        carvertical: src?.currentFiles?.carvertical ? { found: true, ...src.currentFiles.carvertical.payload } : undefined,
      });
      return;
    }
    // v39 — Renseigner le prix de vente ne passe pas par Estimer : direction
    // le Suivi de vente. "done" (dossier complet) y renvoie aussi, pour
    // consulter/modifier la vente déjà enregistrée.
    if (type === "sale" || type === "done") {
      onOpenSaleTracking && onOpenSaleTracking();
      onClose();
      return;
    }
    // v66 — Lot : "Estimer"/"Continuer" doit rouvrir l'Estimation en mode
    // GROUPÉ avec le contexte COMPLET de chaque véhicule du lot (immat, VIN,
    // constructeur, modèle, version, énergie, km, HistoVec, CarVertical,
    // photos, analyse IA) — même passerelle que le Contrôle/Estimation
    // simultanés (window.__viq_batch_check_results), mais alimentée depuis
    // le lot plutôt que depuis un Contrôle qui vient d'être fait.
    if (d.lot_id) {
      apiGet(`/api/dossier-lots/${d.lot_id}/full`).then(r => {
        const members = r?.success ? r.lot?.members : null;
        if (!members || !members.length) { onOpenSell({ _dossier_id: d.id, make: d.make, model: d.model, year_sell: d.year, fuel_sell: d.fuel, vin: d.vin, plaque: d.plaque }); return; }
        window.__viq_batch_check_results = members.map(m => {
          const lc = m.latestCheck || {};
          // v94 — Règle : même logique "zéro ressaisie" que pour un véhicule
          // seul — si le contrôle n'a pas encore de valeur, on retombe sur
          // ce que le client a déjà fourni dans son formulaire lead.
          const la = m.currentFiles?.lead_answers?.payload || {};
          return {
            vin: m.dossier?.vin || null, plaque: m.dossier?.plaque || null,
            make: m.dossier?.make || lc.make, model: m.dossier?.model || lc.model,
            year: m.dossier?.year || lc.year, fuel: m.dossier?.fuel || lc.fuel,
            km: lc.km || lc.mileage || la.km || la.mileage || la.kilometrage || null,
            gearbox: lc.gearbox || la.gearbox || la.boite || null,
            finition: lc.finition || la.finition || null, power_cv: lc.power_cv || null,
            dossierId: m.dossier?.id || null,
            hvData: m.currentFiles?.histovec ? { found: true, ...m.currentFiles.histovec.payload } : null,
            cvData: m.currentFiles?.carvertical ? { found: true, ...m.currentFiles.carvertical.payload } : null,
            photos: m.photos || [],
            visualInspection: lc.visual_inspection || null,
          };
        });
        onOpenSell({});
      }).catch(() => { onOpenSell({ _dossier_id: d.id, make: d.make, model: d.model, year_sell: d.year, fuel_sell: d.fuel, vin: d.vin, plaque: d.plaque }); });
      onClose();
      return;
    }
    const src = detail && detail.dossier.id === d.id ? detail : null;
    const hv = src?.currentFiles?.histovec;
    // v94 — Règle : les réponses déjà données par le client dans le
    // formulaire lead (km, carburant, boîte, état...) sont désormais
    // transférées sur le Dossier VIQ à sa création (voir
    // createDossierFromLead) — on les reprend ici pour ne jamais les
    // redemander au pro. Les champs explicites ci-dessous restent
    // prioritaires s'ils sont déjà renseignés sur le dossier lui-même.
    const leadAnswers = src?.currentFiles?.lead_answers?.payload || {};
    const normalizedLeadAnswers = Object.keys(leadAnswers).length ? normalizeSellAnswers(leadAnswers) : {};
    onOpenSell({
      ...normalizedLeadAnswers,
      _dossier_id: d.id, make: d.make, model: d.model, year_sell: d.year, fuel_sell: d.fuel,
      vin: d.vin, plaque: d.plaque,
      _histovec: hv ? { found: true, ...hv.payload } : undefined,
      // v38/v52 — les photos déjà chargées (Contrôle ou Estimation, toutes
      // fournées confondues) doivent être reprises ici, pas redemandées.
      _photos: (src?.photos && src.photos.length) ? src.photos : undefined,
    });
  };

  // ─── Vue détail : la fiche véhicule, style CRM ───
  // v65 — Garde-fou : si nextAction est absent/mal formé pour une raison
  // quelconque (donnée historique, réponse API partielle), retomber sur une
  // valeur sûre plutôt que de laisser planter tout le rendu — plusieurs
  // endroits plus bas lisent s.nextAction.type sans vérification, un
  // nextAction manquant faisait planter TOUT React (page blanche complète,
  // pas seulement la fiche) faute de error boundary.
  const rawSelected = rows.find(d => d.id === selectedId);
  const selected = rawSelected ? { ...rawSelected, nextAction: rawSelected.nextAction || { type: "check", label: "Identifier le véhicule" } } : rawSelected;
  // v41 — Navigation ← → entre dossiers : se déplace dans la liste FILTRÉE
  // actuellement affichée (mêmes véhicules que ceux visibles derrière la
  // fiche), pas dans la liste complète non filtrée.
  const filteredIds = filtered.map(d => d.id);
  const curIdx = filteredIds.indexOf(selectedId);
  const hasPrev = curIdx > 0;
  const hasNext = curIdx >= 0 && curIdx < filteredIds.length - 1;
  const goToPrevDossier = () => { if (hasPrev) setSelectedId(filteredIds[curIdx - 1]); };
  const goToNextDossier = () => { if (hasNext) setSelectedId(filteredIds[curIdx + 1]); };
  if (selectedId) {
    const s = selected;
    return (
      <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="panel panel-wide panel-fullscreen" style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div className="panel-header">
            {/* v91 — Règle : ces boutons de navigation ne respectaient pas le
                style visuel global (trop petits, non alignés sur les boutons
                arrondis/accent utilisés partout ailleurs) — la classe
                .btn-icon vient d'une feuille de style externe non éditable
                ici, donc restylés en inline pour matcher le reste de l'app. */}
            {(() => {
              const navBtnStyle = (disabled) => ({
                width: 38, height: 38, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                border: "1.5px solid var(--accent)", background: "var(--card)", color: "var(--accent)",
                fontSize: "1.1rem", fontWeight: 700, cursor: disabled ? "default" : "pointer",
                opacity: disabled ? .35 : 1, flexShrink: 0,
              });
              return (
                <>
                  <button onClick={() => setSelectedId(null)} title="Retour aux dossiers" style={navBtnStyle(false)}>←</button>
                  <button onClick={goToPrevDossier} disabled={!hasPrev} title="Dossier précédent" style={navBtnStyle(!hasPrev)}>‹</button>
                  <div className="panel-title">Dossier VIQ{filteredIds.length > 1 ? ` (${curIdx + 1}/${filteredIds.length})` : ""}</div>
                  <button onClick={goToNextDossier} disabled={!hasNext} title="Dossier suivant" style={navBtnStyle(!hasNext)}>›</button>
                  <button onClick={onClose} title="Fermer" style={{ ...navBtnStyle(false), background: "var(--accent)", color: "#fff" }}>✕</button>
                </>
              );
            })()}
          </div>
          <div className="panel-body" style={{ overflowY: "auto", overflowX: "hidden" }}>
            {(detailLoading || (!detail && !detailError)) ? (
              <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>Chargement du dossier…</div>
            ) : detailError ? (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <div style={{ color: "var(--muted)", marginBottom: "1rem" }}>⚠ {detailError}</div>
                <button className="btn btn-outline btn-sm" onClick={() => fetchDossierDetail(selectedId)}>Réessayer</button>
              </div>
            ) : s ? (
              <>
                <div style={{ display: "flex", gap: "1.2rem", flexWrap: "wrap", alignItems: "flex-start", marginBottom: "1.4rem", paddingBottom: "1.2rem", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ width: 64, height: 64, borderRadius: 12, background: "var(--card)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.8rem", flexShrink: 0 }}>🚗</div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontFamily: "var(--font-head)", fontSize: "1.35rem", fontWeight: 800, color: "var(--text)" }}>
                      {s.label}
                      {s.sentViaCRM && <span title={s.sentViaCRMLastAt ? `Dernière transmission : ${new Date(s.sentViaCRMLastAt).toLocaleDateString("fr-FR")}` : undefined} style={{ marginLeft: ".5rem", fontSize: ".62rem", fontWeight: 700, color: "var(--green)", background: "rgba(46,111,92,.1)", border: "1px solid rgba(46,111,92,.3)", borderRadius: 10, padding: ".1rem .5rem", verticalAlign: "middle" }}>📤 Transmis via CRM</span>}
                    </div>
                    <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", marginTop: ".4rem" }}>
                      {s.plaque && (
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: "1.05rem", fontWeight: 800, letterSpacing: "1px", color: "var(--text)", background: "var(--card)", border: "1.5px solid var(--accent)", borderRadius: 6, padding: ".25rem .7rem" }}>
                          {s.plaque}
                        </span>
                      )}
                      {s.vin && (
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: ".92rem", fontWeight: 700, letterSpacing: ".5px", color: "var(--sub)", background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 6, padding: ".25rem .7rem" }}>
                          VIN {s.vin}
                        </span>
                      )}
                      {!s.plaque && !s.vin && <span style={{ fontSize: ".82rem", color: "var(--muted)" }}>Identifiant non disponible</span>}
                    </div>
                    {s.customer_name && (
                      <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap", fontSize: ".78rem", color: "var(--sub)", marginTop: ".3rem" }}>
                        <span>👤 {s.customer_name}</span>
                        {s.customer_email && <a href={"mailto:" + s.customer_email} style={{ color: "var(--accent)" }}>✉️ {s.customer_email}</a>}
                        {s.customer_phone && <a href={"tel:" + String(s.customer_phone).replace(/[\s.]/g, "")} style={{ color: "var(--accent)" }}>📞 {s.customer_phone}</a>}
                      </div>
                    )}
                  </div>
                  {s.score != null && (
                    <div style={{ textAlign: "center" }}>
                      <ScoreGauge score={s.score} size={56} />
                      <div style={{ fontSize: ".62rem", color: "var(--muted)", marginTop: ".2rem" }}>Score VIQ.AI®</div>
                    </div>
                  )}
                  {s.latest_estimation?.prix_viq && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", fontWeight: 700, color: "var(--green)" }}>{Number(s.latest_estimation.prix_viq).toLocaleString("fr-FR")} €</div>
                      <div style={{ fontSize: ".62rem", color: "var(--muted)" }}>Prix de vente conseillé</div>
                    </div>
                  )}
                  {/* v104 — "Vérifier le marché" disponible depuis chaque
                      Dossier VIQ, pré-rempli avec marque/année/prix cible. */}
                  <button className="btn btn-outline btn-sm" style={{ alignSelf: "center" }} onClick={() => setShowMarketModal(true)}>
                    🔎 Vérifier le marché sur LeBonCoin
                  </button>
                </div>

                {/* v90 — Règle : déplacé en tête de dossier (juste après le
                    titre/score), au lieu d'être enterré sous "État
                    d'avancement" — visible sans avoir à faire défiler.
                    Simplifié à "Estimer le lot" uniquement : les deux autres
                    actions ouvraient déjà LotProcessingModal, qui ne les
                    propose plus depuis la simplification précédente — elles
                    ne faisaient donc plus rien d'utile ici. */}
                {s.lot_id && (
                  <div style={{ marginBottom: "1.2rem", padding: ".6rem .9rem", background: "var(--accent-soft)", border: "1px solid var(--accent)", borderRadius: 8, fontSize: ".8rem" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".5rem", marginBottom: ".6rem" }}>
                      <span>🔗 Ce véhicule fait partie d'un <strong>lot</strong> traité groupé.</span>
                      {s.lot_total_price != null && <span style={{ fontWeight: 700, color: "var(--accent)" }}>Prix du lot : {Number(s.lot_total_price).toLocaleString("fr-FR")} €</span>}
                    </div>
                    <button className="btn btn-accent btn-sm" onClick={() => setShowLotModal(true)}>💰 Estimer le lot</button>
                  </div>
                )}

                {/* v53 — Assistant du dossier : navigation en langage naturel */}
                <DossierAssistantBar onNavigate={setDossierTab} onContinue={() => continueDossier(s)} />

                {/* v43 — Le Dossier VIQ devient un hub complet : Vue d'ensemble,
                    fiche Véhicule complète, positionnement Marché (+ simulateur
                    de rachat), et suivi de Vente éditable — tout interconnecté,
                    au lieu d'outils séparés (Suivi de vente, Scan d'opportunité)
                    déconnectés du véhicule concerné. */}
                <div style={{ display: "flex", gap: ".4rem", borderBottom: "1px solid var(--border)", marginBottom: "1.2rem", overflowX: "auto" }}>
                  {[
                    ["overview", "📋", "Vue d'ensemble"],
                    ["vehicle", "🚗", "HistoVec"],
                    ["carvertical", "🔧", "CarVertical"],
                    ["market", "📊", "Marché"],
                    ["propose", "📣", "Prospecter"],
                    ["documents", "📄", "Documents"],
                    ["sale", "💰", "Suivi de vente"],
                    ["multidiffusion", "📡", "Multidiffusion"],
                    ["accounting", "🧮", "Comptabilité et Facturation Electronique"],
                  ].map(([id, icon, label]) => (
                    <button key={id} onClick={() => setDossierTab(id)}
                      style={{
                        background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap",
                        padding: ".6rem .9rem", fontSize: ".82rem", fontWeight: 700,
                        color: dossierTab === id ? "var(--accent)" : "var(--muted)",
                        borderBottom: dossierTab === id ? "2px solid var(--accent)" : "2px solid transparent",
                        marginBottom: "-1px",
                      }}>
                      {icon} {label}{id === "sale" && s.hasVente ? " ✓" : ""}{["accounting", "multidiffusion"].includes(id) ? <span style={{ fontSize: ".62rem", fontWeight: 700, color: "var(--muted)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 4, padding: "1px 4px", marginLeft: ".3rem" }}>bientôt</span> : ""}
                    </button>
                  ))}
                </div>

                <div style={{ display: "flex", gap: "1.4rem", flexWrap: "wrap" }}>
                  <div style={{ flex: "2 1 400px", minWidth: 280 }}>
                    {dossierTab === "overview" && (
                    <>
                    <div style={{ fontWeight: 700, fontSize: ".85rem", marginBottom: ".6rem" }}>📋 État d'avancement</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginBottom: "1.2rem" }}>
                      <MilestoneBadge ok={s.hasHistovec} label="HistoVec" />
                      <MilestoneBadge ok={s.hasCarVertical} label="CarVertical" />
                      <MilestoneBadge ok={s.hasPhotos} label={s.photo_count ? `Photos (${s.photo_count})` : "Photos"} />
                      <MilestoneBadge ok={s.hasPriceEstimate} label="Cote estimée" />
                      <MilestoneBadge ok={s.hasAnnonce} label="Annonce générée" />
                      <MilestoneBadge ok={s.hasVente} label="Vente" />
                    </div>
                    {s.knownIssues && <KnownIssuesPanel data={s.knownIssues} loading={false} />}

                    {/* v87 — Règle : "Données HistoVec importées" déplacé vers l'onglet
                        HistoVec (dossierTab === "vehicle") — voir plus bas. */}

                    {/* v42 — Galerie photos : utilise désormais `detail.photos` (toutes les
                        fournées de photos fusionnées, pas seulement la dernière — voir
                        buildVehicleDossier). L'agrandissement au clic n'utilise plus
                        window.open() : les navigateurs modernes bloquent la navigation
                        vers une URL data: dans un nouvel onglet, ce qui rendait le clic
                        silencieusement inopérant. Une visionneuse plein écran intégrée
                        remplace ce comportement. */}
                    {(detail?.photos?.length > 0) && (
                      <div style={{ margin: "1rem 0" }}>
                        <div style={{ fontWeight: 700, fontSize: ".82rem", marginBottom: ".5rem" }}>📷 Photos ({detail.photos.length})</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: ".7rem" }}>
                          {detail.photos.map((src, i) => (
                            <img key={i} src={src} alt={`Photo ${i + 1}`}
                              style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)", cursor: "zoom-in" }}
                              onClick={() => setLightboxSrc(src)} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* v40 — Section Cotation : consomme directement l'objet `cotation`
                        consolidé par buildVehicleDossier(). Règle métier : dès qu'une
                        cotation existe (peu importe la source), on affiche toujours
                        prix conseillé + date + accès au rapport — jamais "Cote à estimer". */}
                    {detail?.cotation?.realisee && (
                      <div style={{ margin: "1rem 0", padding: ".9rem 1rem", background: "var(--accent-soft)", border: "1px solid var(--accent)", borderRadius: 10 }}>
                        <div style={{ fontWeight: 700, fontSize: ".82rem", color: "var(--accent)", marginBottom: ".5rem" }}>
                          ✦ {s.lot_id ? "Cotation individuelle de référence" : "Cotation"}
                        </div>
                        {s.lot_id && (
                          <div style={{ fontSize: ".74rem", color: "var(--sub)", marginBottom: ".6rem", fontStyle: "italic" }}>
                            Ce véhicule fait partie d'un lot — ce prix est une référence individuelle, pas le prix de vente du lot{s.lot_total_price != null ? ` (${Number(s.lot_total_price).toLocaleString("fr-FR")} € au total)` : ""}.
                          </div>
                        )}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", fontSize: ".82rem" }}>
                          {detail.cotation.prix_conseille != null && <div><span style={{ color: "var(--muted)" }}>{s.lot_id ? "Prix conseillé (individuel) : " : "Prix conseillé : "}</span><strong>{Number(detail.cotation.prix_conseille).toLocaleString("fr-FR")} €</strong></div>}
                          {(detail.cotation.argus_low != null && detail.cotation.argus_high != null) && <div><span style={{ color: "var(--muted)" }}>Fourchette : </span><strong>{Number(detail.cotation.argus_low).toLocaleString("fr-FR")} – {Number(detail.cotation.argus_high).toLocaleString("fr-FR")} €</strong></div>}
                          {detail.cotation.date && <div><span style={{ color: "var(--muted)" }}>Date : </span><strong>{new Date(detail.cotation.date).toLocaleDateString("fr-FR")}</strong></div>}
                        </div>
                      </div>
                    )}

                    {/* v40 — Section Annonce : consomme l'objet `annonce` consolidé. */}
                    {detail?.annonce?.generee && (
                      <div style={{ margin: "1rem 0", padding: ".9rem 1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".8rem", flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: ".82rem", marginBottom: ".2rem" }}>📣 Annonce générée</div>
                            {detail.annonce.titre && <div style={{ fontSize: ".8rem", color: "var(--sub)" }}>{detail.annonce.titre}</div>}
                          </div>
                          <div style={{ display: "flex", gap: ".5rem", flexShrink: 0 }}>
                            <button className="btn btn-ghost btn-sm" disabled={improvingAnnonce} onClick={() => improveAnnonce(s.id)}>
                              {improvingAnnonce ? "✨ …" : "✨ Améliorer l'annonce"}
                            </button>
                            <button className="btn btn-outline btn-sm" onClick={() => setAnnonceOpen(o => !o)}>
                              {annonceOpen ? "Masquer" : "Ouvrir l'annonce"}
                            </button>
                          </div>
                        </div>
                        {annonceOpen && detail.annonce.texte && (
                          <div style={{ marginTop: ".7rem", fontSize: ".8rem", color: "var(--text)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{detail.annonce.texte}</div>
                        )}
                        {annonceError && <div style={{ marginTop: ".5rem", fontSize: ".76rem", color: "var(--red, #c0392b)" }}>{annonceError}</div>}
                      </div>
                    )}

                    <ListingPhotoStudio photos={detail?.photos} dossierId={s.id} onSaved={async () => { const rd = await apiGet(`/api/dossiers/${s.id}`); if (rd?.success) setDetail(rd); }} />

                    {/* v77 — Règle : les photos IA déjà traitées et enregistrées
                        n'étaient jamais réaffichées ensuite (seul un indicateur
                        booléen "Photos" en profitait) — galerie persistante ici. */}
                    {detail?.currentFiles?.photo_annonce?.payload?.images?.length > 0 && (
                      <div style={{ margin: "1rem 0", padding: ".9rem 1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10 }}>
                        <div style={{ fontWeight: 700, fontSize: ".82rem", marginBottom: ".6rem" }}>✅ Photos annonce déjà traitées ({detail.currentFiles.photo_annonce.payload.images.length})</div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: ".6rem" }}>
                          {detail.currentFiles.photo_annonce.payload.images.map((img, i) => (
                            <div key={i} style={{ borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)" }}>
                              <img src={img} alt="" style={{ width: "100%", display: "block", background: "var(--bg)" }} />
                              <a href={img} download={`viq-annonce-${i + 1}.jpg`} style={{ display: "block", textAlign: "center", fontSize: ".72rem", padding: ".3rem", color: "var(--accent)", textDecoration: "none", background: "var(--white)" }}>
                                ⬇ Télécharger
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* v39 — Section Vente : suivi de vente désormais intégré au cycle de vie du dossier */}
                    {detail?.saleInfo && (
                      <div style={{ margin: "1rem 0", padding: ".9rem 1rem", background: "rgba(46,111,92,.06)", border: "1px solid rgba(46,111,92,.2)", borderRadius: 10 }}>
                        <div style={{ fontWeight: 700, fontSize: ".82rem", color: "var(--green)", marginBottom: ".5rem" }}>💰 Vente</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", fontSize: ".82rem" }}>
                          <div><span style={{ color: "var(--muted)" }}>Prix de vente : </span><strong>{Number(detail.saleInfo.sold_price).toLocaleString("fr-FR")} €</strong></div>
                          {detail.saleInfo.sold_date && <div><span style={{ color: "var(--muted)" }}>Date : </span><strong>{new Date(detail.saleInfo.sold_date).toLocaleDateString("fr-FR")}</strong></div>}
                          {detail.saleInfo.margin != null && <div><span style={{ color: "var(--muted)" }}>Marge : </span><strong>{Number(detail.saleInfo.margin).toLocaleString("fr-FR")} €</strong></div>}
                          {detail.saleInfo.platform && <div><span style={{ color: "var(--muted)" }}>Plateforme : </span><strong>{detail.saleInfo.platform}</strong></div>}
                          {detail.saleInfo.client_type && <div><span style={{ color: "var(--muted)" }}>Client : </span><strong>{detail.saleInfo.client_type}</strong></div>}
                        </div>
                      </div>
                    )}

                    {/* Règle 10/11 : dernière opération de chaque type + compteur, historique complet à part */}
                    <div style={{ fontWeight: 700, fontSize: ".85rem", margin: "1.2rem 0 .6rem" }}>🕐 Historique condensé</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                      {detail?.operationsSummary?.map(op => (
                        <div key={op.type} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: ".55rem .8rem", background: "var(--card)", borderRadius: 8, fontSize: ".82rem" }}>
                          <span>
                            {op.type === "check" ? "🔍 Contrôle véhicule"
                              : op.type === "estimation" ? "✦ Estimation de cote"
                              : op.type === "annonce" ? "📣 Annonce générée"
                              : op.type === "sale" ? "💰 Vente"
                              : op.type === "photos" ? "📷 Photos ajoutées"
                              : op.type === "photo_analysis" ? "🔬 Analyse photo IA"
                              : op.type === "histovec_import" ? "🚙 Import HistoVec"
                              : op.type === "carvertical_import" ? "🔧 Import CarVertical"
                              : op.type}
                          </span>
                          <span style={{ color: "var(--muted)", fontSize: ".72rem" }}>
                            {op.count} occurrence{op.count > 1 ? "s" : ""} · dernière le {new Date(op.latest.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                          </span>
                        </div>
                      ))}
                    </div>
                    </>
                    )}

                    {dossierTab === "vehicle" && (
                      <div>
                        <DossierVehicleTab detail={detail} />
                        {/* v87 — Règle : déplacé depuis Vue d'ensemble — les
                            données HistoVec importées (propriétaires, CT,
                            kilométrage, gage/opposition) sont désormais
                            réunies avec le reste du contenu HistoVec, plutôt
                            que dispersées entre deux onglets. */}
                        {detail?.currentFiles?.histovec && <HistovecSummary hv={detail.currentFiles.histovec.payload} />}
                        {(() => {
                          const r = detail?.latestCheck || detail?.latestEstimation || {};
                          if (!(r.make || r.model || r.vin)) return null;
                          return (
                            <div style={{ marginTop: "1.4rem", paddingTop: "1.2rem", borderTop: "1px dashed var(--border)" }}>
                              <div style={{ fontWeight: 700, fontSize: ".85rem", marginBottom: ".8rem" }}>🧾 Carte grise & malus</div>
                              <CarteGriseCalculator prefill={{
                                ch: r.power_cv, co2: r.co2, poids: r.weight, fuel: r.fuel || r.engine,
                                dateImmat: r.first_registration_date, immat: detail?.dossier?.plaque, vin: r.vin,
                                makeModel: [r.make, r.model].filter(Boolean).join(" "),
                              }} />
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    {dossierTab === "carvertical" && (
                      <div>
                        <div style={{ fontWeight: 700, fontSize: ".85rem", marginBottom: ".8rem" }}>🔧 Rapport CarVertical</div>
                        {detail?.currentFiles?.carvertical?.payload ? (
                          <CarVerticalSummary cv={detail.currentFiles.carvertical.payload} />
                        ) : (
                          <div style={{ fontSize: ".84rem", color: "var(--muted)", background: "var(--card)", borderRadius: 10, padding: "1rem" }}>
                            Aucun rapport CarVertical importé pour ce véhicule. Comme HistoVec, achetez le rapport sur carvertical.com puis importez-le (PDF/capture) depuis le Contrôle ou l'Estimation.
                          </div>
                        )}
                      </div>
                    )}
                    {dossierTab === "accounting" && (
                      <div style={{ fontSize: ".84rem", color: "var(--muted)", background: "var(--card)", borderRadius: 10, padding: "1.4rem", textAlign: "center" }}>
                        🧮 <strong>Comptabilité et Facturation Electronique</strong> — bientôt disponible.<br />
                        Marge, TVA sur marge, facturation électronique et export comptable directement depuis chaque Dossier VIQ.
                      </div>
                    )}
                    {dossierTab === "multidiffusion" && (
                      <div style={{ fontSize: ".84rem", color: "var(--muted)", background: "var(--card)", borderRadius: 10, padding: "1.4rem", textAlign: "center" }}>
                        📡 <strong>Multidiffusion</strong> — bientôt disponible.<br />
                        Publication de l'annonce sur plusieurs marketplaces en un clic, directement depuis ce Dossier VIQ.
                      </div>
                    )}
                    {dossierTab === "market" && <DossierMarketTab detail={detail} />}
                    {dossierTab === "documents" && (
                      <DossierDocumentsTab s={s} detail={detail} onCreateMandat={onCreateMandat} onCreateCession={onCreateCession} onNavigate={setDossierTab} />
                    )}
                    {dossierTab === "sale" && <DossierSaleTab detail={detail} dossierId={s.id} onSaved={async () => { await refresh(); const rd = await apiGet(`/api/dossiers/${s.id}`); if (rd?.success) setDetail(rd); }} />}
                    {dossierTab === "propose" && (
                      <ProspectionAgent user={user} vehicleContext={{
                        id: s.id,
                        label: s.label + (s.plaque ? ` · ${s.plaque}` : s.vin ? ` · ${s.vin}` : ""),
                        price: detail?.cotation?.prix_conseille ? `${Number(detail.cotation.prix_conseille).toLocaleString("fr-FR")} €` : null,
                        plaque: s.plaque,
                        annonceTexte: detail?.annonce?.texte || null,
                        latestEstimation: detail?.latestEstimation || null,
                        latestCheck: detail?.latestCheck || null,
                      }} />
                    )}
                  </div>

                  <div style={{ flex: "1 1 220px", minWidth: 220 }}>
                    {(() => {
                      const autoIdx = Math.max(0, WORKFLOW_STEPS_CLIENT.findIndex(st => st.type === s.nextAction.type));
                      const effectiveIdx = browseIndex !== null ? browseIndex : (s.nextAction.type === "done" ? WORKFLOW_STEPS_CLIENT.length - 1 : autoIdx);
                      const displayedStep = s.nextAction.type === "done" && browseIndex === null
                        ? { type: "done", label: "Dossier complet", hint: "Contrôle, HistoVec, photos, cote, annonce et vente sont réunis dans ce Dossier VIQ. Vous pouvez revenir sur n'importe quelle étape ci-dessous à tout moment." }
                        : WORKFLOW_STEPS_CLIENT[effectiveIdx];
                      const isBrowsing = browseIndex !== null;
                      return (
                        <div style={{ background: "var(--accent-soft)", border: "1.5px solid var(--accent)", borderRadius: 12, padding: "1.2rem", minHeight: 210, boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".5rem" }}>
                            <div style={{ fontSize: ".68rem", textTransform: "uppercase", letterSpacing: ".6px", color: "var(--accent)", fontWeight: 700 }}>
                              {isBrowsing ? "Étape" : "Prochaine action"}
                            </div>
                            {isBrowsing && (
                              <button onClick={() => setBrowseIndex(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".68rem", color: "var(--muted)", textDecoration: "underline" }}>
                                Revenir à l'auto
                              </button>
                            )}
                          </div>
                          <div style={{ fontSize: ".95rem", fontWeight: 700, color: "var(--text)", marginBottom: ".5rem" }}>{displayedStep.label}</div>
                          {/* v90 — Règle : texte explicatif pour combler le vide visuel des
                              étapes courtes (ex. "Ajouter des photos"), plutôt qu'un grand
                              espace vide avant le bouton d'action. */}
                          {displayedStep.hint && (
                            <div style={{ fontSize: ".78rem", color: "var(--sub)", lineHeight: 1.5, marginBottom: "1rem" }}>{displayedStep.hint}</div>
                          )}
                          {directImportMsg && (
                            <div style={{ fontSize: ".76rem", color: directImportMsg.startsWith("✓") ? "var(--green)" : "var(--red, #c0392b)", marginBottom: ".7rem" }}>{directImportMsg}</div>
                          )}
                          {/* v68 — Règle 10 : HistoVec/CarVertical/Photos sont désormais
                              des actions DIRECTES depuis le Dossier VIQ, plus besoin
                              d'ouvrir le module Contrôler pour ça. */}
                          {displayedStep.type === "histovec" && (
                            <HistovecImport onImported={hv => persistDirectFile(s.id, "histovec", hv)} compact />
                          )}
                          {displayedStep.type === "carvertical" && (
                            <CarVerticalImport onImported={cv => persistDirectFile(s.id, "carvertical", cv)} compact vin={s.vin} plaque={s.plaque} />
                          )}
                          {displayedStep.type === "photos" && (
                            <div>
                              <input type="file" accept="image/*" multiple id={`direct-photos-${s.id}`} style={{ display: "none" }}
                                onChange={e => { uploadPhotosDirect(s.id, e.target.files); e.target.value = ""; }} />
                              <label htmlFor={`direct-photos-${s.id}`} className="btn btn-accent" style={{ width: "100%", justifyContent: "center", cursor: directImportBusy ? "wait" : "pointer", opacity: directImportBusy ? .6 : 1 }}>
                                {directImportBusy ? "Envoi…" : "📷 Ajouter des photos"}
                              </label>
                            </div>
                          )}
                          {/* v68 — Règle 2 : "Analyser les photos" dès que des photos
                              existent mais qu'aucun rapport IA n'est encore là — sans
                              passer par Contrôler non plus. */}
                          {s.hasPhotos && !detail?.latestCheck?.visual_inspection && !["histovec", "carvertical", "photos"].includes(displayedStep.type) && (
                            <button className="btn btn-outline" disabled={analyzingPhotos} style={{ width: "100%", justifyContent: "center", marginBottom: ".6rem" }} onClick={() => analyzePhotosDirect(s.id)}>
                              {analyzingPhotos ? "Analyse en cours…" : "🔬 Analyser les photos"}
                            </button>
                          )}
                          {!["histovec", "carvertical", "photos", "done"].includes(displayedStep.type) && (
                            <button className="btn btn-accent" style={{ width: "100%", justifyContent: "center" }} onClick={() => continueDossier(s, displayedStep.type)}>
                              Continuer →
                            </button>
                          )}
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto", paddingTop: ".8rem" }}>
                            <button onClick={() => setBrowseIndex(Math.max(0, effectiveIdx - 1))} disabled={effectiveIdx <= 0}
                              style={{ background: "none", border: "none", cursor: effectiveIdx <= 0 ? "default" : "pointer", fontSize: ".78rem", color: effectiveIdx <= 0 ? "var(--border)" : "var(--accent)", fontWeight: 600 }}>
                              ‹ Précédent
                            </button>
                            <button onClick={() => setBrowseIndex(Math.min(WORKFLOW_STEPS_CLIENT.length - 1, effectiveIdx + 1))} disabled={effectiveIdx >= WORKFLOW_STEPS_CLIENT.length - 1}
                              style={{ background: "none", border: "none", cursor: effectiveIdx >= WORKFLOW_STEPS_CLIENT.length - 1 ? "default" : "pointer", fontSize: ".78rem", color: effectiveIdx >= WORKFLOW_STEPS_CLIENT.length - 1 ? "var(--border)" : "var(--accent)", fontWeight: 600 }}>
                              Suivant ›
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                    {/* v89 — Règle : Analyse photo IA déplacée ici — dans la même
                        colonne, juste sous le bloc Étape/Prochaine action (taille
                        fixe ci-dessus), plutôt que sur toute la largeur, en dessous
                        de TOUTE la mise en page, sur chaque onglet sans distinction. */}
                    {(() => {
                      const vi = detail?.latestCheck?.visual_inspection || detail?.latestEstimation?.visual_inspection;
                      if (!vi || !vi.zones) return null;
                      return (
                        <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12 }}>
                          <div style={{ fontWeight: 700, fontSize: ".85rem", marginBottom: ".8rem" }}>📸 Analyse photo IA (état réel du véhicule)</div>
                          <div style={{ display: "flex", gap: "1.2rem", flexWrap: "wrap", alignItems: "flex-start" }}>
                            {vi.synthesis_svg && (
                              <div style={{ width: 160, flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: vi.synthesis_svg }} />
                        )}
                        <div style={{ flex: "1 1 220px", minWidth: 220 }}>
                          {vi.synthese && <div style={{ fontSize: ".84rem", color: "var(--text)", marginBottom: ".6rem", fontStyle: "italic" }}>« {vi.synthese} »</div>}
                          {vi.vigilance_points?.length > 0 && (
                            <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.6 }}>
                              {vi.vigilance_points.map((v, i) => <li key={i}>{v}</li>)}
                            </ul>
                          )}
                          {typeof vi.score_delta === "number" && vi.score_delta !== 0 && (
                            <div style={{ marginTop: ".6rem", fontSize: ".78rem", fontWeight: 600, color: vi.score_delta < 0 ? "var(--red, #c0392b)" : "var(--green)" }}>
                              Score ajusté selon l'état visuel réel : {vi.score_delta > 0 ? "+" : ""}{vi.score_delta} points
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "2rem", color: "var(--sub)" }}>Dossier introuvable.</div>
            )}
          </div>
        </div>
        {/* v42 — Visionneuse photo plein écran (remplace window.open, bloqué
            par les navigateurs pour les URLs data:). Clic n'importe où pour fermer. */}
        {lightboxSrc && (
          <div onClick={() => setLightboxSrc(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.88)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", cursor: "zoom-out" }}>
            <img src={lightboxSrc} alt="Photo agrandie" style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: 8, boxShadow: "0 20px 60px rgba(0,0,0,.5)" }} />
            <button onClick={() => setLightboxSrc(null)} aria-label="Fermer"
              style={{ position: "absolute", top: "1.2rem", right: "1.2rem", width: 40, height: 40, borderRadius: "50%", border: "none", background: "rgba(255,255,255,.15)", color: "#fff", fontSize: "1.3rem", cursor: "pointer" }}>✕</button>
          </div>
        )}
        {showLotModal && s.lot_id && (
          <LotProcessingModal lotId={s.lot_id} onClose={() => setShowLotModal(false)}
            onSaved={() => { fetchDossierDetail(selectedId); refresh(); }} />
        )}
        {showMarketModal && (
          <MarketCheckModal onClose={() => setShowMarketModal(false)} prefill={{
            brand: s.make || "",
            keywords: s.model || "",
            fuel: s.fuel || detail?.latestCheck?.fuel || "",
            gearbox: detail?.latestCheck?.gearbox || "",
            km: detail?.latestCheck?.mileage || detail?.latestEstimation?.km || "",
            priceMin: s.latest_estimation?.prix_viq ? Math.round(s.latest_estimation.prix_viq * 0.85) : "",
            priceMax: s.latest_estimation?.prix_viq ? Math.round(s.latest_estimation.prix_viq * 1.15) : "",
            yearMin: s.year || "",
          }} />
        )}
      </div>
    );
  }

  // ─── Vue liste : classée par actions manquantes ───
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel panel-wide panel-fullscreen" style={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <div className="panel-title">📁 Dossiers VIQ</div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body" style={{ overflowY: "auto", overflowX: "hidden" }}>
          {/* v104 — Règle : ces deux CTA ont été déplacés vers l'en-tête de
              l'Agenda (accueil), sur la même ligne que Ouvrir mes dossiers /
              Coter un véhicule — ils ne sont plus dupliqués ici. */}
          <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap", alignItems: "center", marginBottom: "1.2rem" }}>
            <input type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Rechercher par plaque, VIN, client ou modèle…"
              style={{ flex: "1 1 320px", minWidth: 200, padding: ".6rem .9rem", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: ".88rem" }} />
            <button className="btn btn-accent" style={{ flexShrink: 0, whiteSpace: "nowrap" }} onClick={() => onOpenCheck()}>+ Créer un Dossier VIQ</button>
          </div>

          {(() => {
            const FILTER_OPTIONS = [
              ["histovec", "HistoVec manquant", countMissing.histovec],
              ["carvertical", "CarVertical manquant", countMissing.carvertical],
              ["photos", "Photos non analysées", countMissing.photos],
              ["cote", "Cote à estimer", countMissing.cote],
              ["annonce", "Annonce à générer", countMissing.annonce],
              ["vente", "Vente à renseigner", countMissing.vente],
              ["lot", "Lot", countMissing.lot],
              ["complete", "Dossiers complets", countMissing.complete],
              ["crm", "Transmis via CRM", countMissing.crm],
            ];
            const toggleFilter = (key) => {
              setFilters(prev => {
                if (key === "all") return ["all"];
                const withoutAll = prev.filter(f => f !== "all");
                const next = withoutAll.includes(key) ? withoutAll.filter(f => f !== key) : [...withoutAll, key];
                return next.length ? next : ["all"];
              });
            };
            const isAll = filters.includes("all");
            const summaryLabel = isAll ? `Tous (${rows.length})` : `${filters.length} filtre${filters.length > 1 ? "s" : ""} sélectionné${filters.length > 1 ? "s" : ""}`;
            return (
              <div style={{ position: "relative", marginBottom: "1.2rem" }}>
                <button type="button" onClick={() => setFilterMenuOpen(o => !o)}
                  style={{ display: "flex", alignItems: "center", gap: ".5rem", fontSize: ".82rem", fontWeight: 700, padding: ".5rem .9rem", borderRadius: 8, cursor: "pointer",
                    border: "1.5px solid " + (isAll ? "var(--border)" : "var(--accent)"),
                    background: isAll ? "var(--card)" : "var(--accent-soft)", color: isAll ? "var(--sub)" : "var(--accent)" }}>
                  {summaryLabel}
                  <span style={{ fontSize: ".7rem" }}>{filterMenuOpen ? "▲" : "▼"}</span>
                </button>
                {filterMenuOpen && (
                  <>
                    <div onClick={() => setFilterMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
                    <div style={{ position: "absolute", top: "calc(100% + .4rem)", left: 0, zIndex: 20, background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 10, padding: ".6rem", minWidth: 280, maxHeight: 360, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.12)" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: ".55rem", padding: ".35rem .2rem", cursor: "pointer", fontSize: ".82rem", color: "var(--text)", fontWeight: 700, borderBottom: "1px solid var(--border)", marginBottom: ".3rem" }}>
                        <input type="checkbox" checked={isAll} onChange={() => toggleFilter("all")} />
                        Tous ({rows.length})
                      </label>
                      {FILTER_OPTIONS.map(([k, label, count]) => (
                        <label key={k} style={{ display: "flex", alignItems: "center", gap: ".55rem", padding: ".35rem .2rem", cursor: "pointer", fontSize: ".82rem", color: "var(--text)" }}>
                          <input type="checkbox" checked={filters.includes(k)} onChange={() => toggleFilter(k)} />
                          {label} ({count})
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {loading && <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>Chargement des dossiers…</div>}

          {!loading && dossiers.length > 0 && filtered.length === 0 && (
            <div style={{ background: "var(--card)", border: "1.5px dashed var(--border)", borderRadius: 12, padding: "1.6rem", textAlign: "center", color: "var(--sub)", fontSize: ".85rem" }}>
              {query ? "Aucun dossier ne correspond à votre recherche." : "Aucun dossier dans cette catégorie. 🎉"}
            </div>
          )}

          {/* v69 — Règle 2 : barre d'action pour la sélection de lot */}
          {Object.keys(lotSelection).length > 0 && (() => {
            const selCount = Object.keys(lotSelection).length;
            const existingLots = [];
            const seenLotIds = new Set();
            dossiers.forEach(d => {
              if (d.lot_id && !seenLotIds.has(d.lot_id)) { seenLotIds.add(d.lot_id); existingLots.push({ id: d.lot_id, name: d.lot_name }); }
            });
            return (
              <div style={{ display: "flex", alignItems: "center", gap: ".7rem", flexWrap: "wrap", padding: ".7rem .9rem", background: "var(--accent-soft)", border: "1px solid var(--accent)", borderRadius: 10, marginBottom: ".8rem" }}>
                <span style={{ fontSize: ".84rem", fontWeight: 700, color: "var(--accent)", whiteSpace: "nowrap", flexShrink: 0 }}>{selCount} véhicule{selCount > 1 ? "s" : ""} sélectionné{selCount > 1 ? "s" : ""}</span>
                <button className="btn btn-accent btn-sm" style={{ flexShrink: 0, whiteSpace: "nowrap" }} disabled={lotActionBusy || selCount < 2} onClick={createLotFromSelection}>
                  📦 Créer un lot
                </button>
                {existingLots.length > 0 && (
                  <select disabled={lotActionBusy} defaultValue="" onChange={e => { if (e.target.value) addSelectionToLot(e.target.value); e.target.value = ""; }}
                    style={{ fontSize: ".8rem", padding: ".4rem .6rem", borderRadius: 8, border: "1px solid var(--border)", flexShrink: 0, maxWidth: 220 }}>
                    <option value="" disabled>Ajouter à un lot existant…</option>
                    {existingLots.map(l => <option key={l.id} value={l.id}>{l.name || `Lot #${l.id.slice(0, 8)}`}</option>)}
                  </select>
                )}
                <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0, whiteSpace: "nowrap" }} onClick={clearLotSelection}>Annuler</button>
                {lotActionMsg && <span style={{ fontSize: ".76rem", color: "var(--red, #c0392b)" }}>{lotActionMsg}</span>}
              </div>
            );
          })()}

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: ".7rem" }}>
            {(() => {
              // v67 — Regroupe visuellement les Dossiers VIQ appartenant au même
              // lot (règle 5) — chaque véhicule garde son propre Dossier, la
              // carte individuelle ci-dessous reste totalement inchangée.
              const groups = [];
              const lotIndex = {};
              filtered.forEach(d => {
                if (d.lot_id && lotIndex[d.lot_id] != null) { groups[lotIndex[d.lot_id]].items.push(d); return; }
                if (d.lot_id) lotIndex[d.lot_id] = groups.length;
                groups.push({ lot_id: d.lot_id || null, items: [d] });
              });
              return groups.map(g => {
                const cards = g.items.map(d => (
              <div key={d.id} className="viq-dossier-card" style={{ background: "var(--card)", border: "1.5px solid var(--border)", borderRadius: 12, padding: ".9rem 1rem" }}>
                <div className="viq-dossier-card-header" style={{ display: "flex", justifyContent: "space-between", gap: ".8rem", flexWrap: "wrap", alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: ".66rem", color: "var(--muted)", fontFamily: "var(--font-mono)", marginBottom: ".3rem" }}>
                      {new Date(d.last_activity).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                    </div>
                    <div style={{ fontWeight: 700, color: "var(--text)", fontSize: ".9rem" }}>
                      {d.label}
                      {d.lot_id && <span style={{ marginLeft: ".4rem", fontSize: ".64rem", fontWeight: 700, color: "var(--accent)", background: "var(--accent-soft)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 10, padding: ".05rem .4rem" }}>🔗 Lot</span>}
                      {/* v72 — Règle 1 : tag "Transmis via CRM" — déjà inclus dans au moins une campagne de prospection envoyée. */}
                      {d.sentViaCRM && <span title={d.sentViaCRMLastAt ? `Dernière transmission : ${new Date(d.sentViaCRMLastAt).toLocaleDateString("fr-FR")}` : undefined} style={{ marginLeft: ".4rem", fontSize: ".64rem", fontWeight: 700, color: "var(--green)", background: "rgba(46,111,92,.1)", border: "1px solid rgba(46,111,92,.3)", borderRadius: 10, padding: ".05rem .4rem" }}>📤 Transmis via CRM</span>}
                    </div>
                    <div style={{ fontSize: ".74rem", color: "var(--sub)", marginBottom: ".5rem" }}>{d.customer_name || d.nextAction.label}</div>
                    {/* v68 — Aperçu enrichi (règle 4) : cote, plaque, VIN, provenance,
                        visibles sans ouvrir le dossier. */}
                    <div className="viq-dossier-identifiers" style={{ display: "flex", flexWrap: "wrap", gap: ".7rem", fontSize: ".76rem", color: "var(--sub)", marginBottom: ".5rem" }}>
                      {d.currentPrice != null && <span>💰 <strong style={{ color: "var(--green)" }}>{Number(d.currentPrice).toLocaleString("fr-FR")} €</strong></span>}
                      {d.plaque && <span style={{ fontFamily: "var(--font-mono)" }}>🔖 {d.plaque}</span>}
                      {d.vin && <span style={{ fontFamily: "var(--font-mono)" }}>VIN {d.vin}</span>}
                      {d.provenance && <span style={{ color: "var(--muted)" }}>📍 {d.provenance}</span>}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: ".35rem" }}>
                      <MilestoneBadge ok={d.hasHistovec} label="HistoVec" />
                      <MilestoneBadge ok={d.hasCarVertical} label="CarVertical" />
                      <MilestoneBadge ok={d.hasPhotos} label="Photos" />
                      <MilestoneBadge ok={d.hasPriceEstimate} label="Cote" />
                      <MilestoneBadge ok={d.hasAnnonce} label="Annonce" />
                      <MilestoneBadge ok={d.hasVente} label="Vente" />
                    </div>
                  </div>
                  {d.score != null && (
                    <div className="viq-dossier-score" style={{ display: "flex", alignItems: "center", gap: ".4rem", flexShrink: 0 }}>
                      <ScoreGauge score={d.score} size={42} />
                    </div>
                  )}
                  <span className="viq-dossier-afficher" onClick={() => setSelectedId(d.id)}
                    style={{ cursor: "pointer", textDecoration: "underline", color: "var(--accent)", fontSize: ".84rem", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, alignSelf: "center" }}>
                    Afficher
                  </span>
                </div>
                {d.knownIssues && (
                  <div style={{ marginTop: ".6rem", fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.5, background: "var(--bg)", border: "1px solid var(--border)", borderLeft: "3px solid var(--accent)", borderRadius: 8, padding: ".55rem .75rem" }}>
                    <strong style={{ color: "var(--text)" }}>🛡 Points de vigilance : </strong>{vigilancePoints(d.score, d.knownIssues)}
                  </div>
                )}
              </div>
                ));
                if (g.lot_id && g.items.length > 1) {
                  return (
                    <LotDossierGroup key={"lot-" + g.lot_id} items={g.items} cards={cards}
                      onDissolve={dissolveLot} onRename={renameLot} onRemoveMember={removeDossierFromLot} busy={lotActionBusy} />
                  );
                }
                // v69 — Règle 2 : case à cocher pour sélection multiple (créer un
                // lot / rejoindre un lot existant) — uniquement pour les
                // dossiers non groupés, la carte individuelle reste inchangée.
                return (
                  <div key={g.items[0].id} style={{ display: "flex", gap: ".5rem", alignItems: "flex-start" }}>
                    <input type="checkbox" checked={!!lotSelection[g.items[0].id]} onChange={() => toggleLotSelection(g.items[0].id)}
                      title="Sélectionner pour créer ou rejoindre un lot" style={{ marginTop: "1.1rem", flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>{cards[0]}</div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// v53 — Barre d'agent IA façon barre de recherche navigateur : guide rapide
// vers Dossier VIQ / Gestion client. Une plaque ou un VIN tapé directement
// est détecté côté client (aucun appel serveur) ; sinon une formulation
// libre est classée par /api/assistant/route (Claude), avec repli local si
// l'appel échoue — jamais bloquant.
function AppAssistantBar({ onCreateDossier, onOpenDossiers, onOpenLeads, onOpenProspection, onDashTab, onOpenCalls, onDashboard, onHistory, onPayment, onAffiliate }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // v84 — Règle : recherche prédictive — suggestions affichées DANS la
  // barre au fur et à mesure de la saisie, requête directe (pas d'IA),
  // séparée du bouton "→" qui lui déclenche la classification complète.
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // v113 — Agent IA multimodal (texte + image)
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoResult, setPhotoResult] = useState(null);
  const barRef = useRef(null);

  const PLATE_RE = /^[A-Z]{2}-?\d{3}-?[A-Z]{2}$/i;
  const VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/i;

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setSuggestions([]); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      apiGet(`/api/assistant/search-suggestions?q=${encodeURIComponent(term)}`).then(r => {
        if (!cancelled && r?.success) { setSuggestions(r.suggestions || []); setSuggestOpen(true); }
      }).catch(() => {});
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q]);

  useEffect(() => {
    const onDocClick = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setSuggestOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const localFallback = (query) => {
    const s = query.toLowerCase();
    if (/(client|lead|demande)/.test(s)) return "leads";
    if (/(campagne|prospect|contact|email|whatsapp|sms)\b/.test(s)) return "prospection";
    if (/(appel|t[ée]l[ée]phon)/.test(s)) return "calls";
    if (/(vente|vendu|vendre)/.test(s)) return "sale";
    if (/(paiement|abonnement|facture|tarif)/.test(s)) return "payment";
    if (/(mon compte|mon profil|mon espace|param[èe]tres)/.test(s)) return "account";
    if (/(tableau de bord|dashboard|statistiques?)/.test(s)) return "dashboard";
    if (/(apporteur|affili[ée]|parrainage)/.test(s)) return "affiliate";
    if (/(plaque|vin|contr[ôo]ler|identifier|nouveau)/.test(s)) return "check";
    return "dossiers";
  };

  // v84 — Élargi : ouvre désormais aussi Mon espace, Paiements, Dashboard,
  // Affaires, Mes appels — et un dossier PRÉCIS quand un seul correspond
  // (résolu côté serveur, voir action "open_dossier").
  const go = {
    check: () => onCreateDossier(),
    dossiers: () => onOpenDossiers(),
    open_dossier: (r) => onOpenDossiers(r?.dossier_id),
    leads: onOpenLeads,
    prospection: onOpenProspection,
    calls: () => (onOpenCalls ? onOpenCalls() : onOpenProspection()),
    sale: () => onDashTab && onDashTab("sale"),
    account: () => onHistory && onHistory(),
    payment: () => onPayment && onPayment(),
    dashboard: () => onDashboard && onDashboard(),
    affiliate: () => onAffiliate && onAffiliate(),
  };

  const run = async (override) => {
    const query = (override ?? q).trim();
    if (!query || busy) return;
    setSuggestOpen(false);
    const cleaned = query.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (PLATE_RE.test(query.replace(/\s/g, "")) || VIN_RE.test(cleaned)) {
      // v107 — Parcours pro express (zéro formulaire) : la plaque/le VIN
      // suffit. Le Dossier VIQ est créé côté serveur, décodage + contrôle +
      // estimation s'enrichissent en arrière-plan, et on ouvre directement le
      // dossier — plus aucune modale à remplir. En cas d'échec réseau, on
      // retombe sur l'ancien flux (modale pré-remplie) pour ne rien casser.
      setBusy(true);
      setMsg("Identification du véhicule…");
      try {
        const r = await apiPost("/api/dossiers/express", { identifier: cleaned });
        if (r?.success && r.dossier_id) {
          onOpenDossiers(r.dossier_id);
        } else {
          onCreateDossier(cleaned);
        }
      } catch (e) {
        onCreateDossier(cleaned);
      } finally { setBusy(false); setQ(""); setMsg(null); }
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const r = await apiPost("/api/assistant/route", { query });
      const action = (r?.success && r.action && !r.fallback) ? r.action : localFallback(query);
      setMsg(r?.message || "Voici ce qui devrait vous aider.");
      setTimeout(() => { (go[action] || go.dossiers)(r); }, 450);
    } catch (e) {
      setMsg(null);
      (go[localFallback(query)] || go.dossiers)();
    } finally { setBusy(false); setQ(""); }
  };

  const pickSuggestion = (s) => {
    setSuggestOpen(false); setQ(""); setMsg(null);
    onOpenDossiers(s.id);
  };

  return (
    <div style={{ marginBottom: "1.4rem" }} ref={barRef}>
      <div style={{ position: "relative" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: ".8rem",
          background: "linear-gradient(135deg, var(--white), var(--card))",
          border: "1.5px solid transparent",
          backgroundImage: "linear-gradient(var(--white), var(--white)), linear-gradient(120deg, var(--accent), #d9b877)",
          backgroundOrigin: "border-box", backgroundClip: "padding-box, border-box",
          borderRadius: 40, padding: ".5rem .6rem .5rem 1.4rem", boxShadow: "0 6px 22px rgba(168,128,58,.14)",
        }}>
          <div style={{
            flexShrink: 0, width: 38, height: 38, borderRadius: "50%",
            background: "linear-gradient(135deg, var(--accent), #d9b877)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.15rem", boxShadow: "0 2px 8px rgba(168,128,58,.35)",
          }}>
            🤖
          </div>
          <input
            value={q} onChange={e => setQ(e.target.value)}
            onFocus={() => { if (suggestions.length) setSuggestOpen(true); }}
            onKeyDown={e => e.key === "Enter" && run()}
            placeholder="Plaque, VIN, ou dites-moi ce que vous cherchez… (ex : « mes dernières demandes clients »)"
            style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontSize: ".95rem", padding: ".85rem 0", color: "var(--text)", fontWeight: 500 }} />
          {/* v113 — Agent IA multimodal : analyse photo directement depuis la
              barre de saisie (galerie ou appareil photo). Réutilise le moteur
              de vision déjà en place (/api/estimate/from-photos) — aucun
              nouveau traitement dupliqué. */}
          <input type="file" accept="image/*" multiple id="viq-agent-photos" style={{ display: "none" }}
            onChange={async e => {
              const files = Array.from(e.target.files || []).slice(0, 10);
              e.target.value = "";
              if (!files.length || photoBusy) return;
              setPhotoBusy(true); setMsg("Analyse des photos en cours…"); setPhotoResult(null);
              try {
                const imgs = (await Promise.all(files.map(f => compressImageFile(f)))).filter(Boolean);
                const r = await apiPost("/api/estimate/from-photos", { images: imgs });
                if (r?.success && r.data) { setPhotoResult(r.data); setMsg(null); }
                else setMsg(r?.error || "Analyse impossible sur ces photos.");
              } catch (err) { setMsg("Service d'analyse momentanément indisponible."); }
              finally { setPhotoBusy(false); }
            }} />
          <button type="button" title="Analyser des photos de véhicule"
            onClick={() => document.getElementById("viq-agent-photos")?.click()}
            disabled={photoBusy}
            style={{ flexShrink: 0, border: "none", background: "none", cursor: photoBusy ? "default" : "pointer", fontSize: "1.25rem", padding: ".4rem", opacity: photoBusy ? .5 : 1, lineHeight: 1 }}>
            {photoBusy ? "⏳" : "📷"}
          </button>
          <button onClick={() => run()} disabled={busy || !q.trim()}
            style={{ flexShrink: 0, border: "none", borderRadius: 32, padding: ".85rem 1.4rem", background: "linear-gradient(120deg, var(--accent), #d9b877)", color: "#fff", fontWeight: 700, fontSize: ".88rem", cursor: busy || !q.trim() ? "default" : "pointer", opacity: busy || !q.trim() ? .45 : 1, boxShadow: busy || !q.trim() ? "none" : "0 3px 10px rgba(168,128,58,.3)" }}>
            {busy ? "…" : "→"}
          </button>
        </div>
        {/* Résultat de l'analyse photo, rendu dans le fil de l'agent */}
        {photoResult && (
          <div style={{ marginTop: ".7rem", background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "1rem 1.2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: ".6rem" }}>
              <div style={{ fontWeight: 800, fontSize: ".92rem" }}>📷 Analyse photo IA</div>
              <button onClick={() => setPhotoResult(null)} style={{ border: "none", background: "none", cursor: "pointer", color: "var(--muted)", fontSize: ".9rem" }}>✕</button>
            </div>
            {(photoResult.make || photoResult.model) && (
              <div style={{ fontSize: ".82rem", color: "var(--sub)", marginTop: ".3rem" }}>
                {[photoResult.make, photoResult.model, photoResult.year].filter(Boolean).join(" ")}
              </div>
            )}
            {photoResult.visual_inspection?.summary && (
              <div style={{ fontSize: ".84rem", color: "var(--text)", lineHeight: 1.6, marginTop: ".5rem" }}>
                {photoResult.visual_inspection.summary}
              </div>
            )}
            {photoResult.visual_inspection?.score != null && (
              <div style={{ fontSize: ".82rem", marginTop: ".5rem" }}>
                Score esthétique : <strong style={{ color: "var(--green)" }}>{photoResult.visual_inspection.score}/100</strong>
              </div>
            )}
            {Array.isArray(photoResult.visual_inspection?.defects) && photoResult.visual_inspection.defects.length > 0 && (
              <ul style={{ fontSize: ".8rem", color: "var(--sub)", lineHeight: 1.6, marginTop: ".5rem", paddingLeft: "1.1rem" }}>
                {photoResult.visual_inspection.defects.slice(0, 8).map((d, i) => (
                  <li key={i}>{typeof d === "string" ? d : (d.label || d.zone || JSON.stringify(d))}</li>
                ))}
              </ul>
            )}
            {photoResult.argus_mid && (
              <div style={{ fontSize: ".84rem", marginTop: ".5rem" }}>
                Impact sur la cote : <strong>{Number(photoResult.argus_mid).toLocaleString("fr-FR")} €</strong> estimé
              </div>
            )}
          </div>
        )}
        {/* v84 — Suggestions prédictives : dossiers correspondant à la saisie en cours */}
        {suggestOpen && suggestions.length > 0 && (
          <div style={{ position: "absolute", top: "calc(100% + .5rem)", left: "1.4rem", right: "1.4rem", zIndex: 30, background: "var(--white)", border: "1.5px solid var(--border)", borderRadius: 16, boxShadow: "0 10px 30px rgba(0,0,0,.12)", overflow: "hidden" }}>
            {suggestions.map(s => (
              <button key={s.id} type="button" onClick={() => pickSuggestion(s)}
                style={{ display: "block", width: "100%", textAlign: "left", padding: ".7rem 1rem", background: "none", border: "none", borderBottom: "1px solid var(--border)", cursor: "pointer", fontSize: ".86rem", color: "var(--text)" }}>
                <span style={{ fontWeight: 700 }}>{s.label}</span>
                <span style={{ color: "var(--muted)", fontSize: ".76rem", marginLeft: ".5rem" }}>
                  {s.plaque || s.vin || ""}{s.customer_name ? ` · ${s.customer_name}` : ""}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
      {msg && <div style={{ fontSize: ".78rem", color: "var(--accent)", marginTop: ".6rem", paddingLeft: "1.4rem", fontWeight: 600 }}>💬 {msg}</div>}
    </div>
  );
}

// ─── v99 — Agenda Intelligent VehicleIQ ──────────────────────────────────────
// Centre de pilotage quotidien : agrège en direct (aucune duplication côté
// client) les événements réels de VehicleIQ — leads, Dossiers VIQ, campagnes,
// ventes, appels IA — plus les items saisis à la main (rappel/tâche/note/rdv).
// La catégorie "Comptabilité" (facturation, TVA, URSSAF) n'existe pas encore
// comme module VehicleIQ : elle reste donc simplement vide plutôt que d'
// afficher des chiffres inventés.
const AGENDA_CATEGORY_HEX = {
  lead: "var(--blue)", dossier: "var(--green)", crm: "var(--blue)",
  marketing: "#8B5CF6", vente: "var(--gold)", compta: "var(--orange)",
  alerte: "var(--red)", important: "var(--gold)",
};
const AGENDA_ITEM_TYPES = [
  { key: "rappel", label: "Rappel", icon: "🔔" },
  { key: "tache", label: "Tâche", icon: "✅" },
  { key: "rdv", label: "Rendez-vous", icon: "📅" },
  { key: "note", label: "Note", icon: "📝" },
];

function agendaFmtTime(d) {
  try { return new Date(d).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch (e) { return ""; }
}
function agendaFmtDay(d) {
  try { return new Date(d).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" }); } catch (e) { return ""; }
}
function agendaStartOfWeek(d) {
  const x = new Date(d); const day = (x.getDay() + 6) % 7; // lundi=0
  x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - day); return x;
}

// ─── v104 — "Fixer mes objectifs" : critères d'auto-validation des Dossiers
// VIQ depuis les leads entrants (backend déjà en place : GET/PATCH
// /api/lead-automation/settings). ────────────────────────────────────────────
function LeadAutomationSettingsModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [profileTypes, setProfileTypes] = useState(["pro", "particulier", "autre"]);
  const [autoRunCheck, setAutoRunCheck] = useState(true);
  const [autoRunEstimate, setAutoRunEstimate] = useState(true);
  const [saved, setSaved] = useState(false);

  // v105 — Critères véhicule : marque, modèle, motorisation, année, en listes
  // déroulantes, + exigences HistoVec/photo pour qu'un lead soit auto-validé.
  const [brand, setBrand] = useState(""); // "" = toutes marques
  const [model, setModel] = useState(""); // "" = tous modèles
  const [fuelType, setFuelType] = useState(""); // "" = toutes motorisations
  const [yearMin, setYearMin] = useState("");
  const [yearMax, setYearMax] = useState("");
  const [requireHistovec, setRequireHistovec] = useState(false);
  const [requirePhoto, setRequirePhoto] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);

  const FUEL_OPTIONS = ["Essence", "Diesel", "Hybride", "Électrique", "GPL"];
  const YEARS = Array.from({ length: 2026 - 2000 + 1 }, (_, i) => 2026 - i);

  useEffect(() => {
    apiGet("/api/catalogue/brands").then(r => { if (r?.success) setBrandOptions(r.brands || []); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!brand) { setModelOptions([]); return; }
    apiGet(`/api/catalogue/models?brand=${encodeURIComponent(brand)}`).then(r => { if (r?.success) setModelOptions(r.models || []); }).catch(() => {});
  }, [brand]);

  useEffect(() => {
    apiGet("/api/lead-automation/settings").then(r => {
      if (r?.success && r.settings) {
        setEnabled(!!r.settings.enabled);
        setProfileTypes(r.settings.profileTypes || ["pro", "particulier", "autre"]);
        setAutoRunCheck(!!r.settings.autoRunCheck);
        setAutoRunEstimate(!!r.settings.autoRunEstimate);
        setBrand((r.settings.brands || [])[0] || "");
        setModel((r.settings.models || [])[0] || "");
        setFuelType((r.settings.fuelTypes || [])[0] || "");
        setYearMin(r.settings.yearMin || "");
        setYearMax(r.settings.yearMax || "");
        setRequireHistovec(!!r.settings.requireHistovec);
        setRequirePhoto(!!r.settings.requirePhoto);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggleProfile = (p) => setProfileTypes(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiPatch("/api/lead-automation/settings", {
        enabled, profileTypes, autoRunCheck, autoRunEstimate,
        brands: brand ? [brand] : [], models: model ? [model] : [], fuelTypes: fuelType ? [fuelType] : [],
        yearMin: yearMin || null, yearMax: yearMax || null,
        requireHistovec, requirePhoto,
      });
      if (r?.success) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    } catch (e) { console.warn("[lead-automation save]", e.message); }
    finally { setSaving(false); }
  };

  const PROFILES = [["pro", "Professionnel de l'achat/revente"], ["particulier", "Particulier"], ["autre", "Autre"]];
  const selectStyle = { width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", borderRadius: 8, padding: ".5rem .6rem", fontSize: ".84rem", background: "var(--card)", color: "var(--text)" };
  const labelStyle = { fontSize: ".74rem", fontWeight: 700, color: "var(--sub)", display: "block", marginBottom: ".3rem" };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 560 }}>
        <div className="panel-header">
          <div className="panel-title">🎯 Fixer mes objectifs</div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          {loading ? <div style={{ fontSize: ".85rem", color: "var(--muted)" }}>Chargement…</div> : (
            <>
              <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1rem" }}>
                Définissez quand VehicleIQ doit créer automatiquement un Dossier VIQ à partir d'un lead reçu, et lancer seul le Contrôle et l'Estimation — sans que vous ayez à cliquer sur "Oui".
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: ".6rem", cursor: "pointer", marginBottom: "1.1rem", padding: ".7rem .9rem", background: "var(--card)", borderRadius: 10 }}>
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} style={{ width: 18, height: 18, accentColor: "var(--accent)" }} />
                <span style={{ fontWeight: 700, fontSize: ".88rem" }}>Activer l'automatisation</span>
              </label>

              <div style={{ opacity: enabled ? 1 : .45, pointerEvents: enabled ? "auto" : "none" }}>
                <div style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--sub)", marginBottom: ".5rem" }}>Profils clients à auto-valider</div>
                <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", marginBottom: "1.1rem" }}>
                  {PROFILES.map(([key, label]) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: ".6rem", cursor: "pointer", fontSize: ".85rem" }}>
                      <input type="checkbox" checked={profileTypes.includes(key)} onChange={() => toggleProfile(key)} style={{ width: 16, height: 16, accentColor: "var(--accent)" }} />
                      {label}
                    </label>
                  ))}
                </div>

                <div style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--sub)", marginBottom: ".5rem" }}>Critères véhicule (optionnel — laissez sur "Tous" pour ne pas filtrer)</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".7rem", marginBottom: ".7rem" }}>
                  <div>
                    <label style={labelStyle}>Marque</label>
                    <select style={selectStyle} value={brand} onChange={e => { setBrand(e.target.value); setModel(""); }}>
                      <option value="">Toutes les marques</option>
                      {brandOptions.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Modèle</label>
                    <select style={selectStyle} value={model} onChange={e => setModel(e.target.value)} disabled={!brand}>
                      <option value="">{brand ? "Tous les modèles" : "Choisissez une marque d'abord"}</option>
                      {modelOptions.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: ".7rem", marginBottom: "1.1rem" }}>
                  <div>
                    <label style={labelStyle}>Motorisation</label>
                    <select style={selectStyle} value={fuelType} onChange={e => setFuelType(e.target.value)}>
                      <option value="">Toutes</option>
                      {FUEL_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Année min</label>
                    <select style={selectStyle} value={yearMin} onChange={e => setYearMin(e.target.value)}>
                      <option value="">—</option>
                      {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Année max</label>
                    <select style={selectStyle} value={yearMax} onChange={e => setYearMax(e.target.value)}>
                      <option value="">—</option>
                      {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", marginBottom: "1.1rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: ".6rem", cursor: "pointer", fontSize: ".85rem" }}>
                    <input type="checkbox" checked={requireHistovec} onChange={e => setRequireHistovec(e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--accent)" }} />
                    Auto-valider uniquement si le client a importé un rapport HistoVec
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: ".6rem", cursor: "pointer", fontSize: ".85rem" }}>
                    <input type="checkbox" checked={requirePhoto} onChange={e => setRequirePhoto(e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--accent)" }} />
                    Auto-valider uniquement si le client a ajouté au moins une photo
                  </label>
                </div>

                <div style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--sub)", marginBottom: ".5rem" }}>Une fois le Dossier VIQ créé</div>
                <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: ".6rem", cursor: "pointer", fontSize: ".85rem" }}>
                    <input type="checkbox" checked={autoRunEstimate} onChange={e => setAutoRunEstimate(e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--accent)" }} />
                    Lancer automatiquement l'Estimation (la Cote apparaît seule)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: ".6rem", cursor: "pointer", fontSize: ".85rem" }}>
                    <input type="checkbox" checked={autoRunCheck} onChange={e => setAutoRunCheck(e.target.checked)} style={{ width: 16, height: 16, accentColor: "var(--accent)" }} />
                    Lancer automatiquement le Contrôle (si plaque/VIN fourni par le client)
                  </label>
                </div>
              </div>

              <button className="btn btn-accent" disabled={saving} onClick={save} style={{ width: "100%", justifyContent: "center", marginTop: "1.4rem" }}>
                {saving ? "…" : saved ? "✓ Enregistré" : "Enregistrer"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── v104 — "Vérifier le marché" : génère un lien de recherche LeBonCoin
// ciblé (marque, mots-clés, prix, année) — plus d'appel web direct, un lien
// prêt à ouvrir vers la recherche LeBonCoin correspondante. ──────────────────
function buildLeBonCoinUrl({ brand, keywords, priceMin, priceMax, yearMin, yearMax, fuel, gearbox, kmMax }) {
  const params = new URLSearchParams();
  params.set("category", "2"); // Voitures
  const text = [brand, keywords].filter(Boolean).join(" ").trim();
  if (text) params.set("text", text);
  if (priceMin || priceMax) params.set("price", `${priceMin || "min"}-${priceMax || "max"}`);
  if (yearMin || yearMax) params.set("regdate", `${yearMin || "min"}-${yearMax || "max"}`);
  if (kmMax) params.set("mileage", `min-${kmMax}`);
  // Codes carburant LeBonCoin : 1=Essence, 2=Diesel, 3=GPL, 4=Électrique, 6=Hybride
  const FUEL_CODES = { essence: "1", diesel: "2", gpl: "3", "électrique": "4", electrique: "4", hybride: "6" };
  const fuelCode = FUEL_CODES[String(fuel || "").toLowerCase().trim()];
  if (fuelCode) params.set("fuel", fuelCode);
  // Codes boîte LeBonCoin : 1=Manuelle, 2=Automatique
  const gb = String(gearbox || "").toLowerCase();
  if (gb.startsWith("man")) params.set("gearbox", "1");
  else if (gb.startsWith("auto")) params.set("gearbox", "2");
  if (brand) params.set("u_car_brand", brand.trim().toUpperCase());
  return `https://www.leboncoin.fr/recherche?${params.toString()}`;
}

function MarketCheckModal({ onClose, prefill }) {
  const [brand, setBrand] = useState(prefill?.brand || "");
  const [keywords, setKeywords] = useState(prefill?.keywords || "");
  const [priceMin, setPriceMin] = useState(prefill?.priceMin || "");
  const [priceMax, setPriceMax] = useState(prefill?.priceMax || "");
  const [yearMin, setYearMin] = useState(prefill?.yearMin || "");
  const [yearMax, setYearMax] = useState(prefill?.yearMax || "");
  // v107 — Champs pré-remplis depuis le Dossier VIQ (le pro ne modifie que
  // si nécessaire) — carburant / boîte / km max, transposés en filtres
  // natifs LeBonCoin dans l'URL générée.
  const [fuel, setFuel] = useState(prefill?.fuel || "");
  const [gearbox, setGearbox] = useState(prefill?.gearbox || "");
  const [kmMax, setKmMax] = useState(prefill?.km ? Math.round(Number(prefill.km) * 1.3) : "");

  const url = buildLeBonCoinUrl({ brand, keywords, priceMin, priceMax, yearMin, yearMax, fuel, gearbox, kmMax });
  const inputStyle = { width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", borderRadius: 8, padding: ".55rem .7rem", fontSize: ".85rem", background: "var(--card)", color: "var(--text)" };
  const labelStyle = { fontSize: ".74rem", fontWeight: 700, color: "var(--sub)", display: "block", marginBottom: ".3rem" };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="panel" style={{ maxWidth: 560 }}>
        <div className="panel-header">
          <div className="panel-title">🔎 Vérifier le marché sur LeBonCoin</div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          <div style={{ fontSize: ".82rem", color: "var(--sub)", lineHeight: 1.6, marginBottom: "1rem" }}>
            Générez un lien de recherche LeBonCoin ciblé pour voir les annonces comparables actuellement sur le marché.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".8rem", marginBottom: ".8rem" }}>
            <div>
              <label style={labelStyle}>Marque</label>
              <input style={inputStyle} value={brand} onChange={e => setBrand(e.target.value)} placeholder="Ex : Peugeot" />
            </div>
            <div>
              <label style={labelStyle}>Modèle / mots-clés</label>
              <input style={inputStyle} value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="Ex : 308 GT" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".8rem", marginBottom: ".8rem" }}>
            <div>
              <label style={labelStyle}>Prix min (€)</label>
              <input style={inputStyle} type="number" value={priceMin} onChange={e => setPriceMin(e.target.value)} placeholder="0" />
            </div>
            <div>
              <label style={labelStyle}>Prix max (€)</label>
              <input style={inputStyle} type="number" value={priceMax} onChange={e => setPriceMax(e.target.value)} placeholder="—" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".8rem", marginBottom: ".8rem" }}>
            <div>
              <label style={labelStyle}>Année min</label>
              <input style={inputStyle} type="number" value={yearMin} onChange={e => setYearMin(e.target.value)} placeholder="Ex : 2018" />
            </div>
            <div>
              <label style={labelStyle}>Année max</label>
              <input style={inputStyle} type="number" value={yearMax} onChange={e => setYearMax(e.target.value)} placeholder="—" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: ".8rem", marginBottom: "1.1rem" }}>
            <div>
              <label style={labelStyle}>Carburant</label>
              <select style={inputStyle} value={fuel} onChange={e => setFuel(e.target.value)}>
                <option value="">Tous</option>
                {["Essence", "Diesel", "Hybride", "Électrique", "GPL"].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Boîte</label>
              <select style={inputStyle} value={gearbox} onChange={e => setGearbox(e.target.value)}>
                <option value="">Toutes</option>
                <option value="Manuelle">Manuelle</option>
                <option value="Automatique">Automatique</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Km max</label>
              <input style={inputStyle} type="number" value={kmMax} onChange={e => setKmMax(e.target.value)} placeholder="—" />
            </div>
          </div>
          <button className="btn btn-accent" style={{ width: "100%", justifyContent: "center" }}
            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}>
            Voir les annonces sur LeBonCoin →
          </button>
          <div style={{ fontSize: ".68rem", color: "var(--muted)", marginTop: ".6rem", wordBreak: "break-all" }}>{url}</div>
        </div>
      </div>
    </div>
  );
}

function AgendaQuickAddPopover({ onClose, onCreated }) {
  const [type, setType] = useState("rappel");
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [date, setDate] = useState(() => {
    const n = new Date(); n.setMinutes(n.getMinutes() - n.getMinutes() % 15 + 15);
    return n.toISOString().slice(0, 16);
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      const r = await apiPost("/api/agenda/items", { type, title: title.trim(), detail: detail.trim() || null, eventDate: new Date(date).toISOString() });
      if (r?.success) { onCreated?.(); onClose(); }
    } catch (e) { console.warn("[agenda quick-add]", e.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 39 }} />
      <div style={{ position: "absolute", right: 0, top: "calc(100% + .5rem)", zIndex: 40, background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "0 12px 34px rgba(0,0,0,.18)", padding: "1rem", width: 280 }}>
        <div style={{ display: "flex", gap: ".35rem", marginBottom: ".7rem", flexWrap: "wrap" }}>
          {AGENDA_ITEM_TYPES.map(t => (
            <button key={t.key} type="button" onClick={() => setType(t.key)}
              style={{ border: "1.5px solid " + (type === t.key ? "var(--accent)" : "var(--border)"), background: type === t.key ? "var(--accent-soft, rgba(168,128,58,.1))" : "var(--card)", borderRadius: 10, padding: ".3rem .55rem", fontSize: ".76rem", fontWeight: 700, cursor: "pointer", color: "var(--text)" }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Titre" autoFocus
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", borderRadius: 8, padding: ".55rem .7rem", fontSize: ".85rem", marginBottom: ".5rem" }} />
        <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)}
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", borderRadius: 8, padding: ".5rem .7rem", fontSize: ".82rem", marginBottom: ".5rem" }} />
        <textarea value={detail} onChange={e => setDetail(e.target.value)} placeholder="Détail (optionnel)" rows={2}
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", borderRadius: 8, padding: ".5rem .7rem", fontSize: ".82rem", marginBottom: ".7rem", resize: "vertical", fontFamily: "inherit" }} />
        <button className="btn btn-accent" disabled={!title.trim() || busy} onClick={submit} style={{ width: "100%", justifyContent: "center", opacity: !title.trim() || busy ? .5 : 1 }}>
          {busy ? "…" : "Ajouter"}
        </button>
      </div>
    </>
  );
}

function AgendaIntelligent({ onOpenDossiers, onOpenLeads, onOpenProspection, onOpenDocuments }) {
  const [view, setView] = useState("semaine"); // jour | semaine | mois | timeline
  const [anchor, setAnchor] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [insights, setInsights] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showGoalsModal, setShowGoalsModal] = useState(false); // v104 — Fixer mes objectifs
  const [showMarketModal, setShowMarketModal] = useState(false); // v104 — Vérifier le marché

  const range = useMemo(() => {
    if (view === "jour") { const s = new Date(anchor); s.setHours(0, 0, 0, 0); const e = new Date(s); e.setDate(e.getDate() + 1); return [s, e]; }
    if (view === "mois") { const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1); const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1); return [s, e]; }
    if (view === "timeline") { const s = new Date(); s.setHours(0, 0, 0, 0); const e = new Date(s); e.setDate(e.getDate() + 30); return [s, e]; }
    const s = agendaStartOfWeek(anchor); const e = new Date(s); e.setDate(e.getDate() + 7); return [s, e];
  }, [view, anchor]);

  const reload = useCallback(() => {
    setLoading(true);
    const [s, e] = range;
    Promise.all([
      apiGet(`/api/agenda/events?start=${encodeURIComponent(s.toISOString())}&end=${encodeURIComponent(e.toISOString())}`).catch(() => null),
      apiGet("/api/agenda/kpis").catch(() => null),
    ]).then(([ev, k]) => {
      if (ev?.success) { setEvents(ev.events || []); setInsights(ev.insights || []); }
      if (k?.success) setKpis(k.kpis);
    }).finally(() => setLoading(false));
  }, [range]);

  useEffect(() => { reload(); }, [reload]);

  const filteredEvents = typeFilter === "all" ? events : events.filter(e => e.category === typeFilter);
  const upcoming = useMemo(() => {
    const now = new Date();
    return filteredEvents.filter(e => new Date(e.date) >= new Date(now.getTime() - 3600000)).slice(0, 8);
  }, [filteredEvents]);

  const goToday = () => setAnchor(new Date());
  const nav = (dir) => setAnchor(a => {
    const n = new Date(a);
    if (view === "jour") n.setDate(n.getDate() + dir);
    else if (view === "mois") n.setMonth(n.getMonth() + dir);
    else n.setDate(n.getDate() + dir * 7);
    return n;
  });

  const openEvent = (ev) => {
    if (ev.dossierId) return onOpenDossiers(ev.dossierId);
    if (ev.category === "lead") return onOpenLeads();
    if (ev.category === "marketing") return onOpenProspection();
    if (ev.manual) {
      apiPatch(`/api/agenda/items/${ev.itemId}`, { done: !ev.done }).then(reload).catch(() => {});
    }
  };

  const days = useMemo(() => {
    if (view !== "semaine") return [];
    const [s] = range;
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(d.getDate() + i); return d; });
  }, [range, view]);
  const HOURS = Array.from({ length: 11 }, (_, i) => 8 + i); // 08:00 → 18:00

  const eventsForDay = (d) => filteredEvents.filter(e => new Date(e.date).toDateString() === d.toDateString());

  const kpiCards = kpis ? [
    ["👤", kpis.leadsToday, "Leads du jour", onOpenLeads],
    ["📁", kpis.dossiersToday, "Dossiers créés", () => onOpenDossiers()],
    ["📊", kpis.estimationsToday, "Estimations réalisées", () => onOpenDossiers()],
    ["📣", kpis.activeCampaigns, "Campagnes actives", onOpenProspection],
    ["🔔", kpis.remindersDue, "Rappels à faire", null],
    ["💰", `${Number(kpis.revenueToday || 0).toLocaleString("fr-FR")} €`, "Chiffre d'affaires du jour", null],
    ["✅", kpis.salesToday, "Ventes du jour", () => onOpenDossiers()],
  ] : [];

  if (collapsed) {
    const todays = filteredEvents.filter(e => new Date(e.date).toDateString() === new Date().toDateString());
    return (
      <div style={{ background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, padding: "1rem 1.1rem", minWidth: 220, display: "flex", flexDirection: "column", gap: ".6rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontWeight: 800, fontSize: ".95rem" }}>📅 Aujourd'hui</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setCollapsed(false)} style={{ padding: ".3rem .5rem" }}>⤢</button>
        </div>
        {todays.length === 0 && <div style={{ fontSize: ".78rem", color: "var(--muted)" }}>Rien de prévu aujourd'hui.</div>}
        {todays.slice(0, 6).map(ev => (
          <div key={ev.id} onClick={() => openEvent(ev)} style={{ display: "flex", gap: ".5rem", alignItems: "flex-start", cursor: "pointer", fontSize: ".78rem" }}>
            <span style={{ width: 8, height: 8, borderRadius: 4, background: AGENDA_CATEGORY_HEX[ev.category] || "var(--muted)", marginTop: 4, flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, color: "var(--text)", textDecoration: ev.done ? "line-through" : "none" }}>{ev.title}</div>
              <div style={{ color: "var(--muted)" }}>{agendaFmtTime(ev.date)}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
    <div style={{ background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, padding: "1.4rem 1.5rem", display: "flex", flexDirection: "column", gap: "1rem", height: "100%" }}>
      {/* ── En-tête ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".6rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".6rem" }}>
          <span style={{ fontSize: "1.3rem" }}>📆</span>
          <span style={{ fontWeight: 800, fontSize: "1.05rem", color: "var(--text)" }}>Agenda</span>
          <select value={view} onChange={e => setView(e.target.value)}
            style={{ border: "1px solid var(--border)", borderRadius: 10, padding: ".3rem .6rem", fontSize: ".78rem", fontWeight: 600, background: "var(--card)", color: "var(--text)" }}>
            <option value="jour">Vue Jour</option>
            <option value="semaine">Vue Semaine</option>
            <option value="mois">Vue Mois</option>
            <option value="timeline">Timeline</option>
          </select>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: ".4rem", flexWrap: "wrap" }}>
          {/* v106 — Règle : trop de CTA sur cette ligne — ne conserver que ces
              deux-là. Ouvrir mes dossiers / Coter un véhicule / Afficher mes
              leads / Trouver des leads restent accessibles via la barre de
              recherche et les Dossiers VIQ, pas ici. */}
          <button className="btn btn-outline btn-sm" onClick={() => setShowGoalsModal(true)}>🎯 Fixer mes objectifs</button>
          <button className="btn btn-outline btn-sm" onClick={() => setShowMarketModal(true)}>🔎 Vérifier le marché sur LeBonCoin</button>
          <button className="btn btn-outline btn-sm" onClick={goToday}>Aujourd'hui</button>
          <button className="btn btn-ghost btn-sm" onClick={() => nav(-1)} style={{ padding: ".4rem .6rem" }}>‹</button>
          <button className="btn btn-ghost btn-sm" onClick={() => nav(1)} style={{ padding: ".4rem .6rem" }}>›</button>
          <button className="btn btn-outline btn-sm" onClick={() => setCollapsed(true)}>Replier ▲</button>
        </div>
      </div>

      {/* ── Insights IA ── */}
      {insights.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: ".35rem" }}>
          {insights.map((msg, i) => (
            <div key={i} style={{ fontSize: ".78rem", color: "var(--accent)", fontWeight: 600, background: "var(--card)", borderRadius: 10, padding: ".5rem .8rem" }}>
              ✦ {msg}
            </div>
          ))}
        </div>
      )}

      {/* ── Corps : grille + activité à venir ── */}
      <div style={{ display: "flex", gap: "1.2rem", flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: "2 1 380px", minWidth: 280, overflowX: "auto" }}>
          {view === "semaine" && (
            <div style={{ display: "grid", gridTemplateColumns: "44px repeat(7, 1fr)", fontSize: ".7rem", minWidth: 560 }}>
              <div />
              {days.map(d => (
                <div key={+d} style={{ textAlign: "center", fontWeight: 700, color: d.toDateString() === new Date().toDateString() ? "var(--accent)" : "var(--sub)", padding: ".2rem 0" }}>
                  {agendaFmtDay(d)}
                </div>
              ))}
              {HOURS.map(h => (
                <Fragment key={h}>
                  <div style={{ color: "var(--muted)", textAlign: "right", paddingRight: ".3rem", borderTop: "1px solid var(--border)" }}>{String(h).padStart(2, "0")}:00</div>
                  {days.map(d => {
                    const dayEvents = eventsForDay(d).filter(e => new Date(e.date).getHours() === h);
                    return (
                      <div key={+d + "-" + h} style={{ borderTop: "1px solid var(--border)", borderLeft: "1px solid var(--border)", minHeight: 30, padding: 2 }}>
                        {dayEvents.map(ev => (
                          <div key={ev.id} onClick={() => openEvent(ev)} title={ev.subtitle || ev.title}
                            style={{ background: (AGENDA_CATEGORY_HEX[ev.category] || "var(--muted)") + "22", borderLeft: "3px solid " + (AGENDA_CATEGORY_HEX[ev.category] || "var(--muted)"), borderRadius: 5, padding: "2px 4px", marginBottom: 2, cursor: "pointer", fontSize: ".64rem", lineHeight: 1.3, opacity: ev.done ? .5 : 1 }}>
                            <div style={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.title}</div>
                            {ev.subtitle && <div style={{ color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.subtitle}</div>}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          )}

          {view === "jour" && (
            <div style={{ display: "flex", flexDirection: "column", gap: ".3rem" }}>
              <div style={{ fontWeight: 700, fontSize: ".85rem", marginBottom: ".2rem" }}>{agendaFmtDay(anchor)}</div>
              {HOURS.map(h => {
                const hourEvents = eventsForDay(anchor).filter(e => new Date(e.date).getHours() === h);
                return (
                  <div key={h} style={{ display: "flex", gap: ".6rem", borderTop: "1px solid var(--border)", padding: ".3rem 0", minHeight: 30 }}>
                    <div style={{ width: 44, fontSize: ".72rem", color: "var(--muted)", flexShrink: 0 }}>{String(h).padStart(2, "0")}:00</div>
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
                      {hourEvents.map(ev => (
                        <div key={ev.id} onClick={() => openEvent(ev)}
                          style={{ background: (AGENDA_CATEGORY_HEX[ev.category] || "var(--muted)") + "22", borderLeft: "3px solid " + (AGENDA_CATEGORY_HEX[ev.category] || "var(--muted)"), borderRadius: 6, padding: ".3rem .5rem", cursor: "pointer", fontSize: ".76rem", opacity: ev.done ? .5 : 1 }}>
                          <span style={{ fontWeight: 700 }}>{ev.title}</span>{ev.subtitle && <span style={{ color: "var(--muted)" }}> — {ev.subtitle}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {view === "mois" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3, fontSize: ".68rem" }}>
              {Array.from({ length: new Date(anchor.getFullYear(), anchor.getMonth(), 1).getDay() === 0 ? 6 : new Date(anchor.getFullYear(), anchor.getMonth(), 1).getDay() - 1 }).map((_, i) => <div key={"pad" + i} />)}
              {Array.from({ length: new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate() }, (_, i) => i + 1).map(day => {
                const d = new Date(anchor.getFullYear(), anchor.getMonth(), day);
                const dayEvents = eventsForDay(d);
                return (
                  <div key={day} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: ".3rem", minHeight: 56, background: d.toDateString() === new Date().toDateString() ? "var(--card)" : "transparent" }}>
                    <div style={{ fontWeight: 700, color: "var(--sub)" }}>{day}</div>
                    <div style={{ display: "flex", gap: 2, flexWrap: "wrap", marginTop: 3 }}>
                      {dayEvents.slice(0, 5).map(ev => <span key={ev.id} title={ev.title} style={{ width: 6, height: 6, borderRadius: 3, background: AGENDA_CATEGORY_HEX[ev.category] || "var(--muted)" }} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {view === "timeline" && (
            <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
              {filteredEvents.length === 0 && <div style={{ fontSize: ".82rem", color: "var(--muted)" }}>Aucun événement sur cette période.</div>}
              {filteredEvents.map(ev => (
                <div key={ev.id} onClick={() => openEvent(ev)} style={{ display: "flex", gap: ".7rem", alignItems: "flex-start", cursor: "pointer", opacity: ev.done ? .5 : 1 }}>
                  <div style={{ fontSize: ".72rem", color: "var(--muted)", width: 78, flexShrink: 0 }}>{agendaFmtDay(ev.date)} · {agendaFmtTime(ev.date)}</div>
                  <span style={{ width: 8, height: 8, borderRadius: 4, background: AGENDA_CATEGORY_HEX[ev.category] || "var(--muted)", marginTop: 5, flexShrink: 0 }} />
                  <div style={{ fontSize: ".82rem" }}>
                    <span style={{ fontWeight: 700, color: "var(--text)" }}>{ev.title}</span>
                    {ev.subtitle && <span style={{ color: "var(--muted)" }}> — {ev.subtitle}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          {loading && <div style={{ fontSize: ".76rem", color: "var(--muted)", marginTop: ".5rem" }}>Chargement…</div>}
        </div>

        {/* ── Activité à venir ── */}
        <div style={{ flex: "1 1 240px", minWidth: 220, display: "flex", flexDirection: "column", gap: ".6rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontWeight: 800, fontSize: ".88rem" }}>Activité à venir</span>
            <div style={{ display: "flex", gap: ".3rem", position: "relative" }}>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                style={{ border: "1px solid var(--border)", borderRadius: 8, padding: ".25rem .4rem", fontSize: ".7rem", background: "var(--card)" }}>
                <option value="all">Tous les types</option>
                <option value="lead">Leads</option>
                <option value="dossier">Dossiers VIQ</option>
                <option value="marketing">Marketing</option>
                <option value="vente">Ventes</option>
                <option value="crm">CRM</option>
              </select>
              <button className="btn btn-accent btn-sm" onClick={() => setQuickAddOpen(o => !o)} style={{ padding: ".25rem .55rem", fontSize: ".8rem" }}>+</button>
              {quickAddOpen && <AgendaQuickAddPopover onClose={() => setQuickAddOpen(false)} onCreated={reload} />}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".5rem", maxHeight: 340, overflowY: "auto" }}>
            {upcoming.length === 0 && <div style={{ fontSize: ".78rem", color: "var(--muted)" }}>Rien à venir pour l'instant.</div>}
            {upcoming.map(ev => (
              <div key={ev.id} onClick={() => openEvent(ev)}
                style={{ display: "flex", alignItems: "flex-start", gap: ".55rem", cursor: "pointer", borderBottom: "1px solid var(--border)", paddingBottom: ".45rem" }}>
                <span style={{ width: 8, height: 8, borderRadius: 4, background: AGENDA_CATEGORY_HEX[ev.category] || "var(--muted)", marginTop: 5, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: ".8rem", fontWeight: 700, color: "var(--text)", textDecoration: ev.done ? "line-through" : "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.title}</div>
                  {ev.subtitle && <div style={{ fontSize: ".72rem", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.subtitle}</div>}
                </div>
                <div style={{ fontSize: ".72rem", color: "var(--muted)", flexShrink: 0 }}>{agendaFmtTime(ev.date)}</div>
              </div>
            ))}
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => setView("timeline")}>Voir toutes les tâches →</button>
        </div>
      </div>

      {/* ── KPIs ── */}
      {kpis && (
        <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap", borderTop: "1px solid var(--border)", paddingTop: ".9rem" }}>
          {kpiCards.map(([icon, value, label, onClick]) => (
            <div key={label} onClick={onClick || undefined}
              style={{ flex: "1 1 120px", minWidth: 110, background: "var(--card)", borderRadius: 10, padding: ".6rem .7rem", cursor: onClick ? "pointer" : "default" }}>
              <div style={{ fontSize: "1.05rem" }}>{icon}</div>
              <div style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: "1.15rem", color: "var(--text)" }}>{value}</div>
              <div style={{ fontSize: ".68rem", color: "var(--muted)" }}>{label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
    {showGoalsModal && <LeadAutomationSettingsModal onClose={() => setShowGoalsModal(false)} />}
    {showMarketModal && <MarketCheckModal onClose={() => setShowMarketModal(false)} />}
    </>
  );
}

function AppHome({ user, isAdmin, walletBalance, pricingUsage, onService, onDashboard, onDashTab, onHistory, onAffiliate, onAdmin, onLogin, onSignup, onOpenDossiers, onCreateDossier, onOpenLeads, onOpenDocuments, onOpenProspection, onOpenCalls, onOpenSettings, onOpenAdmin, onPayment }) {
  // v33 — Nouvel accueil : plus un tableau de bord, un point d'entrée. Trois
  // blocs, rien d'autre (KPI/graphiques/activité déménagés dans "Dossiers VIQ"
  // et les Analyses, accessibles depuis le menu — plus sur cet écran).
  const firstName = user ? String(user.name || user.displayName || (user.email ? user.email.split("@")[0] : "") || "").split(" ")[0] : "";
  const [leadLink, setLeadLink] = useState(null);
  const [leadLinkCopied, setLeadLinkCopied] = useState(false);
  const [quickStats, setQuickStats] = useState(null); // v41 — chiffres rapides en bas de l'accueil
  const [menuOpen, setMenuOpen] = useState(false); // v56 — menu hamburger (raccourcis)

  useEffect(() => {
    if (!user?.uid) return;
    apiGet("/api/pro-leads/link").then(j => { if (j?.success) setLeadLink(j.url); }).catch(() => {});
    apiGet("/api/dossiers/quick-stats").then(j => { if (j?.success) setQuickStats(j.stats); }).catch(() => {});
  }, [user?.uid]);

  const BlockCard = ({ icon, title, desc, children }) => (
    <div style={{ background: "var(--white)", border: "1px solid var(--border)", borderRadius: 16, padding: "1.4rem 1.5rem", display: "flex", flexDirection: "column", gap: ".8rem", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".6rem" }}>
        <span style={{ fontSize: "1.4rem" }}>{icon}</span>
        <span style={{ fontWeight: 800, fontSize: "1.05rem", color: "var(--text)" }}>{title}</span>
      </div>
      <div style={{ fontSize: ".84rem", color: "var(--sub)", lineHeight: 1.5 }}>{desc}</div>
      {children}
    </div>
  );

  return (
    <div className="app-home-wrap" style={{ margin: "0 auto", padding: "1.2rem 0 2rem", maxWidth: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
      <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 20, padding: "1.8rem clamp(1rem, 4vw, 1.8rem) 2.2rem", boxShadow: "0 12px 48px rgba(0,0,0,.18)", boxSizing: "border-box", maxWidth: "100%" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: "1.8rem" }}>
          <div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: "1.7rem", fontWeight: 800, color: "var(--text)", letterSpacing: "-.02em" }}>
              {user ? `Bonjour ${firstName} 👋`.trim() : "Bienvenue sur votre espace VehicleIQ"}
            </div>
            <div style={{ fontSize: ".92rem", color: "var(--sub)", marginTop: ".25rem" }}>
              {user ? "Que souhaitez-vous faire aujourd'hui ?" : "Connectez-vous pour accéder à vos Dossiers VIQ."}
            </div>
          </div>
          {/* v61 — Chiffres de la Vue d'ensemble dans l'espace vide entre la
              formule de politesse et le menu (repéré comme inutilisé). */}
          {user && quickStats && quickStats.totalDossiers > 0 && (
            <div style={{ display: "flex", gap: "1.6rem", flexWrap: "wrap", alignItems: "center", padding: ".5rem 1.2rem" }}>
              {[
                [quickStats.totalDossiers, "Dossiers"],
                [quickStats.totalChecks, "Contrôles"],
                [quickStats.salesThisMonth, "Ventes ce mois"],
                ...(quickStats.marginThisMonth ? [[`${Number(quickStats.marginThisMonth).toLocaleString("fr-FR")} €`, "Marge ce mois"]] : []),
              ].map(([value, label]) => (
                <div key={label} style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "var(--font-head)", fontSize: "1.3rem", fontWeight: 800, color: "var(--accent)", lineHeight: 1.1 }}>{value}</div>
                  <div style={{ fontSize: ".68rem", color: "var(--muted)", whiteSpace: "nowrap" }}>{label}</div>
                </div>
              ))}
            </div>
          )}
          {/* v56 — Raccourcis regroupés dans un menu hamburger : la rangée de
              5 boutons débordait sur mobile (cause probable du problème de
              responsivité remonté), en plus d'être demandée explicitement. */}
          {user && (
            <div style={{ position: "relative" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setMenuOpen(o => !o)} aria-label="Menu" style={{ fontSize: "1.1rem", padding: ".5rem .8rem", lineHeight: 1 }}>
                ☰
              </button>
              {menuOpen && (
                <>
                  <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
                  <div style={{ position: "absolute", right: 0, top: "115%", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "0 10px 30px rgba(0,0,0,.15)", padding: ".4rem", zIndex: 20, display: "flex", flexDirection: "column", minWidth: 190 }}>
                    {[
                      ["Dossiers VIQ", onOpenDossiers],
                      ["Afficher mes leads", onOpenLeads],
                      ["Documents", onOpenDocuments],
                      ["Paramètres", onOpenSettings],
                      ...(isAdmin ? [["Administration", onOpenAdmin]] : []),
                    ].map(([label, fn]) => (
                      <button key={label} className="btn btn-ghost btn-sm" style={{ justifyContent: "flex-start", width: "100%" }}
                        onClick={() => { setMenuOpen(false); fn(); }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {!user ? (
          <div style={{ display: "flex", gap: ".7rem", flexWrap: "wrap" }}>
            <button className="btn btn-accent" onClick={onLogin} style={{ border: "none", cursor: "pointer", padding: ".8rem 1.5rem" }}>🔑 Se connecter</button>
            <button className="btn btn-outline" onClick={onSignup} style={{ cursor: "pointer", padding: ".8rem 1.5rem" }}>✨ Créer un compte</button>
          </div>
        ) : (
          <>
          <AppAssistantBar onCreateDossier={onCreateDossier} onOpenDossiers={onOpenDossiers} onOpenLeads={onOpenLeads} onOpenProspection={onOpenProspection} onDashTab={onDashTab}
            onOpenCalls={onOpenCalls} onDashboard={onDashboard} onHistory={onHistory} onPayment={onPayment} onAffiliate={onAffiliate} />
          <div style={{ display: "flex", gap: "1.1rem", flexWrap: "wrap", alignItems: "stretch" }}>

            {/* v102 — Règle : le carré "Dossier VIQ" est retiré de l'accueil —
                l'Agenda occupe maintenant tout l'espace, et ses deux actions
                ("Coter un véhicule" / "Ouvrir mes dossiers") vivent désormais
                dans l'en-tête de l'Agenda lui-même, juste avant "Aujourd'hui". */}
            {user && (
              <div style={{ flex: "1 1 100%", minWidth: 320 }}>
                <AgendaIntelligent onOpenDossiers={onOpenDossiers} onCreateDossier={onCreateDossier} onOpenLeads={onOpenLeads} onOpenProspection={onOpenProspection} onOpenDocuments={onOpenDocuments} />
              </div>
            )}

          </div>
          </>
        )}

        {/* v53 — Liens légaux : le Footer (qui les porte normalement) est masqué
            dans l'environnement "app" (viqEnv()==="app", celui de cet accueil
            Pro) — sans ce rappel, ces pages restaient inaccessibles depuis ici. */}
        <div style={{ marginTop: "1.6rem", paddingTop: "1.1rem", borderTop: "1px solid var(--border)", display: "flex", gap: "1.2rem", flexWrap: "wrap", justifyContent: "center" }}>
          {[
            ["Mentions légales", "mentions"],
            ["Politique de confidentialité", "privacy"],
            ["CGU", "cgu"],
            ["Conditions de vente", "cgv"],
          ].map(([label, type]) => (
            <button key={type} type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("viq-open-legal", { detail: type }))}
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".74rem", color: "var(--muted)", textDecoration: "underline", padding: 0 }}>
              {label}
            </button>
          ))}
          <button type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("viq-open-charte"))}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: ".74rem", color: "var(--muted)", textDecoration: "underline", padding: 0 }}>
            Charte graphique
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── App (wrapper) — aiguillage page publique « Formulaire client » ─────────
// https://vehicleiq.fr/?lead=TOKEN court-circuite l'app sans violer les règles
// des hooks : ce wrapper n'a qu'un seul hook, toujours exécuté, et rend soit
// la page lead publique, soit l'application complète (MainApp).
// v65 — Aucun error boundary n'existait nulle part dans l'app : la moindre
// erreur de rendu (n'importe où) faisait disparaître TOUTE la page, sans
// aucun message — exactement le symptôme "page blanche" remonté sur le
// Dossier VIQ. Un boundary React ne peut être qu'un composant classe (les
// hooks ne peuvent pas intercepter les erreurs de rendu) : ceci n'altère
// AUCUN comportement existant quand tout fonctionne, seulement le cas où
// quelque chose plante déjà — additif, pas un refactoring.
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("[ErrorBoundary]", error, info?.componentStack); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "3rem 1.5rem", textAlign: "center", maxWidth: 480, margin: "3rem auto", fontFamily: "system-ui, sans-serif" }}>
          <div style={{ fontSize: "2rem", marginBottom: ".8rem" }}>⚠️</div>
          <div style={{ fontWeight: 700, fontSize: "1.15rem", marginBottom: ".5rem", color: "#1a1a1a" }}>Une erreur est survenue</div>
          <div style={{ fontSize: ".82rem", color: "#888", marginBottom: "1.4rem", wordBreak: "break-word" }}>
            {this.state.error?.message || "Erreur inconnue"}
          </div>
          <button onClick={() => { this.setState({ hasError: false, error: null }); if (this.props.onReset) this.props.onReset(); }}
            style={{ padding: ".7rem 1.4rem", borderRadius: 8, border: "1.5px solid #A8803A", background: "#fff", color: "#A8803A", fontWeight: 700, cursor: "pointer", marginRight: ".6rem" }}>
            Réessayer
          </button>
          <button onClick={() => window.location.reload()}
            style={{ padding: ".7rem 1.4rem", borderRadius: 8, border: "none", background: "#A8803A", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
            Recharger la page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [leadToken] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("lead") || null; } catch (e) { return null; }
  });
  if (leadToken) return <LeadFormPage token={leadToken} />;
  return <ErrorBoundary><MainApp /></ErrorBoundary>;
}

function MainApp() {
  const [appReady, setAppReady] = useState(false);
  const [loaderOut, setLoaderOut] = useState(false);
  // ── Heartbeat de présence (suivi visiteurs pour le dashboard admin) ─────────
  // Envoie un ping anonyme au chargement puis toutes les 60 s. Un identifiant
  // visiteur persistant (localStorage) permet de compter les visiteurs UNIQUES
  // sans cookie tiers. La localisation est déduite côté serveur via l'IP/CDN.
  useEffect(() => {
    let vid;
    try {
      vid = localStorage.getItem("viq_vid");
      if (!vid) { vid = "v_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("viq_vid", vid); }
    } catch (e) { vid = "v_" + Math.random().toString(36).slice(2); }
    const ping = () => {
      try {
        fetch(`${API_URL}/api/track/ping`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ vid, path: window.location.pathname || "/", uid: window.__viq_uid || null }),
          keepalive: true,
        }).catch(() => {});
      } catch (e) {}
    };
    ping();
    const t = setInterval(ping, 60000);
    const onVis = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  useEffect(() => {
    // Pre-paint background : light mode by default (off-white warm)
    document.documentElement.style.background = "#FAFAF7";
    document.body.style.background = "#FAFAF7";
    // Ne plus forcer dark-mode au boot — l'utilisateur peut basculer via le toggle
    document.body.classList.remove("dark-mode");
    // ── Splash screen piloté par le chargement RÉEL des ressources ─────────
    // On attend que la page soit effectivement prête (event `load` = images,
    // scripts, CSS + document.fonts.ready = polices) avant de retirer le splash,
    // au lieu d'une durée fixe qui laissait un écran blanc sur connexion lente.
    //  • MIN_MS  : durée minimale d'affichage (laisse l'animation de la barre se jouer)
    //  • MAX_MS  : garde-fou — on ne bloque jamais l'accès plus de 8 s même si
    //              une ressource tierce (police, image, script) traîne ou échoue.
    const MIN_MS = 1100;
    const MAX_MS = 8000;
    const FADE_MS = 420; // doit suivre la transition CSS .app-loader (.4s)
    const start = Date.now();
    let finished = false;
    const timers = [];
    const finish = () => {
      if (finished) return;
      finished = true;
      const wait = Math.max(0, MIN_MS - (Date.now() - start));
      timers.push(setTimeout(() => {
        setLoaderOut(true);
        timers.push(setTimeout(() => setAppReady(true), FADE_MS));
      }, wait));
    };
    const onResourcesReady = () => {
      // Attendre aussi les polices (sinon flash de texte invisible/substitué)
      const fontsReady = (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === "function")
        ? document.fonts.ready : Promise.resolve();
      Promise.resolve(fontsReady).then(finish).catch(finish);
    };
    if (document.readyState === "complete") onResourcesReady();
    else window.addEventListener("load", onResourcesReady, { once: true });
    const failsafe = setTimeout(finish, MAX_MS);
    return () => {
      clearTimeout(failsafe);
      timers.forEach(clearTimeout);
      window.removeEventListener("load", onResourcesReady);
    };
  }, []);

  const [activePanel, setActivePanel] = useState(null);

  // ─── FAB scroll-reveal ──────────────────────────────────────────────────
  // Le FAB n'apparaît que quand le hero a quitté le viewport (l'utilisateur
  // a commencé à explorer la page). On utilise IntersectionObserver sur la
  // section .hero : tant qu'elle intersecte le viewport, FAB caché.
  const [fabVisible, setFabVisible] = useState(false);
  useEffect(() => {
    const hero = document.querySelector("section.hero");
    if (!hero) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFabVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px 0px 0px" }
      // rootMargin négatif en haut : on déclenche quand le hero est passé
      // sous la navbar (≈80px), pas seulement quand il sort complètement.
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  const [showAuth, setShowAuth] = useState(false);
  const [authDefaultMode, setAuthDefaultMode] = useState(null); // null = signin (défaut) | "signup" = ouvrir l'onglet Créer un compte
  const [pendingPanel, setPendingPanel] = useState(null); // panel à ouvrir après login
  const [sellPrefill, setSellPrefill] = useState(null); // préremplissage SellPanel (hero / replay) — prop React fiable
  const [showLegal, setShowLegal] = useState(null);
  // Restore user from Firebase session (persists across page refresh)
  const [user, setUser] = useState(() => {
    try { const s = localStorage.getItem("viq_session"); return s ? JSON.parse(s) : null; } catch(e) { return null; }
  });
  const [authLoading, setAuthLoading] = useState(false);

  // Expose les coordonnées du client connecté pour les générateurs PDF.
  // Les fonctions d'export (analyse, estimation, VIN, achat) sont appelées depuis
  // de nombreux endroits sans recevoir `user` en prop ; elles lisent donc le
  // client courant via window.__viq_user (mis à jour ici à chaque changement).
  useEffect(() => {
    window.__viq_user = user ? {
      name: user.name || user.displayName || "",
      email: user.email || "",
      siret: user.siret || "",
      siret_name: user.siretName || user.siret_name || "",
      address: user.address || "",
      phone: user.phone || user.phone_number || "",
    } : null;
  }, [user]);

  // Reveal au scroll : observe les sections de la home et déclenche l'animation
  // d'apparition quand elles entrent dans le viewport. Léger, sans dépendance.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    // Cible les sections de la page d'accueil (hors hero déjà visible).
    const selectors = [
      ".why-section .why-inner", ".dash-mock-section .dash-mock-inner",
      ".engines-inner", ".market-section", ".actors-inner",
      ".argus-alt-inner", ".process-inner", ".brands-platforms-section",
      ".testi-inner", ".home-faq-inner", ".pro-cta-inner",
    ];
    const targets = [];
    selectors.forEach(sel => document.querySelectorAll(sel).forEach(el => {
      if (!el.classList.contains("reveal-on-scroll")) {
        el.classList.add("reveal-on-scroll");
        targets.push(el);
      }
    }));
    if (!targets.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    targets.forEach(t => io.observe(t));
    return () => io.disconnect();
  }, []);

  // Écouter les événements de replay depuis l'historique
  useEffect(() => {
    const handleReplay = () => {
      const replay = window.__viq_replay;
      if (!replay) return;
      window.__viq_replay = null;
      if (replay.type === "buy")   { setActivePanel("buy");   window.__viq_buy_prefill  = replay.answers; }
      if (replay.type === "sell")  {
        setActivePanel("sell");
        window.__viq_sell_prefill = replay.answers;
        // v28 — Photos déjà transmises par le client (formulaire lead) : à
        // reprendre telles quelles dans le champ photo de l'estimation.
        window.__viq_sell_photos_prefill = (replay.answers && replay.answers._photos) || null;
      }
      if (replay.type === "check") { setActivePanel("check"); window.__viq_vin_prefill  = replay.vin; }
    };
    window.addEventListener("viq-replay", handleReplay);
    // Open a specific panel from any component
    const handleOpenPanel = (e) => {
      if (e.detail === "profile") { setShowHistory(true); } // HistoryModal contient l'onglet profil
      else setActivePanel(e.detail);
    };
    window.addEventListener("viq-open-panel", handleOpenPanel);
    // Open pricing modal from any component (utilisé quand limite mensuelle atteinte)
    const handleOpenPricing = () => { setShowPricing(true); };
    window.addEventListener("viq-open-pricing", handleOpenPricing);
    // Open profile tab in history modal
    const handleOpenProfile = () => { setShowHistory(true); setTimeout(() => window.dispatchEvent(new CustomEvent("viq-history-tab", { detail: "profile" })), 100); };
    window.addEventListener("viq-open-profile", handleOpenProfile);
    // v5 : ouvrir l'onglet Paiement (utilisé par RechargeModal après activation
    // carte quand l'utilisateur avait initié une souscription Multi-Agents).
    const handleOpenPaymentTab = () => { setShowHistory(true); setTimeout(() => window.dispatchEvent(new CustomEvent("viq-history-tab", { detail: "payment" })), 100); };
    window.addEventListener("viq-open-payment-tab", handleOpenPaymentTab);
    // v5 : ouvrir une page légale (mentions, cgu, privacy, cgv) depuis n'importe où.
    // Usage : window.dispatchEvent(new CustomEvent("viq-open-legal", { detail: "cgv" }))
    const handleOpenLegal = (e) => { setShowLegal(e.detail || "cgu"); };
    window.addEventListener("viq-open-legal", handleOpenLegal);
    // v53 — Même principe pour la charte graphique, utilisée depuis l'accueil
    // Pro (AppHome), qui n'a pas accès au Footer (masqué en env "app").
    const handleOpenCharte = () => setShowCharte(true);
    window.addEventListener("viq-open-charte", handleOpenCharte);
    // Permet à n'importe quel composant de fermer la modale admin (utilisé par Relancer depuis SuperAdmin)
    const handleCloseAdmin = () => { setShowAdmin(false); };
    window.addEventListener("viq-close-admin", handleCloseAdmin);
    // Ouvre le Dashboard Pro avec une estimation pré-sélectionnée
    // Usage : window.dispatchEvent(new CustomEvent("viq-open-prodash", { detail: { estimationId, tab } }))
    const handleOpenProdash = (e) => {
      const detail = e.detail || {};
      setDashboardPreselect({ estimationId: detail.estimationId, tab: detail.tab || "vehicle" });
      // Fermer la modale Mon espace si elle est ouverte
      setShowHistory(false);
      setShowDashboard(true);
    };
    window.addEventListener("viq-open-prodash", handleOpenProdash);
    return () => {
      window.removeEventListener("viq-replay", handleReplay);
      window.removeEventListener("viq-open-panel", handleOpenPanel);
      window.removeEventListener("viq-open-pricing", handleOpenPricing);
      window.removeEventListener("viq-close-admin", handleCloseAdmin);
      window.removeEventListener("viq-open-profile", handleOpenProfile);
      window.removeEventListener("viq-open-payment-tab", handleOpenPaymentTab);
      window.removeEventListener("viq-open-legal", handleOpenLegal);
      window.removeEventListener("viq-open-charte", handleOpenCharte);
      window.removeEventListener("viq-open-prodash", handleOpenProdash);
    };
  }, []);

  useEffect(() => {
    // Safety timeout: if Firebase takes >4s, stop blocking the UI
    const authTimeout = setTimeout(() => setAuthLoading(false), 4000);
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Si un AUTRE utilisateur se connecte sur le même navigateur → vider le localStorage
        const prevSession = (() => { try { return JSON.parse(localStorage.getItem("viq_session") || "{}"); } catch(e) { return {}; } })();
        if (prevSession.uid && prevSession.uid !== firebaseUser.uid) {
          clearUserLocalStorage();
        }
        const userData = { uid: firebaseUser.uid, email: firebaseUser.email, name: firebaseUser.displayName };
        setUser(userData);
        window.__viq_uid = firebaseUser.uid;
        localStorage.setItem("viq_session", JSON.stringify(userData));
        // Rouvrir le panel si l'utilisateur s'est connecté depuis un gate
        if (pendingPanel) { setTimeout(() => { setActivePanel(pendingPanel); setPendingPanel(null); }, 300); }
        // Charger le statut apporteur depuis la map locale (instantané, sans API)
        // ATTENTION : cette map ne contient que les apporteurs historiques.
        // Les nouveaux apporteurs créés en DB seront chargés par /api/affiliate/me ci-dessous.
        const localAff = AFFILIATES_MAP[firebaseUser.uid];
        if (localAff) setAffiliateData(localAff);
        // Sync backend silently
        try {
          await apiPost("/api/users/sync", { uid: firebaseUser.uid, email: firebaseUser.email, name: firebaseUser.displayName });
          const res = await apiGet("/api/users/me");
          if (res.success && res.user) {
            const p = res.user;
            // Merger siret + is_pro dans le state user
            setUser(prev => ({ ...prev, siret: p.siret || "", is_pro: p.is_pro || false, siretName: p.siret_name || "" }));
            localStorage.setItem("viq_profile", JSON.stringify({ phone: p.phone||"", address: p.address||"", siret: p.siret||"", siretName: p.siret_name||"", isPro: p.is_pro||false }));
            if (typeof p.wallet_balance === "number" && p.wallet_balance >= 0) { setWallet(p.wallet_balance); setWalletBalance(p.wallet_balance); }
            // OPTIMISATION : /api/users/me retourne déjà res.organization → on l'utilise directement.
            // Évite un 2ème appel à /api/org/my et garantit que le bouton "Mon équipe" reste visible
            // dès la 1ère confirmation backend (pas de flash UI).
            persistUserOrg(res.organization || null);

            // ── Charger le statut apporteur depuis la DB (gère les nouveaux apporteurs créés via admin) ──
            try {
              const affRes = await apiGet("/api/affiliate/me");
              if (affRes && affRes.success && affRes.stats && affRes.stats.code) {
                setAffiliateData({
                  code: affRes.stats.code,
                  name: affRes.stats.name || affRes.stats.user_name || firebaseUser.displayName || "",
                });
              }
            } catch(e) { /* silencieux */ }

            // ── Onboarding recharge : si nouvel inscrit (< 5 min) avec wallet vide,
            // ouvrir le modal de recharge au bout de 10 secondes (1 seule fois) ──
            try {
              const creationTimeStr = firebaseUser.metadata?.creationTime;
              const creationMs = creationTimeStr ? new Date(creationTimeStr).getTime() : 0;
              const ageMinutes = creationMs ? (Date.now() - creationMs) / 1000 / 60 : Infinity;
              const walletEmpty = !p.wallet_balance || p.wallet_balance === 0;
              const promptShownKey = `viq_recharge_prompt_${firebaseUser.uid}`;
              const alreadyShown = localStorage.getItem(promptShownKey) === "1";

              if (ageMinutes < 5 && walletEmpty && !alreadyShown) {
                setTimeout(() => {
                  setShowRecharge(true);
                  localStorage.setItem(promptShownKey, "1");
                }, 10000);
              }
            } catch(e) { /* silencieux : metadata peut être absent selon provider */ }
          }
        } catch(e) {}
      } else {
        setUser(null);
        setAffiliateData(null);
        window.__viq_uid = null;
        localStorage.removeItem("viq_session");
      }
      clearTimeout(authTimeout);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);
  const [selectedModel, setSelectedModel] = useState(null);
  const [showService, setShowService] = useState(null);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showB2B, setShowB2B] = useState(false);
  const [showAPI, setShowAPI] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // v33 — Dossier VIQ : état de la nouvelle page centrale.
  const [showDossiers, setShowDossiers] = useState(false);
  const [dossierInitialKey, setDossierInitialKey] = useState(null);
  const [dossierSelectedId, setDossierSelectedId] = useState(null); // v71 — dossier actuellement ouvert dans Dossiers VIQ, pour le lien direct
  const [historyInitialTab, setHistoryInitialTab] = useState("requests"); // onglet initial de Mon espace ("requests" | "payment")
  const [showDashboard, setShowDashboard] = useState(false);
  const [dashboardPreselect, setDashboardPreselect] = useState(null); // { estimationId, tab } pour ouverture ciblée
  const [showContact, setShowContact] = useState(false);
  const [showCharte, setShowCharte] = useState(false);
  const [showMandat, setShowMandat] = useState(false);
  const [mandatPrefill, setMandatPrefill] = useState(null);
  const [showCession, setShowCession] = useState(false);
  const [cessionPrefill, setCessionPrefill] = useState(null);
  const [showDemoBooking, setShowDemoBooking] = useState(false);
  const [showDemoVideo, setShowDemoVideo] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showBlog, setShowBlog] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // SUJET 4 PHASE 2 : Organisation de l'utilisateur (chargée au login)
  // Hydratation instantanée depuis localStorage au mount.
  // Permet au bouton "Mon équipe" d'apparaître AVANT que Firebase reconnecte au refresh.
  // La source de vérité reste la DB : le cache est rafraîchi dès que /api/users/me répond.
  const [userOrg, setUserOrg] = useState(() => {
    try {
      const cached = localStorage.getItem("viq_org_cache");
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      // Validation minimale : doit avoir un id et un siret
      if (parsed && parsed.id && parsed.siret) return parsed;
      return null;
    } catch (_) { return null; }
  });
  // Wrapper persistant : à chaque setUserOrg, on sync le localStorage.
  // Centralisation pour ne pas oublier la sync à un endroit.
  const persistUserOrg = (org) => {
    setUserOrg(org);
    try {
      if (org && org.id) {
        localStorage.setItem("viq_org_cache", JSON.stringify({
          id: org.id, company_name: org.company_name, siret: org.siret, my_role: org.my_role,
          max_agents: org.max_agents, plan_tier: org.plan_tier
        }));
      } else {
        localStorage.removeItem("viq_org_cache");
      }
    } catch(_) {}
  };
  // Throttle anti-spam : on n'appelle pas /api/org/my plus d'une fois toutes les 2 secondes.
  // Sans ce guard, des useEffect mal contrôlés ou des events en cascade pouvaient saturer
  // le backend (~50-100 appels/seconde observés en condition de re-render).
  const orgFetchLockRef = useRef({ inFlight: false, lastAt: 0 });
  const refreshUserOrg = async ({ force = false } = {}) => {
    if (!user?.uid) { persistUserOrg(null); return; }
    const lock = orgFetchLockRef.current;
    const now = Date.now();
    // Si un fetch est déjà en cours OU si le dernier date de moins de 2s → skip
    if (lock.inFlight) return;
    if (!force && (now - lock.lastAt) < 2000) return;
    lock.inFlight = true;
    try {
      const r = await apiGet("/api/org/my");
      // IMPORTANT : on ne writes le cache que si la réponse est claire (success).
      // Si l'API échoue (timeout, 500), on GARDE le cache existant — pas de flash UI.
      if (r && r.success) {
        persistUserOrg(r.organization || null);
      } else {
        console.warn("[refreshUserOrg] response non-success, keeping cached org");
      }
    } catch(e) {
      // Erreur réseau / backend down : on garde le cache localStorage existant.
      // C'est volontaire : si le backend crash, le bouton "Mon équipe" reste visible
      // grâce au cache (jusqu'à ce qu'on confirme via API que l'utilisateur n'est plus dans l'org).
      console.warn("[refreshUserOrg] fetch failed, keeping cached org:", e?.message);
    } finally {
      lock.inFlight = false;
      lock.lastAt = Date.now();
    }
  };
  // Logout → vider le cache. (Login : la sync est déjà faite dans onAuthStateChanged
  // via /api/users/me, donc on n'a pas besoin d'un 2ème refresh ici.)
  useEffect(() => {
    if (!user?.uid) persistUserOrg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);
  // Écouter "viq-org-changed" pour re-fetch (déclenché à création/acceptation d'invitation).
  // On force le fetch (bypass throttle 2s) car l'event est intentionnel utilisateur.
  useEffect(() => {
    const handler = () => refreshUserOrg({ force: true });
    window.addEventListener("viq-org-changed", handler);
    return () => window.removeEventListener("viq-org-changed", handler);
  }, [user?.uid]);
  // Ouverture de la modale de contact depuis n'importe quel CTA (event global).
  // Le detail.mailto éventuel sert de repli e-mail pré-rempli dans la modale.
  useEffect(() => {
    const handler = (e) => {
      try { window.__viq_contact_fallback = (e && e.detail) || {}; } catch (_) {}
      setShowContact(true);
    };
    window.addEventListener("viq-open-contact", handler);
    return () => window.removeEventListener("viq-open-contact", handler);
  }, []);
  const [showOrgManagement, setShowOrgManagement] = useState(false);
  // Product Tour : auto à la 1re visite + bouton manuel navbar.
  // Distinct de showOnboarding (= signup) — il s'agit ici du guide produit avec vidéo HeyGen.
  // v115 — Règle : plus aucun écran d'introduction à la première visite. Le
  // visiteur arrive directement sur le site ; la présentation produit reste
  // accessible à la demande (lien "À propos" de la barre de navigation).
  const [showProductTour, setShowProductTour] = useState(false);
  const openDashboard = () => {
    // Refresh data from localStorage on each open
    try { setDashHistory(JSON.parse(localStorage.getItem("viq_history") || "[]")); } catch(e) {}
    setShowDashboard(true);
  };
  // Ouvre le Dashboard Pro directement sur un onglet ciblé (ex : "cartegrise", "documents").
  // Auth-gate identique aux autres entrées dashboard.
  const openDashTab = (tab) => {
    if (!user) { setPendingPanel("sell"); setShowAuth(true); return; }
    try { setDashHistory(JSON.parse(localStorage.getItem("viq_history") || "[]")); } catch(e) {}
    setDashboardPreselect({ tab });
    setShowDashboard(true);
  };
  // Siret du profil (disponible pour le dashboard pro)
  const [appSiret, setAppSiret] = useState(() => {
    try { return JSON.parse(localStorage.getItem("viq_profile") || "{}").siret || ""; } catch(e) { return ""; }
  });
  // Sync appSiret when user profile is loaded (siret arrives async after auth)
  useEffect(() => {
    if (user?.siret) setAppSiret(user.siret);
    else {
      try {
        const s = JSON.parse(localStorage.getItem("viq_profile") || "{}").siret || "";
        if (s) setAppSiret(s);
      } catch(e) {}
    }
  }, [user]);

  // ── Pre-load dashboard data immediately + on login ────────────────────────
  const [dashHistory, setDashHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("viq_history") || "[]"); } catch(e) { return []; }
  });
  // Recharge l'historique : lecture localStorage immédiate + sync serveur (merge).
  // Extrait en fonction pour pouvoir être rappelé à la demande (bouton Actualiser).
  const reloadDashHistory = useCallback(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("viq_history") || "[]");
      setDashHistory(raw);
    } catch(e) {}
    if (!user?.uid) return Promise.resolve();
    return apiGet("/api/users/me").then(res => {
      if (!res?.success) return;
      if (res.searches?.length) {
        const remote = res.searches.map(s => {
          const input  = typeof s.input  === "object" ? s.input  : (JSON.parse(s.input  || "{}") || {});
          const result = typeof s.result === "object" ? s.result : (JSON.parse(s.result || "{}") || {});
          if (s.type === "buy" && result.suggestions?.length && !result.models) {
            result.models = result.suggestions;
          }
          return { id: s.id, date: s.created_at, type: s.type, answers: input, results: result, data: input };
        });
        let local = [];
        try { local = JSON.parse(localStorage.getItem("viq_history") || "[]"); } catch(e) {}
        const remoteIds = new Set(remote.map(r => String(r.id)));
        const remoteFingerprints = new Set(remote.map(r => {
          const d = new Date(r.date); d.setSeconds(0, 0);
          return r.type + "_" + d.toISOString();
        }));
        const localOnly = local.filter(h => {
          if (remoteIds.has(String(h.id))) return false;
          const d = new Date(h.date); d.setSeconds(0, 0);
          return !remoteFingerprints.has(h.type + "_" + d.toISOString());
        });
        const merged = [...remote, ...localOnly].sort((a,b) => new Date(b.date) - new Date(a.date));
        setDashHistory(merged);
        try { localStorage.setItem("viq_history", JSON.stringify(merged)); } catch(e) {}
      }
    }).catch(() => {});
  }, [user]);
  useEffect(() => { reloadDashHistory(); }, [user, reloadDashHistory]);
  // Thème clair (beige premium) par défaut. Le choix de l'utilisateur est mémorisé.
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("viq_theme");
      if (saved === "dark") return true;
      if (saved === "light") return false;
    } catch(e) {}
    return false; // défaut : clair
  });
  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.body.classList.remove(darkMode ? "light-mode" : "dark-mode");
    try { localStorage.setItem("viq_theme", darkMode ? "dark" : "light"); } catch(e) {}
  }, [darkMode]);
  // Permet à n'importe quel composant (dashboards, modales) de basculer le thème
  // via un évènement global, sans avoir besoin de recevoir setDarkMode en prop.
  useEffect(() => {
    const toggle = () => setDarkMode(d => !d);
    window.addEventListener("viq-toggle-theme", toggle);
    return () => window.removeEventListener("viq-toggle-theme", toggle);
  }, []);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  // v5 : statut admin du user courant (chargé depuis /api/admin/me)
  // true si dans ADMIN_UIDS env OU promu super admin en base.
  // Utilisé pour afficher le bouton "Admin" dans la nav desktop et mobile.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!user?.uid) { setIsAdmin(false); return; }
    let cancelled = false;
    (async () => {
      try {
        // IMPORTANT : on construit le header manuellement avec user.uid pour
        // éviter une race au démarrage. `getAuthHeaders()` lit window.__viq_uid
        // et auth.currentUser, qui ne sont pas encore définis au premier render
        // après un refresh (Firebase prend ~200-500ms à initialiser). Le state
        // `user`, lui, est restauré immédiatement depuis localStorage. En utilisant
        // user.uid directement, l'effect réussit dès le premier passage.
        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${user.uid}` };
        const r = await fetch(`${API_URL}/api/admin/me`, { headers });
        if (!r.ok) { if (!cancelled) setIsAdmin(false); return; }
        const d = await r.json();
        if (!cancelled) setIsAdmin(!!(d?.isSuperAdmin || d?.isOwner));
      } catch (e) {
        if (!cancelled) setIsAdmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);
  const [showAffiliateAdmin, setShowAffiliateAdmin] = useState(false);
  const [affiliateData, setAffiliateData] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [walletBalance, setWalletBalance] = useState(() => getWallet());
  const refreshWallet = () => setWalletBalance(getWallet());

  // ─── SaaS multi-domaine : ouverture du bon panneau au boot de l'application ──
  // Sur user.vehicleiq.fr, on lit l'intention transmise par le site public via
  // ?go=… et on ouvre le panneau correspondant (après résolution de la session).
  useEffect(() => {
    if (viqEnv() !== "app") return;
    // v8 : cet effet peut s'exécuter 1re fois avec `user` encore null (le
    // miroir localStorage "viq_session" n'a pas eu le temps de se resynchroniser
    // avec la session Firebase réellement persistée — ex. nouvel onglet ouvert
    // depuis le site public) et ouvrir la modale de connexion par précaution.
    // Comme le paramètre ?go= est retiré de l'URL après ce 1er passage, l'effet
    // ne "voit" plus rien à traiter lors de son 2e passage (quand `user` se
    // confirme) — sans ce garde-fou, la modale reste donc affichée par-dessus
    // une session pourtant valide. On la referme dès que `user` est confirmé,
    // indépendamment de l'état de l'URL.
    if (user) setShowAuth(false);
    let go = null;
    try { go = new URLSearchParams(window.location.search).get("go"); } catch (e) {}
    if (!go) return;
    // Nettoyer l'URL pour ne pas ré-ouvrir au rechargement / partage.
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("go");
      u.searchParams.delete("dossier");
      window.history.replaceState({}, "", u.pathname + u.search + u.hash);
    } catch (e) {}
    const needAuth = () => { setAuthDefaultMode(go === "signup" ? "signup" : null); setShowAuth(true); };
    if (go === "login" || go === "signup") { if (!user) needAuth(); return; }
    if (go.indexOf("svc:") === 0) {
      const svc = go.slice(4);
      if (!user) { setPendingPanel(svc); setShowAuth(true); } else setActivePanel(svc);
      return;
    }
    if (!user) { needAuth(); return; }
    if (go === "history" || go === "payment") { if (go === "payment") setHistoryInitialTab("payment"); setShowHistory(true); }
    else if (go === "dashboard") openDashboard();
    else if (go === "affiliate") setShowAffiliateAdmin(true);
    else if (go === "admin") setShowAdmin(true);
    else if (go === "recharge") setShowRecharge(true);
    // v71 — Règle 5 : couverture élargie du lien direct (dossiers, B2B, API,
    // tarifs, blog, FAQ, contact) — mêmes conventions que ci-dessus.
    else if (go === "dossiers") {
      // v71 — Règle 5 (suite) : lien direct vers un dossier précis (?go=dossiers&dossier=123).
      let dossierParam = null;
      try { dossierParam = new URLSearchParams(window.location.search).get("dossier"); } catch (e2) {}
      if (dossierParam && /^\d+$/.test(dossierParam)) setDossierInitialKey(parseInt(dossierParam, 10));
      setShowDossiers(true);
    }
    else if (go === "b2b") setShowB2B(true);
    else if (go === "api") setShowAPI(true);
    else if (go === "pricing") setShowPricing(true);
    else if (go === "blog") setShowBlog(true);
    else if (go === "faq") setShowFAQ(true);
    else if (go === "contact") setShowContact(true);
    else if (go === "orgmanagement") setShowOrgManagement(true);
  }, [user]);

  // v71 — Règle 5 : lien direct PERSISTANT (pas seulement un handoff au boot) —
  // tant qu'une modale reste ouverte, l'URL de user.vehicleiq.fr reflète en
  // permanence laquelle (?go=xxx), pour qu'un lien copié à tout moment rouvre
  // directement la même modale, comme une vraie page web. Additif : ne change
  // rien au fonctionnement des boutons/états eux-mêmes, ne fait que refléter
  // leur état courant dans l'URL.
  useEffect(() => {
    if (viqEnv() !== "app") return;
    let target = null;
    if (showDossiers) target = "dossiers";
    else if (showHistory) target = historyInitialTab === "payment" ? "payment" : "history";
    else if (showDashboard) target = "dashboard";
    else if (showAffiliateAdmin) target = "affiliate";
    else if (showAdmin) target = "admin";
    else if (showRecharge) target = "recharge";
    else if (showB2B) target = "b2b";
    else if (showAPI) target = "api";
    else if (showPricing) target = "pricing";
    else if (showBlog) target = "blog";
    else if (showFAQ) target = "faq";
    else if (showContact) target = "contact";
    else if (showOrgManagement) target = "orgmanagement";
    // v71 — Règle 5 (suite) : un dossier précis ouvert dans Dossiers VIQ
    // s'ajoute en sous-paramètre (?dossier=123), pour un lien copiable qui
    // rouvre directement CE dossier, pas seulement la liste.
    const dossierTarget = (target === "dossiers" && dossierSelectedId) ? String(dossierSelectedId) : null;
    try {
      const u = new URL(window.location.href);
      const currentGo = u.searchParams.get("go");
      const currentDossier = u.searchParams.get("dossier");
      let changed = false;
      if (target && currentGo !== target) { u.searchParams.set("go", target); changed = true; }
      else if (!target && currentGo) { u.searchParams.delete("go"); changed = true; }
      if (dossierTarget && currentDossier !== dossierTarget) { u.searchParams.set("dossier", dossierTarget); changed = true; }
      else if (!dossierTarget && currentDossier) { u.searchParams.delete("dossier"); changed = true; }
      if (changed) window.history.replaceState({}, "", u.pathname + u.search + u.hash);
    } catch (e) { /* URL sync non bloquant */ }
  }, [showDossiers, showHistory, showDashboard, showAffiliateAdmin, showAdmin, showRecharge, showB2B, showAPI, showPricing, showBlog, showFAQ, showContact, showOrgManagement, historyInitialTab, dossierSelectedId]);


  // ─── Filet de sécurité (site public) ─────────────────────────────────────────
  // Garantit qu'AUCUN contenu nécessitant une connexion ne s'affiche sur
  // vehicleiq.fr, même si un bouton contournait l'objet `actions` : dès qu'un
  // panneau authentifié s'ouvre, on redirige vers l'application.
  useEffect(() => {
    if (viqEnv() !== "public") return;
    let go = null;
    if (showAuth) go = authDefaultMode === "signup" ? "signup" : "login";
    else if (showDashboard) go = "dashboard";
    else if (showHistory) go = "history";
    else if (showAffiliateAdmin) go = "affiliate";
    else if (showAdmin) go = "admin";
    else if (showRecharge) go = "recharge";
    else if (activePanel) go = "svc:" + activePanel;
    if (go) {
      viqGoToApp(go);
      // On vient d'ouvrir l'application (nouvel onglet) : on referme tout panneau
      // authentifié pour que le site public reste propre.
      setShowAuth(false); setShowDashboard(false); setShowHistory(false);
      setShowAffiliateAdmin(false); setShowAdmin(false); setShowRecharge(false);
      setActivePanel(null);
    }
  }, [showAuth, showDashboard, showHistory, showAffiliateAdmin, showAdmin, showRecharge, activePanel, authDefaultMode]);

  // v71 — Règle 5 : lien direct pour les modales PUBLIQUES (vehicleiq.fr) —
  // B2B, API, Tarifs, Blog, FAQ, Contact ne nécessitent pas de connexion et
  // peuvent donc s'ouvrir directement sur le site public, sans redirection
  // vers l'app. Boot-read (?go=xxx à l'arrivée) + sync persistant (l'URL
  // reflète la modale ouverte, pour un lien copiable à tout moment).
  useEffect(() => {
    if (viqEnv() !== "public") return;
    let go = null;
    try { go = new URLSearchParams(window.location.search).get("go"); } catch (e) {}
    if (!go) return;
    if (go === "b2b") setShowB2B(true);
    else if (go === "api") setShowAPI(true);
    else if (go === "pricing") setShowPricing(true);
    else if (go === "blog") setShowBlog(true);
    else if (go === "faq") setShowFAQ(true);
    else if (go === "contact") setShowContact(true);
  }, []);

  useEffect(() => {
    if (viqEnv() !== "public") return;
    let target = null;
    if (showB2B) target = "b2b";
    else if (showAPI) target = "api";
    else if (showPricing) target = "pricing";
    else if (showBlog) target = "blog";
    else if (showFAQ) target = "faq";
    else if (showContact) target = "contact";
    try {
      const u = new URL(window.location.href);
      const current = u.searchParams.get("go");
      if (target && current !== target) {
        u.searchParams.set("go", target);
        window.history.replaceState({}, "", u.pathname + u.search + u.hash);
      } else if (!target && current && ["b2b", "api", "pricing", "blog", "faq", "contact"].includes(current)) {
        u.searchParams.delete("go");
        window.history.replaceState({}, "", u.pathname + u.search + u.hash);
      }
    } catch (e) { /* URL sync non bloquant */ }
  }, [showB2B, showAPI, showPricing, showBlog, showFAQ, showContact]);

  // ─── SEO : l'application (user.vehicleiq.fr) ne doit pas être indexée ────────
  // (le contenu marketing indexable vit sur vehicleiq.fr). Filet runtime en
  // complément du robots.txt côté hébergement.
  useEffect(() => {
    if (viqEnv() !== "app") return;
    try {
      let m = document.querySelector('meta[name="robots"]');
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
      m.setAttribute("content", "noindex, nofollow");
    } catch (e) {}
  }, []);

  // ─── Consommation (tarif unique 4,99 € côté UI) ─────────────────────────
  // /api/pricing/usage renvoie l'état de consommation du mois, le palier en cours,
  // le prix de la prochaine estimation et la facture cumulée. Affiché dans la
  // navbar à la place du badge "crédits". Rafraîchi à la connexion et après
  // chaque estimation effectuée.
  const [pricingUsage, setPricingUsage] = useState(null);
  const refreshPricingUsage = useCallback(async () => {
    try {
      const h = await getAuthHeaders();
      if (!h) { setPricingUsage(null); return; }
      const r = await fetch(`${API_URL}/api/pricing/usage`, { headers: h });
      if (!r.ok) { setPricingUsage(null); return; }
      const data = await r.json();
      if (data?.success) setPricingUsage(data);
    } catch (e) { /* silencieux */ }
  }, []);
  useEffect(() => {
    if (user) refreshPricingUsage();
    else setPricingUsage(null);
  }, [user, refreshPricingUsage]);
  // v115 — Règle : plus d'écran d'onboarding (inscription/connexion) à la
  // première visite. Le visiteur accède directement au site ; la connexion et
  // l'inscription restent accessibles via les boutons de la barre de
  // navigation et via les parcours qui l'exigent réellement.
  const [showOnboarding, setShowOnboarding] = useState(false);
  // v8 : garde-fou anti-doublon — ce check initial se base sur le miroir
  // localStorage "viq_session", qui peut ne pas être encore resynchronisé avec
  // la session Firebase réellement persistée au tout premier rendu (ex. nouvel
  // onglet ouvert depuis le site public : onAuthStateChanged n'a pas encore eu
  // le temps de répondre). Dès que Firebase confirme un utilisateur, on ferme
  // l'onboarding (et son formulaire de connexion intégré) plutôt que de le
  // laisser s'afficher par-dessus une session pourtant valide — c'est ce qui
  // provoquait le doublon "onboarding + modale de connexion d'onboarding".
  useEffect(() => {
    if (user && showOnboarding) {
      try { localStorage.setItem("viq_onboarding_seen", "1"); } catch (e) {}
      setShowOnboarding(false);
    }
  }, [user, showOnboarding]);
  const closeOnboarding = async (userData) => {
    try { localStorage.setItem("viq_onboarding_seen", "1"); } catch(e) {}
    setShowOnboarding(false);
    if (!userData) {
      // User chose to explore without account — ensure no ghost session
      try { localStorage.removeItem("viq_session"); } catch(e) {}
      setUser(null);
      return;
    }
    if (userData) {
      setUser(userData);
      if (userData.uid) {
        try {
          await apiPost("/api/users/sync", { uid: userData.uid, email: userData.email, name: userData.name });
        } catch(e) {}
      }
    }
  };
  const [cookieStatus, setCookieStatus] = useState(() => { try { return localStorage.getItem("viq_cookies") || "pending"; } catch(e) { return "pending"; } });

  useEffect(() => {
    // ─── SEO : titre + meta description optimisés ───
    document.title = "VehicleIQ — Outil de Cote automobile professionnelle (IA + données marché)";

    const upsertMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const desc = "VehicleIQ : estimez la valeur réelle d'un véhicule d'occasion, contrôlez son historique (VIN/plaque) et optimisez vos prix VO en 30 secondes. Moteur VIQ.AI® calibré sur le marché réel via 100+ plateformes (estimations indicatives). Pour garages, marchands, mandataires, flottes et concessions.";
    upsertMeta("name", "description", desc);
    upsertMeta("name", "keywords", "estimation automobile, estimation véhicule, cote voiture, contrôle VIN, contrôle plaque, pricing VO, prix marché vs Argus, alternative Argus, logiciel estimation auto pro, reprise auto, véhicule d'occasion, VIQ.AI");
    upsertMeta("property", "og:title", "VehicleIQ — Intelligence automobile pour les professionnels du VO");
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", "https://vehicleiq.fr");
    upsertMeta("property", "og:site_name", "VehicleIQ");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "robots", "index, follow");

    // canonical
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", "https://vehicleiq.fr");

    // ─── Schema Markup JSON-LD : LocalBusiness + Product + FAQPage ───
    const ldId = "viq-jsonld";
    document.getElementById(ldId)?.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = ldId;
    ld.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "VehicleIQ",
        "description": desc,
        "url": "https://vehicleiq.fr",
        "email": "contact@vehicleiq.fr",
        "areaServed": "FR",
        "knowsAbout": ["estimation automobile", "véhicule d'occasion", "pricing VO", "contrôle VIN"]
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "VIQ.AI® Engine — Moteur d'estimation automobile",
        "description": "Moteur d'estimation automobile basé sur l'analyse temps réel de 100+ plateformes (LeBonCoin, La Centrale, AutoScout24). Estimation indicative calibrée sur le marché réel pour les professionnels du VO.",
        "brand": { "@type": "Brand", "name": "VehicleIQ" },
        "category": "Logiciel d'estimation automobile professionnel"
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "Qu'est-ce que VehicleIQ ?",
            "acceptedAnswer": { "@type": "Answer", "text": "VehicleIQ est une plateforme d'intelligence automobile pour les professionnels du VO : estimation calibrée par IA, contrôle d'historique VIN/plaque et outil de pricing, à partir des données marché en temps réel." } },
          { "@type": "Question", "name": "Comment fonctionne l'estimation VIQ.AI ?",
            "acceptedAnswer": { "@type": "Answer", "text": "Le moteur VIQ.AI® analyse en temps réel plus de 100 plateformes d'annonces et fusionne ces signaux marché avec un algorithme propriétaire pour produire une estimation indicative calibrée sur le marché réel." } },
          { "@type": "Question", "name": "VehicleIQ est-il une alternative à l'Argus ?",
            "acceptedAnswer": { "@type": "Answer", "text": "Oui. Là où l'Argus s'appuie sur un barème statique, VehicleIQ s'appuie sur les prix réels du marché en temps réel et un algorithme IA, offrant une précision supérieure pour les professionnels." } }
        ]
      }
    ]);
    document.head.appendChild(ld);
  }, []);


  const handleAuth = async (userData) => {
    setUser(userData);
    window.__viq_uid = userData?.uid || null;
    setShowAuth(false);
    if (userData?.uid) {
      // Sync user to DB
      try { await apiPost("/api/users/sync", { uid: userData.uid, email: userData.email, name: userData.name }); } catch(e) {}
      // SUJET 4 PHASE 2 : Récupérer l'organisation de l'utilisateur (si membre)
      try {
        const orgRes = await apiGet("/api/org/my");
        if (orgRes?.success && orgRes.organization) {
          setUserOrg(orgRes.organization);
        } else {
          setUserOrg(null);
        }
      } catch(e) { setUserOrg(null); }
      // SUJET 3 : Persister le SIRET au signup Pro (déjà validé côté AuthModal)
      if (userData.siret && userData.accountType === "entreprise") {
        try {
          await apiPost("/api/users/pro", {
            is_pro: true,
            siret: userData.siret,
            siret_name: userData.siretName || userData.name || ""
          });
        } catch(e) {
          // Si on arrive ici malgré la pré-validation, c'est une race condition rare.
          // On ne bloque pas le login mais on log silencieusement — l'erreur sera re-vue
          // au prochain "Mon espace" si besoin.
          console.warn("[users/pro] sync échec après signup:", e?.message || e);
        }
      }
      // Pull profile + wallet depuis le backend
      try {
        const res = await apiGet("/api/users/me");
        if (res.success) {
          if (res.user) {
            const p = res.user;
            // Merger siret + is_pro dans le state user (clé pour l'affichage conditionnel)
            setUser(prev => ({ ...prev, siret: p.siret || "", is_pro: p.is_pro || false, siretName: p.siret_name || "" }));
            localStorage.setItem("viq_profile", JSON.stringify({
              phone: p.phone || "", address: p.address || "",
              siret: p.siret || "", siretName: p.siret_name || "",
              isPro: p.is_pro || false,
            }));
            // Sync wallet depuis DB (priorité sur localStorage)
            if (typeof p.wallet_balance === "number" && p.wallet_balance >= 0) {
              setWallet(p.wallet_balance);
              setWalletBalance(p.wallet_balance);
            }
          }
          // Sync historique
          if (res.searches?.length) {
            const remoteHistory = res.searches.map(s => ({ id: s.id, date: s.created_at, type: s.type, data: s.input || {} }));
            localStorage.setItem("viq_history", JSON.stringify(remoteHistory));
          }
          if (res.payments?.length) {
            const remoteBilling = res.payments.map(p => ({ date: p.created_at, plan: p.plan_id||"—", price: p.amount_cents ? `${(p.amount_cents/100).toFixed(2).replace(".",",")} €` : "—", mode: "stripe" }));
            const localWallet = (() => { try { return JSON.parse(localStorage.getItem("viq_billing")||"[]").filter(b=>b.mode==="wallet"); } catch(e) { return []; } })();
            localStorage.setItem("viq_billing", JSON.stringify([...remoteBilling, ...localWallet]));
          }
        }
      } catch(e) {}
    }
  };
  const handleCookieAccept = () => { try { localStorage.setItem("viq_cookies", "accepted"); } catch(e) {} setCookieStatus("accepted"); };
  const handleCookieRefuse = () => { try { localStorage.setItem("viq_cookies", "refused"); } catch(e) {} setCookieStatus("refused"); };

  // ─── ROUTING SEO ──────────────────────────────────────────────────────────
  const viqPath = useViqRoute();
  const goHome = () => viqNavigate("/");
  const seoCta = () => {
    playEstimationClick();
    if (!user) { setPendingPanel("sell"); setShowAuth(true); }
    else setActivePanel("sell");
  };
  const seoContact = () => setShowContact(true);
  const seoDemo = () => setShowDemoBooking(true);

  // v82 — Règle SEO/GEO : la page d'accueil elle-même n'avait ni meta
  // description, ni canonical, ni données structurées — alors que les pages
  // programmatiques (piliers, marques, métiers) en avaient déjà. C'est
  // pourtant la page la plus importante du site. Organization +
  // SoftwareApplication (identité/entité, utile au SEO classique comme aux
  // moteurs génératifs) + FAQPage (réutilise le contenu réel de FAQ_ITEMS,
  // jamais un contenu inventé pour l'occasion).
  useEffect(() => {
    if (viqEnv() !== "public" || viqPath !== "/") return;
    viqSetMeta({
      title: "VehicleIQ — Estimation et cotation voiture, contrôle VIN/plaque par IA",
      description: "Estimation voiture gratuite et cotation en temps réel : VehicleIQ calcule la valeur de votre véhicule d'occasion, contrôle son historique via immatriculation ou VIN, recherche des professionnels par SIRET, et recommande les meilleures annonces par intelligence artificielle.",
      path: "/",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "name": "VehicleIQ",
            "url": "https://vehicleiq.fr",
            "logo": "https://vehicleiq.fr/logo.png",
            "email": "contact@vehicleiq.fr",
            "sameAs": [],
          },
          {
            "@type": "SoftwareApplication",
            "name": "VehicleIQ",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "description": "Plateforme d'intelligence automobile : contrôle d'historique véhicule (VIN/plaque), cotation en temps réel, recommandations d'achat par IA, et outils de vente pour les professionnels de l'automobile.",
            "offers": { "@type": "Offer", "priceCurrency": "EUR", "price": "4.99" },
            "url": "https://vehicleiq.fr",
          },
          {
            "@type": "FAQPage",
            "mainEntity": FAQ_ITEMS.slice(0, 15).map(f => ({
              "@type": "Question", "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
          },
        ],
      },
    });
  }, [viqPath]);

  // Page invitation multi-agent ?
  if (viqPath === "/invitation") {
    return (
      <>
        <style>{CSS}</style>
        <AcceptInvitationPage
          user={user}
          onShowAuth={() => { setShowAuth(true); }}
          onAccepted={() => { /* le redirect interne s'occupe de tout */ }}
        />
        {showAuth && <AuthModal onClose={() => { setShowAuth(false); }} onAuth={handleAuth} defaultMode="signup" />}
      </>
    );
  }
  // Page pilier ?
  if (PILLAR_PAGES[viqPath]) {
    return (
      <>
        <style>{CSS}</style>
        <PillarPage data={PILLAR_PAGES[viqPath]} path={viqPath}
          onCta={seoCta} onContact={seoContact} onHome={goHome} onAPI={() => setShowAPI(true)} />
        {activePanel === "sell" && <SellPanel onClose={() => { setActivePanel(null); setSellPrefill(null); }} sellPrefill={sellPrefill} user={user} onShowAuth={() => { setPendingPanel("sell"); setShowAuth(true); }} onWalletChange={() => {}} onShowRecharge={() => {}} />}
        {showAuth && <AuthModal onClose={() => { setShowAuth(false); }} onAuth={handleAuth} defaultMode={authDefaultMode} />}
        {showContact && <ContactModal onClose={() => setShowContact(false)} />}
        {showDemoBooking && <DemoBookingModal onClose={() => setShowDemoBooking(false)} />}
      </>
    );
  }
  // Page métier ?
  const metierMatch = viqPath.match(/^\/solutions\/([a-z0-9-]+)$/);
  if (metierMatch && METIER_PAGES[metierMatch[1]]) {
    return (
      <>
        <style>{CSS}</style>
        <MetierPage slug={metierMatch[1]}
          onCta={seoDemo} onContact={seoContact} onHome={goHome} onAPI={() => setShowAPI(true)} />
        {showContact && <ContactModal onClose={() => setShowContact(false)} />}
        {showDemoBooking && <DemoBookingModal onClose={() => setShowDemoBooking(false)} />}
        {showAuth && <AuthModal onClose={() => { setShowAuth(false); }} onAuth={handleAuth} defaultMode={authDefaultMode} />}
      </>
    );
  }
  // Page cote modèle+motorisation : /cote/[marque]/[modele]/[motorisation]
  const coteModelMatch = viqPath.match(/^\/cote\/([a-z0-9-]+)\/([a-z0-9-]+)\/([a-z0-9-]+)$/);
  if (coteModelMatch && COTE_CATALOG[coteModelMatch[1]]) {
    return (
      <>
        <style>{CSS}</style>
        <CoteModelPage brandKey={coteModelMatch[1]} modelSlug={coteModelMatch[2]} motorSlug={coteModelMatch[3]}
          onCta={seoCta} onContact={seoContact} onHome={goHome} onAPI={() => setShowAPI(true)} />
        {activePanel === "sell" && <SellPanel onClose={() => { setActivePanel(null); setSellPrefill(null); }} sellPrefill={sellPrefill} user={user} onShowAuth={() => { setPendingPanel("sell"); setShowAuth(true); }} onWalletChange={() => {}} onShowRecharge={() => {}} />}
        {showAuth && <AuthModal onClose={() => { setShowAuth(false); }} onAuth={handleAuth} defaultMode={authDefaultMode} />}
        {showContact && <ContactModal onClose={() => setShowContact(false)} />}
        {showDemoBooking && <DemoBookingModal onClose={() => setShowDemoBooking(false)} />}
      </>
    );
  }
  // Page cote marque : /cote/[marque]
  const coteBrandMatch = viqPath.match(/^\/cote\/([a-z0-9-]+)$/);
  if (coteBrandMatch && COTE_CATALOG[coteBrandMatch[1]]) {
    return (
      <>
        <style>{CSS}</style>
        <CoteBrandPage brandKey={coteBrandMatch[1]}
          onCta={seoCta} onContact={seoContact} onHome={goHome} onAPI={() => setShowAPI(true)} />
        {activePanel === "sell" && <SellPanel onClose={() => { setActivePanel(null); setSellPrefill(null); }} sellPrefill={sellPrefill} user={user} onShowAuth={() => { setPendingPanel("sell"); setShowAuth(true); }} onWalletChange={() => {}} onShowRecharge={() => {}} />}
        {showAuth && <AuthModal onClose={() => { setShowAuth(false); }} onAuth={handleAuth} defaultMode={authDefaultMode} />}
        {showContact && <ContactModal onClose={() => setShowContact(false)} />}
        {showDemoBooking && <DemoBookingModal onClose={() => setShowDemoBooking(false)} />}
      </>
    );
  }

  return (
    <>
      <style>{CSS}</style>

      {/* ─── SPLASH LOADER ─── */}
      <div className={`app-loader${loaderOut ? " fade-out" : ""}`}>
        <div className="loader-logo">
          <div className="loader-monogram">
            V<span>I</span>Q.AI
            <span className="loader-dot-pulse" />
          </div>
          <div className="loader-tagline">Engine · Initialisation…</div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:".6rem" }}>
          <div className="loader-bar-wrap">
            <div className="loader-bar" />
          </div>
          <div className="loader-status">CHARGEMENT DES DONNÉES MARCHÉ</div>
        </div>
      </div>

      <div className="app" style={{ opacity: appReady ? 1 : 0, transition: "opacity .35s ease" }}>
        <section className="hero">
          <div className="hero-grid" />
          <div className="hero-gradient" />

          <div className="hero-content">

            {/* ─── NAV B2B FRESHWORKS-STYLE ─── */}
            <nav>
              {/* Logo — cliquable, retour accueil */}
              <div className="brand-mark brand-mark-clickable"
                role="button" tabIndex={0}
                onClick={() => { if (window.location.pathname !== "/") viqNavigate("/"); setActivePanel(null); setShowB2B(false); setShowPricing(false); setShowBlog(false); setShowFAQ(false); setShowAdmin(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { setActivePanel(null); window.scrollTo({ top: 0, behavior: "smooth" }); } }}
                aria-label="Retour à l'accueil VehicleIQ">
                <div className="logo">
                  <div className="logo-dot" />
                  Vehicle<span>IQ</span>
                </div>
                <div className="brand-tagline">VIQ.AI Engine</div>
              </div>

              {/* Nav center links */}
              <div className="nav-center">
                {viqEnv() === "app" && user ? null : (
                  <>
                    <button className="nav-link" onClick={() => setShowPricing(true)}>Tarification</button>
                    <button className="nav-link" onClick={() => setShowFAQ(true)}>Ressources</button>
                    <button className="nav-link" onClick={() => setShowBlog(true)}>Blog</button>
                    <button className="nav-link" onClick={() => setShowProductTour(true)} title="À propos de VehicleIQ · Vidéo de présentation + accès rapide aux fonctionnalités">
                      <span style={{ marginRight: ".3rem" }}>👋</span> À propos
                    </button>
                    <button className="nav-link" onClick={() => setShowAPI(true)}>Intégration API</button>
                    {isAdmin && (
                      <button className="nav-link" onClick={() => setShowAdmin(true)}>Admin</button>
                    )}
                  </>
                )}
              </div>

              {/* Nav right */}
              <div className="nav-actions">
                {user ? (
                  <>
                    {/* v112 — Règle : la barre de navigation supérieure est
                        supprimée (elle prenait toute la largeur pour rien).
                        Dashboard Pro / Affaires / Mon équipe / Paiements /
                        Mon espace vivent désormais UNIQUEMENT dans le menu
                        hamburger, qui devient le centre de navigation — le
                        contenu principal gagne tout l'espace horizontal. */}
                    <button
                      className="nav-hamburger"
                      title="Menu"
                      onClick={() => setShowMobileMenu(true)}
                      style={{
                        width:36, height:36, borderRadius:8,
                        background:"none", border:"1.5px solid var(--border)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        cursor:"pointer", flexShrink:0, transition:"all .15s",
                        color:"var(--text)"
                      }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <line x1="3" y1="6" x2="21" y2="6"/>
                        <line x1="3" y1="12" x2="21" y2="12"/>
                        <line x1="3" y1="18" x2="21" y2="18"/>
                      </svg>
                    </button>
                    <button
                      title="Déconnexion"
                      onClick={() => { signOut(auth).catch(()=>{}); setUser(null); setAffiliateData(null); setWallet(0); setWalletBalance(0); window.__viq_uid = null; clearUserLocalStorage(); }}
                      style={{ width:32, height:32, borderRadius:"50%", background:"none", border:"1.5px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0, transition:"all .15s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor="#F04438"; e.currentTarget.style.background="rgba(240,68,56,.07)"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor="var(--border)"; e.currentTarget.style.background="none"; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F04438" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/>
                      </svg>
                    </button>
                  </>
                ) : (
                  <>
                    {/* Bouton hamburger pour mobile non-connecté aussi */}
                    <button
                      className="nav-mobile-only nav-hamburger"
                      title="Menu"
                      onClick={() => setShowMobileMenu(true)}
                      style={{
                        width:36, height:36, borderRadius:8,
                        background:"none", border:"1.5px solid var(--border)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        cursor:"pointer", flexShrink:0, transition:"all .15s",
                        color:"var(--text)"
                      }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <line x1="3" y1="6" x2="21" y2="6"/>
                        <line x1="3" y1="12" x2="21" y2="12"/>
                        <line x1="3" y1="18" x2="21" y2="18"/>
                      </svg>
                    </button>
                    <button className="btn btn-ghost btn-sm nav-desktop-only" onClick={() => { if (viqEnv() === "public") { viqGoToApp("login"); return; } setAuthDefaultMode(null); setShowAuth(true); }}>Connexion</button>
                    <button className="btn btn-accent btn-sm nav-desktop-only" onClick={() => { if (viqEnv() === "public") { viqGoToApp("signup"); return; } setAuthDefaultMode("signup"); setShowAuth(true); }}>
                      Inscription
                    </button>
                  </>
                )}
                {/* Toggle dark/light — toujours visible */}
                <button
                  title={darkMode ? "Mode clair" : "Mode sombre"}
                  onClick={() => setDarkMode(d => !d)}
                  style={{ width:32, height:32, borderRadius:"50%", background:"none", border:"1.5px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0, transition:"all .15s", fontSize:"14px" }}>
                  {darkMode ? "☀️" : "🌙"}
                </button>
              </div>
            </nav>

            {/* SaaS multi-domaine : page d'accueil dédiée sur user.vehicleiq.fr */}
            {viqEnv() === "app" && (
              <AppHome
                user={user}
                isAdmin={isAdmin}
                onAffiliate={() => setShowAffiliateAdmin(true)}
                onAdmin={() => setShowAdmin(true)}
                onLogin={() => { setAuthDefaultMode(null); setShowAuth(true); }}
                onSignup={() => { setAuthDefaultMode("signup"); setShowAuth(true); }}
                onOpenDossiers={(id) => { setDossierInitialKey(id || null); setShowDossiers(true); }}
                onCreateDossier={(prefill) => {
                  if (!user) { setPendingPanel("check"); setShowAuth(true); }
                  else { if (prefill) window.__viq_vin_prefill = prefill; setActivePanel("check"); }
                }}
                onOpenLeads={() => openDashTab("leads")}
                onOpenDocuments={() => openDashTab("documents")}
                onOpenProspection={() => openDashTab("prospection")}
                onOpenCalls={() => openDashTab("prospection")}
                onOpenSettings={() => { setHistoryInitialTab("profile"); setShowHistory(true); }}
                onOpenAdmin={() => setShowAdmin(true)}
                onDashboard={() => setShowDashboard(true)}
                onHistory={() => { setHistoryInitialTab("requests"); setShowHistory(true); }}
                onPayment={() => { setHistoryInitialTab("payment"); setShowHistory(true); }}
              />
            )}
            {showDossiers && (
              <DossierVIQPage
                user={user}
                initialKey={dossierInitialKey}
                onSelectedChange={setDossierSelectedId}
                onClose={() => { setShowDossiers(false); setDossierSelectedId(null); setDossierInitialKey(null); }}
                onOpenCheck={(prefill) => {
                  setShowDossiers(false);
                  if (prefill) window.__viq_vin_prefill = prefill;
                  setActivePanel("check");
                }}
                onOpenSell={(prefillAnswers) => {
                  setShowDossiers(false);
                  setSellPrefill(prefillAnswers);
                  setActivePanel("sell");
                }}
                onOpenSaleTracking={() => openDashTab("sale")}
                onCreateMandat={(prefill) => { setMandatPrefill(prefill || null); setShowMandat(true); }}
                onCreateCession={(prefill) => { setCessionPrefill(prefill || null); setShowCession(true); }}
                onOpenLeads={() => { setShowDossiers(false); openDashTab("leads"); }}
                onOpenProspection={() => { setShowDossiers(false); openDashTab("prospection"); }}
              />
            )}
            {viqEnv() !== "app" && (<>
            {/* ─── HERO B2B — 2 colonnes (refonte moderne, fond sombre) ─── */}
            <div className="hero-center hero-modern">
              <div className="hero-2col">

                {/* Colonne gauche : message (brief §1) */}
                <div className="hero-col-left">

                  <div className="hero-badge" style={{ display: "inline-flex", alignItems: "center", gap: ".4rem", fontSize: ".78rem", fontWeight: 700, color: "var(--accent)", background: "rgba(168,128,58,.12)", border: "1px solid rgba(168,128,58,.3)", borderRadius: 20, padding: ".35rem .9rem", marginBottom: "1.1rem" }}>
                    À partir de 4,99 € par véhicule pour les pro
                  </div>

                  <h1 className="hero-h1">
                    Décodage de plaque, estimation rapide,<br />
                    <em>suivi de vente — le tout en un seul endroit</em>
                  </h1>

                  <p className="hero-sub">
                    Contrôle d'historique, cote en temps réel, aide à l'achat par IA et suivi de vente : chaque véhicule traité obtient son propre Dossier VIQ, sans tableurs ni outils éparpillés.
                  </p>

                  <div className="hero-cta-row">
                    {user ? (
                      <>
                        <button className="btn btn-accent hero-cta-primary"
                          onClick={() => { playEstimationClick(); setActivePanel("sell"); }}>
                          Lancer une cotation
                        </button>
                        <button className="btn btn-outline hero-cta-secondary"
                          onClick={() => { setDashboardPreselect(null); setShowDashboard(true); }}>
                          Mon tableau de bord →
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-accent hero-cta-primary" onClick={openLeadForm}>
                          Estimer gratuitement mon véhicule
                        </button>
                        {/* v42 — Déplacé depuis la carte d'illustration, à côté de l'autre CTA. */}
                        <button className="btn btn-outline hero-cta-secondary"
                          onClick={() => { playEstimationClick(); setPendingPanel("check"); setShowAuth(true); }}>
                          Espace Professionnel
                        </button>
                      </>
                    )}
                  </div>

                  {/* Preuve sociale */}
                  <div className="hero-social">
                    <div className="hero-social-avatars">
                      <span style={{ background: "#2E6F5C" }}>GA</span>
                      <span style={{ background: "#A8803A" }}>MV</span>
                      <span style={{ background: "#1E2A4A" }}>JD</span>
                      <span style={{ background: "#6E6E78" }}>+</span>
                    </div>
                    <div>
                      <div className="hero-social-stars">★★★★★</div>
                      <div className="hero-social-text">
                        <strong>+5000 pros</strong> atteignables sur VehicleIQ.fr
                      </div>
                    </div>
                  </div>

                  {/* v90 — Règle : partenariat CarVertical — pas de logo image (aucun
                      fichier de marque disponible ici), badge textuel sobre à la place. */}
                  <div style={{ display: "inline-flex", alignItems: "center", gap: ".5rem", marginTop: ".9rem", fontSize: ".78rem", color: "var(--sub)", background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 10, padding: ".45rem .8rem" }}>
                    <strong style={{ color: "var(--accent)" }}>Partenaire CarVertical</strong>
                    <span>— 20 % de réduction grâce à VehicleIQ.fr</span>
                  </div>
                </div>

                {/* Colonne droite : illustration du Dossier VIQ (v41 — remplace le
                    mini-simulateur de décodage de plaque, qui vendait un outil
                    ponctuel plutôt que la fiche véhicule consolidée). */}
                <div className="hero-col-right">
                  <HeroDossierIllustration />
                </div>

              </div>

              {/* ─── STATS BAR (intégrée au hero sombre) ─── */}
              <div className="stats-bar hero-modern-stats">
                {[
                  ["2.4M+", "Véhicules analysés"],
                  ["100+", "Plateformes scrutées pour la cote"],
                  ["+8000", "Modèles catalogue"],
                  ["1", "Dossier par véhicule, du contrôle à la vente"],
                ].map(([v, l]) => (
                  <div key={l} className="stat-item">
                    <div className="stat-val">{v}</div>
                    <div className="stat-label">{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ─── BANDEAU CONFIANCE ─── */}
            <div className="hero-trustbar">
              <div className="hero-trustbar-label">Ils utilisent VehicleIQ au quotidien</div>
              <div className="hero-trustbar-items">
                <span>Garages indépendants</span>
                <span>Marchands VO</span>
                <span>Mandataires</span>
              </div>
            </div>
            </>)}

          </div>
        </section>

        {viqEnv() !== "app" && (<>
        {/* ═══ HOMEPAGE — architecture conversion (validée le 16/07) ═══
            Sections retirées du scroll principal (redondantes ou à faible impact
            de conversion, cf. audit) : WhySection, DashboardMockupSection,
            CarteGriseServiceCard, MarketSection, LiveMarketPanel (preview),
            SolutionsByActorSection, ValuePropositionSection,
            BrandsAndPlatformsSection, SeoBrandPagesSection, RecentArticles.
            Fonctions conservées (non supprimées) pour ne rien casser ailleurs
            dans le code — simplement retirées de CE flux. */}

        {/* §2 — LE PROBLÈME */}
        <ProblemSection />

        {/* §3 — COMMENT ÇA MARCHE (3 étapes) */}
        <ProcessSection
          onLaunch={openLeadForm}
        />

        {/* §4 — CE QUE VOUS OBTENEZ (bénéfices, pas fonctionnalités) */}
        <FourEnginesSection
          onSell={() => { playEstimationClick(); if (!user) { setPendingPanel("sell"); setShowAuth(true); } else setActivePanel("sell"); }}
          onCheck={() => { if (!user) { setPendingPanel("check"); setShowAuth(true); } else setActivePanel("check"); }}
          onBuy={() => { if (!user) { setPendingPanel("buy"); setShowAuth(true); } else setActivePanel("buy"); }}
          onMarket={() => setShowB2B(true)}
        />

        {/* §5 — COMPARATIF (4 colonnes : Argus / CarVertical / manuel / VehicleIQ) */}
        <ArgusAlternativeSection
          onLaunch={openLeadForm}
        />

        {/* §6 — PREUVES SOCIALES (remontées — étaient en position 12/16 avant) */}
        <TestimonialsSection />
        <LogosStrip />
        <TrustStrip />

        {/* §7 — TARIFICATION (une offre mise en avant, dans le scroll) */}
        <HomePricingSection onLaunch={openLeadForm} onSeeAll={() => setShowPricing(true)} />

        {/* §8 — FAQ (objections commerciales, pas mots-clés SEO) */}
        <HomeFAQSection
          onLaunch={openLeadForm}
          onContact={() => setShowContact(true)}
        />

        {/* ─── PRO CTA FINAL ─── */}
        <ProCtaSection
          onStart={openLeadForm}
        />
        </>)}
      </div>

      {activePanel === "check" && <VINPanel onClose={() => setActivePanel(null)} user={user} affiliateData={affiliateData} onShowAuth={() => { setPendingPanel("check"); setShowAuth(true); }} onWalletChange={refreshWallet} onShowRecharge={() => setShowRecharge(true)} onLaunchBatchSell={() => { window.__viq_batch_sell_autolaunch = true; setActivePanel("sell"); }} onOpenDossiers={(id) => { setActivePanel(null); setDossierInitialKey(id || null); setShowDossiers(true); }} />}
      {activePanel === "buy" && <BuyPanel onClose={() => setActivePanel(null)} user={user} onShowAuth={() => { setPendingPanel("buy"); setShowAuth(true); }} onWalletChange={refreshWallet} onShowRecharge={() => setShowRecharge(true)} />}
      {activePanel === "sell" && <SellPanel onClose={() => { setActivePanel(null); setSellPrefill(null); }} sellPrefill={sellPrefill} user={user} onShowAuth={() => { setPendingPanel("sell"); setShowAuth(true); }} onWalletChange={refreshWallet} onShowRecharge={() => setShowRecharge(true)} />}
      {showAuth && <AuthModal onClose={() => { setShowAuth(false); setAuthDefaultMode(null); }} onAuth={handleAuth} defaultMode={authDefaultMode} />}
      {showLegal && <LegalModal type={showLegal} onClose={() => setShowLegal(null)} />}
      {selectedModel && <ModelPopup brand={selectedModel.brand} model={selectedModel.model} onClose={() => setSelectedModel(null)} onStart={() => { setSelectedModel(null); if (!user) { setPendingPanel("sell"); setShowAuth(true); } else setActivePanel("sell"); }} />}
      {cookieStatus === "pending" && <CookieBanner onAccept={handleCookieAccept} onRefuse={handleCookieRefuse} onLegal={setShowLegal} />}
      {showService && <ServiceModal serviceId={showService} onClose={() => setShowService(null)} onLaunch={(id) => { setShowService(null); setActivePanel(id); }} />}
      {showFAQ && <FAQModal onClose={() => setShowFAQ(false)} />}
      {showB2B && <B2BModal onClose={() => setShowB2B(false)} user={user} />}
      {showAPI && <APIIntegrationModal onClose={() => setShowAPI(false)} user={user} />}
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
        {showDemoBooking && <DemoBookingModal onClose={() => setShowDemoBooking(false)} />}
      {showDemoVideo && <DemoVideoModal onClose={() => setShowDemoVideo(false)} />}
      {showOrgManagement && (
        <OrgManagementModal
          user={user}
          onClose={() => setShowOrgManagement(false)}
        />
      )}
      {showProductTour && !showOnboarding && (
        <OnboardingFirstVisitModal
          onClose={() => {
            try { localStorage.setItem("viq_product_tour_seen", "1"); } catch(_) {}
            setShowProductTour(false);
          }}
          onStartEstimation={() => setActivePanel && setActivePanel("sell")}
          onStartCheck={() => setActivePanel && setActivePanel("check")}
          onStartBuy={() => setActivePanel && setActivePanel("buy")}
        />
      )}
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} onSignup={() => { setShowPricing(false); setShowRecharge(true); }} user={user} pricingUsage={pricingUsage} />}
      {showBlog && <BlogPage onClose={() => setShowBlog(false)} onContact={() => { setShowBlog(false); setShowContact(true); }} />}
      {showDashboard && (
        <ProDashboardModal
          onClose={() => { setShowDashboard(false); setDashboardPreselect(null); }}
          reqHistory={dashHistory.length > 0 ? dashHistory : (() => { try { return JSON.parse(localStorage.getItem("viq_history") || "[]"); } catch(e) { return []; } })()}
          user={user}
          siret={appSiret}
          onRefreshHistory={reloadDashHistory}
          initialEstimationId={dashboardPreselect?.estimationId || null}
          initialTab={dashboardPreselect?.tab || "overview"}
          onLaunchService={(svc) => {
            // svc ∈ "check" | "sell" | "buy" — ferme le dashboard et ouvre le panel demandé
            setShowDashboard(false);
            if (!user) {
              setPendingPanel(svc);
              setShowAuth(true);
            } else {
              setActivePanel(svc);
            }
          }}
          onOpenPricing={() => { setShowDashboard(false); setShowPricing(true); }}
          onCreateMandat={(prefill) => { setMandatPrefill(prefill || null); setShowMandat(true); }}
          onCreateCession={(prefill) => { setCessionPrefill(prefill || null); setShowCession(true); }}
        />
      )}
      {showRecharge && <RechargeModal onClose={() => setShowRecharge(false)} onRecharged={(qty) => { refreshWallet(); setShowRecharge(false); }} user={user} onShowAuth={() => setShowAuth(true)} />}
      {showAdmin && <AdminDashboard user={user} onClose={() => setShowAdmin(false)} />}
      {showAffiliateAdmin && <AffiliateDashboard user={user} affiliateData={affiliateData} onClose={() => setShowAffiliateAdmin(false)} />}
      {showChat && <VIQAIChat user={user} onClose={() => setShowChat(false)} onShowAuth={() => setShowAuth(true)} />}
      {/* Bouton chat flottant VIQ.AI — remonté quand le FAB est visible
          pour éviter le chevauchement, en particulier sur mobile. */}
      {!showChat && <button
        onClick={() => setShowChat(true)}
        className={`viq-chat-fab${fabVisible ? " above-fab" : ""}`}
        title="Assistant VIQ.AI"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" fill="white"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="white" strokeWidth="2" strokeLinecap="round"/></svg>
        {/* Badge "IA" */}
        <div className="viq-chat-fab-badge">VIQ.AI®</div>
      </button>}

      {showHistory && <HistoryModal initialTab={historyInitialTab} onClose={() => { setShowHistory(false); setHistoryInitialTab("requests"); }} user={user} pricingUsage={pricingUsage} onOpenPricing={() => { setShowHistory(false); setShowPricing(true); }} onRecharge={() => { setShowHistory(false); setShowRecharge(true); }} onProfileSaved={async (p) => {
      if (user?.uid && (p.phone || p.address)) {
        try { await apiPost("/api/users/profile", { phone: p.phone, address: p.address }); } catch(e) {}
      }
    }} />}
      {showOnboarding && <OnboardingModal onClose={closeOnboarding} onShowAuth={(u) => { setUser(u); }} />}

      {activePanel === "check" && <VINPanel onClose={() => setActivePanel(null)} user={user} affiliateData={affiliateData} onShowAuth={() => { setPendingPanel("check"); setShowAuth(true); }} onWalletChange={refreshWallet} onShowRecharge={() => setShowRecharge(true)} onLaunchBatchSell={() => { window.__viq_batch_sell_autolaunch = true; setActivePanel("sell"); }} onOpenDossiers={(id) => { setActivePanel(null); setDossierInitialKey(id || null); setShowDossiers(true); }} />}
      {activePanel === "buy" && <BuyPanel onClose={() => setActivePanel(null)} user={user} onShowAuth={() => { setPendingPanel("buy"); setShowAuth(true); }} onWalletChange={refreshWallet} onShowRecharge={() => setShowRecharge(true)} />}
      {activePanel === "sell" && <SellPanel onClose={() => { setActivePanel(null); setSellPrefill(null); }} sellPrefill={sellPrefill} user={user} onShowAuth={() => { setPendingPanel("sell"); setShowAuth(true); }} onWalletChange={refreshWallet} onShowRecharge={() => setShowRecharge(true)} />}
      {showAuth && <AuthModal onClose={() => { setShowAuth(false); setAuthDefaultMode(null); }} onAuth={handleAuth} defaultMode={authDefaultMode} />}
      {showLegal && <LegalModal type={showLegal} onClose={() => setShowLegal(null)} />}
      {selectedModel && <ModelPopup brand={selectedModel.brand} model={selectedModel.model} onClose={() => setSelectedModel(null)} onStart={() => { setSelectedModel(null); if (!user) { setPendingPanel("sell"); setShowAuth(true); } else setActivePanel("sell"); }} />}
      {cookieStatus === "pending" && <CookieBanner onAccept={handleCookieAccept} onRefuse={handleCookieRefuse} onLegal={setShowLegal} />}
      {showService && <ServiceModal serviceId={showService} onClose={() => setShowService(null)} onLaunch={(id) => { setShowService(null); setActivePanel(id); }} />}
      {showFAQ && <FAQModal onClose={() => setShowFAQ(false)} />}
      {showB2B && <B2BModal onClose={() => setShowB2B(false)} user={user} />}
      {showAPI && <APIIntegrationModal onClose={() => setShowAPI(false)} user={user} />}
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
        {showDemoBooking && <DemoBookingModal onClose={() => setShowDemoBooking(false)} />}
      {showDemoVideo && <DemoVideoModal onClose={() => setShowDemoVideo(false)} />}
      {showOrgManagement && (
        <OrgManagementModal
          user={user}
          onClose={() => setShowOrgManagement(false)}
        />
      )}
      {showProductTour && !showOnboarding && (
        <OnboardingFirstVisitModal
          onClose={() => {
            try { localStorage.setItem("viq_product_tour_seen", "1"); } catch(_) {}
            setShowProductTour(false);
          }}
          onStartEstimation={() => setActivePanel && setActivePanel("sell")}
          onStartCheck={() => setActivePanel && setActivePanel("check")}
          onStartBuy={() => setActivePanel && setActivePanel("buy")}
        />
      )}
      {showPricing && <PricingModal onClose={() => setShowPricing(false)} onSignup={() => { setShowPricing(false); setShowRecharge(true); }} user={user} pricingUsage={pricingUsage} />}
      {showBlog && <BlogPage onClose={() => setShowBlog(false)} onContact={() => { setShowBlog(false); setShowContact(true); }} />}
      {showRecharge && <RechargeModal onClose={() => setShowRecharge(false)} onRecharged={(qty) => { refreshWallet(); setShowRecharge(false); }} user={user} onShowAuth={() => setShowAuth(true)} />}
      {showAdmin && <AdminDashboard user={user} onClose={() => setShowAdmin(false)} />}
      {showAffiliateAdmin && <AffiliateDashboard user={user} affiliateData={affiliateData} onClose={() => setShowAffiliateAdmin(false)} />}
      {showChat && <VIQAIChat user={user} onClose={() => setShowChat(false)} onShowAuth={() => setShowAuth(true)} />}
      {showMobileMenu && (
        <MobileMenuDrawer
          onClose={() => setShowMobileMenu(false)}
          user={user}
          isAdmin={isAdmin}
          pricingUsage={pricingUsage}
          actions={{
            // Espaces nécessitant une connexion : sur le site public, on redirige
            // vers l'application (user.vehicleiq.fr) en conservant l'intention.
            openRecharge: () => viqEnv() === "public" ? viqGoToApp("recharge") : setShowRecharge(true),
            openPayment: () => viqEnv() === "public" ? viqGoToApp("payment") : (setHistoryInitialTab("payment"), setShowHistory(true)),
            openB2B: () => setShowB2B(true),
            openAPI: () => setShowAPI(true),
            openPricing: () => setShowPricing(true),
            openBlog: () => setShowBlog(true),
            openFAQ: () => setShowFAQ(true),
            openHistory: () => viqEnv() === "public" ? viqGoToApp("history") : setShowHistory(true),
            openDashboard: () => viqEnv() === "public" ? viqGoToApp("dashboard") : openDashboard(),
            openAffiliate: () => viqEnv() === "public" ? viqGoToApp("affiliate") : setShowAffiliateAdmin(true),
            openOrgManagement: () => setShowOrgManagement(true),
            openAdmin: () => viqEnv() === "public" ? viqGoToApp("admin") : setShowAdmin(true),
            openLogin: () => viqEnv() === "public" ? viqGoToApp("login") : (setAuthDefaultMode(null), setShowAuth(true)),
            openSignup: () => viqEnv() === "public" ? viqGoToApp("signup") : (setAuthDefaultMode("signup"), setShowAuth(true)),
            launchService: (svc) => {
              if (viqEnv() === "public") { viqGoToApp("svc:" + svc); return; }
              if (!user) { setPendingPanel(svc); setShowAuth(true); }
              else setActivePanel(svc);
            },
          }}
        />
      )}

      {/* ─── FAB flottant : Estimer un VO / Trouver un VO ───
          Toujours présent dans le DOM (transitions fluides) mais visible
          seulement quand le hero a quitté le viewport (fabVisible=true),
          sur la home, et hors de tout modal/panel ouvert. */}
      {viqPath === "/" && !activePanel && !showB2B && !showPricing && !showBlog
        && !showFAQ && !showAdmin && !showAuth && !showContact && !showLegal
        && !selectedModel && !showService && (
        <div
          className={`viq-fab${fabVisible ? " visible" : ""}`}
          role="navigation"
          aria-label="Actions rapides"
          aria-hidden={!fabVisible}
        >
          <button
            className="viq-fab-btn primary"
            onClick={() => {
              if (!user) { setPendingPanel("check"); setShowAuth(true); }
              else setActivePanel("check");
            }}
            aria-label="Créer un nouveau Dossier VIQ"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9"/>
              <line x1="12" y1="8" x2="12" y2="16"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
            Nouveau Dossier
          </button>
          <button
            className="viq-fab-btn secondary"
            onClick={openLeadForm}
            aria-label="Demander une démo gratuite"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 17 9 11 13 15 21 7"/>
              <polyline points="14 7 21 7 21 14"/>
            </svg>
            Démo gratuite
          </button>
        </div>
      )}

      {/* ─── SOCIAL PROOF / RATINGS — déplacé en bas de page, format compact ─── */}
      <div className="ratings-bottom">
        <ServiceRatings user={user} onShowAuth={() => setShowAuth(true)} inline={true} />
      </div>

      {viqEnv() !== "app" && <Footer onLegal={setShowLegal} onModel={(brand, model) => setSelectedModel({ brand, model })} onService={setShowService} onDashTab={openDashTab} onFAQ={() => setShowFAQ(true)} onB2B={() => setShowB2B(true)} onAPI={() => setShowAPI(true)} onContact={() => setShowContact(true)} onBlog={() => setShowBlog(true)} onCharte={() => setShowCharte(true)} user={user} onShowAuth={() => setShowAuth(true)} />}
      {showCharte && <CharteModal onClose={() => setShowCharte(false)} />}
      {showMandat && <MandatModal onClose={() => { setShowMandat(false); setMandatPrefill(null); }} user={user} siret={appSiret} prefill={mandatPrefill} />}
      {showCession && <CessionModal onClose={() => { setShowCession(false); setCessionPrefill(null); }} prefill={cessionPrefill} user={user} />}
    </>
  );
}
